import React, { useState, useMemo, createContext, useContext, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, AuthProvider } from './context/AuthContext';
import SyncButton from './components/SyncButton';
import SettlementStatement, { buildPlayerSummary } from './components/SettlementStatement';
import PlayerAvatar from './components/PlayerAvatar';
import PlayerPhotoUpload from './components/PlayerPhotoUpload';
import BulkInvitationModal from './components/BulkInvitationModal';
import AddToCalendarButton from './components/AddToCalendarButton';
import PhotoViewerModal from './components/PhotoViewerModal';
import LandingPage from './components/LandingPage';
import AdLandingPage from './components/AdLandingPage';
import EventsPage from './components/EventsPage';
import GroupsPage from './components/GroupsPage';   // Nov 1, 2026 — /groups, Weekly Golf Groups (plans & pricing)
import EventPage, { PayButtons, payOptions, money } from './components/EventPage'; // Oct 11, 2026 — public event page /e/<address>; Oct 23 — pay buttons
import EventPagePanel from './components/EventPageSetup';      // Oct 11, 2026 — Event page block in Set up event
import MyTeam from './components/MyTeam';                      // Oct 26, 2026 — a captain fills in his team (player app)
import EventTeamsModal, { EventTeamsButton, fetchEventTeams } from './components/EventTeams'; // Oct 26, 2026 — Teams & captains (organizer); the modal opens from the Ready Check too (Oct 28)
import GamesPage from './components/GamesPage';   // Oct 5, 2026 — public /games
import EventPassStart from './components/EventPassStart';
import EventPassReady from './components/EventPassReady';
import PlayerImport from './components/PlayerImport';
import TryScoring from './components/TryScoring';
import { SAMPLE_BOARD, SAMPLE_ROWS } from './sampleEvent';
import FoundersPage from './components/FoundersPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import WelcomePage from './components/WelcomePage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
// ============================================================================
// PHASE 2E: Admin Panel Imports
// ============================================================================
import AdminLoginPage from './components/AdminLoginPage';
import AdminDashboard from './components/AdminDashboard';
import AcceptInvitePage from './components/AcceptInvitePage';
import CheckoutSuccessPage from './components/CheckoutSuccessPage';
import SignupSuccessPage from './components/SignupSuccessPage';
import CheckoutCancelPage from './components/CheckoutCancelPage';
import SMSTermsPage from './components/SMSTermsPage';
import PrivacyPage from './components/PrivacyPage';
import HandicapGuidePage from './components/HandicapGuidePage';
import WDHandicapCalc from './components/WDHandicapCalc';
import GuidesIndex from './components/guides/GuidesIndex';
import GuideArticle from './components/guides/GuideArticle';
import CompareIndex from './components/compare/CompareIndex';
import CompareArticle from './components/compare/CompareArticle';
// ============================================================================
// Help System Imports
// ============================================================================
import HelpInfoModal from './components/HelpInfoModal';
import WhatsNewModal, { getUnreadCount } from './components/WhatsNewModal';
import { getHelpContent } from './data/helpContent';
// ============================================================================
// Advertising System Imports
// ============================================================================
import InFeedAd from './components/ads/InFeedAd';
import RSVPSuccessModal from './components/ads/RSVPSuccessModal';
import ScoringStats from './components/scoring/ScoringStats';
import ScorecardAllPlayers from './components/scoring/ScorecardAllPlayers';
import PlayerScorecardModal from './components/scoring/PlayerScorecardModal';

// ============================================================================
// BUILD MARKER - For debugging deployment
// ============================================================================
console.log('=== CANCEL-MODAL-FIX-V2 ===');

// ============================================================================
// Clear Storage Handler - Clears localStorage/sessionStorage when ?clear=storage
// Supports: /dashboard?clear=storage, /portal?clear=storage, /admin?clear=storage
// ============================================================================
if (typeof window !== 'undefined') {
  const urlParams = new URLSearchParams(window.location.search);
  const clearParam = urlParams.get('clear');
  
  if (clearParam === 'storage') {
    console.log('🧹 Clearing all browser storage...');
    localStorage.clear();
    sessionStorage.clear();
    // Remove the parameter and reload to clean URL - PRESERVE HASH
    urlParams.delete('clear');
    const newSearch = urlParams.toString();
    const hash = window.location.hash;
    const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + hash;
    window.location.href = newUrl;
  } else if (clearParam === 'auth') {
    console.log('🧹 Clearing all auth data...');
    // Clear all auth-related localStorage items
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('playerToken');
    localStorage.removeItem('playerTokenTimestamp');
    localStorage.removeItem('user');
    localStorage.removeItem('organization');
    localStorage.removeItem('organizations');
    localStorage.removeItem('impersonatingOrg');
    // Clear sessionStorage
    sessionStorage.removeItem('impersonationToken');
    sessionStorage.removeItem('impersonationOrg');
    // Remove the parameter and reload - PRESERVE HASH for scroll target
    urlParams.delete('clear');
    const newSearch = urlParams.toString();
    const hash = window.location.hash; // Preserve #try-demo etc.
    const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + hash;
    window.location.href = newUrl;
  }
}
// ============================================================================
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// Oct 10, 2026: a player may have no email. Two golfers with NO email are not the
// same golfer — every "same player?" test goes by id, and by email only when both
// actually have one. (Manage Players showed Kirk Wurtzel three times: Mark and
// Phillip, both without email, "matched" the first no-email player in the list.)
const sameEmail = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
const samePlayer = (a, b) => {
  if (!a || !b) return false;
  if (typeof a === 'string' || typeof b === 'string') {
    const ea = typeof a === 'string' ? a : a.email, eb = typeof b === 'string' ? b : b.email;
    return sameEmail(ea, eb);
  }
  if (a.id && b.id) return String(a.id) === String(b.id);
  return sameEmail(a.email, b.email);
};

const CONFIG = {
  TOAST_DURATION_SUCCESS: 2200,
  TOAST_DURATION_ERROR: 3600,
};

// ============================================================================
// useIsMobile — Mobile layout hook (originally Phase 1B May 2026, simplified May 2026)
// Returns true when viewport width < 768px. Earlier versions also gated this
// on a per-org `mobile_layout_enabled` feature flag, but the flag was retired
// once the mobile layout shipped to all orgs. The flag column on `groups` is
// left in place but is no longer read.
// ============================================================================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

// ============================================================================
// PaymentSuccessPage - Shown after admin-initiated Stripe checkout completes
// ============================================================================
function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orgId = searchParams.get('org_id');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [setupMode, setSetupMode] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  
  useEffect(() => {
    const fetchPaymentInfo = async () => {
      if (!sessionId || !orgId) {
        setError('Invalid payment confirmation link.');
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/subscription/payment-success?session_id=${sessionId}&org_id=${orgId}`);
        const data = await response.json();
        
        if (!response.ok) {
          setError(data.error || 'Failed to verify payment.');
          setLoading(false);
          return;
        }
        
        setPaymentInfo(data);
        setSetupMode(data.setup?.needsSetup);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching payment info:', err);
        setError('Failed to verify payment. Please contact support.');
        setLoading(false);
      }
    };
    
    fetchPaymentInfo();
  }, [sessionId, orgId]);
  
  const handleSetupAccount = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      alert('Password must be at least 8 characters.');
      return;
    }
    
    if (password !== confirmPassword) {
      alert('Passwords do not match.');
      return;
    }
    
    setSettingUp(true);
    
    try {
      // Use the setup token to create the user account
      const response = await fetch(`${API_URL}/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: paymentInfo.setup.setupToken,
          password: password
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        alert(data.error || 'Failed to set up account.');
        setSettingUp(false);
        return;
      }
      
      // Store tokens
      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Error setting up account:', err);
      alert('Failed to set up account. Please try again.');
      setSettingUp(false);
    }
  };
  
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)'
      }}>
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <div style={{
            width: 48, height: 48,
            border: '4px solid rgba(255,255,255,0.3)',
            borderTopColor: '#fff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>Verifying payment...</p>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
        padding: 24
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 48,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>⚠️</div>
          <h1 style={{ fontSize: 24, color: '#dc2626', marginBottom: 16 }}>Payment Verification Failed</h1>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>{error}</p>
          <a href="/" style={{
            display: 'inline-block',
            padding: '12px 24px',
            background: '#2d6a4f',
            color: '#fff',
            borderRadius: 8,
            textDecoration: 'none'
          }}>Return to Home</a>
        </div>
      </div>
    );
  }
  
  // If user needs to set up their password
  if (setupMode) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
        padding: 24
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 48,
          maxWidth: 500,
          width: '100%'
        }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
            <h1 style={{ fontSize: 24, color: '#166534', marginBottom: 8 }}>Payment Successful!</h1>
            <p style={{ color: '#6b7280' }}>
              Now let's set up your login for <strong>{paymentInfo?.organization?.name}</strong>
            </p>
          </div>
          
          <div style={{
            background: '#f0fdf4',
            borderRadius: 8,
            padding: 16,
            marginBottom: 24
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#6b7280' }}>Plan:</span>
              <span style={{ fontWeight: 600, color: '#166534' }}>
                {paymentInfo?.organization?.tier?.charAt(0).toUpperCase() + paymentInfo?.organization?.tier?.slice(1)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Billing:</span>
              <span style={{ fontWeight: 600, color: '#166534' }}>
                {paymentInfo?.organization?.billingCycle === 'annual' ? 'Annual' : 'Monthly'}
              </span>
            </div>
          </div>
          
          <form onSubmit={handleSetupAccount}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                Your Email
              </label>
              <input
                type="email"
                value={paymentInfo?.setup?.email || ''}
                disabled
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  background: '#f9fafb',
                  color: '#6b7280',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                Create Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '12px 48px 12px 16px',
                    borderRadius: 8,
                    border: `2px solid ${password.length > 0 && password.length < 8 ? '#fca5a5' : '#e5e7eb'}`,
                    fontSize: 16,
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 18
                  }}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6, 
                marginTop: 6,
                fontSize: 12,
                color: password.length >= 8 ? '#16a34a' : (password.length > 0 ? '#dc2626' : '#6b7280')
              }}>
                <span>{password.length >= 8 ? '✓' : '○'}</span>
                <span>Minimum 8 characters {password.length > 0 && `(${password.length}/8)`}</span>
              </div>
            </div>
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                Confirm Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 8,
                  border: '2px solid #e5e7eb',
                  fontSize: 16,
                  boxSizing: 'border-box'
                }}
              />
              {confirmPassword && password !== confirmPassword && (
                <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
                  Passwords don't match
                </p>
              )}
            </div>
            
            <button
              type="submit"
              disabled={settingUp || password.length < 8 || password !== confirmPassword}
              style={{
                width: '100%',
                padding: '14px 24px',
                background: (settingUp || password.length < 8 || password !== confirmPassword)
                  ? '#9ca3af'
                  : 'linear-gradient(135deg, #2d6a4f, #1b4332)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: (settingUp || password.length < 8 || password !== confirmPassword)
                  ? 'not-allowed'
                  : 'pointer'
              }}
            >
              {settingUp ? 'Setting up...' : '🚀 Complete Setup & Go to Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }
  
  // User already exists - just show success and redirect options
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 48,
        maxWidth: 500,
        width: '100%',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h1 style={{ fontSize: 28, color: '#166534', marginBottom: 8 }}>Payment Successful!</h1>
        <p style={{ color: '#6b7280', marginBottom: 24 }}>
          Your subscription for <strong>{paymentInfo?.organization?.name}</strong> is now active.
        </p>
        
        <div style={{
          background: '#f0fdf4',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          textAlign: 'left'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#6b7280' }}>Plan:</span>
            <span style={{ fontWeight: 600, color: '#166534' }}>
              {paymentInfo?.organization?.tier?.charAt(0).toUpperCase() + paymentInfo?.organization?.tier?.slice(1)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280' }}>Status:</span>
            <span style={{ fontWeight: 600, color: '#166534' }}>Active</span>
          </div>
        </div>
        
        <button
          onClick={() => navigate('/login')}
          style={{
            width: '100%',
            padding: '14px 24px',
            background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: 12
          }}
        >
          🚀 Go to Login
        </button>
        
        <button
          onClick={() => window.close()}
          style={{
            width: '100%',
            padding: '12px 24px',
            background: '#f3f4f6',
            color: '#6b7280',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            fontSize: 14,
            cursor: 'pointer'
          }}
        >
          Close This Window
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// PaymentCancelledPage - Shown when user cancels admin-initiated checkout
// ============================================================================
function PaymentCancelledPage() {
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('org_id');
  
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 48,
        maxWidth: 500,
        width: '100%',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>😕</div>
        <h1 style={{ fontSize: 24, color: '#6b7280', marginBottom: 16 }}>Payment Cancelled</h1>
        <p style={{ color: '#6b7280', marginBottom: 24 }}>
          Your payment was not completed. If you'd like to try again, please use the payment link sent to your email.
        </p>
        <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 24 }}>
          If you have questions or need help, please contact the person who sent you the payment request.
        </p>
        <a href="/" style={{
          display: 'inline-block',
          padding: '12px 24px',
          background: '#2d6a4f',
          color: '#fff',
          borderRadius: 8,
          textDecoration: 'none',
          fontWeight: 600
        }}>Return to Home</a>
      </div>
    </div>
  );
}

const utils = {
  toInt: (n) => {
    const x = Number(n);
    return Number.isFinite(x) ? x : 0;
  },
  digitsOnly: (s) => String(s || "").replace(/[^0-9]/g, ""),
  formatPhone: (v) => {
    const d = utils.digitsOnly(v).slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  },
  safePlayerCount: (arr) => Array.isArray(arr) ? arr.filter((v) => (typeof v === "string" ? v.trim() : v)).length : 0,
  convertStateAbbreviation: (location) => {
    if (!location) return location;
    
    const stateMap = {
      'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
      'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
      'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
      'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
      'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
      'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
      'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
      'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
      'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
      'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
      'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
      'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
      'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia'
    };
    
    // Replace state abbreviations with full names
    let result = location;
    Object.entries(stateMap).forEach(([abbr, fullName]) => {
      // Match ", XX" or ", XX " at end of string (case insensitive)
      const regex = new RegExp(`,\\s*${abbr}\\s*$`, 'i');
      result = result.replace(regex, `, ${fullName}`);
    });
    
    return result;
  },
  formatDate: (iso) => {
    try {
      if (!iso) return "";
      
      // Extract just the date part if it's an ISO timestamp
      let dateStr = String(iso);
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      }
      
      // Parse date components manually to avoid Safari/iOS timezone issues
      // Safari interprets "2026-01-06T00:00:00" as UTC, Chrome as local
      const [year, month, day] = dateStr.split('-').map(Number);
      if (!year || !month || !day) return String(iso);
      
      // Create date using local timezone (month is 0-indexed)
      const d = new Date(year, month - 1, day);
      if (isNaN(d.getTime())) return String(iso);
      
      // Format as "Fri, Oct 25, 2025"
      return d.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return String(iso || "");
    }
  },
  formatTime: (time24) => {
    try {
      if (!time24) return "";
      const [h, m] = String(time24).split(":").map(Number);
      if (isNaN(h) || isNaN(m)) return time24;
      const isPM = h >= 12;
      const h12 = h % 12 || 12;
      return `${h12}:${String(m).padStart(2, "0")} ${isPM ? "PM" : "AM"}`;
    } catch {
      return String(time24 || "");
    }
  },
  generateId: () => crypto.randomUUID(),
  getDateTimestamp: (date, time) => {
    try {
      const ds = String(date || "");
      // Extract just the date part if it's an ISO timestamp (YYYY-MM-DDTHH:MM:SS.sssZ)
      const dateOnly = ds.includes('T') ? ds.split('T')[0] : ds;
      const ts = String(time || "00:00");
      
      // Parse date components manually to avoid Safari/iOS timezone issues
      const [year, month, day] = dateOnly.split('-').map(Number);
      if (!year || !month || !day) return Number.MAX_SAFE_INTEGER;
      
      // Parse time components
      const timeParts = ts.split(':').map(Number);
      const hours = timeParts[0] || 0;
      const minutes = timeParts[1] || 0;
      const seconds = timeParts[2] || 0;
      
      // Create date using local timezone (month is 0-indexed)
      const d = new Date(year, month - 1, day, hours, minutes, seconds);
      return isNaN(d.getTime()) ? Number.MAX_SAFE_INTEGER : d.getTime();
    } catch {
      return Number.MAX_SAFE_INTEGER;
    }
  },
  getTodayTimestamp: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime();
  },
  getTodayDate: () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },
  getDateOnly: (date) => {
    // Extract just the date part (YYYY-MM-DD) from either a date string or ISO timestamp
    const ds = String(date || "");
    return ds.includes('T') ? ds.split('T')[0] : ds;
  },
};

// Export utilities
const exportUtils = {
  downloadCSV: (data, filename) => {
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  teeTimesToCSV: (teeTimes) => {
    const headers = ['Date', 'Time', 'Course', 'Total Slots', 'Filled', 'Available', 'Players', 'Waitlist Count', 'Status'];
    const rows = teeTimes.map(t => [
      t.date,
      t.time,
      t.course,
      t.slots,
      utils.safePlayerCount(t.players),
      utils.toInt(t.slots) - utils.safePlayerCount(t.players),
      (t.players || []).map(p => typeof p === 'object' ? (p.name || p.email) : p).join('; '),
      Array.isArray(t.waitlist) ? t.waitlist.length : 0,
      t.status || 'Published'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        const str = String(cell || '');
        // Escape cells that contain commas, quotes, or newlines
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(','))
    ].join('\n');
    
    return csvContent;
  },

  playersToCSV: (players) => {
    const headers = ['Name', 'Email', 'Phone', 'GHIN', 'Handicap', 'Club Member', 'Role', 'Active'];
    const rows = players.map(p => [
      p.name,
      p.email,
      p.phone,
      p.ghin,
      p.hcp || p.handicap,
      (p.clubMember || p.club_member) ? 'Yes' : 'No', // Check both camelCase and snake_case
      p.role || 'Player',
      p.active ? 'Yes' : 'No'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        const str = String(cell || '');
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(','))
    ].join('\n');
    
    return csvContent;
  },

  printTeeTimes: (teeTimes, filter) => {
    const printWindow = window.open('', '_blank');
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tee Times - ${filter}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #2d6a4f; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #2d6a4f; color: white; }
          tr:nth-child(even) { background-color: #f9fafb; }
          .print-date { color: #6b7280; font-size: 12px; margin-top: 20px; }
          @media print {
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>⛳ Tee Time Organizer</h1>
        <h2>${filter.charAt(0).toUpperCase() + filter.slice(1)} Tee Times</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Course</th>
              <th>Slots</th>
              <th>Players</th>
            </tr>
          </thead>
          <tbody>
            ${teeTimes.map(t => `
              <tr>
                <td>${utils.formatDate(t.date)}</td>
                <td>${utils.formatTime(t.time)}</td>
                <td>${t.course}</td>
                <td>${utils.safePlayerCount(t.players)} players of ${t.slots} available playing slots</td>
                <td>${(t.players || []).map(p => typeof p === 'object' ? (p.name || p.email) : p).join(', ') || 'No players'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="print-date">Printed: ${new Date().toLocaleString()}</div>
        <button onclick="window.print()" style="margin-top: 20px; padding: 10px 20px; background: #2d6a4f; color: white; border: none; border-radius: 6px; cursor: pointer; margin-right: 10px;">Print</button>
        <button onclick="window.close()" style="margin-top: 20px; padding: 10px 20px; background: #6b7280; color: white; border: none; border-radius: 6px; cursor: pointer;">Close Window</button>
        <script>
          // Auto-close after printing (if user prints)
          window.onafterprint = function() {
            setTimeout(function() {
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  }
};

// ============================================================================
// PHASE 2E FIX: Token retrieval helper for impersonation support
// Checks sessionStorage first (tab-specific impersonation) then localStorage
// ============================================================================
/**
 * Parse JWT token to check expiration
 */
function parseJwtExpiry(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    return payload.exp ? payload.exp * 1000 : null; // Convert to ms
  } catch {
    return null;
  }
}

/**
 * Check if token is expired (with 60 second buffer)
 */
function isTokenExpired(token) {
  if (!token) return true;
  const expiresAt = parseJwtExpiry(token);
  if (!expiresAt) return true;
  return Date.now() >= (expiresAt - 60000); // 60 second buffer
}

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    console.log('⚠️ No refresh token available');
    return null;
  }
  
  try {
    console.log('🔄 Refreshing expired access token...');
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Oct 10, 2026: say which organization this browser is on, so the renewed token
      // stays in it (it used to fall back to the first organization A–Z).
      body: JSON.stringify({ refreshToken, organizationId: (() => { try { const o = JSON.parse(localStorage.getItem('organization') || 'null'); return o && o.id ? o.id : null; } catch (e) { return null; } })() })
    });
    
    if (!response.ok) {
      console.log('❌ Token refresh failed');
      return null;
    }
    
    const data = await response.json();
    localStorage.setItem('accessToken', data.accessToken);
    // The server sends no new refresh token: keep the one we have (storing the
    // missing one wrote "undefined", and the next renewal then failed).
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    console.log('✅ Token refreshed successfully');
    return data.accessToken;
  } catch (error) {
    console.error('Token refresh error:', error);
    return null;
  }
}

// ============================================================================
// EVENT DESK (Sept 17, 2026) — Docs: Event_Desk_Plan_v1.md. Staff given an event
// desk code get the organizer dashboard for ONE event at /desk. The desk session
// (a scoped token from /api/t/desk-enter + the event's name) lives in THIS TAB's
// sessionStorage, so closing the tab ends it and a shared pro-shop computer does
// not stay signed in. On /desk every API call carries the desk token and never
// the organizer's own token — and the desk token is never refreshed through the
// organizer refresh path (that could hand the desk tab a full-org token when an
// organizer is signed in on the same browser). The server enforces the scope.
// ============================================================================
const DESK_SESSION_KEY = 'tto_event_desk';
const DESK_PATH = '/desk';
function onDeskPage() {
  try { return typeof window !== 'undefined' && window.location.pathname.startsWith(DESK_PATH); } catch (e) { return false; }
}
function readDeskSession() {
  try {
    const s = JSON.parse(sessionStorage.getItem(DESK_SESSION_KEY) || 'null');
    if (!s || !s.token) return null;
    const exp = parseJwtExpiry(s.token);
    if (!exp || Date.now() >= exp) { sessionStorage.removeItem(DESK_SESSION_KEY); return null; }
    return s;
  } catch (e) { return null; }
}
function saveDeskSession(s) {
  try { sessionStorage.setItem(DESK_SESSION_KEY, JSON.stringify(s)); return true; } catch (e) { return false; }
}
function clearDeskSession() {
  try { sessionStorage.removeItem(DESK_SESSION_KEY); } catch (e) { /* storage blocked */ }
}
const DeskContext = createContext(null);

// Back button on the event desk (Oct 10, 2026 — Dennis: closing a tile's window
// "makes you log into the event desk again"). The desk keeps its windows and tabs in
// React state, not in the browser history, so Back (a phone's back swipe above all)
// left /desk for the page before it, the desk code page. DeskRoute now keeps one
// history entry above /desk and turns Back into a 'deskBack' event: a screen with a
// window open closes it and marks the event handled; when nothing was open the desk
// goes to its front page. Back never leaves the desk; "Leave desk" does that.
function useDeskBack(isOpen, close) {
  const deskSession = useContext(DeskContext);
  const latest = useRef(null);
  latest.current = { isOpen, close };
  useEffect(() => {
    if (!deskSession) return undefined;
    const onBack = (e) => {
      const c = latest.current;
      if (c && c.isOpen) { c.close(); if (e && e.detail) e.detail.handled = true; }
    };
    window.addEventListener('deskBack', onBack);
    return () => window.removeEventListener('deskBack', onBack);
  }, [deskSession]);
}

// App navigation hand-off (Sept 17, 2026): the Print tab, the Pairings tab and the
// Admin reports send each other to a round. goTo(tab, intent) switches the main tab
// and leaves an intent ({ kind, teetimeId, ... }) for the screen that opens.
// readIntent(kind) only PEEKS (StrictMode runs state initializers twice); the
// screen consumes it with clearIntent(kind) in an effect.
const AppNavContext = createContext({ goTo: () => {}, readIntent: () => null, clearIntent: () => {} });

/**
 * Get auth token - checks sessionStorage first (for impersonation), then localStorage
 * SYNC version - returns token without refresh (use for headers)
 */
function getAuthToken() {
  // Event desk page: the desk token or nothing — never the organizer's token.
  if (onDeskPage()) {
    const desk = readDeskSession();
    return desk ? desk.token : null;
  }
  // First check sessionStorage for impersonation token (tab-specific)
  const impersonationToken = sessionStorage.getItem('impersonationToken');
  if (impersonationToken) {
    return impersonationToken;
  }
  // Fall back to regular token in localStorage
  return localStorage.getItem('accessToken');
}

/**
 * Get valid auth token - ASYNC version that refreshes if expired
 * Use this when making important API calls that might fail with 401
 */
async function getValidAuthToken() {
  // Event desk page: never refreshed here (see DESK_SESSION_KEY).
  if (onDeskPage()) {
    const desk = readDeskSession();
    return desk ? desk.token : null;
  }
  // First check sessionStorage for impersonation token (tab-specific)
  const impersonationToken = sessionStorage.getItem('impersonationToken');
  if (impersonationToken && !isTokenExpired(impersonationToken)) {
    return impersonationToken;
  }
  
  // Check regular token
  const token = localStorage.getItem('accessToken');
  
  // If token is expired or about to expire, try to refresh
  if (isTokenExpired(token)) {
    const newToken = await refreshAccessToken();
    return newToken;
  }
  
  return token;
}

// Oct 28, 2026 (Oct 22 dry-run list #2): a "not signed in" answer on the organizer dashboard.
// Oct 20 night the browser had lost its tokens: the dashboard looked normal and every save
// failed with a generic message. Now a 401 first tries to renew the sign-in once (a laptop that
// slept can miss the scheduled renewal), retries, and only if that fails tells the dashboard
// (OrganizerDashboard listens) to send the organizer to sign in again. Not on the Event Desk or
// while impersonating: those have their own tokens.
function reportSessionExpired() {
  if (onDeskPage() || sessionStorage.getItem('impersonationToken')) return;
  try { window.dispatchEvent(new CustomEvent('tto-session-expired')); } catch (e) { /* old browser */ }
}
async function fetchWithAuthRetry(url, opts = {}) {
  let res = await fetch(url, opts);
  if (res.status !== 401 || onDeskPage() || sessionStorage.getItem('impersonationToken')) return res;
  const fresh = await refreshAccessToken();
  if (fresh) res = await fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${fresh}` } });
  if (res.status === 401) reportSessionExpired();
  return res;
}
// "Error saving: <the server's reason>" (dry-run list #3): the reason used to reach only the console.
const withReason = (msg, err) => {
  const why = err && err.message ? String(err.message) : '';
  if (!why || why === msg) return msg;
  return `${msg}: ${why === 'Failed to fetch' ? 'no connection to the server' : why}`;
};

// Custom hook for API data
// ============================================================================
// PHASE 2D FIX: Include JWT Authorization header for multi-tenant support
// The accessToken contains organizationId which determines data isolation
// PHASE 2E FIX: Use getAuthToken() to support impersonation via sessionStorage
// ============================================================================
// ============================================================================
// PHASE 2 — Tier-aware feature gating (Free + Starter Tier Rollout, May 2026)
// ============================================================================
// useOrgTier(): React hook that fetches the org's subscription tier on mount.
// Returns { tier, loading }. Cached across calls via a module-level promise so
// multiple components calling the hook don't trigger multiple network requests.
//
// canUseFeature(tier, feature, grandfatheredFeatures): client-side mirror of
// the backend's subscriptionService.isFeatureAvailable(). Encodes the same
// Feature Matrix rules. Used to show/hide/dim feature UI based on the org's
// current tier. Returns boolean.
//
// IMPORTANT: client-side checks are UI affordances only — they are NOT security.
// Every gated feature ALSO has a backend gate (route-level pre-check +
// in-service gate). The frontend can lie; the backend cannot be deceived.
//
// Copy convention for locked-feature messaging (per UX review May 10, 2026):
//   - Speak about the user's plan ("Your current plan doesn't include X")
//     rather than tier jargon ("requires Plus tier"). The modal itself
//     handles tier names + pricing in detail.
//   - "Your current plan doesn't include {featureName}" pattern is grammar-safe
//     for any feature name (singular or plural). Prefer this in reusable
//     components like LockBadge tooltips.
//   - In feature-specific surfaces (custom-message SMS overlay, settings
//     banners), feature-specific copy is fine and may read more naturally.
// ============================================================================

// Module-level cache for the tier fetch — avoids duplicate requests when
// multiple components mount and call useOrgTier in the same render pass.
//
// 🔴 KEYED BY THE ACTIVE AUTH TOKEN (_orgTierToken). Impersonation, org switching,
// and login/logout all change getAuthToken(); whenever the current token differs
// from the one the cached tier was fetched under, the cache is invalidated and the
// tier is refetched for the new context. Without this, the first org's tier (e.g.
// an admin's own Free org) would stick for the whole tab even after entering an
// impersonated Founders org — the "impersonation shows my plan, not theirs" bug,
// which mislocked the Scoring tab (and every other tier-gated control) for orgs an
// admin was viewing. See TRKMGL_Tier_Badge_And_Stray_Org_Notes.
let _orgTierCache = null;
let _orgTierToken = null;   // the auth token the cached tier belongs to
let _orgTierPromise = null;
// Event Pass (Sept 30, 2026): features a live pass adds on top of the plan
// (/subscription/status → passFeatures), and whether this account was made through
// the Event Pass door. Fetched and invalidated with the tier, under the same token.
let _orgPassFeatures = [];
let _orgEventAccount = false;

// Trigger a refresh of the cached tier (call after subscription changes, or when
// the org/auth context changes). Safe to call anytime.
function clearOrgTierCache() {
  _orgTierCache = null;
  _orgTierToken = null;
  _orgTierPromise = null;
  _orgPassFeatures = [];
  _orgEventAccount = false;
}

function useOrgTier() {
  // Read the CURRENT token every render (impersonation-aware via getAuthToken).
  const currentToken = getAuthToken() || null;

  // Seed initial state from the cache ONLY if it was fetched under the current
  // token; otherwise start empty so the effect refetches for this context.
  const cacheMatches = _orgTierCache !== null && _orgTierToken === currentToken;
  const [tier, setTier] = useState(cacheMatches ? _orgTierCache : null);
  const [loading, setLoading] = useState(!cacheMatches);

  useEffect(() => {
    let cancelled = false;
    const token = getAuthToken() || null;

    // Cache belongs to a different token (entered/left impersonation, switched
    // orgs, logged out/in)? Invalidate it before using or refetching.
    if (_orgTierToken !== token) {
      _orgTierCache = null;
      _orgTierPromise = null;
      _orgTierToken = null;
      _orgPassFeatures = [];
      _orgEventAccount = false;
    }

    // Serve a cache that matches the current token.
    if (_orgTierCache !== null) {
      setTier(_orgTierCache);
      setLoading(false);
      return;
    }

    setLoading(true);
    // Start (or reuse) a fetch bound to the current token. A null result (no token,
    // fetch failed) is not cached, so a later mount can retry.
    if (!_orgTierPromise) {
      _orgTierPromise = (async () => {
        if (!token) return null;
        try {
          const response = await fetch(`${API_URL}/subscription/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!response.ok) return null;
          const data = await response.json();
          const t = data.subscription?.currentTier || 'free';
          _orgPassFeatures = Array.isArray(data.subscription?.passFeatures) ? data.subscription.passFeatures : [];
          _orgEventAccount = data.subscription?.eventAccount === true;
          _orgTierCache = t;
          _orgTierToken = token;   // stamp which token this tier belongs to
          return t;
        } catch (err) {
          console.error('useOrgTier fetch error:', err);
          return null;
        }
      })();
    }
    _orgTierPromise.then(t => {
      if (cancelled) return;
      setTier(t);
      setLoading(false);
      // A null result isn't cached — drop the promise so a later mount can retry.
      if (t === null) {
        _orgTierPromise = null;
      }
    });

    return () => { cancelled = true; };
  }, [currentToken]);

  return { tier, loading, eventAccount: _orgEventAccount };
}

// Client-side feature-tier map. Mirror of backend subscriptionService.FEATURE_TIER_MAP.
// IMPORTANT: keep in sync with backend if either changes.
// Migrated to canonical post-rename ladder (Phase 4 tier rename, commit d3a9afd).
// Old keys removed: 'starter_new' (now 'starter'), 'basic' (now 'professional'),
// 'enterprise' (dropped). NOTE: membership lists are INCLUSION-based — a tier
// HAS a feature when its key is present. After the rename 'starter' = $4.99 tier,
// so it must be EXCLUDED from Plus-and-up features (sms, ghin_sync) to avoid the
// inversion hazard. Canonical order: free, starter, plus, professional, league, club.
const CLIENT_FEATURE_TIER_MAP = {
  rsvp:                ['free', 'starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  email_reminders:     ['free', 'starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  weather_reminders:   ['free', 'starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  player_portal:       ['free', 'starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  course_handicap:     ['free', 'starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  // sms: Plus-and-up only — Starter ($4.99) and Free EXCLUDED. Real per-use Twilio cost.
  sms:                 ['plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  // ghin_sync replaces 'ghin' (May 13, 2026) — gates the GHIN auto-sync API mechanism only.
  // Plus-and-up only. Handicap display + WHS course handicap calc remain universal.
  ghin_sync:           ['plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  // pairings: Starter-and-up (Free excluded). Drip from Plus → Starter (May 14, 2026).
  pairings:            ['starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  // scoring: Starter-and-up (Free excluded). The admin scoring_enabled flag is the
  // operational gate; this tier check powers the upsell-locked Scoring tab affordance
  // for Free orgs (Show + Lock + Educate per Rule 12).
  scoring:             ['starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  // multi_organizer / multi_groups / custom_templates: gates dropped (May 16/18, 2026) — universal.
  multi_organizer:     ['free', 'starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  multi_groups:        ['free', 'starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  custom_templates:    ['free', 'starter', 'plus', 'professional', 'league', 'club', 'complimentary', 'founders'],
  multi_course:        ['club', 'complimentary'],
  api_access:          ['club', 'complimentary'],
  custom_branding:     ['club', 'complimentary'],
  priority_support:    ['club', 'complimentary'],
  // games (Oct 5, 2026 — Dennis): Plus and up, Founders included. Complimentary orgs
  // get games only from the admin switch. The server's /scoring/status answer
  // (games_enabled / games_locked) is what the dashboard actually uses.
  games:               ['plus', 'professional', 'league', 'club', 'founders'],
};

function canUseFeature(tier, feature) {
  if (!tier || !feature) return false;
  const tierKey = String(tier).toLowerCase();
  const featureKey = String(feature).toLowerCase();
  // A live Event Pass unlocks the event tools whatever the plan (Sept 30, 2026;
  // mirrors subscriptionService.isFeatureAvailableForOrg).
  if (_orgPassFeatures.includes(featureKey)) return true;
  const allowedTiers = CLIENT_FEATURE_TIER_MAP[featureKey];
  if (!allowedTiers) return tierKey !== 'free';
  return allowedTiers.includes(tierKey);
}

// Reusable lock badge component. Renders inline next to a label.
// Click triggers the upgrade modal for the named feature/tier.
const LockBadge = ({ feature, requiredTier = 'plus', style = {} }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    triggerUpgradeModal({ feature, requiredTier });
  };
  // Friendly tooltip — speaks to user's current state, not tier jargon.
  // The modal itself shows tier names + pricing in detail.
  const featureName = FEATURE_DISPLAY_INFO[feature]?.name || feature;
  return (
    <span
      onClick={handleClick}
      title={`Your current plan doesn't include ${featureName}. Click to see upgrade options.`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        marginLeft: 8,
        padding: '2px 8px',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        color: '#92400e',
        border: '1px solid #fcd34d',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        cursor: 'pointer',
        verticalAlign: 'middle',
        ...style
      }}
    >
      🔒 {requiredTier === 'plus' ? 'Plus' : requiredTier === 'starter' ? 'Starter' : requiredTier}
    </span>
  );
};

// ── Named nines (Oct 8, 2026 — claude/Named_Nines_Courses_Plan_v1.md) ─────────────
// A course built from two named nines (Wescott: Oak Forest / Burn Kill) stays an
// 18-hole course inside TTO — holes 1–18, one combined stroke index — but on the
// property each nine's holes are numbered 1–9, so "Hole 12" is "Burn Kill 3". The
// names live on the course (front_nine_name / back_nine_name; the first-named nine
// is holes 1–9, the club's own order). The organizer's course list registers them
// here by course name (useApiData('/courses')); player-side screens pass the nines
// that come with their tee time. No nines → every label reads exactly as before.
const NINES_BY_COURSE = new Map();
const ninesOf = (row) => {
  if (!row) return null;
  const f = String(row.front_nine_name ?? row.front ?? '').trim();
  const b = String(row.back_nine_name ?? row.back ?? '').trim();
  return f && b ? { front: f, back: b } : null;
};
const rememberCourses = (list) => {
  if (!Array.isArray(list)) return;
  for (const c of list) if (c && c.name) NINES_BY_COURSE.set(String(c.name), ninesOf(c));
};
const ninesForCourse = (name) => (name ? NINES_BY_COURSE.get(String(name)) || null : null);
// "Burn Kill 3" on a named-nines course; else the plain number ("12").
const holeName = (hole, nines) => {
  const h = Number(hole);
  if (!nines || !Number.isInteger(h) || h < 1 || h > 18) return hole == null ? '' : String(hole);
  return h <= 9 ? `${nines.front} ${h}` : `${nines.back} ${h - 9}`;
};
// "Burn Kill 3" on a named-nines course; else "Hole 12".
const holeText = (hole, nines) => (nines ? holeName(hole, nines) : `Hole ${hole}`);
// A shotgun start: "Burn Kill 3A"; else "Hole 12A".
const startHoleText = (hole, groupLabel, nines) => `${holeText(hole, nines)}${groupLabel || ''}`;

function useApiData(endpoint) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  if (endpoint === '/courses') rememberCourses(data);   // edits to a course's nine names show at once (Oct 8, 2026)

  const fetchData = async (silent = false) => {
    try {
      // Only show loading spinner on initial load or manual refresh, not auto-refresh
      if (!silent) {
        setLoading(true);
      }
      
      // Build headers - include JWT token if available for multi-tenant support
      const headers = {};
      const token = await getValidAuthToken(); // AUTO-REFRESH FIX: Refreshes expired token (was: getAuthToken)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetchWithAuthRetry(`${API_URL}${endpoint}`, {
        credentials: 'include',  // Still include session cookie for backward compatibility
        headers
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const result = await response.json();
      if (endpoint === '/courses') rememberCourses(result);   // named nines (Oct 8, 2026)
      
      setData(result);
      setError(null);
    } catch (err) {
      console.error(`Error fetching ${endpoint}:`, err);
      setError(err.message);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const addItem = async (item) => {
    try {
      // Build headers - include JWT token if available
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken(); // PHASE 2E FIX
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetchWithAuthRetry(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(item)
      });
      
      if (!response.ok) {
        // Try to get error message from response
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || 'Failed to add item';
        const err = new Error(errorMessage);
        err.responseData = errorData;  // Preserve full response (e.g., existingPlayer for duplicate-email errors)
        err.status = response.status;
        throw err;
      }
      
      const createdItem = await response.json();
      await fetchData();
      return createdItem; // Return the created item
    } catch (err) {
      console.error('Error adding item:', err);
      throw err;
    }
  };

  const updateItem = async (id, item) => {
    try {
      // Build headers - include JWT token if available
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken(); // PHASE 2E FIX
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetchWithAuthRetry(`${API_URL}${endpoint}/${id}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(item)
      });
      
      if (!response.ok) {
        // Get error details from backend
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const e = new Error(errorData.error || `Server error: ${response.status}`);
        e.code = errorData.code; e.body = errorData;   // Event Pass refusals carry a code (Sept 28, 2026)
        throw e;
      }
      
      await response.json();
      await fetchData();
    } catch (err) {
      console.error('Error updating item:', err);
      throw err;
    }
  };

  const deleteItem = async (id) => {
    try {
      // Build headers - include JWT token if available
      const headers = {};
      const token = getAuthToken(); // PHASE 2E FIX
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetchWithAuthRetry(`${API_URL}${endpoint}/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to delete item');
      }
      await fetchData();
    } catch (err) {
      console.error('Error deleting item:', err);
      throw err;
    }
  };

  return { data, loading, error, addItem, updateItem, deleteItem, refresh: fetchData };
}

// Custom hook for Groups with localStorage fallback (until backend is ready)
// ============================================================================
// PHASE 2D FIX: Include JWT Authorization header for multi-tenant support
// ============================================================================
function useGroupsData() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const STORAGE_KEY = 'teeTimeGroups';

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build headers - include JWT token if available
      const headers = {};
      const token = getAuthToken(); // PHASE 2E FIX
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Try to fetch from API first
      const response = await fetch(`${API_URL}/groups`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
        setError(null);
      } else {
        // Backend endpoint doesn't exist yet, use localStorage
        console.log('📦 Groups endpoint not available, using localStorage');
        const stored = localStorage.getItem(STORAGE_KEY);
        const groups = stored ? JSON.parse(stored) : [];
        setData(groups);
        setError(null);
      }
    } catch (err) {
      // Fallback to localStorage on any error
      console.log('📦 Using localStorage for groups:', err.message);
      const stored = localStorage.getItem(STORAGE_KEY);
      const groups = stored ? JSON.parse(stored) : [];
      setData(groups);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const addItem = async (item) => {
    try {
      // Build headers - include JWT token if available
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken(); // PHASE 2E FIX
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Try API first
      const response = await fetch(`${API_URL}/groups`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(item)
      });
      
      if (response.ok) {
        await fetchData();
        return;
      }
    } catch (err) {
      console.log('📦 API not available, saving group to localStorage');
    }
    
    // Fallback to localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    const groups = stored ? JSON.parse(stored) : [];
    groups.push(item);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
    setData(groups);
  };

  const updateItem = async (id, item) => {
    try {
      // Build headers - include JWT token if available
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken(); // PHASE 2E FIX
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Try API first
      const response = await fetch(`${API_URL}/groups/${id}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(item)
      });
      
      if (response.ok) {
        await fetchData();
        return;
      }
    } catch (err) {
      console.log('📦 API not available, updating group in localStorage');
    }
    
    // Fallback to localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    const groups = stored ? JSON.parse(stored) : [];
    const index = groups.findIndex(g => g.id === id);
    if (index !== -1) {
      groups[index] = item;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
      setData(groups);
    }
  };

  const deleteItem = async (id) => {
    try {
      // Build headers - include JWT token if available
      const headers = {};
      const token = getAuthToken(); // PHASE 2E FIX
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Try API first
      const response = await fetch(`${API_URL}/groups/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchData();
        return;
      }
    } catch (err) {
      console.log('📦 API not available, deleting group from localStorage');
    }
    
    // Fallback to localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    const groups = stored ? JSON.parse(stored) : [];
    const filtered = groups.filter(g => g.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    setData(filtered);
  };

  return { data, loading, error, addItem, updateItem, deleteItem, refresh: fetchData };
}


const ToastContext = createContext({ addToast: () => {} });
const CourseContext = createContext({ courses: [], loading: false, refreshCourses: () => {} });
const GroupsContext = createContext({ groups: [], loading: false, addGroup: () => {}, updateGroup: () => {}, deleteGroup: () => {} });
const SubscriptionContext = createContext({ subscriptionStatus: null, isReadOnly: false, isExpired: false, refreshSubscription: () => {} });
const OrganizerRefreshContext = createContext({ refreshOrganization: () => {} });
// Mobile-Aware Layout context (Phase 2 — May 2026)
// AuthenticatedApp computes isMobile and provides it here so any descendant
// component (CreateTeeTimeInline, TeeTimeCard, etc.) can read it without prop drilling.
const MobileContext = createContext({ isMobile: false });

// Custom hook for subscription status
const useSubscription = () => useContext(SubscriptionContext);
const useToast = () => useContext(ToastContext);
const useOrganizerRefresh = () => useContext(OrganizerRefreshContext);
const useCourses = () => useContext(CourseContext);
const useGroups = () => useContext(GroupsContext);

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const addToast = (message, kind = "success") => {
    const id = Math.random().toString(36).slice(2, 8);
    const ms = kind === "error" ? CONFIG.TOAST_DURATION_ERROR : CONFIG.TOAST_DURATION_SUCCESS;
    setToasts((t) => [...t, { id, message, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ms);
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div style={{ position: "fixed", right: 16, bottom: 16, display: "grid", gap: 8, zIndex: 9999 }}>
        {toasts.map((t) => (
          <div key={t.id} style={{ padding: "8px 12px", borderRadius: 10, background: t.kind === "error" ? "#fee2e2" : "#ecfeff", color: t.kind === "error" ? "#7f1d1d" : "#0c4a6e", border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const Card = ({ children, style, ...rest }) => (
  <div style={{ border: "3px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", ...(style || {}) }} {...rest}>{children}</div>
);

const Button = ({ children, variant = "default", style, disabled, ...rest }) => {
  const getStyles = () => {
    if (variant === "outline") return { border: "1px solid #e5e7eb", background: "transparent", color: "#111827" };
    if (variant === "danger") return { border: "1px solid transparent", background: "#b91c1c", color: "#fff" };
    return { border: "1px solid transparent", background: "#111827", color: "#fff" };
  };
  return <button style={{ padding: "8px 12px", borderRadius: 10, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...getStyles(), ...(style || {}) }} disabled={disabled} {...rest}>{children}</button>;
};

const Label = ({ children }) => <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{children}</div>;
const Input = (props) => <input {...props} style={{ padding: 8, border: "1px solid #e5e7eb", borderRadius: 10, width: "100%", fontSize: 14, boxSizing: "border-box", minWidth: 0 }} />;

// Phone sizing for the two PUBLIC doors only (the golf invite and the social
// signup). Both are opened from an emailed link, so most traffic is a phone.
// Scoped to .tto-public-form on purpose: the shared Input primitive above is
// used on ~70 dashboard screens and is not being resized 11 days before the
// Oct 1 send. !important is required because Input writes fontSize inline.
//   16px  - below this iOS Safari zooms the page when an input takes focus,
//           and the reader then has to pinch back out for every field.
//   44px  - the iOS minimum tap target; the small underlined text buttons
//           ("Send me my link", "Cancel my reservation") were 17px tall.
const PublicFormMobileCss = () => (
  <style>{`
    .tto-public-form input[type="text"],
    .tto-public-form input[type="email"],
    .tto-public-form input[type="tel"],
    .tto-public-form input[type="number"],
    .tto-public-form input:not([type]),
    .tto-public-form select,
    .tto-public-form textarea {
      font-size: 16px !important;
      padding: 12px !important;
    }
    .tto-public-form input[type="checkbox"] {
      width: 20px !important;
      height: 20px !important;
      flex: none;
    }
    .tto-public-form label {
      padding: 6px 0;
    }
    .tto-public-form button {
      min-height: 44px;
    }
  `}</style>
);
const Select = (props) => <select {...props} style={{ padding: 8, border: "1px solid #e5e7eb", borderRadius: 10, width: "100%", background: "#fff", cursor: "pointer", fontSize: 14 }} />;

// Women's tees are marked in production by a trailing (W) or (L) suffix
// (verified against all 59 marked tee names, July 19 2026). Module-scope so
// both TeeSheetReport and PlayerPortal can gate tee choice by player gender.
const isWomensTee = (teeName) => /\((?:w|l)\)\s*$/i.test(teeName || '');
const Badge = ({ children, style }) => <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 9999, background: "#eef2ff", color: "#4338ca", border: "1px solid #e5e7eb", ...(style || {}) }}>{children}</span>;

// AvatarCircle removed - now using PlayerAvatar component


const ConfirmDialog = ({ title = "Are you sure?", message, onConfirm, onCancel, confirmText = "Delete", cancelText = "Cancel", confirmVariant = "danger" }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }} onClick={onCancel}>
    <Card style={{ width: "100%", maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
      <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>{title}</div>
      {message && <div style={{ color: "#6b7280", marginBottom: 12 }}>{message}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button variant="outline" onClick={onCancel}>{cancelText}</Button>
        <Button variant={confirmVariant} onClick={onConfirm}>{confirmText}</Button>
      </div>
    </Card>
  </div>
);

const PlayerPickerModal = ({ teeTime, players, groups = [], onConfirm, onClose, isEvent = false }) => {
  const [selected, setSelected] = useState([]);
  // Quiet events (Oct 8, 2026 — Dennis): on an EVENT the organizer loads his field
  // without emailing anyone and sends the event's welcome email when he is ready
  // (event page → Welcome email). On an ordinary tee time the players added or
  // removed are emailed, as always. Either way the organizer can change it per save.
  const [notifyPlayers, setNotifyPlayers] = useState(!isEvent);
  const [showGuests, setShowGuests] = useState(() => {
    const saved = localStorage.getItem('tto_showGuests');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showGroups, setShowGroups] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  
  // Persist showGuests preference to localStorage
  useEffect(() => {
    localStorage.setItem('tto_showGuests', JSON.stringify(showGuests));
  }, [showGuests]);
  
  const activePlayers = useMemo(() => {
    let filtered = players.filter((p) => p.active);
    if (!showGuests) {
      filtered = filtered.filter((p) => p.role !== 'guest');
    }
    // §6.9 Pending visibility in Manage Players modal (April 2026):
    // Players with an active pending_nomination for THIS tee time are
    // excluded from the selectable pool. They render in a dedicated
    // read-only "Pending Invitations" section above the list. Without
    // this filter, the organizer could check the empty checkbox next to
    // a pending player, save, and trigger a 23505 unique-constraint
    // collision against the existing pending_nomination row.
    const pendingIds = new Set(
      (teeTime?.pending_players || [])
        .map(pp => pp?.id)
        .filter(Boolean)
    );
    if (pendingIds.size > 0) {
      filtered = filtered.filter((p) => !pendingIds.has(p.id));
    }
    return filtered;
  }, [players, showGuests, teeTime]);

  useEffect(() => {
    // Create email-to-id map since teeTime.players could contain emails or objects
    const idByEmail = new Map(activePlayers.map((p) => [p.email, p.id]));
    
    const currentPlayerIds = (teeTime?.players || [])
      .map((playerData) => {
        if (typeof playerData === 'string') {
          // It's an email
          return idByEmail.get(playerData);
        } else if (playerData && typeof playerData === 'object') {
          // It's a player object
          return playerData.id || idByEmail.get(playerData.email);
        }
        return null;
      })
      .filter(Boolean);
      
    const waitlistIds = (teeTime?.waitlist || []).map(w => typeof w === 'string' ? w : w?.id).filter(Boolean);
    setSelected([...currentPlayerIds, ...waitlistIds]);
  }, [teeTime, activePlayers]);

  const toggle = (id) => {
    setSelected((prev) => {
      const isCurrentlySelected = prev.includes(id);
      return isCurrentlySelected ? prev.filter((x) => x !== id) : [...prev, id];
    });
  };

  const idByEmail = new Map(activePlayers.map((p) => [p.email, p.id]));
  const nameById = new Map(activePlayers.map((p) => [p.id, p.name]));
  const emailById = new Map(activePlayers.map((p) => [p.id, p.email]));
  
  // Create lookup map for addedAt timestamps from teeTime.players AND teeTime.waitlist
  const teamNameById = new Map((teeTime?.players || []).filter(p => p && typeof p === 'object' && p.id && p.teamName).map(p => [p.id, p.teamName]));
  const teamCaptainIds = new Set((teeTime?.players || []).filter(p => p && typeof p === 'object' && p.id && p.teamCaptain === true).map(p => p.id));
  // Each confirmed golfer's registered team, for ordering the list team by team (Nov 2, 2026).
  const teamInfoById = new Map((teeTime?.players || [])
    .filter(p => p && typeof p === 'object' && p.id && p.teamId)
    .map(p => [p.id, { order: Number.isFinite(Number(p.teamOrder)) ? Number(p.teamOrder) : 9999, name: String(p.teamName || ''), captain: p.teamCaptain === true }]));
  const addedAtById = new Map([
    ...(teeTime?.players || [])
      .filter(p => p && typeof p === 'object' && p.addedAt)
      .map(p => [p.id, p.addedAt]),
    ...(teeTime?.waitlist || [])
      .filter(w => w && typeof w === 'object' && w.addedAt)
      .map(w => [w.id, w.addedAt])
  ]);
  
  // Format RSVP timestamp as [MM/DD/YYYY HH:MMA] in local timezone
  const formatRsvpTimestamp = (addedAt) => {
    if (!addedAt) return '';
    // Database returns timestamp without timezone - treat as UTC
    let dateStr = addedAt;
    if (typeof dateStr === 'string' && !dateStr.endsWith('Z') && !dateStr.includes('+')) {
      dateStr = dateStr + 'Z'; // Append Z to interpret as UTC
    }
    const date = new Date(dateStr);
    // Use America/New_York timezone for consistent display regardless of browser locale
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      month: '2-digit', day: '2-digit', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    }).formatToParts(date);
    const p = {};
    parts.forEach(({ type, value }) => { p[type] = value; });
    const ampm = p.dayPeriod === 'AM' ? 'A' : 'P';
    return `[${p.month}/${p.day}/${p.year} ${p.hour}:${p.minute}${ampm}]`;
  };
  
  // Build preCheckedIds - handle both email strings and player objects
  const currentPlayerIds = (teeTime?.players || [])
    .map((playerData) => {
      if (typeof playerData === 'string') {
        // It's an email
        return idByEmail.get(playerData);
      } else if (playerData && typeof playerData === 'object') {
        // It's a player object
        return playerData.id || idByEmail.get(playerData.email);
      }
      return null;
    })
    .filter(Boolean);
    
  const waitlistPlayerIds = (teeTime?.waitlist || []).map(w => typeof w === 'string' ? w : w?.id).filter(Boolean);
  const preCheckedIds = [...currentPlayerIds, ...waitlistPlayerIds];
  const newSelections = selected.filter((id) => !preCheckedIds.includes(id));
  const removedSelections = preCheckedIds.filter((id) => !selected.includes(id));
  const hasChanges = newSelections.length > 0 || removedSelections.length > 0;
  const currentCount = utils.safePlayerCount(teeTime?.players || []);
  const availableSlots = Math.max(0, utils.toInt(teeTime?.slots) - currentCount);
  const willWaitlist = Math.max(0, newSelections.length - availableSlots);

  const getPlayerStatus = (player) => {
    const isCancelled = (teeTime?.cancelled || []).some(c => c.id === player.id);
    
    // Check if player is in the players array - handle both emails and objects
    const isPlaying = (teeTime?.players || []).some(playerData => {
      if (typeof playerData === 'string') {
        return sameEmail(playerData, player.email);
      } else if (playerData && typeof playerData === 'object') {
        return samePlayer(playerData, player);
      }
      return false;
    });
    
    const isWaitlist = (teeTime?.waitlist || []).some(w => (typeof w === 'string' ? w : w?.id) === player.id);
    const isSelected = selected.includes(player.id);
    
    // Use "removing" statuses to keep players in their section while showing empty checkbox
    if (isPlaying && isSelected) return "playing";
    if (isPlaying && !isSelected) return "playing-removing";  // Stay in Active section, show empty checkbox
    if (isWaitlist && isSelected) return "waitlist";
    if (isWaitlist && !isSelected) return "waitlist-removing";  // Stay in Waitlist section, show empty checkbox
    if (isSelected) return "selected";  // New selections
    if (isCancelled) return "cancelled";
    return "available";
  };

  const getCancelledInfo = (playerId) => {
    const cancelled = (teeTime?.cancelled || []).find(c => c.id === playerId);
    return cancelled;
  };

  // Sort players: Active players first (in order added), then waitlist (in order added), then cancelled, then available
  const sortedPlayers = useMemo(() => {
    // Helper function to extract last name
    const getLastName = (fullName) => {
      // Nov 2, 2026: a held spot ("Spruill team — player 2") files under its team's name.
      const s = String(fullName || '').trim();
      const held = s.match(/^(\S+).*?(\d+)$/);
      if (held && /\D/.test(held[1])) {
        return `${held[1].replace(/[^A-Za-z'’-]/g, '').toLowerCase()}\uffff${held[2].padStart(3, '0')}`;
      }
      const parts = s.split(/\s+/).filter((w, i, a) => !(i > 0 && i === a.length - 1 && /^(jr|sr|ii|iii|iv)\.?$/i.test(w)));
      return parts[parts.length - 1].toLowerCase();
    };
    
    // teeTime.players could contain emails OR objects - handle both
    const activePlaying = (teeTime?.players || [])
      .map(playerData => {
        if (typeof playerData === 'string') {
          // It's an email
          return activePlayers.find(p => p.email === playerData);
        } else if (playerData && typeof playerData === 'object') {
          // It's already a player object
          return activePlayers.find(p => samePlayer(p, playerData));
        }
        return null;
      })
      .filter(Boolean)
      // Sort by RSVP time (addedAt) - earliest first.
      // Nov 2, 2026 (Dennis — Turkey Shoot): when the tee time has registered teams, list
      // them team by team in the team list's order, captain first, then by name; golfers
      // with no team follow, by RSVP time. An imported field all shares one added time, so
      // RSVP order alone came out jumbled.
      .sort((a, b) => {
        const ta = teamInfoById.get(a.id), tb = teamInfoById.get(b.id);
        if (ta || tb) {
          if (!ta) return 1;
          if (!tb) return -1;
          if (ta.order !== tb.order) return ta.order - tb.order;
          if (ta.name !== tb.name) return ta.name.localeCompare(tb.name);
          if (ta.captain !== tb.captain) return ta.captain ? -1 : 1;
          return String(a.name || '').localeCompare(String(b.name || ''));
        }
        const dateA = addedAtById.get(a.id) ? new Date(addedAtById.get(a.id)) : new Date(0);
        const dateB = addedAtById.get(b.id) ? new Date(addedAtById.get(b.id)) : new Date(0);
        return dateA - dateB;
      });
    
    // Waitlist sorted by addedAt (RSVP time)
    const onWaitlist = (teeTime?.waitlist || [])
      .map(wl => {
        // waitlist could be IDs or objects with addedAt
        const playerId = typeof wl === 'string' ? wl : wl?.id;
        return activePlayers.find(p => p.id === playerId);
      })
      .filter(Boolean)
      .sort((a, b) => {
        // Use waitlist addedAt if available
        const wlA = (teeTime?.waitlist || []).find(w => w?.id === a.id);
        const wlB = (teeTime?.waitlist || []).find(w => w?.id === b.id);
        const dateA = wlA?.addedAt ? new Date(wlA.addedAt) : new Date(0);
        const dateB = wlB?.addedAt ? new Date(wlB.addedAt) : new Date(0);
        return dateA - dateB;
      });
    
    // Cancelled in the order they cancelled (preserve array order)
    const cancelledPlayers = (teeTime?.cancelled || [])
      .map(c => activePlayers.find(p => p.id === c.id))
      .filter(Boolean);
    
    const playingIds = new Set(activePlaying.map(p => p.id));
    const waitlistIds = new Set(onWaitlist.map(p => p.id));
    const cancelledIds = new Set(cancelledPlayers.map(p => p.id));
    
    // Available players sorted alphabetically by last name
    const available = activePlayers.filter(p => 
      !playingIds.has(p.id) && !waitlistIds.has(p.id) && !cancelledIds.has(p.id)
    ).sort((a, b) => {
      const lastNameA = getLastName(a.name);
      const lastNameB = getLastName(b.name);
      return lastNameA.localeCompare(lastNameB);
    });
    
    return [...activePlaying, ...onWaitlist, ...cancelledPlayers, ...available];
  }, [activePlayers, teeTime]);

  // Inline checkbox styles - moved outside component to avoid recreation
  const getCheckboxStyle = (status) => {
    const styles = {
      playing: { bg: "#16a34a", icon: "✓", color: "#fff" },
      "playing-removing": { bg: "transparent", icon: "", color: "#111827" },  // Empty checkbox for removal
      waitlist: { bg: "#f59e0b", icon: "W", color: "#fff" },
      "waitlist-removing": { bg: "transparent", icon: "", color: "#111827" },  // Empty checkbox for removal
      selected: { bg: "#2563eb", icon: "✓", color: "#fff" },
      cancelled: { bg: "#dc2626", icon: "−", color: "#fff" },
      available: { bg: "transparent", icon: "", color: "#111827" },
    };
    return styles[status] || styles.available;
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={onClose}>
      <Card style={{ width: "100%", maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Manage Players – {teeTime?.course || "—"}</div>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Available slots: {availableSlots} • New selections: {newSelections.length}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: "#6b7280" }}>
              <input 
                type="checkbox" 
                checked={showGroups} 
                onChange={(e) => { setShowGroups(e.target.checked); if (!e.target.checked) setSelectedGroupId(""); }}
                style={{ width: 14, height: 14 }}
              />
              <span>Show Groups</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: "#6b7280" }}>
              <input 
                type="checkbox" 
                checked={showGuests} 
                onChange={(e) => setShowGuests(e.target.checked)}
                style={{ width: 14, height: 14 }}
              />
              <span>Show Guests</span>
            </label>
          </div>
        </div>
        {showGroups && (
          <div style={{ marginBottom: 8 }}>
            <select
              value={selectedGroupId}
              onChange={(e) => {
                const groupId = e.target.value;
                setSelectedGroupId(groupId);
                if (groupId) {
                  const group = groups.find(g => g.id === groupId);
                  if (group) {
                    const groupPlayerIds = (group.playerIds || group.player_ids || []);
                    setSelected(prev => {
                      const combined = new Set([...prev, ...groupPlayerIds]);
                      return [...combined];
                    });
                  }
                }
              }}
              style={{ padding: 8, border: "1px solid #e5e7eb", borderRadius: 10, width: "100%", background: "#fff", cursor: "pointer", fontSize: 14 }}
            >
              <option value="">— Select a Group —</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.name} ({(g.playerIds || g.player_ids || []).length} players)</option>
              ))}
            </select>
          </div>
        )}
        {/* §6.9 Pending Invitations (read-only) — April 2026
            Renders above the main player list so the organizer has visibility
            into outstanding group-RSVP invites for this tee time. Read-only:
            no checkbox, no cancel button. The nominee must accept/decline/
            expire through the normal Group RSVP flow. */}
        {Array.isArray(teeTime?.pending_players) && teeTime.pending_players.length > 0 && (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, marginBottom: 8, overflow: "hidden" }}>
            <div style={{ background: "#fef3c7", padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              ⏳ Pending Invitations ({teeTime.pending_players.length})
            </div>
            {teeTime.pending_players.map((pp, idx) => {
              const captainName = pp.nominated_by_name || 'captain';
              let expiresText = null;
              if (pp.nomination_expires_at) {
                try {
                  const exp = new Date(pp.nomination_expires_at);
                  expiresText = exp.toLocaleString('en-US', {
                    month: 'short', day: 'numeric',
                    hour: 'numeric', minute: '2-digit'
                  });
                } catch (e) { /* swallow */ }
              }
              return (
                <div
                  key={`pp-${pp.id || idx}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: 10,
                    borderBottom: idx < teeTime.pending_players.length - 1 ? "1px solid #f3f4f6" : "none",
                    background: "#fffbeb"
                  }}
                >
                  <div style={{ width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
                    ⏳
                  </div>
                  <PlayerAvatar name={pp.name || pp.email || 'Invited'} photoUrl={pp.photoUrl} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontStyle: "italic", color: "#6b7280", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span>{pp.name || pp.email || 'Invited player'}</span>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                        background: "#e5e7eb",
                        color: "#4b5563",
                        padding: "2px 6px",
                        borderRadius: 10,
                        fontStyle: "normal"
                      }}>
                        Pending
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {pp.email}
                    </div>
                    <div style={{ fontSize: 11, color: "#92400e", marginTop: 2 }}>
                      Invited by {captainName}{expiresText ? ` • Expires ${expiresText}` : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 12 }}>
          {sortedPlayers.map((p, idx) => {
            const status = getPlayerStatus(p);
            const prevPlayer = idx > 0 ? sortedPlayers[idx - 1] : null;
            const prevStatus = prevPlayer ? getPlayerStatus(prevPlayer) : null;
            
            // Helper to check if status is in a category (including "removing" variants)
            const isPlayingStatus = (s) => s === "playing" || s === "playing-removing";
            const isWaitlistStatus = (s) => s === "waitlist" || s === "waitlist-removing";
            
            // Show section header when status changes
            let sectionHeader = null;
            if (isPlayingStatus(status) && !isPlayingStatus(prevStatus)) {
              sectionHeader = (
                <div style={{ background: "#dcfce7", padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  ✓ Active Players ({(teeTime?.players || []).length})
                </div>
              );
            } else if (isWaitlistStatus(status) && !isWaitlistStatus(prevStatus)) {
              sectionHeader = (
                <div style={{ background: "#fef3c7", padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  ⏳ Waitlist ({(teeTime?.waitlist || []).length})
                </div>
              );
            } else if (status === "cancelled" && prevStatus !== "cancelled") {
              sectionHeader = (
                <div style={{ background: "#fee2e2", padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "#991b1b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  ✕ Cancelled ({(teeTime?.cancelled || []).length})
                </div>
              );
            } else if (status === "available" && (isPlayingStatus(prevStatus) || isWaitlistStatus(prevStatus) || prevStatus === "cancelled")) {
              sectionHeader = (
                <div style={{ background: "#f3f4f6", padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Available Players
                </div>
              );
            }

            const cancelledInfo = status === "cancelled" ? getCancelledInfo(p.id) : null;
            
            return (
              <React.Fragment key={p.id}>
                {sectionHeader}
                <div 
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(p.id);
                }} 
                style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 10, 
                  padding: 10, 
                  borderBottom: "1px solid #f3f4f6",
                  cursor: "pointer",
                  transition: "background 0.2s",
                  background: selected.includes(p.id) ? "#f0f9ff" : "transparent"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = selected.includes(p.id) ? "#e0f2fe" : "#f9fafb"}
                onMouseLeave={(e) => e.currentTarget.style.background = selected.includes(p.id) ? "#f0f9ff" : "transparent"}
              >
                {/* Checkbox with key to force re-render on status change */}
                <div 
                  key={`checkbox-${p.id}-${status}`}
                  style={{ 
                    width: 18, 
                    height: 18, 
                    borderRadius: 4, 
                    background: getCheckboxStyle(status).bg, 
                    border: "1px solid #e5e7eb", 
                    display: "inline-flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    fontSize: 12, 
                    fontWeight: 700, 
                    color: getCheckboxStyle(status).color 
                  }}
                >
                  {getCheckboxStyle(status).icon}
                </div>
                <PlayerAvatar name={p.name} photoUrl={p.photoUrl} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span>{p.name}{teamCaptainIds.has(p.id) && <span data-testid="picker-captain" style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginLeft: 6 }}>(Captain)</span>}</span>
                    {(p.clubMember || p.club_member) && <Badge style={{ background: "#dcfce7", color: "#15803d", fontSize: 10 }}>⭐ Member</Badge>}
                    {p.hcp && <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>HCP: {p.hcp || p.handicap}</span>}
                  </div>
                  {cancelledInfo && (
                    <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>
                      Cancelled: {(() => {
                        let ts = cancelledInfo.timestamp;
                        if (typeof ts === 'string' && !ts.endsWith('Z') && !ts.includes('+')) {
                          ts = ts + 'Z'; // Treat as UTC
                        }
                        return new Date(ts).toLocaleString();
                      })()}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {p.email}
                    {(status === "playing" || status === "playing-removing" || status === "waitlist" || status === "waitlist-removing") && addedAtById.get(p.id) && (
                      <span style={{ marginLeft: 8, color: "#9ca3af", fontSize: 11 }}>
                        {formatRsvpTimestamp(addedAtById.get(p.id))}
                      </span>
                    )}
                    {/* Registered team on this round (Oct 10, 2026) — changed on ⛳ Pairings */}
                    {teamNameById.get(p.id) && (
                      <span data-testid="picker-team" style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#14532d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '1px 7px' }}>
                        👥 {teamNameById.get(p.id)}
                      </span>
                    )}
                  </div>
                </div>
                <Badge>{p.ghin || "—"}</Badge>
              </div>
            </React.Fragment>
            );
          })}
        </div>
        {willWaitlist > 0 && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 8 }}>{willWaitlist} player{willWaitlist > 1 ? "s" : ""} will be placed on the waitlist.</div>}
        
        {/* Show warning if players are being removed */}
        {removedSelections.length > 0 && (
          <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 8, padding: 12, marginTop: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#92400e", marginBottom: 4 }}>
              ⚠️ Removing {removedSelections.length} player{removedSelections.length > 1 ? "s" : ""}:
            </div>
            <div style={{ fontSize: 12, color: "#78350f" }}>
              {removedSelections.map(id => nameById.get(id)).filter(Boolean).join(", ")}
            </div>
            <div style={{ fontSize: 11, color: "#92400e", marginTop: 4 }}>
              They will be moved to the Cancelled section with a timestamp.
            </div>
          </div>
        )}
        
        {hasChanges && (
          <div data-testid="notify-players-box" style={{ background: notifyPlayers ? "#f0fdf4" : "#f9fafb", border: `1px solid ${notifyPlayers ? "#bbf7d0" : "#e5e7eb"}`, borderRadius: 8, padding: "10px 12px", marginTop: 10 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 13, color: "#111827" }}>
              <input
                type="checkbox"
                checked={notifyPlayers}
                onChange={(e) => setNotifyPlayers(e.target.checked)}
                style={{ width: 16, height: 16, marginTop: 1 }}
                data-testid="notify-players-checkbox"
              />
              <span>
                <span style={{ fontWeight: 600 }}>Email the players I'm adding or removing</span>
                <span style={{ display: "block", fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {isEvent
                    ? (notifyPlayers
                        ? "They'll get the usual tee-time confirmation or cancellation notice."
                        : "Nobody is emailed. When your field is loaded, send it from ✉️ Field welcome on the event — it shows who hasn't had it yet.")
                    : (notifyPlayers
                        ? "They'll get the usual confirmation or cancellation notice."
                        : "Nobody is emailed about this change.")}
                </span>
              </span>
            </label>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={() => onConfirm({ selectedIds: selected, preCheckedIds, notifyPlayers })} 
            disabled={!hasChanges}
            style={removedSelections.length > 0 ? { background: "#dc2626", color: "#fff" } : {}}
          >
            {removedSelections.length > 0 ? `Remove ${removedSelections.length} Player${removedSelections.length > 1 ? "s" : ""}` :
             willWaitlist > 0 ? "Save (with waitlist)" : 
             hasChanges ? "Save Changes" : "No Changes"}
          </Button>
        </div>
      </Card>
    </div>
  );
};

const PublishModal = ({ teeTimeInfo, players, groups, onConfirm, onClose, isPublishing = false, mode = "publish" }) => {
  const [publishTo, setPublishTo] = useState("all"); // all, select, group
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [customMessage, setCustomMessage] = useState(""); // NEW: Custom message field
  const [sendEmail, setSendEmail] = useState(true); // NEW: Toggle for email notification
  
  // Filter active players, excluding guests (role = 'guest' or 'Guest')
  // "All Players" population: active non-guests only (guests never get blast notifications)
  const activePlayers = useMemo(() => players.filter((p) => 
    p.active && p.role && p.role.toLowerCase() !== 'guest'
  ), [players]);

  // "Select Specific Players" population: active players INCLUDING guests, so an organizer
  // can deliberately notify a guest about a specific tee time (e.g. first-time guest + app link)
  const selectablePlayers = useMemo(() => players.filter((p) => 
    p.active && p.role
  ), [players]);
  
  // Sort selectable players alphabetically by last name
  const sortedActivePlayers = useMemo(() => {
    const getLastName = (name) => {
      const parts = (name || "").trim().split(" ");
      return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : parts[0].toLowerCase();
    };
    
    return [...selectablePlayers].sort((a, b) => {
      const lastNameA = getLastName(a.name);
      const lastNameB = getLastName(b.name);
      return lastNameA.localeCompare(lastNameB);
    });
  }, [selectablePlayers]);
  
  const togglePlayer = (playerId) => {
    setSelectedPlayers(prev => 
      prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };
  
  const getRecipientCount = () => {
    if (publishTo === "all") {
      return activePlayers.length;
    } else if (publishTo === "select") {
      return selectedPlayers.length;
    } else if (publishTo === "group" && selectedGroup) {
      const group = groups.find(g => g.id === selectedGroup);
      return group ? (group.player_ids || []).length : 0;
    }
    return 0;
  };
  
  const handlePublish = () => {
    const publishData = {
      publishTo,
      playerIds: publishTo === "select" ? selectedPlayers : [],
      groupId: publishTo === "group" ? selectedGroup : "",
      customMessage: customMessage.trim(), // NEW: Include custom message
      sendEmail: mode === "notify" ? sendEmail : true // NEW: Email flag for notify mode
    };
    onConfirm(publishData);
  };
  
  const recipientCount = getRecipientCount();
  const canPublish = publishTo === "all" || 
                     publishTo === "none" ||
                     (publishTo === "select" && selectedPlayers.length > 0) ||
                     (publishTo === "group" && selectedGroup);
  
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 20 }} onClick={onClose}>
      <Card style={{ width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>
          {mode === "notify" ? "Notify Players About Tee Time" : "Publish Tee Time to Players"}
        </div>
        {mode === "notify" && (
          <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5, marginBottom: 16 }}>
            Post this tee time to players who did not receive the original notification.
          </div>
        )}
        
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Tee Time Details:</div>
          <div style={{ fontWeight: 600 }}>{teeTimeInfo.course}</div>
          <div style={{ fontSize: 14, color: "#374151" }}>
            {utils.formatDate(teeTimeInfo.date)} at {utils.formatTime(teeTimeInfo.time)} • {teeTimeInfo.slots} slots
          </div>
        </div>
        
        <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
          {/* All Active Players Option */}
          <div 
            onClick={() => setPublishTo("all")}
            style={{ 
              padding: 12, 
              border: publishTo === "all" ? "2px solid #2563eb" : "1px solid #e5e7eb",
              borderRadius: 8,
              background: publishTo === "all" ? "#eff6ff" : "#fff",
              cursor: "pointer"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <input 
                type="radio" 
                checked={publishTo === "all"}
                onChange={() => setPublishTo("all")}
                style={{ cursor: "pointer" }}
              />
              <div style={{ fontWeight: 600 }}>All Active Players</div>
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginLeft: 26 }}>
              Send to all {activePlayers.length} active players
            </div>
          </div>
          
          {/* Select Specific Players Option */}
          <div 
            onClick={() => setPublishTo("select")}
            style={{ 
              padding: 12, 
              border: publishTo === "select" ? "2px solid #2563eb" : "1px solid #e5e7eb",
              borderRadius: 8,
              background: publishTo === "select" ? "#eff6ff" : "#fff",
              cursor: "pointer"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <input 
                type="radio" 
                checked={publishTo === "select"}
                onChange={() => setPublishTo("select")}
                style={{ cursor: "pointer" }}
              />
              <div style={{ fontWeight: 600 }}>Select Specific Players</div>
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginLeft: 26, marginBottom: publishTo === "select" ? 8 : 0 }}>
              Choose which players to notify
            </div>
            
            {publishTo === "select" && (
              <div style={{ marginLeft: 26, maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 6, padding: 8, background: "#fff" }}>
                {sortedActivePlayers.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 16, color: "#6b7280", fontSize: 13 }}>No active players available</div>
                ) : (
                  sortedActivePlayers.map((player) => {
                    const isSelected = selectedPlayers.includes(player.id);
                    return (
                      <div 
                        key={player.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePlayer(player.id);
                        }}
                        style={{ 
                          padding: 6, 
                          cursor: "pointer", 
                          borderRadius: 4,
                          marginBottom: 4,
                          background: isSelected ? "#eef2ff" : "transparent",
                          display: "flex",
                          alignItems: "center",
                          gap: 8
                        }}
                      >
                        <input 
                          type="checkbox" 
                          checked={isSelected}
                          onChange={() => {}}
                          style={{ cursor: "pointer" }}
                        />
                        <div style={{ fontSize: 13 }}>
                          <div style={{ fontWeight: isSelected ? 600 : 400 }}>{player.name}</div>
                          <div style={{ fontSize: 11, color: "#6b7280" }}>{player.email}</div>
                        </div>
                      </div>
                    );
                  })
                )}
                {selectedPlayers.length > 0 && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #e5e7eb", fontSize: 12, color: "#2563eb", fontWeight: 600 }}>
                    {selectedPlayers.length} player{selectedPlayers.length === 1 ? '' : 's'} selected
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Player Group Option */}
          <div 
            onClick={() => setPublishTo("group")}
            style={{ 
              padding: 12, 
              border: publishTo === "group" ? "2px solid #2563eb" : "1px solid #e5e7eb",
              borderRadius: 8,
              background: publishTo === "group" ? "#eff6ff" : "#fff",
              cursor: "pointer"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <input 
                type="radio" 
                checked={publishTo === "group"}
                onChange={() => setPublishTo("group")}
                style={{ cursor: "pointer" }}
              />
              <div style={{ fontWeight: 600 }}>Player Group</div>
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginLeft: 26, marginBottom: publishTo === "group" ? 8 : 0 }}>
              Send to a predefined group
            </div>
            
            {publishTo === "group" && (
              <div style={{ marginLeft: 26 }}>
                {groups.length === 0 ? (
                  <div style={{ fontSize: 13, color: "#dc2626", background: "#fee2e2", padding: 8, borderRadius: 6 }}>
                    No groups available. Create groups in Settings → Groups
                  </div>
                ) : (
                  <Select 
                    value={selectedGroup} 
                    onChange={(e) => {
                      e.stopPropagation();
                      setSelectedGroup(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">Select a group...</option>
                    {groups.map((group) => {
                      const playerCount = (group.player_ids || []).length;
                      return (
                        <option key={group.id} value={group.id}>
                          {group.name} ({playerCount} player{playerCount === 1 ? '' : 's'})
                        </option>
                      );
                    })}
                  </Select>
                )}
              </div>
            )}
          </div>

          {/* Send No Notifications Option */}
          <div 
            onClick={() => setPublishTo("none")}
            style={{ 
              padding: 12, 
              border: publishTo === "none" ? "2px solid #2563eb" : "1px solid #e5e7eb",
              borderRadius: 8,
              background: publishTo === "none" ? "#eff6ff" : "#fff",
              cursor: "pointer"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <input 
                type="radio" 
                checked={publishTo === "none"}
                onChange={() => setPublishTo("none")}
                style={{ cursor: "pointer" }}
              />
              <div style={{ fontWeight: 600 }}>Send No Notifications</div>
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginLeft: 26 }}>
              Publish without sending any player notifications
            </div>
          </div>
        </div>
        
        {/* Custom Message Field - hide when no notifications */}
        {publishTo !== "none" && (
        <div style={{ marginBottom: 16 }}>
          <Label style={{ marginBottom: 8, display: "block", fontWeight: 600 }}>
            Custom Message <span style={{ color: "#6b7280", fontWeight: 400, fontSize: 13 }}>(Optional)</span>
          </Label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Add any specific details about this tee time (weather update, meeting spot, equipment needed, etc.)..."
            maxLength={500}
            rows={4}
            style={{
              width: "100%",
              padding: 12,
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: "80px",
              boxSizing: "border-box"
            }}
          />
          <div style={{ 
            fontSize: 11, 
            color: customMessage.length > 450 ? "#dc2626" : "#9ca3af", 
            marginTop: 4,
            textAlign: "right"
          }}>
            {customMessage.length}/500 characters
          </div>
        </div>
        )}
        
        {mode === "notify" && (
          <>
            {/* Notification Options */}
            <div style={{ marginBottom: 16, padding: 12, background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Notification Options:</div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 6 }}>
                <input 
                  type="checkbox" 
                  checked={sendEmail} 
                  onChange={(e) => setSendEmail(e.target.checked)}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: 13 }}>Send email notification</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input 
                  type="checkbox" 
                  checked={!sendEmail} 
                  onChange={(e) => setSendEmail(!e.target.checked)}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: 13 }}>Add to portal only (no email)</span>
              </label>
            </div>
            
            {/* Info Banner */}
            <div style={{ 
              background: "#dbeafe", 
              border: "1px solid #93c5fd", 
              borderRadius: 8, 
              padding: 12, 
              marginBottom: 16,
              display: "flex",
              gap: 8,
              alignItems: "flex-start"
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
              <span style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.5 }}>
                Players already registered for this tee time or the tee time is present in their available tee time list will be automatically excluded.
              </span>
            </div>
          </>
        )}
        
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            {canPublish && !isPublishing && publishTo !== "none" && `Will notify ${recipientCount} player${recipientCount === 1 ? '' : 's'}`}
            {canPublish && !isPublishing && publishTo === "none" && 'No notifications will be sent'}
            {isPublishing && (
              <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#2563eb", fontWeight: 600 }}>
                <span className="spinner" style={{ 
                  display: "inline-block", 
                  width: 14, 
                  height: 14, 
                  border: "2px solid #e5e7eb", 
                  borderTopColor: "#2563eb", 
                  borderRadius: "50%", 
                  animation: "spin 0.8s linear infinite" 
                }}></span>
                Publishing and sending notifications...
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="outline" onClick={onClose} disabled={isPublishing}>Cancel</Button>
            <Button 
              onClick={handlePublish}
              disabled={!canPublish || isPublishing}
              style={{ background: isPublishing ? "#9ca3af" : "#2563eb", cursor: (!canPublish || isPublishing) ? "not-allowed" : "pointer" }}
            >
              {isPublishing ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="spinner" style={{ 
                    display: "inline-block", 
                    width: 14, 
                    height: 14, 
                    border: "2px solid #fff", 
                    borderTopColor: "transparent", 
                    borderRadius: "50%", 
                    animation: "spin 0.8s linear infinite" 
                  }}></span>
                  {mode === "notify" ? "Notifying..." : "Publishing..."}
                </span>
              ) : (
                mode === "notify" 
                  ? `Notify ${recipientCount} Player${recipientCount === 1 ? '' : 's'}` 
                  : publishTo === "none"
                    ? 'Publish (No Notifications)'
                    : `Publish to ${recipientCount} Player${recipientCount === 1 ? '' : 's'}`
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

const PlayerProfile = ({ player, onClose }) => {
  const { data: teeTimes } = useApiData('/teetimes');
  const { data: allPlayers } = useApiData('/players');

  const playerStats = useMemo(() => {
    // Filter tee times by player EMAIL (not name)
    // Handle both email strings and player objects
    const playerTeeTimes = teeTimes.filter(t => {
      const players = t.players || [];
      return players.some(p => {
        if (typeof p === 'string') return sameEmail(p, player.email);
        if (p && typeof p === 'object') return samePlayer(p, player);
        return false;
      });
    });
    
    // Create email to name lookup for displaying other players
    const playerByEmail = new Map(allPlayers.map(p => [p.email, p.name]));
    const emailToName = (emailOrPlayer) => {
      if (typeof emailOrPlayer === 'string') {
        return playerByEmail.get(emailOrPlayer) || emailOrPlayer;
      }
      if (emailOrPlayer && typeof emailOrPlayer === 'object') {
        return emailOrPlayer.name || playerByEmail.get(emailOrPlayer.email) || emailOrPlayer.email;
      }
      return emailOrPlayer;
    };

    const courseFrequency = {};
    const monthlyActivity = {};
    const playedWith = {};

    playerTeeTimes.forEach(t => {
      // Course frequency
      courseFrequency[t.course] = (courseFrequency[t.course] || 0) + 1;

      // Monthly activity
      if (t.date) {
        // Parse date components manually to avoid Safari/iOS timezone issues
        const dateStr = typeof t.date === 'string' ? t.date.split('T')[0] : '';
        const [year, month] = dateStr.split('-').map(Number);
          
        if (year && month) {
          const monthKey = `${year}-${String(month).padStart(2, '0')}`;
          monthlyActivity[monthKey] = (monthlyActivity[monthKey] || 0) + 1;
        }
      }

      // Played with - convert emails/objects to names
      (t.players || []).forEach(playerOrEmail => {
        const pEmail = typeof playerOrEmail === 'string' ? playerOrEmail : playerOrEmail?.email;
        if (pEmail && pEmail !== player.email) {
          const playerName = emailToName(playerOrEmail);
          playedWith[playerName] = (playedWith[playerName] || 0) + 1;
        }
      });
    });

    const sortedCourses = Object.entries(courseFrequency)
      .sort((a, b) => b[1] - a[1])
      .map(([course, count]) => ({ course, count }));

    const sortedPlayedWith = Object.entries(playedWith)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    const upcomingTeeTimes = teeTimes.filter(t => {
      const players = t.players || [];
      const hasPlayer = players.some(p => {
        if (typeof p === 'string') return sameEmail(p, player.email);
        if (p && typeof p === 'object') return samePlayer(p, player);
        return false;
      });
      return hasPlayer && utils.getDateTimestamp(t.date, t.time) >= utils.getTodayTimestamp();
    }).sort((a, b) => utils.getDateTimestamp(a.date, a.time) - utils.getDateTimestamp(b.date, b.time));

    const pastTeeTimes = playerTeeTimes.filter(t => 
      utils.getDateTimestamp(t.date, t.time) < utils.getTodayTimestamp()
    ).sort((a, b) => utils.getDateTimestamp(b.date, b.time) - utils.getDateTimestamp(a.date, a.time));

    const lastPlayed = pastTeeTimes.length > 0 ? pastTeeTimes[0] : null;
    const nextPlaying = upcomingTeeTimes.length > 0 ? upcomingTeeTimes[0] : null;

    return {
      totalRounds: playerTeeTimes.length,
      upcomingRounds: upcomingTeeTimes.length,
      pastRounds: pastTeeTimes.length,
      favoriteCourse: sortedCourses[0]?.course || 'N/A',
      courseFrequency: sortedCourses,
      playedWith: sortedPlayedWith,
      lastPlayed,
      nextPlaying,
      upcomingTeeTimes,
      pastTeeTimes,
      monthlyActivity
    };
  }, [player, teeTimes, allPlayers]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, overflowY: "auto", padding: 20 }} onClick={onClose}>
      <div style={{ width: "100%", maxWidth: 900, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <Card>
          {/* Create email-to-name converter for this component */}
          {(() => {
            const playerByEmail = new Map(allPlayers.map(p => [p.email, p.name]));
            const emailToName = (emailOrPlayer) => {
              if (typeof emailOrPlayer === 'string') {
                return playerByEmail.get(emailOrPlayer) || emailOrPlayer;
              }
              if (emailOrPlayer && typeof emailOrPlayer === 'object') {
                return emailOrPlayer.name || playerByEmail.get(emailOrPlayer.email) || emailOrPlayer.email || 'Unknown';
              }
              return String(emailOrPlayer || 'Unknown');
            };
            
            return (
              <>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: 9999, background: "linear-gradient(135deg, #2d6a4f, #1b4332)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700 }}>
                {((player.name || "P").split(" ").map((s) => s && s[0]).filter(Boolean).slice(0, 2).join("") || "P").toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 800 }}>{player.name}</div>
                <div style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>
                  {player.email} • {player.phone}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace", marginTop: 4 }}>
                  Player ID: {player.id}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  {player.role === 'Organizer' && <Badge style={{ background: "#fbbf24", color: "#78350f", fontWeight: 700 }}>👑 Organizer</Badge>}
                  {player.role === 'Captain' && <Badge style={{ background: "#ede9fe", color: "#6b21a8", fontWeight: 600 }}>👨‍✈️ Captain</Badge>}
                  {(player.role === 'Player' || !player.role) && <Badge style={{ background: "#dbeafe", color: "#1e40af" }}>🏌️ Player</Badge>}
                  {player.role === 'guest' && <Badge style={{ background: "#f3f4f6", color: "#4b5563" }}>👤 Guest</Badge>}
                  {(player.clubMember || player.club_member) && <Badge style={{ background: "#dcfce7", color: "#15803d" }}>⭐ Member</Badge>}
                  {player.active ? <Badge style={{ background: "#d1fae5", color: "#065f46" }}>Active</Badge> : <Badge style={{ background: "#fee2e2", color: "#991b1b" }}>Inactive</Badge>}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6, fontSize: 12 }}>
                  {player.comm_email && (
                    <span style={{ padding: "2px 8px", background: "#f3f4f6", borderRadius: 4, color: "#374151" }}>
                      📧 Email
                    </span>
                  )}
                  {player.comm_text && (
                    <span style={{ padding: "2px 8px", background: "#f3f4f6", borderRadius: 4, color: "#374151" }}>
                      📱 Text
                    </span>
                  )}
                  {!player.comm_email && !player.comm_text && (
                    <span style={{ padding: "2px 8px", background: "#fef2f2", borderRadius: 4, color: "#991b1b" }}>
                      ⚠️ No notifications enabled
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={onClose}>✕ Close</Button>
          </div>

          {/* Stats Overview */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
            <Card style={{ background: "linear-gradient(135deg, #2d6a4f, #1b4332)", color: "#fff" }}>
              <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4 }}>Total Rounds</div>
              <div style={{ fontSize: 32, fontWeight: 800 }}>{playerStats.totalRounds}</div>
            </Card>
            <Card style={{ background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff" }}>
              <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4 }}>Upcoming</div>
              <div style={{ fontSize: 32, fontWeight: 800 }}>{playerStats.upcomingRounds}</div>
            </Card>
            <Card>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>GHIN</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{player.ghin || '—'}</div>
            </Card>
            <Card>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Handicap</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{player.hcp || player.handicap || '—'}</div>
            </Card>
          </div>

          {/* Quick Info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            <Card style={{ background: "#f0fdf4" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#15803d", marginBottom: 8 }}>🏌️ Favorite Course</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{playerStats.favoriteCourse}</div>
              {playerStats.courseFrequency[0] && (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  Played {playerStats.courseFrequency[0].count} times
                </div>
              )}
            </Card>
            <Card style={{ background: "#fef3c7" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#92400e", marginBottom: 8 }}>📅 Last Played</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {playerStats.lastPlayed ? utils.formatDate(playerStats.lastPlayed.date) : 'Never'}
              </div>
              {playerStats.lastPlayed && (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  {playerStats.lastPlayed.course} at {utils.formatTime(playerStats.lastPlayed.time)}
                </div>
              )}
            </Card>
          </div>

          {/* Course Breakdown */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Course History</div>
            <Card>
              {playerStats.courseFrequency.length > 0 ? (
                <div style={{ display: "grid", gap: 8 }}>
                  {playerStats.courseFrequency.map((cf, idx) => (
                    <div key={cf.course} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: idx < playerStats.courseFrequency.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                      <div style={{ fontSize: 18, fontWeight: 600, color: "#6b7280", minWidth: 30 }}>#{idx + 1}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{cf.course}</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{((cf.count / playerStats.totalRounds) * 100).toFixed(0)}% of rounds</div>
                      </div>
                      <Badge style={{ background: "#2d6a4f", color: "#fff" }}>{cf.count} rounds</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: 24, textAlign: "center", color: "#9ca3af" }}>No course history yet</div>
              )}
            </Card>
          </div>

          {/* Frequently Played With */}
          {playerStats.playedWith.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Frequently Played With</div>
              <Card>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {playerStats.playedWith.map((pw) => (
                    <div key={pw.name} style={{ display: "flex", alignItems: "center", gap: 6, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 999, padding: "6px 12px 6px 6px" }}>
                      <PlayerAvatar name={pw.name} photoUrl={pw.photoUrl} />
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{pw.name}</span>
                      <Badge style={{ fontSize: 10 }}>{pw.count}x</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {/* Upcoming Tee Times */}
          {playerStats.upcomingTeeTimes.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Upcoming Tee Times ({playerStats.upcomingTeeTimes.length})</div>
              <Card>
                <div style={{ display: "grid", gap: 8 }}>
                  {playerStats.upcomingTeeTimes.slice(0, 5).map((t) => (
                    <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
                      <div style={{ minWidth: 100 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{utils.formatDate(t.date)}</div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>{utils.formatTime(t.time)}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{t.course}</div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>
                          Playing with: {(t.players || []).filter(p => (typeof p === 'string' ? p : p?.email) !== player.email).map(emailToName).join(", ") || "Solo"}
                        </div>
                      </div>
                      <Badge>{t.players.length}/{t.slots}</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {/* Past Round History */}
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Round History ({playerStats.pastRounds})</div>
            <Card>
              {playerStats.pastTeeTimes.length > 0 ? (
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  <div style={{ display: "grid", gap: 6 }}>
                    {playerStats.pastTeeTimes.slice(0, 20).map((t) => (
                      <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", borderBottom: "1px solid #f9fafb", fontSize: 13 }}>
                        <div style={{ minWidth: 90, color: "#6b7280" }}>{utils.formatDate(t.date)}</div>
                        <div style={{ flex: 1, fontWeight: 500 }}>{t.course}</div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>{utils.formatTime(t.time)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ padding: 24, textAlign: "center", color: "#9ca3af" }}>No past rounds recorded</div>
              )}
            </Card>
          </div>
              </>
            );
          })()}
        </Card>
      </div>
    </div>
  );
};

const PlayerManager = () => {
  const { addToast } = useToast();
  const { isReadOnly, isExpired } = useSubscription();
  const { isMobile } = useContext(MobileContext);
  const { data: players, loading, addItem, updateItem, deleteItem, refresh } = useApiData('/players');
  const { tier: orgTier, loading: tierLoading, eventAccount } = useOrgTier();
  // Default to LOCKED while tier is loading. See PlayerMessageForm for rationale.
  const smsLocked = tierLoading || !orgTier || !canUseFeature(orgTier, 'sms');
  const [showImport, setShowImport] = useState(false);   // 📥 Import players (Sept 30, 2026)
  const [mode, setMode] = useState("list");
  const [working, setWorking] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc"); // asc or desc
  const [groupByRole, setGroupByRole] = useState(false); // group players by role
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showGuests, setShowGuests] = useState(() => {
    const saved = localStorage.getItem('tto_showGuests');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // Read-only mode click handler
  const handleReadOnlyClick = () => {
    addToast(isExpired 
      ? "Your free trial has ended. Please subscribe to continue making changes." 
      : "Your account is in read-only mode. Please reactivate your subscription to make changes.", "error");
  };
  
  // Persist showGuests preference to localStorage
  useEffect(() => {
    localStorage.setItem('tto_showGuests', JSON.stringify(showGuests));
  }, [showGuests]);

  // Oct 11, 2026: a guest who plays as often as a regular counts toward the plan, so
  // he is listed (and counted in the badge) with the regulars even when guests are
  // hidden — the badge then equals the plan's own count ("20 of 40 regular players").
  const countsAsRegular = (p) => p.role === 'guest' && !!(p.frequent_guest && p.frequent_guest.counts);
  const hiddenAsGuest = (p) => p.role === 'guest' && !countsAsRegular(p);
  
  // Photo viewer modal state
  const [viewingPhoto, setViewingPhoto] = useState(null);
  
  // Bulk invitation modal state
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [newlyCreatedPlayer, setNewlyCreatedPlayer] = useState(null);

  // Duplicate-email modal state (shows when add-player hits an existing record)
  const [duplicatePlayerInfo, setDuplicatePlayerInfo] = useState(null);
  // Sept 30, 2026: the plan's regular-player limit, shown as a modal (organizers
  // miss toasts) that offers to add the same person as a Guest instead.
  const [limitInfo, setLimitInfo] = useState(null);

  // Preferred tee (Sept 15, 2026): the organizer sets a player's tee here instead of
  // impersonating them. Same list the player app offers — every tee on the org's
  // courses (GET /api/tees, the tee sheet's source).
  const [orgTees, setOrgTees] = useState([]);
  useEffect(() => {
    const fetchTees = async () => {
      try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const resp = await fetch(`${API_URL}/tees`, { headers, credentials: 'include' });
        if (resp.ok) setOrgTees(await resp.json());
      } catch (e) {
        console.error('Error fetching tees:', e);
      }
    };
    fetchTees();
  }, []);
  
  // HCP Upload modal state
  const [showHcpUpload, setShowHcpUpload] = useState(false);
  const [hcpUploadFile, setHcpUploadFile] = useState(null);
  const [hcpParsedData, setHcpParsedData] = useState(null);
  const [hcpMatches, setHcpMatches] = useState(null);
  const [hcpUploading, setHcpUploading] = useState(false);
  const [hcpParsing, setHcpParsing] = useState(false);

  // HCP Upload - Parse file to extract GHIN/HI pairs
  const parseHcpFile = async (file) => {
    setHcpParsing(true);
    try {
      const pairs = [];
      const fileName = file.name.toLowerCase();
      
      if (fileName.endsWith('.csv') || fileName.endsWith('.tsv') || fileName.endsWith('.txt')) {
        // CSV/TSV/TXT - read as text
        const text = await file.text();
        const lines = text.split(/\r?\n/);
        for (const line of lines) {
          const found = extractGhinHiFromLine(line);
          if (found) pairs.push(found);
        }
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        // Excel - use SheetJS
        // Load SheetJS from CDN
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');
        const data = await file.arrayBuffer();
        const workbook = window.XLSX.read(data);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = window.XLSX.utils.sheet_to_json(sheet, { header: 1 });
        for (const row of rows) {
          const line = row.map(c => String(c || '')).join('\t');
          const found = extractGhinHiFromLine(line);
          if (found) pairs.push(found);
        }
      } else if (fileName.endsWith('.pdf')) {
        // PDF - use pdfjsLib
        const pdfjsLib = window.pdfjsLib || await loadPdfJs();
        const data = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          // Group text items by Y position to reconstruct lines
          const lineMap = {};
          textContent.items.forEach(item => {
            const y = Math.round(item.transform[5]);
            if (!lineMap[y]) lineMap[y] = [];
            lineMap[y].push({ x: item.transform[4], text: item.str });
          });
          // Sort by Y descending (top to bottom), then X ascending (left to right)
          const sortedYs = Object.keys(lineMap).sort((a, b) => b - a);
          for (const y of sortedYs) {
            const lineItems = lineMap[y].sort((a, b) => a.x - b.x);
            const line = lineItems.map(item => item.text).join(' ');
            const found = extractGhinHiFromLine(line);
            if (found) pairs.push(found);
          }
        }
      } else {
        addToast('Unsupported file type. Use PDF, Excel, or CSV.', 'error');
        setHcpParsing(false);
        return;
      }

      // Deduplicate by GHIN (keep last occurrence)
      const deduped = {};
      pairs.forEach(p => { deduped[p.ghin] = p; });
      const uniquePairs = Object.values(deduped);

      setHcpParsedData(uniquePairs);
      
      // Match against current players
      const matches = [];
      const activePlayers = players.filter(p => p.active);
      for (const pair of uniquePairs) {
        const player = activePlayers.find(p => p.ghin && p.ghin.trim() === pair.ghin);
        if (player) {
          const currentHcp = player.hcp || player.handicap;
          const changed = currentHcp === null || currentHcp === undefined || 
                         Math.abs(parseFloat(currentHcp) - pair.hi) > 0.01;
          matches.push({
            playerId: player.id,
            playerName: player.name,
            ghin: pair.ghin,
            pdfName: pair.name || '',
            currentHcp: currentHcp,
            newHi: pair.hi,
            changed,
            autoSync: player.ghin_auto_sync || false,
          });
        }
      }
      
      // Sort: changed first, then alphabetical
      matches.sort((a, b) => {
        if (a.changed !== b.changed) return a.changed ? -1 : 1;
        return a.playerName.localeCompare(b.playerName);
      });
      
      setHcpMatches(matches);
      
      if (uniquePairs.length === 0) {
        addToast('No GHIN/Handicap data found in file. Ensure the file contains GHIN numbers and Handicap Index values.', 'error');
      } else {
        addToast(`Found ${uniquePairs.length} handicap entries, ${matches.length} matched to your players`);
      }
    } catch (error) {
      console.error('HCP file parse error:', error);
      addToast('Error parsing file: ' + error.message, 'error');
    }
    setHcpParsing(false);
  };

  // Load pdf.js dynamically
  // Load a script from CDN and return a promise
  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      if (src.includes('xlsx') && window.XLSX) return resolve();
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) return resolve();
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  // Load pdf.js dynamically
  const loadPdfJs = () => {
    return new Promise((resolve, reject) => {
      if (window.pdfjsLib) return resolve(window.pdfjsLib);
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve(window.pdfjsLib);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  // Extract GHIN # and HI from a single line of text
  const extractGhinHiFromLine = (line) => {
    if (!line || typeof line !== 'string') return null;
    const trimmed = line.trim();
    
    // Skip header/label lines
    if (/GHIN\s*(Number|#)|Handicap.*Index|Golfer.*Name|Total.*Golfer|Report.*Execution|Page\s+\d|Handicap.*Revision|Handicap.*Allowance/i.test(trimmed)) return null;
    // Skip lines with slope/rating format (e.g., "73.4/134/72")
    if (/\d{2}\.\d\/\d{3}\/\d{2}/.test(trimmed)) return null;
    // Skip NH (no handicap) lines
    if (/\bNH\s*$/.test(trimmed)) return null;
    
    // Find all numbers in the line
    const tokens = trimmed.split(/\s+/);
    
    // Strategy: Look for a GHIN-like number (5-8 digits) and an HI-like number (X.X with optional +/-)
    let ghin = null;
    let hi = null;
    let name = '';
    
    // First pass: identify GHIN (first 5-8 digit number) 
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      if (/^\d{5,8}$/.test(t) && !ghin) {
        ghin = t;
        continue;
      }
    }
    
    if (!ghin) return null;
    
    // Second pass: find HI (number with exactly one decimal, range -10 to 54)
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      // Match patterns like: 11.9, +1.3, -2.5, 0.0
      const hiMatch = t.match(/^([+-]?\d{1,2}\.\d)$/);
      if (hiMatch) {
        const val = parseFloat(hiMatch[1]);
        // Valid HI range: -10 to 54
        if (val >= -10 && val <= 54) {
          // Make sure this isn't a course rating (typically > 60)
          if (val < 55) {
            hi = val;
            break; // Take the first valid HI after GHIN
          }
        }
      }
    }
    
    if (hi === null) return null;
    
    // Try to extract name (text between GHIN and HI)
    const ghinIdx = trimmed.indexOf(ghin);
    const hiStr = hi >= 0 ? hi.toFixed(1) : '+' + Math.abs(hi).toFixed(1);
    const hiIdx = trimmed.indexOf(hiStr, ghinIdx + ghin.length);
    if (hiIdx > ghinIdx) {
      name = trimmed.substring(ghinIdx + ghin.length, hiIdx).trim();
      // Clean up: remove local numbers at the start (like "1048" in "1048 David Agee")
      name = name.replace(/^\d+\s+/, '').trim();
      // Remove Mr./Dr./Mrs. prefixes
      name = name.replace(/^(Mr\.\s*|Dr\.\s*|Mrs\.\s*)/i, '').trim();
    }
    
    // Handle plus handicaps: +1.3 means HI is stored as negative in our system
    // Actually, in USGA reports, +1.3 means the player is BETTER than scratch
    // Store as negative to match our DB convention
    
    return { ghin, hi, name };
  };

  // Apply HCP updates
  // HCP Upload success state
  const [hcpUpdateResult, setHcpUpdateResult] = useState(null);

  const applyHcpUpdates = async () => {
    const toUpdate = hcpMatches.filter(m => m.changed); // include auto-sync players — HCP upload always wins
    if (toUpdate.length === 0) {
      addToast('No updates to apply', 'info');
      return;
    }
    
    setHcpUploading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/players/batch-update-handicaps`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          updates: toUpdate.map(m => ({ playerId: m.playerId, handicap: m.newHi }))
        })
      });
      
      if (!response.ok) throw new Error('Failed to update handicaps');
      
      const result = await response.json();
      setHcpUpdateResult(result);
      refresh(); // Refresh player list
    } catch (error) {
      console.error('HCP update error:', error);
      addToast('Failed to update handicaps: ' + error.message, 'error');
    }
    setHcpUploading(false);
  };

  // Auto-refresh players every 30 seconds (SILENT - no visual reload)
  // Pauses when user is actively working (editing, viewing profile, or confirming delete)
  useEffect(() => {
    // Don't auto-refresh if user is editing, viewing a profile, or has a modal open
    const isUserWorking = mode !== "list" || viewingProfile || confirmDeleteId;
    
    if (isUserWorking) {
      return; // Don't set up interval if user is working
    }
    
    const intervalId = setInterval(() => {
      refresh(true); // SILENT refresh - no loading spinner
      setLastRefresh(new Date());
    }, 30000); // 30 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [refresh, mode, viewingProfile, confirmDeleteId]);

  const getPlayerLink = (player) => {
    if (!player.magic_token) return null;
    const baseUrl = window.location.origin;
    return `${baseUrl}/portal?token=${player.magic_token}`;
  };

  // Oct 11, 2026: "Address is correct — resume email". A player whose address SendGrid
  // won't deliver to (bounced / invalid / spam report) carries p.email_issue from
  // GET /players. The organizer can clear it once per address; a spam report needs a
  // tick first ("I've checked with them").
  const [resumingEmailId, setResumingEmailId] = useState(null);
  const [spamConfirmId, setSpamConfirmId] = useState(null);
  const [spamConfirmed, setSpamConfirmed] = useState(false);
  const resumeEmail = async (player, confirmSpam = false) => {
    if (!player || !player.email_issue) return;
    if (player.email_issue.kind === 'spam_reports' && !confirmSpam) {
      setSpamConfirmId(player.id); setSpamConfirmed(false);
      return;
    }
    setResumingEmailId(player.id);
    try {
      const _headers = { 'Content-Type': 'application/json' };
      const _token = getAuthToken();
      if (_token) _headers['Authorization'] = `Bearer ${_token}`;
      const response = await fetch(`${API_URL}/players/${player.id}/resume-email`, {
        method: 'POST',
        credentials: 'include',
        headers: _headers,
        body: JSON.stringify({ confirmSpam: !!confirmSpam }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        addToast(data.error || 'Could not resume email for this player', 'error');
      } else {
        addToast(`Email resumed for ${player.name}`);
        setSpamConfirmId(null);
      }
      await refresh(true);
    } catch (e) {
      addToast('Could not resume email for this player', 'error');
    } finally {
      setResumingEmailId(null);
    }
  };

  const copyPlayerLink = (player) => {
    const link = getPlayerLink(player);
    if (!link) {
      addToast("Player has no magic link", "error");
      return;
    }
    
    navigator.clipboard.writeText(link).then(() => {
      addToast(`Link copied for ${player.name}!`);
    }).catch(() => {
      addToast("Failed to copy link", "error");
    });
  };

  const copyShortUrl = async (player) => {
    try {
      // Call backend to generate/retrieve short URL
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/players/${player.id}/generate-short-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const shortUrl = data.shortUrl; // e.g., https://teetimeorganizer.net/p/xk7m2q
      
      // Copy to clipboard
      await navigator.clipboard.writeText(shortUrl);
      addToast(`Short link copied for ${player.name}!`);
    } catch (error) {
      console.error('Error generating/copying short URL:', error);
      addToast("Failed to copy short link", "error");
    }
  };

  const previewAsPlayer = (player) => {
    const link = getPlayerLink(player);
    if (!link) {
      addToast("Player has no magic link", "error");
      return;
    }
    window.open(link, '_blank');
  };

  const regenerateToken = async (player) => {
    const who = player.name || 'this player';
    if (!window.confirm(`Reset ${who}'s link?\n\nTheir current player link (and any bookmarked copy, QR code, or short link) will stop working, and a new link is generated. Use this to cut off an old or unwanted link.`)) return;

    // Offer to hand the new link straight to the player. Without this the reset is a
    // silent lockout — they're cut off the moment it lands and stay cut off until the
    // organizer remembers to copy the new link and send it. Only offered when there's
    // an address to send to; otherwise the organizer copies it manually as before.
    let sendEmail = false;
    if (player.email) {
      sendEmail = window.confirm(`Email ${who} their new link?\n\nSends to: ${player.email}\n\nOK = send it now.\nCancel = don't send (you'll need to copy the new link and give it to them yourself, or they won't be able to get in).`);
    }

    try {
      // Call backend to regenerate token (auth headers for organizer/impersonation)
      const _headers = { 'Content-Type': 'application/json' };
      const _token = getAuthToken();
      if (_token) _headers['Authorization'] = `Bearer ${_token}`;
      const response = await fetch(`${API_URL}/players/${player.id}/regenerate-token`, {
        method: 'POST',
        credentials: 'include',
        headers: _headers,
        body: JSON.stringify({ sendEmail }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Refresh the players list to get the updated token
      await refresh();

      // Report the email outcome honestly — a failed send is the difference between
      // "handed off" and "locked out", so it must not be swallowed by a success toast.
      if (!sendEmail) {
        addToast("New link generated — copy it and send it to the player");
      } else if (data && data.emailSent) {
        addToast(`New link generated and emailed to ${player.email}`);
      } else {
        addToast(`Link reset, but the email FAILED${data && data.emailError ? ': ' + data.emailError : ''}. Copy the new link and send it manually.`, "error");
      }
    } catch (err) {
      console.error('Error regenerating token:', err);
      addToast("Failed to regenerate link", "error");
    }
  };

  // When a player is saved as Suspended, offer to cancel their upcoming tee times
  // in THIS org (removing them from the sheet and any games). Per-org only.
  const offerCancelUpcomingOnSuspend = async (id, name) => {
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const resp = await fetch(`${API_URL}/teetimes`, { credentials: 'include', headers });
      if (!resp.ok) return;
      const teeTimes = await resp.json();
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const upcoming = teeTimes.filter(tt => {
        const [y, m, d] = tt.date.split('T')[0].split('-').map(Number);
        const dt = new Date(y, m - 1, d); dt.setHours(0, 0, 0, 0);
        if (dt < today) return false;
        return (tt.players || []).some(pd => (pd && typeof pd === 'object') ? (pd.id === id) : false);
      });
      const count = upcoming.length;
      if (count === 0) return;
      const ok = window.confirm(
        `${name || 'This player'} has ${count} upcoming tee time${count > 1 ? 's' : ''}. Cancel ${count > 1 ? 'them' : 'it'} now so they're removed from the sheet (and any games)?\n\nClick OK to cancel, or Cancel to leave ${count > 1 ? 'them' : 'it'} in place.`
      );
      if (!ok) return;
      const headers2 = { 'Content-Type': 'application/json' };
      if (token) headers2['Authorization'] = `Bearer ${token}`;
      const cr = await fetch(`${API_URL}/players/${id}/cancel-upcoming-reservations`, { method: 'POST', credentials: 'include', headers: headers2 });
      const data = await cr.json().catch(() => ({}));
      if (cr.ok) {
        const n = (data.cancelled != null) ? data.cancelled : count;
        addToast(`Cancelled ${n} upcoming tee time${n !== 1 ? 's' : ''}.`);
        await refresh();
      } else {
        addToast(data.error || 'Failed to cancel tee times', 'error');
      }
    } catch (e) {
      console.error('offerCancelUpcomingOnSuspend error:', e);
    }
  };


  const getTokenStatus = (player) => {
    if (!player.magic_token) {
      return { status: 'missing', label: 'No Link', color: '#dc2626' };
    }
    if (!player.token_expires_at) {
      return { status: 'no-expiry', label: 'No Expiry', color: '#f59e0b' };
    }
    
    const expiresAt = new Date(player.token_expires_at);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiresAt - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', color: '#dc2626' };
    }
    if (daysUntilExpiry < 30) {
      return { status: 'expiring-soon', label: `${daysUntilExpiry}d left`, color: '#f59e0b' };
    }
    return { status: 'active', label: 'Active', color: '#15803d' };
  };

  const startNew = () => {
    setWorking({ 
      id: utils.generateId(), 
      name: "", 
      phone: "", 
      email: "", 
      ghin: "", 
      hcp: "", 
      clubMember: false, 
      active: true, 
      suspended: false, 
      role: eventAccount ? "guest" : "Player",  // Event Pass accounts add golfers as guests (Sept 30, 2026)
      comm_email: true,
      comm_text: false,
      photoUrl: "",
      notify_on_cancellation: false,
      preferred_tee: ""
    });
    setMode("new");
  };

  // Oct 11, 2026 — plan usage on the Players page. A counter under the heading
  // ("18 of 20 regular players", amber within 2 of the limit, red at it) and a one-time
  // notice when an add or edit brings the group to 2 below the limit and to the limit,
  // so the block on the next add never comes as a surprise. The count is the plan's
  // own (/subscription/status: regulars + guests who play like regulars), so it also
  // moves when a guest starts counting. Unlimited plans see nothing.
  const [planStatus, setPlanStatus] = useState(null);
  const [planNotice, setPlanNotice] = useState(null);
  const fetchPlanStatus = async () => {
    try {
      const _headers = {};
      const _token = getAuthToken();
      if (_token) _headers['Authorization'] = `Bearer ${_token}`;
      const r = await fetch(`${API_URL}/subscription/status`, { headers: _headers, credentials: 'include' });
      if (!r.ok) return null;
      const d = await r.json();
      const st = d.subscription || d;
      setPlanStatus(st);
      return st;
    } catch (e) {
      return null;
    }
  };
  useEffect(() => { fetchPlanStatus(); }, [players.length]);   // eslint-disable-line react-hooks/exhaustive-deps
  const planLimitOf = (st) => {
    if (!st) return null;
    if (['complimentary', 'founders', 'club'].includes(String(st.currentTier || '').toLowerCase())) return null;
    const max = st.tierLimits && st.tierLimits.maxPlayers;
    return Number.isFinite(max) && max > 0 ? max : null;
  };
  const checkPlanMilestone = async (before) => {
    const st = await fetchPlanStatus();
    const max = planLimitOf(st);
    if (!max || before == null) return;
    const now = Number(st.playerCount) || 0;
    if (now <= before) return;
    const planName = st.hasPlayerLimitOverride ? null : (st.currentTierName || null);
    if (now >= max && before < max) setPlanNotice({ kind: 'reached', count: now, max, planName });
    else if (now >= max - 2 && before < max - 2) setPlanNotice({ kind: 'approaching', count: now, max, planName });
  };

  const startEdit = (p) => {
    // Map database fields to form fields
    setWorking({
      ...p,
      hcp: p.hcp || p.handicap || "",
      clubMember: p.clubMember !== undefined ? p.clubMember : (p.club_member || false),
      active: p.active !== undefined ? p.active : true,
      suspended: p.suspended === true,
      role: p.role || "Player",
      comm_email: p.comm_email !== undefined ? p.comm_email : true,
      comm_text: p.comm_text !== undefined ? p.comm_text : false,
      photoUrl: p.photoUrl || "",
      notify_on_cancellation: p.notify_on_cancellation || false,
      preferred_tee: p.preferred_tee || ""
    });
    setMode("edit");
  };

  const cancel = () => {
    setMode("list");
    setWorking(null);
    setLimitInfo(null);
  };

  const save = async (draft) => {
    // VALIDATION: Name is required
    if (!draft.name || !draft.name.trim()) {
      addToast("Player name is required", "error");
      return;
    }
    
    // Email is optional (Oct 10, 2026 — Dennis: a player may have no email; event
    // fields and held spots often don't). A golfer with none gets no emails.
    // VALIDATION: Email format, when one is given
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (draft.email && draft.email.trim() && !emailRegex.test(draft.email.trim())) {
      addToast("Please enter a valid email address", "error");
      return;
    }
    
    // VALIDATION: Phone number must be 10 digits if provided
    if (draft.phone && draft.phone.trim()) {
      const digitsOnly = utils.digitsOnly(draft.phone);
      if (digitsOnly.length !== 10) {
        addToast("Phone number must be exactly 10 digits", "error");
        return;
      }
    }
    
    const cleaned = { 
      id: draft.id, 
      name: draft.name.trim(), 
      phone: utils.formatPhone(draft.phone), 
      email: (draft.email || '').trim(), 
      ghin: draft.ghin, 
      hcp: draft.hcp || "", 
      clubMember: !!draft.clubMember, 
      active: !!draft.active,
      suspended: !!draft.suspended,
      role: draft.role || 'Player',
      comm_email: !!draft.comm_email,
      comm_text: !!draft.comm_text,
      photoUrl: draft.photoUrl || "",
      notify_on_cancellation: !!draft.notify_on_cancellation,
      preferred_tee: draft.preferred_tee || ""
    };
    
    const planBefore = planStatus ? Number(planStatus.playerCount) : null;   // Oct 11, 2026: plan milestones
    try {
      // Save player
      if (mode === "new") {
        const savedPlayer = await addItem(cleaned);
        addToast("Player added successfully");
        checkPlanMilestone(planBefore);
        
        // Show bulk invitation modal for newly created player
        setNewlyCreatedPlayer(savedPlayer);
        setShowInvitationModal(true);
        // Don't call cancel() yet - modal will handle it
      } else if (mode === "edit") {
        await updateItem(cleaned.id, cleaned);
        addToast("Player updated successfully");
        checkPlanMilestone(planBefore);
        if (cleaned.suspended) {
          await offerCancelUpcomingOnSuspend(cleaned.id, cleaned.name);
        }
        cancel(); // Cancel for edit mode
      }
    } catch (err) {
      console.error(err);
      // Detect duplicate-email error (409) from POST /players — show modal that lets organizer find existing record
      const existing = err?.responseData?.existingPlayer;
      if (err?.status === 409 && existing) {
        setDuplicatePlayerInfo(existing);
        return;
      }
      // Oct 11, 2026: the limit can also refuse an EDIT (Guest → Player, or reactivating
      // a regular) and, when the group is already over its limit, a new guest. Neither
      // offers "Add as Guest" (useApiData's update error carries .code/.body instead of
      // .responseData).
      const limitBody = err?.responseData || err?.body || {};
      if ((limitBody.code || err?.code) === 'TIER_LIMIT_REACHED') {
        setLimitInfo({ message: limitBody.message || err.message, draft, noGuest: mode !== "new" || !!limitBody.guestRefused, over: !!limitBody.guestRefused });
        return;
      }
      addToast(err.message || "Failed to save player", "error");
    }
  };

  const remove = async (id) => {
    try {
      // CRITICAL VALIDATION: Check if player has upcoming reservations
      // Include auth headers for impersonation support
      const headers = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const teeTimesResponse = await fetch(`${API_URL}/teetimes`, {
        credentials: 'include',
        headers
      });
      if (!teeTimesResponse.ok) throw new Error('Failed to fetch tee times');
      const teeTimes = await teeTimesResponse.json();
      
      const player = players.find(p => p.id === id);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Find upcoming reservations for this player
      const upcomingReservations = teeTimes.filter(tt => {
        const [year, month, day] = tt.date.split('T')[0].split('-').map(Number);
	const teeTimeDate = new Date(year, month - 1, day);  // ✅ Local date
        teeTimeDate.setHours(0, 0, 0, 0);
        const isFuture = teeTimeDate >= today;
        
        if (!isFuture) return false;
        
        // Check if player is in the players array (handle both string emails and objects)
        const hasPlayer = (tt.players || []).some(playerData => {
          if (typeof playerData === 'string') {
            return sameEmail(playerData, player?.email);
          } else if (playerData && typeof playerData === 'object') {
            return playerData.id === id || (!playerData.id && sameEmail(playerData.email, player?.email));
          }
          return false;
        });
        
        return hasPlayer;
      });
      
      if (upcomingReservations.length > 0) {
        addToast(
          `Cannot delete this player. They have ${upcomingReservations.length} upcoming tee time reservation${upcomingReservations.length > 1 ? 's' : ''}. Please remove them from those tee times first.`,
          "error"
        );
        setConfirmDeleteId(null);
        return;
      }
      
      // No upcoming reservations, safe to delete
      await deleteItem(id);
      addToast("Player removed successfully");
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('Error deleting player:', err);
      addToast(err.message || "Error deleting player", "error");
      setConfirmDeleteId(null);
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>Loading players...</div>;
  }

  // Filter and sort players
  // Nov 2, 2026 (Dennis): a held spot ("Spruill team — player 2") ended in a number and
  // sorted above every name. It now files under its team's name, just after the real
  // golfers with that surname; the full name breaks ties between equal surnames.
  const getLastName = (name) => {
    const s = String(name || '').trim();
    // Any name ending in a number ("Spruill team — player 2", "Worley Player 3") is a
    // spot, not a surname: file it under its first word, after that surname's golfers.
    const held = s.match(/^(\S+).*?(\d+)$/);
    if (held && /\D/.test(held[1])) {
      return `${held[1].replace(/[^A-Za-z'’-]/g, '').toLowerCase()}\uffff${held[2].padStart(3, '0')}`;
    }
    // "Jim Conley Jr" files under Conley, not Jr.
    const parts = s.split(/\s+/).filter((w, i, a) => !(i > 0 && i === a.length - 1 && /^(jr|sr|ii|iii|iv)\.?$/i.test(w)));
    return parts[parts.length - 1].toLowerCase();
  };

  const filteredAndSortedPlayers = players
    .filter(p => {
      // Filter out guests if toggle is off
      if (!showGuests && hiddenAsGuest(p)) return false;
      
      if (!searchTerm.trim()) return true;
      const search = searchTerm.toLowerCase();
      return (
        p.name.toLowerCase().includes(search) ||
        (p.email && p.email.toLowerCase().includes(search)) ||
        (p.phone && p.phone.includes(search)) ||
        (p.ghin && p.ghin.includes(search)) ||
        (p.role && p.role.toLowerCase().includes(search))
      );
    })
    .sort((a, b) => {
      if (groupByRole) {
        const roleRank = (p) => {
          const r = (p.role || 'Player');
          if (r === 'Organizer') return 0;
          if (r === 'Captain') return 1;
          if (r === 'guest') return 3;
          return 2; // Player or null
        };
        const rankDiff = roleRank(a) - roleRank(b);
        if (rankDiff !== 0) return rankDiff;
      }
      const aLast = getLastName(a.name);
      const bLast = getLastName(b.name);
      const comparison = aLast.localeCompare(bLast) || String(a.name || '').localeCompare(String(b.name || ''));
      return sortOrder === "asc" ? comparison : -comparison;
    });

  if (mode === "new" || mode === "edit") {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>{mode === "new" ? "Add Player" : "Edit Player"}</div>
        <Card>
          <div style={{ display: "grid", gap: 12 }}>
            {/* Phase 5 (May 2026): Smart hybrid mobile layout for the 3 paired-column form rows.
                Mobile: each input on its own row (cleaner labels, no cramped fields).
                Desktop: original 2-column layout. */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div><Label>Name</Label><Input value={working.name} onChange={(e) => setWorking({ ...working, name: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={working.phone} onChange={(e) => setWorking({ ...working, phone: utils.formatPhone(e.target.value) })} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div><Label>Email</Label><Input type="email" value={working.email} onChange={(e) => setWorking({ ...working, email: e.target.value })} /></div>
              <div><Label>GHIN</Label><Input value={working.ghin} onChange={(e) => setWorking({ ...working, ghin: e.target.value })} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div><Label>Handicap</Label><Input value={working.hcp} onChange={(e) => setWorking({ ...working, hcp: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <Select value={working.role === 'Organizer' ? 'Organizer' : (working.role || 'Player')} onChange={(e) => setWorking({ ...working, role: e.target.value })} disabled={working.role === 'Organizer'}>
                  <option value="Player">Player</option>
                  <option value="Captain">Captain</option>
                  {working.role === 'Organizer' && <option value="Organizer">Organizer</option>}
                  <option value="guest">Guest</option>
                </Select>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  {eventAccount && (
                    <div style={{ color: "#166534", fontWeight: 600, marginBottom: 4 }}>
                      Adding golfers for your event? Keep them as Guest: guests are free and don&rsquo;t count toward your plan.
                    </div>
                  )}
                  <strong>Player:</strong> A regular in your group. Gets an invitation whenever you post a new tee time, sees all your group&rsquo;s tee times, and counts toward your plan.<br/>
                  <strong>Captain:</strong> A regular player (counts toward your plan) who can reserve spots for others when Group RSVP is in Designated Captains mode.<br/>
                  <strong>Guest:</strong> Plays when invited, whether a regular&rsquo;s guest or an event golfer. Sees only the rounds they&rsquo;re invited to, gets no new-tee-time invitations, and is free. Guests don&rsquo;t count toward your plan unless they play often enough to be a regular in practice; then they count like one.
                  {working.role === 'Organizer' && (
                    <>
                      <br/><strong>Organizer:</strong> Set via Team Invite in Settings. Use Team Members to manage access.
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Preferred tee (Sept 15, 2026). Same list as the player app: every tee on the
                org's courses, grouped by course, narrowed to the player's category (M/W)
                when it is known. A value that isn't on the list (a legacy tee name, or a
                tee on a course this org no longer has) stays selectable so Save leaves it
                alone unless the organizer picks something else. */}
            {orgTees.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div>
                  <Label>Preferred Tee</Label>
                  <Select value={working.preferred_tee || ''} onChange={(e) => setWorking({ ...working, preferred_tee: e.target.value })}>
                    <option value="">-- Not set --</option>
                    {(() => {
                      const g = working.gender;
                      const genderTees = (g === 'M' || g === 'W') ? orgTees.filter(t => isWomensTee(t.tee_name) === (g === 'W')) : orgTees;
                      const scoped = genderTees.length > 0 ? genderTees : orgTees;
                      const current = working.preferred_tee || '';
                      const known = current && orgTees.some(t => t.id === current);
                      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(current);
                      return [
                        current && !known ? (
                          <option key="__current" value={current}>{isUuid ? 'Tee on a course no longer in your list' : `${current} (matched by name)`}</option>
                        ) : null,
                        ...[...new Set(scoped.map(t => t.course_name))].map(courseName => (
                          <optgroup key={courseName} label={courseName}>
                            {scoped.filter(t => t.course_name === courseName).map(tee => (
                              <option key={tee.id} value={tee.id}>{tee.tee_name}</option>
                            ))}
                          </optgroup>
                        )),
                      ];
                    })()}
                  </Select>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                    Sets the course handicap on the tee sheet and in scoring; matched by tee name on your other courses. The player can also change it in their profile.
                  </div>
                </div>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={working.clubMember} onChange={(e) => setWorking({ ...working, clubMember: e.target.checked })} />
                <span style={{ fontSize: 13 }}>Club Member</span>
              </label>
            </div>
            <div style={{ padding: "4px 0 8px" }}>
              <Label>Player Status</Label>
              <div style={{ maxWidth: 340 }}>
              <Select value={working.active === false ? 'inactive' : (working.suspended ? 'suspended' : 'active')} onChange={(e) => {
                const v = e.target.value;
                if (v === 'active') setWorking({ ...working, active: true, suspended: false });
                else if (v === 'suspended') setWorking({ ...working, active: true, suspended: true });
                else setWorking({ ...working, active: false, suspended: false });
              }}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="inactive">Inactive</option>
              </Select>
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                <strong>Active:</strong> Full access — sees and signs up for tee times, gets notifications. Included in the player count.<br/>
                <strong>Suspended:</strong> Temporary block — can't see or sign up for your tee times and is asked to contact the organizer. Keeps their spot and history, and is still included in the player count. Reversible anytime.<br/>
                <strong>Inactive:</strong> No longer active in your group — stops notifications and new tee times, but keeps their history. No longer included in the player count.<br/>
                <span style={{ color: "#9ca3af" }}>To permanently remove a player and their history, use Delete below.</span>
              </div>
            </div>
                        {/* Player Photo Upload Section */}
            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 16,
              background: '#f9fafb',
              marginBottom: 16
            }}>
              <div style={{
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 12,
                color: '#374151'
              }}>
                Player Photo
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: '160px', height: '160px', flexShrink: 0 }}>
                  <PlayerAvatar 
                    key={working.photoUrl || 'no-photo'}
                    name={working.name} 
                    photoUrl={working.photoUrl}
                    style={{ width: '160px', height: '160px', fontSize: '52px', minWidth: '160px', minHeight: '160px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <PlayerPhotoUpload
                    currentPhotoUrl={working.photoUrl}
                    onPhotoChange={(photoUrl) => {
                      setWorking({
                        ...working,
                        photoUrl: photoUrl || ''
                      });
                    }}
                  />
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    Upload a profile photo (JPG, PNG). Will be resized to 200x200px.
                  </div>
                </div>
              </div>
            </div>
            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
              <Label>Communication Preferences</Label>
              <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input 
                    type="checkbox" 
                    checked={working.comm_email} 
                    onChange={(e) => setWorking({ ...working, comm_email: e.target.checked })} 
                  />
                  <span style={{ fontSize: 13 }}>📧 Email</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", opacity: smsLocked ? 0.55 : 1 }}
                  onClick={(e) => {
                    if (smsLocked) {
                      e.preventDefault();
                      triggerUpgradeModal({ feature: 'sms', requiredTier: 'plus', currentTier: orgTier });
                    }
                  }}
                >
                  <input 
                    type="checkbox" 
                    checked={smsLocked ? false : working.comm_text} 
                    onChange={(e) => {
                      if (smsLocked) return;
                      setWorking({ ...working, comm_text: e.target.checked });
                    }}
                    disabled={smsLocked}
                  />
                  <span style={{ fontSize: 13 }}>📱 Text {smsLocked && <LockBadge feature="sms" requiredTier="plus" style={{ marginLeft: 4, fontSize: 10, padding: '1px 6px' }} />}</span>
                </label>
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                Select how this player prefers to receive notifications
              </div>
            </div>
            {working.role === 'Organizer' && (
              <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
                <Label>Organizer Notifications</Label>
                <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input 
                      type="checkbox" 
                      checked={working.notify_on_cancellation} 
                      onChange={(e) => setWorking({ ...working, notify_on_cancellation: e.target.checked })} 
                    />
                    <span style={{ fontSize: 13 }}>🔔 Receive cancellation notifications</span>
                  </label>
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  When checked, this person will receive an email when any player cancels their RSVP
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button variant="outline" onClick={cancel}>Cancel</Button>
              <Button onClick={() => save(working)}>Save</Button>
            </div>
          </div>
        </Card>
        
        {/* Sept 30, 2026: these two come from Save, which happens in this form view, so
            they render here. In the list view they only appeared after Cancel. */}
        {/* Regular-player limit (Sept 30, 2026). Upgrade is the main action. Guest is
            offered for a genuine visitor or event golfer, with what a guest gives up spelled
            out, so it never reads as a way around the plan (Dennis, Sept 30). */}
        {limitInfo && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }} onClick={() => setLimitInfo(null)}>
            <Card style={{ width: "100%", maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>{limitInfo.over ? "Your group is over its plan limit" : "Regular-player limit reached"}</div>
              <div style={{ color: "#374151", marginBottom: 12, lineHeight: 1.5 }}>{limitInfo.message}</div>
              {!limitInfo.noGuest && <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 13.5, color: "#78350f", lineHeight: 1.5 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Before choosing Guest, note that a guest:</div>
                <ul style={{ margin: "0 0 6px 18px", padding: 0 }}>
                  <li>does not see new tee times in their player app</li>
                  <li>gets no email or text when you post a new tee time</li>
                  <li>can only play when you invite them to a specific round</li>
                </ul>
                <div>Guest is meant for visitors and event golfers. For someone who plays with your group regularly, upgrade your plan. A guest who plays as often as a regular will count toward your plan.</div>
              </div>}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                <Button variant="outline" onClick={() => setLimitInfo(null)}>Cancel</Button>
                {!limitInfo.noGuest && <Button variant="outline" onClick={() => {
                  const asGuest = { ...limitInfo.draft, role: 'guest' };
                  setLimitInfo(null);
                  setWorking(asGuest);
                  save(asGuest);
                }}>Add as Guest</Button>}
                <Button onClick={() => { setLimitInfo(null); window.location.href = window.location.pathname + '?tab=settings&upgrade=1'; }}>Upgrade Plan</Button>
              </div>
            </Card>
          </div>
        )}

        {/* Duplicate Player Modal — shown when "Add Player" hits an existing record (often a hidden guest) */}
        {duplicatePlayerInfo && (
          <ConfirmDialog
            title="Player already exists"
            message={`A player with email "${duplicatePlayerInfo.email}" already exists: ${duplicatePlayerInfo.name}. They may be hidden by the "Show Guests" filter. Click View Player to find them in the list.`}
            confirmText="View Player"
            confirmVariant="default"
            cancelText="Cancel"
            onConfirm={() => {
              // Force-show guests so player will appear, populate search to surface them, return to list view
              setShowGuests(true);
              setSearchTerm(duplicatePlayerInfo.email || "");
              setDuplicatePlayerInfo(null);
              cancel(); // Close the add-player form, return to list (mode = "list")
            }}
            onCancel={() => setDuplicatePlayerInfo(null)}
          />
        )}

        {/* Bulk Invitation Modal - ALSO rendered in form view */}
        {showInvitationModal && newlyCreatedPlayer && (
          <BulkInvitationModal
            isOpen={showInvitationModal}
            newPlayer={newlyCreatedPlayer}
            groupId="default"
            onClose={() => {
              setShowInvitationModal(false);
              setNewlyCreatedPlayer(null);
              cancel(); // Close the add player form
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <PlayerImport isOpen={showImport} onClose={() => setShowImport(false)} eventAccount={eventAccount} apiUrl={API_URL} getToken={getAuthToken}
        onImported={(d) => { refresh(true); fetchPlanStatus(); const s = d && d.summary;   /* silent: a loading refresh unmounted the window and threw away its Done screen (Oct 8, 2026) */ if (s) addToast(`Imported: ${s.created} added, ${s.updated} updated${s.skipped ? `, ${s.skipped} skipped` : ''}`); }} />
      {planNotice && !showInvitationModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }} onClick={() => setPlanNotice(null)}>
          <Card style={{ width: "100%", maxWidth: 460 }} onClick={(e) => e.stopPropagation()} data-testid="plan-notice">
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>
              {planNotice.kind === 'reached' ? "You've reached your plan's player limit" : 'Getting close to your plan limit'}
            </div>
            <div style={{ color: "#374151", marginBottom: 14, lineHeight: 1.5 }}>
              {planNotice.kind === 'reached'
                ? <>You now have {planNotice.count} regular players, the {planNotice.max} {planNotice.planName ? `your ${planNotice.planName} plan includes` : 'your account is set up for'}. To add another regular player, you&rsquo;ll need to {planNotice.planName ? 'upgrade your plan' : 'contact us to raise your limit'} or remove a player. Occasional guests can still be added.</>
                : <>You now have {planNotice.count} of the {planNotice.max} regular players {planNotice.planName ? `your ${planNotice.planName} plan includes` : 'your account is set up for'}, so there&rsquo;s room for {Math.max(0, planNotice.max - planNotice.count)} more. Occasional guests are always free.</>}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              {planNotice.planName && <Button variant="outline" onClick={() => { setPlanNotice(null); window.location.href = window.location.pathname + '?tab=settings&upgrade=1'; }}>See plans</Button>}
              <Button onClick={() => setPlanNotice(null)}>OK</Button>
            </div>
          </Card>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Players</div>
          {/* Aug 16, 2026: this badge counted every active row regardless of role and
              ignored the Show Guests toggle, so it contradicted the "Showing X of Y
              players • N guests hidden" line directly below it. Harmless until an org
              actually had guests — Low Country's 110-guest load made it read 158 while
              the list showed 48. Now it follows the toggle: guests shown = everyone,
              guests hidden = the roster that counts toward the plan cap (Player +
              Captain + Organizer), which is the same figure the admin dashboard and
              subscriptionService report as billable.
              Search is deliberately NOT applied here — the badge describes the roster,
              not the current query; "Showing X of Y" already covers filtering. */}
          <Badge>{players.filter((p) => p.active && (showGuests || !hiddenAsGuest(p))).length}</Badge>
          {/* Oct 28, 2026 (dry-run list #1): a hidden guest looked like a missing player. */}
          {!showGuests && players.filter(hiddenAsGuest).length > 0 && (
            <button type="button" data-testid="show-hidden-guests" onClick={() => setShowGuests(true)}
              style={{ background: "none", border: "none", padding: 0, font: "inherit", fontSize: 13, color: "#1d4ed8", textDecoration: "underline", cursor: "pointer", whiteSpace: "nowrap" }}>
              {players.filter(hiddenAsGuest).length} guest{players.filter(hiddenAsGuest).length !== 1 ? 's' : ''} hidden · Show
            </button>
          )}
          {(() => {
            const max = planLimitOf(planStatus);
            if (!max) return null;
            const n = Number(planStatus.playerCount) || 0;
            const fg = Number(planStatus.frequentGuestCount) || 0;
            const color = n >= max ? '#b91c1c' : (n >= max - 2 ? '#b45309' : '#6b7280');
            return (
              <span data-testid="plan-usage" title={fg > 0 ? `Includes ${fg} ${fg === 1 ? 'guest who plays' : 'guests who play'} as often as a regular` : 'Regular players (Player, Captain, Organizer). Occasional guests are free.'}
                style={{ fontSize: 12, fontWeight: n >= max - 2 ? 700 : 500, color }}>
                {n} of {max} regular players
              </span>
            );
          })()}
          {!isMobile && (
            <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 4 }}>
              🔄 Background sync: 30s
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <Button 
            variant="outline"
            onClick={() => {
              setShowHcpUpload(true);
              setHcpUploadFile(null);
              setHcpParsedData(null);
              setHcpMatches(null);
              setHcpUpdateResult(null);
            }}
            style={{ fontSize: 13, background: '#ecfdf5', borderColor: '#86efac', color: '#166534' }}
            title="Upload a handicap report to update player indexes"
          >
            📄 Upload HCPs
          </Button>
          <Button 
            variant="outline"
            onClick={() => {
              refresh();
              setLastRefresh(new Date());
              addToast('Players list refreshed');
            }}
            style={{ fontSize: 13 }}
            title="Manually refresh player list"
          >
            🔄 Refresh
          </Button>
          <Button 
            variant="outline"
            onClick={() => {
              const csv = exportUtils.playersToCSV(players);
              exportUtils.downloadCSV(csv, `players-${new Date().toISOString().split('T')[0]}.csv`);
              addToast('Players exported to CSV');
            }}
            style={{ fontSize: 13 }}
          >
            📥 Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={isReadOnly ? handleReadOnlyClick : () => setShowImport(true)}
            disabled={isReadOnly}
            style={{ fontSize: 13, background: '#ecfdf5', borderColor: '#86efac', color: '#166534' }}
            title="Import a list of golfers from a CSV or Excel file, or paste it"
            data-testid="open-player-import"
          >
            📥 Import players
          </Button>
          <Button 
            onClick={isReadOnly ? handleReadOnlyClick : startNew}
            disabled={isReadOnly}
            style={{ 
              background: isReadOnly ? "#9ca3af" : "#16a34a", 
              cursor: isReadOnly ? "not-allowed" : "pointer" 
            }}
          >
            + Add Player
          </Button>
        </div>
      </div>
      
      {/* Search and Sort Controls */}
      <Card style={{ background: "#f9fafb" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <div style={{ flex: 1, minWidth: isMobile ? '100%' : 0 }}>
            <Input 
              type="text"
              placeholder="🔍 Search by name, email, phone, GHIN, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ fontSize: 14 }}
            />
          </div>
          <Button 
            variant="outline"
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            style={{ fontSize: 13, padding: "8px 16px", whiteSpace: "nowrap" }}
            title={sortOrder === "asc" ? "Sort Z → A" : "Sort A → Z"}
          >
            {sortOrder === "asc" ? "🔤 A → Z" : "🔤 Z → A"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setGroupByRole(!groupByRole)}
            style={{
              fontSize: 13,
              padding: "8px 16px",
              whiteSpace: "nowrap",
              ...(groupByRole ? { background: "#dbeafe", borderColor: "#1e40af", color: "#1e40af", fontWeight: 700 } : {})
            }}
            title={groupByRole ? "Ungroup roles" : "Group by role"}
          >
            👥 By Role
          </Button>
          <label style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 6, 
            cursor: "pointer", 
            fontSize: 13,
            padding: "8px 12px",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: showGuests ? "#f0fdf4" : "#fff",
            whiteSpace: "nowrap"
          }}>
            <input 
              type="checkbox" 
              checked={showGuests} 
              onChange={(e) => setShowGuests(e.target.checked)}
              style={{ width: 14, height: 14 }}
            />
            <span>Show Guests</span>
            {!showGuests && players.filter(hiddenAsGuest).length > 0 && (
              <span style={{ color: "#6b7280", fontSize: 11 }}>
                ({players.filter(hiddenAsGuest).length} hidden)
              </span>
            )}
          </label>
          {searchTerm && (
            <Button 
              variant="outline"
              onClick={() => setSearchTerm("")}
              style={{ fontSize: 13, padding: "8px 12px" }}
            >
              ✕ Clear
            </Button>
          )}
        </div>
        {(searchTerm || !showGuests) && (
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
            Showing {filteredAndSortedPlayers.length} of {players.length} players
            {!showGuests && players.filter(hiddenAsGuest).length > 0 && (
              <span> • {players.filter(hiddenAsGuest).length} guest{players.filter(hiddenAsGuest).length !== 1 ? 's' : ''} hidden</span>
            )}
          </div>
        )}
      </Card>

      {/* Organizer Role Info */}
      {players.filter(p => p.role === 'Organizer').length > 0 && (
        <Card style={{ background: "linear-gradient(135deg, #fef3c7, #fde68a)", border: "1px solid #fbbf24" }}>
          <div style={{ display: "flex", alignItems: "start", gap: 12 }}>
            <div style={{ fontSize: 24 }}>👑</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#78350f", marginBottom: 4 }}>
                Organizers
              </div>
              <div style={{ fontSize: 13, color: "#92400e", lineHeight: 1.5 }}>
                {players.filter(p => p.role === 'Organizer').map(p => p.name).join(", ")} can manage all players, 
                courses, and tee times. Organizers can also assign the Organizer role to other players.
              </div>
            </div>
          </div>
        </Card>
      )}
      
      <div style={{ display: "grid", gap: 8 }}>
        {filteredAndSortedPlayers.length > 0 ? (
          filteredAndSortedPlayers.map((p) => {
            const tokenStatus = getTokenStatus(p);
            return (
          <Card key={p.id}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Main Player Info Row */}
              {/* Phase 5 (May 2026): On mobile, restructure into a stacked card so avatar+name
                  get the full width, GHIN/HCP info wraps to its own line, and action buttons
                  get a full-width row at the bottom. Desktop unchanged. */}
              <div style={{
                display: "flex",
                alignItems: isMobile ? "flex-start" : "center",
                gap: 12,
                flexWrap: isMobile ? "wrap" : "nowrap"
              }}>
                <div 
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewingPhoto({ name: p.name, photoUrl: p.photoUrl });
                  }}
                  title="Click to view larger photo"
                >
                  <PlayerAvatar 
                    name={p.name} 
                    photoUrl={p.photoUrl}
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewingPhoto({ name: p.name, photoUrl: p.photoUrl });
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span 
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setViewingProfile(p)}
                    >
                      {p.name}
                    </span>
                    {p.role === 'Organizer' && <Badge style={{ background: "#fbbf24", color: "#78350f", fontWeight: 700 }}>👑 Organizer</Badge>}
                    {p.role === 'Captain' && <Badge style={{ background: "#ede9fe", color: "#6b21a8", fontWeight: 600 }}>👨‍✈️ Captain</Badge>}
                    {(p.role === 'Player' || !p.role) && <Badge style={{ background: "#dbeafe", color: "#1e40af" }}>🏌️ Player</Badge>}
                    {p.role === 'guest' && <Badge style={{ background: "#f3f4f6", color: "#4b5563" }}>👤 Guest</Badge>}
                    {p.frequent_guest && p.frequent_guest.counts && <Badge style={{ background: "#fef3c7", color: "#92400e", fontWeight: 600 }} data-testid={`fg-counts-${p.id}`}>Counts as a regular</Badge>}
                    {(p.clubMember || p.club_member) && <Badge style={{ background: "#dcfce7", color: "#15803d" }}>⭐ Member</Badge>}
                    {!p.active && <Badge style={{ background: "#fee2e2", color: "#991b1b" }}>Inactive</Badge>}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", wordBreak: "break-word" }}>
                    {p.email} • {p.phone}
                    {(p.comm_email || p.comm_text) && (
                      <span style={{ marginLeft: 8, fontSize: 11 }}>
                        {p.comm_email && '📧'}
                        {p.comm_text && '📱'}
                      </span>
                    )}
                  </div>
                  {p.frequent_guest && (p.frequent_guest.counts || p.frequent_guest.nearing) && (
                    <div data-testid={`fg-note-${p.id}`} style={{ marginTop: 4, fontSize: 12, color: p.frequent_guest.counts ? '#92400e' : '#6b7280' }}>
                      {p.frequent_guest.counts
                        ? `Plays as often as a regular (${p.frequent_guest.roundsSince} rounds since ${new Date(p.frequent_guest.since + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}), so they count toward your plan like one. Their role stays Guest.`
                        : `Has played ${p.frequent_guest.recentRounds} times recently. If they keep playing this often, they'll count toward your plan as a regular.`}
                    </div>
                  )}
                  {p.email_issue && (
                    <div data-testid={`email-issue-${p.id}`} style={{ marginTop: 6, padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#991b1b' }}>
                      <div style={{ fontWeight: 700 }}>
                        ⚠️ {p.email_issue.label || 'Email not delivering'}
                        {p.email_issue.since && <span style={{ fontWeight: 400, color: '#b91c1c' }}> · since {new Date(p.email_issue.since).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                      </div>
                      <div style={{ color: '#7f1d1d', marginTop: 2 }}>
                        TTO isn't emailing this address{p.email_issue.reason ? `: ${p.email_issue.reason}` : ''}. If it's wrong, fix it with Edit.
                      </div>
                      {p.email_issue.canResume ? (
                        spamConfirmId === p.id ? (
                          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#7f1d1d' }}>
                              <input type="checkbox" checked={spamConfirmed} onChange={(e) => setSpamConfirmed(e.target.checked)} data-testid={`spam-confirm-${p.id}`} />
                              I've checked with {p.name.split(' ')[0]} and they want TTO emails
                            </label>
                            <Button onClick={() => resumeEmail(p, true)} disabled={!spamConfirmed || resumingEmailId === p.id || isReadOnly}
                              style={{ fontSize: 12, padding: '4px 10px', background: spamConfirmed ? '#2d6a4f' : '#9ca3af', color: '#fff' }}>
                              {resumingEmailId === p.id ? 'Resuming…' : 'Resume email'}
                            </Button>
                            <Button variant="outline" onClick={() => setSpamConfirmId(null)} style={{ fontSize: 12, padding: '4px 10px' }}>Cancel</Button>
                          </div>
                        ) : (
                          <Button variant="outline" onClick={isReadOnly ? handleReadOnlyClick : () => resumeEmail(p)} disabled={resumingEmailId === p.id}
                            data-testid={`resume-email-${p.id}`}
                            style={{ marginTop: 6, fontSize: 12, padding: '4px 10px', background: '#fff', borderColor: '#fca5a5', color: '#991b1b' }}>
                            {resumingEmailId === p.id ? 'Resuming…' : 'Address is correct — resume email'}
                          </Button>
                        )
                      ) : (
                        <div style={{ marginTop: 4, fontWeight: 600 }}>Already resumed once and still failing — please check the address with the player.</div>
                      )}
                    </div>
                  )}
                  {/* On mobile, show GHIN/HCP under the name+contact block */}
                  {isMobile && (
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                      {p.has_ghin_credentials ? "Auto GHIN" : "GHIN"}: {p.ghin || "—"} • HCP: {p.hcp || p.handicap || "—"}
                    </div>
                  )}
                </div>
                {!isMobile && (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{p.has_ghin_credentials ? "Auto GHIN" : "GHIN"}: {p.ghin || "—"} • HCP: {p.hcp || p.handicap || "—"}</div>
                )}
                <div style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  width: isMobile ? "100%" : "auto",
                  marginTop: isMobile ? 4 : 0
                }}>
                  <Button 
                    variant="outline" 
                    onClick={() => setViewingProfile(p)} 
                    style={{
                      fontSize: 12,
                      background: "#2d6a4f",
                      color: "#fff",
                      flex: isMobile ? "1 1 auto" : "0 0 auto",
                      minHeight: isMobile ? 36 : undefined
                    }}
                  >
                    👤 Profile
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={isReadOnly ? handleReadOnlyClick : () => startEdit(p)} 
                    disabled={isReadOnly}
                    style={{
                      fontSize: 12,
                      opacity: isReadOnly ? 0.5 : 1,
                      cursor: isReadOnly ? "not-allowed" : "pointer",
                      flex: isMobile ? "1 1 auto" : "0 0 auto",
                      minHeight: isMobile ? 36 : undefined
                    }}
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="danger" 
                    onClick={isReadOnly ? handleReadOnlyClick : () => setConfirmDeleteId(p.id)} 
                    disabled={isReadOnly}
                    style={{
                      fontSize: 12,
                      opacity: isReadOnly ? 0.5 : 1,
                      cursor: isReadOnly ? "not-allowed" : "pointer",
                      flex: isMobile ? "1 1 auto" : "0 0 auto",
                      minHeight: isMobile ? 36 : undefined
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              
              {/* Player Link Management Row */}
              <div style={{ 
                background: "#f9fafb", 
                border: "1px solid #e5e7eb", 
                borderRadius: 8, 
                padding: "10px 12px",
                display: "flex",
                alignItems: isMobile ? "flex-start" : "center",
                gap: 12,
                flexWrap: isMobile ? "wrap" : "nowrap"
              }}>
                <div style={{ fontSize: 18 }}>🔗</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                    Player Portal Link
                  </div>
                  {p.magic_token ? (
                    <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <a
                        href={getPlayerLink(p)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          fontSize: 12,
                          color: "#2563eb",
                          textDecoration: "none",
                          fontWeight: 500,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 8px",
                          borderRadius: 4,
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          transition: "all 0.2s"
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = "#dbeafe";
                          e.currentTarget.style.borderColor = "#93c5fd";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = "#eff6ff";
                          e.currentTarget.style.borderColor = "#bfdbfe";
                        }}
                      >
                        🔗 Access Player Portal
                      </a>
                      <Button 
                        variant="outline" 
                        onClick={() => copyPlayerLink(p)}
                        style={{ fontSize: 12, padding: "4px 8px", whiteSpace: "nowrap", fontWeight: 500 }}
                        title="Copy full player portal link"
                      >
                        📋 Copy Link
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => copyShortUrl(p)}
                        style={{ 
                          fontSize: 12, 
                          padding: "4px 8px", 
                          whiteSpace: "nowrap", 
                          fontWeight: 500,
                          background: "#f0fdf4",
                          borderColor: "#86efac",
                          color: "#166534"
                        }}
                        title="Copy short URL (e.g., /p/xk7m2q)"
                      >
                        ⚡ Copy Short URL
                      </Button>
                    </div>

                    {/* Revoke & regenerate — Aug 16, 2026.
                        This used to sit as a fourth red button in the row above, beside the two
                        copy actions an organizer uses constantly. It is the opposite kind of
                        action: destructive, rarely needed, and it locks the player out the moment
                        it lands. Moved to its own secondary line and labelled for the SITUATION
                        ("link shared, email changed") rather than the mechanism, so the reason to
                        click it is visible before the click instead of only in the confirm dialog. */}
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed #e5e7eb", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: "#6b7280" }}>
                        Link shared with someone else, or email changed?
                      </span>
                      <button
                        onClick={() => regenerateToken(p)}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#b91c1c",
                          background: "none",
                          border: "none",
                          padding: 0,
                          textDecoration: "underline",
                          cursor: "pointer"
                        }}
                        title="Permanently invalidate this player's current link and generate a new one"
                      >
                        Revoke &amp; regenerate link
                      </button>
                    </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: "#dc2626" }}>
                      No magic link available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
          );
        })
        ) : (
          <Card style={{ padding: 48, textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
            <div style={{ color: "#6b7280", fontSize: 16, fontWeight: 600 }}>No players found</div>
            <div style={{ color: "#9ca3af", fontSize: 14, marginTop: 6 }}>
              {searchTerm ? `No players match "${searchTerm}"` : "No players in the system"}
            </div>
            {searchTerm && (
              <Button 
                variant="outline" 
                onClick={() => setSearchTerm("")}
                style={{ marginTop: 12 }}
              >
                Clear Search
              </Button>
            )}
          </Card>
        )}
      </div>
      {confirmDeleteId && <ConfirmDialog title="Delete player?" message="This permanently removes the player and all their history — past scores, settlement records, and reservations — and can't be undone. To remove them from play but keep their records, set their Status to Inactive instead." onConfirm={() => remove(confirmDeleteId)} onCancel={() => setConfirmDeleteId(null)} />}
      {viewingProfile && <PlayerProfile player={viewingProfile} onClose={() => setViewingProfile(null)} />}

      {/* Bulk Invitation Modal */}
      {showInvitationModal && newlyCreatedPlayer && (
        <BulkInvitationModal
          isOpen={showInvitationModal}
          newPlayer={newlyCreatedPlayer}
          groupId="default"
          onClose={() => {
            setShowInvitationModal(false);
            setNewlyCreatedPlayer(null);
            cancel(); // Close the add player form
          }}
        />
      )}
      
      {/* Photo Viewer Modal */}
      <PhotoViewerModal
        isOpen={!!viewingPhoto}
        playerName={viewingPhoto?.name || ""}
        photoUrl={viewingPhoto?.photoUrl || ""}
        onClose={() => setViewingPhoto(null)}
      />

      {/* HCP Upload Modal */}
      {showHcpUpload && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowHcpUpload(false); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '95%', maxWidth: 700, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📄 Upload Handicap Report</h3>
              <button onClick={() => setShowHcpUpload(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            
            {/* Instructions */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#166534', lineHeight: 1.5 }}>
              <strong>How it works:</strong> Upload a handicap report (PDF, Excel, or CSV) that contains <strong>GHIN numbers</strong> and <strong>Handicap Index (H.I.)</strong> values. 
              We'll automatically match players in your roster by GHIN number and update their handicap index.
              <div style={{ marginTop: 8, fontSize: 12, color: '#15803d' }}>
                ✅ Accepted formats: PDF, Excel (.xlsx/.xls), CSV, TXT &nbsp;|&nbsp; 
                ℹ️ Players with GHIN auto-sync enabled will also be updated from the uploaded file
              </div>
            </div>

            {/* File Upload */}
            {!hcpParsedData && (
              <div style={{ border: '2px dashed #d1d5db', borderRadius: 8, padding: 32, textAlign: 'center', cursor: 'pointer', background: hcpUploadFile ? '#f0fdf4' : '#f9fafb', transition: 'all 0.2s' }}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#2d6a4f'; e.currentTarget.style.background = '#ecfdf5'; }}
                onDragLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#f9fafb'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#d1d5db';
                  const file = e.dataTransfer.files[0];
                  if (file) { setHcpUploadFile(file); parseHcpFile(file); }
                }}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.pdf,.xlsx,.xls,.csv,.txt,.tsv';
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) { setHcpUploadFile(file); parseHcpFile(file); }
                  };
                  input.click();
                }}
              >
                {hcpParsing ? (
                  <div>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Parsing {hcpUploadFile?.name}...</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Extracting GHIN numbers and handicap indexes</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Drop file here or click to browse</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>PDF, Excel, CSV — must contain GHIN # & H.I.</div>
                  </div>
                )}
              </div>
            )}

            {/* Results Preview */}
            {hcpUpdateResult ? (
              /* Success Confirmation */
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: '#166534', marginBottom: 8 }}>Handicaps Updated!</h3>
                <div style={{ fontSize: 15, color: '#374151', marginBottom: 20 }}>
                  Successfully updated <strong>{hcpUpdateResult.updated}</strong> player handicap{hcpUpdateResult.updated !== 1 ? 's' : ''}.
                  {hcpUpdateResult.skipped > 0 && (
                    <span style={{ color: '#6b7280' }}> ({hcpUpdateResult.skipped} skipped)</span>
                  )}
                </div>
                
                {/* Show updated players summary */}
                {hcpMatches && (
                  <div style={{ maxHeight: 250, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 20, textAlign: 'left' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f0fdf4' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #bbf7d0' }}>Player</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #bbf7d0' }}>Previous</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #bbf7d0' }}>→</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #bbf7d0' }}>Updated To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hcpMatches.filter(m => m.changed).map((m, idx) => (
                          <tr key={idx} style={{ background: '#f0fdf4' }}>
                            <td style={{ padding: '6px 12px', borderBottom: '1px solid #dcfce7', fontWeight: 600 }}>{m.playerName}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid #dcfce7', color: '#6b7280' }}>
                              {m.currentHcp !== null && m.currentHcp !== undefined ? m.currentHcp : '—'}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid #dcfce7', color: '#6b7280' }}>→</td>
                            <td style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid #dcfce7', fontWeight: 700, color: '#166534' }}>{m.newHi}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                <Button 
                  onClick={() => {
                    setShowHcpUpload(false);
                    setHcpUploadFile(null);
                    setHcpParsedData(null);
                    setHcpMatches(null);
                    setHcpUpdateResult(null);
                  }}
                  style={{ background: '#2d6a4f', fontSize: 14, padding: '10px 32px' }}
                >
                  Done
                </Button>
              </div>
            ) : hcpMatches && (
              <div>
                {/* Summary */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                  <div style={{ background: '#e0e7ff', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}>
                    <strong>{hcpParsedData?.length || 0}</strong> entries in file
                  </div>
                  <div style={{ background: '#dcfce7', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}>
                    <strong>{hcpMatches.length}</strong> matched to your players
                  </div>
                  <div style={{ background: '#fef3c7', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}>
                    <strong>{hcpMatches.filter(m => m.changed).length}</strong> to update
                  </div>
                  {hcpMatches.filter(m => m.autoSync).length > 0 && (
                    <div style={{ background: '#f3f4f6', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}>
                      <strong>{hcpMatches.filter(m => m.autoSync).length}</strong> auto-sync (included)
                    </div>
                  )}
                </div>

                {/* Match Table */}
                {hcpMatches.length > 0 ? (
                  <div style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f9fafb' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Player</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>GHIN</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Current</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>New H.I.</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hcpMatches.map((m, idx) => (
                          <tr key={idx} style={{ background: m.changed ? '#f0fdf4' : '#fff' }}>
                            <td style={{ padding: '6px 12px', borderBottom: '1px solid #f3f4f6' }}>
                              <div style={{ fontWeight: 600 }}>{m.playerName}</div>
                              {m.pdfName && m.pdfName.toLowerCase() !== m.playerName.toLowerCase() && (
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>PDF: {m.pdfName}</div>
                              )}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace', fontSize: 12 }}>{m.ghin}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                              {m.currentHcp !== null && m.currentHcp !== undefined ? m.currentHcp : '—'}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', fontWeight: m.changed ? 700 : 400, color: m.changed ? '#166534' : '#6b7280' }}>
                              {m.newHi}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                              {m.autoSync ? (
                                <span style={{ fontSize: 11, color: '#2563eb' }}>🔄 Auto-sync ✅</span>
                              ) : m.changed ? (
                                <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>✅ Will update</span>
                              ) : (
                                <span style={{ fontSize: 11, color: '#6b7280' }}>— No change</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', background: '#f9fafb', borderRadius: 8 }}>
                    No matches found. Ensure your players have GHIN numbers entered in their profiles.
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, gap: 12 }}>
                  <Button variant="outline" onClick={() => {
                    setHcpUploadFile(null);
                    setHcpParsedData(null);
                    setHcpMatches(null);
                  }} style={{ fontSize: 13 }}>
                    ← Upload Different File
                  </Button>
                  <Button 
                    onClick={applyHcpUpdates}
                    disabled={hcpUploading || hcpMatches.filter(m => m.changed).length === 0}
                    style={{ 
                      background: hcpMatches.filter(m => m.changed).length > 0 ? '#2d6a4f' : '#9ca3af',
                      fontSize: 13,
                      cursor: hcpMatches.filter(m => m.changed).length > 0 ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {hcpUploading ? '⏳ Updating...' : `✅ Update ${hcpMatches.filter(m => m.changed).length} Players`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Scorecard photo → upload-ready image (Sept 11, 2026)
// ============================================================================
// The scorecard reader (Claude vision, server side) accepts JPEG/PNG/GIF/WebP up
// to 5 MB and 8000 px. A phone photo today is 12 MP and commonly 4–6 MB, and an
// iPhone may hand the browser a HEIC — either way the API rejects it and the
// organizer sees a failure toast. Draw the photo onto a canvas at most
// SCORECARD_MAX_EDGE px on the long side and re-encode as JPEG: a few hundred KB,
// always a supported type, and the small print on a card is still crisp at
// 2,000 px. EXIF orientation is honoured by the browser's image decoder
// (`image-orientation: from-image` is the default in every current browser).
// If anything in the canvas path fails (very old browser, odd file), fall back
// to sending the original bytes exactly as before.
const SCORECARD_MAX_EDGE = 2000;
const SCORECARD_JPEG_QUALITY = 0.85;
const prepareScorecardImage = (file) => new Promise((resolve, reject) => {
  const readOriginal = () => {
    const reader = new FileReader();
    reader.onload = () => resolve({ base64: reader.result.split(',')[1], media_type: file.type || 'image/jpeg', resized: false });
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  };
  try {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth, h = img.naturalHeight;
        const scale = Math.min(1, SCORECARD_MAX_EDGE / Math.max(w, h));
        const cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement('canvas');
        canvas.width = cw; canvas.height = ch;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, cw, ch);   // PNG transparency → white, not black
        ctx.drawImage(img, 0, 0, cw, ch);
        const dataUrl = canvas.toDataURL('image/jpeg', SCORECARD_JPEG_QUALITY);
        URL.revokeObjectURL(url);
        if (!dataUrl || dataUrl.length < 100) return readOriginal();
        resolve({ base64: dataUrl.split(',')[1], media_type: 'image/jpeg', resized: scale < 1 || file.type !== 'image/jpeg', width: cw, height: ch });
      } catch (e) { URL.revokeObjectURL(url); readOriginal(); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); readOriginal(); };   // e.g. HEIC the browser cannot decode → server will say so
    img.src = url;
  } catch (e) { readOriginal(); }
});

const CourseManager = () => {
  const { addToast } = useToast();
  const { isReadOnly, isExpired } = useSubscription();
  const { refreshCourses } = useCourses();
  const { data: courses, loading, addItem, updateItem, deleteItem, refresh } = useApiData('/courses');
  const [mode, setMode] = useState("list");
  const [working, setWorking] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null); // { name, teeCount, courseId }

  // Tee summaries for course list view (tee name, color, total yardage)
  const [teeSummaries, setTeeSummaries] = useState({});

  useEffect(() => {
    const fetchTeeSummaries = async () => {
      if (!courses || courses.length === 0) return;
      const coursesWithTees = courses.filter(c => c.tee_count > 0);
      if (coursesWithTees.length === 0) return;

      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const summaries = {};
      await Promise.all(coursesWithTees.map(async (course) => {
        try {
          const teesResp = await fetch(`${API_URL}/courses/${course.id}/tees`, { credentials: 'include', headers });
          if (!teesResp.ok) return;
          const teesData = await teesResp.json();
          
          const teeDetails = await Promise.all(teesData.map(async (tee) => {
            let totalYardage = null;
            try {
              const holesResp = await fetch(`${API_URL}/scoring/courses/${course.id}/tees/${tee.id}/holes`, { credentials: 'include', headers });
              if (holesResp.ok) {
                const holes = await holesResp.json();
                if (holes.length > 0) {
                  totalYardage = holes.reduce((sum, h) => sum + (parseInt(h.yardage) || 0), 0);
                }
              } else {
                console.warn(`Holes fetch failed for ${course.name} / ${tee.tee_name}: ${holesResp.status}`);
              }
            } catch (e) { console.warn(`Holes fetch error for ${course.name} / ${tee.tee_name}:`, e.message); }
            return { tee_name: tee.tee_name, tee_color: tee.tee_color, totalYardage, slope_rating: tee.slope_rating };
          }));

          teeDetails.sort((a, b) => (b.totalYardage || b.slope_rating || 0) - (a.totalYardage || a.slope_rating || 0));
          summaries[course.id] = teeDetails;
        } catch (e) { /* ignore */ }
      }));
      setTeeSummaries(summaries);
    };
    if (mode === 'list') fetchTeeSummaries();
  }, [courses, mode]);
  
  // Read-only mode click handler
  const handleReadOnlyClick = () => {
    addToast(isExpired 
      ? "Your free trial has ended. Please subscribe to continue making changes." 
      : "Your account is in read-only mode. Please reactivate your subscription to make changes.", "error");
  };
  
  // Tees management state
  const [tees, setTees] = useState([]);
  const [loadingTees, setLoadingTees] = useState(false);
  const [editingTee, setEditingTee] = useState(null);
  const [showTeeForm, setShowTeeForm] = useState(false);
  const [teeForm, setTeeForm] = useState({
    tee_name: '',
    tee_color: '#000000',
    course_rating: '',
    slope_rating: '',
    par: '72',
    display_order: 0
  });
  
  // Default tee colors for quick selection
  const defaultTeeColors = [
    { name: 'Black', color: '#000000' },
    { name: 'Blue', color: '#1e40af' },
    { name: 'White', color: '#f3f4f6' },
    { name: 'Gold', color: '#ca8a04' },
    { name: 'Red', color: '#dc2626' },
    { name: 'Green', color: '#16a34a' },
  ];

  // Scoring module state
  const [scoringEnabled, setScoringEnabled] = useState(false);
  const [gamesEnabled, setGamesEnabled] = useState(false);
  const [eventsEnabled, setEventsEnabled] = useState(false); // Association events platform gate (per-org)
  const [holeDataTee, setHoleDataTee] = useState(null); // tee object for hole data modal
  const [holeDataCourseId, setHoleDataCourseId] = useState(null);
  const [holeRows, setHoleRows] = useState([]);
  const [loadingHoles, setLoadingHoles] = useState(false);
  const [savingHoles, setSavingHoles] = useState(false);
  const [importingScorecard, setImportingScorecard] = useState(false);
  const [importingFullScorecard, setImportingFullScorecard] = useState(false);
  // Sept 11, 2026: result panel for the full-card import. A 2-second toast was
  // the only feedback and a card without printed ratings produced "Scorecard
  // processed!" with nothing saved. Shape: { kind:'report'|'error', message,
  // saved:[{tee_name,holes_count,holes_note}], pending:[{tee_name,par,holes,...,
  // course_rating:'', slope_rating:''}], skipped:[{tee_name,reason}], saving }.
  const [scorecardImport, setScorecardImport] = useState(null);

  // GolfCourseAPI.com search state
  const [apiSearchQuery, setApiSearchQuery] = useState('');
  const [apiSearchResults, setApiSearchResults] = useState([]);
  const [apiSearching, setApiSearching] = useState(false);
  const [apiImporting, setApiImporting] = useState(null); // api_id of course being imported
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Check if scoring is enabled for this org
  useEffect(() => {
    const checkScoring = async () => {
      try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(`${API_URL}/scoring/status`, { credentials: 'include', headers });
        if (response.ok) {
          const data = await response.json();
          setScoringEnabled(data.scoring_enabled || false);
          setGamesEnabled(data.games_enabled || false);
          setEventsEnabled(data.events_enabled || false);
        }
      } catch (error) {
        console.log('Scoring status check failed (module may not be enabled)');
      }
    };
    checkScoring();
  }, []);

  // Initialize empty hole template based on course holes (9 or 18)
  const createEmptyHoles = (count) => {
    const holeCount = count || 18;
    return Array.from({ length: holeCount }, (_, i) => ({
      hole_number: i + 1,
      par: 4,
      yardage: '',
      handicap_index: ''
    }));
  };

  // Open hole data modal for a tee
  const openHoleData = async (courseId, tee) => {
    setShowTeeForm(false); // Close tee edit form if open
    setEditingTee(null);
    setHoleDataCourseId(courseId);
    setHoleDataTee(tee);
    setLoadingHoles(true);
    const course = courses.find(c => c.id === courseId);
    const holeCount = course?.holes || 18;
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API_URL}/scoring/courses/${courseId}/tees/${tee.id}/holes`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setHoleRows(data);
        } else {
          setHoleRows(createEmptyHoles(holeCount));
        }
      } else {
        setHoleRows(createEmptyHoles(holeCount));
      }
    } catch (error) {
      console.error('Error fetching hole data:', error);
      setHoleRows(createEmptyHoles(holeCount));
    } finally {
      setLoadingHoles(false);
    }
  };

  // Save hole data
  const saveHoleData = async () => {
    // Validate before saving
    const errors = [];
    const handicaps = new Set();
    for (const hole of holeRows) {
      if (!hole.yardage || hole.yardage <= 0) errors.push(`Hole ${hole.hole_number}: yardage required`);
      if (!hole.handicap_index) errors.push(`Hole ${hole.hole_number}: handicap index required`);
      if (hole.handicap_index && handicaps.has(hole.handicap_index)) {
        errors.push(`Duplicate handicap index: ${hole.handicap_index}`);
      }
      if (hole.handicap_index) handicaps.add(hole.handicap_index);
    }
    if (errors.length > 0) {
      addToast(errors[0], 'error');
      return;
    }

    setSavingHoles(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(
        `${API_URL}/scoring/courses/${holeDataCourseId}/tees/${holeDataTee.id}/holes`,
        {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({ 
            holes: holeRows.map(h => ({
              hole_number: h.hole_number,
              par: parseInt(h.par),
              yardage: parseInt(h.yardage),
              handicap_index: parseInt(h.handicap_index)
            }))
          })
        }
      );
      if (response.ok) {
        addToast('Hole data saved successfully!');
        setHoleDataTee(null);
        setHoleDataCourseId(null);
      } else {
        const err = await response.json();
        addToast(err.details?.[0] || err.error || 'Failed to save hole data', 'error');
      }
    } catch (error) {
      console.error('Error saving hole data:', error);
      addToast('Failed to save hole data', 'error');
    } finally {
      setSavingHoles(false);
    }
  };

  // Update a single hole row
  const updateHoleRow = (holeNumber, field, value) => {
    setHoleRows(prev => prev.map(h => 
      h.hole_number === holeNumber ? { ...h, [field]: value } : h
    ));
  };

  // Import hole data from scorecard image
  const importFromScorecard = async (file) => {
    if (!file || !holeDataTee) return;
    
    setImportingScorecard(true);
    try {
      // Downscale + re-encode as JPEG before upload (see prepareScorecardImage).
      const { base64, media_type } = await prepareScorecardImage(file);

      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/scoring/import-scorecard`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          image: base64,
          tee_name: holeDataTee.tee_name,
          media_type
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.holes && data.holes.length > 0) {
          setHoleRows(data.holes);
          addToast(`Extracted ${data.holes.length} holes for ${holeDataTee.tee_name} tees — review and save!`);
        } else {
          addToast('No hole data found in image. Try a clearer photo.', 'error');
        }
      } else {
        // .catch: a proxy/body-limit error page is not JSON; keep a readable toast.
        const err = await response.json().catch(() => ({}));
        addToast(err.error || `Failed to read scorecard (${response.status})`, 'error');
      }
    } catch (error) {
      console.error('Scorecard import error:', error);
      addToast('Failed to process scorecard image', 'error');
    } finally {
      setImportingScorecard(false);
    }
  };

  // Import ALL tees + hole data from scorecard image.
  // The server saves every tee the card fully describes and hands back the ones
  // it can't (no printed rating/slope) as `needs_ratings`, holes intact; those
  // go into the panel below the Course Tees header for the organizer to finish.
  const scorecardToast = (summary = {}, pendingCount = 0) => {
    const parts = [];
    if (summary.created > 0) parts.push(`${summary.created} tee${summary.created > 1 ? 's' : ''} created`);
    if (summary.updated > 0) parts.push(`${summary.updated} tee${summary.updated > 1 ? 's' : ''} updated`);
    if (summary.holesLoaded > 0) parts.push(`${summary.holesLoaded} with hole data`);
    if (pendingCount > 0) parts.push(`${pendingCount} need${pendingCount === 1 ? 's' : ''} ratings — see below`);
    return parts.length > 0 ? parts.join(', ') : 'Scorecard read — see the result below';
  };

  const importFullScorecard = async (file) => {
    if (!file || !working?.id) return;
    
    setImportingFullScorecard(true);
    setScorecardImport(null);
    try {
      // Downscale + re-encode as JPEG before upload (see prepareScorecardImage).
      const { base64, media_type } = await prepareScorecardImage(file);

      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Step 1: Extract all tees from scorecard
      addToast('Reading scorecard...', 'info');
      const response = await fetch(`${API_URL}/scoring/import-scorecard-full`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          image: base64,
          course_id: working.id,
          media_type
        })
      });

      // .catch: a proxy/body-limit error page is not JSON; keep a readable message.
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data.error || `Failed to read scorecard (${response.status})`;
        setScorecardImport({ kind: 'error', message, saved: [], pending: [], skipped: data.skipped || [], saving: false });
        addToast(message, 'error');
        return;
      }

      // Backend saved what it could — refresh the list, then show the full result.
      await fetchTees(working.id);
      const pending = (data.needs_ratings || []).map(t => ({ ...t, course_rating: '', slope_rating: '' }));
      setScorecardImport({
        kind: 'report',
        message: null,
        saved: data.saved || [],
        pending,
        skipped: data.skipped || [],
        saving: false
      });
      addToast(scorecardToast(data.summary, pending.length), pending.length > 0 && !(data.summary?.created || data.summary?.updated) ? 'info' : 'success');
    } catch (error) {
      console.error('Full scorecard import error:', error);
      setScorecardImport({ kind: 'error', message: 'Failed to process scorecard image — check your connection and try again.', saved: [], pending: [], skipped: [], saving: false });
      addToast('Failed to process scorecard image', 'error');
    } finally {
      setImportingFullScorecard(false);
    }
  };

  const updatePendingTee = (teeName, field, value) => {
    setScorecardImport(s => s ? { ...s, pending: s.pending.map(t => t.tee_name === teeName ? { ...t, [field]: value } : t) } : s);
  };

  const removePendingTee = (teeName) => {
    setScorecardImport(s => s ? { ...s, pending: s.pending.filter(t => t.tee_name !== teeName) } : s);
  };

  const pendingTeeIsFilled = (t) => String(t.course_rating).trim() !== '' && String(t.slope_rating).trim() !== '';

  // Second half of the import: send the held tees back with the ratings typed in.
  // Server re-validates everything; a bad number comes back as 400 with nothing
  // saved, and the form stays so it can be corrected.
  const completeScorecardImport = async () => {
    if (!scorecardImport || !working?.id) return;
    const filled = scorecardImport.pending.filter(pendingTeeIsFilled);
    if (filled.length === 0) {
      addToast('Enter a course rating and slope for at least one tee', 'error');
      return;
    }

    setScorecardImport(s => ({ ...s, saving: true, message: null }));
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/scoring/import-scorecard-full/complete`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          course_id: working.id,
          tees: filled.map(t => ({
            tee_name: t.tee_name,
            gender: t.gender || null,
            par: t.par,
            course_rating: t.course_rating,
            slope_rating: t.slope_rating,
            holes: t.holes || []
          }))
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data.error || `Could not save tees (${response.status})`;
        setScorecardImport(s => ({ ...s, saving: false, message }));
        addToast(message, 'error');
        return;
      }

      await fetchTees(working.id);
      const savedNames = new Set((data.saved || []).map(t => String(t.tee_name).toLowerCase()));
      setScorecardImport(s => ({
        ...s,
        saving: false,
        message: null,
        saved: [...s.saved, ...(data.saved || [])],
        pending: s.pending.filter(t => !savedNames.has(String(t.tee_name).toLowerCase())),
        skipped: [...s.skipped, ...(data.skipped || [])]
      }));
      addToast(scorecardToast(data.summary));
    } catch (error) {
      console.error('Scorecard import completion error:', error);
      setScorecardImport(s => ({ ...s, saving: false, message: 'Could not save tees — check your connection and try again.' }));
      addToast('Could not save tees', 'error');
    }
  };

  // GolfCourseAPI.com search (debounced from input)
  const searchCoursesApi = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 3) {
      setApiSearchResults([]);
      return;
    }
    setApiSearching(true);
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(
        `${API_URL}/scoring/courses/search-api?q=${encodeURIComponent(searchTerm.trim())}`,
        { credentials: 'include', headers }
      );
      
      if (response.ok) {
        const data = await response.json();
        setApiSearchResults(data.courses || []);
      } else {
        setApiSearchResults([]);
      }
    } catch (error) {
      console.error('Course API search error:', error);
      setApiSearchResults([]);
    } finally {
      setApiSearching(false);
    }
  };

  // Import course from API search results
  const importCourseFromApi = async (apiCourse) => {
    setApiImporting(apiCourse.api_id);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/scoring/courses/import-from-api`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          name: apiCourse.name,
          location: apiCourse.location,
          city: apiCourse.city,
          state: apiCourse.state,
          address: apiCourse.address,
          tees: apiCourse.tees
        })
      });

      if (!response.ok) {
        const err = await response.json();
        addToast(err.error || 'Failed to import course', 'error');
        return;
      }

      const data = await response.json();
      
      const teeCount = data.summary?.teesCreated || 0;
      const courseId = data.course?.id;
      const courseName = data.course?.name || apiCourse.name;

      // Clear search state
      setApiSearchQuery('');
      setApiSearchResults([]);
      setShowManualEntry(false);
      
      // Show success confirmation modal FIRST (before any refresh that could reset state)
      setMode('list');
      setImportSuccess({ name: courseName, teeCount, courseId });

      // Refresh courses lists
      refresh(true);
      refreshCourses();

    } catch (error) {
      console.error('Course import error:', error);
      addToast('Failed to import course: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setApiImporting(null);
    }
  };

  // Debounce timer ref for API search
  const apiSearchTimerRef = React.useRef(null);
  const handleApiSearchChange = (value) => {
    setApiSearchQuery(value);
    if (apiSearchTimerRef.current) clearTimeout(apiSearchTimerRef.current);
    if (value.trim().length >= 3) {
      apiSearchTimerRef.current = setTimeout(() => searchCoursesApi(value), 500);
    } else {
      setApiSearchResults([]);
    }
  };
  
  // Fetch tees when editing a course
  const fetchTees = async (courseId) => {
    if (!courseId) return;
    setLoadingTees(true);
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/courses/${courseId}/tees`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setTees(data);
      } else {
        setTees([]);
      }
    } catch (error) {
      console.error('Error fetching tees:', error);
      setTees([]);
    } finally {
      setLoadingTees(false);
    }
  };
  
  // Save a tee (create or update)
  const saveTee = async () => {
    if (!teeForm.tee_name || !teeForm.course_rating || !teeForm.slope_rating || !teeForm.par) {
      addToast('Please fill in all required fields', 'error');
      return;
    }
    
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const url = editingTee 
        ? `${API_URL}/courses/${working.id}/tees/${editingTee.id}`
        : `${API_URL}/courses/${working.id}/tees`;
      
      const response = await fetch(url, {
        method: editingTee ? 'PUT' : 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          tee_name: teeForm.tee_name,
          tee_color: teeForm.tee_color,
          course_rating: parseFloat(teeForm.course_rating),
          slope_rating: parseInt(teeForm.slope_rating),
          par: parseInt(teeForm.par),
          display_order: teeForm.display_order || 0
        })
      });
      
      if (response.ok) {
        addToast(editingTee ? 'Tee updated successfully' : 'Tee added successfully');
        fetchTees(working.id);
        resetTeeForm();
      } else {
        const error = await response.json();
        addToast(error.error || 'Failed to save tee', 'error');
      }
    } catch (error) {
      console.error('Error saving tee:', error);
      addToast('Failed to save tee', 'error');
    }
  };
  
  // Delete a tee
  const deleteTee = async (teeId) => {
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/courses/${working.id}/tees/${teeId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        addToast('Tee deleted successfully');
        fetchTees(working.id);
      } else {
        addToast('Failed to delete tee', 'error');
      }
    } catch (error) {
      console.error('Error deleting tee:', error);
      addToast('Failed to delete tee', 'error');
    }
  };
  
  // Reset tee form
  const resetTeeForm = () => {
    setTeeForm({
      tee_name: '',
      tee_color: '#000000',
      course_rating: '',
      slope_rating: '',
      par: '72',
      display_order: tees.length
    });
    setEditingTee(null);
    setShowTeeForm(false);
  };
  
  // Start editing a tee
  const startEditTee = (tee) => {
    setTeeForm({
      tee_name: tee.tee_name,
      tee_color: tee.tee_color || '#000000',
      course_rating: tee.course_rating?.toString() || '',
      slope_rating: tee.slope_rating?.toString() || '',
      par: tee.par?.toString() || '72',
      display_order: tee.display_order || 0
    });
    setEditingTee(tee);
    setShowTeeForm(true);
  };

  const startNew = () => {
    setWorking({ id: utils.generateId(), name: "", location: "", proshop_phone: "", proshop_email: "", holes: 18, tee_time_interval: "" });
    setTees([]);
    setScorecardImport(null);
    setApiSearchQuery('');
    setApiSearchResults([]);
    setApiImporting(null);
    setShowManualEntry(false);
    setMode("new");
  };

  const startEdit = (c) => {
    setWorking({ ...c });
    fetchTees(c.id);
    setScorecardImport(null);
    setMode("edit");
  };

  const cancel = () => {
    setMode("list");
    setWorking(null);
    setTees([]);
    setScorecardImport(null);
    resetTeeForm();
    setApiSearchQuery('');
    setApiSearchResults([]);
    setApiImporting(null);
    setShowManualEntry(false);
  };

  const save = async (draft) => {
    const cleaned = { 
      id: draft.id, 
      name: (draft.name || "").trim(), 
      location: utils.convertStateAbbreviation((draft.location || "").trim()),
      proshop_phone: (draft.proshop_phone || "").trim(),
      proshop_email: (draft.proshop_email || "").trim(),
      holes: parseInt(draft.holes) || 18,
      tee_time_interval: draft.tee_time_interval ? parseInt(draft.tee_time_interval) : null,
      ghin_course_id: draft.ghin_course_id || null,
      // Named nines (Oct 8, 2026): both or neither; '' = not a two-nines course.
      front_nine_name: (parseInt(draft.holes) || 18) === 18 ? (draft.front_nine_name || "").trim() : "",
      back_nine_name: (parseInt(draft.holes) || 18) === 18 ? (draft.back_nine_name || "").trim() : ""
    };
    if (!!cleaned.front_nine_name !== !!cleaned.back_nine_name) {
      addToast("Enter both nine names, or leave both blank", "error");
      return;
    }
    
    // Validate required fields
    if (!cleaned.name) {
      addToast("Course name is required", "error");
      return;
    }
    if (!cleaned.location) {
      addToast("Location is required for weather forecasts", "error");
      return;
    }
    
    try {
      if (mode === "new") {
        const newCourse = await addItem(cleaned);
        addToast("Course added successfully");
        // Switch to edit mode to add tees
        setWorking(newCourse);
        setMode("edit");
        addToast("Now add tee information for this course", "info");
        refreshCourses();
      } else if (mode === "edit") {
        await updateItem(cleaned.id, cleaned);
        addToast("Course updated successfully");
        cancel();
        refreshCourses();
      }
    } catch (err) {
      console.error('Error saving course:', err);
      addToast(`Error saving course: ${err.message}`, "error");
    }
  };

  const remove = async (id) => {
    try {
      // VALIDATION: Check if course has associated tee times
      // Include auth headers for impersonation support
      const headers = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const teeTimesResponse = await fetch(`${API_URL}/teetimes`, {
        credentials: 'include',
        headers
      });
      if (!teeTimesResponse.ok) throw new Error('Failed to fetch tee times');
      const teeTimes = await teeTimesResponse.json();
      
      const course = courses.find(c => c.id === id);
      
      // Find tee times using this course
      const teeTimesUsingCourse = teeTimes.filter(tt => tt.course === course?.name);
      
      if (teeTimesUsingCourse.length > 0) {
        addToast(
          `Cannot delete this course. It has ${teeTimesUsingCourse.length} associated tee time${teeTimesUsingCourse.length > 1 ? 's' : ''}. Please delete or reassign those tee times first.`,
          "error"
        );
        setConfirmDeleteId(null);
        return;
      }
      
      // No associated tee times, safe to delete
      await deleteItem(id);
      addToast("Course removed successfully");
      setConfirmDeleteId(null);
      refreshCourses();
    } catch (err) {
      console.error('Error deleting course:', err);
      addToast(err.message || "Error deleting course", "error");
      setConfirmDeleteId(null);
    }
  };
  
  // Calculate course handicap helper
  const calculateCourseHandicap = (handicapIndex, slope, rating, par) => {
    if (!handicapIndex || !slope || !rating || !par) return null;
    const courseHcp = (handicapIndex * slope / 113) + (rating - par);
    return Math.round(courseHcp);
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>Loading courses...</div>;
  }

  // NEW MODE: Search-first screen with API search
  if (mode === "new" && !showManualEntry) {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>Add Course</div>
        <Card>
          <div style={{ display: "grid", gap: 16 }}>
            <div>
              <Label>Search Course Database</Label>
              <Input
                value={apiSearchQuery}
                onChange={(e) => handleApiSearchChange(e.target.value)}
                placeholder="Search 25,000+ courses..."
                autoFocus
                style={{ fontSize: 15 }}
              />
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                Type at least 3 characters to search. Results include tee and hole data.
              </div>
            </div>

            {/* Searching indicator */}
            {apiSearching && (
              <div style={{ textAlign: "center", padding: 16, color: "#6b7280" }}>
                Searching courses...
              </div>
            )}

            {/* Search results */}
            {!apiSearching && apiSearchResults.length > 0 && (
              <div style={{ display: "grid", gap: 6, maxHeight: 400, overflowY: "auto" }}>
                {apiSearchResults.map((course) => (
                  <div
                    key={course.api_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 14px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      background: apiImporting === course.api_id ? "#f0f9ff" : "#fff",
                      cursor: apiImporting ? "default" : "pointer",
                      transition: "background 0.15s"
                    }}
                    onClick={() => !apiImporting && importCourseFromApi(course)}
                    onMouseEnter={(e) => { if (!apiImporting) e.currentTarget.style.background = "#f0f9ff"; }}
                    onMouseLeave={(e) => { if (apiImporting !== course.api_id) e.currentTarget.style.background = "#fff"; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{course.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{course.location}</div>
                      {course.tee_count > 0 && (
                        <div style={{ fontSize: 11, color: "#16a34a", marginTop: 2 }}>
                          ⛳ {course.tee_count} tee{course.tee_count !== 1 ? 's' : ''} with hole data
                        </div>
                      )}
                      {course.tee_count === 0 && (
                        <div style={{ fontSize: 11, color: "#92400e", marginTop: 2 }}>
                          No tee data available — import course name only
                        </div>
                      )}
                    </div>
                    <div style={{ marginLeft: 12, flexShrink: 0 }}>
                      {apiImporting === course.api_id ? (
                        <span style={{ fontSize: 12, color: "#1e40af", fontWeight: 600 }}>Importing...</span>
                      ) : (
                        <span style={{ 
                          fontSize: 12, fontWeight: 600, color: "#16a34a",
                          padding: "4px 10px", border: "1px solid #bbf7d0", borderRadius: 6, background: "#f0fdf4"
                        }}>
                          + Import
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No results message */}
            {!apiSearching && apiSearchQuery.trim().length >= 3 && apiSearchResults.length === 0 && (
              <div style={{ textAlign: "center", padding: 16, color: "#6b7280", fontSize: 13 }}>
                No courses found for "{apiSearchQuery}". Try a different search or add manually below.
              </div>
            )}
          </div>
        </Card>

        {/* Action buttons */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            onClick={() => setShowManualEntry(true)}
            style={{
              background: "none", border: "none", color: "#2563eb", fontSize: 13,
              cursor: "pointer", textDecoration: "underline", padding: 0
            }}
          >
            Can't find your course? Add manually
          </button>
          <Button variant="outline" onClick={cancel}>Cancel</Button>
        </div>
      </div>
    );
  }

  if (mode === "new" || mode === "edit") {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>{mode === "new" ? "Add Course" : "Edit Course"}</div>
        <Card>
          <div style={{ display: "grid", gap: 12 }}>
            <div><Label>Course Name</Label><Input value={working.name || ""} onChange={(e) => setWorking({ ...working, name: e.target.value })} /></div>
            <div>
              <Label>Location <span style={{ color: '#ef4444' }}>*</span></Label>
              <Input 
                required
                value={working.location || ""} 
                onChange={(e) => setWorking({ ...working, location: e.target.value })} 
                placeholder="Charleston, SC" 
              />
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                💡 Use format: "City, State" (e.g., "Charleston, SC" or "Charleston, South Carolina"). State abbreviations will be auto-converted for weather compatibility.
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Holes</Label>
                <select
                  value={working.holes || 18}
                  onChange={(e) => setWorking({ ...working, holes: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 14,
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <option value={9}>9 Holes</option>
                  <option value={18}>18 Holes</option>
                </select>
              </div>
              <div />
            </div>
            {(parseInt(working.holes) || 18) === 18 && (
              <div data-testid="course-nine-names">
                <Label>Two named nines (optional)</Label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>First nine (holes 1–9)</div>
                    <Input value={working.front_nine_name || ""} onChange={(e) => setWorking({ ...working, front_nine_name: e.target.value })} placeholder="e.g. Oak Forest" maxLength={60} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>Second nine (holes 10–18)</div>
                    <Input value={working.back_nine_name || ""} onChange={(e) => setWorking({ ...working, back_nine_name: e.target.value })} placeholder="e.g. Burn Kill" maxLength={60} />
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  For a course played as two named nines whose holes are each numbered 1–9, like Wescott's Oak Forest / Burn Kill. Pairings, scorecards, cart signs and the scoring screens then say "Burn Kill 3" instead of "Hole 12". Enter the nines in the club's order — the first nine carries the odd stroke indexes. Leave both blank for an ordinary course.
                </div>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><Label>Proshop Phone #</Label><Input value={working.proshop_phone || ""} onChange={(e) => setWorking({ ...working, proshop_phone: utils.formatPhone(e.target.value) })} placeholder="(555) 123-4567" /></div>
              <div><Label>Proshop Email</Label><Input type="email" value={working.proshop_email || ""} onChange={(e) => setWorking({ ...working, proshop_email: e.target.value })} placeholder="proshop@course.com" /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Tee Time Interval (minutes)</Label>
                <Input 
                  type="number" 
                  min="1" 
                  max="30" 
                  value={working.tee_time_interval || ""} 
                  onChange={(e) => setWorking({ ...working, tee_time_interval: e.target.value })} 
                  placeholder="e.g. 10" 
                />
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Auto-fills group tee times in Pairings</div>
              </div>
              <div />
            </div>
            
            {/* GHIN Course ID - organizer-settable; required for GHIN score posting from this course */}
            {mode === "edit" && (
              <div>
                <Label>GHIN Course ID</Label>
                <Input
                  value={working.ghin_course_id || ""}
                  onChange={(e) => setWorking({ ...working, ghin_course_id: e.target.value.replace(/\D/g, '') })}
                  placeholder="Enter GHIN Course ID (e.g. 24017)"
                  style={{
                    background: '#fff',
                    color: working.ghin_course_id ? '#111827' : '#9ca3af'
                  }}
                />
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Required for posting scores to GHIN from this course. To find it, start a score post for this course on ghin.com and copy the number at the end of the web address (e.g. …/post/24017 → 24017). You only need to set this once.
                </div>
              </div>
            )}

            {/* Tees Section - Only show when editing existing course */}
            {mode === "edit" && (
              <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, marginTop: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div>
                    <Label style={{ marginBottom: 0 }}>Course Tees</Label>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      Add tee information for course handicap calculations
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <label style={{ 
                      display: "inline-flex", alignItems: "center", gap: 4, padding: "8px 12px", 
                      background: importingFullScorecard ? "#f3f4f6" : "#eff6ff", 
                      border: "1px solid #bfdbfe", borderRadius: 10, 
                      cursor: importingFullScorecard ? "wait" : "pointer",
                      fontSize: 12, fontWeight: 600, color: "#1e40af",
                      animation: importingFullScorecard ? "pulse 1.5s ease-in-out infinite" : "none"
                    }}>
                      📷 {importingFullScorecard ? 'Reading scorecard...' : 'Import from Scorecard'}
                      <input 
                        type="file" 
                        accept="image/*" 
                        capture="environment"
                        style={{ display: "none" }} 
                        disabled={importingFullScorecard}
                        onChange={(e) => {
                          if (e.target.files[0]) importFullScorecard(e.target.files[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        resetTeeForm();
                        setTeeForm(prev => ({ ...prev, display_order: tees.length }));
                        setShowTeeForm(true);
                      }}
                      style={{ fontSize: 12, background: "#16a34a" }}
                    >
                      + Add Tee
                    </Button>
                  </div>
                </div>

                {/* Scorecard import result (Sept 11, 2026) — stays until dismissed.
                    Amber while tees are waiting for ratings, red for a failure,
                    green when everything saved. */}
                {scorecardImport && (() => {
                  const sc = scorecardImport;
                  const filledCount = sc.pending.filter(pendingTeeIsFilled).length;
                  const tone = sc.pending.length > 0
                    ? { bg: '#fffbeb', border: '#fcd34d', rule: '#fde68a' }
                    : (sc.kind === 'error' || (sc.saved.length === 0 && sc.skipped.length > 0))
                      ? { bg: '#fef2f2', border: '#fecaca', rule: '#fecaca' }
                      : { bg: '#f0fdf4', border: '#bbf7d0', rule: '#bbf7d0' };
                  return (
                    <div data-testid="scorecard-import-panel" style={{ background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13, color: '#1f2937' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ fontWeight: 700 }}>📷 Scorecard import</div>
                        <button
                          type="button"
                          aria-label="Dismiss"
                          onClick={() => setScorecardImport(null)}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
                        >✕</button>
                      </div>

                      {sc.message && (
                        <div style={{ color: '#991b1b', marginTop: 6 }}>{sc.message}</div>
                      )}

                      {sc.saved.length > 0 && (
                        <div style={{ color: '#166534', marginTop: 6 }}>
                          ✓ Saved: {sc.saved.map(t => `${t.tee_name}${t.holes_count ? ` (${t.holes_count} holes)` : ''}${t.holes_note ? ` — ${t.holes_note}` : ''}`).join(', ')}
                        </div>
                      )}

                      {sc.pending.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ color: '#92400e', marginBottom: 6, lineHeight: 1.4 }}>
                            This card doesn't print a course rating or slope for {sc.pending.length === 1 ? 'this tee' : 'these tees'}. TTO needs both to compute course handicaps, so {sc.pending.length === 1 ? 'it was' : 'they were'} not saved yet — the hole data was read and is held here. Enter the ratings (from the club, or the course lookup on ghin.com) and save.
                          </div>
                          {sc.pending.map(t => (
                            <div key={t.tee_name} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${tone.rule}` }}>
                              <div style={{ flex: '1 1 170px', minWidth: 0 }}>
                                <strong>{t.tee_name}</strong>
                                <span style={{ color: '#6b7280' }}> · par {t.par ?? '?'}{t.yardage_total ? ` · ${t.yardage_total.toLocaleString()} yds` : ''} · {t.holes_count ? `${t.holes_count} holes ✓` : 'no hole data'}</span>
                                {t.holes_note && <div style={{ fontSize: 11, color: '#b45309' }}>{t.holes_note}</div>}
                              </div>
                              {/* Inputs + remove wrap together as one unit under the name on a phone */}
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '0 0 auto' }}>
                                <div style={{ width: 118 }}>
                                  <Input
                                    type="number" step="0.1" inputMode="decimal" placeholder="Rating (71.7)" aria-label={`${t.tee_name} course rating`}
                                    value={t.course_rating}
                                    onChange={(e) => updatePendingTee(t.tee_name, 'course_rating', e.target.value)}
                                  />
                                </div>
                                <div style={{ width: 104 }}>
                                  <Input
                                    type="number" step="1" inputMode="numeric" placeholder="Slope (128)" aria-label={`${t.tee_name} slope`}
                                    value={t.slope_rating}
                                    onChange={(e) => updatePendingTee(t.tee_name, 'slope_rating', e.target.value)}
                                  />
                                </div>
                                <button
                                  type="button"
                                  title="Don't add this tee"
                                  aria-label={`Remove ${t.tee_name}`}
                                  onClick={() => removePendingTee(t.tee_name)}
                                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af', fontSize: 15, lineHeight: 1, padding: '0 4px' }}
                                >✕</button>
                              </div>
                            </div>
                          ))}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
                            <Button
                              onClick={completeScorecardImport}
                              disabled={sc.saving || filledCount === 0}
                              style={{ background: '#16a34a', color: '#fff', fontSize: 12 }}
                            >
                              {sc.saving ? 'Saving…' : `Save ${filledCount || ''} tee${filledCount === 1 ? '' : 's'} with hole data`}
                            </Button>
                            <span style={{ fontSize: 11, color: '#6b7280' }}>Tees without both numbers stay here until you fill them in or remove them.</span>
                          </div>
                        </div>
                      )}

                      {sc.skipped.length > 0 && (
                        <div style={{ color: '#991b1b', marginTop: 6 }}>
                          Skipped: {sc.skipped.map(k => `${k.tee_name} — ${k.reason}`).join('; ')}
                        </div>
                      )}
                    </div>
                  );
                })()}
                
                {/* Tee Form */}
                {showTeeForm && (
                  <div style={{ 
                    background: "#f9fafb", 
                    border: "1px solid #e5e7eb", 
                    borderRadius: 8, 
                    padding: 16, 
                    marginBottom: 12 
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>
                      {editingTee ? 'Edit Tee' : 'Add New Tee'}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <Label>Tee Name <span style={{ color: '#ef4444' }}>*</span></Label>
                        <Input 
                          value={teeForm.tee_name} 
                          onChange={(e) => setTeeForm({ ...teeForm, tee_name: e.target.value })}
                          placeholder="e.g., Blue, White, Gold"
                        />
                      </div>
                      <div>
                        <Label>Tee Color</Label>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input 
                            type="color" 
                            value={teeForm.tee_color} 
                            onChange={(e) => setTeeForm({ ...teeForm, tee_color: e.target.value })}
                            style={{ width: 40, height: 32, padding: 0, border: "1px solid #e5e7eb", borderRadius: 4, cursor: "pointer" }}
                          />
                          <div style={{ display: "flex", gap: 4 }}>
                            {defaultTeeColors.map(tc => (
                              <button
                                key={tc.name}
                                onClick={() => setTeeForm({ ...teeForm, tee_color: tc.color, tee_name: teeForm.tee_name || tc.name })}
                                title={tc.name}
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 4,
                                  border: teeForm.tee_color === tc.color ? "2px solid #2563eb" : "1px solid #d1d5db",
                                  background: tc.color,
                                  cursor: "pointer"
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
                      <div>
                        <Label>Course Rating <span style={{ color: '#ef4444' }}>*</span></Label>
                        <Input 
                          type="number"
                          step="0.1"
                          min="50"
                          max="90"
                          value={teeForm.course_rating} 
                          onChange={(e) => setTeeForm({ ...teeForm, course_rating: e.target.value })}
                          placeholder="e.g., 72.4"
                        />
                      </div>
                      <div>
                        <Label>Slope Rating <span style={{ color: '#ef4444' }}>*</span></Label>
                        <Input 
                          type="number"
                          min="55"
                          max="155"
                          value={teeForm.slope_rating} 
                          onChange={(e) => setTeeForm({ ...teeForm, slope_rating: e.target.value })}
                          placeholder="e.g., 131"
                        />
                      </div>
                      <div>
                        <Label>Par <span style={{ color: '#ef4444' }}>*</span></Label>
                        <Input 
                          type="number"
                          min="60"
                          max="80"
                          value={teeForm.par} 
                          onChange={(e) => setTeeForm({ ...teeForm, par: e.target.value })}
                          placeholder="e.g., 72"
                        />
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
                      <Button variant="outline" onClick={resetTeeForm}>Cancel</Button>
                      <Button onClick={saveTee}>{editingTee ? 'Update Tee' : 'Add Tee'}</Button>
                    </div>
                  </div>
                )}
                
                {/* Tees List */}
                {loadingTees ? (
                  <div style={{ textAlign: "center", padding: 16, color: "#6b7280" }}>Loading tees...</div>
                ) : tees.length === 0 ? (
                  <div style={{ 
                    textAlign: "center", 
                    padding: 24, 
                    color: "#6b7280", 
                    background: "#f9fafb", 
                    borderRadius: 8,
                    border: "1px dashed #d1d5db"
                  }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>⛳</div>
                    <div style={{ fontSize: 13 }}>No tees configured yet</div>
                    <div style={{ fontSize: 11, marginTop: 4 }}>Add tees to enable course handicap calculations</div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {[...tees].sort((a, b) => b.slope_rating - a.slope_rating).map((tee, index) => (
                      <div 
                        key={tee.id} 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between",
                          padding: "10px 12px",
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 6
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div 
                            style={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: 4, 
                              background: tee.tee_color || '#000',
                              border: "1px solid #d1d5db"
                            }} 
                          />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{tee.tee_name}</div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                              Rating: {tee.course_rating} • Slope: {tee.slope_rating} • Par: {tee.par}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          {scoringEnabled && (
                            <Button 
                              variant="outline" 
                              onClick={() => openHoleData(working.id, tee)}
                              style={{ fontSize: 11, padding: "4px 8px", color: "#166534", borderColor: "#bbf7d0", background: "#f0fdf4" }}
                            >
                              ⛳ Holes
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            onClick={() => startEditTee(tee)}
                            style={{ fontSize: 11, padding: "4px 8px" }}
                          >
                            Edit
                          </Button>
                          <Button 
                            variant="danger" 
                            onClick={() => deleteTee(tee.id)}
                            style={{ fontSize: 11, padding: "4px 8px" }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Example course handicap calculation */}
                    {tees.length > 0 && (
                      <div style={{ 
                        marginTop: 8, 
                        padding: 12, 
                        background: "#f0fdf4", 
                        border: "1px solid #bbf7d0", 
                        borderRadius: 6,
                        fontSize: 12
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, color: "#166534" }}>
                          📊 Course Handicap Formula
                        </div>
                        <div style={{ color: "#15803d" }}>
                          Course HCP = (Handicap Index × Slope ÷ 113) + (Course Rating - Par)
                        </div>
                        <div style={{ marginTop: 8, color: "#166534" }}>
                          <strong>Example:</strong> Player with 15.0 index playing {tees[0].tee_name} tees = 
                          <strong> {calculateCourseHandicap(15.0, tees[0].slope_rating, parseFloat(tees[0].course_rating), tees[0].par)}</strong> course handicap
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button variant="outline" onClick={cancel}>Cancel</Button>
              <Button onClick={() => save(working)}>Save</Button>
            </div>
          </div>
        </Card>

        {/* Hole Data Entry Modal */}
        {holeDataTee && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }} onClick={() => { setHoleDataTee(null); setHoleDataCourseId(null); }}>
            <Card style={{ width: "100%", maxWidth: 600, maxHeight: "90vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                <div style={{ fontWeight: 800, fontSize: 18 }}>
                  ⛳ Hole Data — {holeDataTee.tee_name} Tees
                </div>
                <label style={{ 
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", 
                  background: importingScorecard ? "#f3f4f6" : "#eff6ff", 
                  border: "1px solid #bfdbfe", borderRadius: 8, 
                  cursor: importingScorecard ? "wait" : "pointer",
                  fontSize: 12, fontWeight: 600, color: "#1e40af",
                  animation: importingScorecard ? "pulse 1.5s ease-in-out infinite" : "none"
                }}>
                  📷 {importingScorecard ? 'Reading scorecard...' : 'Import from Scorecard'}
                  <input 
                    type="file" 
                    accept="image/*" 
                    capture="environment"
                    style={{ display: "none" }} 
                    disabled={importingScorecard}
                    onChange={(e) => {
                      if (e.target.files[0]) importFromScorecard(e.target.files[0]);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
                Enter manually or snap a photo of your scorecard to auto-fill.
              </div>

              {loadingHoles ? (
                <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>Loading hole data...</div>
              ) : (
                <>
                  {/* Header */}
                  <div style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 1fr", gap: 8, marginBottom: 8, fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>
                    <div>Hole</div>
                    <div>Par</div>
                    <div>Yardage</div>
                    <div>HCP</div>
                  </div>

                  {/* Front 9 */}
                  {holeRows.filter(h => h.hole_number <= 9).map(hole => (
                    <div key={hole.hole_number} style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 1fr", gap: 8, marginBottom: 6, alignItems: "center" }}>
                      <div style={{ fontWeight: 700, fontSize: 14, textAlign: "center", color: "#111827" }}>{hole.hole_number}</div>
                      <select
                        value={hole.par}
                        onChange={(e) => updateHoleRow(hole.hole_number, 'par', parseInt(e.target.value))}
                        style={{ padding: 6, border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 14, background: "#fff" }}
                      >
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                      </select>
                      <input
                        type="number"
                        value={hole.yardage}
                        onChange={(e) => updateHoleRow(hole.hole_number, 'yardage', e.target.value)}
                        placeholder="Yds"
                        style={{ padding: 6, border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 14, width: "100%" }}
                      />
                      <select
                        value={hole.handicap_index || ''}
                        onChange={(e) => updateHoleRow(hole.hole_number, 'handicap_index', parseInt(e.target.value))}
                        style={{ padding: 6, border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 14, background: "#fff" }}
                      >
                        <option value="">—</option>
                        {Array.from({ length: holeRows.length }, (_, i) => i + 1).map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  ))}

                  {/* Front 9 Summary */}
                  <div style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 1fr", gap: 8, margin: "8px 0", padding: "8px 0", borderTop: "2px solid #111827", borderBottom: holeRows.length > 9 ? "2px solid #111827" : "none", fontWeight: 700, fontSize: 13 }}>
                    <div style={{ textAlign: "center" }}>{holeRows.length > 9 ? 'OUT' : 'TOT'}</div>
                    <div>{holeRows.filter(h => h.hole_number <= 9).reduce((sum, h) => sum + (parseInt(h.par) || 0), 0)}</div>
                    <div>{holeRows.filter(h => h.hole_number <= 9).reduce((sum, h) => sum + (parseInt(h.yardage) || 0), 0)}</div>
                    <div></div>
                  </div>

                  {/* Back 9 - only for 18-hole courses */}
                  {holeRows.length > 9 && holeRows.filter(h => h.hole_number > 9).map(hole => (
                    <div key={hole.hole_number} style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 1fr", gap: 8, marginBottom: 6, alignItems: "center" }}>
                      <div style={{ fontWeight: 700, fontSize: 14, textAlign: "center", color: "#111827" }}>{hole.hole_number}</div>
                      <select
                        value={hole.par}
                        onChange={(e) => updateHoleRow(hole.hole_number, 'par', parseInt(e.target.value))}
                        style={{ padding: 6, border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 14, background: "#fff" }}
                      >
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                      </select>
                      <input
                        type="number"
                        value={hole.yardage}
                        onChange={(e) => updateHoleRow(hole.hole_number, 'yardage', e.target.value)}
                        placeholder="Yds"
                        style={{ padding: 6, border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 14, width: "100%" }}
                      />
                      <select
                        value={hole.handicap_index || ''}
                        onChange={(e) => updateHoleRow(hole.hole_number, 'handicap_index', parseInt(e.target.value))}
                        style={{ padding: 6, border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 14, background: "#fff" }}
                      >
                        <option value="">—</option>
                        {Array.from({ length: holeRows.length }, (_, i) => i + 1).map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  ))}

                  {/* Back 9 Summary - only for 18-hole courses */}
                  {holeRows.length > 9 && (
                  <div style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 1fr", gap: 8, margin: "8px 0", padding: "8px 0", borderTop: "2px solid #111827", fontWeight: 700, fontSize: 13 }}>
                    <div style={{ textAlign: "center" }}>IN</div>
                    <div>{holeRows.filter(h => h.hole_number > 9).reduce((sum, h) => sum + (parseInt(h.par) || 0), 0)}</div>
                    <div>{holeRows.filter(h => h.hole_number > 9).reduce((sum, h) => sum + (parseInt(h.yardage) || 0), 0)}</div>
                    <div></div>
                  </div>
                  )}

                  {/* Total - only for 18-hole courses */}
                  {holeRows.length > 9 && (
                  <div style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 1fr", gap: 8, padding: "8px 0", borderTop: "2px solid #111827", fontWeight: 800, fontSize: 14, color: "#166534" }}>
                    <div style={{ textAlign: "center" }}>TOT</div>
                    <div>{holeRows.reduce((sum, h) => sum + (parseInt(h.par) || 0), 0)}</div>
                    <div>{holeRows.reduce((sum, h) => sum + (parseInt(h.yardage) || 0), 0).toLocaleString()}</div>
                    <div></div>
                  </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                    <Button variant="outline" onClick={() => { setHoleDataTee(null); setHoleDataCourseId(null); }} style={{ flex: 1 }}>Cancel</Button>
                    <Button onClick={saveHoleData} disabled={savingHoles} style={{ flex: 1, background: "#166534" }}>
                      {savingHoles ? 'Saving...' : 'Save Hole Data'}
                    </Button>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Courses</div>
          <Badge>{courses.length}</Badge>
        </div>
        <Button 
          onClick={isReadOnly ? handleReadOnlyClick : startNew}
          disabled={isReadOnly}
          style={{ 
            background: isReadOnly ? "#9ca3af" : "#16a34a", 
            cursor: isReadOnly ? "not-allowed" : "pointer" 
          }}
        >
          + Add Course
        </Button>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {courses.map((c) => (
          <Card key={c.id}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <Badge style={{ background: "#f0f9ff", color: "#0369a1", fontSize: 10 }}>
                    {c.holes || 18} holes
                  </Badge>
                  {c.tee_count > 0 ? (
                    <Badge style={{ background: "#dcfce7", color: "#166534", fontSize: 10 }}>
                      ⛳ {c.tee_count} tee{c.tee_count !== 1 ? 's' : ''}
                    </Badge>
                  ) : (
                    <Badge style={{ background: "#fef3c7", color: "#92400e", fontSize: 10 }}>
                      No tees
                    </Badge>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>{c.location}</div>
                {(c.proshop_phone || c.proshop_email || c.tee_time_interval) && (
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                    {c.proshop_phone && <span>📞 {c.proshop_phone}</span>}
                    {c.proshop_phone && c.proshop_email && <span style={{ margin: "0 6px" }}>•</span>}
                    {c.proshop_email && <span>📧 {c.proshop_email}</span>}
                    {c.tee_time_interval && (
                      <span>{(c.proshop_phone || c.proshop_email) ? <span style={{ margin: "0 6px" }}>•</span> : null}⏱️ {c.tee_time_interval} min intervals</span>
                    )}
                  </div>
                )}
                {teeSummaries[c.id] && teeSummaries[c.id].length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                    {teeSummaries[c.id].map((tee, i) => (
                      <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#374151" }}>
                        <div style={{ 
                          width: 12, height: 12, borderRadius: 3, 
                          background: tee.tee_color || '#000',
                          border: ['#f3f4f6', '#fff', '#ffffff'].includes(tee.tee_color) ? '1px solid #d1d5db' : 'none'
                        }} />
                        <span style={{ fontWeight: 600 }}>{tee.tee_name}</span>
                        {tee.totalYardage && (
                          <span style={{ color: "#6b7280" }}>- {tee.totalYardage.toLocaleString()} yds</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <Button 
                  variant="outline" 
                  onClick={isReadOnly ? handleReadOnlyClick : () => startEdit(c)} 
                  disabled={isReadOnly}
                  style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer" }}
                >
                  Edit
                </Button>
                <Button 
                  variant="danger" 
                  onClick={isReadOnly ? handleReadOnlyClick : () => setConfirmDeleteId(c.id)} 
                  disabled={isReadOnly}
                  style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer" }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {confirmDeleteId && <ConfirmDialog title="Delete course?" message="This will permanently remove the course." onConfirm={() => remove(confirmDeleteId)} onCancel={() => setConfirmDeleteId(null)} />}
      
      {/* Import Success Confirmation Modal */}
      {importSuccess && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: "white", borderRadius: 12, padding: "32px 28px", maxWidth: 420, width: "90%", textAlign: "center", boxShadow: "0 25px 50px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⛳</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Course Imported!</div>
            <div style={{ fontSize: 15, color: "#4b5563", marginBottom: 4 }}>
              <strong>{importSuccess.name}</strong> has been added to your courses.
            </div>
            <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 24 }}>
              {importSuccess.teeCount > 0 
                ? `${importSuccess.teeCount} tee${importSuccess.teeCount !== 1 ? 's' : ''} with hole data imported.`
                : 'No tee data found — you can add tees manually.'}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              {importSuccess.courseId && (
                <Button onClick={() => {
                  const courseId = importSuccess.courseId;
                  const courseName = importSuccess.name;
                  setImportSuccess(null);
                  refreshCourses(); // Update global course dropdown
                  const found = courses?.find(c => c.id === courseId);
                  if (found) { setWorking(found); } else { setWorking({ id: courseId, name: courseName, holes: 18 }); }
                  fetchTees(courseId);
                  setMode('edit');
                }} style={{ background: "#1e40af", color: "white", padding: "10px 20px", borderRadius: 8, fontWeight: 600, border: "none", cursor: "pointer" }}>
                  Edit Course
                </Button>
              )}
              <Button onClick={() => { setImportSuccess(null); setMode('list'); refreshCourses(); }} style={{ background: importSuccess.courseId ? "#f3f4f6" : "#1e40af", color: importSuccess.courseId ? "#374151" : "white", padding: "10px 20px", borderRadius: 8, fontWeight: 600, border: "none", cursor: "pointer" }}>
                {importSuccess.courseId ? 'Back to Courses' : 'OK'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const GroupManager = () => {
  const { addToast } = useToast();
  const { isReadOnly, isExpired } = useSubscription();
  const { groups, loading: loadingGroups, addGroup, updateGroup, deleteGroup } = useGroups();
  const { data: players } = useApiData('/players');
  const [mode, setMode] = useState("list");
  const [working, setWorking] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  
  // Read-only mode click handler
  const handleReadOnlyClick = () => {
    addToast(isExpired 
      ? "Your free trial has ended. Please subscribe to continue making changes." 
      : "Your account is in read-only mode. Please reactivate your subscription to make changes.", "error");
  };

  const activePlayers = useMemo(() => players.filter((p) => p.active), [players]);
  
  // Sort players alphabetically by last name
  const sortedActivePlayers = useMemo(() => {
    const getLastName = (name) => {
      const parts = (name || "").trim().split(" ");
      return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : parts[0].toLowerCase();
    };
    
    return [...activePlayers].sort((a, b) => {
      const lastNameA = getLastName(a.name);
      const lastNameB = getLastName(b.name);
      return lastNameA.localeCompare(lastNameB);
    });
  }, [activePlayers]);

  const startNew = () => {
    setWorking({ id: utils.generateId(), name: "", playerIds: [] });
    setMode("new");
  };

  const startEdit = (g) => {
    setWorking({ ...g, playerIds: g.playerIds || g.player_ids || [] });
    setMode("edit");
  };

  const cancel = () => {
    setMode("list");
    setWorking(null);
  };

  const save = async (draft) => {
    if (!draft.name.trim()) {
      addToast("Group name is required", "error");
      return;
    }
    
    const cleaned = { 
      id: draft.id, 
      name: draft.name.trim(), 
      player_ids: draft.playerIds || [] 
    };
    
    try {
      if (mode === "new") {
        await addGroup(cleaned);
        addToast(`Group "${cleaned.name}" created successfully`);
      } else if (mode === "edit") {
        await updateGroup(cleaned.id, cleaned);
        addToast(`Group "${cleaned.name}" updated successfully`);
      }
      cancel();
    } catch (err) {
      console.error('Error saving group:', err);
      const errorMessage = err.message || "Unable to save group. Please try again.";
      addToast(errorMessage, "error");
    }
  };

  const remove = async (id) => {
    try {
      await deleteGroup(id);
      addToast("Group deleted successfully");
      setConfirmDeleteId(null);
    } catch (err) {
      addToast("Error deleting group", "error");
    }
  };

  const togglePlayer = (playerId) => {
    setWorking(prev => {
      const currentIds = prev.playerIds || [];
      const isSelected = currentIds.includes(playerId);
      return {
        ...prev,
        playerIds: isSelected 
          ? currentIds.filter(id => id !== playerId)
          : [...currentIds, playerId]
      };
    });
  };

  if (loadingGroups) {
    return <div style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>Loading groups...</div>;
  }

  if (mode === "new" || mode === "edit") {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>{mode === "new" ? "Create Group" : "Edit Group"}</div>
        <Card>
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <Label>Group Name</Label>
              <Input 
                value={working.name} 
                onChange={(e) => setWorking({ ...working, name: e.target.value })} 
                placeholder="e.g. Weekend Warriors, Tuesday Group"
              />
            </div>
            
            <div>
              <Label>Select Players ({(working.playerIds || []).length} selected)</Label>
              <div style={{ maxHeight: 300, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 10, padding: 8 }}>
                {sortedActivePlayers.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 16, color: "#6b7280" }}>No active players available</div>
                ) : (
                  sortedActivePlayers.map((player) => {
                    const isSelected = (working.playerIds || []).includes(player.id);
                    return (
                      <div 
                        key={player.id}
                        onClick={() => togglePlayer(player.id)}
                        style={{ 
                          padding: 8, 
                          cursor: "pointer", 
                          borderRadius: 6,
                          marginBottom: 4,
                          background: isSelected ? "#eef2ff" : "transparent",
                          border: isSelected ? "1px solid #4338ca" : "1px solid transparent",
                          display: "flex",
                          alignItems: "center",
                          gap: 8
                        }}
                      >
                        <input 
                          type="checkbox" 
                          checked={isSelected}
                          onChange={() => {}}
                          style={{ cursor: "pointer" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: isSelected ? 600 : 400 }}>{player.name}</div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>{player.email}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button variant="outline" onClick={cancel}>Cancel</Button>
              <Button onClick={() => save(working)}>Save Group</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Player Groups</div>
          <Badge>{groups.length}</Badge>
        </div>
        <Button 
          onClick={isReadOnly ? handleReadOnlyClick : startNew}
          disabled={isReadOnly}
          style={{ 
            background: isReadOnly ? "#9ca3af" : "#16a34a", 
            cursor: isReadOnly ? "not-allowed" : "pointer" 
          }}
        >
          + Create Group
        </Button>
      </div>
      
      <div style={{ 
        padding: "12px 16px", 
        background: "#f8f9fa", 
        borderLeft: "4px solid #2d6a4f", 
        borderRadius: 4, 
        color: "#555", 
        fontSize: 14, 
        lineHeight: 1.6 
      }}>
        Player Groups help you organize players into teams, leagues, or regular playing groups for easier tee time management. Use groups to quickly assign multiple players to a tee time at once, send targeted notifications to specific sets of players, or manage recurring weekly games. For example, create groups like "Tuesday Morning Regulars," "Ladies League," or "Weekend Warriors" to streamline your scheduling. Players can belong to multiple groups, and groups can be edited anytime without affecting individual player records.
      </div>
      
      {groups.length === 0 ? (
        <Card>
          <div style={{ textAlign: "center", padding: 24, color: "#6b7280" }}>
            <div style={{ fontSize: 16, marginBottom: 8 }}>No groups yet</div>
            <div style={{ fontSize: 14 }}>Create groups to easily publish tee times to specific sets of players</div>
          </div>
        </Card>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {groups.map((group) => {
            const groupPlayers = activePlayers.filter(p => (group.player_ids || []).includes(p.id));
            return (
              <Card key={group.id}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{group.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {groupPlayers.length === 0 ? (
                        "No players"
                      ) : (
                        `${groupPlayers.length} player${groupPlayers.length === 1 ? '' : 's'}: ${groupPlayers.map(p => p.name).join(", ")}`
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button 
                      variant="outline" 
                      onClick={isReadOnly ? handleReadOnlyClick : () => startEdit(group)} 
                      disabled={isReadOnly}
                      style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer" }}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={isReadOnly ? handleReadOnlyClick : () => setConfirmDeleteId(group.id)} 
                      disabled={isReadOnly}
                      style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer" }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      
      {confirmDeleteId && (
        <ConfirmDialog 
          title="Delete group?" 
          message="This will permanently remove the group. This won't affect the players themselves." 
          onConfirm={() => remove(confirmDeleteId)} 
          onCancel={() => setConfirmDeleteId(null)} 
        />
      )}
    </div>
  );
};


// ============================================================================
// Guides chooser (Dennis, Sept 27, 2026). The dashboard's 📖 Guides button opens
// this: organizers pick "Getting Started" (which replaced the old Implementation
// Guide modal) or "Setting up Events / Tournaments", which lists one setup guide
// per format, with "Scoring Your Rounds" second (a games guide comes later). Guides are public
// articles under /guides/<slug> and open in a new tab, so the organizer can keep
// the guide beside the screen he's working in. The ?from=app on every in-app link shows the
// guide's "Close guide" bar (GuideArticle.js). The same chooser opens straight
// to the event list from a "📖 Event setup guides" link beside "This is an event"
// and in the Set up event header (EventGuidesLink below).
// To add a format: add a line to EVENT_GUIDES (and the article under
// components/guides) and drop it from EVENT_GUIDES_SOON.
// ============================================================================
// A to Z by title (Oct 5, 2026 — Dennis); keep new lines in order.
const EVENT_GUIDES = [
  { slug: 'how-to-set-up-a-best-ball-golf-tournament', title: 'Best Ball', blurb: 'Everyone plays his own ball; the team counts its best score(s). Teams, allowances, the blind draw, money, ties.' },
  { slug: 'how-to-set-up-a-captains-choice-golf-tournament', title: "Captain's Choice (Scramble)", blurb: 'One score per team. Teams, team handicaps, team codes, printed cards, leaderboard, ties.' },
  { slug: 'how-to-set-up-a-shamble-golf-tournament', title: 'Shamble', blurb: 'The team picks the best drive, then everyone plays his own ball in; the team counts its best score(s). Allowances, the drive rule, the team row on the cards, GHIN off.' },
  { slug: 'how-to-set-up-a-skins-game', title: 'Skins', blurb: 'Lowest score on a hole wins a skin. Gross or net, carryover, no carryover or pot, strokes off the low man or full handicap. On its own or beside the main game.' },
  { slug: 'how-to-set-up-a-stroke-play-golf-tournament', title: 'Stroke Play', blurb: 'Every player plays his own ball; lowest total wins, net, gross or both. Allowance, payouts, ties, scorecard codes.' },
];
const EVENT_GUIDES_SOON = [];
// Game guides (Oct 4, 2026 — Dennis): the games groups play every week, written for a
// weekly round first, with a short "in an event" note. Skins is listed in both.
// A to Z by title (Oct 5, 2026 — Dennis); keep new lines in order.
const GAME_GUIDES = [
  { slug: 'how-to-set-up-a-best-ball-game', title: 'Best Ball', blurb: 'Teams from your pairings count their best score on each hole. 2-man or best 1, 2 or 3 of 4.' },
  { slug: 'how-to-set-up-closest-to-the-pin', title: 'Closest to the Pin', blurb: 'On the par 3s, the closest player claims the hole on his phone. Must make par optional; a prize per hole or so much a player.' },
  { slug: 'how-to-set-up-a-dots-golf-game', title: 'Dots', blurb: 'A dot for every birdie, sandy and poley. Birdies count from the scores; players tap Sandy or Poley. Settled within each group or across the field.' },
  { slug: 'how-to-set-up-a-match-play-golf-game', title: 'Match Play', blurb: 'Hole by hole, one bet a match: singles in your group or 2 vs 2 four-ball. USGA strokes, full handicap or gross; 18 holes or a nine.' },
  { slug: 'how-to-set-up-a-nassau-golf-game', title: 'Nassau', blurb: 'Front, back and overall, match play. Every pair or 2 vs 2 best ball, net or gross, presses automatic, asked or off.' },
  { slug: 'how-to-set-up-a-putts-golf-game', title: 'Putts Differential', blurb: 'Fewer putts wins. Each pair of players settles the difference in total putts.' },
  { slug: 'how-to-set-up-a-sixes-golf-game', title: 'Sixes', blurb: 'Three six-hole 2 vs 2 matches per foursome, partners by cart and driver. Net or gross, presses automatic, asked or off.' },
  { slug: 'how-to-set-up-a-skins-game', title: 'Skins', blurb: 'Lowest score on a hole wins a skin. Gross or net, carryover, no carryover or pot, strokes off the low man or full handicap.' },
  { slug: 'how-to-set-up-a-stableford-golf-game', title: 'Stableford', blurb: 'Points on every hole, most points wins. Within each group or the whole field; net or gross; a pot paid by places (front, back and overall too), or so much a point.' },
  { slug: 'how-to-set-up-a-stroke-play-game', title: 'Stroke Play', blurb: 'Lowest total wins, net, gross or both. A buy-in and the places you pay; front and back nines optional.' },
];

const GuidesChooserModal = ({ isOpen, onClose, startAt = 'home', showAll = false }) => {
  const [view, setView] = useState(startAt);
  useEffect(() => { if (isOpen) setView(startAt); }, [isOpen, startAt]);
  if (!isOpen) return null;
  const card = { display: 'flex', alignItems: 'flex-start', gap: 14, width: '100%', textAlign: 'left', padding: '16px 18px', border: '1px solid #d1d5db', borderRadius: 12, background: '#fff', cursor: 'pointer', textDecoration: 'none', color: '#111827', boxSizing: 'border-box' };
  const icon = { fontSize: 26, lineHeight: 1, flexShrink: 0 };
  const title = { fontSize: 16, fontWeight: 800, color: '#14532d' };
  const sub = { fontSize: 13, color: '#4b5563', marginTop: 3, lineHeight: 1.45 };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 12000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', background: '#f9fafb', borderRadius: 16, boxShadow: '0 12px 40px rgba(0,0,0,.25)' }}>
        <div style={{ background: 'linear-gradient(135deg, #2d6a4f, #1b4332)', color: '#fff', padding: '18px 22px', borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 20 }}>📖 {view === 'events' ? 'Setting up Events / Tournaments' : view === 'games' ? 'Game guides' : 'Guides'}</div>
            <div style={{ fontSize: 13, opacity: .9 }}>{view === 'events' ? 'Pick your format. Each guide lists every step in order, and you can print it.' : view === 'games' ? 'The games your group plays: setup, scoring and settling up, in order. You can print each one.' : 'Step-by-step help for running Tee Time Organizer.'}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: '18px 22px 22px', display: 'grid', gap: 12 }}>
          {view === 'home' ? (
            <>
              {/* Getting Started (Sept 27, 2026) replaces the old Implementation Guide
                  modal: a public article like the event guides, first in line so a new
                  organizer knows where to begin. "Scoring Your Rounds" is second. */}
              <a href="/guides/getting-started-with-tee-time-organizer?from=app" target="_blank" rel="noopener noreferrer" style={card}>
                <span style={icon}>🧭</span>
                <span><div style={title}>Getting Started ↗</div><div style={sub}>New to TTO? Start here: settings, courses, players, your first tee time, invitations, reminders and pairings.</div></span>
              </a>
              {/* Sept 30, 2026: the whole path for one event, from an empty account to
                  final results — what an Event Pass buyer's Next Steps page links to. */}
              <a href="/guides/how-to-run-a-golf-tournament-with-tee-time-organizer?from=app" target="_blank" rel="noopener noreferrer" style={card}>
                <span style={icon}>🏆</span>
                <span><div style={title}>Setting Up Your Tournament ↗</div><div style={sub}>Running one event? Start here: your course, the event, your field, then your format&rsquo;s guide for teams, games, cards and results.</div></span>
              </a>
              <a href="/guides/scoring-your-golf-rounds?from=app" target="_blank" rel="noopener noreferrer" style={card}>
                <span style={icon}>⛳</span>
                <span><div style={title}>Scoring Your Rounds ↗</div><div style={sub}>Live, hole-by-hole or total scores, yards to the green, the leaderboard, celebrations, posting to GHIN, scorecards and stats.</div></span>
              </a>
              {/* Player Guide (Sept 28, 2026): what players see. Also a 📖 Player Guide
                  button in the player app, so players find it without the organizer. */}
              <a href="/guides/tee-time-organizer-player-guide?from=app" target="_blank" rel="noopener noreferrer" style={card}>
                <span style={icon}>🏌️</span>
                <span><div style={title}>Player Guide ↗</div><div style={sub}>What your players see: the app, their profile, signing up, events, scoring and GHIN. Players also have it in their app, and you can send them the link.</div></span>
              </a>
              <button type="button" style={card} onClick={() => setView('games')}>
                <span style={icon}>🎮</span>
                <span><div style={title}>Game guides ›</div><div style={sub}>Best Ball, Closest to the Pin, Dots, Match Play, Nassau, Putts, Sixes, Skins, Stableford and Stroke Play: setup, scoring and settling up for your weekly round.</div></span>
              </button>
              <button type="button" style={card} onClick={() => setView('events')}>
                <span style={icon}>🏆</span>
                <span><div style={title}>Setting up Events / Tournaments ›</div><div style={sub}>A guide for each tournament format, from creating the event to final results.</div></span>
              </button>
            </>
          ) : view === 'games' ? (
            <>
              {GAME_GUIDES.map((g) => (
                <a key={g.slug} href={`/guides/${g.slug}?from=app`} target="_blank" rel="noopener noreferrer" style={card}>
                  <span style={icon}>🎮</span>
                  <span><div style={title}>{g.title} ↗</div><div style={sub}>{g.blurb}</div></span>
                </a>
              ))}
              <button type="button" onClick={() => setView('home')} style={{ justifySelf: 'start', background: 'none', border: 'none', color: '#2d6a4f', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0 }}>‹ All guides</button>
            </>
          ) : (
            <>
              <a href="/guides/how-to-run-a-golf-tournament-with-tee-time-organizer?from=app" target="_blank" rel="noopener noreferrer" style={{ ...card, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                <span style={icon}>🏆</span>
                <span><div style={title}>First event? Setting Up Your Tournament ↗</div><div style={sub}>The whole path in order: course, event, field, then the format guide below.</div></span>
              </a>
              {EVENT_GUIDES.map((g, i) => (
                <a key={g.slug} href={`/guides/${g.slug}?from=app`} target="_blank" rel="noopener noreferrer" style={card}>
                  <span style={{ ...icon, fontSize: 15, fontWeight: 800, width: 28, height: 28, borderRadius: 14, background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                  <span><div style={title}>{g.title} ↗</div><div style={sub}>{g.blurb}</div></span>
                </a>
              ))}
              {EVENT_GUIDES_SOON.length > 0 && (
                <div style={{ fontSize: 13, color: '#6b7280', padding: '0 4px' }}>Coming soon: {EVENT_GUIDES_SOON.join(', ')}.</div>
              )}
              {showAll && (
                <button type="button" onClick={() => setView('home')} style={{ justifySelf: 'start', background: 'none', border: 'none', color: '#2d6a4f', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0 }}>‹ All guides</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// A "📖 Event setup guides" link that opens the chooser at the event list.
// `light` = white text, for the green Set up event header.
const EventGuidesLink = ({ light = false, label = '📖 Event setup guides' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        style={{ background: light ? 'rgba(255,255,255,.15)' : '#ecfdf5', border: light ? '1px solid rgba(255,255,255,.5)' : '1px solid #86efac', color: light ? '#fff' : '#166534', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
      >
        {label}
      </button>
      <GuidesChooserModal isOpen={open} onClose={() => setOpen(false)} startAt="events" />
    </>
  );
};

// InfoTip — instant hover tooltip for the little "i" help icons. Replaces the
// native `title` attribute (browser-controlled ~1s delay) with a bubble that
// appears immediately on hover. No delay, consistent across the setup screens.
const InfoTip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex", marginLeft: 4, verticalAlign: "middle" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span
        aria-label={text}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 16, height: 16, borderRadius: "50%", border: "1px solid #9ca3af",
          color: "#6b7280", fontSize: 11, fontWeight: 700, fontStyle: "normal",
          lineHeight: 1, cursor: "help", userSelect: "none"
        }}
      >
        i
      </span>
      {show && (
        <span
          role="tooltip"
          style={{
            position: "absolute", bottom: "calc(100% + 6px)", left: "50%",
            transform: "translateX(-50%)", zIndex: 100, width: 240, maxWidth: "70vw",
            background: "#1f2937", color: "#fff", fontSize: 12, fontWeight: 400,
            lineHeight: 1.4, textAlign: "left", padding: "8px 10px", borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,.18)", pointerEvents: "none", whiteSpace: "normal"
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

// Public leaderboard address (Sept 16, 2026) — client-side mirror of
// services/eventSlugService: TTOleaderboard.com/<slug> is the event's live board,
// printed on the scorecards and cart signs. The server is authoritative (it
// generates, de-duplicates and enforces the reprint rule); these only preview and
// tidy what the organizer types.
const PUBLIC_BOARD_HOST = 'TTOleaderboard.com';
const slugifyPublic = (text) => String(text || '')
  .normalize('NFKD').replace(/\p{M}+/gu, '').replace(/['\u2019]/g, '').replace(/&/g, ' and ')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const normalizePublicSlug = (input) => slugifyPublic(
  String(input || '').replace(/^(?:https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}(?=\/)/i, '').replace(/^\/+(lb\/)?/i, '')
).slice(0, 48);
const suggestPublicSlug = (name, year) => {
  const y = String(year);
  let base = slugifyPublic(name).slice(0, 48 - y.length - 1).replace(/-+$/, '') || 'event';
  if (!base.includes(y)) base = `${base}-${y}`;
  return base.slice(0, 48);
};

// Event logo (Sept 16, 2026) — shown on the public leaderboard beside the event
// name (screen only; the printed cards keep the TTO badge). The picked file is
// downscaled on a canvas to LOGO_MAX_EDGE px before upload (a phone photo is
// megabytes; a leaderboard logo needs a few hundred px), PNG kept when the file
// is a PNG so a crest on a clear background stays transparent, else JPEG. Any
// failure in the canvas path (old browser, odd file) falls back to the original
// bytes — the server resizes again with sharp regardless. Resolves { blob, name, type }.
const LOGO_MAX_EDGE = 800;
const LOGO_MAX_BYTES = 6 * 1024 * 1024;
const prepareLogoImage = (file) => new Promise((resolve) => {
  const original = () => resolve({ blob: file, name: file.name || 'logo', type: file.type || 'image/png' });
  try {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth, h = img.naturalHeight;
        const scale = Math.min(1, LOGO_MAX_EDGE / Math.max(w, h));
        if (scale >= 1 && file.size < 400 * 1024) { URL.revokeObjectURL(url); return original(); }   // already small: keep the bytes as they are
        const cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement('canvas');
        canvas.width = cw; canvas.height = ch;
        const ctx = canvas.getContext('2d');
        if (!ctx) { URL.revokeObjectURL(url); return original(); }
        const keepPng = /png|gif|webp/i.test(file.type || '');
        if (!keepPng) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, cw, ch); }
        ctx.drawImage(img, 0, 0, cw, ch);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return original();
          resolve({ blob, name: keepPng ? 'logo.png' : 'logo.jpg', type: keepPng ? 'image/png' : 'image/jpeg' });
        }, keepPng ? 'image/png' : 'image/jpeg', 0.9);
      } catch (e) { URL.revokeObjectURL(url); original(); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); original(); };
    img.src = url;
  } catch (e) { original(); }
});

// ============================================================================
// EVENT PASS (Sept 28, 2026 — Event_Pass_Decisions_Sep28.md). One pass = one
// event: bought here, attached when the event is created, good for 6 months,
// used up when the event is finalized (30 days read-only after). The server is
// the authority (services/eventPassService.js); these pieces only show what it
// says and open Stripe Checkout.
//   openEventPass({ message, code, teetimeId, passId })  — from anywhere
//   <EventPassModalHost onChanged>  — mounted once on the organizer dashboard;
//      also finishes a purchase when Stripe sends the organizer back
//   <EventPassLock>  — "This is an event" for an org that has no pass to use
//   <EventPassBadge teetimeId>  — the pass line in the Set up event header
// ============================================================================
const EVENT_PASS_EVENT = 'tto:event-pass';
const EVENT_PASS_CODES = ['EVENT_PASS_REQUIRED', 'EVENT_FIELD_CAP', 'EVENT_AFTER_PASS'];
const openEventPass = (detail = {}) => {
  try { window.dispatchEvent(new CustomEvent(EVENT_PASS_EVENT, { detail })); } catch (e) { /* very old browser */ }
};
const passMoney = (cents) => { const d = (Number(cents) || 0) / 100; return `$${d % 1 === 0 ? d.toFixed(0) : d.toFixed(2)}`; };
const fmtPassDate = (d) => { try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch (e) { return ''; } };
const eventPassRequest = async (path, method = 'GET', body) => {
  const headers = { 'Content-Type': 'application/json' };
  const t = getAuthToken();
  if (t) headers['Authorization'] = `Bearer ${t}`;
  const r = await fetch(`${API_URL}${path}`, { method, headers, credentials: 'include', body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(d.error || `The server answered ${r.status}.`); e.code = d.code; throw e; }
  return d;
};
const PASS_KIND_TEXT = {
  standard_72: { title: 'Up to 72 golfers', sub: 'Most outings and member-guests' },
  standard_250: { title: 'Up to 250 golfers', sub: 'Full-field shotguns and big days' },
  subscriber: { title: 'Up to 250 golfers', sub: 'Your plan’s subscriber price' },
};

const EventPassLock = ({ style }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...(style || {}) }} data-testid="event-pass-lock">
    <input type="checkbox" disabled aria-label="This is an event — needs an Event Pass" style={{ width: 16, height: 16 }} />
    <span style={{ fontSize: 14, color: '#6b7280' }}>🔒 This is an event</span>
    <button type="button" onClick={() => openEventPass({})}
      style={{ background: 'none', border: 'none', color: '#2d6a4f', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
      Get an Event Pass
    </button>
  </span>
);

const EventPassModalHost = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState({});
  const [info, setInfo] = useState(null);      // { access, offer }
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);      // the pass just bought / upgraded
  const load = async () => {
    try { setInfo(await eventPassRequest('/event-pass')); } catch (e) { setError(e.message); }
  };
  useEffect(() => {
    const onOpen = (e) => { setDetail(e.detail || {}); setError(''); setDone(null); setOpen(true); load(); };
    window.addEventListener(EVENT_PASS_EVENT, onOpen);
    return () => window.removeEventListener(EVENT_PASS_EVENT, onOpen);
  }, []);
  // Signed up through "I'm running one event" (/register?event=1): open the window
  // once on the first dashboard visit.
  useEffect(() => {
    let flag = null;
    try { flag = localStorage.getItem('tto_open_event_pass'); } catch (e) { return; }
    if (flag !== '1') return;
    try { localStorage.removeItem('tto_open_event_pass'); } catch (e) { /* ignore */ }
    openEventPass({});
  }, []);
  // Back from Stripe: ?event_pass=success|upgraded&session_id=… — confirm with the
  // server (it asks Stripe), so the organizer never waits on the webhook.
  useEffect(() => {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    const state = params.get('event_pass');
    if (!state) return;
    const sessionId = params.get('session_id');
    params.delete('event_pass'); params.delete('session_id');
    try {
      const qs = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`);
    } catch (e) { /* leave the URL */ }
    if ((state === 'success' || state === 'upgraded') && sessionId) {
      setOpen(true); setBusy('confirm'); setDetail({ returning: state });
      eventPassRequest(`/event-pass/session/${encodeURIComponent(sessionId)}`)
        .then((d) => { setDone(d.pass || { pending: true }); if (onChanged) onChanged(); })
        .catch((e) => setError(e.message))
        .finally(() => setBusy(''));
    }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;

  const offer = info && info.offer;
  const access = info && info.access;
  const pass = detail.passId && access ? (access.passes || []).find(p => p.id === detail.passId) : null;
  const upgradeMode = !!(pass && pass.can_upgrade && detail.code === 'EVENT_FIELD_CAP');
  const buy = async (kind) => {
    setBusy(kind); setError('');
    try {
      const d = await eventPassRequest('/event-pass/checkout', 'POST', { kind, teetime_id: detail.teetimeId || null, return_path: '/dashboard' });
      if (d.url) window.location.assign(d.url);
    } catch (e) { setError(e.message); setBusy(''); }
  };
  const upgrade = async () => {
    setBusy('upgrade'); setError('');
    try {
      const d = await eventPassRequest(`/event-pass/${pass.id}/upgrade-checkout`, 'POST', { return_path: '/dashboard' });
      if (d.url) window.location.assign(d.url);
    } catch (e) { setError(e.message); setBusy(''); }
  };
  const close = () => { if (!busy) setOpen(false); };
  const btn = (primary) => ({ padding: '10px 16px', borderRadius: 10, border: primary ? 'none' : '1px solid #d1d5db', background: primary ? '#2d6a4f' : '#fff', color: primary ? '#fff' : '#374151', fontSize: 15, fontWeight: 700, cursor: 'pointer' });

  return (
    <div onClick={close} data-testid="event-pass-modal"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ background: 'linear-gradient(135deg, #2d6a4f, #1b4332)', color: '#fff', padding: '18px 22px', borderRadius: '16px 16px 0 0' }}>
          <div style={{ fontWeight: 800, fontSize: 20 }}>🎟️ Event Pass</div>
          <div style={{ fontSize: 13, opacity: .9 }}>Everything the day needs, for one event.</div>
        </div>
        <div style={{ padding: '18px 22px 22px', display: 'grid', gap: 14 }}>
          {busy === 'confirm' && <div style={{ fontSize: 15, color: '#374151' }}>Confirming your purchase…</div>}

          {done && !done.pending && (
            <div data-testid="event-pass-done" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px', color: '#14532d', fontSize: 15, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4 }}>{detail.returning === 'upgraded' ? '✅ Upgraded to 250 golfers' : '✅ Your Event Pass is ready'}</div>
              {done.teetime_id
                ? <>It’s attached to your event — a field of up to {done.size_cap} golfers.</>
                : <>Good for one event on or before {fmtPassDate(done.expires_at)}, a field of up to {done.size_cap} golfers. Create a tee time and tick <strong>This is an event</strong> to use it.</>}
            </div>
          )}
          {done && done.pending && (
            <div style={{ fontSize: 15, color: '#374151' }}>Your payment went through. The pass will show here in a minute — refresh the page if it doesn’t.</div>
          )}

          {!done && busy !== 'confirm' && (
            <>
              {detail.message && (
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '10px 14px', color: '#78350f', fontSize: 14, lineHeight: 1.5 }}>{detail.message}</div>
              )}
              {!detail.message && (
                <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.5 }}>
                  Running an outing, a member-guest or a tournament? An Event Pass unlocks the event tools for one event: pairings and shotgun starts, printed scorecards and cart signs, scan-to-score, the live leaderboard and TV board, and results.
                </div>
              )}

              {!info && !error && <div style={{ fontSize: 14, color: '#6b7280' }}>Loading…</div>}

              {/* Sept 30, 2026 — the /events page can open this window for any org.
                  An org with events included needs no pass: say so and offer nothing. */}
              {access && access.unlimited && !upgradeMode && (
                <div data-testid="event-pass-included" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px', color: '#14532d', fontSize: 15, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4 }}>✅ Events are included for your group</div>
                  No pass needed. Create a tee time and tick <strong>This is an event</strong>.
                </div>
              )}
              {access && !access.unlimited && access.allowance && !upgradeMode && (
                <div data-testid="event-pass-allowance" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 14px', color: '#14532d', fontSize: 14, lineHeight: 1.5 }}>
                  Your League plan includes one event this year, so you don’t need a pass for your next event. Buy one below only if you’re running more than one.
                </div>
              )}

              {upgradeMode && (
                <div style={{ border: '2px solid #2d6a4f', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>Upgrade to 250 golfers</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>Pay the difference; same event, same dates.</div>
                  </div>
                  <button type="button" disabled={!!busy || !(offer && offer.upgrade && offer.upgrade.configured)} onClick={upgrade} style={btn(true)}>
                    {busy === 'upgrade' ? 'Opening checkout…' : `Upgrade — ${passMoney(offer && offer.upgrade ? offer.upgrade.price_cents : 5000)}`}
                  </button>
                </div>
              )}

              {!upgradeMode && offer && !(access && access.unlimited) && (offer.kinds || []).map(k => (
                <div key={k.kind} data-testid={`event-pass-kind-${k.kind}`}
                  style={{ border: '1px solid #d1d5db', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>{(PASS_KIND_TEXT[k.kind] || {}).title || k.label} · {passMoney(k.price_cents)}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{(PASS_KIND_TEXT[k.kind] || {}).sub || ''}</div>
                  </div>
                  <button type="button" disabled={!!busy || !k.configured} onClick={() => buy(k.kind)} style={btn(true)}>
                    {busy === k.kind ? 'Opening checkout…' : k.configured ? 'Buy' : 'Not available yet'}
                  </button>
                </div>
              ))}

              {!(access && access.unlimited) && <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.55 }}>
                One-time purchase, no subscription. Good for one event within 6 months of purchase — if it rains out, the pass moves with your new date. After the event, results and the leaderboard stay viewable for 30 days.
                Golfers who aren’t regular players join as guests: free, and they don’t count toward your plan.
                Cancel before pairings are saved and we’ll refund it.
              </div>}
            </>
          )}

          {error && <div style={{ color: '#b91c1c', fontSize: 14 }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={close} disabled={!!busy && busy !== 'confirm'} style={btn(false)}>{done ? 'Done' : 'Close'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// The pass line under the Set up event header: what covers this event. Nothing for
// an org with events included (no pass involved).
const EventPassBadge = ({ teetimeId }) => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let off = false;
    eventPassRequest('/event-pass').then((d) => { if (!off) setInfo(d); }).catch(() => {});
    return () => { off = true; };
  }, [teetimeId]);
  if (!info || !info.access || info.access.unlimited) return null;
  const pass = teetimeId ? (info.access.passes || []).find(p => p.teetime_id === teetimeId && p.status !== 'refunded') : null;
  const line = { marginTop: 8, fontSize: 12.5, background: 'rgba(255,255,255,.14)', borderRadius: 8, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
  const link = { background: 'none', border: 'none', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', padding: 0, textDecoration: 'underline' };
  if (pass) {
    const st = pass.status === 'read_only' ? ` · final — viewable until ${fmtPassDate(pass.read_only_until)}`
      : pass.status === 'closed' ? ' · closed' : ` · good through ${fmtPassDate(pass.expires_at)}`;
    return (
      <div style={line} data-testid="event-pass-badge">
        <span>🎟️ Event Pass · up to {pass.size_cap} golfers{st}</span>
        {pass.can_upgrade && <button type="button" style={link} onClick={() => openEventPass({ code: 'EVENT_FIELD_CAP', passId: pass.id, teetimeId, message: 'Upgrade this event’s pass to a field of up to 250 golfers.' })}>Upgrade to 250</button>}
      </div>
    );
  }
  if (info.access.available) {
    return <div style={line} data-testid="event-pass-badge">🎟️ Your Event Pass (up to {info.access.available.size_cap} golfers) is used when you save this event.</div>;
  }
  if (info.access.allowance) return <div style={line} data-testid="event-pass-badge">🎟️ This uses the one event a year included with your League plan.</div>;
  return null;
};

// EventSetupModal (Slice 3a) — the dedicated event setup surface, opened from the
// "Set up event" button on the create bar and the Edit Tee Time modal. Controlled:
// it edits a { name, description, rsvp_mode } value and returns it via onSave; the
// caller owns persistence (create flow / event reconcile). Games stay in the Games
// tab (referenced read-only here); flights and team registration are later slices,
// so "As a team" is shown disabled and rsvp_mode is individual for now.
// `date` (the round's date) only feeds the year in the address preview.
// Event desk access (Sept 17, 2026): the organizer's controls for the code that
// opens this event's desk. Acts immediately (not staged with Save event) — a code
// is a permission, not an event field. GET/POST/DELETE /api/events/:id/desk.
const deskAccessRequest = async (eventId, method) => {
  const headers = {};
  const t = getAuthToken();
  if (t) headers['Authorization'] = `Bearer ${t}`;
  const r = await fetch(`${API_URL}/events/${eventId}/desk`, { method, headers, credentials: 'include' });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `The server answered ${r.status}.`);
  return d;
};
// ============================================================================
// EVENT FEES (Oct 4, 2026 — Event_Ready_Check_Plan_v1.md slice 2b)
// Optional. Dennis: "Not every event will have mulligans. And not every event will
// collect at check-in." An entry fee amount and up to three optional items (label,
// amount, how many one golfer may buy). Blank = no fee columns anywhere. Saves on
// its own button, like the desk code — not with the event form. Each golfer's
// payments are ticked on the Check-in tab (ahead of time if paid outside TTO) and
// print on the check-in sheet. TTO records what was collected; it never handles money.
// ============================================================================
const EventFeesPanel = ({ eventId }) => {
  const { addToast } = useToast();
  const [entry, setEntry] = useState('');
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const headers = (json) => { const h = {}; const t = getAuthToken(); if (t) h['Authorization'] = `Bearer ${t}`; if (json) h['Content-Type'] = 'application/json'; return h; };
  const apply = (fees) => {
    setEntry(fees && fees.entry ? String(fees.entry.amount) : '');
    setItems(fees && Array.isArray(fees.items) ? fees.items.map(it => ({ id: it.id, label: it.label, amount: String(it.amount), max_qty: String(it.max_qty || 1) })) : []);
    setDirty(false);
  };
  useEffect(() => {
    let off = false;
    fetch(`${API_URL}/events/${eventId}/fees`, { credentials: 'include', headers: headers() })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (off) return; if (ok) { apply(d.fees); setLoaded(true); } else setError(d.error || 'Fees could not load.'); })
      .catch(() => { if (!off) setError('Network error — fees could not load.'); });
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);
  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API_URL}/events/${eventId}/fees`, {
        method: 'PUT', credentials: 'include', headers: headers(true),
        body: JSON.stringify({ entry: { amount: entry }, items: items.map(it => ({ id: it.id, label: it.label, amount: it.amount, max_qty: it.max_qty })) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Fees did not save.'); return; }
      apply(d.fees);
      addToast(d.fees ? 'Fees saved' : 'Fees cleared — no fee columns on check-in', 'success');
    } catch (e) { setError('Network error — fees did not save.'); }
    finally { setBusy(false); }
  };
  const setItem = (i, k, v) => { setItems(list => list.map((it, j) => (j === i ? { ...it, [k]: v } : it))); setDirty(true); };
  const box = { padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', minWidth: 0 };
  const money = (props) => (
    <div style={{ position: 'relative', width: 96, flex: '0 0 auto' }}>
      <span style={{ position: 'absolute', left: 10, top: 9, fontSize: 14, color: '#6b7280' }}>$</span>
      <input inputMode="decimal" {...props} style={{ ...box, width: '100%', paddingLeft: 20 }} />
    </div>
  );
  const linkBtn = { background: 'none', border: 'none', color: '#2d6a4f', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0 };
  return (
    <div data-testid="event-fees">
      <Label>Fees (optional) <InfoTip text="Only if you want to track who has paid. Leave it blank and check-in shows no fee columns. Tick a golfer's box ahead of time when he paid outside TTO — it prints already ticked on the check-in sheet, so whoever is at the table knows to collect or not. TTO records what was collected; it doesn't take payments." /></Label>
      {!loaded && !error && <div style={{ fontSize: 13, color: '#6b7280' }}>Loading…</div>}
      {loaded && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: '#374151', width: 130 }}>Entry fee</span>
            {money({ value: entry, placeholder: 'none', 'aria-label': 'Entry fee amount', onChange: (e) => { setEntry(e.target.value); setDirty(true); } })}
            <span style={{ fontSize: 12, color: '#6b7280' }}>per golfer</span>
          </div>
          {items.map((it, i) => (
            <div key={it.id || `new${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <input value={it.label} placeholder="e.g. Mulligans" maxLength={40} aria-label="Optional item name"
                onChange={(e) => setItem(i, 'label', e.target.value)} style={{ ...box, width: 130 }} />
              {money({ value: it.amount, placeholder: '0', 'aria-label': `${it.label || 'Item'} amount`, onChange: (e) => setItem(i, 'amount', e.target.value) })}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#4b5563' }}>Up to
                <input type="number" min={1} max={20} value={it.max_qty} aria-label={`${it.label || 'Item'}: most one golfer may buy`}
                  onChange={(e) => setItem(i, 'max_qty', e.target.value)} style={{ ...box, width: 60 }} />
                per golfer</label>
              <button type="button" style={{ ...linkBtn, color: '#b91c1c' }} onClick={() => { setItems(list => list.filter((_, j) => j !== i)); setDirty(true); }}>Remove</button>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {items.length < 3 && <button type="button" style={linkBtn} onClick={() => { setItems(list => [...list, { id: '', label: '', amount: '', max_qty: '1' }]); setDirty(true); }}>+ Add an optional item</button>}
            {dirty && <Button type="button" variant="outline" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save fees'}</Button>}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.45 }}>Up to 1 shows a tick box on check-in; more than 1 shows a count. Payments are ticked on the Ready check's Check-in tab.</div>
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{error}</div>}
    </div>
  );
};

// Results recap on/off for one event (Oct 4, 2026 — Event_Recap_Plan_v1.md step 4, §7).
// Default on. Saves the moment it's ticked, like the fees and the desk code. The org-wide
// recap setting governs group rounds only; this governs this event.
const EventRecapSettingPanel = ({ eventId }) => {
  const { addToast } = useToast();
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = (json) => { const h = {}; const t = getAuthToken(); if (t) h['Authorization'] = `Bearer ${t}`; if (json) h['Content-Type'] = 'application/json'; return h; };
  useEffect(() => {
    let off = false;
    fetch(`${API_URL}/events/${eventId}/recap-setting`, { credentials: 'include', headers: headers() })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (!off && d) setOn(d.recap_enabled !== false); })
      .catch(() => {});
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);
  const save = async (next) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/events/${eventId}/recap-setting`, { method: 'PUT', credentials: 'include', headers: headers(true), body: JSON.stringify({ recap_enabled: next }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { addToast(d.error || 'The recap setting did not save', 'error'); return; }
      setOn(d.recap_enabled !== false);
      addToast(d.recap_enabled !== false ? 'A results recap will be written when you finalize' : 'No results recap for this event', 'success');
    } catch (e) { addToast('Network error — the recap setting did not save', 'error'); }
    finally { setBusy(false); }
  };
  if (on === null) return null;
  return (
    <div data-testid="event-recap-setting">
      <Label>Results recap <InfoTip text="When the round is finalized, TTO writes a short results story — winners, ties and how they were decided, closest to the pin, the best shots. It shows in the player app, can be emailed to the field, and once posted stays on the event's leaderboard address for a year. You can edit it or choose Don't post on the Scoring tab." /></Label>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#374151', cursor: busy ? 'default' : 'pointer' }}>
        <input type="checkbox" checked={on} disabled={busy} onChange={(e) => save(e.target.checked)} style={{ width: 18, height: 18, marginTop: 1 }} />
        <span>Write a results recap for this event<span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginTop: 2 }}>{on ? 'Written when you finalize. Edit it or choose Don’t post on the Scoring tab.' : 'Off: no recap, no results email, no results page.'}</span></span>
      </label>
    </div>
  );
};

// ============================================================================
// WHO SIGNS UP (Oct 28, 2026 — team sign-up through the invite link; Dennis: "I think build
// now"). One golfer at a time (what the invite link always did), or a team of 2, 3 or 4: the
// person who opens the link signs up as captain with up to size − 1 teammates, names now or
// later. Saves at once, like the desk code. Can't change once a team has signed up online.
// GET/PUT /api/events/:id/signup. Not shown on the Event Desk.
// ============================================================================
const EventSignupPanel = ({ eventId }) => {
  const { addToast } = useToast();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const headers = (json) => { const h = {}; const t = getAuthToken(); if (t) h['Authorization'] = `Bearer ${t}`; if (json) h['Content-Type'] = 'application/json'; return h; };
  useEffect(() => {
    if (!eventId) return undefined;
    let off = false;
    fetch(`${API_URL}/events/${eventId}/signup`, { credentials: 'include', headers: headers() })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (off) return; if (ok) setInfo(d); else setError(d.error || 'This setting could not load.'); })
      .catch(() => { if (!off) setError('Network error — this setting could not load.'); });
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);
  const choose = async (size) => {
    if (!info || busy || size === info.team_size) return;
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API_URL}/events/${eventId}/signup`, { method: 'PUT', credentials: 'include', headers: headers(true), body: JSON.stringify({ team_size: size }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'This setting did not save.'); return; }
      setInfo(d);
      addToast(size === 1 ? 'Golfers sign up one at a time' : `Golfers sign up as teams of ${size}`, 'success');
    } catch (e) { setError('Network error — this setting did not save.'); }
    finally { setBusy(false); }
  };
  const tip = "How your invite link (🎟️ Invite on the tee time) and the event page's Sign up button work. One at a time: each golfer signs himself up; you team them up later. As a team: the golfer who opens the link signs up as captain with his teammates — names now or later. Teammates aren't emailed; they get your welcome email.";
  if (!eventId) {
    return (
      <div data-testid="event-signup">
        <Label>Who signs up <InfoTip text={tip} /></Label>
        <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.45 }}>One golfer at a time. To have captains sign up whole teams, save the event first, then open Set up event again.</div>
      </div>
    );
  }
  const options = [{ size: 1, label: 'One at a time' }, { size: 2, label: 'Team of 2' }, { size: 3, label: 'Team of 3' }, { size: 4, label: 'Team of 4' }];
  const cur = info ? info.team_size : null;
  const note = { fontSize: 12.5, color: '#4b5563', marginTop: 8, lineHeight: 1.45 };
  return (
    <div data-testid="event-signup">
      <Label>Who signs up <InfoTip text={tip} /></Label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {options.map(o => {
          const on = cur === o.size;
          const off = !info || busy || (info.locked && !on);
          return (
            <button key={o.size} type="button" data-testid={`event-signup-${o.size}`} disabled={off} onClick={() => choose(o.size)}
              style={{ minHeight: 44, padding: '8px 14px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: off ? 'default' : 'pointer',
                border: on ? '2px solid #2d6a4f' : '1px solid #d1d5db', background: on ? '#e8f5ee' : '#fff', color: on ? '#1b4332' : (off ? '#9ca3af' : '#374151') }}>
              {on ? '✓ ' : ''}{o.label}
            </button>
          );
        })}
      </div>
      {info && info.team_size === 1 && (
        <div style={note}>Each golfer signs himself up through your invite link. You can team them up later.</div>
      )}
      {info && info.team_size > 1 && (
        <div style={note}>
          The golfer who opens your invite link signs up as captain with up to {info.team_size - 1} teammate{info.team_size === 2 ? '' : 's'} — names now, or held spots he fills in later from his team page. He can also ask to be paired up. Teammates aren't emailed; they get your welcome email. A team that doesn't fit in the field is turned away, never split.
        </div>
      )}
      {info && info.team_size > 1 && info.game_team_size && info.game_team_size !== info.team_size && (
        <div style={{ ...note, color: '#b45309', fontWeight: 600 }}>⚠️ Your scramble game is set for teams of {info.game_team_size}.</div>
      )}
      {info && info.team_size > 1 && !info.invite_on && (
        <div style={{ ...note, color: '#b45309', fontWeight: 600 }}>The invite link is off. Turn it on with 🎟️ Invite on the tee time so captains can sign up.</div>
      )}
      {info && info.locked && (
        <div style={note}>🔒 {info.online_teams} team{info.online_teams === 1 ? ' has' : 's have'} signed up online, so this can't change now.</div>
      )}
      {error && <div style={{ ...note, color: '#b91c1c', fontWeight: 600 }}>{error}</div>}
    </div>
  );
};

const EventDeskAccessPanel = ({ eventId }) => {
  const { addToast } = useToast();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmNew, setConfirmNew] = useState(false);
  const [copied, setCopied] = useState("");
  useEffect(() => {
    let off = false;
    deskAccessRequest(eventId, 'GET')
      .then((d) => { if (!off) setInfo(d); })
      .catch((e) => { if (!off) setError(e.message); });
    return () => { off = true; };
  }, [eventId]);
  const act = async (method) => {
    setBusy(true); setError("");
    try {
      setInfo(await deskAccessRequest(eventId, method));
      setConfirmNew(false);
      addToast(method === 'POST' ? 'Event desk code ready' : 'Event desk code turned off');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const copy = (text, what) => {
    if (!navigator.clipboard) { addToast("Could not copy — select it and copy it instead", "error"); return; }
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(what); setTimeout(() => setCopied(""), 2000); })
      .catch(() => addToast("Could not copy — select it and copy it instead", "error"));
  };
  const until = info && info.valid_until ? new Date(info.valid_until) : null;
  const lastDay = until ? new Date(until.getTime() - 1) : null;
  const fmtDay = (d) => d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  const fmtWhen = (s) => { const d = new Date(s); return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`; };
  const shortUrl = info && info.url ? info.url.replace(/^https?:\/\//, "") : "";
  const linkBtn = { background: "none", border: "none", color: "#2d6a4f", fontWeight: 600, fontSize: 12, cursor: "pointer", padding: 0 };
  return (
    <div data-testid="event-desk-access">
      <Label>Event desk access <InfoTip text="Give pro shop staff or a helper this code and they can run this event for you — players, pairings, games, scores, printing and the TV — from ttogolf.com. They see this event only: not your other tee times, players' settings or your club settings, and they can't cancel or delete the event. Their name shows below when they open the desk. New code or Turn off locks the old code out at once." /></Label>
      {!info && !error && <div style={{ fontSize: 13, color: "#6b7280" }}>Loading…</div>}
      {info && !info.code && (
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.45 }}>Staff can run this event — players, pairings, games, scores, printing and the TV — with a code you give them. They see this event only.</div>
          {info.expired
            ? <div style={{ fontSize: 13, color: "#92400e" }}>The event desk closed {lastDay ? `after ${fmtDay(lastDay)}` : ""}.</div>
            : <div><Button type="button" variant="outline" disabled={busy} onClick={() => act('POST')}>{busy ? "Creating…" : "🗝️ Create event desk code"}</Button></div>}
        </div>
      )}
      {info && info.code && (
        <div style={{ border: "1px solid #bbf7d0", background: "#f0fdf4", borderRadius: 10, padding: "10px 12px", display: "grid", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 22, fontWeight: 800, letterSpacing: 2, color: "#14532d" }}>{info.code}</div>
            <div style={{ display: "flex", gap: 12 }}>
              <button type="button" style={linkBtn} onClick={() => copy(info.code, "code")}>{copied === "code" ? "✓ Copied" : "Copy code"}</button>
              {info.url && <button type="button" style={linkBtn} onClick={() => copy(info.url, "link")}>{copied === "link" ? "✓ Copied" : "Copy link"}</button>}
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.45 }}>
            Staff open <strong>{shortUrl || "ttogolf.com"}</strong> — or type the code at ttogolf.com — and enter their name.{" "}
            {info.expired ? <span style={{ color: "#92400e", fontWeight: 600 }}>This code has expired.</span> : (lastDay ? <>Works through {fmtDay(lastDay)}.</> : null)}
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
            {confirmNew ? (
              <>
                <span style={{ color: "#92400e" }}>The current code stops working at once.</span>
                <button type="button" style={{ ...linkBtn, color: "#b45309" }} disabled={busy} onClick={() => act('POST')}>{busy ? "Working…" : "Make a new code"}</button>
                <button type="button" style={{ ...linkBtn, color: "#6b7280" }} onClick={() => setConfirmNew(false)}>Keep this one</button>
              </>
            ) : (
              <>
                <button type="button" style={linkBtn} disabled={busy} onClick={() => setConfirmNew(true)}>New code</button>
                <button type="button" style={{ ...linkBtn, color: "#b91c1c" }} disabled={busy} onClick={() => act('DELETE')}>Turn off</button>
              </>
            )}
          </div>
        </div>
      )}
      {info && Array.isArray(info.sessions) && info.sessions.length > 0 && (
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6, lineHeight: 1.5 }}>
          Opened by:{" "}
          {info.sessions.map((s, i) => (
            <span key={i}>{i ? " · " : ""}<strong style={{ color: s.current_code ? "#374151" : "#9ca3af" }}>{s.name}</strong> {fmtWhen(s.started_at)}{s.current_code ? "" : " (earlier code)"}</span>
          ))}
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>{error}</div>}
    </div>
  );
};

// ============================================================================
// GUEST LIST (Sept 20, 2026) — a panel over the dashboard
//
// The organizer's view of a social event: who is coming, who is waiting, who is
// on a club account, and how it lines up against the golf field. Its own page
// with its own address, reached from the Reception section in Event Setup, so
// none of it lands on the golf screens.
//
// Organizer-only by the API, not by this page: every endpoint scopes on the
// row's own group_id. A 403 here means the server said no, which is the answer
// we want — never a client-side guess about who may look.
//
// Edit tokens are never returned on these endpoints, so they cannot leak from
// this screen. Do not add them.
// ============================================================================
const SocialEventListPage = ({ socialEventId = null }) => {
  // Given an id, it is embedded in the dashboard; otherwise it would be reading
  // a route param, which no longer happens — kept so the component has one way
  // to be wrong rather than two.
  const id = socialEventId;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [rowForm, setRowForm] = useState({});
  const [savingRow, setSavingRow] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [filter, setFilter] = useState("all");
  const [printing, setPrinting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "", covers: 1, guest_names: "", dietary: "", is_member: false, member_note: "" });
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState("");
  const [addNote, setAddNote] = useState("");
  const [copied, setCopied] = useState(false);
  // Writing to the guest list. Strictly this list: the audience choices below
  // are the only ones the endpoint accepts, and none of them widen past this
  // event. Nothing here can reach a golfer who did not reserve a place.
  const [messaging, setMessaging] = useState(false);
  const [msgForm, setMsgForm] = useState({ audience: "confirmed", subject: "", body: "" });
  const [picked, setPicked] = useState(() => new Set());
  const [msgBusy, setMsgBusy] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [msgResult, setMsgResult] = useState(null);

  const authed = async (path, options = {}) => {
    const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
    const token = await getValidAuthToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API_URL}${path}`, { credentials: "include", ...options, headers });
    const body = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, body };
  };

  // The PDF endpoint is authenticated, so it cannot simply be linked: the token
  // has to go in a header and the bytes come back as a blob. The window is
  // opened on the CLICK, before the await, or the browser treats it as a popup
  // and blocks it. Same shape as openRoundPdf uses for the scorecards.
  const printList = async () => {
    const w = window.open("", "_blank");
    if (!w) { setError("Your browser blocked the print window. Allow pop-ups for this site and try again."); return; }
    const say = (html) => { try { w.document.body.innerHTML = html; } catch (e) { /* window closed */ } };
    say('<p style="font-family:sans-serif;padding:24px;color:#374151">Building the guest list…</p>');
    setPrinting(true);
    try {
      const headers = {};
      const token = await getValidAuthToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const r = await fetch(`${API_URL}/social-events/${id}/print.pdf`, { credentials: "include", headers });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        say(`<div style="font-family:sans-serif;padding:24px;max-width:560px"><h2 style="margin:0 0 8px;font-size:18px;color:#991b1b">Couldn't build the guest list</h2><p style="color:#374151;line-height:1.5">${(d.error || `The server answered ${r.status}.`)}</p></div>`);
        return;
      }
      const blob = await r.blob();
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const file = new File([blob], m ? m[1] : "guest-list.pdf", { type: "application/pdf" });
      w.location.href = URL.createObjectURL(file);
    } catch (e) {
      say('<p style="font-family:sans-serif;padding:24px;color:#991b1b">Could not reach the server. Try again.</p>');
    } finally {
      setPrinting(false);
    }
  };

  const load = async () => {
    const { ok, status, body } = await authed(`/social-events/${id}`);
    if (!ok) {
      setError(status === 403 ? "This list belongs to another organization."
        : status === 401 ? "Please sign in to see this list."
        : body.error || "Could not load the guest list.");
      setData(null);
    } else {
      setData(body.data); setError("");
    }
    setLoading(false);
  };

  useEffect(() => { let c = false; (async () => { if (!c) await load(); })(); return () => { c = true; }; }, [id]);

  const blankAdd = () => setAddForm({ name: "", email: "", phone: "", covers: 1, guest_names: "", dietary: "", is_member: false, member_note: "" });

  // Someone phoned, or caught you at the bar. Goes through the same placement
  // rule as the public form, so a hand-added guest never quietly pushes the room
  // past what you can seat — if it is full you are told, and you can change the
  // status on the row if you mean to seat them anyway.
  const addReservation = async () => {
    if (!addForm.name.trim() || !addForm.email.trim()) { setAddError("Name and email are both needed."); return; }
    if (!String(addForm.phone || "").trim()) { setAddError("A phone number is needed so you can reach them."); return; }
    setAddBusy(true); setAddError(""); setAddNote("");
    const { ok, body } = await authed(`/social-events/${id}/rsvps`, {
      method: "POST",
      body: JSON.stringify({ ...addForm, covers: Number(addForm.covers), email: addForm.email.trim().toLowerCase() }),
    });
    setAddBusy(false);
    if (!ok) { setAddError(body.error || "Could not add that reservation."); return; }
    const placed = body.data;
    setAddNote(placed.status === "waitlist"
      ? `${placed.name} is on the waitlist at #${placed.waitlist_position} — the event is full. Change the status on their row if you mean to seat them anyway.`
      : `${placed.name} is confirmed. A confirmation with their own change link is on its way to ${placed.email}.`);
    blankAdd();
    setAdding(false);
    await load();
  };

  const saveRow = async (row) => {
    setSavingRow(true);
    const { ok, body } = await authed(`/social-events/${id}/rsvps/${row.id}`, {
      method: "PUT", body: JSON.stringify(rowForm),
    });
    setSavingRow(false);
    if (!ok) { setError(body.error || "Could not save that reservation."); return; }
    setEditingRow(null);
    await load();
  };

  const setMember = async (row, value) => {
    const { ok, body } = await authed(`/social-events/${id}/rsvps/${row.id}`, {
      method: "PUT", body: JSON.stringify({ is_member: value }),
    });
    if (!ok) { setError(body.error || "Could not save that."); return; }
    await load();
  };

  const cancelRow = async (row) => {
    const { ok, body } = await authed(`/social-events/${id}/rsvps/${row.id}`, {
      method: "PUT", body: JSON.stringify({ status: "cancelled" }),
    });
    setConfirmCancel(null);
    if (!ok) { setError(body.error || "Could not cancel that reservation."); return; }
    await load();
  };

  const togglePicked = (rowId) => setPicked((s) => {
    const next = new Set(s);
    if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
    return next;
  });

  const sendGuestMessage = async (recipientCount) => {
    if (!msgForm.subject.trim()) { setMsgError("A subject is needed."); return; }
    if (!msgForm.body.trim()) { setMsgError("Write something to send."); return; }
    if (recipientCount === 0) { setMsgError("Nobody on this list matches that choice."); return; }
    setMsgBusy(true); setMsgError(""); setMsgResult(null);
    const { ok, body } = await authed(`/social-events/${id}/message`, {
      method: "POST",
      body: JSON.stringify({
        audience: msgForm.audience,
        subject: msgForm.subject.trim(),
        body: msgForm.body,
        rsvpIds: msgForm.audience === "selected" ? [...picked] : [],
      }),
    });
    setMsgBusy(false);
    if (!ok) { setMsgError(body.error || "Could not send that message."); return; }
    setMsgResult(body.data);
    setMsgForm((f) => ({ ...f, subject: "", body: "" }));
    setPicked(new Set());
    setMessaging(false);
  };

  const page = { background: "#f3f4f6", padding: 16 };
  const th = { textAlign: "left", fontSize: 12, color: "#6b7280", fontWeight: 600, padding: "6px 8px", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" };
  const td = { fontSize: 13, padding: "8px", borderBottom: "1px solid #f3f4f6", verticalAlign: "top" };
  const field = { padding: 6, border: "1px solid #e5e7eb", borderRadius: 8, width: "100%", fontSize: 13, boxSizing: "border-box" };

  if (loading) return <div style={page}><Card>Loading the guest list…</Card></div>;
  if (error && !data) return <div style={page}><Card><div style={{ fontWeight: 700, marginBottom: 6 }}>Cannot show this list</div><div style={{ color: "#6b7280" }}>{error}</div></Card></div>;

  const { event, counts, buckets, rows } = data;
  const capped = counts.capacity !== null && counts.capacity !== undefined;
  const live = rows.filter((r) => r.status !== "cancelled");
  // Who a send would actually reach, worked out from the same rules the server
  // applies, so the number on the button is the number that gets mail. Someone
  // who cancelled is never in it — they are not on `live` at all.
  const picking = messaging && msgForm.audience === "selected";
  const msgRecipients = msgForm.audience === "confirmed" ? live.filter((r) => r.status === "confirmed")
    : msgForm.audience === "waitlist" ? live.filter((r) => r.status === "waitlist")
    : msgForm.audience === "selected" ? live.filter((r) => picked.has(r.id))
    : live;
  const shown = filter === "all" ? rows
    : filter === "waitlist" ? rows.filter((r) => r.status === "waitlist")
    : filter === "cancelled" ? rows.filter((r) => r.status === "cancelled")
    : rows.filter((r) => r.status === "confirmed");

  const stat = (label, value, tone) => (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "10px 14px", minWidth: 120 }}>
      <div style={{ fontSize: 12, color: "#6b7280" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: tone || "#111827" }}>{value}</div>
    </div>
  );

  return (
    <div style={page}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{event.title}</div>
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            {event.starts_at ? new Date(event.starts_at).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" }) : null}
            {event.location ? ` · ${event.location}` : ""}
          </div>
          {/* The signup link, here as well as in Event Setup: this is the screen
              you are on when someone says "send me the link". */}
          {event.public_slug && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>Signup link</span>
              <code data-testid="signup-link" style={{ fontSize: 12.5, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "3px 8px" }}>
                ttogolf.com/rsvp/{event.public_slug}
              </code>
              <button type="button"
                      onClick={() => {
                        // Clipboard can be refused (insecure context, permissions).
                        // The address is on screen either way — never let a copy
                        // button throw and take the list down with it.
                        try {
                          navigator.clipboard.writeText(`https://ttogolf.com/rsvp/${event.public_slug}`);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 1800);
                        } catch (e) { /* on screen regardless */ }
                      }}
                      style={{ background: "none", border: "none", color: "#2d6a4f", fontWeight: 600, fontSize: 12.5, cursor: "pointer", padding: 0 }}>
                {copied ? "Copied" : "Copy"}
              </button>
              <a href={`https://ttogolf.com/rsvp/${event.public_slug}`} target="_blank" rel="noreferrer"
                 style={{ color: "#2d6a4f", fontWeight: 600, fontSize: 12.5, textDecoration: "none" }}>Open ↗</a>
            </div>
          )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="outline" onClick={() => { setAdding((v) => !v); setAddError(""); setAddNote(""); }}
                    data-testid="add-reservation-toggle"
                    style={{ fontSize: 13, color: "#166534", borderColor: "#bbf7d0", background: "#f0fdf4" }}>
              {adding ? "Cancel" : "＋ Add reservation"}
            </Button>
            <Button variant="outline" onClick={printList} disabled={printing}
                    data-testid="print-guest-list"
                    style={{ fontSize: 13, color: "#166534", borderColor: "#bbf7d0", background: "#f0fdf4" }}>
              {printing ? "Building…" : "🖨️ Print"}
            </Button>
            <Button variant="outline" onClick={() => { setMessaging((v) => !v); setMsgError(""); setMsgResult(null); }}
                    data-testid="message-guests-toggle"
                    style={{ fontSize: 13, color: "#166534", borderColor: "#bbf7d0", background: "#f0fdf4" }}>
              {messaging ? "Close" : "✉️ Message guests"}
            </Button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          {stat("Confirmed", capped ? `${counts.confirmed_covers} / ${counts.capacity}` : counts.confirmed_covers)}
          {capped && stat("Still free", Math.max(0, counts.remaining), counts.remaining <= 0 ? "#b45309" : undefined)}
          {stat("Waitlisted", `${counts.waitlist_covers}`)}
          {counts.members !== null && counts.members !== undefined && stat("On a club account", counts.members)}
        </div>

        {counts.over_by > 0 && (
          <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "#92400e", marginBottom: 14 }}>
            <strong>{counts.over_by} more confirmed than you can seat.</strong> Nobody has been moved — everyone who was told they are in is still in. Raise the number you can seat in Event Setup, or call someone.
          </div>
        )}

        {/* The three buckets: the whole reason this hangs off the tee time. */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 22, flexWrap: "wrap", fontSize: 14 }}>
            <div><strong>{buckets.both}</strong> <span style={{ color: "#6b7280" }}>playing and coming</span></div>
            <div><strong>{buckets.golf_only}</strong> <span style={{ color: "#6b7280" }}>playing only</span></div>
            <div><strong>{buckets.reception_only}</strong> <span style={{ color: "#6b7280" }}>coming only</span></div>
          </div>
        </Card>

        {addNote && (
          <div style={{ background: "#ecfdf5", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "#166534", marginBottom: 14 }}>{addNote}</div>
        )}

        {adding && (
          <Card style={{ marginBottom: 14 }} data-testid="add-reservation-form">
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Add a reservation</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 80px", gap: 8, marginBottom: 8 }}>
              <div><Label>Name</Label><input style={field} value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div><Label>Email</Label><input style={field} type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div><Label>Phone</Label><input style={field} value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} /></div>
              <div><Label>Party</Label><input style={field} type="number" min="1" value={addForm.covers} onChange={(e) => setAddForm((f) => ({ ...f, covers: e.target.value }))} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div><Label>Guests, one per line</Label><textarea style={{ ...field, fontFamily: "inherit" }} rows={2} value={addForm.guest_names} onChange={(e) => setAddForm((f) => ({ ...f, guest_names: e.target.value }))} /></div>
              <div><Label>Dietary</Label><textarea style={{ ...field, fontFamily: "inherit" }} rows={2} value={addForm.dietary} onChange={(e) => setAddForm((f) => ({ ...f, dietary: e.target.value }))} /></div>
            </div>
            {event.ask_club_account && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!addForm.is_member} onChange={(e) => setAddForm((f) => ({ ...f, is_member: e.target.checked }))} />
                Charging a club account
                {addForm.is_member && (
                  <input style={{ ...field, width: 200 }} placeholder="Member name or number"
                         value={addForm.member_note} onChange={(e) => setAddForm((f) => ({ ...f, member_note: e.target.value }))} />
                )}
              </label>
            )}
            {addError && <div style={{ fontSize: 13, color: "#b91c1c", marginBottom: 8 }}>{addError}</div>}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Button onClick={addReservation} disabled={addBusy} style={{ background: "#2d6a4f", color: "#fff", fontSize: 13 }}>
                {addBusy ? "Adding…" : "Add reservation"}
              </Button>
              <span style={{ fontSize: 12, color: "#6b7280" }}>They get a confirmation email with their own link to change or cancel.</span>
            </div>
          </Card>
        )}

        {msgResult && (
          <div style={{ background: "#ecfdf5", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "#166534", marginBottom: 14 }} data-testid="message-result">
            <strong>Sent to {msgResult.sent} {msgResult.sent === 1 ? "person" : "people"} on this guest list.</strong>
            {msgResult.failed > 0 && (
              <div style={{ marginTop: 6, color: "#b45309" }}>
                {msgResult.failed} did not go through: {msgResult.failures.map((f) => f.name || f.email).join(", ")}. Their reservations are untouched — try again or reach them another way.
              </div>
            )}
          </div>
        )}

        {messaging && (
          <Card style={{ marginBottom: 14 }} data-testid="message-guests-form">
            <div style={{ fontWeight: 700, marginBottom: 2 }}>Message this guest list</div>
            <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 10 }}>
              Goes only to people who reserved a place at {event.title}. It never reaches anyone who is just playing golf, and it carries nothing about the round.
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {[["confirmed", `Confirmed (${live.filter((r) => r.status === "confirmed").length})`],
                ["waitlist", `Waitlist (${live.filter((r) => r.status === "waitlist").length})`],
                ["everyone", `Everyone on the list (${live.length})`],
                ["selected", "Pick people"]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => { setMsgForm((f) => ({ ...f, audience: k })); setMsgError(""); }}
                        data-testid={`audience-${k}`}
                        style={{ padding: "5px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer",
                                 border: msgForm.audience === k ? "1px solid #2d6a4f" : "1px solid #e5e7eb",
                                 background: msgForm.audience === k ? "#ecfdf5" : "#fff",
                                 color: msgForm.audience === k ? "#166534" : "#374151",
                                 fontWeight: msgForm.audience === k ? 600 : 400 }}>
                  {label}
                </button>
              ))}
            </div>

            {picking && (
              <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 10 }}>
                Tick the people you want in the table below. {picked.size} picked.
              </div>
            )}

            <div style={{ marginBottom: 8 }}>
              <Label>Subject</Label>
              <input style={field} data-testid="message-subject" value={msgForm.subject}
                     placeholder="Reception moved to 6:30"
                     onChange={(e) => setMsgForm((f) => ({ ...f, subject: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Message</Label>
              <textarea style={{ ...field, fontFamily: "inherit" }} rows={6} data-testid="message-body"
                        value={msgForm.body}
                        placeholder={"Plain words. Line breaks are kept."}
                        onChange={(e) => setMsgForm((f) => ({ ...f, body: e.target.value }))} />
            </div>

            {msgError && <div style={{ fontSize: 13, color: "#b91c1c", marginBottom: 8 }}>{msgError}</div>}

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Button onClick={() => sendGuestMessage(msgRecipients.length)} disabled={msgBusy || msgRecipients.length === 0}
                      data-testid="message-send"
                      style={{ background: msgRecipients.length === 0 ? "#9ca3af" : "#2d6a4f", color: "#fff", fontSize: 13 }}>
                {msgBusy ? "Sending…" : `Send to ${msgRecipients.length} ${msgRecipients.length === 1 ? "person" : "people"}`}
              </Button>
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                Everyone gets their own link to change or cancel, the same one that was in their confirmation.
              </span>
            </div>
          </Card>
        )}

        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "#b91c1c", marginBottom: 14 }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          {[["all", `Everyone (${rows.length})`], ["confirmed", `Confirmed (${rows.filter(r => r.status === "confirmed").length})`],
            ["waitlist", `Waitlist (${rows.filter(r => r.status === "waitlist").length})`],
            ["cancelled", `Cancelled (${rows.filter(r => r.status === "cancelled").length})`]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)}
                    style={{ padding: "5px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer",
                             border: filter === k ? "1px solid #2d6a4f" : "1px solid #e5e7eb",
                             background: filter === k ? "#ecfdf5" : "#fff",
                             color: filter === k ? "#166534" : "#374151", fontWeight: filter === k ? 600 : 400 }}>
              {label}
            </button>
          ))}
        </div>

        <Card style={{ padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {picking && <th style={{ ...th, width: 28 }}></th>}
                <th style={th}>Who</th>
                <th style={th}>Party</th>
                <th style={th}>Guests</th>
                <th style={th}>Dietary</th>
                {event.ask_club_account && <th style={th}>Club account</th>}
                <th style={th}>Status</th>
                <th style={th}>Last change</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td style={{ ...td, color: "#6b7280" }} colSpan={(event.ask_club_account ? 8 : 7) + (picking ? 1 : 0)}>
                  {rows.length === 0 ? "Nobody has signed up yet." : "Nothing in this view."}
                </td></tr>
              )}
              {shown.map((r) => {
                const isEditing = editingRow === r.id;
                const cancelled = r.status === "cancelled";
                return (
                  <tr key={r.id} style={cancelled ? { opacity: 0.55 } : undefined}>
                    {picking && (
                      <td style={td}>
                        {/* a cancelled reservation can never be a recipient, so it
                            is never offered as one */}
                        {!cancelled && (
                          <input type="checkbox" checked={picked.has(r.id)}
                                 data-testid={`pick-${r.id}`}
                                 onChange={() => togglePicked(r.id)} />
                        )}
                      </td>
                    )}
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div style={{ color: "#6b7280", fontSize: 12 }}>{r.email}</div>
                      {r.bucket === "both" && <div style={{ fontSize: 11, color: "#166534" }}>also playing</div>}
                    </td>
                    <td style={td}>
                      {isEditing
                        ? <input style={{ ...field, width: 60 }} type="number" min="1" value={rowForm.covers}
                                 onChange={(e) => setRowForm((f) => ({ ...f, covers: Number(e.target.value) }))} />
                        : r.covers}
                    </td>
                    <td style={{ ...td, maxWidth: 220 }}>
                      {isEditing
                        ? <textarea style={field} rows={3} value={rowForm.guest_names || ""}
                                    onChange={(e) => setRowForm((f) => ({ ...f, guest_names: e.target.value }))} />
                        : (
                          <div>
                            <div style={{ whiteSpace: "pre-wrap" }}>{r.guest_names || <span style={{ color: "#9ca3af" }}>—</span>}</div>
                            {/* names entered should equal covers - 1; a mismatch is a nudge, never a block */}
                            {!cancelled && r.guest_name_count !== r.covers - 1 && (
                              <div style={{ fontSize: 11, color: "#b45309" }}>{r.guest_name_count} of {r.covers - 1} named</div>
                            )}
                          </div>
                        )}
                    </td>
                    <td style={{ ...td, maxWidth: 180 }}>
                      {isEditing
                        ? <textarea style={field} rows={2} value={rowForm.dietary || ""}
                                    onChange={(e) => setRowForm((f) => ({ ...f, dietary: e.target.value }))} />
                        : (r.dietary || <span style={{ color: "#9ca3af" }}>—</span>)}
                    </td>
                    {event.ask_club_account && (
                      <td style={td}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: cancelled ? "default" : "pointer" }}>
                          <input type="checkbox" checked={!!r.is_member} disabled={cancelled}
                                 onChange={(e) => setMember(r, e.target.checked)} />
                          <span style={{ fontSize: 12, color: "#6b7280" }}>{r.member_note || ""}</span>
                        </label>
                      </td>
                    )}
                    <td style={td}>
                      {r.status === "confirmed" && <span style={{ color: "#166534", fontWeight: 600 }}>Confirmed</span>}
                      {r.status === "waitlist" && <span style={{ color: "#b45309", fontWeight: 600 }}>Waitlist #{r.waitlist_position}</span>}
                      {cancelled && <span style={{ color: "#6b7280" }}>Cancelled</span>}
                    </td>
                    <td style={{ ...td, color: "#6b7280", fontSize: 12 }}>
                      {r.change_note ? `${r.change_note}` : "—"}
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          <Button onClick={() => saveRow(r)} disabled={savingRow} style={{ background: "#2d6a4f", color: "#fff", fontSize: 12 }}>Save</Button>
                          <Button variant="outline" onClick={() => setEditingRow(null)} style={{ fontSize: 12 }}>Cancel</Button>
                        </div>
                      ) : confirmCancel === r.id ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          <Button onClick={() => cancelRow(r)} style={{ background: "#b91c1c", color: "#fff", fontSize: 12 }}>Cancel it</Button>
                          <Button variant="outline" onClick={() => setConfirmCancel(null)} style={{ fontSize: 12 }}>Keep</Button>
                        </div>
                      ) : cancelled ? null : (
                        <div style={{ display: "flex", gap: 10 }}>
                          <button type="button"
                                  onClick={() => { setEditingRow(r.id); setRowForm({ covers: r.covers, guest_names: r.guest_names || "", dietary: r.dietary || "" }); }}
                                  style={{ background: "none", border: "none", color: "#2d6a4f", fontWeight: 600, fontSize: 12, cursor: "pointer", padding: 0 }}>Edit</button>
                          <button type="button" onClick={() => setConfirmCancel(r.id)}
                                  style={{ background: "none", border: "none", color: "#b91c1c", fontSize: 12, cursor: "pointer", padding: 0 }}>Cancel</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
          {live.length} live {live.length === 1 ? "reservation" : "reservations"}. Guests change their own through the link in their confirmation email; anything you change here is marked as changed by the organizer.
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SOCIAL EVENT — the shortcut on the tee time card (Sept 20, 2026).
//
// Dennis, Sept 20: "seems too many steps to get to social event... Maybe the
// event tee time should show Social Event as a choice at the bottom of the tee
// time or some other way to get there quicker without cluttering the tee time."
//
// It sits in the card's action row beside Games / TV / Invite, which is what
// "at the bottom of the tee time" means and where every other choice already
// lives. Modelled on tvButton a few lines above it: computed per card, present
// only when it applies.
//
// The no-clutter half is why this renders NOTHING when the round has no social
// event, and never offers to create one — that is a once-per-event act and it
// belongs in Event Setup. A round without a dinner looks exactly as it did
// before this feature existed.
//
// It fetches per card, and only for event rounds. One indexed lookup on a
// screen that already issues several; if a club ever lists dozens of event
// rounds at once, fold the count into the tee times payload instead.
// ============================================================================
const SocialEventTeeTimeLink = ({ teetimeId, eventId }) => {
  const [state, setState] = useState(null);   // { event, counts } | null
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!teetimeId || !eventId) return;
      try {
        const headers = {};
        const token = await getValidAuthToken();
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/social-events/by-teetime/${teetimeId}`, { credentials: "include", headers });
        if (!res.ok) return;                 // no social event, or not ours — stay silent
        const body = await res.json();
        if (!cancelled && body && body.data) setState(body.data);
      } catch (e) {
        // A shortcut that cannot load is not worth an error on a golf screen.
      }
    })();
    return () => { cancelled = true; };
  }, [teetimeId, eventId]);

  if (!state) return null;

  const { event, counts } = state;
  const capped = counts && counts.capacity !== null && counts.capacity !== undefined;
  const count = counts ? (capped ? `${counts.confirmed_covers}/${counts.capacity}` : `${counts.confirmed_covers}`) : null;
  const over = counts && counts.over_by > 0;
  const waiting = counts && counts.waitlist_parties > 0;

  const title = [
    event.title,
    counts ? `${counts.confirmed_covers} confirmed${capped ? ` of ${counts.capacity}` : ""}` : null,
    waiting ? `${counts.waitlist_covers} on the waitlist` : null,
    over ? `${counts.over_by} more than you can seat` : null,
    "Opens the guest list",
  ].filter(Boolean).join(" · ");

  return (
    <>
    <Button
      variant="outline"
      onClick={() => setOpen(true)}
      title={title}
      data-testid="card-social-event-button"
      style={{
        fontSize: 12,
        color: over ? "#92400e" : "#166534",
        borderColor: over ? "#fcd34d" : "#bbf7d0",
        background: over ? "#fffbeb" : "#f0fdf4",
      }}
    >
      🥂 Social Event{count ? ` · ${count}` : ""}{waiting ? ` +${counts.waitlist_covers}` : ""}
    </Button>

    {/* In a panel over the dashboard, not a new tab: a cold page load has no
        organizer session (see the note on SocialEventListPage). */}
    {open && (
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", backdropFilter: "blur(4px)", zIndex: 60, padding: 20, overflowY: "auto" }}
        onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      >
        <div style={{ background: "#f3f4f6", borderRadius: 14, width: "100%", maxWidth: 1140, margin: "0 auto" }}
             data-testid="social-event-panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", borderBottom: "1px solid #e5e7eb", background: "#fff", borderRadius: "14px 14px 0 0" }}>
            <strong style={{ fontSize: 15 }}>🥂 Guest list</strong>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close"
                    style={{ background: "none", border: "none", fontSize: 20, lineHeight: 1, cursor: "pointer", color: "#6b7280", padding: 4 }}>
              ✕
            </button>
          </div>
          <SocialEventListPage socialEventId={event.id} />
        </div>
      </div>
    )}
    </>
  );
};

// ============================================================================
// SOCIAL EVENT, inside Event Setup (Sept 20, 2026).
//
// Deliberately generic vocabulary (Dennis, Sept 20): a socialEvent, a dinner, an
// auction, a prize presentation — the product must not assume which. The
// organizer names the specific thing in the title; the UI around it stays
// neutral. Same principle as flights, where nothing in the schema, API or UI
// contains the word "Senior".
//
// A social event is its OWN entity with its own endpoints, not a field on the
// golf event — so this section saves itself rather than riding the modal's
// onSave, and the golf screens stay exactly as they were.
//
// Dennis, Sept 20: "I don't want it to clutter or otherwise get in the way of
// the golf event info as that is the main thing." So: one collapsed line until
// there is a socialEvent, then a summary with the headcount visible without
// opening anything, and the reservation list lives on its own page.
//
// The signup address is ttogolf.com/rsvp/<address>, generated server-side in
// the same namespace as the event's leaderboard address.
// ============================================================================
const SocialEventSection = ({ teetimeId, eventId, eventName, deskMode = false }) => {
  const [loading, setLoading] = useState(true);
  const [socialEvent, setSocialEvent] = useState(null);
  const [counts, setCounts] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", starts_at: "", location: "", capacity: "",
    rsvp_deadline: "", payment_note: "", ask_club_account: false, club_account_prompt: "",
  });

  const authed = async (path, options = {}) => {
    const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
    const token = await getValidAuthToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API_URL}${path}`, { credentials: "include", ...options, headers });
    const body = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, body };
  };

  const toLocalInput = (iso, dateOnly = false) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return dateOnly ? ymd : `${ymd}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const fill = (r) => setForm({
    title: r.title || "",
    description: r.description || "",
    starts_at: toLocalInput(r.starts_at),
    location: r.location || "",
    capacity: r.capacity === null || r.capacity === undefined ? "" : String(r.capacity),
    rsvp_deadline: toLocalInput(r.rsvp_deadline, true),
    payment_note: r.payment_note || "",
    ask_club_account: !!r.ask_club_account,
    club_account_prompt: r.club_account_prompt || "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!teetimeId || !eventId || deskMode) { setLoading(false); return; }
      const { ok, body } = await authed(`/social-events/by-teetime/${teetimeId}`);
      if (cancelled) return;
      if (ok && body.data) { setSocialEvent(body.data.event); setCounts(body.data.counts); fill(body.data.event); }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [teetimeId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const signupUrl = socialEvent && socialEvent.public_slug
    ? `ttogolf.com/rsvp/${socialEvent.public_slug}` : null;

  const save = async () => {
    if (!String(form.title || "").trim()) { setError("Give it a name."); return; }
    setSaving(true); setError("");
    const payload = {
      title: String(form.title).trim(),
      description: form.description || null,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
      location: form.location || null,
      // Blank means uncapped, which is different from zero. Between two guesses
      // take the lower one: raising capacity later just calls the waitlist,
      // lowering it means taking back a confirmation by hand.
      capacity: form.capacity === "" ? null : Number(form.capacity),
      rsvp_deadline: form.rsvp_deadline ? new Date(`${form.rsvp_deadline}T23:59:59`).toISOString() : null,
      payment_note: form.payment_note || null,
      ask_club_account: !!form.ask_club_account,
      club_account_prompt: form.ask_club_account ? (form.club_account_prompt || null) : null,
    };
    const res = socialEvent
      ? await authed(`/social-events/${socialEvent.id}`, { method: "PUT", body: JSON.stringify(payload) })
      : await authed(`/social-events`, { method: "POST", body: JSON.stringify({ ...payload, teetime_id: teetimeId }) });
    setSaving(false);
    if (!res.ok) { setError(res.body.error || "Could not save the social event."); return; }
    setSocialEvent(res.body.data);
    if (res.body.counts) setCounts(res.body.counts);
    fill(res.body.data);
    setEditing(false);
  };

  // Server-formatted in the organization's timezone (see socialEventService).
  // The fallback is the browser's zone, which is right only by coincidence.
  const whenText = (ev) => {
    if (!ev) return null;
    if (ev.starts_at_text) return ev.starts_at_text;
    if (!ev.starts_at) return null;
    try {
      return new Date(ev.starts_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return null; }
  };

  const box = { borderTop: "1px solid #e5e7eb", paddingTop: 12 };
  const field = { padding: 8, border: "1px solid #e5e7eb", borderRadius: 10, width: "100%", fontSize: 14, boxSizing: "border-box" };

  // The Event Desk is a door for pro-shop staff and event assistants to run the
  // GOLF on the day. The social event is the organizer's own business and its guest
  // list is not theirs to see, so this section is absent there entirely -- the
  // same call EventDeskAccessPanel already makes one block above.
  if (deskMode) return null;

  if (loading) {
    return <div style={box}><Label>Social event</Label><div style={{ fontSize: 13, color: "#6b7280" }}>Loading…</div></div>;
  }

  // Before the golf event row exists there is nothing to hang this on, and no
  // event address to build its signup address from. Say so rather than hiding
  // it -- an organizer who cannot see the option assumes it does not exist.
  if (!teetimeId || !eventId) {
    return (
      <div style={box}>
        <Label>Social event <InfoTip text="A dinner, socialEvent, auction or any other gathering tied to this round — its own signup page and guest list, separate from the golf. Nothing about it appears in the player app." /></Label>
        <div style={{ fontSize: 13, color: "#4b5563" }}>Save the event first, then reopen this panel to add a social event — a dinner, reception or anything else, with its own signup page for players and non-players alike.</div>
      </div>
    );
  }

  // ---- the form (new or editing) ----
  if (editing || !socialEvent) {
    const isNew = !socialEvent;
    if (isNew && !editing) {
      return (
        <div style={box}>
          <Label>Social event <InfoTip text="A dinner, socialEvent, auction or any other gathering tied to this round — its own signup page and guest list, separate from the golf. Nothing about it appears in the player app." /></Label>
          <div style={{ fontSize: 13, color: "#4b5563", marginBottom: 8 }}>
            Nothing scheduled alongside this round. Add a dinner, socialEvent, auction or anything else and guests get their own signup page — players, spouses and anyone else, whether or not they are playing.
          </div>
          <button type="button" onClick={() => { setEditing(true); set("title", eventName ? `${eventName} Reception` : "Reception"); }}
                  style={{ background: "none", border: "none", color: "#2d6a4f", fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0 }}>
            Add a social event ▸
          </button>
        </div>
      );
    }
    return (
      <div style={box}>
        <Label>Social event <InfoTip text="A dinner, socialEvent, auction or any other gathering tied to this round — its own signup page and guest list, separate from the golf. Nothing about it appears in the player app." /></Label>
        <div style={{ display: "grid", gap: 8 }}>
          <div>
            <Label>Name</Label>
            <input style={field} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Last Chance Reception, Awards Dinner, Charity Auction…" />
          </div>
          <div>
            <Label>Description (optional) <InfoTip text="Shown on the signup page under the date and place. Say what the evening is — dress, what is included, whether spouses are welcome, anything a guest would want to know before saying yes." /></Label>
            <textarea style={{ ...field, fontFamily: "inherit" }} rows={3} value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                      placeholder="Cocktails and heavy hors d'oeuvres. Spouses and guests welcome. Pairings for tomorrow announced at 7pm." />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <Label>When</Label>
              <input style={field} type="datetime-local" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} />
            </div>
            <div>
              <Label>Where</Label>
              <input style={field} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Grand Pavilion" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <Label>How many can you seat? <InfoTip text="Leave blank for no limit. Anyone past the limit goes on a waitlist instead of being turned away. You can change this any time — lowering it never un-confirms anyone, it just shows you that you are over." /></Label>
              <input style={field} type="number" min="1" value={form.capacity}
                     onChange={(e) => set("capacity", e.target.value)} placeholder="No limit" />
            </div>
            <div>
              <Label>Signup closes <InfoTip text="Usually the date the club needs your guaranteed count. After it, the signup page and everyone's change links go read-only." /></Label>
              <input style={field} type="date" value={form.rsvp_deadline} onChange={(e) => set("rsvp_deadline", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>What it costs, in your own words <InfoTip text="Free text, shown on the signup form and in the confirmation email. TTO does not take payment — say what the club told you to say, for example: $45 per person, charged to your member account." /></Label>
            <input style={field} value={form.payment_note} onChange={(e) => set("payment_note", e.target.value)}
                   placeholder="$00.00 per person, charged to your member account" />
          </div>
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={form.ask_club_account} onChange={(e) => set("ask_club_account", e.target.checked)} />
              Ask registrants to charge a club account
              <InfoTip text="Only turn this on if the venue bills member accounts. Off, the question never appears and no member column is kept — which is right for a company outing, a church group, or anywhere without club accounts." />
            </label>
            {form.ask_club_account && (
              <div style={{ marginTop: 8 }}>
                <Label>What to call it on the form</Label>
                <input style={field} value={form.club_account_prompt} onChange={(e) => set("club_account_prompt", e.target.value)}
                       placeholder="Charge to my club account" />
              </div>
            )}
          </div>
          {error && <div style={{ fontSize: 12, color: "#b91c1c" }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={save} disabled={saving} style={{ background: "#2d6a4f", color: "#fff", fontSize: 13 }}>
              {saving ? "Saving…" : socialEvent ? "Save social event" : "Create social event"}
            </Button>
            {socialEvent && (
              <Button variant="outline" onClick={() => { fill(socialEvent); setEditing(false); setError(""); }} style={{ fontSize: 13 }}>Cancel</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- the summary ----
  const capped = counts && counts.capacity !== null && counts.capacity !== undefined;
  return (
    <div style={box}>
      <Label>Social event <InfoTip text="A dinner, socialEvent, auction or any other gathering tied to this round — its own signup page and guest list, separate from the golf. Nothing about it appears in the player app." /></Label>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{socialEvent.title}</div>
      {whenText(socialEvent) && (
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          {whenText(socialEvent)}{socialEvent.location ? ` · ${socialEvent.location}` : ""}
        </div>
      )}

      {counts && (
        <div style={{ marginTop: 8, fontSize: 14 }}>
          <strong>{counts.confirmed_covers}</strong>
          {capped ? ` of ${counts.capacity}` : " confirmed"}
          {counts.waitlist_parties > 0 && ` · ${counts.waitlist_covers} waitlisted`}
          {counts.members !== null && counts.members !== undefined && ` · ${counts.members} on a club account`}
        </div>
      )}

      {/* Lowering capacity never demotes anyone — it shows up here instead. */}
      {counts && counts.over_by > 0 && (
        <div style={{ marginTop: 8, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "#92400e", lineHeight: 1.45 }}>
          You have {counts.over_by} more confirmed than you can seat. Nobody has been moved — everyone who was told they are in is still in. Raise the number you can seat, or call someone.
        </div>
      )}

      {signupUrl && (
        <div style={{ marginTop: 10 }}>
          <Label>Signup address <InfoTip text="Put this in the invite email. It opens the signup form — anyone with the link can reserve a place, whether or not they are playing golf." /></Label>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <code style={{ fontSize: 13, background: "#f3f4f6", borderRadius: 6, padding: "4px 8px" }}>{signupUrl}</code>
            <button type="button"
                    onClick={() => {
                      // Clipboard can be refused (insecure context, permissions).
                      // Never let a copy button throw and blank the panel.
                      try {
                        navigator.clipboard.writeText(`https://${signupUrl}`);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1800);
                      } catch (e) { /* the address is on screen either way */ }
                    }}
                    style={{ background: "none", border: "none", color: "#2d6a4f", fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0 }}>
              {copied ? "Copied" : "Copy"}
            </button>
            <a href={`https://${signupUrl}`} target="_blank" rel="noreferrer"
               style={{ color: "#2d6a4f", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>Open ↗</a>
          </div>
        </div>
      )}

      <div style={{ marginTop: 10, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" onClick={() => setEditing(true)}
                style={{ background: "none", border: "none", color: "#2d6a4f", fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0 }}>
          Edit social event
        </button>
        {/* The guest list opens from the 🥂 Social Event button on the tee time
            card. It is not linked from here: this panel is already a modal, and
            a second one stacked on top of it is a worse way to read a table. */}
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          Guest list is on the tee time, under 🥂 Social Event.
        </span>
      </div>
    </div>
  );
};

const EventSetupModal = ({ initial, slots, teetimeId, date, teamScoringEnabled = false, onOpenGames, onSave, onClose, eventId = null, deskMode = false }) => {
  const { addToast } = useToast();
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  // Public address (Sept 16, 2026). An existing event edits its address here; a new
  // event gets one generated from its name + year when saved (the preview shows what
  // that will be) unless the organizer types one. Only sent to the server when typed
  // or changed. Once cards were GENERATED with it (slug_printed_at — we cannot know
  // whether they reached paper; Dennis, Sept 16), a change needs the confirm tick.
  const hasAddress = !!(initial && initial.public_slug);
  const [slugInput, setSlugInput] = useState(hasAddress ? initial.public_slug : "");
  const [slugError, setSlugError] = useState("");
  const [confirmReprint, setConfirmReprint] = useState(false);
  const [addressCopied, setAddressCopied] = useState(false);
  const eventYear = /^\d{4}/.test(String(date || "")) ? String(date).slice(0, 4) : String(new Date().getFullYear());
  const suggestedSlug = suggestPublicSlug(name, eventYear);
  const cleanedSlug = slugInput.trim() ? normalizePublicSlug(slugInput) : "";
  const slugChanged = hasAddress ? (cleanedSlug !== "" && cleanedSlug !== initial.public_slug) : cleanedSlug !== "";
  const printedAt = initial && initial.slug_printed_at ? new Date(initial.slug_printed_at) : null;
  const needsReprintConfirm = hasAddress && slugChanged && !!printedAt;
  const currentAddressUrl = hasAddress ? (initial.public_url || `https://${PUBLIC_BOARD_HOST.toLowerCase()}/${initial.public_slug}`) : null;
  // Event logo (Sept 16, 2026): shown on the public leaderboard beside the name.
  // A picked file is STAGED here and uploaded by the caller after the event is
  // saved (a new event has no id yet); Remove is staged the same way.
  const currentLogoUrl = initial && initial.logo_url ? `${API_URL}${initial.logo_url}` : null;
  const [logoStaged, setLogoStaged] = useState(null);     // { blob, name, type, preview } | null
  const [removeLogo, setRemoveLogo] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputRef = useRef(null);
  useEffect(() => () => { if (logoStaged && logoStaged.preview) URL.revokeObjectURL(logoStaged.preview); }, [logoStaged]);
  const shownLogo = logoStaged ? logoStaged.preview : (removeLogo ? null : currentLogoUrl);
  const pickLogo = async (file) => {
    if (!file) return;
    setLogoError("");
    if (!/^image\//i.test(file.type || "")) { setLogoError("Choose an image file (PNG or JPG)."); return; }
    if (file.size > LOGO_MAX_BYTES) { setLogoError("That image is larger than 6 MB. Use a smaller file."); return; }
    setLogoBusy(true);
    try {
      const prepared = await prepareLogoImage(file);
      if (logoStaged && logoStaged.preview) URL.revokeObjectURL(logoStaged.preview);
      setLogoStaged({ ...prepared, preview: URL.createObjectURL(prepared.blob) });
      setRemoveLogo(false);
    } catch (e) {
      setLogoError("That image could not be read.");
    } finally {
      setLogoBusy(false);
    }
  };
  // Scoring format (Team scoring, Sept 13, 2026): 'individual' (default) or 'team'
  // (one score per team per hole — captain's choice / scramble). Shown only when the
  // org has team scoring switched on; the server enforces the same gate and locks
  // the value once team scores exist (§15.3).
  const [scoringMode, setScoringMode] = useState(initial?.scoring_mode === "team" ? "team" : "individual");
  // GHIN posting eligible (Sept 15, 2026): the organizer's Yes/No, default Yes.
  // Locked at No while the round is one score per team — a scramble is never a
  // handicap round — and the server enforces the same. TTO never posts for a
  // player; No only silences the posting reminder and hides Post to GHIN.
  const [ghinEligible, setGhinEligible] = useState(initial?.postable_to_ghin !== false);
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(false);

  // Slice 3b — reflect the actual games configured on this tee time (read-only).
  // Games live in the Games tab; here we just list what is attached so the organizer
  // can confirm at a glance, and offer a jump to manage them. No config happens here.
  useEffect(() => {
    if (!teetimeId) { setGames([]); return; }
    let cancelled = false;
    (async () => {
      setGamesLoading(true);
      try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const [catRes, gamesRes] = await Promise.all([
          fetch(`${API_URL}/scoring/game-catalog`, { credentials: 'include', headers }),
          fetch(`${API_URL}/scoring/teetimes/${teetimeId}/games`, { credentials: 'include', headers }),
        ]);
        const catData = catRes.ok ? await catRes.json() : { games: [] };
        const gamesData = gamesRes.ok ? await gamesRes.json() : { games: [] };
        const labelByType = {};
        (catData.games || []).forEach((cg) => { if (cg && cg.game_type) labelByType[cg.game_type] = cg.display_name || cg.game_type; });
        const prettify = (t) => String(t || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
        const list = (gamesData.games || []).map((g) => ({ id: g.id, label: labelByType[g.game_type] || prettify(g.game_type) }));
        if (!cancelled) setGames(list);
      } catch (_) {
        if (!cancelled) setGames([]);
      } finally {
        if (!cancelled) setGamesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [teetimeId]);

  const save = () => {
    if (!String(name || "").trim()) {
      addToast("Please enter an event name", "error");
      return;
    }
    // Address: sent only when typed (new event) or changed (existing event).
    let slugOut;
    if (slugChanged) {
      if (cleanedSlug.length < 3) { setSlugError("The address needs at least 3 characters — letters, numbers and hyphens."); return; }
      if (needsReprintConfirm && !confirmReprint) { setSlugError("Tick “I’ll reprint” to confirm the new address, or put the old one back."); return; }
      slugOut = cleanedSlug;
    }
    setSlugError("");
    onSave({
      name: String(name).trim(),
      description: String(description || "").trim() || null,
      rsvp_mode: "individual",
      scoring_mode: scoringMode,
      postable_to_ghin: scoringMode === "team" ? false : ghinEligible,
      ...(slugOut !== undefined ? { public_slug: slugOut, confirm_reprint: needsReprintConfirm ? true : undefined } : {}),
      ...(logoStaged ? { logo_file: logoStaged.blob, logo_name: logoStaged.name } : {}),
      ...(removeLogo && !logoStaged && currentLogoUrl ? { remove_logo: true } : {})
    });
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 80, padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: 16, boxShadow: "0 12px 40px rgba(0,0,0,.2)" }}
      >
        <div style={{ background: "linear-gradient(135deg, #2d6a4f, #1b4332)", color: "#fff", padding: "18px 22px", borderRadius: "16px 16px 0 0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 800, fontSize: 20 }}>Set up event</div>
            <EventGuidesLink light label="📖 Setup guides" />
          </div>
          <div style={{ fontSize: 13, opacity: .9 }}>Name it and choose how players register.</div>
          {!deskMode && <EventPassBadge teetimeId={teetimeId} />}
        </div>

        <div style={{ padding: "20px 22px", display: "grid", gap: 14 }}>
          <div>
            <Label>Event name <span style={{ color: "#dc2626" }}>*</span></Label>
            <Input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Last Chance Invitational" maxLength={120} />
          </div>

          <div>
            <Label>Description (optional)</Label>
            <Input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short note about this event" maxLength={500} />
          </div>

          <div>
            <Label>Live leaderboard address <InfoTip text="Spectators follow the live board at this address — it prints on the scorecards and cart signs and is easy to read out over a PA. Made from the event name and the year; shorten it if you like. Anyone with the address can see the board (sharing can be turned off on the Scoring tab)." /></Label>
            <div style={{ display: "flex", alignItems: "stretch", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
              <span style={{ padding: "8px 10px", background: "#f3f4f6", color: "#6b7280", fontSize: 14, borderRight: "1px solid #e5e7eb", whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>{PUBLIC_BOARD_HOST}/</span>
              <input
                type="text"
                value={slugInput}
                onChange={(e) => { setSlugInput(e.target.value); setSlugError(""); setConfirmReprint(false); }}
                placeholder={hasAddress ? initial.public_slug : suggestedSlug}
                maxLength={60}
                spellCheck={false}
                autoCapitalize="none"
                style={{ flex: 1, minWidth: 0, border: "none", outline: "none", padding: 8, fontSize: 14, color: "#166534", fontWeight: 600, boxSizing: "border-box" }}
              />
            </div>
            {!hasAddress && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                {cleanedSlug
                  ? <>Saved as <strong>{PUBLIC_BOARD_HOST}/{cleanedSlug}</strong> if no other event has it.</>
                  : <>Made from the name when you save: <strong>{PUBLIC_BOARD_HOST}/{suggestedSlug}</strong>. You can change it afterwards.</>}
              </div>
            )}
            {hasAddress && !slugChanged && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6, fontSize: 12, flexWrap: "wrap" }}>
                <a href={currentAddressUrl} target="_blank" rel="noreferrer" style={{ color: "#2d6a4f", fontWeight: 600, textDecoration: "none" }}>Open the board ↗</a>
                <a href={`${currentAddressUrl}/tv`} target="_blank" rel="noreferrer" title="Full-screen view for a clubhouse TV — pages through the whole field" style={{ color: "#2d6a4f", fontWeight: 600, textDecoration: "none" }}>TV view ↗</a>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(currentAddressUrl)
                      .then(() => { setAddressCopied(true); setTimeout(() => setAddressCopied(false), 2000); })
                      .catch(() => addToast("Could not copy — select the address and copy it instead", "error"));
                  }}
                  style={{ background: "none", border: "none", color: "#2d6a4f", fontWeight: 600, fontSize: 12, cursor: "pointer", padding: 0 }}
                >
                  {addressCopied ? "✓ Copied" : "Copy address"}
                </button>
                {printedAt && <span style={{ color: "#6b7280" }}>Scorecards generated with it {printedAt.toLocaleDateString([], { month: "short", day: "numeric" })}</span>}
              </div>
            )}
            {hasAddress && slugChanged && cleanedSlug && !needsReprintConfirm && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>New address: <strong>{PUBLIC_BOARD_HOST}/{cleanedSlug}</strong>. The old one keeps working until another event takes it.</div>
            )}
            {needsReprintConfirm && (
              <div style={{ marginTop: 8, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "#92400e", lineHeight: 1.45 }}>
                Scorecards or cart signs carrying <strong>{PUBLIC_BOARD_HOST}/{initial.public_slug}</strong> were generated on {printedAt.toLocaleDateString([], { month: "short", day: "numeric" })} — anything already printed shows that address (it keeps working until another event takes it). The new address will be <strong>{PUBLIC_BOARD_HOST}/{cleanedSlug}</strong>.
                <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, cursor: "pointer", fontWeight: 600 }}>
                  <input type="checkbox" checked={confirmReprint} onChange={(e) => setConfirmReprint(e.target.checked)} style={{ width: 15, height: 15 }} /> Understood — anything already printed will need printing again
                </label>
              </div>
            )}
            {slugError && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>{slugError}</div>}
          </div>

          <div>
            <Label>Event logo (optional) <InfoTip text="Shows on the live leaderboard beside the event name, next to the Tee Time Organizer mark, and prints top-left on the scorecards and cart signs in place of the organization name. PNG or JPG, up to 6 MB — a square or wide mark works best." /></Label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ width: 64, height: 64, borderRadius: 10, border: "1px solid #e5e7eb", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                {shownLogo
                  ? <img src={shownLogo} alt="Event logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                  : <span style={{ fontSize: 22, color: "#d1d5db" }}>🏆</span>}
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: "none" }}
                  onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; pickLogo(f); }} />
                <Button variant="outline" type="button" disabled={logoBusy} onClick={() => logoInputRef.current && logoInputRef.current.click()}>
                  {logoBusy ? "Reading…" : (shownLogo ? "Replace logo" : "Upload logo")}
                </Button>
                {shownLogo && (
                  <button type="button" onClick={() => { if (logoStaged && logoStaged.preview) URL.revokeObjectURL(logoStaged.preview); setLogoStaged(null); setRemoveLogo(true); setLogoError(""); }}
                    style={{ background: "none", border: "none", color: "#b91c1c", fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0 }}>
                    Remove
                  </button>
                )}
                {logoStaged && <span style={{ fontSize: 12, color: "#6b7280" }}>Saved with the event</span>}
                {!logoStaged && removeLogo && currentLogoUrl && <span style={{ fontSize: 12, color: "#6b7280" }}>Removed when you save</span>}
              </div>
            </div>
            {logoError && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>{logoError}</div>}
          </div>

          {eventId && !deskMode && <EventPagePanel eventId={eventId} apiUrl={API_URL} getToken={getAuthToken} addToast={addToast} />}

          {eventId && <EventFeesPanel eventId={eventId} />}

          {eventId && <EventRecapSettingPanel eventId={eventId} />}

          {eventId && !deskMode && <EventDeskAccessPanel eventId={eventId} />}

          <div>
            <Label>Field size <InfoTip text="Comes from the tee time's Slots. Change it on the tee time." /></Label>
            <div style={{ background: "#f3f4f6", border: "1px dashed #d1d5db", borderRadius: 8, padding: "9px 10px", fontSize: 14, color: "#374151" }}>
              {slots || "—"} golfers <span style={{ color: "#6b7280", fontSize: 12 }}>— from tee time slots</span>
            </div>
            <div style={{ fontSize: 12.5, color: "#4b5563", marginTop: 6, lineHeight: 1.45 }}>
              Anyone in your group can play. Golfers who aren&rsquo;t regular players join as guests: free, and they won&rsquo;t get your weekly tee time invitations.
            </div>
          </div>

          {!deskMode && <EventSignupPanel eventId={eventId} />}

          {(teamScoringEnabled || scoringMode === "team") && (
            <div>
              <Label>Scoring <InfoTip text="Individual: every player enters their own score, as usual. Team: one score per team per hole (captain's choice / scramble) — one player keeps the team card, teammates can watch or take it over, and nothing posts to GHIN. Locked once team scores exist." /></Label>
              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14, cursor: teamScoringEnabled ? "pointer" : "default" }}>
                  <input type="radio" name="event-scoring-mode" checked={scoringMode !== "team"} disabled={!teamScoringEnabled} onChange={() => setScoringMode("individual")} style={{ width: 16, height: 16, marginTop: 2 }} />
                  <span><strong>Each player enters their own score</strong><div style={{ fontSize: 12, color: "#6b7280" }}>Stroke play, best ball, skins — scoring works exactly as today.</div></span>
                </label>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14, cursor: teamScoringEnabled ? "pointer" : "default" }}>
                  <input type="radio" name="event-scoring-mode" checked={scoringMode === "team"} disabled={!teamScoringEnabled} onChange={() => setScoringMode("team")} style={{ width: 16, height: 16, marginTop: 2 }} />
                  <span><strong>One score per team</strong> <span style={{ fontSize: 12, color: "#6b7280" }}>(captain's choice / scramble)</span><div style={{ fontSize: 12, color: "#6b7280" }}>Players open a team card instead of their own; one teammate keeps it. Teams are your saved pairings — twosomes for a 2-man event, foursomes for a 4-man event — set on the Pairings page. Scores don't post to GHIN.</div></span>
                </label>
              </div>
              {/* Event setup guide (Sept 27, 2026): the steps in order, public article. */}
              <a href="/guides/how-to-set-up-a-captains-choice-golf-tournament?from=app" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", marginTop: 6, fontSize: 12, fontWeight: 600, color: "#15803d" }}>📖 Captain's Choice setup guide — every step, in order ↗</a>
            </div>
          )}

          <div>
            <Label>GHIN posting eligible <InfoTip text="Players always post their own scores — TTO never posts for them. Yes: after the round, players get the posting reminder and see Post to GHIN. No: for a format the Rules of Handicapping don't accept (scramble, shamble, alternate shot) or a day played outside the Rules (mulligans, temporary greens) — no reminder, no button." /></Label>
            {scoringMode === "team" ? (
              <div style={{ background: "#f3f4f6", border: "1px dashed #d1d5db", borderRadius: 8, padding: "9px 10px", fontSize: 14, color: "#374151" }}>
                No <span style={{ color: "#6b7280", fontSize: 12 }}>— one score per team is never a handicap round</span>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer" }}>
                  <input type="radio" name="event-ghin-eligible" checked={ghinEligible} onChange={() => setGhinEligible(true)} style={{ width: 16, height: 16 }} /> Yes
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer" }}>
                  <input type="radio" name="event-ghin-eligible" checked={!ghinEligible} onChange={() => setGhinEligible(false)} style={{ width: 16, height: 16 }} /> No
                </label>
                {!ghinEligible && <span style={{ fontSize: 12, color: "#6b7280" }}>no posting reminder, no Post to GHIN button</span>}
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
            <Label>Games <InfoTip text="Games are set up in the Games tab, the same for events and ordinary rounds. Any game on this tee time publishes its results with the event." /></Label>
            {gamesLoading ? (
              <div style={{ fontSize: 13, color: "#6b7280" }}>Loading games…</div>
            ) : games.length > 0 ? (
              <div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                  {games.map((g) => (
                    <span key={g.id} style={{ fontSize: 12, fontWeight: 600, background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0", borderRadius: 999, padding: "3px 10px" }}>{g.label}</span>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>{games.length === 1 ? "This game's results publish with the event." : "Each game's results publish with the event."}</div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#4b5563" }}>No games are set up on this tee time yet. Add them in the Games tab &mdash; each game's results publish with the event. No game means the event publishes a gross &amp; net leaderboard.</div>
            )}
            {onOpenGames && (
              <button type="button" onClick={() => onOpenGames()} style={{ marginTop: 8, background: "none", border: "none", color: "#2d6a4f", fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0 }}>
                {games.length > 0 ? "Manage games in the Games tab ▸" : "Set up games in the Games tab ▸"}
              </button>
            )}
          </div>

          {/* Social event (Sept 20, 2026) — a dinner, reception, auction or
              anything else. Last block on purpose: adjacent to the golf, not
              part of it, and it saves itself through its own endpoints rather
              than this modal's onSave. */}
          <SocialEventSection teetimeId={teetimeId} eventId={eventId} eventName={name} deskMode={deskMode} />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 22px", borderTop: "1px solid #e5e7eb", background: "#fafafa" }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} style={{ background: "#2d6a4f", color: "#fff" }}>Save event</Button>
        </div>
      </div>
    </div>
  );
};

const CreateTeeTimeInline = ({ onCreate, isReadOnly = false, onReadOnlyClick, eventsEnabled = false, canCreateEvent, onSaveEvent }) => {
  // Event Pass (Sept 28, 2026): the box is offered only when a new event can be made
  // now; otherwise it shows locked with "Get an Event Pass" (Show + Lock + Educate).
  const eventOpen = canCreateEvent === undefined ? eventsEnabled : !!canCreateEvent;
  const { addToast } = useToast();
  const { courses } = useCourses();
  const { groups } = useGroups();
  const { data: players } = useApiData('/players');
  const { isMobile } = useContext(MobileContext);
  const [mode, setMode] = useState("simple"); // simple, advanced, recurring, bulk
  const [form, setForm] = useState({ course: "", date: "", time: "", slots: "4", title: "", private: false, competition_hole_set: "all18", shotgun_mode: false, is_event: false, event_name: "", event_description: "" });
  // Slice 3a — the create-flow Event Setup modal lives in the parent (it holds the
  // newly created tee time directly), so no local modal state is needed here.
  const [showPublishOptions, setShowPublishOptions] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false); // NEW: Publishing loading state
  const [publishTo, setPublishTo] = useState("all"); // all, select, group
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  
  // Advanced options
  const [recurring, setRecurring] = useState({ enabled: false, frequency: "weekly", endDate: "", count: 4 });
  const [bulkCreate, setBulkCreate] = useState({ startTime: "", count: 10, interval: 10, date: "" });

  // Filter active players, excluding guests (role = 'guest' or 'Guest')
  const activePlayers = useMemo(() => players.filter((p) => 
    p.active && p.role && p.role.toLowerCase() !== 'guest'
  ), [players]);

  const handleCreate = async () => {
    // Block if read-only (cancelled subscription)
    if (isReadOnly) {
      onReadOnlyClick?.();
      return;
    }
    
    if (!form.course || !form.date || !form.time) {
      addToast("Please fill in all required fields", "error");
      return;
    }
    
    // Validate date is not in the past
    const today = utils.getTodayDate();
    if (form.date < today) {
      addToast("Cannot create tee times for past dates", "error");
      return;
    }

    // Event (Slice 3a): whether to open the Event Setup modal after the tee time is
    // created. No pre-save name entry — the modal collects the event against the real
    // tee time once it exists.
    const wantEvent = eventsEnabled && form.is_event;

    // Create tee time as draft
    const newTeeTime = {
      id: utils.generateId(),
      course: form.course,
      date: form.date,
      time: form.time,
      slots: utils.toInt(form.slots),
      filled: 0,
      status: "Draft",
      players: [],
      waitlist: [],
      cancelled: [],
      private: form.private || false,
      competition_hole_set: form.competition_hole_set || "all18",
        shotgun_mode: form.shotgun_mode || false,
      title: String(form.title || "").trim() || null
    };

    // Save the tee time — the parent opens the Event Setup modal on the new tee time
    // when wantEvent is true (the parent holds the created record directly).
    await onCreate(newTeeTime, wantEvent);

    // Reset form
    setForm({ course: "", date: "", time: "", slots: "4", title: "", private: false, competition_hole_set: "all18", shotgun_mode: false, is_event: false, event_name: "", event_description: "" });
    addToast("Tee time created as draft");
  };
  
  const handlePublishConfirm = async (publishData) => {
    setIsPublishing(true); // START loading
    
    try {
      if (mode === "recurring") {
        await handleRecurringPublishConfirm(publishData);
        return;
      }
      
      if (mode === "bulk") {
        await handleBulkPublishConfirm(publishData);
        return;
      }
      
      // Simple mode - create single tee time
      const newTeeTime = { 
        id: utils.generateId(), 
        course: form.course, 
        date: form.date, 
        time: form.time, 
        slots: utils.toInt(form.slots), 
        filled: 0, 
        status: "Published", 
        players: [], 
        waitlist: [],
        cancelled: [],
        private: form.private || false,
      competition_hole_set: form.competition_hole_set || "all18",
        shotgun_mode: form.shotgun_mode || false,
      title: String(form.title || "").trim() || null
      };

      // Event (Slice 3a): open the Event Setup modal after publishing, if flagged.
      const wantEvent = eventsEnabled && form.is_event;

      // Add the tee time — the parent opens Event Setup on the new tee time when
      // wantEvent is true.
      await onCreate(newTeeTime, wantEvent);

      // Send notifications
      await sendPublishNotifications(newTeeTime, publishData);

      // Reset form and close modal
      setForm({ course: "", date: "", time: "", slots: "4", title: "", private: false, competition_hole_set: "all18", shotgun_mode: false, is_event: false, event_name: "", event_description: "" });
      setShowPublishOptions(false);
      setSelectedPlayers([]);
      setSelectedGroup("");
    } finally {
      setIsPublishing(false); // END loading (even if error)
    }
  };
  
  const sendPublishNotifications = async (teeTime, publishData) => {
    // Skip notifications entirely if "none" selected
    if (publishData.publishTo === "none") {
      addToast("Tee time published (no notifications sent)", "success");
      return;
    }
    
    // Determine recipients
    let recipients = [];
    
    if (publishData.publishTo === "all") {
      // ✅ Exclude guests from "All Players" notifications
      recipients = activePlayers.filter(p => p.role !== 'guest');
    } else if (publishData.publishTo === "select") {
      // Include guests when explicitly selected (activePlayers excludes guests, so use full players list)
      recipients = players.filter(p => p.active && publishData.playerIds.includes(p.id));
    } else if (publishData.publishTo === "group") {
      const group = groups.find(g => g.id === publishData.groupId);
      if (group) {
        recipients = activePlayers.filter(p => (group.player_ids || []).includes(p.id));
      }
    }
    
    if (recipients.length === 0) {
      addToast("No recipients found", "error");
      return;
    }
    
    // Send notifications via API
    // FIXED: Include JWT auth header for multi-tenant support
    try {
      const pubHeaders = { 'Content-Type': 'application/json' };
      const pubToken = getAuthToken();
      if (pubToken) {
        pubHeaders['Authorization'] = `Bearer ${pubToken}`;
      }
      
      await fetch(`${API_URL}/teetimes/${teeTime.id}/publish`, {
        method: 'POST',
        headers: pubHeaders,
        credentials: 'include',
        body: JSON.stringify({
          teeTime,
          publishTo: publishData.publishTo, // backend uses this to allow guests only in "select" mode
          recipients: recipients.map(p => ({ id: p.id, email: p.email, name: p.name })),
          customMessage: publishData.customMessage || "" // NEW: Include custom message
        })
      });
      
      addToast(`Tee time published to ${recipients.length} player${recipients.length === 1 ? '' : 's'}`);
    } catch (err) {
      console.error('Error publishing tee time:', err);
      addToast("Tee time created but failed to send notifications", "error");
    }
  };

  const handleRecurringCreate = async () => {
    if (!form.course || !form.date || !form.time) {
      addToast("Please fill in all required fields", "error");
      return;
    }

    // Validate date is not in the past
    const today = utils.getTodayDate();
    if (form.date < today) {
      addToast("Cannot create tee times for past dates", "error");
      return;
    }

    // Create recurring tee times as drafts
    const teeTimes = [];
    // Parse date manually to avoid Safari/iOS timezone issues
    const [startYear, startMonth, startDay] = form.date.split('-').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const frequency = recurring.frequency;
    const count = recurring.count || 4;

    for (let i = 0; i < count; i++) {
      const teeDate = new Date(startDate);
      
      if (frequency === "daily") {
        teeDate.setDate(startDate.getDate() + i);
      } else if (frequency === "weekly") {
        teeDate.setDate(startDate.getDate() + (i * 7));
      } else if (frequency === "biweekly") {
        teeDate.setDate(startDate.getDate() + (i * 14));
      } else if (frequency === "monthly") {
        teeDate.setMonth(startDate.getMonth() + i);
      }

      const dateStr = teeDate.toISOString().split('T')[0];
      
      teeTimes.push({
        id: utils.generateId(),
        course: form.course,
        date: dateStr,
        time: form.time,
        slots: utils.toInt(form.slots),
        filled: 0,
        status: "Draft",
        players: [],
        waitlist: [],
        cancelled: [],
        private: form.private || false,
        competition_hole_set: form.competition_hole_set || "all18",
        shotgun_mode: form.shotgun_mode || false,
        title: String(form.title || "").trim() || null
      });
    }

    // Create all tee times
    for (const tt of teeTimes) {
      await onCreate(tt);
    }
    
    addToast(`Created ${teeTimes.length} recurring tee times as drafts`);
    setForm({ course: "", date: "", time: "", slots: "4", title: "", private: false, competition_hole_set: "all18", shotgun_mode: false, is_event: false, event_name: "", event_description: "" });
    setRecurring({ enabled: false, frequency: "weekly", endDate: "", count: 4 });
  };
  
  const handleRecurringPublishConfirm = async (publishData) => {
    const teeTimes = [];
    // Parse date manually to avoid Safari/iOS timezone issues
    const [startYear, startMonth, startDay] = form.date.split('-').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const frequency = recurring.frequency;
    const count = recurring.count || 4;

    for (let i = 0; i < count; i++) {
      const teeDate = new Date(startDate);
      
      if (frequency === "daily") {
        teeDate.setDate(startDate.getDate() + i);
      } else if (frequency === "weekly") {
        teeDate.setDate(startDate.getDate() + (i * 7));
      } else if (frequency === "biweekly") {
        teeDate.setDate(startDate.getDate() + (i * 14));
      } else if (frequency === "monthly") {
        teeDate.setMonth(startDate.getMonth() + i);
      }

      const dateStr = teeDate.toISOString().split('T')[0];
      
      teeTimes.push({
        id: utils.generateId(),
        course: form.course,
        date: dateStr,
        time: form.time,
        slots: utils.toInt(form.slots),
        filled: 0,
        status: "Published",
        players: [],
        waitlist: [],
        cancelled: [],
        private: form.private || false,
        competition_hole_set: form.competition_hole_set || "all18",
        shotgun_mode: form.shotgun_mode || false,
        title: String(form.title || "").trim() || null
      });
    }

    // Create all tee times
    teeTimes.forEach(tt => onCreate(tt));
    
    // Send notifications for each tee time
    for (const tt of teeTimes) {
      await sendPublishNotifications(tt, publishData);
    }
    
    addToast(`Created and published ${teeTimes.length} recurring tee times`);
    setForm({ course: "", date: "", time: "", slots: "4", title: "", private: false, competition_hole_set: "all18", shotgun_mode: false, is_event: false, event_name: "", event_description: "" });
    setRecurring({ enabled: false, frequency: "weekly", endDate: "", count: 4 });
    setShowPublishOptions(false);
  };

  const handleBulkCreate = async () => {
    if (!bulkCreate.date || !bulkCreate.startTime || !bulkCreate.count || !form.course) {
      addToast("Please fill in all fields for bulk creation", "error");
      return;
    }

    // Validate date is not in the past
    const today = utils.getTodayDate();
    if (bulkCreate.date < today) {
      addToast("Cannot create tee times for past dates", "error");
      return;
    }

    // Create bulk tee times as drafts
    const teeTimes = [];
    const [startHour, startMin] = bulkCreate.startTime.split(':').map(Number);
    let currentMinutes = startHour * 60 + startMin;
    const interval = utils.toInt(bulkCreate.interval);
    const count = utils.toInt(bulkCreate.count);

    for (let i = 0; i < count; i++) {
      const hours = Math.floor(currentMinutes / 60);
      const mins = currentMinutes % 60;
      const timeStr = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
      
      teeTimes.push({
        id: utils.generateId(),
        course: form.course,
        date: bulkCreate.date,
        time: timeStr,
        slots: utils.toInt(form.slots),
        filled: 0,
        status: "Draft",
        players: [],
        waitlist: [],
        cancelled: [],
        private: form.private || false,
        competition_hole_set: form.competition_hole_set || "all18",
        shotgun_mode: form.shotgun_mode || false,
        title: String(form.title || "").trim() || null
      });
      
      currentMinutes += interval;
    }

    // Create all tee times
    for (const tt of teeTimes) {
      await onCreate(tt);
    }
    
    addToast(`Created ${teeTimes.length} tee times as drafts`);
    setBulkCreate({ startTime: "", count: 10, interval: 10, date: "" });
    setForm({ course: "", date: "", time: "", slots: "4", title: "", private: false, competition_hole_set: "all18", shotgun_mode: false, is_event: false, event_name: "", event_description: "" });
  };
  
  const handleBulkPublishConfirm = async (publishData) => {
    const teeTimes = [];
    const [startHour, startMin] = bulkCreate.startTime.split(':').map(Number);
    let currentMinutes = startHour * 60 + startMin;
    const interval = utils.toInt(bulkCreate.interval);
    const count = utils.toInt(bulkCreate.count);

    for (let i = 0; i < count; i++) {
      const hours = Math.floor(currentMinutes / 60);
      const mins = currentMinutes % 60;
      const timeStr = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
      
      teeTimes.push({
        id: utils.generateId(),
        course: form.course,
        date: bulkCreate.date,
        time: timeStr,
        slots: utils.toInt(form.slots),
        filled: 0,
        status: "Published",
        players: [],
        waitlist: [],
        cancelled: [],
        private: form.private || false,
        competition_hole_set: form.competition_hole_set || "all18",
        shotgun_mode: form.shotgun_mode || false,
        title: String(form.title || "").trim() || null
      });
      
      currentMinutes += interval;
    }

    // Create all tee times
    teeTimes.forEach(tt => onCreate(tt));
    
    // Send notifications for each tee time
    for (const tt of teeTimes) {
      await sendPublishNotifications(tt, publishData);
    }
    
    addToast(`Created and published ${teeTimes.length} tee times`);
    setBulkCreate({ startTime: "", count: 10, interval: 10, date: "" });
    setForm({ course: "", date: "", time: "", slots: "4", title: "", private: false, competition_hole_set: "all18", shotgun_mode: false, is_event: false, event_name: "", event_description: "" });
    setShowPublishOptions(false);
  };


  return (
    <Card style={{ background: "linear-gradient(135deg, #f0fdf4, #dcfce7)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>Create Tee Time</div>
        <div style={{ display: "flex", gap: 6 }}>
          <Button 
            variant={mode === "simple" ? "default" : "outline"} 
            onClick={() => setMode("simple")}
            style={{ fontSize: 12, padding: "6px 10px" }}
          >
            Simple
          </Button>
          <Button 
            variant={mode === "recurring" ? "default" : "outline"} 
            onClick={() => setMode("recurring")}
            style={{ fontSize: 12, padding: "6px 10px" }}
          >
            Recurring
          </Button>
          <Button 
            variant={mode === "bulk" ? "default" : "outline"} 
            onClick={() => setMode("bulk")}
            style={{ fontSize: 12, padding: "6px 10px" }}
          >
            Bulk
          </Button>
        </div>
      </div>

      {/* Simple Mode */}
      {mode === "simple" && (
        <>
          {isMobile ? (
            // Smart hybrid mobile layout (Phase 2 — May 2026):
            //   Row 1: Course (full width) — prevents long-name cutoff bug
            //   Row 2: Date + Time (paired — naturally related "when")
            //   Row 3: Slots + Add Tee Time button (input + action)
            <div style={{ display: "grid", gap: 14 }}>
              <div>
                <Label>Course</Label>
                <Select value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}>
                  <option value="">Select course...</option>
                  {courses.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}
                </Select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 160px) minmax(0, 140px)", gap: 16 }}>
                <div>
                  <Label>Date</Label>
                  <Input type="date" min={utils.getTodayDate()} value={form.date} onChange={(e) => {
                    const selectedDate = e.target.value;
                    const today = utils.getTodayDate();
                    if (selectedDate < today) {
                      addToast("Cannot create tee times for past dates. Please select today or a future date.", "error");
                      setForm({ ...form, date: "" });
                    } else {
                      setForm({ ...form, date: selectedDate });
                    }
                  }} />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 12, alignItems: "end" }}>
                <div>
                  <Label>Slots</Label>
                  <Input type="number" min="1" max="8" value={form.slots} onChange={(e) => setForm({ ...form, slots: e.target.value })} />
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={isReadOnly}
                  style={{ background: isReadOnly ? "#9ca3af" : "#2d6a4f", color: "#fff", cursor: isReadOnly ? "not-allowed" : "pointer", minHeight: 44 }}
                >
                  Add Tee Time
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(250px, 1fr) 180px 160px 80px auto", gap: 24, alignItems: "end" }}>
              <div><Label>Course</Label><Select value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}><option value="">Select course...</option>{courses.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}</Select></div>
              <div><Label>Date</Label><Input type="date" min={utils.getTodayDate()} value={form.date} onChange={(e) => {
                const selectedDate = e.target.value;
                const today = utils.getTodayDate();
                if (selectedDate < today) {
                  addToast("Cannot create tee times for past dates. Please select today or a future date.", "error");
                  setForm({ ...form, date: "" });
                } else {
                  setForm({ ...form, date: selectedDate });
                }
              }} /></div>
              <div><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
              <div><Label>Slots</Label><Input type="number" min="1" max="8" value={form.slots} onChange={(e) => setForm({ ...form, slots: e.target.value })} /></div>
              <Button onClick={handleCreate} disabled={isReadOnly} style={{ background: isReadOnly ? "#9ca3af" : "#2d6a4f", color: "#fff", cursor: isReadOnly ? "not-allowed" : "pointer" }}>Add Tee Time</Button>
            </div>
          )}
        </>
      )}

      {/* Recurring Mode */}
      {mode === "recurring" && (
        <div style={{ display: "grid", gap: 12 }}>
          {isMobile ? (
            <>
              <div>
                <Label>Course</Label>
                <Select value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}>
                  <option value="">Select course...</option>
                  {courses.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}
                </Select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 160px) minmax(0, 140px)", gap: 16 }}>
                <div><Label>Start Date</Label><Input type="date" min={utils.getTodayDate()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
              </div>
              <div>
                <Label>Slots</Label>
                <Input type="number" min="1" max="8" value={form.slots} onChange={(e) => setForm({ ...form, slots: e.target.value })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <Label>Frequency</Label>
                  <Select value={recurring.frequency} onChange={(e) => setRecurring({ ...recurring, frequency: e.target.value })}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </Select>
                </div>
                <div>
                  <Label>Occurrences</Label>
                  <Input type="number" min="1" max="52" value={recurring.count} onChange={(e) => setRecurring({ ...recurring, count: utils.toInt(e.target.value) })} />
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(250px, 1fr) 180px 160px 80px", gap: 24 }}>
                <div><Label>Course</Label><Select value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}><option value="">Select course...</option>{courses.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}</Select></div>
                <div><Label>Start Date</Label><Input type="date" min={utils.getTodayDate()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
                <div><Label>Slots</Label><Input type="number" min="1" max="8" value={form.slots} onChange={(e) => setForm({ ...form, slots: e.target.value })} /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                <div>
                  <Label>Frequency</Label>
                  <Select value={recurring.frequency} onChange={(e) => setRecurring({ ...recurring, frequency: e.target.value })}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </Select>
                </div>
                <div>
                  <Label>Number of Occurrences</Label>
                  <Input type="number" min="1" max="52" value={recurring.count} onChange={(e) => setRecurring({ ...recurring, count: utils.toInt(e.target.value) })} />
                </div>
              </div>
            </>
          )}
          <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 8, padding: 12, fontSize: 13 }}>
            <strong>Preview:</strong> Will create {recurring.count} tee times, {recurring.frequency}, starting {form.date || 'on selected date'}. Helpful for loading tee times connected with leagues, golf association recurring tee times, etc.
          </div>
          <Button onClick={handleRecurringCreate} style={{ background: "#2d6a4f", color: "#fff", minHeight: isMobile ? 44 : undefined }}>Create {recurring.count} Recurring Tee Times</Button>
        </div>
      )}

      {/* Bulk Mode */}
      {mode === "bulk" && (
        <div style={{ display: "grid", gap: 12 }}>
          {isMobile ? (
            <>
              <div>
                <Label>Course</Label>
                <Select value={form.course} onChange={(e) => {
                  setForm({ ...form, course: e.target.value });
                  const selectedCourse = courses.find(c => c.name === e.target.value);
                  if (selectedCourse?.tee_time_interval) {
                    setBulkCreate(prev => ({ ...prev, interval: selectedCourse.tee_time_interval }));
                  }
                }}>
                  <option value="">Select course...</option>
                  {courses.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}
                </Select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 12 }}>
                <div><Label>Date</Label><Input type="date" min={utils.getTodayDate()} value={bulkCreate.date} onChange={(e) => setBulkCreate({ ...bulkCreate, date: e.target.value })} /></div>
                <div><Label>Slots</Label><Input type="number" min="1" max="8" value={form.slots} onChange={(e) => setForm({ ...form, slots: e.target.value })} /></div>
              </div>
              <div>
                <Label>Start Time</Label>
                <Input type="time" value={bulkCreate.startTime} onChange={(e) => setBulkCreate({ ...bulkCreate, startTime: e.target.value })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <Label>Interval (min)</Label>
                  <Input type="number" min="5" max="60" value={bulkCreate.interval} onChange={(e) => setBulkCreate({ ...bulkCreate, interval: e.target.value })} placeholder="10" />
                </div>
                <div>
                  <Label># of Tee Times</Label>
                  <Input type="number" min="1" max="40" value={bulkCreate.count} onChange={(e) => setBulkCreate({ ...bulkCreate, count: e.target.value })} placeholder="10" />
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(250px, 1fr) 180px 80px", gap: 24 }}>
                <div><Label>Course</Label><Select value={form.course} onChange={(e) => {
                  setForm({ ...form, course: e.target.value });
                  const selectedCourse = courses.find(c => c.name === e.target.value);
                  if (selectedCourse?.tee_time_interval) {
                    setBulkCreate(prev => ({ ...prev, interval: selectedCourse.tee_time_interval }));
                  }
                }}><option value="">Select course...</option>{courses.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}</Select></div>
                <div><Label>Date</Label><Input type="date" min={utils.getTodayDate()} value={bulkCreate.date} onChange={(e) => setBulkCreate({ ...bulkCreate, date: e.target.value })} /></div>
                <div><Label>Slots</Label><Input type="number" min="1" max="8" value={form.slots} onChange={(e) => setForm({ ...form, slots: e.target.value })} /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24 }}>
                <div>
                  <Label>Start Time</Label>
                  <Input type="time" value={bulkCreate.startTime} onChange={(e) => setBulkCreate({ ...bulkCreate, startTime: e.target.value })} />
                </div>
                <div>
                  <Label>Interval (minutes)</Label>
                  <Input type="number" min="5" max="60" value={bulkCreate.interval} onChange={(e) => setBulkCreate({ ...bulkCreate, interval: e.target.value })} placeholder="10" />
                </div>
                <div>
                  <Label># of Tee Times</Label>
                  <Input type="number" min="1" max="40" value={bulkCreate.count} onChange={(e) => setBulkCreate({ ...bulkCreate, count: e.target.value })} placeholder="10" />
                </div>
              </div>
            </>
          )}
          <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 8, padding: 12, fontSize: 13 }}>
            <strong>Preview:</strong> {(() => {
              if (!bulkCreate.startTime || !bulkCreate.count) return 'Enter a start time and number of tee times to see preview.';
              const [sh, sm] = bulkCreate.startTime.split(':').map(Number);
              const count = utils.toInt(bulkCreate.count);
              const interval = utils.toInt(bulkCreate.interval);
              const lastMinutes = (sh * 60 + sm) + (count - 1) * interval;
              const lastH = Math.floor(lastMinutes / 60);
              const lastM = lastMinutes % 60;
              const lastTime = `${lastH % 12 || 12}:${String(lastM).padStart(2, '0')} ${lastH >= 12 ? 'PM' : 'AM'}`;
              const firstTime = `${sh % 12 || 12}:${String(sm).padStart(2, '0')} ${sh >= 12 ? 'PM' : 'AM'}`;
              return `${count} tee times from ${firstTime} to ${lastTime}, every ${interval} minutes.`;
            })()}
          </div>
          <Button onClick={handleBulkCreate} style={{ background: "#2d6a4f", color: "#fff", minHeight: isMobile ? 44 : undefined }}>Create Bulk Tee Times</Button>
        </div>
      )}

      {/* ================================================================
          SHARED OPTIONS ROW — applies to ALL THREE modes (simple/recurring/bulk).
          `form` is shared across modes, so one control governs all of them; this
          deliberately avoids triplicating the same JSX per mode (the maintenance
          hazard called out in Nine_Hole_Scoring_Plan_v1_4 §7a for the board renderers).

          Private was previously in the simple-mode block ONLY — recurring and bulk
          could not be made private at all, and their handlers dropped the field.
          That was an oversight, not a decision (the shared `form.private` state already
          existed); fixed here. An organizer creating a member-guest or outing in bulk
          is exactly the case that needs it.

          competition_hole_set (Nine_Hole_Scoring_Plan_v1_4 §6): which holes the
          COMPETITION scores. Radios, not checkboxes — the three values are mutually
          exclusive and the browser enforces that for free.
          ================================================================ */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
        <input 
          type="checkbox" 
          id="private-checkbox" 
          checked={form.private || false}
          onChange={(e) => setForm({ ...form, private: e.target.checked })}
          style={{ width: 16, height: 16 }}
        />
        <label 
          htmlFor="private-checkbox" 
          style={{ fontSize: 14, cursor: "pointer" }}
          title="Private tee times are not shown in the player portal and cannot be published to all players. Use for private outings, board meetings, or member-guest events."
        >
          Private Tee Time
        </label>
        <InfoTip text="Private tee times are not shown in the player portal and cannot be published to all players. Use for private outings, board meetings, or member-guest events." />

        {/* Event (Slice 1) — sits on the Private line to save vertical space; gated on
            the org's events_enabled flag and simple mode only. Ticking it reveals the
            name/description fields below Start Type. */}
        {mode === "simple" && !eventOpen && <EventPassLock style={{ marginLeft: 12 }} />}
        {eventOpen && mode === "simple" && (
          <>
            <input
              type="checkbox"
              id="event-checkbox"
              checked={form.is_event || false}
              onChange={(e) => setForm({ ...form, is_event: e.target.checked })}
              style={{ width: 16, height: 16, marginLeft: 12 }}
            />
            <label htmlFor="event-checkbox" style={{ fontSize: 14, cursor: "pointer" }}>
              This is an event
            </label>
            <InfoTip text="Mark this tee time as an event (an outing, tournament, or special day). You can name it now; event setup will grow over time." />
            <span style={{ marginLeft: 4 }}><EventGuidesLink /></span>
          </>
        )}

        {/* Hole set — right-aligned on wide screens, wraps under on narrow */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" }}>
          {[
            { value: "all18", label: "18H" },
            { value: "front9", label: "F9" },
            { value: "back9", label: "B9" }
          ].map(opt => (
            <label 
              key={opt.value}
              style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 14, cursor: "pointer" }}
              title={opt.value === "all18" ? "Score all 18 holes" : `Score only the ${opt.value === "front9" ? "front" : "back"} nine`}
            >
              <input 
                type="radio" 
                name="competition-hole-set" 
                value={opt.value}
                checked={(form.competition_hole_set || "all18") === opt.value}
                onChange={(e) => setForm({ ...form, competition_hole_set: e.target.value })}
                style={{ width: 16, height: 16 }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* Start Type — shotgun vs sequential tee times. A tee-time property
          (shotgun_mode); sets the MODE only. Starting holes for a shotgun are
          assigned later, at pairings — not here. Radios like the hole-set above. */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>Start Type</span>
        {[
          { value: false, label: "Tee Times" },
          { value: true, label: "Shotgun" }
        ].map(opt => (
          <label
            key={String(opt.value)}
            style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 14, cursor: "pointer" }}
          >
            <input
              type="radio"
              name="start-type"
              checked={(form.shotgun_mode || false) === opt.value}
              onChange={() => setForm({ ...form, shotgun_mode: opt.value })}
              style={{ width: 16, height: 16 }}
            />
            {opt.label}
          </label>
        ))}
        <InfoTip text="Shotgun: all groups start at once from different holes. Tee Times: groups start in sequence. Starting holes for a shotgun are assigned later, when you set pairings." />
      </div>

      {/* Optional title (Sept 21, 2026) — a plain label for the round: "TRKMGL
          League", "NC Road Trip", or a tournament another organization runs that
          this group is only playing in. Shared across all three modes, like the
          options row above, so a bulk-created league carries one label.
          HIDDEN when "This is an event" is ticked: an event is named in Event
          Setup moments later, and two name boxes on one screen is exactly the
          confusion a separate title was meant to avoid. */}
      {!(eventsEnabled && mode === "simple" && form.is_event) && (
        <div style={{ marginTop: 8 }}>
          <label htmlFor="teetime-title" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
            Title <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <input
              id="teetime-title"
              type="text"
              maxLength={60}
              value={form.title || ""}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Optional Event Title"
              style={{ flex: "1 1 260px", minWidth: 0, padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14 }}
            />
            <InfoTip text="An optional name for this round, shown on the tee time in the player app. Use it for a league, a trip, or a tournament another group runs that you are just playing in. It does not create an event." />
          </div>
        </div>
      )}

      {/* Event note (Slice 3a) — before save, ticking only flags it; the Event Setup
          modal opens on the new tee time right after it's created. */}
      {eventsEnabled && mode === "simple" && form.is_event && (
        <div style={{ marginTop: 8, fontSize: 13, color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 10px" }}>
          📅 You'll name and set up this event right after you create the tee time.
        </div>
      )}


      {/* 9-hole ratings unavailable warning (Master_Course_DB_Plan_v1_5 §12.8.1).
          A WARNING, not a block — the organizer may still run the round; it simply
          scores gross-only. Inline, not a toast: toasts vanish, and this must stay
          visible while the organizer decides. */}
      {form.competition_hole_set && form.competition_hole_set !== "all18" && form.course && (() => {
        const selected = courses.find(c => c.name === form.course);
        if (!selected || (selected.nine_hole_tee_count ?? 0) > 0) return null;
        return (
          <div style={{ marginTop: 8, padding: "10px 12px", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, fontSize: 13, lineHeight: 1.5 }}>
            <strong>Nine-hole ratings are estimated for this course</strong>
            <div style={{ marginTop: 4, color: "#4b5563" }}>
              Official front-nine and back-nine ratings aren't on file, so net scores and games will use a 9-hole handicap estimated from the 18-hole rating, marked "est". Strokes between players are rarely affected, but a league that alternates nines should use the official ratings.
            </div>
            <div style={{ marginTop: 6, color: "#4b5563" }}>
              Your pro shop or handicap chair can pull them from GHIN — the Handicap Index &amp; Course/Playing Handicap Report, run with Holes set to 9. Send it to <a href="mailto:support@teetimeorganizer.net" style={{ color: "#2d6a4f" }}>support@teetimeorganizer.net</a> and we'll load the official ratings.
            </div>
          </div>
        );
      })()}
      
      {/* Publish Modal */}
      {showPublishOptions && (
        <PublishModal
          teeTimeInfo={{
            course: form.course,
            date: form.date,
            time: form.time,
            slots: form.slots
          }}
          players={players}
          groups={groups}
          onConfirm={handlePublishConfirm}
          onClose={() => setShowPublishOptions(false)}
          isPublishing={isPublishing}
        />
      )}
    </Card>
  );
};

const EditTeeTimeModal = ({ teeTime, onSave, onCancel, eventsEnabled = false, canCreateEvent, teamScoringEnabled = false, event = null, onSaveEvent, onOpenGames, deskMode = false }) => {
  // Event Pass (Sept 28, 2026): marking THIS tee time as a new event needs a pass the
  // org can use now; an existing event is always shown as before.
  const eventOpen = canCreateEvent === undefined ? eventsEnabled : !!canCreateEvent;
  const { addToast } = useToast();
  const { courses } = useCourses();

  // Event (Slice 2) — view/edit the linked event from the Edit modal. Initialized
  // from the event prop; a separate slice of state from the tee-time form.
  // scoring_mode (Team scoring, Sept 13, 2026) travels with it: 'individual' | 'team'.
  // public_slug / public_url / slug_printed_at (Sept 16, 2026) ride along for the
  // Event Setup address box; a change is staged as public_slug_new (+ confirm_reprint)
  // and sent when the Edit modal saves.
  const [eventForm, setEventForm] = useState({
    is_event: !!event,
    name: event ? (event.name || "") : "",
    description: event ? (event.description || "") : "",
    scoring_mode: event ? (event.scoring_mode || "individual") : "individual",
    postable_to_ghin: event ? (event.postable_to_ghin !== false) : true,
    public_slug: event ? (event.public_slug || null) : null,
    public_url: event ? (event.public_url || null) : null,
    slug_printed_at: event ? (event.slug_printed_at || null) : null,
    logo_url: event ? (event.logo_url || null) : null
  });
  useEffect(() => {
    setEventForm({
      is_event: !!event,
      name: event ? (event.name || "") : "",
      description: event ? (event.description || "") : "",
      scoring_mode: event ? (event.scoring_mode || "individual") : "individual",
      postable_to_ghin: event ? (event.postable_to_ghin !== false) : true,
      public_slug: event ? (event.public_slug || null) : null,
      public_url: event ? (event.public_url || null) : null,
      slug_printed_at: event ? (event.slug_printed_at || null) : null,
      logo_url: event ? (event.logo_url || null) : null
    });
  }, [event]);
  const [showEventSetup, setShowEventSetup] = useState(false); // Slice 3a — Event Setup modal

  // Helper to extract date in YYYY-MM-DD format
  const extractDate = (dateValue) => {
    if (!dateValue) return "";
    if (typeof dateValue === 'string') {
      return dateValue.split('T')[0];
    } else if (dateValue instanceof Date) {
      return dateValue.toISOString().split('T')[0];
    }
    return "";
  };
  
  const [form, setForm] = useState({
    course: teeTime.course || "",
    date: extractDate(teeTime.date),
    time: teeTime.time || "",
    slots: String(teeTime.slots || "4"),
    status: teeTime.status || "Draft",
    private: teeTime.private || false,
    locked: teeTime.locked || false,
    competition_hole_set: teeTime.competition_hole_set || "all18",
      shotgun_mode: teeTime.shotgun_mode || false,
    title: teeTime.title || ""
  });

  // Update form when teeTime prop changes
  useEffect(() => {
    const dateValue = extractDate(teeTime.date);
    
    setForm({
      course: teeTime.course || "",
      date: dateValue,
      time: teeTime.time || "",
      slots: String(teeTime.slots || "4"),
      status: teeTime.status || "Draft",
      private: teeTime.private || false,
      locked: teeTime.locked || false,
      competition_hole_set: teeTime.competition_hole_set || "all18",
      shotgun_mode: teeTime.shotgun_mode || false,
      title: teeTime.title || ""
    });
  }, [teeTime]);

  const handleSave = async () => {
    if (!form.course || !form.date || !form.time) {
      addToast("Please fill in all required fields", "error");
      return;
    }

    // Validate date is not in the past
    const today = utils.getTodayDate();

    if (form.date < today) {
      addToast("Cannot set tee time to a past date", "error");
      return;
    }

    // Event (Slice 2): if marked as an event, a name is required.
    if (eventsEnabled && eventForm.is_event && !String(eventForm.name || "").trim()) {
      addToast("Please enter an event name", "error");
      return;
    }
    
    // VALIDATION: Prevent changing from Published to Draft if there are active players
    if (teeTime.status === "Published" && form.status === "Draft") {
      const playerCount = utils.safePlayerCount(teeTime.players);
      if (playerCount > 0) {
        addToast(`Cannot change to Draft status. This tee time has ${playerCount} active player${playerCount > 1 ? 's' : ''}.`, "error");
        return;
      }
    }

    // Only send fields needed for teetimes table update
    // Players/waitlist are in tee_time_players table and handled separately
    // This prevents 413 Payload Too Large errors when tee times have many players
    const updatedTeeTime = {
      id: teeTime.id,
      course: form.course,
      course_id: teeTime.course_id, // Preserve course_id for backend
      date: form.date,
      time: form.time,
      slots: utils.toInt(form.slots),
      status: form.status,
      notes: teeTime.notes || "",
      private: form.private,
      locked: form.locked,
      competition_hole_set: form.competition_hole_set || "all18",
        shotgun_mode: form.shotgun_mode || false,
      title: String(form.title || "").trim() || null
    };
    
    // Event (Slice 2): reconcile the linked event (create / update / remove) before
    // saving the tee time. Independent of the tee-time fields, so it runs regardless
    // of which save path the parent takes. Errors surface as a toast in the parent.
    if (eventsEnabled && onSaveEvent) {
      await onSaveEvent(teeTime.id, {
        is_event: eventForm.is_event,
        name: String(eventForm.name || "").trim(),
        description: String(eventForm.description || "").trim() || null,
        scoring_mode: eventForm.scoring_mode || "individual",
        postable_to_ghin: eventForm.postable_to_ghin !== false,
        public_slug: eventForm.public_slug_new,          // undefined = address untouched
        confirm_reprint: eventForm.confirm_reprint === true ? true : undefined,
        logo_file: eventForm.logo_file,                    // undefined = logo untouched
        logo_name: eventForm.logo_name,
        remove_logo: eventForm.remove_logo === true ? true : undefined
      });
    }

    // Pass original teeTime data for frontend processing (waitlist promotion, etc.)
    // but the API call will only send the minimal updatedTeeTime
    onSave(updatedTeeTime, teeTime);
  };

  return (
    <div 
      style={{ 
        position: "fixed", 
        inset: 0, 
        background: "rgba(0,0,0,.4)", 
        backdropFilter: "blur(2px)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        zIndex: 60 
      }} 
      onClick={onCancel}
    >
      <Card
        style={{ width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 4 }}>✏️ Edit Tee Time</div>
          <div style={{ color: "#6b7280", fontSize: 14 }}>Update tee time details</div>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          {/* Course Selection */}
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
              Course <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <Select 
              value={form.course} 
              onChange={(e) => setForm({ ...form, course: e.target.value })}
              style={{ width: "100%" }}
            >
              <option value="">Select a course</option>
              {courses.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </Select>
          </div>

          {/* Date and Time */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                Date <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <Input 
                type="date" 
                value={form.date} 
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                Time <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <Input 
                type="time" 
                value={form.time} 
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {/* Slots */}
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
              Slots <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <Input 
              type="number" 
              min="1" 
              max="10"
              value={form.slots} 
              onChange={(e) => setForm({ ...form, slots: e.target.value })}
              style={{ width: "100%" }}
            />
            {utils.toInt(form.slots) < utils.safePlayerCount(teeTime.players) && (
              <div style={{ 
                marginTop: 6, 
                padding: "6px 10px", 
                background: "#fef3c7", 
                color: "#92400e", 
                fontSize: 12, 
                borderRadius: 6,
                border: "1px solid #fbbf24"
              }}>
                ⚠️ Warning: Reducing slots below current player count ({utils.safePlayerCount(teeTime.players)})
              </div>
            )}
          </div>
          
          {/* Status */}
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
              Status <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <Select 
              value={form.status} 
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              style={{ width: "100%" }}
            >
              <option value="Draft">Draft</option>
              <option value="Published">Published</option>
            </Select>
            {teeTime.status === "Published" && form.status === "Draft" && utils.safePlayerCount(teeTime.players) > 0 && (
              <div style={{ 
                marginTop: 6, 
                padding: "6px 10px", 
                background: "#fee2e2", 
                color: "#991b1b", 
                fontSize: 12, 
                borderRadius: 6,
                border: "1px solid #fca5a5"
              }}>
                ⚠️ Cannot change to Draft: {utils.safePlayerCount(teeTime.players)} player{utils.safePlayerCount(teeTime.players) > 1 ? 's' : ''} registered
              </div>
            )}
          </div>

          {/* Private Tee Time Checkbox */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input 
              type="checkbox" 
              id="edit-private-checkbox" 
              checked={form.private || false}
              onChange={(e) => setForm({ ...form, private: e.target.checked })}
              style={{ width: 16, height: 16 }}
            />
            <label 
              htmlFor="edit-private-checkbox" 
              style={{ fontSize: 14, cursor: "pointer" }}
              title="Private tee times are not shown in the player portal and cannot be published to all players"
            >
              Private Tee Time
            </label>
            <InfoTip text="Private tee times are not shown in the player portal and cannot be published to all players" />
          </div>

          {/* Start Type — shotgun vs sequential. Mode only; starting holes assigned at pairings. */}
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
              Start Type
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {[
                { value: false, label: "Tee Times" },
                { value: true, label: "Shotgun" }
              ].map(opt => (
                <label
                  key={String(opt.value)}
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer" }}
                >
                  <input
                    type="radio"
                    name="edit-start-type"
                    checked={(form.shotgun_mode || false) === opt.value}
                    onChange={() => setForm({ ...form, shotgun_mode: opt.value })}
                    style={{ width: 16, height: 16 }}
                  />
                  {opt.label}
                </label>
              ))}
              <InfoTip text="Shotgun: all groups start at once from different holes. Tee Times: groups start in sequence. Starting holes for a shotgun are assigned when you set pairings." />
            </div>
          </div>

          {/* Optional title (Sept 21, 2026) — see CreateTeeTimeInline. Hidden while
              this tee time is an event: the event's own name is the title then, and
              the card renders that instead. Clearing the box clears the title (the
              PUT distinguishes an omitted key from an empty one). */}
          {!(eventsEnabled && eventForm.is_event) && (
            <div>
              <label htmlFor="edit-teetime-title" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                Title <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  id="edit-teetime-title"
                  type="text"
                  maxLength={60}
                  value={form.title || ""}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Optional Event Title"
                  style={{ flex: "1 1 auto", minWidth: 0, padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, boxSizing: "border-box" }}
                />
                <InfoTip text="An optional name for this round, shown on the tee time in the player app. Use it for a league, a trip, or a tournament another group runs that you are just playing in. It does not create an event." />
              </div>
            </div>
          )}

          {/* Event (Slice 2) — view / edit / add / remove the linked event. Gated on
              the org's events_enabled flag. Ticking reveals name + description; saving
              creates, updates, or (when un-ticked) removes the event. */}
          {!deskMode && !event && !eventOpen && (
            <div><EventPassLock /></div>
          )}
          {eventsEnabled && (event || eventOpen || deskMode) && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  id="edit-event-checkbox"
                  disabled={deskMode && !!event}
                  checked={eventForm.is_event || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEventForm({ ...eventForm, is_event: checked });
                    if (checked && !String(eventForm.name || "").trim()) setShowEventSetup(true);
                  }}
                  style={{ width: 16, height: 16 }}
                />
                <label htmlFor="edit-event-checkbox" style={{ fontSize: 14, cursor: "pointer" }}>
                  This is an event
                </label>
                <InfoTip text="Mark this tee time as an event and give it a name. Un-tick to remove the event; the tee time stays." />
                {!deskMode && <EventGuidesLink />}
                {deskMode && event && <span style={{ fontSize: 12, color: "#6b7280" }}>Only the organizer can remove the event.</span>}
              </div>

              {eventForm.is_event && (
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  {String(eventForm.name || "").trim() ? (
                    <>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#dcfce7", color: "#166534", padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                        📅 {eventForm.name}{eventForm.scoring_mode === "team" ? <span style={{ fontWeight: 500, opacity: 0.85 }}>· 👥 team score</span> : eventForm.postable_to_ghin === false ? <span style={{ fontWeight: 500, opacity: 0.85 }}>· no GHIN posting</span> : null}
                      </span>
                      <button type="button" onClick={() => setShowEventSetup(true)} style={{ background: "none", border: "none", color: "#2d6a4f", fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0 }}>
                        Edit event ▸
                      </button>
                      {(eventForm.public_slug_new || eventForm.public_slug) && (
                        <span style={{ fontSize: 12, color: "#6b7280", flexBasis: "100%" }}>🏆 {PUBLIC_BOARD_HOST}/{eventForm.public_slug_new || eventForm.public_slug}{eventForm.public_slug_new && eventForm.public_slug_new !== eventForm.public_slug ? " (new — saved with the tee time)" : ""}</span>
                      )}
                    </>
                  ) : (
                    <button type="button" onClick={() => setShowEventSetup(true)} style={{ background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                      Set up event ▸
                    </button>
                  )}
                </div>
              )}

              {showEventSetup && (
                <EventSetupModal
                  initial={{ name: eventForm.name, description: eventForm.description, scoring_mode: eventForm.scoring_mode, postable_to_ghin: eventForm.postable_to_ghin, public_slug: eventForm.public_slug, public_url: eventForm.public_url, slug_printed_at: eventForm.slug_printed_at, logo_url: eventForm.remove_logo ? null : eventForm.logo_url }}
                  teetimeId={teeTime?.id}
                  eventId={event ? event.id : null}
                  deskMode={deskMode}
                  date={form.date}
                  teamScoringEnabled={teamScoringEnabled}
                  onOpenGames={onOpenGames ? () => { setShowEventSetup(false); onOpenGames(); } : undefined}
                  slots={form.slots}
                  onSave={(d) => {
                    setEventForm({
                      ...eventForm, is_event: true, name: d.name, description: d.description || "", scoring_mode: d.scoring_mode || "individual", postable_to_ghin: d.postable_to_ghin !== false,
                      ...(d.public_slug !== undefined ? { public_slug_new: d.public_slug, confirm_reprint: d.confirm_reprint === true } : {}),
                      ...(d.logo_file ? { logo_file: d.logo_file, logo_name: d.logo_name, remove_logo: false } : {}),
                      ...(d.remove_logo ? { remove_logo: true, logo_file: undefined, logo_name: undefined } : {})
                    });
                    setShowEventSetup(false);
                  }}
                  onClose={() => setShowEventSetup(false)}
                />
              )}
            </div>
          )}

          {/* Lock Tee Time Checkbox */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input 
              type="checkbox" 
              id="edit-locked-checkbox" 
              checked={form.locked || false}
              onChange={(e) => setForm({ ...form, locked: e.target.checked })}
              style={{ width: 16, height: 16 }}
            />
            <label 
              htmlFor="edit-locked-checkbox" 
              style={{ fontSize: 14, cursor: "pointer" }}
              title="Locked tee times do not accept new RSVPs from players or invite links"
            >
              🔒 Lock (no new RSVPs)
            </label>
            <InfoTip text="Locked tee times do not accept new RSVPs from players or invite links" />
          </div>

          {/* Competition hole set (Nine_Hole_Scoring_Plan_v1_4 §6).
              Editable AFTER creation on purpose: an organizer with a season already
              scheduled (a recurring league, say) must be able to flip an existing round
              to F9/B9 without deleting and rebuilding it. The backend PUT uses COALESCE
              so a caller omitting this field preserves the stored value (Rule 22) —
              this modal now sends it explicitly. */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 14, color: "#374151" }}>Scoring</span>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" }}>
              {[
                { value: "all18", label: "18H" },
                { value: "front9", label: "F9" },
                { value: "back9", label: "B9" }
              ].map(opt => (
                <label 
                  key={opt.value}
                  style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 14, cursor: "pointer" }}
                  title={opt.value === "all18" ? "Score all 18 holes" : `Score only the ${opt.value === "front9" ? "front" : "back"} nine`}
                >
                  <input 
                    type="radio" 
                    name="edit-competition-hole-set" 
                    value={opt.value}
                    checked={(form.competition_hole_set || "all18") === opt.value}
                    onChange={(e) => setForm({ ...form, competition_hole_set: e.target.value })}
                    style={{ width: 16, height: 16 }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* 9-hole ratings unavailable (Master_Course_DB_Plan_v1_5 §12.8.1) */}
          {form.competition_hole_set && form.competition_hole_set !== "all18" && form.course && (() => {
            const selected = courses.find(c => c.name === form.course);
            if (!selected || (selected.nine_hole_tee_count ?? 0) > 0) return null;
            return (
              <div style={{ padding: "10px 12px", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, fontSize: 13, lineHeight: 1.5 }}>
                <strong>Nine-hole ratings are estimated for this course</strong>
                <div style={{ marginTop: 4, color: "#4b5563" }}>
                  Net scores and games will use a 9-hole handicap estimated from the 18-hole rating, marked "est". For the official ratings, ask your pro shop or handicap chair for GHIN's Handicap Index &amp; Course/Playing Handicap Report, run with Holes set to 9, and send it to <a href="mailto:support@teetimeorganizer.net" style={{ color: "#2d6a4f" }}>support@teetimeorganizer.net</a>.
                </div>
              </div>
            );
          })()}

          {/* Current Players Info */}
          <div style={{ 
            padding: 12, 
            background: "#f9fafb", 
            borderRadius: 8, 
            border: "1px solid #e5e7eb" 
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#6b7280" }}>
              Current Status
            </div>
            <div style={{ fontSize: 13 }}>
              <div>Players: {utils.safePlayerCount(teeTime.players)} / {teeTime.slots}</div>
              {Array.isArray(teeTime.waitlist) && teeTime.waitlist.length > 0 && (
                <div>Waitlist: {teeTime.waitlist.length}</div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              style={{ background: "#2d6a4f", color: "#fff" }}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// InviteLinkModal - Shows shareable invite link for a tee time
const InviteLinkModal = ({ teeTime, onClose }) => {
  const { addToast } = useToast();
  const [inviteUrl, setInviteUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  // Oct 28, 2026 — a link forwarded further than meant can be turned off or replaced.
  // GET reads the link without making one, so a link turned off stays off. When the GET is
  // refused (the Event Desk can only POST), fall back to the old POST and hide the controls.
  const [isOff, setIsOff] = useState(false);
  const [canManage, setCanManage] = useState(true);
  const [confirmAct, setConfirmAct] = useState(null);   // 'new' | 'off'
  const [busy, setBusy] = useState(false);

  const inviteHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };
  const makeLink = async (replace) => {
    const response = await fetch(`${API_URL}/teetimes/${teeTime.id}/invite-code`, {
      method: 'POST', headers: inviteHeaders(), credentials: 'include',
      body: JSON.stringify(replace ? { replace: true } : {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Failed to generate invite code');
    setInviteUrl(data.inviteUrl || ''); setIsOff(!data.inviteUrl);
    return data;
  };

  useEffect(() => {
    const fetchInviteCode = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/teetimes/${teeTime.id}/invite-code`, { headers: inviteHeaders(), credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setInviteUrl(data.inviteUrl || ''); setIsOff(!data.inviteUrl);
        } else {
          setCanManage(false);
          await makeLink(false);
        }
      } catch (error) {
        console.error('Error generating invite code:', error);
        addToast('Failed to generate invite link', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (teeTime?.id) {
      fetchInviteCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teeTime?.id, addToast]);

  const doNewLink = async () => {
    setBusy(true);
    try { await makeLink(true); addToast('New invite link made — the old one no longer works', 'success'); }
    catch (e) { addToast(e.message || 'Could not make a new link', 'error'); }
    finally { setBusy(false); setConfirmAct(null); }
  };
  const doTurnOff = async () => {
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}/teetimes/${teeTime.id}/invite-code`, { method: 'DELETE', headers: inviteHeaders(), credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not turn the link off');
      setInviteUrl(''); setIsOff(true);
      addToast('Invite link turned off', 'success');
    } catch (e) { addToast(e.message || 'Could not turn the link off', 'error'); }
    finally { setBusy(false); setConfirmAct(null); }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      addToast('Invite link copied to clipboard!', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = inviteUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      addToast('Invite link copied to clipboard!', 'success');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div 
      style={{ 
        position: "fixed", 
        inset: 0, 
        background: "rgba(0,0,0,.4)", 
        backdropFilter: "blur(2px)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        zIndex: 60 
      }} 
      onClick={onClose}
    >
      <Card 
        style={{ width: "100%", maxWidth: 500 }} 
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 4 }}>🎟️ Invite Link</div>
          <div style={{ color: "#6b7280", fontSize: 14 }}>Share this link to invite players to this tee time</div>
        </div>

        {/* Tee Time Info */}
        <div style={{ 
          padding: 12, 
          background: "#f0fdf4", 
          borderRadius: 8, 
          marginBottom: 16,
          border: "1px solid #bbf7d0"
        }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{teeTime.course}</div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
            {utils.formatDate(teeTime.date)} • {utils.formatTime(teeTime.time)}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            {utils.safePlayerCount(teeTime.players)}/{teeTime.slots} spots filled
            {teeTime.locked && <span style={{ color: "#dc2626", marginLeft: 8 }}>🔒 Locked</span>}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <div style={{ fontSize: 14, color: "#6b7280" }}>Generating invite link...</div>
          </div>
        ) : isOff ? (
          <div data-testid="invite-link-off" style={{ padding: 14, background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 4 }}>The invite link is off.</div>
            <div style={{ fontSize: 13, color: "#4b5563", marginBottom: 12 }}>Anyone who opens an old link is told it is no longer active. Make a new link when you want to invite players again.</div>
            <Button data-testid="invite-make-link" disabled={busy} onClick={doNewLink} style={{ background: "#2d6a4f", color: "#fff" }}>{busy ? 'Making…' : 'Make a new link'}</Button>
          </div>
        ) : (
          <>
            {/* Invite URL Box */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                Shareable Link
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  value={inviteUrl}
                  readOnly
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    fontSize: 13,
                    background: "#f9fafb",
                    color: "#374151"
                  }}
                />
                <Button 
                  onClick={handleCopy}
                  style={{ 
                    background: copied ? "#16a34a" : "#2d6a4f", 
                    color: "#fff",
                    minWidth: 100
                  }}
                >
                  {copied ? "✓ Copied!" : "📋 Copy"}
                </Button>
              </div>
            </div>

            {/* Instructions */}
            <div style={{ 
              padding: 12, 
              background: "#fffbeb", 
              borderRadius: 8, 
              border: "1px solid #fde68a",
              marginBottom: 16
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#92400e", marginBottom: 6 }}>
                How it works:
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: "#78350f" }}>
                <li>Share this link via email, text, or any messaging app</li>
                <li>Recipients enter their name and email to RSVP</li>
                <li>New players are automatically added as guests</li>
                <li>Everyone receives a confirmation email</li>
              </ul>
            </div>

            {/* Oct 28, 2026: turn the link off, or replace it, when it has gone further than meant. */}
            {canManage && (
              <div data-testid="invite-manage" style={{ padding: 12, background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb", marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Link gone further than you meant?</div>
                {confirmAct ? (
                  <div>
                    <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
                      {confirmAct === 'new'
                        ? 'Make a new link? The current link stops working. Anyone who hasn\u2019t signed up yet will need the new one. An event page\u2019s Sign up button switches to the new link by itself.'
                        : 'Turn the link off? Nobody new can sign up through it, and an event page\u2019s Sign up button is hidden until you make a new link. Players already signed up keep their spots.'}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Button data-testid="invite-confirm" disabled={busy} onClick={confirmAct === 'new' ? doNewLink : doTurnOff} style={{ background: confirmAct === 'off' ? "#b91c1c" : "#2d6a4f", color: "#fff" }}>{busy ? 'Working…' : (confirmAct === 'new' ? 'Yes, make a new link' : 'Yes, turn it off')}</Button>
                      <Button variant="outline" disabled={busy} onClick={() => setConfirmAct(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button variant="outline" data-testid="invite-new" onClick={() => setConfirmAct('new')}>Make a new link</Button>
                    <Button variant="outline" data-testid="invite-off" onClick={() => setConfirmAct('off')} style={{ color: "#b91c1c", borderColor: "#fca5a5" }}>Turn the link off</Button>
                  </div>
                )}
              </div>
            )}

            {/* Warning if locked */}
            {teeTime.locked && (
              <div style={{ 
                padding: 12, 
                background: "#fee2e2", 
                borderRadius: 8, 
                border: "1px solid #fecaca",
                marginBottom: 16
              }}>
                <div style={{ fontSize: 13, color: "#991b1b" }}>
                  ⚠️ This tee time is <strong>locked</strong>. New RSVPs will be rejected until you unlock it.
                </div>
              </div>
            )}
          </>
        )}

        {/* Close Button */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </Card>
    </div>
  );
};

// ============================================================================
// GAME SETUP MODAL - Games Module Session 3
// Organizer selects games and sets wagers for a tee time.
// Only visible when games_enabled = true for the organization.
// ============================================================================
// ============================================================================
// SendSettlementEmailModal
// Lets organizer choose which tee-time players to email and add an optional note
// ============================================================================
const SendSettlementEmailModal = ({ teeTime, onClose, onSend, sending }) => {
  const players = Array.isArray(teeTime.players) ? teeTime.players : [];
  const [selectedIds, setSelectedIds] = useState(players.map(p => p.id));
  const [note, setNote] = useState('');

  const togglePlayer = (id) => setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const allSelected = selectedIds.length === players.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : players.map(p => p.id));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>📧 Send Settlement Emails</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20, lineHeight: 1.5 }}>
          Each player receives their net total, who to pay or collect from, and the full scorecard.
        </div>

        {/* Player list */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Recipients</div>
            <button onClick={toggleAll} style={{ fontSize: 12, color: '#1d4ed8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            {players.length === 0 ? (
              <div style={{ padding: 16, color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>No players on this tee time</div>
            ) : players.map((player, i) => {
              const checked = selectedIds.includes(player.id);
              return (
                <div key={player.id}
                  onClick={() => togglePlayer(player.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: checked ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#f9fafb', cursor: 'pointer', borderBottom: i < players.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <input type="checkbox" checked={checked} onChange={() => togglePlayer(player.id)} onClick={e => e.stopPropagation()} style={{ cursor: 'pointer' }} />
                  <span style={{ fontSize: 14, fontWeight: checked ? 600 : 400, color: '#1f2937' }}>{player.name}</span>
                  {!player.email && <span style={{ fontSize: 11, color: '#f59e0b', marginLeft: 'auto' }}>No email</span>}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
            {selectedIds.length} of {players.length} player{players.length !== 1 ? 's' : ''} selected
          </div>
        </div>

        {/* Note field */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
            Note from Organizer <span style={{ fontWeight: 400, color: '#6b7280' }}>(Optional)</span>
          </label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Great round today! Payments are due by Friday..."
            maxLength={500}
            rows={3}
            style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
          />
          <div style={{ fontSize: 11, color: note.length > 450 ? '#dc2626' : '#9ca3af', textAlign: 'right', marginTop: 2 }}>
            {note.length}/500
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Cancel
          </button>
          <button
            onClick={() => onSend({ player_ids: selectedIds, note: note.trim() })}
            disabled={sending || selectedIds.length === 0}
            style={{ flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: selectedIds.length === 0 || sending ? '#9ca3af' : '#1d4ed8', color: '#fff', cursor: selectedIds.length === 0 || sending ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}
          >
            {sending ? '📤 Sending...' : `📧 Send to ${selectedIds.length} Player${selectedIds.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ScrambleGamePanel — config for the TEAM-scoped Captain's Choice (Scramble) game
// (Sept 14, 2026, recap 6b). Rendered inside GameSetupModal's instance card in
// place of the wager row + opt-ins (a scramble is results-only and everyone on the
// saved pairings plays). Team size · basis · Appendix C allowance (editable) ·
// stroke-index tee · how ties are decided (Sept 16) · per-team handicap table with
// organizer overrides (net only, collapsed — the teams themselves are the Pairings page).
// `teamHandicaps` comes from GET /scoring/teetimes/:id/standings → games[].team_handicaps
// once the game has been saved; until then the table says "save to see".
// ============================================================================
const SCRAMBLE_PRESETS = { 2: [35, 15], 3: [30, 20, 10], 4: [25, 20, 15, 10] };
// How ties are decided (Sept 16, 2026 — Dennis): three choices on the game, never
// locked by scores (they order tied teams, they never change a score). The engine
// and the board share backend gameEngines/tieBreak.js.
const TIE_BREAK_CHOICES = [
  { key: 'usga', label: 'Scorecard playoff (USGA)',
    help: (net, nine) => `Best score over the ${nine ? 'last 6 holes, then the last 3, then the last hole' : 'last 9 holes, then the last 6, the last 3, then the 18th'}${net ? ' — with that share of the team handicap taken off' : ''}.` },
  { key: 'hole_handicap', label: 'Hole handicap',
    help: (net) => `Compare the cards hole by hole, hardest hole first — the first better ${net ? 'net ' : ''}score wins.` },
  { key: 'committee', label: "We'll decide",
    help: () => 'Tied teams stay tied until you put them in order here — after a playoff, a chip-off or a ruling.' },
];
const DEFAULT_PLACES_PAID = 3;
const tieOrdinal = (n) => { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`; };
// The ties the organizer puts in order, from the SAVED game's results: tied teams
// the method left undecided (pending) and ties his saved order already decided (PO),
// one group per tie_sub (the first place the group covers).
const scrambleTieGroups = (totals) => {
  const groups = [];
  for (const t of totals || []) {
    if (t.tie_sub == null || !(t.tie_status === 'pending' || t.tie_mark === 'PO')) continue;
    let g = groups.find(x => x.sub === t.tie_sub);
    if (!g) { g = { sub: t.tie_sub, teams: [], decided: true }; groups.push(g); }
    g.teams.push(t);
    if (t.tie_status === 'pending') g.decided = false;
  }
  return groups;
};
// A group's team numbers in the organizer's order when he has set one, else as listed.
const tieGroupOrder = (group, tieOrder) => {
  const ids = group.teams.map(t => Number(t.team_id));
  const order = (tieOrder || []).map(Number);
  return ids.every(id => order.includes(id)) ? ids.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b)) : ids;
};
const ScrambleGamePanel = ({ settings, onChange, teamHandicaps, teamResults, savedSettings, locked, saved, nines = null }) => {
  const [showTeams, setShowTeams] = useState(null);   // null = automatic (open only when a team has no handicap)
  const s = settings || {};
  const size = [2, 3, 4].includes(Number(s.team_size)) ? Number(s.team_size) : 4;
  const basis = s.basis === 'net' ? 'net' : 'gross';
  const allowance = Array.isArray(s.allowance) && s.allowance.length === size ? s.allowance : SCRAMBLE_PRESETS[size];
  const overrides = s.team_overrides || {};
  const preset = SCRAMBLE_PRESETS[size];
  const isPreset = allowance.every((v, i) => Number(v) === preset[i]);
  const tieBreak = TIE_BREAK_CHOICES.some(c => c.key === s.tie_break) ? s.tie_break : 'usga';
  const placesPaid = Number(s.places_paid) >= 1 ? Number(s.places_paid) : DEFAULT_PLACES_PAID;
  const tieOrder = Array.isArray(s.tie_order) ? s.tie_order.map(Number) : [];
  const nine = teamResults?.hole_count === 9;
  const rankWord = (i, n) => (n === 2 ? ['Low', 'High'][i] : n === 3 ? ['Low', 'Mid', 'High'][i] : ['Low', '2nd', '3rd', 'High'][i]);
  const radio = (checked) => ({ padding: '6px 12px', borderRadius: 8, border: checked ? '2px solid #166534' : '1px solid #d1d5db', background: checked ? '#dcfce7' : '#fff', color: checked ? '#166534' : '#374151', fontSize: 13, fontWeight: 700, cursor: locked ? 'default' : 'pointer', opacity: locked ? 0.7 : 1 });
  const sectionHead = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };
  const smallBtn = { fontSize: 12, padding: '4px 9px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', color: '#374151', fontWeight: 600 };

  // Ties left for the organizer — only meaningful for the settings the results were computed with.
  const savedTieBreak = savedSettings ? (TIE_BREAK_CHOICES.some(c => c.key === savedSettings.tie_break) ? savedSettings.tie_break : 'usga') : null;
  const savedPlaces = savedSettings ? (Number(savedSettings.places_paid) >= 1 ? Number(savedSettings.places_paid) : DEFAULT_PLACES_PAID) : null;
  const savedOrder = savedSettings && Array.isArray(savedSettings.tie_order) ? savedSettings.tie_order.map(Number) : [];
  const tieRulesChanged = !!savedSettings && (savedTieBreak !== tieBreak || savedPlaces !== placesPaid);
  const scoredTeams = (teamResults?.totals || []).filter(t => Number(t.thru) > 0).length;
  const tieGroups = saved && teamResults && !tieRulesChanged ? scrambleTieGroups(teamResults.totals) : [];
  const setGroupOrder = (group, ids) => {
    const inGroup = new Set(group.teams.map(t => Number(t.team_id)));
    onChange({ tie_order: [...tieOrder.filter(n => !inGroup.has(n)), ...(ids || [])] });
  };
  const moveInGroup = (group, from, to) => {
    const ids = tieGroupOrder(group, tieOrder);
    if (to < 0 || to >= ids.length) return;
    const next = ids.slice();
    const [id] = next.splice(from, 1);
    next.splice(to, 0, id);
    setGroupOrder(group, next);
  };

  // Team list summary (net) — the teams themselves live on the Pairings page.
  const teamList = teamHandicaps || [];
  const hcps = teamList.map(t => t.team_handicap).filter(v => v != null);
  const setByYou = teamList.filter(t => { const ov = overrides[String(t.team_id)]; return ov !== undefined && ov !== null && ov !== ''; }).length;
  const noHcp = teamList.filter(t => t.team_handicap == null).length;
  const teamsOpen = showTeams == null ? noHcp > 0 : showTeams;
  const teamWord = (n) => `${n} team${n === 1 ? '' : 's'}`;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, color: '#166534', background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: 6, padding: '5px 9px', marginBottom: 10 }}>
        Results only — one score per team, nothing settled, nothing posts to GHIN. Teams are your saved pairings ({size === 2 ? 'twosomes' : size === 3 ? 'threesomes' : 'foursomes'} on the Pairings page). Saving this game marks the round <strong>one score per team</strong>.
        {' '}<a href="/guides/how-to-set-up-a-captains-choice-golf-tournament?from=app" target="_blank" rel="noopener noreferrer" style={{ color: '#15803d', fontWeight: 600 }}>📖 Setup guide ↗</a>
      </div>
      {locked && (
        <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '5px 9px', marginBottom: 10 }}>
          🔒 Scores have been entered — team size, scoring, percentages and the handicap snapshot are locked. A team's handicap override and how ties are decided can still change.
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>
        <div>
          <div style={sectionHead}>Team size</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[2, 3, 4].map(n => (
              <button key={n} type="button" disabled={locked} onClick={() => onChange({ team_size: n, allowance: SCRAMBLE_PRESETS[n] })} style={radio(size === n)}>{n}-man</button>
            ))}
          </div>
        </div>
        <div>
          <div style={sectionHead}>Scoring</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" disabled={locked} onClick={() => onChange({ basis: 'gross' })} style={radio(basis === 'gross')}>Gross</button>
            <button type="button" disabled={locked} onClick={() => onChange({ basis: 'net' })} style={radio(basis === 'net')}>Net (team handicap)</button>
          </div>
        </div>
      </div>

      {basis === 'net' && (
        <div style={{ marginTop: 12 }}>
          <div style={sectionHead}>
            Team handicap allowance <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— % of each player's course handicap, lowest handicap first{isPreset ? ' · USGA Appendix C' : ' · custom'}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {allowance.map((v, i) => (
              <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' }}>
                <span style={{ color: '#6b7280' }}>{rankWord(i, size)}</span>
                <input type="number" min="0" max="100" step="1" value={v} disabled={locked}
                  onChange={(e) => { const next = allowance.slice(); next[i] = e.target.value === '' ? '' : Number(e.target.value); onChange({ allowance: next }); }}
                  style={{ width: 54, padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, textAlign: 'center' }} />%
              </label>
            ))}
            {!isPreset && !locked && (
              <button type="button" onClick={() => onChange({ allowance: preset })} style={{ ...smallBtn, fontWeight: 400 }}>
                Reset to {preset.join('/')}
              </button>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
            Added first, rounded once: course handicaps 8 and 24 at 35/15 → 2.8 + 3.6 = 6.4 → a team handicap of <strong>6</strong>. Strokes fall on the hardest holes, by the stroke index of the lower-handicap player's tee.
          </div>
        </div>
      )}

      {/* How ties are decided — never locked */}
      <div style={{ marginTop: 14, borderTop: '1px solid #d1fae5', paddingTop: 12 }} data-testid="tie-break">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={sectionHead}>How ties are decided</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', marginBottom: 6 }}>
            Places paid
            <select value={placesPaid} onChange={(e) => onChange({ places_paid: Number(e.target.value) })}
              style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontWeight: 700 }}>
              {Array.from({ length: Math.max(10, placesPaid) }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {TIE_BREAK_CHOICES.map(c => {
            const on = tieBreak === c.key;
            return (
              <button key={c.key} type="button" onClick={() => onChange({ tie_break: c.key })} aria-pressed={on}
                style={{ textAlign: 'left', padding: '7px 10px', borderRadius: 8, cursor: 'pointer', border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#f0fdf4' : '#fff' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: on ? '#166534' : '#374151' }}>{on ? '● ' : '○ '}{c.label}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.35 }}>{c.help(basis === 'net', nine)}</div>
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
          Only ties for the top {placesPaid === 1 ? 'place' : `${placesPaid} places`} are decided, once every team in the tie has finished; other ties keep their T.
          {tieBreak !== 'committee' && ' Teams with identical cards still need your call — they show up below.'}
          {' '}The leaderboard marks a place won on the cards <strong>SP</strong> and one decided by playoff or by you <strong>PO</strong>. You can change this at any time, even after scores are in.
        </div>

        {saved && tieRulesChanged && (
          <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>Save to see which ties are left for you to decide under this choice.</div>
        )}
        {saved && !tieRulesChanged && tieBreak === 'committee' && tieGroups.length === 0 && scoredTeams > 0 && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>No ties to decide right now — a tie for the top {placesPaid === 1 ? 'place' : `${placesPaid}`} shows up here once those teams finish.</div>
        )}
        {tieGroups.length > 0 && (
          <div style={{ marginTop: 10 }} data-testid="tie-groups">
            <div style={sectionHead}>Ties to decide</div>
            {tieGroups.map(group => {
              const ids = tieGroupOrder(group, tieOrder);
              const inOrder = group.teams.every(t => tieOrder.includes(Number(t.team_id)));
              const savedIn = group.teams.every(t => savedOrder.includes(Number(t.team_id)));
              const changed = inOrder !== savedIn || (inOrder && tieGroupOrder(group, savedOrder).join(',') !== ids.join(','));
              const last = group.sub + group.teams.length - 1;
              const byId = Object.fromEntries(group.teams.map(t => [Number(t.team_id), t]));
              const score = (t) => (basis === 'net' ? (t.net_total != null ? `net ${t.net_total}` : '') : (t.gross_total != null ? `${t.gross_total}` : ''));
              return (
                <div key={group.sub} style={{ border: `1px solid ${inOrder ? '#bbf7d0' : '#fcd34d'}`, background: inOrder ? '#f0fdf4' : '#fffbeb', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>
                      Tied for {tieOrdinal(group.sub)}{score(group.teams[0]) ? ` · ${score(group.teams[0])}` : ''}
                      <span style={{ fontWeight: 400, color: '#6b7280' }}> — places {group.sub}–{last}</span>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: changed ? '#b45309' : (inOrder ? '#166534' : '#92400e') }}>
                      {changed ? 'Save to apply' : (inOrder ? '✓ Decided — shows as PO' : `Playoff pending — shows as T${group.sub}`)}
                    </div>
                  </div>
                  {ids.map((id, i) => {
                    const t = byId[id];
                    return (
                      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: i ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
                        <span style={{ width: 26, textAlign: 'center', fontSize: 13, fontWeight: 800, color: inOrder ? '#166534' : '#9ca3af' }}>{inOrder ? group.sub + i : `T${group.sub}`}</span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#1f2937' }}>
                          <strong>Team {id}</strong> — {t.team_name}
                        </span>
                        <button type="button" aria-label={`Move team ${id} up`} disabled={i === 0} onClick={() => moveInGroup(group, i, i - 1)}
                          style={{ ...smallBtn, padding: '2px 8px', opacity: i === 0 ? 0.35 : 1, cursor: i === 0 ? 'default' : 'pointer' }}>▲</button>
                        <button type="button" aria-label={`Move team ${id} down`} disabled={i === ids.length - 1} onClick={() => moveInGroup(group, i, i + 1)}
                          style={{ ...smallBtn, padding: '2px 8px', opacity: i === ids.length - 1 ? 0.35 : 1, cursor: i === ids.length - 1 ? 'default' : 'pointer' }}>▼</button>
                      </div>
                    );
                  })}
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    {!inOrder && <button type="button" onClick={() => setGroupOrder(group, ids)} style={{ ...smallBtn, borderColor: '#166534', color: '#166534' }}>Use this order</button>}
                    {inOrder && <button type="button" onClick={() => setGroupOrder(group, [])} style={smallBtn}>Back to tied</button>}
                  </div>
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.4 }}>Put the teams in finishing order (▲ ▼), then Save — the leaderboard updates right away.</div>
          </div>
        )}
      </div>

      {/* Teams — the saved pairings; net games show each team's handicap on request */}
      <div style={{ marginTop: 14, borderTop: '1px solid #d1fae5', paddingTop: 12 }} data-testid="scramble-teams">
        <div style={sectionHead}>Teams</div>
        {!saved && basis === 'net' && <div style={{ fontSize: 12, color: '#9ca3af' }}>Save Games, then come back here to check each team's handicap or set one by hand.</div>}
        {!saved && basis === 'gross' && <div style={{ fontSize: 12, color: '#6b7280' }}>Your saved pairings — set them on the Pairings page.</div>}
        {saved && !teamHandicaps && (
          <div style={{ fontSize: 12, color: '#92400e' }}>Couldn't load the teams just now — close and reopen Games Setup.</div>
        )}
        {saved && teamHandicaps && teamHandicaps.length === 0 && (
          <div style={{ fontSize: 12, color: '#92400e' }}>No teams yet — save pairings on the Pairings page first.</div>
        )}
        {saved && teamList.length > 0 && basis === 'gross' && (
          <div style={{ fontSize: 12, color: '#374151' }}>{teamWord(teamList.length)} from your saved pairings — change them on the Pairings page.</div>
        )}
        {saved && teamList.length > 0 && basis === 'net' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: '#374151' }}>
              {teamWord(teamList.length)}
              {hcps.length > 0 && ` · team handicaps ${Math.min(...hcps)}${Math.max(...hcps) !== Math.min(...hcps) ? `–${Math.max(...hcps)}` : ''}`}
              {setByYou > 0 && ` · ${setByYou} set by you`}
              {noHcp > 0 && <span style={{ color: '#b45309', fontWeight: 700 }}> · {noHcp} without a handicap</span>}
            </div>
            <button type="button" onClick={() => setShowTeams(!teamsOpen)} style={smallBtn}>{teamsOpen ? 'Hide teams' : 'Show teams'}</button>
          </div>
        )}
        {saved && basis === 'net' && teamsOpen && teamList.map(t => {
          const ov = overrides[String(t.team_id)];
          const hasOv = ov !== undefined && ov !== null && ov !== '';
          return (
            <div key={t.team_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 600 }}>
                  Team {t.team_id}{t.starting_hole ? ` · ${startHoleText(t.starting_hole, t.group_label, nines)}` : ''} — {t.team_name}
                  {t.short_handed && <span style={{ marginLeft: 6, fontSize: 11, color: '#92400e', fontWeight: 700 }}>{(t.members || []).length}-man</span>}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                  {(t.members || []).map(m => `${(m.name || '').split(' ').slice(-1)[0]} ${m.course_hcp != null ? m.course_hcp : '?'}${m.tee_name ? ` (${m.tee_name})` : ''} × ${m.pct}%`).join(' · ')}
                  {t.computed_handicap != null ? ` = ${t.computed_handicap}` : (t.reason === 'missing_handicap' ? ' — a player has no handicap' : '')}
                  {t.si_tee_name ? ` · strokes on ${t.si_tee_name}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <input type="number" step="1" value={hasOv ? ov : ''} placeholder={t.computed_handicap != null ? String(t.computed_handicap) : '—'}
                  onChange={(e) => { const next = { ...overrides }; if (e.target.value === '') delete next[String(t.team_id)]; else next[String(t.team_id)] = e.target.value; onChange({ team_overrides: next }); }}
                  title="Type a number to override this team's handicap; clear it to use the computed value"
                  style={{ width: 58, padding: '5px 6px', border: hasOv ? '2px solid #b45309' : '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 700, textAlign: 'center', background: hasOv ? '#fffbeb' : '#fff' }} />
                {hasOv && <span style={{ fontSize: 10, color: '#b45309', fontWeight: 700 }}>override</span>}
              </div>
            </div>
          );
        })}
        {saved && basis === 'net' && teamsOpen && teamList.length > 0 && (
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, lineHeight: 1.4 }}>
            Handicap indexes are captured when you save{locked ? '' : ' — save again before play to refresh them'}. Type in the box to set a team's handicap by hand; clear it to go back to the computed number. Save to apply.
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// StrokePlayGamePanel — config for the individual Stroke Play game (Sept 23, 2026,
// Stroke_Play_Design_v1). Rendered inside GameSetupModal's card in place of the
// wager row: ranking (net / gross / both) · allowance (USGA 95%, editable) ·
// payouts (outside TTO / winner takes all / paid places with percentages) ·
// buy-in and, for both, its gross/net split · money ties · how ties are decided ·
// ties left for the organizer to order (by player). The opt-in list stays below.
// ============================================================================
const STROKE_PLAY_USGA_PCT = 95;
// One line for a stroke play row on every live standings surface (app, portal, modal):
// "1st · net 64 (−8) · gross 72 · hcp 8" — place label from the game's primary ranking.
const fmtVsPar = (v) => (v == null ? '' : v === 0 ? 'E' : v > 0 ? `+${v}` : `${v}`);
const strokePlayPlace = (game, row) => {
  const comp = game.results?.primary === 'gross' ? 'gross' : 'net';
  const label = row[`${comp}_place_label`];
  if (!label) return '–';
  const mark = row[`${comp}_tie_mark`];
  return `${label}${mark ? ` ${mark}` : ''}`;
};
// full = the whole line (portal text lists). On the leaderboard cards the primary score
// and "thru" sit big on the right (strokePlayScore), so the line carries the rest.
const strokePlayStat = (game, row, full = true) => {
  const res = game.results || {};
  const primary = res.primary === 'gross' ? 'gross' : 'net';
  // Best Ball (Sept 23, 2026): the row is a TEAM — no single handicap to show, and a
  // team the game could not rank says why.
  const team = isBestBallGameType(game.game_type);   // Best Ball or Shamble
  if (team && row.ranked === false) return `not ranked — ${row.unranked_reason === 'oversize' ? 'too many players on the team' : row.unranked_reason === 'no_handicap' ? 'a player has no handicap' : 'short a player'}`;
  const parts = [];
  if (res.competition !== 'gross' && (full || primary !== 'net')) parts.push(`net ${row.net_total ?? '—'}${row.net_vs_par != null ? ` (${fmtVsPar(row.net_vs_par)})` : ''}${res.competition === 'both' && row.net_place_label ? ` · ${row.net_place_label}${row.net_tie_mark ? ` ${row.net_tie_mark}` : ''} net` : ''}`);
  if (full || primary !== 'gross') parts.push(`gross ${row.gross_total ?? '—'}${row.gross_vs_par != null ? ` (${fmtVsPar(row.gross_vs_par)})` : ''}${res.competition === 'both' && row.gross_place_label ? ` · ${row.gross_place_label}${row.gross_tie_mark ? ` ${row.gross_tie_mark}` : ''} gross` : ''}`);
  if (!team && res.competition !== 'gross' && row.playing_handicap != null) parts.push(`hcp ${row.playing_handicap}`);
  if (team && row.short) parts.push('short a player');
  // The blind draw (Sept 26, 2026): "+ blind" on the short team's row, named only once
  // the server sends names (the organizer during the round, everyone once it is final).
  if (team && row.includes_blind) {
    const named = row.blind && Array.isArray(row.blind.chain) ? row.blind.chain.filter(c => c.role === 'blind').map(c => c.player_name).filter(Boolean) : [];
    parts.push(named.length ? `+ blind ${named.join(', ')}` : '+ Blind Draw');
  }
  if (full && row.thru != null && res.hole_count && row.thru < res.hole_count) parts.push(`thru ${row.thru}`);
  if (row.net_tie_status === 'pending' || row.gross_tie_status === 'pending') parts.push('playoff pending');
  return parts.join(' · ');
};
// ── Front / Back / Overall (Sept 28, 2026) ─────────────────────────────────────
// A Stroke Play / Best Ball / Shamble game that pays the nines separately carries
// results.segments { mode, order, paid, front, back, overall }: each part's own
// ranked rows (places, prizes and money for that part). segmentGame() hands a
// renderer a copy of the game whose results ARE that part, so every existing row
// renderer (strokePlayPlace / Stat / Score) works unchanged. The Overall tab keeps
// the game's own results: overall places with the game's TOTAL money (all parts).
const segmentGame = (game, key) => {
  const segs = game && game.results && game.results.segments;
  if (!segs || !key || key === 'overall' || !segs[key]) return game;
  const s = segs[key];
  return { ...game, results: { ...game.results, totals: s.totals || [], ...(s.teams ? { teams: s.teams } : {}), is_complete: s.is_complete, money_pending: s.money_pending, results_only: s.results_only, hole_count: s.hole_count, holes_completed: s.holes_completed, places_pct: s.places_pct, ties_pending: s.ties_pending, segment_key: key } };
};
// The part a leaderboard opens on: the overall when it is paid; otherwise the nine in
// play during the round, and the front nine (the first part) once the round is over.
const defaultSegmentKey = (segs) => {
  if (!segs) return null;
  if (segs.paid && segs.paid.includes('overall')) return 'overall';
  if (segs.overall && segs.overall.is_complete) return 'front';
  return segs.front && segs.front.is_complete ? 'back' : 'front';
};
const SegmentSwitch = ({ game, children, noMoney = false }) => {
  const segs = game && game.results && game.results.segments;
  const [picked, setPicked] = useState(null);
  if (!segs) return children(game);
  const key = picked && segs[picked] ? picked : defaultSegmentKey(segs);
  const tab = (on) => ({ flex: 1, padding: '7px 6px', border: 'none', borderBottom: on ? '3px solid #166534' : '3px solid transparent', background: on ? '#f0fdf4' : '#fff', color: on ? '#166534' : '#6b7280', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
  return (
    <>
      <div role="tablist" data-testid="segment-tabs" style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
        {segs.order.map(k => (
          <button key={k} type="button" role="tab" aria-selected={key === k} onClick={() => setPicked(k)} style={tab(key === k)}>
            {segs[k].label}{segs.paid.includes(k) ? '' : <span style={{ fontWeight: 400 }}> · not paid</span>}
          </button>
        ))}
      </div>
      {key === 'overall' && game.status === 'completed' && !game.results.results_only && !noMoney && (
        <div style={{ padding: '6px 14px', fontSize: 11, color: '#6b7280', background: '#f9fafb' }}>Money shown is the total for the whole game ({segs.paid.map(k => segs[k].label).join(' + ')}).</div>
      )}
      {children(segmentGame(game, key))}
    </>
  );
};
// Leaderboard rows (all renderers): the other basis, small, UNDER the big score —
// gross under the net score on the Net view, net under the gross on the Gross view
// (Dennis, Sept 23 — "show the net score with gross somewhere"). It sat on the thru
// line first, where "Gross 66" was the only labelled score on a Net row and read as
// the row's score; under the big number it reads as the sidekick it is. Plain text.
const otherBasisText = (r, isNet, withVs = true) => {
  if (!r || !r.has_scores) return '';
  if (isNet) return r.gross_total != null ? `Gross ${r.gross_total}${withVs && r.gross_vs_par != null ? ` (${fmtVsPar(r.gross_vs_par)})` : ''}` : '';
  return r.net_total != null ? `Net ${r.net_total}${withVs && r.net_vs_par != null ? ` (${fmtVsPar(r.net_vs_par)})` : ''}` : '';
};
// ── The stroke play game on an individual board (Sept 23, 2026 — Stroke_Play_Slice2) ──
// The server (spectatorBoardService) ranks the board's rows on the field's stroke
// play game and says so in board.game; every row then carries net_place /
// net_place_label / net_tied / net_tie_mark / net_tie_status (and gross_*),
// playing_handicap and in_game. These helpers are the ONE reading of that for the
// four renderers (Scores tab, LeaderboardModal, PublicLeaderboard, TV). No game →
// they all say "not ranked" and the renderers behave exactly as before.
const boardGame = (board) => (board && board.game && board.game.type === 'stroke_play') ? board.game : null;
// Is the view (net / gross) one the game ranks? A net game's Gross view is unofficial.
const boardRankedOn = (board, isNet) => {
  const g = boardGame(board);
  return !!g && (isNet ? g.net_ranked !== false : g.gross_ranked !== false);
};
// The basis a board OPENS on (Dennis: "if it's a net game it should open to a net
// leaderboard, if gross open to gross"; net & gross opens on net, the primary).
const boardOpensNet = (board) => { const g = boardGame(board); return !!g && g.competition !== 'gross'; };
const boardPlace = (r, isNet) => {
  const b = isNet ? 'net' : 'gross';
  return { place: r[`${b}_place`] ?? null, label: r[`${b}_place_label`] || null, tied: !!r[`${b}_tied`], mark: r[`${b}_tie_mark`] || null, status: r[`${b}_tie_status`] || null };
};
// Row order for a view. With a game ranking this view: the game's finishing order
// (participants with a place, in place order — the server's order inside an
// undecided tie stands), then everyone else on the plain key. Without one: the
// plain key — to-par (total when a hole had no par), then further along, then name;
// null scores last; sections in order; "no scores" by name. The ONE sort for all
// four renderers (it replaced three copies of the Net re-sort).
const BOARD_SECTION_ORDER = { live: 0, not_current: 1, no_scores: 2 };
const sortBoardRows = (rows, isNet, board) => {
  const ranked = boardRankedOn(board, isNet);
  const b = isNet ? 'net' : 'gross';
  const keyOf = (r) => (r[`${b}_vs_par`] != null ? r[`${b}_vs_par`] : (r[`${b}_total`] != null ? r[`${b}_total`] + 1000 : null));
  return (rows || []).map((r, i) => ({ r, i })).sort((A, B) => {
    const a = A.r, c = B.r;
    if (BOARD_SECTION_ORDER[a.section] !== BOARD_SECTION_ORDER[c.section]) return (BOARD_SECTION_ORDER[a.section] ?? 9) - (BOARD_SECTION_ORDER[c.section] ?? 9);
    if (a.section === 'no_scores') return (a.name || '').localeCompare(c.name || '');
    if (ranked) {
      const oa = a[`${b}_place`] != null ? (a[`${b}_order`] ?? a[`${b}_place`]) : null;
      const oc = c[`${b}_place`] != null ? (c[`${b}_order`] ?? c[`${b}_place`]) : null;
      if (oa != null || oc != null) {
        if (oa == null) return 1;
        if (oc == null) return -1;
        if (oa !== oc) return oa - oc;
        return A.i - B.i;
      }
    }
    const ka = keyOf(a), kc = keyOf(c);
    if ((ka == null) !== (kc == null)) return ka == null ? 1 : -1;
    if (ka != null && ka !== kc) return ka - kc;
    if (ka != null && a.thru !== c.thru) return (c.thru || 0) - (a.thru || 0);
    return (a.name || '').localeCompare(c.name || '');
  }).map(x => x.r);
};
// The handicap on a row's second line. Plain board: "Crse Hcp 21". With a game whose
// allowance moved the number: "Hcp 20 (95% of 21)"; a participant with no index:
// "Hcp 0 (no index)".
const boardHcpText = (r, board) => {
  if (!r) return '';
  if (boardGame(board) && r.playing_handicap != null) {
    if (r.scratch_no_index) return 'Hcp 0 (no index)';
    if (r.course_hcp != null && r.playing_handicap !== r.course_hcp) return `Hcp ${r.playing_handicap} (${boardGame(board).allowance_pct}% of ${r.course_hcp})`;
    return `Crse Hcp ${r.playing_handicap}`;
  }
  return r.course_hcp != null ? `Crse Hcp ${r.course_hcp}` : '';
};
// The big number for a stroke play row: the primary competition's total and to-par,
// the way the Scores tab shows them (Dennis, Sept 23: "it would show a big 47").
const strokePlayScore = (game, row) => {
  const res = game.results || {};
  const comp = res.primary === 'gross' ? 'gross' : 'net';
  const total = comp === 'net' ? row.net_total : row.gross_total;
  const vs = comp === 'net' ? row.net_vs_par : row.gross_vs_par;
  const thru = row.thru != null && res.hole_count && row.thru < res.hole_count ? `thru ${row.thru}` : (row.thru != null && res.hole_count ? 'F' : '');
  return { total: total ?? '—', vs: fmtVsPar(vs), comp, thru };
};
const STROKE_TIE_HELP = {
  usga: (nine) => `Best score over the ${nine ? 'last 6 holes, then the last 3, then the last hole' : 'last 9 holes, then the last 6, the last 3, then the 18th'} — on net, with that share of the playing handicap taken off.`,
  hole_handicap: () => 'Compare the cards hole by hole, hardest hole first — the first better score wins.',
  committee: () => 'Tied players stay tied until you put them in order here — after a playoff or a ruling.',
};
const strokeTieGroups = (totals, comp) => {
  const groups = [];
  for (const t of totals || []) {
    const sub = t[`${comp}_tie_sub`], status = t[`${comp}_tie_status`], mark = t[`${comp}_tie_mark`];
    if (sub == null || !(status === 'pending' || mark === 'PO')) continue;
    let g = groups.find(x => x.sub === sub);
    if (!g) { g = { sub, rows: [], decided: true }; groups.push(g); }
    g.rows.push(t);
    if (status === 'pending') g.decided = false;
  }
  return groups;
};
const strokeGroupOrder = (group, order, keyOf = (t) => t.player_id) => {
  const ids = group.rows.map(keyOf);
  const o = order || [];
  return ids.every(id => o.includes(id)) ? ids.slice().sort((a, b) => o.indexOf(a) - o.indexOf(b)) : ids;
};
// Best Ball (Sept 23, 2026 — Stroke_Play_Teams_Design_v1): the same panel with a format
// row. Teams are the saved pairing groups; each format has its USGA Appendix C allowance
// per PLAYER (there is no team handicap). Mirrors scoringService.BEST_BALL_FORMATS.
const BEST_BALL_FORMATS_UI = [
  { key: '2man',     label: '2-man best ball', usga: 85,  bestOf: 1, size: 2, help: 'The better of the two scores counts on each hole. Pair it as twosomes — two twosomes to a hole or tee time.' },
  // Oct 17, 2026 (Dennis): 3-man, Best Ball only. No USGA figure for 3-man; 75 / 85 mirror best 1 / 2 of 4.
  { key: 'best1of3', label: 'Best 1 of 3',     usga: 75,  bestOf: 1, size: 3, help: 'A threesome team; the best score of the three counts on each hole. No blind is drawn.' },
  { key: 'best2of3', label: 'Best 2 of 3',     usga: 85,  bestOf: 2, size: 3, help: 'A threesome team; the best two scores of the three count on each hole. No blind is drawn.' },
  { key: 'best1of4', label: 'Best 1 of 4',     usga: 75,  bestOf: 1, size: 4, help: 'A foursome team; the best score of the four counts on each hole.' },
  { key: 'best2of4', label: 'Best 2 of 4',     usga: 85,  bestOf: 2, size: 4, help: 'A foursome team; the best two scores of the four count on each hole.' },
  { key: 'best3of4', label: 'Best 3 of 4',     usga: 100, bestOf: 3, size: 4, help: 'A foursome team; the best three scores of the four count on each hole.' },
  // Oct 10, 2026 (Dennis): Best Ball only — not a Shamble format.
  { key: 'onegross1net', label: 'One gross + one net', usga: 100, bestOf: 2, size: 4, mode: 'gross_net', help: "A foursome team; each hole counts one player's gross score plus a different player's net score — the pair with the lowest total." },
];
// Shamble (Sept 28, 2026): the same team game after a selected drive — same formats,
// its own USGA Appendix C allowances (2-person 75%, 4-person 65%) and names. Mirrors
// backend gameEngines/bestBallTypes.
const isBestBallGameType = (gt) => gt === 'best_ball' || gt === 'shamble';
const teamGameName = (gt) => (gt === 'shamble' ? 'Shamble' : 'Best Ball');
const SHAMBLE_FORMATS_UI = {
  '2man':     { label: '2-person',    usga: 75, help: 'Both players drive, the team picks the better drive, then each plays his own ball in; the better of the two scores counts on each hole. Pair it as twosomes.' },
  'best1of4': { label: 'Best 1 of 4', usga: 65, help: 'A foursome team picks the best drive, then all four play their own balls in; the best score of the four counts on each hole.' },
  'best2of4': { label: 'Best 2 of 4', usga: 65, help: 'A foursome team picks the best drive, then all four play their own balls in; the best two scores of the four count on each hole.' },
  'best3of4': { label: 'Best 3 of 4', usga: 65, help: 'A foursome team picks the best drive, then all four play their own balls in; the best three scores of the four count on each hole.' },
};
const bestBallFormatUi = (key, gameType) => {
  const f = BEST_BALL_FORMATS_UI.find(x => x.key === key) || BEST_BALL_FORMATS_UI[0];
  return gameType === 'shamble' ? { ...f, ...SHAMBLE_FORMATS_UI[f.key] } : f;
};
// Closest to the Pin (Sept 28, 2026) — the Games card panel: the wager, the holes,
// Must make par, and — once play has started — each hole's holder, which the
// organizer can set or clear (a dispute, a mis-tap).
// Sept 30, 2026 (Dennis): the holes are the round's par 3s, each Include / Exclude
// (all included by default), plus any other hole typed in. settings.holes stays null
// while every par 3 is in and nothing is added, so the game follows the course; else
// it is the list. "Who has it" no longer shows at setup — only once the round's games
// are under way (status past 'setup') or a hole already has a holder.
const CtpGamePanel = ({ settings, onChange, teeTimeId, saved, prize, players, roundDate, nines = null }) => {
  // Oct 28, 2026: named nines read "Black Robin 4 (#13)" — the number is what TTO stores and what the box below takes.
  const holeLbl = (n) => (nines ? `${holeName(n, nines)} (#${n})` : `#${n}`);
  const s = settings || {};
  const perPlayer = s.funding === 'per_player';
  const money = (v) => `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
  const [par3s, setPar3s] = useState(null);          // null = loading; [] = none found
  const [extrasText, setExtrasText] = useState('');
  const [holesMsg, setHolesMsg] = useState(null);
  const [state, setState] = useState(null);
  const [busyHole, setBusyHole] = useState(null);
  const [err, setErr] = useState(null);
  const headers = () => { const h = { 'Content-Type': 'application/json' }; const t = getAuthToken(); if (t) h.Authorization = `Bearer ${t}`; return h; };
  const parseHoles = (txt) => [...new Set(String(txt).split(/[^0-9]+/).map(Number).filter(n => Number.isInteger(n) && n >= 1 && n <= 18))].sort((a, b) => a - b);
  useEffect(() => {
    if (!teeTimeId) return undefined;
    let live = true;
    fetch(`${API_URL}/scoring/teetimes/${teeTimeId}/ctp-holes`, { credentials: 'include', headers: headers() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!live) return;
        const list = d && Array.isArray(d.par3s) ? d.par3s : [];
        setPar3s(list);
        const own = Array.isArray(s.holes) ? s.holes.map(Number) : [];
        setExtrasText(own.filter(h => !list.includes(h)).join(', '));
      })
      .catch(() => { if (live) setPar3s([]); });
    return () => { live = false; };
  }, [teeTimeId]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!saved || !teeTimeId) return undefined;
    let live = true;
    fetch(`${API_URL}/scoring/teetimes/${teeTimeId}/ctp`, { credentials: 'include', headers: headers() })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (live && d) setState(d.ctp); }).catch(() => {});
    return () => { live = false; };
  }, [saved, teeTimeId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const setHolder = async (hole, playerId) => {
    setBusyHole(hole); setErr(null);
    try {
      const res = await fetch(`${API_URL}/scoring/teetimes/${teeTimeId}/ctp`, { method: 'POST', credentials: 'include', headers: headers(), body: JSON.stringify({ hole_number: hole, player_id: playerId || null }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(d.error || 'Could not update that hole.'); return; }
      setState(d.ctp);
    } catch (e) { setErr('Could not update that hole.'); } finally { setBusyHole(null); }
  };
  // Which par 3s are in, from the saved list (none saved = all of them).
  const own = Array.isArray(s.holes) && s.holes.length ? s.holes.map(Number) : null;
  const par3List = par3s || [];
  const included = (h) => !own || own.includes(h);
  const extras = parseHoles(extrasText).filter(h => !par3List.includes(h));
  const commit = (inPar3s, extraList) => {
    const all = [...new Set([...inPar3s, ...extraList])].sort((a, b) => a - b);
    if (!all.length) { setHolesMsg('Closest to the Pin needs at least one hole.'); return false; }
    setHolesMsg(null);
    const everyPar3 = par3List.length > 0 && inPar3s.length === par3List.length;
    onChange({ holes: everyPar3 && extraList.length === 0 ? null : all });
    return true;
  };
  const setIncluded = (hole, yes) => {
    const inNow = par3List.filter(included);
    commit(yes ? [...inNow, hole] : inNow.filter(h => h !== hole), extras);
  };
  const onExtras = (txt) => {
    setExtrasText(txt);
    const ex = parseHoles(txt).filter(h => !par3List.includes(h));
    const inNow = par3List.filter(included);
    if (inNow.length || ex.length) commit(inNow, ex);
  };
  // "Who has it" is for the day itself (a volunteer at the hole, a dispute, a mis-tap):
  // shown from the round's date on, or earlier once scores or a claim exist.
  const todayLocal = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const roundDay = roundDate ? String(roundDate).slice(0, 10) : null;
  const playStarted = !!(state && (state.status !== 'setup' || (state.holes || []).some(h => h.holder_id) || (roundDay && todayLocal >= roundDay)));
  const radio = (on) => ({ padding: '6px 12px', borderRadius: 8, border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#dcfce7' : '#fff', color: on ? '#166534' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
  const sectionHead = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };
  const row = { display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderTop: '1px solid #f3f4f6' };
  const select = { flex: 1, maxWidth: 260, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 };
  return (
    <div style={{ marginTop: 12 }} data-testid="ctp-panel">
      <div style={sectionHead}>The wager is</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }} data-testid="ctp-funding">
        <button type="button" onClick={() => onChange({ funding: 'per_hole' })} style={radio(!perPlayer)}>A set prize per hole</button>
        <button type="button" onClick={() => onChange({ funding: 'per_player' })} style={radio(perPlayer)}>Each player puts in, per hole</button>
      </div>
      <div style={sectionHead}>Holes</div>
      <div data-testid="ctp-holes">
        {par3s === null && <div style={{ fontSize: 12, color: '#6b7280' }}>Finding the par 3s…</div>}
        {par3s !== null && par3List.length === 0 && (
          <div style={{ fontSize: 12, color: '#92400e', marginBottom: 6 }}>No par 3s found on this course&rsquo;s tees. Type the holes below.</div>
        )}
        {par3List.map(h => (
          <div key={h} style={row} data-testid={`ctp-hole-${h}`}>
            <span style={{ minWidth: 36, fontWeight: 700, fontSize: 13 }}>{holeLbl(h)}</span>
            <select value={included(h) ? 'include' : 'exclude'} onChange={(e) => setIncluded(h, e.target.value === 'include')} style={select} aria-label={`Hole ${h}`}>
              <option value="include">Include</option>
              <option value="exclude">Exclude</option>
            </select>
            <span style={{ fontSize: 11, color: '#6b7280' }}>par 3</span>
          </div>
        ))}
        {par3s !== null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: '#374151' }}>
            <span>{par3List.length ? 'Also on another hole (optional):' : 'Holes:'}</span>
            {nines && <span style={{ color: '#6b7280' }}>(hole numbers 1&ndash;18: {nines.front} 1&ndash;9 = 1&ndash;9, {nines.back} 1&ndash;9 = 10&ndash;18)</span>}
            <input type="text" value={extrasText} placeholder="e.g. 8, 14" onChange={(e) => onExtras(e.target.value)} data-testid="ctp-extra-holes"
              style={{ width: 130, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
          </div>
        )}
        {holesMsg && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{holesMsg}</div>}
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
        <input type="checkbox" checked={s.must_make_par === true} onChange={() => onChange({ must_make_par: !(s.must_make_par === true) })} style={{ width: 16, height: 16, marginTop: 2, accentColor: '#166534' }} />
        <span><strong>Must make par</strong><span style={{ color: '#6b7280' }}> — the claim reads &ldquo;On the green, closer, and made par?&rdquo; A closer player who misses his par doesn&apos;t claim, so the hole stays with the last player who did.</span></span>
      </label>
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8, lineHeight: 1.45 }}>
        Players claim a hole on its scoring screen (&ldquo;On the green and closer?&rdquo;); a closer player takes it over. TTO doesn&apos;t measure anything — use a marker on the green. Nobody on the green: no money on that hole.{prize > 0 ? (perPlayer
          ? ` Each player puts in ${money(prize)} a hole; the hole's winner collects ${money(prize)} × the players in the game${players > 1 ? ` (${money(prize * players)} with ${players} players)` : ''}.`
          : ` Each hole's winner collects ${money(prize)}, paid equally by the players in the game.`) : ''}
      </div>
      {saved && state && playStarted && (
        <div style={{ marginTop: 12 }} data-testid="ctp-holders">
          <div style={sectionHead}>Who has it</div>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Players claim holes as they play. Change a holder here only to settle a dispute or fix a mis-tap.</div>
          {state.holes.map(h => (
            <div key={h.hole_number} style={row}>
              <span style={{ minWidth: 36, fontWeight: 700, fontSize: 13 }}>{holeLbl(h.hole_number)}</span>
              <select value={h.holder_id || ''} disabled={busyHole === h.hole_number} onChange={(e) => setHolder(h.hole_number, e.target.value || null)} style={select}>
                <option value="">— nobody —</option>
                {(state.participants || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {h.set_by_organizer && <span style={{ fontSize: 11, color: '#6b7280' }}>set by you</span>}
            </div>
          ))}
          {err && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{err}</div>}
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Changing a holder after the round is final: re-finalize so the money follows.</div>
        </div>
      )}
    </div>
  );
};

// ── Dots (Oct 4, 2026 — Dots_Game_And_Hole_Screen_Oct04) ───────────────────────────
// The organizer picks which dots count (birdies from the gross score — eagle 2 — and
// sandies / polies the players tap) and individual or teams. Once play starts he can
// add or take off any player's sandy or poley here (a dispute, a mis-tap).
// Dots — who settles with whom (Oct 6, 2026): 'group' = within each pairing group (the
// default for a new game), 'field' = every player in the game. Saved games from before
// carry 'individual' (= field) or the retired 'teams' (= group). Mirrors
// dotsService.modeOf on the server.
const dotsModeOf = (s) => {
  const m = s && s.mode;
  if (m === 'group' || m === 'teams') return 'group';
  if (m === 'field' || m === 'individual') return 'field';
  return 'group';
};
// Dots played within each group: a row's place inside its group, and whether it is the
// first row of its group (the board puts the group's name above it). null otherwise.
const dotsGroupInfo = (rows, idx) => {
  const p = rows[idx];
  if (!p || p.in_group === undefined) return null;
  let first = idx;
  while (first > 0 && rows[first - 1].group_id === p.group_id && rows[first - 1].in_group === p.in_group) first -= 1;
  return { place: idx - first + 1, startsGroup: first === idx, name: p.in_group ? (p.group_name || 'Group') : 'Not in a group' };
};
const dotsGroupHeading = (name) => (
  <div style={{ padding: '7px 14px 5px', background: '#f9fafb', borderBottom: '1px solid #f3f4f6', fontSize: 11, fontWeight: 800, color: '#6b7280', letterSpacing: 0.4, textTransform: 'uppercase' }}>{name}</div>
);
// A game the server couldn't work out says why (Oct 6, 2026 — Sellers' Dots showed every
// player as "Not started" while the reason sat in the server log).
const gameErrorBox = (game) => (game && game.error ? (
  <div data-testid="game-error" style={{ margin: '10px 14px', padding: '9px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13, lineHeight: 1.45 }}>
    ⚠️ This game can&rsquo;t be worked out right now: {game.error}
  </div>
) : null);

const DotsGamePanel = ({ settings, onChange, teeTimeId, saved, bet, roundDate }) => {
  const s = settings || {};
  const c = s.count || {};
  const count = { birdies: c.birdies !== false, sandies: c.sandies !== false, polies: c.polies !== false };
  const inGroups = dotsModeOf(s) === 'group';   // Oct 6, 2026
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [add, setAdd] = useState({ player_id: '', hole: '', kind: 'sandy' });
  const headers = () => { const h = { 'Content-Type': 'application/json' }; const t = getAuthToken(); if (t) h.Authorization = `Bearer ${t}`; return h; };
  useEffect(() => {
    if (!saved || !teeTimeId) return undefined;
    let live = true;
    fetch(`${API_URL}/scoring/teetimes/${teeTimeId}/dots`, { credentials: 'include', headers: headers() })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (live && d) setState(d.dots); }).catch(() => {});
    return () => { live = false; };
  }, [saved, teeTimeId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const setMark = async (playerId, hole, kind, on) => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API_URL}/scoring/teetimes/${teeTimeId}/dots`, { method: 'POST', credentials: 'include', headers: headers(), body: JSON.stringify({ player_id: playerId, hole_number: hole, kind, on }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(d.error || 'Could not change that dot.'); return false; }
      setState(prev => ({ ...(d.dots || {}), participants: (prev && prev.participants) || [] }));
      return true;
    } catch (e) { setErr('Could not change that dot.'); return false; } finally { setBusy(false); }
  };
  const toggle = (k) => {
    const next = { ...count, [k]: !count[k] };
    if (!next.birdies && !next.sandies && !next.polies) return;   // at least one counts
    onChange({ count: next });
  };
  const todayLocal = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const roundDay = roundDate ? String(roundDate).slice(0, 10) : null;
  const hasMarks = !!(state && Object.keys(state.marks || {}).length);
  const playStarted = !!(state && (state.status !== 'setup' || hasMarks || (roundDay && todayLocal >= roundDay)));
  const kindsOn = (state && state.tap_kinds) || [];
  const radio = (on) => ({ padding: '6px 12px', borderRadius: 8, border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#dcfce7' : '#fff', color: on ? '#166534' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
  const sectionHead = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };
  const money = (v) => `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
  const box = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer', padding: '3px 0' };
  const kindLabel = { sandy: 'Sandy', poley: 'Poley' };
  return (
    <div style={{ marginTop: 12 }} data-testid="dots-panel">
      <div style={sectionHead}>What counts</div>
      <div data-testid="dots-count">
        <label style={box}><input type="checkbox" checked={count.birdies} onChange={() => toggle('birdies')} style={{ width: 16, height: 16, accentColor: '#166534' }} /><span><strong>Birdies</strong><span style={{ color: '#6b7280' }}> — from the gross score: birdie 1 dot, eagle 2</span></span></label>
        <label style={box}><input type="checkbox" checked={count.sandies} onChange={() => toggle('sandies')} style={{ width: 16, height: 16, accentColor: '#166534' }} /><span><strong>Sandies</strong><span style={{ color: '#6b7280' }}> — the player taps Sandy on the hole</span></span></label>
        <label style={box}><input type="checkbox" checked={count.polies} onChange={() => toggle('polies')} style={{ width: 16, height: 16, accentColor: '#166534' }} /><span><strong>Polies</strong><span style={{ color: '#6b7280' }}> — the player taps Poley on the hole</span></span></label>
      </div>
      <div style={{ ...sectionHead, marginTop: 12 }}>Who settles with whom</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }} data-testid="dots-mode">
        <button type="button" onClick={() => onChange({ mode: 'group' })} style={radio(inGroups)}>Within each group</button>
        <button type="button" onClick={() => onChange({ mode: 'field' })} style={radio(!inGroups)}>Whole field</button>
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.45 }}>
        {inGroups
          ? 'Each player settles the difference in dots with the others in his own group on the Pairings page. Any group size works; with no pairings saved, everyone in the game is one group.'
          : 'Every player in the game settles the difference in dots with every other player, whatever group they play in.'}
        {bet > 0 ? ` At ${money(bet)} a dot: 4 dots vs 1 is ${money(bet * 3)}.` : ''}
        {' '}Whatever your group counts as a sandy or a poley, the player marks it — TTO doesn&rsquo;t check.
      </div>
      {saved && state && playStarted && kindsOn.length > 0 && (
        <div style={{ marginTop: 12 }} data-testid="dots-marks">
          <div style={sectionHead}>Sandies &amp; polies marked</div>
          {!hasMarks && <div style={{ fontSize: 12, color: '#6b7280' }}>None yet.</div>}
          {(state.participants || []).filter(p => state.marks && state.marks[p.id]).map(p => (
            <div key={p.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '5px 0', borderTop: '1px solid #f3f4f6', fontSize: 13 }}>
              <span style={{ fontWeight: 700, minWidth: 120 }}>{p.name}</span>
              {Object.entries(state.marks[p.id]).sort((a, b) => Number(a[0]) - Number(b[0])).flatMap(([h, kinds]) => Object.keys(kinds).filter(k => kinds[k]).map(k => (
                <span key={`${h}-${k}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #d1d5db', borderRadius: 999, padding: '2px 4px 2px 9px', background: '#fff' }}>
                  #{h} {kindLabel[k]}
                  <button type="button" disabled={busy} onClick={() => setMark(p.id, Number(h), k, false)} aria-label={`Take off ${p.name}'s ${kindLabel[k]} on hole ${h}`}
                    style={{ border: 'none', background: '#f3f4f6', borderRadius: 999, width: 22, height: 22, cursor: 'pointer', fontWeight: 700, color: '#374151' }}>✕</button>
                </span>
              )))}
            </div>
          ))}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13 }} data-testid="dots-add">
            <span style={{ color: '#374151' }}>Add:</span>
            <select value={add.player_id} onChange={(e) => setAdd(a => ({ ...a, player_id: e.target.value }))} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
              <option value="">Player…</option>
              {(state.participants || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={add.hole} onChange={(e) => setAdd(a => ({ ...a, hole: e.target.value }))} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
              <option value="">Hole…</option>
              {Array.from({ length: 18 }, (_, i) => i + 1).map(h => <option key={h} value={h}>#{h}</option>)}
            </select>
            <select value={kindsOn.includes(add.kind) ? add.kind : kindsOn[0]} onChange={(e) => setAdd(a => ({ ...a, kind: e.target.value }))} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
              {kindsOn.map(k => <option key={k} value={k}>{kindLabel[k]}</option>)}
            </select>
            <button type="button" disabled={busy || !add.player_id || !add.hole} onClick={async () => { if (await setMark(add.player_id, Number(add.hole), kindsOn.includes(add.kind) ? add.kind : kindsOn[0], true)) setAdd(a => ({ ...a, hole: '' })); }}
              style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #166534', background: '#166534', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (!add.player_id || !add.hole) ? 0.5 : 1 }}>Add</button>
          </div>
          {err && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{err}</div>}
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>Players mark their own as they play. Change one here only to settle a dispute or fix a mis-tap. After the round is final: re-finalize so the money follows.</div>
        </div>
      )}
    </div>
  );
};

// ── Nassau (Oct 4, 2026 — Nassau_Design_Oct04) ──────────────────────────────────────
// Front 9, back 9 and overall, match play. The organizer picks the matches (every pair
// in a group, or 2 vs 2 best ball), the strokes and the presses.
const NassauGamePanel = ({ settings, onChange, bet }) => {
  const s = settings || {};
  const mode = s.mode === 'teams' ? 'teams' : 'pairs';
  const basis = ['gross', 'full'].includes(s.handicap_basis) ? s.handicap_basis : 'low_man';
  const press = ['auto', 'none'].includes(s.press) ? s.press : 'ask';
  const money = (v) => `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
  const amt = bet > 0 ? money(bet) : 'the bet';
  const radio = (on) => ({ padding: '6px 12px', borderRadius: 8, border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#dcfce7' : '#fff', color: on ? '#166534' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
  const sectionHead = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 0 6px' };
  const note = { fontSize: 11, color: '#6b7280', lineHeight: 1.45, marginTop: 4 };
  return (
    <div data-testid="nassau-panel">
      <div style={sectionHead}>Matches</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => onChange({ mode: 'pairs' })} style={radio(mode === 'pairs')}>Every player vs every player in his group</button>
        <button type="button" onClick={() => onChange({ mode: 'teams' })} style={radio(mode === 'teams')}>2 vs 2 best ball</button>
      </div>
      <div style={note}>{mode === 'teams'
        ? 'In each foursome the first two names on the pairing play the last two; the team\u2019s better score on a hole counts. Change the sides by reordering the pairing. A group that isn\u2019t four players has no match.'
        : 'Each player has a match with every other player in his pairing group (three matches each in a foursome).'}</div>
      <div style={sectionHead}>Strokes</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => onChange({ handicap_basis: 'low_man' })} style={radio(basis === 'low_man')}>Net — off the low man</button>
        <button type="button" onClick={() => onChange({ handicap_basis: 'full' })} style={radio(basis === 'full')}>Net — full course handicap</button>
        <button type="button" onClick={() => onChange({ handicap_basis: 'gross' })} style={radio(basis === 'gross')}>Gross</button>
      </div>
      <div style={sectionHead}>Presses</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => onChange({ press: 'auto' })} style={radio(press === 'auto')}>Automatic at 2 down</button>
        <button type="button" onClick={() => onChange({ press: 'ask' })} style={radio(press === 'ask')}>Ask the player at 2 down</button>
        <button type="button" onClick={() => onChange({ press: 'none' })} style={radio(press === 'none')}>No presses</button>
      </div>
      <div style={note}>{press === 'auto'
        ? `A new ${amt} bet starts by itself when a side goes 2 down on a nine, and again when it goes 2 down on that press. It runs to the end of the nine.`
        : press === 'ask'
        ? `A player who goes 2 down on a nine sees "Press?" on his hole screen. A press is a new ${amt} bet to the end of that nine; it can be pressed again at 2 down.`
        : 'Just the three bets — front, back and overall.'}</div>
      <div style={{ ...note, marginTop: 10 }}>Each hole is won by the lower score; the side that wins more holes wins the bet. All square pays nothing. Each bet won collects {amt}{mode === 'teams' ? ' from one player on the other side' : ''}. 18-hole rounds only.</div>
    </div>
  );
};

// ── Match Play (Oct 6, 2026) ─────────────────────────────────────────────────────────
// Hole by hole, one bet a match over the round (18 or the nine). The organizer picks the
// matches (every pair in a group, or 2 vs 2 four-ball) and the strokes (USGA by default:
// singles off the low man, four-ball 90% then off the low man).
const MatchPlayGamePanel = ({ settings, onChange, bet }) => {
  const s = settings || {};
  const mode = s.mode === 'teams' ? 'teams' : 'pairs';
  const basis = ['gross', 'full'].includes(s.handicap_basis) ? s.handicap_basis : 'usga';
  const money = (v) => `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
  const amt = bet > 0 ? money(bet) : 'the bet';
  const radio = (on) => ({ padding: '6px 12px', borderRadius: 8, border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#dcfce7' : '#fff', color: on ? '#166534' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
  const sectionHead = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 0 6px' };
  const note = { fontSize: 11, color: '#6b7280', lineHeight: 1.45, marginTop: 4 };
  return (
    <div data-testid="match-play-panel">
      <div style={sectionHead}>Matches</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => onChange({ mode: 'pairs' })} style={radio(mode === 'pairs')}>Singles — every player vs every player in his group</button>
        <button type="button" onClick={() => onChange({ mode: 'teams' })} style={radio(mode === 'teams')}>2 vs 2 four-ball</button>
      </div>
      <div style={note}>{mode === 'teams'
        ? 'In each foursome the first two names on the pairing play the last two; the side’s better score on a hole counts. Change the sides by reordering the pairing. A group that isn’t four players has no match.'
        : 'Each player has a match with every other player in his pairing group (three matches each in a foursome).'}</div>
      <div style={sectionHead}>Strokes</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => onChange({ handicap_basis: 'usga' })} style={radio(basis === 'usga')}>Net — USGA</button>
        <button type="button" onClick={() => onChange({ handicap_basis: 'full' })} style={radio(basis === 'full')}>Net — full course handicap</button>
        <button type="button" onClick={() => onChange({ handicap_basis: 'gross' })} style={radio(basis === 'gross')}>Gross</button>
      </div>
      <div style={note}>{basis === 'usga'
        ? (mode === 'teams'
          ? 'Each player plays off 90% of his course handicap, then off the low man: the lowest in the match plays off scratch and the others get the difference.'
          : 'The lower handicap plays off scratch; the other gets the difference.')
        : basis === 'full' ? 'Each player gets his full course handicap.' : 'No strokes.'} Strokes fall on the hardest holes first.</div>
      <div style={{ ...note, marginTop: 10 }}>Each hole is won by the lower score, or halved. A match is over once a side is up by more holes than are left: 3 up with 2 to play is won &ldquo;3 &amp; 2&rdquo;. All square at the end is halved and pays nothing. The winner collects {amt}{mode === 'teams' ? ' from one player on the other side' : ''}. 18 holes or a nine.</div>
    </div>
  );
};

// The matches in the Results tab: the viewer's (all of them when the viewer has none —
// the organizer), each with its standing from the viewer's side.
const MatchPlayMatches = ({ game, viewerId }) => {
  const res = game && game.results;
  if (!res || !Array.isArray(res.matches)) return null;
  const all = res.matches;
  const mine = viewerId ? all.filter(m => m.side_a.player_ids.includes(viewerId) || m.side_b.player_ids.includes(viewerId)) : [];
  const list = mine.length ? mine : all;
  const bet = parseFloat(res.bet_amount) || 0;
  return (
    <div data-testid="match-play-matches" style={{ borderTop: '1px solid #e5e7eb' }}>
      {list.map(m => {
        const onB = mine.length && m.side_b.player_ids.includes(viewerId);
        const us = onB ? m.side_b : m.side_a;
        const them = onB ? m.side_a : m.side_b;
        const d = onB ? -m.diff : m.diff;
        const myNet = onB ? -m.net_a : m.net_a;
        const title = mine.length ? `${us.names.length > 1 ? `${us.names.join(' & ')} ` : 'You '}vs ${them.names.join(' & ')}` : `${m.side_a.names.join(' & ')} vs ${m.side_b.names.join(' & ')}`;
        const won = m.winner && ((m.winner === 'b') === !!onB);
        const text = m.decided
          ? (m.winner == null ? 'halved' : `${won ? 'won' : 'lost'} ${m.status}`)
          : (m.holes_played ? `${d === 0 ? 'all square' : d > 0 ? `${d} up` : `${-d} down`}${Math.abs(d) === m.holes_left && d !== 0 ? ' (dormie)' : ''} thru ${m.holes_played}` : 'not started');
        const col = m.decided ? (m.winner == null ? '#374151' : won ? '#166534' : '#b91c1c') : (d > 0 ? '#166534' : d < 0 ? '#b91c1c' : '#374151');
        return (
          <div key={m.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>{title}</span>
            <span style={{ textAlign: 'right' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: col }}>{text}</span>
              {bet > 0 && m.decided && <span style={{ display: 'block', fontSize: 13, fontWeight: 800, color: myNet > 0 ? '#166534' : myNet < 0 ? '#dc2626' : '#6b7280' }}>{myNet === 0 ? 'Even' : `${myNet > 0 ? '+' : '−'}$${Math.abs(myNet).toFixed(2)}`}{m.side_a.names.length > 1 && myNet !== 0 ? ' each' : ''}</span>}
            </span>
          </div>
        );
      })}
      {(res.skipped || []).length > 0 && (
        <div style={{ padding: '8px 14px', fontSize: 12, color: '#92400e' }}>No match for {(res.skipped || []).map(x => x.group_name || `group ${x.group_id}`).join(', ')} — {res.mode === 'teams' ? '2 vs 2 needs four players in the group' : 'a player alone in his group'}.</div>
      )}
    </div>
  );
};

// ── Stableford (Oct 5, 2026 — claude/Stableford_Plan_v1.md) ──────────────────────────
// Standard points. The organizer picks net or gross (net: the allowance, USGA 95%), and
// how the money goes: a pot (winner takes all or paid places — the default), so much a
// point against the field average, or so much a point player by player.
const STABLEFORD_PLACES = [
  { key: 'winner', label: 'Winner takes all', places: [100] },
  { key: 'two', label: 'Top 2 — 60 / 40', places: [60, 40] },
  { key: 'three', label: 'Top 3 — 50 / 30 / 20', places: [50, 30, 20] },
];
const stablefordPlacesKey = (places) => {
  const p = (places || [100]).map(Number).join(',');
  const hit = STABLEFORD_PLACES.find(o => o.places.join(',') === p);
  return hit ? hit.key : 'winner';
};
const StablefordGamePanel = ({ settings, onChange, bet, nine = false }) => {
  const s = settings || {};
  const competition = s.competition === 'gross' ? 'gross' : 'net';
  // Who plays whom (Oct 7, 2026 — Dennis: individual play only, like Dots): within each
  // pairing group (the default) or the whole field. Team Stableford is retired.
  const teams = false;
  const playMode = s.mode === 'field' ? 'field' : 'group';
  const mode = ['per_point_field', 'per_point_pairs'].includes(s.payout_mode) ? s.payout_mode : 'pot';
  const placesKey = stablefordPlacesKey(s.places);
  const money = (v) => `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
  const radio = (on) => ({ padding: '6px 12px', borderRadius: 8, border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#dcfce7' : '#fff', color: on ? '#166534' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
  const sectionHead = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 0 6px' };
  const note = { fontSize: 11, color: '#6b7280', lineHeight: 1.45, marginTop: 4 };
  return (
    <div data-testid="stableford-panel">
      <div style={sectionHead}>Who plays whom</div>
      <div data-testid="stableford-mode" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => onChange({ mode: 'group' })} style={radio(playMode === 'group')}>Within each group</button>
        <button type="button" onClick={() => onChange({ mode: 'field' })} style={radio(playMode === 'field')}>Whole field</button>
      </div>
      <div style={note}>{playMode === 'group'
        ? 'Each group on the Pairings page plays its own Stableford, with its own places and its own money. Any size works: a foursome, a threesome or a twosome, and a late cancel just makes a group smaller. With no pairings saved, everyone in the game is one group.'
        : 'Everyone in the game plays one Stableford, across every group.'}</div>
      <div style={sectionHead}>Scoring</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" onClick={() => onChange({ competition: 'net' })} style={radio(competition === 'net')}>Net</button>
        <button type="button" onClick={() => onChange({ competition: 'gross' })} style={radio(competition === 'gross')}>Gross</button>
        {competition === 'net' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', marginLeft: 6 }}>
            Allowance
            <input type="number" min="0" max="100" step="1" value={s.allowance_pct ?? 95}
              onChange={(e) => onChange({ allowance_pct: e.target.value })}
              style={{ width: 64, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
            % of course handicap
          </span>
        )}
      </div>
      <div style={note}>Points on each hole: double bogey or worse 0, bogey 1, par 2, birdie 3, eagle 4, albatross 5. {competition === 'net' ? `Points come from each player\u2019s net score; strokes fall on the hardest holes first. The USGA allowance is ${teams ? '85% for team (four-ball) Stableford' : '95% for individual Stableford'}.` : 'Points come from the gross score — no strokes.'} A picked-up hole (X) scores 0.</div>
      <div style={sectionHead}>Money</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => onChange({ payout_mode: 'pot' })} style={radio(mode === 'pot')}>Pot</button>
        {teams ? (
          <button type="button" onClick={() => onChange({ payout_mode: 'per_point_teams' })} style={radio(mode === 'per_point_teams')}>Per point, team against team</button>
        ) : (
          <>
            <button type="button" onClick={() => onChange({ payout_mode: 'per_point_field' })} style={radio(mode === 'per_point_field')}>{playMode === 'group' ? 'Per point vs the group' : 'Per point vs the field'}</button>
            <button type="button" onClick={() => onChange({ payout_mode: 'per_point_pairs' })} style={radio(mode === 'per_point_pairs')}>Per point, player by player</button>
          </>
        )}
      </div>
      {mode === 'pot' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {STABLEFORD_PLACES.map(o => (
            <button key={o.key} type="button" onClick={() => onChange({ places: o.places })} style={radio(placesKey === o.key)}>{o.label}</button>
          ))}
        </div>
      )}
      {/* Front / back / overall (Oct 7, 2026): the pot only, 18-hole rounds only. */}
      {mode === 'pot' && !nine && (() => {
        const segMode = SEGMENT_CHOICES.some(c => c.key === s.segments) ? s.segments : 'overall';
        const parts = (SEGMENT_CHOICES.find(c => c.key === segMode) || SEGMENT_CHOICES[0]).parts;
        const split = parts.length && bet > 0 ? segmentSplitCents(bet, parts, s.segment_buy_in) : null;
        const sumOk = !split || Object.values(split).reduce((a, b) => a + b, 0) === Math.round(bet * 100);
        const given = s.segment_buy_in && typeof s.segment_buy_in === 'object' ? s.segment_buy_in : {};
        const setPart = (k, v) => onChange({ segment_buy_in: { ...Object.fromEntries(parts.map(p => [p, given[p] == null ? '' : given[p]])), [k]: v } });
        return (
          <div data-testid="stableford-segments">
            <div style={sectionHead}>What it pays</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SEGMENT_CHOICES.map(c => (
                <button key={c.key} type="button" onClick={() => onChange({ segments: c.key, ...(c.key === 'overall' ? { segment_buy_in: null } : {}) })} style={radio(segMode === c.key)}>{c.label}</button>
              ))}
            </div>
            {parts.length > 0 && bet > 0 && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
                {parts.map(k => (
                  <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#374151' }}>
                    {SEGMENT_PART_LABEL[k]} $
                    <input type="number" min="0" step="1" value={given[k] == null ? '' : given[k]} placeholder={split ? (split[k] / 100).toFixed(2) : ''}
                      onChange={(e) => setPart(k, e.target.value)}
                      style={{ width: 70, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  </label>
                ))}
              </div>
            )}
            {!sumOk && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>The {parts.map(k => SEGMENT_PART_LABEL[k].toLowerCase()).join(', ')} amounts must add up to the {money(bet)} buy-in. Leave them all blank to split it evenly.</div>}
            <div style={note}>{parts.length
              ? `The front nine and the back nine${parts.includes('overall') ? ' and the full round' : ''} are each their own Stableford, with ${teams ? 'their own winning team' : 'their own places'} and their share of the buy-in${split && sumOk ? ` (${parts.map(k => `${SEGMENT_PART_LABEL[k]} ${money(split[k] / 100)}`).join(', ')})` : ''}. Each player keeps his 18-hole strokes where they fall.${parts.includes('overall') ? '' : ' The overall still shows, unpaid.'}`
              : 'One Stableford over the whole round.'}</div>
          </div>
        );
      })()}
      <div style={note}>{teams && mode === 'pot'
        ? `Everyone pays the buy-in${bet > 0 ? ` (${money(bet)})` : ''}; ${placesKey === 'winner' ? 'the team with the most points takes the pot' : 'the paid teams split the pot'}, shared evenly among its players. Teams tied on points share the money for the places they cover.`
        : teams
        ? `Each player on the team with fewer points pays ${bet > 0 ? money(bet) : 'the amount'} for every point of difference, and each player on the other team collects the same.${bet > 0 ? ` Team 74 vs 71 at ${money(bet)} a point: each loser pays ${money(bet * 3)}, each winner collects ${money(bet * 3)}.` : ''}`
        : mode === 'pot'
        ? `Everyone pays the buy-in${bet > 0 ? ` (${money(bet)})` : ''}; ${playMode === 'group' ? (placesKey === 'winner' ? 'in each group the most points takes that group\u2019s pot' : 'in each group the paid places split that group\u2019s pot') : (placesKey === 'winner' ? 'the most points takes the pot' : 'the paid places split the pot')}. Players tied on points share the money for the places they cover; the order shown is decided on the cards (the points over the last 9, 6, 3 and 1 holes).`
        : mode === 'per_point_field'
        ? `Each player wins or pays ${bet > 0 ? money(bet) : 'the amount'} a point × his points minus the ${playMode === 'group' ? 'group\u2019s' : 'field\u2019s'} average.${bet > 0 ? ` ${playMode === 'group' ? 'Group' : 'Field'} average 35: 38 points wins ${money(bet * 3)}, 33 points pays ${money(bet * 2)}.` : ''}`
        : `Every pair of players${playMode === 'group' ? ' in a group' : ''} settles the difference in points.${bet > 0 ? ` 38 vs 33 at ${money(bet)} a point: ${money(bet * 5)}.` : ''}${playMode === 'group' ? '' : ' In a big field this adds up quickly — each player settles with everyone else.'}`}</div>
    </div>
  );
};

// The Nassau matches, bet by bet, in the Results tab: the viewer's matches (all of them
// when the viewer has none — the organizer).
const NassauMatches = ({ game, viewerId }) => {
  const res = game && game.results;
  if (!res || !Array.isArray(res.matches) || res.matches.length === 0) return null;
  const all = res.matches;
  const mine = viewerId ? all.filter(m => m.side_a.player_ids.includes(viewerId) || m.side_b.player_ids.includes(viewerId)) : [];
  const list = mine.length ? mine : all;
  const fmt = (d) => d === 0 ? 'all square' : (d > 0 ? `${d} up` : `${-d} down`);
  const bet = parseFloat(res.bet_amount) || 0;
  return (
    <div data-testid="nassau-matches" style={{ borderTop: '1px solid #e5e7eb' }}>
      {list.map(m => {
        const onB = mine.length && m.side_b.player_ids.includes(viewerId);
        const us = onB ? m.side_b : m.side_a;
        const them = onB ? m.side_a : m.side_b;
        const myNet = onB ? -m.net_a : m.net_a;
        const title = mine.length ? `${us.names.length > 1 ? `${us.names.join(' & ')} ` : 'You '}vs ${them.names.join(' & ')}` : `${m.side_a.names.join(' & ')} vs ${m.side_b.names.join(' & ')}`;
        return (
          <div key={m.key} style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>{title}</span>
              {bet > 0 && m.is_complete && <span style={{ fontSize: 14, fontWeight: 800, color: myNet > 0 ? '#166534' : myNet < 0 ? '#dc2626' : '#6b7280' }}>{myNet === 0 ? 'Even' : `${myNet > 0 ? '+' : '−'}$${Math.abs(myNet).toFixed(2)}`}{m.side_a.names.length > 1 && myNet !== 0 ? ' each' : ''}</span>}
            </div>
            {m.bets.map(b => {
              const d = onB ? -b.diff : b.diff;
              const text = b.finished ? (d === 0 ? 'push' : d > 0 ? `won ${fmt(d)}` : `lost ${fmt(d)}`) : (b.holes_played ? `${fmt(d)} thru ${b.holes_played}` : 'not started');
              return (
                <div key={`${b.kind}-${b.nine}-${b.index}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', padding: '2px 0 2px 8px' }}>
                  <span>{b.label}</span>
                  <span style={{ fontWeight: 600, color: d > 0 ? '#166534' : d < 0 ? '#b91c1c' : '#374151' }}>{text}</span>
                </div>
              );
            })}
          </div>
        );
      })}
      {(res.skipped || []).length > 0 && (
        <div style={{ padding: '8px 14px', fontSize: 12, color: '#92400e' }}>No match for {(res.skipped || []).map(x => x.group_name || `group ${x.group_id}`).join(', ')} — {res.mode === 'teams' ? '2 vs 2 needs four players in the group' : 'a player alone in his group'}.</div>
      )}
    </div>
  );
};

// ── Sixes (Oct 5, 2026 — Sixes_COD_Design_Sep28) ─────────────────────────────────────
// Per foursome: three six-hole 2 vs 2 best-ball matches, partners by cart and driver.
// The organizer picks the strokes and the presses; the players set the carts.
const SixesGamePanel = ({ settings, onChange, bet }) => {
  const s = settings || {};
  const basis = ['gross', 'full'].includes(s.handicap_basis) ? s.handicap_basis : 'low_man';
  const press = ['auto', 'ask'].includes(s.press) ? s.press : 'none';
  const money = (v) => `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
  const amt = bet > 0 ? money(bet) : 'the bet';
  const radio = (on) => ({ padding: '6px 12px', borderRadius: 8, border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#dcfce7' : '#fff', color: on ? '#166534' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
  const sectionHead = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 0 6px' };
  const note = { fontSize: 11, color: '#6b7280', lineHeight: 1.45, marginTop: 4 };
  return (
    <div data-testid="sixes-panel">
      <div style={note}>Each foursome plays three six-hole matches, 2 vs 2: holes 1–6 cart vs cart, 7–12 each driver with the other cart{'’'}s passenger, 13–18 drivers vs passengers. Until the players set the carts, the pairing order is used — players 1 &amp; 2 in one cart, 3 &amp; 4 in the other, the first of each driving. A foursome plays only when all four are in.</div>
      <div style={sectionHead}>Strokes</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => onChange({ handicap_basis: 'low_man' })} style={radio(basis === 'low_man')}>Net — off the low man</button>
        <button type="button" onClick={() => onChange({ handicap_basis: 'full' })} style={radio(basis === 'full')}>Net — full course handicap</button>
        <button type="button" onClick={() => onChange({ handicap_basis: 'gross' })} style={radio(basis === 'gross')}>Gross</button>
      </div>
      <div style={sectionHead}>Presses</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => onChange({ press: 'none' })} style={radio(press === 'none')}>No presses</button>
        <button type="button" onClick={() => onChange({ press: 'auto' })} style={radio(press === 'auto')}>Automatic at 2 down</button>
        <button type="button" onClick={() => onChange({ press: 'ask' })} style={radio(press === 'ask')}>Ask the player at 2 down</button>
      </div>
      <div style={note}>{press === 'auto'
        ? `A new ${amt} bet starts by itself when a side goes 2 down in a match, and again at 2 down on that press. It runs to the end of the match.`
        : press === 'ask'
        ? `A player whose side goes 2 down in a match sees "Press?" on his hole screen. A press is a new ${amt} bet to the end of that match; it can be pressed again at 2 down.`
        : 'Just the three matches.'}</div>
      <div style={{ ...note, marginTop: 10 }}>The side that wins more of the six holes wins the match; a tied match is a push. Each match won, every winner collects {amt} from one player on the other side. 18-hole rounds only.</div>
    </div>
  );
};

// The Sixes matches in the Results tab: the viewer's foursome (every foursome when the
// viewer isn't playing — the organizer), its carts and its three matches.
const SixesMatches = ({ game, viewerId }) => {
  const res = game && game.results;
  if (!res || !Array.isArray(res.groups) || res.groups.length === 0) return null;
  const mineG = viewerId ? res.groups.filter(g => (g.member_ids || []).includes(viewerId)) : [];
  const list = mineG.length ? mineG : res.groups;
  const fmt = (d) => d === 0 ? 'all square' : (d > 0 ? `${d} up` : `${-d} down`);
  const bet = parseFloat(res.bet_amount) || 0;
  return (
    <div data-testid="sixes-matches" style={{ borderTop: '1px solid #e5e7eb' }}>
      {list.map(g => {
        const head = g.group_name || `Group ${g.group_id}`;
        if (g.status !== 'playing') {
          const miss = g.missing_names || [];
          return (
            <div key={g.group_id} style={{ padding: '8px 14px', fontSize: 12, color: '#92400e', borderBottom: '1px solid #f3f4f6' }}>
              {head}: {g.status === 'no_handicap' ? `on hold — ${(g.no_handicap_names || []).join(', ')} ${(g.no_handicap_names || []).length === 1 ? 'has' : 'have'} no handicap. The matches count once it\u2019s added in Profile.` : g.status === 'missing' ? `not playing — ${miss.join(', ')} ${miss.length === 1 ? 'isn’t' : 'aren’t'} in the game.` : (g.note || 'Sixes needs a foursome.')}
            </div>
          );
        }
        const c = g.carts || {};
        const cartText = (c.names || []).map((pair, i) => pair.map(n => n === (c.driver_names || [])[i] ? `${n} 🚗` : n).join(' & ')).join('  ·  ');
        return (
          <div key={g.group_id} style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
              {mineG.length ? '' : `${head} — `}Carts: {cartText}{c.source === 'set' ? (c.set_by_name ? ` (set by ${c.set_by_name})` : '') : ' (pairing order)'}
            </div>
            {g.matches.map(m => {
              const onB = mineG.length > 0 && m.side_b.player_ids.includes(viewerId);
              const us = onB ? m.side_b : m.side_a;
              const them = onB ? m.side_a : m.side_b;
              const myNet = onB ? -m.net_a : m.net_a;
              const partner = mineG.length ? us.names.find((n, i) => us.player_ids[i] !== viewerId) : null;
              const title = mineG.length ? `With ${partner} vs ${them.names.join(' & ')}` : `${m.side_a.names.join(' & ')} vs ${m.side_b.names.join(' & ')}`;
              return (
                <div key={m.key} style={{ padding: '4px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}><span style={{ fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{m.holes_label} </span>{title}</span>
                    {bet > 0 && mineG.length > 0 && m.bets.every(b => b.finished) && <span style={{ fontSize: 14, fontWeight: 800, color: myNet > 0 ? '#166534' : myNet < 0 ? '#dc2626' : '#6b7280' }}>{myNet === 0 ? 'Even' : `${myNet > 0 ? '+' : '−'}$${Math.abs(myNet).toFixed(2)} each`}</span>}
                  </div>
                  {m.bets.map(b => {
                    const d = onB ? -b.diff : b.diff;
                    // The organizer (not in this foursome) sees who is ahead, not "won" / "lost".
                    const lead = b.diff > 0 ? m.side_a.names.join(' & ') : m.side_b.names.join(' & ');
                    const text = mineG.length
                      ? (b.finished ? (d === 0 ? 'push' : d > 0 ? `won ${fmt(d)}` : `lost ${fmt(d)}`) : (b.holes_played ? `${fmt(d)} thru ${b.holes_played}` : 'not started'))
                      : (b.finished ? (b.diff === 0 ? 'push' : `${lead} won ${fmt(Math.abs(b.diff))}`) : (!b.holes_played ? 'not started' : b.diff === 0 ? `all square thru ${b.holes_played}` : `${lead} ${fmt(Math.abs(b.diff))} thru ${b.holes_played}`));
                    return (
                      <div key={`${m.key}-${b.index}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', padding: '1px 0 1px 8px' }}>
                        <span>{b.kind === 'match' ? 'Match' : b.label}</span>
                        <span style={{ fontWeight: 600, color: !mineG.length ? '#374151' : d > 0 ? '#166534' : d < 0 ? '#b91c1c' : '#374151' }}>{text}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

// Front / Back / Overall (Sept 28, 2026): what the game pays. Mirrors strokePlay.SEGMENT_MODES.
const SEGMENT_CHOICES = [
  { key: 'overall', label: 'Whole round', parts: [] },
  { key: 'front_back', label: 'Front & back', parts: ['front', 'back'] },
  { key: 'front_back_overall', label: 'Front, back & overall', parts: ['front', 'back', 'overall'] },
];
const SEGMENT_PART_LABEL = { front: 'Front 9', back: 'Back 9', overall: 'Overall' };
// Each part's share of the buy-in in whole cents; a part left blank splits evenly (the last takes the odd cents).
const segmentSplitCents = (bet, parts, given) => {
  const betCents = Math.round((Number(bet) || 0) * 100);
  const raw = given && typeof given === 'object' ? given : {};
  if (parts.every(k => raw[k] === '' || raw[k] == null)) {
    const each = Math.floor(betCents / parts.length);
    return Object.fromEntries(parts.map((k, i) => [k, i === parts.length - 1 ? betCents - each * (parts.length - 1) : each]));
  }
  return Object.fromEntries(parts.map(k => [k, Math.round((Number(raw[k]) || 0) * 100)]));
};
const StrokePlayGamePanel = ({ settings, onChange, betAmount, onWager, results, savedSettings, saved, isEvent, bestBall = false, gameType = null, fieldSize = null }) => {
  const s = settings || {};
  // One gross + one net (Oct 10, 2026): one combined score per team, so it always ranks
  // on that — no Net / Gross / Both choice (the engine forces it too).
  const grossNetFmt = bestBall && gameType !== 'shamble' && s.format === 'onegross1net';
  const competition = grossNetFmt ? 'net' : (['net', 'gross', 'both'].includes(s.competition) ? s.competition : 'net');
  const usesNet = competition !== 'gross';
  const shamble = bestBall && gameType === 'shamble';
  const bbFormat = bestBall ? bestBallFormatUi(s.format, gameType) : null;
  const usgaPct = bbFormat ? bbFormat.usga : STROKE_PLAY_USGA_PCT;
  const allowance = s.allowance_pct === '' || s.allowance_pct == null ? usgaPct : Number(s.allowance_pct);
  const isUsga = Number(allowance) === usgaPct;
  const who = bestBall ? 'teams' : 'players';
  const bet = parseFloat(betAmount);
  const hasBet = bet > 0;
  const payoutMode = ['external', 'winner', 'places'].includes(s.payout_mode) ? s.payout_mode : (isEvent ? 'external' : 'places');
  const places = Array.isArray(s.places) && s.places.length ? s.places : [50, 30, 20];
  const placesSum = places.reduce((a, b) => a + (Number(b) || 0), 0);
  const grossBuyIn = s.gross_buy_in === '' || s.gross_buy_in == null ? '' : s.gross_buy_in;
  const moneyTies = s.money_ties === 'countback' ? 'countback' : 'split';
  const tieBreak = TIE_BREAK_CHOICES.some(c => c.key === s.tie_break) ? s.tie_break : 'usga';
  const placesPaid = Number(s.places_paid) >= 1 ? Number(s.places_paid) : DEFAULT_PLACES_PAID;
  const nine = results?.hole_count === 9;
  const radio = (on) => ({ padding: '6px 12px', borderRadius: 8, border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#dcfce7' : '#fff', color: on ? '#166534' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
  const sectionHead = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };
  const smallBtn = { fontSize: 12, padding: '4px 9px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', color: '#374151', fontWeight: 600 };
  const moneyBox = { display: 'inline-flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' };
  const moneyInput = { width: 70, padding: '6px 8px 6px 0', border: 'none', fontSize: 14, outline: 'none', background: 'transparent' };
  const money = (v) => `$${Number(v) % 1 === 0 ? Number(v).toFixed(0) : Number(v).toFixed(2)}`;
  const effectiveMode = hasBet ? payoutMode : 'external';
  const paidPlaces = effectiveMode === 'winner' ? 1 : effectiveMode === 'places' ? places.length : placesPaid;
  // Front / Back / Overall (Sept 28, 2026). Net OR gross, 18 holes only.
  const segMode = SEGMENT_CHOICES.some(c => c.key === s.segments) && competition !== 'both' && !nine ? s.segments : 'overall';
  const segParts = (SEGMENT_CHOICES.find(c => c.key === segMode) || SEGMENT_CHOICES[0]).parts;
  const segmented = segParts.length > 0;
  const segCents = segmented && hasBet ? segmentSplitCents(bet, segParts, s.segment_buy_in) : null;
  const segSumOk = !segCents || Object.values(segCents).reduce((a, b) => a + b, 0) === Math.round(bet * 100);
  const segPlaces = (k) => (s.segment_places && Array.isArray(s.segment_places[k]) && s.segment_places[k].length ? s.segment_places[k] : (Array.isArray(s.places) && s.places.length ? s.places : [100]));
  const setSegPlaces = (k, list) => onChange({ segment_places: { ...Object.fromEntries(segParts.map(p => [p, segPlaces(p)])), [k]: list } });
  const setSegBuyIn = (k, v) => onChange({ segment_buy_in: { ...Object.fromEntries(segParts.map(p => [p, segCents ? segCents[p] / 100 : ''])), [k]: v } });
  // What a place pays at today's field: the part's purse × its %; a team place splits among its players.
  const teamSize = bbFormat ? bbFormat.size : 1;
  const placeDollars = (k, pct) => (fieldSize > 0 && segCents ? (segCents[k] * fieldSize * (Number(pct) || 0)) / 10000 : null);

  // Ties left for the organizer — only meaningful for the settings the results were computed with.
  const tieRulesChanged = !!savedSettings && (savedSettings.tie_break !== tieBreak || (!segmented && Number(savedSettings.places_paid || DEFAULT_PLACES_PAID) !== paidPlaces) || savedSettings.competition !== competition || (savedSettings.segments || 'overall') !== segMode);
  const comps = competition === 'both' ? ['net', 'gross'] : [competition];
  // The organizer orders tied PLAYERS (by id) — or, in Best Ball, tied TEAMS (by team number).
  const tieKeyOf = (t) => (bestBall ? Number(t.side_id) : t.player_id);
  // field: tie_order_net / tie_order_gross (the round), or tie_order_front_net … (a nine).
  const setGroupOrder = (field, group, ids) => {
    const inGroup = new Set(group.rows.map(tieKeyOf));
    onChange({ [field]: [...(s[field] || []).filter(id => !inGroup.has(id)), ...(ids || [])] });
  };
  const moveInGroup = (field, group, from, to) => {
    const ids = strokeGroupOrder(group, s[field], tieKeyOf);
    if (to < 0 || to >= ids.length) return;
    const next = ids.slice(); const [id] = next.splice(from, 1); next.splice(to, 0, id);
    setGroupOrder(field, group, next);
  };
  // The parts whose ties the organizer may order: the round, or each nine (+ the overall).
  const segResults = results && results.segments;
  const tieParts = segmented && segResults
    ? segResults.order.map(k => ({ key: k, label: SEGMENT_PART_LABEL[k], totals: (segResults[k] && segResults[k].totals) || [], field: (comp) => (k === 'overall' ? `tie_order_${comp}` : `tie_order_${k}_${comp}`) }))
    : [{ key: 'round', label: null, totals: results ? results.totals : [], field: (comp) => `tie_order_${comp}` }];
  const setPlacesCount = (n) => {
    const next = places.slice(0, n);
    while (next.length < n) next.push(0);
    onChange({ places: next });
  };
  const summary = () => {
    const rank = grossNetFmt ? "the team's gross + net score" : competition === 'both' ? 'net and gross' : competition;
    const hc = usesNet ? ` · ${allowance}% playing handicap` : '';
    const pre = bbFormat ? `${shamble ? `${bbFormat.size === 4 ? `4-person shamble, ${bbFormat.label.toLowerCase()}` : '2-person shamble'}` : (bbFormat.size > 2 ? `${bbFormat.size}-man ${bbFormat.label.toLowerCase()}` : bbFormat.label)} · ` : '';
    const split = bbFormat && hasBet && effectiveMode !== 'external' ? ' · teammates split their winnings' : '';
    if (segmented) {
      const partsTxt = segParts.map(p => SEGMENT_PART_LABEL[p].toLowerCase()).join(', ');
      if (!hasBet) return `${pre}Ranks on ${rank}${hc} · ${partsTxt} each ranked · placings only — no money settled in TTO.`;
      const eachTxt = segParts.map(p => `${SEGMENT_PART_LABEL[p]} ${money((segCents ? segCents[p] : 0) / 100)}${effectiveMode === 'places' ? ` (${segPlaces(p).map(x => `${x}%`).join(' / ')})` : ''}`).join(' · ');
      return `${pre}Ranks on ${rank}${hc} · ${money(bet)} buy-in per player: ${eachTxt}${effectiveMode === 'winner' ? ' · winner takes each' : ''}${split}.`;
    }
    if (!hasBet) return `${pre}Ranks on ${rank}${hc} · placings only — no money settled in TTO.`;
    if (effectiveMode === 'winner') return `${pre}Ranks on ${rank}${hc} · ${money(bet)} buy-in per player · winner takes all${competition === 'both' ? ' in each purse' : ''}${split}.`;
    return `${pre}Ranks on ${rank}${hc} · ${money(bet)} buy-in per player · ${places.length} place${places.length === 1 ? '' : 's'} paid ${places.map(p => `${p}%`).join(' / ')}${competition === 'both' ? ' in each purse' : ''}${split}.`;
  };

  return (
    <div style={{ marginTop: 12 }}>
      {bbFormat && (
        <div style={{ marginBottom: 12 }} data-testid="best-ball-format">
          <div style={sectionHead}>Format</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {BEST_BALL_FORMATS_UI.filter(f0 => !shamble || SHAMBLE_FORMATS_UI[f0.key]).map(f0 => { const f = bestBallFormatUi(f0.key, gameType); return (
              <button key={f.key} type="button"
                onClick={() => onChange({ format: f.key, ...(isUsga ? { allowance_pct: f.usga } : {}), ...(f.mode === 'gross_net' ? { competition: 'net' } : {}) })}
                style={radio(bbFormat.key === f.key)}>{f.label}</button>
            ); })}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
            {bbFormat.help} The teams are the groups on the Pairings page.
            {' '}<a href={shamble ? '/guides/how-to-set-up-a-shamble-golf-tournament?from=app' : '/guides/how-to-set-up-a-best-ball-golf-tournament?from=app'} target="_blank" rel="noopener noreferrer" style={{ color: '#15803d', fontWeight: 600 }}>📖 Setup guide ↗</a>
          </div>
        </div>
      )}
      {shamble && (
        <div style={{ marginBottom: 12 }} data-testid="shamble-rules">
          <div style={sectionHead}>Drive rule <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· printed on the cards and shown on the players' scoring screens</span></div>
          <input type="text" maxLength={140} value={s.rules_note || ''}
            onChange={(e) => onChange({ rules_note: e.target.value })}
            placeholder={bbFormat.size === 4 ? "e.g. Each player's drive must be used 3 times" : "e.g. Each player's drive must be used 7 times"}
            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
            Optional. TTO doesn't track whose drive was used — the rule is for the players. GHIN: a shamble score is not acceptable for handicap purposes, so saving this game turns the round's GHIN posting off.
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>
        {!grossNetFmt && <div>
          <div style={sectionHead}>Rank on</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['net', 'Net'], ['gross', 'Gross'], ['both', 'Both']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => onChange({ competition: v, ...(v === 'both' && segmented ? { segments: 'overall' } : {}) })} style={radio(competition === v)}>{l}</button>
            ))}
          </div>
        </div>}
        {usesNet && (
          <div>
            <div style={sectionHead}>Handicap allowance <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· {isUsga ? (bbFormat && bbFormat.size === 3 ? 'TTO default (no USGA figure for 3-man)' : 'USGA Appendix C') : 'set by you'}</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" min="0" max="100" step="1" value={s.allowance_pct == null ? usgaPct : s.allowance_pct}
                onChange={(e) => onChange({ allowance_pct: e.target.value === '' ? '' : Number(e.target.value) })}
                style={{ width: 62, padding: '5px 6px', border: isUsga ? '1px solid #d1d5db' : '2px solid #b45309', borderRadius: 6, fontSize: 13, textAlign: 'center', background: isUsga ? '#fff' : '#fffbeb' }} />
              <span style={{ fontSize: 13, color: '#374151' }}>%</span>
              {!isUsga && <button type="button" onClick={() => onChange({ allowance_pct: usgaPct })} style={{ ...smallBtn, fontWeight: 400 }}>Reset to {usgaPct}</button>}
            </div>
          </div>
        )}
      </div>
      {usesNet && !bestBall && (
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
          Applied to each player's course handicap before rounding, then rounded once (course handicap 9.5 × 95% = 9.0 → 9). Strokes fall on the hardest holes of the player's own tee. The USGA suggests 95% for a field of 30–100; a small group may prefer 100%.
        </div>
      )}
      {usesNet && bestBall && (
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
          No team handicap: each player plays off {allowance}% of his own course handicap (applied before rounding, then rounded once), with strokes on the hardest holes of his own tee. The team then counts {grossNetFmt ? "one player's gross score and a different player's net score" : bbFormat.bestOf === 1 ? 'its best net score' : `its best ${bbFormat.bestOf} net scores`} on each hole. {shamble
            ? 'USGA Appendix C for a selected-drive format: 2-person 75%, 4-person 65%. Many clubs play a shamble at 80% or 70% — set yours here.'
            : grossNetFmt ? 'The net ball starts at 100% of course handicap — change it if your event uses another allowance.'
            : bbFormat && bbFormat.size === 3 ? 'The USGA has no figure for 3-man best ball; TTO starts best 1 of 3 at 75% and best 2 of 3 at 85%, like best 1 and 2 of 4. Change it if your group uses another.'
            : 'USGA Appendix C: 2-man best ball 85%; best 1, 2 or 3 of 4: 75%, 85%, 100%.'}
        </div>
      )}

      {/* Stroke Play setup guide (Sept 28, 2026) — Best Ball links its own guide in the Format row. */}
      {!bestBall && (
        <div style={{ fontSize: 11, marginTop: 6 }}>
          <a href="/guides/how-to-set-up-a-stroke-play-golf-tournament?from=app" target="_blank" rel="noopener noreferrer" style={{ color: '#15803d', fontWeight: 600 }}>📖 Stroke Play setup guide ↗</a>
        </div>
      )}

      {/* What it pays: the whole round, or the nines (Sept 28, 2026) */}
      {!nine && (
        <div style={{ marginTop: 14, borderTop: '1px solid #d1fae5', paddingTop: 12 }} data-testid="stroke-segments">
          <div style={sectionHead}>What it pays</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {SEGMENT_CHOICES.map(c => (
              <button key={c.key} type="button" disabled={competition === 'both' && c.key !== 'overall'}
                onClick={() => onChange({ segments: c.key })}
                style={{ ...radio(segMode === c.key), opacity: competition === 'both' && c.key !== 'overall' ? 0.45 : 1, cursor: competition === 'both' && c.key !== 'overall' ? 'not-allowed' : 'pointer' }}>{c.label}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
            {competition === 'both'
              ? 'To pay the nines separately, rank on Net or Gross.'
              : segmented
                ? `The front nine and the back nine${segParts.includes('overall') ? ' and the full round' : ''} are each their own competition, with their own places and money. Each player keeps his 18-hole strokes where they fall.${segParts.includes('overall') ? '' : ' The overall still shows on the leaderboard, unpaid.'}`
                : 'One competition over the full round.'}
          </div>
        </div>
      )}

      {/* Payouts */}
      <div style={{ marginTop: 14, borderTop: '1px solid #d1fae5', paddingTop: 12 }} data-testid="stroke-payouts">
        <div style={sectionHead}>Payouts</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            ['external', 'Handled outside TTO', isEvent ? 'TTO names the places; the club pays from its own prize table.' : 'TTO names the places; you settle the money your own way.'],
            ['winner', 'Winner takes all', 'Every buy-in goes to first place — split on a tie.'],
            ['places', 'Paid places', 'You set how many places are paid and what each gets.'],
          ].map(([v, l, h]) => {
            const on = payoutMode === v;
            return (
              <button key={v} type="button" aria-pressed={on}
                onClick={() => { onChange({ payout_mode: v }); if (v === 'external') onWager('0'); else if (!hasBet) onWager(''); }}
                style={{ textAlign: 'left', padding: '7px 10px', borderRadius: 8, cursor: 'pointer', border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#f0fdf4' : '#fff' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: on ? '#166534' : '#374151' }}>{on ? '● ' : '○ '}{l}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.35 }}>{h}</div>
              </button>
            );
          })}
        </div>
        {payoutMode !== 'external' && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Buy-in</span>
              <span style={moneyBox}>
                <span style={{ padding: '0 4px 0 8px', color: '#6b7280', fontSize: 13 }}>$</span>
                <input type="number" min="0" step="1" value={betAmount} onChange={(e) => onWager(e.target.value)} placeholder="0.00" style={moneyInput} />
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>per player</span>
            </div>
            {!hasBet && <div style={{ marginTop: 6, fontSize: 12, color: '#92400e' }}>Enter a buy-in, or choose Handled outside TTO for placings only.</div>}
            {hasBet && competition === 'both' && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13, color: '#374151' }}>
                Of the {money(bet)}, <span style={moneyBox}><span style={{ padding: '0 4px 0 8px', color: '#6b7280', fontSize: 13 }}>$</span>
                  <input type="number" min="0" max={bet} step="1" value={grossBuyIn} onChange={(e) => onChange({ gross_buy_in: e.target.value === '' ? '' : Number(e.target.value) })} placeholder="0" style={{ ...moneyInput, width: 56 }} /></span>
                goes to the gross purse and {money(Math.max(0, bet - (Number(grossBuyIn) || 0)))} to net.
              </div>
            )}
            {hasBet && segmented && (
              <div style={{ marginTop: 10 }} data-testid="segment-money">
                <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 6 }}>How the {money(bet)} is split</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  {segParts.map(p => (
                    <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
                      {SEGMENT_PART_LABEL[p]}
                      <span style={moneyBox}><span style={{ padding: '0 4px 0 8px', color: '#6b7280', fontSize: 13 }}>$</span>
                        <input type="number" min="0" step="1" value={s.segment_buy_in && s.segment_buy_in[p] != null && s.segment_buy_in[p] !== '' ? s.segment_buy_in[p] : (segCents ? segCents[p] / 100 : '')}
                          onChange={(e) => setSegBuyIn(p, e.target.value === '' ? '' : Number(e.target.value))} style={{ ...moneyInput, width: 56 }} /></span>
                    </label>
                  ))}
                </div>
                {!segSumOk && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>The parts need to add up to the {money(bet)} buy-in.</div>}
                {payoutMode === 'places' && segParts.map(p => {
                  const list = segPlaces(p);
                  const sum = list.reduce((a, b) => a + (Number(b) || 0), 0);
                  return (
                    <div key={p} style={{ marginTop: 10 }} data-testid={`segment-places-${p}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#166534', minWidth: 58 }}>{SEGMENT_PART_LABEL[p]}</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
                          Places paid
                          <select value={list.length} onChange={(e) => { const n = Number(e.target.value); const next = list.slice(0, n); while (next.length < n) next.push(0); setSegPlaces(p, next); }} style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontWeight: 700 }}>
                            {Array.from({ length: 10 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </label>
                        {list.map((v, i) => {
                          const d = placeDollars(p, v);
                          return (
                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' }}>
                              <span style={{ color: '#6b7280' }}>{tieOrdinal(i + 1)}</span>
                              <input type="number" min="0" max="100" step="1" value={v}
                                onChange={(e) => { const next = list.slice(); next[i] = e.target.value === '' ? '' : Number(e.target.value); setSegPlaces(p, next); }}
                                style={{ width: 54, padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, textAlign: 'center' }} />%
                              {d != null && <span style={{ color: '#6b7280' }}>= {money(d)}{teamSize > 1 ? ` (${money(d / teamSize)} each)` : ''}</span>}
                            </label>
                          );
                        })}
                        <span style={{ fontSize: 12, fontWeight: 700, color: Math.round(sum * 100) === 10000 ? '#166534' : '#b91c1c' }}>= {Math.round(sum * 100) / 100}%</span>
                      </div>
                    </div>
                  );
                })}
                {fieldSize > 0 && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>Dollar amounts are for today&apos;s field of {fieldSize} player{fieldSize === 1 ? '' : 's'}; they follow the field if it changes.</div>}
              </div>
            )}
            {hasBet && payoutMode === 'places' && !segmented && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
                    Places paid
                    <select value={places.length} onChange={(e) => setPlacesCount(Number(e.target.value))} style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontWeight: 700 }}>
                      {Array.from({ length: 10 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  {places.map((v, i) => (
                    <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' }}>
                      <span style={{ color: '#6b7280' }}>{tieOrdinal(i + 1)}</span>
                      <input type="number" min="0" max="100" step="1" value={v}
                        onChange={(e) => { const next = places.slice(); next[i] = e.target.value === '' ? '' : Number(e.target.value); onChange({ places: next }); }}
                        style={{ width: 54, padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, textAlign: 'center' }} />%
                    </label>
                  ))}
                  <span style={{ fontSize: 12, fontWeight: 700, color: Math.round(placesSum * 100) === 10000 ? '#166534' : '#b91c1c' }}>= {Math.round(placesSum * 100) / 100}%</span>
                </div>
                {Math.round(placesSum * 100) !== 10000 && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>The percentages need to add up to 100.</div>}
              </div>
            )}
            {hasBet && (
              <div style={{ marginTop: 10 }}>
                <div style={sectionHead}>Money on a tie</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => onChange({ money_ties: 'split' })} style={radio(moneyTies === 'split')}>Tied scores share the money</button>
                  <button type="button" onClick={() => onChange({ money_ties: 'countback' })} style={radio(moneyTies === 'countback')}>Pay by the decided order</button>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, lineHeight: 1.4 }}>
                  {moneyTies === 'split'
                    ? `${bestBall ? 'Teams' : 'Players'} on the same score split the money for the places they occupy (three tied for 2nd share 2nd + 3rd + 4th), whatever the countback says about the trophy.`
                    : 'The countback below decides the places, and each place is paid its own share. A tie it cannot split still shares.'}
                  {bestBall && ' Every player pays the buy-in; teammates split their team\'s winnings evenly.'}
                </div>
              </div>
            )}
            {hasBet && bbFormat && bbFormat.size === 4 && (
              <div style={{ marginTop: 10 }} data-testid="best-ball-blind-shares">
                {/* The blind draw (Sept 26, 2026): the organizer decides whether a blind is paid. */}
                <div style={sectionHead}>The blind</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => onChange({ blind_shares: true })} style={radio(s.blind_shares !== false)}>Blind is paid</button>
                  <button type="button" onClick={() => onChange({ blind_shares: false })} style={radio(s.blind_shares === false)}>Blind is not paid</button>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, lineHeight: 1.4 }}>
                  A team one player short gets a blind: TTO draws a player at random from the missing player's handicap band once the round starts, and his scores fill the empty spot.{' '}
                  {s.blind_shares !== false
                    ? 'If the team wins money, the blind takes a player\'s share of it, on top of whatever his own team wins.'
                    : 'The blind plays for the team but takes none of its money; the team\'s three players split its whole share.'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* How ties are decided */}
      <div style={{ marginTop: 14, borderTop: '1px solid #d1fae5', paddingTop: 12 }} data-testid="stroke-tie-break">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={sectionHead}>How ties are decided</div>
          {effectiveMode === 'external' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', marginBottom: 6 }}>
              Places decided
              <select value={placesPaid} onChange={(e) => onChange({ places_paid: Number(e.target.value) })} style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontWeight: 700 }}>
                {Array.from({ length: Math.max(10, placesPaid) }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {TIE_BREAK_CHOICES.map(c => {
            const on = tieBreak === c.key;
            return (
              <button key={c.key} type="button" onClick={() => onChange({ tie_break: c.key })} aria-pressed={on}
                style={{ textAlign: 'left', padding: '7px 10px', borderRadius: 8, cursor: 'pointer', border: on ? '2px solid #166534' : '1px solid #d1d5db', background: on ? '#f0fdf4' : '#fff' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: on ? '#166534' : '#374151' }}>{on ? '● ' : '○ '}{c.label}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.35 }}>{bestBall
                  ? (c.key === 'usga'
                      ? `Best team score over the ${nine ? 'last 6 holes, then the last 3, then the last hole' : 'last 9 holes, then the last 6, the last 3, then the 18th'} — on the team's hole-by-hole net (each player's strokes are already on his own holes).`
                      : c.key === 'committee' ? 'Tied teams stay tied until you put them in order here — after a playoff or a ruling.' : STROKE_TIE_HELP[c.key](nine))
                  : STROKE_TIE_HELP[c.key](nine)}</div>
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
          Only ties for the top {paidPlaces === 1 ? 'place' : `${paidPlaces} places`} are decided, once every {bestBall ? 'team' : 'player'} in the tie has finished; other ties keep their T.
          {tieBreak !== 'committee' && ' Identical cards still need your call — they show up below.'}
          {' '}Results mark a place won on the cards <strong>SP</strong> and one decided by playoff or by you <strong>PO</strong>.
        </div>
        {saved && tieRulesChanged && <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>Save to see which ties are left for you to decide under this choice.</div>}
        {saved && !tieRulesChanged && tieParts.flatMap(part => comps.map(comp => {
          const groups = results ? strokeTieGroups(part.totals, comp) : [];
          if (groups.length === 0) return null;
          const field = part.field(comp);
          const order = (s[field] || []);
          const savedOrder = (savedSettings && savedSettings[field]) || [];
          return (
            <div key={`${part.key}-${comp}`} style={{ marginTop: 10 }} data-testid={`stroke-tie-groups-${part.key === 'round' ? '' : `${part.key}-`}${comp}`}>
              <div style={sectionHead}>Ties to decide{part.label ? ` — ${part.label}` : ''}{competition === 'both' ? ` — ${comp}` : ''}</div>
              {groups.map(group => {
                const ids = strokeGroupOrder(group, order, tieKeyOf);
                const inOrder = group.rows.every(t => order.includes(tieKeyOf(t)));
                const savedIn = group.rows.every(t => savedOrder.includes(tieKeyOf(t)));
                const changed = inOrder !== savedIn || (inOrder && strokeGroupOrder(group, savedOrder, tieKeyOf).join(',') !== ids.join(','));
                const last = group.sub + group.rows.length - 1;
                const byId = Object.fromEntries(group.rows.map(t => [tieKeyOf(t), t]));
                const score = comp === 'net' ? (group.rows[0].net_total != null ? `net ${group.rows[0].net_total}` : '') : (group.rows[0].gross_total != null ? `${group.rows[0].gross_total}` : '');
                return (
                  <div key={group.sub} style={{ border: `1px solid ${inOrder ? '#bbf7d0' : '#fcd34d'}`, background: inOrder ? '#f0fdf4' : '#fffbeb', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>
                        Tied for {tieOrdinal(group.sub)}{score ? ` · ${score}` : ''}<span style={{ fontWeight: 400, color: '#6b7280' }}> — places {group.sub}–{last}</span>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: changed ? '#b45309' : (inOrder ? '#166534' : '#92400e') }}>
                        {changed ? 'Save to apply' : (inOrder ? '✓ Decided — shows as PO' : `Playoff pending — shows as T${group.sub}`)}
                      </div>
                    </div>
                    {ids.map((id, i) => (
                      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: i ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
                        <span style={{ width: 26, textAlign: 'center', fontSize: 13, fontWeight: 800, color: inOrder ? '#166534' : '#9ca3af' }}>{inOrder ? group.sub + i : `T${group.sub}`}</span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#1f2937' }}><strong>{byId[id].player_name}</strong></span>
                        <button type="button" aria-label={`Move ${byId[id].player_name} up`} disabled={i === 0} onClick={() => moveInGroup(field, group, i, i - 1)} style={{ ...smallBtn, padding: '2px 8px', opacity: i === 0 ? 0.35 : 1 }}>▲</button>
                        <button type="button" aria-label={`Move ${byId[id].player_name} down`} disabled={i === ids.length - 1} onClick={() => moveInGroup(field, group, i, i + 1)} style={{ ...smallBtn, padding: '2px 8px', opacity: i === ids.length - 1 ? 0.35 : 1 }}>▼</button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                      {!inOrder && <button type="button" onClick={() => setGroupOrder(field, group, ids)} style={{ ...smallBtn, borderColor: '#166534', color: '#166534' }}>Use this order</button>}
                      {inOrder && <button type="button" onClick={() => setGroupOrder(field, group, [])} style={smallBtn}>Back to tied</button>}
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.4 }}>Put the {who} in finishing order (▲ ▼), then Save.</div>
            </div>
          );
        }))}
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: '#166534', background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: 6, padding: '5px 9px' }}>{summary()}</div>
    </div>
  );
};

// ── Skins family (Sept 15, 2026) ─────────────────────────────────────────────
// The four (now six) skins catalog entries are ONE card in Games Setup: basis
// (gross / net) x payout (carryover / no carryover / pot) picks the catalog key.
// round_games rows keep their keys, so nothing already saved moves, and the
// roster carry-over on re-save (matched by game_type + stake) keeps working.
const SKINS_KEY = {
  net:   { carryover: 'skins_carryover',       nocarry: 'skins_no_carryover',       pot: 'skins_pot' },
  gross: { carryover: 'skins_carryover_gross', nocarry: 'skins_no_carryover_gross', pot: 'skins_pot_gross' },
};
const isSkinsType = (gt) => typeof gt === 'string' && gt.startsWith('skins_');
// Oct 28, 2026: games that can take "Everyone in the field plays" (scoringService.everyoneEligible).
const everyoneGameType = (gt) => isSkinsType(gt) || ['ctp', 'putts', 'dots', 'stroke_play', 'stableford'].includes(gt);
const skinsParts = (gt) => {
  for (const basis of ['net', 'gross']) for (const payout of ['carryover', 'nocarry', 'pot']) {
    if (SKINS_KEY[basis][payout] === gt) return { basis, payout };
  }
  return { basis: 'net', payout: 'carryover' };
};

const GameSetupModal = ({ teeTime, onClose, onSaved, onFinalize, onEmailSettlement, isEvent = false }) => {
  const { addToast } = useToast();
  const [catalog, setCatalog] = useState([]);
  // instances: array of { instanceId, game_type, checked, bet_amount, physical_pot }
  // Supports multiple instances of the same game_type (e.g. two skins games at different stakes)
  const [instances, setInstances] = useState([]);
  const [rulesGame, setRulesGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [finalizedAt, setFinalizedAt] = useState(null);
  const [existingGames, setExistingGames] = useState([]);
  const [togglingOptIn, setTogglingOptIn] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [standings, setStandings] = useState(null);
  const [bbNotice, setBbNotice] = useState(null);   // Best Ball blind: "the draw is final" — a modal, not a toast (organizers miss toasts)
  const [settlements, setSettlements] = useState(null);
  const [orgTimezone, setOrgTimezone] = useState('America/New_York');
  const [autoFinalize, setAutoFinalize] = useState(false);
  const [autoFinalizeSaving, setAutoFinalizeSaving] = useState(false);
  const [roundReady, setRoundReady] = useState(false);
  const [incompleteFinalize, setIncompleteFinalize] = useState(null); // WS-4 completeness prompt
  const [dnfBusyId, setDnfBusyId] = useState(null);
  const [readyParticipantCount, setReadyParticipantCount] = useState(0);
  const instanceCounter = React.useRef(0);
  const SCRAMBLE_DEFAULT = { team_size: 4, basis: 'gross', allowance: [25, 20, 15, 10], stroke_index_tee: 'low_player', team_overrides: {}, tie_break: 'usga', places_paid: 3, tie_order: [] };
  // Stroke play (Sept 23, 2026): an event defaults to placings only (the club has its
  // own prize table); a regular round defaults to paid places 50/30/20 (Dennis).
  const STROKE_DEFAULT = { competition: 'net', allowance_pct: STROKE_PLAY_USGA_PCT, payout_mode: isEvent ? 'external' : 'places', places: [50, 30, 20], gross_buy_in: '', money_ties: 'split', tie_break: 'usga', places_paid: 3, tie_order_net: [], tie_order_gross: [] };
  // Best Ball (Sept 23, 2026) rides the same panel and settings shape, plus a format
  // (2-man best ball by default, USGA 85%). Its tie orders are TEAM numbers.
  // Shamble (Sept 28, 2026) too: 2-person by default, USGA 75%.
  const isStrokeType = (gt) => gt === 'stroke_play' || isBestBallGameType(gt);
  const strokeDefaultFor = (gt) => (isBestBallGameType(gt) ? { ...STROKE_DEFAULT, format: '2man', allowance_pct: bestBallFormatUi('2man', gt).usga, ...(gt === 'shamble' ? { rules_note: '' } : {}) } : STROKE_DEFAULT);

  const newInstanceId = () => `inst_${++instanceCounter.current}`;

  // Format a DB "timestamp without time zone" (stored as UTC) into the org's
  // timezone. Appends 'Z' so the value is interpreted as UTC, then formats with
  // an explicit timeZone — fixes the finalized-time being hours off.
  const formatFinalizedAt = (ts) => {
    if (!ts) return null;
    let s = ts;
    if (typeof s === 'string' && !s.endsWith('Z') && !s.includes('+')) s = s + 'Z';
    return new Date(s).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZone: orgTimezone || 'America/New_York',
    });
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const [catRes, existRes] = await Promise.all([
        fetch(`${API_URL}/scoring/game-catalog`, { credentials: 'include', headers }),
        fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/games`, { credentials: 'include', headers }),
      ]);
      const catData = await catRes.json();
      const existData = await existRes.json();
      const games = catData.games || [];
      const existing = existData.games || [];

      setCatalog(games);
      const finalized = existing.some(e => e.status === 'completed');
      setIsFinalized(finalized);
      // NOTE: finalizedAt is set from the settlement record's finalized_at below
      // (the true finalize moment). round_games has no finalized timestamp — its
      // created_at is when the game was set up, not when the round was finalized.

      // Org timezone for correct finalized-time display (settings stores IANA tz).
      try {
        const tzRes = await fetch(`${API_URL}/settings`, { credentials: 'include', headers });
        if (tzRes.ok) {
          const tzData = await tzRes.json();
          if (tzData?.data?.timezone) setOrgTimezone(tzData.data.timezone);
        }
      } catch (_) { /* keep default America/New_York */ }

      setExistingGames(existing);

      // Load the per-tee-time auto-finalize toggle state.
      try {
        const afRes = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/auto-finalize`, { credentials: 'include', headers });
        if (afRes.ok) { const afData = await afRes.json(); setAutoFinalize(afData.auto_finalize === true); setRoundReady(afData.ready === true); setReadyParticipantCount(afData.participant_count || 0); }
      } catch (_) { /* default false */ }

      // Build instances array:
      // - One instance per existing round_games row (preserves multiple instances of same type)
      // - One unchecked instance per catalog game that has no existing row
      const builtInstances = [];
      const coveredTypes = new Set();

      const scopeOf = (gt) => (games.find(g => g.game_type === gt)?.competitor_scope) || 'individual';
      for (const ex of existing) {
        builtInstances.push({
          instanceId: newInstanceId(),
          game_type: ex.game_type,
          checked: true,
          bet_amount: String(ex.bet_amount),
          physical_pot: ex.settings?.physical_pot === true,
          everyone: ex.settings?.everyone === true,   // Oct 28, 2026: "Everyone in the field plays"
          // Skins (Sept 29, 2026): how a net game gives strokes — off the low man (default) or full course handicap.
          skins_hcp: ex.settings?.handicap_basis === 'full' ? 'full' : 'low_man',
          // Closest to the Pin (Sept 28, 2026): must make par + the holes (null = the par 3s).
          nassau_settings: ex.game_type === 'nassau' ? { mode: ex.settings?.mode === 'teams' ? 'teams' : 'pairs', handicap_basis: ['gross', 'full'].includes(ex.settings?.handicap_basis) ? ex.settings.handicap_basis : 'low_man', press: ['auto', 'none'].includes(ex.settings?.press) ? ex.settings.press : 'ask' } : null,
          match_play_settings: ex.game_type === 'match_play' ? { mode: ex.settings?.mode === 'teams' ? 'teams' : 'pairs', handicap_basis: ['gross', 'full'].includes(ex.settings?.handicap_basis) ? ex.settings.handicap_basis : 'usga' } : null,   // Oct 6, 2026
          sixes_settings: ex.game_type === 'sixes' ? { handicap_basis: ['gross', 'full'].includes(ex.settings?.handicap_basis) ? ex.settings.handicap_basis : 'low_man', press: ['auto', 'ask'].includes(ex.settings?.press) ? ex.settings.press : 'none' } : null,   // Oct 5, 2026
          stableford_settings: ex.game_type === 'stableford' ? { competition: ex.settings?.competition === 'gross' ? 'gross' : 'net', allowance_pct: ex.settings?.team_mode === 'teams' && (ex.settings?.allowance_pct == null || Number(ex.settings.allowance_pct) === 85) ? 95 : (ex.settings?.allowance_pct ?? 95), payout_mode: ['per_point_field', 'per_point_pairs'].includes(ex.settings?.payout_mode) ? ex.settings.payout_mode : ex.settings?.payout_mode === 'per_point_teams' ? 'per_point_pairs' : 'pot', places: Array.isArray(ex.settings?.places) && ex.settings.places.length ? ex.settings.places : [100], mode: ex.settings?.mode === 'group' || ex.settings?.mode === 'field' ? ex.settings.mode : (ex.settings?.team_mode === 'teams' ? 'group' : 'field'), segments: ex.settings?.segments || 'overall', segment_buy_in: ex.settings?.segment_buy_in || null } : null,
          dots_settings: ex.game_type === 'dots' ? { count: { birdies: ex.settings?.count?.birdies !== false, sandies: ex.settings?.count?.sandies !== false, polies: ex.settings?.count?.polies !== false }, mode: dotsModeOf(ex.settings) } : null,
          ctp_settings: ex.game_type === 'ctp' ? { must_make_par: ex.settings?.must_make_par === true, holes: Array.isArray(ex.settings?.holes) ? ex.settings.holes : null, funding: ex.settings?.funding === 'per_player' ? 'per_player' : 'per_hole' } : null,
          existingId: ex.id, // link back to round_games row for opt-in display
          status: ex.status,
          // Team game (scramble): its config rides in settings — keep the server's copy
          // (incl. handicap_snapshot) and edit only the fields the panel exposes.
          team_settings: (ex.competitor_scope === 'team' || scopeOf(ex.game_type) === 'team') ? { ...SCRAMBLE_DEFAULT, ...(ex.settings || {}) } : null,
          stroke_settings: isStrokeType(ex.game_type) ? { ...strokeDefaultFor(ex.game_type), ...(ex.settings || {}) } : null,
        });
        coveredTypes.add(ex.game_type);
      }

      // Add one unchecked placeholder for each catalog game not yet added.
      // Skins is one family: ONE placeholder (net carryover) unless a skins row exists.
      let skinsCovered = existing.some(e => isSkinsType(e.game_type));
      for (const g of games) {
        if (isSkinsType(g.game_type)) {
          if (skinsCovered) continue;
          skinsCovered = true;
          builtInstances.push({
            instanceId: newInstanceId(),
            game_type: games.some(x => x.game_type === 'skins_carryover') ? 'skins_carryover' : g.game_type,
            checked: false, bet_amount: '', physical_pot: false, skins_hcp: 'low_man', existingId: null, status: null, team_settings: null,
          });
          continue;
        }
        if (!coveredTypes.has(g.game_type)) {
          builtInstances.push({
            instanceId: newInstanceId(),
            game_type: g.game_type,
            checked: false,
            bet_amount: '',
            physical_pot: false,
            existingId: null,
            status: null,
            team_settings: (g.competitor_scope === 'team') ? { ...SCRAMBLE_DEFAULT } : null,
            stroke_settings: isStrokeType(g.game_type) ? { ...strokeDefaultFor(g.game_type) } : null,
          });
        }
      }

      setInstances(builtInstances);

      // Team game support: standings (for the per-team handicap table) whenever a
      // scramble game already exists. (Strokes always fall on the lower-handicap
      // player's tee — the tee picker was removed Sept 15 at Dennis's request; the
      // backend default 'low_player' stands.) A stroke play game needs them too —
      // for the ties left to the organizer (Sept 23, 2026).
      if (!finalized && existing.some(e => e.competitor_scope === 'team' || scopeOf(e.game_type) === 'team' || isStrokeType(e.game_type))) {
        try {
          const stRes = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/standings`, { credentials: 'include', headers });
          if (stRes.ok) setStandings(await stRes.json());
        } catch (_) { /* table shows "save to see" */ }
      }

      if (finalized) {
        const stRes = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/standings`, { credentials: 'include', headers });
        if (stRes.ok) setStandings(await stRes.json());
        const settlRes = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/settlements`, { credentials: 'include', headers });
        if (settlRes.ok) {
          const settlData = await settlRes.json();
          setSettlements(settlData);
          // True finalize time comes from the settlement record, not the game row.
          const finalizedTs = settlData?.settlements?.find(s => s.finalized_at)?.finalized_at || null;
          setFinalizedAt(finalizedTs);
        }
      }
    } catch (err) {
      console.error('GameSetupModal load error:', err);
      addToast('Failed to load games', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [teeTime.id]);

  // Add a second (or third) instance of a game type
  const handleAddInstance = (game_type) => {
    setInstances(prev => [...prev, {
      instanceId: newInstanceId(),
      game_type,
      checked: true,
      bet_amount: '',
      physical_pot: false,
      existingId: null,
    }]);
  };

  // Remove an instance (only unchecked placeholders or extra instances)
  const handleRemoveInstance = (instanceId) => {
    setInstances(prev => prev.filter(i => i.instanceId !== instanceId));
  };

  const handleToggle = (instanceId) => {
    setInstances(prev => prev.map(i =>
      i.instanceId === instanceId ? { ...i, checked: !i.checked, bet_amount: i.bet_amount || '' } : i
    ));
  };

  const handleWager = (instanceId, value) => {
    setInstances(prev => prev.map(i =>
      i.instanceId === instanceId ? { ...i, bet_amount: value } : i
    ));
  };

  const handlePhysicalPot = (instanceId) => {
    setInstances(prev => prev.map(i =>
      i.instanceId === instanceId ? { ...i, physical_pot: !i.physical_pot } : i
    ));
  };

  // Scramble config edits (team size / basis / allowance / stroke-index tee / overrides).
  const handleTeamSettings = (instanceId, patch) => {
    setInstances(prev => prev.map(i =>
      i.instanceId === instanceId ? { ...i, team_settings: { ...(i.team_settings || SCRAMBLE_DEFAULT), ...patch } } : i
    ));
  };
  // Stroke play config edits (ranking / allowance / payouts / ties).
  const handleStrokeSettings = (instanceId, patch) => {
    setInstances(prev => prev.map(i =>
      i.instanceId === instanceId ? { ...i, stroke_settings: { ...(i.stroke_settings || strokeDefaultFor(i.game_type)), ...patch } } : i
    ));
  };

  const handleOrganizerOptIn = async (game, playerId, currentlyOptedIn) => {
    const key = `${game.id}-${playerId}`;
    setTogglingOptIn(prev => ({ ...prev, [key]: true }));
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/games/${game.id}/opt-in`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ player_id: playerId, opt_in: !currentlyOptedIn }),
      });
      if (res.ok) {
        const data = await res.json();
        setExistingGames(prev => prev.map(g => g.id === game.id ? { ...g, participant_ids: data.game?.participant_ids ?? g.participant_ids } : g));
        if (data.handicap_warning) setBbNotice(data.handicap_warning);   // Oct 5, 2026: no index — warned, not refused
        // Best Ball (Sept 26, 2026): an Out mid-round may have drawn a blind, an In may
        // have released one — reload the teams so the card shows it.
        if (isBestBallGameType(game.game_type)) {
          try {
            const stRes = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/standings`, { credentials: 'include', headers });
            if (stRes.ok) setStandings(await stRes.json());
          } catch (_) { /* the card refreshes on the next open */ }
        }
      } else {
        const err = await res.json().catch(() => ({}));
        if (err.code === 'BLIND_FINAL') setBbNotice(err.error);
        else addToast('Failed to update opt-in', 'error');
      }
    } catch (err) {
      addToast('Failed to update opt-in', 'error');
    } finally {
      setTogglingOptIn(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSave = async () => {
    const checkedInstances = instances.filter(i => i.checked);
    // Best Ball and Shamble are one team game on the round (Sept 28, 2026): the teams,
    // the blind draw, the board and the printed card belong to a single team game.
    if (checkedInstances.filter(i => isBestBallGameType(i.game_type)).length > 1) {
      setBbNotice({ title: 'One team game per round', text: 'Best Ball and Shamble are the same team game — the teams, the blind draw, the leaderboard and the printed cards belong to one of them. Untick one and save again.' });
      return;
    }
    if (skinsDuplicate) {
      addToast('Two identical skins games — change the basis, payout or stake of one, or remove it', 'error');
      return;
    }
    for (const inst of checkedInstances) {
      if (inst.team_settings) {
        const ts = inst.team_settings;
        const size = Number(ts.team_size);
        const allowance = (ts.allowance || []).map(Number);
        if (ts.basis === 'net' && (allowance.length !== size || allowance.some(v => Number.isNaN(v) || v < 0 || v > 100))) {
          addToast(`Enter ${size} allowance percentages (0–100) for the scramble, lowest handicap first`, 'error');
          return;
        }
        const pp = Number(ts.places_paid ?? 3);
        if (!(Number.isInteger(pp) && pp >= 1 && pp <= 20)) {
          addToast('Places paid must be between 1 and 20', 'error');
          return;
        }
        continue; // results only — no wager
      }
      if (inst.stroke_settings) {
        const ss = inst.stroke_settings;
        const bet = parseFloat(inst.bet_amount);
        const mode = ss.payout_mode || 'external';
        const gname = isBestBallGameType(inst.game_type) ? teamGameName(inst.game_type) : 'stroke play';
        if (mode === 'external') {
          // placings only — no buy-in
        } else if (inst.bet_amount === '' || inst.bet_amount == null || isNaN(bet) || bet < 0) {
          addToast(`Enter a buy-in for the ${gname} game, or choose Handled outside TTO`, 'error');
          return;
        }
        if (bet > 0 && mode === 'places' && !(SEGMENT_CHOICES.find(c => c.key === ss.segments) || { parts: [] }).parts.length) {
          const pl = (ss.places || []).map(Number);
          if (pl.length < 1 || pl.some(v => Number.isNaN(v) || v < 0 || v > 100) || Math.round(pl.reduce((a, b) => a + b, 0) * 100) !== 10000) {
            addToast(`${gname === 'stroke play' ? 'Stroke play' : gname} paid places must be percentages that add up to 100`, 'error');
            return;
          }
        }
        if (bet > 0 && ss.competition === 'both') {
          const gb = ss.gross_buy_in === '' || ss.gross_buy_in == null ? 0 : Number(ss.gross_buy_in);
          if (Number.isNaN(gb) || gb < 0 || gb > bet) { addToast('The gross share of the buy-in must be between $0 and the buy-in', 'error'); return; }
        }
        // Front / Back / Overall (Sept 28, 2026): the parts add up to the buy-in; each part's places to 100.
        const segChoice = SEGMENT_CHOICES.find(c => c.key === ss.segments && ss.competition !== 'both');
        if (segChoice && segChoice.parts.length && bet > 0 && mode !== 'external') {
          const cents = segmentSplitCents(bet, segChoice.parts, ss.segment_buy_in);
          if (Object.values(cents).reduce((a, b) => a + b, 0) !== Math.round(bet * 100)) { setBbNotice({ title: 'Check the buy-in split', text: `The ${segChoice.parts.map(p => SEGMENT_PART_LABEL[p].toLowerCase()).join(', ')} amounts must add up to the $${bet.toFixed(2)} buy-in.` }); return; }
          if (mode === 'places') {
            for (const p of segChoice.parts) {
              const pl = (ss.segment_places && Array.isArray(ss.segment_places[p]) && ss.segment_places[p].length ? ss.segment_places[p] : (ss.places || [100])).map(Number);
              if (pl.some(v => Number.isNaN(v) || v < 0 || v > 100) || Math.round(pl.reduce((a, b) => a + b, 0) * 100) !== 10000) { setBbNotice({ title: 'Check the paid places', text: `${SEGMENT_PART_LABEL[p]}: the paid-place percentages must add up to 100.` }); return; }
            }
          }
        }
        const apDefault = isBestBallGameType(inst.game_type) ? bestBallFormatUi(ss.format, inst.game_type).usga : STROKE_PLAY_USGA_PCT;
        const ap = ss.allowance_pct === '' || ss.allowance_pct == null ? apDefault : Number(ss.allowance_pct);
        if (ss.competition !== 'gross' && (Number.isNaN(ap) || ap < 0 || ap > 100)) { addToast('The handicap allowance must be a percentage from 0 to 100', 'error'); return; }
        continue;
      }
      const val = parseFloat(inst.bet_amount);
      // Allow exactly 0 (results-only game). Reject blank / non-numeric / negative.
      if (inst.bet_amount === '' || inst.bet_amount == null || isNaN(val) || val < 0) {
        const cat = catalog.find(c => c.game_type === inst.game_type);
        addToast(`Enter a wager for ${cat?.display_name || inst.game_type} — use 0 for a results-only game`, 'error');
        return;
      }
    }
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const games = checkedInstances.map(inst => inst.team_settings
        ? {
            game_type: inst.game_type,
            bet_amount: 0,
            settings: {
              team_size: Number(inst.team_settings.team_size),
              basis: inst.team_settings.basis === 'net' ? 'net' : 'gross',
              allowance: (inst.team_settings.allowance || []).map(Number),
              stroke_index_tee: inst.team_settings.stroke_index_tee || 'low_player',
              team_overrides: inst.team_settings.team_overrides || {},
              tie_break: inst.team_settings.tie_break || 'usga',
              places_paid: Number(inst.team_settings.places_paid ?? 3),
              tie_order: (inst.team_settings.tie_order || []).map(Number).filter(n => Number.isInteger(n) && n > 0),
            },
          }
        : inst.stroke_settings
        ? (() => {
            const ss = inst.stroke_settings;
            const mode = ss.payout_mode || 'external';
            const bet = mode === 'external' ? 0 : (parseFloat(inst.bet_amount) || 0);
            const bb = isBestBallGameType(inst.game_type);
            return {
              game_type: inst.game_type,
              bet_amount: bet,
              settings: {
                ...(bb ? { format: bestBallFormatUi(ss.format, inst.game_type).key, blind_shares: ss.blind_shares !== false } : {}),
                ...(inst.game_type === 'shamble' ? { rules_note: String(ss.rules_note || '').trim() } : {}),
                competition: ['net', 'gross', 'both'].includes(ss.competition) ? ss.competition : 'net',
                allowance_pct: ss.allowance_pct === '' || ss.allowance_pct == null ? (bb ? bestBallFormatUi(ss.format, inst.game_type).usga : STROKE_PLAY_USGA_PCT) : Number(ss.allowance_pct),
                payout_mode: mode,
                places: (ss.places || []).map(Number),
                gross_buy_in: ss.gross_buy_in === '' || ss.gross_buy_in == null ? 0 : Number(ss.gross_buy_in),
                money_ties: ss.money_ties === 'countback' ? 'countback' : 'split',
                tie_break: ss.tie_break || 'usga',
                places_paid: Number(ss.places_paid ?? 3),
                // Stroke play orders PLAYERS (ids); Best Ball orders TEAMS (numbers).
                tie_order_net: (ss.tie_order_net || []).filter(v => (bb ? Number.isInteger(v) && v > 0 : typeof v === 'string')),
                tie_order_gross: (ss.tie_order_gross || []).filter(v => (bb ? Number.isInteger(v) && v > 0 : typeof v === 'string')),
                // Front / Back / Overall (Sept 28, 2026).
                ...(() => {
                  const choice = SEGMENT_CHOICES.find(c => c.key === ss.segments && ss.competition !== 'both');
                  if (!choice || !choice.parts.length) return { segments: 'overall' };
                  const betNow = mode === 'external' ? 0 : (parseFloat(inst.bet_amount) || 0);
                  const cents = betNow > 0 ? segmentSplitCents(betNow, choice.parts, ss.segment_buy_in) : null;
                  return {
                    segments: choice.key,
                    segment_buy_in: cents ? Object.fromEntries(choice.parts.map(p => [p, cents[p] / 100])) : null,
                    segment_places: mode === 'places' ? Object.fromEntries(choice.parts.map(p => [p, ((ss.segment_places && Array.isArray(ss.segment_places[p]) && ss.segment_places[p].length) ? ss.segment_places[p] : (ss.places && ss.places.length ? ss.places : [100])).map(Number)])) : null,
                    ...Object.fromEntries(['tie_order_front_net', 'tie_order_front_gross', 'tie_order_back_net', 'tie_order_back_gross'].map(fl => [fl, (ss[fl] || []).filter(v => (bb ? Number.isInteger(v) && v > 0 : typeof v === 'string'))])),
                  };
                })(),
              },
            };
          })()
        : {
            game_type: inst.game_type,
            bet_amount: parseFloat(inst.bet_amount),
            settings: inst.game_type === 'stableford'
              ? (() => { const ss = inst.stableford_settings || {}; const pm = ['per_point_field', 'per_point_pairs'].includes(ss.payout_mode) ? ss.payout_mode : 'pot'; return { competition: ss.competition === 'gross' ? 'gross' : 'net', allowance_pct: ss.allowance_pct === '' || ss.allowance_pct == null ? 95 : Number(ss.allowance_pct), payout_mode: pm, places: pm === 'pot' ? (Array.isArray(ss.places) && ss.places.length ? ss.places.map(Number) : [100]) : [], money_ties: 'split', tie_break: 'usga', team_mode: 'individual', mode: ss.mode === 'field' ? 'field' : 'group', segments: pm === 'pot' && SEGMENT_CHOICES.some(c => c.key === ss.segments) ? ss.segments : 'overall', segment_buy_in: pm === 'pot' && ss.segments && ss.segments !== 'overall' && ss.segment_buy_in && Object.values(ss.segment_buy_in).some(v => v !== '' && v != null) ? ss.segment_buy_in : null }; })()
              : inst.game_type === 'sixes'
              ? { handicap_basis: (inst.sixes_settings && inst.sixes_settings.handicap_basis) || 'low_man', press: inst.sixes_settings && ['auto', 'ask'].includes(inst.sixes_settings.press) ? inst.sixes_settings.press : 'none' }
              : inst.game_type === 'match_play'
              ? { mode: inst.match_play_settings && inst.match_play_settings.mode === 'teams' ? 'teams' : 'pairs', handicap_basis: (inst.match_play_settings && ['gross', 'full'].includes(inst.match_play_settings.handicap_basis)) ? inst.match_play_settings.handicap_basis : 'usga' }
              : inst.game_type === 'nassau'
              ? { mode: inst.nassau_settings && inst.nassau_settings.mode === 'teams' ? 'teams' : 'pairs', handicap_basis: (inst.nassau_settings && inst.nassau_settings.handicap_basis) || 'low_man', press: (inst.nassau_settings && inst.nassau_settings.press) || 'ask' }
              : inst.game_type === 'dots'
              ? { count: (inst.dots_settings && inst.dots_settings.count) || { birdies: true, sandies: true, polies: true }, mode: dotsModeOf(inst.dots_settings) }
              : inst.game_type === 'ctp'
              ? { must_make_par: !!(inst.ctp_settings && inst.ctp_settings.must_make_par), holes: inst.ctp_settings && Array.isArray(inst.ctp_settings.holes) && inst.ctp_settings.holes.length ? inst.ctp_settings.holes : null, funding: inst.ctp_settings && inst.ctp_settings.funding === 'per_player' ? 'per_player' : 'per_hole' }
              : {
                physical_pot: inst.physical_pot === true,
                // Net skins (Sept 29, 2026): how strokes are given.
                ...(isSkinsType(inst.game_type) && !inst.game_type.endsWith('_gross') ? { handicap_basis: inst.skins_hcp === 'full' ? 'full' : 'low_man' } : {}),
              },
          });
      // Oct 28, 2026: "Everyone in the field plays" rides in each game's settings.
      games.forEach((g, i) => { const it = checkedInstances[i]; if (it && it.everyone && everyoneGameType(g.game_type)) g.settings = { ...(g.settings || {}), everyone: true }; });
      const res = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/games`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ games }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        addToast(data.scoring_mode_set === 'team' ? 'Games saved — this round is now scored one score per team'
          : data.scoring_mode_set === 'individual' ? 'Games saved — this round is back to individual scoring'
          : 'Games saved successfully', 'success');
        if (data.scoring_mode_hint) addToast(data.scoring_mode_hint, 'info');
        // Shamble (Sept 28, 2026): the save turned the round's GHIN posting off (or back
        // on when the shamble was removed) — say so in a modal; organizers miss toasts.
        if (data.ghin_posting_set === false) setBbNotice({ title: 'GHIN posting is off for this round', text: "A shamble score isn't acceptable for handicap purposes, so players won't see Post to GHIN or get the posting reminder for this round. If you want it on anyway, change GHIN posting eligible under Edit → Set up event." });
        else if (data.ghin_posting_set === true) setBbNotice({ title: 'GHIN posting is back on', text: 'The shamble is gone from this round, so players can post their scores to GHIN again.' });
        // Oct 5, 2026: players in a game with strokes who have no handicap index — warned, not refused.
        if (Array.isArray(data.handicap_warnings) && data.handicap_warnings.length) {
          const names = data.handicap_warnings;
          const msg = `${names.join(', ')} ${names.length === 1 ? 'has' : 'have'} no handicap index. Games with strokes won\u2019t count ${names.length === 1 ? 'him' : 'them'} properly until one is added (Sixes holds the foursome). Add it on the Players page, or ask the player to add it in his Profile.`;
          if (data.ghin_posting_set === true || data.ghin_posting_set === false) addToast(msg, 'info');
          else setBbNotice({ title: 'No handicap on file', text: msg });
        }
        onSaved?.();
        await loadAll();
      } else {
        const err = await res.json();
        addToast(err.error || 'Failed to save games', 'error');
      }
    } catch (err) {
      addToast('Failed to save games', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizeRound = async (isReFinalize = false) => {
    if (isReFinalize && !window.confirm('Re-calculate and overwrite existing settlements?')) return;
    if (isEvent) {   // slice 3: must-fix items ask first
      const stops = await readyStops({ teetimeId: teeTime.id });
      if (stops.length && !window.confirm(mustFixMessage(stops, 'finalize'))) return;
    }
    setFinalizing(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/finalize`, {
        method: 'POST', credentials: 'include', headers,
      });
      const data = await res.json();
      if (res.ok) {
        addToast(`Round finalized! ${data.settlements_saved} settlement(s) saved.`, 'success');
        setIncompleteFinalize(null);
        onSaved?.();
        await loadAll();
      } else if (res.status === 409 && data.error === 'round_incomplete') {
        // WS-4 completeness gate: a participant has a partial card. Surface the
        // named list so the organizer can wait for them or mark them DNF.
        setIncompleteFinalize({
          expectedHoles: data.expected_holes,
          holeSet: data.hole_set,
          incomplete: data.incomplete || [],
        });
      } else {
        addToast(data.error || 'Failed to finalize', 'error');
      }
    } catch (e) {
      addToast('Failed to finalize round', 'error');
    } finally {
      setFinalizing(false);
    }
  };

  const handleMarkDnf = async (playerId, playerName) => {
    if (!window.confirm(`Mark ${playerName} DNF and remove them from this round's games?\n\nThey won't be in the settlement. Removing a player can change the other players' skins — a hole he tied may now be won, and a net game played off the low man figures strokes off the lowest handicap left in it. To undo, re-add them from the game roster.`)) return;
    setDnfBusyId(playerId);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/dnf-player`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ player_id: playerId }),
      });
      const data = await res.json();
      if (!res.ok) { addToast(data.error || 'Failed to mark DNF', 'error'); return; }
      addToast(`${playerName} marked DNF and removed from the games.`, 'success');
      await loadAll();
      // Re-attempt finalize: if other players are still short this refreshes the
      // list; if this cleared the last one, the round finalizes.
      await handleFinalizeRound(false);
    } catch (e) {
      addToast('Failed to mark DNF', 'error');
    } finally {
      setDnfBusyId(null);
    }
  };

  const handleReopenRound = async () => {
    if (!window.confirm('Reopen this round?\n\nThis will delete all saved settlements and return the round to active status. You will need to re-finalize to generate new settlements.\n\nContinue?')) return;
    setFinalizing(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/reopen`, {
        method: 'POST', credentials: 'include', headers,
      });
      const data = await res.json();
      if (res.ok) {
        addToast(`Round reopened. ${data.settlements_deleted} settlement(s) cleared.`, 'success');
        onSaved?.();
        await loadAll();
      } else {
        addToast(data.error || 'Failed to reopen round', 'error');
      }
    } catch (e) {
      addToast('Failed to reopen round', 'error');
    } finally {
      setFinalizing(false);
    }
  };

  const getGameReadiness = (existingGame) => {
    if (!existingGame) return null;
    if (existingGame.game_type === 'scramble') return null;   // teams, not opt-ins — no badge
    // Oct 28, 2026: CTP on a Captain's Choice round — every player on a team is in (ctpService, Oct 22).
    if (existingGame.game_type === 'ctp' && instances.some(i => i.checked && i.game_type === 'scramble')) return null;
    const participants = existingGame.participant_ids || [];
    if (participants.length === 0) return { ready: false, reason: isBestBallGameType(existingGame.game_type) ? 'No teams — save the pairings' : 'No players opted in' };
    if (standings) {
      const gs = standings.games?.find(g => g.game_id === existingGame.id);
      if (gs?.results) {
        if (gs.results.is_complete) return { ready: true };
        const incomplete = (gs.results.totals || []).filter(t => (t.holes_entered || 0) < 18);
        if (incomplete.length > 0) return { ready: false, reason: `${incomplete.length} incomplete` };
      }
    }
    return { ready: participants.length > 0, reason: null };
  };

  const checkedCount = instances.filter(i => i.checked).length;
  const isPotGame = (gt) => gt === 'skins_pot' || gt === 'skins_pot_gross';

  // ── Skins family card (Sept 15, 2026) ──────────────────────────────────────
  const skinsCatalog = catalog.filter(g => isSkinsType(g.game_type));
  const skinsAvailable = new Set(skinsCatalog.map(g => g.game_type));
  const skinsInstances = instances.filter(i => isSkinsType(i.game_type));
  const checkedSkins = skinsInstances.filter(i => i.checked);
  const stakeOf = (i) => String(parseFloat(i.bet_amount) || 0);
  const skinsDuplicate = checkedSkins.some((a, i) => checkedSkins.some((b, j) => j > i && a.game_type === b.game_type && stakeOf(a) === stakeOf(b)));
  const defaultSkinsKey = () => (skinsAvailable.has('skins_carryover') ? 'skins_carryover' : (skinsCatalog[0]?.game_type || 'skins_carryover'));
  const newSkinsInstance = (game_type) => ({ instanceId: newInstanceId(), game_type, checked: true, bet_amount: '', physical_pot: false, skins_hcp: 'low_man', existingId: null, status: null, team_settings: null });

  // The card's checkbox: off keeps ONE unchecked placeholder so it can be re-ticked;
  // an existing row left unchecked is removed on save, as any game is today.
  const handleSkinsFamilyToggle = () => {
    if (checkedSkins.length > 0) {
      setInstances(prev => {
        const first = prev.find(i => isSkinsType(i.game_type));
        return prev.filter(i => !isSkinsType(i.game_type) || i === first).map(i => (i === first ? { ...i, checked: false } : i));
      });
    } else if (skinsInstances[0]) {
      handleToggle(skinsInstances[0].instanceId);
    } else {
      setInstances(prev => [...prev, newSkinsInstance(defaultSkinsKey())]);
    }
  };
  const handleSkinsVariant = (instanceId, patch) => {
    setInstances(prev => prev.map(i => {
      if (i.instanceId !== instanceId) return i;
      const next = { ...skinsParts(i.game_type), ...patch };
      return { ...i, game_type: SKINS_KEY[next.basis][next.payout], physical_pot: next.payout === 'pot' ? i.physical_pot : false };
    }));
  };
  // Net skins strokes (Sept 29, 2026): 'low_man' (off the lowest handicap) or 'full'.
  const handleSkinsHcp = (instanceId, v) => {
    setInstances(prev => prev.map(i => (i.instanceId === instanceId ? { ...i, skins_hcp: v === 'full' ? 'full' : 'low_man' } : i)));
  };
  // Second game defaults to the OTHER basis, so gross-and-net is one click.
  const handleAddSkins = () => {
    const first = checkedSkins[0];
    const cur = first ? skinsParts(first.game_type) : { basis: 'net', payout: 'carryover' };
    let key = SKINS_KEY[cur.basis === 'net' ? 'gross' : 'net'][cur.payout === 'pot' ? 'pot' : 'carryover'];
    if (!skinsAvailable.has(key)) key = skinsCatalog.find(g => g.game_type !== first?.game_type)?.game_type || defaultSkinsKey();
    setInstances(prev => [...prev, newSkinsInstance(key)]);
  };
  const skinsSummary = (inst) => {
    const { basis, payout } = skinsParts(inst.game_type);
    const b = basis === 'net' ? 'Net' : 'Gross';
    const hc = basis === 'net' ? (inst.skins_hcp === 'full' ? 'everyone gets his full course handicap' : 'strokes off the low handicap') : 'no handicaps';
    const amt = parseFloat(inst.bet_amount);
    const money = amt > 0 ? `$${amt % 1 === 0 ? amt.toFixed(0) : amt.toFixed(2)}` : null;
    if (payout === 'pot') return `${b} skins · ${money ? `${money} buy-in per player` : 'no buy-in'} · pot split by skins won · ${hc}.`;
    if (payout === 'carryover') return `${b} skins · ${money ? `${money} a hole` : 'no wager'} · tied holes carry over, next winner collects · ${hc}.`;
    return `${b} skins · ${money ? `${money} a hole` : 'no wager'} · tied holes are voided · ${hc}.`;
  };
  const segBtn = (on, disabled) => ({
    padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    border: `1px solid ${on ? '#166534' : '#d1d5db'}`, background: on ? '#166534' : '#fff', color: on ? '#fff' : (disabled ? '#9ca3af' : '#374151'),
  });
  const moneyBox = { display: 'inline-flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' };
  const moneySign = { padding: '0 4px 0 8px', color: '#6b7280', fontSize: 13 };
  const moneyInput = { width: 70, padding: '6px 8px 6px 0', border: 'none', fontSize: 14, outline: 'none', background: 'transparent' };

  // Per-instance opt-in list (shared by every individual game card).
  const renderOptIns = (existingGame, inst = null) => {
    const participants = existingGame?.participant_ids || [];
    const players = Array.isArray(teeTime.players) ? teeTime.players : [];
    // Oct 28, 2026 (Dennis): "Everyone in the field plays" — unticked by default. While on,
    // the game's players are everyone confirmed on the tee time (late adds in, cancellations
    // out) until the first score freezes the list; then the organizer's In / Out works again.
    const canEveryone = !!(inst && everyoneGameType(inst.game_type));
    const savedEveryone = !!(existingGame && existingGame.settings && existingGame.settings.everyone === true);
    const frozen = savedEveryone && existingGame.settings.everyone_locked === true;
    const everyoneBox = canEveryone ? (
      <label data-testid={`everyone-${inst.game_type}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!inst.everyone}
          onChange={() => setInstances(prev => prev.map(i => i.instanceId === inst.instanceId ? { ...i, everyone: !i.everyone } : i))}
          style={{ width: 16, height: 16, marginTop: 2, accentColor: '#166534', flexShrink: 0 }} />
        <span><strong>Everyone in the field plays</strong><span style={{ color: '#6b7280' }}> — every player confirmed on the tee time is in, including anyone added later. No opt-ins. The list freezes at the first score.</span></span>
      </label>
    ) : null;
    if (canEveryone && inst.everyone && !frozen) {
      return (
        <div style={{ marginTop: 14, borderTop: '1px solid #d1fae5', paddingTop: 2 }}>
          {everyoneBox}
          <div data-testid="everyone-note" style={{ marginTop: 8, fontSize: 13, color: '#166534', fontWeight: 600 }}>
            {savedEveryone ? `👥 Everyone plays — ${participants.length} player${participants.length === 1 ? '' : 's'} confirmed right now.` : '👥 Everyone plays — save the games to put the field in.'}
          </div>
        </div>
      );
    }
    if (players.length === 0) return everyoneBox;
    return (
      <div style={{ marginTop: 14, borderTop: '1px solid #d1fae5', paddingTop: 12 }}>
        {everyoneBox && <div style={{ marginTop: -14, marginBottom: 10 }}>{everyoneBox}</div>}
        {frozen && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Everyone plays — the list froze at the first score. Take a player out (did not finish) or put one in here.</div>}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
          Player Opt-Ins {existingGame ? `(${participants.length}/${players.length})` : '— save games first'}
        </div>
        {players.map(player => {
          const optedIn = participants.includes(player.id);
          const key = `${existingGame?.id}-${player.id}`;
          const toggling = togglingOptIn[key];
          return (
            <div key={player.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontSize: 13, color: '#1f2937' }}>{player.name}</span>
              {existingGame ? (
                <button onClick={() => handleOrganizerOptIn(existingGame, player.id, optedIn)} disabled={toggling}
                  style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6, cursor: toggling ? 'wait' : 'pointer', border: 'none',
                    background: optedIn ? '#dcfce7' : '#f3f4f6', color: optedIn ? '#166534' : '#6b7280' }}>
                  {toggling ? '...' : optedIn ? '✓ In' : '— Out'}
                </button>
              ) : (
                <span style={{ fontSize: 12, color: '#9ca3af' }}>— Out</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Best Ball teams (Sept 23, 2026): in place of the opt-in list. Each saved pairing
  // group is a team; the organizer's In/Out works as on any game (Out = a DNF, which
  // leaves the team short). Team data comes from the standings (team_info).
  const renderBestBallTeams = (existingGame) => {
    const gs = existingGame ? standings?.games?.find(g => g.game_id === existingGame.id) : null;
    const info = gs?.team_info || null;
    const participants = existingGame?.participant_ids || [];
    const head = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 };
    const note = { fontSize: 12, color: '#6b7280', lineHeight: 1.45 };
    const warn = { fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 9px', lineHeight: 1.45 };
    return (
      <div style={{ marginTop: 14, borderTop: '1px solid #d1fae5', paddingTop: 12 }} data-testid="best-ball-teams">
        <div style={head}>Teams — from Pairings{info && info.teams.length ? ` (${info.teams.length})` : ''}</div>
        {!existingGame && <div style={note}>Save to see the teams. Each group on the Pairings page is a team — pair a 2-man best ball as twosomes, a 3-man as threesomes and a 4-man as foursomes.</div>}
        {existingGame && !info && <div style={note}>Save again to load the teams from your pairings.</div>}
        {info && info.teams.length === 0 && <div style={warn}>No pairings are saved for this round yet. Set the groups on the Pairings page and save them — each group is a team — then Save here.</div>}
        {/* Oct 17, 2026 (Dennis): every group the same size but the format is for another
            size — e.g. two threesomes on a 4-man format, which drew a blind for each. */}
        {info && info.teams.length > 0 && (() => {
          const sizes = [...new Set(info.teams.map(t => t.members.length))];
          const n = sizes.length === 1 ? sizes[0] : null;
          if (!n || n < 2 || n > 4 || n === info.team_size) return null;
          const groupsWord = { 2: 'twosomes', 3: 'threesomes', 4: 'foursomes' }[n];
          const fix = existingGame?.game_type === 'shamble' && n === 3
            ? 'Shamble teams are 2 or 4 players. Pair the field as twosomes or foursomes, or play Best Ball with a 3-man format.'
            : n === 2 ? 'Change the Format to 2-man best ball.' : `Change the Format to a ${n}-man one.`;
          return <div style={{ ...warn, marginBottom: 8 }} data-testid="best-ball-size-mismatch">⚠ Your groups are {groupsWord}, but this format is for {info.team_size}-man teams. {fix}</div>;
        })()}
        {info && info.teams.map(t => {
          const inCount = t.members.filter(m => participants.includes(m.player_id)).length;
          const size = info.team_size;
          // The blind draw (Sept 26, 2026): a 4-man team one player short gets a blind,
          // drawn by TTO when the round starts (or the moment a player is taken Out
          // mid-round). The organizer sees who; players don't until the round is final.
          const blindable = info.blind_draw && size === 4 && inCount === size - 1;
          const blinds = (t.blinds || []).filter(b => b.active);
          const flag = inCount < Math.max(2, info.best_of) ? 'Not ranked — short a player'
            : inCount > size ? `Not ranked — ${inCount} players on a ${size}-man team`
            : blindable && blinds.length ? null
            : blindable ? 'Short a player — a blind will be drawn when the round starts'
            : inCount < size ? 'Short a player' : null;
          const drawnAt = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch (_) { return ''; } };
          return (
            <div key={t.team_number} style={{ border: `1px solid ${flag ? '#fcd34d' : '#e5e7eb'}`, background: flag ? '#fffbeb' : '#fff', borderRadius: 8, padding: '6px 10px', marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>Team {t.team_number}{t.group_label || ''} <span style={{ fontWeight: 400, color: '#6b7280' }}>· {t.name}</span></span>
                {flag && <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>⚠ {flag}</span>}
              </div>
              {t.members.map(m => {
                const optedIn = participants.includes(m.player_id);
                const key = `${existingGame?.id}-${m.player_id}`;
                const toggling = togglingOptIn[key];
                return (
                  <div key={m.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={{ fontSize: 13, color: optedIn ? '#1f2937' : '#9ca3af' }}>{m.name}</span>
                    <button onClick={() => handleOrganizerOptIn(existingGame, m.player_id, optedIn)} disabled={toggling}
                      style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6, cursor: toggling ? 'wait' : 'pointer', border: 'none',
                        background: optedIn ? '#dcfce7' : '#f3f4f6', color: optedIn ? '#166534' : '#6b7280' }}>
                      {toggling ? '...' : optedIn ? '✓ In' : '— Out'}
                    </button>
                  </div>
                );
              })}
              {blindable && blinds.map((b, i) => (
                <div key={`blind-${i}`} data-testid="best-ball-blind" style={{ fontSize: 12, color: '#1e3a5f', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 8px', marginTop: 4, lineHeight: 1.4 }}>
                  <strong>+ blind: {b.player_name || 'drawn'}</strong>{(() => { const bits = [b.band ? `${b.band} band` : null, b.drawn_at ? `drawn ${drawnAt(b.drawn_at)}` : null].filter(Boolean); return bits.length ? ` (${bits.join(', ')})` : ''; })()}
                  {b.replaces_name && i === 0 && <span style={{ color: '#6b7280' }}> · for {b.replaces_name}{(t.members.find(m => m.player_id === b.replaces_id) || {}).scored ? ' — his scored holes stand' : ''}</span>}
                  {i === blinds.length - 1 && <div style={{ fontSize: 11, color: '#6b7280' }}>Only you and the event desk can see who. Players see "+ Blind Draw" until the round is final.{!b.replaces_id ? '' : (t.members.find(m => m.player_id === b.replaces_id) || {}).scored
                    ? ` Took ${b.replaces_name || 'him'} out by mistake? Putting him back In removes the blind.`
                    : ` Putting ${b.replaces_name || 'the missing player'} back In removes the blind — until his team has entered a score.`}</div>}
                </div>
              ))}
            </div>
          );
        })}
        {info && info.not_on_team && info.not_on_team.length > 0 && (
          <div style={{ ...warn, marginTop: 6 }}>Not on a team, so not counted: {info.not_on_team.map(p => p.name).join(', ')}. Put them in a group on the Pairings page and save it.</div>
        )}
        {info && info.teams.length > 0 && (
          <div style={{ ...note, fontSize: 11, marginTop: 6 }}>{info.blind_draw
            ? 'Taking a player out mid-round (a DNF) draws a blind for his team: his scored holes stand and the blind covers the rest. Everyone holes out. Changed the pairings? Save here again before the first score is entered and the teams refresh.'
            : 'Taking a player out (a DNF) leaves his team short. Changed the pairings? Save here again before the first score is entered and the teams refresh.'}</div>
        )}
      </div>
    );
  };

  const renderSkinsCard = () => {
    const on = checkedSkins.length > 0;
    return (
      <div key="skins" style={{ marginBottom: 12 }}>
        <div style={{ border: on ? '2px solid #166534' : '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: on ? '#f0fdf4' : '#fff', transition: 'all 0.15s' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={on} onChange={handleSkinsFamilyToggle}
              style={{ width: 18, height: 18, marginTop: 2, cursor: 'pointer', accentColor: '#166534' }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>Skins</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Lowest score on a hole wins the skin. Pick the basis and how the money pays out. Want gross and net side by side? Create an additional skins game below.{' '}<a href="/guides/how-to-set-up-a-skins-game?from=app" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#15803d', fontWeight: 600 }}>📖 Skins guide ↗</a></div>
            </div>
          </label>

          {on && checkedSkins.map((inst, idx) => {
            const existingGame = existingGames.find(g => g.id === inst.existingId);
            const readiness = getGameReadiness(existingGame);
            const { basis, payout } = skinsParts(inst.game_type);
            const cat = catalog.find(c => c.game_type === inst.game_type);
            const pot = payout === 'pot';
            const rowStyle = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' };
            const rowLabel = { width: 60, fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' };
            return (
              <div key={inst.instanceId} style={{ marginTop: 12, background: '#fff', border: '1px solid #d1fae5', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Skins game {idx + 1}</span>
                  {readiness && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                      background: readiness.ready ? '#dcfce7' : '#fef3c7',
                      color: readiness.ready ? '#166534' : '#92400e' }}>
                      {readiness.ready ? '✓ Ready' : `⚠ ${readiness.reason}`}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    {cat && (
                      <button onClick={() => setRulesGame(cat)}
                        style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        ? Rules
                      </button>
                    )}
                    {idx > 0 && (
                      <button onClick={() => handleRemoveInstance(inst.instanceId)}
                        style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', color: '#dc2626', fontWeight: 600 }}>
                        Remove
                      </button>
                    )}
                  </span>
                </div>
                <div style={rowStyle}>
                  <span style={rowLabel}>Basis</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[['gross', 'Gross'], ['net', 'Net (handicap)']].map(([v, l]) => {
                      const ok = skinsAvailable.has(SKINS_KEY[v][payout]);
                      return (
                        <button key={v} disabled={!ok} title={ok ? undefined : 'Not enabled for your organization'}
                          onClick={() => handleSkinsVariant(inst.instanceId, { basis: v })} style={segBtn(basis === v, !ok)}>{l}</button>
                      );
                    })}
                  </div>
                </div>
                <div style={rowStyle}>
                  <span style={rowLabel}>Payout</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[['carryover', 'Carryover'], ['nocarry', 'No carryover'], ['pot', 'Pot']].map(([v, l]) => {
                      const ok = skinsAvailable.has(SKINS_KEY[basis][v]);
                      return (
                        <button key={v} disabled={!ok} title={ok ? undefined : 'Not enabled for your organization'}
                          onClick={() => handleSkinsVariant(inst.instanceId, { payout: v })} style={segBtn(payout === v, !ok)}>{l}</button>
                      );
                    })}
                  </div>
                </div>
                {basis === 'net' && (
                  <div style={rowStyle}>
                    <span style={rowLabel}>Strokes</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {[['low_man', 'Off the low man'], ['full', 'Full course handicap']].map(([v, l]) => (
                        <button key={v} onClick={() => handleSkinsHcp(inst.instanceId, v)} style={segBtn((inst.skins_hcp || 'low_man') === v, false)}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div style={rowStyle}>
                  <span style={rowLabel}>{pot ? 'Buy-in' : 'Wager'}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={moneyBox}>
                      <span style={moneySign}>$</span>
                      <input type="number" min="0" step="0.25" value={inst.bet_amount}
                        onChange={(e) => handleWager(inst.instanceId, e.target.value)}
                        placeholder="0.00" style={moneyInput} />
                    </span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{pot ? 'per player, into the pot' : 'per hole'}</span>
                  </div>
                </div>
                {parseFloat(inst.bet_amount) === 0 && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#166534', background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: 6, padding: '5px 9px' }}>
                    Results only — TTO tracks skins won; no money is settled.
                  </div>
                )}
                {pot && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <input type="checkbox" checked={inst.physical_pot === true} onChange={() => handlePhysicalPot(inst.instanceId)}
                      style={{ width: 16, height: 16, marginTop: 2, cursor: 'pointer', accentColor: '#166534' }} />
                    <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.4 }}>
                      <span style={{ fontWeight: 600 }}>Physical pot settlement</span>
                      <span style={{ color: '#6b7280' }}> — players collect gross winnings from a shared pot. Uncheck for net-only settlement.</span>
                    </span>
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 12, color: '#166534', background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: 6, padding: '5px 9px' }}>
                  {skinsSummary(inst)}
                </div>
                {renderOptIns(existingGame, inst)}
              </div>
            );
          })}

          {on && skinsDuplicate && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px' }}>
              Two identical skins games — change the basis, payout or stake of one, or remove it.
            </div>
          )}
          {on && checkedSkins.length < 2 && (
            <button onClick={handleAddSkins}
              style={{ width: '100%', marginTop: 10, padding: '8px', borderRadius: 8, border: '2px dashed #d1d5db', background: '#f9fafb', color: '#6b7280', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              + Create an additional skins game
            </button>
          )}
          {on && checkedSkins.length >= 2 && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>Two skins games is the limit. They have to differ in basis, payout or stake.</div>
          )}
        </div>
      </div>
    );
  };

  // Cards in catalog order: every skins entry collapses into ONE card at the
  // position of the first skins entry; other games render exactly as before.
  const setupCards = [];
  for (const g of catalog) {
    if (isSkinsType(g.game_type)) {
      if (!setupCards.some(c => c.family === 'skins')) setupCards.push({ family: 'skins' });
      continue;
    }
    setupCards.push({ family: null, game: g });
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>🎮 Games Setup</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>{teeTime.course} · {new Date(teeTime.date.split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</div>
          {isFinalized && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#166534', background: '#dcfce7', borderRadius: 6, padding: '3px 10px', display: 'inline-block', fontWeight: 600 }}>
              ✅ Round Finalized{finalizedAt ? ` · ${formatFinalizedAt(finalizedAt)}` : ''}
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>Loading games...</div>

        ) : isFinalized ? (
          /* ── RESULTS PANEL ── */
          <div>
            {/* Team game (scramble) results — results only, so they sit above the money statement */}
            {(standings?.games || []).filter(g => g.competitor_scope === 'team' && g.results).map(g => (
              <div key={g.game_id} style={{ marginBottom: 14, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ background: '#1f2937', color: '#fff', padding: '8px 12px', fontWeight: 700, fontSize: 14 }}>
                  {g.display_name} · {g.results.basis === 'net' ? 'Net' : 'Gross'}
                </div>
                {g.results.ties_pending && (
                  <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', padding: '6px 12px', borderBottom: '1px solid #fcd34d' }}>
                    A tie for a paid place is still undecided — Reopen Round, put the teams in order under How ties are decided, then finalize again.
                  </div>
                )}
                {(g.results.totals || []).map(t => (
                  <div key={t.team_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 800, marginRight: 8, color: t.place != null ? '#1f2937' : '#9ca3af' }}>{t.place_label || '–'}<TieMark mark={t.tie_mark} /></span>
                      <span style={{ fontWeight: 600 }}>{t.team_name}</span>
                      {t.tie_status === 'pending' && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#b45309' }}>playoff pending</span>}
                      <span style={{ color: '#6b7280' }}> · Team {t.team_id}{t.team_handicap != null ? ` · hcp ${t.team_handicap}` : ''}{t.thru < (g.results.hole_count || 18) ? ` · thru ${t.thru}` : ''}</span>
                    </div>
                    <div style={{ fontWeight: 800, flexShrink: 0 }}>
                      {g.results.basis === 'net' ? (t.net_total ?? '—') : (t.gross_total ?? '—')}
                      <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12, marginLeft: 6 }}>{g.results.basis === 'net' ? `gross ${t.gross_total ?? '—'}` : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <SettlementStatement
              games={(standings?.games || []).filter(g => g.competitor_scope !== 'team')}
              playerSummary={buildPlayerSummary(standings ? { ...standings, games: (standings.games || []).filter(g => g.competitor_scope !== 'team') } : standings, settlements)}
              scorecard={standings?.scorecard}
              meta={{
                courseName: teeTime?.course || '',
                dateLabel: teeTime?.date ? new Date(String(teeTime.date).split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '', // noon local: a bare date parsed as UTC showed the day before (Oct 6, 2026)
                finalizedAtLabel: formatFinalizedAt(finalizedAt),
              }}
              collapsibleScorecards={true}
            />

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button onClick={() => handleFinalizeRound(true)} disabled={finalizing}
                style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', cursor: finalizing ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {finalizing ? 'Recalculating...' : '↺ Re-finalize'}
              </button>
              {onEmailSettlement && (
                <button onClick={() => setShowEmailModal(true)}
                  style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  📧 Send Emails
                </button>
              )}
              <button onClick={handleReopenRound} disabled={finalizing}
                style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fef2f2', color: '#b91c1c', cursor: finalizing ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                ↶ Reopen Round
              </button>
            </div>
            <button onClick={onClose}
              style={{ width: '100%', padding: '9px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
              Close
            </button>
          </div>

        ) : (
          /* ── SETUP PANEL ── */
          <>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Select the games for today's round and set the wager amount.
            </div>

            {setupCards.map(card => {
              if (card.family === 'skins') return renderSkinsCard();
              const game = card.game;
              // Get all instances for this game type
              const gameInstances = instances.filter(i => i.game_type === game.game_type);
              const checkedInstances = gameInstances.filter(i => i.checked);

              return (
                <div key={game.game_type} style={{ marginBottom: 12 }}>
                  {gameInstances.map((inst, instIdx) => {
                    const existingGame = existingGames.find(g => g.id === inst.existingId);
                    const readiness = inst.checked ? getGameReadiness(existingGame) : null;
                    const isExtra = instIdx > 0; // second or third instance of same type

                    return (
                      <div key={inst.instanceId} style={{
                        border: inst.checked ? '2px solid #166534' : '1px solid #e5e7eb',
                        borderRadius: 10, padding: 16,
                        marginBottom: isExtra ? 6 : 0,
                        background: inst.checked ? '#f0fdf4' : '#fff', transition: 'all 0.15s',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', flex: 1 }}>
                            <input type="checkbox" checked={inst.checked} onChange={() => handleToggle(inst.instanceId)}
                              disabled={!!inst.team_settings && !!existingGame && (existingGame.status !== 'setup' || existingGame.settings?.locked === true)}
                              title={inst.team_settings && existingGame && (existingGame.status !== 'setup' || existingGame.settings?.locked === true) ? 'Team scores have been entered — this game can no longer be removed' : undefined}
                              style={{ width: 18, height: 18, marginTop: 2, cursor: 'pointer', accentColor: '#166534' }} />
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>
                                  {game.display_name}
                                  {isExtra && <span style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>(2nd game)</span>}
                                </span>
                                {readiness && (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                                    background: readiness.ready ? '#dcfce7' : '#fef3c7',
                                    color: readiness.ready ? '#166534' : '#92400e' }}>
                                    {readiness.ready ? '✓ Ready' : `⚠ ${readiness.reason}`}
                                  </span>
                                )}
                              </div>
                              {!isExtra && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{game.short_desc}</div>}
                            </div>
                          </label>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {isExtra && (
                              <button onClick={() => handleRemoveInstance(inst.instanceId)}
                                style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', color: '#dc2626', fontWeight: 600 }}>
                                ✕
                              </button>
                            )}
                            <button onClick={() => setRulesGame(game)}
                              style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              ? Rules
                            </button>
                          </div>
                        </div>

                        {inst.checked && inst.team_settings && (
                          <ScrambleGamePanel
                            nines={ninesForCourse(teeTime?.course)}
                            settings={inst.team_settings}
                            onChange={(patch) => handleTeamSettings(inst.instanceId, patch)}
                            teamHandicaps={existingGame ? (standings?.games?.find(g => g.game_id === existingGame.id)?.team_handicaps || null) : null}
                            teamResults={existingGame ? (standings?.games?.find(g => g.game_id === existingGame.id)?.results || null) : null}
                            savedSettings={existingGame ? (existingGame.settings || {}) : null}
                            locked={!!existingGame && (existingGame.status !== 'setup' || existingGame.settings?.locked === true
                              || (standings?.games?.find(g => g.game_id === existingGame.id)?.results?.totals || []).some(t => Number(t.thru) > 0))}
                            saved={!!existingGame}
                          />
                        )}

                        {inst.checked && inst.stroke_settings && (
                          <StrokePlayGamePanel
                            settings={inst.stroke_settings}
                            onChange={(patch) => handleStrokeSettings(inst.instanceId, patch)}
                            betAmount={inst.bet_amount}
                            onWager={(v) => handleWager(inst.instanceId, v)}
                            results={existingGame ? (standings?.games?.find(g => g.game_id === existingGame.id)?.results || null) : null}
                            savedSettings={existingGame ? (existingGame.settings || {}) : null}
                            saved={!!existingGame}
                            isEvent={isEvent}
                            bestBall={isBestBallGameType(inst.game_type)}
                            gameType={inst.game_type}
                            fieldSize={existingGame ? (existingGame.participant_ids || []).length : null}
                          />
                        )}

                        {inst.checked && !inst.team_settings && !inst.stroke_settings && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Wager:</span>
                              <span style={moneyBox}>
                                <span style={moneySign}>$</span>
                                <input type="number" min="0" step="0.25" value={inst.bet_amount}
                                  onChange={(e) => handleWager(inst.instanceId, e.target.value)}
                                  placeholder="0.00"
                                  style={moneyInput} />
                              </span>
                              <span style={{ fontSize: 12, color: '#6b7280' }}>
                                {game.game_type === 'putts' ? 'per putt differential' : game.game_type === 'dots' ? 'per dot' : game.game_type === 'stableford' ? (['per_point_field', 'per_point_pairs', 'per_point_teams'].includes(inst.stableford_settings && inst.stableford_settings.payout_mode) ? 'per point' : 'per player buy-in') : game.game_type === 'nassau' ? 'each bet — front, back, overall, each press' : game.game_type === 'match_play' ? 'per match' : game.game_type === 'sixes' ? 'each match (and each press)' : isPotGame(game.game_type) ? 'per player buy-in' : game.game_type === 'ctp' ? (inst.ctp_settings && inst.ctp_settings.funding === 'per_player' ? 'per player, per hole' : 'prize per hole') : 'per skin'}
                              </span>
                            </div>
                            {parseFloat(inst.bet_amount) === 0 && (
                              <div style={{ marginTop: 6, fontSize: 12, color: '#166534', background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: 6, padding: '5px 9px' }}>
                                {game.game_type === 'ctp' ? 'Winners only — TTO names each hole’s winner; no money is settled.' : game.game_type === 'stableford' ? 'Results only — TTO keeps the points and the places; no money is settled.' : game.game_type === 'dots' ? 'Results only — TTO counts the dots; no money is settled.' : (game.game_type === 'nassau' || game.game_type === 'sixes' || game.game_type === 'match_play') ? 'Results only — TTO keeps the matches; no money is settled.' : 'Results only — TTO tracks skins won; no money is settled.'}
                              </div>
                            )}
                            {game.game_type === 'ctp' && (
                              <CtpGamePanel settings={inst.ctp_settings || {}} onChange={(patch) => setInstances(prev => prev.map(i => i.instanceId === inst.instanceId ? { ...i, ctp_settings: { ...(i.ctp_settings || {}), ...patch } } : i))}
                                teeTimeId={teeTime.id} roundDate={teeTime.date} saved={!!existingGame} prize={parseFloat(inst.bet_amount) || 0}
                nines={ninesForCourse(teeTime?.course)}
                                players={existingGame ? (existingGame.participant_ids || []).length : null} />
                            )}
                            {game.game_type === 'stableford' && (
                              <StablefordGamePanel settings={inst.stableford_settings || {}} bet={parseFloat(inst.bet_amount) || 0}
                                nine={!!(teeTime && teeTime.competition_hole_set && teeTime.competition_hole_set !== 'all18')}
                                onChange={(patch) => setInstances(prev => prev.map(i => i.instanceId === inst.instanceId ? { ...i, stableford_settings: { ...(i.stableford_settings || {}), ...patch } } : i))} />
                            )}
                            {game.game_type === 'sixes' && (
                              <SixesGamePanel settings={inst.sixes_settings || {}} bet={parseFloat(inst.bet_amount) || 0}
                                onChange={(patch) => setInstances(prev => prev.map(i => i.instanceId === inst.instanceId ? { ...i, sixes_settings: { ...(i.sixes_settings || {}), ...patch } } : i))} />
                            )}
                            {game.game_type === 'match_play' && (
                              <MatchPlayGamePanel settings={inst.match_play_settings || {}} bet={parseFloat(inst.bet_amount) || 0}
                                onChange={(patch) => setInstances(prev => prev.map(i => i.instanceId === inst.instanceId ? { ...i, match_play_settings: { ...(i.match_play_settings || {}), ...patch } } : i))} />
                            )}
                            {game.game_type === 'nassau' && (
                              <NassauGamePanel settings={inst.nassau_settings || {}} bet={parseFloat(inst.bet_amount) || 0}
                                onChange={(patch) => setInstances(prev => prev.map(i => i.instanceId === inst.instanceId ? { ...i, nassau_settings: { ...(i.nassau_settings || {}), ...patch } } : i))} />
                            )}
                            {game.game_type === 'dots' && (
                              <DotsGamePanel settings={inst.dots_settings || {}} onChange={(patch) => setInstances(prev => prev.map(i => i.instanceId === inst.instanceId ? { ...i, dots_settings: { ...(i.dots_settings || {}), ...patch } } : i))}
                                teeTimeId={teeTime.id} roundDate={teeTime.date} saved={!!existingGame} bet={parseFloat(inst.bet_amount) || 0} />
                            )}
                            {isPotGame(game.game_type) && (
                              <div style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                <input type="checkbox" checked={inst.physical_pot === true} onChange={() => handlePhysicalPot(inst.instanceId)}
                                  style={{ width: 16, height: 16, marginTop: 2, cursor: 'pointer', accentColor: '#166534' }} />
                                <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.4 }}>
                                  <span style={{ fontWeight: 600 }}>Physical pot settlement</span>
                                  <span style={{ color: '#6b7280' }}> — players collect gross winnings from a shared pot. Uncheck for net-only settlement.</span>
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {inst.checked && !inst.team_settings && (isBestBallGameType(inst.game_type) ? renderBestBallTeams(existingGame)
                          : (inst.game_type === 'ctp' && instances.some(i => i.checked && i.game_type === 'scramble')) ? (
                            <div data-testid="ctp-team-round" style={{ marginTop: 14, borderTop: '1px solid #d1fae5', paddingTop: 12, fontSize: 13, color: '#166534', fontWeight: 600 }}>
                              👥 Every player on a team plays — no opt-ins. Players count once the pairings are saved.
                            </div>
                          ) : renderOptIns(existingGame, inst))}
                      </div>
                    );
                  })}

                  {/* Add Another button — only show for checked skins games (not putts, not pot variants, not stroke play) */}
                  {checkedInstances.length > 0 && !isPotGame(game.game_type) && game.game_type !== 'putts' && !isStrokeType(game.game_type) && game.game_type !== 'ctp' && game.game_type !== 'dots' && game.game_type !== 'nassau' && game.game_type !== 'sixes' && game.game_type !== 'match_play' && game.game_type !== 'stableford' && game.competitor_scope !== 'team' && checkedInstances.length < 4 && (
                    <button onClick={() => handleAddInstance(game.game_type)}
                      style={{ width: '100%', marginTop: 4, padding: '7px', borderRadius: 8,
                        border: '1px dashed #86efac', background: '#f0fdf4',
                        color: '#166534', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      + Add Another {game.display_name} (different stakes)
                    </button>
                  )}
                </div>
              );
            })}

            {catalog.length === 0 && (
              <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af' }}>No games available.</div>
            )}

            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16, marginTop: 4 }}>
              ℹ️ Players opt in from their Player Portal before the round starts.
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: '#166534', color: '#fff', cursor: saving ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {saving ? 'Saving...' : `Save Games${checkedCount > 0 ? ` (${checkedCount} selected)` : ''}`}
              </button>
            </div>
            {/* Ready-to-finalize nudge: every participant has 18 holes in, round not
                yet finalized. Mirrors the player-portal nudge so the organizer sees it
                whether they are in the dashboard modal or the app. */}
            {!isFinalized && roundReady && readyParticipantCount > 0 && (
              <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 8, border: '1px solid #6ee7b7', background: '#ecfdf5' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#065f46', marginBottom: 2 }}>✅ All players have finished</div>
                <div style={{ fontSize: 12, color: '#047857', lineHeight: 1.4 }}>
                  {autoFinalize
                    ? 'Auto-finalize is on — this round will finalize itself shortly. You can also finalize now below.'
                    : 'Scores are in — finalize now to settle the games.'}
                </div>
              </div>
            )}
            {/* Auto-finalize toggle — per tee time. When on, the round finalizes itself
                once all game participants have entered 18 holes, or 6 hours after tee
                time as a drop-out backstop. Settlement is then viewable in the player
                app; settlement email stays a manual action. */}
            <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>Auto-finalize this round</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>
                    Finalizes automatically once every player has entered every hole (or 6 hours after tee time). Players see settlement in the app.{' '}
                    {isEvent ? 'Off to start for an event, so you can check the cards and settle ties first.' : 'On to start for new rounds.'}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    const next = !autoFinalize;
                    setAutoFinalizeSaving(true);
                    setAutoFinalize(next); // optimistic
                    try {
                      const headers = { 'Content-Type': 'application/json' };
                      const token = getAuthToken();
                      if (token) headers['Authorization'] = `Bearer ${token}`;
                      const res = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/auto-finalize`, {
                        method: 'PUT', credentials: 'include', headers,
                        body: JSON.stringify({ auto_finalize: next }),
                      });
                      if (!res.ok) throw new Error('save failed');
                      const d = await res.json();
                      setAutoFinalize(d.auto_finalize === true);
                    } catch (e) {
                      setAutoFinalize(!next); // revert
                      addToast('Could not update auto-finalize', 'error');
                    } finally {
                      setAutoFinalizeSaving(false);
                    }
                  }}
                  disabled={autoFinalizeSaving}
                  aria-pressed={autoFinalize}
                  style={{ position: 'relative', width: 46, height: 26, borderRadius: 13, border: 'none', flexShrink: 0, cursor: autoFinalizeSaving ? 'wait' : 'pointer', background: autoFinalize ? '#166534' : '#d1d5db', transition: 'background 0.15s' }}>
                  <span style={{ position: 'absolute', top: 3, left: autoFinalize ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
                </button>
              </div>
            </div>
            <button onClick={() => handleFinalizeRound(false)} disabled={finalizing || checkedCount === 0}
              style={{ width: '100%', marginTop: 8, padding: '10px', borderRadius: 8, border: '1px solid #fcd34d', background: '#fffbeb', color: checkedCount === 0 ? '#d1d5db' : '#92400e', cursor: finalizing || checkedCount === 0 ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
              {finalizing ? 'Finalizing...' : '🏁 Finalize Round & Save Settlements'}
            </button>
          </>
        )}

        {/* Finalize completeness prompt (WS-4): a participant has a partial card. */}
        {incompleteFinalize && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460, maxHeight: '85vh', overflowY: 'auto' }}>
              <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4, color: '#92400e' }}>⛳ Not everyone has finished scoring</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                Settling now would compute the games on an incomplete card. Finish entering their scores, or mark a player DNF to remove them from this round's games.
              </div>
              <div style={{ border: '1px solid #fde68a', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
                {incompleteFinalize.incomplete.map((pl, i) => (
                  <div key={pl.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px', background: i % 2 ? '#fffbeb' : '#fff', borderTop: i ? '1px solid #fef3c7' : 'none' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{pl.name}</div>
                      <div style={{ fontSize: 12, color: '#b45309' }}>{pl.thru} of {incompleteFinalize.expectedHoles} holes entered</div>
                    </div>
                    <button onClick={() => handleMarkDnf(pl.id, pl.name)} disabled={dnfBusyId === pl.id}
                      style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fef2f2', color: '#b91c1c', cursor: dnfBusyId === pl.id ? 'wait' : 'pointer', fontSize: 12.5, fontWeight: 600, flexShrink: 0 }}>
                      {dnfBusyId === pl.id ? 'Removing…' : 'Mark DNF'}
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: '#9ca3af', marginBottom: 14 }}>
                Removing a player can change the others' skins — a hole he tied may now be won, and a net game played off the low man figures strokes off the lowest handicap left in it.
              </div>
              <button onClick={() => setIncompleteFinalize(null)}
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                Wait for scores
              </button>
            </div>
          </div>
        )}

        {/* Best Ball: the blind draw is final (Sept 26, 2026) */}
        {bbNotice && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 440 }}>
              <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 12 }}>{typeof bbNotice === 'object' ? bbNotice.title : 'The blind stays in'}</div>
              <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0 }}>{typeof bbNotice === 'object' ? bbNotice.text : bbNotice}</p>
              <button onClick={() => setBbNotice(null)}
                style={{ marginTop: 20, width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                OK
              </button>
            </div>
          </div>
        )}

        {/* Rules modal */}
        {rulesGame && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 440, maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 12 }}>How {rulesGame.display_name} Works</div>
              <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 }}>
                {rulesGame.rules_text || 'No rules text available.'}
              </p>
              <button onClick={() => setRulesGame(null)}
                style={{ marginTop: 20, width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                Got It
              </button>
            </div>
          </div>
        )}

        {/* Settlement Email Modal */}
        {showEmailModal && (
          <SendSettlementEmailModal
            teeTime={teeTime}
            onClose={() => setShowEmailModal(false)}
            onSend={async ({ player_ids, note }) => {
              await onEmailSettlement(teeTime.id, { player_ids, note });
              setShowEmailModal(false);
            }}
          />
        )}

      </div>
    </div>
  );
};


// ============================================================================
// Round printouts + the event's TV board (Sept 17, 2026) — shared by the Print
// tab, the tee time card and the Scoring tab.
// ============================================================================
const eventBoardUrl = (ev) => (ev && ev.public_slug ? (ev.public_url || `https://${PUBLIC_BOARD_HOST.toLowerCase()}/${ev.public_slug}`) : null);

// Printed scorecards / cart signs (Sept 16, 2026 — events recap item 6): PDFs built
// server-side to golfcardstock.com's perforated stock (printService.js). The window
// is opened synchronously on the click (pop-up blockers), then pointed at the PDF
// once it arrives; a server error — pairings not saved, say — is written into that
// same window so the organizer sees it where they looked. The blob is wrapped in a
// File so a download keeps a real name. Resolves true when the PDF opened.
// Every document the print route serves, for the progress window's wording.
// ============================================================================
// READY CHECK AT THE MOMENTS THAT MATTER (Oct 4, 2026 — Event_Ready_Check_Plan_v1.md slice 3)
// The check warns, never blocks (Dennis). Printing scorecards and finalizing are the
// two moments where a must-fix item becomes paper or money, so they ask first.
// Never blocks on a failed check: if the ready check can't run, the action goes ahead.
// ============================================================================
const readyStops = async ({ eventId = null, teetimeId = null }) => {
  try {
    const h = {};
    const t = getAuthToken();
    if (t) h['Authorization'] = `Bearer ${t}`;
    let id = eventId;
    if (!id && teetimeId) {
      const r = await fetch(`${API_URL}/events?teetime_id=${teetimeId}`, { credentials: 'include', headers: h });
      if (!r.ok) return [];
      const d = await r.json().catch(() => ({}));
      const ev = Array.isArray(d.events) ? d.events[0] : null;
      if (!ev) return [];          // not an event: nothing to check
      id = ev.id;
    }
    if (!id) return [];
    const r = await fetch(`${API_URL}/events/${id}/ready`, { credentials: 'include', headers: h });
    if (!r.ok) return [];
    const d = await r.json().catch(() => ({}));
    const items = (d.ready && d.ready.items) || [];
    return items.filter(i => i.level === 'stop' && i.group !== 'after');
  } catch (e) { return []; }
};
const mustFixMessage = (stops, action) => {
  const n = stops.length;
  const list = stops.slice(0, 6).map(i => `• ${i.title}`).join('\n') + (n > 6 ? `\n• …and ${n - 6} more` : '');
  const noIndex = stops.some(i => i.id === 'index');
  const what = action === 'print'
    ? `The cards print as the event stands now${noIndex ? ' — a player with no handicap index prints without strokes' : ''}.`
    : `Results are figured as the event stands now${noIndex ? ' — a player with no handicap index is scored as scratch' : ''}.`;
  return `The Ready check has ${n} must-fix item${n === 1 ? '' : 's'}:\n\n${list}\n\n${what}\n\n${action === 'print' ? 'Print anyway?' : 'Finalize anyway?'}`;
};
// The "3 to fix" badge on an event's Ready check button: must-fix count, fetched once.
const useReadyStopCount = (eventId, enabled = true, refreshKey = 0) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!eventId || !enabled) { setN(0); return undefined; }
    let off = false;
    readyStops({ eventId }).then(st => { if (!off) setN(st.length); });
    return () => { off = true; };
  }, [eventId, enabled, refreshKey]);
  return n;
};
const fixBadge = (n) => (n > 0
  ? <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 999, background: '#B42318', color: '#fff', fontSize: 11.5, fontWeight: 800 }}>{n} to fix</span>
  : null);

const PRINT_LABELS = {
  'scorecards': 'scorecards',
  'cart-signs': 'cart signs',
  'posting-sheets': 'posting sheets',
  'event-sign': 'event sign',
  'desk-sheet': 'desk recording sheet',
  'checkin-sheet': 'check-in sheet',
};
// `query` carries per=<teams per sheet> for the posting sheets; omitted, the
// renderer picks the recommendation for the field (Score_Posting_Sheets_Plan_v2 §4).
const openRoundPdf = async (teetimeId, kind, query) => {
  if (!teetimeId) return false;
  const label = PRINT_LABELS[kind] || 'document';
  const w = window.open('', '_blank');
  if (!w) { window.alert('Your browser blocked the print window. Allow pop-ups for this site and try again.'); return false; }
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const say = (html) => { try { w.document.title = `Printing ${label}`; w.document.body.innerHTML = html; } catch (e) { /* window closed */ } };
  say(`<p style="font-family:sans-serif;padding:24px;color:#374151">Building the ${esc(label)} PDF…</p>`);
  try {
    const t = getAuthToken();
    const r = await fetch(`${API_URL}/teetimes/${teetimeId}/print/${kind}.pdf${query ? `?${query}` : ''}`, { headers: t ? { 'Authorization': `Bearer ${t}` } : {} });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      say(`<div style="font-family:sans-serif;padding:24px;max-width:560px"><h2 style="margin:0 0 8px;font-size:18px;color:#991b1b">Couldn't print the ${esc(label)}</h2><p style="color:#374151;line-height:1.5">${esc(d.error || `The server answered ${r.status}.`)}</p></div>`);
      return false;
    }
    const blob = await r.blob();
    const cd = r.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="([^"]+)"/);
    const file = new File([blob], m ? m[1] : `${kind}.pdf`, { type: 'application/pdf' });
    w.location.href = URL.createObjectURL(file);
    return true;
  } catch (e) {
    say('<p style="font-family:sans-serif;padding:24px;color:#991b1b">Could not reach the server. Try again.</p>');
    return false;
  }
};

// 📺 TV on an event's tee time card. The window opens on the click; the board's
// public state is checked first so an unshared board says so here instead of
// opening a TV page that reads "unavailable".
const openEventTv = async (ev, addToast) => {
  const url = eventBoardUrl(ev);
  if (!url) return;
  const w = window.open('', '_blank');
  if (!w) { window.alert('Your browser blocked the new window. Allow pop-ups for this site and try again.'); return; }
  try { w.document.title = 'TV leaderboard'; w.document.body.innerHTML = '<p style="font-family:sans-serif;padding:24px;color:#374151">Opening the TV leaderboard…</p>'; } catch (e) { /* closed */ }
  try {
    const r = await fetch(`${API_URL}/public/leaderboard/${encodeURIComponent(ev.public_slug)}?t=${Date.now()}`, { cache: 'no-store' });
    const d = await r.json().catch(() => ({}));
    if (d && d.state === 'unavailable') {
      try { w.close(); } catch (e) { /* closed */ }
      if (addToast) addToast("This event's live leaderboard isn't shared yet — turn sharing on in the Live Leaderboard box on the Scoring tab.", 'error');
      return;
    }
  } catch (e) { /* can't tell — open it anyway */ }
  w.location.href = `${url}/tv`;
};

// ============================================================================
// ⋯ MORE ACTIONS MENU (Sept 25, 2026 — dashboard home redesign, Dennis).
// Delete used to be a red button on every tee-time card and list row; it now
// sits one click further away, behind this menu. What it calls is unchanged
// (the same confirm dialog opens). Closes on an outside click/tap or Escape.
// ============================================================================
const MoreActionsMenu = ({ items = [], disabled = false, onDisabledClick, size = 'md', style }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const dim = size === 'sm' ? 30 : 34;
  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-flex', ...(style || {}) }}>
      <button
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { if (disabled) { onDisabledClick?.(); return; } setOpen((o) => !o); }}
        style={{ width: dim, height: dim, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, lineHeight: 1, color: '#4b5563', padding: 0 }}
      >
        ⋯
      </button>
      {open && (
        <div role="menu" style={{ position: 'absolute', right: 0, top: dim + 4, minWidth: 180, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 4, zIndex: 30 }}>
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); it.onClick?.(); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: 'transparent', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: it.danger ? '#b91c1c' : '#111827', whiteSpace: 'nowrap' }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// EVENT READY CHECK (Oct 4, 2026) — claude/Event_Ready_Check_Plan_v1.md
// Backend: services/eventReadinessService.js, GET /api/events/:id/ready
//
// Dennis, Oct 4: one screen that says whether the event is ready to roll — a
// checklist with items checked off as completed, run any time, on the event's tee
// time (organizer) and on the Event Desk. It WARNS, never blocks. Every problem
// links straight to where it's fixed. Worked out fresh on every open / Check again.
// ============================================================================
const READY_TONE = {
  stop: { fg: '#B42318', bg: '#FEF3F2', line: '#FECDCA', label: 'Must fix' },
  warn: { fg: '#B54708', bg: '#FFFAEB', line: '#FEDF89', label: 'Check these' },
  ok: { fg: '#1b4332', bg: '#F3F5F1', line: '#E2E8E4', label: 'Ready' },
};
const READY_GROUP_LABEL = { event: 'The event', course: 'The course · every tee in play', field: 'The field', teams: 'Teams', games: 'Games', tools: 'Event day tools', day: 'On the day' };
const DESK_CANNOT = ['courses', 'player_records'];

const EventReadyCheck = ({ eventId, teetimeId, onManagePlayers, onEdit, onGames, onPublish, onNavigate, collapsible = false, startView = 'setup' }) => {
  const { addToast } = useToast();
  const deskSession = useContext(DeskContext);
  const appNav = useContext(AppNavContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(!collapsible);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(startView === 'checkin' ? 'checkin' : 'setup');  // 'setup' | 'checkin' (slice 2, Oct 4, 2026)
  const [teamsOpen, setTeamsOpen] = useState(false);  // 👥 Teams window from a Ready Check line (Oct 28, 2026 — Captain team Slice 3)

  const headers = (json = false) => {
    const h = {};
    const t = getAuthToken();
    if (t) h['Authorization'] = `Bearer ${t}`;
    if (json) h['Content-Type'] = 'application/json';
    return h;
  };
  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/events/${eventId}/ready`, { credentials: 'include', headers: headers() });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setData(d.ready || null); setError(null); }
      else setError(d.error || 'The ready check could not run.');
    } catch (e) { setError('Network error — the ready check could not run.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [eventId]);

  const deskBlocked = (it) => !!deskSession && (it.organizerOnly || DESK_CANNOT.includes(it.fix));
  const go = async (it) => {
    if (!it || !it.fix || deskBlocked(it)) return;
    const tt = teetimeId || (data && data.teetime_id);
    switch (it.fix) {
      case 'players': if (onManagePlayers) onManagePlayers(); break;
      case 'pairings': appNav.goTo('pairings', { kind: 'pairings-round', teetimeId: tt }); break;
      case 'print': appNav.goTo('print', { kind: 'print-round', teetimeId: tt }); break;
      case 'games': if (onGames) onGames(); break;
      case 'edit': if (onEdit) onEdit(); break;
      case 'publish': if (onPublish) onPublish(); else if (onEdit) onEdit(); break;
      case 'scoring': if (deskSession) appNav.goTo('deskscoring'); else appNav.goTo('admin', { kind: 'scoring-round', teetimeId: tt }); break;
      case 'courses': appNav.goTo('admin', { kind: 'admin-courses' }); break;
      case 'player_records': appNav.goTo('admin', { kind: 'admin-players' }); break;
      case 'checkin': setView('checkin'); setOpen(true); return;   // stays on this screen
      case 'teams': setTeamsOpen(true); return;   // the 👥 Teams window opens over this screen; Check again on close
      case 'share': {
        setBusy(true);
        try {
          const r = await fetch(`${API_URL}/scoring/teetimes/${tt}/share-leaderboard`, { method: 'POST', credentials: 'include', headers: headers(true) });
          if (r.ok) { addToast('Leaderboard shared'); await load(); } else addToast('Could not share the leaderboard', 'error');
        } catch (e) { addToast('Network error — the leaderboard was not shared', 'error'); }
        finally { setBusy(false); }
        return;   // stays on this screen
      }
      default: return;
    }
    if (onNavigate) onNavigate();
  };

  const fixButton = (it, small) => {
    if (!it.fix) return null;
    if (deskBlocked(it)) return <span style={{ fontSize: 13, color: '#6b7280' }}>The organizer fixes this</span>;
    return (
      <button type="button" disabled={busy} onClick={() => go(it)}
        style={{ minHeight: small ? 32 : 40, padding: small ? '0 10px' : '0 14px', borderRadius: 9, border: '1px solid #c2dacc', background: '#fff', color: '#1b4332', font: 'inherit', fontSize: small ? 13 : 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        {it.fixLabel || 'Fix'} ›
      </button>
    );
  };

  const items = (data && data.items) || [];
  const problems = items.filter(i => (i.level === 'stop' || i.level === 'warn') && i.group !== 'after');
  const stops = problems.filter(i => i.level === 'stop');
  const warns = problems.filter(i => i.level === 'warn');
  const readyGroups = ['event', 'course', 'field', 'teams', 'games', 'tools'];
  const notices = items.filter(i => i.group === 'notices');
  const after = items.filter(i => i.group === 'after');
  const s = data ? data.summary : null;

  const problemRow = (it) => {
    const tone = READY_TONE[it.level];
    return (
      <div key={it.group + it.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-start', padding: '14px 16px', borderTop: `1px solid ${tone.line}` }}>
        <div aria-hidden="true" style={{ width: 26, height: 26, borderRadius: '50%', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, boxSizing: 'border-box', ...(it.level === 'stop' ? { background: tone.fg, color: '#fff' } : { border: `2px solid ${tone.fg}`, color: tone.fg }) }}>!</div>
        <div style={{ flex: '999 1 360px', minWidth: 0, display: 'grid', gap: 5 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{it.group === 'day' ? 'On the day: ' : ''}{it.title}</div>
          {it.detail && <div style={{ fontSize: 14, color: '#4b5361', lineHeight: 1.45 }}>{it.detail}</div>}
          {Array.isArray(it.names) && it.names.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {it.names.map((n, i) => (
                it.fix && !deskBlocked(it)
                  ? <button key={i} type="button" onClick={() => go(it)} style={{ minHeight: 32, padding: '0 10px', borderRadius: 8, border: 'none', background: '#f3f5f1', color: '#1b4332', font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{n} ›</button>
                  : <span key={i} style={{ padding: '6px 10px', borderRadius: 8, background: '#f3f5f1', fontSize: 14 }}>{n}</span>
              ))}
            </div>
          )}
          {Array.isArray(it.list) && it.list.length > 0 && (
            <ol style={{ margin: '2px 0 0 18px', padding: 0, fontSize: 14, color: '#111827', lineHeight: 1.7 }}>
              {it.list.map(w => <li key={w.n}>{w.name}{w.phone ? <span style={{ color: '#4b5361' }}> · {w.phone}</span> : <span style={{ color: '#9ca3af' }}> · no phone</span>}</li>)}
            </ol>
          )}
          {it.fixNote && <div style={{ fontSize: 12.5, color: '#6b7280' }}>{it.fixNote}</div>}
        </div>
        <div style={{ flex: '0 0 auto' }}>{fixButton(it)}</div>
      </div>
    );
  };

  const section = (title, tone, rows) => (
    <div style={{ border: `1px solid ${tone.line}`, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      <div style={{ padding: '10px 16px', background: tone.bg, fontSize: 12.5, fontWeight: 800, letterSpacing: '0.08em', color: tone.fg, textTransform: 'uppercase' }}>{title}</div>
      {rows}
    </div>
  );

  const header = (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginRight: 4 }}>Ready check</span>
        {/* The strip (collapsible) shows only what matters: zero counts drop out, and all-clear says so. */}
        {s && (!collapsible || s.stop > 0) && <span style={{ padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 700, background: s.stop ? '#B42318' : '#f3f4f6', color: s.stop ? '#fff' : '#6b7280' }}>{s.stop} must fix</span>}
        {s && (!collapsible || s.warn > 0) && <span style={{ padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 700, background: s.warn ? '#FFFAEB' : '#f3f4f6', color: s.warn ? '#B54708' : '#6b7280', border: s.warn ? '1px solid #FEDF89' : '1px solid transparent' }}>{s.warn} to check</span>}
        {s && collapsible && !s.stop && !s.warn
          ? <span style={{ padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 700, background: '#1b4332', color: '#fff' }}>✓ Ready to roll</span>
          : null}
        {s && <span style={{ padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 700, background: '#eaf3ee', color: '#1b4332' }}>{s.ok} ready</span>}
        {data && <span style={{ fontSize: 13, color: '#6b7280' }}>Checked {new Date(data.checked_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="button" variant="outline" disabled={loading} onClick={load} style={{ fontSize: 14 }}>{loading ? 'Checking…' : 'Check again'}</Button>
        {collapsible && <Button type="button" variant="outline" onClick={() => setOpen(o => !o)} style={{ fontSize: 14 }}>{open ? 'Hide' : 'Show the checklist'}</Button>}
      </div>
    </div>
  );

  const tabBtn = (key, label) => (
    <button type="button" onClick={() => { setView(key); setOpen(true); if (key === 'setup') load(); }} aria-pressed={view === key}
      style={{ minHeight: 40, padding: '0 16px', borderRadius: 999, font: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
        border: view === key ? 'none' : '1px solid #c2dacc', background: view === key ? '#1b4332' : '#fff', color: view === key ? '#fff' : '#1b4332' }}>{label}</button>
  );

  // The strip on Event Home (collapsible) wears its worst finding (Oct 4, 2026): red when
  // something must be fixed, amber when there's something to check, a calm slim green when clear.
  const sev = !s ? null : s.stop ? 'stop' : s.warn ? 'warn' : 'ok';
  const frame = !collapsible ? {} : {
    borderRadius: 14, boxShadow: '0 1px 3px rgba(16, 24, 40, 0.06)',
    ...(sev === 'stop' ? { border: '2px solid #B42318', background: '#FFFBFA', padding: 15 }
      : sev === 'warn' ? { border: '2px solid #FEDF89', background: '#fff', padding: 15 }
      : sev === 'ok' ? { border: '1px solid #c2dacc', background: '#f3faf5', padding: open ? 16 : '10px 16px' }
      : { border: '1px solid #e5e7eb', background: '#fff', padding: 16 }),
  };
  return (
    <div style={{ display: 'grid', gap: 14, ...frame }}>
      {header}
      {(open || !collapsible) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {tabBtn('setup', 'Setup checklist')}
          {tabBtn('checkin', 'Event morning check-in')}
        </div>
      )}
      {open && view === 'checkin' && (
        <EventCheckIn eventId={eventId} onManagePlayers={onManagePlayers} onGames={onGames} onNavigate={onNavigate} />
      )}
      {error && view === 'setup' && <div style={{ fontSize: 14, color: '#B42318' }}>{error}</div>}
      {!data && !error && view === 'setup' && <div style={{ fontSize: 14, color: '#6b7280' }}>Checking the event…</div>}
      {data && open && view === 'setup' && (
        <>
          {stops.length > 0 && section(READY_TONE.stop.label, READY_TONE.stop, stops.map(problemRow))}
          {warns.length > 0 && section(READY_TONE.warn.label, READY_TONE.warn, warns.map(problemRow))}
          {stops.length === 0 && warns.length === 0 && (
            <div style={{ padding: '14px 16px', borderRadius: 12, background: '#eaf3ee', color: '#1b4332', fontSize: 15, fontWeight: 700 }}>✓ Nothing to fix. The event is ready.</div>
          )}

          {section(READY_TONE.ok.label, READY_TONE.ok, (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0 28px', padding: '4px 16px 12px' }}>
              {readyGroups.map(g => {
                const rows = items.filter(i => i.group === g && (i.level === 'ok' || i.level === 'info'));
                if (!rows.length) return null;
                return (
                  <div key={g}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '12px 0 4px' }}>{READY_GROUP_LABEL[g]}</div>
                    {rows.map(it => (
                      <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: '1px solid #f0f2ef', fontSize: 14.5 }}>
                        <span style={{ width: 16, fontWeight: 800, color: it.level === 'ok' ? '#1b4332' : '#9ca3af' }}>{it.level === 'ok' ? '✓' : '–'}</span>
                        <span style={{ flex: 1, color: it.level === 'ok' ? '#111827' : '#4b5361' }}>{it.title}{it.level === 'info' && it.detail ? <span style={{ display: 'block', fontSize: 12.5, color: '#6b7280' }}>{it.detail}</span> : null}</span>
                        {it.fix && !deskBlocked(it) && <button type="button" onClick={() => go(it)} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 13.5, color: '#1b4332', textDecoration: 'underline', cursor: 'pointer' }}>{it.level === 'info' ? (it.fixLabel || 'Open') : 'Open'}</button>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}

          {notices.length > 0 && section('What players will get · your settings', { fg: '#2D4B8E', bg: '#EEF4FF', line: '#C7D7FE' }, notices.map(it => (
            <div key={it.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #EEF2FF' }}>
              <div style={{ flex: '1 1 200px', fontSize: 15, fontWeight: 700 }}>{it.title}</div>
              <div style={{ flex: '999 1 320px', minWidth: 0, fontSize: 14.5 }}>
                <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 800, marginRight: 8, background: it.on ? '#eaf3ee' : '#f3f4f6', color: it.on ? '#1b4332' : '#4b5361' }}>{it.on ? 'ON' : 'OFF'}</span>
                {it.value}
                {it.note && <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>{it.note}</div>}
              </div>
              <div>{fixButton(it, true)}</div>
            </div>
          )))}

          {after.length > 0 && section('After the round', { fg: '#4b5361', bg: '#F3F5F1', line: '#E2E8E4' }, (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0 28px', padding: '4px 16px 10px' }}>
              {after.map(it => (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #f0f2ef', fontSize: 14.5 }}>
                  <span style={{ width: 16, fontWeight: 800, color: it.level === 'ok' ? '#1b4332' : it.level === 'warn' ? '#B54708' : '#9ca3af' }}>{it.level === 'ok' ? '✓' : it.level === 'warn' ? '!' : '–'}</span>
                  <span style={{ flex: 1, color: it.level === 'ok' ? '#111827' : '#4b5361' }}>{it.title}</span>
                  {it.level !== 'ok' && it.fix && !deskBlocked(it) && <button type="button" onClick={() => go(it)} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 13.5, color: '#1b4332', textDecoration: 'underline', cursor: 'pointer' }}>{it.fixLabel || 'Open'}</button>}
                </div>
              ))}
            </div>
          ))}
        </>
      )}
      {teamsOpen && (eventId || (data && data.event_id)) && (
        <EventTeamsModal eventId={eventId || data.event_id} apiUrl={API_URL} getToken={getAuthToken} onClose={() => { setTeamsOpen(false); load(); }} />
      )}
    </div>
  );
};

// ============================================================================
// EVENT MORNING CHECK-IN (Oct 4, 2026) — Event_Ready_Check_Plan_v1.md, slice 2
// Backend: services/eventCheckinService.js, GET/POST /api/events/:id/checkin
// Dennis, Oct 4: no-shows are always a person's call; the committee decides how a
// short team plays; the organizer dashboard and the Event Desk can both do it.
// ============================================================================
const EventCheckIn = ({ eventId, onManagePlayers, onGames, onNavigate }) => {
  const { addToast } = useToast();
  const appNav = useContext(AppNavContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);         // player_id being saved
  const [confirmWithdraw, setConfirmWithdraw] = useState(null);
  const [find, setFind] = useState('');
  const [order, setOrder] = useState('az');        // 'az' (default — a golfer gives his name) | 'team'

  const headers = (json = false) => {
    const h = {};
    const t = getAuthToken();
    if (t) h['Authorization'] = `Bearer ${t}`;
    if (json) h['Content-Type'] = 'application/json';
    return h;
  };
  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/events/${eventId}/checkin`, { credentials: 'include', headers: headers() });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setData(d.checkin || null); setError(null); } else setError(d.error || 'Check-in could not load.');
    } catch (e) { setError('Network error — check-in could not load.'); }
  };
  useEffect(() => { if (eventId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [eventId]);

  const act = async (m, action, extra = null) => {
    setBusy(m.player_id);
    try {
      const r = await fetch(`${API_URL}/events/${eventId}/checkin`, {
        method: 'POST', credentials: 'include', headers: headers(true), body: JSON.stringify({ player_id: m.player_id, action, ...(extra || {}) }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setData(d.checkin || data); if (d.message && action !== 'fee') addToast(d.message, 'success'); }
      else alert(d.error || 'That did not save. Please try again.');
    } catch (e) { alert('Network error. Please try again.'); }
    finally { setBusy(null); setConfirmWithdraw(null); }
  };

  if (error) return <div style={{ fontSize: 14, color: '#B42318' }}>{error}</div>;
  if (!data) return <div style={{ fontSize: 14, color: '#6b7280' }}>Loading check-in…</div>;

  const c = data.counts;
  const word = data.format.word;
  const Word = word === 'team' ? 'Team' : 'Group';
  const when = (ts) => (ts ? new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
  const slot = (t) => (data.shotgun && t.starting_hole != null ? `${startHoleText(t.starting_hole, t.group_label, data.nines || null)} · ` : '');
  const needs = data.teams.filter(t => t.attention);
  const q = find.trim().toLowerCase();
  const shown = q ? data.teams.filter(t => t.members.some(m => m.name.toLowerCase().includes(q))) : data.teams;
  // A–Z by last name (Dennis, Oct 4): a golfer walks up and gives his name, not his starting hole.
  // Same order as the printed check-in sheet; each row carries its team and start.
  const lastFirst = (n) => { const p = String(n || '').trim().split(/\s+/); return p.length > 1 ? `${p[p.length - 1]}, ${p.slice(0, -1).join(' ')}` : (p[0] || ''); };
  const alpha = [
    ...data.teams.flatMap(t => t.members.map(m => ({ ...m, where: `${Word} ${t.number}${data.shotgun && t.starting_hole != null ? ` · ${startHoleText(t.starting_hole, t.group_label, data.nines || null)}` : ''}` }))),
    ...data.unpaired.map(m => ({ ...m, where: `Not on a ${word}` })),
  ].sort((a, b) => lastFirst(a.name).toLowerCase().localeCompare(lastFirst(b.name).toLowerCase()));
  const alphaShown = q ? alpha.filter(m => m.name.toLowerCase().includes(q)) : alpha;
  const letterOf = (m) => (lastFirst(m.name).charAt(0) || '#').toUpperCase();
  const btn = (primary, danger) => ({
    minHeight: 40, padding: '0 14px', borderRadius: 10, font: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    border: primary ? 'none' : `1px solid ${danger ? '#FECDCA' : '#c2dacc'}`,
    background: primary ? '#1b4332' : '#fff', color: primary ? '#fff' : danger ? '#B42318' : '#1b4332',
  });
  const stat = (n, label, tone) => (
    <div style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', border: `2px solid ${tone}` }}>
      <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: tone === '#c2dacc' ? '#1b4332' : tone }}>{n}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600, marginTop: 4, color: '#374151' }}>{label}</div>
    </div>
  );

  const memberActions = (m) => {
    const dis = busy === m.player_id;
    if (m.state === 'checked_in') return <button type="button" aria-pressed="true" disabled={dis} onClick={() => act(m, 'undo_check_in')} style={{ ...btn(false), background: '#eaf3ee', border: 'none', minWidth: 130 }} title="Tap to undo">✓ Checked in</button>;
    if (m.state === 'withdrawn') return <button type="button" disabled={dis} onClick={() => act(m, 'undo_withdraw')} style={btn(false)}>Undo withdraw</button>;
    if (m.state === 'no_show') {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
          <button type="button" disabled={dis} onClick={() => act(m, 'check_in')} style={btn(true)}>He's here — check in</button>
          {confirmWithdraw === m.player_id
            ? <button type="button" disabled={dis} onClick={() => act(m, 'withdraw')} style={{ ...btn(true), background: '#B42318' }}>Confirm: withdraw {m.name.split(' ')[0]}</button>
            : <button type="button" disabled={dis} onClick={() => setConfirmWithdraw(m.player_id)} style={btn(false, true)}>Withdraw</button>}
          <button type="button" disabled={dis} onClick={() => act(m, 'undo_no_show')} style={btn(false)}>Undo no-show</button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
        <button type="button" disabled={dis} onClick={() => act(m, 'check_in')} style={{ ...btn(true), minWidth: 130 }}>Check in</button>
        <button type="button" disabled={dis} onClick={() => act(m, 'no_show')} style={btn(false, true)}>Mark no-show</button>
      </div>
    );
  };
  // Fees (slice 2b): a tick box per fee, or a − n + count for an item a golfer may buy several of.
  const fees = data.fees;
  const ft = data.fee_totals;
  const fmt$ = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 })}`;
  const feeControls = (m) => {
    if (!fees || !m.fees) return null;
    const dis = busy === m.player_id;
    const chip = (on) => ({
      minHeight: 34, padding: '0 10px', borderRadius: 8, font: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
      border: on ? '1px solid #95c4a8' : '1px dashed #d1d5db', background: on ? '#eaf3ee' : '#fff', color: on ? '#1b4332' : '#6b7280',
    });
    const step = { width: 30, minHeight: 34, border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: 'pointer', color: '#1b4332' };
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
        {fees.entry && (
          <button type="button" disabled={dis} aria-pressed={!!m.fees.entry} title={m.fees.entry ? 'Recorded as paid — tap to clear' : 'Tap when he pays'}
            onClick={() => act(m, 'fee', { item: 'entry', paid: !m.fees.entry })} style={chip(m.fees.entry)}>
            {m.fees.entry ? '✓' : '☐'} Entry {fmt$(fees.entry.amount)}
          </button>
        )}
        {fees.items.map(it => {
          const n = m.fees[it.id] || 0;
          if (it.max_qty <= 1) {
            return (
              <button key={it.id} type="button" disabled={dis} aria-pressed={n > 0} onClick={() => act(m, 'fee', { item: it.id, qty: n > 0 ? 0 : 1 })} style={chip(n > 0)}>
                {n > 0 ? '✓' : '☐'} {it.label} {fmt$(it.amount)}
              </button>
            );
          }
          return (
            <span key={it.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, ...chip(n > 0), cursor: 'default', padding: '0 4px 0 10px' }}>
              {it.label} {fmt$(it.amount)}
              <button type="button" disabled={dis || n <= 0} aria-label={`One fewer ${it.label}`} onClick={() => act(m, 'fee', { item: it.id, qty: n - 1 })} style={{ ...step, opacity: n <= 0 ? 0.4 : 1 }}>−</button>
              <span style={{ minWidth: 18, textAlign: 'center', color: '#111827' }}>{n}</span>
              <button type="button" disabled={dis || n >= it.max_qty} aria-label={`One more ${it.label}`} onClick={() => act(m, 'fee', { item: it.id, qty: n + 1 })} style={{ ...step, opacity: n >= it.max_qty ? 0.4 : 1 }}>+</button>
            </span>
          );
        })}
      </div>
    );
  };

  const memberNote = (m) => (
    m.state === 'checked_in' ? <span style={{ fontSize: 12.5, color: '#6b7280' }}>{when(m.checked_in_at)}</span>
      : m.state === 'no_show' ? <span style={{ fontSize: 12.5, color: '#B42318', fontWeight: 700 }}>No-show · {m.no_show_by || ''} {when(m.no_show_at)}</span>
      : m.state === 'withdrawn' ? <span style={{ fontSize: 12.5, color: '#B42318', fontWeight: 700 }}>Withdrawn · {m.withdrawn_by || ''} {when(m.withdrawn_at)}</span>
      : <span style={{ fontSize: 12.5, color: '#B54708' }}>not here yet</span>
  );
  const memberRow = (m) => (
    <div key={m.player_id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px', padding: '9px 16px', borderTop: '1px solid #f0f2ef' }}>
      <div style={{ flex: '999 1 200px', minWidth: 0 }}>
        <div style={{ fontSize: 15.5, fontWeight: m.state === 'not_here' || m.state === 'no_show' ? 700 : 500, color: m.state === 'withdrawn' ? '#6b7280' : '#111827', textDecoration: m.state === 'withdrawn' ? 'line-through' : 'none' }}>
          {order === 'az' ? lastFirst(m.name) : m.name}
          {m.captain && <span data-testid="checkin-captain" style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginLeft: 6 }}>(Captain)</span>}
          {order === 'az' && m.where && <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280', marginLeft: 8 }}>{m.where}</span>}
        </div>
        {memberNote(m)}
        {feeControls(m)}
      </div>
      <div style={{ flex: '0 0 auto' }}>{memberActions(m)}</div>
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        {stat(`${c.checked_in} / ${c.confirmed}`, 'Checked in', '#c2dacc')}
        {stat(c.not_here, 'Not here yet', c.not_here ? '#B54708' : '#c2dacc')}
        {stat(c.no_show, 'No-shows to decide', c.no_show ? '#B42318' : '#c2dacc')}
        {c.withdrawn > 0 && stat(c.withdrawn, 'Withdrawn', '#9ca3af')}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button type="button" onClick={() => openRoundPdf(data.teetime_id, 'checkin-sheet')} style={btn(false)}>🖨️ Print check-in sheet</button>
        <button type="button" onClick={() => openRoundPdf(data.teetime_id, 'checkin-sheet', 'split=2')} style={btn(false)}>Two copies (split by last name)</button>
        <span style={{ fontSize: 12.5, color: '#6b7280' }}>For the table: A to Z, anything recorded here prints ticked.</span>
      </div>

      {ft && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: '12px 16px', display: 'grid', gap: 6 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.08em', color: '#1b4332' }}>FEES RECORDED</div>
          {ft.entry && (
            <div style={{ fontSize: 14.5, color: '#111827' }}>
              <strong>Entry fees: {ft.entry.paid} of {ft.entry.of} paid</strong> ({fmt$(ft.entry.collected)})
              {ft.entry.unpaid_names.length > 0
                ? <span style={{ color: '#B54708' }}> · {ft.entry.unpaid_names.length} still to collect{ft.entry.unpaid_names.length <= 8 ? `: ${ft.entry.unpaid_names.join(', ')}` : ''}</span>
                : <span style={{ color: '#166534' }}> · everyone has paid</span>}
            </div>
          )}
          {ft.items.map(it => (
            <div key={it.id} style={{ fontSize: 14.5, color: '#111827' }}>
              <strong>{it.label}:</strong> {it.max_qty > 1 ? `${it.qty} bought by ${it.buyers} ${it.buyers === 1 ? 'golfer' : 'golfers'}` : `${it.qty} ${it.qty === 1 ? 'golfer' : 'golfers'}`} ({fmt$(it.collected)})
            </div>
          ))}
          <div style={{ fontSize: 14.5, color: '#111827' }}>
            <strong>Total recorded: {fmt$((ft.entry ? ft.entry.collected : 0) + ft.items.reduce((n, it) => n + it.collected, 0))}</strong>
            <span style={{ fontSize: 12.5, color: '#6b7280' }}> — what's been ticked here, for balancing the cash box. TTO doesn't take payments.</span>
          </div>
        </div>
      )}

      {needs.filter(t => t.short || t.members.some(m => m.state === 'no_show')).length > 0 && (
        <div style={{ border: '1px solid #FECDCA', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
          <div style={{ padding: '10px 16px', background: '#FEF3F2', fontSize: 12.5, fontWeight: 800, letterSpacing: '0.08em', color: '#B42318' }}>NEEDS A COMMITTEE CALL</div>
          {needs.filter(t => t.short || t.members.some(m => m.state === 'no_show')).map(t => (
            <div key={t.number} style={{ padding: '12px 16px', borderTop: '1px solid #FECDCA', display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 15.5, fontWeight: 800 }}>{slot(t)}{Word} {t.number}{t.short && data.format.team_size ? ` · playing ${t.playing} of ${data.format.team_size}` : ''}</div>
              <div style={{ fontSize: 13.5, color: '#4b5361', lineHeight: 1.5 }}>
                {data.format.best_ball
                  ? 'Withdraw a no-show and a 4-man team gets its blind draw automatically. Or move a player in on Pairings.'
                  : data.format.team
                    ? 'Withdraw a no-show and the team plays short (a player hits two balls). Or move a player in on Pairings.'
                    : 'Withdraw a no-show to take him off the leaderboard and out of the games.'}
                {data.waitlist.length > 0 ? ` Waitlist: ${data.waitlist.map(w => w.name + (w.phone ? ` (${w.phone})` : '')).join(', ')}.` : ''}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button type="button" onClick={() => { appNav.goTo('pairings', { kind: 'pairings-round', teetimeId: data.teetime_id }); if (onNavigate) onNavigate(); }} style={btn(false)}>Move a player in · Pairings ›</button>
                {data.waitlist.length > 0 && onManagePlayers && <button type="button" onClick={() => { onManagePlayers(); if (onNavigate) onNavigate(); }} style={btn(false)}>Waitlist · Players ›</button>}
                {data.format.best_ball && onGames && <button type="button" onClick={() => { onGames(); if (onNavigate) onNavigate(); }} style={btn(false)}>Blind draw · Games ›</button>}
                <button type="button" onClick={() => { appNav.goTo('print', { kind: 'print-round', teetimeId: data.teetime_id }); if (onNavigate) onNavigate(); }} style={btn(false)}>Reprint · Print ›</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.08em', color: '#1b4332' }}>THE FIELD</div>
            {[['az', 'A–Z'], ['team', data.shotgun ? 'By starting hole' : `By ${word}`]].map(([k, label]) => (
              <button key={k} type="button" aria-pressed={order === k} onClick={() => setOrder(k)}
                style={{ minHeight: 32, padding: '0 12px', borderRadius: 999, font: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  border: order === k ? 'none' : '1px solid #c2dacc', background: order === k ? '#1b4332' : '#fff', color: order === k ? '#fff' : '#1b4332' }}>{label}</button>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#4b5361' }}>Find a player
            <input type="search" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Name"
              style={{ minHeight: 40, padding: '0 12px', border: '1px solid #d1d5db', borderRadius: 8, font: 'inherit', width: 180 }} />
          </label>
        </div>
        {order === 'az' && alphaShown.map((m, i) => (
          <React.Fragment key={m.player_id}>
            {(i === 0 || letterOf(alphaShown[i - 1]) !== letterOf(m)) && (
              <div style={{ padding: '8px 16px 2px', fontSize: 12.5, fontWeight: 800, color: '#6b7280', background: '#fafbfa', borderTop: i ? '1px solid #eef0ed' : 'none' }}>{letterOf(m)}</div>
            )}
            {memberRow(m)}
          </React.Fragment>
        ))}
        {order === 'az' && alphaShown.length === 0 && <div style={{ padding: 16, fontSize: 14, color: '#6b7280' }}>No player by that name.</div>}
        {order === 'team' && shown.map(t => (
          <div key={t.number}>
            <div style={{ padding: '10px 16px 4px', fontSize: 12.5, fontWeight: 800, color: t.attention ? '#B54708' : '#6b7280', letterSpacing: '0.04em' }}>{slot(t).toUpperCase()}{Word.toUpperCase()} {t.number}</div>
            {t.members.map(memberRow)}
          </div>
        ))}
        {order === 'team' && data.unpaired.length > 0 && !q && (
          <div>
            <div style={{ padding: '10px 16px 4px', fontSize: 12.5, fontWeight: 800, color: '#B54708' }}>NOT ON A {word.toUpperCase()}</div>
            {data.unpaired.map(memberRow)}
          </div>
        )}
        {order === 'team' && shown.length === 0 && <div style={{ padding: 16, fontSize: 14, color: '#6b7280' }}>No player by that name.</div>}
      </div>
      <div style={{ fontSize: 12.5, color: '#6b7280' }}>A no-show is always someone's call. TTO never marks one on its own, and every action here can be undone.{!fees ? ' Collecting an entry fee or mulligans? Add them under Fees in Event Setup and a tick box appears for each golfer.' : ''}</div>
    </div>
  );
};

// The organizer's tee-time card opens the checklist in a window.
const EventReadyCheckModal = ({ event, teeTime, onClose, onManagePlayers, onEdit, onGames, onPublish, startView = 'setup' }) => (
  <div role="dialog" aria-label="Ready check" onClick={onClose}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 9000, padding: 16, overflowY: 'auto' }}>
    <div onClick={(e) => e.stopPropagation()} style={{ background: '#f9fafb', borderRadius: 14, width: '100%', maxWidth: 860, margin: '24px 0', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#fff', borderRadius: '14px 14px 0 0' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', color: '#1b4332' }}>READY CHECK</div>
          <div style={{ fontSize: 19, fontWeight: 800, color: '#111827' }}>{(event && event.name) || 'Event'}</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" style={{ minWidth: 44, minHeight: 44, borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Close</button>
      </div>
      <div style={{ padding: 18 }}>
        <EventReadyCheck eventId={event && event.id} teetimeId={teeTime && teeTime.id}
          onManagePlayers={onManagePlayers} onEdit={onEdit} onGames={onGames} onPublish={onPublish} onNavigate={onClose} startView={startView} />
      </div>
    </div>
  </div>
);

const TeeTimeCard = ({ teeTime, onDelete, onManagePlayers, onEdit, onPublish, onNotify, onInvite, onGames, onFinalize, gamesEnabled = false, gamesLocked = false, event = null, playerByEmail, isReadOnly = false, onReadOnlyClick, settings, onEventSetup }) => {
  const [showTTID, setShowTTID] = useState(false);
  const [showReady, setShowReady] = useState(false);   // Event Ready Check (Oct 4, 2026)
  const { addToast: cardToast } = useToast();
  // Slice 3: "3 to fix" on the button — upcoming events only (a past event's check is history).
  const readyFixCount = useReadyStopCount(event && event.id, !!(event && event.id) && !showReady && utils.getDateOnly(teeTime.date) >= utils.getTodayDate());
  const tvButton = event && event.public_slug ? (
    <Button variant="outline" onClick={() => openEventTv(event, cardToast)} style={{ fontSize: 12 }} title="Open this event's leaderboard in TV view (for the clubhouse screen)" data-testid="card-tv-button">📺 TV</Button>
  ) : null;
  const readyButton = event && event.id ? (
    <Button variant="outline" onClick={() => setShowReady(true)} style={{ fontSize: 12, color: '#1b4332', borderColor: '#c2dacc' }} title="Is the event ready to roll? Setup, field, teams, course and event-day checks">✅ Ready check{fixBadge(readyFixCount)}</Button>
  ) : null;
  const slotsText = `${utils.safePlayerCount(teeTime.players)}/${teeTime.slots}`;
  const isFull = utils.safePlayerCount(teeTime.players) >= utils.toInt(teeTime.slots);
  const waitlistCount = Array.isArray(teeTime.waitlist) ? teeTime.waitlist.length : 0;
  const isDraft = teeTime.status === "Draft";
  
  // Helper to wrap actions and block if read-only
  const handleAction = (action) => {
    console.log('=== handleAction called ===', { isReadOnly, action: !!action });
    if (isReadOnly) {
      console.log('Blocked by isReadOnly');
      onReadOnlyClick?.();
      return;
    }
    console.log('Calling action...');
    action?.();
  };
  
  // Convert emails to names - defensive to handle both emails and objects
  const getPlayerName = (playerData) => {
    if (typeof playerData === 'string') {
      return playerByEmail?.get(playerData) || playerData;
    } else if (playerData && typeof playerData === 'object') {
      return playerData.name || playerData.email || 'Unknown';
    }
    return 'Unknown';
  };
  
  // FIX: Keep full player objects to preserve photoUrl
  // Sort by addedAt (RSVP time) - earliest first
  const players = [...(teeTime.players || [])].sort((a, b) => {
    const dateA = a?.addedAt ? new Date(a.addedAt) : new Date(0);
    const dateB = b?.addedAt ? new Date(b.addedAt) : new Date(0);
    return dateA - dateB;
  });

  // §6.7 Organizer Dashboard Pending Badges (April 2026):
  // pending_players is a separate array from `players` so the dashboard's
  // slot-count math stays confirmed-only (unchanged). These render as
  // grayed-italic pills with a ⏳ clock icon, a "Pending" badge, and a
  // title-tooltip showing who invited them and when the invite expires.
  const pendingPlayers = [...(teeTime.pending_players || [])].sort((a, b) => {
    const dateA = a?.addedAt ? new Date(a.addedAt) : new Date(0);
    const dateB = b?.addedAt ? new Date(b.addedAt) : new Date(0);
    return dateA - dateB;
  });

  return (
    <Card style={{ border: isDraft ? "2px dashed #f59e0b" : "1px solid #e5e7eb" }}>
      {showReady && (
        <EventReadyCheckModal event={event} teeTime={teeTime} onClose={() => setShowReady(false)}
          onManagePlayers={() => handleAction(onManagePlayers)} onEdit={() => handleAction(onEdit)}
          onGames={() => handleAction(onGames)} onPublish={() => handleAction(onPublish)} />
      )}
      <div style={{ display: "grid", gap: 10 }}>
        {event && event.name && (
          // Oct 12, 2026: "Event setup ▸" beside the event name opens Event Setup (and its
          // Event page section) straight from the card, instead of Edit → Edit event.
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div
            title={event.description || event.name}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              alignSelf: "flex-start",
              background: "#dcfce7",
              color: "#166534",
              padding: "4px 10px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600
            }}
          >
            📅 {event.name}{event.scoring_mode === "team" ? <span style={{ fontWeight: 500, opacity: 0.85 }}>· 👥 team score</span> : event.postable_to_ghin === false ? <span style={{ fontWeight: 500, opacity: 0.85 }}>· no GHIN posting</span> : null}
          </div>
          {onEventSetup && (
            <button type="button" data-testid="card-event-setup" onClick={() => handleAction(onEventSetup)} style={{ background: "none", border: "none", color: "#1f6b45", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "6px 4px", minHeight: 32 }}>
              Event setup ▸
            </button>
          )}
          </div>
        )}
        {!(event && event.name) && teeTime.title && (
          <div
            title={teeTime.title}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              alignSelf: "flex-start",
              background: "#f3f4f6",
              color: "#374151",
              padding: "4px 10px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600
            }}
          >
            🏷️ {teeTime.title}
          </div>
        )}
        {isDraft && (
          <div style={{ 
            background: "#fef3c7", 
            color: "#92400e", 
            padding: "4px 8px", 
            borderRadius: 6, 
            fontSize: 11, 
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
            📝 Not Published
          </div>
        )}
        {teeTime.private && (
          <div style={{ 
            background: "#fce7f3", 
            color: "#9f1239", 
            padding: "4px 8px", 
            borderRadius: 6, 
            fontSize: 11, 
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
            🔒 Private Tee Time
          </div>
        )}
        {teeTime.locked && (
          <div style={{ 
            background: "#fee2e2", 
            color: "#991b1b", 
            padding: "4px 8px", 
            borderRadius: 6, 
            fontSize: 11, 
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
            🚫 Locked - No New RSVPs
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{teeTime.course}</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>{utils.formatDate(teeTime.date)} • {utils.formatTime(teeTime.time)}</div>
            {/* Weather display */}
            {teeTime.weather_display && (
              <div style={{ fontSize: 12, color: "#059669", marginTop: 6, background: "#d1fae5", padding: "4px 8px", borderRadius: 6, display: "inline-block", whiteSpace: "pre-line" }}>
                {teeTime.weather_display}
              </div>
            )}
            {/* Sunrise/Sunset display */}
            {teeTime.weather_data && (settings?.show_sunrise || settings?.show_sunset) && (() => {
              try {
                const wd = typeof teeTime.weather_data === 'string' ? JSON.parse(teeTime.weather_data) : teeTime.weather_data;
                if (!wd?.sunrise && !wd?.sunset) return null;
                return (
                  <div style={{ fontSize: 11, color: '#92400e', marginTop: 4, display: 'flex', gap: 12 }}>
                    {settings?.show_sunrise && wd?.sunrise && (
                      <span style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>🌅 Sunrise: {wd.sunrise}</span>
                    )}
                    {settings?.show_sunset && wd?.sunset && (
                      <span style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>🌇 Sunset: {wd.sunset}</span>
                    )}
                  </div>
                );
              } catch { return null; }
            })()}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {showTTID ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>TTID: {teeTime.id}</span>
                <button
                  onClick={() => setShowTTID(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#2563eb",
                    cursor: "pointer",
                    fontSize: 11,
                    padding: 0,
                    textDecoration: "underline"
                  }}
                >
                  Hide
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowTTID(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#2563eb",
                  cursor: "pointer",
                  fontSize: 11,
                  padding: 0,
                  textDecoration: "underline"
                }}
              >
                Show TTID
              </button>
            )}
            <Badge style={{ background: isFull ? "#dcfce7" : "#fef3c7", color: isFull ? "#15803d" : "#92400e" }}>{slotsText} Slots</Badge>
          </div>
        </div>
        {waitlistCount > 0 && <div style={{ fontSize: 12, color: "#b91c1c", background: "#fee2e2", padding: "4px 8px", borderRadius: 6 }}>Waitlist: {waitlistCount} player{waitlistCount > 1 ? "s" : ""}</div>}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {players.length > 0 ? (players.map((player, i) => {
            const playerName = typeof player === 'string' ? player : (player.name || player.email || 'Unknown');
            const photoUrl = typeof player === 'object' ? player.photoUrl : null;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 999, padding: "4px 10px 4px 4px", fontSize: 13 }}>
                <PlayerAvatar name={playerName} photoUrl={photoUrl} />
                <span>{playerName}</span>
              </div>
            );
          })) : (<div style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>No players added yet</div>)}
          {/* §6.7: Pending nomination pills — render after confirmed players.
              Clock icon + gray italic name + Pending badge + tooltip. */}
          {pendingPlayers.map((pp, i) => {
            const captainName = pp.nominated_by_name || 'the captain';
            let expiresText = 'soon';
            if (pp.nomination_expires_at) {
              try {
                const exp = new Date(pp.nomination_expires_at);
                expiresText = exp.toLocaleString('en-US', {
                  month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit'
                });
              } catch (e) { /* leave as 'soon' */ }
            }
            const tooltip = `Invited by ${captainName} — awaiting confirmation. Expires ${expiresText}.`;
            return (
              <div
                key={`pending-${i}`}
                title={tooltip}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#f9fafb",
                  border: "1px dashed #d1d5db",
                  borderRadius: 999,
                  padding: "4px 8px 4px 8px",
                  fontSize: 13,
                  color: "#6b7280",
                  fontStyle: "italic"
                }}
              >
                <span aria-label="Pending invitation" style={{ fontSize: 12 }}>⏳</span>
                <span>{pp.name || pp.email || 'Unknown'}</span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  background: "#e5e7eb",
                  color: "#4b5563",
                  padding: "2px 6px",
                  borderRadius: 10,
                  fontStyle: "normal"
                }}>
                  Pending
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {isDraft ? (
            <>
              <Button onClick={() => handleAction(onManagePlayers)} disabled={isReadOnly} style={{ flex: 1, fontSize: 12, background: isReadOnly ? "#9ca3af" : "#2d6a4f", color: "#fff", cursor: isReadOnly ? "not-allowed" : "pointer" }}>Manage Players</Button>
              {teeTime.private && (
                <div style={{ 
                  fontSize: 12, 
                  background: "#fce7f3", 
                  color: "#9f1239", 
                  padding: "8px 16px", 
                  borderRadius: 8, 
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  border: "2px solid #f9a8d4"
                }}>
                  🔒 Private
                </div>
              )}
              <Button onClick={() => handleAction(onPublish)} disabled={isReadOnly} style={{ fontSize: 12, background: isReadOnly ? "#9ca3af" : "#f59e0b", color: "#fff", minWidth: 140, cursor: isReadOnly ? "not-allowed" : "pointer" }}>📣 Publish</Button>
              {gamesEnabled && (
                <Button variant="outline" onClick={() => handleAction(onGames)} disabled={isReadOnly} style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer", color: "#166534", borderColor: "#bbf7d0", background: "#f0fdf4" }}>🎮 Games</Button>
              )}
              {!gamesEnabled && gamesLocked && (
                <Button variant="outline" onClick={() => triggerUpgradeModal({ feature: 'games', requiredTier: 'plus' })} title="Games come with the Plus plan and above" style={{ fontSize: 12, color: "#92400e", borderColor: "#fcd34d", background: "#fffbeb" }}>🎮 Games 🔒</Button>
              )}
              {tvButton}
              {readyButton}
              <EventWelcomeButton event={event} isReadOnly={isReadOnly} onReadOnlyClick={onReadOnlyClick} />
              <EventTeamsSlot event={event} isReadOnly={isReadOnly} onReadOnlyClick={onReadOnlyClick} />
              <SocialEventTeeTimeLink teetimeId={teeTime.id} eventId={event ? event.id : null} />
              <Button variant="outline" onClick={() => handleAction(onInvite)} disabled={isReadOnly} style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer" }}>🎟️ Invite</Button>
              <Button variant="outline" onClick={() => handleAction(onEdit)} disabled={isReadOnly} style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer" }}>✏️ Edit</Button>
              {onDelete && <MoreActionsMenu items={[{ label: '🗑️ Delete tee time', danger: true, onClick: () => handleAction(onDelete) }]} disabled={isReadOnly} onDisabledClick={onReadOnlyClick} style={{ marginLeft: 'auto' }} />}
            </>
          ) : (
            <>
              <Button onClick={() => handleAction(onManagePlayers)} disabled={isReadOnly} style={{ flex: 1, fontSize: 12, background: isReadOnly ? "#9ca3af" : "#2d6a4f", color: "#fff", cursor: isReadOnly ? "not-allowed" : "pointer" }}>Manage Players</Button>
              {teeTime.private && (
                <div style={{ 
                  fontSize: 12, 
                  background: "#fce7f3", 
                  color: "#9f1239", 
                  padding: "8px 16px", 
                  borderRadius: 8, 
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  border: "2px solid #f9a8d4"
                }}>
                  🔒 Private
                </div>
              )}
              <Button variant="outline" onClick={() => handleAction(onNotify)} disabled={isReadOnly} style={{ fontSize: 12, color: "#1f4d36", borderColor: "#b9c9bf", background: "#fff", cursor: isReadOnly ? "not-allowed" : "pointer" }}>📧 Notify Players</Button>
              {gamesEnabled && (
                <Button variant="outline" onClick={() => handleAction(onGames)} disabled={isReadOnly} style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer", color: "#166534", borderColor: "#bbf7d0", background: "#f0fdf4" }}>🎮 Games</Button>
              )}
              {!gamesEnabled && gamesLocked && (
                <Button variant="outline" onClick={() => triggerUpgradeModal({ feature: 'games', requiredTier: 'plus' })} title="Games come with the Plus plan and above" style={{ fontSize: 12, color: "#92400e", borderColor: "#fcd34d", background: "#fffbeb" }}>🎮 Games 🔒</Button>
              )}
              {tvButton}
              {readyButton}
              <EventWelcomeButton event={event} isReadOnly={isReadOnly} onReadOnlyClick={onReadOnlyClick} />
              <EventTeamsSlot event={event} isReadOnly={isReadOnly} onReadOnlyClick={onReadOnlyClick} />
              <SocialEventTeeTimeLink teetimeId={teeTime.id} eventId={event ? event.id : null} />
              <Button variant="outline" onClick={() => handleAction(onInvite)} disabled={isReadOnly} style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer" }}>🎟️ Invite</Button>
              <Button variant="outline" onClick={() => handleAction(onEdit)} disabled={isReadOnly} style={{ fontSize: 12, opacity: isReadOnly ? 0.5 : 1, cursor: isReadOnly ? "not-allowed" : "pointer" }}>✏️ Edit</Button>
              {onDelete && <MoreActionsMenu items={[{ label: '🗑️ Delete tee time', danger: true, onClick: () => handleAction(onDelete) }]} disabled={isReadOnly} onDisabledClick={onReadOnlyClick} style={{ marginLeft: 'auto' }} />}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

// ============================================================================
// UPGRADE MODAL — Phase 2 of Free + Starter Tier Rollout (May 2026)
// ============================================================================
// Cross-cutting modal shown when a Free or Starter org tries to use a paid
// feature. Triggered via window CustomEvent 'showUpgradeModal' from anywhere
// in the app — matches the existing 'navigateToTab' event-bus pattern.
//
// To trigger:
//   triggerUpgradeModal({ feature: 'sms', requiredTier: 'plus' })
// or directly:
//   window.dispatchEvent(new CustomEvent('showUpgradeModal', { detail: { feature, requiredTier } }))
//
// To use in an API error catch block:
//   handleFeatureUpgradeRequired(errorData)
//   — returns true if it handled an upgrade-required error (you should stop)
//   — returns false otherwise (continue with normal error handling)
// ============================================================================

// Map of feature key → human-readable description shown in the modal
const FEATURE_DISPLAY_INFO = {
  sms:               { name: 'Text Messages',         icon: '📱', description: 'Send text messages to players for reminders, custom messages, RSVPs, cancellations, and waitlist updates.' },
  ghin_sync:         { name: 'GHIN Auto-Sync',        icon: '⛳', description: 'Automatically sync players\' USGA handicaps from GHIN — no more manual updates.' },
  pairings:          { name: 'Pairings Generator',    icon: '🎯', description: 'Auto-generate balanced pairings by handicap, and run shotgun starts with the Shotgun Wizard.' },
  scoring:           { name: 'Scoring',                icon: '⛳', description: 'Enter scores, review and revise scorecards, and run the full-field Scorecard Report. Your group\'s scoring history accumulates over time.' },
  multi_organizer:   { name: 'Multi-Organizer',       icon: '👥', description: 'Invite co-organizers to share dashboard access and tee time management.' },
  multi_groups:      { name: 'Multiple Groups',       icon: '🏌️', description: 'Run several leagues or buddy groups from one account.' },
  custom_templates:  { name: 'Custom Message Templates', icon: '✏️', description: 'Create your own email and text message templates beyond the defaults.' },
  course_handicap:   { name: 'Course Handicap (WHS)', icon: '📊', description: 'Calculate USGA World Handicap System course handicaps for every player on every course.' },
  games:             { name: 'Games',                 icon: '🎮', description: 'Skins, Nassau, Sixes, Stableford, Dots and more, scored live on your players\' phones, with the handicap math done and every player\'s settlement worked out for you.' },
};

// Map of tier key → display info shown in the modal
// Phase 4 tier rename COMPLETE (commit d3a9afd). Backend internal keys now match
// customer-facing keys: 'starter' = $4.99 tier, 'plus' = $9.99 tier. The backend
// sends requiredTier as the customer-facing key directly (e.g. 'plus' for SMS/GHIN,
// 'starter' for pairings/scoring), which maps to the entries below.
// 'professional' is included so a requiredTier:'professional' never falls back to the
// Plus card (wrong name/price). Today no FEATURE gate resolves to Professional (it's a
// capacity tier granting nothing Plus lacks), but keeping the map complete prevents a
// silent wrong-price modal if that ever changes. The lookup falls back to plus only as
// a last resort for an unrecognized key.
const TIER_DISPLAY_INFO = {
  starter:      { name: 'Starter',      monthly: '$4.99/mo',  annual: '$47.90/yr',  pitch: 'Ad-free experience plus pairings, scoring, and the full handicap display.' },
  plus:         { name: 'Plus',         monthly: '$9.99/mo',  annual: '$95.90/yr',  pitch: 'Everything in Starter plus games, text messages and GHIN auto-sync.' },
  professional: { name: 'Professional', monthly: '$14.99/mo', annual: '$143.90/yr', pitch: 'Everything in Plus, with room for up to 100 regular players.' },
  league:       { name: 'League',       monthly: '$24.99/mo', annual: '$239.90/yr', pitch: 'Built for formal leagues — up to 250 regular players.' },
  club:         { name: 'Club',         monthly: 'Contact us', annual: 'Contact us', pitch: 'For proshops, course operators, and institutional buyers.' },
};

// Programmatic trigger from anywhere in the app
function triggerUpgradeModal({ feature, requiredTier, currentTier }) {
  window.dispatchEvent(new CustomEvent('showUpgradeModal', {
    detail: { feature, requiredTier, currentTier }
  }));
}

/**
 * Helper for API error catch blocks. If the error indicates an upgrade is needed,
 * fires the upgrade modal and returns true (caller should stop). Otherwise
 * returns false so the caller can continue with normal error handling.
 *
 * Usage:
 *   catch (error) {
 *     const errorData = error.responseData || {};
 *     if (handleFeatureUpgradeRequired(errorData)) return; // Modal will show
 *     addToast(error.message, 'error');                    // Otherwise normal toast
 *   }
 */
function handleFeatureUpgradeRequired(errorData) {
  if (errorData && errorData.code === 'FEATURE_REQUIRES_UPGRADE') {
    triggerUpgradeModal({
      feature: errorData.feature,
      requiredTier: errorData.requiredTier || 'plus',
      currentTier: errorData.currentTier
    });
    return true;
  }
  return false;
}

const UpgradeModal = ({ isOpen, onClose, feature, requiredTier, currentTier }) => {
  if (!isOpen) return null;

  const featureInfo = FEATURE_DISPLAY_INFO[feature] || { name: feature, icon: '✨', description: 'This is a paid feature.' };
  const tierInfo = TIER_DISPLAY_INFO[requiredTier] || TIER_DISPLAY_INFO.plus;

  const handleUpgrade = () => {
    // Navigate to the Settings tab where Subscription & Billing lives.
    // Uses URL ?tab= param which the top-level useEffect at App mount reads via
    // `if (tabParam) setTab(tabParam)` (App.js line ~30049). Full reload, but
    // reliable — the alternative (CustomEvent 'navigateToTab') has no listener
    // in the current code, so dispatching it would do nothing.
    onClose();
    // Sept 30, 2026: &upgrade=1 opens Subscription & Billing and scrolls to the plans.
    window.location.href = window.location.pathname + '?tab=settings&upgrade=1';
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 16,
        width: '100%', maxWidth: 480,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px',
          background: 'linear-gradient(135deg, #166534 0%, #22c55e 100%)',
          color: '#fff'
        }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>{featureInfo.icon}</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
            Unlock {featureInfo.name}
          </h3>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: '0 0 16px' }}>
            {featureInfo.description}
          </p>

          <div style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: 10,
            padding: 16,
            marginBottom: 20
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#166534' }}>
                {tierInfo.name} tier
              </span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#166534' }}>
                {tierInfo.monthly}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#15803d', marginBottom: 8 }}>
              or {tierInfo.annual} (save with annual billing)
            </div>
            <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
              {tierInfo.pitch}
            </div>
          </div>

          {currentTier && (
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16, textAlign: 'center' }}>
              Your current plan: <strong style={{ textTransform: 'capitalize' }}>{currentTier}</strong>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Not now
            </button>
            <button
              onClick={handleUpgrade}
              style={{
                flex: 1.5,
                padding: '12px 16px',
                border: 'none',
                background: '#166534',
                color: '#fff',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              View upgrade options →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// EventHome — the event desk's first page (Sept 17, 2026)
// Docs: claude/Next_Session_Handoff_Sep17.md, claude/Event_Desk_Plan_v1.md
//
// Dennis, Sept 17: with one event per desk the Show filters and the rest of the
// toolbar have no use there. "Make this page easy to use, simple (choices
// obvious), yet very professional UI. Hopefully by introducing a club pro to
// this page, it could cause them to want to explore the full TTO product."
//
// So the desk's landing tab stops being the org's tee-time list and becomes this:
// the event, its live status, and large plain-language tiles for the jobs someone
// actually does on event morning. The organizer dashboard is untouched — this
// renders only when deskSession is set, and the tiles call the SAME handlers the
// tee-time card offers, so nothing here is a second implementation of anything.
//
// The promo link at the foot goes to teetimeorganizer.net for now. Dennis, Sept 17:
// "when we have a club level product that serves MGA, LGA, Couples, Seniors
// associations, we should take them to whatever landing page exists for that
// product." That is why the URL is one named constant.
// ============================================================================
const DESK_PROMO_URL = 'https://teetimeorganizer.net';
const DESK_PROMO_HREF = `${DESK_PROMO_URL}?utm_source=event_desk`;

const deskFmtDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(String(dateStr).split('T')[0] + 'T12:00:00');
  return isNaN(d.getTime()) ? String(dateStr) : d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};
const deskFmtTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  const hh = parseInt(h, 10);
  if (isNaN(hh)) return String(t);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${m || '00'} ${ampm}`;
};
const deskFmtStamp = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

// Single-weight stroke icons rather than emoji. Emoji arrive at whatever weight and
// colour the platform feels like, which is the detail that makes a page read as an
// internal tool; these are the icons from the approved mockup (Sept 17, 2026).
const DeskIcon = ({ name }) => {
  const shapes = {
    players: <g><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9" /></g>,
    pairings: <g><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></g>,
    handicap: <g><path d="M12 3v18" /><path d="M5 7h14" /><path d="M5 7 2 14h6z" /><path d="M19 7l-3 7h6z" /></g>,
    print: <g><path d="M6 9V3h12v6" /><rect x="3" y="9" width="18" height="8" rx="2" /><path d="M6 17h12v4H6z" /></g>,
    scores: <g><rect x="4" y="4" width="16" height="16" rx="1.5" /><path d="M4 9h16" /><path d="M9 9v11" /></g>,
    tv: <g><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" /></g>,
    message: <g><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></g>,
    checkin: <g><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V2.5h6V4" /><path d="m9 13 2.2 2.2L15.5 11" /></g>,
    trophy: <g><path d="M8 21h8" /><path d="M12 17v4" /><path d="M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M17 5h3v2a3 3 0 0 1-3 3" /><path d="M7 5H4v2a3 3 0 0 0 3 3" /></g>,
  };
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1b4332" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {shapes[name] || null}
    </svg>
  );
};

// Web addresses in an organizer's note become links (Oct 12, 2026 — oct12j, Dennis).
// Only http(s):// and www. addresses are linked, so ordinary words with a dot never are.
// Trailing punctuation (a full stop after the address) stays text. Opens in a new tab.
const NOTE_LINK_RE = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;
const LinkifiedText = ({ text }) => {
  const s = String(text || '');
  const out = [];
  let last = 0;
  let m;
  NOTE_LINK_RE.lastIndex = 0;
  while ((m = NOTE_LINK_RE.exec(s)) !== null) {
    let url = m[0];
    const trail = url.match(/[.,;:!?)\]]+$/);
    if (trail) url = url.slice(0, url.length - trail[0].length);
    if (!url || /^(https?:\/\/|www\.)$/i.test(url)) continue;
    if (m.index > last) out.push(s.slice(last, m.index));
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    out.push(
      <a key={m.index} href={href} target="_blank" rel="noopener noreferrer"
        style={{ color: '#1d4ed8', textDecoration: 'underline', wordBreak: 'break-all' }}>{url}</a>
    );
    last = m.index + url.length;
  }
  if (last < s.length) out.push(s.slice(last));
  return <>{out}</>;
};

// 📋 Note on a tee time row when the organizer left a note (Oct 12, 2026 — oct12k).
// Tapping it opens View details, where the note is shown.
const hasNote = (tt) => !!(tt && tt.notes && String(tt.notes).trim());
// Oct 12, 2026 (oct12l, Dennis): a plain icon with the word Note as a link beside it.
const NoteIcon = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title="Note from your organizer — tap to read it"
    aria-label="Note from your organizer — tap to read it"
    data-testid="teetime-note-icon"
    style={{ background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
  >
    <span aria-hidden="true">📋</span>
    <span style={{ color: '#1d4ed8', textDecoration: 'underline', fontWeight: 600 }}>Note</span>
  </button>
);

const EventHomeTile = ({ title, blurb, status, statusColor, accent, onClick, icon, actions }) => {
  // A tile has to look pressable. Inline styles can't do :hover, so the lift is state.
  const [hot, setHot] = useState(false);
  // Oct 12, 2026 (oct12h): a tile can carry its own buttons instead of being one big
  // button (Leaderboard & TV: phone board and TV screen). It is a plain box then, so
  // no button sits inside another.
  if (actions && actions.length) {
    return (
      <div
        style={{
          textAlign: 'left',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 14,
          padding: 18,
          minHeight: 150,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        }}
      >
        <DeskIcon name={icon} />
        <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</span>
        <span style={{ fontSize: 13.5, color: '#6b7280', lineHeight: 1.45 }}>{blurb}</span>
        <div style={{ marginTop: 'auto', paddingTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={a.onClick}
              title={a.title}
              data-testid={a.testId}
              style={{
                flex: '1 1 auto',
                background: '#fff',
                border: '1px solid #c2dacc',
                borderRadius: 10,
                padding: '9px 12px',
                minHeight: 42,
                fontSize: 15,
                fontWeight: 700,
                color: '#1b4332',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)}
      onBlur={() => setHot(false)}
      style={{
        textAlign: 'left',
        background: '#fff',
        border: accent ? '2px solid #c2dacc' : `1px solid ${hot ? '#c2dacc' : '#e5e7eb'}`,
        borderRadius: 14,
        padding: accent ? 17 : 18,
        minHeight: 150,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        boxShadow: hot ? '0 4px 14px rgba(27, 67, 50, 0.13)' : '0 1px 2px rgba(16, 24, 40, 0.04)',
        transform: hot ? 'translateY(-1px)' : 'none',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
      }}
    >
      <DeskIcon name={icon} />
      <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</span>
      <span style={{ fontSize: 13.5, color: '#6b7280', lineHeight: 1.45 }}>{blurb}</span>
      <span style={{ marginTop: 'auto', paddingTop: 4, fontSize: 15, fontWeight: 700, color: statusColor || '#111827' }}>{status}</span>
    </button>
  );
};

const EventHome = ({ teeTime, event, onManagePlayers, onEdit, onNotify, onGames, onRefresh, gamesEnabled, onEventSetup }) => {
  const { addToast } = useToast();
  const deskSession = useContext(DeskContext);
  const appNav = useContext(AppNavContext);
  const [scoring, setScoring] = useState(null);     // /scoring/teetimes row for this round
  const [adjusted, setAdjusted] = useState(null);   // count of event handicap corrections
  const [showHow, setShowHow] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [qr, setQr] = useState(null);            // { shared, url, address, qr_svg }
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sitePage, setSitePage] = useState(null);   // the event's published web page (oct12j) — { page_url } or null
  const [siteCopied, setSiteCopied] = useState(false);
  const [blinds, setBlinds] = useState([]);        // Best Ball blind draw: [{ team_number, player_name, band, replaces_name }]
  const [reloadTick, setReloadTick] = useState(0);
  // Check-in tile (Oct 4, 2026): live counts from the check-in board; the tile opens it in a window.
  const [checkin, setCheckin] = useState(null);
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinTick, setCheckinTick] = useState(0);
  useDeskBack(showCheckin, () => { setShowCheckin(false); setCheckinTick(t => t + 1); });   // Back closes it (Oct 10, 2026)

  const teetimeId = teeTime ? teeTime.id : null;
  const eventIdForCheckin = event && event.id ? event.id : null;

  useEffect(() => {
    if (!eventIdForCheckin) return;
    let cancelled = false;
    (async () => {
      try {
        const h = {};
        const t = getAuthToken();
        if (t) h['Authorization'] = `Bearer ${t}`;
        const r = await fetch(`${API_URL}/events/${eventIdForCheckin}/checkin`, { credentials: 'include', headers: h });
        if (r.ok) { const d = await r.json(); if (!cancelled) setCheckin(d.checkin || null); }
      } catch (e) { /* the tile shows its plain line */ }
    })();
    return () => { cancelled = true; };
  }, [eventIdForCheckin, reloadTick, checkinTick]);

  // The event's website (Oct 12, 2026 — oct12j, Dennis): the public event page, when the
  // organizer has published it. Read from the same public endpoint the page itself uses,
  // so the desk shows exactly what golfers see; not published → no card.
  const pageSlug = event && event.public_slug ? event.public_slug : null;
  useEffect(() => {
    if (!pageSlug) { setSitePage(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/public/event-page/${encodeURIComponent(pageSlug)}?t=${Date.now()}`, { cache: 'no-store' });
        const d = r.ok ? await r.json() : null;
        if (!cancelled) setSitePage(d && d.published && d.page_url ? { page_url: d.page_url } : null);
      } catch (e) { if (!cancelled) setSitePage(null); }
    })();
    return () => { cancelled = true; };
  }, [pageSlug, reloadTick]);
  const copySiteLink = async () => {
    if (!sitePage) return;
    try {
      await navigator.clipboard.writeText(sitePage.page_url);
      setSiteCopied(true);
      setTimeout(() => setSiteCopied(false), 2000);
    } catch (e) {
      addToast('Copy failed — the address is shown on the card', 'error');
    }
  };

  useEffect(() => {
    if (!teetimeId) return;
    let cancelled = false;
    const headers = () => {
      const h = {};
      const t = getAuthToken();
      if (t) h['Authorization'] = `Bearer ${t}`;
      return h;
    };
    const load = async () => {
      // Both of these sit behind requireScoring. An org without Scoring simply gets
      // tiles with no live status rather than an error — the page still works.
      try {
        const r = await fetch(`${API_URL}/scoring/teetimes`, { credentials: 'include', headers: headers() });
        if (r.ok) {
          const rows = await r.json();
          const row = Array.isArray(rows) ? rows.find(x => String(x.id) === String(teetimeId)) : null;
          if (!cancelled) setScoring(row || null);
        }
      } catch (e) { /* status is optional */ }
      try {
        const r = await fetch(`${API_URL}/scoring/teetimes/${teetimeId}/event-handicaps`, { credentials: 'include', headers: headers() });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setAdjusted((d.players || []).filter(p => p.adjusted).length);
        }
      } catch (e) { /* status is optional */ }
      try {
        const r = await fetch(`${API_URL}/scoring/teetimes/${teetimeId}/leaderboard-qr`, { credentials: 'include', headers: headers() });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setQr(d);
        }
      } catch (e) { /* the card simply does not render */ }
      // Best Ball blind draw (Sept 27, 2026): shown under its team in Today's field.
      try {
        const r = await fetch(`${API_URL}/scoring/teetimes/${teetimeId}/blinds`, { credentials: 'include', headers: headers() });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setBlinds(Array.isArray(d.blinds) ? d.blinds : []);
        }
      } catch (e) { /* no blind line */ }
    };
    load();
    return () => { cancelled = true; };
  }, [teetimeId, reloadTick]);

  if (!teeTime) {
    return (
      <Card style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⛳</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>This event has no tee time yet.</div>
        <div style={{ fontSize: 14, color: '#6b7280', marginTop: 6 }}>Ask the organizer to add one, then refresh.</div>
        <div style={{ marginTop: 16 }}>
          <Button type="button" variant="outline" onClick={onRefresh}>🔄 Refresh</Button>
        </div>
      </Card>
    );
  }

  const players = Array.isArray(teeTime.players) ? teeTime.players : [];
  const waitlist = Array.isArray(teeTime.waitlist) ? teeTime.waitlist : [];
  const slots = Number(teeTime.slots) || null;
  const isTeam = (teeTime.scoring_mode || 'individual') === 'team';

  // Teams / groups from the saved pairings. Unpaired players fall into one bucket.
  const byGroup = {};
  const unpaired = [];
  for (const p of players) {
    const g = p.pairingGroup;
    if (g == null) { unpaired.push(p); continue; }
    if (!byGroup[g]) byGroup[g] = [];
    byGroup[g].push(p);
  }
  const groupNumbers = Object.keys(byGroup).map(Number).sort((a, b) => a - b);
  const hasPairings = groupNumbers.length > 0;

  const pairingsAt = deskFmtStamp(teeTime.pairings_saved_at);
  const cartSignsAt = deskFmtStamp(teeTime.cart_signs_printed_at);
  const cardsAt = deskFmtStamp(teeTime.scorecards_printed_at);
  const shared = scoring ? !!scoring.leaderboard_shared : false;
  const publicSlug = scoring ? scoring.event_public_slug : null;
  const scoresIn = scoring ? Number(isTeam ? scoring.team_scores_entered : scoring.scores_entered) || 0 : 0;

  // team_scores / round_scores counts are ROWS — one per hole. "396 team scores in"
  // means nothing to a pro standing at the desk, so say it against the holes the
  // round actually expects: competitors x holes in the competition set.
  const holesPerCard = (teeTime.competition_hole_set && teeTime.competition_hole_set !== 'all18') ? 9 : 18;
  const cardCount = isTeam ? groupNumbers.length : players.length;
  const holesExpected = cardCount * holesPerCard;
  const scoresStatus = !scoring
    ? 'Open the Scoring tab'
    : scoresIn === 0
      ? 'No scores yet'
      : (holesExpected > 0 && scoresIn >= holesExpected)
        ? 'All holes in'
        : holesExpected > 0
          ? `${scoresIn} of ${holesExpected} holes in`
          : `${scoresIn} holes in`;

  const chip = (label, strong, tone) => {
    const tones = {
      plain: { background: '#fff', border: '1px solid #e5e7eb', color: '#111827' },
      warn: { background: '#fef3c7', border: '1px solid #e2cb9e', color: '#6b4100' },
      good: { background: '#eaf3ee', border: '1px solid #c2dacc', color: '#1b4332' },
    };
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px', borderRadius: 999, fontSize: 15, ...(tones[tone] || tones.plain) }}>
        {label ? <span style={{ opacity: 0.75 }}>{label}</span> : null}
        <strong>{strong}</strong>
      </span>
    );
  };

  // The board is shared when an event is created (eventRoutes), so this is the rare
  // path — someone turned it off on the Scoring tab. Offer it here rather than
  // sending the pro to another tab to find the setting.
  const shareBoard = async () => {
    if (sharing || !teetimeId) return;
    setSharing(true);
    try {
      const h = { 'Content-Type': 'application/json' };
      const t = getAuthToken();
      if (t) h['Authorization'] = `Bearer ${t}`;
      const r = await fetch(`${API_URL}/scoring/teetimes/${teetimeId}/share-leaderboard`, { method: 'POST', credentials: 'include', headers: h });
      if (!r.ok) { addToast('Could not share the leaderboard', 'error'); return; }
      setQr(prev => (prev ? { ...prev, shared: true } : prev));
      setScoring(prev => (prev ? { ...prev, leaderboard_shared: true } : prev));
      addToast('Leaderboard shared');
    } catch (e) {
      addToast('Network error — the leaderboard was not shared', 'error');
    } finally {
      setSharing(false);
    }
  };

  const copyBoardLink = async () => {
    if (!qr || !qr.url) return;
    try {
      await navigator.clipboard.writeText(qr.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      addToast('Copy failed — the address is shown below the code', 'error');
    }
  };

  const openBoard = () => {
    if (publicSlug) {
      window.open(`https://${PUBLIC_BOARD_HOST.toLowerCase()}/${publicSlug}`, '_blank', 'noopener');
    } else {
      appNav.goTo('deskscoring');
      addToast('The live leaderboard box is on the Scoring tab');
    }
  };
  // The clubhouse TV view of the same board (oct12h): full screen, pages through the field.
  const openTv = () => {
    if (publicSlug) {
      window.open(`https://${PUBLIC_BOARD_HOST.toLowerCase()}/${publicSlug}/tv`, '_blank', 'noopener');
    } else {
      openBoard();
    }
  };

  // Check-in tile: first on event day (that morning it's the first job), after Players otherwise.
  const checkinFirst = utils.getDateOnly(teeTime.date) === utils.getTodayDate();
  const ckStatus = (() => {
    if (!checkin) return { text: 'Print the sheet · check golfers in' };
    const c = checkin.counts;
    const ft = checkin.fee_totals;
    const owe = ft && ft.entry && ft.entry.unpaid_names.length ? ` · ${ft.entry.unpaid_names.length} owe entry fee` : '';
    if (c.no_show > 0) return { text: `${c.no_show} no-show${c.no_show === 1 ? '' : 's'} to decide${owe}`, color: '#B42318' };
    if (c.confirmed > 0 && c.checked_in >= c.confirmed) return { text: `All ${c.confirmed} checked in${owe}`, color: owe ? '#6b4100' : '#1b4332' };
    if (c.checked_in > 0) return { text: `${c.checked_in} of ${c.confirmed} checked in${owe}`, color: '#6b4100' };
    return { text: `Print the sheet · check golfers in${owe}`, color: owe ? '#6b4100' : undefined };
  })();
  const checkinTile = event && event.id ? (
    <EventHomeTile
      icon="checkin" title="Check-in"
      blurb={checkin && checkin.fees ? 'Tick golfers in as they arrive and record fees, or print the sheet for the table' : 'Tick golfers in as they arrive, or print the sheet for the table'}
      status={ckStatus.text}
      statusColor={ckStatus.color}
      accent={checkinFirst}
      onClick={() => setShowCheckin(true)}
    />
  ) : null;

  return (
    <div style={{ display: 'grid', gap: 22 }}>
      {showCheckin && event && (
        <EventReadyCheckModal event={event} teeTime={teeTime} startView="checkin"
          onClose={() => { setShowCheckin(false); setCheckinTick(t => t + 1); }}
          onManagePlayers={onManagePlayers} onEdit={onEdit} onGames={onGames} />
      )}

      {/* 1. Event header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
          {event && event.logo_url && !logoFailed ? (
            <img
              src={`${API_URL}${event.logo_url}`}
              alt=""
              onError={() => setLogoFailed(true)}
              style={{ width: 76, height: 76, borderRadius: 14, objectFit: 'contain', background: '#fff', border: '1px solid #e5e7eb', flexShrink: 0 }}
            />
          ) : (
            <div style={{ width: 76, height: 76, borderRadius: 14, background: '#eaf3ee', border: '1px solid #c2dacc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 32 }} aria-hidden="true">⛳</div>
          )}
          <div>
            <h1 style={{ margin: 0, fontSize: 34, fontWeight: 800, lineHeight: 1.1, color: '#111827' }}>
              {(event && event.name) || (deskSession && deskSession.event_name) || 'Event'}
            </h1>
            <div style={{ marginTop: 8, fontSize: 17, color: '#111827' }}>
              {teeTime.course}{teeTime.course ? ' · ' : ''}{deskFmtDate(teeTime.date)}
              {teeTime.time ? ` · ${deskFmtTime(teeTime.time)}${teeTime.shotgun_mode ? ' shotgun' : ''}` : ''}
            </div>
            {deskSession && deskSession.org_name && (
              <div style={{ marginTop: 5, fontSize: 14.5, color: '#4b5361' }}>{deskSession.org_name}</div>
            )}
            {event && event.id && onEventSetup && (
              <button type="button" data-testid="desk-event-setup" onClick={onEventSetup} style={{ ...{ background: "none", border: "none", color: "#1f6b45", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "6px 4px", minHeight: 32 }, fontSize: 15, marginTop: 4, marginLeft: -4 }}>
                Event setup ▸
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
          {teeTime.weather_display && (
            <div style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>{teeTime.weather_display}</div>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => { if (onRefresh) onRefresh(); setReloadTick(t => t + 1); addToast('Refreshed'); }}
            style={{ fontSize: 15, padding: '11px 16px' }}
            title="Other people change the roster on event day"
          >
            🔄 Refresh
          </Button>
        </div>
      </div>

      {/* 2. Status strip */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
        {chip('Field', slots ? `${players.length} of ${slots} golfers` : `${players.length} golfers`, slots && players.length >= slots ? 'good' : 'plain')}
        {waitlist.length > 0 && chip('Waitlist', String(waitlist.length), 'warn')}
        {hasPairings
          ? chip(isTeam ? 'Teams' : 'Groups', `${groupNumbers.length} saved`, 'plain')
          : chip('', 'Pairings not saved', 'warn')}
        {scoring && chip('Scoring', scoresStatus, scoresIn > 0 ? 'warn' : 'plain')}
        {scoring && chip('', shared ? 'Leaderboard shared' : 'Leaderboard not shared', shared ? 'good' : 'plain')}
      </div>

      {/* 2b. Ready check (Oct 4, 2026) — the event's checklist; every problem links to its fix */}
      {event && event.id && (
        <EventReadyCheck eventId={event.id} teetimeId={teetimeId} collapsible
          onManagePlayers={onManagePlayers} onEdit={onEdit} onGames={onGames} />
      )}

      {/* 3. Task tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 14 }}>
        {checkinFirst && checkinTile}
        <EventHomeTile
          icon="players" title="Players & waitlist"
          blurb="Add a walk-up, or move someone off the waitlist"
          status={waitlist.length > 0 ? `${players.length} in · ${waitlist.length} waiting` : `${players.length} in`}
          onClick={onManagePlayers}
        />
        {!checkinFirst && checkinTile}
        <EventHomeTile
          icon="pairings" title="Pairings"
          blurb="Who plays with whom, and off which hole"
          status={pairingsAt
            ? `Saved ${pairingsAt}`
            : hasPairings
              ? `${groupNumbers.length} ${isTeam ? 'teams' : 'groups'} saved`
              : 'Not saved yet'}
          statusColor={(pairingsAt || hasPairings) ? undefined : '#6b4100'}
          onClick={() => appNav.goTo('pairings')}
        />
        <EventHomeTile
          icon="handicap" title="Event handicaps"
          blurb="Adjust a player's handicap for today only"
          status={adjusted == null ? 'On the Scoring tab' : adjusted === 0 ? 'None adjusted' : `${adjusted} adjusted`}
          statusColor={adjusted ? '#6b4100' : undefined}
          accent
          onClick={() => appNav.goTo('deskscoring')}
        />
        <EventHomeTile
          icon="print" title="Print"
          blurb="Scorecards, cart signs, posting board and the tee sheet"
          status={cartSignsAt ? `Cart signs ${cartSignsAt}` : cardsAt ? `Scorecards ${cardsAt}` : 'Nothing printed yet'}
          onClick={() => appNav.goTo('print')}
        />
        <EventHomeTile
          icon="scores" title="Scores"
          blurb={isTeam ? "Enter or fix a team's card" : "Enter or fix a player's card"}
          status={scoresStatus}
          statusColor={scoresIn > 0 ? '#6b4100' : undefined}
          onClick={() => appNav.goTo('deskscoring')}
        />
        <EventHomeTile
          icon="tv" title="Leaderboard & TV"
          blurb="The live board, and the screen in the grill"
          status={shared ? (publicSlug ? 'Shared · open the board' : 'Shared') : 'Not shared yet'}
          statusColor={shared ? '#1b4332' : undefined}
          onClick={openBoard}
          actions={shared && publicSlug ? [
            { label: '📱 Phone board', onClick: openBoard, testId: 'desk-board-phone', title: 'The leaderboard as players see it on their phones' },
            { label: '📺 TV screen', onClick: openTv, testId: 'desk-board-tv', title: 'Full-screen view for the clubhouse TV — pages through the whole field' },
          ] : undefined}
        />
        <EventHomeTile
          icon="message" title="Message players"
          blurb="Tell everyone about a delay or a change"
          status="Send a message"
          onClick={onNotify}
        />
        {gamesEnabled && (
          <EventHomeTile
            icon="trophy" title="Games"
            blurb="Setup, teams and the blind, ties, finalize"
            status="Open the games"
            onClick={onGames}
          />
        )}
      </div>

      {/* 3b. The leaderboard QR (Sept 17, 2026). Dennis: "place a leaderboard QR
          code there with a message below that says View Leaderboard on your Phone".
          The address sits under it in plain text too, so the pro in the proshop can
          type it onto the monitor beside EZLinks — one card, both his people. */}
      {qr && qr.qr_svg && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
            <div
              style={{ width: 132, height: 132, flexShrink: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 8, boxSizing: 'border-box', opacity: qr.shared ? 1 : 0.35 }}
              dangerouslySetInnerHTML={{ __html: qr.qr_svg }}
            />
            <div style={{ flexGrow: 1, minWidth: 220 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>View the live leaderboard on your phone</div>
              <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>
                Point a camera at the code, or type the address. Anyone can follow along — the pros, the players&rsquo; families, whoever is waiting.
              </div>
              <div style={{ marginTop: 12, fontSize: 19, fontWeight: 700, color: '#1b4332', wordBreak: 'break-all' }}>{qr.address}</div>
              {qr.shared ? (
                <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button type="button" variant="outline" onClick={copyBoardLink} style={{ fontSize: 14 }}>
                    {copied ? 'Copied' : 'Copy link'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => window.open(qr.url, '_blank', 'noopener')} style={{ fontSize: 14 }}>
                    Open the board
                  </Button>
                </div>
              ) : (
                <div style={{ marginTop: 12, background: '#fef3c7', border: '1px solid #e2cb9e', borderRadius: 10, padding: '11px 14px' }}>
                  <div style={{ fontSize: 14, color: '#6b4100', lineHeight: 1.5 }}>
                    The leaderboard is switched off, so this code goes nowhere yet.
                  </div>
                  <div style={{ marginTop: 9 }}>
                    <Button type="button" disabled={sharing} onClick={shareBoard} style={{ fontSize: 14 }}>
                      {sharing ? 'Sharing…' : 'Share the leaderboard'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* 3c. The event's website (oct12j) — under the leaderboard card, same shape. */}
      {sitePage && (
        <Card>
          <div style={{ flexGrow: 1, minWidth: 220 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>The event&rsquo;s website</div>
            <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>
              The page golfers sign up on: the date, the course, the start and how to reach the organizer.
            </div>
            <div style={{ marginTop: 12, fontSize: 19, fontWeight: 700, color: '#1b4332', wordBreak: 'break-all' }}>
              {sitePage.page_url.replace(/^https?:\/\//i, '').replace(/^ttoevent\.com/i, 'TTOevent.com')}
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button type="button" variant="outline" onClick={copySiteLink} style={{ fontSize: 14 }} data-testid="desk-site-copy">
                {siteCopied ? 'Copied' : 'Copy link'}
              </Button>
              <Button type="button" variant="outline" onClick={() => window.open(sitePage.page_url, '_blank', 'noopener')} style={{ fontSize: 14 }} data-testid="desk-site-open">
                Open the website
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 4. Roster */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>Today&rsquo;s field</div>
          <div style={{ fontSize: 14.5, color: '#4b5361' }}>
            {hasPairings ? `${groupNumbers.length} ${isTeam ? 'teams' : 'groups'}` : `${players.length} players`}
            {teeTime.shotgun_mode ? ' · shotgun start' : ''}
          </div>
        </div>

        {hasPairings ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
            {groupNumbers.map(g => {
              const members = byGroup[g];
              const hole = members.find(m => m.startingHole != null);
              const label = members.find(m => m.groupLabel);
              return (
                <div key={g} style={{ border: '1px solid #eef0f2', borderRadius: 10, padding: '13px 15px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1b4332', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {isTeam ? 'Team' : 'Group'} {g}
                    {hole ? ` · ${startHoleText(hole.startingHole, label && label.groupLabel ? label.groupLabel : '', ninesForCourse(teeTime?.course))}` : ''}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 15.5, lineHeight: 1.75, color: '#111827' }}>
                    {members.map(m => <div key={m.id}>{m.name}</div>)}
                    {blinds.filter(b => Number(b.team_number) === Number(g)).map(b => (
                      <div key={`blind-${b.player_id}`} data-testid="desk-blind" style={{ color: '#1e3a5f', fontStyle: 'italic' }}
                        title={b.replaces_name ? `Blind draw, covering ${b.replaces_name}` : 'Blind draw'}>
                        + {b.player_name || 'Blind'} <span style={{ fontSize: 13, fontStyle: 'normal', color: '#4b5361' }}>· blind{b.band ? `, ${b.band}` : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8, fontSize: 15.5, color: '#111827' }}>
            {players.map(p => <div key={p.id}>{p.name}</div>)}
          </div>
        )}

        {unpaired.length > 0 && hasPairings && (
          <div style={{ marginTop: 14, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 15px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#4b5361', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Not paired · {unpaired.length}</div>
            <div style={{ marginTop: 6, fontSize: 15, color: '#111827' }}>{unpaired.map(p => p.name).join(' · ')}</div>
          </div>
        )}

        {waitlist.length > 0 && (
          <div style={{ marginTop: 14, background: '#fef3c7', border: '1px solid #e2cb9e', borderRadius: 10, padding: '12px 15px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6b4100', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Waitlist · {waitlist.length}</div>
            <div style={{ marginTop: 6, fontSize: 15, color: '#111827' }}>
              {waitlist.map(w => (typeof w === 'string' ? w : (w && w.name) || '')).filter(Boolean).join(' · ')}
            </div>
          </div>
        )}
      </Card>

      {/* 5. How the desk works + 6. the quiet TTO footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setShowHow(v => !v)}
          style={{ background: 'transparent', border: 'none', padding: 0, font: 'inherit', fontSize: 14.5, color: '#4b5361', textDecoration: 'underline', cursor: 'pointer' }}
        >
          {showHow ? 'Hide how the desk works' : 'How the desk works'}
        </button>
        <div style={{ fontSize: 14, color: '#4b5361' }}>
          Powered by Tee Time Organizer ·{' '}
          <a href={DESK_PROMO_HREF} target="_blank" rel="noopener noreferrer" style={{ color: '#1b4332', fontWeight: 700, textDecoration: 'none' }}>
            See what TTO does for your club →
          </a>
        </div>
      </div>

      {showHow && (
        <Card style={{ background: '#f9fafb' }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>How the desk works</div>
          <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.65, display: 'grid', gap: 9 }}>
            <div><strong>This page</strong> is the event. Every tile opens one job, and each shows where that job stands right now. Refresh picks up changes other people made.</div>
            <div><strong>Pairings</strong> sets who plays together and, on a shotgun, which hole they start on. Print the cards and signs after the pairings are saved.</div>
            <div><strong>Scoring</strong> is where scores are entered or corrected, and where a player&rsquo;s handicap can be adjusted for this round only. The club&rsquo;s own handicap records are never changed.</div>
            <div><strong>Print</strong> has the scorecards, cart signs and tee sheet. It warns you if the pairings changed after you printed.</div>
            <div>Anything not on this page — the club&rsquo;s settings, other events, player records — stays with the organizer.</div>
          </div>
        </Card>
      )}

    </div>
  );
};

// ============================================================================
// NEXT UP CARD (Sept 25, 2026 — dashboard home redesign, Dennis).
// The soonest PUBLISHED upcoming tee time, shown first so the dashboard answers
// "what's happening" before "create something". Same summary-chip language as
// Event Home. Every button calls an existing dashboard handler.
// ============================================================================
// ============================================================================
// EVENT WELCOME EMAIL (Oct 8, 2026 — Dennis). An event's organizer loads his
// field quietly (Manage Players on an event emails nobody unless he ticks the box;
// the player import never emails), then sends ONE welcome email to the field from
// here. The window shows who has had it, so late adds are his call: "Send to the
// N not yet sent". Backend: eventWelcomeService, GET/PUT/POST /events/:id/welcome.
// Organizer only — the Event Desk does not get this button.
// ============================================================================
const EventWelcomeButton = ({ event, isReadOnly = false, onReadOnlyClick, style }) => {
  const deskSession = useContext(DeskContext);
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState(null);
  const [tick, setTick] = useState(0);
  const eventId = event && event.id ? event.id : null;
  useEffect(() => {
    if (!eventId || deskSession) return;
    let off = false;
    const h = {}; const t = getAuthToken(); if (t) h['Authorization'] = `Bearer ${t}`;
    fetch(`${API_URL}/events/${eventId}/welcome`, { credentials: 'include', headers: h })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (!off && d) setCounts(d.counts || null); })
      .catch(() => {});
    return () => { off = true; };
  }, [eventId, deskSession, tick]);
  if (!eventId || deskSession) return null;
  const toSend = counts ? counts.not_sent : 0;
  return (
    <>
      <Button
        variant="outline"
        data-testid="event-welcome-button"
        onClick={() => { if (isReadOnly) { onReadOnlyClick?.(); return; } setOpen(true); }}
        title="Field welcome: one email to every golfer in the field, each with his own personal link"
        style={{ fontSize: 12, color: "#1f4d36", borderColor: "#b9c9bf", background: "#fff", opacity: isReadOnly ? 0.5 : 1, ...(style || {}) }}
      >
        ✉️ Field welcome
        {toSend > 0 && (
          <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, background: "#fef3c7", color: "#7a5410", border: "1px solid #efd594", borderRadius: 999, padding: "1px 7px" }}>
            {toSend} to send
          </span>
        )}
      </Button>
      {open && <EventWelcomeModal event={event} onClose={() => { setOpen(false); setTick(t => t + 1); }} />}
    </>
  );
};

// Teams & captains (Oct 26, 2026 — components/EventTeams.js): lock the teams, what each golfer
// needs, and the "N changes" captains made. Organizer only, like the welcome email; shows only
// when the event has teams.
const EventTeamsSlot = ({ event, isReadOnly = false, onReadOnlyClick, style }) => {
  const deskSession = useContext(DeskContext);
  if (!event || !event.id || deskSession) return null;
  return <EventTeamsButton eventId={event.id} apiUrl={API_URL} getToken={getAuthToken} isReadOnly={isReadOnly} onReadOnlyClick={onReadOnlyClick} style={style} />;
};

const EventWelcomeModal = ({ event, onClose }) => {
  const { addToast } = useToast();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);        // GET /events/:id/welcome
  const [loadError, setLoadError] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState([]);      // player_ids ticked for "Send to selected"
  const [confirm, setConfirm] = useState(null);  // { label, payload } awaiting "Yes, send"
  const [result, setResult] = useState(null);    // last send: { sent, skipped, failed }
  const bodyRef = useRef(null);

  const headers = (json) => { const h = {}; const t = getAuthToken(); if (t) h['Authorization'] = `Bearer ${t}`; if (json) h['Content-Type'] = 'application/json'; return h; };
  const take = (d) => { setData(d); setSubject(d.subject || ''); setBody(d.body || ''); };

  useEffect(() => {
    let off = false;
    fetch(`${API_URL}/events/${event.id}/welcome`, { credentials: 'include', headers: headers() })
      .then(async r => { const d = await r.json().catch(() => ({})); if (off) return; if (!r.ok) { setLoadError(d.error || 'Could not load the field welcome'); return; } take(d); })
      .catch(() => { if (!off) setLoadError('Network error — could not load the field welcome'); });
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  const dirty = !!data && (subject !== data.subject || body !== data.body);

  const save = async ({ reset = false, quiet = false } = {}) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/events/${event.id}/welcome`, { method: 'PUT', credentials: 'include', headers: headers(true), body: JSON.stringify(reset ? { reset: true } : { subject, body }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { addToast(d.error || 'The field welcome did not save', 'error'); return false; }
      take(d);
      if (!quiet) addToast(reset ? 'Back to the standard field welcome' : 'Field welcome saved', 'success');
      return true;
    } catch (e) { addToast('Network error — the field welcome did not save', 'error'); return false; }
    finally { setBusy(false); }
  };

  const send = async (payload) => {
    if (dirty && !(await save({ quiet: true }))) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/events/${event.id}/welcome/send`, { method: 'POST', credentials: 'include', headers: headers(true), body: JSON.stringify(payload || {}) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { addToast(d.error || 'The field welcome was not sent', 'error'); return; }
      if (d.welcome) take(d.welcome);
      setPicked([]);
      setResult({ sent: d.sent || [], skipped: d.skipped || [], failed: d.failed || [] });
      const n = (d.sent || []).length;
      addToast(n > 0 ? `Field welcome sent to ${n} golfer${n === 1 ? '' : 's'}` : 'Nobody was sent the email — see the notes in the window', n > 0 ? 'success' : 'error');
    } catch (e) { addToast('Network error — the field welcome was not sent', 'error'); }
    finally { setBusy(false); setConfirm(null); }
  };

  const insertPlaceholder = (key) => {
    const tag = `{${key}}`;
    const el = bodyRef.current;
    if (!el) { setBody(b => b + tag); return; }
    const start = el.selectionStart != null ? el.selectionStart : body.length;
    const end = el.selectionEnd != null ? el.selectionEnd : body.length;
    const next = body.slice(0, start) + tag + body.slice(end);
    setBody(next);
    setTimeout(() => { try { el.focus(); el.selectionStart = el.selectionEnd = start + tag.length; } catch (e) { /* fine */ } }, 0);
  };

  const fillSample = (text) => {
    if (!data) return text;
    const first = (data.field || []).find(p => p.can_email) || (data.field || [])[0];
    const name = first ? first.name : 'Pat Golfer';
    const values = { ...(data.sample || {}), player_name: name, player_first_name: String(name).split(' ')[0], player_portal_url: 'https://teetimeorganizer.net/s/your-own-link' };
    let out = String(text || '');
    (data.placeholders || []).forEach(k => { out = out.split(`{${k}}`).join(values[k] != null ? String(values[k]) : ''); });
    return out;
  };

  const fmtSent = (ts) => {
    try { return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { return 'Sent'; }
  };

  const field = data ? (data.field || []) : [];
  const c = data ? data.counts : null;
  const pickable = field.filter(p => p.can_email);
  const allPicked = pickable.length > 0 && pickable.every(p => picked.includes(p.player_id));
  const togglePick = (id) => setPicked(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const LABELS = {
    player_first_name: 'First name', player_name: 'Full name', player_portal_url: 'Their personal link',
    event_name: 'Event name', event_date: 'Date', start_time: 'Start time', course_name: 'Course',
    group_name: 'Your organization', organizer_name: 'Your name',
  };
  const smallBtn = { fontSize: 12, padding: '6px 10px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'center', zIndex: 60, padding: isMobile ? 8 : 24, overflowY: 'auto' }} onClick={() => { if (!busy) onClose(); }}>
      <Card style={{ width: '100%', maxWidth: 760, maxHeight: isMobile ? 'none' : '92vh', overflowY: 'auto', boxSizing: 'border-box' }} onClick={(e) => e.stopPropagation()} data-testid="event-welcome-modal">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 19 }}>✉️ Field welcome</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{event.name}</div>
          </div>
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
        </div>
        <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, marginBottom: 12 }}>
          Load your field first — adding golfers to an event doesn&rsquo;t email them. When you&rsquo;re ready, send this once. Each golfer gets his own link. Anyone you add later shows here as <strong>Not sent</strong>; send to them when you&rsquo;re ready.
        </div>

        {loadError && <div style={{ color: '#b91c1c', fontSize: 14 }}>{loadError}</div>}
        {!data && !loadError && <div style={{ color: '#6b7280', fontSize: 14, padding: '20px 0' }}>Loading…</div>}

        {data && (
          <div style={{ display: 'grid', gap: 14 }}>
            {/* Counts */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ padding: '5px 12px', borderRadius: 999, background: '#fff', border: '1px solid #d1d5db', fontSize: 13, fontWeight: 600 }}>{c.confirmed} golfer{c.confirmed === 1 ? '' : 's'}</span>
              <span style={{ padding: '5px 12px', borderRadius: 999, background: '#e6f4ea', border: '1px solid #bfe0c9', color: '#1f5a3c', fontSize: 13, fontWeight: 600 }}>{c.sent} sent</span>
              <span style={{ padding: '5px 12px', borderRadius: 999, background: c.not_sent ? '#fff6df' : '#fff', border: `1px solid ${c.not_sent ? '#efd594' : '#d1d5db'}`, color: c.not_sent ? '#7a5410' : '#374151', fontSize: 13, fontWeight: 600 }}>{c.not_sent} not sent yet</span>
              {c.cannot_email > 0 && <span style={{ padding: '5px 12px', borderRadius: 999, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13, fontWeight: 600 }}>{c.cannot_email} can&rsquo;t be emailed</span>}
            </div>

            {/* The email */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  The email {dirty ? <span style={{ fontWeight: 600, color: '#b45309' }}>(not saved yet)</span> : data.is_default ? <span style={{ fontWeight: 500, color: '#6b7280' }}>(standard text)</span> : <span style={{ fontWeight: 500, color: '#6b7280' }}>(your Welcome template)</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Button variant="outline" style={smallBtn} onClick={() => setPreview(p => !p)} data-testid="welcome-preview-toggle">{preview ? 'Hide preview' : 'Preview'}</Button>
                  <Button variant="outline" style={smallBtn} onClick={() => setEditing(e => !e)} data-testid="welcome-edit-toggle">{editing ? 'Done editing' : 'Edit the email'}</Button>
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#374151', marginTop: 8 }}>
                <span style={{ color: '#6b7280' }}>Subject:</span> {fillSample(subject)}
              </div>

              {preview && (
                <div data-testid="welcome-preview" style={{ marginTop: 10, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 360, overflowY: 'auto' }}>
                  {fillSample(body)}
                </div>
              )}

              {editing && (
                <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                  {/* Oct 9, 2026 (Dennis): one copy of the text — this IS the org's Welcome template. */}
                  <div style={{ fontSize: 12.5, color: '#374151', background: '#f0f9f3', border: '1px solid #c2dacc', borderRadius: 8, padding: '8px 10px', lineHeight: 1.45 }}>
                    {data.from_template
                      ? <>This is your organization's <strong>Welcome</strong> email, the same text as Tools → Message → Templates. Saving here changes it there too.</>
                      : <>Saving creates your organization's <strong>Welcome</strong> email (Tools → Message → Templates), so it's ready for your next event too.</>}
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} data-testid="welcome-subject" />
                  </div>
                  <div>
                    <Label>Message <span style={{ color: body.length > (data.max_body || 2500) ? '#b91c1c' : '#9ca3af' }}>· {body.length} of {data.max_body || 2500} characters</span></Label>
                    <textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={isMobile ? 12 : 16} data-testid="welcome-body"
                      style={{ width: '100%', boxSizing: 'border-box', padding: 10, border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13.5, fontFamily: 'inherit', lineHeight: 1.45, resize: 'vertical' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Tap to insert — each golfer&rsquo;s own details go in when it&rsquo;s sent. Keep <strong>{'{player_portal_url}'}</strong>: it&rsquo;s his personal link.</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(data.placeholders || []).map(k => (
                        <button key={k} type="button" onClick={() => insertPlaceholder(k)}
                          style={{ fontSize: 12, padding: '4px 9px', borderRadius: 999, border: '1px solid #c2dacc', background: '#eaf3ee', color: '#1b4332', cursor: 'pointer' }}>
                          {LABELS[k] || k}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {dirty && <Button variant="outline" style={smallBtn} disabled={busy} onClick={() => { setSubject(data.subject); setBody(data.body); }}>Undo changes</Button>}
                    <Button style={smallBtn} disabled={busy || !dirty} onClick={() => save()} data-testid="welcome-save">Save</Button>
                  </div>
                </div>
              )}
            </div>

            {/* The field */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Who has had it</div>
                {pickable.length > 0 && (
                  <button type="button" onClick={() => setPicked(allPicked ? [] : pickable.map(p => p.player_id))}
                    style={{ background: 'none', border: 'none', color: '#1f6b45', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', padding: 0 }}>
                    {allPicked ? 'Clear ticks' : 'Tick everyone'}
                  </button>
                )}
              </div>
              {field.length === 0 ? (
                <div style={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic' }}>Nobody is on the event yet. Add your golfers with Manage Players, then come back here.</div>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, maxHeight: 300, overflowY: 'auto' }}>
                  {field.map((p, i) => (
                    <label key={p.player_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: i ? '1px solid #f3f4f6' : 'none', cursor: p.can_email ? 'pointer' : 'default', fontSize: 13.5 }}>
                      <input type="checkbox" disabled={!p.can_email} checked={picked.includes(p.player_id)} onChange={() => togglePick(p.player_id)} style={{ width: 15, height: 15 }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 600 }}>{p.name}</span>
                        {p.email && <span style={{ display: isMobile ? 'block' : 'inline', marginLeft: isMobile ? 0 : 8, color: '#9ca3af', fontSize: 12, wordBreak: 'break-all' }}>{p.email}</span>}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: !p.can_email ? '#991b1b' : p.welcome_sent_at ? '#1f5a3c' : '#7a5410' }}>
                        {!p.can_email ? p.reason : p.welcome_sent_at ? `Sent ${fmtSent(p.welcome_sent_at)}` : 'Not sent'}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {result && (result.skipped.length > 0 || result.failed.length > 0) && (
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: 10, fontSize: 12.5, color: '#78350f', lineHeight: 1.5 }}>
                {result.skipped.length > 0 && <div><strong>Not sent:</strong> {result.skipped.map(s => `${s.name} (${s.reason})`).join(', ')}</div>}
                {result.failed.length > 0 && <div><strong>Failed:</strong> {result.failed.map(s => s.name).join(', ')} — try them again in a minute.</div>}
              </div>
            )}

            {/* Send */}
            {confirm ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 10 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: '#14532d', marginRight: 'auto' }}>{confirm.label}</span>
                <Button variant="outline" disabled={busy} onClick={() => setConfirm(null)}>Cancel</Button>
                <Button disabled={busy} onClick={() => send(confirm.payload)} data-testid="welcome-confirm-send" style={{ background: '#2d6a4f', color: '#fff' }}>{busy ? 'Sending…' : 'Yes, send'}</Button>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                {picked.length > 0 && (
                  <Button variant="outline" disabled={busy} data-testid="welcome-send-picked"
                    onClick={() => setConfirm({ label: `Send the field welcome to the ${picked.length} golfer${picked.length === 1 ? '' : 's'} you ticked${field.some(p => picked.includes(p.player_id) && p.welcome_sent_at) ? ' (some have had it before)' : ''}?`, payload: { player_ids: picked } })}>
                    Send to {picked.length} ticked
                  </Button>
                )}
                <Button disabled={busy || c.not_sent === 0} data-testid="welcome-send-new"
                  onClick={() => setConfirm({ label: c.sent === 0 ? `Send the field welcome to all ${c.not_sent} golfer${c.not_sent === 1 ? '' : 's'}?` : `Send the welcome email to the ${c.not_sent} golfer${c.not_sent === 1 ? '' : 's'} who haven't had it?`, payload: {} })}
                  style={{ background: c.not_sent === 0 ? '#9ca3af' : '#2d6a4f', color: '#fff' }}>
                  {c.not_sent === 0 ? (c.sent > 0 ? 'Everyone has had it' : 'Nobody to send to') : c.sent === 0 ? `Send to all ${c.not_sent}` : `Send to the ${c.not_sent} not sent yet`}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

const NextUpCard = ({ teeTime, event, whenLabel, gamesEnabled = false, gamesLocked = false, playerByEmail, isReadOnly = false, onReadOnlyClick, onManagePlayers, onNotify, onGames, onInvite, onEdit, onPublish, onDelete, onEventSetup }) => {
  const { addToast: cardToast } = useToast();
  // Ready check / Check-in (Oct 4, 2026): on an event, the checklist one tap from the dashboard.
  // On event day the button says Check-in and opens on that tab; the setup tab is one tap away.
  const [showReady, setShowReady] = useState(false);
  const readyFixCount = useReadyStopCount(event && event.id, !!(event && event.id) && !showReady);
  if (!teeTime) return null;
  const eventDay = utils.getDateOnly(teeTime.date) === utils.getTodayDate();
  const guard = (fn) => () => { if (isReadOnly) { onReadOnlyClick?.(); return; } fn?.(); };
  const filled = utils.safePlayerCount(teeTime.players);
  const slots = utils.toInt(teeTime.slots);
  const openSpots = Math.max(0, slots - filled);
  const waitlistCount = Array.isArray(teeTime.waitlist) ? teeTime.waitlist.length : 0;
  const players = [...(teeTime.players || [])].sort((a, b) => {
    const dateA = a?.addedAt ? new Date(a.addedAt) : new Date(0);
    const dateB = b?.addedAt ? new Date(b.addedAt) : new Date(0);
    return dateA - dateB;
  });
  const chip = (bg, border, color) => ({ padding: "5px 12px", borderRadius: 999, background: bg, border: `1px solid ${border}`, fontSize: 13, fontWeight: 600, color });
  const chipGreen = chip("#e6f4ea", "#bfe0c9", "#1f5a3c");
  const chipAmber = chip("#fff6df", "#efd594", "#7a5410");
  const chipPlain = chip("#fff", "#d1d5db", "#374151");
  const tag = (bg, color) => ({ fontSize: 12, fontWeight: 700, padding: "2px 10px", borderRadius: 999, background: bg, color });
  const outlineBtn = { fontSize: 13, color: "#1f4d36", borderColor: "#b9c9bf", background: "#fff", padding: "9px 14px", borderRadius: 8, opacity: isReadOnly ? 0.5 : 1 };
  return (
    <Card style={{ border: "1px solid #dfe6e1", boxShadow: "0 2px 6px rgba(0,0,0,0.06)", padding: 20 }} data-testid="next-up-card">
      <div style={{ display: "grid", gap: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", color: "#1f6b45", textTransform: "uppercase" }}>Next up · {whenLabel}</span>
              {event && event.name && <span style={tag("#dcfce7", "#166534")}>📅 {event.name}</span>}
              {event && event.name && onEventSetup && (
                <button type="button" data-testid="nextup-event-setup" onClick={guard(onEventSetup)} style={{ background: "none", border: "none", color: "#1f6b45", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "6px 4px", minHeight: 32 }}>Event setup ▸</button>
              )}
              {!(event && event.name) && teeTime.title && <span style={tag("#f3f4f6", "#374151")}>🏷️ {teeTime.title}</span>}
              {teeTime.private && <span style={tag("#fce7f3", "#9f1239")}>🔒 Private</span>}
              {teeTime.locked && <span style={tag("#fee2e2", "#991b1b")}>🚫 Locked</span>}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#14281d", marginTop: 4 }}>{teeTime.course}</div>
            <div style={{ fontSize: 14, color: "#4b5563", marginTop: 2 }}>{utils.formatDate(teeTime.date)} · {utils.formatTime(teeTime.time)}</div>
          </div>
          {teeTime.weather_display && (
            <div style={{ fontSize: 13, color: "#065f46", background: "#ecfdf5", border: "1px solid #a7f3d0", padding: "6px 10px", borderRadius: 8, whiteSpace: "pre-line", maxWidth: 340 }}>
              {teeTime.weather_display}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <span style={openSpots === 0 ? chipGreen : chipPlain}>{filled} of {slots} {event ? "golfers" : "players"}</span>
          {openSpots > 0 && <span style={chipAmber}>{openSpots} spot{openSpots === 1 ? "" : "s"} open</span>}
          <span style={waitlistCount > 0 ? chipAmber : chipPlain}>{waitlistCount > 0 ? `Waitlist ${waitlistCount}` : "No waitlist"}</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {players.length > 0 ? players.map((player, i) => {
            const playerName = typeof player === 'string'
              ? (playerByEmail?.get(player) || player)
              : (player?.name || player?.email || 'Unknown');
            const photoUrl = typeof player === 'object' ? player?.photoUrl : null;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, background: "#f6f8f6", border: "1px solid #e1e8e3", borderRadius: 999, padding: "4px 10px 4px 4px", fontSize: 13 }}>
                <PlayerAvatar name={playerName} photoUrl={photoUrl} />
                <span>{playerName}</span>
              </div>
            );
          }) : (
            <div style={{ color: "#6b7280", fontSize: 13, fontStyle: "italic" }}>No players yet</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Button onClick={guard(onManagePlayers)} style={{ fontSize: 13, fontWeight: 700, background: isReadOnly ? "#9ca3af" : "#2d6a4f", color: "#fff", padding: "9px 16px", borderRadius: 8 }}>Manage players</Button>
          <Button variant="outline" onClick={guard(onNotify)} style={outlineBtn}>📧 Notify players</Button>
          <EventWelcomeButton event={event} isReadOnly={isReadOnly} onReadOnlyClick={onReadOnlyClick} style={outlineBtn} />
          <EventTeamsSlot event={event} isReadOnly={isReadOnly} onReadOnlyClick={onReadOnlyClick} style={outlineBtn} />
          {gamesEnabled && <Button variant="outline" onClick={guard(onGames)} style={outlineBtn}>🎮 Games</Button>}
          {!gamesEnabled && gamesLocked && <Button variant="outline" onClick={() => triggerUpgradeModal({ feature: 'games', requiredTier: 'plus' })} title="Games come with the Plus plan and above" style={{ ...outlineBtn, color: '#92400e', borderColor: '#fcd34d', background: '#fffbeb' }}>🎮 Games 🔒</Button>}
          {event && event.public_slug && (
            <Button variant="outline" onClick={() => openEventTv(event, cardToast)} style={{ ...outlineBtn, opacity: 1 }} title="Open this event's leaderboard in TV view (for the clubhouse screen)">📺 TV</Button>
          )}
          <Button variant="outline" onClick={guard(onInvite)} style={outlineBtn}>🎟️ Invite</Button>
          {event && event.id && (
            <Button variant="outline" onClick={() => setShowReady(true)} style={{ ...outlineBtn, opacity: 1, fontWeight: 700, color: "#1b4332", borderColor: "#95c4a8", background: "#f0f9f3" }}
              title={eventDay ? "Event morning check-in — and the setup checklist" : "Is the event ready to roll? Setup, field, teams, course and event-day checks"}>
              {eventDay ? "✅ Check-in" : "✅ Ready check"}{fixBadge(readyFixCount)}
            </Button>
          )}
          <Button variant="outline" onClick={guard(onEdit)} style={{ ...outlineBtn, marginLeft: "auto", color: "#374151", borderColor: "#d1d5db" }}>✏️ Edit</Button>
          {/* Oct 5, 2026 (Dennis): the Next up tee time is left out of the list below, so
              without this menu it had no Delete anywhere on the default view. Same flow as
              the list cards (cancel-with-reason when players are on it, else a confirm). */}
          {onDelete && <MoreActionsMenu items={[{ label: '🗑️ Delete tee time', danger: true, onClick: guard(onDelete) }]} disabled={isReadOnly} onDisabledClick={onReadOnlyClick} />}
        </div>
      </div>
      {showReady && event && (
        <EventReadyCheckModal event={event} teeTime={teeTime} onClose={() => setShowReady(false)} startView={eventDay ? 'checkin' : 'setup'}
          onManagePlayers={guard(onManagePlayers)} onEdit={guard(onEdit)} onGames={guard(onGames)} onPublish={onPublish ? guard(onPublish) : undefined} />
      )}
    </Card>
  );
};
const OrganizerDashboard = () => {
  const { addToast } = useToast();
  // Event desk (Sept 17, 2026): the same dashboard, one event. The server narrows
  // the lists; here the org-level actions are simply not offered.
  const deskSession = useContext(DeskContext);
  // Dashboard home greeting (Sept 25, 2026): the SIGNED-IN account, so a co-organizer
  // sees his own name, never the org owner's.
  const { user: authUser, logout: authLogout } = useAuth();
  // Oct 28, 2026: the sign-in is gone (fetchWithAuthRetry): say so, and send him to sign in.
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    const on = () => setSessionExpired(true);
    window.addEventListener('tto-session-expired', on);
    return () => window.removeEventListener('tto-session-expired', on);
  }, []);
  const signInAgain = async () => {
    try { if (authLogout) await authLogout(); } catch (e) { /* signing in again anyway */ }
    window.location.href = '/login';
  };
  const { data: players, loading: loadingPlayers } = useApiData('/players');
  const { data: teeTimes, loading: loadingTeeTimes, addItem, updateItem, deleteItem, refresh } = useApiData('/teetimes');
  const { groups } = useGroups();
  const { isMobile } = useContext(MobileContext);
  const [orgSettings, setOrgSettings] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showPlayerPicker, setShowPlayerPicker] = useState(false);
  const [selectedTeeTimeId, setSelectedTeeTimeId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTeeTime, setEditingTeeTime] = useState(null);
  const [gameSetupTeeTime, setGameSetupTeeTime] = useState(null); // tee time object for game setup modal
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishingTeeTime, setPublishingTeeTime] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false); // NEW: Publishing loading state
  const [showNotifyModal, setShowNotifyModal] = useState(false); // NEW: Notify modal state
  const [notifyingTeeTime, setNotifyingTeeTime] = useState(null); // NEW: Tee time being notified
  const [refreshingWeather, setRefreshingWeather] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false); // NEW: Invite link modal state
  const [invitingTeeTime, setInvitingTeeTime] = useState(null); // NEW: Tee time for invite link
  
  // Cancel tee time modal state (NEW - January 29, 2026)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingTeeTime, setCancellingTeeTime] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Slot reduction confirmation modal state
  const [showSlotReductionModal, setShowSlotReductionModal] = useState(false);
  const [slotReductionData, setSlotReductionData] = useState(null); // { updatedTeeTime, playersToWaitlist }
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Tee time overlap conflict modal state
  const [showOverlapModal, setShowOverlapModal] = useState(false);
  const [overlapConflict, setOverlapConflict] = useState(null);
  
  // Upgrade modal state (Phase 2 — Free + Starter Tier Rollout, May 2026)
  // Shown when a paid feature is denied. Triggered via 'showUpgradeModal'
  // window CustomEvent — fired by handleFeatureUpgradeRequired() helper.
  const [upgradeModal, setUpgradeModal] = useState({ isOpen: false, feature: null, requiredTier: null, currentTier: null });
  
  // Subscription status for read-only mode (cancelled or expired accounts)
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isExpiredTrial, setIsExpiredTrial] = useState(false);
  
  // Help & Implementation Guide state
  const [showHelp, setShowHelp] = useState(false);
  const [showGuidesChooser, setShowGuidesChooser] = useState(false); // 📖 Guide → chooser (Sept 27, 2026)
  const helpContent = getHelpContent('teeTimes');
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [whatsNewUnread, setWhatsNewUnread] = useState(0);

  // Load unread count — runs when orgSettings loads (group_id needed for per-org key)
  useEffect(() => {
    if (!orgSettings?.group_id) return; // wait until org is known
    if (onDeskPage()) return;            // event desk: no product news
    fetch(`${API_URL}/content/whats-new`)
      .then(r => r.ok ? r.json() : [])
      .then(items => setWhatsNewUnread(getUnreadCount(items, orgSettings.group_id)))
      .catch(() => {});
  }, [orgSettings?.group_id]);

  // Listen for upgrade-modal events fired from anywhere in the app (Phase 2 — May 2026)
  useEffect(() => {
    const handleShowUpgrade = (e) => {
      const { feature, requiredTier, currentTier } = (e && e.detail) || {};
      setUpgradeModal({
        isOpen: true,
        feature: feature || null,
        requiredTier: requiredTier || 'plus',
        currentTier: currentTier || null
      });
    };
    window.addEventListener('showUpgradeModal', handleShowUpgrade);
    return () => window.removeEventListener('showUpgradeModal', handleShowUpgrade);
  }, []);

  // Scoring & Games module state
  const [scoringEnabled, setScoringEnabled] = useState(false);
  const [gamesEnabled, setGamesEnabled] = useState(false);
  const [gamesLocked, setGamesLocked] = useState(false);   // Oct 5, 2026: Starter sees 🎮 Games with a lock (games come with Plus)
  const [eventsEnabled, setEventsEnabled] = useState(false); // Association events platform gate (per-org)
  // Event Pass (Sept 28, 2026): can this org mark a NEW tee time as an event right now
  // (unlimited, a pass waiting, or League annual's included event)? null until known.
  const [canCreateEvent, setCanCreateEvent] = useState(null);
  const [isEventAccount, setIsEventAccount] = useState(false); // made through the Event Pass door (Sept 30, 2026)
  const [eventAccessNonce, setEventAccessNonce] = useState(0);
  const refreshEventAccess = () => setEventAccessNonce(n => n + 1);
  const [eventsByTeetime, setEventsByTeetime] = useState({}); // teetime_id -> event, for card badges
  const [eventSetupTeetime, setEventSetupTeetime] = useState(null); // Slice 3a — create-flow Event Setup modal target
  const [cardEventSetupId, setCardEventSetupId] = useState(null); // Oct 12, 2026 — Event Setup opened from a card's "Event setup ▸"
  const [teamScoringEnabled, setTeamScoringEnabled] = useState(false); // Team scoring gate (per-org, Sept 13, 2026) — shows the one-score-per-team option in Event Setup
  const [eventSaveError, setEventSaveError] = useState(null); // { action: 'create'|'update'|'remove', message } — shown as a MODAL, not a toast (organizers miss toasts — Dennis, Sept 13, 2026)

  // ------------------------------------------------------------------
  // AUTO-REFRESH (Sept 16, 2026 — Sunday Golfers / Kurt Eilo report).
  // This was the one organizer view with NO periodic refresh ("manual refresh
  // only ... users are actively creating tee times"), so a player-app RSVP stayed
  // invisible until the organizer reloaded — Kurt's "he signed up but doesn't
  // show" was a tab left open for eleven hours. Same pattern as the Players page:
  // SILENT re-fetch every 30 s (no spinner), PAUSED while any modal or the player
  // picker is open — PlayerPickerModal re-seeds its checkboxes from the teeTime
  // prop, so a refresh underneath it would wipe in-progress edits; the inline
  // create form keeps its own state and is unaffected. Also re-fetch when the tab
  // comes back into view: an interval does not run reliably in a backgrounded
  // tab, and that is exactly the case that bit Kurt. refreshRef keeps the latest
  // fetchData without making it an effect dependency (it is re-created per render).
  // ------------------------------------------------------------------
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  // Event desk Back button (Oct 10, 2026): closes whichever of these windows is open,
  // the same as its Close / Cancel button. Never while a send is under way.
  useDeskBack(
    !!(showPlayerPicker || showEditModal || showPublishModal || showNotifyModal || showInviteModal ||
       gameSetupTeeTime || eventSetupTeetime || eventSaveError || isPublishing),
    () => {
      if (isPublishing) return;
      setShowPlayerPicker(false); setSelectedTeeTimeId(null);
      setShowEditModal(false); setEditingTeeTime(null);
      setShowPublishModal(false); setPublishingTeeTime(null);
      setShowNotifyModal(false); setNotifyingTeeTime(null);
      setShowInviteModal(false); setInvitingTeeTime(null);
      setGameSetupTeeTime(null); setEventSetupTeetime(null); setEventSaveError(null);
    }
  );
  const refreshPaused = !!(
    showPlayerPicker || showEditModal || showPublishModal || showNotifyModal ||
    showInviteModal || showCancelModal || showSlotReductionModal || showOverlapModal ||
    gameSetupTeeTime || eventSetupTeetime || eventSaveError || confirmDeleteId ||
    upgradeModal.isOpen || isPublishing || isCancelling
  );
  useEffect(() => {
    if (refreshPaused) return undefined;
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refreshRef.current(true); // silent — fetchData catches its own errors
    };
    const intervalId = setInterval(tick, 30000);
    const onVisibility = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshPaused]);

  useEffect(() => {
    const checkScoring = async () => {
      try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(`${API_URL}/scoring/status`, { credentials: 'include', headers });
        if (response.ok) {
          const data = await response.json();
          setScoringEnabled(data.scoring_enabled || false);
          setGamesEnabled(data.games_enabled || false);
          setGamesLocked(data.games_locked === true);
          setEventsEnabled(data.events_enabled || false);
          setTeamScoringEnabled(data.team_scoring_enabled || false);
        }
        // Event Pass (Sept 28, 2026): whether a new event can be created now.
        const passResp = await fetch(`${API_URL}/event-pass`, { credentials: 'include', headers });
        if (passResp.ok) {
          const pd = await passResp.json();
          setCanCreateEvent(!!(pd.access && pd.access.can_create_event));
          setIsEventAccount(!!(pd.access && pd.access.event_account));
        }
      } catch (error) {
        console.log('Scoring status check failed (module may not be enabled)');
      }
    };
    checkScoring();
  }, [eventAccessNonce]);

  // Events (Slice 1): once we know the org has events enabled, load its events and
  // index them by teetime_id so tee-time cards can show an event badge. Runs only
  // for enabled orgs; the endpoint 403s for everyone else, so this stays dark.
  useEffect(() => {
    if (!eventsEnabled) return;
    const loadEvents = async () => {
      try {
        const headers = {};
        const token = getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(`${API_URL}/events`, { credentials: 'include', headers });
        if (response.ok) {
          const data = await response.json();
          const map = {};
          (data.events || []).forEach(ev => {
            if (ev.teetime_id) map[ev.teetime_id] = ev;
          });
          setEventsByTeetime(map);
        }
      } catch (error) {
        console.log('Events load failed (feature may not be enabled)');
      }
    };
    loadEvents();
  }, [eventsEnabled]);

  // Fetch org settings to control sunrise/sunset display on cards
  useEffect(() => {
    const fetchOrgSettings = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const response = await fetch(`${API_URL}/settings`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setOrgSettings(data.data);
        }
      } catch (error) {
        console.log('Failed to fetch org settings for dashboard');
      }
    };
    fetchOrgSettings();
  }, []);
  
  // Fetch subscription status on mount to determine if read-only mode
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const response = await fetch(`${API_URL}/subscription/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const status = data.subscription?.status;
          setSubscriptionStatus(status);
          setIsReadOnly(status === 'cancelled' || status === 'canceled' || status === 'expired');
          setIsExpiredTrial(status === 'expired');
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };
    fetchSubscriptionStatus();
  }, []);
  
  // Helper to show read-only toast when user tries blocked action
  const showReadOnlyToast = () => {
    addToast(isExpiredTrial 
      ? 'Your free trial has ended. Please subscribe to continue making changes.' 
      : 'Your account is cancelled. Please reactivate your subscription to make changes.', 'error');
  };
  
  // Manual refresh only - no auto-refresh for this page since users are actively creating tee times
  
  // Create email-to-name lookup map
  const playerByEmail = useMemo(() => {
    return new Map(players.map(p => [p.email, p.name]));
  }, [players]);
  
  // Helper function to convert email to name, or return email if not found
  const emailToName = (email) => playerByEmail.get(email) || email;
  
  // Helper to convert tee time players (emails) to names for display
  const getPlayerNames = (teeTime) => {
    if (!teeTime || !Array.isArray(teeTime.players)) return [];
    return teeTime.players.map(playerData => {
      // Handle both email strings and player objects
      if (typeof playerData === 'string') {
        return emailToName(playerData);
      } else if (playerData && typeof playerData === 'object') {
        return playerData.name || playerData.email || 'Unknown';
      }
      return 'Unknown';
    });
  };
  
  // Filter and view state
  const [filter, setFilter] = useState(() => (onDeskPage() ? "all" : "upcoming")); // all, upcoming, past, today (the desk's one event may be past)
  const [viewMode, setViewMode] = useState(() => {
    // Load saved view mode from localStorage, default to "card" if not found
    const savedViewMode = localStorage.getItem('teeTimeViewMode');
    return savedViewMode || "card";
  }); // card, list
  const [sortBy, setSortBy] = useState("date"); // date, date-desc, course, slots
  const [searchTerm, setSearchTerm] = useState("");

  // Save viewMode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('teeTimeViewMode', viewMode);
  }, [viewMode]);

  const filteredAndSortedTeeTimes = useMemo(() => {
    const todayTs = utils.getTodayTimestamp();
    const todayDateStr = utils.getTodayDate();
    // Upcoming vs Past (Sept 29, 2026 — Dennis): a round stays Upcoming until six
    // hours after its start, the same rule as the Next up card, then moves to Past.
    // Today still shows every round dated today.
    const nowTs = Date.now();
    const stillUpcoming = (t) => utils.getDateTimestamp(t.date, t.time) + 6 * 3600 * 1000 >= nowTs;
    
    // Apply filters
    let filtered = teeTimes.filter((t) => {
      try {
        const teeTimeDateStr = utils.getDateOnly(t.date);
        
        if (filter === "today") {
          return teeTimeDateStr === todayDateStr;
        }
        
        if (filter === "upcoming") {
          return stillUpcoming(t);
        }
        
        if (filter === "past") {
          return !stillUpcoming(t);
        }
        
        return true; // "all"
      } catch (err) {
        console.error('❌ Error filtering tee time:', t, err);
        return false; // Exclude invalid tee times
      }
    });
    
    // Apply search
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((t) => {
        const playerNames = getPlayerNames(t);
        return t.course.toLowerCase().includes(search) ||
               playerNames.some(name => name.toLowerCase().includes(search));
      });
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      if (sortBy === "date") {
        // For "past" filter, show most recent first (descending)
        // For "upcoming" and other filters, show soonest first (ascending)
        if (filter === "past") {
          return utils.getDateTimestamp(b.date, b.time) - utils.getDateTimestamp(a.date, a.time);
        }
        return utils.getDateTimestamp(a.date, a.time) - utils.getDateTimestamp(b.date, b.time);
      }
      if (sortBy === "date-desc") {
        // Date descending (newest first) - explicit user choice
        return utils.getDateTimestamp(b.date, b.time) - utils.getDateTimestamp(a.date, a.time);
      }
      if (sortBy === "course") {
        // Course alphabetically, then by date within each course
        const courseCompare = a.course.localeCompare(b.course);
        if (courseCompare !== 0) return courseCompare;
        return utils.getDateTimestamp(a.date, a.time) - utils.getDateTimestamp(b.date, b.time);
      }
      if (sortBy === "slots") {
        const aAvail = utils.toInt(a.slots) - utils.safePlayerCount(a.players);
        const bAvail = utils.toInt(b.slots) - utils.safePlayerCount(b.players);
        return bAvail - aAvail; // Most available first
      }
      return 0;
    });
  }, [teeTimes, filter, sortBy, searchTerm]);

  // ------------------------------------------------------------------
  // DASHBOARD HOME (Sept 25, 2026 — Dennis). The page now opens on what is
  // happening rather than on an empty create form:
  //   - a greeting for the signed-in account and the day at a glance
  //   - "New tee time" opens the create form; it starts OPEN only for an org
  //     with no tee times yet, so first-time setup is never hidden
  //   - Next up: the soonest PUBLISHED upcoming tee time (drafts are not "next")
  //   - a reminder strip for upcoming drafts, which players cannot see yet
  // The list below keeps every filter, sort, view and action it had.
  // ------------------------------------------------------------------
  const [createOpen, setCreateOpen] = useState(null); // null = automatic (open only when the org has no tee times)
  const createFormOpen = createOpen === null ? (Array.isArray(teeTimes) && teeTimes.length === 0) : createOpen;
  useEffect(() => {
    // The phone bottom bar's "+ New" (MobileBottomBar) asks for the form this way.
    const onOpenCreate = () => setCreateOpen(true);
    window.addEventListener('openCreateTeeTime', onOpenCreate);
    return () => window.removeEventListener('openCreateTeeTime', onOpenCreate);
  }, []);

  const homeInfo = useMemo(() => {
    const list = Array.isArray(teeTimes) ? teeTimes : [];
    const now = Date.now();
    const byStart = (a, b) => utils.getDateTimestamp(a.date, a.time) - utils.getDateTimestamp(b.date, b.time);
    // A round stays upcoming (and "next") until six hours after its start, so a
    // morning round is still on top while it is being played — the same rule as
    // the Upcoming / Past list filters (Sept 29, 2026).
    const upcoming = list.filter((t) => utils.getDateTimestamp(t.date, t.time) + 6 * 3600 * 1000 >= now);
    const nextUp = upcoming
      .filter((t) => t.status === 'Published')
      .sort(byStart)[0] || null;
    const drafts = upcoming.filter((t) => t.status === 'Draft').sort(byStart);
    return { upcomingCount: upcoming.length, nextUp, drafts };
  }, [teeTimes]);

  const homeTz = orgSettings?.timezone || undefined;
  const homeGreeting = (() => {
    let hour = new Date().getHours();
    try {
      if (homeTz) hour = Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: homeTz }).format(new Date())) % 24;
    } catch (e) { /* unrecognised time zone string: keep the browser's hour */ }
    const part = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
    // No name, or an address in the name field, means no name: a wrong name is worse than none.
    const first = String(authUser?.name || '').trim().split(/\s+/)[0];
    return first && !first.includes('@') ? `${part}, ${first}` : part;
  })();
  const homeSubline = (() => {
    let dateText = '';
    try {
      dateText = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', ...(homeTz ? { timeZone: homeTz } : {}) });
    } catch (e) {
      dateText = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    }
    const n = homeInfo.upcomingCount;
    return `${dateText} · ${n === 0 ? 'no upcoming tee times yet' : `${n} upcoming tee time${n === 1 ? '' : 's'}`}`;
  })();
  const nextUpWhen = (() => {
    const t = homeInfo.nextUp;
    if (!t) return '';
    const d = utils.getDateOnly(t.date);
    const todayStr = utils.getTodayDate();
    const tm = new Date(); tm.setDate(tm.getDate() + 1);
    const tomorrowStr = `${tm.getFullYear()}-${String(tm.getMonth() + 1).padStart(2, '0')}-${String(tm.getDate()).padStart(2, '0')}`;
    if (d === todayStr) return 'Today';
    if (d === tomorrowStr) return 'Tomorrow';
    const days = Math.round((utils.getDateTimestamp(d, '00:00') - utils.getTodayTimestamp()) / 86400000);
    if (days > 1 && days < 7) {
      const [y, m, dd] = d.split('-').map(Number);
      return new Date(y, m - 1, dd).toLocaleDateString('en-US', { weekday: 'long' });
    }
    return utils.formatDate(d);
  })();
  // The Next up round is not repeated in the list below it — only on the default
  // Upcoming view with no search, where it would otherwise be the first row.
  const hideNextFromList = !!(homeInfo.nextUp && filter === 'upcoming' && !searchTerm.trim());
  const listTeeTimes = hideNextFromList
    ? filteredAndSortedTeeTimes.filter((t) => t.id !== homeInfo.nextUp.id)
    : filteredAndSortedTeeTimes;

  const handleCreate = async (teeTime, openEventSetup = false) => {
    try {
      const created = await addItem(teeTime);
      addToast("Tee time created successfully");

      // Events (Slice 1): if this tee time was marked as an event, create the
      // linked event record now that the tee time exists and has a real id.
      // Gated twice: teeTime.event is only ever set when the org's events_enabled
      // checkbox was ticked, and the backend route independently re-checks the flag.
      if (teeTime && teeTime.event && teeTime.event.name && created && created.id) {
        try {
          const headers = { 'Content-Type': 'application/json' };
          const token = getAuthToken();
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const resp = await fetch(`${API_URL}/events`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
              name: teeTime.event.name,
              description: teeTime.event.description || null,
              teetime_id: created.id
            })
          });
          if (resp.ok) {
            const payload = await resp.json();
            const ev = payload.event;
            if (ev && ev.teetime_id) {
              setEventsByTeetime(prev => ({ ...prev, [ev.teetime_id]: ev }));
            }
            addToast("Event created");
          } else {
            addToast("Tee time created, but the event could not be saved", "error");
          }
        } catch (evErr) {
          console.error('Error creating event:', evErr);
          addToast("Tee time created, but the event could not be saved", "error");
        }
      }

      // Slice 3a: if the create bar flagged this as an event, open the Event Setup
      // modal on the just-created tee time. The parent holds `created` directly, so
      // this never depends on a return value crossing component boundaries.
      if (openEventSetup && created && created.id) {
        setEventSetupTeetime(created);
      }

      return created;
    } catch (err) {
      console.error('Error creating tee time:', err);
      addToast(withReason("Error creating tee time", err), "error");
    }
  };


  // UPDATED: January 29, 2026 - Now uses modal for tee times with players
  const handleDelete = async (id) => {
    // Block if read-only (cancelled subscription)
    if (isReadOnly) {
      showReadOnlyToast();
      setConfirmDeleteId(null);
      return;
    }
    
    try {
      const teeTime = teeTimes.find(t => t.id === id);
      if (!teeTime) {
        addToast("Tee time not found", "error");
        return;
      }
      
      // No players - proceed with simple deletion
      await deleteItem(id);
      addToast("Tee time deleted successfully");
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('Error deleting tee time:', err);
      addToast(withReason("Error deleting tee time", err), "error");
    }
  };

  // NEW: Check for players BEFORE showing any dialog (January 29, 2026)
  const handleDeleteClick = (id) => {
    console.log('=== handleDeleteClick called ===', { id, isReadOnly });
    if (deskSession) {
      addToast("Only the organizer can cancel or delete this tee time.", "error");
      return;
    }
    if (isReadOnly) {
      showReadOnlyToast();
      return;
    }
    
    const teeTime = teeTimes.find(t => t.id === id);
    console.log('Found teeTime:', teeTime ? 'yes' : 'no');
    if (!teeTime) {
      addToast("Tee time not found", "error");
      return;
    }
    
    const playerCount = utils.safePlayerCount(teeTime.players);
    console.log('Player count:', playerCount);
    
    if (playerCount > 0) {
      // Has players - show cancel modal with reason field
      console.log('Showing cancel modal');
      setCancellingTeeTime(teeTime);
      setCancelReason('');
      setShowCancelModal(true);
    } else {
      // No players - show simple confirmation
      console.log('Showing simple confirm dialog');
      setConfirmDeleteId(id);
    }
  };

  // NEW: Handle the actual cancellation with reason (January 29, 2026)
  const handleConfirmCancel = async () => {
    if (!cancellingTeeTime) return;
    
    setIsCancelling(true);
    const playerCount = utils.safePlayerCount(cancellingTeeTime.players);
    
    try {
      // Send cancellation notifications to all players
      const cancelHeaders = { 'Content-Type': 'application/json' };
      const cancelToken = getAuthToken();
      if (cancelToken) {
        cancelHeaders['Authorization'] = `Bearer ${cancelToken}`;
      }
      
      const cancelResponse = await fetch(`${API_URL}/teetimes/${cancellingTeeTime.id}/cancel`, {
        method: 'POST',
        headers: cancelHeaders,
        credentials: 'include',
        body: JSON.stringify({
          reason: cancelReason.trim() || null
        })
      });
      
      let notifyResult = { notified: 0 };
      if (cancelResponse.ok) {
        notifyResult = await cancelResponse.json();
        console.log('Cancellation notifications sent:', notifyResult);
      } else {
        console.error('Failed to send cancellation notifications:', await cancelResponse.text());
      }
      
      // Proceed with deletion
      await deleteItem(cancellingTeeTime.id);
      
      // Show success message with notification count
      if (notifyResult.notified > 0) {
        addToast(`Tee time deleted and ${notifyResult.notified} player${notifyResult.notified > 1 ? 's' : ''} notified`);
      } else {
        addToast("Tee time deleted successfully");
      }
      
    } catch (err) {
      console.error('Error cancelling tee time:', err);
      addToast(withReason("Error cancelling tee time", err), "error");
    } finally {
      setIsCancelling(false);
      setShowCancelModal(false);
      setCancellingTeeTime(null);
      setCancelReason('');
      setConfirmDeleteId(null);
    }
  };

  const handleManagePlayers = async (id) => {
    setSelectedTeeTimeId(id);
    // Fresh roster BEFORE the picker opens (Sept 16, 2026): the picker seeds its
    // checkboxes from the tee time as loaded, and the save computes removals
    // against it — both must reflect what the player app has done since this
    // page last loaded. Silent, and fetchData never throws (it catches internally).
    await refresh(true);
    setShowPlayerPicker(true);
  };

  const handleEdit = (id) => {
    const teeTime = teeTimes.find(t => t.id === id);
    if (teeTime) {
      setEditingTeeTime({ ...teeTime });
      setShowEditModal(true);
    }
  };

  const handleGames = (id) => {
    const teeTime = teeTimes.find(t => t.id === id);
    if (teeTime) {
      setGameSetupTeeTime(teeTime);
    }
  };

  const handleFinalize = async (id) => {
    // Slice 3 (Oct 4, 2026): on an event with must-fix items, that question replaces the plain one.
    const stops = await readyStops({ teetimeId: id });
    if (stops.length) { if (!window.confirm(mustFixMessage(stops, 'finalize'))) return; }
    else if (!window.confirm('Finalize this round? This will save all settlements and mark games as completed.')) return;
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/scoring/teetimes/${id}/finalize`, {
        method: 'POST', credentials: 'include', headers,
      });
      const data = await res.json();
      if (res.ok) {
        addToast(`Round finalized! ${data.settlements_saved} settlement(s) saved.`, 'success');
      } else {
        addToast(data.error || 'Failed to finalize', 'error');
      }
    } catch (e) {
      addToast('Failed to finalize round', 'error');
    }
  };

  const handleSendSettlementEmails = async (id, { player_ids, note } = {}) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/scoring/teetimes/${id}/email-settlement`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ player_ids, note }),
      });
      const data = await res.json();
      if (res.ok) {
        const skipped = data.no_email && data.no_email.length > 0 ? ` (${data.no_email.join(', ')} skipped — no email on file)` : '';
        addToast(`Settlement emails sent to ${data.sent} player(s).${skipped}`, 'success');
      } else {
        addToast(data.error || 'Failed to send settlement emails', 'error');
      }
    } catch (e) {
      addToast('Failed to send settlement emails', 'error');
    }
  };

  // Events (Slice 2): reconcile a tee time's linked event from the Edit modal.
  // Compares desired state against the current event and creates, updates, or
  // deletes accordingly, then keeps eventsByTeetime (the card-badge map) in sync.
  // Re-read the org's events after a failed event save so the card badges reflect
  // the server rather than the attempt (a refusal can leave the index stale — e.g.
  // the row was created but the caller never learned its id). Same fetch as the
  // load effect above.
  const refreshEventsIndex = async () => {
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API_URL}/events`, { credentials: 'include', headers });
      if (response.ok) {
        const data = await response.json();
        const map = {};
        (data.events || []).forEach(ev => { if (ev.teetime_id) map[ev.teetime_id] = ev; });
        setEventsByTeetime(map);
      }
    } catch (e) { /* badges keep their last-known state */ }
  };
  // One place for every event-save failure: modal + index refresh.
  const failEventSave = (action, message) => {
    setEventSaveError({ action, message });
    refreshEventsIndex();
  };

  // Event logo (Sept 16, 2026): uploaded / removed AFTER the event row is saved (a
  // new event has no id until then). Failures are reported in the event error
  // modal — the event itself is already saved, and the modal says so.
  const syncEventLogo = async (eventId, teetimeId, desired) => {
    if (!eventId || (!desired.logo_file && !desired.remove_logo)) return;
    const authHeaders = {};
    const token = getAuthToken();
    if (token) authHeaders['Authorization'] = `Bearer ${token}`;
    try {
      let resp;
      if (desired.logo_file) {
        const fd = new FormData();
        fd.append('logo', desired.logo_file, desired.logo_name || 'logo.png');
        resp = await fetch(`${API_URL}/events/${eventId}/logo`, { method: 'POST', headers: authHeaders, credentials: 'include', body: fd });
      } else {
        resp = await fetch(`${API_URL}/events/${eventId}/logo`, { method: 'DELETE', headers: authHeaders, credentials: 'include' });
      }
      if (resp.ok) {
        const { event: ev } = await resp.json();
        if (ev && ev.teetime_id) setEventsByTeetime(prev => ({ ...prev, [ev.teetime_id]: ev }));
      } else {
        const err = await resp.json().catch(() => ({}));
        failEventSave('update', `The event was saved, but the logo could not be ${desired.logo_file ? 'uploaded' : 'removed'}: ${err.error || 'the server refused it.'}`);
      }
    } catch (err) {
      failEventSave('update', `The event was saved, but the logo could not be ${desired.logo_file ? 'uploaded' : 'removed'} — check your connection and try again from Edit event.`);
    }
  };

  const saveEventForTeetime = async (teetimeId, desired) => {
    const existing = eventsByTeetime[teetimeId];
    const wantEvent = !!desired.is_event && !!String(desired.name || "").trim();
    const headers = { 'Content-Type': 'application/json' };
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      if (wantEvent && existing) {
        // Update name/description on the existing event.
        // scoring_mode rides along when the caller supplies it (Team scoring, Sept 13,
        // 2026); when undefined JSON.stringify drops it and the server preserves the
        // tee time's current value (Rule 22).
        const resp = await fetch(`${API_URL}/events/${existing.id}`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify({ name: desired.name, description: desired.description, scoring_mode: desired.scoring_mode, postable_to_ghin: desired.postable_to_ghin, public_slug: desired.public_slug, confirm_reprint: desired.confirm_reprint })
        });
        if (resp.ok) {
          const { event: ev } = await resp.json();
          if (ev) setEventsByTeetime(prev => ({ ...prev, [teetimeId]: ev }));
          addToast("Event updated");
          await syncEventLogo(existing.id, teetimeId, desired);
        } else {
          // The server explains refusals (team scoring not enabled for the org; scoring
          // format locked because team scores exist) — show its words, not a generic one.
          const err = await resp.json().catch(() => ({}));
          failEventSave('update', err.error || "The event could not be updated.");
        }
      } else if (wantEvent && !existing) {
        // Newly marked as an event — create it, linked to this tee time.
        const resp = await fetch(`${API_URL}/events`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ name: desired.name, description: desired.description, teetime_id: teetimeId, scoring_mode: desired.scoring_mode, postable_to_ghin: desired.postable_to_ghin, public_slug: desired.public_slug })
        });
        if (resp.ok) {
          const { event: ev } = await resp.json();
          if (ev && ev.teetime_id) setEventsByTeetime(prev => ({ ...prev, [ev.teetime_id]: ev }));
          addToast("Event created");
          if (ev) await syncEventLogo(ev.id, teetimeId, desired);
          refreshEventAccess();   // the pass it used is no longer waiting
        } else {
          const err = await resp.json().catch(() => ({}));
          if (EVENT_PASS_CODES.includes(err.code)) {
            // No pass to use, or the tee time is bigger / later than the pass covers:
            // the Event Pass window explains it and offers the purchase or upgrade.
            refreshEventsIndex();
            openEventPass({ message: err.error, code: err.code, passId: err.pass_id || null, teetimeId });
          } else {
            failEventSave('create', err.error || "The event could not be created.");
          }
        }
      } else if (!desired.is_event && existing) {
        if (deskSession) { addToast("Only the organizer can remove the event.", "error"); return; }
        // Un-ticked — remove the event (the tee time stays).
        const resp = await fetch(`${API_URL}/events/${existing.id}`, {
          method: 'DELETE',
          headers,
          credentials: 'include'
        });
        if (resp.ok) {
          setEventsByTeetime(prev => {
            const next = { ...prev };
            delete next[teetimeId];
            return next;
          });
          addToast("Event removed");
          refreshEventAccess();   // an unfinalized event gives its pass back
        } else {
          const err = await resp.json().catch(() => ({}));
          failEventSave('remove', err.error || "The event could not be removed.");
        }
      }
      // wantEvent false and no existing event → nothing to do.
    } catch (err) {
      console.error('Error saving event from edit:', err);
      failEventSave(desired.is_event ? 'update' : 'remove', "Could not reach the server to save the event. Check your connection and try again.");
    }
  };

  const handleSaveEdit = async (updatedTeeTime, originalData) => {
    try {
      const originalTeeTime = originalData || teeTimes.find(t => t.id === updatedTeeTime.id);
      
      if (originalTeeTime) {
        // Check if status changed from Draft to Published
        const wasUnpublished = !originalTeeTime.status || originalTeeTime.status.toLowerCase() === 'draft';
        const nowPublished = updatedTeeTime.status && updatedTeeTime.status.toLowerCase() === 'published';
        const statusChangedToPublished = wasUnpublished && nowPublished;
        
        if (statusChangedToPublished) {
          await updateItem(updatedTeeTime.id, updatedTeeTime);
          addToast("Tee time updated successfully");
          setShowEditModal(false);
          setEditingTeeTime(null);
          setNotifyingTeeTime(originalTeeTime);
          setShowNotifyModal(true);
          return;
        }
        
        const oldSlots = originalTeeTime.slots || 0;
        const newSlots = updatedTeeTime.slots || 0;
        const currentPlayers = Array.isArray(originalTeeTime.players) ? originalTeeTime.players : [];
        const currentWaitlist = Array.isArray(originalTeeTime.waitlist) ? originalTeeTime.waitlist : [];

        // Check if slot reduction would push confirmed players over the new limit
        const slotsReduced = newSlots < oldSlots;
        const confirmedCount = currentPlayers.length;

        if (slotsReduced && confirmedCount > newSlots) {
          // Sort by addedAt descending — most recently confirmed get bumped first
          const sorted = [...currentPlayers].sort((a, b) => {
            const dateA = a.addedAt ? new Date(a.addedAt) : new Date(0);
            const dateB = b.addedAt ? new Date(b.addedAt) : new Date(0);
            return dateB - dateA; // newest first
          });
          const excessCount = confirmedCount - newSlots;
          const playersToWaitlist = sorted.slice(0, excessCount);

          // Show confirmation modal before proceeding
          setSlotReductionData({ updatedTeeTime, playersToWaitlist });
          setShowSlotReductionModal(true);
          return; // Wait for organizer to confirm or cancel
        }

        // If slots increased and there are waitlisted players, auto-promote them
        const slotsIncreased = newSlots > oldSlots;
        if (slotsIncreased && currentWaitlist.length > 0) {
          const availableSlots = newSlots - confirmedCount;
          if (availableSlots > 0) {
            // Save the slot change first
            await updateItem(updatedTeeTime.id, updatedTeeTime);

            // Promote waitlist players into confirmed slots (oldest waitlist first)
            const promoteCount = Math.min(availableSlots, currentWaitlist.length);
            const idByEmail = new Map(players.map(p => [p.email, p.id]));
            const emailById = new Map(players.map(p => [p.id, p.email]));

            // Build current confirmed emails list
            let newPlayerEmails = currentPlayers.map(p =>
              typeof p === 'string' ? p : p.email
            ).filter(Boolean);

            // Normalize waitlist to IDs
            const currentWaitlistIds = currentWaitlist.map(w => typeof w === 'string' ? w : w?.id).filter(Boolean);

            // Promote the first N waitlist players (by position — already ordered by addedAt)
            const promoteIds = currentWaitlistIds.slice(0, promoteCount);
            const remainingWaitlistIds = currentWaitlistIds.slice(promoteCount);

            promoteIds.forEach(id => {
              const email = emailById.get(id);
              if (email && !newPlayerEmails.includes(email)) {
                newPlayerEmails.push(email);
              }
            });

            await updateItem(updatedTeeTime.id, {
              ...updatedTeeTime,
              players: newPlayerEmails,
              waitlist: remainingWaitlistIds,
              // Nothing is newly cancelled in a promotion (Sept 16, 2026). The backend
              // preserves unnamed rows (Rule 22) and upserts every cancelled[] entry it
              // is handed, so re-sending the historical list only risked flipping a
              // player who had re-RSVP'd since this page loaded back to cancelled.
              cancelled: [],
            });

            const promotedNames = promoteIds
              .map(id => players.find(p => p.id === id)?.name || 'Player')
              .join(', ');
            addToast(`Slots updated. ${promoteCount} player${promoteCount > 1 ? 's' : ''} promoted from waitlist: ${promotedNames}`, 'success');
            setShowEditModal(false);
            setEditingTeeTime(null);
            return;
          }
        }
      }
      
      await updateItem(updatedTeeTime.id, updatedTeeTime);
      addToast("Tee time updated successfully");
      setShowEditModal(false);
      setEditingTeeTime(null);
    } catch (err) {
      console.error('Error in handleSaveEdit:', err);
      if (err && EVENT_PASS_CODES.includes(err.code)) {
        // More spots / golfers than the event's pass covers, or a date past it.
        openEventPass({ message: err.message, code: err.code, passId: err.body && err.body.pass_id, teetimeId: updatedTeeTime && updatedTeeTime.id });
        return;
      }
      addToast(withReason("Error updating tee time", err), "error");
    }
  };

  // Called when organizer confirms the slot reduction modal
  const handleConfirmSlotReduction = async () => {
    if (!slotReductionData) return;
    const { updatedTeeTime, playersToWaitlist } = slotReductionData;
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Save the slot change
      await updateItem(updatedTeeTime.id, updatedTeeTime);

      // Move each excess player to waitlist via API
      for (const player of playersToWaitlist) {
        await fetch(`${API_URL}/teetimes/${updatedTeeTime.id}/move-to-waitlist`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({ player_id: player.id }),
        });
      }

      addToast(`Slots updated. ${playersToWaitlist.length} player${playersToWaitlist.length > 1 ? 's' : ''} moved to waitlist.`, 'success');
      setShowSlotReductionModal(false);
      setSlotReductionData(null);
      setShowEditModal(false);
      setEditingTeeTime(null);
    } catch (err) {
      console.error('Error applying slot reduction:', err);
      addToast(withReason("Error updating tee time", err), "error");
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingTeeTime(null);
  };

  const handlePublish = (id) => {
    const teeTime = teeTimes.find(t => t.id === id);
    if (teeTime) {
      setPublishingTeeTime(teeTime);
      setShowPublishModal(true);
    }
  };

  const handlePublishConfirm = async (publishData) => {
    if (!publishingTeeTime) return;
    
    setIsPublishing(true); // START loading
    
    const activePlayers = players.filter((p) => p.active);
    
    try {
      // 1. Update tee time status to Published FIRST. Tee-time fields only
      //    (Sept 16, 2026): the roster lives in tee_time_players and is untouched
      //    when players/waitlist/cancelled are absent (undefined is dropped by
      //    JSON.stringify). The full object used to re-send the historical
      //    cancelled list (stale-cancel hazard) and waitlist OBJECTS where the
      //    backend expects ids.
      const updatedTeeTime = { ...publishingTeeTime, status: "Published", players: undefined, waitlist: undefined, cancelled: undefined };
      await updateItem(publishingTeeTime.id, updatedTeeTime);
      
      // Skip notifications if "none" selected
      if (publishData.publishTo === "none") {
        await refresh();
        addToast("Tee time published (no notifications sent)", "success");
        setShowPublishModal(false);
        setPublishingTeeTime(null);
        return;
      }
      
      // Determine recipients
      let recipients = [];
      
      if (publishData.publishTo === "all") {
        recipients = activePlayers.filter(p => p.role !== 'guest');
      } else if (publishData.publishTo === "select") {
        recipients = activePlayers.filter(p => publishData.playerIds.includes(p.id));
      } else if (publishData.publishTo === "group") {
        const group = groups.find(g => g.id === publishData.groupId);
        if (group) {
          recipients = activePlayers.filter(p => (group.player_ids || []).includes(p.id));
        }
      }
      
      if (recipients.length === 0) {
        addToast("No recipients found", "error");
        return;
      }
      
      // 2. Send notifications via API
      // FIXED: Include JWT auth header for multi-tenant support
      const publishHeaders = { 'Content-Type': 'application/json' };
      const publishToken = getAuthToken();
      if (publishToken) {
        publishHeaders['Authorization'] = `Bearer ${publishToken}`;
      }
      
      const publishRes = await fetchWithAuthRetry(`${API_URL}/teetimes/${publishingTeeTime.id}/publish`, {
        method: 'POST',
        headers: publishHeaders,
        credentials: 'include',
        body: JSON.stringify({
          teeTime: updatedTeeTime,
          publishTo: publishData.publishTo, // backend uses this to allow guests only in "select" mode
          recipients: recipients.map(p => ({ id: p.id, email: p.email, name: p.name })),
          customMessage: publishData.customMessage || ""
        })
      });
      // Oct 28, 2026: a refused send used to be reported as "published to N players".
      if (!publishRes.ok) {
        const pd = await publishRes.json().catch(() => ({}));
        throw new Error(pd.error || pd.message || `the notices were not sent (${publishRes.status})`);
      }
      
      // 3. Refresh tee times data to update UI immediately
      await refresh();
      
      addToast(`Tee time published to ${recipients.length} player${recipients.length === 1 ? '' : 's'}`);
      setShowPublishModal(false);
      setPublishingTeeTime(null);
    } catch (err) {
      console.error('Error publishing tee time:', err);
      addToast(withReason("Failed to publish tee time", err), "error");
    } finally {
      setIsPublishing(false); // END loading (even if error)
    }
  };

  // NEW: Notify Players handlers
  const handleNotify = (id) => {
    const teeTime = teeTimes.find(t => t.id === id);
    if (teeTime) {
      setNotifyingTeeTime(teeTime);
      setShowNotifyModal(true);
    }
  };

  // NEW: Invite Link handler
  const handleInvite = (id) => {
    const teeTime = teeTimes.find(t => t.id === id);
    if (teeTime) {
      setInvitingTeeTime(teeTime);
      setShowInviteModal(true);
    }
  };

  const handleNotifyConfirm = async (publishData) => {
    if (!notifyingTeeTime) return;
    
    setIsPublishing(true); // Reuse publishing state for loading indicator
    
    const activePlayers = players.filter((p) => p.active);
    
    try {
      // Determine recipients
      let recipients = [];
      
      if (publishData.publishTo === "all") {
        // Exclude guests from "All Players" notifications
        recipients = activePlayers.filter(p => p.role !== 'guest');
      } else if (publishData.publishTo === "select") {
        recipients = activePlayers.filter(p => publishData.playerIds.includes(p.id));
      } else if (publishData.publishTo === "group") {
        const group = groups.find(g => g.id === publishData.groupId);
        if (group) {
          recipients = activePlayers.filter(p => (group.player_ids || []).includes(p.id));
        }
      }
      
      if (recipients.length === 0) {
        addToast("No recipients found", "error");
        setIsPublishing(false);
        return;
      }
      
      // Filter out players already registered for this tee time
      const playersOnTeeTime = notifyingTeeTime.players || [];
      const playersOnWaitlist = notifyingTeeTime.waitlist || [];
      
      const filteredRecipients = recipients.filter(p => {
        const isOnTeeTime = playersOnTeeTime.some(player => samePlayer(player, p));
        const isOnWaitlist = playersOnWaitlist.some(player => samePlayer(player, p));
        return !isOnTeeTime && !isOnWaitlist;
      });
      
      if (filteredRecipients.length === 0) {
        addToast("All selected players are already registered for this tee time", "error");
        setIsPublishing(false);
        return;
      }
      
      // Send notifications via API (tee time status stays "Published")
      // FIXED: Include JWT auth header for multi-tenant support
      const notifyHeaders = { 'Content-Type': 'application/json' };
      const notifyToken = getAuthToken();
      if (notifyToken) {
        notifyHeaders['Authorization'] = `Bearer ${notifyToken}`;
      }
      
      await fetch(`${API_URL}/teetimes/${notifyingTeeTime.id}/notify`, {
        method: 'POST',
        headers: notifyHeaders,
        credentials: 'include',
        body: JSON.stringify({
          teeTime: notifyingTeeTime,
          publishTo: publishData.publishTo, // backend uses this to allow guests only in "select" mode
          recipients: filteredRecipients.map(p => ({ id: p.id, email: p.email, name: p.name })),
          customMessage: publishData.customMessage || "",
          sendEmail: publishData.sendEmail !== false // Default to true if not specified
        })
      });
      
      const excludedCount = recipients.length - filteredRecipients.length;
      const message = excludedCount > 0 
        ? `Notifications sent to ${filteredRecipients.length} player${filteredRecipients.length === 1 ? '' : 's'} (${excludedCount} already registered, excluded)`
        : `Notifications sent to ${filteredRecipients.length} player${filteredRecipients.length === 1 ? '' : 's'}`;
      
      addToast(message);
      setShowNotifyModal(false);
      setNotifyingTeeTime(null);
    } catch (err) {
      console.error('Error notifying players:', err);
      addToast(withReason("Failed to send notifications", err), "error");
    } finally {
      setIsPublishing(false);
    }
  };

  // Weather refresh handler
  const handleWeatherRefresh = async () => {
    setRefreshingWeather(true);
    try {
      // FIXED: Include JWT auth header for multi-tenant support
      const weatherHeaders = { 'Content-Type': 'application/json' };
      const weatherToken = getAuthToken();
      if (weatherToken) {
        weatherHeaders['Authorization'] = `Bearer ${weatherToken}`;
      }
      
      const response = await fetch(`${API_URL}/weather/refresh-all`, {
        method: 'POST',
        headers: weatherHeaders,
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Refresh tee times to show updated weather
        await refresh();
        
        const { updated, skipped, failed } = result.results;
        addToast(`Weather updated! ✅ ${updated} updated, ⏭️ ${skipped} skipped, ❌ ${failed} failed`);
      } else {
        addToast('Weather refresh failed', 'error');
      }
    } catch (error) {
      console.error('Error refreshing weather:', error);
      addToast('Weather refresh error', 'error');
    } finally {
      setRefreshingWeather(false);
    }
  };

  const handleConfirmPlayers = async ({ selectedIds, preCheckedIds, notifyPlayers }) => {
    const tt = teeTimes.find(t => t.id === selectedTeeTimeId);
    if (!tt) return;

    // Create maps for ID/email conversions
    const idByEmail = new Map(players.map((p) => [p.email, p.id]));
    const emailById = new Map(players.map((p) => [p.id, p.email]));
    const nameById = new Map(players.map((p) => [p.id, p.name]));
    
    // tt.players could contain emails OR objects - normalize to emails
    const currentEmails = (tt.players || []).map(playerData => {
      if (typeof playerData === 'string') {
        return playerData;  // It's already an email
      } else if (playerData && typeof playerData === 'object') {
        return playerData.email;  // Extract email from object
      }
      return null;
    }).filter(Boolean);
    
    const currentWaitlist = Array.isArray(tt.waitlist) ? tt.waitlist : [];
    // BUGFIX: Normalize waitlist to IDs - waitlist can contain strings OR objects with {id, name, ...}
    const currentWaitlistIds = currentWaitlist.map(w => typeof w === 'string' ? w : w?.id).filter(Boolean);
    const currentCancelled = Array.isArray(tt.cancelled) ? tt.cancelled : [];
    const removedIds = preCheckedIds.filter((id) => !selectedIds.includes(id));
    
    // Track newly cancelled players with timestamp
    let newCancelled = [...currentCancelled];
    removedIds.forEach(id => {
      // Check if this player was active or on waitlist (not already cancelled)
      const playerEmail = emailById.get(id);
      const wasPlaying = currentEmails.includes(playerEmail);
      const wasWaitlist = currentWaitlistIds.includes(id);  // FIXED: Use normalized IDs
      const alreadyCancelled = currentCancelled.some(c => c.id === id);
      
      if ((wasPlaying || wasWaitlist) && !alreadyCancelled) {
        newCancelled.push({
          id: id,
          name: nameById.get(id),
          timestamp: new Date().toISOString()
        });
      }
    });
    
    // Remove cancelled players that are being re-added
    const readdedIds = selectedIds.filter(id => currentCancelled.some(c => c.id === id));
    newCancelled = newCancelled.filter(c => !readdedIds.includes(c.id));
    
    // Filter out removed players (working with emails)
    let newPlayerEmails = currentEmails.filter((email) => {
      const playerId = idByEmail.get(email);
      return !removedIds.includes(playerId);
    });
    
    // CRITICAL FIX: Determine which players were removed from active slots vs waitlist
    const removedFromActive = removedIds.filter(id => {
      const email = emailById.get(id);
      return currentEmails.includes(email);
    });
    
    const removedFromWaitlist = removedIds.filter(id => currentWaitlistIds.includes(id));  // FIXED: Use normalized IDs
    
    // Remove players from waitlist - filter using normalized IDs
    let newWait = currentWaitlistIds.filter((id) => !removedIds.includes(id));
    
    // CRITICAL FIX: Only promote from waitlist if ACTIVE players were removed
    // Do NOT promote if only waitlist players were removed
    const slotsAvailable = Math.max(0, utils.toInt(tt.slots) - newPlayerEmails.length);
    
    // Promote waitlist players ONLY if active slots opened up (not just waitlist removals)
    if (slotsAvailable > 0 && newWait.length > 0 && removedFromActive.length > 0) {
      const promoteCount = Math.min(slotsAvailable, newWait.length);
      const promoteIds = newWait.slice(0, promoteCount);
      promoteIds.forEach((id) => {
        const email = emailById.get(id);
        if (email && !newPlayerEmails.includes(email)) {
          newPlayerEmails.push(email);
        }
      });
      newWait = newWait.slice(promoteCount);
    }
    
    // Find newly selected players
    const confirmedPlayerIds = currentEmails.map((email) => idByEmail.get(email)).filter(Boolean);
    const newOnlyIds = selectedIds.filter((id) => 
      !confirmedPlayerIds.includes(id) && 
      !currentWaitlistIds.includes(id) &&   // FIXED: Use normalized IDs
      !currentCancelled.some(c => c.id === id)
    );
    
    const remaining = Math.max(0, utils.toInt(tt.slots) - newPlayerEmails.length);
    const addNowIds = newOnlyIds.slice(0, remaining);
    const overflowIds = newOnlyIds.slice(remaining);
    
    // Add new players
    addNowIds.forEach((id) => {
      const email = emailById.get(id);
      if (email && !newPlayerEmails.includes(email)) {
        newPlayerEmails.push(email);
      }
    });
    
    // Add overflow to waitlist
    overflowIds.forEach((id) => {
      if (!newWait.includes(id)) newWait.push(id);
    });

    // Re-add cancelled players who are being selected again
    readdedIds.forEach((id) => {
      const email = emailById.get(id);
      if (email && !newPlayerEmails.includes(email)) {
        const remainingSlots = utils.toInt(tt.slots) - newPlayerEmails.length;
        if (remainingSlots > 0) {
          newPlayerEmails.push(email);
        } else {
          if (!newWait.includes(id)) newWait.push(id);
        }
      }
    });

    try {
      // Convert waitlist IDs to player objects for backend compatibility
      const newWaitlistObjects = newWait.map(playerId => {
        const playerEmail = emailById.get(playerId);
        const playerName = nameById.get(playerId);
        return {
          id: playerId,
          email: playerEmail,
          name: playerName
        };
      });
      
      await updateItem(selectedTeeTimeId, {
        ...tt,
        players: newPlayerEmails,  // Email strings
        waitlist: newWait,  // Send IDs only
        // Only the cancellations made in THIS save (Sept 16, 2026). The backend
        // upserts every cancelled[] entry it is handed and preserves unnamed rows
        // (Rule 22), so re-sending the historical list was pure hazard: a player
        // who cancelled, then re-RSVP'd from the player app after this page
        // loaded, was flipped back to cancelled by his stale entry — signup lost,
        // confirmation email in hand. (Sunday Golfers / Kurt Eilo, Sept 15, 2026.)
        cancelled: newCancelled.filter(c => !currentCancelled.some(x => x.id === c.id)),
        filled: Math.min(utils.toInt(tt.slots), newPlayerEmails.length),
        // Quiet events (Oct 8, 2026): the modal's "Email the players I'm adding or
        // removing" box. The backend defaults to quiet on an event and to emailing
        // on an ordinary tee time when this is absent.
        ...(typeof notifyPlayers === 'boolean' ? { notify_players: notifyPlayers } : {})
      });
      
      addToast(`Player roster updated: ${newPlayerEmails.length} playing, ${newWait.length} waitlisted${notifyPlayers === false ? ' · nobody emailed' : ''}`);
      setShowPlayerPicker(false);
      setSelectedTeeTimeId(null);
    } catch (err) {
      console.error('Error updating players:', err);
      if (err.message && err.message.includes('already booked for a tee time at')) {
        setOverlapConflict({ message: err.message });
        setShowOverlapModal(true);
      } else {
        addToast(`Error updating players: ${err.message}`, "error");
      }
    }
  };

  const selectedTeeTime = teeTimes.find((t) => t.id === selectedTeeTimeId);

  if (loadingPlayers || loadingTeeTimes) {
    return <div style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>Loading data from server...</div>;
  }

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {sessionExpired && !deskSession && (
        <div data-testid="session-expired" style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(17,24,39,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: "22px 24px", maxWidth: 420, width: "100%", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", marginBottom: 8 }}>Please sign in again</div>
            <div style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.5, marginBottom: 16 }}>Your sign-in has expired, so changes can&rsquo;t be saved. Sign in again and carry on where you left off.</div>
            <button type="button" onClick={signInAgain} style={{ padding: "10px 18px", fontSize: 15, fontWeight: 700, borderRadius: 8, border: "none", background: "#2d6a4f", color: "#fff", cursor: "pointer" }}>Sign in</button>
          </div>
        </div>
      )}
      {/* DASHBOARD HOME HEADER (Sept 25, 2026 — Dennis). Replaces the top-right help
          bar: the greeting, the day at a glance, the same Guide / Help / What's New
          buttons, and "New tee time", which opens and closes the create form.
          Not on the event desk: it has "How the desk works" instead. */}
      {!deskSession && (
      <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: "#14281d", lineHeight: 1.2 }}>{homeGreeting}</div>
          <div style={{ fontSize: 14, color: "#4b5563", marginTop: 4 }}>{homeSubline}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {isEventAccount && (
            <a
              href="/events/ready"
              data-testid="event-checklist-link"
              style={{ padding: "7px 12px", border: "1px solid #86efac", background: "#f0fdf4", borderRadius: 8, fontSize: 13, color: "#166534", fontWeight: 700, display: "flex", alignItems: "center", gap: 6, textDecoration: "none" }}
              title="Your event's next steps, in order"
            >
              <span style={{ fontSize: 14 }}>📋</span>
              <span>Event checklist</span>
            </a>
          )}
          <button
            onClick={() => setShowGuidesChooser(true)}
            style={{ padding: "7px 12px", border: "1px solid #d1d5db", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#4b5563", display: "flex", alignItems: "center", gap: 6 }}
            title="Guides: getting started, and setting up events / tournaments"
          >
            <span style={{ fontSize: 14 }}>📖</span>
            <span>Guides</span>
          </button>
          <button
            onClick={() => setShowHelp(true)}
            style={{ padding: "7px 12px", border: "1px solid #d1d5db", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#4b5563", display: "flex", alignItems: "center", gap: 6 }}
            title="What is this page about?"
          >
            <span style={{ fontSize: 14 }}>ⓘ</span>
            <span>Help</span>
          </button>
          <button
            onClick={() => setShowWhatsNew(true)}
            style={{
              padding: "7px 12px",
              border: `1px solid ${whatsNewUnread > 0 ? '#f59e0b' : '#d1d5db'}`,
              background: whatsNewUnread > 0 ? '#fffbeb' : '#fff',
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
              color: whatsNewUnread > 0 ? '#b45309' : '#4b5563',
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
            title={whatsNewUnread > 0 ? `${whatsNewUnread} new update${whatsNewUnread > 1 ? 's' : ''}` : "What's New & Tips"}
          >
            <span style={{ fontSize: 14 }}>{whatsNewUnread > 0 ? '🔔' : '🔕'}</span>
            <span>What's New</span>
          </button>
          <button
            onClick={() => setCreateOpen(!createFormOpen)}
            aria-expanded={createFormOpen}
            style={{
              padding: "9px 16px",
              border: createFormOpen ? "1px solid #d1d5db" : "1px solid #2d6a4f",
              background: createFormOpen ? "#fff" : "#2d6a4f",
              color: createFormOpen ? "#374151" : "#fff",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 700,
              whiteSpace: "nowrap"
            }}
          >
            {createFormOpen ? "✕ Close form" : "+ New tee time"}
          </button>
        </div>
      </div>
      )}

      {/* Read-Only Banner for Cancelled Accounts */}
      {isReadOnly && (
        <div style={{
          padding: 16,
          background: isExpiredTrial ? '#fef3c7' : '#fef2f2',
          border: `1px solid ${isExpiredTrial ? '#fcd34d' : '#fecaca'}`,
          borderRadius: 8,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <span style={{ fontSize: 24 }}>{isExpiredTrial ? '⏰' : '🚫'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: isExpiredTrial ? '#92400e' : '#991b1b' }}>
              {isExpiredTrial ? 'Free Trial Ended — View-Only Mode' : 'Account Cancelled — Read-Only Mode'}
            </div>
            <div style={{ fontSize: 13, color: isExpiredTrial ? '#a16207' : '#b91c1c', marginTop: 2 }}>
              {isExpiredTrial 
                ? 'Your free trial has ended. You can view your data but cannot make changes. Go to Settings → Subscription to subscribe.'
                : 'You can view your data but cannot create, edit, or delete tee times. Go to Settings → Subscription to reactivate.'}
            </div>
          </div>
          <button
            onClick={async () => {
              if (isExpiredTrial) {
                // Go directly to Stripe Checkout for Starter plan
                try {
                  const token = getAuthToken();
                  const response = await fetch(`${API_URL}/subscription/checkout`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      tier: 'starter',
                      billingCycle: 'monthly',
                      isReactivation: false,
                      successUrl: `${window.location.origin}/dashboard?tab=settings&subscription=success`,
                      cancelUrl: `${window.location.origin}/dashboard?tab=settings`
                    })
                  });
                  const data = await response.json();
                  if (data.url) window.location.href = data.url;
                } catch (err) {
                  console.error('Error starting checkout:', err);
                }
              } else {
                // Cancelled accounts need plan selection first - go to Settings
                const event = new CustomEvent('navigateToTab', { detail: 'settings' });
                window.dispatchEvent(event);
              }
            }}
            style={{
              padding: '8px 16px',
              background: '#2d6a4f',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {isExpiredTrial ? 'Subscribe' : 'Reactivate'}
          </button>
        </div>
      )}
      
      {!deskSession && createFormOpen && <CreateTeeTimeInline onCreate={handleCreate} isReadOnly={isReadOnly} onReadOnlyClick={showReadOnlyToast} eventsEnabled={eventsEnabled} canCreateEvent={canCreateEvent === null ? eventsEnabled : canCreateEvent} onSaveEvent={saveEventForTeetime} />}

      {/* Next up (Sept 25, 2026) — the soonest published upcoming tee time. */}
      {!deskSession && homeInfo.nextUp && (
        <NextUpCard
          teeTime={homeInfo.nextUp}
          event={eventsByTeetime[homeInfo.nextUp.id]}
          whenLabel={nextUpWhen}
          gamesEnabled={gamesEnabled}
          gamesLocked={gamesLocked}
          playerByEmail={playerByEmail}
          isReadOnly={isReadOnly}
          onReadOnlyClick={showReadOnlyToast}
          onManagePlayers={() => handleManagePlayers(homeInfo.nextUp.id)}
          onNotify={() => handleNotify(homeInfo.nextUp.id)}
          onGames={() => handleGames(homeInfo.nextUp.id)}
          onInvite={() => handleInvite(homeInfo.nextUp.id)}
          onEdit={() => handleEdit(homeInfo.nextUp.id)}
          onPublish={() => handlePublish(homeInfo.nextUp.id)}
          onDelete={() => handleDeleteClick(homeInfo.nextUp.id)}
          onEventSetup={() => setCardEventSetupId(homeInfo.nextUp.id)}
        />
      )}

      {/* Draft reminder (Sept 25, 2026) — upcoming drafts are invisible to players. */}
      {!deskSession && homeInfo.drafts.length > 0 && (
        <div style={{ display: "grid", gap: 8, padding: "12px 16px", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10 }}>
          {homeInfo.drafts.slice(0, 3).map((d) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, color: "#78350f", flex: 1, minWidth: 220 }}>
                📝 <strong>{utils.formatDate(d.date)} · {d.course}</strong> is still a draft, so players can't see it yet.
              </span>
              <Button
                onClick={() => { if (isReadOnly) { showReadOnlyToast(); return; } handlePublish(d.id); }}
                style={{ fontSize: 13, fontWeight: 700, background: isReadOnly ? "#9ca3af" : "#d97706", color: "#fff", padding: "7px 14px", borderRadius: 8 }}
              >
                📣 Publish
              </Button>
            </div>
          ))}
          {homeInfo.drafts.length > 3 && (
            <div style={{ fontSize: 13, color: "#92400e" }}>…and {homeInfo.drafts.length - 3} more draft{homeInfo.drafts.length - 3 === 1 ? '' : 's'} in the list below.</div>
          )}
        </div>
      )}
      
      {/* EVENT DESK FIRST PAGE (Sept 17, 2026) — Dennis: with one event per desk the
          Show filters and the rest of the toolbar have no use here. The desk gets the
          event itself; the organizer dashboard below is unchanged. */}
      {deskSession && (() => {
        const deskTeeTime = filteredAndSortedTeeTimes[0] || (Array.isArray(teeTimes) ? teeTimes[0] : null) || null;
        return (
          <EventHome
            teeTime={deskTeeTime}
            event={deskTeeTime ? eventsByTeetime[deskTeeTime.id] : null}
            gamesEnabled={gamesEnabled}
            onManagePlayers={() => { if (deskTeeTime) handleManagePlayers(deskTeeTime.id); }}
            onEdit={() => { if (deskTeeTime) handleEdit(deskTeeTime.id); }}
            onNotify={() => { if (deskTeeTime) handleNotify(deskTeeTime.id); }}
            onGames={() => { if (deskTeeTime) handleGames(deskTeeTime.id); }}
            onEventSetup={() => { if (deskTeeTime) setCardEventSetupId(deskTeeTime.id); }}
            onRefresh={() => refresh()}
          />
        );
      })()}

      {/* Filter Controls - Compact Layout */}
      {!deskSession && (
      <Card>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {/* Filter Buttons */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Show:</span>
            <Button 
              variant={filter === "upcoming" ? "default" : "outline"} 
              onClick={() => setFilter("upcoming")}
              style={{ fontSize: 12, padding: "5px 10px" }}
            >
              Upcoming
            </Button>
            <Button 
              variant={filter === "today" ? "default" : "outline"} 
              onClick={() => setFilter("today")}
              style={{ fontSize: 12, padding: "5px 10px" }}
            >
              Today
            </Button>
            <Button 
              variant={filter === "past" ? "default" : "outline"} 
              onClick={() => setFilter("past")}
              style={{ fontSize: 12, padding: "5px 10px" }}
            >
              Past
            </Button>
            <Button 
              variant={filter === "all" ? "default" : "outline"} 
              onClick={() => setFilter("all")}
              style={{ fontSize: 12, padding: "5px 10px" }}
            >
              All
            </Button>
          </div>
          
          <div style={{ width: 1, height: 20, background: "#e5e7eb" }}></div>
          
          {/* Sort Dropdown */}
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            style={{ width: 80, fontSize: 12, padding: "5px 8px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", cursor: "pointer" }}
          >
            <option value="date">Date ↑</option>
            <option value="date-desc">Date ↓</option>
            <option value="course">Course</option>
            <option value="slots">Slots</option>
          </select>
          
          <div style={{ width: 1, height: 20, background: "#e5e7eb" }}></div>
          
          {/* View Toggle */}
          <Button 
            variant={viewMode === "card" ? "default" : "outline"} 
            onClick={() => setViewMode("card")}
            style={{ fontSize: 12, padding: "5px 8px" }}
            title="Card View"
          >
            📇
          </Button>
          <Button 
            variant={viewMode === "list" ? "default" : "outline"} 
            onClick={() => setViewMode("list")}
            style={{ fontSize: 12, padding: "5px 8px" }}
            title="List View"
          >
            📋
          </Button>
          
          <div style={{ width: 1, height: 20, background: "#e5e7eb" }}></div>
          
          {/* Search Bar */}
          <input 
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontSize: 13, width: 160, padding: "5px 10px", border: "1px solid #e5e7eb", borderRadius: 6 }}
          />
          
          {/* Spacer to push export buttons right */}
          <div style={{ flex: 1 }}></div>
          
          {/* Export Buttons */}
          <Button 
            variant="outline"
            onClick={() => {
              const csv = exportUtils.teeTimesToCSV(filteredAndSortedTeeTimes);
              const filterLabel = filter === 'all' ? 'all' : filter;
              exportUtils.downloadCSV(csv, `tee-times-${filterLabel}-${new Date().toISOString().split('T')[0]}.csv`);
              addToast('Tee times exported to CSV');
            }}
            style={{ fontSize: 12, padding: "5px 10px", whiteSpace: "nowrap" }}
          >
            📥 Export
          </Button>
          
          <Button 
            variant="outline"
            onClick={() => {
              exportUtils.printTeeTimes(filteredAndSortedTeeTimes, filter);
              addToast('Print view opened');
            }}
            style={{ fontSize: 12, padding: "5px 10px", whiteSpace: "nowrap" }}
            title="Print this list of tee times (scorecards, cart signs and tee sheets are on the Print tab)"
          >
            🖨️ Print list
          </Button>
        </div>
      </Card>
      )}
      
      {/* Tee Times Display */}
      {!deskSession && listTeeTimes.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>
                {filter === "upcoming" ? (hideNextFromList ? "Coming up" : "Upcoming Tee Times") : 
                 filter === "today" ? "Today's Tee Times" : 
                 filter === "past" ? "Past Tee Times" : "All Tee Times"}
              </div>
              <Badge style={{ background: "#2d6a4f", color: "#fff" }}>{listTeeTimes.length}</Badge>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {!deskSession && <Button 
                variant="outline"
                onClick={handleWeatherRefresh}
                disabled={refreshingWeather}
                style={{ 
                  fontSize: 13,
                  background: refreshingWeather ? '#f3f4f6' : '#fff',
                  cursor: refreshingWeather ? 'not-allowed' : 'pointer',
                  opacity: refreshingWeather ? 0.6 : 1
                }}
                title="Manually refresh weather for all upcoming tee times"
              >
                {refreshingWeather ? '🔄 Updating...' : '🌤️ Refresh Weather'}
              </Button>}
              <Button 
                variant="outline"
                onClick={() => {
                  refresh();
                  addToast('Tee times refreshed');
                }}
                style={{ fontSize: 13 }}
                title="Refresh tee times to see latest player RSVPs"
              >
                🔄 Refresh
              </Button>
            </div>
          </div>
          
          {viewMode === "card" ? (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
              {listTeeTimes.map((t) => (
                <TeeTimeCard 
                  key={t.id} 
                  teeTime={t} 
                  onDelete={deskSession ? null : () => handleDeleteClick(t.id)} 
                  onManagePlayers={() => handleManagePlayers(t.id)}
                  onEdit={() => handleEdit(t.id)}
                  onPublish={() => handlePublish(t.id)}
                  onNotify={() => handleNotify(t.id)}
                  onInvite={() => handleInvite(t.id)}
                  onGames={() => handleGames(t.id)}
                  onFinalize={() => handleFinalize(t.id)}
                  gamesEnabled={gamesEnabled}
                  gamesLocked={gamesLocked}
                  event={eventsByTeetime[t.id]}
                  playerByEmail={playerByEmail}
                  isReadOnly={isReadOnly}
                  onReadOnlyClick={showReadOnlyToast}
                  settings={orgSettings}
                  onEventSetup={() => setCardEventSetupId(t.id)}
                />
              ))}
            </div>
          ) : (
            <Card>
              <div style={{ display: "grid", gap: isMobile ? 8 : 1 }}>
                {listTeeTimes.map((t, idx) => (
                  isMobile ? (
                    // Mobile list row — stacked compact format
                    <div
                      key={t.id}
                      style={{
                        padding: 12,
                        background: idx % 2 === 0 ? "#f9fafb" : "#fff",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb"
                      }}
                    >
                      {/* Top row: course + slots badge */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, flex: 1, minWidth: 0 }}>{t.course}</div>
                        <Badge style={{
                          background: utils.safePlayerCount(t.players) >= utils.toInt(t.slots) ? "#dcfce7" : "#fef3c7",
                          color: utils.safePlayerCount(t.players) >= utils.toInt(t.slots) ? "#15803d" : "#92400e",
                          flexShrink: 0
                        }}>
                          {utils.safePlayerCount(t.players)}/{t.slots}
                        </Badge>
                      </div>

                      {/* Date / Time line */}
                      <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>
                        {utils.formatDate(t.date)} · {utils.formatTime(t.time)}
                        {Array.isArray(t.waitlist) && t.waitlist.length > 0 && (
                          <span style={{ marginLeft: 8, color: "#92400e" }}>
                            · ⏳ {t.waitlist.length} waiting
                          </span>
                        )}
                      </div>

                      {/* Player names */}
                      {getPlayerNames(t).length > 0 && (
                        <div style={{
                          fontSize: 12,
                          color: "#6b7280",
                          marginBottom: 10,
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden"
                        }}>
                          {getPlayerNames(t).join(", ")}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {t.status === "Draft" ? (
                          <>
                            <Button
                              onClick={() => handleManagePlayers(t.id)}
                              style={{ flex: "1 1 auto", fontSize: 12, background: "#2d6a4f", color: "#fff", padding: "8px 10px", minHeight: 36 }}
                            >
                              Manage
                            </Button>
                            <Button
                              onClick={() => handlePublish(t.id)}
                              style={{ flex: "1 1 auto", fontSize: 12, background: "#f59e0b", color: "#fff", padding: "8px 10px", minHeight: 36 }}
                            >
                              📣 Publish
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleEdit(t.id)}
                              style={{ fontSize: 12, padding: "8px 10px", minHeight: 36 }}
                            >
                              Edit
                            </Button>
                            {!deskSession && <MoreActionsMenu
                              items={[{ label: '🗑️ Delete tee time', danger: true, onClick: () => handleDeleteClick(t.id) }]}
                              style={{ marginLeft: 'auto' }}
                            />}
                          </>
                        ) : (
                          <>
                            <Button
                              onClick={() => handleManagePlayers(t.id)}
                              style={{ flex: "1 1 auto", fontSize: 12, background: "#2d6a4f", color: "#fff", padding: "8px 10px", minHeight: 36 }}
                            >
                              Manage
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleEdit(t.id)}
                              style={{ fontSize: 12, padding: "8px 10px", minHeight: 36 }}
                            >
                              Edit
                            </Button>
                            {!deskSession && <MoreActionsMenu
                              items={[{ label: '🗑️ Delete tee time', danger: true, onClick: () => handleDeleteClick(t.id) }]}
                              style={{ marginLeft: 'auto' }}
                            />}
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                  <div 
                    key={t.id} 
                    style={{ 
                      display: "grid", 
                      gridTemplateColumns: "2fr 0.8fr 0.7fr 0.8fr 90px 200px", 
                      gap: 12, 
                      padding: 12, 
                      alignItems: "center",
                      background: idx % 2 === 0 ? "#f9fafb" : "#fff",
                      borderRadius: 6
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{t.course}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {getPlayerNames(t).length > 0 ? getPlayerNames(t).join(", ") : "No players"}
                      </div>
                    </div>
                    <div style={{ fontSize: 13 }}>{utils.formatDate(t.date)}</div>
                    <div style={{ fontSize: 13 }}>{utils.formatTime(t.time)}</div>
                    <div>
                      <Badge style={{ 
                        background: utils.safePlayerCount(t.players) >= utils.toInt(t.slots) ? "#dcfce7" : "#fef3c7", 
                        color: utils.safePlayerCount(t.players) >= utils.toInt(t.slots) ? "#15803d" : "#92400e" 
                      }}>
                        {utils.safePlayerCount(t.players)}/{t.slots} Slots
                      </Badge>
                    </div>
                    <div>
                      {Array.isArray(t.waitlist) && t.waitlist.length > 0 ? (
                        <Badge style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fbbf24" }}>
                          ⏳ {t.waitlist.length} waiting
                        </Badge>
                      ) : (
                        <span style={{ fontSize: 12, color: "#9ca3af" }}>No waitlist</span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {t.status === "Draft" ? (
                        <>
                          <Button 
                            onClick={() => handleManagePlayers(t.id)} 
                            style={{ flex: 1, fontSize: 11, background: "#2d6a4f", color: "#fff", padding: "6px 8px" }}
                          >
                            Manage
                          </Button>
                          <Button 
                            onClick={() => handlePublish(t.id)} 
                            style={{ fontSize: 11, background: "#f59e0b", color: "#fff", padding: "6px 8px" }}
                          >
                            📣 Publish
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => handleEdit(t.id)} 
                            style={{ fontSize: 11, padding: "6px 8px" }}
                          >
                            Edit
                          </Button>
                          {!deskSession && <MoreActionsMenu
                            size="sm"
                            items={[{ label: '🗑️ Delete tee time', danger: true, onClick: () => handleDeleteClick(t.id) }]}
                          />}
                        </>
                      ) : (
                        <>
                          <Button 
                            onClick={() => handleManagePlayers(t.id)} 
                            style={{ flex: 1, fontSize: 11, background: "#2d6a4f", color: "#fff", padding: "6px 8px" }}
                          >
                            Manage
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => handleEdit(t.id)} 
                            style={{ fontSize: 11, padding: "6px 8px" }}
                          >
                            Edit
                          </Button>
                          {!deskSession && <MoreActionsMenu
                            size="sm"
                            items={[{ label: '🗑️ Delete tee time', danger: true, onClick: () => handleDeleteClick(t.id) }]}
                          />}
                        </>
                      )}
                    </div>
                  </div>
                  )
                ))}
              </div>
            </Card>
          )}
        </>
      )}
      
      {!deskSession && filteredAndSortedTeeTimes.length === 0 && (
        <Card style={{ padding: 48, textAlign: "center", background: "linear-gradient(135deg, #f9fafb, #f3f4f6)", border: "2px dashed #d1d5db" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⛳</div>
          <div style={{ color: "#6b7280", fontSize: 16, fontWeight: 600 }}>
            {searchTerm ? "No tee times match your search" : 
             filter === "today" ? "No tee times scheduled for today" :
             filter === "past" ? "No past tee times" :
             "No tee times scheduled yet"}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 14, marginTop: 6 }}>
            {searchTerm ? "Try a different search term" : "Create your first tee time above to get started"}
          </div>
        </Card>
      )}
      
      {confirmDeleteId && <ConfirmDialog title="Delete tee time?" message="This will permanently remove the tee time." onConfirm={() => handleDelete(confirmDeleteId)} onCancel={() => setConfirmDeleteId(null)} />}

      {/* Cancel Tee Time Modal - with reason input (NEW - January 29, 2026) */}
      {showCancelModal && cancellingTeeTime && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}>
          <Card style={{ maxWidth: 450, width: '100%' }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ background: '#dc2626', color: '#fff', padding: '16px 20px', marginBottom: 16, borderRadius: '8px 8px 0 0', margin: '-16px -16px 16px -16px' }}>
              <div style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                🚫 Cancel Tee Time
              </div>
            </div>
            
            {/* Warning */}
            <div style={{ marginBottom: 16 }}>
              <p style={{ color: '#374151', marginBottom: 8 }}>
                <strong>⚠️ Warning:</strong> This tee time has{' '}
                <span style={{ fontWeight: 700, color: '#dc2626' }}>
                  {utils.safePlayerCount(cancellingTeeTime.players)} player{utils.safePlayerCount(cancellingTeeTime.players) > 1 ? 's' : ''}
                </span>{' '}
                registered.
              </p>
              <p style={{ color: '#6b7280', fontSize: 14 }}>
                Deleting will remove their reservations and send them a cancellation notice.
              </p>
            </div>
            
            {/* Tee Time Details */}
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, marginBottom: 16, borderLeft: '4px solid #dc2626' }}>
              <div style={{ fontSize: 14, color: '#4b5563' }}>
                <div><strong>📅 Date:</strong> {utils.formatDate(cancellingTeeTime.date)}</div>
                <div><strong>⏰ Time:</strong> {utils.formatTime(cancellingTeeTime.time)}</div>
                <div><strong>🏌️ Course:</strong> {cancellingTeeTime.course || 'Unknown'}</div>
              </div>
            </div>
            
            {/* Reason Input */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
                Reason for cancellation (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g., Weather conditions, Course maintenance, etc."
                style={{ 
                  width: '100%', 
                  padding: '8px 12px', 
                  border: '1px solid #d1d5db', 
                  borderRadius: 8, 
                  fontSize: 14,
                  resize: 'none',
                  minHeight: 60
                }}
                rows={2}
                maxLength={200}
                disabled={isCancelling}
              />
              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                {cancelReason.length}/200 characters • This will be included in the notification to players
              </p>
            </div>
            
            {/* Player List Preview */}
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 500, color: '#6b7280', marginBottom: 6 }}>Players to be notified:</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(cancellingTeeTime.players || []).slice(0, 5).map((player, idx) => (
                  <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500, background: '#f3f4f6', color: '#374151' }}>
                    {typeof player === 'object' ? player.name : player}
                  </span>
                ))}
                {(cancellingTeeTime.players || []).length > 5 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500, background: '#f3f4f6', color: '#6b7280' }}>
                    +{(cancellingTeeTime.players || []).length - 5} more
                  </span>
                )}
              </div>
            </div>
            
            {/* Footer Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellingTeeTime(null);
                  setCancelReason('');
                }}
                disabled={isCancelling}
              >
                Keep Tee Time
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmCancel}
                disabled={isCancelling}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {isCancelling ? (
                  <>
                    <span style={{ 
                      display: 'inline-block',
                      width: 16, 
                      height: 16, 
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Cancelling...
                  </>
                ) : (
                  <>
                    🗑️ Delete & Notify Players
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
      {showPlayerPicker && selectedTeeTime && <PlayerPickerModal teeTime={selectedTeeTime} players={players} groups={groups} isEvent={!!eventsByTeetime[selectedTeeTime.id]} onConfirm={handleConfirmPlayers} onClose={() => { setShowPlayerPicker(false); setSelectedTeeTimeId(null); }} />}
      {showEditModal && editingTeeTime && <EditTeeTimeModal deskMode={!!deskSession} teeTime={editingTeeTime} onSave={handleSaveEdit} onCancel={handleCancelEdit} eventsEnabled={eventsEnabled} canCreateEvent={canCreateEvent === null ? eventsEnabled : canCreateEvent} teamScoringEnabled={teamScoringEnabled} event={eventsByTeetime[editingTeeTime.id]} onSaveEvent={saveEventForTeetime} onOpenGames={gamesEnabled ? () => { const tt = editingTeeTime; setShowEditModal(false); setEditingTeeTime(null); setGameSetupTeeTime(tt); } : undefined} />}

      {/* Event Pass (Sept 28, 2026): buy / upgrade window, and the return from Stripe. */}
      {!deskSession && <EventPassModalHost onChanged={refreshEventAccess} />}

      {/* Event save error — a MODAL, not a toast (organizers miss toasts — Dennis, Sept 13, 2026).
          zIndex above the Edit modal and the Event Setup modal so it is never hidden behind either. */}
      {eventSaveError && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setEventSaveError(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>⚠️ Event not {eventSaveError.action === 'remove' ? 'removed' : 'saved'}</div>
            <div style={{ fontSize: 14, color: '#374151', marginBottom: 12, lineHeight: 1.5 }}>{eventSaveError.message}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, lineHeight: 1.5 }}>
              {eventSaveError.action === 'remove'
                ? 'The event is still attached to this tee time.'
                : 'The tee time itself is unaffected. Open Edit → Set up event to try again.'}
            </div>
            <button onClick={() => setEventSaveError(null)} style={{ width: '100%', padding: '11px', background: '#1f2937', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>OK</button>
          </div>
        </div>
      )}

      {/* Oct 12, 2026 — Event Setup opened from a card's "Event setup ▸" (organizer list,
          Next up, and the Event Desk). The same window as Edit → Edit event, but there is
          no tee-time Save behind it here, so Save writes the event straight away through
          saveEventForTeetime (the path the create flow uses). */}
      {cardEventSetupId && (() => {
        const tt = (Array.isArray(teeTimes) ? teeTimes : []).find(x => String(x.id) === String(cardEventSetupId));
        const ev = eventsByTeetime[cardEventSetupId];
        if (!tt || !ev) return null;
        return (
          <EventSetupModal
            initial={{ name: ev.name || "", description: ev.description || "", scoring_mode: ev.scoring_mode || "individual", postable_to_ghin: ev.postable_to_ghin !== false, public_slug: ev.public_slug || null, public_url: ev.public_url || null, slug_printed_at: ev.slug_printed_at || null, logo_url: ev.logo_url || null }}
            slots={tt.slots}
            teetimeId={tt.id}
            eventId={ev.id}
            deskMode={!!deskSession}
            date={tt.date}
            teamScoringEnabled={teamScoringEnabled}
            onOpenGames={gamesEnabled ? () => { setCardEventSetupId(null); setGameSetupTeeTime(tt); } : undefined}
            onSave={async (d) => {
              await saveEventForTeetime(tt.id, {
                is_event: true, name: d.name, description: d.description, scoring_mode: d.scoring_mode, postable_to_ghin: d.postable_to_ghin,
                public_slug: d.public_slug, confirm_reprint: d.confirm_reprint === true ? true : undefined,
                logo_file: d.logo_file, logo_name: d.logo_name, remove_logo: d.remove_logo === true ? true : undefined
              });
              setCardEventSetupId(null);
            }}
            onClose={() => setCardEventSetupId(null)}
          />
        );
      })()}

      {/* Slice 3a — create-flow Event Setup modal, opened by handleCreate on the new tee time */}
      {eventSetupTeetime && (
        <EventSetupModal
          initial={{ name: "", description: "", scoring_mode: "individual", postable_to_ghin: true }}
          slots={eventSetupTeetime.slots}
          teetimeId={eventSetupTeetime.id}
          date={eventSetupTeetime.date}
          teamScoringEnabled={teamScoringEnabled}
          onOpenGames={gamesEnabled ? () => { const tt = eventSetupTeetime; setEventSetupTeetime(null); setGameSetupTeeTime(tt); } : undefined}
          onSave={(d) => {
            saveEventForTeetime(eventSetupTeetime.id, { is_event: true, name: d.name, description: d.description, scoring_mode: d.scoring_mode, postable_to_ghin: d.postable_to_ghin, public_slug: d.public_slug, logo_file: d.logo_file, logo_name: d.logo_name });
            setEventSetupTeetime(null);
          }}
          onClose={() => setEventSetupTeetime(null)}
        />
      )}

      {/* Slot Reduction Confirmation Modal */}
      {showSlotReductionModal && slotReductionData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>⚠️ Confirm Slot Reduction</div>
            <div style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
              Reducing to <strong>{slotReductionData.updatedTeeTime.slots} slots</strong> will move the following player{slotReductionData.playersToWaitlist.length > 1 ? 's' : ''} to the waitlist:
            </div>
            <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: 12, marginBottom: 16 }}>
              {slotReductionData.playersToWaitlist.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < slotReductionData.playersToWaitlist.length - 1 ? '1px solid #fde68a' : 'none' }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#92400e' }}>{p.name}</span>
                  <span style={{ fontSize: 12, color: '#92400e' }}>
                    #{slotReductionData.playersToWaitlist.length - i} on waitlist
                  </span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              Each player will receive an email notification that they have been moved to the waitlist.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setShowSlotReductionModal(false); setSlotReductionData(null); }}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                Cancel
              </button>
              <button
                onClick={handleConfirmSlotReduction}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                Proceed — Move to Waitlist
              </button>
            </div>
          </div>
        </div>
      )}
      {gameSetupTeeTime && (
        <GameSetupModal
          teeTime={gameSetupTeeTime}
          isEvent={!!(eventsByTeetime && eventsByTeetime[gameSetupTeeTime.id])}
          onClose={() => setGameSetupTeeTime(null)}
          onSaved={() => setGameSetupTeeTime(null)}
          onFinalize={handleFinalize}
          onEmailSettlement={handleSendSettlementEmails}
        />
      )}
      {showPublishModal && publishingTeeTime && <PublishModal teeTimeInfo={publishingTeeTime} players={players} groups={groups} onConfirm={handlePublishConfirm} onClose={() => { setShowPublishModal(false); setPublishingTeeTime(null); }} isPublishing={isPublishing} />}
      {showNotifyModal && notifyingTeeTime && <PublishModal mode="notify" teeTimeInfo={notifyingTeeTime} players={players} groups={groups} onConfirm={handleNotifyConfirm} onClose={() => { setShowNotifyModal(false); setNotifyingTeeTime(null); }} isPublishing={isPublishing} />}
      {showInviteModal && invitingTeeTime && <InviteLinkModal teeTime={invitingTeeTime} onClose={() => { setShowInviteModal(false); setInvitingTeeTime(null); }} />}
      
      {/* Tee Time Overlap Conflict Modal */}
      {showOverlapModal && overlapConflict && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: 32, maxWidth: 480,
            width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h3 style={{ margin: '0 0 16px 0', color: '#b91c1c', fontSize: 20 }}>
              Tee Time Conflict
            </h3>
            <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 24px 0', fontSize: 15 }}>
              {overlapConflict.message}
            </p>
            <button
              onClick={() => { setShowOverlapModal(false); setOverlapConflict(null); }}
              style={{
                padding: '10px 32px', backgroundColor: '#166534', color: 'white',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15,
                fontWeight: 600
              }}
            >
              OK, Got It
            </button>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <HelpInfoModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={helpContent.title}
        content={helpContent.content}
      />
      
      {/* Guides chooser — Getting Started or Events / Tournaments (Sept 27, 2026) */}
      <GuidesChooserModal
        isOpen={showGuidesChooser}
        onClose={() => setShowGuidesChooser(false)}
        showAll
      />
      <WhatsNewModal
        isOpen={showWhatsNew}
        onClose={() => setShowWhatsNew(false)}
        orgId={orgSettings?.group_id}
        onMarkRead={() => setWhatsNewUnread(0)}
      />
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        feature={upgradeModal.feature}
        requiredTier={upgradeModal.requiredTier}
        currentTier={upgradeModal.currentTier}
        onClose={() => setUpgradeModal({ isOpen: false, feature: null, requiredTier: null, currentTier: null })}
      />
    </div>
  );
};

// ============================================================================
// TeamCodesModal — organizer: printed team codes for a team-scored round (D13,
// Sept 15, 2026). Opens from the Pairings toolbar ("🔑 Team codes") when the
// selected round is scored one score per team. One six-digit code per saved
// pairing group; the QR (rendered server-side) opens https://ttogolf.com/<code>,
// the scorer's door onto that team's card. "Print codes" makes a one-per-team
// sheet — the interim print piece until the scorecards / cart signs (recap
// item 6) carry the QR themselves. Regenerate revokes every printed code.
// ============================================================================
const TeamCodesModal = ({ teeTime, onClose }) => {
  const [data, setData] = useState(null);
  const codeNines = ninesForCourse(teeTime?.course);   // named nines (Oct 8, 2026): "Burn Kill 3A"
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const authHeaders = () => {
    const h = { 'Content-Type': 'application/json' };
    const t = getAuthToken();
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
  };
  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/team-codes`, { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Could not load team codes'); setData(null); } else setData(d);
    } catch (e) { setError('Could not load team codes'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teeTime?.id]);

  const issue = async (regenerate) => {
    setBusy(true); setError(null); setConfirmRegen(false);
    try {
      const r = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/team-codes`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ regenerate: !!regenerate }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) setError(d.error || 'Could not issue team codes'); else setData(d);
    } catch (e) { setError('Could not issue team codes'); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true); setError(null); setConfirmRevoke(false);
    try {
      const r = await fetch(`${API_URL}/scoring/teetimes/${teeTime.id}/team-codes`, { method: 'DELETE', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) setError(d.error || 'Could not revoke team codes'); else await load();
    } catch (e) { setError('Could not revoke team codes'); }
    finally { setBusy(false); }
  };

  const fmtDate = (ds) => { if (!ds) return ''; const d = new Date(String(ds).slice(0, 10) + 'T12:00:00'); return isNaN(d.getTime()) ? ds : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const teams = data?.teams || [];
  const withCodes = teams.filter(t => t.code);
  const missing = teams.filter(t => !t.code);
  const host = (data?.base_url || 'https://ttogolf.com').replace(/^https?:\/\//, '');
  // Individual rounds get codes too (Sept 28, 2026): one per saved GROUP (one per
  // printed card); the scanner taps his own name and his own card opens.
  const indiv = !!(data && data.scoring_mode !== 'team');
  const unit = indiv ? 'Group' : 'Team';
  const units = (n) => `${n} ${indiv ? 'group' : 'team'}${n === 1 ? '' : 's'}`;
  // A registered team prints its name, as the scorecard does ("TEAM – Samuelson"); the group
  // number reads like a starting hole (Dennis, Oct 10 and again Oct 26: "Team 1A").
  const teamLabel = (t) => (!indiv && t.team_name) ? `Team – ${t.team_name}` : `${unit} ${t.pairing_group}${t.group_label || ''}`;

  const printSheet = () => {
    if (withCodes.length === 0) return;
    const title = `${data.event_name ? esc(data.event_name) + ' — ' : ''}${indiv ? 'Scorecard codes' : 'Team codes'} — ${esc(data.course_name || '')} · ${esc(fmtDate(data.date))}`;
    const cards = withCodes.map(t => `
      <div class="card">
        <div class="hdr"><span class="team">${esc(teamLabel(t))}</span>${t.starting_hole ? `<span class="start">Start: ${esc(startHoleText(t.starting_hole, t.group_label, codeNines))}</span>` : ''}</div>
        <div class="names">${t.members.map(m => esc(m.name)).join(' · ')}</div>
        <div class="row">
          <div class="qr">${t.qr_svg || ''}</div>
          <div class="code-block">
            <div class="lbl">${indiv ? 'Scorecard code' : 'Team code'}</div>
            <div class="code">${esc(t.code_display)}</div>
            <div class="how">Point your phone's camera at the square, or go to <b>${esc(host)}</b> and enter the code.</div>
          </div>
        </div>
      </div>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 24px; color: #111827; }
        h1 { font-size: 18px; margin: 0 0 4px; } .sub { color: #6b7280; font-size: 13px; margin-bottom: 16px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .card { border: 2px solid #111827; border-radius: 10px; padding: 12px 14px; break-inside: avoid; page-break-inside: avoid; }
        .hdr { display: flex; justify-content: space-between; align-items: baseline; }
        .team { font-size: 20px; font-weight: 800; } .start { font-size: 14px; font-weight: 700; }
        .names { font-size: 14px; margin: 4px 0 10px; }
        .row { display: flex; gap: 14px; align-items: center; }
        .qr svg { width: 120px; height: 120px; display: block; }
        .lbl { font-size: 11px; letter-spacing: 1px; color: #6b7280; font-weight: 700; }
        .code { font-size: 34px; font-weight: 900; letter-spacing: 3px; line-height: 1.1; }
        .how { font-size: 11px; color: #374151; margin-top: 6px; line-height: 1.35; }
        @media print { body { margin: 10mm; } .grid { gap: 10px; } }
      </style></head><body>
      <h1>${title}</h1>
      <div class="sub">${indiv
        ? `One code per group. Each player scans the square (or types the code at ${esc(host)}) and taps his own name to open his card. Codes work the day before, the day of, and the day after the round.`
        : `One code per team. Whoever keeps the card scans the square (or types the code at ${esc(host)}), taps their name, and scores. Codes work the day before, the day of, and the day after the round.`}</div>
      <div class="grid">${cards}</div>
      <script>window.onload = function () { setTimeout(function () { window.print(); }, 200); };</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { setError('Your browser blocked the print window. Allow pop-ups for this site and try again.'); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 760, maxHeight: '90vh', overflow: 'auto', padding: 22 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1f2937' }}>🔑 {indiv ? 'Scorecard codes' : 'Team codes'}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
              {data ? <>{data.event_name ? `${data.event_name} · ` : ''}{data.course_name} · {fmtDate(data.date)}</> : 'Loading…'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }} aria-label="Close">×</button>
        </div>
        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginBottom: 12 }}>
          {indiv
            ? <>One six-digit code per group, printed on that group's scorecard and cart sign as a QR square and as digits. Scanning opens <strong>{host}/&lt;code&gt;</strong>; each player taps his own name and gets his own card — no TTO link needed. Posting to GHIN still needs the player's own link or ghin.com.</>
            : <>One six-digit code per team, printed on that team's scorecard and cart sign as a QR square and as digits. Scanning opens <strong>{host}/&lt;code&gt;</strong>; the scorer taps their name and gets the team card — no TTO link needed.</>}
          {' '}Players who have their TTO link don't need a code at all.
        </div>
        {error && <div style={{ padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Loading team codes…</div>
        ) : data && teams.length === 0 ? (
          <div style={{ padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#92400e', fontSize: 13 }}>
            {indiv ? 'Save the pairings first — each saved group gets one code, printed on its scorecard.' : 'Save the pairings first — each saved group is a team, and each team gets a code.'}
          </div>
        ) : data ? (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {missing.length > 0 && (
                <Button onClick={() => issue(false)} disabled={busy} style={{ background: '#2d6a4f', color: '#fff', fontSize: 13, fontWeight: 600 }}>
                  {withCodes.length === 0 ? `Issue codes for ${units(teams.length)}` : `Issue ${missing.length} missing code${missing.length === 1 ? '' : 's'}`}
                </Button>
              )}
              {withCodes.length > 0 && <Button variant="outline" onClick={printSheet} disabled={busy} style={{ fontSize: 13 }}>🖨️ Print codes</Button>}
              {withCodes.length > 0 && <Button variant="outline" onClick={() => setConfirmRegen(true)} disabled={busy} style={{ fontSize: 13 }}>♻️ Regenerate all</Button>}
              {withCodes.length > 0 && <Button variant="outline" onClick={() => setConfirmRevoke(true)} disabled={busy} style={{ fontSize: 13, color: '#b91c1c' }}>Revoke all</Button>}
            </div>
            {confirmRegen && (
              <div style={{ padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#92400e' }}>
                Regenerate every code for this round? <strong>Codes already printed stop working</strong> and any phone using one is logged out. You'll need to reprint.
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <Button onClick={() => issue(true)} disabled={busy} style={{ background: '#b45309', color: '#fff', fontSize: 13 }}>Yes, regenerate</Button>
                  <Button variant="outline" onClick={() => setConfirmRegen(false)} style={{ fontSize: 13 }}>Keep the current codes</Button>
                </div>
              </div>
            )}
            {confirmRevoke && (
              <div style={{ padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#991b1b' }}>
                Revoke every code for this round? Printed codes stop working immediately and any phone using one is logged out.
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <Button onClick={revoke} disabled={busy} style={{ background: '#b91c1c', color: '#fff', fontSize: 13 }}>Yes, revoke all</Button>
                  <Button variant="outline" onClick={() => setConfirmRevoke(false)} style={{ fontSize: 13 }}>Cancel</Button>
                </div>
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {[unit, 'Players', 'Start', 'Code', 'QR'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 700, fontSize: 12 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {teams.map(t => (
                    <tr key={t.pairing_group} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 700, whiteSpace: 'nowrap' }}>{teamLabel(t)}</td>
                      <td style={{ padding: '8px 10px' }}>{t.members.map(m => m.name).join(', ')}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{t.starting_hole ? startHoleText(t.starting_hole, t.group_label, codeNines) : '—'}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        {t.code ? <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 18, fontWeight: 800, letterSpacing: 2 }}>{t.code_display}</span> : <span style={{ color: '#9ca3af' }}>not issued</span>}
                        {t.url && <div style={{ fontSize: 11, color: '#6b7280' }}>{t.url.replace(/^https?:\/\//, '')}</div>}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {t.qr_svg ? <div style={{ width: 56, height: 56 }} dangerouslySetInnerHTML={{ __html: t.qr_svg }} /> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 12, lineHeight: 1.5 }}>
              {indiv
                ? "Codes work from the day before the round through the day after, and stop once every game on the round is finalized. A code lists the players in its group when it's scanned, so if you move players between groups after printing, reprint their cards."
                : "Codes work from the day before the round through the day after, and stop once the Captain's Choice game is finalized. If you change the teams after printing, regenerate and reprint — a code is tied to its team number."}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};


const TeeSheetReport = ({ teeTimes, players, courses, onShowHelp, eventByTeetime = {} }) => {
  const { isMobile } = useContext(MobileContext);
  const appNav = useContext(AppNavContext);   // Sept 17, 2026: Print tab ⇄ Pairings hand-off
  const [pendingTeeSheetPrint, setPendingTeeSheetPrint] = useState(null); // { teetimeId, win } — the Print tab asked for the tee sheet
  // ============================================================================
  // PAIRINGS TIER GATE (May 14, 2026 — Phase 2 Pairings sweep)
  // First Starter-tier gate. Uses default-to-locked pattern per v49 lesson:
  // pairingsLocked is `true` until canUseFeature returns explicit `true`.
  // This prevents flash-of-unlocked-feature during the async tier load.
  // Gates: Balanced Flights button (+ dropdown), Shotgun mode toggle.
  // NOT gated: Randomize, Sort-by-HCP, manual drag/drop, save (non-Shotgun).
  // ============================================================================
  const { tier: orgTier, loading: tierLoading } = useOrgTier();
  const pairingsLocked = tierLoading || canUseFeature(orgTier, 'pairings') !== true;
  const [selectedTeeTimeId, setSelectedTeeTimeId] = useState(null);
  const [randomizedPlayers, setRandomizedPlayers] = useState([]);
  const [settings, setSettings] = useState(null);
  // Email Tee Sheet feature states
  const [showEmailMenu, setShowEmailMenu] = useState(false);
  // Sept 27, 2026: exports sit behind a plain "Export" menu (organizer only, not the event desk)
  const [showExportMenu, setShowExportMenu] = useState(false);
  const deskSession = useContext(DeskContext);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // 'proshop', 'players', 'select'
  const [customEmailMessage, setCustomEmailMessage] = useState('');
  const [selectedEmailPlayers, setSelectedEmailPlayers] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // NEW: Pairing Groups feature states
  const [pairingGroups, setPairingGroups] = useState({}); // { playerId: groupNumber }
  const [groupTimes, setGroupTimes] = useState({}); // { groupNumber: "8:00 AM" }
  const [savingPairings, setSavingPairings] = useState(false);
  // Scorecards, cart signs and team codes moved to the Print tab (Sept 17, 2026) —
  // openRoundPdf / TeamCodesModal; this page keeps the tee sheet print.
  const [pairingsChanged, setPairingsChanged] = useState(false);
  // Sept 21, 2026 — the share gate. undefined means "no local opinion, read the
  // tee time"; after a save or a toggle we hold the answer locally, because the
  // tee time object in state is stale until the next list refresh.
  const [pairingsSharedOverride, setPairingsSharedOverride] = useState(undefined);
  const [confirmUnshare, setConfirmUnshare] = useState(false);
  const [sharingPairings, setSharingPairings] = useState(false);
  
  // Shotgun Start feature states
  const [shotgunMode, setShotgunMode] = useState(false);
  const [startingHoles, setStartingHoles] = useState({}); // { groupNumber: holeNumber (1-18) }
  const [groupLabels, setGroupLabels] = useState({}); // { groupNumber: 'A' | 'B' | 'C' | 'D' }
  const [showShotgunWizard, setShowShotgunWizard] = useState(false);
  const [wizardHoleConfig, setWizardHoleConfig] = useState([]); // [{ hole: 1, groups: 1 }, ...]
  const [wizardGroupSize, setWizardGroupSize] = useState(4); // players per starting group (4/3/2) — drives Fit to players + capacity (Sept 15, 2026)
  const [wizardPreset, setWizardPreset] = useState(null); // 'standard' | 'modified' | 'fit' | null once hand-edited — a Fit layout re-fits when the size changes
  const [lastShotgunFill, setLastShotgunFill] = useState(null); // { method, size } of the auto-fill last applied — the banner fills that button (Sept 15, 2026)
  const [shotgunSlotTimes, setShotgunSlotTimes] = useState({}); // { "1-A": "9:00 AM", "1-B": "9:10 AM" } keyed by hole-label
  
  // Balanced Flights feature states
  const [showPairByHcpMenu, setShowPairByHcpMenu] = useState(false);
  const [playerTiers, setPlayerTiers] = useState({}); // { playerId: 'A'|'B'|'C'|'D' }
  const [pairingBlinds, setPairingBlinds] = useState([]); // Best Ball blind draw for the selected round (Sept 27, 2026)
  
  // Tee Sheet display options (Show checkboxes from control bar)
  const [teeSheetShowOptions, setTeeSheetShowOptions] = useState({ showMember: true, showIndex: true, showCourseHcp: true });
  
  // Course Handicap feature states
  const [allTees, setAllTees] = useState([]); // All tees for all courses
  const [playerTeeSelections, setPlayerTeeSelections] = useState({}); // { playerId: teeId }
  
  // Multi-Tee-Time Consolidation states (Phase 2 - Feb 2026)
  const [multiSelectMode, setMultiSelectMode] = useState(false); // True when multiple tee times are checked
  useEffect(() => {
    // Organizer / desk screen only: the shared pairings and the printed or emailed
    // tee sheet never carry the blind (players see "+ Blind Draw" until final).
    setPairingBlinds([]);
    if (!selectedTeeTimeId || multiSelectMode) return;
    let cancelled = false;
    (async () => {
      try {
        const h = {};
        const t = getAuthToken();
        if (t) h['Authorization'] = `Bearer ${t}`;
        const r = await fetch(`${API_URL}/scoring/teetimes/${selectedTeeTimeId}/blinds`, { credentials: 'include', headers: h });
        if (r.ok) { const d = await r.json(); if (!cancelled) setPairingBlinds(Array.isArray(d.blinds) ? d.blinds : []); }
      } catch (_) { /* no blind badge */ }
    })();
    return () => { cancelled = true; };
  }, [selectedTeeTimeId, multiSelectMode]);
  const [selectedTeeTimeIds, setSelectedTeeTimeIds] = useState([]); // Array of checked tee time IDs
  const [expandedGroupKey, setExpandedGroupKey] = useState(null); // Which date/course group is expanded in left panel
  const [playerOriginalTeeTimeMap, setPlayerOriginalTeeTimeMap] = useState({}); // { playerId: originalTeeTimeId }
  const [multiTeeTimeMap, setMultiTeeTimeMap] = useState({}); // { teeTimeId: teeTimeObject } for selected tee times
  // Tee time note (Sept 7, 2026) — the organizer's note for this tee time, or for
  // EVERY tee time in a multi-select league day. Saved by handleSavePairings via
  // PUT /teetimes/notes. Shown to players in View Details and carried by the
  // same-day + 24-hour reminders as {tee_time_note}. Empty = nothing rendered.
  const [teeTimeNote, setTeeTimeNote] = useState('');
  const [waitlistPlayers, setWaitlistPlayers] = useState([]); // Players on waitlist across selected tee times
  const [releasedTeeTimes, setReleasedTeeTimes] = useState([]); // Tee times with no players (initial empty slots from consolidation)
  
  // Dynamically compute which tee times are empty based on current pairings
  // This updates in real-time as users move players between groups
  const emptyTeeTimes = useMemo(() => {
    if (!multiSelectMode || Object.keys(multiTeeTimeMap).length === 0) return releasedTeeTimes;
    
    // Get all group numbers that have at least one player assigned
    const activeGroups = new Set();
    Object.values(pairingGroups).forEach(groupNum => {
      if (groupNum) activeGroups.add(groupNum);
    });
    
    // Get all time slots sorted by time (same order as consolidation)
    const allSlots = Object.values(multiTeeTimeMap)
      .sort((a, b) => {
        const aMin = parseInt(a.time?.split(':')[0] || 0) * 60 + parseInt(a.time?.split(':')[1] || 0);
        const bMin = parseInt(b.time?.split(':')[0] || 0) * 60 + parseInt(b.time?.split(':')[1] || 0);
        return aMin - bMin;
      });
    
    // Each slot index+1 is the group number. Find slots with no players in that group.
    const empty = [];
    allSlots.forEach((tt, idx) => {
      const groupNum = idx + 1;
      if (!activeGroups.has(groupNum)) {
        empty.push({
          id: tt.id,
          time: tt.time,
          formattedTime: utils.formatTime(tt.time),
          course: tt.course || '',
          date: tt.date || ''
        });
      }
    });
    
    return empty;
  }, [multiSelectMode, multiTeeTimeMap, pairingGroups, releasedTeeTimes]);
  
  const { addToast } = useToast();

  // Fetch all tees for course handicap calculations
  useEffect(() => {
    const fetchTees = async () => {
      try {
        const headers = {};
        const token = getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${API_URL}/tees`, {
          headers,
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setAllTees(data);
        }
      } catch (error) {
        console.error('Error fetching tees:', error);
      }
    };
    fetchTees();
  }, []);

  // Shotgun Setup belongs to ONE tee time (Oct 10, 2026 — handoff item 4). Its hole
  // layout was kept across tee times, and openShotgunWizard reuses any layout it
  // has, so the Nov 23 round could open with the Oct 8 round's holes. Forget it
  // whenever the tee time changes. Declared BEFORE the auto-open effect below:
  // effects run in order, so a newly loaded shotgun round clears first, then opens fresh.
  const wizardRoundKey = multiSelectMode ? `multi:${(selectedTeeTimeIds || []).join(',')}` : `one:${selectedTeeTimeId || ''}`;
  useEffect(() => {
    setWizardHoleConfig([]);
    setWizardPreset(null);
    setShowShotgunWizard(false);
  }, [wizardRoundKey]);

  // Auto-open shotgun wizard when shotgun mode is toggled on
  useEffect(() => {
    if (shotgunMode && !showShotgunWizard && Object.keys(startingHoles).length === 0) {
      const course = courses.find(c => c.name === (effectiveSelectedTeeTime || selectedTeeTime)?.course);
      const holeCount = course?.holes || 18;
      const defaultConfig = Array.from({ length: holeCount }, (_, i) => ({ hole: i + 1, groups: 1 }));
      setWizardHoleConfig(defaultConfig);
      setShowShotgunWizard(true);
    }
  }, [shotgunMode]);

  // Fetch organization settings for organization name
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // PHASE 2D FIX: Include JWT Authorization header for multi-tenant support
        const headers = {};
        const token = getAuthToken(); // PHASE 2E FIX
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${API_URL}/settings`, {
          headers,
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSettings(data.data); // Response is nested: { data: { organization_name: ... } }
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const selectedTeeTime = teeTimes.find(t => t.id === selectedTeeTimeId);
  // Named nines (Oct 8, 2026): the course's two nines, or null — "Burn Kill 3" vs "Hole 12".
  const pairingNines = () => {
    const name = (selectedTeeTime || effectiveSelectedTeeTime)?.course;
    return ninesOf((courses || []).find(c => c.name === name)) || ninesForCourse(name);
  };
  
  // In multi-select mode, create a synthetic selectedTeeTime from the first loaded tee time
  // so existing code that references selectedTeeTime?.course etc. doesn't crash
  const effectiveSelectedTeeTime = selectedTeeTime || (multiSelectMode && Object.values(multiTeeTimeMap).length > 0 
    ? (() => {
        const first = Object.values(multiTeeTimeMap).sort((a, b) => (a.time || '').localeCompare(b.time || ''))[0];
        return {
          ...first,
          slots: Object.values(multiTeeTimeMap).reduce((sum, t) => sum + (t.slots || 4), 0),
          players: randomizedPlayers
        };
      })()
    : null);
  
  // Alias for backward compatibility — guards throughout the right panel check selectedTeeTime
  // We shadow it ONLY within functions that need it. The original const remains for single-mode logic.
  
  // Create email-to-player lookup (full player object)
  const playerByEmail = useMemo(() => {
    return new Map(players.map(p => [p.email, p]));
  }, [players]);
  
  // Create id-to-player lookup
  const playerById = useMemo(() => {
    return new Map(players.map(p => [p.id, p]));
  }, [players]);

  // ==========================================================================
  // Registered teams (Oct 10, 2026 — Event_Registration_Plan_v1.md §3, slice 4).
  // A team is what the golfers came in with (the player import's Team column /
  // one row per team); it lives on tee_time_players.registration_group_id and
  // reaches this screen on each tee time's players as teamId / teamName /
  // teamOrder / teamCaptain. It is read from the tee time(s) on screen — never
  // from the player objects below, which are rebuilt field by field (Rule 30).
  // Pairings READ it ("👥 By team": one group per team) and never write it, so
  // Randomize or Balanced Flights can mix the teams up (they ask first) and
  // By team puts them back.
  // ==========================================================================
  // The team pill (Oct 10, 2026): PUT /teetimes/:id/team answers the round's whole
  // roster; it stands in for the tee time's players until the list is next fetched.
  const [teamRoster, setTeamRoster] = useState(null);   // { teetimeId, roster: [{ player_id, teamId, teamName, teamOrder, teamCaptain }] }
  const [showTeamTools, setShowTeamTools] = useState(false);   // "👥 Use teams" on a round with none yet
  const [teamSaving, setTeamSaving] = useState(false);
  useEffect(() => { setTeamRoster(null); }, [teeTimes]);
  useEffect(() => { setShowTeamTools(false); }, [selectedTeeTimeId]);
  const teamByPlayer = useMemo(() => {
    const ids = multiSelectMode ? selectedTeeTimeIds : [selectedTeeTimeId];
    const out = {};
    (teeTimes || []).filter(t => t && ids.includes(t.id)).forEach(t => {
      const fresh = teamRoster && !multiSelectMode && teamRoster.teetimeId === t.id ? teamRoster.roster : null;
      const rows = fresh ? fresh.map(r => ({ id: r.player_id, teamId: r.teamId, teamName: r.teamName, teamOrder: r.teamOrder, teamCaptain: r.teamCaptain })) : (t.players || []);
      rows.forEach(p => {
        if (p && typeof p === 'object' && p.id && p.teamId) {
          out[p.id] = { id: p.teamId, name: p.teamName || 'Team', order: Number(p.teamOrder) || 0, captain: p.teamCaptain === true };
        }
      });
    });
    return out;
  }, [teeTimes, selectedTeeTimeId, selectedTeeTimeIds, multiSelectMode, teamRoster]);
  // The round's teams with their size on the round: [{ id, name, order, count }] in order.
  const roundTeams = useMemo(() => {
    const m = new Map();
    Object.values(teamByPlayer).forEach(t => {
      if (!m.has(t.id)) m.set(t.id, { id: t.id, name: t.name, order: t.order, count: 0 });
      m.get(t.id).count++;
    });
    return [...m.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
  }, [teamByPlayer]);
  // Team size: the team game's (Captain's Choice size / Best Ball format), else the
  // team list's, else the largest team on the round. Below it, a team shows open spots.
  const teamSizeOfRound = (() => {
    const t = (teeTimes || []).find(x => x && x.id === selectedTeeTimeId);
    return Number(t && t.team_game_size) || Number(t && t.team_list_size) || Math.max(0, ...roundTeams.map(r => r.count)) || null;
  })();
  // ✎ Rename (Oct 10, 2026): the team keeps its golfers, captain and order.
  const renameTeam = async (teamId, name) => {
    if (!selectedTeeTimeId || teamSaving) return;
    setTeamSaving(true);
    try {
      const h = { 'Content-Type': 'application/json' };
      const tok = getAuthToken();
      if (tok) h['Authorization'] = `Bearer ${tok}`;
      const r = await fetch(`${API_URL}/teetimes/${selectedTeeTimeId}/teams/${teamId}`, { method: 'PUT', credentials: 'include', headers: h, body: JSON.stringify({ name }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { addToast(d.error || 'The team was not renamed.', 'error'); return; }
      const roster = Object.entries(teamByPlayer).map(([pid, t]) => ({ player_id: pid, teamId: t.id, teamName: t.id === teamId ? d.team.name : t.name, teamOrder: t.order, teamCaptain: t.captain }));
      setTeamRoster({ teetimeId: selectedTeeTimeId, roster });
      addToast(`Team renamed to ${d.team.name}`, 'success');
    } catch (e) {
      addToast('Network error: the team was not renamed.', 'error');
    } finally {
      setTeamSaving(false);
    }
  };
  const assignTeam = async (playerId, body) => {
    if (!selectedTeeTimeId || teamSaving) return;
    setTeamSaving(true);
    try {
      const h = { 'Content-Type': 'application/json' };
      const tok = getAuthToken();
      if (tok) h['Authorization'] = `Bearer ${tok}`;
      const r = await fetch(`${API_URL}/teetimes/${selectedTeeTimeId}/team`, { method: 'PUT', credentials: 'include', headers: h, body: JSON.stringify({ player_id: playerId, ...body }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { addToast(d.error || 'The team was not changed.', 'error'); return; }
      setTeamRoster({ teetimeId: selectedTeeTimeId, roster: d.roster || [] });
      if (!body.captain) addToast('Team changed. Press 👥 By team to regroup.', 'success');
    } catch (e) {
      addToast('Network error: the team was not changed.', 'error');
    } finally {
      setTeamSaving(false);
    }
  };
  // The teams among the players on screen, in the list's order: [{ id, name, players }].
  // Within a team (Dennis, Oct 10): the captain is player 1, then A–Z by last name
  // (then first name). By team saves that order, so the cards and cart signs print it.
  const lastFirstKey = (name) => {
    const parts = String(name || '').trim().split(/\s+/);
    const last = parts.length > 1 ? parts[parts.length - 1] : parts[0] || '';
    return `${last} ${parts.slice(0, -1).join(' ')}`.toLowerCase();
  };
  const byCaptainThenName = (a, b) => {
    const ca = teamByPlayer[a.id] && teamByPlayer[a.id].captain ? 1 : 0;
    const cb = teamByPlayer[b.id] && teamByPlayer[b.id].captain ? 1 : 0;
    return (cb - ca) || lastFirstKey(a.name).localeCompare(lastFirstKey(b.name));
  };
  const teamsOnScreen = () => {
    const byId = new Map();
    randomizedPlayers.forEach(p => {
      const t = p && teamByPlayer[p.id];
      if (!t) return;
      if (!byId.has(t.id)) byId.set(t.id, { id: t.id, name: t.name, order: t.order, players: [] });
      byId.get(t.id).players.push(p);
    });
    const list = [...byId.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
    list.forEach(t => t.players.sort(byCaptainThenName));
    return list;
  };
  const hasRegisteredTeams = Object.keys(teamByPlayer).length > 0;
  // Until groups exist, the list shows the field team by team (Oct 10, 2026), so
  // the organizer sees who is with whom. Only reorders when the order is different.
  // Re-sorts only when the round or its teams change, so a manual ▲ / ▼ move sticks.
  const teamSortKey = useRef('');
  useEffect(() => {
    if (!hasRegisteredTeams || Object.keys(pairingGroups).length > 0 || randomizedPlayers.length < 2) return;
    const key = `${selectedTeeTimeId}|${randomizedPlayers.length}|${Object.keys(teamByPlayer).sort().map(id => `${id}:${teamByPlayer[id].id}:${teamByPlayer[id].captain ? 1 : 0}`).join(',')}`;
    if (teamSortKey.current === key) return;
    teamSortKey.current = key;
    const teams = teamsOnScreen();
    const ordered = [...teams.flatMap(t => t.players), ...randomizedPlayers.filter(p => p && !teamByPlayer[p.id])];
    if (ordered.length === randomizedPlayers.length && ordered.some((p, i) => p !== randomizedPlayers[i])) setRandomizedPlayers(ordered);
  }, [teamByPlayer, pairingGroups, randomizedPlayers, selectedTeeTimeId]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Shotgun "Fit to players": groups the field needs at this group size. With
  // registered teams, one per team plus groups for the golfers without one.
  const fitGroupsNeeded = (size) => {
    const teams = teamsOnScreen();
    if (!teams.length) return Math.max(1, Math.ceil(randomizedPlayers.length / (size || 4)));
    const loose = randomizedPlayers.filter(p => p && !teamByPlayer[p.id]).length;
    return Math.max(1, teams.length + Math.ceil(loose / (size || 4)));
  };
  // Randomize / Balanced Flights / the shotgun fills deal players one by one. With
  // teams on the round, say so before mixing them up.
  const okToSplitTeams = () => {
    if (!hasRegisteredTeams) return true;
    const n = teamsOnScreen().filter(t => t.players.length > 1).length;
    if (n === 0) return true;
    return window.confirm(`This round has ${n} registered ${n === 1 ? 'team' : 'teams'}.\n\nThis will mix the golfers up and split the teams. Press 👥 By team afterwards to put them back together.\n\nContinue?`);
  };
  // 👥 By team: each registered team is one group (captain first), in the list's
  // order; golfers with no team follow in groups the size of the largest team.
  // In a shotgun with a structure, the groups take its starting holes in order.
  const handlePairByTeam = () => {
    const teams = teamsOnScreen();
    if (!teams.length) return;
    const loose = randomizedPlayers.filter(p => p && !teamByPlayer[p.id]);
    const size = Math.max(2, Math.min(4, Math.max(...teams.map(t => t.players.length))));
    const groups = teams.map(t => [...t.players]);   // already captain first, then A–Z (teamsOnScreen)
    for (let i = 0; i < loose.length; i += size) groups.push(loose.slice(i, i + size));
    const slots = Object.keys(startingHoles).length;
    if (shotgunMode && slots > 0 && groups.length > slots) {
      window.alert(`Not enough starting groups\n\n${teams.length} ${teams.length === 1 ? 'team' : 'teams'}${loose.length ? ` and ${loose.length} golfers without a team` : ''} need ${groups.length} groups, but the shotgun structure has ${slots}.\n\nOpen ⚡ Edit Setup and press Fit to players (or add groups), then press 👥 By team again.`);
      return;
    }
    const newPairings = {};
    const newOrder = [];
    groups.forEach((g, i) => g.forEach(p => { newPairings[p.id] = i + 1; newOrder.push(p); }));
    setRandomizedPlayers(newOrder);
    setPairingGroups(newPairings);
    setPlayerTiers({});
    setPairingsChanged(true);
    setLastShotgunFill(shotgunMode ? { method: 'team', size: 0 } : null);
    addToast(`👥 ${teams.length} ${teams.length === 1 ? 'team' : 'teams'} in their own groups${loose.length ? ` · ${loose.length} without a team in ${groups.length - teams.length} more` : ''}`, 'success');
  };
  
  const emailToPlayer = (playerOrEmail) => {
    // Handle both email strings and player objects {id, name, email}
    if (typeof playerOrEmail === 'string') {
      return playerByEmail.get(playerOrEmail) || { name: playerOrEmail, email: playerOrEmail };
    }
    if (playerOrEmail && typeof playerOrEmail === 'object' && playerOrEmail.email) {
      // Even if it's already an object, look it up to get latest data (including club_member)
      return playerByEmail.get(playerOrEmail.email) || playerOrEmail;
    }
    return { name: playerOrEmail, email: playerOrEmail };
  };

  // Get tees for a specific course name
  const getTeesForCourse = (courseName) => {
    return allTees.filter(t => t.course_name === courseName);
  };
  
  // Calculate course handicap using WHS formula
  const calculateCourseHandicap = (handicapIndex, tee) => {
    if (handicapIndex === null || handicapIndex === undefined || handicapIndex === '' || !tee || !tee.slope_rating || !tee.course_rating || !tee.par) {
      return null;
    }
    const hcpIndex = parseFloat(handicapIndex);
    if (isNaN(hcpIndex)) return null;
    
    const courseHcp = (hcpIndex * tee.slope_rating / 113) + (parseFloat(tee.course_rating) - tee.par);
    return Math.round(courseHcp);
  };
  
  // Get the middle (White) tee as fallback — tees are sorted slope_rating DESC so middle index = median difficulty
  // --- Gender-aware tee filtering (July 19, 2026) --------------------------
  // Women's tees are marked in production by a trailing (W) or (L) suffix.
  // Verified against all 59 marked tee names: every women's tee ends in (W)
  // or (L); men's tees never do (a literal 'Gold Men' / 'Mens Red' stays in
  // the men's set correctly because it carries no parenthetical marker).
  // isWomensTee is hoisted to module scope (used by PlayerPortal too).
  // Keep only tees matching the player's category. Unknown gender => no filter.
  // Never strand: if filtering leaves nothing (e.g. a course with only (W)
  // rows and an 'M' player), fall back to the full list rather than empty.
  const filterTeesByGender = (courseTees, gender) => {
    if (!courseTees || courseTees.length === 0) return courseTees;
    if (gender !== 'M' && gender !== 'W') return courseTees;
    const wantWomens = gender === 'W';
    const filtered = courseTees.filter(t => isWomensTee(t.tee_name) === wantWomens);
    return filtered.length > 0 ? filtered : courseTees;
  };

  const getMiddleTee = (courseTees, gender) => {
    const pool = filterTeesByGender(courseTees, gender);
    if (!pool || pool.length === 0) return null;
    // Try to find a tee named "White" first (within the gender-filtered pool)
    const whiteTee = pool.find(t => t.tee_name.toUpperCase() === 'WHITE');
    if (whiteTee) return whiteTee;
    // Otherwise use the middle index (rounds down for even counts = slightly harder middle)
    return pool[Math.floor((pool.length - 1) / 2)];
  };

  // Resolve a player's preferred tee within the current course's tee list.
  // preferred_tee is a UUID pointing to course_tees.id — but it may reference a tee on a
  // DIFFERENT course (e.g. player set "Gold" on Links Course, current sheet is Harbor Course).
  // Strategy:
  //   1. Direct UUID match within current course tees (same course, exact match)
  //   2. Cross-course: look up the tee name from allTees by UUID, then find same name in current course
  //   3. Name string match (legacy preferred_tee stored as name)
  //   4. Fallback to middle (White) tee
  const resolvePreferredTee = (preferredTee, courseTees, gender) => {
    // Restrict all matching to the player's category, then fall back within it.
    const pool = filterTeesByGender(courseTees, gender);
    if (!preferredTee || !pool || pool.length === 0) return getMiddleTee(courseTees, gender);
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(preferredTee)) {
      // Step 1: direct UUID match on this course (within the gender pool)
      const direct = pool.find(t => t.id === preferredTee);
      if (direct) return direct;
      // Step 2: cross-course — find tee name from allTees, match by name on this course
      const sourceTee = allTees.find(t => t.id === preferredTee);
      if (sourceTee) {
        const byName = pool.find(t => t.tee_name.toUpperCase() === sourceTee.tee_name.toUpperCase());
        if (byName) return byName;
      }
      return getMiddleTee(courseTees, gender);
    } else {
      // Legacy: preferred_tee stored as a name string
      return pool.find(t => t.tee_name.toUpperCase() === preferredTee.toUpperCase()) || getMiddleTee(courseTees, gender);
    }
  };

  // Get course handicap for a player
  // The index pairings use (Oct 4, 2026): this round's event handicap when the pro set one
  // (the same number the scoring grid, cards and games use), else the club index. A 0 is a
  // real index (scratch) — it used to read as "no handicap" and blocked balanced pairings.
  const pairingIndex = (player) => {
    for (const v of [player.event_handicap, player.hcp, player.handicap]) {
      if (v === null || v === undefined || v === '') continue;
      const n = parseFloat(v);
      if (!Number.isNaN(n)) return n;
    }
    return null;
  };
  const getPlayerCourseHandicap = (player, courseName) => {
    const playerHcp = pairingIndex(player);
    if (playerHcp === null) return null;
    
    const selectedTeeId = playerTeeSelections[player.id];
    const courseTees = getTeesForCourse(courseName);
    
    if (courseTees.length === 0) return null;
    
    // Priority: 1) Organizer-selected tee, 2) Player's preferred tee (cross-course aware), 3) Middle (White) tee
    let tee;
    if (selectedTeeId) {
      tee = courseTees.find(t => t.id === selectedTeeId) || getMiddleTee(courseTees, player.gender);
    } else {
      tee = resolvePreferredTee(player.preferred_tee, courseTees, player.gender);
    }
    
    return calculateCourseHandicap(playerHcp, tee);
  };
  
  // Get the tee being used for a player (for display)
  const getPlayerTee = (player, courseName) => {
    const selectedTeeId = playerTeeSelections[player.id];
    const courseTees = getTeesForCourse(courseName);
    
    if (courseTees.length === 0) return null;
    
    // Priority: 1) Organizer-selected tee, 2) Player's preferred tee (cross-course aware), 3) Middle (White) tee
    if (selectedTeeId) {
      return courseTees.find(t => t.id === selectedTeeId) || getMiddleTee(courseTees, player.gender);
    }
    return resolvePreferredTee(player.preferred_tee, courseTees, player.gender);
  };
  
  // The tee to SAVE for a player = the tee the sheet shows for him (Sept 15, 2026).
  // Before this, only a hand-picked tee reached tee_time_players.course_tee_id; a
  // player whose tee the sheet had resolved itself (preferred tee, or the White /
  // middle fallback) was saved with NULL, and every other surface then resolved him
  // its own way — the scramble handicap put Billy Bob on Black (the course's
  // highest-slope tee) while the printed sheet said White. Saving what is shown
  // makes the sheet the round's source of truth: games, boards and scoring all
  // read course_tee_id first. The organizer can still change any tee here first.
  const teeIdToSave = (player) => {
    const tee = getPlayerTee(player, (effectiveSelectedTeeTime || selectedTeeTime)?.course);
    return tee ? tee.id : (playerTeeSelections[player.id] || null);
  };

  // Handle player tee selection change
  const handlePlayerTeeChange = (playerId, teeId) => {
    setPlayerTeeSelections(prev => ({
      ...prev,
      [playerId]: teeId || null
    }));
    // Mark pairings dirty (Aug 5, 2026). This handler was the only pairing-screen
    // control that did not — the group dropdown, the up/down arrows, group times,
    // Randomize, Balanced Flights and HCP Sort all do. But the tee selection IS
    // part of the save: buildPairingsPayload sends courseTeeId and
    // PUT /teetimes/:id/pairings writes course_tee_id on tee_time_players.
    //
    // So changing a player's tee produced no Save Pairings button, and because
    // handleSelectTeeTime resets pairingsChanged to false, selecting another tee
    // time discarded the change with no warning. Silent data loss.
    setPairingsChanged(true);
  };
  
  // Sort players by course handicap
  const handleSortByCourseHandicap = () => {
    if (!selectedTeeTime) return;
    
    const courseName = selectedTeeTime?.course;
    const courseTees = getTeesForCourse(courseName);
    
    // Check for missing data
    const playersWithoutHcp = randomizedPlayers.filter(p => pairingIndex(p) === null);
    const noTeesConfigured = courseTees.length === 0;
    
    if (noTeesConfigured) {
      addToast(`Cannot sort: "${courseName}" has no tees configured. Edit the course to add tee data.`, 'error');
      return;
    }
    
    if (playersWithoutHcp.length > 0 && playersWithoutHcp.length === randomizedPlayers.length) {
      addToast('Cannot sort: No players have handicap indexes entered.', 'error');
      return;
    }
    
    // Sort players - those with course handicaps first (ascending), then those without
    const sorted = [...randomizedPlayers].sort((a, b) => {
      const aHcp = getPlayerCourseHandicap(a, courseName);
      const bHcp = getPlayerCourseHandicap(b, courseName);
      
      // Players without handicap go to the bottom
      if (aHcp === null && bHcp === null) return 0;
      if (aHcp === null) return 1;
      if (bHcp === null) return -1;
      
      return aHcp - bHcp;
    });
    
    setRandomizedPlayers(sorted);
    setPairingsChanged(true);
    
    // Show warning if some players are missing data
    if (playersWithoutHcp.length > 0) {
      addToast(`Sorted by course handicap. ${playersWithoutHcp.length} player(s) without handicap index shown at bottom.`, 'info');
    } else {
      addToast('Sorted by course handicap (lowest to highest)', 'success');
    }
  };

  // ============================================================================
  // BALANCED PAIRING CANDIDATE SEARCH (July 29, 2026)
  // Origin: Todd Murphy -- "Ted and Joe are on the same team over and over."
  //
  // The draft used to be fully DETERMINISTIC: sort by course handicap, cut into
  // quartiles, run one fixed snake. Same roster + same handicaps => the same
  // foursomes every single week. With 16 players in 4 groups an A player only
  // ever sees 3 of the other 15; the other 12 he never plays with at all. Ted
  // and Joe were not unlucky, they were welded together by the algorithm.
  //
  // FIX: generate many arrangements by shuffling WITHIN each tier (tier
  // membership is what carries the balance, so permuting inside a tier barely
  // moves the spread), score each by handicap spread, keep the tightest, and
  // pick at random among ties. There are usually many arrangements with the
  // identical best spread -- that slack is spent buying variety for free.
  //
  // 🔴 The unshuffled arrangement is ALWAYS candidate #1, so the result can
  // never be worse-balanced than the old deterministic behavior. That property
  // is what makes this safe to ship without a tier-by-tier regression.
  //
  // Deliberately NOT an optimizer over partner history: that needs data we have
  // not validated, and it produces teams the organizer cannot explain. Variety
  // first; if clustering persists, a history term drops into groupHcpSpread's
  // place as a second scoring term without changing any of this.
  // ============================================================================
  const BALANCE_CANDIDATES = 200;

  const groupHcpSpread = (groups) => {
    const totals = Object.values(groups)
      .filter(arr => arr && arr.length > 0)
      .map(arr => arr.reduce((sum, p) => sum + (p._courseHcp || 0), 0));
    if (totals.length === 0) return 0;
    return Math.max(...totals) - Math.min(...totals);
  };

  // ABCD template: round 0 forward, 1 reverse, 2 forward, 3 reverse -- byte-for-byte
  // the same walk the four unrolled loops used to do.
  const snakeAssignTiers = (tierArrays, numGroups) => {
    const groups = {};
    for (let g = 1; g <= numGroups; g++) groups[g] = [];
    tierArrays.forEach((tierPlayers, round) => {
      tierPlayers.forEach((p, i) => {
        const pos = i % numGroups;
        const g = (round % 2 === 0) ? pos + 1 : numGroups - pos;
        if (groups[g]) groups[g].push(p);
      });
    });
    return groups;
  };

  // AADD / BBCC template (Todd's suggestion): pair the extremes against the
  // middle. Reaches same-tier partnerships that ABCD structurally forbids.
  // Leftovers when the tiers do not divide by 2 land in trailing mixed groups
  // rather than blocking the whole action -- real fields are rarely tidy.
  const buildAaddBbcc = (tierArrays) => {
    const [A, B, C, D] = tierArrays.map(t => [...t]);
    const groups = {};
    let g = 1;
    const nAD = Math.min(Math.floor(A.length / 2), Math.floor(D.length / 2));
    const nBC = Math.min(Math.floor(B.length / 2), Math.floor(C.length / 2));
    for (let i = 0; i < nAD; i++) groups[g++] = [A.shift(), A.shift(), D.shift(), D.shift()];
    for (let i = 0; i < nBC; i++) groups[g++] = [B.shift(), B.shift(), C.shift(), C.shift()];
    const rest = [...A, ...B, ...C, ...D];
    while (rest.length > 0) groups[g++] = rest.splice(0, 4);
    return groups;
  };

  // Reuses the existing shuffleArray (Fisher-Yates, defined below in this
  // component) rather than introducing a second copy.
  const pickBalancedAssignment = (tiers, numGroups, builder) => {
    const base = [tiers.A, tiers.B, tiers.C, tiers.D];
    const candidates = [builder(base, numGroups)];
    for (let k = 1; k < BALANCE_CANDIDATES; k++) {
      candidates.push(builder(base.map(t => shuffleArray(t)), numGroups));
    }
    let bestSpread = Infinity;
    let pool = [];
    for (const c of candidates) {
      const sp = groupHcpSpread(c);
      if (sp < bestSpread) { bestSpread = sp; pool = [c]; }
      else if (sp === bestSpread) pool.push(c);
    }
    return {
      groups: pool[Math.floor(Math.random() * pool.length)],
      spread: bestSpread,
      tried: candidates.length,
      tied: pool.length,
    };
  };

  // ============================================================================
  // AUTO-PAIR BY HCP - Balanced group creation using A/B/C/D tier snake draft
  // Mode: 'foursomes' (4 per group) or 'twosomes' (2-player teams, 2 per hole)
  // ============================================================================
  const handlePairByHCP = (mode) => {
    if (!okToSplitTeams()) { setShowPairByHcpMenu(false); return; }   // registered teams (Oct 10, 2026)
    setShowPairByHcpMenu(false);

    // ============================================================================
    // LAYER 1 — PAIRINGS GATE (May 14, 2026 — Phase 2 Pairings sweep)
    // Balanced Flights (foursomes / threesomes / twosomes) is the gated algorithm.
    // Free orgs trigger the upgrade modal instead. The dropdown close above ensures
    // the menu collapses behind the modal.
    // ============================================================================
    if (pairingsLocked) {
      triggerUpgradeModal({ feature: 'pairings', requiredTier: 'starter', currentTier: orgTier });
      return;
    }

    if (!selectedTeeTime) return;

    const courseName = selectedTeeTime?.course;
    const courseTees = getTeesForCourse(courseName);

    // Validation: need course tees configured
    if (courseTees.length === 0) {
      addToast(`Cannot pair: "${courseName}" has no tees configured. Go to Settings → Courses to add tee data.`, 'error');
      return;
    }

    // Validation: need at least 2 players
    if (randomizedPlayers.length < 2) {
      addToast('Need at least 2 players to create pairings.', 'error');
      return;
    }

    // ============================================================================
    // PRE-FLIGHT VALIDATION (added May 9, 2026)
    // Block bad inputs up front rather than producing broken pairings silently.
    // - Missing HCP (any mode): hard block, name the player(s)
    // - Twosomes with odd count: hard block (twosomes require even count)
    // - Foursomes/Threesomes with uneven count: warn-and-proceed (uneven groups
    //   are normal in real golf events: e.g. 11 players = 2 foursomes + 1 threesome)
    // ============================================================================

    // Check for players missing a Course HCP
    const playersWithoutHcp = randomizedPlayers.filter(
      p => getPlayerCourseHandicap(p, courseName) === null
    );
    if (playersWithoutHcp.length > 0) {
      const names = playersWithoutHcp.map(p => p.name).join(', ');
      const playerWord = playersWithoutHcp.length === 1 ? 'player has' : 'players have';
      window.alert(
        `Cannot create balanced pairings\n\n${playersWithoutHcp.length} ${playerWord} no handicap:\n${names}\n\nAdd a handicap index in the player profile, remove the player from this tee time, or use Randomize instead.`
      );
      return;
    }

    // Mode-specific count checks
    const playerCount = randomizedPlayers.length;
    if (mode === 'twosomes') {
      if (playerCount % 2 !== 0) {
        window.alert(
          `Cannot create balanced twosomes\n\n${playerCount} players is an odd number. Twosomes require an even number of players.\n\nAdd or remove a player, or use Balanced Foursomes/Threesomes instead.`
        );
        return;
      }
    } else if (mode === 'foursomes') {
      if (playerCount % 4 !== 0) {
        const fullGroups = Math.floor(playerCount / 4);
        const remainder = playerCount % 4;
        const msg = remainder === 1
          ? `${playerCount} players doesn't divide evenly into foursomes — you'll get ${fullGroups} foursome(s) plus 1 player left over (will be added as a 5th to one group).\n\nProceed anyway?`
          : `${playerCount} players doesn't divide evenly into foursomes — you'll get ${fullGroups} foursome(s) plus a group of ${remainder}.\n\nProceed anyway?`;
        if (!window.confirm(msg)) return;
      }
    } else if (mode === 'threesomes') {
      if (playerCount % 3 !== 0) {
        const fullGroups = Math.floor(playerCount / 3);
        const remainder = playerCount % 3;
        const msg = `${playerCount} players doesn't divide evenly into threesomes — you'll get ${fullGroups} threesome(s) plus a group of ${remainder}.\n\nProceed anyway?`;
        if (!window.confirm(msg)) return;
      }
    }

    // Shotgun structure (Sept 15, 2026). When Shotgun Start is on AND a structure
    // exists (Edit Setup / Fit to players — e.g. holes 1, 18, 17, 16), Balanced
    // Flights fills THAT structure group by group, exactly like the banner
    // buttons do. It used to invent its own holes — twosomes as A/B pairs on
    // holes 1, 2, 3 …, foursomes and threesomes on 1, 2, 3 … — silently replacing
    // the layout the organizer had just built (Dennis, Sept 15). With no
    // structure it still auto-assigns holes as before, so the toolbar keeps
    // working on its own. A structure with too few groups is refused with the
    // same message the banner uses, never filled partially.
    const structureGroups = shotgunMode ? getShotgunGroupCount() : 0;
    const hasStructure = structureGroups > 0;
    const refuseStructure = (needed, word) => {
      window.alert(
        `Not enough starting groups\n\n${randomizedPlayers.length} players in ${word} need ${needed} groups, but the shotgun structure has ${structureGroups}.\n\nOpen ⚡ Edit Setup, pick ${word[0].toUpperCase() + word.slice(1)} and press Fit to players (or add groups), then pair again.`
      );
    };

    // Split players into ranked (have course HCP) and unranked (no HCP)
    const ranked = [];
    const unranked = [];
    randomizedPlayers.forEach(player => {
      const courseHcp = getPlayerCourseHandicap(player, courseName);
      if (courseHcp !== null) {
        ranked.push({ ...player, _courseHcp: courseHcp });
      } else {
        unranked.push(player);
      }
    });

    if (ranked.length < 2) {
      addToast('Need at least 2 players with handicap indexes to pair by HCP.', 'error');
      return;
    }

    // Sort ranked players by course HCP ascending (lowest = best)
    ranked.sort((a, b) => a._courseHcp - b._courseHcp);

    // Divide into A/B/C/D quartile tiers
    const tierSize = Math.ceil(ranked.length / 4);
    const tiers = { A: [], B: [], C: [], D: [] };
    ranked.forEach((player, i) => {
      if (i < tierSize) tiers.A.push(player);
      else if (i < tierSize * 2) tiers.B.push(player);
      else if (i < tierSize * 3) tiers.C.push(player);
      else tiers.D.push(player);
    });

    // Assign tier labels for badge display
    const newTiers = {};
    Object.entries(tiers).forEach(([tier, players]) => {
      players.forEach(p => { newTiers[p.id] = tier; });
    });

    let newPairings = {};
    let newOrder = [];
    let numGroups = 0;

    if (mode === 'foursomes') {
      // === FOURSOMES: 4 players per group, snake draft ===
      const groupSize = 4;
      numGroups = Math.ceil(ranked.length / groupSize);

      if (ranked.length < 4) {
        // Too few for proper balancing — put everyone in Group 1
        const allPlayers = [...ranked, ...unranked];
        allPlayers.forEach(p => { newPairings[p.id] = 1; });
        newOrder = allPlayers;
        numGroups = 1;
        addToast(`Only ${ranked.length} player(s) with HCP — all placed in Group 1.`, 'info');
      } else {
        // Snake draft (A forward, B reverse, C forward, D reverse) is now run
        // MANY times with the tiers shuffled, and the tightest-spread result
        // wins -- ties broken at random. See pickBalancedAssignment above.
        const picked = pickBalancedAssignment(tiers, numGroups, snakeAssignTiers);
        const groups = picked.groups;

        // Distribute unranked players evenly across groups
        unranked.forEach((p, i) => {
          const g = (i % numGroups) + 1;
          if (groups[g]) groups[g].push(p);
        });

        // Build final order and pairings map
        for (let g = 1; g <= numGroups; g++) {
          (groups[g] || []).forEach(p => {
            newPairings[p.id] = g;
            newOrder.push(p);
          });
        }

        addToast(`⚖️ ${numGroups} balanced foursomes created. HCP spread: ${picked.spread}${picked.tied > 1 ? ` — mixed up from ${picked.tied} equally-balanced options` : ''}`, 'success');
      }

    } else if (mode === 'twosomes') {
      // === TWOSOMES: 2-player teams, two teams per hole (A/B labels) ===
      // Create balanced pairs: A+D (best+worst), B+C (middle+middle)
      const adPairs = [];
      const bcPairs = [];

      // Reverse D tier so A1 pairs with D_last (most balanced)
      const dReversed = [...tiers.D].reverse();

      // Create A+D pairs
      const adLen = Math.max(tiers.A.length, dReversed.length);
      for (let i = 0; i < adLen; i++) {
        const pair = [];
        if (i < tiers.A.length) pair.push(tiers.A[i]);
        if (i < dReversed.length) pair.push(dReversed[i]);
        if (pair.length > 0) adPairs.push(pair);
      }

      // Create B+C pairs
      const cReversed = [...tiers.C].reverse();
      const bcLen = Math.max(tiers.B.length, cReversed.length);
      for (let i = 0; i < bcLen; i++) {
        const pair = [];
        if (i < tiers.B.length) pair.push(tiers.B[i]);
        if (i < cReversed.length) pair.push(cReversed[i]);
        if (pair.length > 0) bcPairs.push(pair);
      }

      // Interleave pairs: Hole 1 gets AD pair (Group 1) + BC pair (Group 2),
      // Hole 2 gets next AD pair (Group 3) + BC pair (Group 4), etc.
      let groupNum = 1;
      const newHoles = {};
      const newLabels = {};
      const totalPairings = Math.max(adPairs.length, bcPairs.length);

      for (let h = 0; h < totalPairings; h++) {
        const holeNum = h + 1;

        // AD pair → Group N with label A
        if (h < adPairs.length) {
          adPairs[h].forEach(p => {
            newPairings[p.id] = groupNum;
            newOrder.push(p);
          });
          if (shotgunMode && !hasStructure) {
            newHoles[groupNum] = holeNum;
            newLabels[groupNum] = 'A';
          }
          groupNum++;
        }

        // BC pair → Group N+1 with label B (same hole)
        if (h < bcPairs.length) {
          bcPairs[h].forEach(p => {
            newPairings[p.id] = groupNum;
            newOrder.push(p);
          });
          if (shotgunMode && !hasStructure) {
            newHoles[groupNum] = holeNum;
            newLabels[groupNum] = 'B';
          }
          groupNum++;
        }
      }

      numGroups = groupNum - 1;

      // Distribute unranked players evenly across existing teams
      if (unranked.length > 0) {
        unranked.forEach((p, i) => {
          const targetGroup = (i % numGroups) + 1;
          newPairings[p.id] = targetGroup;
          newOrder.push(p);
        });
      }

      // Structure present: fill it (or refuse if it is too small) — never overwrite it.
      if (hasStructure && numGroups > structureGroups) { refuseStructure(numGroups, 'twosomes'); return; }

      // Apply shotgun holes and labels — only when there is no structure to honor
      if (shotgunMode && !hasStructure) {
        setStartingHoles(newHoles);
        setGroupLabels(newLabels);
      }

      const pairCount = Math.ceil(numGroups / 2);
      const holeNote = !shotgunMode ? 'Turn on Shotgun to assign holes.' : hasStructure ? 'Placed into your shotgun structure.' : 'Holes & A/B labels auto-assigned.';
      addToast(hasStructure ? `🤝 ${numGroups} balanced twosomes created. ${holeNote}` : `🤝 ${numGroups} balanced twosomes created (${pairCount} holes). ${holeNote}`, 'success');

    } else if (mode === 'aaddbbcc') {
      // === AADD vs BBCC: 4 per group, pairing WITHIN tiers ===
      // Todd Murphy's suggestion. Under ABCD an A can never be grouped with
      // another A and the C players never play each other -- the template itself
      // forbids it. This reaches those combinations.
      if (ranked.length < 8) {
        window.alert(
          `Cannot create AADD / BBCC groups\n\n${ranked.length} ranked player(s) — this format needs at least 8 (two players from each of two tiers).\n\nUse Balanced Foursomes instead.`
        );
        return;
      }

      const pickedAB = pickBalancedAssignment(tiers, 0, buildAaddBbcc);
      const groupsAB = pickedAB.groups;
      numGroups = Object.keys(groupsAB).length;

      // Distribute unranked players evenly across groups
      unranked.forEach((p, i) => {
        const g = (i % numGroups) + 1;
        if (groupsAB[g]) groupsAB[g].push(p);
      });

      for (let g = 1; g <= numGroups; g++) {
        (groupsAB[g] || []).forEach(p => {
          newPairings[p.id] = g;
          newOrder.push(p);
        });
      }

      const nADg = Math.min(Math.floor(tiers.A.length / 2), Math.floor(tiers.D.length / 2));
      const nBCg = Math.min(Math.floor(tiers.B.length / 2), Math.floor(tiers.C.length / 2));
      const mixedG = numGroups - nADg - nBCg;
      addToast(
        `🔀 ${numGroups} groups — ${nADg} AADD, ${nBCg} BBCC${mixedG > 0 ? `, ${mixedG} mixed (leftover players)` : ''}. HCP spread: ${pickedAB.spread}`,
        'success'
      );

    } else if (mode === 'threesomes') {
      // === THREESOMES: 3 players per group, A/B/C tier balancing ===
      // Split into 3 tiers: A (best), B (middle), C (worst)
      // Each group gets one from each tier; C tier is reversed for tightest HCP spread
      const numGroups3 = Math.ceil(ranked.length / 3);

      if (ranked.length < 3) {
        // Too few for balancing — put everyone in Group 1
        const allPlayers = [...ranked, ...unranked];
        allPlayers.forEach(p => { newPairings[p.id] = 1; });
        newOrder = allPlayers;
        numGroups = 1;
        addToast(`Only ${ranked.length} player(s) with HCP — all placed in Group 1.`, 'info');
      } else {
        // Divide into 3 tiers
        const tierSize3 = Math.ceil(ranked.length / 3);
        const tiersABC = { A: [], B: [], C: [] };
        ranked.forEach((player, i) => {
          if (i < tierSize3) tiersABC.A.push(player);
          else if (i < tierSize3 * 2) tiersABC.B.push(player);
          else tiersABC.C.push(player);
        });

        // Override tier labels for display badges (A/B/C instead of A/B/C/D)
        Object.entries(tiersABC).forEach(([tier, players]) => {
          players.forEach(p => { newTiers[p.id] = tier; });
        });

        // Reverse C so best C pairs with best A and B (minimizes spread)
        const cReversed3 = [...tiersABC.C].reverse();

        const groups3 = {};
        for (let g = 1; g <= numGroups3; g++) groups3[g] = [];

        // Assign one from each tier per group
        tiersABC.A.forEach((p, i) => {
          const g = (i % numGroups3) + 1;
          if (groups3[g]) groups3[g].push(p);
        });
        tiersABC.B.forEach((p, i) => {
          const g = (i % numGroups3) + 1;
          if (groups3[g]) groups3[g].push(p);
        });
        cReversed3.forEach((p, i) => {
          const g = (i % numGroups3) + 1;
          if (groups3[g]) groups3[g].push(p);
        });

        // Distribute unranked players evenly across groups
        unranked.forEach((p, i) => {
          const g = (i % numGroups3) + 1;
          groups3[g].push(p);
        });

        // Build final order and pairings map
        for (let g = 1; g <= numGroups3; g++) {
          groups3[g].forEach(p => {
            newPairings[p.id] = g;
            newOrder.push(p);
          });
        }

        numGroups = numGroups3;

        // Calculate HCP spread for feedback
        const groupHcps3 = [];
        for (let g = 1; g <= numGroups3; g++) {
          const total = groups3[g].reduce((sum, p) => sum + (p._courseHcp || 0), 0);
          groupHcps3.push(total);
        }
        const spread3 = Math.max(...groupHcps3) - Math.min(...groupHcps3);
        addToast(`⚖️ ${numGroups3} balanced threesomes created. HCP spread: ${spread3} (lower = more balanced)`, 'success');
      }

      // Auto-assign sequential starting holes for threesomes in shotgun mode —
      // only when there is no structure to honor (see hasStructure above).
      if (shotgunMode && !hasStructure && numGroups > 0) {
        const newHoles = {};
        for (let g = 1; g <= numGroups; g++) {
          newHoles[g] = g;
        }
        setStartingHoles(newHoles);
        autoAssignLabels(newHoles);
      }
    }

    // Structure present: it must hold every group we made (twosomes checked above).
    if (hasStructure && numGroups > structureGroups) {
      refuseStructure(numGroups, mode === 'threesomes' ? 'threesomes' : 'foursomes');
      return;
    }

    // Apply results
    setRandomizedPlayers(newOrder);
    setPairingGroups(newPairings);
    setPlayerTiers(newTiers);
    setPairingsChanged(true);

    // Auto-assign sequential starting holes for foursomes in shotgun mode
    // (AADD/BBCC is also 4-per-group, so it takes the same path.) Only when
    // there is no structure to honor.
    if ((mode === 'foursomes' || mode === 'aaddbbcc') && shotgunMode && !hasStructure && numGroups > 0) {
      const newHoles = {};
      for (let g = 1; g <= numGroups; g++) {
        newHoles[g] = g; // Group 1 → Hole 1, Group 2 → Hole 2, etc.
      }
      setStartingHoles(newHoles);
      // autoAssignLabels will handle any label needs (single group per hole = no labels needed)
      autoAssignLabels(newHoles);
    }
  };

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleSelectTeeTime = (teeTime) => {
    setSelectedTeeTimeId(teeTime.id);
    setPairingsChanged(false);
    
    if (teeTime.players && teeTime.players.length > 0) {
      // Convert to player objects, preserving pairing data from backend
      const playerObjects = teeTime.players.map(p => {
        const fullPlayer = typeof p === 'object' && p.id ? playerById.get(p.id) || p : emailToPlayer(p);
        return {
          ...fullPlayer,
          // Preserve pairing data from the tee time players
          pairingGroup: typeof p === 'object' ? p.pairingGroup : null,
          sortOrder: typeof p === 'object' ? p.sortOrder : 0,
          startingHole: typeof p === 'object' ? p.startingHole : null,
          groupLabel: typeof p === 'object' ? p.groupLabel : null,
          course_tee_id: typeof p === 'object' ? p.course_tee_id : null,
          // Event handicap (Oct 4, 2026): this round's index, set on the Scoring tab, wins here too.
          event_handicap: typeof p === 'object' && p.event_handicap != null ? p.event_handicap : null
        };
      });
      
      // Sort by pairing group, then sort order
      playerObjects.sort((a, b) => {
        const aGroup = a.pairingGroup || 999;
        const bGroup = b.pairingGroup || 999;
        if (aGroup !== bGroup) return aGroup - bGroup;
        return (a.sortOrder || 0) - (b.sortOrder || 0);
      });
      
      setRandomizedPlayers(playerObjects);
      
      // Initialize pairing groups state from player data
      const initialPairings = {};
      playerObjects.forEach(p => {
        if (p.id && p.pairingGroup !== null && p.pairingGroup !== undefined) {
          initialPairings[p.id] = p.pairingGroup;
        }
      });
      setPairingGroups(initialPairings);
      
      // Load group times from tee time
      if (teeTime.pairing_group_times) {
        setGroupTimes(teeTime.pairing_group_times);
      } else {
        setGroupTimes({});
      }
      
      // Initialize shotgun data from player data
      const initialHoles = {};
      const initialLabels = {};
      playerObjects.forEach(p => {
        if (p.id && p.pairingGroup !== null && p.pairingGroup !== undefined) {
          if (p.startingHole) {
            initialHoles[p.pairingGroup] = p.startingHole;
          }
          if (p.groupLabel) {
            initialLabels[p.pairingGroup] = p.groupLabel;
          }
        }
      });
      setStartingHoles(initialHoles);
      setGroupLabels(initialLabels);
      setShotgunMode(teeTime.shotgun_mode === true);
      setTeeTimeNote(teeTime.notes || '');
      setPlayerTiers({}); // Clear tier badges when loading new tee time
      setLastShotgunFill(null);

      // Load saved tee selections per player (course_tee_id from tee_time_players)
      const savedTeeSelections = {};
      playerObjects.forEach(p => {
        if (p.id && p.course_tee_id) {
          savedTeeSelections[p.id] = p.course_tee_id;
        }
      });
      setPlayerTeeSelections(savedTeeSelections);
      
      // Rebuild shotgunSlotTimes from groupTimes + hole/label data
      if (teeTime.pairing_group_times) {
        const slotTimes = {};
        Object.entries(teeTime.pairing_group_times).forEach(([groupNum, time]) => {
          const hole = initialHoles[parseInt(groupNum)];
          const label = initialLabels[parseInt(groupNum)] || '';
          if (hole && time) {
            slotTimes[`${hole}-${label}`] = time;
          }
        });
        setShotgunSlotTimes(slotTimes);
      } else {
        setShotgunSlotTimes({});
      }
    } else {
      setRandomizedPlayers([]);
      setPairingGroups({});
      setGroupTimes({});
      setStartingHoles({});
      setGroupLabels({});
      setShotgunMode(false);
      setShotgunSlotTimes({});
      setPlayerTiers({}); // Clear tier badges
      setLastShotgunFill(null);
    }
  };

  const handleRandomize = () => {
    if (!okToSplitTeams()) return;   // registered teams (Oct 10, 2026)
    if (selectedTeeTime && selectedTeeTime?.players && selectedTeeTime?.players.length > 0) {
      const playerObjects = selectedTeeTime?.players.map(p => {
        const fullPlayer = typeof p === 'object' && p.id ? playerById.get(p.id) || p : emailToPlayer(p);
        return fullPlayer;
      });
      const shuffled = shuffleArray(playerObjects);
      setRandomizedPlayers(shuffled);
      // Clear pairing groups, shotgun data, and tier badges when randomizing
      setPairingGroups({});
      setStartingHoles({});
      setGroupLabels({});
      setPlayerTiers({});
      setPairingsChanged(true);
    }
  };

  // Move player up in the list (manual sorting)
  // When crossing a group boundary, reassign the moved player to the neighbor's group
  const handleMoveUp = (index) => {
    if (index === 0) return; // Already at top
    const newOrder = [...randomizedPlayers];
    const movingPlayer = newOrder[index];
    const neighborPlayer = newOrder[index - 1];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setRandomizedPlayers(newOrder);

    // If crossing a group boundary, adopt the neighbor's group
    const movingGroup = pairingGroups[movingPlayer.id];
    const neighborGroup = pairingGroups[neighborPlayer?.id];
    if (movingGroup !== undefined && neighborGroup !== undefined && movingGroup !== neighborGroup) {
      setPairingGroups(prev => ({
        ...prev,
        [movingPlayer.id]: neighborGroup
      }));
      // Re-sync shotgun slot times after group boundary crossing
      setTimeout(() => syncSlotTimesToGroupTimes(), 50);
    }
    setPairingsChanged(true);
  };

  // Move player down in the list (manual sorting)
  // When crossing a group boundary, reassign the moved player to the neighbor's group
  const handleMoveDown = (index) => {
    if (index === randomizedPlayers.length - 1) return; // Already at bottom
    const newOrder = [...randomizedPlayers];
    const movingPlayer = newOrder[index];
    const neighborPlayer = newOrder[index + 1];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setRandomizedPlayers(newOrder);

    // If crossing a group boundary, adopt the neighbor's group
    const movingGroup = pairingGroups[movingPlayer.id];
    const neighborGroup = pairingGroups[neighborPlayer?.id];
    if (movingGroup !== undefined && neighborGroup !== undefined && movingGroup !== neighborGroup) {
      setPairingGroups(prev => ({
        ...prev,
        [movingPlayer.id]: neighborGroup
      }));
      // Re-sync shotgun slot times after group boundary crossing
      setTimeout(() => syncSlotTimesToGroupTimes(), 50);
    }
    setPairingsChanged(true);
  };

  // NEW: Handle pairing group change for a player
  const handlePairingGroupChange = (playerId, groupNum) => {
    setPairingGroups(prev => {
      const updated = { ...prev };
      if (groupNum === '' || groupNum === null) {
        delete updated[playerId];
      } else {
        updated[playerId] = parseInt(groupNum);
      }
      return updated;
    });
    setPairingsChanged(true);
  };

  // NEW: Handle group time change
  const handleGroupTimeChange = (groupNum, time) => {
    setGroupTimes(prev => ({
      ...prev,
      [groupNum]: time
    }));
    setPairingsChanged(true);
  };

  // Auto-fill group times from course tee time interval
  const autoFillGroupTimesFromInterval = () => {
    const courseName = (effectiveSelectedTeeTime || selectedTeeTime)?.course;
    const course = courses?.find(c => c.name === courseName);
    const interval = course?.tee_time_interval;
    if (!interval) return;
    
    const baseTime = (effectiveSelectedTeeTime || selectedTeeTime)?.time;
    if (!baseTime) return;
    
    // Parse base time (HH:MM or HH:MM:SS format)
    const [hours, minutes] = baseTime.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return;
    const baseMinutes = hours * 60 + minutes;
    
    const activeGroups = getActiveGroups().sort((a, b) => a - b);
    const newTimes = {};
    
    activeGroups.forEach((groupNum, i) => {
      const totalMin = baseMinutes + (i * interval);
      const h = Math.floor(totalMin / 60) % 24;
      const m = totalMin % 60;
      const period = h >= 12 ? 'PM' : 'AM';
      const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
      newTimes[groupNum] = `${displayH}:${m.toString().padStart(2, '0')} ${period}`;
    });
    
    setGroupTimes(newTimes);
    setPairingsChanged(true);
    addToast(`⏱️ Group times filled: ${interval}-min intervals from ${utils.formatTime(baseTime)}`, 'success');
  };

  // Get course interval for current tee time
  const getCourseInterval = () => {
    const courseName = (effectiveSelectedTeeTime || selectedTeeTime)?.course;
    const course = courses?.find(c => c.name === courseName);
    return course?.tee_time_interval || null;
  };

  // NEW: Save pairings to backend
  // Reset the local opinion whenever a different round is selected.
  useEffect(() => { setPairingsSharedOverride(undefined); setConfirmUnshare(false); }, [selectedTeeTime?.id]);

  const pairingsAreShared = pairingsSharedOverride !== undefined
    ? pairingsSharedOverride
    : !!selectedTeeTime?.pairings_shared_at;

  // Sept 21, 2026 (corrected same day, on Dennis's first look at it). Both lines
  // below describe what the field can see of SAVED pairings — so neither belongs
  // on a round nobody has paired yet. Keying only on "not shared" was wrong,
  // because an unpaired round has no pairings_shared_at either: every organizer
  // opening any unpaired tee time was told "Players cannot see these pairings",
  // which is true, and useless, and alarming.
  //
  // pairingsSharedOverride !== undefined means a save or a toggle happened in
  // this session, which is itself proof that saved pairings exist. The two
  // stamps cover a round loaded from the list.
  const hasSavedPairings = pairingsSharedOverride !== undefined
    || !!selectedTeeTime?.pairings_saved_at
    || !!selectedTeeTime?.pairings_shared_at;

  // The deliberate act, both directions. Turning sharing OFF removes pairings the
  // field could already see — there is one copy, so the stamp decides WHETHER they
  // are shown, never WHICH VERSION. The confirm below is what keeps that from being
  // a silent removal.
  const setPairingsShared = async (shared) => {
    const ttId = selectedTeeTime?.id || selectedTeeTimeIds?.[0];
    if (!ttId) return;
    setSharingPairings(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const ids = (multiSelectMode && selectedTeeTimeIds?.length) ? selectedTeeTimeIds : [ttId];
      for (const id of ids) {
        const res = await fetch(`${API_URL}/teetimes/${id}/pairings/share`, {
          method: 'POST', headers, credentials: 'include', body: JSON.stringify({ shared })
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          addToast(e.error || 'Could not change sharing for these pairings', 'error');
          setSharingPairings(false);
          return;
        }
      }
      setPairingsSharedOverride(shared);
      setConfirmUnshare(false);
      addToast(shared ? 'Pairings are now visible to players' : 'Pairings hidden from players', 'success');
    } catch (e) {
      addToast('Could not reach the server', 'error');
    } finally {
      setSharingPairings(false);
    }
  };

  // share=true is the ordinary Save and is what this button has always done.
  // "Save without sharing" passes false, which holds the round as a working copy.
  const handleSavePairings = async (share = true) => {
    if (!selectedTeeTime && !multiSelectMode) return;
    
    setSavingPairings(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // ----- Tee time note -------------------------------------------------
      // Written to EVERY tee time in the current selection, not just one.
      // 🔴 selectedTeeTimeIds, NOT emailTeeTimeId (~13023 takes the FIRST id of
      // the multi map for the tee-sheet email) — a TRKMGL league night is 7-12
      // separate rows, and a note on one would reach only that foursome.
      // Independent of the pairings save: either can fail without the other.
      const noteTargetIds = (multiSelectMode && selectedTeeTimeIds.length > 0)
        ? selectedTeeTimeIds
        : (selectedTeeTime?.id ? [selectedTeeTime.id] : []);
      if (noteTargetIds.length > 0) {
        try {
          const noteRes = await fetch(`${API_URL}/teetimes/notes`, {
            method: 'PUT',
            headers,
            credentials: 'include',
            body: JSON.stringify({ teeTimeIds: noteTargetIds, notes: teeTimeNote })
          });
          const noteBody = await noteRes.json().catch(() => ({}));
          if (!noteRes.ok) {
            // Sept 22, 2026: the backend now 404s when the note matched NO tee times
            // (another org, or deleted), so this branch catches the case that used to
            // return 200 and say nothing at all.
            addToast(noteBody.error || 'Could not save the note to players', 'error');
          } else if (noteBody.warning) {
            // Partial save across a multi-tee-time day — some rows took it, some did not.
            addToast(noteBody.warning, 'warning');
          }
        } catch (noteErr) {
          console.error('Error saving tee time note:', noteErr);
          addToast('Could not save the note to players', 'error');
        }
      }

      if (multiSelectMode && selectedTeeTimeIds.length >= 2) {
        // Multi-tee-time save — call the new batch endpoint
        const pairingsData = randomizedPlayers.map((player, index) => {
          const groupNum = pairingGroups[player.id] || null;
          return {
            playerId: player.id,
            sortOrder: index,
            pairingGroup: groupNum,
            originalTeeTimeId: playerOriginalTeeTimeMap[player.id] || null,
            startingHole: groupNum ? (startingHoles[groupNum] || null) : null,
            groupLabel: groupNum ? (groupLabels[groupNum] || null) : null,
            fromWaitlist: false,
            courseTeeId: teeIdToSave(player)
          };
        });
        
        // Add any waitlist players that were moved into groups
        // (Waitlist players that remain in the waitlist section are NOT included)
        
        const response = await fetch(`${API_URL}/teetimes/save-multi-pairings`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            teeTimeIds: selectedTeeTimeIds,
            pairings: pairingsData,
            groupTimes: groupTimes,
            shotgunMode: shotgunMode,
            share,
            releasedTeeTimeIds: [] // No longer auto-releasing — organizer deletes empty tee times manually
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          const movedCount = result.movedPlayers?.length || 0;
          const emptyCount = result.emptyTeeTimeIds?.length || 0;
          
          let msg = 'Pairings saved successfully!';
          if (movedCount > 0) {
            msg = `Saved! ${movedCount} player${movedCount !== 1 ? 's' : ''} moved between tee times.`;
          }
          if (emptyCount > 0) {
            msg += ` ${emptyCount} tee time${emptyCount !== 1 ? 's' : ''} now empty — delete from Tee Times tab to release.`;
          }
          
          // Reflect the share state locally too (Sept 21, 2026) — the tee time
          // objects in state are stale until the next list refresh, and the
          // banner below the toolbar reads from this.
          addToast(share ? msg : msg.replace(/^Saved!?/, 'Saved — not shared with players.'), 'success');
          setPairingsChanged(false);
          setPairingsSharedOverride(share);
        } else {
          const error = await response.json();
          // LAYER 3 — Backend Layer 2 may return FEATURE_REQUIRES_UPGRADE (Shotgun gate).
          // Trigger upgrade modal cleanly instead of a generic error toast.
          if (handleFeatureUpgradeRequired(error)) return;
          addToast(error.error || 'Failed to save multi-pairings', 'error');
        }
      } else {
        // Single tee time save — existing endpoint
        const pairingsData = randomizedPlayers.map((player, index) => {
          const groupNum = pairingGroups[player.id] || null;
          return {
            playerId: player.id,
            sortOrder: index,
            pairingGroup: groupNum,
            startingHole: groupNum ? (startingHoles[groupNum] || null) : null,
            groupLabel: groupNum ? (groupLabels[groupNum] || null) : null,
            courseTeeId: teeIdToSave(player)
          };
        });
        
        const response = await fetch(`${API_URL}/teetimes/${selectedTeeTime?.id}/pairings`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            pairings: pairingsData,
            groupTimes: groupTimes,
            shotgunMode: shotgunMode,
            share
          })
        });
        
        if (response.ok) {
          addToast(share ? 'Pairings saved and shared with players' : 'Pairings saved — not shared with players', 'success');
          setPairingsChanged(false);
          setPairingsSharedOverride(share);
        } else {
          const error = await response.json();
          // LAYER 3 — Backend Layer 2 may return FEATURE_REQUIRES_UPGRADE (Shotgun gate).
          // Trigger upgrade modal cleanly instead of a generic error toast.
          if (handleFeatureUpgradeRequired(error)) return;
          addToast(error.error || 'Failed to save pairings', 'error');
        }
      }
    } catch (error) {
      console.error('Error saving pairings:', error);
      addToast('Failed to save pairings', 'error');
    } finally {
      setSavingPairings(false);
    }
  };

  // Get unique group numbers that have players assigned
  const getActiveGroups = () => {
    const groups = new Set(Object.values(pairingGroups));
    return Array.from(groups).sort((a, b) => a - b);
  };

  // Group players by pairing group for display
  const getGroupedPlayers = () => {
    const groups = {};
    const unassigned = [];
    
    randomizedPlayers.forEach((player, index) => {
      const groupNum = pairingGroups[player.id];
      if (groupNum !== undefined && groupNum !== null) {
        if (!groups[groupNum]) {
          groups[groupNum] = [];
        }
        groups[groupNum].push({ ...player, displayIndex: index });
      } else {
        unassigned.push({ ...player, displayIndex: index });
      }
    });
    
    return { groups, unassigned };
  };

  // Check if any pairing groups are set
  const hasPairingGroups = Object.keys(pairingGroups).length > 0;

  // Shotgun: Handle starting hole change for a group
  const handleStartingHoleChange = (groupNum, hole) => {
    const holeVal = hole === '' ? null : parseInt(hole);
    setStartingHoles(prev => {
      const updated = { ...prev };
      if (holeVal === null) {
        delete updated[groupNum];
        // Also clear label since no hole assigned
        setGroupLabels(prevLabels => {
          const updatedLabels = { ...prevLabels };
          delete updatedLabels[groupNum];
          return updatedLabels;
        });
      } else {
        updated[groupNum] = holeVal;
        // Auto-assign labels when groups share the same hole
        autoAssignLabels({ ...updated, [groupNum]: holeVal });
      }
      return updated;
    });
    setPairingsChanged(true);
  };

  // Shotgun: Auto-assign A/B/C/D labels when multiple groups share same hole
  const autoAssignLabels = (holesMap) => {
    // Group groupNums by hole number
    const holeGroups = {};
    for (const [gNum, hole] of Object.entries(holesMap)) {
      if (hole) {
        if (!holeGroups[hole]) holeGroups[hole] = [];
        holeGroups[hole].push(parseInt(gNum));
      }
    }
    
    const newLabels = {};
    for (const [hole, groups] of Object.entries(holeGroups)) {
      if (groups.length > 1) {
        // Sort groups numerically and assign A, B, C, D...
        groups.sort((a, b) => a - b);
        groups.forEach((gNum, idx) => {
          newLabels[gNum] = String.fromCharCode(65 + idx); // A, B, C, D...
        });
      }
      // Single group on a hole: no label needed
    }
    
    setGroupLabels(newLabels);
  };

  // Shotgun: Handle manual group label change with auto-swap
  const handleGroupLabelChange = (groupNum, newLabel) => {
    if (!newLabel) return;
    const myHole = startingHoles[groupNum];
    if (!myHole) return;
    
    // Find the other group on the same hole that currently has this label
    const conflictGroup = Object.entries(groupLabels).find(
      ([gNum, label]) => parseInt(gNum) !== groupNum && label === newLabel && startingHoles[parseInt(gNum)] === myHole
    );
    
    setGroupLabels(prev => {
      const updated = { ...prev };
      if (conflictGroup) {
        // Swap: give the conflicting group our old label
        updated[parseInt(conflictGroup[0])] = prev[groupNum] || 'A';
      }
      updated[groupNum] = newLabel;
      return updated;
    });
    
    // After label swap, sync slot times to group times
    // The slot times are keyed by hole-label, so they stay with the slot
    // We just need to re-map which group number gets which time
    setTimeout(() => syncSlotTimesToGroupTimes(), 50);
    setPairingsChanged(true);
  };

  // Shotgun: Get groups that share the same starting hole
  const getGroupsOnSameHole = (groupNum) => {
    const myHole = startingHoles[groupNum];
    if (!myHole) return [];
    return getActiveGroups().filter(g => startingHoles[g] === myHole);
  };

  // Shotgun: Calculate HCP total for a group
  const getGroupHcpTotal = (groupNum) => {
    // Multi-select aware: selectedTeeTime is null when multiple tee times are loaded
    const _tt = effectiveSelectedTeeTime || selectedTeeTime;
    if (!_tt) return null;
    const groupPlayerIds = Object.entries(pairingGroups)
      .filter(([_, g]) => g === groupNum)
      .map(([pid]) => pid);
    
    let total = 0;
    let hasAny = false;
    for (const pid of groupPlayerIds) {
      const player = randomizedPlayers.find(p => p.id === pid);
      if (player) {
        const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
        if (courseHcp !== null) {
          total += courseHcp;
          hasAny = true;
        }
      }
    }
    return hasAny ? total : null;
  };

  // Shared CSV download for the Export menu (Sept 27, 2026)
  const downloadPairingsCsv = (rows, prefix) => {
    const csvContent = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = selectedTeeTime?.date ? selectedTeeTime.date.substring(0, 10) : 'export';
    link.download = `${prefix}_${String(selectedTeeTime?.course || 'course').replace(/[^a-zA-Z0-9]/g, '_')}_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Shotgun: Generate Golf Genius CSV export
  const handleGolfGeniusExport = () => {
    setShowExportMenu(false);
    if (!selectedTeeTime || randomizedPlayers.length === 0) return;
    
    const rows = [['First Name', 'Last Name', 'Email', 'GHIN', 'Handicap Index', 'Tee', 'Starting Hole', 'Group']];
    
    // Sort by hole → label → course HCP for export
    const sortedPlayers = getShotgunSortedPlayers();
    
    for (const player of sortedPlayers) {
      const groupNum = pairingGroups[player.id];
      const hole = groupNum ? (startingHoles[groupNum] || '') : '';
      const label = groupNum ? (groupLabels[groupNum] || '') : '';
      const nameParts = (player.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const playerTee = getPlayerTee(player, selectedTeeTime?.course);
      const teeName = playerTee?.tee_name || '';
      const hcpIndex = player.hcp || player.handicap || '';
      const ghin = player.ghin || '';
      const groupDisplay = hole ? startHoleText(hole, label, pairingNines()) : (groupNum || '');
      
      rows.push([firstName, lastName, player.email || '', ghin, hcpIndex, teeName, hole, groupDisplay]);
    }
    
    const csvContent = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = selectedTeeTime?.date ? selectedTeeTime?.date.substring(0, 10) : 'export';
    link.download = `GolfGenius_${selectedTeeTime?.course.replace(/[^a-zA-Z0-9]/g, '_')}_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    addToast('Golf Genius CSV exported!', 'success');
  };

  // Shotgun: Sort players in STRUCTURE order — group 1, 2, 3 … — then array order.
  // Sept 15, 2026: this used to sort by hole number, which listed a reverse-shotgun
  // field (1, 18, 17, 16) as 1, 16, 17, 18. Group numbers are assigned by Edit
  // Setup in the order the holes were laid out (the convoy: group 1 leads on
  // hole 1, group 2 follows from 18 …), so group order IS the intended order —
  // and for a standard 1–18 shotgun it is still 1 … 18. Groups on the same hole
  // (A/B) are consecutive numbers, so they stay together. Unassigned trail.
  // The Hole Assignments panel, the printed / emailed tee sheet and the
  // reminder-email tee sheets (reminderService) follow the same rule.
  const getShotgunSortedPlayers = () => {
    if (!shotgunMode) return randomizedPlayers;
    
    // Build index map to preserve manual ordering within groups
    const indexMap = new Map();
    randomizedPlayers.forEach((p, idx) => indexMap.set(p.id, idx));
    
    const sorted = [...randomizedPlayers].sort((a, b) => {
      const aGroup = pairingGroups[a.id];
      const bGroup = pairingGroups[b.id];
      const aOrder = aGroup ? Number(aGroup) : Number.MAX_SAFE_INTEGER;
      const bOrder = bGroup ? Number(bGroup) : Number.MAX_SAFE_INTEGER;
      
      if (aOrder !== bOrder) return aOrder - bOrder;
      
      // Within same group, preserve array order (supports manual arrow moves)
      return indexMap.get(a.id) - indexMap.get(b.id);
    });
    
    return sorted;
  };

  // Export: plain pairings CSV — our own layout, opens in Excel/Sheets (Sept 27, 2026)
  const handlePairingsCsvExport = () => {
    setShowExportMenu(false);
    if (!selectedTeeTime || randomizedPlayers.length === 0) return;
    const rows = [['Group', 'Starting Hole', 'Player', 'Handicap Index', 'Tee', 'Email', 'GHIN']];
    for (const player of getShotgunSortedPlayers()) {
      const groupNum = pairingGroups[player.id];
      const hole = groupNum ? (startingHoles[groupNum] || '') : '';
      const label = groupNum ? (groupLabels[groupNum] || '') : '';
      const playerTee = getPlayerTee(player, selectedTeeTime?.course);
      rows.push([
        groupNum || '',
        hole ? `${holeName(hole, pairingNines())}${label || ''}` : '',
        player.name || '',
        player.hcp || player.handicap || '',
        playerTee?.tee_name || '',
        player.email || '',
        player.ghin || ''
      ]);
    }
    downloadPairingsCsv(rows, 'Pairings');
    addToast('Pairings CSV exported', 'success');
  };

  // Shotgun Wizard: Initialize hole config from preset
  const initWizardPreset = (preset, size = wizardGroupSize) => {
    const course = courses.find(c => c.name === (effectiveSelectedTeeTime || selectedTeeTime)?.course);
    const holeCount = course?.holes || 18;
    setWizardPreset(preset);
    
    if (preset === 'standard') {
      // Standard: one group per hole for all holes
      setWizardHoleConfig(
        Array.from({ length: holeCount }, (_, i) => ({ hole: i + 1, groups: 1 }))
      );
    } else if (preset === 'modified') {
      // Modified: front 9 only, one group per hole
      const count = Math.min(9, holeCount);
      setWizardHoleConfig(
        Array.from({ length: count }, (_, i) => ({ hole: i + 1, groups: 1 }))
      );
    } else if (preset === 'fit') {
      // Fit to players: only as many starting groups as the field needs at the
      // wizard's group size — foursomes, threesomes or twosomes (Sept 15, 2026:
      // 8 players in twosomes need 4 groups, not the 2 a foursome fit made).
      // Holes are filled as a REVERSE shotgun — 1 first, then 18, 17, 16 … —
      // the way Wild Dunes (and Golf Genius's "Reverse Shotgun") seat a partial
      // field: the groups play as one convoy behind hole 1, so hole 1 clears in
      // minutes for the starter's regular play, the rest of the course stays
      // open ahead of them, and everyone finishes on 18/17/16 by the clubhouse.
      // (Dennis, Sept 15; was an even spread 1, 5, 10, 14, which scattered the
      // finishes and blocked the whole course.) Group 1 sits on hole 1, group 2
      // on 18, group 3 on 17 — group number = place in the convoy. More groups
      // than holes → second (A/B) groups in the same order, up to 4 per hole.
      const needed = fitGroupsNeeded(size);
      const priority = [1, ...Array.from({ length: holeCount - 1 }, (_, i) => holeCount - i)]; // 1, 18, 17, … 2
      const groups = new Map();
      for (let i = 0; i < needed; i++) {
        const hole = priority[i % holeCount];
        if ((groups.get(hole) || 0) >= 4) continue; // never more than 4 on a hole (A–D)
        groups.set(hole, (groups.get(hole) || 0) + 1);
      }
      setWizardHoleConfig(priority.filter(h => groups.has(h)).map(h => ({ hole: h, groups: groups.get(h) })));
    }
  };

  // Shotgun Wizard: change the group size; a Fit layout re-fits to the new size
  const setWizardSize = (size) => {
    setWizardGroupSize(size);
    if (wizardPreset === 'fit') initWizardPreset('fit', size);
  };

  // Shotgun Wizard: Open wizard with current config or default
  const openShotgunWizard = () => {
    const activeGroups = getActiveGroups();
    const course = courses.find(c => c.name === (effectiveSelectedTeeTime || selectedTeeTime)?.course);
    const holeCount = course?.holes || 18;
    
    // If there's already a hole config from a previous wizard session, restore it
    if (wizardHoleConfig.length > 0) {
      setShowShotgunWizard(true);
      return;
    }
    
    // Build config from existing startingHoles if any
    if (Object.keys(startingHoles).length > 0) {
      const holeGroupCount = {};
      Object.values(startingHoles).forEach(h => {
        holeGroupCount[h] = (holeGroupCount[h] || 0) + 1;
      });
      const config = Object.entries(holeGroupCount)
        .map(([hole, groups]) => ({ hole: parseInt(hole), groups }))
        .sort((a, b) => a.hole - b.hole);
      setWizardHoleConfig(config);
      setWizardPreset(null);
    } else {
      // Default: standard preset
      setWizardHoleConfig(
        Array.from({ length: holeCount }, (_, i) => ({ hole: i + 1, groups: 1 }))
      );
      setWizardPreset('standard');
    }
    setShowShotgunWizard(true);
  };

  // Shotgun Wizard: Update group count for a specific hole
  const updateWizardHoleGroups = (holeNum, newCount) => {
    setWizardHoleConfig(prev => 
      prev.map(h => h.hole === holeNum ? { ...h, groups: Math.max(0, Math.min(4, newCount)) } : h)
    );
    setWizardPreset(null);
  };

  // Shotgun Wizard: Add a hole row to the config
  const addWizardHoleRow = () => {
    const course = courses.find(c => c.name === (effectiveSelectedTeeTime || selectedTeeTime)?.course);
    const holeCount = course?.holes || 18;
    const usedHoles = new Set(wizardHoleConfig.map(h => h.hole));
    // Find first unused hole
    for (let i = 1; i <= holeCount; i++) {
      if (!usedHoles.has(i)) {
        setWizardHoleConfig(prev => [...prev, { hole: i, groups: 1 }].sort((a, b) => a.hole - b.hole));
        setWizardPreset(null);
        return;
      }
    }
  };

  // Shotgun Wizard: Remove a hole row
  const removeWizardHoleRow = (holeNum) => {
    setWizardHoleConfig(prev => prev.filter(h => h.hole !== holeNum));
    setWizardPreset(null);
  };

  // Shotgun Wizard: Change which hole number a row uses
  const changeWizardHoleNumber = (oldHole, newHole) => {
    setWizardHoleConfig(prev => 
      prev.map(h => h.hole === oldHole ? { ...h, hole: newHole } : h)
        .sort((a, b) => a.hole - b.hole)
    );
    setWizardPreset(null);
  };

  // Shotgun Wizard: Calculate total slots from config
  const getWizardTotalSlots = () => {
    return wizardHoleConfig.reduce((sum, h) => sum + h.groups, 0);
  };

  // Shotgun Wizard: Apply config - creates group structure with hole assignments
  const applyWizardConfig = () => {
    const newHoles = {};
    const newLabels = {};
    
    // Build slot list: [{hole, label}, ...] from config
    const slots = [];
    wizardHoleConfig.forEach(({ hole, groups }) => {
      for (let g = 0; g < groups; g++) {
        const label = groups > 1 ? String.fromCharCode(65 + g) : ''; // A, B, C, D
        slots.push({ hole, label });
      }
    });
    
    // Create group structure: Group 1 = Slot 1, Group 2 = Slot 2, etc.
    slots.forEach((slot, i) => {
      const groupNum = i + 1;
      newHoles[groupNum] = slot.hole;
      if (slot.label) {
        newLabels[groupNum] = slot.label;
      }
    });
    
    // If players already have group assignments, remap them to fit the new structure
    const activeGroups = getActiveGroups();
    if (activeGroups.length > 0) {
      // Existing groups get remapped to new slot structure
      const existingGroupMap = {}; // oldGroup -> newGroup
      activeGroups.sort((a, b) => a - b).forEach((oldGroup, i) => {
        if (i < slots.length) {
          existingGroupMap[oldGroup] = i + 1;
        }
      });
      
      const newPairings = {};
      Object.entries(pairingGroups).forEach(([playerId, oldGroup]) => {
        const newGroup = existingGroupMap[oldGroup];
        if (newGroup) {
          newPairings[playerId] = newGroup;
        }
        // Players in groups beyond slot count become unassigned
      });
      setPairingGroups(newPairings);
    }
    
    setStartingHoles(newHoles);
    setGroupLabels(newLabels);
    setLastShotgunFill(null);
    setPairingsChanged(true);
    setShowShotgunWizard(false);
    addToast(`🎯 Shotgun structure created: ${slots.length} groups across ${wizardHoleConfig.length} holes`, 'success');
  };

  // Shotgun: Get the slot key (hole-label) for a group number
  const getSlotKey = (groupNum) => {
    const hole = startingHoles[groupNum];
    const label = groupLabels[groupNum] || '';
    return hole ? `${hole}-${label}` : null;
  };

  // Shotgun: Get display label for a group (e.g., "1A", "10B")
  const getSlotLabel = (groupNum) => {
    const hole = startingHoles[groupNum];
    const label = groupLabels[groupNum] || '';
    return hole ? `${holeName(hole, pairingNines())}${label}` : `Grp ${groupNum}`;
  };

  // Shotgun: Get time for a slot (by hole-label key)
  const getSlotTime = (groupNum) => {
    const key = getSlotKey(groupNum);
    return key ? (shotgunSlotTimes[key] || '') : '';
  };

  // Shotgun: Set time for a slot
  const setSlotTime = (groupNum, time) => {
    const key = getSlotKey(groupNum);
    if (key) {
      setShotgunSlotTimes(prev => ({ ...prev, [key]: time }));
      // Also sync to groupTimes for save/print compatibility
      setGroupTimes(prev => ({ ...prev, [groupNum]: time }));
      setPairingsChanged(true);
    }
  };

  // Shotgun: Sync slot times to groupTimes (call after any hole/label change)
  const syncSlotTimesToGroupTimes = () => {
    const newGroupTimes = {};
    Object.keys(startingHoles).forEach(groupNum => {
      const key = getSlotKey(parseInt(groupNum));
      if (key && shotgunSlotTimes[key]) {
        newGroupTimes[parseInt(groupNum)] = shotgunSlotTimes[key];
      }
    });
    setGroupTimes(newGroupTimes);
  };

  // Shotgun: Get total group count from startingHoles
  const getShotgunGroupCount = () => {
    return Object.keys(startingHoles).length;
  };

  // Shotgun: Auto-fill players into the shotgun group structure
  const shotgunAutoFill = (method, groupSize = 4) => {
    const numGroups = getShotgunGroupCount();
    if (numGroups === 0) return;
    if (!okToSplitTeams()) return;   // registered teams (Oct 10, 2026)
    
    // How many groups we actually need for these players
    const neededGroups = Math.ceil(randomizedPlayers.length / groupSize);
    // Sept 15, 2026: the structure must have room for everyone. Filling only the
    // groups that exist left 4 of 8 players unassigned with no explanation
    // (twosomes into a 2-group structure that Fit had built for foursomes).
    if (neededGroups > numGroups) {
      const word = groupSize === 2 ? 'twosomes' : groupSize === 3 ? 'threesomes' : 'foursomes';
      window.alert(
        `Not enough starting groups\n\n${randomizedPlayers.length} players in ${word} need ${neededGroups} groups, but the shotgun structure has ${numGroups}.\n\nOpen ⚡ Edit Setup, pick ${word[0].toUpperCase() + word.slice(1)} and press Fit to players (or add groups), then fill again.`
      );
      return;
    }
    const fillGroups = Math.min(neededGroups, numGroups);
    
    let players = [...randomizedPlayers];
    
    if (method === 'randomize') {
      // Fisher-Yates shuffle
      for (let i = players.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [players[i], players[j]] = [players[j], players[i]];
      }
      setRandomizedPlayers(players);
    } else if (method === 'hcp') {
      // Sort by course HCP (lowest first)
      const courseName = (effectiveSelectedTeeTime || selectedTeeTime)?.course;
      players.sort((a, b) => {
        const aHcp = getPlayerCourseHandicap(a, courseName) ?? 999;
        const bHcp = getPlayerCourseHandicap(b, courseName) ?? 999;
        return aHcp - bHcp;
      });
      setRandomizedPlayers(players);
    } else if (method === 'balanced') {
      // Snake draft balanced flights into shotgun groups
      const courseName = (effectiveSelectedTeeTime || selectedTeeTime)?.course;

      // ============================================================================
      // PRE-FLIGHT VALIDATION (added May 9, 2026)
      // Same rules as handlePairByHCP: block missing HCPs hard, warn on uneven distribution.
      // ============================================================================
      const playersWithoutHcp = players.filter(
        p => getPlayerCourseHandicap(p, courseName) === null
      );
      if (playersWithoutHcp.length > 0) {
        const names = playersWithoutHcp.map(p => p.name).join(', ');
        const playerWord = playersWithoutHcp.length === 1 ? 'player has' : 'players have';
        window.alert(
          `Cannot create balanced shotgun groups\n\n${playersWithoutHcp.length} ${playerWord} no handicap:\n${names}\n\nAdd a handicap index in the player profile, remove the player from this tee time, or use Randomize instead.`
        );
        return;
      }
      if (groupSize === 2 && players.length % 2 !== 0) {
        window.alert(
          `Cannot create balanced twosomes\n\n${players.length} players is an odd number. Twosomes require an even number of players.\n\nAdd or remove a player, or use a different group size.`
        );
        return;
      }
      if (groupSize === 4 && players.length % 4 !== 0) {
        const fullGroups = Math.floor(players.length / 4);
        const remainder = players.length % 4;
        const msg = `${players.length} players doesn't divide evenly into foursomes — you'll get ${fullGroups} foursome(s) plus a group of ${remainder}.\n\nProceed anyway?`;
        if (!window.confirm(msg)) return;
      }

      const ranked = players.filter(p => {
        const hcp = getPlayerCourseHandicap(p, courseName);
        return hcp !== null && hcp !== undefined;
      }).sort((a, b) => {
        return (getPlayerCourseHandicap(a, courseName) || 0) - (getPlayerCourseHandicap(b, courseName) || 0);
      });
      const unranked = players.filter(p => {
        const hcp = getPlayerCourseHandicap(p, courseName);
        return hcp === null || hcp === undefined;
      });
      
      // Snake draft across only the groups we need (not all shotgun groups)
      const groups = {};
      for (let g = 1; g <= fillGroups; g++) groups[g] = [];
      
      ranked.forEach((p, i) => {
        const round = Math.floor(i / fillGroups);
        const pos = i % fillGroups;
        const g = round % 2 === 0 ? pos + 1 : fillGroups - pos;
        groups[g].push(p);
      });
      unranked.forEach((p, i) => {
        const g = (i % fillGroups) + 1;
        groups[g].push(p);
      });
      
      // Build order and pairings
      const newOrder = [];
      const newPairings = {};
      for (let g = 1; g <= fillGroups; g++) {
        groups[g].forEach(p => {
          newPairings[p.id] = g;
          newOrder.push(p);
        });
      }
      // Add any remaining players (shouldn't happen but be safe)
      players.forEach(p => {
        if (!newPairings[p.id] && !newOrder.includes(p)) newOrder.push(p);
      });
      setRandomizedPlayers(newOrder);
      setPairingGroups(newPairings);
      setPairingsChanged(true);
      setLastShotgunFill({ method, size: groupSize });
      const emptyGroups = numGroups - fillGroups;
      addToast(`⚖️ ${players.length} players in ${fillGroups} balanced ${groupSize === 2 ? 'twosomes' : 'foursomes'}${emptyGroups > 0 ? ` (${emptyGroups} groups empty)` : ''}`, 'success');
      return;
    }
    
    // For randomize and hcp: sequential fill, groupSize per group
    const newPairings = {};
    players.forEach((p, i) => {
      const g = Math.floor(i / groupSize) + 1;
      if (g <= numGroups) {
        newPairings[p.id] = g;
      }
    });
    setPairingGroups(newPairings);
    setPairingsChanged(true);
    setLastShotgunFill({ method, size: groupSize });
    const filledCount = Math.min(fillGroups, numGroups);
    const emptyGroups = numGroups - filledCount;
    addToast(`🎯 ${players.length} players filled into ${filledCount} groups (${method})${emptyGroups > 0 ? ` • ${emptyGroups} groups empty` : ''}`, 'success');
  };

  // Shotgun banner: the fill last applied is the filled button, the rest are outline.
  // (Sept 15, 2026 — static "primary" styling on Foursomes › Balanced read as a selection.)
  const shotgunFillButtonStyle = (method, size) => {
    const on = !!lastShotgunFill && lastShotgunFill.method === method && lastShotgunFill.size === size;
    return {
      padding: '5px 12px', border: `1px solid ${on ? '#2d6a4f' : '#6b7280'}`, borderRadius: 6,
      background: on ? '#2d6a4f' : '#fff', color: on ? '#fff' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer'
    };
  };

  // Generate print HTML with group structure
  const generatePrintHtml = () => {
    // Multi-select aware: fall back to effectiveSelectedTeeTime so Course HCP renders
    // correctly when multiple tee times are loaded (selectedTeeTime is null in that case).
    const _tt = effectiveSelectedTeeTime || selectedTeeTime;
    const { groups, unassigned } = getGroupedPlayers();
    const sortedGroupNums = Object.keys(groups).map(Number).sort((a, b) => a - b);
    
    let playerListHtml = '';
    let playerNumber = 1;
    
    if (hasPairingGroups && sortedGroupNums.length > 0) {
      if (shotgunMode) {
        // Shotgun layout: organize by starting hole
        // Build hole → groups mapping
        const holeMap = {}; // { hole: [groupNum, ...] }
        const noHoleGroups = [];
        
        for (const groupNum of sortedGroupNums) {
          const hole = startingHoles[groupNum];
          if (hole) {
            if (!holeMap[hole]) holeMap[hole] = [];
            holeMap[hole].push(groupNum);
          } else {
            noHoleGroups.push(groupNum);
          }
        }
        
        // Holes in STRUCTURE order — by the first group on each hole (1, 18, 17, 16
        // for a reverse-shotgun fit; 1 … 18 for a standard one). Was: by hole number.
        const sortedHoles = Object.keys(holeMap).map(Number).sort((a, b) => Math.min(...holeMap[a]) - Math.min(...holeMap[b]));
        
        for (const hole of sortedHoles) {
          const holeGroups = holeMap[hole];
          // Sort groups on same hole by label
          holeGroups.sort((a, b) => {
            const aLabel = groupLabels[a] || '';
            const bLabel = groupLabels[b] || '';
            return aLabel.localeCompare(bLabel);
          });
          
          for (const groupNum of holeGroups) {
            const groupPlayers = groups[groupNum];
            const label = groupLabels[groupNum] || '';
            // Use slot time (hole-label keyed), then groupTimes, then fall back to tee time
            const slotKey = `${hole}-${label}`;
            const groupTime = (shotgunMode ? shotgunSlotTimes[slotKey] : null) || groupTimes[groupNum] || (_tt?.time ? utils.formatTime(_tt?.time) : '');
            const headerText = `${startHoleText(hole, label, pairingNines()).toUpperCase()}${groupTime ? ` - ${groupTime}` : ''}`;
            
            // Calculate group HCP total for shotgun print
            let shotgunHcpTotal = null;
            if (settings?.teesheet_show_handicap && _tt) {
              let hcpSum = 0;
              let hasHcp = false;
              groupPlayers.forEach(player => {
                const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
                if (courseHcp !== null) { hcpSum += courseHcp; hasHcp = true; }
              });
              if (hasHcp) shotgunHcpTotal = hcpSum;
            }
            
            playerListHtml += `
              <div style="margin-bottom: 16px; padding: 12px; background: #f9fafb; border-radius: 8px; border-left: 4px solid #2d6a4f;">
                <div style="font-weight: 700; font-size: 14px; color: #1b4332; margin-bottom: 8px;">
                  ${headerText}${shotgunHcpTotal !== null ? ` <span style="font-size: 11px; padding: 2px 8px; border-radius: 4px; background: #e0f2fe; color: #1e40af; font-weight: 700; border: 1px solid #bfdbfe; margin-left: 8px;">HCP Total: ${shotgunHcpTotal}</span>` : ''}
                </div>
                <ul class="player-list" style="margin: 0; padding: 0;">
                  ${groupPlayers.map(player => {
                    const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
                    const playerTee = getPlayerTee(player, _tt?.course);
                    return `
                    <li class="player-item">
                      <span class="player-number">${playerNumber++}</span>
                      <span>${player.name || player}</span>
                      ${(player.clubMember || player.club_member) ? '<span class="member-badge">⭐ Member</span>' : ''}
                      ${settings?.teesheet_show_handicap && (player.hcp || player.handicap) ? `<span class="handicap-badge">HCP: ${player.hcp || player.handicap}</span>` : ''}
                      ${settings?.teesheet_show_handicap && courseHcp !== null ? `<span class="course-hcp-badge" style="background: ${playerTee?.tee_color || '#000'}; color: ${['#f3f4f6', '#fef3c7', '#ffffff', '#fff'].includes(playerTee?.tee_color) ? '#374151' : '#fff'}; ${['#f3f4f6', '#fef3c7', '#ffffff', '#fff'].includes(playerTee?.tee_color) ? 'border: 1px solid #d1d5db;' : ''}">Course: ${courseHcp}${playerTee?.tee_name ? ` (${playerTee.tee_name})` : ''}</span>` : ''}
                    </li>
                  `}).join('')}
                </ul>
              </div>
            `;
          }
        }
        
        // Add groups without holes assigned
        if (noHoleGroups.length > 0) {
          playerListHtml += `
            <div style="margin-top: 16px; padding-top: 12px; border-top: 2px dashed #fbbf24;">
              <div style="font-weight: 600; font-size: 13px; color: #92400e; margin-bottom: 8px;">⚠️ No Starting Hole Assigned:</div>
          `;
          for (const groupNum of noHoleGroups) {
            const groupPlayers = groups[groupNum];
            const groupTime = groupTimes[groupNum] || (_tt?.time ? utils.formatTime(_tt?.time) : '');
            playerListHtml += `
              <div style="margin-bottom: 12px; padding: 8px; background: #fef3c7; border-radius: 6px;">
                <div style="font-weight: 600; font-size: 13px; color: #92400e; margin-bottom: 4px;">
                  GROUP ${groupNum}${groupTime ? ` - ${groupTime}` : ''}
                </div>
                <ul class="player-list" style="margin: 0; padding: 0;">
                  ${groupPlayers.map(player => {
                    const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
                    const playerTee = getPlayerTee(player, _tt?.course);
                    return `
                    <li class="player-item">
                      <span class="player-number" style="background: #f59e0b;">${playerNumber++}</span>
                      <span>${player.name || player}</span>
                      ${settings?.teesheet_show_handicap && courseHcp !== null ? `<span class="course-hcp-badge" style="background: ${playerTee?.tee_color || '#000'}; color: ${['#f3f4f6', '#fef3c7', '#ffffff', '#fff'].includes(playerTee?.tee_color) ? '#374151' : '#fff'};">Course: ${courseHcp}</span>` : ''}
                    </li>
                  `}).join('')}
                </ul>
              </div>
            `;
          }
          playerListHtml += '</div>';
        }
      } else {
        // Standard grouped layout (unchanged)
        for (const groupNum of sortedGroupNums) {
          const groupPlayers = groups[groupNum];
          const groupTime = groupTimes[groupNum] || '';
          
          // Calculate group HCP total for print
          let groupHcpTotal = null;
          if (settings?.teesheet_show_handicap && _tt) {
            let hcpSum = 0;
            let hasHcp = false;
            groupPlayers.forEach(player => {
              const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
              if (courseHcp !== null) { hcpSum += courseHcp; hasHcp = true; }
            });
            if (hasHcp) groupHcpTotal = hcpSum;
          }
          
          playerListHtml += `
            <div style="margin-bottom: 16px; padding: 12px; background: #f9fafb; border-radius: 8px; border-left: 4px solid #2d6a4f;">
              <div style="font-weight: 700; font-size: 14px; color: #1b4332; margin-bottom: 8px;">
                GROUP ${groupNum}${groupTime ? ` - ${groupTime}` : ''}${groupHcpTotal !== null ? ` <span style="font-size: 11px; padding: 2px 8px; border-radius: 4px; background: #e0f2fe; color: #1e40af; font-weight: 700; border: 1px solid #bfdbfe; margin-left: 8px;">HCP Total: ${groupHcpTotal}</span>` : ''}
              </div>
              <ul class="player-list" style="margin: 0; padding: 0;">
                ${groupPlayers.map(player => {
                  const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
                  const playerTee = getPlayerTee(player, _tt?.course);
                  return `
                  <li class="player-item">
                    <span class="player-number">${playerNumber++}</span>
                    <span>${player.name || player}</span>
                    ${(player.clubMember || player.club_member) ? '<span class="member-badge">⭐ Member</span>' : ''}
                    ${settings?.teesheet_show_handicap && (player.hcp || player.handicap) ? `<span class="handicap-badge">HCP: ${player.hcp || player.handicap}</span>` : ''}
                    ${settings?.teesheet_show_handicap && courseHcp !== null ? `<span class="course-hcp-badge" style="background: ${playerTee?.tee_color || '#000'}; color: ${['#f3f4f6', '#fef3c7', '#ffffff', '#fff'].includes(playerTee?.tee_color) ? '#374151' : '#fff'}; ${['#f3f4f6', '#fef3c7', '#ffffff', '#fff'].includes(playerTee?.tee_color) ? 'border: 1px solid #d1d5db;' : ''}">Course: ${courseHcp}${playerTee?.tee_name ? ` (${playerTee.tee_name})` : ''}</span>` : ''}
                  </li>
                `}).join('')}
              </ul>
            </div>
          `;
        }
      }
      
      // Add unassigned players
      if (unassigned.length > 0) {
        playerListHtml += `
          <div style="margin-top: 16px; padding-top: 12px; border-top: 2px dashed #e5e7eb;">
            <div style="font-weight: 600; font-size: 13px; color: #6b7280; margin-bottom: 8px;">Unassigned:</div>
            <ul class="player-list" style="margin: 0; padding: 0;">
              ${unassigned.map(player => {
                const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
                const playerTee = getPlayerTee(player, _tt?.course);
                return `
                <li class="player-item">
                  <span class="player-number" style="background: #9ca3af;">${playerNumber++}</span>
                  <span>${player.name || player}</span>
                  ${(player.clubMember || player.club_member) ? '<span class="member-badge">⭐ Member</span>' : ''}
                  ${settings?.teesheet_show_handicap && (player.hcp || player.handicap) ? `<span class="handicap-badge">HCP: ${player.hcp || player.handicap}</span>` : ''}
                  ${settings?.teesheet_show_handicap && courseHcp !== null ? `<span class="course-hcp-badge" style="background: ${playerTee?.tee_color || '#000'}; color: ${['#f3f4f6', '#fef3c7', '#ffffff', '#fff'].includes(playerTee?.tee_color) ? '#374151' : '#fff'}; ${['#f3f4f6', '#fef3c7', '#ffffff', '#fff'].includes(playerTee?.tee_color) ? 'border: 1px solid #d1d5db;' : ''}">Course: ${courseHcp}${playerTee?.tee_name ? ` (${playerTee.tee_name})` : ''}</span>` : ''}
                </li>
              `}).join('')}
            </ul>
          </div>
        `;
      }
    } else {
      // Flat list (no groups)
      playerListHtml = randomizedPlayers.length > 0 ? `
        <ul class="player-list">
          ${randomizedPlayers.map((player, i) => {
            const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
            const playerTee = getPlayerTee(player, _tt?.course);
            return `
            <li class="player-item">
              <span class="player-number">${i + 1}</span>
              <span>${player.name || player}</span>
              ${(player.clubMember || player.club_member) ? '<span class="member-badge">⭐ Member</span>' : ''}
              ${settings?.teesheet_show_handicap && (player.hcp || player.handicap) ? `<span class="handicap-badge">HCP: ${player.hcp || player.handicap}</span>` : ''}
              ${settings?.teesheet_show_handicap && courseHcp !== null ? `<span class="course-hcp-badge" style="background: ${playerTee?.tee_color || '#000'}; color: ${['#f3f4f6', '#fef3c7', '#ffffff', '#fff'].includes(playerTee?.tee_color) ? '#374151' : '#fff'}; ${['#f3f4f6', '#fef3c7', '#ffffff', '#fff'].includes(playerTee?.tee_color) ? 'border: 1px solid #d1d5db;' : ''}">Course: ${courseHcp}${playerTee?.tee_name ? ` (${playerTee.tee_name})` : ''}</span>` : ''}
            </li>
          `}).join('')}
        </ul>
      ` : '<p style="text-align: center; color: #9ca3af; padding: 40px;">No players assigned</p>';
    }
    
    return playerListHtml;
  };

  // preOpened: a window the Print tab opened on its click (pop-up blockers) — the
  // tee sheet is written into it. A click on this page passes the click event.
  const handlePrint = (preOpened) => {
    if (!selectedTeeTime && !multiSelectMode) return;
    
    // In multi-mode, derive info from the loaded tee times
    const printCourse = selectedTeeTime?.course || Object.values(multiTeeTimeMap)[0]?.course || '';
    const printDate = selectedTeeTime?.date || Object.values(multiTeeTimeMap)[0]?.date || '';
    const printTime = multiSelectMode 
      ? (() => {
          // Only show tee times that have players assigned (exclude empty ones)
          const emptyIds = new Set(emptyTeeTimes.map(t => t.id));
          return Object.values(multiTeeTimeMap)
            .filter(t => !emptyIds.has(t.id))
            .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
            .map(t => utils.formatTime(t.time)).join(', ');
        })()
      : utils.formatTime(selectedTeeTime?.time);
    const printSlots = multiSelectMode
      ? (() => {
          const emptyIds = new Set(emptyTeeTimes.map(t => t.id));
          return Object.values(multiTeeTimeMap).filter(t => !emptyIds.has(t.id)).reduce((sum, t) => sum + (t.slots || 4), 0);
        })()
      : selectedTeeTime?.slots;
    
    const printWindow = (preOpened && typeof preOpened.close === 'function' && !preOpened.closed) ? preOpened : window.open('', '_blank');
    if (!printWindow) { window.alert('Your browser blocked the print window. Allow pop-ups for this site and try again.'); return; }
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tee Time Organizer Tee Sheet</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 40px; 
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            border-bottom: 3px solid #2d6a4f;
            padding-bottom: 20px;
          }
          .title { 
            font-size: 28px;
            font-weight: 800;
            color: #2d6a4f;
            margin: 0 0 8px 0;
          }
          .subtitle {
            font-size: 14px;
            color: #6b7280;
            margin: 0;
          }
          .info-section {
            background: #f9fafb;
            padding: 12px;
            border-radius: 8px;
            margin-bottom: 30px;
          }
          .info-row {
            display: flex;
            padding: 4px 0;
            border-bottom: 1px solid #e5e7eb;
          }
          .info-row:last-child {
            border-bottom: none;
          }
          .info-label {
            font-weight: 600;
            color: #374151;
            width: 120px;
          }
          .info-value {
            color: #111827;
          }
          .players-section {
            margin-top: 30px;
          }
          .section-title {
            font-size: 16px;
            font-weight: 700;
            color: #111827;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e5e7eb;
          }
          .player-list {
            list-style: none;
            padding: 0;
            margin: 0;
          }
          .player-item {
            padding: 4px 8px;
            margin: 2px 0;
            background: #fff;
            font-size: 13px;
          }
          .player-number {
            display: inline-block;
            width: 20px;
            height: 20px;
            background: #2d6a4f;
            color: white;
            border-radius: 50%;
            text-align: center;
            line-height: 20px;
            margin-right: 12px;
            font-weight: 600;
            font-size: 11px;
          }
          .member-badge {
            display: inline-block;
            background: #dcfce7;
            color: #15803d;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: 600;
            margin-left: 8px;
          }
          .handicap-badge {
            display: inline-block;
            background: #e0e7ff;
            color: #4338ca;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: 600;
            margin-left: 8px;
          }
          .course-hcp-badge {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: 600;
            margin-left: 8px;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            color: #9ca3af;
            font-size: 10px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
          }
          .button-container {
            margin-top: 30px;
            text-align: center;
            display: flex;
            gap: 12px;
            justify-content: center;
          }
          .btn {
            padding: 12px 24px;
            border: none;
            border-radius: 6px;
            font-size: 16px;
            cursor: pointer;
            font-weight: 500;
          }
          .btn-print {
            background: #2d6a4f;
            color: white;
          }
          .btn-print:hover {
            background: #245740;
          }
          .btn-cancel {
            background: #e5e7eb;
            color: #374151;
          }
          .btn-cancel:hover {
            background: #d1d5db;
          }
          @media print {
            body { padding: 20px; }
            button { display: none; }
            .button-container { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="title">Tee Time Organizer Tee Sheet</h1>
          <div class="subtitle">TeeTimeOrganizer.net</div>
        </div>
        
        <div class="info-section">
          <div class="info-row">
            <div class="info-label">Course:</div>
            <div class="info-value">${printCourse}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Date:</div>
            <div class="info-value">${utils.formatDate(printDate)}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Tee Time:</div>
            <div class="info-value">${printTime}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Group:</div>
            <div class="info-value">${settings?.organization_name || 'No organization name set'}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Players:</div>
            <div class="info-value">${randomizedPlayers.length} players of ${printSlots} available playing slots</div>
          </div>
        </div>
        
        <div class="players-section">
          <h2 class="section-title">${hasPairingGroups ? 'Player Pairings' : 'Player Lineup'}</h2>
          ${generatePrintHtml()}
        </div>
        
        <div class="footer">
          Generated on ${new Date().toLocaleString()} • TeeTimeOrganizer.net
        </div>
        
        <div class="button-container">
          <button onclick="window.close()" class="btn btn-cancel">Cancel & Return</button>
          <button onclick="window.print()" class="btn btn-print">Print Tee Sheet</button>
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Generate email HTML with group structure
  const generateEmailHtml = () => {
    // Multi-select aware: fall back to effectiveSelectedTeeTime so Course HCP totals render
    // correctly when multiple tee times are loaded (selectedTeeTime is null in that case).
    const _tt = effectiveSelectedTeeTime || selectedTeeTime;
    const { groups, unassigned } = getGroupedPlayers();
    const sortedGroupNums = Object.keys(groups).map(Number).sort((a, b) => a - b);
    
    let playerListHtml = '';
    let playerNumber = 1;
    
    if (hasPairingGroups && sortedGroupNums.length > 0) {
      // Grouped layout for email
      for (const groupNum of sortedGroupNums) {
        const groupPlayers = groups[groupNum];
        // Use slot time (hole-label keyed) for shotgun, then groupTimes, then fall back to tee time
        const hole = shotgunMode ? startingHoles[groupNum] : null;
        const label = shotgunMode ? (groupLabels[groupNum] || '') : '';
        const slotKey = hole ? `${hole}-${label}` : null;
        const groupTime = (slotKey ? shotgunSlotTimes[slotKey] : null) || groupTimes[groupNum] 
          || (_tt?.time ? utils.formatTime(_tt?.time) : '');
        
        // Shotgun: show "HOLE X - time"; Standard: show "GROUP X - time"
        const groupHeader = shotgunMode && hole
          ? `${startHoleText(hole, label, pairingNines()).toUpperCase()}${groupTime ? ` - ${groupTime}` : ''}`
          : `GROUP ${groupNum}${groupTime ? ` - ${groupTime}` : ''}`;
        
        // Calculate group HCP total for email
        let emailGroupHcpTotal = null;
        if (settings?.teesheet_show_handicap && _tt) {
          let hcpSum = 0;
          let hasHcp = false;
          groupPlayers.forEach(player => {
            const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
            if (courseHcp !== null) { hcpSum += courseHcp; hasHcp = true; }
          });
          if (hasHcp) emailGroupHcpTotal = hcpSum;
        }
        
        playerListHtml += `
          <div style="margin-bottom: 16px; padding: 12px; background: #f9fafb; border-radius: 8px; border-left: 4px solid #2d6a4f;">
            <div style="font-weight: 700; font-size: 14px; color: #1b4332; margin-bottom: 8px;">
              ${groupHeader}${emailGroupHcpTotal !== null ? ` <span style="font-size: 11px; padding: 2px 8px; border-radius: 4px; background: #e0f2fe; color: #1e40af; font-weight: 700; border: 1px solid #bfdbfe; margin-left: 8px;">HCP Total: ${emailGroupHcpTotal}</span>` : ''}
            </div>
            <ul style="list-style: none; padding: 0; margin: 0;">
              ${groupPlayers.map(player => `
                <li style="padding: 3px 8px; margin: 1px 0; background: #fff;">
                  <span style="display: inline-block; width: 18px; height: 18px; background: #2d6a4f; color: white; border-radius: 50%; text-align: center; line-height: 18px; margin-right: 8px; font-weight: 600; font-size: 10px;">${playerNumber++}</span>
                  <span style="font-size: 13px;">${player.name || player}</span>
                  ${(player.clubMember || player.club_member) ? '<span style="display: inline-block; background: #dcfce7; color: #15803d; padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 600; margin-left: 8px;">⭐ Member</span>' : ''}
                </li>
              `).join('')}
            </ul>
          </div>
        `;
      }
      
      // Add unassigned players
      if (unassigned.length > 0) {
        playerListHtml += `
          <div style="margin-top: 16px; padding-top: 12px; border-top: 2px dashed #e5e7eb;">
            <div style="font-weight: 600; font-size: 12px; color: #6b7280; margin-bottom: 8px;">Unassigned:</div>
            <ul style="list-style: none; padding: 0; margin: 0;">
              ${unassigned.map(player => `
                <li style="padding: 3px 8px; margin: 1px 0; background: #fff;">
                  <span style="display: inline-block; width: 18px; height: 18px; background: #9ca3af; color: white; border-radius: 50%; text-align: center; line-height: 18px; margin-right: 8px; font-weight: 600; font-size: 10px;">${playerNumber++}</span>
                  <span style="font-size: 13px;">${player.name || player}</span>
                </li>
              `).join('')}
            </ul>
          </div>
        `;
      }
    } else {
      // Flat list (no groups)
      playerListHtml = randomizedPlayers.length > 0 ? `
        <ul style="list-style: none; padding: 0; margin: 0;">
          ${randomizedPlayers.map((player, i) => `
            <li style="padding: 3px 8px; margin: 1px 0; background: #fff;">
              <span style="display: inline-block; width: 18px; height: 18px; background: #2d6a4f; color: white; border-radius: 50%; text-align: center; line-height: 18px; margin-right: 8px; font-weight: 600; font-size: 10px;">${i + 1}</span>
              <span style="font-size: 13px;">${player.name || player}</span>
              ${(player.clubMember || player.club_member) ? '<span style="display: inline-block; background: #dcfce7; color: #15803d; padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 600; margin-left: 8px;">⭐ Member</span>' : ''}
            </li>
          `).join('')}
        </ul>
      ` : '<p style="text-align: center; color: #9ca3af; padding: 16px;">No players assigned</p>';
    }
    
    return playerListHtml;
  };

  const handleEmail = async () => {
    if (!selectedTeeTime && !multiSelectMode) return;
    
    // Multi-mode derived values for email content
    const emailCourse = selectedTeeTime?.course || Object.values(multiTeeTimeMap)[0]?.course || '';
    const emailDate = selectedTeeTime?.date || Object.values(multiTeeTimeMap)[0]?.date || '';
    const emailTime = multiSelectMode 
      ? (() => {
          const emptyIds = new Set(emptyTeeTimes.map(t => t.id));
          return Object.values(multiTeeTimeMap)
            .filter(t => !emptyIds.has(t.id))
            .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
            .map(t => utils.formatTime(t.time)).join(', ');
        })()
      : utils.formatTime(selectedTeeTime?.time);
    const emailSlots = multiSelectMode
      ? (() => {
          const emptyIds = new Set(emptyTeeTimes.map(t => t.id));
          return Object.values(multiTeeTimeMap).filter(t => !emptyIds.has(t.id)).reduce((sum, t) => sum + (t.slots || 4), 0);
        })()
      : selectedTeeTime?.slots;
    const emailTeeTimeId = selectedTeeTime?.id || Object.values(multiTeeTimeMap)[0]?.id;
    
    // Determine recipients based on target (for confirmation message)
    let recipients = [];
    let recipientDescription = '';
    
    if (emailTarget === 'proshop') {
      const course = courses?.find(c => c.name === emailCourse);
      if (!course?.proshop_email) {
        addToast("No proshop email found for this course.", "error");
        return;
      }
      recipients = [course.proshop_email];
      recipientDescription = `the proshop at ${course.proshop_email}`;
    } else if (emailTarget === 'players') {
      // Get emails of players on the tee sheet
      recipients = randomizedPlayers
        .filter(p => p.email)
        .map(p => p.email);
      recipientDescription = `${recipients.length} player(s) on the tee sheet`;
    } else if (emailTarget === 'select') {
      // Get emails of selected players
      recipients = selectedEmailPlayers
        .map(id => players.find(p => p.id === id))
        .filter(p => p?.email)
        .map(p => p.email);
      recipientDescription = `${recipients.length} selected player(s)`;
    }
    
    if (recipients.length === 0) {
      addToast("No valid recipients found.", "error");
      return;
    }

    // Confirm before sending
    if (!window.confirm(`Send tee sheet to ${recipientDescription}?`)) {
      return;
    }
    
    setSendingEmail(true);

    // Replace placeholders in custom message
    const resolveEmailPlaceholders = (text) => {
      if (!text) return text;
      
      // Get weather data from the tee time (single or first of multi)
      const weatherTeeTime = selectedTeeTime || Object.values(multiTeeTimeMap).sort((a, b) => (a.time || '').localeCompare(b.time || ''))[0];
      let weatherData = null;
      try {
        const wd = weatherTeeTime?.weather_data;
        if (wd) weatherData = typeof wd === 'string' ? JSON.parse(wd) : wd;
      } catch {}
      
      const placeholders = {
        '{course}': emailCourse,
        '{date}': utils.formatDate(emailDate),
        '{time}': emailTime,
        '{players}': String(randomizedPlayers.length),
        '{slots}': String(emailSlots),
        '{org_name}': settings?.organization_name || '',
        '{organizer_name}': settings?.organizer_name || '',
        '{organizer_phone}': settings?.organizer_phone || '',
        '{organizer_email}': settings?.organizer_email || '',
        '{weather}': (weatherTeeTime?.weather_display || '').replace(/\n/g, '<br>'),
        '{sunrise}': weatherData?.sunrise || '',
        '{sunset}': weatherData?.sunset || '',
        '{daylight}': weatherData?.daylight_hours || '',
        // Emoji shortcodes
        '{golf}': '⛳',
        '{trophy}': '🏆',
        '{beer}': '🍺',
        '{thumbsup}': '👍',
        '{fire}': '🔥',
        '{sun}': '☀️',
        '{rain}': '🌧️',
        '{cold}': '🥶',
        '{hot}': '🥵',
        '{clap}': '👏',
        '{flag}': '🚩',
        '{wave}': '👋',
      };
      
      let result = text;
      for (const [placeholder, value] of Object.entries(placeholders)) {
        result = result.split(placeholder).join(value);
      }
      return result;
    };
    
    const resolvedEmailMessage = resolveEmailPlaceholders(customEmailMessage.trim());

    // Build custom message block for HTML
    const customMessageHtml = resolvedEmailMessage ? `
        <div style="margin-top: 20px; padding: 16px; background: #f0fdf4; border-left: 4px solid #2d6a4f; border-radius: 4px;">
          <div style="font-weight: 600; color: #1b4332; margin-bottom: 8px;">📝 Message from Organizer:</div>
          <p style="margin: 0; color: #374151; white-space: pre-line;">${resolvedEmailMessage.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
        </div>
    ` : '';

    // Create the tee sheet HTML content for email
    const emailHtml = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="text-align: center; padding: 20px; background: #2d6a4f; color: white;">
          <h1 style="margin: 0; font-size: 24px;">Tee Time Organizer Tee Sheet</h1>
          <p style="margin: 5px 0 0 0; font-size: 14px;">TeeTimeOrganizer.net</p>
        </div>
        
        <div style="padding: 16px; background: #f9fafb; margin-top: 16px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 6px 0; font-weight: 600; color: #374151; width: 100px;">Course:</td>
              <td style="padding: 6px 0;">${emailCourse}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 6px 0; font-weight: 600; color: #374151;">Date:</td>
              <td style="padding: 6px 0;">${utils.formatDate(emailDate)}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 6px 0; font-weight: 600; color: #374151;">Tee Time:</td>
              <td style="padding: 6px 0;">${emailTime}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 6px 0; font-weight: 600; color: #374151;">Group:</td>
              <td style="padding: 6px 0;">${settings?.organization_name || 'No organization name set'}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 6px 0; font-weight: 600; color: #374151;">Organizer:</td>
              <td style="padding: 6px 0;">
                ${settings?.organizer_name || 'Not set'}${settings?.organizer_phone ? ', ' + settings.organizer_phone : ''}${settings?.organizer_email ? ', ' + settings.organizer_email : ''}
              </td>
            </tr>
            <tr>
              <td style="padding: 6px 0; font-weight: 600; color: #374151;">Players:</td>
              <td style="padding: 6px 0;">${randomizedPlayers.length} players of ${emailSlots} available playing slots</td>
            </tr>
          </table>
        </div>
        
        <div style="margin-top: 16px; padding: 16px;">
          <h2 style="font-size: 16px; font-weight: 700; color: #111827; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #e5e7eb;">${hasPairingGroups ? 'Player Pairings' : 'Player Lineup'}</h2>
          ${generateEmailHtml()}
        </div>
        
        ${customMessageHtml}
        
        <div style="margin-top: 16px; padding-top: 16px; text-align: center; color: #9ca3af; font-size: 11px; border-top: 1px solid #e5e7eb;">
          Generated on ${new Date().toLocaleString()} • TeeTimeOrganizer.net
        </div>
      </div>
    `;

    try {
      // FIXED: Include JWT auth header for multi-tenant support
      const emailHeaders = { 'Content-Type': 'application/json' };
      const emailToken = getAuthToken();
      if (emailToken) {
        emailHeaders['Authorization'] = `Bearer ${emailToken}`;
      }
      
      const response = await fetch(`${API_URL}/teetimes/${emailTeeTimeId}/email-tee-sheet`, {
        method: 'POST',
        headers: emailHeaders,
        credentials: 'include',
        body: JSON.stringify({
          to: recipients.join(','),
          html: emailHtml,
          customMessage: resolvedEmailMessage,
          courseName: emailCourse,
          date: utils.formatDate(emailDate),
          time: emailTime,
          players: randomizedPlayers.map(p => ({ name: p.name, isMember: p.is_member || p.isMember || false })),
          slots: emailSlots,
          playerCount: randomizedPlayers.length,
          organizationName: settings?.organization_name || '',
          organizerName: settings?.organizer_name || '',
          organizerPhone: settings?.organizer_phone || '',
          organizerEmail: settings?.organizer_email || ''
        })
      });
      
      if (response.ok) {
        addToast(`Tee sheet emailed to ${recipients.length} recipient(s)`, 'success');
        setShowEmailModal(false);
        setCustomEmailMessage('');
        setSelectedEmailPlayers([]);
      } else {
        const data = await response.json();
        addToast(data.error || 'Failed to send email', 'error');
      }
    } catch (error) {
      console.error('Error sending tee sheet email:', error);
      addToast('Failed to send email', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const openEmailModal = (target) => {
    setShowEmailMenu(false);
    setEmailTarget(target);
    setShowEmailModal(true);
    setSelectedEmailPlayers([]);
    setCustomEmailMessage('');
  };

  // Get list of tee times that have players (today forward only)
  const teeTimesWithPlayers = teeTimes.filter(t => 
    t.players && t.players.length > 0 && 
    (t.status === 'Published' || t.status === 'published' || t.status === 'Open' || t.status === 'open') &&
    utils.getDateTimestamp(t.date, t.time) >= utils.getTodayTimestamp()
  ).sort((a, b) => utils.getDateTimestamp(a.date, a.time) - utils.getDateTimestamp(b.date, b.time));

  // All published tee times including empty ones (for consolidation grouping)
  const allPublishedTeeTimes = teeTimes.filter(t => 
    (t.status === 'Published' || t.status === 'published' || t.status === 'Open' || t.status === 'open') &&
    utils.getDateTimestamp(t.date, t.time) >= utils.getTodayTimestamp()
  ).sort((a, b) => utils.getDateTimestamp(a.date, a.time) - utils.getDateTimestamp(b.date, b.time));

  // Group tee times by date + course for multi-select consolidation (includes empty tee times)
  const teeTimeGroups = useMemo(() => {
    const groups = {};
    allPublishedTeeTimes.forEach(t => {
      const key = `${t.date}__${t.course_id || t.course}`;
      if (!groups[key]) {
        groups[key] = { date: t.date, course: t.course, course_id: t.course_id, teeTimes: [] };
      }
      groups[key].teeTimes.push(t);
    });
    // Sort tee times within each group by time
    Object.values(groups).forEach(g => {
      g.teeTimes.sort((a, b) => {
        const aMin = parseInt(a.time?.split(':')[0] || 0) * 60 + parseInt(a.time?.split(':')[1] || 0);
        const bMin = parseInt(b.time?.split(':')[0] || 0) * 60 + parseInt(b.time?.split(':')[1] || 0);
        return aMin - bMin;
      });
    });
    return groups;
  }, [allPublishedTeeTimes]);

  // Restore last Pairings selection from localStorage on mount
  const [pairingsRestored, setPairingsRestored] = useState(false);
  useEffect(() => {
    // The Print tab sent us here for a round (Sept 17, 2026): open that round (it may
    // be a past one the list below doesn't show) and, when asked, print its tee sheet.
    const intent = appNav.readIntent('pairings-round');
    if (intent && !pairingsRestored) {
      if (!teeTimes || teeTimes.length === 0) return;
      appNav.clearIntent('pairings-round');
      setPairingsRestored(true);
      const tt = teeTimes.find(t => t.id === intent.teetimeId);
      if (!tt || !tt.players || tt.players.length === 0) {
        if (intent.win) { try { intent.win.close(); } catch (e) { /* closed */ } }
        addToast('That round has no players on it yet.', 'error');
        return;
      }
      setMultiSelectMode(false);
      setSelectedTeeTimeIds([]);
      handleSelectTeeTime(tt);
      try { localStorage.setItem('pairings_selection', JSON.stringify({ mode: 'single', id: tt.id })); } catch (e) { /* storage blocked */ }
      if (intent.printTeeSheet) setPendingTeeSheetPrint({ teetimeId: tt.id, win: intent.win || null, waited: false });
      return;
    }
    if (pairingsRestored || teeTimesWithPlayers.length === 0) return;
    // Phase 3c (May 2026): On mobile, skip the auto-restore. The mobile drill-down
    // pattern means restoring would jump the user past the tee time list straight
    // into the detail of their last selection — which makes them think their tap
    // on "Pairings" did the wrong thing. On mobile we always start fresh on the list.
    if (isMobile) { setPairingsRestored(true); return; }
    // Only restore if nothing is currently selected
    if (selectedTeeTimeId || multiSelectMode) { setPairingsRestored(true); return; }
    
    try {
      const saved = JSON.parse(localStorage.getItem('pairings_selection'));
      if (!saved) { setPairingsRestored(true); return; }
      
      if (saved.mode === 'single' && saved.id) {
        const tt = teeTimesWithPlayers.find(t => t.id === saved.id);
        if (tt) {
          handleSelectTeeTime(tt);
          setPairingsRestored(true);
          return;
        }
      }
      
      if (saved.mode === 'multi' && saved.ids?.length >= 2) {
        // Verify all saved IDs still exist in available tee times
        const allAvailable = allPublishedTeeTimes.length > 0 ? allPublishedTeeTimes : teeTimesWithPlayers;
        const validIds = saved.ids.filter(id => allAvailable.some(t => t.id === id));
        
        if (validIds.length >= 2) {
          // Only restore IDs from a single date/course group
          const firstTt = allAvailable.find(t => t.id === validIds[0]);
          if (firstTt) {
            const firstDate = typeof firstTt.date === 'string' ? firstTt.date.split('T')[0] : '';
            const firstCourse = firstTt.course_id || firstTt.course;
            const sameGroupIds = validIds.filter(id => {
              const tt = allAvailable.find(t => t.id === id);
              if (!tt) return false;
              const ttDate = typeof tt.date === 'string' ? tt.date.split('T')[0] : '';
              return ttDate === firstDate && (tt.course_id || tt.course) === firstCourse;
            });
            
            if (sameGroupIds.length >= 2) {
              setSelectedTeeTimeIds(sameGroupIds);
              setMultiRestorePending(true);
              setPairingsRestored(true);
              return;
            }
          }
        }
      }
    } catch (e) { /* ignore parse errors */ }
    setPairingsRestored(true);
  }, [teeTimesWithPlayers, pairingsRestored]);

  // Print the tee sheet the Print tab asked for, once the round is on screen (and the
  // org settings — the sheet's header — have loaded, or 1.5 s have passed).
  useEffect(() => {
    const p = pendingTeeSheetPrint;
    if (!p) return undefined;
    if (!selectedTeeTime || selectedTeeTime.id !== p.teetimeId || randomizedPlayers.length === 0) return undefined;
    if (!settings && !p.waited) {
      const timer = setTimeout(() => setPendingTeeSheetPrint(cur => (cur ? { ...cur, waited: true } : cur)), 1500);
      return () => clearTimeout(timer);
    }
    setPendingTeeSheetPrint(null);
    handlePrint(p.win);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTeeSheetPrint, selectedTeeTime, randomizedPlayers, settings]);

  // Auto-load multi-select after IDs are restored from localStorage
  const [multiRestorePending, setMultiRestorePending] = useState(false);
  useEffect(() => {
    if (!multiRestorePending) return;
    if (selectedTeeTimeIds.length >= 2 && !multiSelectMode) {
      handleLoadMultipleTeeTimes();
      setMultiRestorePending(false);
    }
  }, [multiRestorePending, selectedTeeTimeIds]);

  // Get fill status color for a tee time
  const getFillStatusColor = (t) => {
    const filled = t.players?.length || 0;
    const slots = t.slots || 4;
    if (filled >= slots) return '#22c55e'; // green - full
    if (filled >= slots - 1) return '#eab308'; // yellow - one short
    return '#ef4444'; // red - underfilled
  };

  // Handle checkbox toggle for multi-select tee times
  const handleTeeTimeCheckToggle = (teeTimeId, groupKey) => {
    setSelectedTeeTimeIds(prev => {
      // Find which group this tee time belongs to
      const thisGroupIds = teeTimeGroups[groupKey]?.teeTimes.map(t => t.id) || [];
      
      // If checking a tee time from a DIFFERENT group than what's already selected,
      // clear old selections first (only allow one date/course group at a time)
      const existingFromOtherGroup = prev.filter(id => !thisGroupIds.includes(id));
      if (existingFromOtherGroup.length > 0 && !prev.includes(teeTimeId)) {
        // Starting fresh with this group
        return [teeTimeId];
      }
      
      const next = prev.includes(teeTimeId) ? prev.filter(id => id !== teeTimeId) : [...prev, teeTimeId];
      return next;
    });
  };

  // Handle "Select All" for a date/course group
  const handleSelectAllGroup = (groupTeeTimes, isAllSelected) => {
    const groupIds = groupTeeTimes.map(t => t.id);
    if (isAllSelected) {
      // Uncheck all in this group
      setSelectedTeeTimeIds(prev => prev.filter(id => !groupIds.includes(id)));
    } else {
      // Select all in this group — REPLACE any selections from other groups
      setSelectedTeeTimeIds(groupIds);
    }
  };

  // Load multiple tee times into the tee sheet (multi-select mode)
  const handleLoadMultipleTeeTimes = () => {
    if (selectedTeeTimeIds.length < 2) return;
    
    // Get ALL selected tee times (including empty), sorted by time
    let selected = allPublishedTeeTimes
      .filter(t => selectedTeeTimeIds.includes(t.id))
      .sort((a, b) => {
        const aMin = parseInt(a.time?.split(':')[0] || 0) * 60 + parseInt(a.time?.split(':')[1] || 0);
        const bMin = parseInt(b.time?.split(':')[0] || 0) * 60 + parseInt(b.time?.split(':')[1] || 0);
        return aMin - bMin;
      });
    
    // SAFETY: If selections span multiple dates, only use the first date's tee times
    if (selected.length > 0) {
      const firstDate = typeof selected[0].date === 'string' ? selected[0].date.split('T')[0] : '';
      const firstCourse = selected[0].course_id || selected[0].course;
      const sameGroup = selected.filter(t => {
        const tDate = typeof t.date === 'string' ? t.date.split('T')[0] : '';
        return tDate === firstDate && (t.course_id || t.course) === firstCourse;
      });
      if (sameGroup.length < selected.length) {
        selected = sameGroup;
        // Update selectedTeeTimeIds to only include this group
        const sameGroupIds = sameGroup.map(t => t.id);
        setSelectedTeeTimeIds(sameGroupIds);
      }
    }
    
    // Separate tee times with players vs empty
    const withPlayers = selected.filter(t => t.players && t.players.length > 0);
    const empty = selected.filter(t => !t.players || t.players.length === 0);
    
    // All time slots in order (earliest first) — these are the "seats" we assign groups to
    const timeSlots = selected.map(t => ({ id: t.id, time: t.time, formattedTime: utils.formatTime(t.time) }));
    
    // Build tee time map (all selected)
    const ttMap = {};
    selected.forEach(t => { ttMap[t.id] = t; });
    setMultiTeeTimeMap(ttMap);
    // The note belongs to the DAY, not one row — every tee time in the group
    // carries the same text. Load the first non-empty one.
    setTeeTimeNote((selected.find(t => t && t.notes && String(t.notes).trim()) || {}).notes || '');
    
    const slotsPerGroup = utils.toInt(selected[0]?.slots) || 4;
    const origMap = {};
    const wlPlayers = [];

    // ── STEP 1: Build raw groups from tee times that have players ──
    const rawGroups = [];
    const seenPlayerIds = new Set(); // Deduplicate players across tee times
    
    withPlayers.forEach(tt => {
      const groupPlayers = [];
      if (tt.players) {
        tt.players.forEach(p => {
          const fullPlayer = typeof p === 'object' && p.id ? playerById.get(p.id) || p : emailToPlayer(p);
          // Skip duplicate players (same player booked on multiple tee times)
          if (fullPlayer.id && seenPlayerIds.has(fullPlayer.id)) return;
          if (fullPlayer.id) seenPlayerIds.add(fullPlayer.id);
          groupPlayers.push({
            ...fullPlayer,
            sortOrder: typeof p === 'object' ? p.sortOrder : 0,
          });
          if (fullPlayer.id) {
            origMap[fullPlayer.id] = tt.id;
          }
        });
      }
      if (groupPlayers.length > 0) {
        rawGroups.push({ players: groupPlayers, originalTeeTimeId: tt.id });
      }
      
      if (tt.waitlist) {
        tt.waitlist.forEach(p => {
          const fullPlayer = typeof p === 'object' && p.id ? playerById.get(p.id) || p : emailToPlayer(p);
          wlPlayers.push({ ...fullPlayer, waitlistTeeTimeId: tt.id, waitlistTeeTimeName: utils.formatTime(tt.time) });
        });
      }
    });
    
    // ── STEP 2: Smart consolidation — merge singleton groups ──
    let consolidatedGroups = [...rawGroups];
    let mergeOccurred = true;
    
    while (mergeOccurred) {
      mergeOccurred = false;
      consolidatedGroups.sort((a, b) => a.players.length - b.players.length);
      
      for (let i = 0; i < consolidatedGroups.length; i++) {
        const smallGroup = consolidatedGroups[i];
        if (smallGroup.players.length >= 2) break; // Only merge singletons
        
        // Find smallest group that can absorb without exceeding slots
        let bestIdx = -1;
        let bestSize = slotsPerGroup + 1;
        
        for (let j = 0; j < consolidatedGroups.length; j++) {
          if (j === i) continue;
          const target = consolidatedGroups[j];
          const combined = target.players.length + smallGroup.players.length;
          if (combined <= slotsPerGroup && target.players.length < bestSize) {
            bestIdx = j;
            bestSize = target.players.length;
          }
        }
        
        if (bestIdx >= 0) {
          consolidatedGroups[bestIdx].players.push(...smallGroup.players);
          consolidatedGroups.splice(i, 1);
          mergeOccurred = true;
          break;
        }
      }
    }
    
    // Sort: fuller groups get earlier time slots
    consolidatedGroups.sort((a, b) => b.players.length - a.players.length);
    
    // ── STEP 3: Assign groups to time slots (pack to front) ──
    const allPlayers = [];
    const initialPairings = {};
    const initialGroupTimes = {};
    const addedPlayerIds = new Set(); // Safety dedup
    
    consolidatedGroups.forEach((group, idx) => {
      const groupNum = idx + 1;
      const assignedSlot = timeSlots[idx];
      initialGroupTimes[groupNum] = assignedSlot ? assignedSlot.formattedTime : '';
      
      group.players.forEach(p => {
        // Skip if already added (safety dedup)
        if (p.id && addedPlayerIds.has(p.id)) return;
        if (p.id) addedPlayerIds.add(p.id);
        const playerObj = { ...p, pairingGroup: groupNum };
        allPlayers.push(playerObj);
        if (p.id) {
          initialPairings[p.id] = groupNum;
        }
      });
    });
    
    // ── STEP 4: Track empty time slots (informational only — no auto-release) ──
    const emptySlots = [];
    for (let i = consolidatedGroups.length; i < timeSlots.length; i++) {
      const slot = timeSlots[i];
      const tt = ttMap[slot.id];
      emptySlots.push({
        id: slot.id,
        time: slot.time,
        formattedTime: slot.formattedTime,
        course: tt?.course || '',
        date: tt?.date || '',
        originallyEmpty: empty.some(e => e.id === slot.id)
      });
    }
    
    // Sort by group then sort order
    allPlayers.sort((a, b) => {
      if (a.pairingGroup !== b.pairingGroup) return a.pairingGroup - b.pairingGroup;
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });
    
    setMultiSelectMode(true);
    setSelectedTeeTimeId(null);
    setRandomizedPlayers(allPlayers);
    setPairingGroups(initialPairings);
    setGroupTimes(initialGroupTimes);
    setPlayerOriginalTeeTimeMap(origMap);
    setWaitlistPlayers(wlPlayers);
    setReleasedTeeTimes(emptySlots);
    setShotgunMode(false);
    setStartingHoles({});
    setGroupLabels({});
    setPlayerTiers({});
    
    const packedChanged = emptySlots.length > 0 || consolidatedGroups.length < withPlayers.length;
    setPairingsChanged(packedChanged);
    
    const emptyMsg = emptySlots.length > 0 ? ` (${emptySlots.length} tee time${emptySlots.length !== 1 ? 's' : ''} now empty)` : '';
    addToast(`Loaded ${allPlayers.length} players into ${consolidatedGroups.length} groups${emptyMsg}`, 'success');
    try { localStorage.setItem('pairings_selection', JSON.stringify({ mode: 'multi', ids: selectedTeeTimeIds })); } catch(e) {}
  };

  // "As Booked" pairing option - shows players in their original tee time groups without consolidation
  const handleAsBooked = () => {
    if (!multiSelectMode || selectedTeeTimeIds.length < 2) return;
    
    // Get selected tee times sorted by time
    let selected = allPublishedTeeTimes
      .filter(t => selectedTeeTimeIds.includes(t.id))
      .sort((a, b) => {
        const aMin = parseInt(a.time?.split(':')[0] || 0) * 60 + parseInt(a.time?.split(':')[1] || 0);
        const bMin = parseInt(b.time?.split(':')[0] || 0) * 60 + parseInt(b.time?.split(':')[1] || 0);
        return aMin - bMin;
      });

    const timeSlots = selected.map(t => ({ id: t.id, time: t.time, formattedTime: utils.formatTime(t.time) }));
    const ttMap = {};
    selected.forEach(t => { ttMap[t.id] = t; });
    setMultiTeeTimeMap(ttMap);
    // The note belongs to the DAY, not one row — every tee time in the group
    // carries the same text. Load the first non-empty one.
    setTeeTimeNote((selected.find(t => t && t.notes && String(t.notes).trim()) || {}).notes || '');

    const origMap = {};
    const wlPlayers = [];
    const allPlayers = [];
    const initialPairings = {};
    const initialGroupTimes = {};
    const emptySlots = [];
    const addedPlayerIds = new Set();
    let groupNum = 0;

    // Preserve each tee time as its own group — NO consolidation
    selected.forEach((tt, idx) => {
      if (tt.players && tt.players.length > 0) {
        groupNum++;
        initialGroupTimes[groupNum] = timeSlots[idx]?.formattedTime || '';
        
        tt.players.forEach(p => {
          const fullPlayer = typeof p === 'object' && p.id ? playerById.get(p.id) || p : emailToPlayer(p);
          if (fullPlayer.id && addedPlayerIds.has(fullPlayer.id)) return;
          if (fullPlayer.id) addedPlayerIds.add(fullPlayer.id);
          
          const playerObj = {
            ...fullPlayer,
            sortOrder: typeof p === 'object' ? p.sortOrder : 0,
            pairingGroup: groupNum
          };
          allPlayers.push(playerObj);
          if (fullPlayer.id) {
            initialPairings[fullPlayer.id] = groupNum;
            origMap[fullPlayer.id] = tt.id;
          }
        });
      } else {
        // Empty tee time
        emptySlots.push({
          id: tt.id,
          time: tt.time,
          formattedTime: timeSlots[idx]?.formattedTime || '',
          course: tt.course || '',
          date: tt.date || '',
          originallyEmpty: true
        });
      }
      
      if (tt.waitlist) {
        tt.waitlist.forEach(p => {
          const fullPlayer = typeof p === 'object' && p.id ? playerById.get(p.id) || p : emailToPlayer(p);
          wlPlayers.push({ ...fullPlayer, waitlistTeeTimeId: tt.id, waitlistTeeTimeName: utils.formatTime(tt.time) });
        });
      }
    });

    // Sort by group then sort order within group
    allPlayers.sort((a, b) => {
      if (a.pairingGroup !== b.pairingGroup) return a.pairingGroup - b.pairingGroup;
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });

    setSelectedTeeTimeId(null);
    setRandomizedPlayers(allPlayers);
    setPairingGroups(initialPairings);
    setGroupTimes(initialGroupTimes);
    setPlayerOriginalTeeTimeMap(origMap);
    setWaitlistPlayers(wlPlayers);
    setReleasedTeeTimes(emptySlots);
    setShotgunMode(false);
    setStartingHoles({});
    setGroupLabels({});
    setPlayerTiers({});
    setPairingsChanged(false);
    
    addToast(`Reset to As Booked — ${allPlayers.length} players in ${groupNum} groups`, 'info');
  };

  // Override handleSelectTeeTime for single selection to clear multi-select state
  const handleSelectTeeTimeSingle = (teeTime) => {
    // Unsaved-changes guard (Aug 5, 2026). handleSelectTeeTime resets
    // pairingsChanged to false unconditionally, so switching tee times silently
    // discarded any unsaved pairing work — group moves, group times, tee
    // selections. Cancel keeps the organizer where they are so they can save.
    //
    // Deliberately guarding the *Single* wrapper rather than handleSelectTeeTime
    // itself: the localStorage restore path on mount also calls that function,
    // and prompting during a page restore would be wrong.
    if (pairingsChanged && !window.confirm('You have unsaved pairing changes.\n\nLeave this tee time without saving? Your changes will be lost.')) return;
    setMultiSelectMode(false);
    setSelectedTeeTimeIds([]);
    setPlayerOriginalTeeTimeMap({});
    setMultiTeeTimeMap({});
    setWaitlistPlayers([]);
    setReleasedTeeTimes([]);
    handleSelectTeeTime(teeTime);
    // Persist selection to localStorage
    try { localStorage.setItem('pairings_selection', JSON.stringify({ mode: 'single', id: teeTime.id })); } catch(e) {}
  };

  return (
    <div style={{
      display: "flex",
      gap: isMobile ? 0 : 24,
      height: "100%",
      flexDirection: isMobile ? "column" : "row"
    }}>
      {/* Left side: Tee Time Selection */}
      {/* Phase 3 (May 2026): On mobile, hide this pane when a tee time is being viewed
          so the right-side detail can use the full screen. Master-detail drill-down pattern. */}
      {(!isMobile || (!selectedTeeTime && !multiSelectMode)) && (
      <Card style={isMobile
        ? { width: '100%', flexShrink: 0, overflow: "auto" }
        : { width: 300, flexShrink: 0, overflow: "auto" }
      }>
        <div style={{ padding: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📋 Select Tee Time</h3>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
            {Object.values(teeTimeGroups).some(g => g.teeTimes.length >= 2) 
              ? 'Check multiple tee times to consolidate, or click one to view.'
              : 'Choose a tee time to view and randomize the player order.'}
          </p>
          
          {teeTimesWithPlayers.length === 0 ? (
            <div style={{ 
              padding: 24, 
              textAlign: "center", 
              color: "#9ca3af",
              background: "#f9fafb",
              borderRadius: 8,
              border: "2px dashed #e5e7eb"
            }}>
              No published tee times with players available
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {Object.entries(teeTimeGroups).map(([groupKey, group]) => {
                const isMultiGroup = group.teeTimes.length >= 2;
                const allGroupIds = group.teeTimes.map(t => t.id);
                const allSelected = allGroupIds.every(id => selectedTeeTimeIds.includes(id));
                const someSelected = allGroupIds.some(id => selectedTeeTimeIds.includes(id));
                
                // This group is expanded if: it has selections, OR it's the manually expanded group, OR it's a single tee time (no header to click)
                const isExpanded = !isMultiGroup || someSelected || expandedGroupKey === groupKey;
                // Another group has selections — this one should stay collapsed
                const otherGroupActive = selectedTeeTimeIds.length > 0 && !someSelected;
                const totalPlayers = group.teeTimes.reduce((sum, t) => sum + (t.players?.length || 0), 0);
                
                return (
                  <div key={groupKey}>
                    {/* Date/Course group header — always shown, clickable to expand/collapse */}
                    {isMultiGroup && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: isExpanded ? 6 : 0,
                        padding: '6px 10px',
                        background: isExpanded ? '#f0fdf4' : '#f9fafb',
                        borderRadius: 6,
                        border: isExpanded ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
                        cursor: 'pointer',
                        opacity: otherGroupActive ? 0.7 : 1,
                        transition: 'all 0.2s'
                      }}
                        onClick={() => {
                          if (otherGroupActive) {
                            // Switch to this group: clear other selections
                            setSelectedTeeTimeIds([]);
                            setExpandedGroupKey(groupKey);
                          } else if (isExpanded && !someSelected) {
                            // Collapse if no selections
                            setExpandedGroupKey(null);
                          } else {
                            setExpandedGroupKey(groupKey);
                          }
                        }}
                      >
                        {isExpanded && !otherGroupActive && (
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                            onChange={(e) => { e.stopPropagation(); handleSelectAllGroup(group.teeTimes, allSelected); }}
                            onClick={(e) => e.stopPropagation()}
                            style={{ accentColor: '#2d6a4f', width: 16, height: 16, cursor: 'pointer' }}
                          />
                        )}
                        <span style={{ fontSize: 14, color: isExpanded ? '#1b4332' : '#6b7280' }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        <div style={{ fontSize: 12, fontWeight: 700, color: isExpanded ? '#1b4332' : '#6b7280', flex: 1 }}>
                          {utils.formatDate(group.date)} — {group.course}
                          {!isExpanded && (
                            <span style={{ fontWeight: 400, marginLeft: 6 }}>
                              ({group.teeTimes.length} tee times, {totalPlayers} players)
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Individual tee times — only shown when expanded */}
                    {isExpanded && !otherGroupActive && (<>
                    <div style={{ display: "grid", gap: 6 }}>
                      {group.teeTimes.map(t => {
                        const isChecked = selectedTeeTimeIds.includes(t.id);
                        const isSingleSelected = selectedTeeTimeId === t.id && !multiSelectMode;
                        const filled = t.players?.length || 0;
                        const slots = t.slots || 4;
                        
                        return isMultiGroup ? (
                          // Multi-group: checkbox + tee time row
                          <label
                            key={t.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: "10px 12px",
                              border: isChecked ? "2px solid #2d6a4f" : "1px solid #e5e7eb",
                              borderRadius: 8,
                              background: isChecked ? "#f0fdf4" : "#fff",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              marginLeft: 8
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleTeeTimeCheckToggle(t.id, groupKey)}
                              style={{ accentColor: '#2d6a4f', width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontWeight: 600, fontSize: 14 }}>{utils.formatTime(t.time)}</span>
                                <span style={{
                                  width: 8, height: 8, borderRadius: '50%',
                                  background: getFillStatusColor(t),
                                  flexShrink: 0
                                }} title={`${filled}/${slots} filled`} />
                                <span style={{ fontSize: 12, color: '#6b7280' }}>{filled}/{slots}</span>
                              </div>
                            </div>
                          </label>
                        ) : (
                          // Single tee time: original button
                          <button
                            key={t.id}
                            onClick={() => handleSelectTeeTimeSingle(t)}
                            style={{
                              padding: "12px 16px",
                              border: isSingleSelected ? "2px solid #2d6a4f" : "1px solid #e5e7eb",
                              borderRadius: 8,
                              background: isSingleSelected ? "#f0fdf4" : "#fff",
                              cursor: "pointer",
                              textAlign: "left",
                              transition: "all 0.2s"
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.course}</div>
                            <div style={{ fontSize: 13, color: "#6b7280" }}>
                              {utils.formatDate(t.date)} at {utils.formatTime(t.time)}
                            </div>
                            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                              {filled} player{filled !== 1 ? 's' : ''}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Load button when 1+ tee times are checked in this group */}
                    {isMultiGroup && selectedTeeTimeIds.filter(id => allGroupIds.includes(id)).length >= 1 && (
                      <button
                        onClick={() => {
                          const checkedInGroup = selectedTeeTimeIds.filter(id => allGroupIds.includes(id));
                          if (checkedInGroup.length === 1) {
                            // Single tee time — load as single
                            const tt = allPublishedTeeTimes.find(t => t.id === checkedInGroup[0]);
                            if (tt) handleSelectTeeTimeSingle(tt);
                          } else {
                            handleLoadMultipleTeeTimes();
                          }
                        }}
                        style={{
                          width: '100%',
                          marginTop: 6,
                          padding: '8px 12px',
                          background: '#2d6a4f',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        {(() => {
                          const count = selectedTeeTimeIds.filter(id => allGroupIds.includes(id)).length;
                          return count === 1 ? '📋 Load Tee Time' : `📋 Load ${count} Tee Times`;
                        })()}
                      </button>
                    )}
                    </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
      )}

      {/* Right side: Tee Sheet Display */}
      {/* Phase 3: On mobile, the empty-state placeholder is redundant because the left
          pane is already showing the tee time list. Skip it on mobile. */}
      {!selectedTeeTime && !multiSelectMode ? (
        !isMobile && (
        <Card style={{ flex: 1 }}>
          <div style={{ 
            padding: 40, 
            textAlign: "center", 
            color: "#9ca3af" 
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Select a Tee Time</div>
            <div style={{ fontSize: 14 }}>Choose a tee time from the list to view and manage the player order</div>
          </div>
        </Card>
        )
      ) : (
        <>
          <Card style={{ flex: 1, overflow: "auto" }}>
            <div style={{ padding: isMobile ? 12 : 20 }}>
              {/* Phase 3 (May 2026): Back button on mobile to return to tee time list */}
              {/* Phase 3b: Same row hosts Help on the right. */}
              {isMobile && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginBottom: 8
                }}>
                  <button
                    onClick={() => {
                      setSelectedTeeTimeId(null);
                      setMultiSelectMode(false);
                      setSelectedTeeTimeIds([]);
                      try { localStorage.removeItem('pairings_selection'); } catch(e) {}
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#2d6a4f',
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: '8px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      minHeight: 44
                    }}
                  >
                    ← Back to Tee Times
                  </button>
                  {onShowHelp && (
                    <button
                      onClick={onShowHelp}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #d1d5db",
                        background: "#f9fafb",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#6b7280",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        minHeight: 36
                      }}
                      title="What is this page about?"
                    >
                      <span style={{ fontSize: 15 }}>ⓘ</span>
                      <span>Help</span>
                    </button>
                  )}
                </div>
              )}
              {/* Header */}
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-start",
                marginBottom: 20,
                paddingBottom: 16,
                borderBottom: "2px solid #e5e7eb",
                flexWrap: isMobile ? "wrap" : "nowrap",
                gap: isMobile ? 12 : 0
              }}>
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
                    {multiSelectMode 
                      ? (Object.values(multiTeeTimeMap)[0]?.course || 'Consolidation View')
                      : selectedTeeTime?.course}
                  </h2>
                  <div style={{ fontSize: 14, color: "#6b7280" }}>
                    {multiSelectMode 
                      ? `${utils.formatDate(Object.values(multiTeeTimeMap)[0]?.date)} — ${selectedTeeTimeIds.length} tee times loaded`
                      : `${utils.formatDate(selectedTeeTime?.date)} at ${utils.formatTime(selectedTeeTime?.time)}`}
                  </div>
                  <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 4 }}>
                    {multiSelectMode
                      ? `${randomizedPlayers.length} players across ${selectedTeeTimeIds.length} tee times`
                      : `${randomizedPlayers.length} of ${selectedTeeTime?.slots} slots filled`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {/* Email Tee Sheet Dropdown */}
                  <div style={{ position: 'relative' }}>
                    <Button 
                      onClick={() => setShowEmailMenu(!showEmailMenu)}
                      style={{ background: "#1b4332", color: "#fff", fontSize: 13 }}
                    >
                      📧 Email
                    </Button>
                    {showEmailMenu && (
                      <>
                        <div 
                          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                          onClick={() => setShowEmailMenu(false)}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          // Phase 4c (May 2026): On mobile the Email button can wrap to the
                          // left side of the screen due to flex-wrap. right:0 then makes the
                          // dropdown grow leftward off-screen. Anchor to left on mobile.
                          ...(isMobile ? { left: 0 } : { right: 0 }),
                          marginTop: 4,
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                          minWidth: 200
                        }}>
                          <button
                            onClick={() => openEmailModal('proshop')}
                            disabled={!courses?.find(c => c.name === selectedTeeTime?.course)?.proshop_email}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: '#fff',
                              textAlign: 'left',
                              cursor: courses?.find(c => c.name === selectedTeeTime?.course)?.proshop_email ? 'pointer' : 'not-allowed',
                              fontSize: 14,
                              color: courses?.find(c => c.name === selectedTeeTime?.course)?.proshop_email ? '#374151' : '#9ca3af',
                              borderRadius: '8px 8px 0 0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                            onMouseOver={(e) => e.target.style.background = '#f9fafb'}
                            onMouseOut={(e) => e.target.style.background = '#fff'}
                          >
                            🏌️ Proshop
                            {!courses?.find(c => c.name === selectedTeeTime?.course)?.proshop_email && (
                              <span style={{ fontSize: 11, color: '#9ca3af' }}>(no email)</span>
                            )}
                          </button>
                          <button
                            onClick={() => openEmailModal('players')}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: '#fff',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: 14,
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                            onMouseOver={(e) => e.target.style.background = '#f9fafb'}
                            onMouseOut={(e) => e.target.style.background = '#fff'}
                          >
                            👥 Players on Tee Sheet
                            <span style={{ fontSize: 11, color: '#6b7280' }}>({randomizedPlayers.filter(p => p.email).length})</span>
                          </button>
                          <button
                            onClick={() => openEmailModal('select')}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: '#fff',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: 14,
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                            onMouseOver={(e) => e.target.style.background = '#f9fafb'}
                            onMouseOut={(e) => e.target.style.background = '#fff'}
                          >
                            ✓ Select Players...
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <Button 
                    onClick={handlePrint}
                    style={{ background: "#2d6a4f", color: "#fff", fontSize: 13 }}
                    title="Print the tee sheet as it looks on this page"
                  >
                    🖨️ Tee sheet
                  </Button>
                  {!deskSession && shotgunMode && getActiveGroups().length > 0 && (
                    <div style={{ position: 'relative' }}>
                      <Button
                        onClick={() => setShowExportMenu(!showExportMenu)}
                        style={{ background: "#2d6a4f", color: "#fff", fontSize: 13 }}
                        title="Download the pairings as a spreadsheet file"
                      >
                        ⬇ Export ▾
                      </Button>
                      {showExportMenu && (
                        <>
                          <div
                            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                            onClick={() => setShowExportMenu(false)}
                          />
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            ...(isMobile ? { left: 0 } : { right: 0 }),
                            marginTop: 4,
                            background: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            minWidth: 230
                          }}>
                            {[
                              { label: 'Pairings (CSV)', onClick: handlePairingsCsvExport, radius: '8px 8px 0 0' },
                              { label: 'Pairings for Golf Genius (CSV)', onClick: handleGolfGeniusExport, radius: '0 0 8px 8px' }
                            ].map(item => (
                              <button
                                key={item.label}
                                onClick={item.onClick}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  border: 'none',
                                  background: '#fff',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: 14,
                                  color: '#374151',
                                  borderRadius: item.radius
                                }}
                                onMouseOver={(e) => e.target.style.background = '#f9fafb'}
                                onMouseOut={(e) => e.target.style.background = '#fff'}
                              >
                                {item.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Control Bar - Shotgun toggle + Show checkboxes + action buttons, always visible */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                flexWrap: 'wrap',
                padding: '10px 14px',
                marginBottom: 12,
                borderBottom: '1px solid #e5e7eb',
                background: '#f9fafb',
                borderRadius: 8
              }}>
                {/* Shotgun Toggle - always visible, pill-style */}
                {/* LAYER 1 + LAYER 3 — Shotgun mode is gated at Starter+ (May 14, 2026). */}
                {/* Free orgs see the toggle but clicking it fires the upgrade modal instead. */}
                <label
                  onClick={() => {
                    const next = !shotgunMode;
                    // Only gate the ON transition. Allow OFF unconditionally so a user
                    // who somehow ended up in Shotgun mode while locked (e.g. subscription
                    // downgrade) can always exit it without seeing the upgrade modal.
                    if (next === true && pairingsLocked) {
                      triggerUpgradeModal({ feature: 'pairings', requiredTier: 'starter', currentTier: orgTier });
                      return;
                    }
                    setShotgunMode(next);
                    setPairingsChanged(true);
                    if (!next) {
                      setStartingHoles({});
                      setGroupLabels({});
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    padding: '4px 12px',
                    borderRadius: 6,
                    border: shotgunMode ? '2px solid #2d6a4f' : '2px solid #d1d5db',
                    background: shotgunMode ? '#ecfdf5' : '#fff',
                    userSelect: 'none'
                  }}
                >
                  {/* Toggle switch */}
                  <div style={{
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    background: shotgunMode ? '#2d6a4f' : '#d1d5db',
                    position: 'relative',
                    transition: 'background 0.2s',
                    flexShrink: 0
                  }}>
                    <div style={{
                      width: 16,
                      height: 16,
                      borderRadius: 8,
                      background: '#fff',
                      position: 'absolute',
                      top: 2,
                      left: shotgunMode ? 18 : 2,
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: shotgunMode ? '#2d6a4f' : '#6b7280' }}>
                    🎯 Shotgun Start
                  </span>
                  {pairingsLocked && <LockBadge feature="pairings" requiredTier="starter" style={{ marginLeft: 4, fontSize: 10, padding: '1px 6px' }} />}
                </label>

                {/* Separator */}
                <div style={{ width: 1, height: 24, background: '#d1d5db' }} />

                {/* Show checkboxes */}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Show:</span>
                  {[
                    { label: 'Member', key: 'showMember' },
                    { label: 'Index', key: 'showIndex' },
                    { label: 'Course HCP', key: 'showCourseHcp' }
                  ].map(item => {
                    const checked = teeSheetShowOptions?.[item.key] !== false;
                    return (
                      <label
                        key={item.key}
                        onClick={() => setTeeSheetShowOptions(prev => ({ ...prev, [item.key]: !checked }))}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12, color: checked ? '#1f2937' : '#9ca3af', userSelect: 'none' }}
                      >
                        <div style={{
                          width: 16,
                          height: 16,
                          borderRadius: 3,
                          border: checked ? '2px solid #2d6a4f' : '2px solid #d1d5db',
                          background: checked ? '#2d6a4f' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {checked && <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>✓</span>}
                        </div>
                        {item.label}
                      </label>
                    );
                  })}
                </div>

                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {multiSelectMode && (
                    <Button 
                      variant="outline" 
                      onClick={handleAsBooked}
                      style={{ fontSize: 13, borderColor: '#2d6a4f', color: '#2d6a4f' }}
                      title="Reset to how players originally signed up"
                    >
                      📋 As Booked
                    </Button>
                  )}
                  {!hasRegisteredTeams && !multiSelectMode && selectedTeeTimeId && (
                    <Button
                      variant="outline"
                      onClick={() => setShowTeamTools(v => !v)}
                      data-testid="use-teams"
                      style={{ fontSize: 13, borderColor: showTeamTools ? '#2d6a4f' : undefined, color: showTeamTools ? '#2d6a4f' : undefined }}
                      title="Put golfers on teams for this round: a team box appears beside each name"
                    >
                      👥 {showTeamTools ? 'Teams on' : 'Use teams'}
                    </Button>
                  )}
                  {hasRegisteredTeams && (
                    <Button
                      variant="outline"
                      onClick={handlePairByTeam}
                      data-testid="pair-by-team"
                      style={{ fontSize: 13, borderColor: '#2d6a4f', color: '#2d6a4f', fontWeight: 700 }}
                      title="One group per registered team, in the order of your list"
                    >
                      👥 By team
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    onClick={handleRandomize}
                    style={{ fontSize: 13 }}
                  >
                    🎲 Randomize
                  </Button>
                  {/* Balanced Flights dropdown */}
                  {/* LAYER 1 + LAYER 3 — Balanced Flights is gated at Starter+ (May 14, 2026). */}
                  {/* Free orgs: clicking the dropdown button fires the upgrade modal instead */}
                  {/* of opening the menu. LockBadge sits next to label as the visible affordance. */}
                  <div style={{ position: 'relative' }}>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        if (pairingsLocked) {
                          triggerUpgradeModal({ feature: 'pairings', requiredTier: 'starter', currentTier: orgTier });
                          return;
                        }
                        setShowPairByHcpMenu(!showPairByHcpMenu);
                      }}
                      style={{ fontSize: 13 }}
                      title={pairingsLocked
                        ? "Your current plan doesn't include the Pairings Generator. Click to see upgrade options."
                        : "Auto-create balanced groups using handicap tiers"}
                    >
                      ⚖️ Balanced Flights ▾
                      {pairingsLocked && <LockBadge feature="pairings" requiredTier="starter" style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px' }} />}
                    </Button>
                    {showPairByHcpMenu && (
                      <>
                        {/* Click-outside overlay */}
                        <div 
                          onClick={() => setShowPairByHcpMenu(false)} 
                          style={{ position: 'fixed', inset: 0, zIndex: 998 }} 
                        />
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, marginTop: 4,
                          background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 999,
                          minWidth: 260, overflow: 'hidden'
                        }}>
                          <div
                            onClick={() => handlePairByHCP('foursomes')}
                            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            <span style={{ fontSize: 16 }}>👥</span>
                            <div>
                              <div style={{ fontWeight: 600, color: '#1b4332' }}>Balanced Foursomes</div>
                              <div style={{ fontSize: 11, color: '#6b7280' }}>4 players per group (A/B/C/D)</div>
                            </div>
                          </div>
                          <div
                            onClick={() => handlePairByHCP('threesomes')}
                            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            <span style={{ fontSize: 16 }}>👤</span>
                            <div>
                              <div style={{ fontWeight: 600, color: '#1b4332' }}>Balanced Threesomes</div>
                              <div style={{ fontSize: 11, color: '#6b7280' }}>3 players per group (A/B/C)</div>
                            </div>
                          </div>
                          <div
                            onClick={() => handlePairByHCP('twosomes')}
                            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            <span style={{ fontSize: 16 }}>🤝</span>
                            <div>
                              <div style={{ fontWeight: 600, color: '#1b4332' }}>Balanced Twosomes</div>
                              <div style={{ fontSize: 11, color: '#6b7280' }}>2-player teams, 2 teams per hole</div>
                            </div>
                          </div>
                          <div
                            onClick={() => handlePairByHCP('aaddbbcc')}
                            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            <span style={{ fontSize: 16 }}>🔀</span>
                            <div>
                              <div style={{ fontWeight: 600, color: '#1b4332' }}>AADD vs BBCC</div>
                              <div style={{ fontSize: 11, color: '#6b7280' }}>4 per group — pairs within tiers</div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={handleSortByCourseHandicap}
                    style={{ fontSize: 13 }}
                    title="Sort players by course handicap (lowest to highest)"
                  >
                    ⛳ HCP Sort
                  </Button>
                  {/* Scorecards, cart signs and team codes live on the Print tab (Sept 17, 2026 —
                      Dennis: one obvious place for everything printed). They print from the SAVED
                      pairings, so unsaved changes are called out before leaving.

                      EVENT ROUNDS ONLY (Sept 21, 2026). The Sept 17 decision — printed
                      scorecards and cart signs are event pieces, a standard round gets the
                      tee sheet — reached the Print tab (which renders those cards only when
                      an event exists) and the print route (EVENT_ONLY -> 409 NOT_AN_EVENT),
                      but never reached THIS button. So it sat on every round for every org,
                      including orgs with events switched off, where it could never be
                      anything but a dead end. Nothing leaked -- the two layers behind it
                      held -- it just sent people somewhere that had nothing for them.

                      Gate matches the backend's exactly: an event row for this tee time.
                      An org with events off has none, so the map is empty and this stays
                      hidden, which is also what happens while /events is still loading. */}
                  {selectedTeeTime && !multiSelectMode && eventByTeetime[selectedTeeTime.id] && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (pairingsChanged && !window.confirm('You have unsaved pairing changes.\n\nScorecards and cart signs print from the SAVED pairings. Leave this page without saving?')) return;
                        appNav.goTo('print', { kind: 'print-round', teetimeId: selectedTeeTime.id });
                      }}
                      style={{ fontSize: 13 }}
                      title={selectedTeeTime?.scoring_mode === 'team' ? 'Scorecards, cart signs and team codes — opens the Print tab on this round' : 'Scorecards and cart signs — opens the Print tab on this round'}
                    >
                      🗂️ Scorecards & signs ▸
                    </Button>
                  )}
                  {/* Always rendered, disabled when clean (Aug 5, 2026). Previously this
                      button only existed while pairingsChanged was true. Appearing WAS the
                      only signal that there was something to save — and on a narrow window
                      this toolbar wraps to a vertical stack, so the button could arrive
                      below the fold and read as "the feature is broken". Holding a fixed
                      position and changing state instead keeps the signal without the
                      button ever moving or vanishing. */}
                  {/* Sept 21, 2026: explicit () => handleSavePairings(true). Passing the
                      function directly would hand React's click EVENT in as `share` —
                      truthy, so it would happen to work, right up until someone changed
                      the default. */}
                  <Button
                    onClick={() => handleSavePairings(true)}
                    disabled={savingPairings || !pairingsChanged}
                    style={{
                      background: pairingsChanged ? "#2d6a4f" : "#e5e7eb",
                      color: pairingsChanged ? "#fff" : "#9ca3af",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: (savingPairings || !pairingsChanged) ? 'default' : 'pointer'
                    }}
                    title={pairingsChanged ? 'Save pairing changes' : 'No unsaved changes'}
                  >
                    {savingPairings ? '💾 Saving...' : pairingsChanged ? '💾 Save Pairings' : '✓ Saved'}
                  </Button>
                  {/* The opt-out. Save shares by default because that is what organizers
                      already expect; this is for the round where the pairings are the
                      surprise. */}
                  <Button
                    variant="outline"
                    onClick={() => handleSavePairings(false)}
                    disabled={savingPairings || !pairingsChanged}
                    title={pairingsAreShared
                      ? 'Save, and take these pairings back out of the player app'
                      : 'Save your work without showing it to players yet'}
                    style={{ fontSize: 13, color: pairingsChanged ? '#166534' : '#9ca3af',
                             borderColor: '#bbf7d0', background: '#f0fdf4',
                             cursor: (savingPairings || !pairingsChanged) ? 'default' : 'pointer' }}
                  >
                    Save without sharing
                  </Button>
                </div>
              </div>

              {/* THE NUDGE. It is easy to hold pairings back, show them on the wall at the
                  reception, enjoy the evening and never tell the field. This sits in the
                  toolbar you are already looking at when you save, and it does not go away
                  until you share. */}
              {hasSavedPairings && !pairingsAreShared && (
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
                              padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 12,
                              alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, color: '#92400e' }}>
                    <strong>Players cannot see these pairings.</strong>{' '}
                    They are not in the player app. If your reminders include a tee sheet, it shows
                    the lineup without groups.
                  </div>
                  <Button
                    onClick={() => setPairingsShared(true)}
                    disabled={sharingPairings}
                    style={{ background: '#2d6a4f', color: '#fff', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    {sharingPairings ? 'Sharing…' : '👁️ Share with players'}
                  </Button>
                </div>
              )}

              {/* Taking it back. Two-step rather than a browser confirm, matching how the
                  rest of this app asks. */}
              {hasSavedPairings && pairingsAreShared && (
                <div style={{ marginBottom: 12, fontSize: 12, color: '#6b7280', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>Players can see these pairings.</span>
                  {confirmUnshare ? (
                    <>
                      <span style={{ color: '#b45309' }}>Hide them? Players who already saw them will stop seeing them.</span>
                      <button type="button" onClick={() => setPairingsShared(false)} disabled={sharingPairings}
                              style={{ background: 'none', border: 'none', color: '#b91c1c', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0 }}>
                        Yes, hide them
                      </button>
                      <button type="button" onClick={() => setConfirmUnshare(false)}
                              style={{ background: 'none', border: 'none', color: '#374151', fontSize: 12, cursor: 'pointer', padding: 0 }}>
                        Keep sharing
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setConfirmUnshare(true)}
                            style={{ background: 'none', border: 'none', color: '#2d6a4f', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0 }}>
                      Stop sharing
                    </button>
                  )}
                </div>
              )}

              {/* Note to players (Sept 7, 2026). In multi-select this writes to EVERY
                  tee time in the day — a league day is many rows, and a note on one
                  would reach only its foursome. Shown in the player app under View
                  Details and carried by the same-day + 24-hour reminders. */}
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13, color: '#92400e' }}>📋 Note to players</strong>
                  <span style={{ fontSize: 11, color: '#92400e' }}>
                    {multiSelectMode && selectedTeeTimeIds.length > 1
                      ? `Applies to all ${selectedTeeTimeIds.length} tee times this day`
                      : 'Shown in View Details and on reminders'}
                  </span>
                </div>
                <textarea
                  value={teeTimeNote}
                  onChange={(e) => { setTeeTimeNote(e.target.value); setPairingsChanged(true); }}
                  maxLength={2000}
                  rows={2}
                  placeholder="e.g. CART PATH ONLY today. Range on mats. Cancel by 10:00 AM Monday or you will be charged the league rate."
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #fcd34d', borderRadius: 6,
                    padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', background: '#fff' }}
                />
                <div style={{ fontSize: 11, color: '#a16207', marginTop: 4 }}>
                  Saved with Save Pairings. Edit any time — the same-day reminder sends the latest version.
                </div>
              </div>

              {/* Registered teams, not yet in groups (Oct 10, 2026) */}
              {hasRegisteredTeams && getActiveGroups().length === 0 && (() => {
                const n = teamsOnScreen().length;
                return (
                  <div data-testid="teams-hint" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13.5, color: '#1e3a5f' }}>
                      👥 This round has <strong>{n}</strong> registered {n === 1 ? 'team' : 'teams'}. <strong>By team</strong> gives each one its own group{shotgunMode ? ' and starting hole' : ''}.
                    </span>
                    <button onClick={handlePairByTeam} style={{ padding: '5px 12px', border: '1px solid #2d6a4f', borderRadius: 6, background: '#2d6a4f', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                      👥 By team
                    </button>
                  </div>
                );
              })()}

              {/* Shotgun on, no starting holes yet (Oct 10, 2026 — handoff item 3): the
                  wizard only opened itself when Shotgun Start was switched ON, and its
                  ⚡ Edit Setup button lives in the banner below, which needs holes to
                  exist — so a tee time created as shotgun had no way into it. */}
              {shotgunMode && Object.keys(startingHoles).length === 0 && !showShotgunWizard && (
                <div data-testid="shotgun-setup-needed" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, color: '#78350f' }}>
                    🎯 <strong>Shotgun start is on</strong>, but no starting holes are set up yet.
                  </span>
                  <button onClick={openShotgunWizard} style={{ padding: '6px 14px', border: 'none', borderRadius: 6, background: '#2d6a4f', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    ⚡ Set up shotgun
                  </button>
                </div>
              )}

              {/* Shotgun info banner with Setup + Auto-Fill buttons */}
              {shotgunMode && Object.keys(startingHoles).length > 0 && (
                <div style={{ background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>🎯</span>
                      <strong style={{ fontSize: 13, color: '#1b4332' }}>Shotgun Structure: {Object.keys(startingHoles).length} groups</strong>
                    </div>
                    <button
                      onClick={openShotgunWizard}
                      style={{
                        padding: '5px 12px', border: '1px solid #2d6a4f', borderRadius: 6,
                        background: '#fff', color: '#2d6a4f', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                      }}
                    >
                      ⚡ Edit Setup
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {hasRegisteredTeams && (
                      <>
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Teams:</span>
                        <button onClick={handlePairByTeam} style={shotgunFillButtonStyle('team', 0)} data-testid="shotgun-by-team">
                          👥 By team
                        </button>
                        <div style={{ width: 1, height: 20, background: '#d1d5db' }} />
                      </>
                    )}
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Foursomes:</span>
                    <button onClick={() => shotgunAutoFill('balanced', 4)} style={shotgunFillButtonStyle('balanced', 4)}>
                      ⚖️ Balanced
                    </button>
                    <button onClick={() => shotgunAutoFill('randomize', 4)} style={shotgunFillButtonStyle('randomize', 4)}>
                      🎲 Random
                    </button>
                    <button onClick={() => shotgunAutoFill('hcp', 4)} style={shotgunFillButtonStyle('hcp', 4)}>
                      ⛳ HCP
                    </button>
                    <div style={{ width: 1, height: 20, background: '#d1d5db' }} />
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Twosomes:</span>
                    <button onClick={() => shotgunAutoFill('balanced', 2)} style={shotgunFillButtonStyle('balanced', 2)}>
                      ⚖️ Balanced
                    </button>
                    <button onClick={() => shotgunAutoFill('randomize', 2)} style={shotgunFillButtonStyle('randomize', 2)}>
                      🎲 Random
                    </button>
                  </div>
                </div>
              )}

              {/* Group Tee Times - NON-shotgun mode only */}

              {/* Shotgun Setup Wizard Modal */}
              {showShotgunWizard && (() => {
                const course = courses.find(c => c.name === (effectiveSelectedTeeTime || selectedTeeTime)?.course);
                const holeCount = course?.holes || 18;
                const playerCount = randomizedPlayers.length;
                const totalSlots = getWizardTotalSlots();
                const playersPerGroup = wizardGroupSize; // 4 / 3 / 2, chosen in the wizard (Sept 15, 2026)
                const capacity = totalSlots * playersPerGroup;
                const usedHoles = new Set(wizardHoleConfig.map(h => h.hole));
                const hasAvailableHoles = usedHoles.size < holeCount;

                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowShotgunWizard(false); }}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 0, width: '95%', maxWidth: 580, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
                      onClick={(e) => e.stopPropagation()}>
                      
                      {/* Header */}
                      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>⚡ Shotgun Setup</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                            {course?.name || 'Course'} • {holeCount} holes • {playerCount} players
                          </div>
                        </div>
                        <button onClick={() => setShowShotgunWizard(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', padding: '4px 8px' }}>✕</button>
                      </div>

                      {/* Group size — drives Fit to players and the capacity check (Sept 15, 2026) */}
                      <div style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Group size:</span>
                        {[[4, 'Foursomes'], [3, 'Threesomes'], [2, 'Twosomes']].map(([n, label]) => (
                          <button
                            key={n}
                            onClick={() => setWizardSize(n)}
                            style={{
                              padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                              border: `1px solid ${playersPerGroup === n ? '#2d6a4f' : '#d1d5db'}`,
                              background: playersPerGroup === n ? '#2d6a4f' : '#fff', color: playersPerGroup === n ? '#fff' : '#374151'
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>

                      {/* Presets */}
                      <div style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 10, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Presets:</span>
                        <button
                          onClick={() => initWizardPreset('standard')}
                          style={{
                            padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            border: '1px solid #2d6a4f', background: '#ecfdf5', color: '#2d6a4f'
                          }}
                        >
                          Standard ({holeCount})
                        </button>
                        <button
                          onClick={() => initWizardPreset('modified')}
                          style={{
                            padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            border: '1px solid #1d4ed8', background: '#eff6ff', color: '#1d4ed8'
                          }}
                        >
                          Modified (9)
                        </button>
                        <button
                          onClick={() => initWizardPreset('fit')}
                          title="Only as many starting groups as the players need at the group size above — hole 1 first, then 18, 17, 16 … (reverse shotgun), so hole 1 clears quickly and everyone finishes by the clubhouse"
                          style={{
                            padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            border: '1px solid #b45309', background: '#fffbeb', color: '#b45309'
                          }}
                        >
                          Fit to players ({fitGroupsNeeded(playersPerGroup)})
                        </button>
                      </div>

                      {/* Validation Bar */}
                      <div style={{
                        padding: '10px 20px',
                        borderBottom: '1px solid #e5e7eb',
                        background: playerCount <= capacity ? '#f0fdf4' : '#fef2f2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                          <span><strong>{totalSlots}</strong> groups × {playersPerGroup} = <strong>{capacity}</strong> max players</span>
                        </div>
                        <div style={{
                          fontSize: 12, fontWeight: 700,
                          color: playerCount <= capacity ? '#166534' : '#991b1b'
                        }}>
                          {playerCount <= capacity 
                            ? `✓ Fits ${playerCount} players` 
                            : `⚠ ${playerCount - capacity} players over capacity`}
                        </div>
                      </div>

                      {/* Hole Config Table - Scrollable */}
                      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
                        {/* Table Header */}
                        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 40px', gap: 8, marginBottom: 8, fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                          <div>Hole</div>
                          <div>Groups</div>
                          <div></div>
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                          Only the holes listed get a starting group. Press − on a hole with 1 group (or ✕) to drop it; + Add Hole puts one back.
                        </div>

                        {/* Hole Rows */}
                        {wizardHoleConfig.map((row) => (
                          <div key={row.hole} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 40px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                            <select
                              value={row.hole}
                              onChange={(e) => changeWizardHoleNumber(row.hole, parseInt(e.target.value))}
                              style={{ padding: '6px 4px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: '#fff', width: '100%' }}
                            >
                              {Array.from({ length: holeCount }, (_, i) => i + 1)
                                .filter(h => h === row.hole || !usedHoles.has(h))
                                .map(h => (
                                  <option key={h} value={h}>{holeText(h, pairingNines())}</option>
                                ))}
                            </select>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <button
                                onClick={() => row.groups <= 1 ? removeWizardHoleRow(row.hole) : updateWizardHoleGroups(row.hole, row.groups - 1)}
                                title={row.groups <= 1 ? 'Remove this hole' : 'One fewer group on this hole'}
                                style={{
                                  width: 32, height: 32, borderRadius: 6, border: '1px solid #d1d5db',
                                  background: '#fff', fontSize: 16, cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#111827'
                                }}
                              >−</button>
                              <div style={{
                                minWidth: 36, textAlign: 'center', fontWeight: 700, fontSize: 16,
                                color: row.groups > 1 ? '#b45309' : '#111827'
                              }}>
                                {row.groups}
                              </div>
                              <button
                                onClick={() => updateWizardHoleGroups(row.hole, row.groups + 1)}
                                disabled={row.groups >= 4}
                                style={{
                                  width: 32, height: 32, borderRadius: 6, border: '1px solid #d1d5db',
                                  background: row.groups >= 4 ? '#f3f4f6' : '#fff', fontSize: 16, cursor: row.groups >= 4 ? 'not-allowed' : 'pointer',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: row.groups >= 4 ? '#d1d5db' : '#111827'
                                }}
                              >+</button>
                              {row.groups > 1 && (
                                <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600, marginLeft: 4 }}>
                                  {Array.from({ length: row.groups }, (_, i) => String.fromCharCode(65 + i)).join(', ')}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => removeWizardHoleRow(row.hole)}
                              style={{
                                width: 28, height: 28, borderRadius: 6, border: '1px solid #fca5a5',
                                background: '#fef2f2', fontSize: 14, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626'
                              }}
                            >✕</button>
                          </div>
                        ))}

                        {/* Add Hole Button */}
                        {hasAvailableHoles && (
                          <button
                            onClick={addWizardHoleRow}
                            style={{
                              width: '100%', padding: '8px', border: '2px dashed #d1d5db', borderRadius: 8,
                              background: '#f9fafb', fontSize: 13, color: '#6b7280', cursor: 'pointer',
                              marginTop: 4, fontWeight: 600
                            }}
                          >
                            + Add Hole
                          </button>
                        )}
                      </div>

                      {/* Footer */}
                      <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                        <Button variant="outline" onClick={() => setShowShotgunWizard(false)}>Cancel</Button>
                        <Button
                          onClick={applyWizardConfig}
                          style={{ background: '#2d6a4f', color: '#fff', fontWeight: 700 }}
                        >
                          Create {totalSlots} Groups
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Group Tee Times - NON-shotgun mode only */}
              {!shotgunMode && getActiveGroups().length > 0 && (
                <div style={{
                  marginBottom: 16,
                  padding: 12,
                  background: '#f0fdf4',
                  borderRadius: 8,
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1b4332' }}>
                      ⏰ Group Tee Times
                    </div>
                    {getCourseInterval() && (
                      <button
                        onClick={autoFillGroupTimesFromInterval}
                        style={{
                          padding: '4px 10px',
                          border: '1px solid #2d6a4f',
                          borderRadius: 5,
                          background: '#fff',
                          color: '#2d6a4f',
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        ⏱️ Auto-fill ({getCourseInterval()} min intervals)
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {getActiveGroups().map(groupNum => {
                      const hcpTotal = getGroupHcpTotal(groupNum);
                      const playerCount = Object.values(pairingGroups).filter(g => g === groupNum).length;
                      
                      return (
                        <div key={groupNum} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 6,
                          padding: '6px 10px',
                          background: '#fff',
                          borderRadius: 6,
                          border: '1px solid #d1fae5'
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 600, minWidth: 55 }}>
                            Group {groupNum}
                          </span>
                          <span style={{ fontSize: 11, color: '#6b7280' }}>({playerCount})</span>
                          <input
                            type="text"
                            placeholder="e.g. 8:00 AM"
                            value={groupTimes[groupNum] || ''}
                            onChange={(e) => handleGroupTimeChange(groupNum, e.target.value)}
                            style={{
                              width: 90,
                              padding: '4px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: 4,
                              fontSize: 13
                            }}
                          />
                          {hcpTotal !== null && (
                            <span style={{
                              fontSize: 10,
                              padding: '2px 6px',
                              background: '#e0e7ff',
                              color: '#4338ca',
                              borderRadius: 4,
                              fontWeight: 600
                            }} title="Sum of Course HCP (not printed)">
                              Σ{hcpTotal}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Shotgun Group Structure - shows all groups including empty ones */}
              {shotgunMode && Object.keys(startingHoles).length > 0 && (() => {
                // Build hole → groups mapping
                const holeMap = {}; // { holeNum: [groupNum, ...] }
                Object.entries(startingHoles).forEach(([groupNum, hole]) => {
                  if (!holeMap[hole]) holeMap[hole] = [];
                  holeMap[hole].push(parseInt(groupNum));
                });
                // Structure order — by the first group on each hole (see getShotgunSortedPlayers).
                const sortedHoles = Object.keys(holeMap).map(Number).sort((a, b) => Math.min(...holeMap[a]) - Math.min(...holeMap[b]));
                
                // Count players per group
                const groupPlayerCount = {};
                Object.values(pairingGroups).forEach(g => {
                  groupPlayerCount[g] = (groupPlayerCount[g] || 0) + 1;
                });
                
                const totalAssigned = Object.keys(pairingGroups).length;
                const totalPlayers = randomizedPlayers.length;
                const unassignedCount = totalPlayers - totalAssigned;
                
                return (
                  <div style={{ marginBottom: 12, border: '1px solid #d1fae5', borderRadius: 8, overflow: 'hidden' }}>
                    {/* Structure header */}
                    <div style={{ padding: '8px 14px', background: '#f0fdf4', borderBottom: '1px solid #d1fae5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#1b4332' }}>
                        📋 Hole Assignments
                      </span>
                      <span style={{ fontSize: 11, color: totalAssigned === totalPlayers ? '#166534' : '#b45309' }}>
                        {totalAssigned}/{totalPlayers} players assigned
                        {unassignedCount > 0 && ` • ${unassignedCount} unassigned`}
                      </span>
                    </div>
                    {/* Hole rows */}
                    <div style={{ padding: '8px 14px', display: 'grid', gap: 6 }}>
                      {sortedHoles.map(hole => {
                        const groups = holeMap[hole].sort((a, b) => (groupLabels[a] || '').localeCompare(groupLabels[b] || ''));
                        return (
                          <div key={hole} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                            <div style={{
                              minWidth: 60, padding: '3px 8px', borderRadius: 6,
                              background: '#166534', color: '#fff', fontSize: 12, fontWeight: 700, textAlign: 'center'
                            }}>
                              {holeText(hole, pairingNines())}
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                              {groups.map(groupNum => {
                                const count = groupPlayerCount[groupNum] || 0;
                                const label = groupLabels[groupNum];
                                const isFull = count >= 4;
                                const isEmpty = count === 0;
                                const slotLabel = `${hole}${label || ''}`;
                                const slotTime = getSlotTime(groupNum);
                                return (
                                  <div key={groupNum} style={{
                                    padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                    border: `1px solid ${isEmpty ? '#fbbf24' : isFull ? '#86efac' : '#93c5fd'}`,
                                    background: isEmpty ? '#fffbeb' : isFull ? '#f0fdf4' : '#eff6ff',
                                    color: isEmpty ? '#92400e' : isFull ? '#166534' : '#1d4ed8',
                                    display: 'flex', alignItems: 'center', gap: 4
                                  }}>
                                    <span>{slotLabel}: {count}/4</span>
                                    {slotTime && (
                                      <span style={{ fontSize: 10, color: isEmpty ? '#92400e' : isFull ? '#166534' : '#1d4ed8', fontWeight: 600 }}>
                                        {slotTime}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {randomizedPlayers.length > 0 ? (() => {
                // Always sort players so groups display as contiguous blocks
                // In shotgun mode: sort by hole → label → course HCP
                // In regular mode: sort by group number → preserve order within group
                const displayPlayers = shotgunMode ? getShotgunSortedPlayers() : (() => {
                  const hasPairings = Object.keys(pairingGroups).length > 0;
                  if (!hasPairings) return randomizedPlayers;
                  return [...randomizedPlayers].sort((a, b) => {
                    const aGroup = pairingGroups[a.id];
                    const bGroup = pairingGroups[b.id];
                    // Unassigned players go to end
                    if (aGroup === undefined && bGroup === undefined) return 0;
                    if (aGroup === undefined) return 1;
                    if (bGroup === undefined) return -1;
                    // Sort by group number
                    return aGroup - bGroup;
                  });
                })();
                return (
                <div style={{ display: "grid", gap: 8 }}>
                  {displayPlayers.map((player, i) => {
                    const playerGroup = pairingGroups[player.id];
                    const isInGroup = playerGroup !== undefined && playerGroup !== null;
                    
                    // Check if this is the first player in a new group (for visual separator)
                    const prevPlayer = i > 0 ? displayPlayers[i - 1] : null;
                    const prevGroup = prevPlayer ? pairingGroups[prevPlayer.id] : null;
                    const showGroupSeparator = isInGroup && playerGroup !== prevGroup;
                    
                    return (
                      <React.Fragment key={player.id || i}>
                        {showGroupSeparator && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '7px 18px',
                            background: shotgunMode && startingHoles[playerGroup] ? '#ecfdf5' : (shotgunMode && !startingHoles[playerGroup] ? '#fef3c7' : '#ecfdf5'),
                            borderTop: i > 0 ? ('2px solid ' + (shotgunMode && !startingHoles[playerGroup] ? '#fbbf24' : '#86efac')) : 'none',
                            borderBottom: '1px solid ' + (shotgunMode && !startingHoles[playerGroup] ? '#fde68a' : '#d1fae5'),
                            flexWrap: 'wrap',
                            gap: 6,
                            marginTop: i > 0 ? 8 : 0
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {/* Hole badge or group label */}
                              {shotgunMode && startingHoles[playerGroup] ? (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  background: '#166534',
                                  color: '#fff',
                                  fontSize: 12,
                                  fontWeight: 700
                                }}>
                                  {startingHoles[playerGroup]}
                                </span>
                              ) : shotgunMode ? (
                                <span style={{ fontSize: 15 }}>⚠️</span>
                              ) : null}
                              
                              <span style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: shotgunMode && !startingHoles[playerGroup] ? '#92400e' : '#166534'
                              }}>
                                {shotgunMode && startingHoles[playerGroup]
                                  ? startHoleText(startingHoles[playerGroup], groupLabels[playerGroup], pairingNines()).toUpperCase()
                                  : shotgunMode
                                    ? `GROUP ${playerGroup} (unassigned)`
                                    : multiSelectMode && groupTimes[playerGroup]
                                      ? `${groupTimes[playerGroup]} — GROUP ${playerGroup}`
                                      : `GROUP ${playerGroup}`}
                              </span>
                              
                              <span style={{
                                fontSize: 11,
                                color: shotgunMode && !startingHoles[playerGroup] ? '#a16207' : '#16a34a'
                              }}>
                                ({Object.values(pairingGroups).filter(g => g === playerGroup).length} players)
                              </span>

                              {/* Registered team (Oct 10, 2026): shown when the whole group is one team */}
                              {(() => {
                                const ids = Object.keys(pairingGroups).filter(id => pairingGroups[id] === playerGroup);
                                const t0 = ids.length ? teamByPlayer[ids[0]] : null;
                                if (!t0 || !ids.every(id => teamByPlayer[id] && teamByPlayer[id].id === t0.id)) return null;
                                const onRound = (roundTeams.find(rt => rt.id === t0.id) || {}).count || ids.length;
                                const open = teamSizeOfRound && onRound < teamSizeOfRound ? teamSizeOfRound - onRound : 0;
                                return (
                                  <span data-testid="group-team" style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: open ? '#fffbeb' : '#f0fdf4', color: open ? '#92400e' : '#14532d', fontWeight: 700, border: `1px solid ${open ? '#fde68a' : '#bbf7d0'}`, marginLeft: 4 }}>
                                    👥 {t0.name}{open ? ` · ${onRound} of ${teamSizeOfRound} · ${open} open` : ''}
                                  </span>
                                );
                              })()}

                              {/* Best Ball blind draw (Sept 27, 2026) — organizer / desk screen only */}
                              {pairingBlinds.filter(b => Number(b.team_number) === Number(playerGroup)).map(b => (
                                <span key={`pb-${b.player_id}`} data-testid="pairings-blind" style={{
                                  fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#eff6ff', color: '#1e3a5f',
                                  fontWeight: 700, border: '1px solid #bfdbfe', marginLeft: 4
                                }} title={b.replaces_name ? `Blind draw, covering ${b.replaces_name}` : 'Blind draw'}>
                                  + blind: {b.player_name || 'drawn'}{b.band ? ` (${b.band})` : ''}
                                </span>
                              ))}

                              {/* HCP Total badge */}
                              {(() => {
                                const hcpTotal = getGroupHcpTotal(playerGroup);
                                if (hcpTotal !== null) {
                                  return (
                                    <span style={{
                                      fontSize: 11,
                                      padding: '2px 8px',
                                      borderRadius: 4,
                                      background: '#e0f2fe',
                                      color: '#1e40af',
                                      fontWeight: 700,
                                      border: '1px solid #bfdbfe',
                                      marginLeft: 4
                                    }} title="Sum of Course HCP (not printed)">
                                      HCP Total: {hcpTotal}
                                    </span>
                                  );
                                }
                                return null;
                              })()}

                              {!shotgunMode && groupTimes[playerGroup] && (
                                <span style={{ color: '#15803d', fontSize: 13 }}>
                                  — {groupTimes[playerGroup]}
                                </span>
                              )}
                              {shotgunMode && startingHoles[playerGroup] && (
                                <input
                                  type="text"
                                  placeholder="Optional Text"
                                  title="Free text for this group — printed after the hole on the tee sheet and email (e.g. a wave time or a note)"
                                  value={getSlotTime(playerGroup)}
                                  onChange={(e) => setSlotTime(playerGroup, e.target.value)}
                                  style={{
                                    width: 72, padding: '2px 5px', border: '1px solid #86efac',
                                    borderRadius: 4, fontSize: 11, background: '#fff', color: '#166634',
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </div>

                            {/* Right side: Hole & Letter dropdowns (shotgun mode only) */}
                            {shotgunMode && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {/* Letter dropdown when shared hole */}
                                {startingHoles[playerGroup] && getGroupsOnSameHole(playerGroup).length > 1 && groupLabels[playerGroup] && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Group:</span>
                                    <select
                                      value={groupLabels[playerGroup] || ''}
                                      onChange={(e) => handleGroupLabelChange(playerGroup, e.target.value)}
                                      style={{
                                        fontSize: 11,
                                        padding: '2px 2px 2px 6px',
                                        borderRadius: 5,
                                        fontWeight: 700,
                                        minWidth: 38,
                                        cursor: 'pointer',
                                        border: '2px solid ' + (groupLabels[playerGroup] === 'A' ? '#2d6a4f' : groupLabels[playerGroup] === 'B' ? '#7c3aed' : '#d97706'),
                                        background: groupLabels[playerGroup] === 'A' ? '#ecfdf5' : groupLabels[playerGroup] === 'B' ? '#f5f3ff' : '#fffbeb',
                                        color: groupLabels[playerGroup] === 'A' ? '#166534' : groupLabels[playerGroup] === 'B' ? '#6d28d9' : '#92400e'
                                      }}
                                    >
                                      {['A', 'B', 'C', 'D', 'E', 'F'].map(l => (
                                        <option key={l} value={l}>{l}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                                {/* Starting hole dropdown */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Hole:</span>
                                  <select
                                    value={startingHoles[playerGroup] || ''}
                                    onChange={(e) => handleStartingHoleChange(playerGroup, e.target.value)}
                                    style={{
                                      fontSize: 13,
                                      padding: '3px 2px 3px 6px',
                                      borderRadius: 5,
                                      border: '2px solid ' + (startingHoles[playerGroup] ? '#2d6a4f' : '#fbbf24'),
                                      background: startingHoles[playerGroup] ? '#ecfdf5' : '#fffbeb',
                                      fontWeight: 700,
                                      width: pairingNines() ? 'auto' : 66, // was 50 — two-digit holes ("18") were clipped behind the arrow (Dennis, Sept 14); named nines: "Oak Forest 1" (Oct 8, 2026)
                                      textAlign: 'center',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <option value="">—</option>
                                    {Array.from({ length: 18 }, (_, i) => (
                                      <option key={i + 1} value={i + 1}>{holeName(i + 1, pairingNines())}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div 
                          style={{ 
                            padding: 12, 
                            background: isInGroup ? "#f0fdf4" : "#fff", 
                            border: isInGroup ? "1px solid #bbf7d0" : "1px solid #e5e7eb", 
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 8
                          }}
                        >
                          {/* Up/Down Arrow Buttons for Manual Sorting */}
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <button
                              onClick={() => {
                                if (i === 0) return;
                                const neighborPlayer = displayPlayers[i - 1];
                                // When crossing group boundary, adopt neighbor's group
                                const myGroup = pairingGroups[player.id];
                                const theirGroup = pairingGroups[neighborPlayer.id];
                                let updatedGroups = pairingGroups;
                                if (myGroup !== undefined && theirGroup !== undefined && myGroup !== theirGroup) {
                                  updatedGroups = { ...pairingGroups, [player.id]: theirGroup };
                                  setPairingGroups(updatedGroups);
                                }
                                // Swap in display order then write back as new randomizedPlayers
                                const newDisplay = [...displayPlayers];
                                newDisplay[i] = neighborPlayer;
                                newDisplay[i - 1] = player;
                                setRandomizedPlayers(newDisplay);
                                setPairingsChanged(true);
                              }}
                              disabled={i === 0}
                              style={{
                                width: 24,
                                height: 20,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 4,
                                background: i === 0 ? "#f3f4f6" : "#fff",
                                cursor: i === 0 ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 10,
                                color: i === 0 ? "#9ca3af" : "#374151"
                              }}
                              title="Move up"
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => {
                                if (i === displayPlayers.length - 1) return;
                                const neighborPlayer = displayPlayers[i + 1];
                                // When crossing group boundary, adopt neighbor's group
                                const myGroup = pairingGroups[player.id];
                                const theirGroup = pairingGroups[neighborPlayer.id];
                                let updatedGroups = pairingGroups;
                                if (myGroup !== undefined && theirGroup !== undefined && myGroup !== theirGroup) {
                                  updatedGroups = { ...pairingGroups, [player.id]: theirGroup };
                                  setPairingGroups(updatedGroups);
                                }
                                // Swap in display order then write back as new randomizedPlayers
                                const newDisplay = [...displayPlayers];
                                newDisplay[i] = neighborPlayer;
                                newDisplay[i + 1] = player;
                                setRandomizedPlayers(newDisplay);
                                setPairingsChanged(true);
                              }}
                              disabled={i === displayPlayers.length - 1}
                              style={{
                                width: 24,
                                height: 20,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 4,
                                background: i === displayPlayers.length - 1 ? "#f3f4f6" : "#fff",
                                cursor: i === displayPlayers.length - 1 ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 10,
                                color: i === displayPlayers.length - 1 ? "#9ca3af" : "#374151"
                              }}
                              title="Move down"
                            >
                              ▼
                            </button>
                          </div>
                          {/* Position Number */}
                          <div style={{ 
                            width: 32, 
                            height: 32, 
                            background: isInGroup ? "#2d6a4f" : "#2d6a4f", 
                            color: "#fff", 
                            borderRadius: "50%", 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: 14
                          }}>
                            {i + 1}
                          </div>
                          {/* Player Name and Badge */}
                          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <div style={{ fontSize: 15, fontWeight: 500 }}>
                              {player.name || player}
                              {player.id && teamByPlayer[player.id] && teamByPlayer[player.id].captain && (
                                <span data-testid="pairings-captain" style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginLeft: 6 }}>(Captain)</span>
                              )}
                            </div>
                            {/* Team pill + captain star (Oct 10, 2026): this round's team, never the player's */}
                            {!multiSelectMode && selectedTeeTimeId && player.id && (hasRegisteredTeams || showTeamTools) && (() => {
                              const t = teamByPlayer[player.id];
                              const onPick = (e) => {
                                const v = e.target.value;
                                if (v === '__rename' && t) {
                                  const name = window.prompt(`New name for the team "${t.name}"`, t.name);
                                  if (name && name.trim() && name.trim() !== t.name) renameTeam(t.id, name.trim());
                                  return;
                                }
                                if (v === '__new') {
                                  const name = window.prompt(`New team for ${player.name || 'this golfer'}: its name`, '');
                                  if (name && name.trim()) assignTeam(player.id, { new_team: name.trim() });
                                } else if (v === '') {
                                  if (t) assignTeam(player.id, { no_team: true });
                                } else if (!t || v !== t.id) {
                                  assignTeam(player.id, { team_id: v });
                                }
                              };
                              return (
                                <>
                                  <select value={t ? t.id : ''} onChange={onPick} disabled={teamSaving} data-testid="team-pill"
                                    title="This golfer's team for this round"
                                    style={{ fontSize: 12, fontWeight: 700, padding: '2px 6px', borderRadius: 999, cursor: 'pointer', maxWidth: 220,
                                      border: `1px solid ${t ? '#bbf7d0' : '#d1d5db'}`, background: t ? '#f0fdf4' : '#fff', color: t ? '#14532d' : '#6b7280' }}>
                                    <option value="">{t ? 'No team' : '👥 Team…'}</option>
                                    {roundTeams.map(rt => (
                                      <option key={rt.id} value={rt.id}>👥 {rt.name}{teamSizeOfRound ? ` · ${rt.count}/${teamSizeOfRound}` : ''}</option>
                                    ))}
                                    <option value="__new">＋ New team…</option>
                                    {t && <option value="__rename">✎ Rename {t.name}…</option>}
                                  </select>
                                  {t && (
                                    <button type="button" onClick={() => !t.captain && assignTeam(player.id, { captain: true })} disabled={teamSaving}
                                      data-testid="team-captain" title={t.captain ? `Captain of ${t.name}` : `Make captain of ${t.name}`}
                                      style={{ background: 'none', border: 'none', padding: 0, fontSize: 16, lineHeight: 1, cursor: t.captain ? 'default' : 'pointer', color: t.captain ? '#d97706' : '#cbd5e1' }}>
                                      {t.captain ? '★' : '☆'}
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                            {/* Tier Badge from Balanced Flights */}
                            {playerTiers[player.id] && (() => {
                              const tier = playerTiers[player.id];
                              const tierColors = { A: '#dcfce7', B: '#dbeafe', C: '#fef9c3', D: '#fee2e2' };
                              const tierText = { A: '#15803d', B: '#1d4ed8', C: '#a16207', D: '#dc2626' };
                              const tierLabels = { A: 'Tier A (Top 25%)', B: 'Tier B (2nd 25%)', C: 'Tier C (3rd 25%)', D: 'Tier D (Bottom 25%)' };
                              return (
                                <span 
                                  title={tierLabels[tier]}
                                  style={{ 
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 18, height: 18, borderRadius: 3, fontSize: 10, fontWeight: 700,
                                    background: tierColors[tier], color: tierText[tier],
                                    border: `1px solid ${tierText[tier]}30`
                                  }}
                                >
                                  {tier}
                                </span>
                              );
                            })()}
                            {/* Time constraint warning in multi-select mode */}
                            {multiSelectMode && player.id && (() => {
                              const origTeeTimeId = playerOriginalTeeTimeMap[player.id];
                              const currentGroup = pairingGroups[player.id];
                              if (!origTeeTimeId || !currentGroup) return null;
                              
                              // Find original tee time's time
                              const origTT = multiTeeTimeMap[origTeeTimeId];
                              if (!origTT) return null;
                              
                              // Find current group's tee time (by position in sorted order)
                              const sortedTTIds = Object.values(multiTeeTimeMap)
                                .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                                .map(t => t.id);
                              const targetTTId = sortedTTIds[currentGroup - 1];
                              const targetTT = multiTeeTimeMap[targetTTId];
                              if (!targetTT || targetTTId === origTeeTimeId) return null;
                              
                              // Compare times
                              const origMin = parseInt(origTT.time?.split(':')[0] || 0) * 60 + parseInt(origTT.time?.split(':')[1] || 0);
                              const targetMin = parseInt(targetTT.time?.split(':')[0] || 0) * 60 + parseInt(targetTT.time?.split(':')[1] || 0);
                              
                              if (targetMin < origMin) {
                                return (
                                  <span style={{
                                    fontSize: 10, padding: '2px 6px', borderRadius: 4,
                                    background: '#fef3c7', color: '#92400e', fontWeight: 600,
                                    border: '1px solid #fde68a', whiteSpace: 'nowrap'
                                  }} title={`Originally booked for ${utils.formatTime(origTT.time)}, now moved to ${utils.formatTime(targetTT.time)}`}>
                                    ⚠ Booked {utils.formatTime(origTT.time)}
                                  </span>
                                );
                              }
                              return null;
                            })()}
                            {teeSheetShowOptions.showMember && (player.clubMember || player.club_member) && (
                              <Badge style={{ background: "#dcfce7", color: "#15803d", fontSize: 10 }}>⭐ Member</Badge>
                            )}
                            {teeSheetShowOptions.showIndex && settings?.teesheet_show_handicap && pairingIndex(player) !== null && (
                              <Badge style={{ background: "#e0e7ff", color: "#4338ca", fontSize: 10 }} title={player.event_handicap != null ? "Event handicap — set for this round on the Scoring tab" : undefined}>HCP: {pairingIndex(player)}{player.event_handicap != null ? ' (today)' : ''}</Badge>
                            )}
                            {/* Course Handicap Display */}
                            {teeSheetShowOptions.showCourseHcp && settings?.teesheet_show_handicap && (effectiveSelectedTeeTime || selectedTeeTime) && (() => {
                              const _tt = effectiveSelectedTeeTime || selectedTeeTime;
                              const courseHcp = getPlayerCourseHandicap(player, _tt?.course);
                              const playerTee = getPlayerTee(player, _tt?.course);
                              const courseTees = getTeesForCourse(_tt?.course);
                              
                              if (courseTees.length === 0) {
                                return pairingIndex(player) !== null ? (
                                  <Badge style={{ background: "#fef3c7", color: "#92400e", fontSize: 10 }}>
                                    Course HCP: —
                                  </Badge>
                                ) : null;
                              }
                              
                              if (courseHcp !== null) {
                                return (
                                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <Badge style={{ 
                                      background: playerTee?.tee_color || "#000", 
                                      color: ["#f3f4f6", "#fef3c7", "#ffffff", "#fff"].includes(playerTee?.tee_color) ? "#374151" : "#fff",
                                      fontSize: 10,
                                      border: ["#f3f4f6", "#fef3c7", "#ffffff", "#fff"].includes(playerTee?.tee_color) ? "1px solid #d1d5db" : "none"
                                    }}>
                                      Course: {courseHcp}
                                    </Badge>
                                    {courseTees.length > 1 && (
                                      <select
                                        value={playerTee?.id || ''}
                                        onChange={(e) => handlePlayerTeeChange(player.id, e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          padding: '2px 4px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: 4,
                                          fontSize: 10,
                                          background: '#fff',
                                          cursor: 'pointer',
                                          maxWidth: 70
                                        }}
                                        title={player.preferred_tee ? `Player's preferred tee: ${player.preferred_tee}` : "Select tee for course handicap"}
                                      >
                                        <option value="">--</option>
                                        {courseTees.map(t => (
                                          <option key={t.id} value={t.id}>{t.tee_name}</option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                );
                              }
                              
                              return null;
                            })()}
                          </div>
                          {/* Pairing Group Dropdown */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>{shotgunMode ? 'Slot:' : 'Group:'}</span>
                            <select
                              value={pairingGroups[player.id] || ''}
                              onChange={(e) => handlePairingGroupChange(player.id, e.target.value)}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: 4,
                                fontSize: 13,
                                background: '#fff',
                                cursor: 'pointer',
                                minWidth: 60
                              }}
                            >
                              <option value="">--</option>
                              {shotgunMode ? (
                                Object.entries(startingHoles)
                                  .map(([gn, hole]) => ({ gn, hole, label: groupLabels[parseInt(gn)] || '' }))
                                  .sort((a, b) => a.hole !== b.hole ? a.hole - b.hole : a.label.localeCompare(b.label))
                                  .map(({ gn }) => {
                                    const sl = getSlotLabel(parseInt(gn));
                                    return <option key={gn} value={gn}>{sl}</option>;
                                  })
                              ) : (
                                Array.from({ length: 36 }, (_, i) => i + 1).map(num => (
                                  <option key={num} value={num}>{num}</option>
                                ))
                              )}
                            </select>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
                ); })() : (
                <div style={{ 
                  padding: 40, 
                  textAlign: "center", 
                  color: "#9ca3af", 
                  background: "#f9fafb", 
                  borderRadius: 8,
                  border: "2px dashed #e5e7eb"
                }}>
                  No players assigned to this tee time
                </div>
              )}

              {/* Empty Tee Times Section */}
              {multiSelectMode && emptyTeeTimes.length > 0 && (
                <div style={{
                  marginTop: 20,
                  padding: 16,
                  background: '#fef3c7',
                  borderRadius: 8,
                  border: '1px solid #fde68a'
                }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#92400e', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>⚠️</span>
                    Empty Tee Times ({emptyTeeTimes.length})
                  </div>
                  <p style={{ fontSize: 12, color: '#92400e', marginBottom: 10, lineHeight: 1.5 }}>
                    These tee times have no players after consolidation. They will remain as empty tee times — delete them from the Tee Times tab if you need to release them back to the course.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {emptyTeeTimes.map(tt => (
                      <div key={tt.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: '#fff',
                        borderRadius: 6,
                        border: '1px solid #fde68a'
                      }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#78350f' }}>
                          {tt.formattedTime}
                        </span>
                        <span style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 10,
                          background: '#fef3c7',
                          color: '#92400e',
                          fontWeight: 600
                        }}>
                          Empty — no players
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </>
      )}

      {/* Email Tee Sheet Modal */}
      {showEmailModal && (selectedTeeTime || multiSelectMode) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 24
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            maxWidth: 550,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                📧 Email Tee Sheet - {emailTarget === 'proshop' ? 'Proshop' : emailTarget === 'players' ? 'Players on Tee Sheet' : 'Select Players'}
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                {selectedTeeTime?.course || Object.values(multiTeeTimeMap)[0]?.course || ''} • {utils.formatDate(selectedTeeTime?.date || Object.values(multiTeeTimeMap)[0]?.date)} at {multiSelectMode 
                  ? Object.values(multiTeeTimeMap).sort((a, b) => (a.time || '').localeCompare(b.time || '')).map(t => utils.formatTime(t.time)).join(', ')
                  : utils.formatTime(selectedTeeTime?.time)}
              </div>
            </div>
            
            <div style={{ padding: 24 }}>
              {/* Recipients Section */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#374151' }}>Recipients:</div>
                
                {emailTarget === 'proshop' && (
                  <div style={{
                    padding: '12px 16px',
                    background: '#f3f4f6',
                    borderRadius: 8,
                    color: '#1b4332',
                    fontWeight: 500
                  }}>
                    🏌️ {courses?.find(c => c.name === (selectedTeeTime?.course || Object.values(multiTeeTimeMap)[0]?.course))?.proshop_email}
                  </div>
                )}

                {/* Auto-Email Tee Sheet to Proshop — per-tee-time status guard (Aug 2026) */}
                {emailTarget === 'proshop' && selectedTeeTime && (() => {
                  const autoOn = settings?.auto_proshop_email_enabled === true;
                  const proshopEmail = courses?.find(c => c.name === selectedTeeTime?.course)?.proshop_email;
                  const sentAt = selectedTeeTime?.proshop_auto_sent_at;
                  const failedAt = selectedTeeTime?.proshop_auto_send_failed_at;
                  const hoursBefore = parseInt(settings?.auto_proshop_email_hours_before, 10) || 3;

                  const fmtDateTime = (v) => {
                    try {
                      return new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                    } catch { return ''; }
                  };

                  // Approximate scheduled clock time (tee time minus lead hours).
                  let scheduledStr = '';
                  try {
                    if (selectedTeeTime?.date && selectedTeeTime?.time) {
                      const d = new Date(`${String(selectedTeeTime.date).substring(0, 10)}T${String(selectedTeeTime.time).substring(0, 8)}`);
                      d.setHours(d.getHours() - hoursBefore);
                      scheduledStr = d.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
                    }
                  } catch {}

                  let box;
                  if (!proshopEmail) {
                    box = { bg: '#fffbeb', border: '#fcd34d', color: '#92400e', text: <><strong>No Proshop Email set</strong> for this course — automatic send skips it. Add one in Settings → Courses.</> };
                  } else if (!autoOn) {
                    box = { bg: '#f3f4f6', border: '#e5e7eb', color: '#6b7280', text: <>Automatic proshop send is <strong>off</strong> for your organization.</> };
                  } else if (sentAt) {
                    box = { bg: '#fef2f2', border: '#fca5a5', color: '#991b1b', text: <><strong>⚠️ Already auto-sent</strong> to the proshop on {fmtDateTime(sentAt)}. Sending now will deliver a <strong>duplicate</strong>.</> };
                  } else if (failedAt) {
                    box = { bg: '#fffbeb', border: '#fcd34d', color: '#92400e', text: <><strong>⚠️ Automatic send failed</strong> on {fmtDateTime(failedAt)} and won't retry — sending now delivers it manually.</> };
                  } else {
                    box = { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', text: <>🕒 <strong>Auto-send is on.</strong> This tee sheet will send to the proshop automatically about {hoursBefore} hour{hoursBefore === 1 ? '' : 's'} before tee time{scheduledStr ? ` (around ${scheduledStr})` : ''} — no need to send manually unless you want to.</> };
                  }

                  return (
                    <div style={{ marginTop: 10, padding: '10px 12px', background: box.bg, border: `1px solid ${box.border}`, borderRadius: 8, fontSize: 13, color: box.color }}>
                      {box.text}
                    </div>
                  );
                })()}

                {emailTarget === 'players' && (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: '#f3f4f6', 
                    borderRadius: 8,
                    maxHeight: 150,
                    overflowY: 'auto'
                  }}>
                    {randomizedPlayers.filter(p => p.email).map((p, i) => (
                      <div key={i} style={{ padding: '4px 0', fontSize: 14, color: '#374151' }}>
                        👤 {p.name} ({p.email})
                      </div>
                    ))}
                  </div>
                )}
                
                {emailTarget === 'select' && (
                  <div style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: 8,
                    maxHeight: 200,
                    overflowY: 'auto'
                  }}>
                    {players
                      .filter(p => p.active !== false && p.email)
                      .sort((a, b) => {
                        // Sort by last name (last word in name), then first name
                        const aNames = (a.name || '').trim().split(' ');
                        const bNames = (b.name || '').trim().split(' ');
                        const aLast = aNames[aNames.length - 1] || '';
                        const bLast = bNames[bNames.length - 1] || '';
                        const lastCompare = aLast.localeCompare(bLast);
                        if (lastCompare !== 0) return lastCompare;
                        return (aNames[0] || '').localeCompare(bNames[0] || '');
                      })
                      .map(p => (
                      <label 
                        key={p.id} 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          background: selectedEmailPlayers.includes(p.id) ? '#f0fdf4' : '#fff'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEmailPlayers.includes(p.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmailPlayers([...selectedEmailPlayers, p.id]);
                            } else {
                              setSelectedEmailPlayers(selectedEmailPlayers.filter(id => id !== p.id));
                            }
                          }}
                          style={{ marginRight: 10 }}
                        />
                        <span style={{ fontSize: 14 }}>{p.name}</span>
                        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>({p.email})</span>
                      </label>
                    ))}
                  </div>
                )}
                
                {emailTarget === 'select' && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                    {selectedEmailPlayers.length} player(s) selected
                    {selectedEmailPlayers.length > 0 && (
                      <button
                        onClick={() => setSelectedEmailPlayers([])}
                        style={{
                          marginLeft: 12,
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: 13
                        }}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Custom Message Section */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                  Custom Message <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span>
                </div>
                <textarea
                  value={customEmailMessage}
                  onChange={(e) => setCustomEmailMessage(e.target.value)}
                  placeholder="Add a personal message that will appear below the player lineup..."
                  style={{
                    width: '100%',
                    minHeight: 100,
                    padding: 12,
                    border: '1px solid #d1d5db',
                    borderRadius: 8,
                    fontSize: 14,
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  maxLength={500}
                />
                <div style={{ 
                  textAlign: 'right', 
                  fontSize: 12, 
                  color: customEmailMessage.length > 450 ? '#dc2626' : '#9ca3af',
                  marginTop: 4
                }}>
                  {customEmailMessage.length}/500 characters
                </div>
                <details style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                  <summary style={{ cursor: 'pointer', color: '#2d6a4f', fontWeight: 500 }}>Available placeholders</summary>
                  <div style={{ marginTop: 6, padding: '8px 12px', background: '#f9fafb', borderRadius: 6, lineHeight: 1.8 }}>
                    <div><code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{course}'}</code> <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{date}'}</code> <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{time}'}</code> <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{players}'}</code> <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{slots}'}</code></div>
                    <div><code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{org_name}'}</code> <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{organizer_name}'}</code> <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{organizer_phone}'}</code></div>
                    <div><code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{weather}'}</code> <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{sunrise}'}</code> <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{sunset}'}</code> <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{daylight}'}</code></div>
                    <div><code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{golf}'}</code>⛳ <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{trophy}'}</code>🏆 <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{beer}'}</code>🍺 <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{sun}'}</code>☀️ <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{fire}'}</code>🔥 <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{thumbsup}'}</code>👍 <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{rain}'}</code>🌧️ <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{wave}'}</code>👋</div>
                  </div>
                </details>
              </div>
              
              {/* Preview Note */}
              <div style={{
                padding: '12px 16px',
                background: '#fef3c7',
                borderRadius: 8,
                fontSize: 13,
                color: '#92400e'
              }}>
                💡 The email will include the full tee sheet with course details, player lineup{hasPairingGroups ? ' (grouped)' : ''}, and your custom message (if any).
              </div>
            </div>
            
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              background: '#f9fafb'
            }}>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEmailModal(false);
                  setCustomEmailMessage('');
                  setSelectedEmailPlayers([]);
                }}
                disabled={sendingEmail}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleEmail}
                disabled={
                  sendingEmail ||
                  (emailTarget === 'select' && selectedEmailPlayers.length === 0) ||
                  (emailTarget === 'players' && randomizedPlayers.filter(p => p.email).length === 0)
                }
                style={{ background: '#2d6a4f', color: '#fff' }}
              >
                {sendingEmail ? '📤 Sending...' : '📧 Send Email'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



// ============================================================================
// MODAL COMPONENT
// Simple modal for previews and confirmations
// ============================================================================
const Modal = ({ title, onClose, children }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        maxWidth: 600,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{title}</div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 24,
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: 24 }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PLAYER MESSAGE FORM COMPONENT
// Allows organizers to send custom messages to players with template placeholders
// ============================================================================

const PlayerMessageForm = () => {
  const { addToast } = useToast();
  const { data: players } = useApiData('/players');
  const { data: groups } = useApiData('/groups');
  const { data: teeTimes } = useApiData('/teetimes');
  const { tier: orgTier, loading: tierLoading } = useOrgTier();
  // Default to LOCKED while tier is loading — better to briefly lock a paying tier
  // (then unlock) than to briefly show SMS as available to a Free tier user.
  const smsLocked = tierLoading || !orgTier || !canUseFeature(orgTier, 'sms');
  const [recipientType, setRecipientType] = useState('all');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [smsMessage, setSmsMessage] = useState(''); // SMS message (320 char / 2 segment limit)
  const [selectedTeeTime, setSelectedTeeTime] = useState(null);
  const [includeWeather, setIncludeWeather] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedTeeTimePlayers, setSelectedTeeTimePlayers] = useState([]); // Players from selected tee time
  const [teeTimeFilter, setTeeTimeFilter] = useState('registered'); // 'registered' | 'not_registered'
  const messageTextareaRef = useRef(null);

  // Filter active players (excluding guests) and sort by last name
  const activePlayers = players
    .filter(p => p.active !== false && p.role?.toLowerCase() !== 'guest')
    .sort((a, b) => {
      const getLastName = (name) => {
        const parts = name.trim().split(/\s+/);
        return parts[parts.length - 1].toLowerCase();
      };
      const lastNameA = getLastName(a.name);
      const lastNameB = getLastName(b.name);
      return lastNameA.localeCompare(lastNameB);
    });
  const upcomingTeeTimes = teeTimes
    .filter(tt => {
      // Compare dates as strings to avoid timezone issues
      // tt.date is typically "YYYY-MM-DD" format
      const ttDateStr = tt.date?.split('T')[0]; // Handle both "2024-12-31" and "2024-12-31T00:00:00"
      const todayStr = new Date().toLocaleDateString('en-CA'); // Returns "YYYY-MM-DD" format
      // Case-insensitive status check - include today and future
      return ttDateStr >= todayStr && tt.status?.toLowerCase() === 'published';
    })
    .sort((a, b) => {
      const dateA = utils.getDateTimestamp(a.date, a.time);
      const dateB = utils.getDateTimestamp(b.date, b.time);
      return dateA - dateB;
    });

  // Check if weather is available for selected tee time (15-day window)
  const isWeatherAvailable = () => {
    if (!selectedTeeTime) return false;
    
    const teeTimeDate = new Date(selectedTeeTime?.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntil = Math.ceil((teeTimeDate - today) / (1000 * 60 * 60 * 24));
    
    return daysUntil >= 0 && daysUntil <= 15 && selectedTeeTime.weather_display;
  };

  // Get weather block text
  const getWeatherBlock = () => {
    if (!includeWeather || !selectedTeeTime?.weather_display) return '';
    
    return `\n\n-------------------------------------------\nWeather Forecast for ${utils.formatDate(selectedTeeTime?.date)}:\n${selectedTeeTime.weather_display}\n-------------------------------------------`;
  };

  // Get final message with weather if included
  const getFinalMessage = () => {
    return message + getWeatherBlock();
  };

  // Insert placeholder at cursor position
  const insertPlaceholder = (placeholder) => {
    const textarea = messageTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = message;
    const before = text.substring(0, start);
    const after = text.substring(end);
    
    const newText = before + placeholder + after;
    setMessage(newText);
    
    // Set cursor position after inserted placeholder
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + placeholder.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Handle tee time selection
  const handleTeeTimeSelect = (teeTimeId) => {
    if (!teeTimeId) {
      setSelectedTeeTime(null);
      setIncludeWeather(false);
      setSelectedTeeTimePlayers([]);
      setTeeTimeFilter('registered');
      return;
    }
    
    const teeTime = upcomingTeeTimes.find(tt => tt.id === teeTimeId);
    setSelectedTeeTime(teeTime);
    setTeeTimeFilter('registered'); // Reset filter to registered on new tee time selection
    
    // Auto-uncheck weather if not available
    if (teeTime && !isWeatherAvailable()) {
      setIncludeWeather(false);
    }
    
    // Get all players from the tee time (roster + waitlist) and select them all by default
    if (teeTime) {
      const rosterPlayers = Array.isArray(teeTime.players) ? teeTime.players : [];
      const waitlistPlayers = Array.isArray(teeTime.waitlist) ? teeTime.waitlist : [];
      
      // Combine and get unique player IDs
      const allPlayerIds = [...rosterPlayers, ...waitlistPlayers]
        .map(p => typeof p === 'string' ? p : p?.id)
        .filter(id => id); // Remove nulls
      
      setSelectedTeeTimePlayers(allPlayerIds);
    } else {
      setSelectedTeeTimePlayers([]);
    }
  };

  // Get players from selected tee time with full details
  const getTeeTimePlayers = () => {
    if (!selectedTeeTime) return { roster: [], waitlist: [] };
    
    const rosterIds = (Array.isArray(selectedTeeTime?.players) ? selectedTeeTime?.players : [])
      .map(p => typeof p === 'string' ? p : p?.id)
      .filter(id => id);
    
    const waitlistIds = (Array.isArray(selectedTeeTime.waitlist) ? selectedTeeTime.waitlist : [])
      .map(p => typeof p === 'string' ? p : p?.id)
      .filter(id => id);
    
    // Look up full player details from players array
    const roster = rosterIds.map(id => {
      const player = players.find(p => p.id === id);
      return player || { id, name: 'Unknown Player', email: '' };
    });
    
    const waitlist = waitlistIds.map(id => {
      const player = players.find(p => p.id === id);
      return player || { id, name: 'Unknown Player', email: '' };
    });
    
    return { roster, waitlist };
  };

  // Toggle a tee time player selection
  const toggleTeeTimePlayer = (playerId) => {
    setSelectedTeeTimePlayers(prev => 
      prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  // Select/deselect all tee time players
  const toggleAllTeeTimePlayers = (selectAll) => {
    if (selectAll) {
      const { roster, waitlist } = getTeeTimePlayers();
      const allIds = [...roster, ...waitlist].map(p => p.id);
      setSelectedTeeTimePlayers(allIds);
    } else {
      setSelectedTeeTimePlayers([]);
    }
  };

  // Get active players NOT registered for the selected tee time
  const getNotRegisteredPlayers = () => {
    if (!selectedTeeTime) return [];
    const { roster, waitlist } = getTeeTimePlayers();
    const registeredIds = new Set([...roster, ...waitlist].map(p => p.id));
    return activePlayers.filter(p => !registeredIds.has(p.id));
  };

  // Get preview data for first selected player
  const getPreviewData = () => {
    let firstPlayer = null;
    
    if (recipientType === 'all' && activePlayers.length > 0) {
      firstPlayer = activePlayers[0];
    } else if (recipientType === 'selected' && selectedPlayers.length > 0) {
      firstPlayer = activePlayers.find(p => selectedPlayers.includes(p.id));
    } else if (recipientType === 'groups' && selectedGroups.length > 0) {
      // Get first player from first selected group
      const firstGroup = groups.find(g => selectedGroups.includes(g.id));
      if (firstGroup && firstGroup.members && firstGroup.members.length > 0) {
        firstPlayer = activePlayers.find(p => p.id === firstGroup.members[0]);
      }
    }
    
    return {
      player_name: firstPlayer?.name || 'John Doe',
      player_first_name: (firstPlayer?.name || 'John Doe').split(' ')[0],
      course_name: selectedTeeTime?.course || '(no tee time selected)',
      date: selectedTeeTime ? utils.formatDate(selectedTeeTime?.date) : '(no tee time selected)',
      time: selectedTeeTime ? utils.formatTime(selectedTeeTime?.time) : '(no tee time selected)',
      player_portal_url: firstPlayer ? `${window.location.origin}/portal?token=${firstPlayer.portal_token || 'sample-token'}` : 'https://example.com/portal?token=...',
      weather: selectedTeeTime?.weather_display 
        ? selectedTeeTime.weather_display
        : '(weather not available)',
      sunrise: (() => {
        try {
          const wd = selectedTeeTime?.weather_data;
          if (!wd) return '6:42 AM';
          const parsed = typeof wd === 'string' ? JSON.parse(wd) : wd;
          return parsed?.sunrise || '6:42 AM';
        } catch { return '6:42 AM'; }
      })(),
      sunset: (() => {
        try {
          const wd = selectedTeeTime?.weather_data;
          if (!wd) return '7:18 PM';
          const parsed = typeof wd === 'string' ? JSON.parse(wd) : wd;
          return parsed?.sunset || '7:18 PM';
        } catch { return '7:18 PM'; }
      })(),
      daylight_hours: (() => {
        try {
          const wd = selectedTeeTime?.weather_data;
          if (!wd) return '12h 36m';
          const parsed = typeof wd === 'string' ? JSON.parse(wd) : wd;
          return parsed?.daylight_hours || '12h 36m';
        } catch { return '12h 36m'; }
      })()
    };
  };

  // Calculate total recipients
  const getTotalRecipients = () => {
    // If a tee time is selected, count selected tee time players
    if (selectedTeeTime) {
      if (teeTimeFilter === 'not_registered') {
        return selectedTeeTimePlayers.length;
      }
      return selectedTeeTimePlayers.length;
    }
    
    if (recipientType === 'all') {
      return activePlayers.length;
    } else if (recipientType === 'selected') {
      return selectedPlayers.length;
    } else if (recipientType === 'groups') {
      const uniquePlayerIds = new Set();
      selectedGroups.forEach(groupId => {
        const group = groups.find(g => g.id === groupId);
        if (group && group.members) {
          group.members.forEach(memberId => uniquePlayerIds.add(memberId));
        }
      });
      return uniquePlayerIds.size;
    }
    return 0;
  };

  // NEW: Count SMS-eligible recipients
  const getSmsEligibleCount = () => {
    let eligiblePlayers = [];
    
    if (selectedTeeTime) {
      // Get players from selected tee time
      eligiblePlayers = selectedTeeTimePlayers
        .map(id => players.find(p => p.id === id))
        .filter(p => p);
    } else if (recipientType === 'all') {
      eligiblePlayers = activePlayers;
    } else if (recipientType === 'selected') {
      eligiblePlayers = activePlayers.filter(p => selectedPlayers.includes(p.id));
    } else if (recipientType === 'groups') {
      const uniquePlayerIds = new Set();
      selectedGroups.forEach(groupId => {
        const group = groups.find(g => g.id === groupId);
        if (group && group.members) {
          group.members.forEach(memberId => uniquePlayerIds.add(memberId));
        }
      });
      eligiblePlayers = activePlayers.filter(p => uniquePlayerIds.has(p.id));
    }
    
    // Filter to only those with SMS enabled (comm_text === true AND valid phone)
    return eligiblePlayers.filter(p => 
      p.comm_text === true && 
      p.phone && 
      p.phone.replace(/\D/g, '').length >= 10
    ).length;
  };

  // Handle send message
  const handleSendMessage = async () => {
    // Validation - at least one channel must have content
    // PHASE 2 (May 2026): hasSms is FALSE when SMS is tier-locked, regardless of state.
    // Frontend behaves as if SMS doesn't exist for non-eligible tiers.
    const hasEmail = message.trim().length > 0;
    const hasSms = !smsLocked && smsMessage.trim().length > 0;
    
    if (!hasEmail && !hasSms) {
      addToast('Please enter an email message, a text message, or both', 'error');
      return;
    }
    
    // Email requires subject
    if (hasEmail && !subject.trim()) {
      addToast('Please enter a subject for the email', 'error');
      return;
    }
    
    // Validation for tee time specific messages
    if (selectedTeeTime && selectedTeeTimePlayers.length === 0) {
      addToast('Please select at least one player from the tee time', 'error');
      return;
    }
    
    // Validation for other recipient types (only if no tee time selected)
    if (!selectedTeeTime) {
      if (recipientType === 'selected' && selectedPlayers.length === 0) {
        addToast('Please select at least one player', 'error');
        return;
      }
      if (recipientType === 'groups' && selectedGroups.length === 0) {
        addToast('Please select at least one group', 'error');
        return;
      }
    }

    const totalRecipients = getTotalRecipients();
    if (totalRecipients === 0) {
      addToast('No recipients selected', 'error');
      return;
    }

    // Confirm send with context
    let confirmMessage = selectedTeeTime 
      ? `Send message to ${totalRecipients} player(s) ${teeTimeFilter === 'not_registered' ? 'NOT yet registered for' : 'registered for'} the ${selectedTeeTime?.course} tee time on ${utils.formatDate(selectedTeeTime?.date)}?`
      : `Send message to ${totalRecipients} player(s)?`;
    
    // Add channel info
    const channels = [];
    if (hasEmail) channels.push('📧 Email');
    if (hasSms) channels.push(`📱 SMS (${getSmsEligibleCount()} eligible)`);
    confirmMessage += `\n\nChannels: ${channels.join(' + ')}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSending(true);

    try {
      // FIXED: Include JWT auth header for multi-tenant support
      // FIXED Jan 2026: Use async getValidAuthToken to auto-refresh expired tokens
      const msgHeaders = { 'Content-Type': 'application/json' };
      const msgToken = await getValidAuthToken();
      if (msgToken) {
        msgHeaders['Authorization'] = `Bearer ${msgToken}`;
      } else {
        throw new Error('Session expired. Please log in again.');
      }
      
      // Build request body - if tee time is selected, use tee time players
      // PHASE 2 (May 2026): When SMS is locked (org tier can't send), suppress smsMessage
      // entirely from the request body — frontend never tries to send SMS for a Free/Starter org.
      // The backend route gate is still in place as defense-in-depth.
      const requestBody = {
        subject: subject.trim(),
        message: getFinalMessage().trim(), // Use getFinalMessage() to include weather block if checkbox is checked
        smsMessage: smsLocked ? '' : smsMessage.trim(),
        teeTimeId: selectedTeeTime?.id || null,
        includeWeather
      };
      
      if (selectedTeeTime) {
        // When a tee time is selected, send to the selected tee time players
        requestBody.recipientType = 'selected';
        requestBody.playerIds = selectedTeeTimePlayers;
        requestBody.groupIds = [];
        requestBody.teeTimeFilter = teeTimeFilter; // for logging
      } else {
        // Normal recipient selection
        requestBody.recipientType = recipientType;
        requestBody.playerIds = recipientType === 'selected' ? selectedPlayers : [];
        requestBody.groupIds = recipientType === 'groups' ? selectedGroups : [];
      }
      
      const response = await fetch(`${API_URL}/custom-message`, {
        method: 'POST',
        headers: msgHeaders,
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        // PHASE 2 (May 2026): If backend returned a feature-upgrade-required error,
        // fire the upgrade modal instead of a generic error toast. The modal
        // explains which paid tier unlocks the feature and offers the upgrade path.
        // (setSending(false) is handled by the finally block below — no need here.)
        if (handleFeatureUpgradeRequired(data)) {
          return; // Modal handles the UX from here
        }
        throw new Error(data.error || 'Failed to send messages');
      }

      // Build success message showing both channels
      let successMsg = 'Messages sent! ';
      if (data.email?.attempted) {
        successMsg += `📧 Email: ${data.email.sent} sent`;
        if (data.email.failed > 0) successMsg += ` (${data.email.failed} failed)`;
        successMsg += '. ';
      }
      if (data.sms?.attempted) {
        successMsg += `📱 SMS: ${data.sms.sent} sent`;
        if (data.sms.failed > 0) successMsg += ` (${data.sms.failed} failed)`;
        successMsg += '.';
      }
      // PHASE 2 (May 2026): If SMS was requested but tier-blocked, show a separate
      // info toast nudging upgrade. This branch fires when the frontend's lock UI
      // didn't suppress the smsMessage (e.g. stale UI state or a curl bypass).
      if (data.sms?.tierBlocked) {
        addToast(`📧 Email sent. 📱 Text messages aren't included with your current plan — tap the text field to see upgrade options.`, 'info');
      }
      // Fallback for legacy response format
      if (!data.email && !data.sms) {
        successMsg = `Messages sent successfully! Sent: ${data.sent}, Failed: ${data.failed}`;
      }
      addToast(successMsg, 'success');
      
      // Reset form
      setSubject('');
      setMessage('');
      setSmsMessage(''); // NEW: Reset SMS message
      setSelectedTeeTime(null);
      setIncludeWeather(false);
      setSelectedTeeTimePlayers([]);
      setTeeTimeFilter('registered');
      setRecipientType('all');
      setSelectedPlayers([]);
      setSelectedGroups([]);

    } catch (error) {
      console.error('Error sending messages:', error);
      addToast(error.message || 'Error sending messages', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 18 }}>Send Custom Message to Players</div>

      {/* Recipients Selection - Hidden when a tee time is selected */}
      {!selectedTeeTime && (
        <Card>
          <Label style={{ marginBottom: 12, display: 'block', fontWeight: 600 }}>Recipients</Label>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Radio Buttons */}
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                value="all"
                checked={recipientType === 'all'}
                onChange={(e) => setRecipientType(e.target.value)}
                style={{ marginRight: 8 }}
              />
              <span>All Players ({activePlayers.length})</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                value="selected"
                checked={recipientType === 'selected'}
                onChange={(e) => setRecipientType(e.target.value)}
                style={{ marginRight: 8 }}
              />
              <span>Select Players</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                value="groups"
                checked={recipientType === 'groups'}
                onChange={(e) => setRecipientType(e.target.value)}
                style={{ marginRight: 8 }}
              />
              <span>Select Player Groups</span>
            </label>
          </div>

          {/* Player Selection (if "Select Players" chosen) */}
          {recipientType === 'selected' && (
            <div style={{ marginTop: 16, maxHeight: 300, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12 }}>
              {activePlayers.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: 14 }}>No active players available</div>
              ) : (
                activePlayers.map(player => (
                  <label key={player.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedPlayers.includes(player.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPlayers([...selectedPlayers, player.id]);
                        } else {
                          setSelectedPlayers(selectedPlayers.filter(id => id !== player.id));
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    <span>{player.name} ({player.email})</span>
                  </label>
                ))
              )}
          </div>
        )}

        {/* Group Selection (if "Select Groups" chosen) */}
        {recipientType === 'groups' && (
          <div style={{ marginTop: 16, maxHeight: 300, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12 }}>
            {groups.length === 0 ? (
              <div style={{ color: '#6b7280', fontSize: 14 }}>No player groups available</div>
            ) : (
              groups.map(group => (
                <label key={group.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedGroups([...selectedGroups, group.id]);
                      } else {
                        setSelectedGroups(selectedGroups.filter(id => id !== group.id));
                      }
                    }}
                    style={{ marginRight: 8 }}
                  />
                  <span>{group.name} ({group.members?.length || 0} players)</span>
                </label>
              ))
            )}
          </div>
        )}

        {/* Recipient Count */}
        <div style={{ marginTop: 12, padding: 8, background: '#f3f4f6', borderRadius: 4, fontSize: 14, fontWeight: 600 }}>
          Selected Recipients: {getTotalRecipients()} player(s)
        </div>
      </Card>
      )}

      {/* Placeholder Helper */}
      <Card style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>💡 Available Placeholders (Click to insert)</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <button
            onClick={() => insertPlaceholder('{player_name}')}
            style={{
              padding: '6px 12px',
              background: '#d4edda',
              border: '2px solid #28a745',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'monospace'
            }}
            title="Always available - Full name"
          >
            {'{player_name}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{player_first_name}')}
            style={{
              padding: '6px 12px',
              background: '#d4edda',
              border: '2px solid #28a745',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'monospace'
            }}
            title="Always available - First name only"
          >
            {'{player_first_name}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{player_portal_url}')}
            style={{
              padding: '6px 12px',
              background: '#d4edda',
              border: '2px solid #28a745',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'monospace'
            }}
            title="Always available"
          >
            {'{player_portal_url}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{handicap_guide_link}')}
            style={{
              padding: '6px 12px',
              background: '#d4edda',
              border: '2px solid #28a745',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'monospace'
            }}
            title="Always available - Styled link to handicap guide"
          >
            {'{handicap_guide_link}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{ghin_link}')}
            style={{
              padding: '6px 12px',
              background: '#d4edda',
              border: '2px solid #28a745',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'monospace'
            }}
            title="Always available - Styled GHIN post score button"
          >
            {'{ghin_link}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{course_name}')}
            disabled={!selectedTeeTime}
            style={{
              padding: '6px 12px',
              background: selectedTeeTime ? '#fff3cd' : '#e9ecef',
              border: selectedTeeTime ? '2px solid #ffc107' : '2px solid #ced4da',
              borderRadius: 4,
              cursor: selectedTeeTime ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontFamily: 'monospace',
              opacity: selectedTeeTime ? 1 : 0.4
            }}
            title="Requires tee time selection"
          >
            {'{course_name}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{leaderboard_url}')}
            disabled={!selectedTeeTime}
            style={{
              padding: '6px 12px',
              background: selectedTeeTime ? '#fff3cd' : '#e9ecef',
              border: selectedTeeTime ? '2px solid #ffc107' : '2px solid #ced4da',
              borderRadius: 4,
              cursor: selectedTeeTime ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontFamily: 'monospace',
              opacity: selectedTeeTime ? 1 : 0.4
            }}
            title="Requires tee time selection — inserts the live leaderboard link and shares the round"
          >
            {'{leaderboard_url}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{date}')}
            disabled={!selectedTeeTime}
            style={{
              padding: '6px 12px',
              background: selectedTeeTime ? '#fff3cd' : '#e9ecef',
              border: selectedTeeTime ? '2px solid #ffc107' : '2px solid #ced4da',
              borderRadius: 4,
              cursor: selectedTeeTime ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontFamily: 'monospace',
              opacity: selectedTeeTime ? 1 : 0.4
            }}
            title="Requires tee time selection"
          >
            {'{date}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{time}')}
            disabled={!selectedTeeTime}
            style={{
              padding: '6px 12px',
              background: selectedTeeTime ? '#fff3cd' : '#e9ecef',
              border: selectedTeeTime ? '2px solid #ffc107' : '2px solid #ced4da',
              borderRadius: 4,
              cursor: selectedTeeTime ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontFamily: 'monospace',
              opacity: selectedTeeTime ? 1 : 0.4
            }}
            title="Requires tee time selection"
          >
            {'{time}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{sunrise}')}
            disabled={!selectedTeeTime}
            style={{
              padding: '6px 12px',
              background: selectedTeeTime ? '#fef3c7' : '#e9ecef',
              border: selectedTeeTime ? '2px solid #fcd34d' : '2px solid #ced4da',
              borderRadius: 4,
              cursor: selectedTeeTime ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontFamily: 'monospace',
              opacity: selectedTeeTime ? 1 : 0.4
            }}
            title="Requires tee time selection - inserts sunrise time"
          >
            {'{sunrise}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{sunset}')}
            disabled={!selectedTeeTime}
            style={{
              padding: '6px 12px',
              background: selectedTeeTime ? '#fef3c7' : '#e9ecef',
              border: selectedTeeTime ? '2px solid #fcd34d' : '2px solid #ced4da',
              borderRadius: 4,
              cursor: selectedTeeTime ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontFamily: 'monospace',
              opacity: selectedTeeTime ? 1 : 0.4
            }}
            title="Requires tee time selection - inserts sunset time"
          >
            {'{sunset}'}
          </button>
          <button
            onClick={() => insertPlaceholder('{daylight_hours}')}
            disabled={!selectedTeeTime}
            style={{
              padding: '6px 12px',
              background: selectedTeeTime ? '#fef3c7' : '#e9ecef',
              border: selectedTeeTime ? '2px solid #fcd34d' : '2px solid #ced4da',
              borderRadius: 4,
              cursor: selectedTeeTime ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontFamily: 'monospace',
              opacity: selectedTeeTime ? 1 : 0.4
            }}
            title="Requires tee time selection - inserts daylight hours"
          >
            {'{daylight_hours}'}
          </button>
        </div>
        <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
          <span style={{ display: 'inline-block', padding: '2px 6px', background: '#28a745', color: '#fff', borderRadius: 3, marginRight: 6, fontSize: 11 }}>
            ALWAYS AVAILABLE
          </span>
          {'{player_name}'}, {'{player_first_name}'}, {'{player_portal_url}'}, {'{handicap_guide_link}'}, {'{ghin_link}'}
          <br />
          <span style={{ display: 'inline-block', padding: '2px 6px', background: '#fef3c7', color: '#000', border: '1px solid #fcd34d', borderRadius: 3, marginRight: 6, fontSize: 11, marginTop: 4 }}>
            REQUIRES TEE TIME
          </span>
          {'{course_name}'}, {'{leaderboard_url}'}, {'{date}'}, {'{time}'}, {'{sunrise}'}, {'{sunset}'}, {'{daylight_hours}'}
        </div>
      </Card>

      {/* Tee Time Specific Message Selector */}
      <Card>
        <Label style={{ marginBottom: 8, display: 'block', fontWeight: 600 }}>📅 Tee Time Specific Message</Label>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12, marginTop: 0 }}>
          Select a tee time to message players based on their registration status
        </p>
        <select
          value={selectedTeeTime?.id || ''}
          onChange={(e) => handleTeeTimeSelect(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: selectedTeeTime ? '2px solid #2d6a4f' : '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14,
            background: selectedTeeTime ? '#f0fdf4' : '#fff'
          }}
        >
          <option value="">— Select a tee time (or leave blank for general message) —</option>
          {upcomingTeeTimes.map(tt => {
            // Parse date manually to avoid Safari/iOS timezone issues
            const dateStr = String(tt.date || '').split('T')[0];
            const [year, month, day] = dateStr.split('-').map(Number);
            const teeTimeDate = new Date(year, month - 1, day);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const daysUntil = Math.ceil((teeTimeDate - today) / (1000 * 60 * 60 * 24));
            const hasWeather = daysUntil >= 0 && daysUntil <= 15 && tt.weather_display;
            
            // Count players
            const rosterCount = Array.isArray(tt.players) ? tt.players.length : 0;
            const waitlistCount = Array.isArray(tt.waitlist) ? tt.waitlist.length : 0;
            const totalPlayers = rosterCount + waitlistCount;

            return (
              <option key={tt.id} value={tt.id}>
                {tt.course} - {utils.formatDate(tt.date)} at {utils.formatTime(tt.time)} ({totalPlayers} player{totalPlayers !== 1 ? 's' : ''})
                {hasWeather ? ' ☀️' : ''}
              </option>
            );
          })}
        </select>

        {/* Filter radio buttons — shown once a tee time is selected */}
        {selectedTeeTime && (
          <div style={{ marginTop: 12, display: 'flex', gap: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 14 }}>
              <input
                type="radio"
                name="teeTimeFilter"
                value="registered"
                checked={teeTimeFilter === 'registered'}
                onChange={() => {
                  setTeeTimeFilter('registered');
                  const { roster, waitlist } = getTeeTimePlayers();
                  const allIds = [...roster, ...waitlist].map(p => p.id);
                  setSelectedTeeTimePlayers(allIds);
                }}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontWeight: teeTimeFilter === 'registered' ? 600 : 400, color: teeTimeFilter === 'registered' ? '#2d6a4f' : '#374151' }}>
                Registered Players
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 14 }}>
              <input
                type="radio"
                name="teeTimeFilter"
                value="not_registered"
                checked={teeTimeFilter === 'not_registered'}
                onChange={() => {
                  setTeeTimeFilter('not_registered');
                  const notReg = getNotRegisteredPlayers();
                  setSelectedTeeTimePlayers(notReg.map(p => p.id));
                }}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontWeight: teeTimeFilter === 'not_registered' ? 600 : 400, color: teeTimeFilter === 'not_registered' ? '#1d4ed8' : '#374151' }}>
                Not Yet Registered
              </span>
            </label>
          </div>
        )}

        {/* Show player list when a tee time is selected */}
        {selectedTeeTime && (() => {
          if (teeTimeFilter === 'registered') {
            const { roster, waitlist } = getTeeTimePlayers();
            const allPlayers = [...roster, ...waitlist];
            const allSelected = selectedTeeTimePlayers.length === allPlayers.length && allPlayers.length > 0;

            return (
              <div style={{ marginTop: 16, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ 
                  padding: '12px 16px', 
                  background: '#f8f9fa', 
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <strong style={{ color: '#2d6a4f' }}>🏌️ Players for this Tee Time</strong>
                    <span style={{ marginLeft: 8, fontSize: 13, color: '#6b7280' }}>
                      ({selectedTeeTimePlayers.length} of {allPlayers.length} selected)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleAllTeeTimePlayers(!allSelected)}
                    style={{
                      padding: '4px 10px',
                      fontSize: 12,
                      border: '1px solid #d1d5db',
                      borderRadius: 4,
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {/* Roster Players */}
                  {roster.length > 0 && (
                    <div>
                      <div style={{ padding: '8px 16px', background: '#e8f5e9', fontSize: 12, fontWeight: 600, color: '#2d6a4f' }}>
                        ✓ Roster ({roster.length})
                      </div>
                      {roster.map(player => (
                        <label
                          key={player.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px 16px',
                            borderBottom: '1px solid #f3f4f6',
                            cursor: 'pointer',
                            background: selectedTeeTimePlayers.includes(player.id) ? '#fafafa' : '#fff'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedTeeTimePlayers.includes(player.id)}
                            onChange={() => toggleTeeTimePlayer(player.id)}
                            style={{ marginRight: 12, width: 16, height: 16, cursor: 'pointer' }}
                          />
                          <div>
                            <div style={{ fontWeight: 500 }}>{player.name}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{player.email || 'No email'}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {/* Waitlist Players */}
                  {waitlist.length > 0 && (
                    <div>
                      <div style={{ padding: '8px 16px', background: '#fff3cd', fontSize: 12, fontWeight: 600, color: '#856404' }}>
                        ⏳ Waitlist ({waitlist.length})
                      </div>
                      {waitlist.map(player => (
                        <label
                          key={player.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px 16px',
                            borderBottom: '1px solid #f3f4f6',
                            cursor: 'pointer',
                            background: selectedTeeTimePlayers.includes(player.id) ? '#fafafa' : '#fff'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedTeeTimePlayers.includes(player.id)}
                            onChange={() => toggleTeeTimePlayer(player.id)}
                            style={{ marginRight: 12, width: 16, height: 16, cursor: 'pointer' }}
                          />
                          <div>
                            <div style={{ fontWeight: 500 }}>{player.name}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{player.email || 'No email'}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {roster.length === 0 && waitlist.length === 0 && (
                    <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>
                      No players registered for this tee time yet
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // Not Yet Registered view
          const notRegistered = getNotRegisteredPlayers();
          const allSelected = selectedTeeTimePlayers.length === notRegistered.length && notRegistered.length > 0;

          return (
            <div style={{ marginTop: 16, border: '1px solid #bfdbfe', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ 
                padding: '12px 16px', 
                background: '#eff6ff', 
                borderBottom: '1px solid #bfdbfe',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong style={{ color: '#1d4ed8' }}>📋 Players Not Yet Registered</strong>
                  <span style={{ marginLeft: 8, fontSize: 13, color: '#6b7280' }}>
                    ({selectedTeeTimePlayers.length} of {notRegistered.length} selected)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (allSelected) {
                      setSelectedTeeTimePlayers([]);
                    } else {
                      setSelectedTeeTimePlayers(notRegistered.map(p => p.id));
                    }
                  }}
                  style={{
                    padding: '4px 10px',
                    fontSize: 12,
                    border: '1px solid #bfdbfe',
                    borderRadius: 4,
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {notRegistered.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>
                    All active players are already registered for this tee time
                  </div>
                ) : (
                  notRegistered.map(player => (
                    <label
                      key={player.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 16px',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        background: selectedTeeTimePlayers.includes(player.id) ? '#f0f7ff' : '#fff'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTeeTimePlayers.includes(player.id)}
                        onChange={() => toggleTeeTimePlayer(player.id)}
                        style={{ marginRight: 12, width: 16, height: 16, cursor: 'pointer' }}
                      />
                      <div>
                        <div style={{ fontWeight: 500 }}>{player.name}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{player.email || 'No email'}</div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          );
        })()}

        {selectedTeeTime && !isWeatherAvailable() && (
          <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280', fontStyle: 'italic' }}>
            ℹ️ Weather forecast not available (outside 15-day window or no data)
          </div>
        )}

        {/* Weather Checkbox */}
        {isWeatherAvailable() && (
          <div style={{ marginTop: 12, padding: 12, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}>
              <input
                type="checkbox"
                checked={includeWeather}
                onChange={(e) => setIncludeWeather(e.target.checked)}
                style={{ marginRight: 8, width: 18, height: 18, cursor: 'pointer' }}
              />
              Include weather forecast for this tee time
            </label>

            {includeWeather && (
              <div style={{ marginTop: 12, padding: 10, background: '#fff', borderLeft: '4px solid #0d6efd', borderRadius: 4 }}>
                <strong style={{ display: 'block', marginBottom: 8, color: '#0d6efd' }}>📊 Weather Preview:</strong>
                <div style={{ fontSize: 14, lineHeight: 1.6, color: '#495057', whiteSpace: 'pre-line' }}>
                  {selectedTeeTime.weather_display || 'Weather data not available'}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Subject Field */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Label>📧 Email Subject {!message.trim() && <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 'normal' }}>(not required if sending text only)</span>}</Label>
          <span style={{ fontSize: 11, color: subject.length > 50 ? '#dc2626' : '#6b7280' }}>
            {subject.length}/50 characters
          </span>
        </div>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={50}
          placeholder="Email subject line (e.g., 'Upcoming Tee Time at {course_name}')"
        />
      </Card>

      {/* Message Body */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Label>📧 Email Message {!smsMessage.trim() && <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 'normal' }}>*</span>}</Label>
          <span style={{ fontSize: 11, color: message.length > 3500 ? '#dc2626' : '#6b7280' }}>
            {message.length}/3500 characters
          </span>
        </div>
        <textarea
          ref={messageTextareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={3500}
          placeholder="Type your email message here... Use placeholders like {player_name} for personalization. Leave blank to send text only."
          rows={10}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
        <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
          {message.trim() ? '✓ Email will be sent' : 'No email (field empty)'}
        </div>
      </Card>

      {/* Text Message */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Label>
            📱 Text Message {!smsLocked && !message.trim() && <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 'normal' }}>*</span>}
            {smsLocked && <LockBadge feature="sms" requiredTier="plus" />}
          </Label>
          {!smsLocked && (
            <span style={{ 
              fontSize: 11, 
              color: smsMessage.length > 320 ? '#dc2626' : smsMessage.length > 160 ? '#f59e0b' : '#6b7280',
              fontWeight: smsMessage.length > 320 ? 'bold' : 'normal'
            }}>
              {smsMessage.length}/320 characters{smsMessage.length > 160 ? ' (2 segments)' : ''}
            </span>
          )}
        </div>
        {/* Locked-state wrapper: dimmed content, click overlay fires upgrade modal */}
        <div style={{ position: 'relative' }}>
          <div style={{ opacity: smsLocked ? 0.45 : 1, pointerEvents: smsLocked ? 'none' : 'auto' }}>
            <textarea
              value={smsLocked ? '' : smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              maxLength={320}
              placeholder={smsLocked ? "🔒 Text messages aren't included with your current plan. Tap to see upgrade options." : 'Optional: Enter text message (up to 320 chars / 2 segments). Leave blank to send email only.'}
              rows={3}
              disabled={smsLocked}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: smsMessage.length > 320 ? '2px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'vertical',
                background: smsLocked ? '#f9fafb' : '#fff'
              }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
              {!smsLocked && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    📱 Text message will be sent to <strong>{getSmsEligibleCount()}</strong> player(s) with text enabled
                  </span>
                  <span style={{ 
                    padding: '2px 8px', 
                    background: smsMessage.trim() ? '#dcfce7' : '#f3f4f6',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 500
                  }}>
                    {smsMessage.trim() ? '✓ Text message will be sent' : 'No text message (field empty)'}
                  </span>
                </div>
              )}
            </div>
            {!smsLocked && (
              <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 4, fontSize: 12 }}>
                <strong>💡 Text Message Tips:</strong> Up to 320 chars. Placeholders: {'{player_first_name}'}, {'{player_portal_url}'}, {'{course_name}'}, {'{date}'}, {'{time}'}
              </div>
            )}
          </div>
          {smsLocked && (
            <div
              onClick={() => triggerUpgradeModal({ feature: 'sms', requiredTier: 'plus', currentTier: orgTier })}
              style={{
                position: 'absolute',
                inset: 0,
                cursor: 'pointer',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Click to upgrade and unlock text messaging"
            >
              <div style={{
                background: 'rgba(255,255,255,0.95)',
                border: '2px dashed #fcd34d',
                borderRadius: 8,
                padding: '12px 20px',
                color: '#92400e',
                fontSize: 13,
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                🔒 Text messages aren't included with your current plan — tap to see upgrade options
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Preview & Send Buttons */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <Button
          variant="outline"
          onClick={() => setShowPreview(true)}
          disabled={!message && !smsMessage}
        >
          👁️ Preview
        </Button>
        <Button
          onClick={handleSendMessage}
          disabled={sending || (!message && !smsMessage) || getTotalRecipients() === 0}
          style={{
            background: sending ? '#9ca3af' : '#2d6a4f',
            cursor: sending ? 'not-allowed' : 'pointer'
          }}
        >
          {sending ? '📤 Sending...' : '📨 Send Message'}
        </Button>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <Modal title="Message Preview" onClose={() => setShowPreview(false)}>
          <div style={{ display: 'grid', gap: 16 }}>
            
            {/* Email Preview */}
            {message.trim() && (
              <>
                <div style={{ borderBottom: '2px solid #2d6a4f', paddingBottom: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#2d6a4f' }}>📧 Email Preview</span>
                </div>
                <div>
                  <strong style={{ display: 'block', marginBottom: 4 }}>Subject:</strong>
                  <div style={{ padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14 }}>
                    {subject.replace(/\{player_name\}/g, getPreviewData().player_name)
                      .replace(/\{player_first_name\}/g, getPreviewData().player_first_name)
                      .replace(/\{course_name\}/g, getPreviewData().course_name)
                      .replace(/\{date\}/g, getPreviewData().date)
                      .replace(/\{time\}/g, getPreviewData().time)
                      .replace(/\{player_portal_url\}/g, getPreviewData().player_portal_url)
                      .replace(/\{weather\}/g, getPreviewData().weather)
                      .replace(/\{sunrise\}/g, getPreviewData().sunrise)
                      .replace(/\{sunset\}/g, getPreviewData().sunset)
                      .replace(/\{daylight_hours\}/g, getPreviewData().daylight_hours)}
                  </div>
                </div>

                <div>
                  <strong style={{ display: 'block', marginBottom: 4 }}>Message:</strong>
                  <div 
                    style={{ padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, whiteSpace: 'pre-wrap' }}
                    dangerouslySetInnerHTML={{
                      __html: getFinalMessage()
                        .replace(/\{player_name\}/g, getPreviewData().player_name)
                        .replace(/\{player_first_name\}/g, getPreviewData().player_first_name)
                        .replace(/\{course_name\}/g, getPreviewData().course_name)
                        .replace(/\{date\}/g, getPreviewData().date)
                        .replace(/\{time\}/g, getPreviewData().time)
                        .replace(/\{player_portal_url\}/g, getPreviewData().player_portal_url)
                        .replace(/\{weather\}/g, getPreviewData().weather)
                        .replace(/\{sunrise\}/g, getPreviewData().sunrise)
                        .replace(/\{sunset\}/g, getPreviewData().sunset)
                        .replace(/\{daylight_hours\}/g, getPreviewData().daylight_hours)
                        .replace(/\n/g, '<br />')
                    }}
                  />
                </div>
              </>
            )}

            {/* SMS Preview */}
            {smsMessage.trim() && (
              <>
                <div style={{ borderBottom: '2px solid #0ea5e9', paddingBottom: 8, marginBottom: 8, marginTop: message.trim() ? 16 : 0 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#0ea5e9' }}>📱 Text Message Preview</span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>({smsMessage.length}/320 chars{smsMessage.length > 160 ? ' · 2 segments' : ' · 1 segment'})</span>
                </div>
                <div style={{ 
                  padding: 16, 
                  background: '#ecfdf5', 
                  border: '1px solid #a7f3d0', 
                  borderRadius: 12, 
                  fontSize: 14,
                  maxWidth: 300,
                  margin: '0 auto'
                }}>
                  <div style={{ 
                    background: '#fff', 
                    padding: 12, 
                    borderRadius: 8,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}>
                    {smsMessage
                      .replace(/\{player_name\}/g, getPreviewData().player_name)
                      .replace(/\{player_first_name\}/g, getPreviewData().player_first_name)
                      .replace(/\{course_name\}/g, getPreviewData().course_name)
                      .replace(/\{date\}/g, getPreviewData().date)
                      .replace(/\{time\}/g, getPreviewData().time)
                      .replace(/\{weather\}/g, getPreviewData().weather)
                      .replace(/\{sunrise\}/g, getPreviewData().sunrise)
                      .replace(/\{sunset\}/g, getPreviewData().sunset)
                      .replace(/\{daylight_hours\}/g, getPreviewData().daylight_hours)}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                  📱 Will be sent to {getSmsEligibleCount()} SMS-enabled player(s)
                </div>
              </>
            )}

            <div style={{ fontSize: 13, color: '#6b7280', padding: 10, background: '#f3f4f6', borderRadius: 4 }}>
              This preview shows how the message will look for: <strong>{getPreviewData().player_name}</strong>
            </div>

            {!selectedTeeTime && (message.includes('{course_name}') || message.includes('{date}') || message.includes('{time}') || message.includes('{weather}') ||
              smsMessage.includes('{course_name}') || smsMessage.includes('{date}') || smsMessage.includes('{time}') || smsMessage.includes('{weather}')) && (
              <div style={{ fontSize: 13, color: '#dc2626', padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
                ⚠️ <em>Note: Some placeholders show "(no tee time selected)" because no tee time was selected.</em>
              </div>
            )}

            <div style={{ textAlign: 'right' }}>
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
// ============================================================================
// WELCOME EMAIL FORM COMPONENT
// Allows organizers to send welcome emails to new players using the welcome template
// ============================================================================

const WelcomeEmailForm = () => {
  const { addToast } = useToast();
  const { data: players } = useApiData('/players');
  const { data: groups } = useApiData('/groups');
  const { data: templates } = useApiData('/message-templates');
  const [recipientType, setRecipientType] = useState('selected');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Get welcome template
  const welcomeTemplate = templates.find(t => t.template_type === 'welcome');

  // Filter active players (excluding guests) and sort by last name
  const activePlayers = players
    .filter(p => p.active !== false && p.role?.toLowerCase() !== 'guest')
    .sort((a, b) => {
      const getLastName = (name) => {
        const parts = name.trim().split(/\s+/);
        return parts[parts.length - 1].toLowerCase();
      };
      return getLastName(a.name).localeCompare(getLastName(b.name));
    });

  // Get total recipients count
  const getTotalRecipients = () => {
    if (recipientType === 'all') {
      return activePlayers.length;
    } else if (recipientType === 'selected') {
      return selectedPlayers.length;
    } else if (recipientType === 'groups') {
      const uniquePlayerIds = new Set();
      selectedGroups.forEach(groupId => {
        const group = groups.find(g => g.id === groupId);
        if (group && group.members) {
          group.members.forEach(memberId => uniquePlayerIds.add(memberId));
        }
      });
      return uniquePlayerIds.size;
    }
    return 0;
  };

  // Handle send welcome email
  const handleSendWelcome = async () => {
    if (!welcomeTemplate) {
      addToast('Welcome template not found. Please check Templates tab.', 'error');
      return;
    }

    const totalRecipients = getTotalRecipients();
    if (totalRecipients === 0) {
      addToast('Please select at least one recipient', 'error');
      return;
    }

    // Confirm send
    if (!window.confirm(`Send welcome email to ${totalRecipients} player(s)?`)) {
      return;
    }

    setSending(true);

    try {
      const msgHeaders = { 'Content-Type': 'application/json' };
      const msgToken = getAuthToken();
      if (msgToken) {
        msgHeaders['Authorization'] = `Bearer ${msgToken}`;
      }

      const response = await fetch(`${API_URL}/welcome-email`, {
        method: 'POST',
        headers: msgHeaders,
        credentials: 'include',
        body: JSON.stringify({
          recipientType,
          playerIds: recipientType === 'selected' ? selectedPlayers : [],
          groupIds: recipientType === 'groups' ? selectedGroups : []
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send welcome emails');
      }

      addToast(`Welcome emails sent successfully! Sent: ${data.sent}, Failed: ${data.failed}`, 'success');
      
      // Reset form
      setRecipientType('selected');
      setSelectedPlayers([]);
      setSelectedGroups([]);

    } catch (error) {
      console.error('Error sending welcome emails:', error);
      addToast(error.message || 'Error sending welcome emails', 'error');
    } finally {
      setSending(false);
    }
  };

  // Get preview with sample data
  const getPreviewContent = () => {
    if (!welcomeTemplate) return { subject: '', body: '' };
    
    const samplePlayer = activePlayers[0] || { name: 'John Smith' };
    const playerFirstName = (samplePlayer.name || 'John').split(' ')[0];
    
    let subject = welcomeTemplate.subject || '';
    let body = welcomeTemplate.email_body || '';
    
    // Replace placeholders with sample data
    const replacements = {
      '{player_name}': samplePlayer.name || 'John Smith',
      '{player_first_name}': playerFirstName,
      '{player_portal_url}': 'https://tto.yourdomain.com/portal?token=...',
      '{group_name}': process.env.REACT_APP_GROUP_NAME || 'Your Golf Group',
      '{organizer_name}': 'The Organizer'
    };
    
    Object.entries(replacements).forEach(([placeholder, value]) => {
      subject = subject.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
      body = body.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    
    return { subject, body };
  };

  const preview = getPreviewContent();

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 18 }}>Send Welcome Email to Players</div>
      
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Send the welcome email template to new players. This uses the template defined in the Templates tab.
      </p>

      {/* Template Status */}
      <Card style={{ background: welcomeTemplate ? '#f0fdf4' : '#fef2f2', border: welcomeTemplate ? '1px solid #86efac' : '1px solid #fecaca' }}>
        {welcomeTemplate ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 24 }}>✅</span>
            <div>
              <div style={{ fontWeight: 600, color: '#166534' }}>Welcome Template Ready</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Subject: {welcomeTemplate.subject}</div>
            </div>
            <button
              onClick={() => setShowPreview(!showPreview)}
              style={{
                marginLeft: 'auto',
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                background: '#fff',
                cursor: 'pointer',
                fontSize: 13
              }}
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 24 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 600, color: '#991b1b' }}>Welcome Template Not Found</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Please create a welcome template in the Templates tab first.</div>
            </div>
          </div>
        )}
      </Card>

      {/* Preview Section */}
      {showPreview && welcomeTemplate && (
        <Card style={{ background: '#f8f9fa', border: '1px solid #e5e7eb' }}>
          <div style={{ fontWeight: 600, marginBottom: 12, color: '#374151' }}>📧 Email Preview (Sample Data)</div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', background: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
              <strong>Subject:</strong> {preview.subject}
            </div>
            <div style={{ padding: 16, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}>
              {preview.body}
            </div>
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
            * Placeholders will be replaced with actual player data when sent
          </div>
        </Card>
      )}

      {/* Recipients Selection */}
      <Card>
        <Label style={{ marginBottom: 12, display: 'block', fontWeight: 600 }}>Select Recipients</Label>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="all"
              checked={recipientType === 'all'}
              onChange={(e) => setRecipientType(e.target.value)}
              style={{ marginRight: 8 }}
            />
            <span>All Players ({activePlayers.length})</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="selected"
              checked={recipientType === 'selected'}
              onChange={(e) => setRecipientType(e.target.value)}
              style={{ marginRight: 8 }}
            />
            <span>Select Players</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="groups"
              checked={recipientType === 'groups'}
              onChange={(e) => setRecipientType(e.target.value)}
              style={{ marginRight: 8 }}
            />
            <span>Select Player Groups</span>
          </label>
        </div>

        {/* Player Selection */}
        {recipientType === 'selected' && (
          <div style={{ marginTop: 16, maxHeight: 300, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12 }}>
            {activePlayers.length === 0 ? (
              <div style={{ color: '#6b7280', fontSize: 14 }}>No active players available</div>
            ) : (
              <>
                <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' }}>
                  <button
                    type="button"
                    onClick={() => setSelectedPlayers(selectedPlayers.length === activePlayers.length ? [] : activePlayers.map(p => p.id))}
                    style={{
                      padding: '4px 10px',
                      fontSize: 12,
                      border: '1px solid #d1d5db',
                      borderRadius: 4,
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    {selectedPlayers.length === activePlayers.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                {activePlayers.map(player => (
                  <label key={player.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedPlayers.includes(player.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPlayers([...selectedPlayers, player.id]);
                        } else {
                          setSelectedPlayers(selectedPlayers.filter(id => id !== player.id));
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    <span>{player.name} ({player.email || 'no email'})</span>
                  </label>
                ))}
              </>
            )}
          </div>
        )}

        {/* Group Selection */}
        {recipientType === 'groups' && (
          <div style={{ marginTop: 16, maxHeight: 300, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12 }}>
            {groups.length === 0 ? (
              <div style={{ color: '#6b7280', fontSize: 14 }}>No player groups available</div>
            ) : (
              groups.map(group => (
                <label key={group.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedGroups([...selectedGroups, group.id]);
                      } else {
                        setSelectedGroups(selectedGroups.filter(id => id !== group.id));
                      }
                    }}
                    style={{ marginRight: 8 }}
                  />
                  <span>{group.name} ({group.members?.length || 0} players)</span>
                </label>
              ))
            )}
          </div>
        )}

        {/* Recipient Count */}
        <div style={{ marginTop: 12, padding: 8, background: '#f3f4f6', borderRadius: 4, fontSize: 14, fontWeight: 600 }}>
          Selected Recipients: {getTotalRecipients()} player(s)
        </div>
      </Card>

      {/* Send Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button
          onClick={handleSendWelcome}
          disabled={sending || !welcomeTemplate || getTotalRecipients() === 0}
          style={{
            padding: '12px 24px',
            background: sending || !welcomeTemplate || getTotalRecipients() === 0 ? '#9ca3af' : '#2d6a4f',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 15,
            fontWeight: 600,
            cursor: sending || !welcomeTemplate || getTotalRecipients() === 0 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          {sending ? (
            <>
              <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
              Sending...
            </>
          ) : (
            <>
              👋 Send Welcome Email
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// MODIFIED MESSAGE MANAGER COMPONENT
// This replaces the existing MessageManager component in App.js
// Adds sub-tabs for "Player Message" and "Templates"
// ============================================================================

const MessageManager = () => {
  const [subTab, setSubTab] = useState("player-message"); // NEW: Sub-tab state

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Sub-Menu Tabs */}
      <div style={{ borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setSubTab("player-message")}
            style={{
              padding: "10px 16px",
              border: "none",
              background: "transparent",
              borderBottom: subTab === "player-message" ? "3px solid #2d6a4f" : "3px solid transparent",
              cursor: "pointer",
              fontSize: 15,
              fontWeight: subTab === "player-message" ? 700 : 400,
              color: subTab === "player-message" ? "#2d6a4f" : "#6b7280"
            }}
          >
            ✉️ Player Message
          </button>
          <button
            onClick={() => setSubTab("welcome-email")}
            style={{
              padding: "10px 16px",
              border: "none",
              background: "transparent",
              borderBottom: subTab === "welcome-email" ? "3px solid #2d6a4f" : "3px solid transparent",
              cursor: "pointer",
              fontSize: 15,
              fontWeight: subTab === "welcome-email" ? 700 : 400,
              color: subTab === "welcome-email" ? "#2d6a4f" : "#6b7280"
            }}
          >
            👋 Welcome Email
          </button>
          <button
            onClick={() => setSubTab("templates")}
            style={{
              padding: "10px 16px",
              border: "none",
              background: "transparent",
              borderBottom: subTab === "templates" ? "3px solid #2d6a4f" : "3px solid transparent",
              cursor: "pointer",
              fontSize: 15,
              fontWeight: subTab === "templates" ? 700 : 400,
              color: subTab === "templates" ? "#2d6a4f" : "#6b7280"
            }}
          >
            📝 Templates
          </button>
        </div>
      </div>

      {/* Content Based on Selected Sub-Tab */}
      {subTab === "player-message" && <PlayerMessageForm />}
      {subTab === "welcome-email" && <WelcomeEmailForm />}
      {subTab === "templates" && <MessageTemplates />}
    </div>
  );
};

// ============================================================================
// GHIN REMINDER SETTINGS COMPONENT
// Added: January 2026
// ============================================================================

const GHINReminderSettings = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ enabled: true, delayHours: 5 });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/settings/ghin-reminder`, {
        headers,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Error fetching GHIN settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/settings/ghin-reminder`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        addToast("GHIN reminder settings saved");
      } else {
        addToast("Failed to save settings", "error");
      }
    } catch (err) {
      console.error('Error saving GHIN settings:', err);
      addToast("Error saving settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ color: "#6b7280" }}>Loading settings...</div>;
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <input 
          type="checkbox" 
          checked={settings.enabled} 
          onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
          style={{ width: 16, height: 16 }}
        />
        <span style={{ fontWeight: 500 }}>Enable GHIN Posting Reminders</span>
      </label>
      
      <div>
        <Label style={{ marginBottom: 6, display: "block" }}>Hours after tee time to send reminder</Label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Input 
            type="number" 
            value={settings.delayHours}
            onChange={(e) => setSettings({ ...settings, delayHours: parseInt(e.target.value) || 5 })}
            min={1}
            max={24}
            style={{ width: 80 }}
          />
          <span style={{ color: "#6b7280", fontSize: 13 }}>hours (1-24)</span>
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
          This is calculated from the tee time start. A typical round takes ~4 hours, so 5 hours after tee time means ~1 hour after the round ends.
        </div>
      </div>
      
      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 12, marginTop: 4 }}>
        <Button onClick={saveSettings} disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// MESSAGE TEMPLATES COMPONENT
// This is the existing MessageManager logic extracted into its own component
// ============================================================================

const MessageTemplates = () => {
  const { addToast } = useToast();
  const { isReadOnly, isExpired } = useSubscription();
  const { data: templates, loading, updateItem } = useApiData('/message-templates');
  const { data: courses } = useApiData('/courses');
  const [selectedType, setSelectedType] = useState("same_day_reminder");
  const [editing, setEditing] = useState(false);
  const [working, setWorking] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // NEW: Send Test Email state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmailData, setTestEmailData] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Read-only mode click handler
  const handleReadOnlyClick = () => {
    addToast(isExpired 
      ? "Your free trial has ended. Please subscribe to continue making changes." 
      : "Your account is in read-only mode. Please reactivate your subscription to make changes.", "error");
  };

  const templateTypes = [
    { value: "same_day_reminder", label: "Same Day Reminder", icon: "📅" },
    { value: "custom_reminder", label: "Custom Reminder", icon: "⏰" },
    { value: "24hr_reminder", label: "24Hr Reminder", icon: "⏰" },
    { value: "ghin_reminder", label: "GHIN Posting Reminder", icon: "⛳" },
    { value: "welcome", label: "Welcome Email", icon: "👋" },
    { value: "waitlist_opening", label: "Waitlist Alert", icon: "🎉" },
    { value: "cancellation", label: "Cancellation Notice", icon: "❌" },
    { value: "new_tee_time", label: "New Tee Time", icon: "🆕" }
  ];

  const currentTemplate = templates.find(t => t.template_type === selectedType);

  const startEdit = () => {
    if (currentTemplate) {
      // Normalize null values to empty strings to prevent .length errors
      setWorking({ 
        ...currentTemplate,
        subject: currentTemplate.subject || '',
        email_body: currentTemplate.email_body || '',
        text_body: currentTemplate.text_body || ''
      });
      setEditing(true);
    }
  };

  const cancel = () => {
    setEditing(false);
    setWorking(null);
  };

  const save = async () => {
    if (!working) return;

    // Validate field lengths
    if (working.subject.length > 50) {
      addToast("Subject must be 50 characters or less", "error");
      return;
    }
    if (working.email_body.length > 3500) {
      addToast("Email body must be 3500 characters or less", "error");
      return;
    }
    if (working.text_body.length > 320) {
      addToast("Text body must be 320 characters or less (2 segments max)", "error");
      return;
    }

    try {
      await updateItem(working.template_type, {
        subject: working.subject,
        email_body: working.email_body,
        text_body: working.text_body
      });
      addToast("Message template updated successfully");
      cancel();
    } catch (err) {
      console.error('Error saving template:', err);
      addToast("Error saving message template", "error");
    }
  };

  const showPreview = async () => {
    if (!currentTemplate || courses.length === 0) {
      addToast("No template or courses available", "error");
      return;
    }

    setPreviewLoading(true);
    setPreviewing(true);

    try {
      // Use first course for preview
      const course = courses[0];
      
      // Get tomorrow's date for preview
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dateStr = tomorrow.toISOString().split('T')[0];
      
      // Call the preview endpoint
      // FIXED: Include JWT auth header for multi-tenant support
      const previewHeaders = { 'Content-Type': 'application/json' };
      const previewToken = getAuthToken();
      if (previewToken) {
        previewHeaders['Authorization'] = `Bearer ${previewToken}`;
      }
      
      const response = await fetch(`${API_URL}/test-message-preview`, {
        method: 'POST',
        headers: previewHeaders,
        credentials: 'include',
        body: JSON.stringify({
          template: currentTemplate,
          courseLocation: course.location,
          courseName: course.name,
          date: dateStr,
          time: '10:30'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate preview');
      }

      const data = await response.json();
      setPreviewData(data);
    } catch (err) {
      console.error('Error generating preview:', err);
      addToast("Error generating preview", "error");
      setPreviewing(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewing(false);
    setPreviewData(null);
  };

  // NEW: Open test email modal with preview
  const openTestModal = async () => {
    if (!currentTemplate || courses.length === 0) {
      addToast("No template or courses available", "error");
      return;
    }

    setPreviewLoading(true);

    try {
      const course = courses[0];
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dateStr = tomorrow.toISOString().split('T')[0];
      
      const previewHeaders = { 'Content-Type': 'application/json' };
      const previewToken = getAuthToken();
      if (previewToken) {
        previewHeaders['Authorization'] = `Bearer ${previewToken}`;
      }
      
      const response = await fetch(`${API_URL}/test-message-preview`, {
        method: 'POST',
        headers: previewHeaders,
        credentials: 'include',
        body: JSON.stringify({
          template: currentTemplate,
          courseLocation: course.location,
          courseName: course.name,
          date: dateStr,
          time: '10:30'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate preview');
      }

      const data = await response.json();
      setTestEmailData({
        subject: data.subject,
        emailBody: data.emailBody,
        textBody: data.textBody,
        templateType: currentTemplate.template_type,
        usingRealData: data.usingRealData || false,
        realDataSource: data.realDataSource || null
      });
      setShowTestModal(true);
    } catch (err) {
      console.error('Error generating test preview:', err);
      addToast("Error generating preview", "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  // NEW: Send test email
  const sendTestEmail = async () => {
    if (!testEmailData) return;

    setSendingTest(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/send-test-reminder`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          subject: testEmailData.subject,
          emailBody: testEmailData.emailBody,
          textBody: testEmailData.textBody,
          templateType: testEmailData.templateType
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        addToast(`Test email sent to ${result.email}`);
        setShowTestModal(false);
        setTestEmailData(null);
      } else {
        addToast(result.error || "Failed to send test email", "error");
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      addToast("Error sending test email", "error");
    } finally {
      setSendingTest(false);
    }
  };

  const closeTestModal = () => {
    setShowTestModal(false);
    setTestEmailData(null);
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>Loading message templates...</div>;
  }

  if (editing && working) {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>
            Edit {templateTypes.find(t => t.value === working.template_type)?.label}
          </div>
        </div>
        <Card>
          <div style={{ display: "grid", gap: 16 }}>
            {/* Subject Field */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <Label>Subject</Label>
                <span style={{ fontSize: 11, color: working.subject.length > 50 ? "#dc2626" : "#6b7280" }}>
                  {working.subject.length}/50 characters
                </span>
              </div>
              <Input 
                value={working.subject} 
                onChange={(e) => setWorking({ ...working, subject: e.target.value })} 
                maxLength={50}
                placeholder="Email subject line"
              />
            </div>

            {/* Email Body Field */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <Label>Email Body</Label>
                <span style={{ fontSize: 11, color: working.email_body.length > 3500 ? "#dc2626" : "#6b7280" }}>
                  {working.email_body.length}/3500 characters
                </span>
              </div>
              <textarea
                value={working.email_body}
                onChange={(e) => setWorking({ ...working, email_body: e.target.value })}
                maxLength={3500}
                placeholder="Email message body"
                rows={12}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 14,
                  fontFamily: "inherit",
                  resize: "vertical"
                }}
              />
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                Available placeholders: {"{player_name}"}, {"{player_first_name}"}, {"{course_name}"}, {"{date}"}, {"{time}"}, {"{player_portal_url}"}, {"{weather}"}, {"{temperature}"}, {"{conditions}"}, {"{sunrise}"}, {"{sunset}"}, {"{daylight_hours}"}
                {(working.template_type === 'same_day_reminder' || working.template_type === '24hr_reminder' || working.template_type === 'custom_reminder') && (
                  <>
                    <span style={{ display: 'block', marginTop: 4, color: '#2d6a4f', fontWeight: 500 }}>
                      📋 Tee Sheet placeholders (reminders only): {"{tee_sheet}"}, {"{tee_sheet_with_hcp}"}
                      <br />
                      📊 Live Leaderboard (reminders only — inserting this shares that round's live leaderboard): {"{leaderboard_url}"}
                    </span>
                    <span style={{ display: 'block', marginTop: 4, color: '#4338ca', fontWeight: 500 }}>
                      🎯 Target Score placeholders (reminders only): {"{handicap_index}"}, {"{course_hcp}"}, {"{course_par}"}, {"{target_score}"}, {"{preferred_tee}"}
                    </span>
                  </>
                )}
                {working.template_type === 'ghin_reminder' && (
                  <span style={{ display: 'block', marginTop: 4, color: '#2d6a4f', fontWeight: 500 }}>
                    ⛳ GHIN placeholders: {"{ghin_link}"} (styled button), {"{ghin_url}"} (plain URL), {"{handicap_guide_link}"} (styled link), {"{handicap_guide_url}"} (plain URL)
                  </span>
                )}
              </div>
            </div>

            {/* Text Body Field */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <Label>Text Message Body</Label>
                <span style={{ fontSize: 11, color: working.text_body.length > 320 ? "#dc2626" : working.text_body.length > 160 ? "#f59e0b" : "#6b7280" }}>
                  {working.text_body.length}/320 characters{working.text_body.length > 160 ? ' (2 segments)' : ''}
                </span>
              </div>
              <textarea
                value={working.text_body}
                onChange={(e) => setWorking({ ...working, text_body: e.target.value })}
                maxLength={320}
                placeholder="Text message (up to 320 chars / 2 segments)"
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 14,
                  fontFamily: "inherit",
                  resize: "vertical"
                }}
              />
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                Keep text messages short and concise. Placeholders: {"{player_name}"}, {"{player_first_name}"}, {"{course_name}"}, {"{date}"}, {"{time}"}, {"{player_portal_url}"}, {"{weather}"}, {"{temperature}"}, {"{conditions}"}, {"{sunrise}"}, {"{sunset}"}, {"{daylight_hours}"}
                {(working.template_type === 'same_day_reminder' || working.template_type === '24hr_reminder' || working.template_type === 'custom_reminder') && (
                  <span style={{ display: 'block', marginTop: 4, color: '#4338ca', fontWeight: 500 }}>
                    🎯 Target Score: {"{target_score}"}, {"{course_hcp}"}, {"{handicap_index}"}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
              <Button variant="outline" onClick={cancel}>Cancel</Button>
              <Button onClick={save}>Save Template</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ fontWeight: 800, fontSize: 18 }}>System Message Templates</div>
      
      {/* Template Type Selector */}
      <Card style={{ background: "#f9fafb" }}>
        <Label style={{ marginBottom: 8, display: "block" }}>Select Message Type</Label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
          {templateTypes.map(type => (
            <button
              key={type.value}
              onClick={() => setSelectedType(type.value)}
              style={{
                padding: "12px 16px",
                border: selectedType === type.value ? "2px solid #2d6a4f" : "1px solid #e5e7eb",
                background: selectedType === type.value ? "#f0fdf4" : "#fff",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 14,
                fontWeight: selectedType === type.value ? 600 : 400,
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 8
              }}
            >
              <span style={{ fontSize: 20 }}>{type.icon}</span>
              <span>{type.label}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Template Display */}
      {currentTemplate && (
        <Card>
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {templateTypes.find(t => t.value === selectedType)?.icon} {templateTypes.find(t => t.value === selectedType)?.label}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {(selectedType === 'same_day_reminder' || selectedType === '24hr_reminder' || selectedType === 'ghin_reminder' || selectedType === 'custom_reminder') && (
                  <Button variant="outline" onClick={openTestModal} disabled={previewLoading} style={{ background: '#fff', borderColor: '#d1d5db' }}>
                    {previewLoading ? "Loading..." : "📧 Send Test"}
                  </Button>
                )}
                <Button variant="outline" onClick={showPreview} disabled={previewLoading} style={{ background: '#fff', borderColor: '#d1d5db' }}>
                  {previewLoading ? "Loading..." : "👁️ Preview"}
                </Button>
                <Button 
                  variant="outline"
                  onClick={isReadOnly ? handleReadOnlyClick : startEdit}
                  disabled={isReadOnly}
                  style={{ 
                    background: isReadOnly ? "#e5e7eb" : "#fff", 
                    borderColor: '#d1d5db',
                    cursor: isReadOnly ? "not-allowed" : "pointer",
                    opacity: isReadOnly ? 0.6 : 1
                  }}
                >
                  ✏️ Edit
                </Button>
              </div>
            </div>

            <div>
              <Label style={{ marginBottom: 6, display: "block", fontSize: 13, fontWeight: 600 }}>Subject</Label>
              <div style={{ padding: 12, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 14 }}>
                {currentTemplate.subject}
              </div>
            </div>

            <div>
              <Label style={{ marginBottom: 6, display: "block", fontSize: 13, fontWeight: 600 }}>Email Body</Label>
              <div style={{ padding: 12, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 14, whiteSpace: "pre-wrap" }}>
                {currentTemplate.email_body}
              </div>
            </div>

            <div>
              <Label style={{ marginBottom: 6, display: "block", fontSize: 13, fontWeight: 600 }}>Text Message Body</Label>
              <div style={{ padding: 12, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 14 }}>
                {currentTemplate.text_body}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* GHIN Reminder Settings */}
      {selectedType === 'ghin_reminder' && (
        <Card style={{ marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>⏰ GHIN Reminder Timing</div>
          </div>
          <GHINReminderSettings />
        </Card>
      )}

      {/* Preview Modal */}
      {previewing && previewData && (
        <Modal title="Message Preview" onClose={closePreview}>
          <div style={{ display: "grid", gap: 16 }}>
            <div>
              <Label style={{ marginBottom: 6, display: "block", fontWeight: 600 }}>Subject:</Label>
              <div style={{ padding: 10, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4 }}>
                {previewData.subject}
              </div>
            </div>

            <div>
              <Label style={{ marginBottom: 6, display: "block", fontWeight: 600 }}>Email Body:</Label>
              <div 
                style={{ padding: 10, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4 }}
                dangerouslySetInnerHTML={{ __html: previewData.emailBody.replace(/\n/g, '<br>') }}
              />
            </div>

            <div>
              <Label style={{ marginBottom: 6, display: "block", fontWeight: 600 }}>Text Message Body:</Label>
              <div style={{ padding: 10, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4 }}>
                {previewData.textBody}
              </div>
            </div>

            <div style={{ textAlign: "right" }}>
              <Button variant="outline" onClick={closePreview}>Close</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Send Test Email Modal */}
      {showTestModal && testEmailData && (
        <Modal title="📧 Send Test Email" onClose={closeTestModal}>
          <div style={{ display: "grid", gap: 16 }}>
            {testEmailData.usingRealData ? (
              <div style={{ padding: 12, background: "#dcfce7", border: "1px solid #22c55e", borderRadius: 6, fontSize: 13 }}>
                <strong>✅ Using Real Data:</strong> {testEmailData.realDataSource}
                <div style={{ fontSize: 11, color: "#166534", marginTop: 4 }}>
                  Preview uses your actual player profile and next upcoming tee time. This is exactly what players will see!
                </div>
              </div>
            ) : (
              <div style={{ padding: 12, background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, fontSize: 13 }}>
                <strong>⚠️ Sample Data:</strong> No upcoming tee time or player profile found.
                <div style={{ fontSize: 11, color: "#92400e", marginTop: 4 }}>
                  To see real data, ensure you have a published tee time and your email matches a player in the database.
                </div>
              </div>
            )}
            <div style={{ padding: 8, background: "#f3f4f6", borderRadius: 4, fontSize: 12, color: "#4b5563" }}>
              📬 Test email will be sent to your registered email with "[TEST]" in the subject.
            </div>

            <div>
              <Label style={{ marginBottom: 6, display: "block", fontWeight: 600 }}>Subject:</Label>
              <Input
                value={testEmailData.subject}
                onChange={(e) => setTestEmailData({ ...testEmailData, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>

            <div>
              <Label style={{ marginBottom: 6, display: "block", fontWeight: 600 }}>Email Body:</Label>
              <textarea
                value={testEmailData.emailBody}
                onChange={(e) => setTestEmailData({ ...testEmailData, emailBody: e.target.value })}
                rows={12}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 14,
                  fontFamily: "inherit",
                  resize: "vertical"
                }}
              />
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                You can edit the content before sending. HTML formatting will be preserved.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
              <Button variant="outline" onClick={closeTestModal} disabled={sendingTest}>Cancel</Button>
              <Button 
                onClick={sendTestEmail} 
                disabled={sendingTest}
                style={{ background: sendingTest ? '#9ca3af' : '#f59e0b', borderColor: sendingTest ? '#9ca3af' : '#f59e0b' }}
              >
                {sendingTest ? "Sending..." : "📧 Send Test Email"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
const ReportsView = ({ initialScorecardRound = null, onConsumeInitialScorecardRound } = {}) => {
  const { data: players } = useApiData('/players');
  const { data: teeTimes } = useApiData('/teetimes');
  const { data: courses } = useApiData('/courses');
  const { isMobile } = useContext(MobileContext);
  const [reportType, setReportType] = useState(initialScorecardRound ? "scorecard" : "summary");
  const [scorecardTeetimeId, setScorecardTeetimeId] = useState(initialScorecardRound || null); // anchor: which round is picked
  // Multi-tee-time rounds (leagues that publish several tee times for one event):
  // the report aggregates all checked tee times. Defaults to all tee times sharing
  // the anchor's course+date; the organizer can uncheck outliers. For single-tee
  // rounds this is just [anchor] and no checkboxes show.
  const [scorecardSelectedIds, setScorecardSelectedIds] = useState(initialScorecardRound ? [initialScorecardRound] : []);

  // If arriving from the Scoring screen with a round to show, open directly on
  // the Scorecard Report for that round, then clear the hand-off so it doesn't
  // re-trigger on later in-tab navigation.
  useEffect(() => {
    if (initialScorecardRound) {
      setReportType("scorecard");
      setScorecardTeetimeId(initialScorecardRound);
      setScorecardSelectedIds([initialScorecardRound]);
      if (onConsumeInitialScorecardRound) onConsumeInitialScorecardRound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScorecardRound]);

  // Phase 2 Scoring gate (May 14, 2026) + v32.1 fix:
  // The Scorecard Report button was visible to ALL orgs since v32.1 — it rendered
  // for orgs with scoring_enabled = false but returned no data (the v32.1 bug).
  // Fix: hide entirely when scoring_enabled = false; lock-with-LockBadge when tier
  // doesn't include scoring. Two state pieces drive this:
  //   - scoringEnabled: from /scoring/status (admin operational flag)
  //   - scoringLocked: from useOrgTier + canUseFeature('scoring') (commercial gate).
  //     Defaults to LOCKED while tier is loading — mirrors SMS pattern at line ~2683.
  const [scoringEnabled, setScoringEnabled] = useState(false);
  const { tier: orgTier, loading: tierLoading } = useOrgTier();
  const scoringLocked = tierLoading || !orgTier || !canUseFeature(orgTier, 'scoring');

  useEffect(() => {
    const checkScoring = async () => {
      try {
        const resp = await fetch(`${API_URL}/scoring/status`, { credentials: 'include', headers: (() => { const h = {}; const t = getAuthToken(); if (t) h['Authorization'] = `Bearer ${t}`; return h; })() });
        if (resp.ok) {
          const data = await resp.json();
          setScoringEnabled(data.scoring_enabled || false);
        }
      } catch (e) { /* fail closed — button stays hidden */ }
    };
    checkScoring();
  }, []);

  const [dateRange, setDateRange] = useState(() => {
    // Load saved date range from localStorage, default to "all" if not found
    const savedDateRange = localStorage.getItem('reportsDateRange');
    return savedDateRange || "all";
  }); // all, future, past, last30, last90, custom
  const [customStartDate, setCustomStartDate] = useState(() => {
    // Load saved custom start date from localStorage
    return localStorage.getItem('reportsCustomStartDate') || "";
  });
  const [customEndDate, setCustomEndDate] = useState(() => {
    // Load saved custom end date from localStorage
    return localStorage.getItem('reportsCustomEndDate') || "";
  });

  // Save dateRange to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('reportsDateRange', dateRange);
  }, [dateRange]);

  // Save custom dates to localStorage whenever they change
  useEffect(() => {
    if (customStartDate) {
      localStorage.setItem('reportsCustomStartDate', customStartDate);
    }
  }, [customStartDate]);

  useEffect(() => {
    if (customEndDate) {
      localStorage.setItem('reportsCustomEndDate', customEndDate);
    }
  }, [customEndDate]);

  // Calculate date range
  const getDateRangeFilter = () => {
    const now = Date.now();
    const day = 86400000;
    
    if (dateRange === "last30") return (ts) => ts >= now - (30 * day) && ts <= now;
    if (dateRange === "last90") return (ts) => ts >= now - (90 * day) && ts <= now;
    if (dateRange === "future") return (ts) => ts >= now;
    if (dateRange === "past") return (ts) => ts < now;
    if (dateRange === "custom") {
      if (!customStartDate && !customEndDate) return () => true;
      // Parse dates manually to avoid Safari/iOS timezone issues
      let startTs = 0;
      let endTs = Number.MAX_SAFE_INTEGER;
      if (customStartDate) {
        const [sy, sm, sd] = customStartDate.split('-').map(Number);
        startTs = new Date(sy, sm - 1, sd, 0, 0, 0).getTime();
      }
      if (customEndDate) {
        const [ey, em, ed] = customEndDate.split('-').map(Number);
        endTs = new Date(ey, em - 1, ed, 23, 59, 59).getTime();
      }
      return (ts) => ts >= startTs && ts <= endTs;
    }
    return () => true; // all
  };

  const filteredTeeTimes = useMemo(() => {
    const filter = getDateRangeFilter();
    return teeTimes.filter(t => filter(utils.getDateTimestamp(t.date, t.time)));
  }, [teeTimes, dateRange, customStartDate, customEndDate]);

  // Summary Statistics
  const summaryStats = useMemo(() => {
    const totalSlots = filteredTeeTimes.reduce((sum, t) => sum + utils.toInt(t.slots), 0);
    const filledSlots = filteredTeeTimes.reduce((sum, t) => sum + utils.safePlayerCount(t.players), 0);
    const occupancyRate = totalSlots > 0 ? ((filledSlots / totalSlots) * 100).toFixed(1) : 0;
    
    return {
      totalTeeTimes: filteredTeeTimes.length,
      totalSlots,
      filledSlots,
      availableSlots: totalSlots - filledSlots,
      occupancyRate,
      totalPlayers: players.length,
      activePlayers: players.filter(p => p.active).length,
      clubMembers: players.filter(p => p.clubMember || p.club_member).length,
    };
  }, [filteredTeeTimes, players]);

  // Player Activity Report
  const playerActivity = useMemo(() => {
    const activity = {};
    
    // Create email to player name lookup
    const playerByEmail = new Map(players.map(p => [p.email, p.name]));
    
    filteredTeeTimes.forEach(t => {
      (t.players || []).forEach(playerData => {
        // Handle both cases: playerData could be an email string OR a player object
        let playerEmail, playerName;
        
        if (typeof playerData === 'string') {
          // It's an email string (correct format)
          playerEmail = playerData;
          playerName = playerByEmail.get(playerEmail) || playerEmail;
        } else if (playerData && typeof playerData === 'object') {
          // It's a player object (legacy format) - extract email and name
          playerEmail = playerData.email || '';
          playerName = playerData.name || playerEmail;
        } else {
          // Invalid data, skip
          return;
        }
        
        if (!playerEmail) return; // Skip if no email
        
        if (!activity[playerEmail]) {
          activity[playerEmail] = { name: playerName, email: playerEmail, count: 0, teeTimes: [] };
        }
        activity[playerEmail].count++;
        activity[playerEmail].teeTimes.push({ date: t.date, time: t.time, course: t.course });
      });
    });
    
    return Object.values(activity).sort((a, b) => b.count - a.count);
  }, [filteredTeeTimes, players]);

  // Course Utilization
  const courseUtilization = useMemo(() => {
    const utilization = {};
    
    filteredTeeTimes.forEach(t => {
      if (!utilization[t.course]) {
        utilization[t.course] = { 
          course: t.course, 
          teeTimes: 0, 
          totalSlots: 0, 
          filledSlots: 0 
        };
      }
      utilization[t.course].teeTimes++;
      utilization[t.course].totalSlots += utils.toInt(t.slots);
      utilization[t.course].filledSlots += utils.safePlayerCount(t.players);
    });
    
    return Object.values(utilization).map(u => ({
      ...u,
      occupancyRate: u.totalSlots > 0 ? ((u.filledSlots / u.totalSlots) * 100).toFixed(1) : 0
    })).sort((a, b) => b.teeTimes - a.teeTimes);
  }, [filteredTeeTimes]);

  // Monthly trends
  const monthlyTrends = useMemo(() => {
    const trends = {};
    
    filteredTeeTimes.forEach(t => {
      if (!t.date) return; // Skip if no date
      
      // Parse date components manually to avoid Safari/iOS timezone issues
      const dateStr = typeof t.date === 'string' ? t.date.split('T')[0] : '';
      const [year, month] = dateStr.split('-').map(Number);
        
      if (!year || !month) return; // Skip invalid dates
      
      const monthKey = `${year}-${String(month).padStart(2, '0')}`;
      
      if (!trends[monthKey]) {
        trends[monthKey] = { 
          month: monthKey, 
          teeTimes: 0, 
          totalSlots: 0, 
          filledSlots: 0,
          uniquePlayers: new Set()
        };
      }
      trends[monthKey].teeTimes++;
      trends[monthKey].totalSlots += utils.toInt(t.slots);
      trends[monthKey].filledSlots += utils.safePlayerCount(t.players);
      (t.players || []).forEach(p => trends[monthKey].uniquePlayers.add(p));
    });
    
    return Object.values(trends).map(m => ({
      month: m.month,
      teeTimes: m.teeTimes,
      occupancyRate: m.totalSlots > 0 ? ((m.filledSlots / m.totalSlots) * 100).toFixed(1) : 0,
      uniquePlayers: m.uniquePlayers.size
    })).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredTeeTimes]);

  const StatCard = ({ title, value, subtitle, icon }) => (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 32 }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#111827" }}>{value}</div>
          {subtitle && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
    </Card>
  );

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Report Controls */}
      <Card>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}>Report Type:</span>
            <Button variant={reportType === "summary" ? "default" : "outline"} onClick={() => setReportType("summary")} style={{ fontSize: 13, padding: "6px 12px" }}>Summary</Button>
            <Button variant={reportType === "players" ? "default" : "outline"} onClick={() => setReportType("players")} style={{ fontSize: 13, padding: "6px 12px" }}>Player Activity</Button>
            <Button variant={reportType === "courses" ? "default" : "outline"} onClick={() => setReportType("courses")} style={{ fontSize: 13, padding: "6px 12px" }}>Course Usage</Button>
            <Button variant={reportType === "trends" ? "default" : "outline"} onClick={() => setReportType("trends")} style={{ fontSize: 13, padding: "6px 12px" }}>Monthly Trends</Button>
            {/* Scorecard Report — Phase 2 Scoring gate + v32.1 fix (May 14, 2026).
                Hide entirely if admin hasn't enabled scoring (v32.1 bug: button was visible to
                all orgs but rendered no data for non-scoring orgs).
                For Free/Starter (tier doesn't include scoring): show locked variant as upsell. */}
            {scoringEnabled && !scoringLocked && (
              <Button variant={reportType === "scorecard" ? "default" : "outline"} onClick={() => setReportType("scorecard")} style={{ fontSize: 13, padding: "6px 12px" }}>⛳ Scorecard Report</Button>
            )}
            {scoringEnabled && scoringLocked && (
              <Button
                variant="outline"
                onClick={() => triggerUpgradeModal({ feature: 'scoring', requiredTier: 'starter' })}
                style={{ fontSize: 13, padding: "6px 12px", display: "inline-flex", alignItems: "center", gap: 6, color: "#9ca3af" }}
                title="Your current plan doesn't include Scoring. Click to see upgrade options."
              >
                ⛳ Scorecard Report
                <LockBadge feature="scoring" requiredTier="starter" style={{ fontSize: 10, padding: '1px 6px' }} />
              </Button>
            )}
          </div>
          
          {/* Date Range Buttons Row */}
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}>Date Range:</span>
            <Button 
              variant={dateRange === "all" ? "default" : "outline"}
              onClick={() => setDateRange("all")}
              style={{ fontSize: 13, padding: "6px 12px" }}
            >
              All Time
            </Button>
            <Button 
              variant={dateRange === "future" ? "default" : "outline"}
              onClick={() => setDateRange("future")}
              style={{ fontSize: 13, padding: "6px 12px" }}
            >
              Future
            </Button>
            <Button 
              variant={dateRange === "past" ? "default" : "outline"}
              onClick={() => setDateRange("past")}
              style={{ fontSize: 13, padding: "6px 12px" }}
            >
              Past
            </Button>
            <Button 
              variant={dateRange === "last30" ? "default" : "outline"}
              onClick={() => setDateRange("last30")}
              style={{ fontSize: 13, padding: "6px 12px" }}
            >
              Last 30 Days
            </Button>
            <Button 
              variant={dateRange === "last90" ? "default" : "outline"}
              onClick={() => setDateRange("last90")}
              style={{ fontSize: 13, padding: "6px 12px" }}
            >
              Last 90 Days
            </Button>
            <Button 
              variant={dateRange === "custom" ? "default" : "outline"}
              onClick={() => setDateRange("custom")}
              style={{ fontSize: 13, padding: "6px 12px" }}
            >
              📅 Custom Range
            </Button>
          </div>
          
          {/* Custom Date Range Inputs */}
          {dateRange === "custom" && (
            <div style={{ display: "flex", gap: 12, alignItems: "center", paddingTop: 8, borderTop: "1px solid #e5e7eb" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Custom Range:</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div>
                  <Label style={{ fontSize: 11, marginBottom: 4 }}>Start Date</Label>
                  <Input 
                    type="date" 
                    value={customStartDate} 
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    style={{ fontSize: 13, padding: "6px 10px" }}
                  />
                </div>
                <span style={{ color: "#9ca3af", marginTop: 20 }}>to</span>
                <div>
                  <Label style={{ fontSize: 11, marginBottom: 4 }}>End Date</Label>
                  <Input 
                    type="date" 
                    value={customEndDate} 
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    style={{ fontSize: 13, padding: "6px 10px" }}
                  />
                </div>
                {(customStartDate || customEndDate) && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setCustomStartDate("");
                      setCustomEndDate("");
                      localStorage.removeItem('reportsCustomStartDate');
                      localStorage.removeItem('reportsCustomEndDate');
                    }}
                    style={{ fontSize: 12, padding: "6px 10px", marginTop: 20 }}
                  >
                    Clear
                  </Button>
                )}
              </div>
              {customStartDate && customEndDate && customStartDate > customEndDate && (
                <div style={{ 
                  fontSize: 12, 
                  color: "#dc2626", 
                  background: "#fee2e2", 
                  padding: "4px 10px", 
                  borderRadius: 6,
                  marginTop: 20
                }}>
                  ⚠️ Start date must be before end date
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Summary Report */}
      {reportType === "summary" && (
        <>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Summary Statistics</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12 }}>
            <StatCard title="Total Tee Times" value={summaryStats.totalTeeTimes} icon="⛳" />
            <StatCard title="Occupancy Rate" value={`${summaryStats.occupancyRate}%`} subtitle={`${summaryStats.filledSlots} of ${summaryStats.totalSlots} slots`} icon="📊" />
            <StatCard title="Total Players" value={summaryStats.totalPlayers} subtitle={`${summaryStats.activePlayers} active`} icon="👥" />
            <StatCard title="Club Members" value={summaryStats.clubMembers} subtitle={`${((summaryStats.clubMembers / summaryStats.totalPlayers) * 100).toFixed(0)}%`} icon="🏌️" />
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
            <Card>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Quick Stats</div>
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <span style={{ color: "#6b7280" }}>Available Slots</span>
                  <span style={{ fontWeight: 600 }}>{summaryStats.availableSlots}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <span style={{ color: "#6b7280" }}>Filled Slots</span>
                  <span style={{ fontWeight: 600 }}>{summaryStats.filledSlots}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <span style={{ color: "#6b7280" }}>Avg Players/Tee Time</span>
                  <span style={{ fontWeight: 600 }}>{summaryStats.totalTeeTimes > 0 ? (summaryStats.filledSlots / summaryStats.totalTeeTimes).toFixed(1) : 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                  <span style={{ color: "#6b7280" }}>Total Courses</span>
                  <span style={{ fontWeight: 600 }}>{courses.length}</span>
                </div>
              </div>
            </Card>
            
            <Card>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Top 5 Most Active Players</div>
              <div style={{ display: "grid", gap: 6 }}>
                {playerActivity.slice(0, 5).map((pa, idx) => (
                  <div key={pa.email} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                    <Badge style={{ background: idx === 0 ? "#fbbf24" : "#e5e7eb", color: idx === 0 ? "#78350f" : "#374151", minWidth: 24 }}>{idx + 1}</Badge>
                    <PlayerAvatar name={pa.name} photoUrl={pa.photoUrl} />
                    <span style={{ flex: 1, fontSize: 14 }}>{pa.name}</span>
                    <Badge>{pa.count} {pa.count === 1 ? 'round' : 'rounds'}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Player Activity Report */}
      {reportType === "players" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 18 }}>Player Activity Report</div>
            <Button 
              variant="outline"
              onClick={() => {
                const csv = [
                  ['Player Name', 'Total Rounds', 'Last Played'].join(','),
                  ...playerActivity.map(pa => [
                    pa.name,
                    pa.count,
                    pa.teeTimes.length > 0 ? pa.teeTimes[pa.teeTimes.length - 1].date : 'N/A'
                  ].join(','))
                ].join('\n');
                exportUtils.downloadCSV(csv, `player-activity-${new Date().toISOString().split('T')[0]}.csv`);
              }}
            >
              📥 Export
            </Button>
          </div>
          <Card>
            <div style={{ display: "grid", gap: 1 }}>
              {playerActivity.map((pa, idx) => (
                <div key={pa.email} style={{ display: "grid", gridTemplateColumns: "50px 1fr 100px", gap: 12, padding: 12, alignItems: "center", background: idx % 2 === 0 ? "#f9fafb" : "#fff" }}>
                  <div style={{ textAlign: "center", fontWeight: 600, color: "#6b7280" }}>#{idx + 1}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <PlayerAvatar name={pa.name} photoUrl={pa.photoUrl} />
                    <div>
                      <div style={{ fontWeight: 600 }}>{pa.name}</div>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>Last: {pa.teeTimes.length > 0 ? utils.formatDate(pa.teeTimes[pa.teeTimes.length - 1].date) : 'N/A'}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <Badge style={{ background: "#2d6a4f", color: "#fff" }}>{pa.count} {pa.count === 1 ? 'round' : 'rounds'}</Badge>
                  </div>
                </div>
              ))}
              {playerActivity.length === 0 && (
                <div style={{ padding: 48, textAlign: "center", color: "#9ca3af" }}>No player activity for selected date range</div>
              )}
            </div>
          </Card>
        </>
      )}

      {/* Course Utilization Report */}
      {reportType === "courses" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 18 }}>Course Utilization Report</div>
            <Button 
              variant="outline"
              onClick={() => {
                const csv = [
                  ['Course', 'Tee Times', 'Total Slots', 'Filled Slots', 'Occupancy Rate'].join(','),
                  ...courseUtilization.map(cu => [
                    cu.course,
                    cu.teeTimes,
                    cu.totalSlots,
                    cu.filledSlots,
                    cu.occupancyRate + '%'
                  ].join(','))
                ].join('\n');
                exportUtils.downloadCSV(csv, `course-utilization-${new Date().toISOString().split('T')[0]}.csv`);
              }}
            >
              📥 Export
            </Button>
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            {courseUtilization.map((cu) => (
              <Card key={cu.course}>
                <div style={{ display: "grid", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{cu.course}</div>
                    <Badge style={{ background: "#2d6a4f", color: "#fff" }}>{cu.teeTimes} tee times</Badge>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Total Slots</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{cu.totalSlots}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Filled Slots</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "#2d6a4f" }}>{cu.filledSlots}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Occupancy Rate</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: cu.occupancyRate >= 75 ? "#15803d" : cu.occupancyRate >= 50 ? "#f59e0b" : "#dc2626" }}>{cu.occupancyRate}%</div>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${cu.occupancyRate}%`, background: cu.occupancyRate >= 75 ? "#15803d" : cu.occupancyRate >= 50 ? "#f59e0b" : "#dc2626", transition: "width 0.3s" }}></div>
                  </div>
                </div>
              </Card>
            ))}
            {courseUtilization.length === 0 && (
              <Card style={{ padding: 48, textAlign: "center" }}>
                <div style={{ color: "#9ca3af" }}>No course data for selected date range</div>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Monthly Trends Report */}
      {reportType === "trends" && (        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 18 }}>Monthly Trends</div>
            <Button 
              variant="outline"
              onClick={() => {
                const csv = [
                  ['Month', 'Tee Times', 'Occupancy Rate', 'Unique Players'].join(','),
                  ...monthlyTrends.map(mt => [
                    mt.month,
                    mt.teeTimes,
                    mt.occupancyRate + '%',
                    mt.uniquePlayers
                  ].join(','))
                ].join('\n');
                exportUtils.downloadCSV(csv, `monthly-trends-${new Date().toISOString().split('T')[0]}.csv`);
              }}
            >
              📥 Export
            </Button>
          </div>
          <Card>
            <div style={{ display: "grid", gap: 1 }}>
              {monthlyTrends.map((mt, idx) => (
                isMobile ? (
                  <div key={mt.month} style={{ padding: 12, background: idx % 2 === 0 ? "#f9fafb" : "#fff", borderRadius: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{mt.month}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>Tee Times</div>
                        <div style={{ fontWeight: 600 }}>{mt.teeTimes}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>Occupancy</div>
                        <div style={{ fontWeight: 600, color: mt.occupancyRate >= 75 ? "#15803d" : mt.occupancyRate >= 50 ? "#f59e0b" : "#dc2626" }}>{mt.occupancyRate}%</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>Players</div>
                        <div style={{ fontWeight: 600 }}>{mt.uniquePlayers}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                <div key={mt.month} style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr 1fr", gap: 12, padding: 12, alignItems: "center", background: idx % 2 === 0 ? "#f9fafb" : "#fff", borderRadius: 6 }}>
                  <div style={{ fontWeight: 600 }}>{mt.month}</div>
                  <div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>Tee Times</div>
                    <div style={{ fontWeight: 600 }}>{mt.teeTimes}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>Occupancy</div>
                    <div style={{ fontWeight: 600, color: mt.occupancyRate >= 75 ? "#15803d" : mt.occupancyRate >= 50 ? "#f59e0b" : "#dc2626" }}>{mt.occupancyRate}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>Unique Players</div>
                    <div style={{ fontWeight: 600 }}>{mt.uniquePlayers}</div>
                  </div>
                </div>
                )
              ))}
              {monthlyTrends.length === 0 && (
                <div style={{ padding: 48, textAlign: "center", color: "#9ca3af" }}>No trend data available</div>
              )}
            </div>
          </Card>
        </>
      )}

      {/* ── Scorecard Report ──────────────────────────────────────────── */}
      {/* Defense-in-depth (May 14, 2026): button is hidden when scoringEnabled is false or
          scoringLocked is true, but guard the render block too in case reportType state is
          set to "scorecard" by some other code path (URL manipulation, stale state). */}
      {reportType === "scorecard" && scoringEnabled && !scoringLocked && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 18 }}>⛳ Scorecard Report</div>
          </div>
          <Card>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Select Round</label>
              <select
                value={scorecardTeetimeId || ""}
                onChange={e => {
                  const id = e.target.value || null;
                  setScorecardTeetimeId(id);
                  if (!id) { setScorecardSelectedIds([]); return; }
                  // Preselect all tee times sharing this one's course + date (the "round").
                  const anchor = teeTimes.find(t => t.id === id);
                  if (!anchor) { setScorecardSelectedIds([id]); return; }
                  const aDate = String(anchor.date || '').split('T')[0];
                  const aCourse = anchor.course_id || anchor.course;
                  const siblings = teeTimes.filter(t =>
                    String(t.date || '').split('T')[0] === aDate &&
                    (t.course_id || t.course) === aCourse
                  );
                  setScorecardSelectedIds(siblings.map(t => t.id));
                }}
                style={{ width: "100%", maxWidth: 480, padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, background: "#fff" }}
              >
                <option value="">— Choose a tee time —</option>
                {(() => {
                  // Scoring only applies to played rounds → exclude future dates.
                  // Date-only compare (local) so today's rounds stay visible all day.
                  const now = new Date();
                  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
                  return teeTimes
                    .filter(tt => {
                      const d = String(tt.date || '').split('T')[0];
                      return d && d <= todayStr;
                    })
                    .slice()
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .slice(0, 20)   // cap: 20 most recent played rounds keeps the list scannable
                    .map(tt => {
                      const courseName = tt.course_name || courses.find(c => c.id === tt.course_id)?.name || 'Unknown Course';
                      return (
                        <option key={tt.id} value={tt.id}>
                          {new Date(tt.date.split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                          {tt.time ? ` ${tt.time}` : ''} — {courseName}
                        </option>
                      );
                    });
                })()}
              </select>
              {teeTimes.length === 0 && (
                <p style={{ margin: "8px 0 0", fontSize: 13, color: "#9ca3af" }}>No tee times found.</p>
              )}
            </div>

            {/* Multi-tee-time round: show checkboxes ONLY when the picked round has 2+
                tee times on the same course+date. Single-tee rounds see no change. */}
            {scorecardTeetimeId && (() => {
              const anchor = teeTimes.find(t => t.id === scorecardTeetimeId);
              if (!anchor) return null;
              const aDate = String(anchor.date || '').split('T')[0];
              const aCourse = anchor.course_id || anchor.course;
              const siblings = teeTimes
                .filter(t => String(t.date || '').split('T')[0] === aDate && (t.course_id || t.course) === aCourse)
                .sort((a, b) => String(a.time || '').localeCompare(String(b.time || '')));
              if (siblings.length < 2) return null; // single tee time → no checkboxes
              return (
                <div style={{ marginBottom: 12, padding: 12, border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      This round has {siblings.length} tee times — include these in the report:
                    </span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setScorecardSelectedIds(siblings.map(t => t.id))}
                        style={{ fontSize: 11, padding: "3px 10px", border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", cursor: "pointer" }}>Select all</button>
                      <button onClick={() => setScorecardSelectedIds(scorecardTeetimeId ? [scorecardTeetimeId] : [])}
                        style={{ fontSize: 11, padding: "3px 10px", border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", cursor: "pointer" }}>Just this one</button>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 6 }}>
                    {siblings.map(t => {
                      const checked = scorecardSelectedIds.includes(t.id);
                      return (
                        <label key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12, background: "#fff", cursor: "pointer" }}>
                          <input type="checkbox" checked={checked}
                            onChange={e => {
                              setScorecardSelectedIds(prev =>
                                e.target.checked ? [...prev, t.id] : prev.filter(x => x !== t.id)
                              );
                            }} />
                          {t.time || 'No time'}
                        </label>
                      );
                    })}
                  </div>
                  {scorecardSelectedIds.length === 0 && (
                    <p style={{ margin: "8px 0 0", fontSize: 11, color: "#dc2626" }}>Select at least one tee time to view the report.</p>
                  )}
                </div>
              );
            })()}

            {scorecardSelectedIds.length > 0 && (
              <ScorecardAllPlayers
                teetimeIds={scorecardSelectedIds}
              />
            )}
            {scorecardSelectedIds.length === 0 && (
              <div style={{ padding: 32, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                Select a round above to view the full-field scorecard.
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

const SystemGeneratedMessages = () => {
  const { addToast } = useToast();
  const [systemTab, setSystemTab] = useState("reminder24hr");
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Default templates
  const defaultTemplates = {
    reminder24hr: {
      subject: "⛳ Tee Time Reminder - Tomorrow at {time}",
      body: "Hi {player_name},\n\nThis is a friendly reminder that you have a tee time tomorrow:\n\nCourse: {course_name}\nDate: {date}\nTime: {time}\nPlayers: {player_list}\n\nSee you on the course!\n\nTee Time Organizer\nTeeTimeOrganizer.net"
    },
    booking: {
      subject: "✅ Tee Time Confirmed - {course_name}",
      body: "Hi {player_name},\n\nYour tee time has been confirmed!\n\nCourse: {course_name}\nDate: {date}\nTime: {time}\nPlayers: {player_list}\n\nWe look forward to seeing you on the course.\n\nTee Time Organizer\nTeeTimeOrganizer.net"
    },
    newteetime: {
      subject: "📅 New Tee Times Available",
      body: "Hi {player_name},\n\nNew tee times have been posted!\n\n{tee_time_list}\n\nBook your spot before they fill up.\n\nTee Time Organizer\nTeeTimeOrganizer.net"
    }
  };

  // Load templates from localStorage or use defaults
  const [templates, setTemplates] = useState(() => {
    const saved = localStorage.getItem('systemMessageTemplates');
    return saved ? JSON.parse(saved) : defaultTemplates;
  });

  const [editForm, setEditForm] = useState({
    subject: "",
    body: ""
  });

  const currentTemplate = templates[systemTab];

  const handleEdit = () => {
    setEditForm({
      subject: currentTemplate.subject,
      body: currentTemplate.body
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!editForm.subject.trim()) {
      addToast("Subject is required", "error");
      return;
    }
    if (!editForm.body.trim()) {
      addToast("Message body is required", "error");
      return;
    }
    if (editForm.subject.length > 50) {
      addToast("Subject must be 50 characters or less", "error");
      return;
    }
    if (editForm.body.length > 500) {
      addToast("Message body must be 500 characters or less", "error");
      return;
    }

    const updatedTemplates = {
      ...templates,
      [systemTab]: {
        subject: editForm.subject,
        body: editForm.body
      }
    };
    
    setTemplates(updatedTemplates);
    localStorage.setItem('systemMessageTemplates', JSON.stringify(updatedTemplates));
    setIsEditing(false);
    addToast("Template saved successfully");
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({ subject: "", body: "" });
  };

  const handleDelete = () => {
    setConfirmDelete(true);
  };

  const confirmDeleteTemplate = () => {
    const updatedTemplates = {
      ...templates,
      [systemTab]: defaultTemplates[systemTab]
    };
    
    setTemplates(updatedTemplates);
    localStorage.setItem('systemMessageTemplates', JSON.stringify(updatedTemplates));
    setConfirmDelete(false);
    addToast("Template reset to default");
  };

  const getTemplateName = (tab) => {
    switch(tab) {
      case "reminder24hr": return "24-Hour Reminder";
      case "booking": return "Booking Confirmation";
      case "newteetime": return "New Tee Times";
      default: return "";
    }
  };

  const getTemplateIcon = (tab) => {
    switch(tab) {
      case "reminder24hr": return "⏰";
      case "booking": return "✅";
      case "newteetime": return "📅";
      default: return "";
    }
  };
  
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* System Generated Submenu */}
      <Card>
        <div style={{ display: "flex", gap: 8, borderBottom: "1px solid #e5e7eb", paddingBottom: 8 }}>
          <Button 
            variant={systemTab === "reminder24hr" ? "default" : "outline"}
            onClick={() => { setSystemTab("reminder24hr"); setIsEditing(false); }}
            style={{ fontSize: 13, padding: "6px 12px" }}
          >
            ⏰ 24hr Reminder
          </Button>
          <Button 
            variant={systemTab === "booking" ? "default" : "outline"}
            onClick={() => { setSystemTab("booking"); setIsEditing(false); }}
            style={{ fontSize: 13, padding: "6px 12px" }}
          >
            ✅ Booking Confirmation
          </Button>
          <Button 
            variant={systemTab === "newteetime" ? "default" : "outline"}
            onClick={() => { setSystemTab("newteetime"); setIsEditing(false); }}
            style={{ fontSize: 13, padding: "6px 12px" }}
          >
            📅 New Tee Times
          </Button>
        </div>
      </Card>

      {/* Template Editor */}
      <Card>
        <div style={{ padding: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 24, marginBottom: 8 }}>
                {getTemplateIcon(systemTab)} {getTemplateName(systemTab)}
              </div>
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                Email template configuration
              </div>
            </div>
            {!isEditing && (
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="outline" onClick={handleEdit}>
                  ✏️ Edit
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  🔄 Reset to Default
                </Button>
              </div>
            )}
          </div>

          {!isEditing ? (
            /* View Mode */
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <Label style={{ marginBottom: 8, display: "block", fontWeight: 600 }}>Subject Line</Label>
                <div style={{ 
                  padding: 12, 
                  background: "#f9fafb", 
                  border: "1px solid #e5e7eb", 
                  borderRadius: 6,
                  fontSize: 14
                }}>
                  {currentTemplate.subject}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                  {currentTemplate.subject.length}/50 characters
                </div>
              </div>

              <div>
                <Label style={{ marginBottom: 8, display: "block", fontWeight: 600 }}>Message Body</Label>
                <div style={{ 
                  padding: 12, 
                  background: "#f9fafb", 
                  border: "1px solid #e5e7eb", 
                  borderRadius: 6,
                  fontSize: 14,
                  whiteSpace: "pre-wrap",
                  minHeight: 200
                }}>
                  {currentTemplate.body}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                  {currentTemplate.body.length}/500 characters
                </div>
              </div>

              {/* Available Variables */}
              <div style={{ 
                padding: 12, 
                background: "#eff6ff", 
                border: "1px solid #93c5fd", 
                borderRadius: 6 
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e40af", marginBottom: 8 }}>
                  📝 Available Variables:
                </div>
                <div style={{ fontSize: 12, color: "#1e3a8a", display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4 }}>{'{{player_name}}'}</code> - Player's name</div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4 }}>{'{{course_name}}'}</code> - Course name</div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4 }}>{'{{date}}'}</code> - Tee time date</div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4 }}>{'{{time}}'}</code> - Tee time</div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4 }}>{'{{player_list}}'}</code> - All players</div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4 }}>{'{{player_portal_link}}'}</code> - Link: "Access Player Portal"</div>
                  {systemTab === "newteetime" && (
                    <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4 }}>{'{{tee_time_list}}'}</code> - List of tee times</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Edit Mode */
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <Label style={{ marginBottom: 8, display: "block" }}>
                  Subject Line <span style={{ color: "#dc2626" }}>*</span>
                </Label>
                <Input 
                  type="text"
                  value={editForm.subject}
                  onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                  maxLength={50}
                  placeholder="Enter email subject..."
                  style={{ width: "100%" }}
                />
                <div style={{ 
                  fontSize: 11, 
                  color: editForm.subject.length > 50 ? "#dc2626" : "#9ca3af", 
                  marginTop: 4 
                }}>
                  {editForm.subject.length}/50 characters
                </div>
              </div>

              <div>
                <Label style={{ marginBottom: 8, display: "block" }}>
                  Message Body <span style={{ color: "#dc2626" }}>*</span>
                </Label>
                <textarea
                  value={editForm.body}
                  onChange={(e) => setEditForm({ ...editForm, body: e.target.value })}
                  maxLength={500}
                  placeholder="Enter message body..."
                  rows={12}
                  style={{ 
                    width: "100%", 
                    padding: 12, 
                    border: "1px solid #e5e7eb", 
                    borderRadius: 6,
                    fontSize: 14,
                    fontFamily: "inherit",
                    resize: "vertical"
                  }}
                />
                <div style={{ 
                  fontSize: 11, 
                  color: editForm.body.length > 500 ? "#dc2626" : "#9ca3af", 
                  marginTop: 4 
                }}>
                  {editForm.body.length}/500 characters
                </div>
              </div>

              {/* Available Variables in Edit Mode */}
              <div style={{ 
                padding: 12, 
                background: "#eff6ff", 
                border: "1px solid #93c5fd", 
                borderRadius: 6 
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e40af", marginBottom: 8 }}>
                  📝 Available Variables (copy and paste):
                </div>
                <div style={{ fontSize: 12, color: "#1e3a8a", display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4, cursor: "pointer" }}>{'{{player_name}}'}</code></div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4, cursor: "pointer" }}>{'{{course_name}}'}</code></div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4, cursor: "pointer" }}>{'{{date}}'}</code></div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4, cursor: "pointer" }}>{'{{time}}'}</code></div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4, cursor: "pointer" }}>{'{{player_list}}'}</code></div>
                  <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4, cursor: "pointer" }}>{'{{player_portal_link}}'}</code> - Link text</div>
                  {systemTab === "newteetime" && (
                    <div><code style={{ background: "#dbeafe", padding: "2px 6px", borderRadius: 4, cursor: "pointer" }}>{'{{tee_time_list}}'}</code></div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSave} style={{ background: "#2d6a4f", color: "#fff" }}>
                  💾 Save Template
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      {confirmDelete && (
        <ConfirmDialog 
          title="Reset Template to Default?"
          message="This will restore the original template. Your custom changes will be lost."
          onConfirm={confirmDeleteTemplate}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// ORGANIZATION SETTINGS COMPONENT
// ============================================================================

// ============================================================================
// TEAM MEMBERS SECTION - Invite Co-Organizers
// ============================================================================
// Added: December 23, 2025
// Purpose: Allow organizers to invite players to become co-organizers
// Insert this component BEFORE the OrganizationSettings component definition
// Then add <TeamMembersSection /> inside OrganizationSettings before Save Button
// ============================================================================

const TeamMembersSection = () => {
  const { addToast } = useToast();
  const { isReadOnly, isExpired } = useSubscription();
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading
  
  // Read-only mode click handler
  const handleReadOnlyClick = () => {
    addToast(isExpired 
      ? "Your free trial has ended. Please subscribe to continue making changes." 
      : "Your account is in read-only mode. Please reactivate your subscription to make changes.", "error");
  };
  
  // Fetch team members and pending invites on mount
  useEffect(() => {
    fetchTeamData();
  }, []);
  
  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const headers = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch team members
      const teamResponse = await fetch(`${API_URL}/organization/team`, {
        headers,
        credentials: 'include'
      });
      
      if (teamResponse.ok) {
        const teamData = await teamResponse.json();
        setTeamMembers(teamData.data || []);
      }
      
      // Fetch pending invitations
      const pendingResponse = await fetch(`${API_URL}/organization/team/pending`, {
        headers,
        credentials: 'include'
      });
      
      if (pendingResponse.ok) {
        const pendingData = await pendingResponse.json();
        setPendingInvites(pendingData.data || []);
      }
      
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      addToast('Please enter an email address', 'error');
      return;
    }
    
    try {
      setSending(true);
      const headers = {
        'Content-Type': 'application/json'
      };
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/organization/team/invite`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ email: inviteEmail.trim() })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        addToast(data.error || 'Failed to send invitation', 'error');
        return;
      }
      
      addToast(data.message || 'Invitation sent!', 'success');
      setInviteEmail('');
      fetchTeamData(); // Refresh the list
      
    } catch (error) {
      console.error('Error sending invitation:', error);
      addToast('Failed to send invitation', 'error');
    } finally {
      setSending(false);
    }
  };
  
  const handleResendInvite = async (inviteId) => {
    try {
      setActionLoading(`resend-${inviteId}`);
      const headers = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/organization/team/invite/${inviteId}/resend`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        addToast(data.error || 'Failed to resend invitation', 'error');
        return;
      }
      
      addToast(data.message || 'Invitation resent!', 'success');
      fetchTeamData();
      
    } catch (error) {
      console.error('Error resending invitation:', error);
      addToast('Failed to resend invitation', 'error');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }
    
    try {
      setActionLoading(`cancel-${inviteId}`);
      const headers = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/organization/team/invite/${inviteId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        addToast(data.error || 'Failed to cancel invitation', 'error');
        return;
      }
      
      addToast(data.message || 'Invitation cancelled', 'success');
      fetchTeamData();
      
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      addToast('Failed to cancel invitation', 'error');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleRemoveMember = async (odUserId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the team? They will no longer be able to access the organizer dashboard.`)) {
      return;
    }
    
    try {
      setActionLoading(`remove-${odUserId}`);
      const headers = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/organization/team/${odUserId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        addToast(data.error || 'Failed to remove team member', 'error');
        return;
      }
      
      addToast(data.message || 'Team member removed', 'success');
      fetchTeamData();
      
    } catch (error) {
      console.error('Error removing team member:', error);
      addToast('Failed to remove team member', 'error');
    } finally {
      setActionLoading(null);
    }
  };
  
  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'owner':
        return { background: '#fef3c7', color: '#92400e' };
      case 'admin':
        return { background: '#dbeafe', color: '#1e40af' };
      case 'organizer':
        return { background: '#d1fae5', color: '#065f46' };
      default:
        return { background: '#e5e7eb', color: '#4b5563' };
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Database returns timestamp without timezone - treat as UTC
    let ts = dateStr;
    if (typeof ts === 'string' && !ts.endsWith('Z') && !ts.includes('+')) {
      ts = ts + 'Z'; // Append Z to interpret as UTC
    }
    return new Date(ts).toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 16 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
        👥 Team Members
      </h3>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
        Invite players to become co-organizers and help manage tee times
      </p>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>
          Loading team members...
        </div>
      ) : (
        <>
          {/* Current Team Members */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
              Current Team ({teamMembers.length})
            </div>
            
            {teamMembers.length === 0 ? (
              <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, color: '#6b7280', fontSize: 14 }}>
                No team members yet. Invite someone below!
              </div>
            ) : (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                {teamMembers.map((member, index) => (
                  <div
                    key={member.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderBottom: index < teamMembers.length - 1 ? '1px solid #e5e7eb' : 'none',
                      background: index % 2 === 0 ? '#fff' : '#fafafa'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>
                        {member.name}
                      </div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>
                        {member.email}
                      </div>
                      {member.last_login_at && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                          Last login: {formatDate(member.last_login_at)}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        ...getRoleBadgeStyle(member.role)
                      }}>
                        {member.role}
                      </span>
                      
                      {member.role !== 'owner' && (
                        <button
                          onClick={() => handleRemoveMember(member.id, member.name)}
                          disabled={actionLoading === `remove-${member.id}`}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #fecaca',
                            background: '#fff',
                            borderRadius: 6,
                            fontSize: 12,
                            color: '#dc2626',
                            cursor: actionLoading === `remove-${member.id}` ? 'not-allowed' : 'pointer',
                            opacity: actionLoading === `remove-${member.id}` ? 0.5 : 1
                          }}
                        >
                          {actionLoading === `remove-${member.id}` ? '...' : 'Remove'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Pending Invitations */}
          {pendingInvites.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                Pending Invitations ({pendingInvites.length})
              </div>
              
              <div style={{ border: '1px solid #fef3c7', borderRadius: 8, background: '#fffbeb' }}>
                {pendingInvites.map((invite, index) => (
                  <div
                    key={invite.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderBottom: index < pendingInvites.length - 1 ? '1px solid #fef3c7' : 'none'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500, color: '#92400e', fontSize: 14 }}>
                        {invite.name}
                      </div>
                      <div style={{ fontSize: 13, color: '#b45309' }}>
                        {invite.email}
                      </div>
                      <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>
                        Expires: {formatDate(invite.expires_at)}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleResendInvite(invite.id)}
                        disabled={actionLoading === `resend-${invite.id}`}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          background: '#fff',
                          borderRadius: 6,
                          fontSize: 12,
                          color: '#374151',
                          cursor: actionLoading === `resend-${invite.id}` ? 'not-allowed' : 'pointer',
                          opacity: actionLoading === `resend-${invite.id}` ? 0.5 : 1
                        }}
                      >
                        {actionLoading === `resend-${invite.id}` ? '...' : 'Resend'}
                      </button>
                      <button
                        onClick={() => handleCancelInvite(invite.id)}
                        disabled={actionLoading === `cancel-${invite.id}`}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #fecaca',
                          background: '#fff',
                          borderRadius: 6,
                          fontSize: 12,
                          color: '#dc2626',
                          cursor: actionLoading === `cancel-${invite.id}` ? 'not-allowed' : 'pointer',
                          opacity: actionLoading === `cancel-${invite.id}` ? 0.5 : 1
                        }}
                      >
                        {actionLoading === `cancel-${invite.id}` ? '...' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Invite Form */}
          <div style={{
            padding: 20,
            background: '#f0fdf4',
            borderRadius: 10,
            border: '1px solid #bbf7d0'
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#166534', marginBottom: 12 }}>
              ➕ Invite Co-Organizer
            </div>
            
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <input
                type="email"
                placeholder="Enter player's email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendInvite()}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  border: '2px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none'
                }}
              />
              <button
                onClick={isReadOnly ? handleReadOnlyClick : handleSendInvite}
                disabled={isReadOnly || sending || !inviteEmail.trim()}
                style={{
                  padding: '10px 20px',
                  background: isReadOnly || sending || !inviteEmail.trim() ? '#9ca3af' : '#2d6a4f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: isReadOnly || sending || !inviteEmail.trim() ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {sending ? 'Sending...' : '📧 Send Invite'}
              </button>
            </div>
            
            <div style={{ fontSize: 12, color: '#166534' }}>
              <strong>Note:</strong> The person must already exist in your Players database. 
              They will receive an email to set up their organizer account.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// PHASE 3D: SUBSCRIPTION STATUS SECTION
// Shows current plan, status, trial info, and billing portal link
// Updated: January 28, 2026 - Added cancel trial with soft-disable
// ============================================================================
const SubscriptionStatusSection = () => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  // Sept 30, 2026: arriving from an Upgrade button (?upgrade=1) lands on the plan
  // cards, not the top of Settings. Runs once the plans have rendered, then drops
  // the flag from the address so a refresh doesn't jump again.
  useEffect(() => {
    if (!subscriptionData) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') !== '1') return;
    const t = setTimeout(() => {
      const el = document.getElementById('tto-upgrade-plans') || document.getElementById('tto-subscription-top');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      params.delete('upgrade');
      const qs = params.toString();
      try { window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '')); } catch (e) { /* ignore */ }
    }, 150);
    return () => clearTimeout(t);
  }, [subscriptionData]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [selectedReactivateTier, setSelectedReactivateTier] = useState(null);
  const { addToast } = useToast();

  // Tier configuration for upgrade options
  // Migrated to canonical post-rename ladder (Phase 4 tier rename, commit d3a9afd / Free+Starter Rollout v1.18)
  // annualPrice = true 20% discount (monthly x 9.6), matches the active Stripe annual prices.
  // Stripe IDs: Starter price_1Tf5Zo... ($47.90), Plus STRIPE_PRICE_STARTER_ANNUAL ($95.90),
  // Professional STRIPE_PRICE_BASIC_ANNUAL ($143.90), League STRIPE_PRICE_LEAGUE_ANNUAL ($239.90).
  const tierConfig = {
    starter: { name: 'Starter', maxPlayers: 20, monthlyPrice: 4.99, annualPrice: 47.90, color: '#3b82f6' },
    plus: { name: 'Plus', maxPlayers: 40, monthlyPrice: 9.99, annualPrice: 95.90, color: '#8b5cf6' },
    professional: { name: 'Professional', maxPlayers: 100, monthlyPrice: 14.99, annualPrice: 143.90, color: '#f59e0b' },
    league: { name: 'League', maxPlayers: 250, monthlyPrice: 24.99, annualPrice: 239.90, color: '#ef4444' }
  };

  // Org-facing upgrade-card billing cycle. Defaults to monthly (lower perceived price).
  const [upgradeBillingCycle, setUpgradeBillingCycle] = useState('monthly');

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }
      
      const data = await response.json();
      // Extract the subscription object from the response
      // API returns: { success: true, subscription: {...} }
      setSubscriptionData(data.subscription || data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      // Don't show error toast - subscription might not be set up yet
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      setPortalLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/subscription/portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open billing portal');
      }
      
      const data = await response.json();
      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      addToast(error.message || 'Failed to open billing portal', 'error');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async (tier, isReactivation = false, billingCycle = upgradeBillingCycle) => {
    try {
      setUpgradeLoading(true);
      const token = getAuthToken();
      
      const response = await fetch(`${API_URL}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tier,
          billingCycle,
          isReactivation,
          successUrl: `${window.location.origin}/dashboard?tab=settings&subscription=success`,
          cancelUrl: `${window.location.origin}/dashboard?tab=settings`
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      addToast(error.message || 'Failed to start upgrade process', 'error');
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Handle cancel trial
  const handleCancelTrial = async () => {
    try {
      setCancelLoading(true);
      const token = getAuthToken();
      
      const response = await fetch(`${API_URL}/subscription/cancel-trial`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to cancel trial');
      }

      addToast(data.message || 'Trial cancelled successfully', 'success');
      setShowCancelModal(false);
      
      // Refresh subscription status
      fetchSubscriptionStatus();
    } catch (error) {
      console.error('Error cancelling trial:', error);
      addToast(error.message || 'Failed to cancel trial', 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  // Get recommended tier based on player count
  const getRecommendedTier = (playerCount) => {
    if (playerCount <= 20) return 'starter';
    if (playerCount <= 40) return 'plus';
    if (playerCount <= 100) return 'professional';
    return 'league';
  };

  // Handle reactivation - opens modal to select plan
  const handleReactivate = () => {
    // Pre-select recommended tier based on current player count
    const playerCount = subscriptionData?.playerCount || 0;
    const recommended = getRecommendedTier(playerCount);
    setSelectedReactivateTier(recommended);
    setShowReactivateModal(true);
  };

  // Confirm reactivation with selected tier
  const handleConfirmReactivation = (tier) => {
    setShowReactivateModal(false);
    // Pass isReactivation=true to skip the 30-day trial
    handleUpgrade(tier, true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      trial: { bg: '#dbeafe', color: '#1e40af', text: '🎉 Free Trial' },
      active: { bg: '#dcfce7', color: '#166534', text: '✅ Active' },
      past_due: { bg: '#fef3c7', color: '#92400e', text: '⚠️ Past Due' },
      cancelled: { bg: '#fee2e2', color: '#991b1b', text: '❌ Cancelled' },
      canceled: { bg: '#fee2e2', color: '#991b1b', text: '❌ Cancelled' },
      canceling: { bg: '#fef3c7', color: '#92400e', text: '⏳ Cancelling' },
      incomplete: { bg: '#fef3c7', color: '#92400e', text: '⏳ Incomplete' },
      unpaid: { bg: '#fee2e2', color: '#991b1b', text: '💳 Unpaid' },
      expired: { bg: '#f3f4f6', color: '#6b7280', text: '⏰ Expired' }
    };
    const config = statusConfig[status] || { bg: '#f3f4f6', color: '#6b7280', text: status };
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 12px',
        background: config.bg,
        color: config.color,
        borderRadius: 20,
        fontSize: 13,
        fontWeight: 600
      }}>
        {config.text}
      </span>
    );
  };

  const getTierBadge = (tier) => {
    const tierBadgeConfig = {
      free: { icon: '🎁', name: 'Free', limit: '1-8 players' },
      complimentary: { icon: '⭐', name: 'Complimentary', limit: 'Unlimited' },
      founders: { icon: '🏅', name: 'Founders', limit: 'Unlimited' },
      starter: { icon: '🌱', name: 'Starter', limit: '1-20 players' },
      plus: { icon: '⭐', name: 'Plus', limit: '1-40 players' },
      professional: { icon: '🏆', name: 'Professional', limit: '1-100 players' },
      league: { icon: '🚀', name: 'League', limit: '1-250 players' },
      club: { icon: '🏛️', name: 'Club', limit: 'Unlimited' }
    };
    const config = tierBadgeConfig[tier] || { icon: '📋', name: tier || 'Unknown', limit: '' };
    return config;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Check if account is cancelled or expired (read-only mode)
  const isCancelled = subscriptionData?.status === 'cancelled' || subscriptionData?.status === 'canceled';
  const isExpiredAccount = subscriptionData?.status === 'expired';
  
  // Detect demo and complimentary orgs - hide subscription management buttons
  const isDemoOrComplimentary = subscriptionData?.currentTier === 'complimentary' 
    || subscriptionData?.is_complimentary === true
    || subscriptionData?.is_demo === true;

  if (loading) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 16 }}>
          💳 Subscription & Billing
        </h3>
        <div style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>
          Loading subscription information...
        </div>
      </div>
    );
  }

  // If no subscription data, show setup prompt
  if (!subscriptionData || !subscriptionData.status) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 16 }}>
          💳 Subscription & Billing
        </h3>
        <div style={{ 
          padding: 20, 
          background: '#f0f9ff', 
          border: '1px solid #bae6fd', 
          borderRadius: 8,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 14, color: '#0369a1', marginBottom: 12 }}>
            No active subscription found.
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            Contact support if you believe this is an error.
          </div>
        </div>
      </div>
    );
  }

  const tierInfo = getTierBadge(subscriptionData.currentTier);
  const daysRemaining = subscriptionData.status === 'trial' 
    ? getDaysRemaining(subscriptionData.trialEndsAt)
    : null;

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 16 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 16 }}>
        💳 Subscription & Billing
      </h3>
      
      <div style={{ display: 'grid', gap: 16 }}>
        
        {/* CANCELLED ACCOUNT BANNER */}
        {isCancelled && (
          <div style={{ 
            padding: 20, 
            background: '#fef2f2', 
            border: '1px solid #fecaca', 
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 28 }}>🚫</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#991b1b' }}>
                  Account Cancelled
                </div>
                <div style={{ fontSize: 14, color: '#b91c1c', marginTop: 4 }}>
                  Your account is in read-only mode. You can view your data but cannot create or edit tee times, add players, or send messages.
                </div>
              </div>
            </div>
            <div style={{ 
              padding: 12, 
              background: '#fff', 
              borderRadius: 8,
              border: '1px solid #fecaca'
            }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                Ready to come back? Your data is still here!
              </div>
              <button
                onClick={handleReactivate}
                disabled={upgradeLoading}
                style={{
                  padding: '10px 20px',
                  background: upgradeLoading ? '#9ca3af' : '#2d6a4f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: upgradeLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {upgradeLoading ? (
                  <>⏳ Loading...</>
                ) : (
                  <>🔄 Reactivate Account</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* EXPIRED TRIAL BANNER */}
        {isExpiredAccount && (
          <div style={{ 
            padding: 20, 
            background: '#fef3c7', 
            border: '1px solid #fcd34d', 
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 28 }}>⏰</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#92400e' }}>
                  Free Trial Ended
                </div>
                <div style={{ fontSize: 14, color: '#a16207', marginTop: 4 }}>
                  Your account is in view-only mode. You can view your data but cannot create or edit tee times, add players, or send messages. Subscribe to restore full access.
                </div>
              </div>
            </div>
            <div style={{ 
              padding: 12, 
              background: '#fff', 
              borderRadius: 8,
              border: '1px solid #fcd34d'
            }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                Your data is safe and waiting for you!
              </div>
              <button
                onClick={handleReactivate}
                disabled={upgradeLoading}
                style={{
                  padding: '10px 20px',
                  background: upgradeLoading ? '#9ca3af' : '#2d6a4f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: upgradeLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {upgradeLoading ? (
                  <>⏳ Loading...</>
                ) : (
                  <>💳 Subscribe Now</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Current Plan & Status */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: 16 
        }}>
          {/* Plan Info */}
          <div style={{ 
            padding: 16, 
            background: '#f9fafb', 
            borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Current Plan
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{tierInfo.icon}</span>
              <span>{tierInfo.name}</span>
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
              {tierInfo.limit}
            </div>
          </div>

          {/* Status */}
          <div style={{ 
            padding: 16, 
            background: '#f9fafb', 
            borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Status
            </div>
            <div style={{ marginTop: 4 }}>
              {getStatusBadge(subscriptionData.status)}
            </div>
            {subscriptionData.billingCycle && !isCancelled && (
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
                Billed {subscriptionData.billingCycle === 'annual' ? 'annually' : 'monthly'}
              </div>
            )}
          </div>

          {/* Player Usage */}
          <div style={{ 
            padding: 16, 
            background: '#f9fafb', 
            borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Regular Players
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
              {subscriptionData.playerCount || 0} / {subscriptionData.tierLimits?.maxPlayers || '∞'}
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
              Active regular players (Player, Captain, Organizer){subscriptionData.frequentGuestCount > 0 ? `, including ${subscriptionData.frequentGuestCount} ${subscriptionData.frequentGuestCount === 1 ? 'guest who plays' : 'guests who play'} as often as a regular` : ''}. Occasional guests are always free.
            </div>
            {subscriptionData.eventAccount && (
              <div style={{ fontSize: 12.5, color: '#166534', marginTop: 6, lineHeight: 1.45 }}>
                Your event golfers join as guests. Guests are free and don&rsquo;t count here; your Event Pass sets the size of your field.
              </div>
            )}
            {/* Progress Bar */}
            {subscriptionData.tierLimits?.maxPlayers && (
              <div style={{ marginTop: 8 }}>
                <div style={{ 
                  height: 6, 
                  background: '#e5e7eb', 
                  borderRadius: 3, 
                  overflow: 'hidden' 
                }}>
                  <div style={{ 
                    height: '100%', 
                    width: `${Math.min(100, Math.round((subscriptionData.playerCount || 0) / subscriptionData.tierLimits.maxPlayers * 100))}%`,
                    background: (subscriptionData.playerCount || 0) >= subscriptionData.tierLimits.maxPlayers * 0.9 ? '#dc2626' : 
                               (subscriptionData.playerCount || 0) >= subscriptionData.tierLimits.maxPlayers * 0.7 ? '#f59e0b' : '#22c55e',
                    borderRadius: 3,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}
            {subscriptionData.tierLimits?.maxPlayers && subscriptionData.playerCount >= subscriptionData.tierLimits.maxPlayers && (
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4, fontWeight: 500 }}>
                ⚠️ At your regular-player limit
              </div>
            )}
            {subscriptionData.tierLimits?.maxPlayers && subscriptionData.playerCount >= subscriptionData.tierLimits.maxPlayers * 0.8 && subscriptionData.playerCount < subscriptionData.tierLimits.maxPlayers && (
              <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 4, fontWeight: 500 }}>
                ⚠️ Approaching limit
              </div>
            )}
          </div>
        </div>

        {/* Trial Info Banner */}
        {subscriptionData.status === 'trial' && daysRemaining !== null && (
          <div style={{ 
            padding: 16, 
            background: daysRemaining <= 3 ? '#fef3c7' : '#dbeafe', 
            border: `1px solid ${daysRemaining <= 3 ? '#fcd34d' : '#93c5fd'}`, 
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <span style={{ fontSize: 24 }}>{daysRemaining <= 3 ? '⏰' : '🎉'}</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: daysRemaining <= 3 ? '#92400e' : '#1e40af' }}>
                {daysRemaining <= 0 
                  ? 'Trial ends today!' 
                  : daysRemaining === 1 
                    ? '1 day left in your free trial'
                    : `${daysRemaining} days left in your free trial`
                }
              </div>
              <div style={{ fontSize: 13, color: daysRemaining <= 3 ? '#92400e' : '#1e40af', opacity: 0.9 }}>
                Trial ends: {formatDate(subscriptionData.trialEndsAt)}
                {daysRemaining <= 3 && ' • Your card will be charged after the trial ends'}
              </div>
            </div>
          </div>
        )}

        {/* Past Due Warning */}
        {subscriptionData.status === 'past_due' && (
          <div style={{ 
            padding: 16, 
            background: '#fef3c7', 
            border: '1px solid #fcd34d', 
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <span style={{ fontSize: 24 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#92400e' }}>
                Payment Past Due
              </div>
              <div style={{ fontSize: 13, color: '#92400e' }}>
                Please update your payment method to avoid service interruption.
              </div>
            </div>
          </div>
        )}

        {/* Billing Dates */}
        {subscriptionData.currentPeriodEnd && subscriptionData.status !== 'trial' && !isCancelled && (
          <div style={{ 
            padding: 12, 
            background: '#f9fafb', 
            borderRadius: 8,
            fontSize: 13,
            color: '#6b7280'
          }}>
            <strong>Next billing date:</strong> {formatDate(subscriptionData.currentPeriodEnd)}
          </div>
        )}

        {/* Manage Subscription Button - hide for demo/complimentary */}
        {subscriptionData.stripeCustomerId && !isCancelled && !isDemoOrComplimentary && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8 }}>
            <button
              onClick={handleOpenBillingPortal}
              disabled={portalLoading}
              style={{
                padding: '12px 24px',
                background: portalLoading ? '#9ca3af' : '#2d6a4f',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: portalLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {portalLoading ? (
                <>⏳ Opening...</>
              ) : (
                <>💳 Manage Subscription & Billing</>
              )}
            </button>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
              Update payment method, view invoices, or change your plan
            </div>
          </div>
        )}

        {/* Cancel Trial Button - Only show during trial, hide for demo/complimentary */}
        {subscriptionData.status === 'trial' && !isDemoOrComplimentary && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8 }}>
            <button
              onClick={() => setShowCancelModal(true)}
              disabled={cancelLoading}
              style={{
                padding: '10px 20px',
                background: '#fff',
                color: '#dc2626',
                border: '1px solid #fecaca',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: cancelLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {cancelLoading ? (
                <>⏳ Cancelling...</>
              ) : (
                <>✕ Cancel Trial - No Charge</>
              )}
            </button>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
              Cancel your trial at any time. Your card will not be charged.
            </div>
          </div>
        )}

        {/* Upgrade Options - hide for demo/complimentary */}
        {subscriptionData.currentTier !== 'league' && !isCancelled && !isDemoOrComplimentary && (
          <div id="tto-upgrade-plans" style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8, scrollMarginTop: 80 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
              ⬆️ Upgrade Your Plan
            </h4>
            {/* Billing-cycle toggle: monthly (default) vs annual (20% off) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => setUpgradeBillingCycle('monthly')}
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: upgradeBillingCycle === 'monthly' ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                  background: upgradeBillingCycle === 'monthly' ? '#eff6ff' : '#fff',
                  color: upgradeBillingCycle === 'monthly' ? '#1d4ed8' : '#6b7280'
                }}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setUpgradeBillingCycle('annual')}
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: upgradeBillingCycle === 'annual' ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                  background: upgradeBillingCycle === 'annual' ? '#eff6ff' : '#fff',
                  color: upgradeBillingCycle === 'annual' ? '#1d4ed8' : '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                Annual
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#15803d',
                  background: '#dcfce7',
                  padding: '1px 6px',
                  borderRadius: 10
                }}>
                  Save 20%
                </span>
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
              {Object.entries(tierConfig).map(([key, tier]) => {
                const isCurrent = key === subscriptionData.currentTier;
                const isDowngrade = tierConfig[subscriptionData.currentTier]?.maxPlayers > tier.maxPlayers;
                if (isDowngrade) return null; // Don't show downgrade options
                return (
                  <div 
                    key={key}
                    style={{ 
                      padding: 12, 
                      border: isCurrent ? `2px solid ${tier.color}` : '1px solid #e5e7eb',
                      borderRadius: 8,
                      background: isCurrent ? `${tier.color}08` : '#fff',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontWeight: 600, color: tier.color, fontSize: 13 }}>
                      {tier.name}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginTop: 4 }}>
                      ${upgradeBillingCycle === 'annual' ? tier.annualPrice : tier.monthlyPrice}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>
                      {upgradeBillingCycle === 'annual' ? '/year' : '/month'}
                    </div>
                    {upgradeBillingCycle === 'annual' && (
                      <div style={{ fontSize: 10, color: '#15803d', marginTop: 2 }}>
                        ${(tier.annualPrice / 12).toFixed(2)}/mo billed annually
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                      Up to {tier.maxPlayers} regular players · occasional guests free
                    </div>
                    {key !== 'starter' && (
                      <div style={{ fontSize: 11, color: '#166534', fontWeight: 700, marginTop: 4 }}>🎮 Games included</div>
                    )}
                    {!isCurrent && !isDemoOrComplimentary && (
                      <button
                        onClick={() => handleUpgrade(key)}
                        disabled={upgradeLoading}
                        style={{
                          marginTop: 8,
                          width: '100%',
                          padding: '6px 10px',
                          background: tier.color,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: upgradeLoading ? 'not-allowed' : 'pointer',
                          opacity: upgradeLoading ? 0.7 : 1
                        }}
                      >
                        {upgradeLoading ? '...' : 'Upgrade'}
                      </button>
                    )}
                    {isCurrent && (
                      <div style={{ 
                        marginTop: 8, 
                        padding: '6px 10px',
                        background: '#f3f4f6',
                        borderRadius: 6,
                        fontSize: 11,
                        color: '#6b7280'
                      }}>
                        Current
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
              Need to downgrade? Use "Manage Subscription & Billing" above.
            </div>
          </div>
        )}
      </div>

      {/* Cancel Trial Confirmation Modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 450,
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 16 }}>
              Cancel Free Trial?
            </h3>
            
            <div style={{ 
              padding: 16, 
              background: '#f0fdf4', 
              border: '1px solid #bbf7d0', 
              borderRadius: 8,
              marginBottom: 16 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 20 }}>✅</span>
                <span style={{ fontWeight: 600, color: '#166534' }}>No charges will be made</span>
              </div>
              <div style={{ fontSize: 13, color: '#166534' }}>
                Your credit card will not be charged.
              </div>
            </div>

            <div style={{ 
              padding: 16, 
              background: '#fef3c7', 
              border: '1px solid #fcd34d', 
              borderRadius: 8,
              marginBottom: 20 
            }}>
              <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 8 }}>
                What happens when you cancel:
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#92400e' }}>
                <li>Your account will enter read-only mode</li>
                <li>You can still view your tee times, players, and courses</li>
                <li>You cannot create, edit, or publish new tee times</li>
                <li>Your data will be preserved for 90 days</li>
                <li>You can reactivate anytime by subscribing</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowCancelModal(false)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Keep My Trial
              </button>
              <button
                onClick={handleCancelTrial}
                disabled={cancelLoading}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: cancelLoading ? '#9ca3af' : '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: cancelLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel Trial'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Plan Picker Modal */}
      {showReactivateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 600,
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              🔄 Reactivate Your Account
            </h3>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
              Choose a plan to reactivate your account. You'll be charged immediately (no trial period).
            </p>

            {/* Player count info */}
            {subscriptionData?.playerCount > 0 && (
              <div style={{ 
                padding: 12, 
                background: '#f0f9ff', 
                border: '1px solid #bae6fd', 
                borderRadius: 8,
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <span style={{ fontSize: 18 }}>👥</span>
                <span style={{ fontSize: 14, color: '#0369a1' }}>
                  You have <strong>{subscriptionData.playerCount} active players</strong>. 
                  We recommend the <strong>{tierConfig[getRecommendedTier(subscriptionData.playerCount)]?.name}</strong> plan.
                </span>
              </div>
            )}

            {/* Plan Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
              {Object.entries(tierConfig).map(([key, tier]) => {
                const isRecommended = key === getRecommendedTier(subscriptionData?.playerCount || 0);
                const isSelected = key === selectedReactivateTier;
                const isTooSmall = (subscriptionData?.playerCount || 0) > tier.maxPlayers;
                
                return (
                  <div
                    key={key}
                    onClick={() => !isTooSmall && setSelectedReactivateTier(key)}
                    style={{
                      padding: 16,
                      border: isSelected ? `2px solid ${tier.color}` : '1px solid #e5e7eb',
                      borderRadius: 8,
                      background: isTooSmall ? '#f9fafb' : (isSelected ? `${tier.color}10` : '#fff'),
                      cursor: isTooSmall ? 'not-allowed' : 'pointer',
                      opacity: isTooSmall ? 0.6 : 1,
                      position: 'relative',
                      transition: 'all 0.2s'
                    }}
                  >
                    {isRecommended && !isTooSmall && (
                      <div style={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: tier.color,
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 10,
                        textTransform: 'uppercase'
                      }}>
                        Recommended
                      </div>
                    )}
                    {isTooSmall && (
                      <div style={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 10
                      }}>
                        Too Small
                      </div>
                    )}
                    <div style={{ fontWeight: 600, color: tier.color, fontSize: 14, marginBottom: 4 }}>
                      {tier.name}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
                      ${tier.monthlyPrice}
                      <span style={{ fontSize: 14, fontWeight: 400, color: '#6b7280' }}>/mo</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      Up to {tier.maxPlayers} regular players · occasional guests free
                    </div>
                    {isSelected && !isTooSmall && (
                      <div style={{ 
                        marginTop: 8, 
                        fontSize: 12, 
                        color: tier.color, 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        ✓ Selected
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowReactivateModal(false)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirmReactivation(selectedReactivateTier)}
                disabled={!selectedReactivateTier || upgradeLoading}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: (!selectedReactivateTier || upgradeLoading) ? '#9ca3af' : '#2d6a4f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: (!selectedReactivateTier || upgradeLoading) ? 'not-allowed' : 'pointer'
                }}
              >
                {upgradeLoading ? '⏳ Processing...' : `Subscribe to ${tierConfig[selectedReactivateTier]?.name || '...'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// ============================================================================

// ============================================================================
// TESTIMONIAL SECTION - Organizer experience submission
// Added: March 2026
// ============================================================================
const TestimonialSection = ({ settings }) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [existingTestimonial, setExistingTestimonial] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const PROMPTS = [
    { id: 'before_after', fieldKey: 'response_before_after', label: 'Before & After', text: 'What was managing your group like before Tee Time Organizer, and what\'s changed?' },
    { id: 'players_love', fieldKey: 'response_players_love', label: 'Player Reaction', text: 'What\'s the one thing your players love most about it?' },
    { id: 'on_the_fence', fieldKey: 'response_on_the_fence', label: 'For the Skeptic', text: 'What would you tell another golf organizer who\'s on the fence?' },
  ];

  const [form, setForm] = useState({
    rating: 5,
    submitter_name: '',
    response_before_after: '',
    response_players_love: '',
    response_on_the_fence: '',
    permission_granted: false,
    display_name_preference: 'full',
  });

  useEffect(() => {
    const checkExisting = async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(`${API_URL}/referral/testimonial/mine`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.submitted) {
            setSubmitted(true);
            setExistingTestimonial(data.testimonial);
          }
        }
      } catch (e) {
        console.error('Testimonial check error:', e);
      } finally {
        setLoading(false);
      }
    };
    checkExisting();
  }, []);

  const handleSubmit = async () => {
    const hasAtLeastOne = form.response_before_after.trim().length > 9 ||
                          form.response_players_love.trim().length > 9 ||
                          form.response_on_the_fence.trim().length > 9;
    if (!hasAtLeastOne) {
      addToast('Please answer at least one question (at least a sentence).', 'error');
      return;
    }
    if (!form.permission_granted) {
      addToast('Please check the permission box to submit.', 'error');
      return;
    }
    if (!form.submitter_name || form.submitter_name.trim().length < 2) {
      addToast('Please enter your name as you would like it shown.', 'error');
      return;
    }
    setSaving(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/referral/testimonial`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        setShowModal(false);
        addToast('Thank you! Your testimonial has been submitted for review.', 'success');
      } else {
        addToast(data.error || 'Failed to submit testimonial.', 'error');
      }
    } catch (e) {
      addToast('Something went wrong. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <>
      {/* Compact card on Settings page */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          ⭐ Share Your Experience
        </h3>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16, lineHeight: 1.5 }}>
          Loving Tee Time Organizer? We'd love to hear about it — your words help other organizers discover us!
        </p>

        {submitted ? (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div style={{ fontWeight: 600, color: '#166534', fontSize: 14 }}>Testimonial Submitted — Thank You!</div>
              {existingTestimonial && (
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  Review status: <span style={{
                    fontWeight: 600,
                    color: existingTestimonial.status === 'approved' ? '#166534' : existingTestimonial.status === 'rejected' ? '#dc2626' : '#92400e'
                  }}>{existingTestimonial.status.charAt(0).toUpperCase() + existingTestimonial.status.slice(1)}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowModal(true)}
            style={{
              padding: '10px 20px',
              background: '#2d6a4f', color: '#fff',
              border: 'none', borderRadius: 8,
              fontWeight: 600, fontSize: 14, cursor: 'pointer'
            }}
          >
            ⭐ Share My Experience
          </button>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16
        }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div style={{
            background: '#fff', borderRadius: 16,
            width: '100%', maxWidth: 620,
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
              position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderRadius: '16px 16px 0 0'
            }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>⭐ Share Your Experience</h3>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>Answer one or all three — every response helps.</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1, padding: 4 }}
              >×</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', display: 'grid', gap: 20 }}>

              {/* Star Rating */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  How would you rate Tee Time Organizer?
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setForm(f => ({ ...f, rating: star }))}
                      style={{
                        fontSize: 32, background: 'none', border: 'none', cursor: 'pointer',
                        color: star <= form.rating ? '#f59e0b' : '#d1d5db',
                        padding: '0 2px', lineHeight: 1
                      }}
                    >★</button>
                  ))}
                </div>
              </div>

              {/* Three prompt text areas */}
              {PROMPTS.map(p => {
                const val = form[p.fieldKey];
                const filled = val.trim().length > 0;
                return (
                  <div key={p.id} style={{
                    padding: '14px 16px', borderRadius: 10,
                    border: `2px solid ${filled ? '#2d6a4f' : '#e5e7eb'}`,
                    background: filled ? '#f0fdf4' : '#f9fafb',
                    transition: 'border-color 0.15s, background 0.15s'
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#2d6a4f', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                      {p.label}
                    </div>
                    <p style={{ fontSize: 13, color: '#374151', margin: '0 0 10px', lineHeight: 1.5, fontWeight: 500 }}>
                      {p.text}
                    </p>
                    <textarea
                      value={val}
                      onChange={e => setForm(f => ({ ...f, [p.fieldKey]: e.target.value }))}
                      placeholder="Optional — answer if this resonates with you..."
                      rows={3}
                      style={{
                        width: '100%', padding: '8px 10px',
                        border: '1px solid #d1d5db', borderRadius: 6,
                        fontSize: 13, color: '#111827', resize: 'vertical',
                        fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff'
                      }}
                    />
                    {filled && (
                      <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: 2 }}>
                        {val.length} characters
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Your name */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Your name
                </label>
                <input
                  type="text"
                  value={form.submitter_name}
                  onChange={(e) => setForm(f => ({ ...f, submitter_name: e.target.value }))}
                  placeholder="e.g. Todd Murphy"
                  style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  Enter your name as the person giving this testimonial. We'll display it based on your choice below.
                </div>
              </div>

              {/* Attribution preference */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                  If approved, how should we credit you?
                </label>
                <div style={{ display: 'grid', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: '#374151', padding: '8px 12px', borderRadius: 8, border: `2px solid ${form.display_name_preference === 'full' ? '#2d6a4f' : '#e5e7eb'}`, background: form.display_name_preference === 'full' ? '#f0fdf4' : '#fff' }}>
                    <input type="radio" name="t_display_pref" value="full"
                      checked={form.display_name_preference === 'full'}
                      onChange={() => setForm(f => ({ ...f, display_name_preference: 'full' }))}
                      style={{ accentColor: '#2d6a4f' }}
                    />
                    <span>Full name &amp; league — <em style={{ color: '#6b7280' }}>{settings?.organizer_name || 'John Smith'}, {settings?.organization_name || settings?.name || 'My Golf League'}</em></span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: '#374151', padding: '8px 12px', borderRadius: 8, border: `2px solid ${form.display_name_preference === 'first_state' ? '#2d6a4f' : '#e5e7eb'}`, background: form.display_name_preference === 'first_state' ? '#f0fdf4' : '#fff' }}>
                    <input type="radio" name="t_display_pref" value="first_state"
                      checked={form.display_name_preference === 'first_state'}
                      onChange={() => setForm(f => ({ ...f, display_name_preference: 'first_state' }))}
                      style={{ accentColor: '#2d6a4f' }}
                    />
                    <span>First name &amp; state only — <em style={{ color: '#6b7280' }}>{(settings?.organizer_name || 'John').split(' ')[0]} S., {settings?.default_state || 'Your State'}</em></span>
                  </label>
                </div>
              </div>

              {/* Permission checkbox */}
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px', background: '#fefce8',
                border: '1px solid #fde68a', borderRadius: 8,
                cursor: 'pointer', fontSize: 13, color: '#374151', lineHeight: 1.5
              }}>
                <input
                  type="checkbox"
                  checked={form.permission_granted}
                  onChange={e => setForm(f => ({ ...f, permission_granted: e.target.checked }))}
                  style={{ marginTop: 2, accentColor: '#2d6a4f', width: 16, height: 16, flexShrink: 0 }}
                />
                <span>I give Tee Time Organizer permission to use my response on their website and in marketing materials.</span>
              </label>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'flex-end', gap: 12,
              position: 'sticky', bottom: 0, background: '#fff', borderRadius: '0 0 16px 16px'
            }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '10px 20px', background: '#fff', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 8,
                  fontWeight: 600, fontSize: 14, cursor: 'pointer'
                }}
              >Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  padding: '10px 24px',
                  background: saving ? '#9ca3af' : '#2d6a4f',
                  color: '#fff', border: 'none', borderRadius: 8,
                  fontWeight: 600, fontSize: 14,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? '⏳ Submitting...' : '⭐ Submit My Testimonial'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// REFERRAL SHARE SECTION - Enhanced referral with email invitations
// Added: January 31, 2026
// ============================================================================
const ReferralShareSection = ({ settings }) => {
  const { addToast } = useToast();
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [emailContent, setEmailContent] = useState({ subject: '', body: '' });
  const [loadingEmail, setLoadingEmail] = useState(false);
  
  // Determine if this is a complimentary/free account (no personal benefit from referrals)
  const isComplimentary = ['complimentary', 'free'].includes(settings?.subscription_tier?.toLowerCase());
  const isPaid = !isComplimentary && settings?.subscription_status === 'active';
  
  const referralCode = settings?.referral_code;
  const referralLink = referralCode ? `https://teetimeorganizer.net/welcome?ref=${referralCode}` : null;
  
  const handlePreviewEmail = async () => {
    if (!recipientName.trim() || !recipientEmail.trim()) {
      addToast('Please enter recipient name and email', 'error');
      return;
    }
    
    // Extract first name from full name
    const firstName = recipientName.trim().split(' ')[0];
    
    setLoadingEmail(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/referral/email-content?recipientName=${encodeURIComponent(firstName)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailContent(data);
        setShowPreview(true);
      } else {
        // Fallback to default content - use first name
        setEmailContent({
          subject: 'An Easier Way to Manage Your Golf Group',
          body: `Hey ${firstName},\n\nI wanted to put this on your radar in case it's a fit - no pressure either way. I've been running my golf group on an app called Tee Time Organizer and it's honestly made the whole thing effortless, so I thought of you.\n\nI post a tee time and the guys RSVP with one tap on their phones. It handles the waitlist on its own, sets the pairings, sends the reminders, and even shows everyone the weather and their course handicap. The stuff I used to chase people about all week just takes care of itself now.\n\nWhat sold me is it's genuinely inexpensive - free to try for a small group, no credit card - so you can see if it works for your group before spending anything.\n\nIf you want to take a look:\n${referralLink}\n\nBest,\n${settings?.organizer_name || 'Your friend'}`
        });
        setShowPreview(true);
      }
    } catch (error) {
      console.error('Error fetching email content:', error);
      setEmailContent({
        subject: 'An Easier Way to Manage Your Golf Group',
        body: `Hey ${firstName},\n\nI wanted to put this on your radar in case it's a fit - no pressure either way. I've been running my golf group on an app called Tee Time Organizer and it's honestly made the whole thing effortless, so I thought of you.\n\nI post a tee time and the guys RSVP with one tap on their phones. It handles the waitlist on its own, sets the pairings, sends the reminders, and even shows everyone the weather and their course handicap. The stuff I used to chase people about all week just takes care of itself now.\n\nWhat sold me is it's genuinely inexpensive - free to try for a small group, no credit card - so you can see if it works for your group before spending anything.\n\nIf you want to take a look:\n${referralLink}\n\nBest,\n${settings?.organizer_name || 'Your friend'}`
      });
      setShowPreview(true);
    } finally {
      setLoadingEmail(false);
    }
  };
  
  const handleSendInvitation = async () => {
    setLoadingEmail(true);
    try {
      const token = getAuthToken();
      
      // Send the email via TTO backend
      const response = await fetch(`${API_URL}/referral/send-invite`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          recipientEmail: recipientEmail.trim().toLowerCase(),
          recipientName: recipientName.trim(),
          subject: emailContent.subject,
          body: emailContent.body
        })
      });
      
      if (response.ok) {
        addToast('Invitation email sent successfully!', 'success');
        setShowPreview(false);
        setRecipientName('');
        setRecipientEmail('');
      } else {
        const error = await response.json();
        addToast(error.message || 'Failed to send email. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      addToast('Failed to send email. Please try again.', 'error');
    } finally {
      setLoadingEmail(false);
    }
  };
  
  const handleCopyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      addToast('Referral link copied to clipboard!', 'success');
    }
  };
  
  if (!referralCode) {
    return null; // Don't show if no referral code
  }
  
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 16 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
        {'📤 Share Tee Time Organizer'}
      </h3>

      <p style={{ fontSize: 14, fontWeight: 600, color: '#2d6a4f', marginBottom: 6 }}>
        {isComplimentary
          ? "Get your friend a free month when they subscribe!"
          : "Get a free month for both you & your friend!"
        }
      </p>

      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16, lineHeight: 1.5 }}>
        {isComplimentary
          ? "Know someone who organizes a golf group? Share Tee Time Organizer with them — when they subscribe, they'll get a free month on us."
          : "Know someone who organizes a golf group? Share Tee Time Organizer with them — when they subscribe, you both get a free month added to your subscription."
        }
      </p>
      
      {/* Email Invitation Form */}
      <div style={{ 
        padding: 16, 
        background: '#f9fafb', 
        borderRadius: 8, 
        marginBottom: 16 
      }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#374151', marginBottom: 12 }}>
          📧 Send a Personal Invitation
        </div>
        
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
              Friend's Name
            </label>
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="John Smith"
              style={{ 
                width: '100%',
                padding: '10px 12px', 
                border: '1px solid #d1d5db', 
                borderRadius: 6, 
                fontSize: 14,
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
              Friend's Email
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="john@example.com"
              style={{ 
                width: '100%',
                padding: '10px 12px', 
                border: '1px solid #d1d5db', 
                borderRadius: 6, 
                fontSize: 14,
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <button
            onClick={handlePreviewEmail}
            disabled={loadingEmail || !recipientName.trim() || !recipientEmail.trim()}
            style={{
              padding: '10px 16px',
              background: (!recipientName.trim() || !recipientEmail.trim()) ? '#9ca3af' : '#2d6a4f',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: (!recipientName.trim() || !recipientEmail.trim()) ? 'not-allowed' : 'pointer'
            }}
          >
            {loadingEmail ? '⏳ Loading...' : '👁️ Preview Email'}
          </button>
        </div>
      </div>
      
      {/* Or Copy Link */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>
          Or copy your referral link:
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={referralLink || 'Not available'}
            readOnly
            style={{ 
              flex: 1,
              padding: '10px 12px', 
              border: '1px solid #d1d5db', 
              borderRadius: 6, 
              fontSize: 13,
              fontFamily: 'monospace',
              background: '#f9fafb',
              color: '#374151'
            }}
          />
          <button
            onClick={handleCopyLink}
            style={{
              padding: '10px 14px',
              background: '#2d6a4f',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            📋 Copy
          </button>
        </div>
      </div>
      
      {/* Credits Earned (for paid subscribers) */}
      {isPaid && settings?.referral_credit_months > 0 && (
        <div style={{ 
          padding: 12, 
          background: '#f0fdf4', 
          border: '1px solid #86efac', 
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <span style={{ fontSize: 20 }}>🏆</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#166534' }}>
              Free Months Earned: {settings.referral_credit_months}
            </div>
            <div style={{ fontSize: 12, color: '#166534' }}>
              These will be applied to your next billing cycle
            </div>
          </div>
        </div>
      )}
      
      {/* Preview Modal */}
      {showPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 600,
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
              📧 Email Preview
            </h3>
            
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>To:</div>
              <div style={{ fontSize: 14, color: '#111827' }}>{recipientName} &lt;{recipientEmail}&gt;</div>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Subject:</div>
              <div style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>{emailContent.subject}</div>
            </div>
            
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Message:</div>
              <div style={{ 
                fontSize: 14, 
                color: '#374151', 
                background: '#f9fafb', 
                padding: 16, 
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6
              }}>
                {emailContent.body}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPreview(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvitation}
                disabled={loadingEmail}
                style={{
                  padding: '10px 20px',
                  background: loadingEmail ? '#9ca3af' : '#2d6a4f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loadingEmail ? 'not-allowed' : 'pointer'
                }}
              >
                {loadingEmail ? '⏳ Sending...' : '📤 Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// ============================================================================

// ============================================================================
// CollapsibleSection — reusable accordion section for Organization Settings.
// Header (title + plain-language subtitle + chevron); body collapses. Collapsed
// by default. Pure presentation — wraps existing section content unchanged, so
// every control inside keeps its exact behavior and save/load wiring.
// ============================================================================
const CollapsibleSection = ({ title, subtitle, defaultOpen = false, grouped = false, isLast = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  // Standalone: its own bordered card. Grouped: no card border/radius/margin — the
  // parent band container provides the border; sections are separated by hairline
  // dividers (suppressed on the last section in a group).
  const wrapperStyle = grouped
    ? { background: '#fff', borderBottom: isLast ? 'none' : '1px solid #f3f4f6' }
    : { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 16, overflow: 'hidden' };
  return (
    <div style={wrapperStyle}>
      <div
        onClick={() => setOpen(o => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{subtitle}</div>}
        </div>
        <span style={{ fontSize: 18, color: '#9ca3af', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
      </div>
      {open && (
        <div style={{ padding: '4px 20px 20px 20px', borderTop: '1px solid #f3f4f6' }}>
          {children}
        </div>
      )}
    </div>
  );
};

const OrganizationSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();
  const { isReadOnly, isExpired } = useSubscription();
  const { refreshOrganization } = useOrganizerRefresh();
  const { tier: orgTier } = useOrgTier();
  
  // Read-only mode click handler
  const handleReadOnlyClick = () => {
    addToast(isExpired 
      ? "Your free trial has ended. Please subscribe to continue making changes." 
      : "Your account is in read-only mode. Please reactivate your subscription to make changes.", "error");
  };
  
  // Help modal state
  const [showHelp, setShowHelp] = useState(false);
  const helpContent = getHelpContent('settings');
  
  const [form, setForm] = useState({
    organization_name: '',
    display_name: '',
    organizer_name: '',
    organizer_email: '',
    organizer_phone: '',
    reply_to_email: '',
    reply_to_name: '',
    from_name: '',
    timezone: 'America/New_York',
    default_state: '',
    default_city: '',
    default_zip: '',
    same_day_reminder_enabled: true,
    same_day_reminder_time: '06:00:00',
    advance_reminder_enabled: true,
    advance_reminder_time: '09:00:00',
    custom_reminder_enabled: false,
    custom_reminder_days_before: 2,
    custom_reminder_time: '08:00:00',
    reminder_include_weather: true,
    date_format: 'MM/DD/YYYY',
    time_format: '12h',
    teesheet_show_member_badge: true,
    teesheet_show_handicap: false,
    teesheet_randomize_order: false,
    show_sunrise: false,
    show_sunset: false,
    handicap_stale_days: 30,
    // Group RSVP (Session 2 — April 2026)
    allow_group_rsvp: false,
    group_rsvp_confirmation_hours: 24,
    group_rsvp_mode: 'all',
    // Auto-email tee sheet to proshop (Aug 2026)
    auto_proshop_email_enabled: false,
    auto_proshop_email_hours_before: 3,
    auto_proshop_email_bcc_organizer: false
  });
  
  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      setLoading(true);
      // PHASE 2D FIX: Include JWT Authorization header for multi-tenant support
      const headers = {};
      const token = getAuthToken(); // PHASE 2E FIX
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${API_URL}/settings`, {
        headers,
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }
      
      const data = await response.json();
      setSettings(data.data);
      setForm(data.data);
      
    } catch (error) {
      console.error('Error fetching settings:', error);
      addToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    // Pre-submit guard: block multi-address / invalid Reply-To before hitting the
    // server, so the user stays on the form with a clear message instead of a
    // round-trip rejection. The backend still enforces this as the authority.
    const rtCheck = (form.reply_to_email || '').trim();
    if (rtCheck.length > 0) {
      const singleEmailPattern = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;
      if (rtCheck.includes(',') || !singleEmailPattern.test(rtCheck)) {
        addToast(
          rtCheck.includes(',')
            ? 'Reply-To Email can only be one address. Use a forwarding alias or shared inbox to reach multiple organizers.'
            : 'Please enter a valid Reply-To Email address.',
          'error'
        );
        return;
      }
    }

    try {
      setSaving(true);
      
      // PHASE 2D FIX: Include JWT Authorization header for multi-tenant support
      const headers = {
        'Content-Type': 'application/json'
      };
      const token = getAuthToken(); // PHASE 2E FIX
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${API_URL}/settings`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(form)
      });
      
      if (!response.ok) {
        // Surface the backend's specific error message (e.g. the single-address
        // Reply-To rule) instead of a generic "Failed to save settings" toast.
        let backendError = 'Failed to save settings';
        try {
          const errBody = await response.json();
          if (errBody && errBody.error) backendError = errBody.error;
        } catch (_) { /* non-JSON response — keep generic message */ }
        throw new Error(backendError);
      }
      
      const data = await response.json();
      setSettings(data.data);
      addToast('Settings saved successfully!', 'success');
      
      // Refresh the auth context so the org name in the sub-header updates immediately
      if (refreshOrganization) await refreshOrganization();
      
    } catch (error) {
      console.error('Error saving settings:', error);
      addToast(error.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>
        Loading settings...
      </div>
    );
  }
  
  return (
    <div>
      {/* Header row with title and Help button - full width */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>
            🏢 Organization Settings
          </h2>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 8 }}>
            Configure your organization's profile, preferences, and operational settings
          </p>
          {settings && (
            <div style={{ marginTop: 12, padding: 12, background: '#f3f4f6', borderRadius: 8, fontSize: 13, color: '#6b7280' }}>
              <strong>Organization ID:</strong> {settings.group_id}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowHelp(true)}
          style={{
            padding: "6px 12px",
            border: "1px solid #d1d5db",
            background: "#f9fafb",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            color: "#6b7280",
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0
          }}
          title="What is this page about?"
        >
          <span style={{ fontSize: 15 }}>ⓘ</span>
          <span>Help</span>
        </button>
      </div>
      
      {/* Content area */}
      <div style={{ maxWidth: 900 }}>
      
      {/* Band: Operational Settings */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 4px 10px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280', whiteSpace: 'nowrap' }}>Operational Settings</span>
        <span style={{ flex: 1, height: 1, background: '#e5e7eb' }}></span>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
      {/* Reminder Preferences — accordion */}
      <CollapsibleSection grouped={true} title="Player Reminders" subtitle="Automatic email and text reminders before rounds">
        {orgTier && !canUseFeature(orgTier, 'sms') && (
          <div style={{ marginBottom: 16, padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 13, color: '#92400e' }}>
            📧 Email reminders are included with your plan. 📱 <strong>Text message reminders aren't included with your current plan</strong> — players will receive email only. See Subscription &amp; Billing near the top of this page to upgrade.
          </div>
        )}
        
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
            <input
              type="checkbox"
              checked={form.same_day_reminder_enabled}
              onChange={(e) => setForm({ ...form, same_day_reminder_enabled: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                Same-Day Reminders
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Send reminder on the morning of tee time
              </div>
            </div>
            <input
              type="time"
              value={form.same_day_reminder_time?.substring(0, 5) || '06:00'}
              onChange={(e) => setForm({ ...form, same_day_reminder_time: e.target.value + ':00' })}
              disabled={!form.same_day_reminder_enabled}
              style={{ padding: '8px 12px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
            <input
              type="checkbox"
              checked={form.advance_reminder_enabled}
              onChange={(e) => setForm({ ...form, advance_reminder_enabled: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                24-Hour Advance Reminders
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Send reminder day before tee time
              </div>
            </div>
            <input
              type="time"
              value={form.advance_reminder_time?.substring(0, 5) || '09:00'}
              onChange={(e) => setForm({ ...form, advance_reminder_time: e.target.value + ':00' })}
              disabled={!form.advance_reminder_enabled}
              style={{ padding: '8px 12px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
            <input
              type="checkbox"
              checked={form.custom_reminder_enabled}
              onChange={(e) => setForm({ ...form, custom_reminder_enabled: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                Custom Reminder
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Send reminder a set number of days before tee time to those registered to play.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                min="0"
                max="7"
                value={form.custom_reminder_days_before ?? 2}
                onChange={(e) => setForm({ ...form, custom_reminder_days_before: parseInt(e.target.value) })}
                disabled={!form.custom_reminder_enabled}
                style={{ width: 52, padding: '8px 6px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, textAlign: 'center' }}
              />
              <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>days before</span>
              <input
                type="time"
                value={form.custom_reminder_time?.substring(0, 5) || '08:00'}
                onChange={(e) => setForm({ ...form, custom_reminder_time: e.target.value + ':00' })}
                disabled={!form.custom_reminder_enabled}
                style={{ padding: '8px 12px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
            <input
              type="checkbox"
              checked={form.reminder_include_weather}
              onChange={(e) => setForm({ ...form, reminder_include_weather: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                Include Weather in Reminders
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Add weather forecast to reminder emails
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                Handicap Staleness Threshold
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Days before a player's handicap index is considered out of date. Used by the <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{'{handicap_warning}'}</code> reminder template variable.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                min="1"
                max="365"
                value={form.handicap_stale_days ?? 30}
                onChange={(e) => setForm({ ...form, handicap_stale_days: parseInt(e.target.value) || 30 })}
                style={{ width: 60, padding: '8px 6px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, textAlign: 'center' }}
              />
              <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>days</span>
            </div>
          </div>
        </div>
      </CollapsibleSection>
      {/* Player Registration — accordion */}
      <CollapsibleSection grouped={true} title="Player Registration" subtitle="Let players hold or reserve spots for other players">
        
        <div style={{ display: 'grid', gap: 12 }}>
          {/* Master toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
            <input
              type="checkbox"
              checked={!!form.allow_group_rsvp}
              onChange={(e) => setForm({ ...form, allow_group_rsvp: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                Allow players to reserve spots for others
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                When enabled, a confirmed player can hold open slots at their tee time for other players. Nominated players receive an email and must confirm within the window you set, or the spot is released.
              </div>
            </div>
          </div>
          
          {/* Conditional: only visible when toggle is ON */}
          {form.allow_group_rsvp && (
            <>
              {/* Confirmation window */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                    Confirmation Window
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    How long a nominated player has to accept before their held spot is released back to open.
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    min="1"
                    max="72"
                    value={form.group_rsvp_confirmation_hours ?? 24}
                    onChange={(e) => {
                      const n = parseInt(e.target.value);
                      setForm({ ...form, group_rsvp_confirmation_hours: (isNaN(n) ? 24 : Math.max(1, Math.min(72, n))) });
                    }}
                    style={{ width: 60, padding: '8px 6px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, textAlign: 'center' }}
                  />
                  <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>hours (1–72)</span>
                </div>
              </div>
              
              {/* Mode selector */}
              <div style={{ padding: 12, background: '#f9fafb', borderRadius: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 8 }}>
                  Who can reserve spots?
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="group_rsvp_mode"
                    value="all"
                    checked={(form.group_rsvp_mode || 'all') === 'all'}
                    onChange={() => setForm({ ...form, group_rsvp_mode: 'all' })}
                    style={{ marginTop: 3 }}
                  />
                  <div>
                    <div style={{ fontSize: 14, color: '#111827' }}>Any confirmed player</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Any player who has RSVPed can reserve open slots for others.</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="group_rsvp_mode"
                    value="designated"
                    checked={form.group_rsvp_mode === 'designated'}
                    onChange={() => setForm({ ...form, group_rsvp_mode: 'designated' })}
                    style={{ marginTop: 3 }}
                  />
                  <div>
                    <div style={{ fontSize: 14, color: '#111827' }}>Designated Captains only</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Only players with the Captain or Organizer role can reserve spots for others.</div>
                  </div>
                </label>
                {form.group_rsvp_mode === 'designated' && (
                  <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#78350f' }}>
                    Assign the Captain role to players in Player Manager to grant them nomination rights.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </CollapsibleSection>
      {/* Tee Sheet Options — accordion */}
      <CollapsibleSection grouped={true} isLast={true} title="Tee Sheet Display Options" subtitle="What shows on tee sheets — badges, handicaps, times">
        
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="checkbox"
              checked={form.teesheet_show_member_badge}
              onChange={(e) => setForm({ ...form, teesheet_show_member_badge: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                Show Member Badges
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Display ⭐ Member badge for club members in tee sheets
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="checkbox"
              checked={form.teesheet_show_handicap}
              onChange={(e) => setForm({ ...form, teesheet_show_handicap: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                Show Handicaps
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Display player handicaps in tee sheets
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="checkbox"
              checked={form.teesheet_randomize_order}
              onChange={(e) => setForm({ ...form, teesheet_randomize_order: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                Randomize Player Order
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Automatically randomize player order in tee sheets
              </div>
            </div>
          </div>
          
          {/* Sunrise & Sunset Display */}
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
              Sunrise & Sunset
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={form.show_sunrise}
                onChange={(e) => setForm({ ...form, show_sunrise: e.target.checked })}
                style={{ width: 18, height: 18 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                  Show Sunrise on Tee Time Cards
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Display sunrise time on tee time cards and player portal
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="checkbox"
                checked={form.show_sunset}
                onChange={(e) => setForm({ ...form, show_sunset: e.target.checked })}
                style={{ width: 18, height: 18 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                  Show Sunset on Tee Time Cards
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Display sunset time on tee time cards and player portal
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8, fontStyle: 'italic' }}>
              Tip: {'{sunrise}'}, {'{sunset}'}, and {'{daylight_hours}'} placeholders are always available in message templates regardless of these settings.
            </div>
          </div>
        </div>
      </CollapsibleSection>
      </div>
      {/* Band: Initial Setup */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '32px 4px 10px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280', whiteSpace: 'nowrap' }}>Initial Setup</span>
        <span style={{ flex: 1, height: 1, background: '#e5e7eb' }}></span>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
      {/* Identity & Branding — accordion */}
      <CollapsibleSection grouped={true} title="Identity & Branding" subtitle="Your organization's name and email display name">
        
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Organization Name *
            </label>
            <input
              type="text"
              value={form.organization_name}
              onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
              placeholder="My Golf Club"
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
            />
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Used in reports, tee sheets, and emails
            </div>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Display Name (Optional)
            </label>
            <input
              type="text"
              value={form.display_name || ''}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="Short name for emails"
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
            />
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Short version for email "From" field (defaults to Organization Name)
            </div>
          </div>
        </div>
      </CollapsibleSection>
      {/* Contact Information — accordion */}
      <CollapsibleSection grouped={true} title="Organizer Contact & Email Settings" subtitle="Where player replies go, and who they're from">
        
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Organizer Name
              </label>
              <input
                type="text"
                value={form.organizer_name || ''}
                onChange={(e) => setForm({ ...form, organizer_name: e.target.value })}
                placeholder="John Smith"
                style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Organizer Email
              </label>
              <input
                type="email"
                value={form.organizer_email || ''}
                onChange={(e) => setForm({ ...form, organizer_email: e.target.value })}
                placeholder="organizer@example.com"
                style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
              />
            </div>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Organizer Phone
            </label>
            <input
              type="tel"
              value={form.organizer_phone || ''}
              onChange={(e) => setForm({ ...form, organizer_phone: e.target.value })}
              placeholder="(843) 555-1234"
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
            />
          </div>
          
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
              Email Reply Settings
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Reply-To Email
                </label>
                {(() => {
                  const rt = (form.reply_to_email || '').trim();
                  const singleEmailPattern = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;
                  const replyToInvalid = rt.length > 0 && (rt.includes(',') || !singleEmailPattern.test(rt));
                  const hasComma = rt.includes(',');
                  return (
                    <>
                      <input
                        type="email"
                        value={form.reply_to_email || ''}
                        onChange={(e) => setForm({ ...form, reply_to_email: e.target.value })}
                        placeholder="replies@example.com"
                        style={{ width: '100%', padding: '10px 14px', border: `2px solid ${replyToInvalid ? '#dc2626' : '#e5e7eb'}`, borderRadius: 8, fontSize: 14 }}
                      />
                      {replyToInvalid ? (
                        <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>
                          {hasComma
                            ? 'Only one Reply-To address is allowed. To reach several organizers, use a shared inbox or forwarding alias and enter that single address.'
                            : 'Please enter a valid email address.'}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                          Where player replies go — one address only. For multiple people, use a forwarding alias or shared inbox.
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Reply-To Name
                </label>
                <input
                  type="text"
                  value={form.reply_to_name || ''}
                  onChange={(e) => setForm({ ...form, reply_to_name: e.target.value })}
                  placeholder="Golf Committee"
                  style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
                />
              </div>
            </div>
            
            <div style={{ marginTop: 12, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 13 }}>
              <strong>Email Preview:</strong><br />
              From: {form.from_name || form.display_name || form.organization_name} &lt;noreply@teetimeorganizer.net&gt;<br />
              Reply-To: {form.reply_to_name || form.organization_name} &lt;{form.reply_to_email || 'not set'}&gt;
            </div>
          </div>

          {/* Auto-Email Tee Sheet to Proshop (Aug 2026) */}
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Auto-Email Tee Sheet to Proshop
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              Automatically email the tee sheet to the course proshop before each tee time — no manual send needed.
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={form.auto_proshop_email_enabled === true}
                onChange={(e) => setForm({ ...form, auto_proshop_email_enabled: e.target.checked })}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Enable automatic proshop tee sheet emails</span>
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, opacity: form.auto_proshop_email_enabled ? 1 : 0.5 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Hours before tee time
                </label>
                <input
                  type="number"
                  min="1"
                  max="72"
                  value={form.auto_proshop_email_hours_before ?? 3}
                  disabled={!form.auto_proshop_email_enabled}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value, 10);
                    const clamped = isNaN(raw) ? '' : Math.min(72, Math.max(1, raw));
                    setForm({ ...form, auto_proshop_email_hours_before: clamped });
                  }}
                  style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
                />
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  How far ahead of each tee time to send (1-72 hours). Default 3.
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Copy to organizer
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: form.auto_proshop_email_enabled ? 'pointer' : 'not-allowed', padding: '8px 0' }}>
                  <input
                    type="checkbox"
                    checked={form.auto_proshop_email_bcc_organizer === true}
                    disabled={!form.auto_proshop_email_enabled}
                    onChange={(e) => setForm({ ...form, auto_proshop_email_bcc_organizer: e.target.checked })}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontSize: 14, color: '#374151' }}>BCC me on each auto-send</span>
                </label>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Sends a copy to your Organizer Email{form.organizer_email ? ` (${form.organizer_email})` : ''} so you have proof it went out.
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, padding: 12, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
              <strong>Note:</strong> Auto-send only works for courses that have a <strong>Proshop Email</strong> set (Settings → Courses). Tee times at courses without one are skipped. A tee sheet is sent only when the tee time has at least one confirmed player.
            </div>
          </div>
        </div>
      </CollapsibleSection>
      {/* Location & Timezone — accordion */}
      <CollapsibleSection grouped={true} title="Location & Time Zone" subtitle="Your area and timezone for scheduling">
        
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Timezone *
            </label>
            <select
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Phoenix">Arizona Time (no DST)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Anchorage">Alaska Time (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
            </select>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Used for reminder scheduling and display times
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                City
              </label>
              <input
                type="text"
                value={form.default_city || ''}
                onChange={(e) => setForm({ ...form, default_city: e.target.value })}
                placeholder="Charleston"
                style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                State
              </label>
              <input
                type="text"
                value={form.default_state || ''}
                onChange={(e) => setForm({ ...form, default_state: e.target.value })}
                placeholder="South Carolina"
                style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Zip Code
              </label>
              <input
                type="text"
                value={form.default_zip || ''}
                onChange={(e) => setForm({ ...form, default_zip: e.target.value })}
                placeholder="29401"
                style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
              />
            </div>
          </div>
        </div>
      </CollapsibleSection>
      {/* Display Preferences — accordion */}
      <CollapsibleSection grouped={true} title="Display Preferences" subtitle="Date and time formats">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Date Format
            </label>
            <select
              value={form.date_format}
              onChange={(e) => setForm({ ...form, date_format: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY (International)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Time Format
            </label>
            <select
              value={form.time_format}
              onChange={(e) => setForm({ ...form, time_format: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
            >
              <option value="12h">12-hour (9:00 AM)</option>
              <option value="24h">24-hour (09:00)</option>
            </select>
          </div>
        </div>
      </CollapsibleSection>
      {/* Team Members Section — accordion */}
      <CollapsibleSection grouped={true} isLast={true} title="Additional Organizers" subtitle="Organizers who can help manage your group">
        <TeamMembersSection />
      </CollapsibleSection>
      </div>
      {/* Band: Account & Billing */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '32px 4px 10px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280', whiteSpace: 'nowrap' }}>Account & Billing</span>
        <span style={{ flex: 1, height: 1, background: '#e5e7eb' }}></span>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
      {/* Subscription & Billing — accordion */}
      <CollapsibleSection grouped={true} defaultOpen={new URLSearchParams(window.location.search).get('upgrade') === '1'} title="Subscription & Billing" subtitle="Your plan, player count, and payment">
        <SubscriptionStatusSection />
      </CollapsibleSection>
      {/* Referral Program — accordion */}
      <CollapsibleSection grouped={true} title="Referral Program" subtitle="Invite another organizer, both get a free month">
        <ReferralShareSection settings={settings} />
      </CollapsibleSection>
      {/* Testimonial Submission — accordion */}
      <CollapsibleSection grouped={true} isLast={true} title="Testimonials" subtitle="Tell us how it's going — help other organizers find us">
        <TestimonialSection settings={settings} />
      </CollapsibleSection>
      </div>

      
      {/* Save Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <Button
          variant="outline"
          onClick={fetchSettings}
          disabled={saving}
        >
          Reset Changes
        </Button>
        <Button
          onClick={isReadOnly ? handleReadOnlyClick : handleSave}
          disabled={isReadOnly || saving}
          style={{ 
            background: isReadOnly ? '#9ca3af' : '#2d6a4f', 
            color: '#fff', 
            padding: '12px 32px', 
            fontWeight: 600,
            cursor: isReadOnly ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saving...' : '💾 Save Settings'}
        </Button>
      </div>
      </div>
      
      {/* Help Modal */}
      <HelpInfoModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={helpContent.title}
        content={helpContent.content}
      />
    </div>
  );
};

// ============================================================================
// TOOLS VIEW - Organizer Tools including Message Log
// ============================================================================

// ============================================================================
// PrintCenter — the 🖨️ Print tab (Sept 17, 2026). Dennis: "a prints main menu
// choice … so it would be obvious." Pick a round, then everything printable for
// it: scorecards, cart signs, the tee sheet, team codes, and (organizer,
// per-player rounds) the Scorecard Report. On the event desk it holds that one
// event's paperwork only — never the club-wide reports.
// Status lines read the tee time's stamps: pairings_saved_at vs the last
// scorecards / cart signs print, so paper printed before a re-pair is called out.
// ============================================================================
const PRINT_BACK_DAYS = 14;
const printStamp = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// ---------------------------------------------------------------------------
// POSTING SHEET LAYOUT CHOOSER — Sept 19, 2026. Plan: Score_Posting_Sheets_Plan_v2 §4.
//
// The organizer chooses an OUTCOME, not a parameter. "Teams per sheet" is our
// internal variable; what a pro shop knows is how many sheets it wants to
// staple and what board it bought. So every tile states the whole result —
// sheet count, the board it fits, teams per sheet, name height — beside a
// picture of the layout. That framing is what makes a bad pick visible: at
// two-player teams, 2 per sheet and 4 per sheet give IDENTICAL name height
// (the name column's width binds before row height does), so 2 per sheet is
// simply twice the paper for nothing. A dropdown could not show that.
//
// The thumbnails are SVG schematics, not rendered pages: they take their
// proportions from the renderer's own geometry (below) and adapt to any team
// size, where a fixed set of images could not. They never pretend to be the
// document — the Print button opens the real PDF in a tab, which is the true
// preview and already exists.
// ---------------------------------------------------------------------------

// Fractions of the page, lifted from printService's posting-sheet constants
// (PS_HEAD_B / PS_FOOT_T / PS_START / PS_NAME / PS_GROSS / PS_NET over 612 × 792).
const PSF = {
  headB: 122 / 792, footT: 726 / 792,
  start: [18 / 612, 52 / 612], name: [78 / 612, 294 / 612],
  gross: [392 / 612, 96 / 612], net: [498 / 612, 96 / 612],
  nameMax: 46, boxHMax: 140 / 792,
};
const POSTING_OPTIONS = [2, 3, 4, 6, 8];
const POSTING_MIN_NAME = 12;

/** Name point size the renderer will use — mirrors postingNameSize (height only). */
const postingNameSize = (per, members) => {
  const rowH = (726 - 122) / per;
  return Math.min(PSF.nameMax, Math.min((rowH - 18) / Math.max(1, members), 56) * 0.74);
};

// Craft-store foam board sizes, smallest first. Capacity is whole portrait
// letter sheets, better orientation winning.
const FOAM_BOARDS = [[20, 30], [24, 36], [30, 40], [36, 48]];
const boardCapacity = (w, h) =>
  Math.max(Math.floor(w / 8.5) * Math.floor(h / 11), Math.floor(h / 8.5) * Math.floor(w / 11));
/** Plain-language board for a sheet count — computed, never a lookup table. */
const boardFor = (sheets) => {
  for (const [w, h] of FOAM_BOARDS) if (boardCapacity(w, h) >= sheets) return `one ${w} × ${h} board`;
  const [w, h] = FOAM_BOARDS[FOAM_BOARDS.length - 1];
  return `${Math.ceil(sheets / boardCapacity(w, h))} boards of ${w} × ${h}`;
};

/** A miniature of the sheet: header, N rows of M name bars, two score boxes, footer. */
const LayoutThumb = ({ per, members, w = 132, selected }) => {
  const h = Math.round(w * 11 / 8.5);
  const top = h * PSF.headB, bot = h * PSF.footT, rowH = (bot - top) / per;
  const nameSize = postingNameSize(per, members);
  const barH = Math.max(1.2, (nameSize / 792) * h);
  const lead = Math.min((rowH - (18 / 792) * h) / members, barH * 1.26);
  const boxH = Math.min(rowH - (16 / 792) * h, PSF.boxHMax * h);
  const px = (f) => f * w;
  const rows = [];
  for (let i = 0; i < per; i++) {
    const y = top + i * rowH;
    const blockTop = y + (rowH - members * lead) / 2;
    rows.push(
      <g key={i}>
        {i > 0 && <line x1={px(0.029)} x2={px(0.971)} y1={y} y2={y} stroke="#d8d8d8" strokeWidth="0.5" />}
        <rect x={px(PSF.start[0])} y={y + rowH / 2 - barH * 0.9} width={px(PSF.start[1])} height={barH * 1.5} rx="1" fill="#374151" opacity="0.85" />
        {Array.from({ length: members }, (_, j) => (
          <rect key={j} x={px(PSF.name[0])} y={blockTop + j * lead} rx="1"
            width={px(PSF.name[1]) * (j % 2 ? 0.74 : 0.92)} height={barH} fill="#111827" opacity="0.8" />
        ))}
        <rect x={px(PSF.gross[0])} y={y + (rowH - boxH) / 2} width={px(PSF.gross[1])} height={boxH} fill="#fff" stroke="#9a9a9a" strokeWidth="0.7" />
        <rect x={px(PSF.net[0])} y={y + (rowH - boxH) / 2} width={px(PSF.net[1])} height={boxH} fill="#fff" stroke="#9a9a9a" strokeWidth="0.7" />
      </g>
    );
  }
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} role="img" aria-hidden="true"
      style={{ display: 'block', background: '#fff', border: `1px solid ${selected ? '#2d6a4f' : '#e5e7eb'}`, borderRadius: 3 }}>
      <rect x={px(0.029)} y={h * 0.022} width={px(0.088)} height={h * 0.068} fill="none" stroke="#d8d8d8" strokeDasharray="2 2" strokeWidth="0.6" />
      <rect x={px(0.30)} y={h * 0.028} width={px(0.40)} height={h * 0.026} rx="1" fill="#111827" opacity="0.85" />
      <rect x={px(0.36)} y={h * 0.064} width={px(0.28)} height={h * 0.013} rx="1" fill="#9ca3af" />
      <line x1={px(0.029)} x2={px(0.971)} y1={h * 0.098} y2={h * 0.098} stroke="#9a9a9a" strokeWidth="0.8" />
      <rect x={px(0.029)} y={h * 0.106} width={px(0.942)} height={h * 0.025} fill="#ececec" />
      <rect x={px(PSF.gross[0])} y={h * 0.112} width={px(PSF.gross[1]) * 0.62} height={h * 0.011} rx="1" fill="#6b7280" />
      <rect x={px(PSF.net[0])} y={h * 0.112} width={px(PSF.net[1]) * 0.42} height={h * 0.011} rx="1" fill="#b3261e" />
      {rows}
      <line x1={px(0.029)} x2={px(0.971)} y1={bot} y2={bot} stroke="#9a9a9a" strokeWidth="0.8" />
      <rect x={px(0.029)} y={h * 0.952} width={px(0.03)} height={h * 0.022} rx="1.5" fill="#1b4332" />
      <rect x={px(0.70)} y={h * 0.958} width={px(0.27)} height={h * 0.011} rx="1" fill="#9ca3af" />
    </svg>
  );
};

/**
 * The chooser. `rows` is how many rows the board will hold (teams on a team
 * round, players otherwise) and `members` the largest team, which is what
 * decides whether an option's names stay readable.
 */
const PostingLayoutModal = ({ rows, members, isTeam, value, onPick, onClose }) => {
  const usable = POSTING_OPTIONS.filter(p => postingNameSize(p, members) >= POSTING_MIN_NAME);
  // Ordered by SHEET COUNT, the headline number: fewest sheets and smallest
  // names on the left, most paper and biggest names on the right.
  const opts = (usable.length ? usable : [2]).map(p => ({
    per: p, sheets: Math.ceil(Math.max(1, rows) / p), name: postingNameSize(p, members),
  })).sort((a, b) => a.sheets - b.sheets);
  const rec = [...opts].sort((a, b) => Math.abs(a.sheets - 6) - Math.abs(b.sheets - 6) || a.per - b.per)[0];
  const chosen = value ? parseInt(value, 10) : rec.per;
  const noun = isTeam ? 'teams' : 'players';
  // The cheapest option that loses nothing: same name height, fewest sheets.
  const best = [...opts].filter(o => o.name >= rec.name - 0.01).sort((a, b) => a.sheets - b.sheets)[0];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Choose a posting sheet layout"
        style={{ background: '#fff', borderRadius: 14, maxWidth: 900, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 20, boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>📌 Choose a board layout</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>
              {rows} {noun} to post{members > 1 ? ` · ${members} players per team` : ''}. Every sheet looks the same — you are choosing how many {noun} go on each one.
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: '#6b7280', cursor: 'pointer', padding: 4 }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginTop: 16 }}>
          {opts.map(o => {
            const on = o.per === chosen;
            return (
              <button key={o.per} type="button" onClick={() => onPick(String(o.per))}
                data-testid={`layout-opt-${o.per}`}
                style={{ textAlign: 'left', cursor: 'pointer', background: on ? '#f0f7f3' : '#fff', border: `2px solid ${on ? '#2d6a4f' : '#e5e7eb'}`, borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <LayoutThumb per={o.per} members={members} selected={on} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{o.sheets} sheet{o.sheets === 1 ? '' : 's'}</div>
                  <div style={{ fontSize: 12, color: '#4b5563' }}>{boardFor(o.sheets)}</div>
                  <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 3 }}>
                    {o.per} {noun} per sheet · names {(o.name * 0.717 / 72).toFixed(2)}″
                  </div>
                  {o.per === rec.per && (
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#2d6a4f', marginTop: 4 }}>✓ Recommended</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {best && best.per !== chosen && Math.ceil(Math.max(1, rows) / chosen) > best.sheets && (
          <div style={{ marginTop: 14, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, color: '#92400e' }}>
            {best.per} per sheet prints the names at the same size on <strong>{best.sheets} sheets</strong> instead of {Math.ceil(Math.max(1, rows) / chosen)} — below {best.per} per sheet the extra room goes to white space, not bigger type.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={() => onPick(String(chosen), true)}
            style={{ padding: '8px 18px', background: '#2d6a4f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Print {Math.ceil(Math.max(1, rows) / chosen)} sheet{Math.ceil(Math.max(1, rows) / chosen) === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PrintCenter = () => {
  const deskSession = useContext(DeskContext);
  const appNav = useContext(AppNavContext);
  const { isMobile } = useContext(MobileContext);
  const { data: teeTimes, loading, refresh } = useApiData('/teetimes');
  const { data: events } = useApiData('/events');
  const [pickedId, setPickedId] = useState(() => { const i = appNav.readIntent('print-round'); return i ? i.teetimeId : null; });
  const [showCodes, setShowCodes] = useState(false);
  const [busy, setBusy] = useState(null);
  const [per, setPer] = useState('');   // '' = let the renderer recommend
  const [showLayout, setShowLayout] = useState(false);
  const [printStops, setPrintStops] = useState([]);   // slice 3: must-fix items, fetched ahead of the click
  useEffect(() => {
    appNav.clearIntent('print-round');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eventList = Array.isArray(events) ? events : (events && Array.isArray(events.events) ? events.events : []);
  const eventByTt = {};
  eventList.forEach(ev => { if (ev && ev.teetime_id) eventByTt[ev.teetime_id] = ev; });
  const cutoff = utils.getTodayTimestamp() - PRINT_BACK_DAYS * 86400000;
  const rounds = (Array.isArray(teeTimes) ? teeTimes : [])
    .filter(t => Array.isArray(t.players) && t.players.length > 0 && utils.getDateTimestamp(t.date, t.time) >= cutoff)
    .sort((a, b) => utils.getDateTimestamp(a.date, a.time) - utils.getDateTimestamp(b.date, b.time));
  const upcoming = rounds.find(t => utils.getDateTimestamp(t.date, t.time) >= utils.getTodayTimestamp());
  const selectedId = (pickedId && rounds.some(t => t.id === pickedId)) ? pickedId
    : (deskSession && rounds.length === 1 ? rounds[0].id : (pickedId ? null : (upcoming ? upcoming.id : null)));
  const round = rounds.find(t => t.id === selectedId) || null;
  const ev = round ? eventByTt[round.id] || null : null;
  const isTeam = !!round && (round.scoring_mode === 'team' || (ev && ev.scoring_mode === 'team'));
  const groups = round ? new Set(round.players.filter(p => p.pairingGroup != null).map(p => p.pairingGroup)).size : 0;
  const unpaired = round ? round.players.filter(p => p.pairingGroup == null).length : 0;
  const savedAt = round ? round.pairings_saved_at : null;
  const staleSince = (printedAt) => !!(printedAt && savedAt && new Date(savedAt) > new Date(printedAt));
  const label = (t) => `${utils.formatDate(t.date)} · ${utils.formatTime(t.time)} · ${t.course}${eventByTt[t.id] ? ` · 📅 ${eventByTt[t.id].name}` : ''} (${t.players.length} player${t.players.length === 1 ? '' : 's'})`;

  const printEventId = ev ? ev.id : null;
  useEffect(() => {
    let off = false;
    setPrintStops([]);
    if (printEventId) readyStops({ eventId: printEventId }).then(st => { if (!off) setPrintStops(st); });
    return () => { off = true; };
  }, [printEventId]);
  // Oct 26, 2026 (captainTeamService): names on the cards should be final, so printing while the
  // teams are still open asks first — a reminder, never a stop. Fetched ahead of the click.
  // Organizer only: the Event Desk can't read the teams endpoint, so it gets no reminder.
  const [teamsOpen, setTeamsOpen] = useState(false);
  useEffect(() => {
    let off = false;
    setTeamsOpen(false);
    if (printEventId && !deskSession) {
      fetchEventTeams(API_URL, getAuthToken, printEventId)
        .then(d => { if (!off) setTeamsOpen(!!(d && d.totals && d.totals.teams > 0 && !d.locked)); })
        .catch(() => {});
    }
    return () => { off = true; };
  }, [printEventId, deskSession]);

  const printPdf = async (kind, query) => {
    if (!round) return;
    // Slice 3: scorecards carry handicaps, so must-fix items ask first. Synchronous on purpose:
    // the PDF window has to open on the click or the browser blocks it.
    if (kind === 'scorecards' && printStops.length && !window.confirm(mustFixMessage(printStops, 'print'))) return;
    if ((kind === 'scorecards' || kind === 'cart-signs') && teamsOpen
      && !window.confirm("Teams aren't locked — captains can still change names.\n\nPrint anyway? (Cancel, then lock them under 👥 Teams on the event.)")) return;
    setBusy(kind);
    const ok = await openRoundPdf(round.id, kind, query);
    setBusy(null);
    if (ok && refresh) refresh(true);
  };
  const printTeeSheet = () => {
    if (!round) return;
    const w = window.open('', '_blank');
    if (!w) { window.alert('Your browser blocked the print window. Allow pop-ups for this site and try again.'); return; }
    try { w.document.title = 'Tee sheet'; w.document.body.innerHTML = '<p style="font-family:sans-serif;padding:24px;color:#374151">Preparing the tee sheet…</p>'; } catch (e) { /* closed */ }
    appNav.goTo('pairings', { kind: 'pairings-round', teetimeId: round.id, printTeeSheet: true, win: w });
  };

  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 };
  const btn = (disabled) => ({ alignSelf: 'flex-start', padding: '8px 16px', background: disabled ? '#9ca3af' : '#2d6a4f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: disabled ? 'default' : 'pointer' });
  const note = (color) => ({ fontSize: 12, color, lineHeight: 1.45 });
  const noGroups = groups === 0;
  const printedLine = (ts) => {
    const when = printStamp(ts);
    if (!when) return <div style={note('#6b7280')}>Not printed yet.</div>;
    if (staleSince(ts)) return <div style={note('#b45309')} data-testid="print-stale">⚠️ Last printed {when} — the pairings changed after that ({printStamp(savedAt)}). Reprint.</div>;
    return <div style={note('#166534')}>✓ Last printed {when}</div>;
  };
  // Printed scorecards and cart signs are EVENT pieces (Dennis, Sept 17): they are
  // offered only for a tee time flagged as an event, never for a standard round.
  // The Sept 15 note that "any org with saved pairings can print" described the
  // route's lack of a tier gate, not an intention to offer these on every round.
  // The tee sheet and the Scorecard Report are not event pieces and stay for all.
  // POSTING BOARD (Score_Posting_Sheets_Plan_v2 §4). Rows on a board are TEAMS
  // on a team round and PLAYERS otherwise; `boardMembers` is the largest team,
  // which is what decides whether an option's names stay readable. The picker
  // The layout is chosen inside PostingLayoutModal, which the Print button
  // opens — nothing about it sits on the card. The SERVER stays authoritative:
  // leaving `per` empty sends no query and printService recommends.
  const boardRows = round ? (isTeam ? groups : round.players.length) : 0;
  const boardMembers = (isTeam && groups) ? Math.ceil(round.players.length / groups) : 1;

  const items = !round ? [] : [
    ...(!ev ? [] : [
    {
      key: 'scorecards', icon: '🗂️', title: 'Scorecards',
      text: 'One card per saved group, two per letter sheet — for golfcardstock.com 2-up scorecard stock.' + (isTeam ? " Each card carries the team's code and QR." : " Each card carries the group's code and QR once codes are issued (Scorecard codes, below)."),
      action: <button type="button" style={btn(noGroups || busy === 'scorecards')} disabled={noGroups || busy === 'scorecards'} onClick={() => printPdf('scorecards')}>{busy === 'scorecards' ? 'Building…' : 'Print scorecards'}</button>,
      status: (
        <>
          {teamsOpen && <div style={note('#b45309')} data-testid="print-teams-open">🔓 Teams aren't locked — captains can still change names. Lock them under 👥 Teams before printing the final cards.</div>}
          {printStops.length > 0 && <div style={note('#b42318')}>⚠️ The Ready check has {printStops.length} must-fix item{printStops.length === 1 ? '' : 's'}: {printStops.slice(0, 3).map(i => i.title).join('; ')}{printStops.length > 3 ? '…' : ''}. Fix first, or print anyway.</div>}
          {printedLine(round.scorecards_printed_at)}
        </>
      ),
    },
    {
      key: 'cart-signs', icon: '🚗', title: 'Cart signs',
      text: 'One sign per cart, two per letter sheet — names in large type so players find their cart.',
      action: <button type="button" style={btn(noGroups || busy === 'cart-signs')} disabled={noGroups || busy === 'cart-signs'} onClick={() => printPdf('cart-signs')}>{busy === 'cart-signs' ? 'Building…' : 'Print cart signs'}</button>,
      status: (
        <>
          {teamsOpen && <div style={note('#b45309')}>🔓 Teams aren't locked — captains can still change names.</div>}
          {printedLine(round.cart_signs_printed_at)}
        </>
      ),
    },
    {
      key: 'posting-sheets', icon: '📌', title: 'Posting board sheets', thumb: 'posting.png',
      text: 'Best for: a board on the wall when you are not showing the live leaderboard.',
      help: "Blank sheets to staple to a foam board and fill in by hand, ordered by starting hole so the desk can find a team as the card is handed over. You don't need these if you're showing the live leaderboard on a monitor — it updates itself. Print them anyway if you want a paper backup: if the wifi goes down you won't be able to print them then.",
      action: <button type="button" style={btn(noGroups || busy === 'posting-sheets')} disabled={noGroups || busy === 'posting-sheets'} onClick={() => setShowLayout(true)}>{busy === 'posting-sheets' ? 'Building…' : 'Print posting sheets'}</button>,
      status: null,
    },
    {
      key: 'event-sign', icon: '🏁', title: 'Event sign', thumb: 'sign.png',
      text: 'Best for: the middle of the board — the event name, large.',
      help: 'One sheet carrying the event name and date, to sit in the centre of the assembled board. It is what makes a wall of sheets read as one board.',
      action: <button type="button" style={btn(busy === 'event-sign')} disabled={busy === 'event-sign'} onClick={() => printPdf('event-sign')}>{busy === 'event-sign' ? 'Building…' : 'Print event sign'}</button>,
      status: null,
    },
    {
      key: 'desk-sheet', icon: '📝', title: 'Desk recording sheet', thumb: 'desk.png',
      text: 'Best for: writing scores down at the desk as cards come in.',
      help: 'The whole field on one or two pages, with a CARD IN box so you can see which teams are still out. Same caution as the board sheets: print it before the round, because if the wifi is down you cannot generate it then.',
      action: <button type="button" style={btn(noGroups || busy === 'desk-sheet')} disabled={noGroups || busy === 'desk-sheet'} onClick={() => printPdf('desk-sheet')}>{busy === 'desk-sheet' ? 'Building…' : 'Print desk sheet'}</button>,
      status: null,
    },
    {
      // Oct 4, 2026 (Event_Ready_Check_Plan_v1.md slice 2b)
      key: 'checkin-sheet', icon: '✅', title: 'Check-in sheet',
      text: 'Best for: the check-in table — tick golfers in on paper, then key the marks into Check-in.',
      help: "Every golfer A to Z by last name, with his team and start, a HERE box and a box for each fee you set up in Event Setup (entry fee, mulligans…). Anything already recorded in TTO — a prepaid entry fee, someone already checked in — prints ticked, so whoever is at the table knows to collect or not. The waitlist, with phone numbers, is at the end. Two copies splits the list by last name (A–K and L–Z, say) for two tables.",
      action: (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={btn(busy === 'checkin-sheet')} disabled={busy === 'checkin-sheet'} onClick={() => printPdf('checkin-sheet')}>{busy === 'checkin-sheet' ? 'Building…' : 'Print check-in sheet'}</button>
          <button type="button" style={{ ...btn(busy === 'checkin-sheet'), background: '#fff', color: '#2d6a4f', border: '1px solid #2d6a4f' }} disabled={busy === 'checkin-sheet'} onClick={() => printPdf('checkin-sheet', 'split=2')}>Two copies (split by last name)</button>
        </div>
      ),
      status: null,
    },
    ]),
    {
      key: 'tee-sheet', icon: '📋', title: 'Tee sheet',
      text: 'The pairings list as it looks on the Pairings tab — groups, times or starting holes, and tees. Opens the Pairings tab on this round.',
      action: <button type="button" style={btn(false)} onClick={printTeeSheet}>Print tee sheet</button>,
      status: null,
    },
    ...(isTeam ? [{
      key: 'team-codes', icon: '🔑', title: 'Team codes',
      text: "One code per team — it's on the scorecards and cart signs. Print the code sheet, or make new codes after re-pairing.",
      action: <button type="button" style={btn(noGroups)} disabled={noGroups} onClick={() => setShowCodes(true)}>Team codes…</button>,
      status: null,
    }] : ev ? [{
      // Individual event rounds (Sept 28, 2026): one code per group, on its card and cart sign.
      key: 'team-codes', icon: '🔑', title: 'Scorecard codes',
      text: "One code per group, printed on its scorecard and cart sign. A player scans it, taps his own name and his card opens — no TTO link needed. Issue the codes before you print the scorecards.",
      action: <button type="button" style={btn(noGroups)} disabled={noGroups} onClick={() => setShowCodes(true)}>Scorecard codes…</button>,
      status: null,
    }] : []),
  ];
  const after = (!round || deskSession || isTeam) ? [] : [{
    key: 'scorecard-report', icon: '⛳', title: 'Scorecard Report',
    text: "Every player's card for this round — gross, net and putts — ready to print.",
    action: <button type="button" style={btn(false)} onClick={() => appNav.goTo('admin', { kind: 'admin-reports', teetimeId: round.id })}>Open the report</button>,
    status: null,
  }];
  // A picture of the document answers "which one do I want" faster than the copy
  // can. The thumbnails are RENDERED FROM printService against fixture data, not
  // drawn by hand — regenerate them when a layout changes or the tab starts lying.
  const renderItem = (it) => (
    <div key={it.key} style={card} data-testid={`print-item-${it.key}`}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {it.thumb && (
          <img src={`${process.env.PUBLIC_URL || ''}/print-thumbs/${it.thumb}`} alt="" width={96}
            style={{ flex: 'none', width: 96, height: 'auto', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff' }} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{it.icon} {it.title}</div>
          <div style={note('#4b5563')}>{it.text}</div>
          {it.control}
          {it.help && (
            <details style={{ fontSize: 12.5, color: '#4b5563' }}>
              <summary style={{ cursor: 'pointer', color: '#2d6a4f', fontWeight: 600 }}>When to use this</summary>
              <div style={{ marginTop: 6, lineHeight: 1.5 }}>{it.help}</div>
            </details>
          )}
          {it.action}
          {it.status}
        </div>
      </div>
    </div>
  );
  const grid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 };

  return (
    <div style={{ display: 'grid', gap: 16, padding: isMobile ? 4 : 0 }} data-testid="print-center">
      {!isMobile && (
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#111827' }}>🖨️ Print</h2>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Everything to print for a round, in one place.</div>
        </div>
      )}
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
        <label htmlFor="print-round" style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Round</label>
        <select id="print-round" value={selectedId || ''} onChange={(e) => setPickedId(e.target.value || null)} disabled={loading && rounds.length === 0}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff' }}>
          <option value="">{loading && rounds.length === 0 ? 'Loading rounds…' : '— Choose a round —'}</option>
          {rounds.map(t => <option key={t.id} value={t.id}>{label(t)}</option>)}
        </select>
        {!loading && rounds.length === 0 && (
          <div style={{ ...note('#6b7280'), marginTop: 8 }}>No rounds with players in the last {PRINT_BACK_DAYS} days or ahead.</div>
        )}
        {round && (
          <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.5 }} data-testid="print-round-status">
            {noGroups ? (
              <div style={{ color: '#92400e', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: '8px 10px' }}>
                No saved pairings yet — {ev ? 'scorecards and cart signs print' : 'the tee sheet prints'} one per saved group.{' '}
                <button type="button" onClick={() => appNav.goTo('pairings', { kind: 'pairings-round', teetimeId: round.id })}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>
                  Set the groups on the Pairings tab
                </button>
              </div>
            ) : (
              <div style={{ color: '#166534' }}>
                ✓ {groups} group{groups === 1 ? '' : 's'} saved{savedAt ? ` · pairings last saved ${printStamp(savedAt)}` : ''}
                {unpaired > 0 && <span style={{ color: '#b45309' }}> · {unpaired} player{unpaired === 1 ? ' is' : 's are'} not in a group yet</span>}
              </div>
            )}
            {ev && ev.public_slug && (
              <div style={{ color: '#4b5563' }}>Live board address on the paper: <strong>{PUBLIC_BOARD_HOST}/{ev.public_slug}</strong></div>
            )}
          </div>
        )}
      </div>

      {round && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>Before the round</div>
          <div style={grid}>{items.map(renderItem)}</div>
          {after.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>After the round</div>
              <div style={grid}>{after.map(renderItem)}</div>
            </>
          )}
        </>
      )}
      {showLayout && round && (
        <PostingLayoutModal rows={boardRows} members={boardMembers} isTeam={isTeam} value={per}
          onPick={(v, print) => { setPer(v); setShowLayout(false); if (print) printPdf('posting-sheets', `per=${v}`); }}
          onClose={() => setShowLayout(false)} />
      )}
      {showCodes && round && <TeamCodesModal teeTime={round} onClose={() => { setShowCodes(false); if (refresh) refresh(true); }} />}
    </div>
  );
};

const ToolsView = () => {
  const { isMobile } = useContext(MobileContext);
  const [activeToolTab, setActiveToolTab] = useState('messagelog');
  
  return (
    <div style={{ padding: isMobile ? 4 : 16 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16, color: '#111827' }}>
        🛠️ Tools
      </h2>
      
      {/* Tool Tabs */}
      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
        <button
          onClick={() => setActiveToolTab('messagelog')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: 'transparent',
            borderBottom: activeToolTab === 'messagelog' ? '2px solid #2d6a4f' : '2px solid transparent',
            color: activeToolTab === 'messagelog' ? '#2d6a4f' : '#6b7280',
            fontWeight: activeToolTab === 'messagelog' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          📧 Message Log
        </button>
      </div>
      
      {activeToolTab === 'messagelog' && <MessageLogView />}
    </div>
  );
};

// ============================================================================
// MESSAGE LOG VIEW - Email history for the organization
// ============================================================================

const MessageLogView = () => {
  const { addToast } = useContext(ToastContext);
  const { isMobile } = useContext(MobileContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    type: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [types, setTypes] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  // Fetch message types for filter dropdown
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch('/api/message-log/types', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          console.error('Message log types API error:', response.status);
          return;
        }
        const data = await response.json();
        if (data.success) {
          setTypes(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch message types:', error);
      }
    };
    fetchTypes();
  }, []);

  // Fetch messages
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const token = getAuthToken();
        const params = new URLSearchParams({
          page: pagination.page,
          limit: pagination.limit,
          ...(filters.type !== 'all' && { type: filters.type }),
          ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
          ...(filters.dateTo && { dateTo: filters.dateTo }),
          ...(filters.search && { search: filters.search })
        });
        
        const response = await fetch(`/api/message-log?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          console.error('Message log API error:', response.status, response.statusText);
          const errorText = await response.text();
          console.error('Error response:', errorText);
          addToast(`Failed to load message log (${response.status})`, 'error');
          return;
        }
        
        const data = await response.json();
        
        if (data.success) {
          setMessages(data.data);
          setPagination(prev => ({ ...prev, ...data.pagination }));
        } else {
          addToast('Failed to load message log', 'error');
        }
      } catch (error) {
        console.error('Failed to fetch messages:', error);
        addToast('Failed to load message log', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Database returns timestamp without timezone - treat as UTC
    let ts = dateStr;
    if (typeof ts === 'string' && !ts.endsWith('Z') && !ts.includes('+')) {
      ts = ts + 'Z'; // Append Z to interpret as UTC
    }
    return new Date(ts).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatType = (type) => {
    if (!type) return '-';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusBadge = (status) => {
    const colors = {
      sent: { bg: '#d1fae5', color: '#065f46' },
      failed: { bg: '#fee2e2', color: '#991b1b' },
      pending: { bg: '#fef3c7', color: '#92400e' }
    };
    const style = colors[status] || colors.pending;
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 500,
        background: style.bg,
        color: style.color
      }}>
        {status || 'unknown'}
      </span>
    );
  };

  return (
    <div>
      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 16,
        flexWrap: 'wrap',
        alignItems: 'flex-end'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Type</label>
          <select
            value={filters.type}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, type: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          >
            <option value="all">All Types</option>
            {types.map(t => (
              <option key={t} value={t}>{formatType(t)}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>From</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, dateFrom: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>To</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, dateTo: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </div>
        
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Search</label>
          <input
            type="text"
            placeholder="Search by email, name, or subject..."
            value={filters.search}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, search: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </div>
        
        <button
          onClick={() => {
            setFilters({ type: 'all', dateFrom: '', dateTo: '', search: '' });
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          style={{
            padding: '8px 16px',
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          Clear
        </button>
      </div>

      {/* Results Summary */}
      <div style={{ marginBottom: 12, color: '#6b7280', fontSize: 14 }}>
        Showing {messages.length} of {pagination.total} messages
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>Loading...</div>
      ) : messages.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#6b7280', background: '#f9fafb', borderRadius: 8 }}>
          No messages found
        </div>
      ) : isMobile ? (
        // Phase 4 (May 2026): On mobile, render each message as a stacked card with
        // 2-column layout for compact short fields. Subject gets full width on top
        // since it's the primary identifier; Recipient/Date and Type/Status pair up.
        <div style={{ display: 'grid', gap: 8 }}>
          {messages.map(msg => (
            <div
              key={msg.id}
              onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
              style={{
                background: expandedId === msg.id ? '#f0fdf4' : '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: 10,
                cursor: 'pointer'
              }}
            >
              {/* Subject (full width) */}
              <div style={{
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 2,
                lineHeight: 1.3,
                wordBreak: 'break-word'
              }}>
                {msg.subject}
              </div>
              {msg.course_name && (
                <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 8 }}>
                  {msg.course_name}{msg.tee_time_date && ` · ${(() => {
                    const [y, m, d] = (msg.tee_time_date || '').split('T')[0].split('-').map(Number);
                    return new Date(y, m - 1, d).toLocaleDateString();
                  })()}`}
                </div>
              )}
              {!msg.course_name && <div style={{ height: 6 }} />}

              {/* Recipient + Date row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1.3fr 1fr',
                gap: 8,
                fontSize: 12,
                marginBottom: 6
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#6b7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Recipient</div>
                  {msg.recipient_name ? (
                    <>
                      <div style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {msg.recipient_name}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {msg.recipient_email}
                      </div>
                    </>
                  ) : (
                    <div style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {msg.recipient_email || '-'}
                    </div>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#6b7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Sent</div>
                  <div style={{ color: '#111827' }}>{formatDate(msg.sent_at)}</div>
                </div>
              </div>

              {/* Type + Status row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1.3fr 1fr',
                gap: 8,
                alignItems: 'center'
              }}>
                <div style={{ fontSize: 12, color: '#374151' }}>
                  {formatType(msg.template_type)}
                </div>
                <div>{getStatusBadge(msg.status)}</div>
              </div>

              {/* Expanded details */}
              {expandedId === msg.id && (
                <div style={{
                  marginTop: 10,
                  padding: 10,
                  background: '#f9fafb',
                  borderRadius: 6,
                  fontSize: 11,
                  color: '#6b7280',
                  borderLeft: '3px solid #2d6a4f'
                }}>
                  <div><strong>Full Subject:</strong> {msg.subject}</div>
                  {msg.error_message && (
                    <div style={{ marginTop: 6, color: '#dc2626' }}>
                      <strong>Error:</strong> {msg.error_message}
                    </div>
                  )}
                  {msg.tee_time_id && (
                    <div style={{ marginTop: 6 }}>
                      <strong>Tee Time ID:</strong> {msg.tee_time_id}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Date/Time</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Recipient</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Subject</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Type</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {messages.map(msg => (
                <React.Fragment key={msg.id}>
                  <tr
                    onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
                    style={{ 
                      cursor: 'pointer',
                      background: expandedId === msg.id ? '#f0fdf4' : 'white',
                      transition: 'background 0.2s'
                    }}
                  >
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      {formatDate(msg.sent_at)}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                      <div>{msg.recipient_name || '-'}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{msg.recipient_email}</div>
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', maxWidth: 300 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {msg.subject}
                      </div>
                      {msg.course_name && (
                        <div style={{ color: '#6b7280', fontSize: 12 }}>
                          {msg.course_name} {msg.tee_time_date && `• ${(() => {
                            const [y, m, d] = (msg.tee_time_date || '').split('T')[0].split('-').map(Number);
                            return new Date(y, m - 1, d).toLocaleDateString();
                          })()}`}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                      {formatType(msg.template_type)}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                      {getStatusBadge(msg.status)}
                    </td>
                  </tr>
                  {expandedId === msg.id && (
                    <tr>
                      <td colSpan={5} style={{ padding: 16, background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          <strong>Full Subject:</strong> {msg.subject}
                          {msg.error_message && (
                            <div style={{ marginTop: 8, color: '#dc2626' }}>
                              <strong>Error:</strong> {msg.error_message}
                            </div>
                          )}
                          {msg.tee_time_id && (
                            <div style={{ marginTop: 8 }}>
                              <strong>Tee Time ID:</strong> {msg.tee_time_id}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              background: pagination.page === 1 ? '#f3f4f6' : 'white',
              cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
              opacity: pagination.page === 1 ? 0.5 : 1
            }}
          >
            ← Previous
          </button>
          <span style={{ padding: '8px 16px', color: '#6b7280' }}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.totalPages}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              background: pagination.page === pagination.totalPages ? '#f3f4f6' : 'white',
              cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
              opacity: pagination.page === pagination.totalPages ? 0.5 : 1
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

// PairingsView - Promoted tee sheet / pairing management as top-level tab
const PairingsView = () => {
  const { data: teeTimes } = useApiData('/teetimes');
  const { data: players } = useApiData('/players');
  const { data: courses } = useApiData('/courses');
  // Sept 21, 2026: which rounds are events, so the pairings toolbar can offer the
  // printed event pieces only where they exist. Same shape PrintCenter unpacks --
  // /events answers with { events: [...] } and is scoped to the org, so an org with
  // events switched off simply has none and the toolbar button stays hidden.
  const { data: eventsData } = useApiData('/events');
  const { isMobile } = useContext(MobileContext);
  const [showHelp, setShowHelp] = useState(false);
  const helpContent = getHelpContent('pairings');
  const eventList = Array.isArray(eventsData) ? eventsData
    : (eventsData && Array.isArray(eventsData.events) ? eventsData.events : []);
  const eventByTeetime = {};
  eventList.forEach((ev) => { if (ev && ev.teetime_id) eventByTeetime[ev.teetime_id] = ev; });

  return (
    <div>
      {/* Phase 3b (May 2026): On mobile, hide this standalone Help row and let
          TeeSheetReport render Help next to the Back button on the same line. */}
      {!isMobile && (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button
          onClick={() => setShowHelp(true)}
          style={{
            padding: "6px 12px",
            border: "1px solid #d1d5db",
            background: "#f9fafb",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            color: "#6b7280",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
          title="What is this page about?"
        >
          <span style={{ fontSize: 15 }}>ⓘ</span>
          <span>Help</span>
        </button>
      </div>
      )}
      <TeeSheetReport
        teeTimes={teeTimes}
        players={players}
        courses={courses}
        onShowHelp={() => setShowHelp(true)}
        eventByTeetime={eventByTeetime}
      />
      <HelpInfoModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={helpContent.title}
        content={helpContent.content}
      />
    </div>
  );
};

// ============================================================================
// ROUND RECAP — organizer (Oct 3, 2026) — claude/Round_Recap_Plan_v1.md
// RecapSettingsPanel: on/off, post immediately or hold 30 minutes, email players.
// RecapReviewPanel: the selected round's recap — edit, write again, post now,
// don't post. Backend: /api/recap/settings, /api/recap/teetimes/:id[/...]
// ============================================================================
const recapAuthHeaders = (json = false) => {
  const h = {};
  const t = getAuthToken();
  if (t) h['Authorization'] = `Bearer ${t}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

const RecapSettingsPanel = () => {
  const { addToast } = useToast();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/recap/settings`, { credentials: 'include', headers: recapAuthHeaders() });
        if (resp.ok) setS(await resp.json());
      } catch (e) { /* panel stays hidden */ }
    })();
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const resp = await fetch(`${API_URL}/recap/settings`, {
        method: 'PUT', credentials: 'include', headers: recapAuthHeaders(true), body: JSON.stringify(patch),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) { setS(data); addToast('Round recap settings saved', 'success'); }
      else addToast(data.error || 'Failed to save', 'error');
    } catch (e) { addToast('Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  if (!s) return null;
  // Show + Lock + Educate (Rule 12): a plan without live scoring sees the feature, locked.
  if (!s.tier_allowed) {
    return (
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 4 }}>📰 Round recaps <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', background: '#e5e7eb', border: '1px solid #d1d5db', borderRadius: 10, padding: '1px 8px', marginLeft: 6 }}>🔒 Starter</span></div>
        <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.45 }}>
          After each round, TTO writes a short story of the day's highlights for your players. Recaps come with live scoring. Your current plan doesn't include live scoring.
        </div>
      </div>
    );
  }
  if (!s.available) return null;
  const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid #bbf7d0' };
  return (
    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', marginBottom: 4 }}>📰 Round recaps</div>
      <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.45, marginBottom: 6 }}>
        When a round is finished, TTO writes a short story of the day's highlights from the scores. Every player in your group sees the latest recap on the player app's home screen, whether he played or not. Players who want it by email turn that on in their profile.
      </div>
      <div style={row}>
        <label htmlFor="recap-on" style={{ fontSize: 14, color: '#1f2937' }}>Write a recap after each round</label>
        <input id="recap-on" type="checkbox" checked={s.recap_enabled} disabled={saving}
          onChange={(e) => save({ recap_enabled: e.target.checked })} style={{ width: 22, height: 22 }} />
      </div>
      {s.recap_enabled && (
        <>
          <div style={row}>
            <label htmlFor="recap-mode" style={{ fontSize: 14, color: '#1f2937' }}>When to post it</label>
            <select id="recap-mode" value={s.recap_publish_mode} disabled={saving}
              onChange={(e) => save({ recap_publish_mode: e.target.value })}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff', minWidth: 220 }}>
              <option value="immediate">Immediately</option>
              <option value="hold30">After 30 minutes (time to review)</option>
            </select>
          </div>
          <div style={row}>
            <label htmlFor="recap-email" style={{ fontSize: 14, color: '#1f2937' }}>Email the recap to players who ask for it</label>
            <input id="recap-email" type="checkbox" checked={s.recap_email_enabled} disabled={saving}
              onChange={(e) => save({ recap_email_enabled: e.target.checked })} style={{ width: 22, height: 22 }} />
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', borderTop: '1px solid #bbf7d0', paddingTop: 8 }}>
            Money is never in the story. Players who were in the round's games also see a Today's Games box with the results.
          </div>
        </>
      )}
    </div>
  );
};

const RecapReviewPanel = ({ teetimeId, teamRound = false }) => {
  const { addToast } = useToast();
  const [recap, setRecap] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [isEvent, setIsEvent] = useState(false);   // event recap (Oct 4, 2026)
  const [hasScores, setHasScores] = useState(true);
  const [teeTimes, setTeeTimes] = useState(1);   // tee times in this day's round (one recap per field, Oct 3)
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ headline: '', body: '' });

  const load = async () => {
    try {
      const resp = await fetch(`${API_URL}/recap/teetimes/${teetimeId}`, { credentials: 'include', headers: recapAuthHeaders() });
      if (resp.ok) { const d = await resp.json(); setRecap(d.recap || null); setHasScores(d.has_scores !== false); setTeeTimes(Number(d.tee_times) || 1); setIsEvent(d.is_event === true); }
    } catch (e) { /* nothing to show */ }
    finally { setLoaded(true); }
  };
  useEffect(() => { setLoaded(false); setEditing(false); setRecap(null); load(); }, [teetimeId]);

  const act = async (path, okMsg, body) => {
    setBusy(true);
    try {
      const resp = await fetch(`${API_URL}/recap/teetimes/${teetimeId}${path}`, {
        method: body ? 'PUT' : 'POST', credentials: 'include', headers: recapAuthHeaders(!!body), body: body ? JSON.stringify(body) : undefined,
      });
      const d = await resp.json().catch(() => ({}));
      if (resp.ok) { setRecap(d.recap || null); setEditing(false); addToast(okMsg, 'success'); }
      else alert(d.error || 'That did not work. Please try again.');
    } catch (e) { alert('Network error. Please try again.'); }
    finally { setBusy(false); }
  };

  if (!loaded) return null;
  if (teamRound && !isEvent) return null;   // team rounds get a recap only as an event
  const statusLabel = !recap ? null : {
    published: `Posted${recap.emailed_at ? ' and emailed' : ''}`,
    draft: recap.publish_at ? `Posts at ${new Date(recap.publish_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Not posted yet',
    hidden: 'Not posted',
    generating: 'Being written...',
    failed: 'Could not be written',
  }[recap.status] || recap.status;

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>📰 {isEvent ? 'Event recap' : 'Round recap'}</div>
        {statusLabel && <div style={{ fontSize: 12, fontWeight: 600, color: '#4b5563', background: '#f3f4f6', borderRadius: 999, padding: '3px 10px' }}>{statusLabel}</div>}
      </div>
      {teeTimes > 1 && (
        <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>
          One recap for the day: it covers all {teeTimes} tee times at this course, and every player on them gets it.
        </div>
      )}

      {!recap && !hasScores && (
        <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>
          No scores were recorded via live scoring for this round. When players score on their phones, a recap of the day is written for them automatically.
        </div>
      )}

      {!recap && hasScores && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: '#6b7280' }}>{isEvent
            ? "The event's results recap is written when you finalize the round (after any playoff is decided)."
            : teeTimes > 1
            ? 'A recap is written when every tee time is finished (or 6 hours after the last tee time).'
            : 'A recap is written when the round is finished (or 6 hours after the tee time).'}</div>
          <Button variant="outline" disabled={busy} onClick={() => act('/regenerate', 'Recap written')}>{busy ? 'Writing...' : (isEvent ? 'Write the event recap' : 'Write it now')}</Button>
        </div>
      )}

      {recap && !editing && (
        <>
          {recap.stale && (
            <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
              Scores changed after you edited this recap. Use "Write again" to update it from the new scores.
            </div>
          )}
          {recap.headline && <div style={{ fontFamily: 'Georgia, serif', fontSize: 19, fontWeight: 700, color: '#14271D', marginBottom: 8 }}>{recap.headline}</div>}
          {(recap.paragraphs || []).map((p, i) => <p key={i} style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.5, color: '#1f2937' }}>{p}</p>)}
          {(recap.excluded || []).length > 0 && (
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Left out (card not complete): {recap.excluded.map(x => x.name).join(', ')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: 10, borderTop: '1px solid #f3f4f6', paddingTop: 10 }}>
            <Button variant="outline" disabled={busy} onClick={() => { setDraft({ headline: recap.headline || '', body: (recap.paragraphs || []).join('\n\n') }); setEditing(true); }}>Edit</Button>
            <Button variant="outline" disabled={busy} onClick={() => act('/regenerate', 'Recap written again')}>Write again</Button>
            {recap.status !== 'hidden' && <Button variant="outline" disabled={busy} onClick={() => act('/hide', recap.status === 'published' ? 'Recap taken down' : 'Recap will not be posted')}>{recap.status === 'published' ? 'Take down' : "Don't post"}</Button>}
            {recap.status !== 'published' && <Button disabled={busy} onClick={() => act('/publish', 'Recap posted')}>Post now</Button>}
            {/* Event recaps are emailed only when the organizer sends them (Oct 4, 2026) */}
            {isEvent && recap.status === 'published' && !recap.emailed_at && <Button disabled={busy} onClick={() => act('/email', 'Recap emailed to the field')}>Email it to the field</Button>}
          </div>
          {isEvent && !recap.emailed_at && (
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, textAlign: 'right' }}>
              Event recaps show in the player app when posted. They're emailed only if you send them.
            </div>
          )}
        </>
      )}

      {recap && editing && (
        <div style={{ display: 'grid', gap: 8 }}>
          <label htmlFor="recap-headline" style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Headline</label>
          <input id="recap-headline" value={draft.headline} onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
            style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
          <label htmlFor="recap-body" style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Recap (a blank line between paragraphs)</label>
          <textarea id="recap-body" rows={8} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', lineHeight: 1.5 }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="outline" disabled={busy} onClick={() => setEditing(false)}>Cancel</Button>
            <Button disabled={busy} onClick={() => act('', 'Recap saved', { headline: draft.headline, paragraphs: draft.body.split(/\n\s*\n/) })}>Save</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SCORING SETTINGS PANEL (Scoring Enhancement A — May 2026)
// ============================================================================
// Organizer-facing controls for in-round score celebrations.
// scoring_enabled itself is admin-controlled and not shown here — this panel
// only appears when scoring is enabled for the organization.
// Reads/writes the in_round_notification_threshold field on the groups table
// via /api/scoring/status (read) and /api/scoring/settings (PUT).
// ============================================================================
const ScoringSettingsPanel = () => {
  const { addToast } = useToast();
  const [savedThreshold, setSavedThreshold] = useState(0);   // last persisted value
  const [pendingThreshold, setPendingThreshold] = useState(0); // current dropdown value
  const [scoringEnabled, setScoringEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getHeaders = (json = false) => {
    const h = {};
    const t = getAuthToken();
    if (t) h['Authorization'] = `Bearer ${t}`;
    if (json) h['Content-Type'] = 'application/json';
    return h;
  };

  // Load current threshold + scoring_enabled
  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_URL}/scoring/status`, { credentials: 'include', headers: getHeaders() });
        if (resp.ok) {
          const data = await resp.json();
          setScoringEnabled(data.scoring_enabled || false);
          const t = data.in_round_notification_threshold || 0;
          setSavedThreshold(t);
          setPendingThreshold(t);
        }
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSave = async () => {
    if (pendingThreshold === savedThreshold) return;
    setSaving(true);
    try {
      const resp = await fetch(`${API_URL}/scoring/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: getHeaders(true),
        body: JSON.stringify({ in_round_notification_threshold: pendingThreshold }),
      });
      if (resp.ok) {
        setSavedThreshold(pendingThreshold);
        addToast('Notification settings saved', 'success');
      } else {
        const err = await resp.json().catch(() => ({}));
        addToast(err.error || 'Failed to save', 'error');
      }
    } catch (e) {
      addToast('Failed to save', 'error');
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    setPendingThreshold(savedThreshold);
  };

  if (loading) return null;
  // Only show the panel if scoring is enabled (admin-controlled prerequisite)
  if (!scoringEnabled) return null;

  const hasChanges = pendingThreshold !== savedThreshold;

  return (
    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 14, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: '1 1 260px', minWidth: 240 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', marginBottom: 4 }}>
            🎉 In-round score celebrations
          </div>
          <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.45 }}>
            Players see a brief banner on their scoring screen when anyone in their tee time scores at this level or better. Hole-in-one is always celebrated when this is on.
          </div>
        </div>
        <div style={{ flex: '0 0 auto' }}>
          <select
            value={pendingThreshold}
            disabled={saving}
            onChange={(e) => setPendingThreshold(parseInt(e.target.value))}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff', minWidth: 200 }}
          >
            <option value={0}>Off</option>
            <option value={1}>Birdies and better</option>
            <option value={2}>Eagles and better</option>
            <option value={3}>Albatross and better</option>
            <option value={4}>Hole-in-one only</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #bbf7d0', paddingTop: 10 }}>
        {hasChanges && (
          <Button variant="outline" onClick={handleReset} disabled={saving}>
            Reset
          </Button>
        )}
        <Button onClick={handleSave} disabled={saving || !hasChanges}>
          {saving ? 'Saving...' : hasChanges ? 'Save Settings' : 'No Changes'}
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// EventHandicapPanel — per-round handicap corrections (Sept 17, 2026)
// Backend: services/eventHandicapService.js; routes
// GET/PUT /api/scoring/teetimes/:teetimeId/event-handicaps[/:playerId].
//
// Dennis, Sept 17: "A handicap committee member or golf pro may decide to adjust
// a player hcp." What is typed here is a handicap INDEX and it applies to THIS
// round only — players.handicap, the club record the 05:00 GHIN sync owns, is
// never touched. The course handicap column shows what the index becomes on that
// player's own tee, so a pro who thinks in strokes still sees strokes.
//
// On a correction made after play has started: "reflect the change in that round
// and the pro will need to be prepared to explain it to the player the reason
// why." So a save that moves anyone's course handicap prints an explicit line
// naming the strokes that moved — that line is what the pro reads to the player.
//
// Collapsed by default: most rounds never need it and the Scoring tab is busy.
// One component serves the organizer and the event desk — the desk reaches both
// routes through the existing /api/scoring/teetimes/<uuid>/.+ allowlist rule.
// ============================================================================

// GHIN prints a plus handicap as +1.4; TTO stores it as -1.4. Same number.
const fmtEventIndex = (v) => {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  return n < 0 ? `+${Math.abs(n).toFixed(1)}` : n.toFixed(1);
};
const fmtWhenShort = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const EventHandicapPanel = ({ teetimeId, onChanged }) => {
  const { addToast } = useToast();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({});       // { player_id: '12.4' }
  const [savingId, setSavingId] = useState(null);
  const [moved, setMoved] = useState([]);       // what the last save changed

  const headers = (json = false) => {
    const h = {};
    const t = getAuthToken();
    if (t) h['Authorization'] = `Bearer ${t}`;
    if (json) h['Content-Type'] = 'application/json';
    return h;
  };

  const draftFrom = (players) => Object.fromEntries(
    (players || []).map(p => [p.player_id, fmtEventIndex(p.event_handicap)])
  );

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/scoring/teetimes/${teetimeId}/event-handicaps`, { credentials: 'include', headers: headers() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Could not load the roster'); setData(null); return; }
      setError(null);
      setData(d);
      setDraft(draftFrom(d.players));
    } catch (e) {
      setError('Could not load the roster');
    } finally {
      setLoading(false);
    }
  };

  // A new round closes the panel and drops everything the old one held.
  useEffect(() => {
    setOpen(false); setData(null); setDraft({}); setMoved([]); setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teetimeId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data) load();
  };

  // The value is passed in, never read back out of `draft` — Clear sets the draft
  // and saves in the same click, and a setState has not committed by then.
  const save = async (player, rawValue) => {
    if (savingId) return;
    const typed = String(rawValue ?? draft[player.player_id] ?? '').trim();
    const before = data.players.map(p => ({ id: p.player_id, name: p.name, hcp: p.course_hcp }));
    setSavingId(player.player_id);
    try {
      const r = await fetch(`${API_URL}/scoring/teetimes/${teetimeId}/event-handicaps/${player.player_id}`, {
        method: 'PUT', credentials: 'include', headers: headers(true),
        body: JSON.stringify({ event_handicap: typed === '' ? null : typed }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { addToast(d.error || 'Could not save the handicap', 'error'); return; }

      setData(d);
      setDraft(draftFrom(d.players));

      // What actually moved, in strokes. This is the line the pro explains.
      const changes = [];
      for (const now of d.players) {
        const was = before.find(b => b.id === now.player_id);
        if (was && was.hcp != null && now.course_hcp != null && was.hcp !== now.course_hcp) {
          changes.push({ name: now.name, from: was.hcp, to: now.course_hcp });
        }
      }
      setMoved(changes);
      addToast(
        typed === ''
          ? `${player.name} — event handicap cleared`
          : `${player.name} — event handicap ${fmtEventIndex(typed.replace(/^\+/, '-'))}`,
        'success'
      );
      if (onChanged) onChanged();
    } catch (e) {
      addToast('Network error — the handicap was not saved', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const adjustedCount = data ? data.players.filter(p => p.adjusted).length : 0;
  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 };
  const th = { padding: '6px 8px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            ⚖️ Event handicaps
            {adjustedCount > 0 && (
              <span style={{ marginLeft: 8, fontSize: 12, padding: '2px 8px', borderRadius: 9999, background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24' }}>
                {adjustedCount} adjusted
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            Adjust a player's handicap for this round only. The club record and GHIN are not changed.
          </div>
        </div>
        <Button type="button" variant="outline" onClick={toggle}>
          {open ? 'Close' : 'Open'}
        </Button>
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          {loading && <div style={{ padding: 12, textAlign: 'center', color: '#6b7280' }}>Loading roster…</div>}

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, fontSize: 13, color: '#991b1b' }}>
              {error}
            </div>
          )}

          {moved.length > 0 && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 12, fontSize: 13, color: '#1e40af', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Standings recalculated</div>
              {moved.map(m => (
                <div key={m.name}>{m.name}: {m.from} → {m.to} strokes</div>
              ))}
              <div style={{ marginTop: 6, color: '#1d4ed8' }}>
                Holes already played are re-scored at the new handicap, so the leaderboard may shift.
              </div>
            </div>
          )}

          {!loading && data && data.players.length === 0 && (
            <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: 12, fontSize: 13, color: '#92400e' }}>
              No confirmed players on this round yet.
            </div>
          )}

          {!loading && data && data.players.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Player</th>
                    <th style={th}>Club index</th>
                    <th style={th}>Event handicap</th>
                    <th style={th}>Course hcp</th>
                    <th style={th}>Changed by</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.players.map(p => {
                    const typed = String(draft[p.player_id] ?? '');
                    const dirty = typed.trim() !== fmtEventIndex(p.event_handicap);
                    const busy = savingId === p.player_id;
                    return (
                      <tr key={p.player_id} style={p.adjusted ? { background: '#fffbeb' } : undefined}>
                        <td style={{ ...td, fontWeight: 600 }}>
                          {p.name}
                          {p.tee_name && <span style={{ fontWeight: 400, color: '#6b7280' }}> · {p.tee_name}</span>}
                        </td>
                        <td style={{ ...td, color: '#6b7280' }}>{fmtEventIndex(p.club_index) || '—'}</td>
                        <td style={td}>
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={`Event handicap for ${p.name}`}
                            placeholder="—"
                            value={typed}
                            disabled={busy}
                            onChange={(e) => setDraft(prev => ({ ...prev, [p.player_id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && dirty) save(p, typed); }}
                            style={{ width: 84, padding: '4px 6px', textAlign: 'center', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 600, boxSizing: 'border-box', background: busy ? '#f3f4f6' : '#fff' }}
                          />
                        </td>
                        <td style={{ ...td, fontWeight: 700 }}>{p.course_hcp == null ? '—' : p.course_hcp}</td>
                        <td style={{ ...td, fontSize: 12, color: '#6b7280' }}>
                          {p.event_handicap_by ? `${p.event_handicap_by}${p.event_handicap_at ? ` · ${fmtWhenShort(p.event_handicap_at)}` : ''}` : ''}
                        </td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {dirty && (
                            <Button type="button" disabled={busy} onClick={() => save(p, typed)} style={{ padding: '4px 10px', fontSize: 12 }}>
                              {busy ? 'Saving…' : 'Save'}
                            </Button>
                          )}
                          {!dirty && p.adjusted && (
                            <Button type="button" variant="outline" disabled={busy}
                              onClick={() => { setDraft(prev => ({ ...prev, [p.player_id]: '' })); save(p, ''); }}
                              style={{ padding: '4px 10px', fontSize: 12 }}>
                              Clear
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                Enter a handicap index, for example 12.4, or +1.4 for a plus handicap. Empty the box and save to remove an adjustment.
                {data.competition_hole_set && data.competition_hole_set !== 'all18' && (
                  <span> This round is scored over the {data.competition_hole_set === 'front9' ? 'front nine' : 'back nine'}, so the course handicap shown is the nine-hole figure.</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TeamRoundScoreGrid — team-scored rounds on the Scoring tab (Sept 17, 2026)
// A scramble keeps one score per TEAM (team_scores); the per-player grid below
// never saw them, so a team round showed every player with an empty card. One
// team at a time, like the player grid: team buttons with their totals, then par
// and stroke index from the team's own tee (the board's rule) and one Score row.
// Save sends only the holes that changed; an emptied box clears that hole. The
// organizer and the event desk don't take the team's card — the scorer's phone
// shows the correction on its next refresh. A finalized round is read-only here,
// as on the phones, until it is reopened in Games.
// ============================================================================
const TEAM_FRONT = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const TEAM_BACK = [10, 11, 12, 13, 14, 15, 16, 17, 18];
const teamScoreCellStyle = (score, par) => {
  const s = parseInt(score, 10);
  if (isNaN(s) || !par) return {};
  const diff = s - par;
  if (diff <= -2) return { background: '#fbbf24', color: '#000', fontWeight: 700 };
  if (diff === -1) return { background: '#dc2626', color: '#fff', fontWeight: 700 };
  if (diff === 0) return { fontWeight: 600 };
  if (diff === 1) return { background: '#3b82f6', color: '#fff' };
  return { background: '#1e3a5f', color: '#fff' };
};
const teamDraftFrom = (t) => (t ? Object.fromEntries(Object.entries(t.scores || {}).map(([h, v]) => [h, String(v)])) : {});

const TeamRoundScoreGrid = ({ teetimeId, onSaved }) => {
  const { addToast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [team, setTeam] = useState(null);      // selected pairing_group
  const [draft, setDraft] = useState({});      // { hole: '4' } for the selected team
  const [saving, setSaving] = useState(false);
  const inputRefs = useRef({});

  const headers = (json = false) => {
    const h = {};
    const t = getAuthToken();
    if (t) h['Authorization'] = `Bearer ${t}`;
    if (json) h['Content-Type'] = 'application/json';
    return h;
  };
  const load = async (keepTeam) => {
    try {
      const r = await fetch(`${API_URL}/scoring/teetimes/${teetimeId}/team-scores`, { credentials: 'include', headers: headers() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Could not load the team cards'); setData(null); return; }
      setError(null);
      setData(d);
      const pick = keepTeam != null && d.teams.some(t => t.pairing_group === keepTeam) ? keepTeam : (d.teams[0] ? d.teams[0].pairing_group : null);
      setTeam(pick);
      setDraft(teamDraftFrom(d.teams.find(x => x.pairing_group === pick)));
    } catch (e) {
      setError('Could not load the team cards');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setLoading(true);
    load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teetimeId]);

  const box = (bg, border, color) => ({ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: 12, fontSize: 13, color });
  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading team cards...</div>;
  if (error) return <div style={box('#fef2f2', '#fecaca', '#991b1b')}>{error}</div>;
  if (!data) return null;
  if (data.teams.length === 0) {
    return <div style={box('#fef3c7', '#fbbf24', '#92400e')}>No teams yet — this round is scored one score per team, and the teams are the saved groups. Save the pairings first.</div>;
  }

  const current = data.teams.find(t => t.pairing_group === team) || null;
  const meta = current && data.hole_meta ? data.hole_meta[current.course_tee_id] : null;
  const holes = (meta && meta.holes) || {};
  const saved = current ? current.scores || {} : {};
  const changed = [];
  for (let h = 1; h <= 18; h++) {
    const was = saved[h] != null ? String(saved[h]) : '';
    const now = String(draft[h] || '').trim();
    if (was !== now) changed.push({ hole_number: h, gross_score: now === '' ? null : Number(now) });
  }
  const dirty = changed.length > 0;
  const locked = !!data.finalized;
  const sum = (hs) => hs.reduce((s, h) => s + (parseInt(draft[h], 10) || 0), 0);
  const parSum = (hs) => hs.reduce((s, h) => s + ((holes[h] && holes[h].par) || 0), 0);
  const fmtWhen = (ts) => { const d = new Date(ts); return isNaN(d.getTime()) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };

  const selectTeam = (g) => {
    if (g === team) return;
    if (dirty && !window.confirm(`Team ${team} has unsaved scores.\n\nSwitch teams without saving them?`)) return;
    setTeam(g);
    setDraft(teamDraftFrom(data.teams.find(x => x.pairing_group === g)));
  };
  const setCell = (h, v) => {
    if (v !== '' && !/^\d{1,2}$/.test(v)) return;
    setDraft(prev => ({ ...prev, [h]: v }));
    if (v.length === 1 && parseInt(v, 10) >= 1 && h < 18 && inputRefs.current[h + 1]) {
      setTimeout(() => { const el = inputRefs.current[h + 1]; if (el) { el.focus(); el.select(); } }, 50);
    }
  };
  const save = async () => {
    if (!dirty || saving || locked || !current) return;
    if (changed.some(c => c.gross_score === 0)) { addToast('A score must be 1 or more — empty the box to clear a hole.', 'error'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/scoring/teetimes/${teetimeId}/team-scores/${current.pairing_group}`, {
        method: 'PUT', credentials: 'include', headers: headers(true), body: JSON.stringify({ scores: changed })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        addToast(d.error || 'Could not save the team scores', 'error');
        if (r.status === 409) load(current.pairing_group);
        return;
      }
      addToast(`Team ${current.pairing_group} saved — ${changed.length} hole${changed.length === 1 ? '' : 's'}`, 'success');
      await load(current.pairing_group);
      if (onSaved) onSaved();
    } catch (e) {
      addToast('Network error — the scores were not saved', 'error');
    } finally {
      setSaving(false);
    }
  };

  const th = { padding: '6px 4px', textAlign: 'center', fontWeight: 600, minWidth: 38 };
  const sub = { padding: '6px 4px', textAlign: 'center', fontWeight: 700, background: '#374151', minWidth: 38 };
  const small = (color) => ({ padding: '4px', textAlign: 'center', fontSize: 12, color });
  const scoreInput = (h) => (
    <td key={h} style={{ padding: '2px' }}>
      <input
        ref={el => { inputRefs.current[h] = el; }}
        type="text" inputMode="numeric" maxLength={2}
        aria-label={`Hole ${h} score`}
        value={draft[h] || ''}
        disabled={locked}
        onChange={(e) => setCell(h, e.target.value)}
        onFocus={(e) => e.target.select()}
        style={{ width: '100%', padding: '4px 2px', textAlign: 'center', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14, fontWeight: 600, boxSizing: 'border-box', background: locked ? '#f3f4f6' : '#fff', ...teamScoreCellStyle(draft[h], holes[h] && holes[h].par) }}
      />
    </td>
  );

  return (
    <div data-testid="team-score-grid">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{data.course_name}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>👥 Team round — one score per team{data.basis === 'net' ? ' · net game' : ''}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => { if (dirty && !window.confirm('Discard the unsaved scores and reload the cards?')) return; load(team); }}
            style={{ padding: '6px 12px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            🔄 Refresh
          </button>
          <button type="button" onClick={save} disabled={!dirty || saving || locked}
            style={{ padding: '6px 16px', background: (!dirty || saving || locked) ? '#9ca3af' : '#166534', color: '#fff', border: 'none', borderRadius: 6, cursor: (!dirty || saving || locked) ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
            {saving ? 'Saving...' : current ? `Save Team ${current.pairing_group}` : 'Save'}
          </button>
        </div>
      </div>

      {locked && (
        <div style={{ ...box('#f3f4f6', '#d1d5db', '#374151'), marginBottom: 10 }} data-testid="team-grid-locked">
          🔒 This round is finalized — scores are locked. Reopen the round in Games to correct a score.
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        {data.teams.map(t => {
          const active = t.pairing_group === team;
          return (
            <button key={t.pairing_group} type="button" onClick={() => selectTeam(t.pairing_group)} data-testid={`team-tab-${t.pairing_group}`}
              title={(t.members || []).map(m => m.name).join(', ')}
              style={{ padding: '6px 10px', border: active ? '2px solid #166534' : '1px solid #d1d5db', background: active ? '#f0fdf4' : '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400, color: active ? '#166534' : '#374151', textAlign: 'left', lineHeight: 1.25 }}>
              Team {t.pairing_group}
              {t.thru > 0 && <span style={{ marginLeft: 5, fontSize: 11, color: '#6b7280' }}>{t.gross_total}{t.thru < 18 ? ` (${t.thru})` : ''}</span>}
              <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
            </button>
          );
        })}
      </div>

      {current && (
        <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8, lineHeight: 1.6 }} data-testid="team-grid-info">
          <strong style={{ color: '#111827' }}>Team {current.pairing_group}</strong>: {(current.members || []).map(m => m.name).join(', ')}
          {current.start_text && <> · {current.start_text}</>}
          {meta && meta.tee_name && <> · par and stroke index from {meta.tee_name} tees</>}
          {data.basis === 'net' && current.team_handicap != null && <> · team handicap {current.team_handicap}</>}
          <br />
          {current.claim ? <>📱 Card kept by <strong>{current.claim.scorer_name || 'a team member'}</strong></> : <>📱 No one is keeping this card on a phone</>}
          {current.last_edit && <> · last change: hole {current.last_edit.hole_number}{current.last_edit.entered_by_name ? ` by ${current.last_edit.entered_by_name}` : ''}{current.last_edit.entered_at ? `, ${fmtWhen(current.last_edit.entered_at)}` : ''}</>}
        </div>
      )}

      {current && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
            <thead>
              <tr style={{ background: '#1f2937', color: '#fff' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 55, position: 'sticky', left: 0, background: '#1f2937', zIndex: 1 }}>Hole</th>
                {TEAM_FRONT.map(h => <th key={h} style={th}>{h}</th>)}
                <th style={sub}>OUT</th>
                {TEAM_BACK.map(h => <th key={h} style={th}>{h}</th>)}
                <th style={sub}>IN</th>
                <th style={{ ...sub, background: '#166534', minWidth: 42 }}>TOT</th>
              </tr>
              <tr style={{ background: '#f3f4f6' }}>
                <td style={{ padding: '4px 8px', fontWeight: 600, fontSize: 12, color: '#6b7280', position: 'sticky', left: 0, background: '#f3f4f6', zIndex: 1 }}>Par</td>
                {TEAM_FRONT.map(h => <td key={h} style={small('#6b7280')}>{(holes[h] && holes[h].par) || ''}</td>)}
                <td style={{ ...small('#374151'), fontWeight: 700 }}>{parSum(TEAM_FRONT) || ''}</td>
                {TEAM_BACK.map(h => <td key={h} style={small('#6b7280')}>{(holes[h] && holes[h].par) || ''}</td>)}
                <td style={{ ...small('#374151'), fontWeight: 700 }}>{parSum(TEAM_BACK) || ''}</td>
                <td style={{ ...small('#374151'), fontWeight: 700 }}>{parSum(TEAM_FRONT.concat(TEAM_BACK)) || ''}</td>
              </tr>
              <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                <td style={{ padding: '4px 8px', fontWeight: 600, fontSize: 11, color: '#9ca3af', position: 'sticky', left: 0, background: '#f3f4f6', zIndex: 1 }}>HCP</td>
                {TEAM_FRONT.map(h => <td key={h} style={{ ...small('#9ca3af'), fontSize: 11 }}>{(holes[h] && holes[h].index) || ''}</td>)}
                <td />
                {TEAM_BACK.map(h => <td key={h} style={{ ...small('#9ca3af'), fontSize: 11 }}>{(holes[h] && holes[h].index) || ''}</td>)}
                <td /><td />
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <td style={{ padding: '4px 8px', fontWeight: 600, fontSize: 12, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>Score</td>
                {TEAM_FRONT.map(scoreInput)}
                <td style={{ padding: '4px', textAlign: 'center', fontWeight: 700, fontSize: 14, background: '#f3f4f6' }}>{sum(TEAM_FRONT) || ''}</td>
                {TEAM_BACK.map(scoreInput)}
                <td style={{ padding: '4px', textAlign: 'center', fontWeight: 700, fontSize: 14, background: '#f3f4f6' }}>{sum(TEAM_BACK) || ''}</td>
                <td style={{ padding: '4px', textAlign: 'center', fontWeight: 700, fontSize: 14, background: '#166534', color: '#fff' }} data-testid="team-grid-total">{sum(TEAM_FRONT.concat(TEAM_BACK)) || ''}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, lineHeight: 1.5 }}>
        {dirty ? <span style={{ color: '#b45309', fontWeight: 600 }}>Unsaved: {changed.map(c => `#${c.hole_number}`).join(' ')} · </span> : null}
        Empty a box to clear that hole. Your change shows on the team's phone at its next refresh and on the live board.
        {data.unpaired_count > 0 && <> {data.unpaired_count} confirmed player{data.unpaired_count === 1 ? ' is' : 's are'} not on a team yet.</>}
      </div>
    </div>
  );
};

// ============================================================================
// POST-ROUND SCORING GRID (Session 3 - Feb 2026)
// ============================================================================
// Full 18-hole grid for batch score entry from paper scorecard.
// Only visible in scoring-enabled orgs via the Scoring tab in Settings.
// Writes to round_scores table. Same data as on-course entry would use.
// ============================================================================
// ============================================================================
// WALL OF FAME MANAGEMENT (Sept 26, 2026 — Deploy 2)
// ============================================================================
// Entries post automatically when scores are saved (backend
// services/wallOfFameService.js). Here the organizer can HIDE an entry (it stays
// hidden even if the round is re-saved) or EDIT the displayed player/course name.
// Numbers are not editable: a wrong score is fixed by correcting the score in
// Post-Round Score Entry, and the wall follows it. Club-wide: not on the desk.
// API: GET /api/scoring/wall-of-fame, PATCH /api/scoring/wall-of-fame/:id
// ============================================================================
const WOF_MGR_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const wofMgrDate = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return (y && m && d) ? `${WOF_MGR_MONTHS[m - 1]} ${d}, ${y}` : '';
};
const wofMgrToPar = (n) => (n === 0 ? 'E' : n > 0 ? `+${n}` : `${n}`);

const WallOfFameManager = () => {
  const { addToast } = useToast();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editPlayer, setEditPlayer] = useState('');
  const [editCourse, setEditCourse] = useState('');
  const [busyId, setBusyId] = useState(null);

  const getHeaders = (json = false) => {
    const h = {};
    const t = getAuthToken();
    if (t) h['Authorization'] = `Bearer ${t}`;
    if (json) h['Content-Type'] = 'application/json';
    return h;
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/scoring/wall-of-fame`, { credentials: 'include', headers: getHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setData(await resp.json());
    } catch (e) {
      console.error('Wall of Fame load failed:', e);
      setError("Couldn't load the Wall of Fame.");
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !loading) load();
  };

  const patchEntry = async (id, body, successMsg) => {
    setBusyId(id);
    try {
      const resp = await fetch(`${API_URL}/scoring/wall-of-fame/${id}`, {
        method: 'PATCH', credentials: 'include', headers: getHeaders(true), body: JSON.stringify(body)
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`);
      addToast(successMsg, 'success');
      setEditingId(null);
      await load();
    } catch (e) {
      addToast(`Couldn't update the entry: ${e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditPlayer(entry.player_name || '');
    setEditCourse(entry.course_name || '');
  };

  const btn = { padding: '5px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer', border: '1px solid #d1d5db', background: '#fff', color: '#374151' };
  const input = { width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' };

  const renderEntry = (entry) => {
    const isHole = entry.record_type === 'hole';
    const detail = isHole
      ? `${entry.course_name || ''} · Hole ${entry.hole_number} (par ${entry.hole_par}) — scored ${entry.hole_gross}`
      : `${entry.course_name || ''} · ${entry.round_total_gross} (${wofMgrToPar(entry.round_score_to_par)}) · par ${entry.round_total_par}${entry.round_source === 'total' ? ' · total only' : ''}`;
    const busy = busyId === entry.id;
    return (
      <div key={entry.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: entry.hidden ? '#f9fafb' : '#fff', opacity: entry.hidden ? 0.75 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db', color: '#4b5563', background: '#f9fafb' }}>{entry.achievement}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{entry.player_name}</span>
              {entry.hidden && <span style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c' }}>Hidden</span>}
              {entry.edited && <span style={{ fontSize: 11, color: '#6b7280' }}>· name edited</span>}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{detail}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{wofMgrDate(entry.round_date)}{entry.round_deleted ? ' · tee time deleted' : ''}</div>
          </div>
          {editingId !== entry.id && (
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button disabled={busy} onClick={() => startEdit(entry)} style={btn}>Edit</button>
              <button disabled={busy}
                onClick={() => patchEntry(entry.id, { hidden: !entry.hidden }, entry.hidden ? 'Entry is back on the wall' : 'Entry hidden from the wall')}
                style={{ ...btn, color: entry.hidden ? '#166534' : '#b91c1c' }}>
                {entry.hidden ? 'Show' : 'Hide'}
              </button>
            </div>
          )}
        </div>
        {editingId === entry.id && (
          <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
            <label style={{ fontSize: 12, color: '#374151' }}>Player name as shown
              <input value={editPlayer} onChange={e => setEditPlayer(e.target.value)} maxLength={100} style={input} />
            </label>
            <label style={{ fontSize: 12, color: '#374151' }}>Course name as shown
              <input value={editCourse} onChange={e => setEditCourse(e.target.value)} maxLength={150} style={input} />
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button disabled={busy || !editPlayer.trim()}
                onClick={() => patchEntry(entry.id, { player_name: editPlayer, course_name: editCourse }, 'Entry updated')}
                style={{ ...btn, background: '#166534', color: '#fff', border: '1px solid #166534' }}>Save</button>
              <button disabled={busy} onClick={() => setEditingId(null)} style={btn}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const holes = (data && data.holes) || [];
  const rounds = (data && data.rounds) || [];
  const hiddenCount = [...holes, ...rounds].filter(e => e.hidden).length;

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}>
      <button onClick={toggleOpen}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>🏆 Wall of Fame</span>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          {data ? `${holes.length + rounds.length} entries${hiddenCount ? ` · ${hiddenCount} hidden` : ''} · ` : ''}{open ? 'Hide ▲' : 'Manage ▼'}
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px', lineHeight: 1.5 }}>
            Eagles, albatrosses, aces and par-or-better rounds post here automatically when scores are saved.
            To fix a wrong score, correct it in Post-Round Score Entry below and the wall updates itself.
            Use Hide to take an entry off the wall, or Edit to change how a name is shown.
          </p>
          {loading && <div style={{ fontSize: 13, color: '#6b7280', padding: 12 }}>Loading...</div>}
          {!loading && error && (
            <div style={{ fontSize: 13, color: '#b91c1c', padding: 12 }}>
              {error} <button onClick={load} style={btn}>Try again</button>
            </div>
          )}
          {!loading && !error && data && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '4px 0 8px' }}>Notable Holes</div>
              {holes.length ? holes.map(renderEntry) : <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>No eagles, albatrosses or aces yet.</div>}
              <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '14px 0 8px' }}>Notable Rounds</div>
              {rounds.length ? rounds.map(renderEntry) : <div style={{ fontSize: 13, color: '#9ca3af' }}>No par-or-better rounds yet.</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PostRoundScoring = ({ onViewScorecardReport } = {}) => {
  const { addToast } = useToast();
  const deskSession = useContext(DeskContext);   // Event desk (Sept 17, 2026): one round — picked for them; no org-wide settings
  const appNav = useContext(AppNavContext);       // Ready check → this round (Oct 4, 2026)
  const [tvCopied, setTvCopied] = useState(false);
  
  // Auth header helper
  const getHeaders = (json = false) => {
    const h = {};
    const token = getAuthToken();
    if (token) h['Authorization'] = `Bearer ${token}`;
    if (json) h['Content-Type'] = 'application/json';
    return h;
  };
  
  // State: tee time selection
  const [teetimes, setTeetimes] = useState([]);
  const [selectedTeetimeId, setSelectedTeetimeId] = useState(() => { const i = appNav.readIntent('scoring-round'); return i ? i.teetimeId : null; });
  const [loadingTeetimes, setLoadingTeetimes] = useState(true);
  useEffect(() => { appNav.clearIntent('scoring-round'); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // State: Live Leaderboard share panel (independent of the score-entry selection)
  const [shareTeetimeId, setShareTeetimeId] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [sharedMap, setSharedMap] = useState({}); // { teetimeId: bool } — current share state from list
  
  // State: scorecard data
  const [scorecard, setScorecard] = useState(null);
  const [loadingScorecard, setLoadingScorecard] = useState(false);
  // Bumped when an event handicap is saved, so the grid re-reads its scorecard and
  // its course-handicap column moves with the correction (Sept 17, 2026).
  const [scorecardReload, setScorecardReload] = useState(0);
  const [selectedTeeId, setSelectedTeeId] = useState(null);
  const [playerTeeIds, setPlayerTeeIds] = useState({}); // { playerId: teeId } — per-player tee selection
  const [holeData, setHoleData] = useState([]);
  
  // State: score entry grid - { playerId: { holeNumber: { score: '', putts: '' } } }
  const [gridData, setGridData] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [postingGhin, setPostingGhin] = useState(false);
  const [scoresSavedFor, setScoresSavedFor] = useState({});
  const [ghinPostedFor, setGhinPostedFor] = useState({}); // { playerId: true } tracks which players have been saved
  
  // Refs for auto-advance
  const scoreInputRefs = useRef({});
  const puttsInputRefs = useRef({});
  
  // Load available tee times
  useEffect(() => {
    const loadTeetimes = async () => {
      try {
        setLoadingTeetimes(true);
        const resp = await fetch(`${API_URL}/scoring/teetimes`, { credentials: 'include', headers: getHeaders() });
        if (resp.ok) {
          const data = await resp.json();
          setTeetimes(data);
          const sm = {};
          (data || []).forEach(tt => { sm[tt.id] = !!tt.leaderboard_shared; });
          setSharedMap(sm);
          // The desk has exactly one round — open it in both pickers.
          if (deskSession && Array.isArray(data) && data.length === 1) {
            setSelectedTeetimeId(data[0].id);
            setShareTeetimeId(data[0].id);
          }
        }
      } catch (e) {
        console.error('Failed to load teetimes:', e);
      } finally {
        setLoadingTeetimes(false);
      }
    };
    loadTeetimes();
  }, []);
  
  // Load scorecard when tee time is selected
  useEffect(() => {
    if (!selectedTeetimeId) return;
    const loadScorecard = async () => {
      try {
        setLoadingScorecard(true);
        const resp = await fetch(`${API_URL}/scoring/scorecard/${selectedTeetimeId}`, { credentials: 'include', headers: getHeaders() });
        if (resp.ok) {
          const data = await resp.json();
          setScorecard(data);
          setScoresSavedFor({});
          
          // Build per-player tee map: match preferred_tee by UUID (new) or name (legacy).
          // NOTE: preferred_tee stores a UUID from whichever course the player last set it on.
          // That UUID may not exist on the current course (e.g. set on Links, playing Harbor).
          // Fix: try UUID match first; if not found on this course, look up the tee name from
          // all course_tees and match by name against the current course's tees instead.
          const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const newPlayerTeeIds = {};
          for (const player of data.players) {
            if (player.preferred_tee) {
              let tee;
              if (uuidRe.test(player.preferred_tee)) {
                // Try direct UUID match on this course first
                tee = data.tees.find(t => t.id === player.preferred_tee && t.has_hole_data);
                // UUID didn't match this course — find the tee name from the preferred UUID
                // and match by name on the current course's tees
                if (!tee) {
                  const preferredTeeName = player.preferred_tee_name; // included in scorecard response
                  if (preferredTeeName) {
                    tee = data.tees.find(t => t.tee_name.toLowerCase() === preferredTeeName.toLowerCase() && t.has_hole_data)
                       || data.tees.find(t => t.tee_name.toLowerCase() === preferredTeeName.toLowerCase());
                  }
                }
              } else {
                tee = data.tees.find(t => t.tee_name.toLowerCase() === player.preferred_tee.toLowerCase() && t.has_hole_data)
                   || data.tees.find(t => t.tee_name.toLowerCase() === player.preferred_tee.toLowerCase());
              }
              if (tee) newPlayerTeeIds[player.id] = tee.id;
            }
          }
          setPlayerTeeIds(newPlayerTeeIds);

          // Set default tee: prefer first player's preferred_tee, fall back to default_tee
          let initialTee = data.default_tee;
          if (data.players.length > 0 && newPlayerTeeIds[data.players[0].id]) {
            const prefTee = data.tees.find(t => t.id === newPlayerTeeIds[data.players[0].id]);
            if (prefTee) initialTee = prefTee;
          }
          
          if (initialTee) {
            setSelectedTeeId(initialTee.id);
            // Load hole data for this tee
            if (initialTee.id === data.default_tee?.id) {
              setHoleData(data.hole_data || []);
            } else {
              // Need to fetch hole data for the preferred tee
              try {
                const hResp = await fetch(`${API_URL}/scoring/courses/${data.tee_time.course_id}/tees/${initialTee.id}/holes`, { credentials: 'include', headers: getHeaders() });
                if (hResp.ok) {
                  const hData = await hResp.json();
                  setHoleData(hData);
                }
              } catch (e) { setHoleData(data.hole_data || []); }
            }
          }
          
          // Initialize grid from existing scores
          const grid = {};
          for (const player of data.players) {
            grid[player.id] = {};
            for (let h = 1; h <= 18; h++) {
              const existing = data.scores[player.id]?.[h];
              grid[player.id][h] = {
                score: existing?.gross_score?.toString() || '',
                putts: existing?.putts?.toString() || '',
                ...(existing?.picked_up ? { picked: true } : {})   // "7x"
              };
            }
          }
          setGridData(grid);
          
          // Mark players who already have saved scores
          const savedMap = {};
          for (const player of data.players) {
            if (data.scores[player.id] && Object.keys(data.scores[player.id]).length > 0) {
              savedMap[player.id] = true;
            }
          }
          setScoresSavedFor(savedMap);
          
          // Auto-select first player
          if (data.players.length > 0 && !selectedPlayerId) {
            setSelectedPlayerId(data.players[0].id);
          }
        }
      } catch (e) {
        console.error('Failed to load scorecard:', e);
      } finally {
        setLoadingScorecard(false);
      }
    };
    loadScorecard();
  }, [selectedTeetimeId, scorecardReload]);
  
  // Load hole data when tee changes
  const changeTee = async (teeId) => {
    setSelectedTeeId(teeId);
    if (!scorecard || !teeId) return;
    try {
      const courseId = scorecard.tee_time.course_id;
      const resp = await fetch(`${API_URL}/scoring/courses/${courseId}/tees/${teeId}/holes`, { credentials: 'include', headers: getHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setHoleData(data);
      }
    } catch (e) {
      console.error('Failed to load hole data for tee:', e);
    }
  };
  
  // Update a cell in the grid with auto-advance
  const updateCell = (playerId, holeNumber, field, value) => {
    // Picked up ("7x", Sept 27, 2026): an x in a score box marks the hole picked up,
    // scored as the player's net double bogey (the server sets the final number).
    if (field === 'score' && /x/i.test(value)) {
      const hole = holeData.find(hd => hd.hole_number === holeNumber);
      const pl = scorecard?.players?.find(p => p.id === playerId);
      const ndb = hole ? pickupNdb(hole.par, getCourseHandicap(pl), hole.handicap_index) : null;
      if (ndb == null) { addToast('This hole has no par on file — enter a score', 'error'); return; }
      setGridData(prev => ({
        ...prev,
        [playerId]: { ...prev[playerId], [holeNumber]: { ...prev[playerId]?.[holeNumber], score: String(ndb), putts: '', picked: true } }
      }));
      setScoresSavedFor(prev => ({ ...prev, [playerId]: false }));
      const nextHole = holeNumber < 18 ? holeNumber + 1 : null;
      if (nextHole && scoreInputRefs.current[nextHole]) {
        setTimeout(() => { scoreInputRefs.current[nextHole].focus(); scoreInputRefs.current[nextHole].select(); }, 50);
      }
      return;
    }
    if (value !== '' && !/^\d+$/.test(value)) return;
    setGridData(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [holeNumber]: {
          ...prev[playerId]?.[holeNumber],
          [field]: value,
          ...(field === 'score' ? { picked: false } : {})
        }
      }
    }));
    // Mark as unsaved when editing
    setScoresSavedFor(prev => ({ ...prev, [playerId]: false }));
    
    // Auto-advance: if score field and single digit entered, move to next hole
    if (field === 'score' && value.length === 1 && parseInt(value) >= 1) {
      const nextHole = holeNumber < 18 ? holeNumber + 1 : null;
      if (nextHole && scoreInputRefs.current[nextHole]) {
        setTimeout(() => {
          scoreInputRefs.current[nextHole].focus();
          scoreInputRefs.current[nextHole].select();
        }, 50);
      }
    }
    // Auto-advance putts too
    if (field === 'putts' && value.length === 1) {
      const nextHole = holeNumber < 18 ? holeNumber + 1 : null;
      if (nextHole && puttsInputRefs.current[nextHole]) {
        setTimeout(() => {
          puttsInputRefs.current[nextHole].focus();
          puttsInputRefs.current[nextHole].select();
        }, 50);
      }
    }
  };
  
  // A blank hole INSIDE the holes a player has scored needs a score or an X (Dennis,
  // Sept 27: require an entry). Holes are taken in PLAY ORDER from his starting hole
  // (hole 1, or his shotgun hole): blanks before his first scored hole or after his
  // last are fine (not started / stopped); a blank between them is a missed hole.
  // Returns those holes, [] when the card is fine.
  const innerBlankHoles = (playerGrid, startHole = 1) => {
    const start = Number(startHole) >= 1 && Number(startHole) <= 18 ? Number(startHole) : 1;
    const order = Array.from({ length: 18 }, (_, i) => ((start - 1 + i) % 18) + 1);
    const has = (h) => !!(playerGrid && playerGrid[h] && playerGrid[h].score);
    const first = order.findIndex(has);
    if (first < 0) return [];
    let last = -1;
    for (let i = order.length - 1; i >= 0; i--) if (has(order[i])) { last = i; break; }
    return order.slice(first, last + 1).filter(h => !has(h));
  };
  const [gridBlock, setGridBlock] = useState(null);   // [{ name, holes }] — blank holes that need a score or X

  // Save scores for a player. Returns { blocked: { name, holes } } when a hole inside
  // his card is blank (nothing is saved for him then).
  const savePlayerScores = async (playerId, opts = {}) => {
    if (!selectedTeetimeId || !playerId) return;
    const playerGrid = gridData[playerId];
    if (!playerGrid) return;
    const pl = scorecard?.players.find(p => p.id === playerId);
    const inner = innerBlankHoles(playerGrid, scorecard?.tee_time?.shotgun_mode ? (pl?.starting_hole || 1) : 1);
    if (inner.length) {
      const blocked = { name: scorecard?.players.find(p => p.id === playerId)?.name || 'Player', holes: inner };
      if (!opts.quiet) setGridBlock([blocked]);
      return { blocked };
    }
    
    const scores = [];
    for (let h = 1; h <= 18; h++) {
      const cell = playerGrid[h];
      const pk = !!(cell?.picked && cell?.score);   // "7x"
      scores.push({
        hole_number: h,
        gross_score: cell?.score || null,
        putts: pk ? null : (cell?.putts || null),
        picked_up: pk
      });
    }
    
    try {
      setSaving(true);
      const resp = await fetch(`${API_URL}/scoring/scores`, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(true),
        body: JSON.stringify({
          teetime_id: selectedTeetimeId,
          player_id: playerId,
          course_tee_id: playerTeeIds[playerId] || selectedTeeId || null,
          scores
        })
      });
      
      if (resp.ok) {
        const playerName = scorecard?.players.find(p => p.id === playerId)?.name || 'Player';
        addToast(`Scores saved for ${playerName}`, 'success');
        setScoresSavedFor(prev => ({ ...prev, [playerId]: true }));
        // The server's net double bogey for any "7x" (it knows the event handicap).
        const d = await resp.json().catch(() => ({}));
        if (d && d.pickups && Object.keys(d.pickups).length) {
          setGridData(prev => { const pg = { ...(prev[playerId] || {}) }; for (const [h, v] of Object.entries(d.pickups)) if (pg[h] && pg[h].picked) pg[h] = { ...pg[h], score: String(v) }; return { ...prev, [playerId]: pg }; });
        }
      } else {
        const err = await resp.json();
        addToast(err.error || 'Failed to save scores', 'error');
      }
    } catch (e) {
      addToast('Failed to save scores', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  // Save all players
  const saveAllScores = async () => {
    if (!scorecard?.players) return;
    setSaving(true);
    const blocked = [];
    for (const player of scorecard.players) {
      const r = await savePlayerScores(player.id, { quiet: true });
      if (r && r.blocked) blocked.push(r.blocked);
    }
    setSaving(false);
    if (blocked.length) setGridBlock(blocked);
  };
  
  // Post scores to GHIN for a player
  // Backend auto-resolves GHIN course/tee IDs from database or GHIN API search
  const postToGhin = async (playerId) => {
    if (!selectedTeetimeId || !playerId || !scorecard) return;
    
    // Get played date
    const playedAt = scorecard.tee_time.date?.split('T')[0];
    if (!playedAt) {
      addToast('Could not determine round date', 'error');
      return;
    }
    
    try {
      setPostingGhin(true);
      const resp = await fetch(`${API_URL}/scoring/post-to-ghin`, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(true),
        body: JSON.stringify({
          teetime_id: selectedTeetimeId,
          player_id: playerId,
          played_at: playedAt
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        const playerName = scorecard.players.find(p => p.id === playerId)?.name || 'Player';
        addToast(`Score posted to GHIN for ${playerName}! Differential: ${data.differential}`, 'success');
        setGhinPostedFor(prev => ({ ...prev, [playerId]: true }));
      } else {
        const err = await resp.json();
        addToast(err.error || 'Failed to post to GHIN', 'error');
      }
    } catch (e) {
      addToast('Failed to post to GHIN: ' + e.message, 'error');
    } finally {
      setPostingGhin(false);
    }
  };
  
  // Calculate course handicap for a player on the selected tee
  const getCourseHandicap = (player) => {
    // Event handicap (Sept 17, 2026) wins over the club index for THIS round, so this
    // grid cannot show a different net from the leaderboard and the games.
    const hcpIndex = parseFloat(player?.event_handicap != null ? player.event_handicap : player?.handicap);
    if (isNaN(hcpIndex) || !selectedTeeId || !scorecard?.tees) return null;
    const tee = scorecard.tees.find(t => t.id === selectedTeeId);
    if (!tee?.slope_rating || !tee?.course_rating) return null;
    // Course Handicap = Handicap Index × (Slope / 113) + (Course Rating − Par)
    const par = getParTotals().total;
    const ch = Math.round(hcpIndex * (tee.slope_rating / 113) + (tee.course_rating - par));
    return ch;
  };
  
  // Calculate strokes received on a specific hole
  const getStrokesOnHole = (courseHandicap, holeHcpIndex) => {
    if (courseHandicap === null || !holeHcpIndex) return 0;
    const ch = Math.max(0, courseHandicap);
    if (ch >= holeHcpIndex + 18) return 2;
    if (ch >= holeHcpIndex) return 1;
    return 0;
  };
  
  // Calculate adjusted score (Net Double Bogey max)
  const getAdjustedScore = (grossScore, par, strokesOnHole) => {
    const gs = parseInt(grossScore);
    if (isNaN(gs) || !par) return '';
    // Max = Par + 2 + strokes received on this hole
    const maxScore = par + 2 + strokesOnHole;
    return Math.min(gs, maxScore);
  };
  
  // Calculate totals for a player (including adjusted)
  const getPlayerTotals = (playerId) => {
    const pg = gridData[playerId];
    if (!pg) return { front: 0, back: 0, total: 0, adjFront: 0, adjBack: 0, adjTotal: 0, frontPutts: 0, backPutts: 0, totalPutts: 0, holesEntered: 0 };
    const player = scorecard?.players.find(p => p.id === playerId);
    const ch = getCourseHandicap(player);
    
    let front = 0, back = 0, adjFront = 0, adjBack = 0, frontPutts = 0, backPutts = 0, holesEntered = 0;
    for (let h = 1; h <= 18; h++) {
      const s = parseInt(pg[h]?.score);
      const p = parseInt(pg[h]?.putts);
      const hole = holeData.find(hd => hd.hole_number === h);
      const strokes = hole ? getStrokesOnHole(ch, hole.handicap_index) : 0;
      
      if (!isNaN(s) && s > 0) {
        holesEntered++;
        const adj = getAdjustedScore(s, hole?.par, strokes);
        const adjVal = typeof adj === 'number' ? adj : s;
        if (h <= 9) { front += s; adjFront += adjVal; } 
        else { back += s; adjBack += adjVal; }
      }
      if (!isNaN(p) && p >= 0) {
        if (h <= 9) frontPutts += p; else backPutts += p;
      }
    }
    return { front, back, total: front + back, adjFront, adjBack, adjTotal: adjFront + adjBack, frontPutts, backPutts, totalPutts: frontPutts + backPutts, holesEntered };
  };
  
  // Get par totals from hole data
  const getParTotals = () => {
    if (!holeData?.length) return { front: 0, back: 0, total: 0 };
    let front = 0, back = 0;
    for (const h of holeData) {
      if (h.hole_number <= 9) front += h.par; else back += h.par;
    }
    return { front, back, total: front + back };
  };
  
  // Score cell color based on relation to par
  const getScoreColor = (score, par) => {
    const s = parseInt(score);
    if (isNaN(s) || !par) return {};
    const diff = s - par;
    if (diff <= -2) return { background: '#fbbf24', color: '#000', fontWeight: 700 };
    if (diff === -1) return { background: '#dc2626', color: '#fff', fontWeight: 700 };
    if (diff === 0) return { fontWeight: 600 };
    if (diff === 1) return { background: '#3b82f6', color: '#fff' };
    if (diff >= 2) return { background: '#1e3a5f', color: '#fff' };
    return {};
  };
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const dateOnly = dateStr.split('T')[0];
    const d = new Date(dateOnly + 'T12:00:00');
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  
  const parTotals = getParTotals();
  const selectedTee = scorecard?.tees?.find(t => t.id === selectedTeeId);
  const selectedPlayer = scorecard?.players?.find(p => p.id === selectedPlayerId);
  const courseHcp = getCourseHandicap(selectedPlayer);
  
  // ---- RENDER ----
  if (loadingTeetimes) {
    return <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading tee times...</div>;
  }
  
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Scoring Enhancement A — Organizer-facing notification settings (May 2026).
          Club-wide, so not on the event desk (Sept 17, 2026). */}
      {!deskSession && <ScoringSettingsPanel />}

      {/* Round Recap settings (Oct 3, 2026). Club-wide, so not on the event desk. */}
      {!deskSession && <RecapSettingsPanel />}

      {/* Wall of Fame management (Sept 26, 2026). Club-wide, so not on the event desk. */}
      {!deskSession && <WallOfFameManager />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>⛳ Post-Round Score Entry</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>Enter hole-by-hole scores from your scorecard</p>
        </div>
      </div>
      
      {/* Tee Time Selector */}
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Select Round</label>
        <select
          value={selectedTeetimeId || ''}
          onChange={(e) => { setSelectedTeetimeId(e.target.value || null); setScorecard(null); setGridData({}); setSelectedPlayerId(null); setSelectedTeeId(null); setHoleData([]); }}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff' }}
        >
          <option value="">— Choose a tee time —</option>
          {teetimes.map(tt => (
            <option key={tt.id} value={tt.id}>
              {formatDate(tt.date)} — {tt.course_name}{tt.event_name ? ` — ${tt.event_name}` : ''} — {tt.player_count} players {tt.scoring_mode === 'team'
                ? (Number(tt.team_scores_entered) > 0 ? `(team round · ${tt.team_scores_entered} team scores entered)` : '(team round)')
                : (tt.scores_entered > 0 ? `(${tt.scores_entered} scores entered)` : '')}
            </option>
          ))}
        </select>
        {teetimes.length === 0 && (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#9ca3af' }}>No tee times found in the last 14 days. Create a tee time with players assigned first.</p>
        )}
        {selectedTeetimeId && onViewScorecardReport && (teetimes.find(t => t.id === selectedTeetimeId) || {}).scoring_mode !== 'team' && (
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              onClick={() => onViewScorecardReport(selectedTeetimeId)}
              style={{ background: 'none', border: 'none', padding: 0, color: '#166534', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
              title="Open the full-field Scorecard Report for this round (Gross / Net / Putts, printable)"
            >
              ⛳ View full-field Scorecard Report for this round →
            </button>
          </div>
        )}
      </div>

      {/* ================= LIVE LEADERBOARD SHARE PANEL (Enhancement B) ================= */}
      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#166534' }}>📊 Live Leaderboard</span>
        </div>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#374151' }}>
          Share a live, no-login leaderboard link for a round. Anyone with the link can follow along.
          If a round is part of a multi-tee-time day, one link shows the whole field.
        </p>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Choose a round to share</label>
        <select
          value={shareTeetimeId || ''}
          onChange={(e) => { setShareTeetimeId(e.target.value || null); setShareUrl(''); setShareCopied(false); }}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff' }}
        >
          <option value="">— Choose a tee time —</option>
          {teetimes.map(tt => (
            <option key={tt.id} value={tt.id}>
              {formatDate(tt.date)} — {tt.course_name} — {tt.player_count} players{sharedMap[tt.id] ? '  ✓ shared' : ''}
            </option>
          ))}
        </select>

        {shareTeetimeId && (
          <div style={{ marginTop: 10 }}>
            {!sharedMap[shareTeetimeId] ? (
              <button
                type="button"
                disabled={shareBusy}
                onClick={async () => {
                  setShareBusy(true);
                  try {
                    const resp = await fetch(`${API_URL}/scoring/teetimes/${shareTeetimeId}/share-leaderboard`, {
                      method: 'POST', credentials: 'include', headers: getHeaders(true)
                    });
                    if (resp.ok) {
                      const data = await resp.json();
                      setShareUrl(data.url || `${window.location.origin}/lb/${shareTeetimeId}`);
                      setSharedMap(m => ({ ...m, [shareTeetimeId]: true }));
                      addToast('Leaderboard shared — link ready to copy', 'success');
                    } else {
                      addToast('Failed to share leaderboard', 'error');
                    }
                  } catch (e) { addToast('Network error sharing leaderboard', 'error'); }
                  finally { setShareBusy(false); }
                }}
                style={{ padding: '10px 18px', background: '#166534', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: shareBusy ? 'default' : 'pointer', opacity: shareBusy ? 0.6 : 1 }}
              >
                {shareBusy ? 'Sharing…' : '🔗 Share this leaderboard'}
              </button>
            ) : (
              (() => {
              // Sept 17, 2026: an event round shares its public address (the one on the
              // scorecards); every shared round gets the clubhouse TV view next to it.
              const shareRow = teetimes.find(t => t.id === shareTeetimeId) || {};
              const eventAddress = shareRow.event_public_slug ? `https://${PUBLIC_BOARD_HOST.toLowerCase()}/${shareRow.event_public_slug}` : null;
              const boardUrl = eventAddress || shareUrl || `${window.location.origin}/lb/${shareTeetimeId}`;
              const tvUrl = `${boardUrl}/tv`;
              const tvShown = eventAddress ? `${PUBLIC_BOARD_HOST}/${shareRow.event_public_slug}/tv` : tvUrl;
              const linkStyle = { color: '#166534', fontWeight: 700, fontSize: 13, textDecoration: 'none' };
              return (
              <div>
                {eventAddress && (
                  <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>
                    {shareRow.event_name ? <strong>{shareRow.event_name}</strong> : 'This event'} — the address printed on the scorecards and cart signs:
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    readOnly
                    value={boardUrl}
                    data-testid="share-board-url"
                    onFocus={(e) => e.target.select()}
                    style={{ flex: '1 1 240px', minWidth: 0, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: '#fff', color: '#166534', fontWeight: 600 }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const url = boardUrl;
                      navigator.clipboard?.writeText(url).then(() => {
                        setShareCopied(true); addToast('Link copied', 'success');
                        setTimeout(() => setShareCopied(false), 2000);
                      }).catch(() => addToast('Could not copy — select and copy manually', 'error'));
                    }}
                    style={{ padding: '8px 16px', background: '#166534', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                  >
                    {shareCopied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }} data-testid="share-tv-row">
                  <a href={boardUrl} target="_blank" rel="noreferrer" style={linkStyle}>Open the board ↗</a>
                  <a href={tvUrl} target="_blank" rel="noreferrer" style={linkStyle} data-testid="share-tv-link" title="Full-screen view for a clubhouse TV — pages through the whole field">📺 TV view ↗</a>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(tvUrl).then(() => {
                        setTvCopied(true); addToast('TV address copied', 'success');
                        setTimeout(() => setTvCopied(false), 2000);
                      }).catch(() => addToast('Could not copy — select and copy manually', 'error'));
                    }}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#166534', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                  >
                    {tvCopied ? '✓ Copied' : 'Copy TV address'}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  On the clubhouse TV, open <strong style={{ color: '#374151' }}>{tvShown}</strong> in its web browser.
                </div>
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    disabled={shareBusy}
                    onClick={async () => {
                      setShareBusy(true);
                      try {
                        const resp = await fetch(`${API_URL}/scoring/teetimes/${shareTeetimeId}/share-leaderboard`, {
                          method: 'DELETE', credentials: 'include', headers: getHeaders(true)
                        });
                        if (resp.ok) {
                          setSharedMap(m => ({ ...m, [shareTeetimeId]: false }));
                          setShareUrl('');
                          addToast('Sharing turned off — the link no longer shows scores', 'success');
                        } else {
                          addToast('Failed to turn off sharing', 'error');
                        }
                      } catch (e) { addToast('Network error', 'error'); }
                      finally { setShareBusy(false); }
                    }}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Turn off sharing (revoke link)
                  </button>
                </div>
              </div>
              );
              })()
            )}
          </div>
        )}
      </div>
      
      {loadingScorecard && (
        <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading scorecard...</div>
      )}
      
      {scorecard && !scorecard.default_tee && (
        <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: 12, fontSize: 13, color: '#92400e' }}>
          No hole data found for this course. Go to Courses tab, select the course, select a tee, click "Holes" to enter hole data first.
        </div>
      )}
      
      {scorecard && scorecard.players.length === 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: 12, fontSize: 13, color: '#92400e' }}>
          No confirmed players found for this tee time. Add and confirm players first.
        </div>
      )}
      
      {/* Event handicaps (Sept 17, 2026): a per-round index correction by the pro or
          the handicap committee. Above the grids so it is settled before scores go in,
          and on both round types — a scramble uses it through the team handicaps. */}
      {scorecard && selectedTeetimeId && (
        <EventHandicapPanel teetimeId={selectedTeetimeId} onChanged={() => setScorecardReload(n => n + 1)} />
      )}

      {/* Round Recap review (Oct 3, 2026): the selected round's recap — edit, write again, post, don't post */}
      {/* Event recap (Oct 4, 2026): team tee times show it too — the panel hides itself unless it's an event */}
      {/* Event Desk (Oct 4, 2026 — recap step 4, plan §13.2): the desk gets the same panel for its own event */}
      {scorecard && selectedTeetimeId && scorecard.tee_time && (
        <RecapReviewPanel teetimeId={selectedTeetimeId} teamRound={scorecard.tee_time.scoring_mode === 'team'} />
      )}

      {/* Team-scored round (Sept 17, 2026): the team cards, not the per-player grid */}
      {scorecard && scorecard.tee_time && scorecard.tee_time.scoring_mode === 'team' && (
        <TeamRoundScoreGrid teetimeId={selectedTeetimeId} />
      )}

      {/* Scorecard Grid */}
      {scorecard && scorecard.default_tee && scorecard.players.length > 0 && !(scorecard.tee_time && scorecard.tee_time.scoring_mode === 'team') && (
        <div>
          {/* Course & Tee Info */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 600 }}>{scorecard.tee_time.course_name}</span>
              {/* Tee Selector */}
              <select
                value={selectedTeeId || ''}
                onChange={(e) => {
                  changeTee(e.target.value);
                  // Track this tee choice for the current player
                  if (selectedPlayerId) {
                    setPlayerTeeIds(prev => ({ ...prev, [selectedPlayerId]: e.target.value }));
                  }
                }}
                style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: '#fff' }}
              >
                {scorecard.tees.filter(t => t.has_hole_data).map(tee => (
                  <option key={tee.id} value={tee.id}>{tee.tee_name} tees</option>
                ))}
              </select>
              {selectedTee && (
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Par {parTotals.total} • Rating {selectedTee.course_rating} / Slope {selectedTee.slope_rating}
                </span>
              )}
            </div>
            <button
              onClick={saveAllScores}
              disabled={saving}
              style={{ padding: '6px 16px', background: saving ? '#9ca3af' : '#166534', color: '#fff', border: 'none', borderRadius: 6, cursor: saving ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              {saving ? 'Saving...' : 'Save All Scores'}
            </button>
          </div>
          
          {/* Blank holes inside a card: a modal, not a toast — organizers miss toasts */}
          {gridBlock && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
              <div style={{ background: '#fff', borderRadius: 14, padding: 22, maxWidth: 420, width: '100%' }}>
                <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700 }}>A hole is blank</h3>
                {gridBlock.map(b => (
                  <p key={b.name} style={{ margin: '0 0 8px', fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
                    <strong>{b.name}</strong> has nothing on hole{b.holes.length > 1 ? 's' : ''} {b.holes.join(', ')}. Enter a score, or type <strong>X</strong> for a pick-up. This card was not saved.
                  </p>
                ))}
                <button onClick={() => setGridBlock(null)}
                  style={{ marginTop: 8, width: '100%', padding: '10px 0', background: '#166534', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>OK</button>
              </div>
            </div>
          )}

          {/* Player Tabs — LAST names (Dennis, Sept 27: five "Player"s and three "Terry"s
              can't be told apart by first name); a first initial when two share a last
              name. Alphabetical by last name, then first (Dennis, Sept 27: with a field
              of 30+, finding the name beats keeping a group together). */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {postRoundAlpha(scorecard.players).map(player => {
              const totals = getPlayerTotals(player.id);
              const isActive = selectedPlayerId === player.id;
              const ch = getCourseHandicap(player);
              return (
                <button
                  key={player.id}
                  onClick={() => {
                    setSelectedPlayerId(player.id);
                    // Switch to this player's tee (per-player tee map, falls back to current tee)
                    const thisTeeId = playerTeeIds[player.id] || selectedTeeId;
                    if (thisTeeId && thisTeeId !== selectedTeeId) {
                      changeTee(thisTeeId);
                    }
                  }}
                  style={{
                    padding: '6px 12px', border: isActive ? '2px solid #166534' : '1px solid #d1d5db',
                    background: isActive ? '#f0fdf4' : '#fff', borderRadius: 6, cursor: 'pointer',
                    fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#166534' : '#374151'
                  }}
                >
                  {postRoundTabName(player, scorecard.players)}
                  {ch !== null && <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 3 }}>({ch})</span>}
                  {totals.holesEntered > 0 && (
                    <span style={{ marginLeft: 4, fontSize: 11, color: '#6b7280' }}>{totals.total}</span>
                  )}
                </button>
              );
            })}
            {selectedPlayer && courseHcp !== null && (
              <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>Course HCP: {courseHcp}</span>
            )}
          </div>
          
          {/* Score Grid for Selected Player */}
          {selectedPlayerId && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
                <thead>
                  {/* Hole numbers */}
                  <tr style={{ background: '#1f2937', color: '#fff' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 55, position: 'sticky', left: 0, background: '#1f2937', zIndex: 1 }}>Hole</th>
                    {[1,2,3,4,5,6,7,8,9].map(h => (
                      <th key={h} style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, minWidth: 38 }}>{h}</th>
                    ))}
                    <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 700, background: '#374151', minWidth: 38 }}>OUT</th>
                    {[10,11,12,13,14,15,16,17,18].map(h => (
                      <th key={h} style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, minWidth: 38 }}>{h}</th>
                    ))}
                    <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 700, background: '#374151', minWidth: 38 }}>IN</th>
                    <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 700, background: '#166534', minWidth: 42 }}>TOT</th>
                  </tr>
                  {/* Par */}
                  <tr style={{ background: '#f3f4f6' }}>
                    <td style={{ padding: '4px 8px', fontWeight: 600, fontSize: 12, color: '#6b7280', position: 'sticky', left: 0, background: '#f3f4f6', zIndex: 1 }}>Par</td>
                    {[1,2,3,4,5,6,7,8,9].map(h => {
                      const hole = holeData.find(hd => hd.hole_number === h);
                      return <td key={h} style={{ padding: '4px', textAlign: 'center', fontSize: 12, color: '#6b7280' }}>{hole?.par || ''}</td>;
                    })}
                    <td style={{ padding: '4px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#374151' }}>{parTotals.front}</td>
                    {[10,11,12,13,14,15,16,17,18].map(h => {
                      const hole = holeData.find(hd => hd.hole_number === h);
                      return <td key={h} style={{ padding: '4px', textAlign: 'center', fontSize: 12, color: '#6b7280' }}>{hole?.par || ''}</td>;
                    })}
                    <td style={{ padding: '4px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#374151' }}>{parTotals.back}</td>
                    <td style={{ padding: '4px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#374151' }}>{parTotals.total}</td>
                  </tr>
                  {/* Yardage */}
                  <tr style={{ background: '#f9fafb' }}>
                    <td style={{ padding: '4px 8px', fontWeight: 600, fontSize: 11, color: '#9ca3af', position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}>Yds</td>
                    {[1,2,3,4,5,6,7,8,9].map(h => {
                      const hole = holeData.find(hd => hd.hole_number === h);
                      return <td key={h} style={{ padding: '2px', textAlign: 'center', fontSize: 11, color: '#9ca3af' }}>{hole?.yardage || ''}</td>;
                    })}
                    <td style={{ padding: '2px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#6b7280' }}>{holeData.filter(h => h.hole_number <= 9).reduce((s,h) => s + (h.yardage||0), 0)}</td>
                    {[10,11,12,13,14,15,16,17,18].map(h => {
                      const hole = holeData.find(hd => hd.hole_number === h);
                      return <td key={h} style={{ padding: '2px', textAlign: 'center', fontSize: 11, color: '#9ca3af' }}>{hole?.yardage || ''}</td>;
                    })}
                    <td style={{ padding: '2px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#6b7280' }}>{holeData.filter(h => h.hole_number > 9).reduce((s,h) => s + (h.yardage||0), 0)}</td>
                    <td style={{ padding: '2px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#6b7280' }}>{holeData.reduce((s,h) => s + (h.yardage||0), 0)}</td>
                  </tr>
                  {/* HCP */}
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <td style={{ padding: '4px 8px', fontWeight: 600, fontSize: 11, color: '#9ca3af', position: 'sticky', left: 0, background: '#f3f4f6', zIndex: 1 }}>HCP</td>
                    {[1,2,3,4,5,6,7,8,9].map(h => {
                      const hole = holeData.find(hd => hd.hole_number === h);
                      const strokes = hole ? getStrokesOnHole(courseHcp, hole.handicap_index) : 0;
                      return <td key={h} style={{ padding: '2px', textAlign: 'center', fontSize: 11, color: strokes > 0 ? '#166534' : '#9ca3af', fontWeight: strokes > 0 ? 700 : 400 }}>{hole?.handicap_index || ''}{strokes > 0 ? <span style={{ color: '#16a34a', fontSize: 9 }}>{'\u2022'.repeat(strokes)}</span> : ''}</td>;
                    })}
                    <td style={{ padding: '2px' }}></td>
                    {[10,11,12,13,14,15,16,17,18].map(h => {
                      const hole = holeData.find(hd => hd.hole_number === h);
                      const strokes = hole ? getStrokesOnHole(courseHcp, hole.handicap_index) : 0;
                      return <td key={h} style={{ padding: '2px', textAlign: 'center', fontSize: 11, color: strokes > 0 ? '#166534' : '#9ca3af', fontWeight: strokes > 0 ? 700 : 400 }}>{hole?.handicap_index || ''}{strokes > 0 ? <span style={{ color: '#16a34a', fontSize: 9 }}>{'\u2022'.repeat(strokes)}</span> : ''}</td>;
                    })}
                    <td style={{ padding: '2px' }}></td>
                    <td style={{ padding: '2px' }}></td>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const totals = getPlayerTotals(selectedPlayerId);
                    return (
                      <>
                        {/* Score entry row */}
                        <tr>
                          <td style={{ padding: '4px 8px', fontWeight: 600, fontSize: 12, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>Score</td>
                          {[1,2,3,4,5,6,7,8,9].map(h => {
                            const hole = holeData.find(hd => hd.hole_number === h);
                            const val = gridData[selectedPlayerId]?.[h]?.score || '';
                            const colorStyle = getScoreColor(val, hole?.par);
                            return (
                              <td key={h} style={{ padding: '2px' }}>
                                <input ref={el => scoreInputRefs.current[h] = el} type="text" inputMode="numeric" maxLength={3} value={gridData[selectedPlayerId]?.[h]?.picked && val ? `${val}x` : val}
                                  onChange={(e) => updateCell(selectedPlayerId, h, 'score', e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  style={{ width: '100%', padding: '4px 2px', textAlign: 'center', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14, fontWeight: 600, boxSizing: 'border-box', ...colorStyle }}
                                />
                              </td>
                            );
                          })}
                          <td style={{ padding: '4px', textAlign: 'center', fontWeight: 700, fontSize: 14, background: '#f3f4f6' }}>{totals.front || ''}</td>
                          {[10,11,12,13,14,15,16,17,18].map(h => {
                            const hole = holeData.find(hd => hd.hole_number === h);
                            const val = gridData[selectedPlayerId]?.[h]?.score || '';
                            const colorStyle = getScoreColor(val, hole?.par);
                            return (
                              <td key={h} style={{ padding: '2px' }}>
                                <input ref={el => scoreInputRefs.current[h] = el} type="text" inputMode="numeric" maxLength={3} value={gridData[selectedPlayerId]?.[h]?.picked && val ? `${val}x` : val}
                                  onChange={(e) => updateCell(selectedPlayerId, h, 'score', e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  style={{ width: '100%', padding: '4px 2px', textAlign: 'center', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14, fontWeight: 600, boxSizing: 'border-box', ...colorStyle }}
                                />
                              </td>
                            );
                          })}
                          <td style={{ padding: '4px', textAlign: 'center', fontWeight: 700, fontSize: 14, background: '#f3f4f6' }}>{totals.back || ''}</td>
                          <td style={{ padding: '4px', textAlign: 'center', fontWeight: 700, fontSize: 14, background: '#166534', color: '#fff', borderRadius: '0 4px 4px 0' }}>{totals.total || ''}</td>
                        </tr>
                        
                        {/* Adjusted score row */}
                        <tr style={{ background: '#fefce8' }}>
                          <td style={{ padding: '4px 8px', fontWeight: 600, fontSize: 11, color: '#92400e', position: 'sticky', left: 0, background: '#fefce8', zIndex: 1 }}>Adj</td>
                          {[1,2,3,4,5,6,7,8,9].map(h => {
                            const hole = holeData.find(hd => hd.hole_number === h);
                            const val = gridData[selectedPlayerId]?.[h]?.score || '';
                            const strokes = hole ? getStrokesOnHole(courseHcp, hole.handicap_index) : 0;
                            const adj = getAdjustedScore(val, hole?.par, strokes);
                            const wasAdjusted = adj !== '' && parseInt(val) > adj;
                            return <td key={h} style={{ padding: '2px', textAlign: 'center', fontSize: 12, color: wasAdjusted ? '#dc2626' : '#6b7280', fontWeight: wasAdjusted ? 700 : 400 }}>{adj}</td>;
                          })}
                          <td style={{ padding: '4px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#92400e', background: '#fef9c3' }}>{totals.adjFront || ''}</td>
                          {[10,11,12,13,14,15,16,17,18].map(h => {
                            const hole = holeData.find(hd => hd.hole_number === h);
                            const val = gridData[selectedPlayerId]?.[h]?.score || '';
                            const strokes = hole ? getStrokesOnHole(courseHcp, hole.handicap_index) : 0;
                            const adj = getAdjustedScore(val, hole?.par, strokes);
                            const wasAdjusted = adj !== '' && parseInt(val) > adj;
                            return <td key={h} style={{ padding: '2px', textAlign: 'center', fontSize: 12, color: wasAdjusted ? '#dc2626' : '#6b7280', fontWeight: wasAdjusted ? 700 : 400 }}>{adj}</td>;
                          })}
                          <td style={{ padding: '4px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#92400e', background: '#fef9c3' }}>{totals.adjBack || ''}</td>
                          <td style={{ padding: '4px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#92400e', background: '#fef9c3' }}>{totals.adjTotal || ''}</td>
                        </tr>
                        
                        {/* Putts entry row */}
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <td style={{ padding: '4px 8px', fontWeight: 600, fontSize: 12, color: '#6b7280', position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>Putts</td>
                          {[1,2,3,4,5,6,7,8,9].map(h => {
                            const val = gridData[selectedPlayerId]?.[h]?.putts || '';
                            return (
                              <td key={h} style={{ padding: '2px' }}>
                                <input ref={el => puttsInputRefs.current[h] = el} type="text" inputMode="numeric" maxLength={1} value={val}
                                  onChange={(e) => updateCell(selectedPlayerId, h, 'putts', e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  style={{ width: '100%', padding: '4px 2px', textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 12, color: '#6b7280', boxSizing: 'border-box' }}
                                />
                              </td>
                            );
                          })}
                          <td style={{ padding: '4px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#6b7280', background: '#f9fafb' }}>{totals.frontPutts || ''}</td>
                          {[10,11,12,13,14,15,16,17,18].map(h => {
                            const val = gridData[selectedPlayerId]?.[h]?.putts || '';
                            return (
                              <td key={h} style={{ padding: '2px' }}>
                                <input ref={el => puttsInputRefs.current[h] = el} type="text" inputMode="numeric" maxLength={1} value={val}
                                  onChange={(e) => updateCell(selectedPlayerId, h, 'putts', e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  style={{ width: '100%', padding: '4px 2px', textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 12, color: '#6b7280', boxSizing: 'border-box' }}
                                />
                              </td>
                            );
                          })}
                          <td style={{ padding: '4px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#6b7280', background: '#f9fafb' }}>{totals.backPutts || ''}</td>
                          <td style={{ padding: '4px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#6b7280', background: '#f9fafb' }}>{totals.totalPutts || ''}</td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
              
              {/* Legend */}
              <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11, color: '#6b7280', flexWrap: 'wrap', alignItems: 'center' }}>
                <span><span style={{ display: 'inline-block', width: 14, height: 14, background: '#fbbf24', borderRadius: 2, verticalAlign: 'middle', marginRight: 3 }}></span> Eagle+</span>
                <span><span style={{ display: 'inline-block', width: 14, height: 14, background: '#dc2626', borderRadius: 2, verticalAlign: 'middle', marginRight: 3 }}></span> Birdie</span>
                <span style={{ fontWeight: 600 }}>Par</span>
                <span><span style={{ display: 'inline-block', width: 14, height: 14, background: '#3b82f6', borderRadius: 2, verticalAlign: 'middle', marginRight: 3 }}></span> Bogey</span>
                <span><span style={{ display: 'inline-block', width: 14, height: 14, background: '#1e3a5f', borderRadius: 2, verticalAlign: 'middle', marginRight: 3 }}></span> Dbl+</span>
                <span style={{ marginLeft: 8 }}><span style={{ color: '#16a34a', fontSize: 13 }}>{'\u2022'}</span> = stroke hole</span>
                <span><span style={{ color: '#dc2626', fontWeight: 700 }}>Adj</span> = score was capped (Net Dbl Bogey)</span>
                <span><strong>7x</strong> = picked up (type X) — scored as a net double bogey</span>
              </div>
              
              {/* Save button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
                <button
                  onClick={() => savePlayerScores(selectedPlayerId)}
                  disabled={saving}
                  style={{ padding: '8px 20px', background: saving ? '#9ca3af' : '#166534', color: '#fff', border: 'none', borderRadius: 6, cursor: saving ? 'default' : 'pointer', fontSize: 14, fontWeight: 600 }}
                >
                  {saving ? 'Saving...' : `Save ${selectedPlayer?.name || 'Player'}'s Scores`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};




const SettingsMenu = () => {
  const appNav = useContext(AppNavContext);   // Print tab → "Open the report" (Sept 17, 2026)
  const [tab, setTab] = useState(() => (appNav.readIntent('admin-reports') ? "reports"
    : appNav.readIntent('scoring-round') ? "scoring"          // Ready check → Scoring (Oct 4, 2026)
    : appNav.readIntent('admin-courses') ? "courses"          // Ready check → Courses
    : "players"));
  const [showHelp, setShowHelp] = useState(false);
  // Hand-off: when the Scoring screen links to the Scorecard Report, it stashes
  // the selected round here and flips to the Reports tab. ReportsView consumes
  // this as its initial round so the report opens on the round just being scored.
  const [scoringReportRound, setScoringReportRound] = useState(() => { const i = appNav.readIntent('admin-reports'); return i ? i.teetimeId : null; });
  useEffect(() => {
    appNav.clearIntent('admin-courses');
    appNav.clearIntent('admin-players');
    appNav.clearIntent('admin-reports');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [scoringEnabledForTab, setScoringEnabledForTab] = useState(false);
  // Phase 2 Scoring gate, May 14, 2026.
  // Default to LOCKED while tier is loading — mirrors the SMS pattern at line ~2683
  // and the Pairings pattern at line ~9907. Briefly locking a paying tier is better
  // than flashing an unlocked Scoring tab to a Free user.
  const { tier: orgTier, loading: tierLoading } = useOrgTier();
  const scoringLocked = tierLoading || !orgTier || !canUseFeature(orgTier, 'scoring');
  
  // Check if scoring is enabled for this org (for showing Scoring tab)
  useEffect(() => {
    const checkScoring = async () => {
      try {
        const resp = await fetch(`${API_URL}/scoring/status`, { credentials: 'include', headers: (() => { const h = {}; const t = getAuthToken(); if (t) h['Authorization'] = `Bearer ${t}`; return h; })() });
        if (resp.ok) {
          const data = await resp.json();
          setScoringEnabledForTab(data.scoring_enabled || false);
        }
      } catch (e) { /* ignore - just won't show tab */ }
    };
    checkScoring();
  }, []);
  
  // Get help content for current sub-tab
  const currentHelp = getHelpContent('admin', tab);
  
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Tab Navigation */}
      <div style={{ borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => setTab("players")} style={{ padding: "8px 12px", border: "none", background: "transparent", borderBottom: tab === "players" ? "2px solid #111827" : "2px solid transparent", cursor: "pointer", fontSize: 14, fontWeight: tab === "players" ? 600 : 400 }}>Players</button>
          <button onClick={() => setTab("courses")} style={{ padding: "8px 12px", border: "none", background: "transparent", borderBottom: tab === "courses" ? "2px solid #111827" : "2px solid transparent", cursor: "pointer", fontSize: 14, fontWeight: tab === "courses" ? 600 : 400 }}>Courses</button>
          <button onClick={() => setTab("groups")} style={{ padding: "8px 12px", border: "none", background: "transparent", borderBottom: tab === "groups" ? "2px solid #111827" : "2px solid transparent", cursor: "pointer", fontSize: 14, fontWeight: tab === "groups" ? 600 : 400 }}>Groups</button>
          <button onClick={() => setTab("message")} style={{ padding: "8px 12px", border: "none", background: "transparent", borderBottom: tab === "message" ? "2px solid #111827" : "2px solid transparent", cursor: "pointer", fontSize: 14, fontWeight: tab === "message" ? 600 : 400 }}>Message</button>
          <button onClick={() => setTab("reports")} style={{ padding: "8px 12px", border: "none", background: "transparent", borderBottom: tab === "reports" ? "2px solid #111827" : "2px solid transparent", cursor: "pointer", fontSize: 14, fontWeight: tab === "reports" ? 600 : 400 }}>Reports</button>
          {/* Scoring tab — Phase 2 Scoring gate (May 14, 2026):
              Locked branch (scoringLocked = true): Free/Starter orgs AND while tier is loading.
                Shows upsell-locked tab regardless of scoring_enabled (Show + Lock + Educate per
                Rule 12). Click opens UpgradeModal instead of switching tabs.
              Unlocked branch (Plus+ confirmed): existing behavior — tab shows when admin has
                enabled scoring_enabled. Default-to-locked guards against flash-of-unlocked-feature
                during the brief useOrgTier loading window. */}
          {scoringLocked ? (
            <button
              onClick={() => triggerUpgradeModal({ feature: 'scoring', requiredTier: 'starter' })}
              style={{ padding: "8px 12px", border: "none", background: "transparent", borderBottom: "2px solid transparent", cursor: "pointer", fontSize: 14, fontWeight: 400, color: "#9ca3af", display: "inline-flex", alignItems: "center", gap: 6 }}
              title="Your current plan doesn't include Scoring. Click to see upgrade options."
            >
              ⛳ Scoring
              <LockBadge feature="scoring" requiredTier="starter" style={{ fontSize: 10, padding: '1px 6px' }} />
            </button>
          ) : (
            scoringEnabledForTab && (
              <button onClick={() => setTab("scoring")} style={{ padding: "8px 12px", border: "none", background: "transparent", borderBottom: tab === "scoring" ? "2px solid #166534" : "2px solid transparent", cursor: "pointer", fontSize: 14, fontWeight: tab === "scoring" ? 600 : 400, color: tab === "scoring" ? "#166534" : "#6b7280" }}>⛳ Scoring</button>
            )
          )}
          
          {/* Help Button - positioned after tabs */}
          <button
            onClick={() => setShowHelp(true)}
            style={{
              marginLeft: "auto",
              padding: "6px 12px",
              border: "1px solid #d1d5db",
              background: "#f9fafb",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 13,
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
            title="What is this page about?"
          >
            <span style={{ fontSize: 15 }}>ⓘ</span>
            <span>Help</span>
          </button>
        </div>
      </div>
      
      {/* Page Content */}
      {tab === "players" && <PlayerManager />}
      {tab === "courses" && <CourseManager />}
      {tab === "groups" && <GroupManager />}
      {tab === "message" && <MessageManager />}
      {tab === "reports" && (
        <ReportsView
          initialScorecardRound={scoringReportRound}
          onConsumeInitialScorecardRound={() => setScoringReportRound(null)}
        />
      )}
      {tab === "scoring" && (
        <PostRoundScoring
          onViewScorecardReport={(roundId) => { setScoringReportRound(roundId); setTab("reports"); }}
        />
      )}
      
      {/* Help Modal */}
      <HelpInfoModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={currentHelp.title}
        content={currentHelp.content}
      />
    </div>
  );
};

// Player Portal Component
// ============================================================================
// ============================================================================
// PLAYER TESTIMONIAL MODAL - Player experience submission
// Added: March 2026
// ============================================================================
const PlayerTestimonialModal = ({ token, playerData, onClose }) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [existingTestimonial, setExistingTestimonial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    rating: 5,
    response: '',
    permission_granted: false,
    display_name_preference: 'first_state',
  });

  useEffect(() => {
    const checkExisting = async () => {
      try {
        const res = await fetch(`${API_URL}/referral/player/testimonial/mine?token=${token}`);
        if (res.ok) {
          const data = await res.json();
          if (data.submitted) {
            setSubmitted(true);
            setExistingTestimonial(data.testimonial);
          }
        }
      } catch (e) {
        console.error('Player testimonial check error:', e);
      } finally {
        setLoading(false);
      }
    };
    checkExisting();
  }, [token]);

  const handleSubmit = async () => {
    if (!form.response.trim() || form.response.trim().length < 10) {
      addToast('Please share a bit more — at least a sentence!', 'error');
      return;
    }
    if (!form.permission_granted) {
      addToast('Please check the permission box to submit.', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/referral/player/testimonial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, token })
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        addToast('Thank you! Your testimonial has been submitted for review.', 'success');
      } else {
        addToast(data.error || 'Failed to submit. Please try again.', 'error');
      }
    } catch (e) {
      addToast('Something went wrong. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const firstName = (playerData?.name || '').split(' ')[0] || 'A fellow golfer';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 16,
        width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
          borderRadius: '16px 16px 0 0'
        }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>⭐ Share Your Experience</h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>Help other golfers discover Tee Time Organizer</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', padding: 4 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, display: 'grid', gap: 20 }}>
          {submitted ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              <div style={{ fontWeight: 700, color: '#166534', fontSize: 16, marginBottom: 4 }}>Thank You, {firstName}!</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Your testimonial is greatly appreciated!{' '}
                {existingTestimonial && (
                  <span>Status: <strong style={{ color: existingTestimonial.status === 'approved' ? '#166534' : '#92400e' }}>
                    {existingTestimonial.status.charAt(0).toUpperCase() + existingTestimonial.status.slice(1)}
                  </strong></span>
                )}
              </div>
              <button onClick={onClose} style={{ marginTop: 16, padding: '8px 20px', background: '#2d6a4f', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          ) : (
            <>
              {/* Star Rating */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  How would you rate your experience?
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button key={star} onClick={() => setForm(f => ({ ...f, rating: star }))}
                      style={{ fontSize: 32, background: 'none', border: 'none', cursor: 'pointer', color: star <= form.rating ? '#f59e0b' : '#d1d5db', padding: '0 2px', lineHeight: 1 }}>★</button>
                  ))}
                </div>
              </div>

              {/* Question */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  How has Tee Time Organizer made your golf experience better?
                </label>
                <textarea
                  value={form.response}
                  onChange={e => setForm(f => ({ ...f, response: e.target.value }))}
                  placeholder="Tell us what you think..."
                  rows={4}
                  style={{
                    width: '100%', padding: '10px 12px',
                    border: `2px solid ${form.response.trim().length > 0 ? '#2d6a4f' : '#d1d5db'}`,
                    borderRadius: 8, fontSize: 14, color: '#111827',
                    resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                    background: '#fff'
                  }}
                />
                {form.response.length > 0 && (
                  <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: 2 }}>{form.response.length} characters</div>
                )}
              </div>

              {/* Attribution */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  If approved, how should we credit you?
                </label>
                <div style={{ display: 'grid', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: '#374151', padding: '8px 12px', borderRadius: 8, border: `2px solid ${form.display_name_preference === 'first_state' ? '#2d6a4f' : '#e5e7eb'}`, background: form.display_name_preference === 'first_state' ? '#f0fdf4' : '#fff' }}>
                    <input type="radio" name="pt_display_pref" value="first_state"
                      checked={form.display_name_preference === 'first_state'}
                      onChange={() => setForm(f => ({ ...f, display_name_preference: 'first_state' }))}
                      style={{ accentColor: '#2d6a4f' }} />
                    <span>First name only — <em style={{ color: '#6b7280' }}>{firstName}</em></span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: '#374151', padding: '8px 12px', borderRadius: 8, border: `2px solid ${form.display_name_preference === 'full' ? '#2d6a4f' : '#e5e7eb'}`, background: form.display_name_preference === 'full' ? '#f0fdf4' : '#fff' }}>
                    <input type="radio" name="pt_display_pref" value="full"
                      checked={form.display_name_preference === 'full'}
                      onChange={() => setForm(f => ({ ...f, display_name_preference: 'full' }))}
                      style={{ accentColor: '#2d6a4f' }} />
                    <span>Full name — <em style={{ color: '#6b7280' }}>{playerData?.name || 'Your Name'}</em></span>
                  </label>
                </div>
              </div>

              {/* Permission */}
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px', background: '#fefce8',
                border: '1px solid #fde68a', borderRadius: 8,
                cursor: 'pointer', fontSize: 13, color: '#374151', lineHeight: 1.5
              }}>
                <input type="checkbox" checked={form.permission_granted}
                  onChange={e => setForm(f => ({ ...f, permission_granted: e.target.checked }))}
                  style={{ marginTop: 2, accentColor: '#2d6a4f', width: 16, height: 16, flexShrink: 0 }} />
                <span>I give Tee Time Organizer permission to use my response on their website and in marketing materials.</span>
              </label>
            </>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div style={{
            padding: '16px 24px', borderTop: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'flex-end', gap: 12,
            position: 'sticky', bottom: 0, background: '#fff', borderRadius: '0 0 16px 16px'
          }}>
            <button onClick={onClose} style={{ padding: '10px 20px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={saving}
              style={{ padding: '10px 24px', background: saving ? '#9ca3af' : '#2d6a4f', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? '⏳ Submitting...' : '⭐ Submit'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
// ============================================================================

// PLAYER SHARE MODAL - Share Tee Time Organizer with friends
// Added: January 31, 2026
// ============================================================================
const PlayerShareModal = ({ token, playerData, onClose }) => {
  const { addToast } = useToast();
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [emailContent, setEmailContent] = useState({ subject: '', body: '' });
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [showTestimonial, setShowTestimonial] = useState(false);
  
  // Use short_code if available, otherwise use first 8 chars of player ID for tracking
  const playerShortCode = playerData?.short_code;
  const playerId = playerData?.id;
  const referralLink = playerShortCode 
    ? `https://teetimeorganizer.net/welcome?ref=P-${playerShortCode}`
    : playerId
      ? `https://teetimeorganizer.net/welcome?ref=PID-${playerId.substring(0, 8).toUpperCase()}`
      : `https://teetimeorganizer.net/welcome`;
  
  const handlePreviewEmail = async () => {
    if (!recipientName.trim() || !recipientEmail.trim()) {
      addToast('Please enter recipient name and email', 'error');
      return;
    }
    
    // Extract first name from full name
    const firstName = recipientName.trim().split(' ')[0];
    
    setLoadingEmail(true);
    try {
      const response = await fetch(`${API_URL}/referral/player/email-content?token=${token}&recipientName=${encodeURIComponent(firstName)}`);
      
      if (response.ok) {
        const data = await response.json();
        setEmailContent(data);
      } else {
        // Fallback content - use first name
        setEmailContent({
          subject: 'An Easier Way to Manage Your Golf Group',
          body: `Hey ${firstName},\n\nWe've been using this golf app called Tee Time Organizer for our golf group and thought you might like it.\n\nNew tee times are posted to my phone, and I just tap to RSVP to grab a spot - super easy. It handles waitlists, sends reminders, lets me know who else is playing, calculates my course handicap, sets pairings and allows me to do live-scoring & GHIN posting.\n\nYou may want to check it out. It's inexpensive - plans start at $4.99/mo, and they offer a free plan you can try with a small group (up to 8 players).\n\nCheck it out here:\n${referralLink}\n\nHappy to answer any questions, or reach out to the guy who wrote it directly at support@teetimeorganizer.net - he's happy to help.\n\nBest,\n${playerData?.name || 'A fellow golfer'}`
        });
      }
      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching email content:', error);
      setEmailContent({
        subject: 'An Easier Way to Manage Your Golf Group',
        body: `Hey ${firstName},\n\nWe've been using this golf app called Tee Time Organizer for our golf group and thought you might like it.\n\nNew tee times are posted to my phone, and I just tap to RSVP to grab a spot - super easy. It handles waitlists, sends reminders, lets me know who else is playing, calculates my course handicap, sets pairings and allows me to do live-scoring & GHIN posting.\n\nYou may want to check it out. It's inexpensive - plans start at $4.99/mo, and they offer a free plan you can try with a small group (up to 8 players).\n\nCheck it out here:\n${referralLink}\n\nHappy to answer any questions, or reach out to the guy who wrote it directly at support@teetimeorganizer.net - he's happy to help.\n\nBest,\n${playerData?.name || 'A fellow golfer'}`
      });
      setShowPreview(true);
    } finally {
      setLoadingEmail(false);
    }
  };
  
  const handleSendInvitation = async () => {
    setLoadingEmail(true);
    try {
      // Send the email via TTO backend
      const response = await fetch(`${API_URL}/referral/player/send-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token: token,
          recipientEmail: recipientEmail.trim().toLowerCase(),
          recipientName: recipientName.trim(),
          subject: emailContent.subject,
          body: emailContent.body
        })
      });
      
      if (response.ok) {
        addToast('Invitation email sent successfully!', 'success');
        setShowPreview(false);
        setRecipientName('');
        setRecipientEmail('');
        onClose();
      } else {
        const error = await response.json();
        addToast(error.message || 'Failed to send email. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      addToast('Failed to send email. Please try again.', 'error');
    } finally {
      setLoadingEmail(false);
    }
  };
  
  const handleCopyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      addToast('Referral link copied to clipboard!', 'success');
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: 24,
        maxWidth: 500,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>📤 Share Tee Time Organizer</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            ×
          </button>
        </div>
        
        {/* Why-share framing */}
        <div style={{ 
          padding: 12, 
          background: '#f0f9ff', 
          border: '1px solid #0ea5e9', 
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          color: '#0369a1'
        }}>
          <strong>💡 Know someone who runs a golf group?</strong> If you have a friend who's still herding their group with text chains and a notepad, do them a favor — Tee Time Organizer makes the whole thing effortless. Sharing takes ten seconds.
        </div>
        
        {!showPreview ? (
          <>
            {/* Email Invitation Form */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#374151', marginBottom: 12 }}>
                📧 Send a Personal Invitation
              </div>
              
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
                    Friend's Name
                  </label>
                  <input
                    type="text"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="John Smith"
                    style={{ 
                      width: '100%',
                      padding: '10px 12px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: 6, 
                      fontSize: 14,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
                    Friend's Email
                  </label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="john@example.com"
                    style={{ 
                      width: '100%',
                      padding: '10px 12px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: 6, 
                      fontSize: 14,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <button
                  onClick={handlePreviewEmail}
                  disabled={loadingEmail || !recipientName.trim() || !recipientEmail.trim()}
                  style={{
                    padding: '10px 16px',
                    background: (!recipientName.trim() || !recipientEmail.trim()) ? '#9ca3af' : '#2d6a4f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: (!recipientName.trim() || !recipientEmail.trim()) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loadingEmail ? '⏳ Loading...' : '👁️ Preview Email'}
                </button>
              </div>
            </div>
            
            {/* Or Copy Link */}
            <div style={{ 
              padding: 12, 
              background: '#f9fafb', 
              borderRadius: 8,
              marginBottom: 16
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>
                Or copy your referral link:
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  value={referralLink || 'Not available'}
                  readOnly
                  style={{ 
                    flex: 1,
                    padding: '8px 10px', 
                    border: '1px solid #d1d5db', 
                    borderRadius: 6, 
                    fontSize: 12,
                    fontFamily: 'monospace',
                    background: '#fff',
                    color: '#374151'
                  }}
                />
                <button
                  onClick={handleCopyLink}
                  style={{
                    padding: '8px 12px',
                    background: '#2d6a4f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📋 Copy
                </button>
              </div>
            </div>

            {/* Testimonial prompt */}
            <div style={{
              padding: '12px 14px',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12
            }}>
              <div style={{ fontSize: 13, color: '#166534', lineHeight: 1.4 }}>
                <strong>Loving the app?</strong> Share your experience to help other golfers discover it!
              </div>
              <button
                onClick={() => setShowTestimonial(true)}
                style={{
                  padding: '7px 14px',
                  background: '#2d6a4f', color: '#fff',
                  border: 'none', borderRadius: 6,
                  fontWeight: 600, fontSize: 13,
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0
                }}
              >
                ⭐ Leave a Review
              </button>
            </div>
          </>
        ) : (
          /* Email Preview */
          <div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>To:</div>
              <div style={{ fontSize: 14, color: '#111827' }}>{recipientName} &lt;{recipientEmail}&gt;</div>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Subject:</div>
              <div style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>{emailContent.subject}</div>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Message:</div>
              <div style={{ 
                fontSize: 13, 
                color: '#374151', 
                background: '#f9fafb', 
                padding: 12, 
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5
              }}>
                {emailContent.body}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowPreview(false)}
                disabled={loadingEmail}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loadingEmail ? 'not-allowed' : 'pointer'
                }}
              >
                ← Back
              </button>
              <button
                onClick={handleSendInvitation}
                disabled={loadingEmail}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: loadingEmail ? '#9ca3af' : '#2d6a4f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loadingEmail ? 'not-allowed' : 'pointer'
                }}
              >
                {loadingEmail ? '⏳ Sending...' : '📤 Send'}
              </button>
            </div>
          </div>
        )}
      </div>
      {showTestimonial && (
        <PlayerTestimonialModal
          token={token}
          playerData={playerData}
          onClose={() => setShowTestimonial(false)}
        />
      )}
    </div>
  );
};
// ============================================================================


// ============================================================================
// PLAYER TESTIMONIAL MODAL - Player experience submission
// ============================================================================
// Single-hole card view for live score entry during a round.
// Big tap targets, +/- buttons, prev/next navigation.
// Offline support: saves to localStorage, syncs when online.
// Used in Player Portal only.
// ============================================================================

// ============================================================================
// GHIN NO-CREDENTIALS MODAL - shared across all scoring flows
// ============================================================================
// Shown when a player taps "Post to GHIN" but hasn't connected GHIN credentials.
// Replaces a dead/disabled button (whose title tooltip is invisible on touch
// devices) with an explicit, tappable explanation. Informational only — it does
// not navigate; it tells the player where to go (their Profile).
const GhinNoCredsModal = ({ onClose }) => (
  <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', textAlign: 'center' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>⛳</div>
      <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>GHIN Not Connected</h3>
      <p style={{ color: '#4b5563', fontSize: 15, lineHeight: 1.5, margin: '0 0 8px' }}>
        Your scores can't be posted to GHIN yet because your GHIN credentials aren't set up in your profile.
      </p>
      <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.5, margin: '0 0 20px' }}>
        Open your <b>Profile</b> and connect your GHIN account to enable posting. Your saved scores stay safe in the meantime.
      </p>
      <button onClick={onClose} style={{ width: '100%', padding: '12px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Got it</button>
    </div>
  </div>
);

// GHIN posting is gated by the 'scoring' feature key (Starter and up), enforced
// by requirePlayerScoring / requirePersonalRounds. NOT by 'ghin_sync', which
// gates auto-sync only (Plus and up) — both posting endpoints call
// golfer_login.json directly rather than through ghinService.login(), so they
// never reach that gate. A Free-tier player may still hold their own GHIN
// credentials, so the credentials check alone would let them tap through to a
// 403 from the middleware. Tier is checked FIRST for that reason: telling such a
// player to "connect GHIN" would be misdirection.
const GhinTierLockedModal = ({ onClose }) => (
  <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', textAlign: 'center' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>🔒</div>
      <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>GHIN Posting Not Available</h3>
      <p style={{ color: '#4b5563', fontSize: 15, lineHeight: 1.5, margin: '0 0 8px' }}>
        Your group's current plan doesn't include Scoring, which is what GHIN posting needs.
      </p>
      <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.5, margin: '0 0 20px' }}>
        Your scores are still saved here, and your handicap and net scores work as normal. Post this round in the GHIN app to keep your index current, or ask your organizer about upgrading.
      </p>
      <button onClick={onClose} style={{ width: '100%', padding: '12px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Got it</button>
    </div>
  </div>
);

// Server returns code GHIN_COURSE_NOT_MAPPED when resolveGhinIds cannot find a
// GHIN course/tee id. The message matters: it tells the player to post in the
// GHIN app and NOT to retry here (a retry after the organizer sets the id would
// double-post the round). That is too much to put in an auto-dismissing toast,
// so it gets a modal. Copy comes from the server so the two never drift.
// Oct 16, 2026: `message` may also be { title, text } — GHIN_ALREADY_POSTED reuses this modal.
const GhinNotMappedModal = ({ message, onClose }) => (
  <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', textAlign: 'center' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>⛳</div>
      <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>{(message && typeof message === 'object' && message.title) || 'Course Not Linked to GHIN'}</h3>
      <p style={{ color: '#4b5563', fontSize: 15, lineHeight: 1.5, margin: '0 0 20px' }}>
        {(message && typeof message === 'object' ? message.text : message) || "This course isn't linked to GHIN yet. Post this round directly in the GHIN app so your handicap stays current — no need to retry here. Your organizer has been notified and only has to set it up once."}
      </p>
      <button onClick={onClose} style={{ width: '100%', padding: '12px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Got it</button>
    </div>
  </div>
);

// Server returns code GHIN_INVALID_CREDENTIALS (400) when golfer_login.json
// rejects the player's stored login -- almost always a GHIN password changed
// on ghin.com and never updated here -- or GHIN_UNAVAILABLE (502) when GHIN
// itself is not answering. Until Sept 10, 2026 both were a bare 502 shown as
// a four-second toast, which is how a changed password read as "no error
// message" (Dennis, Sept 10). The credential case has a fix the player can
// apply right now, so it gets a modal with a button that opens their Profile
// (onOpenProfile is wired by the portal at each scoring render site; when it
// is absent the modal falls back to telling them where to go). Copy comes
// from the server so the two never drift.
//
// The server has ALSO written players.ghin_sync_error at this point, so the
// home-screen "TTO can't sign in to GHIN" banner will show until a login
// succeeds -- the player is told twice, in two places, until it is fixed.
const GhinLoginFailedModal = ({ failure, onClose, onOpenProfile }) => {
  const isCreds = failure?.code === 'GHIN_INVALID_CREDENTIALS';
  const title = isCreds ? 'GHIN Login Failed' : 'GHIN Not Responding';
  const fallback = isCreds
    ? 'GHIN rejected your login. If you changed your GHIN password recently, update it in your Profile and then post this round.'
    : "GHIN isn't responding right now. Your scores are saved here — try again in a few minutes.";
  return (
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>{isCreds ? '🔑' : '⛳'}</div>
        <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>{title}</h3>
        <p style={{ color: '#4b5563', fontSize: 15, lineHeight: 1.5, margin: '0 0 8px' }}>
          {failure?.message || fallback}
        </p>
        {isCreds && (
          <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.5, margin: '0 0 20px' }}>
            Your scores are saved here and nothing was sent to GHIN. Once your GHIN login works again, come back and tap <b>Post to GHIN</b>.
          </p>
        )}
        {!isCreds && <div style={{ height: 12 }} />}
        {isCreds && onOpenProfile ? (
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '12px 0', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600, color: '#6b7280' }}>Later</button>
            <button onClick={() => { onClose(); onOpenProfile(); }} style={{ flex: 1, padding: '12px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Open Profile</button>
          </div>
        ) : (
          <button onClick={onClose} style={{ width: '100%', padding: '12px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Got it</button>
        )}
      </div>
    </div>
  );
};

// GhinNotPostableNote — stands in for the Post to GHIN button on a round whose
// teetimes.postable_to_ghin is false (recap item 5, Sept 15, 2026). The three
// individual score-entry screens read the round's FLAG, never its format, so a
// future non-handicap format needs no change here. Muted text, not a control.
// HoleOutNote — a 4-man Best Ball round (Sept 26, 2026, the blind draw): every player
// holes out, because any player's card may be a blind's score for another team. The
// scorecard endpoint sends hole_out; who the blind is never reaches a player here.
// ── Closest to the Pin (Sept 28, 2026) ───────────────────────────────────────────
// The app records who holds the marker on each par 3; it measures nothing. The latest
// claim on a hole is its holder. CtpBoardSection is the "📍 Closest to the Pin" list
// under every leaderboard (no money — boards never show money); CtpClaimBar is the
// claim button on a CTP hole's scoring screen, "On the green and closer?" — with
// "… and made par?" when the game's Must make par option is on (the honor system: a
// closer player who missed his par simply doesn't claim).
const CtpBoardSection = ({ ctp, dark = false, nines = null }) => {
  // Oct 28, 2026: a named-nines course shows "Black Robin 4", not "#13" (Dennis, Turkey Shoot).
  const holeLbl = (n) => (nines ? holeName(n, nines) : `#${n}`);
  if (!ctp || !Array.isArray(ctp.holes) || ctp.holes.length === 0) return null;
  return (
    <div data-testid="ctp-board" style={{ marginTop: 14, border: dark ? 'none' : '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', background: dark ? 'transparent' : '#fff' }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: dark ? '#fff' : '#1f2937', marginBottom: 6 }}>📍 Closest to the Pin</div>
      {ctp.holes.map(h => (
        <div key={h.hole_number} style={{ display: 'flex', gap: 12, padding: '4px 0', borderTop: dark ? 'none' : '1px solid #f3f4f6', fontSize: 14, color: dark ? '#fff' : '#1f2937' }}>
          <span style={{ minWidth: 40, fontWeight: 700 }}>{holeLbl(h.hole_number)}</span>
          <span style={{ color: h.holder_name ? undefined : (dark ? 'rgba(255,255,255,0.55)' : '#9ca3af') }}>{h.holder_name || '—'}</span>
        </div>
      ))}
    </div>
  );
};
const CtpClaimBar = ({ ctp: ctpIn, hole, token, teetimeId, selfId, options = [] }) => {
  const [ctp, setCtp] = useState(ctpIn || null);
  const [confirmFor, setConfirmFor] = useState(null);   // { id, name }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { setCtp(ctpIn || null); }, [ctpIn]);
  const isCtpHole = !!(ctp && (ctp.holes || []).some(h => h.hole_number === hole));
  // Fresh holder whenever you arrive on a CTP hole — another group may have taken it.
  useEffect(() => {
    if (!isCtpHole || !teetimeId || !token) return undefined;
    let live = true;
    fetch(`${API_URL}/player/scoring/ctp/${teetimeId}?token=${token}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (live && d && d.ctp) setCtp(d.ctp); })
      .catch(() => {});
    setConfirmFor(null); setErr(null);
    return () => { live = false; };
  }, [hole, isCtpHole, teetimeId, token]);
  if (!isCtpHole) return null;
  const h = ctp.holes.find(x => x.hole_number === hole);
  const inGame = (ctp.participant_ids || []);
  // Who can take it: you (or a player whose card you keep) in the game — not whoever already holds it.
  const eligible = options.filter(o => inGame.includes(o.id));
  const who = eligible.filter(o => !(h && h.holder_id === o.id));
  const question = ctp.must_make_par ? 'On the green, closer, and made par?' : 'On the green and closer?';
  const claim = async (o) => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API_URL}/player/scoring/ctp/claim?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teetime_id: teetimeId, hole_number: hole, ...(o.id !== selfId ? { for_player_id: o.id } : {}) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(d.error || 'Could not record it — try again.'); return; }
      if (d.ctp) setCtp(d.ctp);
      setConfirmFor(null);
    } catch (e) {
      setErr('No connection — try again.');
    } finally {
      setBusy(false);
    }
  };
  // Neutral palette (Oct 4, 2026 — hole screen redesign): black, white and greys; green is kept
  // for the hole number and the Next button only.
  const btn = { minHeight: 48, padding: '0 16px', borderRadius: 10, border: '2px solid #111827', background: '#111827', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' };
  return (
    <div data-testid="ctp-claim" style={{ marginTop: 12, padding: '10px 12px', borderRadius: 12, background: '#fff', border: '2px solid #d1d5db', textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
        Closest to the Pin: {h && h.holder_id && h.holder_id === selfId ? <span>you have it</span> : h && h.holder_name ? <span>{h.holder_name} has it</span> : <span>nobody yet</span>}
      </div>
      {eligible.length > 0 && who.length === 0 ? null : who.length === 0 ? (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Only players in the closest-to-the-pin game can claim it.</div>
      ) : confirmFor ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 14, color: '#1f2937', marginBottom: 8 }}>{confirmFor.id === selfId ? 'You are' : `${confirmFor.name} is`} {ctp.must_make_par ? 'on the green, closest, and made par' : 'on the green and closest'}?</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button type="button" disabled={busy} onClick={() => claim(confirmFor)} style={btn}>{busy ? 'Saving…' : 'Yes, record it'}</button>
            <button type="button" disabled={busy} onClick={() => setConfirmFor(null)} style={{ ...btn, background: '#fff', color: '#111827', border: '2px solid #9ca3af' }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 15, color: '#374151', marginBottom: 6 }}>{question}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {who.map(o => (
              <button key={o.id} type="button" onClick={() => setConfirmFor(o)} style={btn}>{o.id === selfId ? 'I have it' : `${o.name} has it`}</button>
            ))}
          </div>
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{err}</div>}
    </div>
  );
};

// ── Stableford on the hole screen (Oct 5, 2026 — Stableford_Plan_v1 slice 3) ─────────────
// One more box in the dark totals bar: the player's Stableford points so far (net or gross,
// as the game is set up). Looked up again each time he moves to another hole (the hole
// just left is saved first). Nothing when the round has no Stableford or he isn't in it.
const StablefordPtsCell = ({ stableford, token, teetimeId, selfId, hole }) => {
  const [pts, setPts] = useState(null);
  const active = !!(stableford && teetimeId && token && selfId);
  useEffect(() => {
    if (!active) { setPts(null); return undefined; }
    let live = true;
    fetch(`${API_URL}/player/scoring/standings?token=${token}&teetime_id=${teetimeId}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!live || !d) return;
        const g = (d.games || []).find(x => x.game_type === 'stableford');
        const me = g && g.results && (g.results.totals || []).find(t => t.player_id === selfId);
        setPts(me && me.points != null ? me.points : null);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [active, token, teetimeId, selfId, hole]);
  if (!active || pts == null) return null;
  return (
    <div data-testid="stableford-pts" style={{ textAlign: 'center', flex: 1 }}>
      <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 600, letterSpacing: 0.5 }}>PTS</div>
      <div style={{ fontWeight: 800, fontSize: 22, color: '#86efac', marginTop: 2 }}>{pts}</div>
    </div>
  );
};

// ── Sixes on the hole screen (Oct 5, 2026 — Sixes_COD_Design_Sep28) ─────────────────────
// SixesCartsCard: before the first score, any player in the foursome says who rides with
// whom and who drives (tap two names in different carts to swap them; tap the steering
// wheel to make that player the driver — only the wheel turns green, Dennis Oct 5). Done
// folds it to one line for the whole foursome, with Change until the first score; then it
// goes away. SixesMatchOver: when the player moves on from the last hole of match 1 or 2
// (playing order), a one-time note — the match result and the next match's sides.
const SixesWheel = ({ on }) => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={on ? '#16a34a' : '#c4c8ce'} strokeWidth={on ? 2.4 : 2} strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="2.2" />
    <path d="M12 14.2V21M10 11.3L3.6 9.4M14 11.3l6.4-1.9" />
  </svg>
);
const sixesSides = (carts, drivers) => {
  const [d1, d2] = drivers;
  const p1 = carts[0].find(id => id !== d1);
  const p2 = carts[1].find(id => id !== d2);
  return [[[d1, p1], [d2, p2]], [[d1, p2], [d2, p1]], [[d1, d2], [p1, p2]]];
};
const sixesSeenKey = (teetimeId, selfId, i) => `tto-sixes-seen-${teetimeId}-${selfId}-${i}`;
// Short names for the foursome: first names; two with the same first name get the last
// initial ("Terry A."), and if that still matches, the full name (Terry Adams / Terry Anderson).
const sixesShortNames = (members) => {
  const parts = (members || []).map(m => { const w = String(m.name || '').trim().split(/\s+/); return { id: m.id, first: w[0] || 'Player', last: w.slice(1).join(' '), full: String(m.name || '').trim() || 'Player' }; });
  const count = (key) => parts.reduce((acc, p) => { const k = key(p); acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const firstN = count(p => p.first);
  const initN = count(p => `${p.first} ${p.last.charAt(0)}`);
  return Object.fromEntries(parts.map(p => [p.id, firstN[p.first] === 1 || !p.last ? p.first : initN[`${p.first} ${p.last.charAt(0)}`] === 1 ? `${p.first} ${p.last.charAt(0)}.` : p.full]));
};

const SixesCartsCard = ({ sixes: flag, token, teetimeId, selfId, anyScore, onOpenProfile }) => {
  const [st, setSt] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);        // { carts: [[id,id],[id,id]], drivers: [id|null, id|null] }
  const [touched, setTouched] = useState(false);
  const [pick, setPick] = useState(null);          // [cart, index] of the first name tapped
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const active = !!(flag && teetimeId && token);
  const load = React.useCallback(() => {
    if (!active) return;
    fetch(`${API_URL}/player/scoring/sixes/${teetimeId}?token=${token}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setSt(d.sixes || null); })
      .catch(() => {});
  }, [active, teetimeId, token]);
  useEffect(() => { load(); }, [load]);
  // Another phone in the foursome may set the carts — look again every 20 seconds until the
  // first score; and while the foursome is on hold, until the missing handicap is added.
  useEffect(() => {
    if (!active || (st && st.locked && st.status !== 'no_handicap') || (anyScore && st && st.status !== 'no_handicap')) return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [active, st, load, anyScore]);
  // Keep the draft in step with the server until this player starts changing it.
  useEffect(() => {
    if (!st || !['playing', 'no_handicap'].includes(st.status) || !st.carts || touched) return;
    setDraft({ carts: st.carts.carts.map(c => [...c]), drivers: st.carts.source === 'set' ? [...st.carts.drivers] : [null, null] });
  }, [st, touched]);
  if (!active || !st) return null;
  if (st.status === 'missing' || st.status === 'size') {
    const miss = st.missing_names || [];
    return (
      <div data-testid="sixes-carts-note" style={{ textAlign: 'left', margin: '10px 0 0', padding: '8px 12px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a', fontSize: 13, color: '#92400e' }}>
        Sixes: your group isn{'’'}t playing — {st.status === 'missing' ? `${miss.join(', ')} ${miss.length === 1 ? 'isn’t' : 'aren’t'} in the game.` : 'it needs a foursome.'}
      </div>
    );
  }
  // On hold (Oct 5): someone in the foursome has no handicap. Shown to the whole foursome,
  // before and during the round, until he adds one; he gets a button to his Profile.
  const holdIds = st.status === 'no_handicap' ? (st.no_handicap_ids || []) : [];
  const meMissing = holdIds.includes(selfId);
  const others = (st.no_handicap_names || []).filter((n, i) => holdIds[i] !== selfId);
  const hold = holdIds.length ? (
    <div data-testid="sixes-hold" style={{ textAlign: 'left', margin: '10px 0 0', padding: '10px 12px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a', fontSize: 13, color: '#92400e', lineHeight: 1.4 }}>
      <strong>Sixes is on hold</strong> — {meMissing
        ? `you have no handicap${others.length ? ` (nor ${others.join(', ')})` : ''}. Add your handicap index in your Profile; the matches pick up from the scores already entered.`
        : `${others.join(', ')} ${others.length === 1 ? 'has' : 'have'} no handicap yet. Keep scoring; the matches start counting once ${others.length === 1 ? 'he adds one' : 'they add them'} in Profile.`}
      {meMissing && onOpenProfile && (
        <button type="button" onClick={onOpenProfile}
          style={{ display: 'block', marginTop: 8, padding: '8px 14px', borderRadius: 8, border: '1px solid #d97706', background: '#fff', color: '#92400e', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Open my Profile</button>
      )}
    </div>
  ) : null;
  if (!['playing', 'no_handicap'].includes(st.status) || st.locked || anyScore || !draft) return hold;
  return <div>{hold}{renderCard()}</div>;
  function renderCard() {
  const shortOf = sixesShortNames(st.members);
  const nameOf = Object.fromEntries((st.members || []).map(m => [m.id, (m.id === selfId ? 'You' : shortOf[m.id])]));
  const fullName = Object.fromEntries((st.members || []).map(m => [m.id, m.name]));
  const folded = st.carts.source === 'set' && !editing;
  const filled = (dr, carts) => [dr[0] || carts[0][0], dr[1] || carts[1][0]];
  const box = { textAlign: 'left', margin: '12px 0 0', padding: 12, borderRadius: 12, background: '#f3f4f6' };
  const line = { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, color: '#4b5563', padding: '2px 0' };
  const summary = (carts, drivers) => {
    const sides = sixesSides(carts, drivers);
    const labels = ['1–6', '7–12', '13–18'];
    const me1st = (side) => [...side].sort((x, y) => (y === selfId) - (x === selfId));
    return sides.map(([a0, b0], i) => {
      const [a, b] = b0.includes(selfId) ? [me1st(b0), a0] : [me1st(a0), b0];
      return (
      <div key={i} style={line}><span style={{ color: '#6b7280', minWidth: 44 }}>{labels[i]}</span><span style={{ textAlign: 'right' }}>{a.map(id => nameOf[id]).join(' & ')} vs {b.map(id => nameOf[id]).join(' & ')}</span></div>
      );
    });
  };
  if (folded) {
    return (
      <div data-testid="sixes-carts-card" style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: '#1f2937' }}>Sixes <span style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>✓ carts set{st.carts.set_by_name ? ` by ${st.carts.set_by_name.split(' ')[0]}` : ''}</span></span>
          <button type="button" onClick={() => { setEditing(true); setTouched(false); setPick(null); setErr(null); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #9ca3af', background: '#fff', fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>Change</button>
        </div>
        {summary(st.carts.carts, st.carts.drivers)}
      </div>
    );
  }
  const bothSet = !!(draft.drivers[0] && draft.drivers[1]);
  const tapName = (ci, j) => {
    setTouched(true); setErr(null);
    if (!pick) { setPick([ci, j]); return; }
    if (pick[0] !== ci) {
      const carts = draft.carts.map(c => [...c]);
      const a = carts[pick[0]][pick[1]];
      const b = carts[ci][j];
      carts[pick[0]][pick[1]] = b;
      carts[ci][j] = a;
      // A driver who changes carts is no longer that cart's driver.
      const drivers = draft.drivers.map((d, k) => (carts[k].includes(d) ? d : null));
      setDraft({ carts, drivers });
    }
    setPick(null);
  };
  const tapWheel = (ci, id) => { setTouched(true); setErr(null); setPick(null); setDraft({ ...draft, drivers: draft.drivers.map((d, k) => (k === ci ? id : d)) }); };
  const done = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API_URL}/player/scoring/sixes/carts?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teetime_id: teetimeId, carts: draft.carts, drivers: filled(draft.drivers, draft.carts) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(d.error || 'Could not save the carts — try again.'); if (res.status === 409) load(); return; }
      setSt(d.sixes || null); setEditing(false); setTouched(false); setPick(null);
    } catch (e) {
      setErr('No connection — try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="sixes-carts-card" style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#1f2937' }}>Sixes · carts</span>
        {st.bet_amount > 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>${st.bet_amount % 1 === 0 ? st.bet_amount.toFixed(0) : st.bet_amount.toFixed(2)} a match</span>}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, margin: '2px 0 8px', color: bothSet ? '#166534' : '#b45309' }}>{bothSet ? '✓ Both drivers set' : 'Pick a driver in each cart'}</div>
      {draft.carts.map((cart, ci) => (
        <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: '#6b7280', width: 44, flexShrink: 0 }}>Cart {ci + 1}</span>
          {cart.map((id, j) => {
            const picked = pick && pick[0] === ci && pick[1] === j;
            return (
              <div key={id} onClick={() => tapName(ci, j)} role="button"
                style={{ flex: 1, minWidth: 0, minHeight: 46, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, padding: '6px 8px', borderRadius: 10, background: '#fff', border: picked ? '2px solid #2563eb' : '1px solid #d1d5db', cursor: 'pointer' }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf[id]}</span>
                <button type="button" aria-label={`${fullName[id]} drives`} onClick={(e) => { e.stopPropagation(); tapWheel(ci, id); }}
                  style={{ border: 'none', background: 'transparent', padding: 4, margin: -4, cursor: 'pointer', lineHeight: 0 }}>
                  <SixesWheel on={draft.drivers[ci] === id} />
                </button>
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>{pick ? 'Now tap a player in the other cart to swap them.' : 'Tap two names in different carts to swap them. Tap the wheel for the driver.'}</div>
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 6 }}>{summary(draft.carts, filled(draft.drivers, draft.carts))}</div>
      {err && <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 6 }}>{err}</div>}
      <button type="button" disabled={busy} onClick={done}
        style={{ width: '100%', height: 48, marginTop: 8, borderRadius: 12, border: bothSet ? 'none' : '2px solid #9ca3af', background: bothSet ? '#166534' : '#fff', color: bothSet ? '#fff' : '#374151', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
        {busy ? 'Saving…' : bothSet ? 'Done' : 'Done (first in each cart drives)'}
      </button>
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>Anyone in your group can change this until the first score.</div>
    </div>
  );
  }
};

const SixesMatchOver = ({ sixes: flag, hole, token, teetimeId, selfId }) => {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(null);           // index of the match just finished (0 or 1)
  const prev = useRef(hole);
  const seen = useRef(new Set());
  const active = !!(flag && teetimeId && token && selfId);
  const load = React.useCallback(() => (
    fetch(`${API_URL}/player/scoring/sixes/${teetimeId}?token=${token}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { const s = d ? d.sixes || null : null; setSt(s); return s; })
      .catch(() => null)
  ), [teetimeId, token]);
  useEffect(() => { if (active) load(); }, [active, load]);
  useEffect(() => {
    const from = prev.current;
    prev.current = hole;
    if (!active || !st || st.status !== 'playing' || from === hole) return;
    const ms = st.matches || [];
    for (let i = 0; i < 2; i++) {
      const m = ms[i], next = ms[i + 1];
      if (!m || !next || m.holes[5] !== from || next.holes[0] !== hole) continue;
      const key = sixesSeenKey(teetimeId, selfId, i);
      let already = seen.current.has(key);
      try { if (window.localStorage.getItem(key)) already = true; } catch (e) { /* storage blocked — memory only */ }
      if (already) return;
      seen.current.add(key);
      try { window.localStorage.setItem(key, '1'); } catch (e) { /* ignore */ }
      setOpen(i);
      load();   // the scores just saved
    }
  }, [hole, active, st, teetimeId, selfId, load]);
  // The others' scores may land a moment later — look again while the match isn't final.
  useEffect(() => {
    if (open == null || !st || !st.matches || !st.matches[open] || st.matches[open].finished) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [open, st, load]);
  if (open == null || !st || st.status !== 'playing') return null;
  const m = st.matches[open];
  const next = st.matches[open + 1];
  if (!m || !next) return null;
  const firsts = sixesShortNames(st.members);
  const names = (side) => [...side.player_ids].sort((x, y) => (y === selfId) - (x === selfId)).map(id => (id === selfId ? 'You' : firsts[id] || 'Player')).join(' & ');
  const onA = m.side_a.player_ids.includes(selfId);
  const us = onA ? m.side_a : m.side_b, them = onA ? m.side_b : m.side_a;
  const fmt = (d) => (d === 0 ? 'all square' : `${Math.abs(d)} up`);
  const d = onA ? m.diff : -m.diff;
  const myNet = onA ? m.net_a : -m.net_a;
  const money = (v) => `${v > 0 ? '+' : '−'}$${Math.abs(v).toFixed(2)}`;
  const nOnA = next.side_a.player_ids.includes(selfId);
  const close = () => setOpen(null);
  return (
    <div data-testid="sixes-match-over" onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 380, background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Sixes</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: '#1f2937', margin: '2px 0 10px' }}>Match {m.number} is over</div>
        {m.finished ? (
          <div style={{ fontSize: 17, color: '#1f2937', lineHeight: 1.4 }}>
            {d === 0
              ? <span>All square — a push, no money.</span>
              : <span><strong>{names(d > 0 ? us : them)}</strong> won, {fmt(d)}</span>}
            {st.bet_amount > 0 && myNet !== 0 && <span style={{ fontWeight: 800, color: myNet > 0 ? '#166534' : '#dc2626' }}> · {money(myNet)}</span>}
            {m.bets.slice(1).map((b, k) => {
              const bd = onA ? b.diff : -b.diff;
              return <div key={k} style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>{b.label}: {bd === 0 ? 'push' : bd > 0 ? 'won' : 'lost'}</div>;
            })}
          </div>
        ) : (
          <div style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.4 }}>{names(us)} vs {names(them)}: final once all four scores are in.</div>
        )}
        <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 12, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>Next — holes {next.holes_label}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginTop: 2 }}>{names(nOnA ? next.side_a : next.side_b)} vs {names(nOnA ? next.side_b : next.side_a)}</div>
        </div>
        <button type="button" onClick={close} style={{ width: '100%', height: 54, marginTop: 16, borderRadius: 14, border: 'none', background: '#166534', color: '#fff', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  );
};

// ── Nassau press prompt (Oct 4, 2026) ─────────────────────────────────────────────────
// Presses set to "ask the player": when a player on this card goes 2 down on a nine, one
// prompt sits just above the Next bar — "2 down to Bob on the front. Press — $5?" — and
// goes away once answered. Nothing else of the Nassau shows on the hole screen; the
// matches live in the Results tab like every other game. Checked on arriving at a hole
// (the previous hole's scores are saved by then).
// Sixes (Oct 5, 2026 — slice 3) uses the same prompt: game="sixes", its offers name the
// match ("in match 2 (7–12)") instead of the nine.
const NassauPressPrompt = ({ nassau, hole, token, teetimeId, selfId, forIds = [], game = 'nassau' }) => {
  const base = game === 'sixes' ? `${API_URL}/player/scoring/sixes/press` : `${API_URL}/player/scoring/nassau`;
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ask = !!(nassau && nassau.press === 'ask' && nassau.status !== 'completed');
  const forKey = forIds.join(',');
  useEffect(() => {
    if (!ask || !teetimeId || !token) return undefined;
    let live = true;
    setErr(null);
    fetch(`${base}/${teetimeId}?token=${token}${forKey ? `&for=${forKey}` : ''}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (live && d) setOffers(d.offers || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [ask, hole, teetimeId, token, forKey, base]);
  if (!ask || offers.length === 0) return null;
  const o = offers[0];
  const answer = async (action) => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${game === 'sixes' ? base : `${base}/press`}?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teetime_id: teetimeId, match_key: o.match_key, nine: o.nine, action, for_ids: forIds }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 409) { setErr(d.error || 'Could not save — try again.'); return; }
      setOffers(d.offers || offers.filter(x => !(x.match_key === o.match_key && x.nine === o.nine)));
    } catch (e) {
      setErr('No connection — try again.');
    } finally {
      setBusy(false);
    }
  };
  const amount = o.bet_amount > 0 ? ` — $${o.bet_amount % 1 === 0 ? o.bet_amount : o.bet_amount.toFixed(2)}` : '';
  const who = o.player_id === selfId ? (o.side_names.length > 1 ? `${o.side_names.join(' & ')} are` : 'You\u2019re') : `${o.side_names.join(' & ')} ${o.side_names.length > 1 ? 'are' : 'is'}`;
  const what = game === 'sixes'
    ? (o.bet_index > 0 ? `on press ${o.bet_index} in match ${o.match_number}` : `in match ${o.match_number} (${o.holes_label})`)
    : (o.bet_index > 0 ? `on press ${o.bet_index} (${o.nine})` : `on the ${o.nine}`);
  return (
    <div data-testid={`${game}-press-prompt`} style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 'calc(86px + env(safe-area-inset-bottom, 0px))', width: '100%', maxWidth: 500, boxSizing: 'border-box', padding: '0 14px', zIndex: 901 }}>
      <div style={{ background: '#fff', border: '2px solid #111827', borderRadius: 14, padding: '12px 14px', boxShadow: '0 -2px 16px rgba(0,0,0,0.15)' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
          {who} {o.deficit} down to {o.opponents.join(' & ')} {what}. Press?
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" disabled={busy} onClick={() => answer('press')}
            style={{ flex: 1, minHeight: 52, borderRadius: 12, border: '2px solid #111827', background: '#111827', color: '#fff', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>{busy ? 'Saving…' : `Press${amount}`}</button>
          <button type="button" disabled={busy} onClick={() => answer('decline')}
            style={{ width: 120, minHeight: 52, borderRadius: 12, border: '2px solid #9ca3af', background: '#fff', color: '#111827', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>Not now</button>
        </div>
        {err && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{err}</div>}
      </div>
    </div>
  );
};

// ── Dots (Oct 4, 2026) ───────────────────────────────────────────────────────────────
// The Sandy / Poley buttons on the hole screen: one row per player on the card who is in
// the Dots game ("DOTS" on a solo card, the player's name when keeping the card for
// others). A tap marks it, a second tap takes it off. The honor system — no score or
// putts check (Dennis). Birdies count by themselves from the score. Neutral palette:
// a marked dot is black with a ✓, an unmarked one white with a ○.
const DotsTapRow = ({ dots: dotsIn, hole, token, teetimeId, selfId, options = [] }) => {
  const [dots, setDots] = useState(dotsIn || null);
  const [busyKey, setBusyKey] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { setDots(dotsIn || null); }, [dotsIn]);
  useEffect(() => { setErr(null); }, [hole]);
  if (!dots || !(dots.tap_kinds || []).length) return null;
  const rows = options.filter(o => (dots.participant_ids || []).includes(o.id));
  if (!rows.length) return null;
  const final = dots.status === 'completed';
  const isOn = (pid, kind) => !!(dots.marks && dots.marks[pid] && dots.marks[pid][hole] && dots.marks[pid][hole][kind]);
  const tap = async (o, kind) => {
    if (final) return;
    const on = !isOn(o.id, kind);
    const key = `${o.id}|${kind}`;
    setBusyKey(key); setErr(null);
    // Optimistic: flip it now, put it back if the save fails.
    const flip = (val) => setDots(prev => {
      const marks = { ...(prev.marks || {}) };
      const byHole = { ...(marks[o.id] || {}) };
      byHole[hole] = { ...(byHole[hole] || {}), [kind]: val };
      marks[o.id] = byHole;
      return { ...prev, marks };
    });
    flip(on);
    try {
      const res = await fetch(`${API_URL}/player/scoring/dots?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teetime_id: teetimeId, hole_number: hole, kind, on, ...(o.id !== selfId ? { for_player_id: o.id } : {}) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { flip(!on); setErr(d.error || 'Could not save the dot — try again.'); return; }
      if (d.dots) setDots(d.dots);
    } catch (e) {
      flip(!on); setErr('No connection — try again.');
    } finally {
      setBusyKey(null);
    }
  };
  const label = { sandy: 'Sandy', poley: 'Poley' };
  const solo = rows.length === 1 && rows[0].id === selfId;
  return (
    <div data-testid="dots-row" style={{ marginBottom: 16 }}>
      {rows.map(o => (
        <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: 0.8, color: '#374151', width: solo ? 48 : 84, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {solo ? 'DOTS' : (o.id === selfId ? 'You' : o.name)}
          </span>
          {dots.tap_kinds.map(k => {
            const on = isOn(o.id, k);
            return (
              <button key={k} type="button" aria-pressed={on} disabled={final || busyKey === `${o.id}|${k}`} onClick={() => tap(o, k)}
                data-testid={`dots-${k}-${o.id}`}
                style={{ flex: 1, minHeight: 48, borderRadius: 12, border: on ? '2px solid #111827' : '2px solid #9ca3af', background: on ? '#111827' : '#fff', color: on ? '#fff' : '#111827', fontSize: 17, fontWeight: 700, cursor: final ? 'default' : 'pointer', opacity: final ? 0.6 : 1 }}>
                {on ? '✓' : '○'} {label[k]}
              </button>
            );
          })}
        </div>
      ))}
      {final && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, textAlign: 'center' }}>The round is final — ask the organizer to change a dot.</div>}
      {err && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6, textAlign: 'center' }}>{err}</div>}
    </div>
  );
};

// Shamble (Sept 28, 2026): the same note carries the game's name and the organizer's
// drive rule (any shamble — a 2-person one has the rule but no hole-out line).
const HoleOutNote = ({ info }) => (info && (info.required || info.rules_note) ? (
  <div data-testid="hole-out-note" style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 10, padding: '8px 12px', marginBottom: 10, fontSize: 14, fontWeight: 700, textAlign: 'center', lineHeight: 1.35 }}>
    {info.required && (info.blind_in_play ? 'A blind is in play — hole out on every hole' : `${info.label || 'Best Ball'}: all players must hole out`)}
    {info.rules_note && <div data-testid="shamble-rules-note" style={{ fontWeight: 500, fontSize: 13, marginTop: info.required ? 4 : 0 }}>{info.required ? '' : `${info.label || 'Shamble'}: `}{info.rules_note}</div>}
  </div>
) : null);

// GhinConnectForPosting (Sept 27, 2026): posting to GHIN is a Scoring feature
// (Starter and up) but storing a GHIN login used to live only behind the Plus
// auto-sync. On a plan with scoring but not auto-sync, the Profile shows this
// instead of the locked sync button: connect GHIN for posting only
// (POST /player/ghin/connect — no auto-sync, handicap untouched), or disconnect.
const GhinConnectForPosting = ({ token, playerData, setPlayerData, addToast, ghinNumber }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const connected = !!playerData?.has_ghin_credentials;

  const connect = async () => {
    if (!email || !password) { setError('Enter your GHIN email (or GHIN number) and password.'); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API_URL}/player/ghin/connect?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ghin_email: email, ghin_password: password })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Could not connect to GHIN.'); return; }
      setPlayerData(prev => ({ ...prev, has_ghin_credentials: true, ghin_sync_error: null }));
      setOpen(false); setEmail(''); setPassword('');
      addToast('GHIN connected — you can now post scores from your card', 'success');
    } catch (e) {
      setError('Could not connect to GHIN right now. Please try again.');
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/player/ghin/credentials?token=${token}`, { method: 'DELETE' });
      if (res.ok) {
        setPlayerData(prev => ({ ...prev, has_ghin_credentials: false, ghin_auto_sync: false }));
        addToast('GHIN disconnected', 'success');
      } else addToast('Could not disconnect GHIN', 'error');
    } catch (e) { addToast('Could not disconnect GHIN', 'error'); }
    finally { setBusy(false); }
  };

  const btn = { marginTop: 8, padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 };
  const note = <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Automatic handicap updates from GHIN are included from the Plus plan.</div>;

  if (connected) {
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>✓ GHIN connected for score posting</span>
          <button type="button" onClick={disconnect} disabled={busy}
            style={{ background: 'none', border: 'none', color: '#6b7280', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0 }}>
            Disconnect
          </button>
        </div>
        {note}
      </div>
    );
  }
  return (
    <div>
      {!open ? (
        <button type="button"
          onClick={() => { if (!ghinNumber) { addToast('Please enter your GHIN number first', 'info'); return; } setOpen(true); }}
          style={{ ...btn, background: ghinNumber ? '#2d6a4f' : '#6b7280', color: '#fff', opacity: ghinNumber ? 1 : 0.8 }}>
          🔗 Connect GHIN to post scores
        </button>
      ) : (
        <div style={{ marginTop: 8, padding: 12, border: '1px solid #d1d5db', borderRadius: 8, background: '#f9fafb', display: 'grid', gap: 8, maxWidth: 360 }}>
          <div style={{ fontSize: 13, color: '#374151' }}>Sign in with your GHIN login so you can post your scores from your TTO card.</div>
          <input id="ghin-connect-email" type="text" autoComplete="username" placeholder="GHIN email or GHIN number" value={email}
            onChange={e => setEmail(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
          <input id="ghin-connect-password" type="password" autoComplete="current-password" placeholder="GHIN password" value={password}
            onChange={e => setPassword(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
          {error && <div style={{ fontSize: 12, color: '#b91c1c' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={connect} disabled={busy}
              style={{ ...btn, marginTop: 0, background: '#2d6a4f', color: '#fff', opacity: busy ? 0.7 : 1 }}>
              {busy ? 'Connecting…' : 'Connect'}
            </button>
            <button type="button" onClick={() => { setOpen(false); setError(''); }} disabled={busy}
              style={{ ...btn, marginTop: 0, background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>
              Cancel
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Your password is stored encrypted and used only to post the scores you choose.</div>
        </div>
      )}
      {note}
    </div>
  );
};

const GhinNotPostableNote = () => (
  <div style={{ flex: 1, padding: '12px 8px', background: '#f3f4f6', color: '#6b7280', borderRadius: 10, fontSize: 13, fontWeight: 600, textAlign: 'center', lineHeight: 1.3 }}>
    This round's format doesn't post to GHIN
  </div>
);

// ============================================================================
// TOTAL SCORE ENTRY - Quick post-round total only
// ============================================================================
const TotalScoreEntry = ({ token, playerData, onClose, onOpenProfile }) => {
  const { addToast } = useToast();
  const [teetimes, setTeetimes] = useState([]);
  const [selectedTeetimeId, setSelectedTeetimeId] = useState(null);
  const [selectedTeetime, setSelectedTeetime] = useState(null);
  const [front9, setFront9] = useState('');
  const [back9, setBack9] = useState('');
  const [totalPutts, setTotalPutts] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [postingGhin, setPostingGhin] = useState(false);
  const [ghinPosted, setGhinPosted] = useState(false);
  const [showGhinConfirm, setShowGhinConfirm] = useState(false);
  const [showGhinNoCreds, setShowGhinNoCreds] = useState(false);
  const [showGhinTierLocked, setShowGhinTierLocked] = useState(false);
  const [ghinNotMappedMsg, setGhinNotMappedMsg] = useState(null);
  const [ghinLoginFailed, setGhinLoginFailed] = useState(null); // { code, message } from GHIN_INVALID_CREDENTIALS / GHIN_UNAVAILABLE (Sept 10, 2026)
  // GHIN tee info — fetched when opening the confirm modal so the user can verify
  // course + tee before posting. Single source of truth: the tee shown here is the
  // tee we send to the backend (course_tee_id).
  const [ghinTeeInfo, setGhinTeeInfo] = useState(null); // { courseTeeId, teeName, teeColor, courseRating, slopeRating, courseName }
  const [ghinTeeLoading, setGhinTeeLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_URL}/player/scoring/teetimes?token=${token}&mode=totalonly`);
        if (resp.ok) setTeetimes(await resp.json());
      } catch (e) { console.error('Failed to load teetimes:', e); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const selectTeetime = (tt) => {
    setSelectedTeetimeId(tt.id);
    setSelectedTeetime(tt);
    setFront9(''); setBack9(''); setTotalPutts('');
    setSaved(false); setGhinPosted(false);
    setGhinTeeInfo(null);
  };

  const f = parseInt(front9) || 0;
  const b = parseInt(back9) || 0;
  const total = f + b;
  const canSave = f >= 20 && f <= 100 && b >= 20 && b <= 100 && total >= 40 && total <= 200;

  const saveTotal = async () => {
    if (!canSave) { addToast('Enter valid front and back 9 scores', 'error'); return; }
    try {
      setSaving(true);
      const resp = await fetch(`${API_URL}/player/scoring/total-score?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teetime_id: selectedTeetimeId, front9: f, back9: b, total_putts: parseInt(totalPutts) || null })
      });
      if (resp.ok) {
        setSaved(true);
        addToast('Score saved!', 'success');
      } else {
        const err = await resp.json();
        addToast(err.error || 'Save failed', 'error');
      }
    } catch (e) { addToast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const postToGhin = async () => {
    if (!selectedTeetime) return;
    setShowGhinConfirm(false);
    try {
      setPostingGhin(true);
      const resp = await fetch(`${API_URL}/player/scoring/post-to-ghin?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teetime_id: selectedTeetimeId,
          course_tee_id: ghinTeeInfo?.courseTeeId || null,
          played_at: selectedTeetime.date?.split('T')[0]
        })
      });
      if (resp.ok) { const d = await resp.json(); addToast(`✅ Score posted to GHIN successfully! Differential: ${d.differential}`, 'success'); setGhinPosted(true); }
      // GHIN_COURSE_NOT_MAPPED gets a modal rather than a toast: the server copy
      // tells the player to post in the GHIN app and explicitly NOT to retry
      // (retrying after the organizer sets the id would double-post the round),
      // which is too much for an auto-dismissing toast to carry.
      else {
        const err = await resp.json();
        if (err.code === 'GHIN_COURSE_NOT_MAPPED') setGhinNotMappedMsg(err.error || true);
        // Already in GHIN (Oct 16, 2026): same modal, its own title — a toast is too easy to miss.
        else if (err.code === 'GHIN_ALREADY_POSTED') setGhinNotMappedMsg({ title: 'Already Posted in GHIN', text: err.error });
        // Login failures get a modal too (Sept 10, 2026): a changed GHIN
        // password has a fix the player can apply right now, and a toast was
        // easy to miss entirely. See GhinLoginFailedModal.
        else if (err.code === 'GHIN_INVALID_CREDENTIALS' || err.code === 'GHIN_UNAVAILABLE') setGhinLoginFailed({ code: err.code, message: err.error });
        else addToast(err.error || 'GHIN post failed', 'error');
      }
    } catch(e) { addToast('GHIN post failed', 'error'); }
    finally { setPostingGhin(false); }
  };

  // Fetch course + tee info from the scorecard endpoint so the confirm modal
  // shows exactly which tee will be posted. Server uses the same resolution
  // logic, so what we display is what gets sent.
  const openGhinConfirm = async () => {
    setGhinTeeInfo(null);
    setGhinTeeLoading(true);
    setShowGhinConfirm(true);
    try {
      const resp = await fetch(`${API_URL}/player/scoring/scorecard/${selectedTeetimeId}?token=${token}`);
      if (resp.ok) {
        const data = await resp.json();
        const defTee = data.default_tee;
        if (defTee) {
          setGhinTeeInfo({
            courseTeeId: defTee.id,
            teeName: defTee.tee_name,
            teeColor: defTee.tee_color,
            courseRating: defTee.course_rating,
            slopeRating: defTee.slope_rating,
            courseName: data.tee_time?.course_name || selectedTeetime?.course_name || ''
          });
        }
      }
    } catch (e) { console.error('Failed to load tee info for GHIN confirm:', e); }
    finally { setGhinTeeLoading(false); }
  };

  const formatDate = (ds) => { if (!ds) return ''; const d = new Date(ds.split('T')[0] + 'T12:00:00'); if (isNaN(d.getTime())) return ds; return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>Loading...</div>;

  // Tee time selection
  if (!selectedTeetimeId) {
    return (
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>⛳ Total Score Entry</h2>
          <button onClick={onClose} style={{ padding: '6px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Exit</button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 15, color: '#6b7280' }}>Select your round</p>
        {teetimes.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⛳</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>No tee times available</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {teetimes.map(tt => (
              <button key={tt.id} onClick={() => selectTeetime(tt)}
                style={{ padding: 16, background: '#fff', border: '2px solid #e5e7eb', borderRadius: 12, cursor: 'pointer', textAlign: 'left' }}
                onMouseOver={e => e.currentTarget.style.borderColor = '#166534'}
                onMouseOut={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>{formatDate(tt.date)}</div>
                <div style={{ fontSize: 14, color: '#6b7280', marginTop: 2 }}>{tt.course_name} • {tt.time || ''}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Score entry
  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>⛳ Total Score</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setSelectedTeetimeId(null); setSelectedTeetime(null); }}
            style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>← Rounds</button>
          <button onClick={onClose}
            style={{ padding: '6px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Exit</button>
        </div>
      </div>

      {/* Round info */}
      <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 16px', marginBottom: 16, border: '1px solid #e5e7eb' }}>
        <span style={{ fontWeight: 700 }}>{formatDate(selectedTeetime?.date)}</span>
        <span style={{ color: '#6b7280' }}> • {selectedTeetime?.course_name} • {selectedTeetime?.time}</span>
      </div>

      {/* Score inputs */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 20 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>Front 9</div>
            <input type="number" inputMode="numeric" value={front9} onChange={e => { setFront9(e.target.value); setSaved(false); setGhinPosted(false); }}
              placeholder="—" autoFocus
              style={{ width: 90, fontSize: 36, fontWeight: 800, textAlign: 'center', padding: '10px 4px', border: '3px solid #d1d5db', borderRadius: 12, outline: 'none' }}
              onFocus={e => e.target.style.borderColor = '#166534'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>Back 9</div>
            <input type="number" inputMode="numeric" value={back9} onChange={e => { setBack9(e.target.value); setSaved(false); setGhinPosted(false); }}
              placeholder="—"
              style={{ width: 90, fontSize: 36, fontWeight: 800, textAlign: 'center', padding: '10px 4px', border: '3px solid #d1d5db', borderRadius: 12, outline: 'none' }}
              onFocus={e => e.target.style.borderColor = '#166534'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>Putts</div>
            <input type="number" inputMode="numeric" value={totalPutts} onChange={e => { setTotalPutts(e.target.value); setSaved(false); }}
              placeholder="—"
              style={{ width: 72, fontSize: 28, fontWeight: 700, textAlign: 'center', padding: '14px 4px', border: '2px solid #d1d5db', borderRadius: 10, outline: 'none' }}
              onFocus={e => e.target.style.borderColor = '#166534'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
          </div>
        </div>

        {/* Running total */}
        {(f > 0 || b > 0) && (
          <div style={{ textAlign: 'center', padding: '8px 0', borderTop: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 14, color: '#6b7280' }}>Total: </span>
            <span style={{ fontSize: 28, fontWeight: 800, color: canSave ? '#166534' : '#9ca3af' }}>{total}</span>
          </div>
        )}
      </div>

      {/* Save & GHIN buttons — matching LiveScoring pattern */}
      <div style={{ display: 'flex', gap: 10 }}>
        {(() => {
          const hasCreds = !!playerData?.has_ghin_credentials;
          // Tier gate BEFORE the credentials check. GHIN *posting* is gated by the
          // 'scoring' feature key (Starter and up) at requirePlayerScoring /
          // requirePersonalRounds — NOT by 'ghin_sync', which gates auto-sync only
          // (Plus and up). Both posting endpoints call golfer_login.json directly
          // rather than through ghinService.login(), so they never reach that gate.
          // Gating this button on ghin_sync would lock out paying Starter orgs that
          // can post today. Uses !== true so an undefined flag during initial load
          // locks rather than flashes unlocked — same convention as the Profile gate.
          const tierOk = playerData?.scoring_enabled === true;
          let bg = '#d1d5db', col = '#9ca3af', cur = 'default', txt = 'Post to GHIN';
          if (ghinPosted) { bg = '#166534'; col = '#fff'; txt = '✓ GHIN Posted'; }
          else if (postingGhin) { bg = '#9ca3af'; col = '#fff'; txt = 'Posting...'; }
          else if (saved) { bg = '#dc2626'; col = '#fff'; cur = 'pointer'; }
          // When credentials are missing, keep the button tappable so the tap can
          // explain why posting isn't available (a disabled button + title tooltip
          // is invisible on touch devices, where almost all posting happens).
          // The round's postable_to_ghin flag decides before anything else (Sept 15, 2026).
          if (selectedTeetime?.postable_to_ghin === false) return <GhinNotPostableNote />;
          const disabled = !saved || ghinPosted || postingGhin;
          const ghinTitle = !saved ? 'Save scores first' : ghinPosted ? 'Already posted to GHIN' : !tierOk ? "Your current plan doesn't include Scoring. Ask your organizer about upgrading." : !hasCreds ? 'GHIN credentials not set up — visit your Profile to connect GHIN' : '';
          return <button onClick={() => !tierOk ? setShowGhinTierLocked(true) : hasCreds ? openGhinConfirm() : setShowGhinNoCreds(true)} disabled={disabled} title={ghinTitle} style={{ flex: 1, padding: '14px 0', background: bg, color: col, border: 'none', borderRadius: 10, cursor: cur, fontSize: 16, fontWeight: 700 }}>{txt}</button>;
        })()}
        <button onClick={saveTotal} disabled={saving || !canSave}
          style={{ flex: 1, padding: '14px 0', background: saving ? '#9ca3af' : saved ? '#166534' : canSave ? '#166534' : '#d1d5db', color: saving || !canSave ? '#9ca3af' : '#fff', border: 'none', borderRadius: 10, cursor: saving ? 'default' : canSave ? 'pointer' : 'default', fontSize: 16, fontWeight: 700 }}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Score'}
        </button>
      </div>

      {/* GHIN Confirmation Modal */}
      {showGhinConfirm && (() => {
        const teeColor = ghinTeeInfo?.teeColor || '#000';
        const isPaleTee = teeColor && (teeColor.toLowerCase() === '#f3f4f6' || teeColor.toLowerCase() === '#ffffff' || teeColor.toLowerCase() === 'white');
        const playedAt = selectedTeetime?.date ? new Date(selectedTeetime.date.split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';
        const courseName = ghinTeeInfo?.courseName || selectedTeetime?.course_name || '';
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>⛳</div>
              <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700 }}>Post Score to GHIN?</h3>
              {/* Round details — verify this is correct before posting */}
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14, textAlign: 'left' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>{courseName}</div>
                {ghinTeeLoading ? (
                  <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 6, fontStyle: 'italic' }}>Loading tee info…</div>
                ) : ghinTeeInfo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 3, background: teeColor, border: isPaleTee ? '1px solid #d1d5db' : 'none', flexShrink: 0 }} />
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>{ghinTeeInfo.teeName}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{ghinTeeInfo.courseRating} / {ghinTeeInfo.slopeRating}</div>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 6 }}>⚠ Could not load tee info — server will resolve from your preferred tee</div>
                )}
                <div style={{ fontSize: 13, color: '#6b7280' }}>{playedAt}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Score: <b style={{ color: '#1f2937' }}>{total}</b> (Front: {f} / Back: {b})</div>
              </div>
              <p style={{ color: '#9ca3af', fontSize: 12, margin: '0 0 16px' }}>Verify the course and tee above. Once posted, scores can only be corrected on ghin.com.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowGhinConfirm(false)} style={{ flex: 1, padding: '12px 0', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>Cancel</button>
                <button onClick={postToGhin} disabled={ghinTeeLoading} style={{ flex: 1, padding: '12px 0', background: ghinTeeLoading ? '#9ca3af' : '#dc2626', color: '#fff', border: 'none', borderRadius: 10, cursor: ghinTeeLoading ? 'default' : 'pointer', fontSize: 16, fontWeight: 700 }}>Post to GHIN</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showGhinNoCreds && <GhinNoCredsModal onClose={() => setShowGhinNoCreds(false)} />}
      {showGhinTierLocked && <GhinTierLockedModal onClose={() => setShowGhinTierLocked(false)} />}
      {ghinNotMappedMsg && <GhinNotMappedModal message={ghinNotMappedMsg} onClose={() => setGhinNotMappedMsg(null)} />}
      {ghinLoginFailed && <GhinLoginFailedModal failure={ghinLoginFailed} onClose={() => setGhinLoginFailed(null)} onOpenProfile={onOpenProfile} />}
    </div>
  );
};


// ============================================================================
// POST-ROUND GRID — Scorecard-style entry for all 18 holes
// ============================================================================
const PostRoundGrid = ({ token, playerData, onClose, onOpenProfile }) => {
  const { addToast } = useToast();
  const [teetimes, setTeetimes] = useState([]);
  const [selectedTeetimeId, setSelectedTeetimeId] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [selectedTeeId, setSelectedTeeId] = useState(null);
  const [holeData, setHoleData] = useState([]);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scoresSaved, setScoresSaved] = useState(false);
  const [postingGhin, setPostingGhin] = useState(false);
  const [ghinPosted, setGhinPosted] = useState(false);
  const [showGhinConfirm, setShowGhinConfirm] = useState(false);
  const [showGhinNoCreds, setShowGhinNoCreds] = useState(false);
  const [showGhinTierLocked, setShowGhinTierLocked] = useState(false);
  const [ghinNotMappedMsg, setGhinNotMappedMsg] = useState(null);
  const [ghinLoginFailed, setGhinLoginFailed] = useState(null); // { code, message } from GHIN_INVALID_CREDENTIALS / GHIN_UNAVAILABLE (Sept 10, 2026)

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_URL}/player/scoring/teetimes?token=${token}&mode=postround`);
        if (resp.ok) setTeetimes(await resp.json());
      } catch (e) { console.error('Failed to load teetimes:', e); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  useEffect(() => {
    if (!selectedTeetimeId) return;
    const load = async () => {
      try {
        setLoading(true);
        const resp = await fetch(`${API_URL}/player/scoring/scorecard/${selectedTeetimeId}?token=${token}`);
        if (resp.ok) {
          const data = await resp.json();
          setScorecard(data);
          const teeToUse = data.default_tee || data.tees?.find(t => t.has_hole_data);
          if (teeToUse) { setSelectedTeeId(teeToUse.id); setHoleData(data.hole_data || []); }
          const s = {};
          for (let h = 1; h <= 18; h++) {
            const existing = data.scores[h];
            s[h] = { score: existing?.gross_score || '', putts: existing?.putts || '', ...(existing?.picked_up ? { picked: true } : {}) };
          }
          setScores(s);
          setScoresSaved(Object.values(data.scores).length > 0);
          if (data.ghin_posted_at) setGhinPosted(true);
        }
      } catch (e) { console.error('Failed to load scorecard:', e); }
      finally { setLoading(false); }
    };
    load();
  }, [selectedTeetimeId, token]);

  const changeTee = async (teeId) => {
    setSelectedTeeId(teeId);
    if (!scorecard) return;
    try {
      const resp = await fetch(`${API_URL}/player/scoring/holes/${teeId}?token=${token}`);
      if (resp.ok) setHoleData(await resp.json());
    } catch (e) {}
  };

  const updateHole = (hole, field, value) => {
    if (ghinPosted) return;
    const v = value === '' ? '' : Math.max(0, parseInt(value) || 0).toString();
    // A typed score replaces a pick-up ("7x"); putts don't.
    setScores(prev => ({ ...prev, [hole]: { ...prev[hole], [field]: v, ...(field === 'score' ? { picked: false } : {}) } }));
    setScoresSaved(false);
    setGhinPosted(false);
  };

  const saveScores = async () => {
    if (!selectedTeetimeId) return;
    try {
      setSaving(true);
      const arr = [];
      for (let h = 1; h <= 18; h++) {
        const s = scores[h];
        const gs = parseInt(s?.score);
        const pt = parseInt(s?.putts);
        const pk = !!(s?.picked && !isNaN(gs) && gs > 0);   // keep a "7x" as a pick-up
        arr.push({ hole_number: h, gross_score: !isNaN(gs) && gs > 0 ? gs : null, putts: !pk && !isNaN(pt) && pt > 0 ? pt : null, par: holeData.find(x => x.hole_number === h)?.par || null, picked_up: pk });
      }
      const resp = await fetch(`${API_URL}/player/scoring/scores?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teetime_id: selectedTeetimeId, course_tee_id: selectedTeeId || null, scores: arr })
      });
      if (resp.ok) { setScoresSaved(true); addToast('Scores saved!', 'success'); }
      else { const err = await resp.json(); addToast(err.error || 'Save failed', 'error'); }
    } catch (e) { addToast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const postToGhin = async () => {
    if (!selectedTeetimeId || !scorecard) return;
    setShowGhinConfirm(false);
    try {
      setPostingGhin(true);
      const resp = await fetch(`${API_URL}/player/scoring/post-to-ghin?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teetime_id: selectedTeetimeId, course_tee_id: selectedTeeId || null, played_at: scorecard.tee_time.date?.split('T')[0] })
      });
      if (resp.ok) { const d = await resp.json(); addToast(`✅ Score posted to GHIN successfully! Differential: ${d.differential}`, 'success'); setGhinPosted(true); }
      // GHIN_COURSE_NOT_MAPPED gets a modal rather than a toast: the server copy
      // tells the player to post in the GHIN app and explicitly NOT to retry
      // (retrying after the organizer sets the id would double-post the round),
      // which is too much for an auto-dismissing toast to carry.
      else {
        const err = await resp.json();
        if (err.code === 'GHIN_COURSE_NOT_MAPPED') setGhinNotMappedMsg(err.error || true);
        // Already in GHIN (Oct 16, 2026): same modal, its own title — a toast is too easy to miss.
        else if (err.code === 'GHIN_ALREADY_POSTED') setGhinNotMappedMsg({ title: 'Already Posted in GHIN', text: err.error });
        // Login failures get a modal too (Sept 10, 2026): a changed GHIN
        // password has a fix the player can apply right now, and a toast was
        // easy to miss entirely. See GhinLoginFailedModal.
        else if (err.code === 'GHIN_INVALID_CREDENTIALS' || err.code === 'GHIN_UNAVAILABLE') setGhinLoginFailed({ code: err.code, message: err.error });
        else addToast(err.error || 'GHIN post failed', 'error');
      }
    } catch(e) { addToast('GHIN post failed', 'error'); }
    finally { setPostingGhin(false); }
  };

  const formatDate = (ds) => { if (!ds) return ''; const d = new Date(ds.split('T')[0] + 'T12:00:00'); if (isNaN(d.getTime())) return ds; return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };

  // Totals
  const getTotals = (start, end) => {
    let gross = 0, putts = 0, par = 0, count = 0;
    for (let h = start; h <= end; h++) {
      const s = scores[h]; const hd = holeData.find(x => x.hole_number === h);
      const gs = parseInt(s?.score);
      if (!isNaN(gs) && gs > 0) { gross += gs; count++; }
      const pt = parseInt(s?.putts);
      if (!isNaN(pt) && pt > 0) putts += pt;
      if (hd) par += hd.par;
    }
    return { gross, putts, par, count };
  };
  const front = getTotals(1, 9);
  const back = getTotals(10, 18);
  const total = { gross: front.gross + back.gross, putts: front.putts + back.putts, par: front.par + back.par, count: front.count + back.count };

  const getScoreBg = (score, par) => { if (!score || !par) return 'transparent'; const d = score - par; if (d <= -2) return '#fbbf24'; if (d === -1) return '#dc2626'; if (d === 0) return '#166534'; if (d === 1) return '#3b82f6'; return '#1e3a5f'; };

  if (loading && teetimes.length === 0) return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>Loading...</div>;

  // Tee time selection
  if (!selectedTeetimeId) {
    return (
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>⛳ Post-Round Scorecard</h2>
          <button onClick={onClose} style={{ padding: '6px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Exit</button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 15, color: '#6b7280' }}>Select your round</p>
        {teetimes.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⛳</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>No tee times available</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {teetimes.map(tt => (
              <button key={tt.id} onClick={() => setSelectedTeetimeId(tt.id)}
                style={{ padding: 16, background: '#fff', border: '2px solid #e5e7eb', borderRadius: 12, cursor: 'pointer', textAlign: 'left' }}
                onMouseOver={e => e.currentTarget.style.borderColor = '#166534'}
                onMouseOut={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>{formatDate(tt.date)}</div>
                <div style={{ fontSize: 14, color: '#6b7280', marginTop: 2 }}>{tt.course_name} • {tt.time || ''}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>Loading scorecard...</div>;

  const selectedTee = scorecard?.tees?.find(t => t.id === selectedTeeId);

  // Scorecard grid
  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 12px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>⛳ Scorecard</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setSelectedTeetimeId(null); setScorecard(null); }}
            style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>← Rounds</button>
          <button onClick={onClose}
            style={{ padding: '6px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Exit</button>
        </div>
      </div>

      {/* Round info */}
      <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 14px', marginBottom: 10, border: '1px solid #e5e7eb', fontSize: 14 }}>
        <span style={{ fontWeight: 700 }}>{formatDate(scorecard?.tee_time?.date)}</span>
        <span style={{ color: '#6b7280' }}> • {scorecard?.tee_time?.course_name} • {selectedTee?.tee_name || ''}</span>
      </div>
      <HoleOutNote info={scorecard?.hole_out} />

      {/* Tee selector */}
      {scorecard?.tees?.filter(t => t.has_hole_data).length > 1 && (
        <select value={selectedTeeId || ''} onChange={(e) => changeTee(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, marginBottom: 10, background: '#fff' }}>
          {scorecard.tees.filter(t => t.has_hole_data).map(t => (
            <option key={t.id} value={t.id}>{t.tee_name} — {t.course_rating} / {t.slope_rating}</option>
          ))}
        </select>
      )}

      {/* GHIN Lock */}
      {ghinPosted && (
        <div style={{ background: '#166534', color: '#fff', borderRadius: 10, padding: '10px 16px', marginBottom: 10, textAlign: 'center', fontSize: 14, fontWeight: 700 }}>
          🔒 Posted to GHIN — Scores are locked
        </div>
      )}

      {/* Scorecard table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: 12 }}>
        {/* Header row */}
        <div style={{ display: 'flex', background: '#1f2937', color: '#fff', padding: '8px 0', fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
          <div style={{ width: 40, textAlign: 'center' }}>HOLE</div>
          <div style={{ width: 36, textAlign: 'center' }}>PAR</div>
          <div style={{ width: 48, textAlign: 'center' }}>YDS</div>
          <div style={{ width: 36, textAlign: 'center' }}>HCP</div>
          <div style={{ flex: 1, textAlign: 'center' }}>SCORE</div>
          <div style={{ width: 56, textAlign: 'center' }}>PUTTS</div>
        </div>

        {/* Holes 1-9 */}
        {[1,2,3,4,5,6,7,8,9].map(h => {
          const hd = holeData.find(x => x.hole_number === h);
          const s = scores[h] || { score: '', putts: '' };
          const gs = parseInt(s.score);
          const scoreBg = !isNaN(gs) && gs > 0 && hd ? getScoreBg(gs, hd.par) : 'transparent';
          const scoreColor = scoreBg !== 'transparent' ? '#fff' : '#1f2937';
          return (
            <div key={h} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f3f4f6', background: h % 2 === 0 ? '#fafafa' : '#fff' }}>
              <div style={{ width: 40, textAlign: 'center', fontWeight: 800, fontSize: 15, color: '#374151' }}>{h}</div>
              <div style={{ width: 36, textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#6b7280' }}>{hd?.par || ''}</div>
              <div style={{ width: 48, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>{hd?.yardage || ''}</div>
              <div style={{ width: 36, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>{hd?.handicap_index || ''}</div>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <input type="number" inputMode="numeric" min="0" value={s.score} onChange={e => updateHole(h, 'score', e.target.value)}
                  disabled={ghinPosted} placeholder="—"
                  style={{ width: 48, height: 36, textAlign: 'center', fontSize: 18, fontWeight: 800, border: '2px solid #e5e7eb', borderRadius: 8, outline: 'none', background: scoreBg, color: scoreColor,
                    opacity: ghinPosted ? 0.7 : 1 }}
                  onFocus={e => { if (!ghinPosted) e.target.style.borderColor = '#166534'; e.target.select(); }}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                {s.picked && s.score ? <span title="Picked up — a net double bogey" style={{ marginLeft: 2, alignSelf: 'center', fontSize: 14, fontWeight: 800, color: '#1e3a5f' }}>x</span> : null}
              </div>
              <div style={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                <input type="number" inputMode="numeric" min="0" value={s.putts} onChange={e => updateHole(h, 'putts', e.target.value)}
                  disabled={ghinPosted} placeholder="—"
                  style={{ width: 40, height: 36, textAlign: 'center', fontSize: 16, fontWeight: 600, border: '2px solid #e5e7eb', borderRadius: 8, outline: 'none', color: '#6b7280',
                    opacity: ghinPosted ? 0.7 : 1 }}
                  onFocus={e => { if (!ghinPosted) e.target.style.borderColor = '#166534'; e.target.select(); }}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              </div>
            </div>
          );
        })}

        {/* OUT totals */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', background: '#f0f0f0', borderBottom: '2px solid #d1d5db', fontWeight: 700, fontSize: 13 }}>
          <div style={{ width: 40, textAlign: 'center', color: '#374151' }}>OUT</div>
          <div style={{ width: 36, textAlign: 'center', color: '#6b7280' }}>{front.par || ''}</div>
          <div style={{ width: 48 }}></div>
          <div style={{ width: 36 }}></div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 800, color: '#166534' }}>{front.gross || '—'}</div>
          <div style={{ width: 56, textAlign: 'center', color: '#6b7280' }}>{front.putts || '—'}</div>
        </div>

        {/* Holes 10-18 */}
        {[10,11,12,13,14,15,16,17,18].map(h => {
          const hd = holeData.find(x => x.hole_number === h);
          const s = scores[h] || { score: '', putts: '' };
          const gs = parseInt(s.score);
          const scoreBg = !isNaN(gs) && gs > 0 && hd ? getScoreBg(gs, hd.par) : 'transparent';
          const scoreColor = scoreBg !== 'transparent' ? '#fff' : '#1f2937';
          return (
            <div key={h} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f3f4f6', background: h % 2 === 0 ? '#fafafa' : '#fff' }}>
              <div style={{ width: 40, textAlign: 'center', fontWeight: 800, fontSize: 15, color: '#374151' }}>{h}</div>
              <div style={{ width: 36, textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#6b7280' }}>{hd?.par || ''}</div>
              <div style={{ width: 48, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>{hd?.yardage || ''}</div>
              <div style={{ width: 36, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>{hd?.handicap_index || ''}</div>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <input type="number" inputMode="numeric" min="0" value={s.score} onChange={e => updateHole(h, 'score', e.target.value)}
                  disabled={ghinPosted} placeholder="—"
                  style={{ width: 48, height: 36, textAlign: 'center', fontSize: 18, fontWeight: 800, border: '2px solid #e5e7eb', borderRadius: 8, outline: 'none', background: scoreBg, color: scoreColor,
                    opacity: ghinPosted ? 0.7 : 1 }}
                  onFocus={e => { if (!ghinPosted) e.target.style.borderColor = '#166534'; e.target.select(); }}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                {s.picked && s.score ? <span title="Picked up — a net double bogey" style={{ marginLeft: 2, alignSelf: 'center', fontSize: 14, fontWeight: 800, color: '#1e3a5f' }}>x</span> : null}
              </div>
              <div style={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                <input type="number" inputMode="numeric" min="0" value={s.putts} onChange={e => updateHole(h, 'putts', e.target.value)}
                  disabled={ghinPosted} placeholder="—"
                  style={{ width: 40, height: 36, textAlign: 'center', fontSize: 16, fontWeight: 600, border: '2px solid #e5e7eb', borderRadius: 8, outline: 'none', color: '#6b7280',
                    opacity: ghinPosted ? 0.7 : 1 }}
                  onFocus={e => { if (!ghinPosted) e.target.style.borderColor = '#166534'; e.target.select(); }}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              </div>
            </div>
          );
        })}

        {/* IN totals */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', background: '#f0f0f0', borderBottom: '2px solid #d1d5db', fontWeight: 700, fontSize: 13 }}>
          <div style={{ width: 40, textAlign: 'center', color: '#374151' }}>IN</div>
          <div style={{ width: 36, textAlign: 'center', color: '#6b7280' }}>{back.par || ''}</div>
          <div style={{ width: 48 }}></div>
          <div style={{ width: 36 }}></div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 800, color: '#166534' }}>{back.gross || '—'}</div>
          <div style={{ width: 56, textAlign: 'center', color: '#6b7280' }}>{back.putts || '—'}</div>
        </div>

        {/* TOTAL */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', background: '#1f2937', fontWeight: 800, fontSize: 14 }}>
          <div style={{ width: 40, textAlign: 'center', color: '#fff' }}>TOT</div>
          <div style={{ width: 36, textAlign: 'center', color: '#9ca3af' }}>{total.par || ''}</div>
          <div style={{ width: 48 }}></div>
          <div style={{ width: 36 }}></div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 20, fontWeight: 900, color: '#4ade80' }}>{total.gross || '—'}</div>
          <div style={{ width: 56, textAlign: 'center', color: '#9ca3af' }}>{total.putts || '—'}</div>
        </div>
      </div>

      {/* Save & GHIN buttons */}
      <div style={{ display: 'flex', gap: 10 }}>
        {(() => {
          const hasCreds = !!playerData?.has_ghin_credentials;
          // Tier gate BEFORE the credentials check. GHIN *posting* is gated by the
          // 'scoring' feature key (Starter and up) at requirePlayerScoring /
          // requirePersonalRounds — NOT by 'ghin_sync', which gates auto-sync only
          // (Plus and up). Both posting endpoints call golfer_login.json directly
          // rather than through ghinService.login(), so they never reach that gate.
          // Gating this button on ghin_sync would lock out paying Starter orgs that
          // can post today. Uses !== true so an undefined flag during initial load
          // locks rather than flashes unlocked — same convention as the Profile gate.
          const tierOk = playerData?.scoring_enabled === true;
          let bg = '#d1d5db', col = '#9ca3af', cur = 'default', txt = 'Post to GHIN';
          if (ghinPosted) { bg = '#166534'; col = '#fff'; txt = '🔒 GHIN Posted'; }
          else if (postingGhin) { bg = '#9ca3af'; col = '#fff'; txt = 'Posting...'; }
          else if (scoresSaved) { bg = '#111827'; col = '#fff'; cur = 'pointer'; }
          // Keep tappable when credentials are missing so the tap explains why
          // (disabled button + title tooltip is invisible on touch devices).
          // The round's postable_to_ghin flag decides before anything else (Sept 15, 2026).
          if (scorecard?.tee_time?.postable_to_ghin === false) return <GhinNotPostableNote />;
          const disabled = !scoresSaved || ghinPosted || postingGhin;
          const ghinTitle = !scoresSaved ? 'Save scores first' : ghinPosted ? 'Already posted to GHIN' : !tierOk ? "Your current plan doesn't include Scoring. Ask your organizer about upgrading." : !hasCreds ? 'GHIN credentials not set up — visit your Profile to connect GHIN' : '';
          return <button onClick={() => !tierOk ? setShowGhinTierLocked(true) : hasCreds ? setShowGhinConfirm(true) : setShowGhinNoCreds(true)} disabled={disabled} title={ghinTitle} style={{ flex: 1, padding: '14px 0', background: bg, color: col, border: 'none', borderRadius: 10, cursor: cur, fontSize: 16, fontWeight: 700 }}>{txt}</button>;
        })()}
        <button onClick={saveScores} disabled={saving || ghinPosted}
          style={{ flex: 1, padding: '14px 0', background: ghinPosted ? '#d1d5db' : saving ? '#9ca3af' : '#166534', color: ghinPosted ? '#9ca3af' : '#fff', border: 'none', borderRadius: 10, cursor: (saving || ghinPosted) ? 'default' : 'pointer', fontSize: 16, fontWeight: 700 }}>
          {ghinPosted ? '🔒 Locked' : saving ? 'Saving...' : scoresSaved ? '✓ Saved' : 'Save Scores'}
        </button>
      </div>

      {/* GHIN Confirmation Modal */}
      {showGhinConfirm && (() => {
        const selectedTee = scorecard?.tees?.find(t => t.id === selectedTeeId);
        const courseName = scorecard?.tee_time?.course_name || '';
        const playedAt = scorecard?.tee_time?.date ? new Date(scorecard.tee_time.date.split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';
        const teeColor = selectedTee?.tee_color || '#000';
        const isPaleTee = teeColor && (teeColor.toLowerCase() === '#f3f4f6' || teeColor.toLowerCase() === '#ffffff' || teeColor.toLowerCase() === 'white');
        let gross = 0;
        for (let h = 1; h <= 18; h++) { const s = parseInt(scores[h]?.score); if (!isNaN(s) && s > 0) gross += s; }
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>⛳</div>
              <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700 }}>Post Score to GHIN?</h3>
              {/* Round details — verify this is correct before posting */}
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14, textAlign: 'left' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>{courseName}</div>
                {selectedTee && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 3, background: teeColor, border: isPaleTee ? '1px solid #d1d5db' : 'none', flexShrink: 0 }} />
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>{selectedTee.tee_name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{selectedTee.course_rating} / {selectedTee.slope_rating}</div>
                  </div>
                )}
                <div style={{ fontSize: 13, color: '#6b7280' }}>{playedAt}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Score: <b style={{ color: '#1f2937' }}>{gross}</b></div>
              </div>
              <p style={{ color: '#9ca3af', fontSize: 12, margin: '0 0 16px' }}>Verify the course and tee above. Once posted, scores can only be corrected on ghin.com.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowGhinConfirm(false)} style={{ flex: 1, padding: '12px 0', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>Cancel</button>
                <button onClick={postToGhin} style={{ flex: 1, padding: '12px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Post to GHIN</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showGhinNoCreds && <GhinNoCredsModal onClose={() => setShowGhinNoCreds(false)} />}
      {showGhinTierLocked && <GhinTierLockedModal onClose={() => setShowGhinTierLocked(false)} />}
      {ghinNotMappedMsg && <GhinNotMappedModal message={ghinNotMappedMsg} onClose={() => setGhinNotMappedMsg(null)} />}
      {ghinLoginFailed && <GhinLoginFailedModal failure={ghinLoginFailed} onClose={() => setGhinLoginFailed(null)} onOpenProfile={onOpenProfile} />}
    </div>
  );
};

// ============================================================================
// NOTABLE SCORE BANNER — Scoring Enhancement A (May 2026)
// Renders a top-of-screen banner when another player in the same tee time
// scores notably (per the org's threshold setting). Auto-dismisses after 20s.
// Props:
//   event: { player_name, hole_number, type } | null
//   onDismiss: () => void
// type values: 'birdie' | 'eagle' | 'albatross' | 'hole_in_one'
// ============================================================================
const NotableScoreBanner = ({ event, onDismiss }) => {
  // Keep the latest onDismiss in a ref so the auto-dismiss timer depends ONLY on
  // `event`. Previously onDismiss was in the dep array; because the parent passes
  // an inline arrow (a new reference every render) and re-renders constantly during
  // scoring, the effect re-ran on every render — clearing and restarting the timer —
  // so the banner could persist for minutes (until activity quieted for a full
  // interval). Reading onDismiss via a ref makes the timer fire exactly once.
  const onDismissRef = React.useRef(onDismiss);
  React.useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);
  React.useEffect(() => {
    if (!event) return;
    const t = setTimeout(() => onDismissRef.current && onDismissRef.current(), 20000);
    return () => clearTimeout(t);
  }, [event]);

  if (!event) return null;

  // Style differentiation by achievement
  let bg = '#16a34a', emoji = '🐦', label = `just had a birdie on #${event.hole_number}`, fontWeight = 700;
  if (event.type === 'eagle')       { bg = '#ca8a04'; emoji = '🦅'; label = `just had an EAGLE on #${event.hole_number}`; fontWeight = 800; }
  else if (event.type === 'albatross') { bg = '#a16207'; emoji = '🏆'; label = `just had an ALBATROSS on #${event.hole_number}`; fontWeight = 900; }
  else if (event.type === 'hole_in_one') { bg = '#dc2626'; emoji = '🎯'; label = `HOLE IN ONE! Aced #${event.hole_number}`; fontWeight = 900; }
  // Oct 29, 2026: closest to the pin. hole_text is the hole as the property numbers it.
  else if (event.type === 'ctp') { bg = '#1d4ed8'; emoji = '📍'; label = event.from_name ? `took closest to the pin on ${event.hole_text || `#${event.hole_number}`} from ${event.from_name}` : `got closest to the pin on ${event.hole_text || `#${event.hole_number}`}`; fontWeight = 700; }

  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
        background: bg, color: '#fff', padding: '12px 20px', borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)', zIndex: 10000,
        fontSize: 15, fontWeight, maxWidth: 'calc(100% - 24px)',
        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
        animation: 'slideDownFade 0.3s ease-out'
      }}>
      <span style={{ fontSize: 22 }}>{emoji}</span>
      <span><strong>{event.player_name}</strong> {label}</span>
    </div>
  );
};

// ============================================================================
// LIVE SCORING COMPONENT
// ============================================================================
// Board hole-set labelling (Nine_Hole_Scoring_Plan_v1_4 §7).
// The board payload carries competition_hole_set ('all18'|'front9'|'back9'),
// hole_count, and net_unavailable_reason. THREE renderers show this board
// (LiveScoring, PublicLeaderboard, LeaderboardModal) and their JSX is
// triplicated by hand — so the LOGIC lives here, once, and each renderer calls
// it. Do not inline this back into any of them.
// ============================================================================
const HOLE_SET_LABEL = { front9: 'Front 9', back9: 'Back 9', all18: null };

const holeSetSuffix = (board) => {
  const l = HOLE_SET_LABEL[board?.competition_hole_set];
  return l ? ` \u00b7 ${l}` : '';
};

// Section header text for the leaderboard's Live Ranking band.
// Gross view: plain label. Net view: say WHICH handicap is in play, or why net
// is missing. Previously this hardcoded "(using full course handicap)" — which
// was a lie on a 9-hole board, where the 9-hole course handicap is used (§3).
// Header sub-line shared by the board renderers: "<course> · Through N · 18 teams (72 players)"
// for a team board, "<course> · Through N · 36 players" otherwise. The event name, when
// there is one, is rendered ABOVE this line by each caller (recap item 9).
const boardCountText = (board) => {
  if (!board) return '';
  if (board.scoring_mode === 'team') {
    const t = board.team_count != null ? `${board.team_count} team${board.team_count === 1 ? '' : 's'}` : '';
    const p = board.player_count != null ? ` (${board.player_count} players)` : '';
    return t ? ` \u00b7 ${t}${p}` : '';
  }
  return board.player_count != null ? ` \u00b7 ${board.player_count} players` : '';
};

// Staleness — a frozen board is indistinguishable from a live one (Platform Plan §6.5).
// Renderers keep `lastUpdated` (last SUCCESSFUL fetch) and a slow ticker; while the
// round is live and no fetch has landed for this long, the footer turns amber.
const BOARD_STALE_MS = 75000;
const boardFooter = (board, lastUpdated, now) => {
  const t = lastUpdated ? new Date(lastUpdated).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
  const stale = !!(board && board.live && lastUpdated && (now - lastUpdated) > BOARD_STALE_MS);
  if (stale) {
    const mins = Math.max(1, Math.round((now - lastUpdated) / 60000));
    return { stale, text: `\u26a0 Not updating \u2014 last update ${t} (${mins} min ago). Tap Refresh.` };
  }
  const status = board?.live ? 'Live \u00b7 updates automatically' : (board?.through > 0 ? 'Round complete' : 'Not started');
  return { stale, text: `${status}${t ? ` \u00b7 Updated ${t}` : ''}` };
};

// ============================================================================
// TeamBoardRows — the ONE row renderer for a team-scored board (scoring_mode='team').
// Platform Plan §6.4: team label = last names A→Z (composed server-side), never
// truncated (wraps); second line = team number + start hole / time + thru; T-prefixed
// position on a tie; pace flags colour the thru text but never demote. Used by
// PublicLeaderboard and LeaderboardModal — do not inline it into either.
// ============================================================================
// Tie marks (Sept 16, 2026): SP = place won on the scorecards (USGA last 9/6/3/1 or
// hole handicap), PO = place decided by a playoff or the committee (the organizer's
// order in Games Setup). A tie still to be decided keeps its T and reads "Playoff
// pending". Shared by the phone board, the TV, the results panel and the portal.
// Post-Round Score Entry tab label (Sept 27, 2026): the player's LAST name, with a
// first initial when another player on the round shares it ("B. Smith") — the rule
// the team labels use (spectatorBoardService.composeTeamLabel).
const postRoundTabName = (player, players) => {
  const parts = (n) => { const t = String(n || '').trim().split(/\s+/); return { first: t[0] || '', last: t.length > 1 ? t[t.length - 1] : (t[0] || '') }; };
  const me = parts(player && player.name);
  const dup = (players || []).filter(p => parts(p.name).last.toLowerCase() === me.last.toLowerCase()).length > 1;
  return dup && me.first ? `${me.first[0]}. ${me.last}` : me.last;
};
// Picked up ("7x", Sept 27, 2026 — Pick_Up_X_Design_v1): an X is scored as a NET
// DOUBLE BOGEY — par + 2 + the strokes received on the hole off the FULL course
// handicap (a plus handicap gives strokes back on the easiest holes). The screens'
// estimate; the server (services/pickupService.js) sets the stored number with the
// same arithmetic and returns it on save, so the two can only differ while offline.
const pickupNdb = (par, courseHcp, strokeIdx) => {
  if (par == null || !Number.isFinite(Number(par))) return null;
  let st = 0;
  const idx = Number(strokeIdx);
  if (courseHcp != null && Number.isFinite(Number(courseHcp)) && Number.isFinite(idx) && idx > 0) {
    const ch = Math.round(Number(courseHcp));
    if (ch >= 0) st = Math.floor(ch / 18) + (idx <= ch % 18 ? 1 : 0);
    else { const give = -ch; st = -(Math.floor(give / 18) + (idx > 18 - (give % 18) ? 1 : 0)); }
  }
  return Math.max(1, Number(par) + 2 + st);
};
// Post-Round tabs in alphabetical order: last name, then first, then full name.
const postRoundAlpha = (players) => {
  const key = (p) => { const t = String((p && p.name) || '').trim().split(/\s+/); const last = t.length > 1 ? t[t.length - 1] : (t[0] || ''); return [last.toLowerCase(), (t[0] || '').toLowerCase(), String((p && p.name) || '').toLowerCase()]; };
  return (players || []).slice().sort((a, b) => {
    const ka = key(a), kb = key(b);
    for (let i = 0; i < 3; i++) { const c = ka[i].localeCompare(kb[i]); if (c) return c; }
    return 0;
  });
};
const TieMark = ({ mark, tv = false }) => (mark === 'SP' || mark === 'PO') ? (
  <sup title={mark === 'SP' ? 'Won on the scorecard playoff' : 'Decided by playoff'}
    style={tv
      ? { fontSize: 15, fontWeight: 800, color: TV_GOLD, marginLeft: 3, letterSpacing: 0.5, verticalAlign: 'super' }
      : { fontSize: 9, fontWeight: 800, color: '#b45309', marginLeft: 2, letterSpacing: 0.3 }}>{mark}</sup>
) : null;
// `basis` ('net' | 'gross') reads an INDIVIDUAL board ranked on a stroke play game
// (Sept 23, 2026): the row fields are net_tied / net_tie_mark / …, the method and
// places come from board.game, and the notes say "players". Without it: a team board.
const tieFootnotes = (board, rows, basis) => {
  const list = rows || board?.rows || [];
  const pre = basis ? `${basis}_` : '';
  const anyTie = list.some(r => r[`${pre}tied`]);
  const anySP = list.some(r => r[`${pre}tie_mark`] === 'SP');
  const anyPO = list.some(r => r[`${pre}tie_mark`] === 'PO');
  const anyPending = list.some(r => r[`${pre}tie_status`] === 'pending');
  // A Best Ball board (Sept 27, 2026) is a team board whose rows carry net_/gross_
  // places like a stroke play board: its method and places are on the board itself.
  const g = basis ? (boardGame(board) || (isBestBallBoard(board) ? board : null)) : null;
  const method = (basis ? g?.tie_break : board?.tie_break) || 'none';
  const paid = Number(basis ? g?.places_paid : board?.places_paid) || 0;
  const onNet = basis ? basis === 'net' : !!board?.has_net;
  const who = basis && !isBestBallBoard(board) ? 'players' : 'teams';
  const top = paid === 1 ? 'first place' : `the top ${paid} places`;
  const nine = Number(board?.hole_count) === 9;
  const notes = [];
  if (method === 'none') {
    if (anyTie) notes.push(`T = tied. Tied ${who} are listed by match of cards (hardest holes first); the committee's ruling decides prizes.`);
    return notes;
  }
  if (anyTie || anySP || anyPO) {
    const how = method === 'usga'
      ? `a scorecard playoff (best ${nine ? 'last 6, last 3, then the last hole' : 'last 9, last 6, last 3, then the 18th'}${onNet ? ', net' : ''})`
      : method === 'hole_handicap' ? `hole handicap (hardest hole first${onNet ? ', net' : ''})` : 'the committee';
    notes.push(`T = tied. Ties for ${top} are decided by ${how} once the ${who} finish.`);
  }
  if (anySP) notes.push('SP = place won on the scorecard playoff.');
  if (anyPO) notes.push('PO = place decided by playoff or the committee.');
  if (anyPending) notes.push('Playoff pending = tied on the cards; the organizer will post the finishing order.');
  if (basis && list.some(r => r.has_scores && r.in_game === false)) notes.push('– = playing the round but not in the game; not placed.');
  return notes;
};

// ── Best Ball on the team board (Sept 27, 2026 — Best_Ball_Slice3_Board_Sep27) ──
// A round with a Best Ball game gets a TEAM board (scoring_mode 'team', team_kind
// 'best_ball') built from the engine's own standings. It differs from a scramble's
// board in three ways the renderers care about: no team handicap (each player plays
// off his own), BOTH bases may be ranked (a net & gross game — the row carries
// net_place_label / gross_place_label …, board.ranked_bases says which), and the
// blind ("+ Blind Draw" until final; its card is withheld: card_hidden). These are
// the ONE reading of that for TeamBoardRows and the TV.
const isBestBallBoard = (board) => !!board && board.team_kind === 'best_ball';
// Does this team board rank the view (net / gross)? A scramble ranks its one basis.
const teamBoardRanksOn = (board, isNet) => {
  const b = isNet ? 'net' : 'gross';
  if (isBestBallBoard(board)) return (board.ranked_bases || [board.basis || 'net']).includes(b);
  return b === (board?.basis || 'gross');
};
// The place a team row shows on a view: { label, mark, status, tied } (nulls when unranked).
const teamBoardPlace = (board, r, isNet) => {
  if (!teamBoardRanksOn(board, isNet)) return { label: null, mark: null, status: null, tied: false };
  if (isBestBallBoard(board)) {
    const b = isNet ? 'net' : 'gross';
    return { label: r[`${b}_place_label`] || null, mark: r[`${b}_tie_mark`] || null, status: r[`${b}_tie_status`] || null, tied: !!r[`${b}_tied`] };
  }
  return { label: r.position_label || null, mark: r.tie_mark || null, status: r.tie_status || null, tied: !!r.tied };
};

const TeamBoardRows = ({ board, viewerId, onOpenTeam, isNet = false }) => {
  const fmtVs = (v) => v == null ? '\u2014' : (v === 0 ? 'E' : (v > 0 ? `+${v}` : `${v}`));
  // The server ranks on the GAME's basis (board.basis). Showing the other basis is an
  // unofficial re-sort: positions are hidden so a gross order never wears net places.
  // A Best Ball net & gross game ranks both (teamBoardRanksOn).
  const boardBasis = board?.basis || 'gross';
  const bestBall = isBestBallBoard(board);
  const viewBasis = isNet ? 'net' : 'gross';
  const basisMatch = teamBoardRanksOn(board, isNet);
  const keyOf = (r) => isNet
    ? (r.net_total == null ? null : (r.net_vs_par != null ? r.net_vs_par : r.net_total + 1000))
    : (r.gross_total == null ? null : (r.gross_vs_par != null ? r.gross_vs_par : r.gross_total + 1000));
  let rows = board?.rows || [];
  if (!basisMatch) {
    rows = rows.slice().sort((a, b) => {
      if (a.section !== b.section) return a.section === 'live' ? -1 : 1;
      const ka = keyOf(a), kb = keyOf(b);
      if (ka == null && kb == null) return a.team_number - b.team_number;
      if (ka == null) return 1; if (kb == null) return -1;
      if (ka !== kb) return ka - kb;
      return b.thru - a.thru || a.team_number - b.team_number;
    });
  } else if (bestBall && viewBasis !== boardBasis) {
    // Best Ball net & gross: the other ranked basis in its own finishing order.
    const ord = (r) => (r.section === 'live' && r[`${viewBasis}_order`] != null) ? r[`${viewBasis}_order`] : null;
    rows = rows.slice().sort((a, b) => {
      if (a.section !== b.section) return a.section === 'live' ? -1 : 1;
      const oa = ord(a), ob = ord(b);
      if (oa != null || ob != null) { if (oa == null) return 1; if (ob == null) return -1; return oa - ob; }
      return a.team_number - b.team_number;
    });
  }
  const anyFlag = rows.some(r => r.entry_flag === 'red' || r.entry_flag === 'amber');
  const anyTie = basisMatch && rows.some(r => teamBoardPlace(board, r, isNet).tied);
  const anyOverride = isNet && rows.some(r => r.handicap_source === 'override');
  const tieNotes = basisMatch ? (bestBall ? tieFootnotes(board, rows, viewBasis) : tieFootnotes(board, rows)) : [];
  const anyBlindHidden = bestBall && rows.some(r => r.includes_blind && r.card_hidden);
  let lastSection = null;
  return (
    <div>
      {rows.map((r) => {
        const isMine = !!(viewerId && (r.members || []).some(m => m.player_id === viewerId));
        const total = isNet ? r.net_total : r.gross_total;
        const vs = isNet ? r.net_vs_par : r.gross_vs_par;
        const vsColor = vs == null ? '#9ca3af' : (vs < 0 ? '#166534' : vs > 0 ? '#9ca3af' : '#6b7280');
        const thruColor = r.entry_flag === 'red' ? '#dc2626' : r.entry_flag === 'amber' ? '#f59e0b' : '#6b7280';
        const showHeader = r.section !== lastSection;
        lastSection = r.section;
        const pl = teamBoardPlace(board, r, isNet);
        const sub = (bestBall ? [
          r.team_label || null,   // a registered team's name — never the group number (Oct 10, 2026)
          r.start_text ? `Start ${r.start_text}` : null,
          r.has_scores ? `Thru ${r.thru}` : 'No scores yet',
          // Names only when the server allows them — never while a blind is secret.
          r.waiting_on && r.waiting_on.length ? `waiting on ${r.waiting_on.join(', ')}` : (r.waiting ? 'waiting on a card' : null),
          r.blind_text || null,
          r.unranked_text || null,
        ] : [
          r.team_label || null,   // a registered team's name — never the group number (Oct 10, 2026)
          r.start_text ? `Start ${r.start_text}` : null,
          r.has_scores ? `Thru ${r.thru}` : 'No scores yet',
          isNet ? (r.team_handicap != null ? `Hcp ${r.team_handicap}${r.handicap_source === 'override' ? '*' : ''}` : (r.has_scores ? 'no handicap' : null)) : null,
          r.short_handed ? `${(r.members || []).length}-man` : null
        ]).filter(Boolean).join(' \u00b7 ');
        const other = otherBasisText(r, isNet);
        const pending = basisMatch && pl.status === 'pending';
        const canOpen = !!(r.has_scores && onOpenTeam && !r.card_hidden);
        return (
          <React.Fragment key={r.player_id}>
            {showHeader && (
              <div style={{ margin: '14px 2px 6px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#9ca3af' }}>
                  {r.section === 'live'
                    ? (basisMatch
                        ? (bestBall
                            ? `Live Ranking (${[board.format_label || 'best ball', isNet ? `net \u00b7 ${board.allowance_pct != null ? `${board.allowance_pct}% ` : ''}playing handicap` : 'gross'].join(' \u00b7 ')})`
                            : `Live Ranking${isNet ? ' (net \u00b7 team handicap)' : ''}`)
                        : `${isNet ? 'Net' : 'Gross'} view \u2014 unofficial, this event ranks on ${boardBasis}`)
                    : 'No scores entered'}
                </div>
                {r.section === 'no_scores' && (
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginTop: 2, lineHeight: 1.4 }}>
                    These teams haven't entered a score yet.
                  </div>
                )}
              </div>
            )}
            <div
              onClick={canOpen ? () => onOpenTeam(r.player_id) : undefined}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                padding: '10px 12px', borderRadius: 10, marginBottom: 4,
                background: isMine ? '#f0fdf4' : '#fff', border: isMine ? '1px solid #bbf7d0' : '1px solid #f3f4f6',
                cursor: canOpen ? 'pointer' : 'default' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                <div style={{ minWidth: 30, textAlign: 'center', fontSize: 15, fontWeight: 800, color: pl.label ? '#1f2937' : '#d1d5db', flexShrink: 0 }}>
                  {pl.label || '\u2013'}
                  {basisMatch && <TieMark mark={pl.mark} />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: isMine ? 700 : 600, fontSize: 14, color: '#1f2937', lineHeight: 1.25, wordBreak: 'break-word' }}>
                    {r.name}{isMine ? ' (your team)' : ''}
                  </div>
                  <div style={{ fontSize: 11, color: thruColor, marginTop: 2, fontWeight: r.entry_flag !== 'none' ? 700 : 400 }}>
                    {sub}
                    {pending && <span style={{ color: '#b45309', fontWeight: 700 }}>{' \u00b7 '}Playoff pending</span>}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: 22, color: '#1f2937', minWidth: 30, textAlign: 'right' }}>
                    {total != null ? total : '\u2014'}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: vsColor, minWidth: 30, textAlign: 'right' }}>
                    {fmtVs(vs)}
                  </span>
                </div>
                {other && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1, whiteSpace: 'nowrap' }}>{other}</div>}
              </div>
            </div>
          </React.Fragment>
        );
      })}
      {(tieNotes.length > 0 || anyFlag || anyOverride || anyBlindHidden) && (
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, lineHeight: 1.5 }}>
          {tieNotes.map(t => <div key={t}>{t}</div>)}
          {anyOverride && <div>* handicap set by the organizer.</div>}
          {anyBlindHidden && <div>+ Blind Draw = the team is a player short and a player drawn at random fills the spot. Who it is shows once the round is final.</div>}
          {anyFlag && <div><span style={{ color: '#dc2626', fontWeight: 700 }}>Red</span> / <span style={{ color: '#f59e0b', fontWeight: 700 }}>amber</span> thru = 3+ / 2 holes behind the field — that team may not be entering scores.</div>}
        </div>
      )}
    </div>
  );
};

const boardSectionHeader = (sectionLabel, section, isNet, board) => {
  // A stroke play game on the board (Sept 23, 2026): the view it ranks says so; the
  // other view is an unofficial re-sort, like the team board's other basis.
  const g = boardGame(board);
  if (g && section === 'live' && !boardRankedOn(board, isNet)) {
    return `${isNet ? 'Net' : 'Gross'} view — unofficial, this game ranks on ${g.competition === 'gross' ? 'gross' : 'net'}`;
  }
  if (g && section === 'live' && !isNet) return `${sectionLabel} (gross)`;
  if (!isNet || section !== 'live') return sectionLabel;
  const reason = board?.net_unavailable_reason;
  if (reason === 'no_nine_hole_ratings')
    return `${sectionLabel} (net unavailable \u2014 no 9-hole ratings for this course)`;
  if (reason === 'no_handicap')
    return `${sectionLabel} (net unavailable \u2014 no handicap on file)`;
  if (reason === 'no_hole_data')
    return `${sectionLabel} (net unavailable \u2014 course hole data missing)`;
  const l = HOLE_SET_LABEL[board?.competition_hole_set];
  // Estimated nine-hole ratings (Sept 23, 2026) — no official front/back ratings on
  // file, derived from the 18-hole rating. One helper, all three board renderers.
  if (l && board?.nine_hole_rating_estimated)
    return `${sectionLabel} (${l.toLowerCase()} \u00b7 estimated 9-hole handicap)`;
  if (g) return `${sectionLabel} (net \u00b7 ${g.allowance_pct}% playing handicap${l ? ` \u00b7 ${l.toLowerCase()}` : ''})`;
  return l
    ? `${sectionLabel} (${l.toLowerCase()} \u00b7 using 9-hole course handicap)`
    : `${sectionLabel} (using course handicap)`;
};

// ============================================================================
// TeamScoring — the TEAM card (one score per team per hole) for captain's choice /
// scramble rounds. Association Platform Plan §6.7 (screen spec agreed Aug 10,
// 2026); built Sept 13, 2026 on the Aug 5 backend (GET/POST /player/scoring/team).
//
// Same look and feel as LiveScoring, modified for a team:
//   - team banner (last names A→Z, start hole / group letter) + event name
//   - ONE score box — the stepper writes the team's score for the hole
//   - card-holder / take-over control instead of "+ Score for another player"
//   - THRU / TEAM GROSS / VS PAR strip (no ADJ, no PUTTS — one ball, gross)
//   - on a NET Captain's Choice game (Sept 14, 2026): a "Team hcp N · net −1" line in
//     the banner, a TEAM NET box in the strip, and a printed-card style asterisk on
//     every hole where the team gets a stroke (hole boxes + the hole header). The
//     handicap and strokes come from the server (team_game on GET /scoring/team —
//     the same teamHandicapService the board and standings use), never computed here.
//   - NO tee name, tee picker or per-hole yardage (Dennis, Sept 14, 2026): a team's
//     players may be on different tees, so none of those belongs on a team card. Par
//     and stroke index come from the TEAM's tee — `team_tee` on GET /scoring/team,
//     the board's rule (the scramble game's stroke-index tee, else the members'
//     majority tee, else the course default) — so the card and the board read the
//     same par row. Yards-to-Center (GPS) stays: it is tee-independent.
//   - NO Post-to-GHIN anywhere: a scramble is not a handicap round, and there are
//     no round_scores rows to post from (the team rows live in team_scores)
//
// THE LOCK — the one behavior change from individual scoring. There, self-entry
// always wins. A scramble has one ball and no "self", so the team claim is
// required-to-write: a non-holder genuinely cannot type until they take over.
// The read-only state is therefore LOUD (banner + disabled stepper + no Save) and
// takeover is one tap + one confirm. The server enforces the same rule (409
// CARD_CLAIMED); this screen just makes it visible before the tap.
//
// Mounted from LiveScoring's round list when a round has scoring_mode = 'team'.
// resolveTeamContext re-checks the org gate and the round's mode on every call,
// so this screen is never the only guard (Rule 17).
//
// Save model: only DIRTY holes are sent (never the whole card), so a stale view
// can never clear a hole another device entered. Prev/Next save on the move, a
// 30-second auto-save catches the rest, and unsaved holes are mirrored to
// localStorage so a lost signal loses nothing.
// ============================================================================
// Celebration banners on the team card (Oct 4, 2026 — Dennis: "eagles or better on team
// events (both gross, not net)"). Same feed as LiveScoring (/player/scoring/highlights),
// which on a team-scored round reads the TEAM cards: "Team 12 (Hughes / Norris) just had
// an EAGLE on #7". Shares LiveScoring's seen-list so a banner never shows twice on a phone.
// The server returns nothing when the group turned banners off.
const TeamCelebrations = ({ token, teetimeId }) => {
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);
  const sinceRef = React.useRef(null);
  useEffect(() => {
    if (!token || !teetimeId) return undefined;
    let cancelled = false;
    let seen;
    try { seen = new Set(JSON.parse(localStorage.getItem('tto_seen_highlights') || '[]')); } catch (e) { seen = new Set(); }
    const poll = async () => {
      if (cancelled) return;
      try {
        const since = sinceRef.current;
        const r = await fetch(`${API_URL}/player/scoring/highlights?token=${token}&teetime_id=${teetimeId}${since ? `&since=${encodeURIComponent(since)}` : ''}`);
        if (!r.ok) return;
        const d = await r.json();
        const events = d.events || [];
        if (!events.length) return;
        const newest = events.reduce((acc, e) => (!acc || e.saved_at > acc) ? e.saved_at : acc, null);
        if (newest) sinceRef.current = newest;
        // First look when the card opens: only the last 15 minutes are news — older ones are
        // marked seen, so opening the card mid-round doesn't queue a morning of eagles.
        const firstLook = !since;
        const cutoff = Date.now() - 15 * 60 * 1000;
        const fresh = [];
        for (const ev of [...events].reverse()) {
          if (seen.has(ev.dedup_key)) continue;
          seen.add(ev.dedup_key);
          if (!firstLook || new Date(ev.saved_at).getTime() >= cutoff) fresh.push(ev);
        }
        try { const arr = Array.from(seen).slice(-200); seen = new Set(arr); localStorage.setItem('tto_seen_highlights', JSON.stringify(arr)); } catch (e) { /* quota */ }
        if (!fresh.length) return;
        if (!cancelled) setQueue(q => [...q, ...fresh]);
      } catch (e) { /* best effort */ }
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token, teetimeId]);
  useEffect(() => {
    if (!active && queue.length) { setActive(queue[0]); setQueue(q => q.slice(1)); }
  }, [active, queue]);
  return <NotableScoreBanner event={active} onDismiss={() => setActive(null)} />;
};

// Start on any hole (Oct 15, 2026). Dennis: "Anyone could start on any hole; they would just
// tap that hole and start entering their scores, and you'd expect them to come around 18 and
// continue on 1." No setting — the start is read off the card. The scored holes form runs in
// playing order (10, 11, 12 … wrapping past 18 to 1); the start is where the longest run
// begins. Two equal runs — a stray score (hole 1 tapped by mistake, then 10) — go to the run
// holding `hint` (the hole most recently scored from empty), else to the run that does not
// begin on hole 1 (the card opens on 1, so a lone score there is the likely stray).
// Empty or full card → null: the caller keeps its last start.
const inferStartHole = (holes, hint) => {
  const on = new Set((holes || []).map(Number).filter(h => h >= 1 && h <= 18));
  if (on.size === 0 || on.size === 18) return null;
  const nx = (h) => (h % 18) + 1;
  const pv = (h) => (h === 1 ? 18 : h - 1);
  const runs = [];
  for (let h = 1; h <= 18; h++) {
    if (!on.has(h) || on.has(pv(h))) continue;   // only where a run begins
    const run = []; let x = h;
    while (on.has(x) && run.length < 18) { run.push(x); x = nx(x); }
    runs.push(run);
  }
  const best = Math.max(...runs.map(r => r.length));
  const top = runs.filter(r => r.length === best);
  const pick = top.find(r => hint != null && r.includes(Number(hint))) || top.find(r => r[0] !== 1) || top[0];
  return pick[0];
};
// The card's start, held across renders: an assigned hole (shotgun) wins; otherwise the
// start read off the scored holes. A full card keeps the last start (finishing on hole 9
// must not flip the order back to 1–18 and lose "Save round"); an empty card has none
// (the caller walks 1→18 and leaves every hole open). Reset when the round changes.
const useCardStart = (roundKey, holes, assigned) => {
  const mem = useRef({ key: undefined, seen: [], hint: null, start: null });
  const m = mem.current;
  if (m.key !== roundKey) { m.key = roundKey; m.seen = []; m.hint = null; m.start = null; }
  const added = holes.filter(h => !m.seen.includes(h));
  if (added.length === 1) m.hint = added[0];   // one hole just went from empty to scored
  m.seen = holes;
  const read = inferStartHole(holes, m.hint);
  if (read) m.start = read; else if (holes.length === 0) { m.start = null; m.hint = null; }
  return (assigned >= 1 && assigned <= 18) ? Number(assigned) : (m.start || 1);
};

const TeamScoring = ({ token, playerData, teetime, onBack, onClose, backLabel = '← Rounds' }) => {
  const { addToast } = useToast();
  const teetimeId = teetime?.id;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);       // { code, message } when the server refuses the card
  const [scorecard, setScorecard] = useState(null);       // /scoring/scorecard/:id — course, tees, hole data
  const [holeData, setHoleData] = useState([]);           // par / stroke index / hole count for the TEAM's tee (team_tee)
  const [team, setTeam] = useState(null);                 // /scoring/team/:id — members, claim, scores, banner meta
  const [scores, setScores] = useState({});               // { [hole]: gross } — the team card as displayed
  const [dirty, setDirty] = useState({});                 // { [hole]: true } — changed locally, not yet saved
  const [currentHole, setCurrentHole] = useState(1);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [netDown, setNetDown] = useState(false);          // browser says online but the last save/refresh could not reach the server
  const [takeoverConfirm, setTakeoverConfirm] = useState(false);
  const [lostCardTo, setLostCardTo] = useState(null);     // name of the teammate who took the card from me
  const [showScoreRequired, setShowScoreRequired] = useState(false);
  const [showBoard, setShowBoard] = useState(false);      // 🏆 team leaderboard (LeaderboardModal — the shared renderer, not a new one)

  const claim = team?.claim || null;
  // Finalized (Sept 15, 2026): once the organizer finalizes the Captain's Choice
  // game the card is read-only for everyone — the server refuses writes too (409
  // ROUND_FINALIZED) — and points at the results. Read from the same team_game the
  // banner uses, so the card and the code page agree on what "final" means.
  const finalized = !!(team?.team_game && team.team_game.status === 'completed');
  const isHolder = !!(claim && claim.is_me);
  const heldByOther = !!(claim && !claim.is_me);
  const canWrite = !heldByOther && !finalized;            // unclaimed → the first save claims it (server policy); never after finalize
  const dirtyCount = Object.keys(dirty).length;

  // Refs so timers and the visibility handler always see the latest values
  // without re-subscribing on every keystroke (same pattern as LiveScoring).
  const scoresRef = React.useRef(scores);   scoresRef.current = scores;
  const dirtyRef = React.useRef(dirty);     dirtyRef.current = dirty;
  const canWriteRef = React.useRef(canWrite); canWriteRef.current = canWrite;
  const isHolderRef = React.useRef(isHolder); isHolderRef.current = isHolder;
  const savingRef = React.useRef(false);

  const OFFLINE_KEY = teetimeId ? `tto_team_offline_${teetimeId}` : null;
  const saveOffline = (nextScores, nextDirty) => {
    if (!OFFLINE_KEY) return;
    try {
      const keep = {};
      Object.keys(nextDirty).forEach(h => { keep[h] = nextScores[h] > 0 ? nextScores[h] : null; });
      if (Object.keys(keep).length === 0) localStorage.removeItem(OFFLINE_KEY);
      else localStorage.setItem(OFFLINE_KEY, JSON.stringify({ scores: keep, ts: Date.now() }));
    } catch (e) { /* storage unavailable — recovery degrades gracefully */ }
  };
  const readOffline = () => {
    if (!OFFLINE_KEY) return null;
    try { const raw = localStorage.getItem(OFFLINE_KEY); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
  };
  const clearOffline = () => { if (OFFLINE_KEY) { try { localStorage.removeItem(OFFLINE_KEY); } catch (e) {} } };

  // ---- helpers -------------------------------------------------------------
  const scoresFromServer = (rows) => {
    const s = {};
    (rows || []).forEach(r => { if (r.gross_score > 0) s[r.hole_number] = r.gross_score; });
    return s;
  };

  // Team label: last names A→Z (findable), first initial only on a collision (§6.4).
  const teamLabel = (members) => {
    const parts = (members || []).map(m => {
      const t = String(m.name || '').trim().split(/\s+/);
      return { first: t[0] || '', last: t.length > 1 ? t[t.length - 1] : (t[0] || '') };
    });
    const sorted = parts.slice().sort((a, b) => a.last.localeCompare(b.last));
    return sorted.map(p => {
      const dup = sorted.filter(q => q.last.toLowerCase() === p.last.toLowerCase()).length > 1;
      return dup && p.first ? `${p.first[0]}. ${p.last}` : p.last;
    }).join(' / ');
  };

  // Shotgun-aware hole order: the team's starting hole first, wrapping past 18.
  // No starting hole set (Oct 15, 2026): the first hole the card holder scores is the start.
  const holeOrderFrom = (sh) => { const s = (sh >= 1 && sh <= 18) ? sh : 1; return Array.from({ length: 18 }, (_, i) => ((s - 1 + i) % 18) + 1); };
  const teamCardHoles = Object.keys(scores).map(Number).filter(h => scores[h] > 0).sort((a, b) => a - b);
  const startHole = useCardStart(teetimeId, teamCardHoles, team?.starting_hole);
  const holeOrder = holeOrderFrom(startHole);
  const nextHoleOf = (h) => { const i = holeOrder.indexOf(h); return i < 0 || i === 17 ? null : holeOrder[i + 1]; };
  const prevHoleOf = (h) => { const i = holeOrder.indexOf(h); return i <= 0 ? null : holeOrder[i - 1]; };

  const getScoreLabel = (score, par) => { if (!score || !par) return ''; const d = score - par; if (d <= -3) return 'Albatross'; if (d === -2) return 'Eagle'; if (d === -1) return 'Birdie'; if (d === 0) return 'Par'; if (d === 1) return 'Bogey'; if (d === 2) return 'Dbl Bogey'; return '+' + d; };
  const getScoreBg = (score, par) => { if (!score || !par) return '#e5e7eb'; const d = score - par; if (d <= -2) return '#fbbf24'; if (d === -1) return '#dc2626'; if (d === 0) return '#166534'; if (d === 1) return '#3b82f6'; return '#1e3a5f'; };
  const formatDate = (ds) => { if (!ds) return ''; const d = new Date(ds.split('T')[0] + 'T12:00:00'); if (isNaN(d.getTime())) return ds; return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };

  // ---- load ----------------------------------------------------------------
  const fetchTeam = async () => {
    const resp = await fetch(`${API_URL}/player/scoring/team/${teetimeId}?token=${token}`);
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) { const err = new Error(data.message || data.error || 'Could not load the team card'); err.code = data.code || null; err.status = resp.status; throw err; }
    return data;
  };

  useEffect(() => {
    if (!teetimeId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true); setLoadError(null);
        const cardResp = await fetch(`${API_URL}/player/scoring/scorecard/${teetimeId}?token=${token}`);
        if (!cardResp.ok) throw Object.assign(new Error('Could not load the course card'), { code: 'SCORECARD' });
        const card = await cardResp.json();
        const t = await fetchTeam();
        // Par / stroke index come from the TEAM's tee (server rule, same as the board) —
        // never from the viewer's preferred tee, which is what the scorecard's
        // default_tee / hole_data are. Fetch that tee's holes when it differs; fall
        // back to the scorecard's default tee if the team's tee has no hole data here.
        const teamTee = t.team_tee && card.tees?.find(x => x.id === t.team_tee.id && x.has_hole_data);
        const teeToUse = teamTee || card.default_tee || card.tees?.find(x => x.has_hole_data);
        let holes = card.hole_data || [];
        if (teeToUse && !(card.default_tee && teeToUse.id === card.default_tee.id)) {
          try {
            const hr = await fetch(`${API_URL}/player/scoring/holes/${teeToUse.id}?token=${token}`);
            if (hr.ok) holes = await hr.json();
          } catch (e) { /* keep the default tee's holes — par is the same on almost every men's tee */ }
        }
        if (cancelled) return;
        setScorecard(card);
        setHoleData(holes);
        setTeam(t);
        let s = scoresFromServer(t.scores);
        let d = {};
        // Recover unsaved holes from a previous session on this device — but only if
        // nobody else has taken the card since (their card is the truth then).
        const off = readOffline();
        if (off && off.scores && !(t.claim && !t.claim.is_me)) {
          Object.entries(off.scores).forEach(([h, v]) => { if (v > 0) { s[h] = v; d[h] = true; } });
        } else if (off) { clearOffline(); }
        setScores(s); setDirty(d);
        setLastSyncAt(Date.now());
        // Start on the first hole (in start-hole order) that has no score yet.
        const sh = (t.starting_hole >= 1 && t.starting_hole <= 18) ? t.starting_hole
          : (inferStartHole(Object.keys(s).filter(h => s[h] > 0)) || 1);   // Oct 15: read off the card
        const order = Array.from({ length: 18 }, (_, i) => ((sh - 1 + i) % 18) + 1);
        const firstEmpty = order.find(h => !(s[h] > 0));
        setCurrentHole(firstEmpty || sh);
      } catch (e) {
        if (!cancelled) setLoadError({ code: e.code || null, message: e.message || 'Could not load the team card' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teetimeId, token]);

  // ---- poll: claim + scores every 20s -------------------------------------
  // Non-holders get the live card (server truth replaces local). The holder keeps
  // local dirty holes but still merges the rest and, above all, learns immediately
  // if a teammate took the card over.
  const applyTeamUpdate = (t) => {
    const wasHolder = isHolderRef.current;
    const nowHeldByOther = !!(t.claim && !t.claim.is_me);
    setTeam(t);
    setLastSyncAt(Date.now());
    if (nowHeldByOther) {
      if (wasHolder) { setLostCardTo(t.claim.scorer_name || 'A teammate'); }
      // Their card is the truth now — drop local edits (they cannot be written anyway).
      setScores(scoresFromServer(t.scores)); setDirty({}); clearOffline();
    } else {
      // Merge: server values for every hole we have not changed locally.
      const server = scoresFromServer(t.scores);
      setScores(prev => { const merged = { ...server }; Object.keys(dirtyRef.current).forEach(h => { if (prev[h] > 0) merged[h] = prev[h]; else delete merged[h]; }); return merged; });
      if (wasHolder || t.claim) setLostCardTo(null);
    }
  };
  useEffect(() => {
    if (!teetimeId || !token || loading || loadError) return;
    let cancelled = false;
    const tick = async () => { if (cancelled || savingRef.current) return; try { const t = await fetchTeam(); if (!cancelled) { applyTeamUpdate(t); setNetDown(false); } } catch (e) { /* keep last-known */ } };
    const id = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teetimeId, token, loading, loadError]);

  // ---- save (dirty holes only) --------------------------------------------
  // postScores is the single write path; saveDirty and takeOver build its payload.
  const postScores = async (payload, holesCovered, { takeover = false, quiet = false, successMessage = null } = {}) => {
    // Offline: don't even try — keep the holes on the phone and let Prev/Next move
    // at once. The 'online' handler and the 30s auto-save upload them later.
    // (Dennis, Sept 13: "a little slow to move to the next hole" in airplane mode.)
    if (!navigator.onLine && !takeover) {
      saveOffline(scoresRef.current, dirtyRef.current);
      return false;
    }
    // Reachability is not the same as navigator.onLine (wifi icon, no data): cap
    // the wait so a dead link never pins the card on "Saving..." for a minute.
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 8000);
    try {
      savingRef.current = true; setSaving(true);
      const resp = await fetch(`${API_URL}/player/scoring/team?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teetime_id: teetimeId, scores: payload, takeover: !!takeover }),
        signal: abort.signal
      });
      const data = await resp.json().catch(() => ({}));
      setNetDown(false);
      if (resp.ok) {
        setDirty(prev => { const n = { ...prev }; holesCovered.forEach(h => delete n[h]); return n; });
        clearOffline();
        setLastSavedAt(Date.now());
        if (!quiet) addToast(successMessage || (takeover ? 'You have the card' : 'Team score saved'), 'success');
        try {
          const t = await fetchTeam(); applyTeamUpdate(t);
          if (takeover) {
            // Continue where the previous holder left off: the team's first unscored
            // hole in start-hole order (Dennis, Sept 13: after a take-over the card
            // stayed on hole 1 and the next score landed there).
            const s = scoresFromServer(t.scores);
            const order = holeOrderFrom(t.starting_hole || inferStartHole(Object.keys(s).filter(h => s[h] > 0)));
            setCurrentHole(order.find(h => !(s[h] > 0)) || order[17]);
          }
        } catch (e) {}
        return true;
      }
      if (data.code === 'CARD_CLAIMED') {
        // Someone else holds it now. Refresh so the banner shows who; local edits stay
        // visible so the player can decide to take over (or let it go).
        try { const t = await fetchTeam(); applyTeamUpdate(t); } catch (e) {}
        if (!quiet) addToast(data.message || 'Someone else is keeping this card', 'error');
        return false;
      }
      if (!quiet) addToast(data.message || data.error || 'Save failed', 'error');
      return false;
    } catch (e) {
      // Unreachable (or timed out): keep the local copy; the online handler and the
      // 30s auto-save retry. The banner above the card says so — no toast needed.
      saveOffline(scoresRef.current, dirtyRef.current);
      setNetDown(true);
      if (takeover && !quiet) addToast("Couldn't reach the server to take over the card — try again when you have signal", 'error');
      return false;
    } finally {
      clearTimeout(timer);
      savingRef.current = false; setSaving(false);
    }
  };

  const saveDirty = async ({ quiet = false, successMessage = null } = {}) => {
    if (!teetimeId) return false;
    const holes = Object.keys(dirtyRef.current).map(Number);
    if (holes.length === 0) return true;
    if (!canWriteRef.current) return false;
    const payload = holes.map(h => ({ hole_number: h, gross_score: scoresRef.current[h] > 0 ? scoresRef.current[h] : null }));
    return postScores(payload, holes, { quiet, successMessage });
  };

  // Online/offline + retry, 30s auto-save, and a localStorage mirror on phone lock.
  useEffect(() => {
    const on = () => {
      setIsOnline(true);
      if (Object.keys(dirtyRef.current).length > 0 && canWriteRef.current) {
        const n = Object.keys(dirtyRef.current).length;
        saveDirty({ successMessage: `Back online — ${n} hole${n === 1 ? '' : 's'} uploaded` });
      }
    };
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teetimeId, token]);
  useEffect(() => {
    const id = setInterval(() => { if (Object.keys(dirtyRef.current).length > 0 && canWriteRef.current && !savingRef.current) saveDirty({ quiet: true }); }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teetimeId, token]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'hidden') saveOffline(scoresRef.current, dirtyRef.current); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teetimeId]);

  // ---- entry ---------------------------------------------------------------
  const setHoleScore = (h, value) => {
    if (!canWrite) return;
    setScores(prev => { const n = { ...prev }; if (value > 0) n[h] = value; else delete n[h]; saveOffline(n, { ...dirtyRef.current, [h]: true }); return n; });
    setDirty(prev => ({ ...prev, [h]: true }));
  };
  const bump = (h, delta) => {
    const hd = holeData.find(x => x.hole_number === h);
    const cur = scores[h] > 0 ? scores[h] : (hd?.par || 0);   // first tap starts from par
    setHoleScore(h, Math.max(1, cur + delta));
  };
  const tapPar = (h, par) => { if (!par) return; setHoleScore(h, scores[h] === par ? 0 : par); };

  const takeOver = async () => {
    setTakeoverConfirm(false);
    // Refresh first so anything we re-assert is the latest the previous holder wrote.
    let fresh = null;
    try { fresh = await fetchTeam(); setTeam(fresh); } catch (e) {}
    const serverScores = fresh ? scoresFromServer(fresh.scores) : scoresRef.current;
    const holes = Object.keys(dirtyRef.current).map(Number);
    let payload;
    if (holes.length > 0) {
      payload = holes.map(h => ({ hole_number: h, gross_score: scoresRef.current[h] > 0 ? scoresRef.current[h] : null }));
    } else {
      // Nothing new to write: the write path claims on write, so re-assert the
      // current hole's server value (idempotent) purely to move the claim.
      if (fresh) setScores(serverScores);
      payload = [{ hole_number: currentHole, gross_score: serverScores[currentHole] > 0 ? serverScores[currentHole] : null }];
    }
    setLostCardTo(null);
    await postScores(payload, holes, { takeover: true });
  };

  const leave = async (fn) => { if (canWrite && dirtyCount > 0) await saveDirty({ quiet: true }); fn(); };

  // ---- Yards-to-Center (same DB-driven block as LiveScoring; group-level, so it
  //      belongs on the team card — §6.7 keeps it) --------------------------
  const YTC_MAX_YARDS = 600, YTC_ACCURACY_LIMIT_M = 20;
  const ytcDemo = !!(playerData?.email && playerData.email.includes('@demo.teetimeorganizer.net'));
  const YTC_DEMO_YARDS = 138;
  const [ytcGreens, setYtcGreens] = useState(null);
  const [ytcYards, setYtcYards] = useState(null);
  const [ytcStatus, setYtcStatus] = useState('starting');
  const [ytcAcc, setYtcAcc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (ytcDemo) { setYtcGreens({ demo: true }); return; }
    if (!teetimeId || !token) { setYtcGreens(null); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/player/scoring/green-coordinates?token=${token}&teetime_id=${teetimeId}`);
        if (!r.ok) { if (!cancelled) setYtcGreens(null); return; }
        const d = await r.json();
        if (!cancelled) setYtcGreens(d && d.eligible ? d.coordinates : null);
      } catch (e) { if (!cancelled) setYtcGreens(null); }
    })();
    return () => { cancelled = true; };
  }, [teetimeId, token, ytcDemo]);
  const ytcEnabled = ytcGreens != null;
  useEffect(() => {
    if (ytcDemo) { setYtcStatus('ok'); setYtcYards(YTC_DEMO_YARDS); setYtcAcc(null); return; }
    if (!ytcEnabled) { setYtcStatus('off'); setYtcYards(null); return; }
    const green = ytcGreens[currentHole];
    if (!green) { setYtcStatus('nodata'); setYtcYards(null); return; }
    if (!('geolocation' in navigator)) { setYtcStatus('nofix'); return; }
    const yardsBetween = (a, b) => {
      const R = 6371000, toRad = d => d * Math.PI / 180;
      const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon);
      const hv = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat))*Math.cos(toRad(b.lat))*Math.sin(dLon/2)**2;
      return 2 * R * Math.asin(Math.sqrt(hv)) * 1.09361;
    };
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const acc = pos.coords.accuracy; setYtcAcc(acc);
        if (acc > YTC_ACCURACY_LIMIT_M) { setYtcStatus('coarse'); setYtcYards(null); return; }
        const y = Math.round(yardsBetween({ lat: pos.coords.latitude, lon: pos.coords.longitude }, green));
        if (y > YTC_MAX_YARDS) { setYtcStatus('range'); setYtcYards(null); return; }
        setYtcStatus('ok'); setYtcYards(y);
      },
      (err) => { setYtcStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'nofix'); setYtcYards(null); },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [ytcEnabled, ytcGreens, currentHole, ytcDemo]);

  // ---- derived -------------------------------------------------------------
  const currentHoleData = holeData.find(h => h.hole_number === currentHole);
  const currentScore = scores[currentHole] > 0 ? scores[currentHole] : 0;
  const coursePar = holeData.reduce((s, h) => s + (h.par || 0), 0);
  // Captain's Choice game on this round (server-computed; null = no game → gross card).
  const teamGame = team?.team_game || null;
  const netGame = !!(teamGame && teamGame.basis === 'net');
  const teamHcp = netGame && teamGame.team_handicap != null ? teamGame.team_handicap : null;
  const teamStrokesOn = (h) => (teamHcp != null && teamGame.strokes_by_hole) ? (teamGame.strokes_by_hole[h] ?? 0) : 0;
  const totals = (() => {
    let gross = 0, played = 0, parPlayed = 0, strokesPlayed = 0;
    for (let h = 1; h <= 18; h++) { const g = scores[h]; if (g > 0) { played++; gross += g; strokesPlayed += teamStrokesOn(h); const hd = holeData.find(x => x.hole_number === h); if (hd) parPlayed += hd.par; } }
    // Net = gross − strokes on the holes played; net vs par uses the SAME par as gross
    // (the selected tee's), so the two boxes are always comparable on this card.
    return { gross, played, parPlayed, vsPar: gross - parPlayed, strokes: strokesPlayed, net: gross - strokesPlayed, netVsPar: gross - strokesPlayed - parPlayed };
  })();
  const fmtVsPar = (v) => (v === 0 ? 'E' : (v > 0 ? '+' : '') + v);
  const vsParText = totals.played === 0 ? 'E' : fmtVsPar(totals.vsPar);
  const netVsParText = totals.played === 0 ? 'E' : fmtVsPar(totals.netVsPar);
  const teamHcpReason = teamGame ? ({
    missing_handicap: 'a player has no handicap on file',
    no_hole_data: 'no stroke index for this course',
    no_members: 'no team',
  }[teamGame.handicap_reason] || null) : null;
  const currentStrokes = teamStrokesOn(currentHole);
  const members = team?.members || [];
  const label = teamLabel(members);
  const groupText = team ? `Team ${team.pairing_group}${team.group_label ? ' ' + team.group_label : ''}` : '';
  // Named nines (Oct 8, 2026): "Burn Kill 3" — the course's two nines come with the tee time.
  const cardNines = ninesOf(scorecard?.tee_time) || ninesOf(teetime);
  const startText = team && team.starting_hole ? `Start: ${startHoleText(team.starting_hole, team.group_label, cardNines)}` : (team?.shotgun_mode ? 'Shotgun — starting hole not set' : '');
  const syncText = lastSyncAt ? new Date(lastSyncAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';

  // ---- RENDER --------------------------------------------------------------
  if (loading) return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>Loading team card...</div>;

  if (loadError || !scorecard || !team) {
    const friendly = {
      PAIRINGS_NOT_SET: 'Your organizer has not set the teams (pairings) for this round yet. Check back once the tee sheet is out.',
      NOT_TEAM_SCORED: 'This round is not set to team scoring.',
      TEAM_SCORING_DISABLED: 'Team scoring is not switched on for this group.',
    };
    return (
      <div style={{ maxWidth: 500, margin: '0 auto', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>👥 Team Card</h2>
          <button onClick={onBack} style={{ padding: '10px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>← Back</button>
        </div>
        <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 14, padding: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>Can't open the team card yet</div>
          <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5 }}>{(loadError && (friendly[loadError.code] || loadError.message)) || 'Something went wrong loading this round.'}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: '10px 14px', userSelect: 'none' }}>
      <TeamCelebrations token={token} teetimeId={teetimeId} />

      {/* Take-over confirm */}
      {takeoverConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 320, width: '100%', textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 26 }}>🃏</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 6 }}>Take over the card?</div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5, marginBottom: 20 }}>
              {claim?.scorer_name || 'A teammate'} is keeping the team card right now. If you take over, you enter the scores from here on and they become a viewer.
            </div>
            <button onClick={takeOver}
              style={{ width: '100%', padding: '13px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}>
              Yes, I'll keep the card
            </button>
            <button onClick={() => setTakeoverConfirm(false)}
              style={{ width: '100%', padding: '12px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Keep watching
            </button>
          </div>
        </div>
      )}

      {/* Score-required nudge (Next is blocked until the current hole has a score) */}
      {showScoreRequired && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 }}
          onClick={() => setShowScoreRequired(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 320, width: '100%', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 6 }}>Enter the team score first</div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5, marginBottom: 16 }}>{holeText(currentHole, cardNines)} has no team score yet. Tap the box for par, or use − and +.</div>
            <button onClick={() => setShowScoreRequired(false)} style={{ width: '100%', padding: '12px', background: '#1f2937', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>OK</button>
          </div>
        </div>
      )}

      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{scorecard.tee_time.course_name}</div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>
            Par {coursePar} • {formatDate(scorecard.tee_time.date)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: isOnline ? '#22c55e' : '#ef4444', display: 'inline-block' }}></span>
          {dirtyCount > 0 && canWrite && <span style={{ fontSize: 14, color: '#f59e0b' }} title="Unsaved holes">⏳</span>}
          <button onClick={() => leave(onBack)} style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>{backLabel}</button>
          <button onClick={() => leave(onClose)} style={{ padding: '6px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Exit</button>
        </div>
      </div>

      {/* Team banner */}
      <div style={{ background: '#14532d', color: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 8 }}>
        {team.event_name && <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>📅 {team.event_name}</div>}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.25 }}>👥 {label || groupText}</div>
          <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.9, whiteSpace: 'nowrap' }}>{groupText}{startText ? ` • ${startText}` : ''}</div>
        </div>
        {/* Team handicap line (net Captain's Choice game only). "net" here is the team's
            running net vs par — the number the 🏆 board ranks on — so the card and the
            board can never look like they disagree (Dennis, Sept 14: card said E, board
            said −1). An organizer override is named, never asterisked: on this card the
            asterisk means "stroke hole", as on a printed card. */}
        {netGame && (
          <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700 }}>
            {teamHcp != null ? (
              <>
                Team hcp {teamHcp}{teamGame.handicap_source === 'override' ? ' (set by organizer)' : ''} · net {totals.played > 0 ? netVsParText : '—'}
                <span style={{ fontWeight: 500, opacity: 0.85 }}>
                  {totals.played > 0 ? ` · ${totals.strokes} stroke${totals.strokes === 1 ? '' : 's'} so far` : ' · strokes on the * holes'}
                </span>
              </>
            ) : (
              <>Team hcp — <span style={{ fontWeight: 500, opacity: 0.85 }}>· net unavailable{teamHcpReason ? ` — ${teamHcpReason}` : ''}</span></>
            )}
          </div>
        )}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          {/* Final (Oct 22, 2026 — Dennis): nobody is keeping a locked card — say who scored it,
              and no Take over / claim prompts. */}
          {finalized && (
            <span style={{ fontSize: 13, fontWeight: 700, background: 'rgba(255,255,255,0.15)', borderRadius: 999, padding: '4px 10px' }}>🏁 Final{claim ? ` — scored by ${isHolder ? 'you' : (claim.scorer_name || 'a teammate')}` : ''}</span>
          )}
          {!finalized && isHolder && (
            <span style={{ fontSize: 13, fontWeight: 700, background: 'rgba(255,255,255,0.15)', borderRadius: 999, padding: '4px 10px' }}>🃏 You're keeping the card</span>
          )}
          {!finalized && heldByOther && (
            <>
              <span style={{ fontSize: 13, fontWeight: 700, background: 'rgba(255,255,255,0.15)', borderRadius: 999, padding: '4px 10px' }}>👁 {claim.scorer_name || 'A teammate'} is keeping the card</span>
              <button onClick={() => setTakeoverConfirm(true)} style={{ padding: '7px 12px', background: '#fff', color: '#14532d', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 800 }}>Take over</button>
            </>
          )}
          {!finalized && !claim && (
            <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>No one has the card yet — your first save claims it</span>
          )}
        </div>
      </div>

      {/* Offline / unreachable — persistent, so nobody stops scoring for lack of signal
          (Golf Genius shows the same reassurance; Dennis asked for it Sept 13). */}
      {(!isOnline || netDown) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '10px 12px', background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: 10 }}>
          <span style={{ fontSize: 16 }}>📵</span>
          <div style={{ flex: 1, fontSize: 13, color: '#633806', lineHeight: 1.4 }}>
            {canWrite ? (
              <>
                <strong>No signal — keep scoring.</strong> Your team scores are saved on this phone and will upload automatically when you're back online.
                {dirtyCount > 0 && <span> {dirtyCount} hole{dirtyCount === 1 ? '' : 's'} waiting to upload.</span>}
              </>
            ) : (
              <><strong>No signal.</strong> The team card will refresh when you're back online.</>
            )}
          </div>
        </div>
      )}

      {/* Round finalized — scores locked, results on the board (Dennis, Sept 15, 2026) */}
      {finalized && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10 }}>
          <span style={{ fontSize: 16 }}>🏁</span>
          <div style={{ flex: 1, fontSize: 13, color: '#14532d', lineHeight: 1.4 }}><strong>This round is final.</strong> Scores are locked — the results are on the leaderboard.</div>
          <button onClick={() => setShowBoard(true)} style={{ padding: '7px 12px', background: '#166534', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap' }}>See results</button>
        </div>
      )}

      {lostCardTo && !finalized && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '10px 12px', background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: 10 }}>
          <span style={{ fontSize: 16 }}>🃏</span>
          <div style={{ flex: 1, fontSize: 13, color: '#633806' }}><strong>{lostCardTo}</strong> took over the card. You're viewing now; take it back any time.</div>
          <button onClick={() => setLostCardTo(null)} style={{ background: 'transparent', border: 'none', color: '#854F0B', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>OK</button>
        </div>
      )}

      {/* Running totals bar — THRU / TEAM GROSS / VS PAR (no ADJ, no PUTTS: one ball, gross);
          + TEAM NET on a net Captain's Choice game (gross − strokes on the holes played) */}
      <div style={{ display: 'flex', justifyContent: 'space-around', background: '#1f2937', color: '#fff', borderRadius: 10, padding: '10px 6px', marginBottom: 10 }}>
        {[
          { label: 'THRU', val: totals.played },
          { label: 'TEAM GROSS', val: totals.gross || '—' },
          ...(teamHcp != null ? [{ label: 'TEAM NET', val: totals.played > 0 ? totals.net : '—' }] : []),
          { label: 'VS PAR', val: vsParText, color: totals.played > 0 && totals.vsPar < 0 ? '#f87171' : '#fff' }
        ].map((item, i) => (
          <div key={i} style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 600, letterSpacing: 0.5 }}>{item.label}</div>
            <div style={{ fontWeight: 800, fontSize: 22, color: item.color || '#fff', marginTop: 2 }}>{item.val}</div>
          </div>
        ))}
      </div>

      {/* Hole navigation — square boxes, colored by the team's result; an asterisk marks
          each hole where the team gets a stroke (net game), as on a printed card */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 3, marginBottom: 10, flexWrap: 'wrap' }}>
        {Array.from({ length: 18 }, (_, i) => i + 1).map(h => {
          const g = scores[h]; const hd = holeData.find(x => x.hole_number === h);
          const hasScore = g > 0; const isCurrent = currentHole === h;
          const st = teamStrokesOn(h);
          return (
            <button key={h} onClick={() => setCurrentHole(h)}
              title={st > 0 ? `Team gets ${st === 1 ? 'a stroke' : st + ' strokes'} on hole ${h}` : undefined}
              style={{
                width: 30, height: 30, borderRadius: 4, position: 'relative',
                border: isCurrent ? '3px solid #1f2937' : '1px solid #d1d5db',
                background: hasScore ? getScoreBg(g, hd?.par) : '#fff',
                color: hasScore ? '#fff' : '#374151',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>{h}{st > 0 && <span style={{ position: 'absolute', top: -1, right: 2, fontSize: 12, lineHeight: 1, fontWeight: 900, color: hasScore ? '#fff' : '#16a34a' }}>{'*'.repeat(st)}</span>}</button>
          );
        })}
      </div>

      {/* Current hole card */}
      {currentHoleData && (
        <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 14, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            {cardNines && (
              <div data-testid="team-card-nine" style={{ fontSize: 15, fontWeight: 800, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>{currentHole <= 9 ? cardNines.front : cardNines.back}</div>
            )}
            <div style={{ fontSize: 40, fontWeight: 900, color: '#1f2937', lineHeight: 1 }}>
              Hole {cardNines && currentHole > 9 ? currentHole - 9 : currentHole}{currentStrokes > 0 && <span style={{ color: '#16a34a' }}>{'*'.repeat(currentStrokes)}</span>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, fontSize: 17, color: '#6b7280', marginTop: 8 }}>
              <span>Par <strong style={{ color: '#1f2937', fontSize: 19 }}>{currentHoleData.par}</strong></span>
              <span>HCP <strong>{currentHoleData.handicap_index}</strong></span>
            </div>
            {currentStrokes > 0 && (
              <div style={{ marginTop: 4, fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
                {'*'.repeat(currentStrokes)} Team gets {currentStrokes === 1 ? 'a stroke' : `${currentStrokes} strokes`} here
              </div>
            )}
            {ytcEnabled && (
              <div style={{ marginTop: 6 }}>
                {ytcStatus === 'ok' && ytcYards != null ? (
                  <span style={{ fontSize: 26, fontWeight: 800, color: '#374151' }}>
                    Yards to Center: {ytcYards}
                    {!ytcDemo && <span style={{ fontSize: 13, color: '#9ca3af', fontWeight: 400 }}>{' '}(±{ytcAcc ? Math.round(ytcAcc) : '?'}m)</span>}
                  </span>
                ) : (
                  <span style={{ fontSize: 17, color: '#6b7280' }}>
                    {{ starting: 'locating…', coarse: `GPS settling (±${ytcAcc ? Math.round(ytcAcc) : '?'}m)…`, denied: 'location off', nodata: `no data hole ${currentHole}`, nofix: 'no GPS', range: 'out of range (>600)', off: '' }[ytcStatus] || ''}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* TEAM SCORE — one box. Read-only (loud) for non-holders. */}
          <div style={{ textAlign: 'center', marginBottom: 8, opacity: canWrite ? 1 : 0.55, pointerEvents: canWrite ? 'auto' : 'none' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#6b7280', marginBottom: 8, letterSpacing: 1 }}>TEAM SCORE</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
              <button onClick={() => bump(currentHole, -1)} disabled={!canWrite}
                style={{ width: 64, height: 64, borderRadius: 12, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 32, fontWeight: 800, cursor: canWrite ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>−</button>
              <div onClick={() => canWrite && tapPar(currentHole, currentHoleData.par)}
                style={{
                  width: 96, height: 96, borderRadius: 14,
                  background: currentScore > 0 ? getScoreBg(currentScore, currentHoleData.par) : '#f3f4f6',
                  color: currentScore > 0 ? '#fff' : '#c9cdd3',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 48, fontWeight: 900, cursor: canWrite ? 'pointer' : 'default', lineHeight: 1, transition: 'all 0.15s ease'
                }}>
                {currentScore > 0 ? currentScore : (currentHoleData.par ? currentHoleData.par : '—')}
              </div>
              <button onClick={() => bump(currentHole, 1)} disabled={!canWrite}
                style={{ width: 64, height: 64, borderRadius: 12, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 32, fontWeight: 800, cursor: canWrite ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>+</button>
            </div>
            {currentScore > 0 && (
              <div style={{ marginTop: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: getScoreBg(currentScore, currentHoleData.par) }}>{getScoreLabel(currentScore, currentHoleData.par)}</span>
                {currentStrokes > 0 && (
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}> · net {currentScore - currentStrokes} ({getScoreLabel(currentScore - currentStrokes, currentHoleData.par)})</span>
                )}
              </div>
            )}
          </div>
          {/* Closest to the Pin on a Captain's Choice card (Sept 28, 2026): any teammate can
              record it for himself or a teammate — it is not the team score, so the card
              holder rule does not apply. */}
          <div style={{ marginBottom: 12 }}>
            <CtpClaimBar ctp={scorecard?.ctp} hole={currentHole} token={token} teetimeId={teetimeId} selfId={playerData?.id}
              options={[...members].sort((a, b) => (a.id === playerData?.id ? -1 : b.id === playerData?.id ? 1 : 0)).map(m => ({ id: m.id, name: m.name }))} />
          </div>
          {!canWrite && !finalized && (
            <div style={{ textAlign: 'center', fontSize: 12, color: '#6b7280', marginBottom: 12 }}>View only — {claim?.scorer_name || 'a teammate'} is entering the team score{syncText ? ` • updated ${syncText}` : ''}</div>
          )}
          {finalized && (
            <div style={{ textAlign: 'center', fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Final — this round's scores are locked</div>
          )}

          {/* Prev / Next — save on the move, like the individual card */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 8 }}>
            {(() => { const p = prevHoleOf(currentHole); return (
              <button onClick={async () => { if (canWrite) await saveDirty({ quiet: true }); if (p) setCurrentHole(p); }} disabled={!p}
                style={{ flex: 1, padding: '14px 0', background: !p ? '#f3f4f6' : '#fff', border: '2px solid #d1d5db', borderRadius: 10, cursor: !p ? 'default' : 'pointer', fontSize: 18, fontWeight: 700, color: !p ? '#d1d5db' : '#374151' }}>
                ← {p ? holeText(p, cardNines) : ''}</button>
            ); })()}
            {(() => { const n = nextHoleOf(currentHole); return (
              <button onClick={async () => {
                if (canWrite && currentScore === 0) { setShowScoreRequired(true); return; }
                if (canWrite) await saveDirty({ quiet: true });
                if (n) setCurrentHole(n);
              }} disabled={!n}
                style={{ flex: 1, padding: '14px 0', background: !n ? '#f3f4f6' : '#166534', border: 'none', borderRadius: 10, cursor: !n ? 'default' : 'pointer', fontSize: 18, fontWeight: 700, color: !n ? '#d1d5db' : '#fff' }}>
                {n ? holeText(n, cardNines) : ''} →</button>
            ); })()}
          </div>
        </div>
      )}

      {/* Save row */}
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        {finalized ? (
          <button onClick={() => setShowBoard(true)}
            style={{ flex: 1, padding: '14px 0', background: '#166534', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>
            🏁 Round is final — see results
          </button>
        ) : canWrite ? (
          <button onClick={() => saveDirty()} disabled={saving || dirtyCount === 0}
            style={{ flex: 1, padding: '14px 0', background: saving ? '#9ca3af' : (dirtyCount === 0 ? '#d1d5db' : '#166534'), color: dirtyCount === 0 && !saving ? '#6b7280' : '#fff', border: 'none', borderRadius: 10, cursor: (saving || dirtyCount === 0) ? 'default' : 'pointer', fontSize: 16, fontWeight: 700 }}>
            {saving ? 'Saving...' : dirtyCount === 0 ? (lastSavedAt ? '✓ Saved' : 'Save Team Score') : `Save Team Score${dirtyCount > 1 ? ` (${dirtyCount} holes)` : ''}`}
          </button>
        ) : (
          <button onClick={() => setTakeoverConfirm(true)}
            style={{ flex: 1, padding: '14px 0', background: '#fff', color: '#14532d', border: '2px solid #14532d', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>
            🃏 Take over the card
          </button>
        )}
      </div>
      {/* Team leaderboard — opens the shared LeaderboardModal (team-aware since Sept 14, 2026) */}
      <button onClick={() => setShowBoard(true)}
        style={{ width: '100%', marginTop: 10, padding: '12px 0', background: '#fff', color: '#1f2937', border: '2px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>
        🏆 Leaderboard
      </button>
      {showBoard && (
        <LeaderboardModal token={token} teetimeId={teetimeId} viewerId={playerData?.id} onClose={() => setShowBoard(false)} />
      )}
      <div style={{ textAlign: 'center', padding: '10px 0 4px', fontSize: 12, color: '#9ca3af' }}>
        {finalized ? 'This round has been finalized • nothing posts to GHIN' : canWrite ? 'One score per hole for the whole team • nothing posts to GHIN' : 'Updates every 20 seconds while a teammate keeps the card'}
      </div>
    </div>
  );
};


// ============================================================================
// TeamCodePage — /t and /t/:code (D13 printed team codes, Sept 15, 2026).
// The PUBLIC door for a team that has no personal TTO link: the QR on the
// scorecard opens https://ttogolf.com/<code> → this page with the code in the
// URL; the typed fallback is the six digits at /t. Flow: look the code up →
// confirmation (event, course, date, team, names, start hole) → "Who's keeping
// the card?" (one tap on a name — identity required, so claims / take-over /
// entered_by work exactly as for a TTO link) → the same TeamScoring card, with
// the scoped team token passed as its `token`. The session lives in
// localStorage until the code's window closes, so a locked phone or a closed
// tab comes straight back to the card; scanning a DIFFERENT team's code on the
// same phone replaces it. Design notes in teamAccessService.js.
// ============================================================================
const TEAM_SESSION_KEY = 'tto_team_session';
const readTeamSession = () => {
  try {
    const raw = localStorage.getItem(TEAM_SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || !s.token || !s.expires_at || new Date(s.expires_at).getTime() <= Date.now()) { localStorage.removeItem(TEAM_SESSION_KEY); return null; }
    return s;
  } catch (e) { return null; }
};
const writeTeamSession = (s) => { try { localStorage.setItem(TEAM_SESSION_KEY, JSON.stringify(s)); } catch (e) {} };
const clearTeamSession = () => { try { localStorage.removeItem(TEAM_SESSION_KEY); } catch (e) {} };

const TeamCodePage = () => {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  // Event desk (Sept 17, 2026): a code with letters is an event desk code (ten
  // letters and digits); /t?desk=1 opens the desk-code box directly.
  const rawParam = String(codeParam || '');
  const deskParam = /[A-Za-z]/.test(rawParam) && rawParam.replace(/[^A-Za-z0-9]/g, '').length >= 8 ? rawParam.toUpperCase() : '';
  const deskEntry = !!deskParam || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('desk') === '1');
  const initialCode = deskParam ? '' : rawParam.replace(/\D/g, '').slice(0, 6);
  const [digits, setDigits] = useState(initialCode);
  const [step, setStep] = useState(deskEntry ? 'deskcode' : 'enter');   // 'enter' | 'pick' | 'card' | 'results' | 'deskcode' | 'desk'
  const [deskCode, setDeskCode] = useState(deskParam);
  const [deskName, setDeskName] = useState('');
  const liveDesk = readDeskSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);    // /api/t/lookup result (+ code)
  const [session, setSession] = useState(null);    // { token, expires_at, teetime_id, player, code, team, ... }
  const autoLooked = useRef(false);

  const fmtDate = (ds) => { if (!ds) return ''; const d = new Date(String(ds).slice(0, 10) + 'T12:00:00'); return isNaN(d.getTime()) ? ds : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };
  // Same label rule as the team card and the board: last names A→Z, initial only on a collision.
  const teamLabelOf = (members) => {
    const parts = (members || []).map(m => { const t = String(m.name || '').trim().split(/\s+/); return { first: t[0] || '', last: t.length > 1 ? t[t.length - 1] : (t[0] || '') }; });
    const sorted = parts.slice().sort((a, b) => a.last.localeCompare(b.last));
    return sorted.map(p => { const dup = sorted.filter(q => q.last.toLowerCase() === p.last.toLowerCase()).length > 1; return dup && p.first ? `${p.first[0]}. ${p.last}` : p.last; }).join(' / ');
  };
  const postJson = async (path, body) => {
    const resp = await fetch(`${API_URL}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const data = await resp.json().catch(() => ({}));
    return { ok: resp.ok, status: resp.status, data };
  };

  const lookup = async (c, back = 'enter') => {
    setBusy(true); setError(null);
    try {
      const r = await postJson('/t/lookup', { code: c });
      if (!r.ok) { setError(r.data.error || "That code didn't work. Check the six digits on your scorecard."); setStep(back); return; }
      setPreview({ ...r.data, code: c });
      if (r.data.kind === 'desk') { setStep('desk'); return; }
      // A finalized (or expired) code opens the RESULTS, not the card (Dennis, Sept 15):
      // the same scorecard, scanned in the bar afterwards, shows how the team finished.
      if (r.data.results_only) { goToResults(r.data); return; }
      setStep('pick');
    } catch (e) { setError("Couldn't reach the server. Check your signal and try again."); }
    finally { setBusy(false); }
  };
  const enter = async (playerId) => {
    if (!preview) return;
    setBusy(true); setError(null);
    try {
      const r = await postJson('/t/enter', { code: preview.code, player_id: playerId });
      if (!r.ok) {
        if (r.data.results_only) { goToResults({ ...preview, ...r.data }); return; }   // finalized between lookup and tap
        setError(r.data.error || 'Could not open the team card.');
        return;
      }
      const d = r.data;
      const s = { token: d.token, expires_at: d.expires_at, teetime_id: d.teetime_id, player: d.player, code: preview.code,
        date: d.date, course_name: d.course_name, event_name: d.event_name, org_name: d.org_name, team: d.team,
        scoring_mode: d.scoring_mode === 'individual' ? 'individual' : 'team' };
      writeTeamSession(s); setSession(s); setPreview(s); setStep('card');
    } catch (e) { setError("Couldn't reach the server. Check your signal and try again."); }
    finally { setBusy(false); }
  };
  const enterDesk = async () => {
    if (!preview) return;
    const nm = deskName.replace(/\s+/g, ' ').trim();
    if (nm.length < 2) { setError('Enter your name — the organizer sees who opened the desk.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await postJson('/t/desk-enter', { code: preview.code, name: nm });
      if (!r.ok) { setError(r.data.error || 'Could not open the event desk.'); return; }
      const d = r.data;
      const saved = saveDeskSession({ token: d.token, name: d.name, expires_at: d.expires_at, event_id: d.event_id, teetime_id: d.teetime_id,
        event_name: d.event_name, org_name: d.org_name, course_name: d.course_name, date: d.date, time: d.time });
      if (!saved) { setError('This browser is blocking site storage (a private window?). Open the desk in a normal window.'); return; }
      window.location.assign(DESK_PATH);
    } catch (e) { setError("Couldn't reach the server. Check the connection and try again."); }
    finally { setBusy(false); }
  };
  const leaveCard = () => { clearTeamSession(); setSession(null); setPreview(null); setDigits(''); setError(null); setStep('enter'); navigate('/t', { replace: true }); };
  // Results: show a one-line "final" screen, then the public leaderboard (/lb/:ttid —
  // no login, opens on the game's basis with places and the team drill-down).
  const resultsTimer = useRef(null);
  const goToResults = (d) => {
    setPreview(prev => ({ ...(prev || {}), ...d }));
    setStep('results');
    if (resultsTimer.current) clearTimeout(resultsTimer.current);
    if (d.leaderboard_path) resultsTimer.current = setTimeout(() => navigate(d.leaderboard_path), 1800);
  };
  useEffect(() => () => { if (resultsTimer.current) clearTimeout(resultsTimer.current); }, []);

  // Resume a live session on this phone (unless the URL carries a different team's
  // code); otherwise a code in the URL — the QR path — is looked up at once.
  useEffect(() => {
    if (deskParam) {
      if (!autoLooked.current) { autoLooked.current = true; lookup(deskParam, 'deskcode'); }
      return;
    }
    if (deskEntry) return;
    const s = readTeamSession();
    if (s && (!initialCode || s.code === initialCode)) { setSession(s); setPreview(s); setStep('card'); return; }
    if (initialCode.length === 6 && !autoLooked.current) { autoLooked.current = true; lookup(initialCode); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDigits = (v) => {
    const d = String(v || '').replace(/\D/g, '').slice(0, 6);
    setDigits(d); setError(null);
    if (d.length === 6 && !busy) lookup(d);   // six digits in → no button needed
  };

  if (step === 'card' && session) {
    // Individual round (Sept 28, 2026): the code is one per printed card; the name
    // tapped is the player whose own card opens (he can keep the others from there).
    if (session.scoring_mode === 'individual') {
      return (
        <ToastProvider>
          <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
            <LiveScoring token={session.token} playerData={{ id: session.player.id, name: session.player.name }}
              codeSession={{ teetimeId: session.teetime_id, onBack: () => setStep('pick') }} onClose={leaveCard} />
          </div>
        </ToastProvider>
      );
    }
    return (
      <ToastProvider>
        <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
          <TeamScoring token={session.token} playerData={{ id: session.player.id, name: session.player.name }} teetime={{ id: session.teetime_id }}
            backLabel="← Team" onBack={() => setStep('pick')} onClose={leaveCard} />
        </div>
      </ToastProvider>
    );
  }

  const p = preview;
  // Individual round → one card per GROUP and each player opens his own card.
  const isIndividual = !!(p && p.scoring_mode === 'individual');
  const pageTitle = step === 'desk' || step === 'deskcode' ? '🗝️ Event Desk'
    : isIndividual ? '⛳ Your Scorecard'
    : (p && p.scoring_mode === 'team') ? '👥 Team Card'
    : '⛳ Scorecard';
  const errBox = error ? <div style={{ marginTop: 10, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 14, lineHeight: 1.4 }}>{error}</div> : null;
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px', boxSizing: 'border-box' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.5, color: '#6b7280' }}>TEE TIME ORGANIZER</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#14532d', marginTop: 4 }}>{pageTitle}</div>
        </div>

        {step === 'deskcode' && (
          <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 16, padding: 22 }} data-testid="desk-code-step">
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>Enter the event desk code</div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5, marginBottom: 14 }}>The organizer gave you a code of ten letters and numbers (like K7QM-4XPA-9R). It opens this event's dashboard — players, pairings, games, scores, printing and the TV.</div>
            <input autoFocus autoCapitalize="characters" autoComplete="off" spellCheck={false}
              value={deskCode} onChange={e => { setDeskCode(e.target.value.toUpperCase().replace(/[^A-Z0-9 -]/g, '').slice(0, 14)); setError(null); }}
              onKeyDown={e => { if (e.key === 'Enter' && deskCode.replace(/[^A-Z0-9]/g, '').length >= 10 && !busy) lookup(deskCode, 'deskcode'); }}
              placeholder="XXXX-XXXX-XX" aria-label="Event desk code"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 26, fontWeight: 800, letterSpacing: 3, textAlign: 'center', padding: '14px 10px', border: '2px solid #d1d5db', borderRadius: 12, outline: 'none', color: '#1f2937', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }} />
            {errBox}
            <button onClick={() => (deskCode.replace(/[^A-Z0-9]/g, '').length >= 10 ? lookup(deskCode, 'deskcode') : setError('The code has ten letters and numbers.'))} disabled={busy}
              style={{ width: '100%', marginTop: 14, padding: '14px 0', background: busy ? '#9ca3af' : '#166534', color: '#fff', border: 'none', borderRadius: 12, fontSize: 17, fontWeight: 800, cursor: busy ? 'default' : 'pointer' }}>
              {busy ? 'Checking…' : 'Continue'}
            </button>
            {liveDesk && (
              <button onClick={() => window.location.assign(DESK_PATH)}
                style={{ width: '100%', marginTop: 8, padding: '12px 0', background: '#fff', color: '#166534', border: '2px solid #166534', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                Back to the {liveDesk.event_name || 'event'} desk
              </button>
            )}
            <button onClick={() => { setStep('enter'); setError(null); }}
              style={{ width: '100%', marginTop: 8, padding: '10px 0', background: 'none', color: '#6b7280', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Have a six-digit scorecard code instead?
            </button>
          </div>
        )}

        {step === 'desk' && p && (
          <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 16, padding: 22 }} data-testid="desk-name-step">
            <div style={{ background: '#14532d', color: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85 }}>🗝️ Event desk</div>
              <div style={{ fontSize: 19, fontWeight: 800, marginTop: 2, lineHeight: 1.25 }}>{p.event_name}</div>
              <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>{p.course_name}{p.date ? ` • ${fmtDate(p.date)}` : ''}{p.org_name ? ` • ${p.org_name}` : ''}</div>
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>Your name</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10, lineHeight: 1.45 }}>The organizer sees who opened the desk.</div>
            <input autoFocus autoComplete="name" value={deskName} maxLength={60}
              onChange={e => { setDeskName(e.target.value); setError(null); }}
              onKeyDown={e => { if (e.key === 'Enter' && !busy) enterDesk(); }}
              placeholder="e.g. Jim — pro shop" aria-label="Your name"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 18, padding: '12px 12px', border: '2px solid #d1d5db', borderRadius: 12, outline: 'none', color: '#1f2937' }} />
            {errBox}
            <button onClick={enterDesk} disabled={busy}
              style={{ width: '100%', marginTop: 14, padding: '14px 0', background: busy ? '#9ca3af' : '#166534', color: '#fff', border: 'none', borderRadius: 12, fontSize: 17, fontWeight: 800, cursor: busy ? 'default' : 'pointer' }}>
              {busy ? 'Opening…' : 'Open the event desk'}
            </button>
            <button onClick={() => { setStep('deskcode'); setPreview(null); setError(null); }}
              style={{ width: '100%', marginTop: 8, padding: '12px 0', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Not this event? Enter a different code
            </button>
          </div>
        )}

        {step === 'enter' && (
          <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 16, padding: 22 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>Enter the code on your scorecard</div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5, marginBottom: 14 }}>The six digits printed on your scorecard or cart sign. Scanning the square with your phone's camera fills this in for you.</div>
            <input inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" autoFocus
              value={digits.length > 3 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : digits}
              onChange={e => onDigits(e.target.value)} placeholder="000 000" aria-label="Scorecard code"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 34, fontWeight: 800, letterSpacing: 6, textAlign: 'center', padding: '14px 10px', border: '2px solid #d1d5db', borderRadius: 12, outline: 'none', color: '#1f2937' }} />
            {errBox}
            <button onClick={() => (digits.length === 6 ? lookup(digits) : setError('Enter all six digits.'))} disabled={busy}
              style={{ width: '100%', marginTop: 14, padding: '14px 0', background: busy ? '#9ca3af' : '#166534', color: '#fff', border: 'none', borderRadius: 12, fontSize: 17, fontWeight: 800, cursor: busy ? 'default' : 'pointer' }}>
              {busy ? 'Checking…' : 'Open the card'}
            </button>
            <div style={{ marginTop: 14, fontSize: 12, color: '#9ca3af', textAlign: 'center', lineHeight: 1.5 }}>Have your Tee Time Organizer link? Your player app opens the same card — no code needed.</div>
            <button onClick={() => { setStep('deskcode'); setError(null); }}
              style={{ display: 'block', margin: '10px auto 0', background: 'none', border: 'none', color: '#166534', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Helping run the event? Enter an event desk code
            </button>
          </div>
        )}

        {step === 'results' && p && (
          <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 16, padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: 40, lineHeight: 1 }}>🏁</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#14532d', marginTop: 10 }}>
              {p.event_name || 'This round'} {p.results_only === 'expired' ? 'is over' : 'is final'}
            </div>
            <div style={{ fontSize: 14, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
              {p.course_name}{p.date ? ` • ${fmtDate(p.date)}` : ''}
            </div>
            <div style={{ fontSize: 14, color: '#374151', marginTop: 14 }}>Taking you to the results…</div>
            <button onClick={() => p.leaderboard_path && navigate(p.leaderboard_path)}
              style={{ width: '100%', marginTop: 14, padding: '14px 0', background: '#166534', color: '#fff', border: 'none', borderRadius: 12, fontSize: 17, fontWeight: 800, cursor: 'pointer' }}>
              🏆 See results
            </button>
          </div>
        )}

        {step === 'pick' && p && p.team && (
          <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 16, padding: 22 }}>
            <div style={{ background: '#14532d', color: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
              {p.event_name && <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85 }}>📅 {p.event_name}</div>}
              <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2, lineHeight: 1.25 }}>{isIndividual ? '⛳' : '👥'} {teamLabelOf(p.team.members)}</div>
              {/* Team name by the board's rule, then the start — never the group number, which read as a starting hole (Oct 10, 2026). */}
              {(p.team.team_label || p.team.starting_hole) ? <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>{[p.team.team_label, p.team.starting_hole ? `Start: ${startHoleText(p.team.starting_hole, p.team.group_label, ninesOf(p.course_nines))}` : null].filter(Boolean).join(' • ')}</div> : null}
              <div style={{ fontSize: 13, opacity: 0.9 }}>{p.course_name}{p.date ? ` • ${fmtDate(p.date)}` : ''}{p.org_name ? ` • ${p.org_name}` : ''}</div>
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>{isIndividual ? 'Who are you?' : "Who's keeping the card?"}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12, lineHeight: 1.45 }}>{isIndividual
              ? 'Tap your own name to open your card. To keep score for others in your group too, use + Score for another player on your card.'
              : "Tap your name. One person keeps the team's card; teammates can follow along or take it over."}</div>
            {p.team.members.map(m => (
              <button key={m.id} onClick={() => enter(m.id)} disabled={busy}
                style={{ width: '100%', padding: '14px 12px', marginBottom: 8, background: session && session.player && session.player.id === m.id ? '#f0fdf4' : '#fff', border: '2px solid #14532d', borderRadius: 12, fontSize: 17, fontWeight: 700, color: '#14532d', cursor: busy ? 'default' : 'pointer', textAlign: 'left' }}>
                {m.name}{session && session.player && session.player.id === m.id ? <span style={{ fontSize: 12, fontWeight: 600, color: '#166534', float: 'right' }}>you, last time</span> : null}
              </button>
            ))}
            {errBox}
            {session && (
              <button onClick={() => setStep('card')} style={{ width: '100%', marginTop: 8, padding: '12px 0', background: '#166534', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                Back to the card as {session.player.name}
              </button>
            )}
            <button onClick={() => { setStep('enter'); setPreview(session || null); setError(null); }}
              style={{ width: '100%', marginTop: 8, padding: '12px 0', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {isIndividual ? 'Not your group? Enter a different code' : 'Not your team? Enter a different code'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// DeskRoute — /desk (Event Desk, Sept 17, 2026). The organizer dashboard for the
// one event in this tab's desk session: Tee Time (that event's card only — the
// server narrows every list), Pairings and Scoring. A desk request the server
// refuses as expired / revoked (401 DESK_*) ends the session on screen.
// ============================================================================
const DeskEnded = ({ message }) => (
  <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
    <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 16, padding: 24, maxWidth: 420, width: '100%', textAlign: 'center' }} data-testid="desk-ended">
      <div style={{ fontSize: 40, lineHeight: 1 }}>🗝️</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#14532d', marginTop: 8 }}>Event desk</div>
      <div style={{ fontSize: 14, color: '#374151', marginTop: 8, lineHeight: 1.5 }}>{message || 'This browser tab is not signed in to an event desk.'}</div>
      <button onClick={() => { clearDeskSession(); window.location.assign('/t?desk=1'); }}
        style={{ width: '100%', marginTop: 16, padding: '14px 0', background: '#166534', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
        Enter the event code
      </button>
    </div>
  </div>
);

function DeskRoute() {
  const [session] = useState(() => readDeskSession());
  const [tab, setTab] = useState('organizer');
  const [ended, setEnded] = useState(null);
  useEffect(() => {
    if (!session) return undefined;
    const original = window.fetch;
    const watched = async (...args) => {
      const res = await original(...args);
      if (res && res.status === 401) {
        res.clone().json()
          .then((b) => { if (b && typeof b.code === 'string' && b.code.indexOf('DESK_') === 0) setEnded(b.error || 'Your event desk session has ended.'); })
          .catch(() => {});
      }
      return res;
    };
    window.fetch = watched;
    const timer = setInterval(() => { if (!readDeskSession()) setEnded('Your event desk session has ended. Enter the event code again.'); }, 60000);
    return () => { if (window.fetch === watched) window.fetch = original; clearInterval(timer); };
  }, [session]);
  useEffect(() => {
    if (session) document.title = `Event desk · ${session.event_name || 'Tee Time Organizer'}`;
  }, [session]);
  // Back button (Oct 10, 2026): see useDeskBack. One extra entry for /desk sits on top
  // of the history; Back pops to the entry below it (also /desk), and we put it back.
  useEffect(() => {
    if (!session) return undefined;
    const guard = () => {
      try { window.history.pushState({ ...(window.history.state || {}), ttoDesk: true }, '', window.location.href); } catch (e) { /* history blocked */ }
    };
    if (!(window.history.state && window.history.state.ttoDesk)) guard();
    const onPop = () => {
      if (!onDeskPage()) return;
      guard();
      const ev = new CustomEvent('deskBack', { detail: { handled: false } });
      window.dispatchEvent(ev);
      if (!ev.detail.handled) { setTab('organizer'); window.scrollTo({ top: 0, behavior: 'auto' }); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [session]);
  const leave = () => { clearDeskSession(); window.location.assign('/t?desk=1'); };
  if (!session || ended) return <DeskEnded message={ended} />;
  return (
    <DeskContext.Provider value={session}>
      <AuthenticatedApp tab={tab} setTab={setTab} onLogout={leave}
        user={{ name: session.name, email: null }}
        organization={{ id: null, name: session.org_name || '' }}
        organizations={[]}
        switchOrganization={async () => ({ success: false })}
        refreshOrganization={() => {}} />
    </DeskContext.Provider>
  );
}

// codeSession (Sept 28, 2026): the card opened from a printed scorecard code on an
// INDIVIDUAL round (TeamCodePage). { teetimeId, onBack }: no round list — the card
// opens straight on that round, "← Group" goes back to the name pick, and there is
// no Post to GHIN (the server refuses it for a code session too).
const LiveScoring = ({ token, playerData, onClose, onOpenProfile, codeSession = null }) => {
  const { addToast } = useToast();
  const [codeError, setCodeError] = useState(null);   // code session: why the card didn't open
  
  const [teetimes, setTeetimes] = useState([]);
  const [selectedTeetimeId, setSelectedTeetimeId] = useState(null);
  // Team scoring (Sept 13, 2026): a round with scoring_mode = 'team' hands off to the
  // TeamScoring card instead of the individual card. Held here (not in
  // selectedTeetimeId) so none of the individual-card effects run for it.
  const [teamTeetime, setTeamTeetime] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const liveNines = ninesOf(scorecard?.tee_time);   // named nines (Oct 8, 2026): "Burn Kill 3"
  const [selectedTeeId, setSelectedTeeId] = useState(null);
  const [holeData, setHoleData] = useState([]);
  const [currentHole, setCurrentHole] = useState(1);
  // Shotgun (Oct 10, 2026 — TeamScoring's rule): the player's starting hole first, wrapping
  // past 18. Next / Back walk this order; the hole strip stays the 1–18 card.
  // Not a shotgun (Oct 15, 2026): the first hole scored on the card is the start — see
  // inferStartHole / useCardStart and the order block above the backfill detection.
  const liveStartOf = (tt) => (tt && tt.shotgun_mode && tt.starting_hole >= 1 && tt.starting_hole <= 18) ? Number(tt.starting_hole) : null;
  const liveOrderFrom = (sh) => Array.from({ length: 18 }, (_, i) => ((sh - 1 + i) % 18) + 1);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scoresSaved, setScoresSaved] = useState(false);
  const [postingGhin, setPostingGhin] = useState(false);
  const [ghinPosted, setGhinPosted] = useState(false);
  const [showGhinConfirm, setShowGhinConfirm] = useState(false);
  const [showGhinNoCreds, setShowGhinNoCreds] = useState(false);
  const [showGhinTierLocked, setShowGhinTierLocked] = useState(false);
  const [ghinNotMappedMsg, setGhinNotMappedMsg] = useState(null);
  const [ghinLoginFailed, setGhinLoginFailed] = useState(null); // { code, message } from GHIN_INVALID_CREDENTIALS / GHIN_UNAVAILABLE (Sept 10, 2026)
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(false);
  const [scoringTab, setScoringTab] = useState('scorecard'); // 'scorecard' | 'results'
  const [liveStandings, setLiveStandings] = useState(null);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [standingsError, setStandingsError] = useState(null);
  const [expandedGames, setExpandedGames] = useState({});
  // Live Leaderboard (in-app spectator view) — Live Leaderboard Plan v1.0.
  // The Results tab is becoming Option B: a Games sub-tab (existing standings render)
  // + a Scoring sub-tab (this gross/net leaderboard). Step 1 wires the Scoring panel
  // in additively; resultsSubTab defaults to 'games' so existing behavior is unchanged
  // until step 2 makes the smart default + rename deliberate.
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [resultsSubTab, setResultsSubTab] = useState('games');     // 'games' | 'scoring'
  const [leaderboardView, setLeaderboardView] = useState('gross'); // 'gross' | 'net'
  // A board ranked on a stroke play game opens on the game's basis (Sept 23, 2026):
  // net game → Net, gross game → Gross. Runs once per board basis; the toggle still works.
  const lbGameBasis = leaderboard && boardGame(leaderboard) ? (boardOpensNet(leaderboard) ? 'net' : 'gross') : null;
  // A team board (a Best Ball game — Sept 27, 2026) opens on the basis it is ranked on.
  // Applied ONCE per game: a poll that falls back to the player board and back again
  // must not reset a viewer who chose the other basis (independent review, Sept 27).
  const lbTeamBasis = leaderboard && leaderboard.scoring_mode === 'team' ? (leaderboard.basis || 'gross') : null;
  const lbTeamBasisKey = lbTeamBasis ? `${leaderboard.game_id || leaderboard.anchor_teetime_id}:${lbTeamBasis}` : null;
  const lbTeamBasisApplied = useRef(null);
  useEffect(() => {
    if (lbGameBasis) { setLeaderboardView(lbGameBasis); return; }
    if (lbTeamBasisKey && lbTeamBasisApplied.current !== lbTeamBasisKey) {
      lbTeamBasisApplied.current = lbTeamBasisKey;
      setLeaderboardView(lbTeamBasis);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lbGameBasis, lbTeamBasisKey]);
  // Which player's scorecard drill-down is open (Player_Scorecard_Modal_Plan_v2_1).
  // Each of the three board renderers owns this state so PlayerScorecardModal can
  // stay stateless — it just takes a row and renders.
  const [openPlayerId, setOpenPlayerId] = useState(null);
  // Modal shown when player tries to leave a hole that has putts entered but no score
  // (or jumps to another hole without a score). Putts-only entries cannot be saved.
  const [showScoreRequired, setShowScoreRequired] = useState(false);
  // Picked up ("7x", Sept 27, 2026): who is blank when Next Hole is refused (names, or
  // [] for your own card), and the one-time "an X is a net double bogey" question.
  const [blankNames, setBlankNames] = useState([]);
  const [pickupAsk, setPickupAsk] = useState(null);   // { who, hole, ndb, key }
  const pickupNoteSeen = useRef(new Set());

  // Scoring Enhancement A — In-round notable-score notifications (May 2026)
  // notificationThreshold: 0=off, 1=birdies+, 2=eagles+, 3=albatross+, 4=ace-only
  const [notificationThreshold, setNotificationThreshold] = useState(0);
  const [activeBanner, setActiveBanner] = useState(null);   // single event currently shown
  const [bannerQueue, setBannerQueue] = useState([]);       // queued events
  const seenEventsRef = React.useRef(new Set());            // dedup keys we've already shown
  const lastSinceRef = React.useRef(null);                  // last polled timestamp
  // Celebrations log — persistent for the session. Players can re-open it
  // anytime via the 🎉 tab to review what they missed while away from the cart.
  const [celebrationsLog, setCelebrationsLog] = useState([]);  // newest-first array of all events seen this session
  const [unreadCelebrations, setUnreadCelebrations] = useState(0);
  const [showCelebrationsModal, setShowCelebrationsModal] = useState(false);

  // ===== Multi-Player Scoring (Feature A, Slice 2) =====
  // Solo path is UNTOUCHED: when multiMode is false, none of this runs and the
  // existing single-player `scores` object is the only state in play. Multi-player
  // is purely additive — `targetScores` holds OTHER players keyed by player_id; the
  // scorer's own card stays in `scores`. (Plan §3.1: solo stays unchanged.)
  const [multiMode, setMultiMode] = useState(false);          // false = solo render, true = multi-row
  // Stacked rows on phones (Sept 11, 2026). The multi-row grid gives SCORE and
  // PUTTS 132 px each; with the 8-px gaps and the padding stack around the card
  // (14 + 20 + 2 + 2 a side) the NAME column is left `viewport − 356 px`: 19 px
  // on a 375-px phone, 4 px on a 360-px Android. nowrap + ellipsis then showed
  // players "M.." / "G.." — Dan (TRKMGL) reported foursome names appearing as an
  // initial; on a Pro Max (430 px → ~72 px) it merely looked cramped. Under
  // NARROW_ROWS_MAX_WIDTH the name spans the row on its own line and the two
  // control groups sit right-aligned beneath it; above it the one-line layout
  // is unchanged. Button sizes never change.
  const NARROW_ROWS_MAX_WIDTH = 450;
  const [narrowRows, setNarrowRows] = useState(() => typeof window !== 'undefined' && window.innerWidth < NARROW_ROWS_MAX_WIDTH);
  useEffect(() => {
    const onResize = () => setNarrowRows(window.innerWidth < NARROW_ROWS_MAX_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Shared styles for the multi-row grid. Same three tracks in both modes; on a
  // narrow screen the name cell spans all three (its own line, wraps, status
  // label inline beside it) and the controls fall to the next line, right-aligned
  // because track 1 absorbs the slack. Wide: name in track 1, unchanged.
  const multiRowGridCols = '1fr 132px 132px';
  const multiNameCellStyle = narrowRows
    ? { minWidth: 0, gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 8 }
    : { minWidth: 0 };
  const multiNameTextStyle = narrowRows
    ? { fontSize: 15, fontWeight: 700, color: '#1f2937' }
    : { fontSize: 15, fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const [pickerOpen, setPickerOpen] = useState(false);        // picker modal visibility
  const [pairingMembers, setPairingMembers] = useState([]);   // eligible roster from /scoring/pairing-members
  const [targetHandicaps, setTargetHandicaps] = useState({}); // { pid: handicap index } from /scoring/scores-for-player — feeds the Adj in each kept player's running-totals line
  const [pairingLoading, setPairingLoading] = useState(false);
  const [scoringSelf, setScoringSelf] = useState(true);       // is the scorer scoring themselves? (self toggle)
  const [selectedTargets, setSelectedTargets] = useState([]); // player_ids the scorer is keeping (EXCLUDES self)
  // targetScores: { [player_id]: { [hole]: { score, putts } } } — parallel to `scores`, for others only.
  const [targetScores, setTargetScores] = useState({});
  // targetSkips: { [player_id]: { [hole]: true } } — holes the backend protected (player self-entered) on last save.
  const [targetSkips, setTargetSkips] = useState({});
  // followAlongOnly: scorer unchecked themselves AND selected nobody -> they just want to watch (§3.3 self-view).
  const [followAlongOnly, setFollowAlongOnly] = useState(false);
  // Assisted-player follow-along OFFER (the claim signal, read side). When someone else
  // is keeping THIS player's card, their poll surfaces it here and we show a dismissable
  // "[name] is keeping your scores — follow along?" prompt. Offer-only: never forces.
  const [claimOffer, setClaimOffer] = useState(null);     // { scorer_name } when claimed, else null
  const [claimOfferDismissed, setClaimOfferDismissed] = useState(false); // user said "not now"
  // Decision MODAL (Feature A v6). Fires ONCE on entry when the player arrives cold and is
  // already being scored by someone else — forces the "follow along vs. score myself" choice
  // before the solo screen is usable. After they choose, the modal never re-fires this
  // session (claimModalSeen); the gentle banner becomes the persistent recovery path so an
  // accidental "score myself" is always reversible. Does NOT fire mid-round if they've
  // already started scoring (that's the banner's job, not a screen-hijacking modal).
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimModalSeen, setClaimModalSeen] = useState(false);
  const [claimModalScorerName, setClaimModalScorerName] = useState(null);
  
  useEffect(() => {
    const on = () => { setIsOnline(true); syncOfflineScores(); };
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Auto-save to server every 30 seconds if there are unsaved changes
  const scoresRef = React.useRef(scores);
  const savedRef = React.useRef(scoresSaved);
  const autoSavedRef = React.useRef(false); // Track auto-save separately from UI state
  const teetimeRef = React.useRef(selectedTeetimeId);
  const holeDataRef = React.useRef(holeData);
  const selectedTeeIdRef = React.useRef(selectedTeeId);
  scoresRef.current = scores;
  savedRef.current = scoresSaved;
  teetimeRef.current = selectedTeetimeId;
  holeDataRef.current = holeData;
  selectedTeeIdRef.current = selectedTeeId;

  useEffect(() => {
    const autoSave = async () => {
      if (!teetimeRef.current || (savedRef.current && autoSavedRef.current)) return;
      const hasScores = Object.values(scoresRef.current).some(s => s.score > 0);
      if (!hasScores) return;
      try {
        const arr = [];
        // picked_up rides along (review, Sept 27: without it every auto-save flattened a "7x").
        for (let h = 1; h <= 18; h++) { const s = scoresRef.current[h]; const hd = holeDataRef.current.find(x => x.hole_number === h); const pk = !!(s?.picked && s.score > 0); arr.push({ hole_number: h, gross_score: s?.score > 0 ? s.score : null, putts: !pk && (s?.putts !== null && s?.putts !== undefined) ? s.putts : null, par: hd?.par || null, picked_up: pk }); }
        const resp = await fetch(`${API_URL}/player/scoring/scores?token=${token}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ teetime_id: teetimeRef.current, course_tee_id: selectedTeeIdRef.current || null, scores: arr }) });
        if (resp.ok) { autoSavedRef.current = true; console.log('⛳ Auto-saved scores'); }
      } catch (e) { console.log('⛳ Auto-save failed, offline backup'); }
    };
    const interval = setInterval(autoSave, 30000);
    return () => clearInterval(interval);
  }, [token]);

  // Save to localStorage on visibility change (phone lock, app switch)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden' && teetimeRef.current) {
        const hasScores = Object.values(scoresRef.current).some(s => s.score > 0);
        if (hasScores) {
          try { const s = JSON.parse(localStorage.getItem('tto_offline_scores') || '{}'); s[teetimeRef.current] = { scores: scoresRef.current, timestamp: Date.now() }; localStorage.setItem('tto_offline_scores', JSON.stringify(s)); } catch(e){}
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Scoring Enhancement A — load org-level notification threshold once.
  // Defaults to 0 (off) if scoring is disabled or token is bad.
  useEffect(() => {
    const loadThreshold = async () => {
      try {
        const resp = await fetch(`${API_URL}/player/scoring/status?token=${token}`);
        if (resp.ok) {
          const data = await resp.json();
          setNotificationThreshold(data.in_round_notification_threshold || 0);
        }
      } catch (e) { /* silent — feature stays off */ }
    };
    loadThreshold();
    // Also restore the dedup set from localStorage so reloads don't replay old banners
    try {
      const stored = localStorage.getItem('tto_seen_highlights');
      if (stored) seenEventsRef.current = new Set(JSON.parse(stored));
    } catch (e) { /* ignore */ }
  }, [token]);

  // Scoring Enhancement A — poll /scoring/highlights every 20s for notable scores
  // by OTHER players in the same tee time. Hook is a no-op when threshold = 0.
  useEffect(() => {
    if (!selectedTeetimeId || !token) return;
    // Oct 29, 2026: polls even when the group's birdie banners are off — closest-to-the-pin
    // banners come down the same feed whenever the round has a CTP game. With neither, the
    // server answers an empty list.

    // Restore the celebrations log for THIS tee time. Unread count stays 0
    // — anything the player saw before exiting is considered "read" on re-entry.
    try {
      const stored = localStorage.getItem(`tto_celebrations_${selectedTeetimeId}`);
      const parsed = stored ? JSON.parse(stored) : [];
      setCelebrationsLog(Array.isArray(parsed) ? parsed : []);
    } catch (e) { setCelebrationsLog([]); }
    setUnreadCelebrations(0);

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const since = lastSinceRef.current;
        const url = `${API_URL}/player/scoring/highlights?token=${token}&teetime_id=${selectedTeetimeId}${since ? `&since=${encodeURIComponent(since)}` : ''}`;
        const resp = await fetch(url);
        if (!resp.ok) return;
        const data = await resp.json();
        const events = data.events || [];
        if (events.length === 0) return;
        // Track newest saved_at to use as next `since`
        const newest = events.reduce((acc, e) => (!acc || e.saved_at > acc) ? e.saved_at : acc, null);
        if (newest) lastSinceRef.current = newest;
        // Filter out anything we've already shown (dedup), then enqueue oldest-first
        const fresh = [];
        for (const ev of [...events].reverse()) {
          if (!seenEventsRef.current.has(ev.dedup_key)) {
            seenEventsRef.current.add(ev.dedup_key);
            fresh.push(ev);
          }
        }
        if (fresh.length > 0) {
          // Persist dedup set across reloads (cap at ~200 entries to avoid unbounded growth)
          try {
            const arr = Array.from(seenEventsRef.current);
            const trimmed = arr.slice(-200);
            seenEventsRef.current = new Set(trimmed);
            localStorage.setItem('tto_seen_highlights', JSON.stringify(trimmed));
          } catch (e) { /* ignore quota errors */ }
          setBannerQueue(prev => [...prev, ...fresh]);
          // Also append to the persistent celebrations log (newest-first for display)
          // and bump unread count so the 🎉 tab shows a badge.
          setCelebrationsLog(prev => {
            // Guard against double-add if React strict-mode runs the effect twice
            const existingKeys = new Set(prev.map(e => e.dedup_key));
            const toAdd = fresh.filter(e => !existingKeys.has(e.dedup_key));
            if (toAdd.length === 0) return prev;
            // Sort the additions newest-first, prepend to the log
            const sorted = [...toAdd].sort((a, b) => (a.saved_at < b.saved_at ? 1 : -1));
            const updated = [...sorted, ...prev];
            // Persist per-tee-time so log survives exit/re-entry into scoring
            try {
              localStorage.setItem(`tto_celebrations_${selectedTeetimeId}`, JSON.stringify(updated.slice(0, 50)));
            } catch (e) { /* ignore quota errors */ }
            return updated;
          });
          setUnreadCelebrations(prev => prev + fresh.length);
        }
      } catch (e) { /* silent — best effort */ }
    };

    // Run once immediately so the player sees recent events on (re)open
    poll();
    const interval = setInterval(poll, 20000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedTeetimeId, token, notificationThreshold]);

  // Assisted-player follow-along poll (the claim signal, read side). Every 20s, ask the
  // backend whether someone is keeping THIS player's card. Drives two surfaces:
  //   - On COLD ENTRY (claimed, player hasn't started scoring, modal not yet seen): open
  //     the decision modal (follow along vs. score myself).
  //   - Otherwise (claimed, but they've started scoring OR already dismissed the modal):
  //     show the gentle banner as the persistent, reversible "follow along?" recovery.
  // Offer-only throughout — we never auto-route or lock.
  useEffect(() => {
    if (!selectedTeetimeId || !token) return;
    let cancelled = false;
    const pollClaim = async () => {
      // Don't poll/offer when this player IS the scorer (multi-mode) or already watching.
      if (multiMode || followAlongOnly) return;
      try {
        const r = await fetch(`${API_URL}/player/scoring/claims/mine?token=${token}&teetime_id=${selectedTeetimeId}`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        if (d.claimed) {
          const offer = { scorer_name: d.scorer_name || 'Another player' };
          // Has the player started entering their own scores? If not, they've "arrived cold".
          const hasStartedScoring = Object.values(scores).some(s => (s?.score || 0) > 0);
          if (!claimModalSeen && !hasStartedScoring) {
            // Cold entry → forced decision modal (fires once).
            setClaimModalScorerName(offer.scorer_name);
            setClaimModalOpen(true);
          } else {
            // Already scoring, or already chose → gentle banner recovery (unless dismissed).
            setClaimOffer(offer);
          }
        } else {
          // No longer claimed → clear everything; a fresh claim later starts over.
          setClaimOffer(null);
          setClaimOfferDismissed(false);
          setClaimModalOpen(false);
          setClaimModalSeen(false);
        }
      } catch (e) { /* advisory — ignore */ }
    };
    pollClaim();
    const interval = setInterval(pollClaim, 20000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedTeetimeId, token, multiMode, followAlongOnly, claimModalSeen, scores]);

  // Watcher live-update poll: while this player is following along (being scored by
  // someone else), re-fetch their scorecard every ~20s so the read-only view fills in
  // as the scorer saves holes. Only runs in followAlongOnly — solo/scorer paths are
  // unaffected. ~20s lag is expected (same cadence as the rest); the Refresh button
  // gives an on-demand pull for impatience or a failed poll.
  // NOTE: the fetch is inlined here (rather than calling refreshSelfView, declared far
  // below) so each tick uses a fresh closure over the current token/teetime and there's
  // no stale-closure / declaration-order coupling — the earlier version's interval was
  // not reliably re-running refreshSelfView.
  useEffect(() => {
    if (!followAlongOnly || !selectedTeetimeId || !token) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const resp = await fetch(`${API_URL}/player/scoring/scorecard/${selectedTeetimeId}?token=${token}`);
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        const s = {};
        for (let h = 1; h <= 18; h++) {
          const existing = data.scores[h];
          s[h] = {
            score: existing?.gross_score || 0,
            putts: (existing?.putts !== null && existing?.putts !== undefined) ? existing.putts : null,
            ...(existing?.picked_up ? { picked: true } : {}),   // "7x"
          };
        }
        if (cancelled) return;
        setScores(s);
        if (data.ghin_posted_at) setGhinPosted(true);
        setSelfLastUpdated(Date.now());
      } catch (e) { /* keep last-known scores */ }
    };
    tick();
    const interval = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [followAlongOnly, selectedTeetimeId, token]);

  // Drain the banner queue: show one event at a time
  useEffect(() => {
    if (activeBanner) return;            // one is already showing
    if (bannerQueue.length === 0) return;
    const [next, ...rest] = bannerQueue;
    setActiveBanner(next);
    setBannerQueue(rest);
  }, [activeBanner, bannerQueue]);
  
  useEffect(() => {
    // A code session has exactly one round and no round list (its token can't read one).
    if (codeSession) { setSelectedTeetimeId(codeSession.teetimeId); return; }
    const load = async () => {
      try {
        const resp = await fetch(`${API_URL}/player/scoring/teetimes?token=${token}&mode=live`);
        if (resp.ok) setTeetimes(await resp.json());
      } catch (e) { console.error('Failed to load teetimes:', e); }
      finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  
  useEffect(() => {
    if (!selectedTeetimeId) return;
    // Capture any saved multi-mode session SYNCHRONOUSLY, before the resets below
    // (and before the separate persistence effect can clear it), so recovery reads
    // the pre-reset value rather than a wiped key.
    let savedSession = null;
    try {
      const raw = localStorage.getItem(`tto_multimode_${selectedTeetimeId}`);
      if (raw) savedSession = JSON.parse(raw);
    } catch (e) { savedSession = null; }
    // Reset multi-player state on round change so a prior round's kept players /
    // scores can never leak onto a different round (Feature A, Slice 2 safety).
    setMultiMode(false);
    setFollowAlongOnly(false);
    setScoringSelf(true);
    setSelectedTargets([]);
    setTargetScores({});
    setTargetSkips({});
    setPairingMembers([]);   // Sept 27, 2026: the roster belongs to ONE round — never carry it to the next
    setPickerOpen(false);
    setClaimOffer(null);
    setClaimOfferDismissed(false);
    setClaimModalOpen(false);
    setClaimModalSeen(false);
    setClaimModalScorerName(null);
    const load = async () => {
      try {
        setLoading(true);
        const resp = await fetch(`${API_URL}/player/scoring/scorecard/${selectedTeetimeId}?token=${token}`);
        if (resp.ok) {
          const data = await resp.json();
          setScorecard(data);
          const teeToUse = data.default_tee || data.tees?.find(t => t.has_hole_data);
          if (teeToUse) { setSelectedTeeId(teeToUse.id); setHoleData(data.hole_data || []); }
          const s = {};
          for (let h = 1; h <= 18; h++) {
            const existing = data.scores[h];
            // Putts: use null as the "not entered" sentinel so 0 (chip-in) is a valid saved value.
            // Score keeps 0 as "not entered" — golf scores are never 0 so it's safe.
            s[h] = {
              score: existing?.gross_score || 0,
              putts: (existing?.putts !== null && existing?.putts !== undefined) ? existing.putts : null,
              ...(existing?.picked_up ? { picked: true } : {})   // "7x"
            };
          }
          setScores(s);
          setScoresSaved(Object.keys(data.scores).length > 0);
          if (data.ghin_posted_at) setGhinPosted(true);
          // First empty hole in start order (a shotgun player lands on his starting hole; a
          // card started on 10 reopens on the next hole after the last one scored).
          const sHoles = Object.keys(s).map(Number).filter(h => s[h].score > 0);
          const firstEmpty = liveOrderFrom(liveStartOf(data.tee_time) || inferStartHole(sHoles) || 1).find(h => s[h].score === 0);
          if (firstEmpty) setCurrentHole(firstEmpty);
          loadOfflineScores(selectedTeetimeId);
          // RECOVERY: restore an interrupted multi-mode session (scorer identity +
          // kept targets) so an app close/crash doesn't drop "I was scoring for X".
          // Targets are then rehydrated from the server so their saved holes come
          // back rather than starting blank. Self-scoring choice is also restored.
          // Uses savedSession captured synchronously above (pre-reset).
          try {
            if (savedSession) {
              const sess = savedSession;
              const restoredTargets = Array.isArray(sess.targets) ? sess.targets : [];
              const restoredSelf = sess.scoringSelf !== false; // default true
              if (restoredTargets.length > 0) {
                setSelectedTargets(restoredTargets);
                setScoringSelf(restoredSelf);
                setMultiMode(true);
                setFollowAlongOnly(false);
                const seed = {};
                for (const pid of restoredTargets) seed[pid] = {};
                setTargetScores(seed);
                // Pull each restored target's saved holes from the server.
                for (const pid of restoredTargets) { rehydrateTarget(pid); }
              } else if (sess.followAlongOnly) {
                setFollowAlongOnly(true);
                setScoringSelf(false);
              }
            }
          } catch (e) { /* corrupt/missing session — start solo, no harm */ }
        } else if (codeSession) {
          const d = await resp.json().catch(() => ({}));
          setCodeError(d.error || "Your card couldn't be opened. Scan the code on your scorecard again.");
        }
      } catch (e) {
        console.error('Failed to load scorecard:', e);
        if (codeSession) setCodeError("Couldn't reach the server. Check your signal and try again.");
      }
      finally { setLoading(false); }
    };
    load();
  }, [selectedTeetimeId, token]);

  // NAME RESOLUTION SAFETY NET. memberName() reads pairingMembers and nothing else,
  // and pairingMembers used to be populated in exactly ONE place — openPicker().
  // The recovery effect above restores kept targets and rehydrates their SCORES from
  // the server, but never loaded the roster, so every restored target rendered as the
  // fallback string 'Player' until the scorer happened to open the picker. Invisible
  // in normal use (you usually open the picker to get into multi-mode in the first
  // place); reproducible the moment a deploy hands you a fresh bundle mid-round.
  //
  // Rule 31: wire the behavior to EVERY route into the state, not the obvious one.
  // This fires for any path that lands in multi-mode with targets and no roster.
  //
  // Sept 27, 2026: "no roster" was the wrong test. A roster left over from ANOTHER
  // round (or from before the pairings changed) is non-empty, so this used to skip
  // the fetch and every kept player rendered as 'Player'. Fetch whenever a kept
  // player is missing from the roster; the ref allows one attempt per round +
  // target set, so a player the server no longer returns can't cause a fetch loop.
  const rosterFetchKeyRef = useRef(null);
  useEffect(() => {
    if (!selectedTeetimeId || !token) return;
    if (!multiMode || selectedTargets.length === 0) return;
    const missing = selectedTargets.some(pid => !pairingMembers.some(m => m.player_id === pid));
    if (!missing) return;   // every kept player already has a name
    const fetchKey = `${selectedTeetimeId}|${[...selectedTargets].sort().join(',')}`;
    if (rosterFetchKeyRef.current === fetchKey) return;   // already tried for this set
    rosterFetchKeyRef.current = fetchKey;
    let cancelled = false;
    let finished = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/player/scoring/pairing-members?token=${token}&teetime_id=${selectedTeetimeId}`);
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (!cancelled) {
          finished = true;
          const members = data.members || [];
          setPairingMembers(members);
          // Sept 27, 2026: drop kept players who are NOT in this scorer's group. The
          // saved session is keyed by round only, so a session saved by ANOTHER player
          // on this device (or before the pairings changed) can restore someone else's
          // partners: nameless 'Player' rows the server would refuse to save anyway.
          const inGroup = new Set(members.filter(m => !m.is_self).map(m => m.player_id));
          const keep = selectedTargets.filter(pid => inGroup.has(pid));
          if (keep.length < selectedTargets.length) {
            const pick = (obj) => { const out = {}; for (const pid of keep) if (obj[pid]) out[pid] = obj[pid]; return out; };
            setSelectedTargets(keep);
            setTargetScores(prev => { const out = pick(prev); for (const pid of keep) if (!out[pid]) out[pid] = {}; return out; });
            setTargetSkips(prev => pick(prev));
            if (keep.length === 0) { setMultiMode(false); setScoringSelf(true); }
          }
        }
      } catch (e) {
        // Silent: names fall back to 'Player' and scoring still works. No toast —
        // this is a background nicety, not an action the scorer asked for.
      }
    })();
    // Cancelled before the roster arrived (a re-render swapped the deps): let the
    // next pass try again rather than leaving the names on 'Player'.
    return () => { cancelled = true; if (!finished && rosterFetchKeyRef.current === fetchKey) rosterFetchKeyRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeetimeId, token, multiMode, selectedTargets, pairingMembers]);

  // RECOVERY: persist the multi-mode session (kept targets + self-scoring choice)
  // so a mid-round app close/crash can restore it on reopen. Keyed per teetime.
  // Cleared when the scorer leaves multi-mode with no targets.
  useEffect(() => {
    if (!selectedTeetimeId) return;
    try {
      const key = `tto_multimode_${selectedTeetimeId}`;
      const hasSession = (multiMode && selectedTargets.length > 0) || followAlongOnly;
      if (hasSession) {
        localStorage.setItem(key, JSON.stringify({
          targets: selectedTargets,
          scoringSelf,
          followAlongOnly,
          ts: Date.now(),
        }));
      } else {
        localStorage.removeItem(key);
      }
    } catch (e) { /* storage unavailable — recovery degrades gracefully */ }
  }, [selectedTeetimeId, multiMode, selectedTargets, scoringSelf, followAlongOnly]);
  
  const changeTee = async (teeId) => {
    setSelectedTeeId(teeId);
    if (!scorecard) return;
    try {
      const resp = await fetch(`${API_URL}/player/scoring/holes/${teeId}?token=${token}`);
      if (resp.ok) setHoleData(await resp.json());
    } catch (e) {}
  };
  
  const OFFLINE_KEY = 'tto_offline_scores';
  const saveOffline = (tid, data) => { try { const s = JSON.parse(localStorage.getItem(OFFLINE_KEY) || '{}'); s[tid] = { scores: data, timestamp: Date.now() }; localStorage.setItem(OFFLINE_KEY, JSON.stringify(s)); setPendingSync(true); } catch(e){} };
  const loadOfflineScores = (tid) => { try { const s = JSON.parse(localStorage.getItem(OFFLINE_KEY) || '{}'); if (s[tid]) { setScores(prev => { const m = {...prev}; for (const [h,v] of Object.entries(s[tid].scores)) { if (v.score > 0) m[h] = v; } return m; }); setPendingSync(true); } } catch(e){} };
  const clearOffline = (tid) => { try { const s = JSON.parse(localStorage.getItem(OFFLINE_KEY) || '{}'); delete s[tid]; localStorage.setItem(OFFLINE_KEY, JSON.stringify(s)); setPendingSync(false); } catch(e){} };
  const syncOfflineScores = async () => { if (!selectedTeetimeId || !isOnline) return; try { const s = JSON.parse(localStorage.getItem(OFFLINE_KEY) || '{}'); if (s[selectedTeetimeId]) { await saveScores(); clearOffline(selectedTeetimeId); } } catch(e){} };
  
  const updateScore = (holeNum, field, delta) => {
    setScores(prev => {
      const cur = prev[holeNum] || { score: 0, putts: null };
      let currentVal;
      if (field === 'score') {
        currentVal = cur.score || 0;
        // If score is 0 (unentered) and pressing +/-, start from par instead of 0
        if (currentVal === 0) {
          const hd = holeData.find(h => h.hole_number === holeNum);
          if (hd) currentVal = hd.par;
        }
      } else {
        // Putts: null = unentered. +/− starts from 0, so + → 1, − → 0 (commits chip-in).
        currentVal = (cur.putts !== null && cur.putts !== undefined) ? cur.putts : 0;
      }
      const nv = Math.max(0, currentVal + delta);
      const updated = { ...prev, [holeNum]: { ...cur, [field]: nv, ...(field === 'score' ? { picked: false } : {}) } };
      saveOffline(selectedTeetimeId, updated);
      return updated;
    });
    setScoresSaved(false); setGhinPosted(false); autoSavedRef.current = false;
  };
  
  const tapParScore = (holeNum, par) => {
    setScores(prev => {
      const cur = prev[holeNum] || { score: 0, putts: null };
      const newScore = cur.score === par && !cur.picked ? 0 : par;
      const updated = { ...prev, [holeNum]: { ...cur, score: newScore, picked: false } };
      saveOffline(selectedTeetimeId, updated);
      return updated;
    });
    setScoresSaved(false); setGhinPosted(false); autoSavedRef.current = false;
  };

  // Mirrors tapParScore: tap to commit 0 putts (chip-in), tap again to clear back to unentered.
  // null = not entered (display shows greyed 0). 0 = chip-in (display shows dark 0).
  const tapZeroPutts = (holeNum) => {
    setScores(prev => {
      const cur = prev[holeNum] || { score: 0, putts: null };
      const newPutts = cur.putts === 0 ? null : 0;
      const updated = { ...prev, [holeNum]: { ...cur, putts: newPutts } };
      saveOffline(selectedTeetimeId, updated);
      return updated;
    });
    setScoresSaved(false); setGhinPosted(false); autoSavedRef.current = false;
  };
  
  const saveScores = async () => {
    if (!selectedTeetimeId) return;
    // Multi-mode with self unchecked: the scorer is NOT scoring themselves, so skip
    // writing their own (empty) card. Targets are saved separately via saveTargets().
    if (multiMode && !scoringSelf) return;
    try {
      setSaving(true);
      const arr = [];
      for (let h = 1; h <= 18; h++) { const s = scores[h]; const hd = holeData.find(x => x.hole_number === h); const pk = !!(s?.picked && s.score > 0); arr.push({ hole_number: h, gross_score: s?.score > 0 ? s.score : null, putts: !pk && (s?.putts !== null && s?.putts !== undefined) ? s.putts : null, par: hd?.par || null, picked_up: pk }); }
      const resp = await fetch(`${API_URL}/player/scoring/scores?token=${token}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ teetime_id: selectedTeetimeId, course_tee_id: selectedTeeId || null, scores: arr }) });
      if (resp.ok) {
        setScoresSaved(true); clearOffline(selectedTeetimeId); addToast('Scores saved!', 'success');
        // The server's net double bogey for any "7x" (it knows the event handicap).
        const d = await resp.json().catch(() => ({}));
        const pk = d && d.pickups ? d.pickups : {};
        if (Object.keys(pk).length) setScores(prev => { const n = { ...prev }; for (const [h, v] of Object.entries(pk)) if (n[h] && n[h].picked) n[h] = { ...n[h], score: v }; return n; });
      }
      else { const err = await resp.json(); addToast(err.error || 'Save failed', 'error'); }
    } catch (e) { saveOffline(selectedTeetimeId, scores); addToast('Saved offline', 'warning'); }
    finally { setSaving(false); }
  };

  // Combined save used by the multi-mode Save/advance buttons: self (if scoring self)
  // THEN all kept targets. Solo path never calls this — it calls saveScores() directly.
  const saveAll = async () => {
    await saveScores();      // no-op for self when self is unchecked (guarded above)
    if (multiMode) await saveTargets();
  };
  
  const postToGhin = async () => {
    if (!selectedTeetimeId || !scorecard) return;
    setShowGhinConfirm(false);
    try {
      setPostingGhin(true);
      const resp = await fetch(`${API_URL}/player/scoring/post-to-ghin?token=${token}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ teetime_id: selectedTeetimeId, course_tee_id: selectedTeeId || null, played_at: scorecard.tee_time.date?.split('T')[0] }) });
      if (resp.ok) { const d = await resp.json(); addToast(`✅ Score posted to GHIN successfully! Differential: ${d.differential}`, 'success'); setGhinPosted(true); }
      // GHIN_COURSE_NOT_MAPPED gets a modal rather than a toast: the server copy
      // tells the player to post in the GHIN app and explicitly NOT to retry
      // (retrying after the organizer sets the id would double-post the round),
      // which is too much for an auto-dismissing toast to carry.
      else {
        const err = await resp.json();
        if (err.code === 'GHIN_COURSE_NOT_MAPPED') setGhinNotMappedMsg(err.error || true);
        // Already in GHIN (Oct 16, 2026): same modal, its own title — a toast is too easy to miss.
        else if (err.code === 'GHIN_ALREADY_POSTED') setGhinNotMappedMsg({ title: 'Already Posted in GHIN', text: err.error });
        // Login failures get a modal too (Sept 10, 2026) -- see GhinLoginFailedModal.
        else if (err.code === 'GHIN_INVALID_CREDENTIALS' || err.code === 'GHIN_UNAVAILABLE') setGhinLoginFailed({ code: err.code, message: err.error });
        else addToast(err.error || 'Failed', 'error');
      }
    } catch(e) { addToast('GHIN post failed', 'error'); }
    finally { setPostingGhin(false); }
  };
  
  const loadLiveStandings = async () => {
    if (!selectedTeetimeId) return;
    setStandingsLoading(true);
    setStandingsError(null);
    try {
      const resp = await fetch(`${API_URL}/player/scoring/standings?token=${token}&teetime_id=${selectedTeetimeId}`);
      if (resp.ok) {
        const data = await resp.json();
        setLiveStandings(data);
      } else {
        const err = await resp.json().catch(() => ({}));
        setStandingsError(err.error || `Server error (${resp.status})`);
        console.error('Standings fetch failed:', resp.status, err);
      }
    } catch(e) {
      setStandingsError('Network error — check your connection');
      console.error('Failed to load standings:', e);
    }
    finally { setStandingsLoading(false); }
  };

  // Load the in-app live leaderboard (gross/net) for the current tee time's group.
  // Mirrors loadLiveStandings exactly. The endpoint resolves the grouping (same
  // org/date/course + 1h adjacency) and returns three-section rows; the frontend
  // re-sorts by net when the Net view is active (see the panel's sortedRows).
  const loadLeaderboard = async () => {
    if (!selectedTeetimeId) return;
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    try {
      const resp = await fetch(`${API_URL}/player/scoring/leaderboard?token=${token}&teetime_id=${selectedTeetimeId}`);
      if (resp.ok) {
        const data = await resp.json();
        setLeaderboard(data);
      } else {
        const err = await resp.json().catch(() => ({}));
        setLeaderboardError(err.error || `Server error (${resp.status})`);
        console.error('Leaderboard fetch failed:', resp.status, err);
      }
    } catch(e) {
      setLeaderboardError('Network error — check your connection');
      console.error('Failed to load leaderboard:', e);
    }
    finally { setLeaderboardLoading(false); }
  };

  // Auto-refresh the in-scoring leaderboard every ~20s WHILE the Scoring sub-tab is
  // open, mirroring the standalone LeaderboardModal's proven pattern. Without this,
  // the panel loaded once (the `if (!leaderboard)` guard on the tab click) and never
  // updated — so a player ADDED to the tee time mid-round (e.g. a late arrival added
  // via the organizer dashboard) never appeared, and scores went stale. The endpoint
  // re-queries tee_time_players each call, so polling picks up roster changes AND new
  // scores. Inlined fetch for a fresh closure (stale-closure lesson). Only updates the
  // board data — the Gross/Net toggle and sub-tab selection are untouched. Runs only
  // once the board has been opened (leaderboard != null) to avoid fetching for viewers
  // who never open the Scoring tab.
  useEffect(() => {
    if (resultsSubTab !== 'scoring') return;
    if (!selectedTeetimeId || !token) return;
    if (!leaderboard) return; // not opened yet; first load happens on tab click
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const resp = await fetch(`${API_URL}/player/scoring/leaderboard?token=${token}&teetime_id=${selectedTeetimeId}`);
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (cancelled) return;
        setLeaderboard(data);
      } catch (e) { /* keep last-known board */ }
    };
    const interval = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsSubTab, selectedTeetimeId, token, leaderboard != null]);

  // Pre-selects whoever is already kept (re-open case) and the self toggle.
  const openPicker = async () => {
    if (!selectedTeetimeId) return;
    setPickerOpen(true);
    setPairingLoading(true);
    try {
      const resp = await fetch(`${API_URL}/player/scoring/pairing-members?token=${token}&teetime_id=${selectedTeetimeId}`);
      if (resp.ok) {
        const data = await resp.json();
        setPairingMembers(data.members || []);
      } else {
        setPairingMembers([]);
        addToast('Could not load your group', 'error');
      }
    } catch (e) {
      setPairingMembers([]);
      addToast('Could not load your group', 'error');
    } finally {
      setPairingLoading(false);
    }
  };

  // Toggle a non-self player in/out of the selected set.
  const toggleTarget = (playerId) => {
    setSelectedTargets(prev => prev.includes(playerId) ? prev.filter(p => p !== playerId) : [...prev, playerId]);
  };

  // Confirm the picker selection and enter (or leave) multi-mode.
  // Write the claim signal: tell the backend which players this scorer is now keeping,
  // so each kept player's own device can offer them follow-along. Fire-and-forget —
  // claims are advisory, so a failure here never blocks scoring (the per-hole backstop
  // still protects everyone). Reconciles the full set, so dropping a player clears theirs.
  const writeClaims = async (targetIds) => {
    if (!selectedTeetimeId) return;
    try {
      await fetch(`${API_URL}/player/scoring/claims?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teetime_id: selectedTeetimeId, target_player_ids: targetIds }),
      });
    } catch (e) { /* advisory only — ignore */ }
  };

  // Refresh the watcher's read-only self-view: re-fetch THIS player's scorecard and
  // update only the `scores` map (plus saved/locked flags). Used by both the live poll
  // and the manual "Refresh" button on the follow-along screen. Deliberately does NOT
  // touch currentHole or tee selection, which would be disruptive to reset on each poll.
  const [selfRefreshing, setSelfRefreshing] = useState(false);
  const [selfLastUpdated, setSelfLastUpdated] = useState(null);
  const refreshSelfView = async () => {
    if (!selectedTeetimeId) return;
    setSelfRefreshing(true);
    try {
      const resp = await fetch(`${API_URL}/player/scoring/scorecard/${selectedTeetimeId}?token=${token}`);
      if (resp.ok) {
        const data = await resp.json();
        const s = {};
        for (let h = 1; h <= 18; h++) {
          const existing = data.scores[h];
          s[h] = {
            score: existing?.gross_score || 0,
            putts: (existing?.putts !== null && existing?.putts !== undefined) ? existing.putts : null,
            ...(existing?.picked_up ? { picked: true } : {}),   // "7x"
          };
        }
        setScores(s);
        if (data.ghin_posted_at) setGhinPosted(true);
        setSelfLastUpdated(Date.now());
      }
    } catch (e) { /* ignore — keep last-known scores */ }
    finally { setSelfRefreshing(false); }
  };

  // Rehydrate one target's saved holes from the server (source of truth) and merge
  // them into targetScores. RECOVERY FIX: re-selecting a player mid-round, or
  // restoring multi-mode after an app close/crash, must repopulate what was already
  // saved instead of starting blank (which previously caused apparent score loss).
  // Merge rule: the SERVER value wins for any hole the scorer doesn't already have a
  // value for; an in-memory edit the scorer has already made is preserved (we never
  // clobber unsaved local work with a stale fetch). Self-entered holes are also
  // recorded into targetSkips so they render locked, matching post-save behavior.
  const rehydrateTarget = async (pid) => {
    try {
      const resp = await fetch(
        `${API_URL}/player/scoring/scores-for-player?token=${token}&teetime_id=${selectedTeetimeId}&target_player_id=${pid}`
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const serverScores = data.scores || {};
      if (data.target_handicap !== undefined) {
        setTargetHandicaps(prev => ({ ...prev, [pid]: data.target_handicap }));
      }
      setTargetScores(prev => {
        const existing = prev[pid] || {};
        const merged = { ...existing };
        for (let h = 1; h <= 18; h++) {
          const sv = serverScores[h];
          if (!sv || sv.gross_score == null) continue;
          const localHasValue = merged[h] && merged[h].score > 0;
          if (!localHasValue) {
            merged[h] = {
              score: sv.gross_score,
              putts: (sv.putts !== null && sv.putts !== undefined) ? sv.putts : null,
              ...(sv.picked_up ? { picked: true } : {}),   // "7x"
            };
          }
        }
        return { ...prev, [pid]: merged };
      });
      // Record self-entered holes as skips so they render locked (matches save path).
      const selfHoles = {};
      for (let h = 1; h <= 18; h++) {
        if (serverScores[h] && serverScores[h].entered_by_self) selfHoles[h] = true;
      }
      if (Object.keys(selfHoles).length > 0) {
        setTargetSkips(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), ...selfHoles } }));
      }
    } catch (e) { /* network — keep whatever we have, scorer can still enter manually */ }
  };

  const confirmPicker = async () => {
    const others = selectedTargets;
    // Ensure a row exists for each kept player and drop de-selected ones. Existing
    // in-memory scores for still-kept players are preserved; newly-added players get
    // an empty row here, then are rehydrated from the server below.
    const newlyAdded = others.filter(pid => !targetScores[pid]);
    setTargetScores(prev => {
      const next = { ...prev };
      for (const pid of others) {
        if (!next[pid]) next[pid] = {};
      }
      for (const pid of Object.keys(next)) {
        if (!others.includes(pid)) delete next[pid];
      }
      return next;
    });
    setPickerOpen(false);
    // Write/reconcile the claim signal for whoever is now kept (empty = clear all mine).
    writeClaims(others);
    if (!scoringSelf && others.length === 0) {
      // Scorer unchecked themselves and picked nobody -> pure follow-along (read-only self-view).
      setFollowAlongOnly(true);
      setMultiMode(false);
      return;
    }
    setFollowAlongOnly(false);
    setMultiMode(others.length > 0);
    // RECOVERY: pull each newly-added target's saved holes from the server so a
    // re-select repopulates real scores instead of a blank card. Fire-and-forget
    // per player; merge logic guards against clobbering local edits.
    for (const pid of newlyAdded) { rehydrateTarget(pid); }
  };

  // Update one target player's score/putts for the current hole (mirrors solo updateScore).
  const updateTargetScore = (playerId, field, delta) => {
    setTargetScores(prev => {
      const cur = (prev[playerId] && prev[playerId][currentHole]) || { score: 0, putts: null };
      let currentVal;
      if (field === 'score') {
        currentVal = cur.score || 0;
        if (currentVal === 0) { const hd = holeData.find(h => h.hole_number === currentHole); if (hd) currentVal = hd.par; }
      } else {
        currentVal = (cur.putts !== null && cur.putts !== undefined) ? cur.putts : 0;
      }
      const nv = Math.max(0, currentVal + delta);
      return { ...prev, [playerId]: { ...(prev[playerId] || {}), [currentHole]: { ...cur, [field]: nv, ...(field === 'score' ? { picked: false } : {}) } } };
    });
    // A fresh edit clears any prior "self-entered" skip flag for that hole (we'll re-learn on save).
    setTargetSkips(prev => {
      if (!prev[playerId] || !prev[playerId][currentHole]) return prev;
      const next = { ...prev, [playerId]: { ...prev[playerId] } };
      delete next[playerId][currentHole];
      return next;
    });
  };

  // Tap-to-commit-par for a target (mirrors solo tapParScore).
  const tapParTarget = (playerId, par) => {
    setTargetScores(prev => {
      const cur = (prev[playerId] && prev[playerId][currentHole]) || { score: 0, putts: null };
      const newScore = cur.score === par && !cur.picked ? 0 : par;
      return { ...prev, [playerId]: { ...(prev[playerId] || {}), [currentHole]: { ...cur, score: newScore, picked: false } } };
    });
  };

  // Save all kept targets for the round via the scores-for-player endpoint. Self is
  // saved by the existing saveScores() — this only handles OTHERS. Collects per-hole
  // applied/skipped so the UI can show which holes the player self-entered (protected).
  const saveTargets = async () => {
    const ids = Object.keys(targetScores);
    if (ids.length === 0) return;
    let totalSkipped = 0;
    const newSkips = {};
    for (const pid of ids) {
      const holeMap = targetScores[pid] || {};
      const arr = [];
      for (let h = 1; h <= 18; h++) {
        const s = holeMap[h];
        const hd = holeData.find(x => x.hole_number === h);
        if (s && s.score > 0) {
          const pk = !!s.picked;   // "7x"
          arr.push({ hole_number: h, gross_score: s.score, putts: !pk && (s.putts !== null && s.putts !== undefined) ? s.putts : null, par: hd?.par || null, picked_up: pk });
        }
      }
      if (arr.length === 0) continue;
      try {
        const resp = await fetch(`${API_URL}/player/scoring/scores-for-player?token=${token}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ teetime_id: selectedTeetimeId, target_player_id: pid, course_tee_id: selectedTeeId || null, scores: arr })
        });
        if (resp.ok) {
          const data = await resp.json();
          // The server's net double bogey for this player's "7x" holes.
          if (data && data.pickups && Object.keys(data.pickups).length) {
            setTargetScores(prev => { const hm = { ...(prev[pid] || {}) }; for (const [h, v] of Object.entries(data.pickups)) if (hm[h] && hm[h].picked) hm[h] = { ...hm[h], score: v }; return { ...prev, [pid]: hm }; });
          }
          const skipsForPlayer = {};
          for (const r of (data.results || [])) {
            if (r.applied === false && r.skipped_reason === 'player_self_entered') {
              skipsForPlayer[r.hole_number] = true;
              totalSkipped++;
            }
          }
          if (Object.keys(skipsForPlayer).length > 0) newSkips[pid] = skipsForPlayer;
        } else {
          const err = await resp.json().catch(() => ({}));
          addToast(err.error || 'Could not save a player\u2019s scores', 'error');
        }
      } catch (e) {
        addToast('Could not save a player\u2019s scores', 'error');
      }
    }
    setTargetSkips(newSkips);
    if (totalSkipped > 0) {
      addToast(`${totalSkipped} hole${totalSkipped === 1 ? '' : 's'} kept the player\u2019s own score`, 'info');
    }
  };

  // Name lookup for rendering target rows (from the picker roster we fetched).
  const memberName = (pid) => (pairingMembers.find(m => m.player_id === pid)?.name) || 'Player';

  // ===== Hole order (Oct 15, 2026 — start on any hole). =====
  // The card = every hole with a score for you or any kept player. Empty card: every hole on
  // the strip is open — tap the hole you're on and score it. The first hole scored is the
  // start; Next / Back / the strip's forward-jump block / "Save round" follow 10…18, 1…9.
  const liveCardHoles = Array.from({ length: 18 }, (_, i) => i + 1).filter(h =>
    (scores[h]?.score > 0) || Object.values(targetScores).some(hm => ((hm && hm[h]?.score) || 0) > 0));
  const liveOrder = liveOrderFrom(useCardStart(selectedTeetimeId, liveCardHoles, liveStartOf(scorecard?.tee_time)));
  const holePos = (h) => liveOrder.indexOf(h);
  const nextLiveHole = (h) => { const i = holePos(h); return i < 0 || i === 17 ? null : liveOrder[i + 1]; };
  const prevLiveHole = (h) => { const i = holePos(h); return i <= 0 ? null : liveOrder[i - 1]; };
  const lastLiveHole = liveOrder[17];

  // ===== Backfill detection (Feature A, Slice 2b — mid-round add). =====
  // When a player is added mid-round, earlier holes the group has already played have
  // no score for them. We flag those holes amber in the nav so the scorer can jump back
  // and fill them. A hole counts as a "played" hole if it's strictly before the current
  // hole OR the scorer has entered a score there (the round has reached it). A backfill
  // GAP is a played hole where at least one kept target has no score yet.
  const targetIds = Object.keys(targetScores);
  const holePlayed = (h) => holePos(h) < holePos(currentHole) || (scores[h]?.score > 0);   // in start order
  const targetMissingAt = (pid, h) => !((targetScores[pid] && targetScores[pid][h] && targetScores[pid][h].score > 0));
  // Set of hole numbers (1..18) that have at least one target gap on a played hole.
  const backfillHoles = (multiMode && targetIds.length > 0)
    ? liveOrder.filter(h => holePlayed(h) && targetIds.some(pid => targetMissingAt(pid, h)))
    : [];
  const backfillSet = new Set(backfillHoles);
  // Per-current-hole: which kept targets are missing a score right now (for row highlight).
  const targetsMissingHere = targetIds.filter(pid => targetMissingAt(pid, currentHole) && holePlayed(currentHole));

  const currentHoleData = holeData.find(h => h.hole_number === currentHole);
  const currentScore = scores[currentHole] || { score: 0, putts: null };

  // ===== Yards-to-Center (DB-driven; plan v2.1 §9.4) =====
  // Fetches confirmed green centers for this round's course from the server
  // (gated server-side on yardage_eligible). Replaces the old hardcoded block.
  const YTC_MAX_YARDS = 600, YTC_ACCURACY_LIMIT_M = 20;
  // Demo players (@demo.teetimeorganizer.net) can't get a real on-course GPS fix —
  // a prospect at a desk would only ever see "settling"/"out of range". So in the
  // demo we show a fixed, representative reading instead of computing from GPS.
  const ytcDemo = !!(playerData?.email && playerData.email.includes('@demo.teetimeorganizer.net'));
  const YTC_DEMO_YARDS = 138;
  const [ytcGreens, setYtcGreens] = useState(null); // { "1": {lat,lon}, ... } or null when not eligible
  const [ytcYards, setYtcYards] = useState(null);
  const [ytcStatus, setYtcStatus] = useState('starting');
  const [ytcAcc, setYtcAcc] = useState(null);

  // Load greens once per selected round. Empty/ineligible -> feature stays off (blank).
  useEffect(() => {
    let cancelled = false;
    if (ytcDemo) { setYtcGreens({ demo: true }); return; } // demo: enable readout, skip fetch
    if (!selectedTeetimeId || !token) { setYtcGreens(null); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/player/scoring/green-coordinates?token=${token}&teetime_id=${selectedTeetimeId}`);
        if (!r.ok) { if (!cancelled) setYtcGreens(null); return; }
        const d = await r.json();
        if (!cancelled) setYtcGreens(d && d.eligible ? d.coordinates : null);
      } catch (e) { if (!cancelled) setYtcGreens(null); }
    })();
    return () => { cancelled = true; };
  }, [selectedTeetimeId, token]);

  const ytcEnabled = ytcGreens != null;

  useEffect(() => {
    if (ytcDemo) { setYtcStatus('ok'); setYtcYards(YTC_DEMO_YARDS); setYtcAcc(null); return; } // demo: fixed reading, no GPS
    if (!ytcEnabled) { setYtcStatus('off'); setYtcYards(null); return; }
    const green = ytcGreens[currentHole];
    if (!green) { setYtcStatus('nodata'); setYtcYards(null); return; }
    if (!('geolocation' in navigator)) { setYtcStatus('nofix'); return; }
    const yardsBetween = (a, b) => {
      const R = 6371000, toRad = d => d * Math.PI / 180;
      const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon);
      const hv = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat))*Math.cos(toRad(b.lat))*Math.sin(dLon/2)**2;
      return 2 * R * Math.asin(Math.sqrt(hv)) * 1.09361;
    };
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const acc = pos.coords.accuracy; setYtcAcc(acc);
        if (acc > YTC_ACCURACY_LIMIT_M) { setYtcStatus('coarse'); setYtcYards(null); return; }
        const y = Math.round(yardsBetween({lat:pos.coords.latitude,lon:pos.coords.longitude}, green));
        if (y > YTC_MAX_YARDS) { setYtcStatus('range'); setYtcYards(null); return; }
        setYtcStatus('ok'); setYtcYards(y);
      },
      (err) => { setYtcStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'nofix'); setYtcYards(null); },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [ytcEnabled, ytcGreens, currentHole]);
  // ===== END Yards-to-Center =====

  
  const getCourseHandicap = () => {
    const hi = parseFloat(scorecard?.player?.handicap);
    if (isNaN(hi) || !selectedTeeId || !scorecard?.tees) return null;
    const tee = scorecard.tees.find(t => t.id === selectedTeeId);
    if (!tee?.slope_rating || !tee?.course_rating) return null;
    return Math.round(hi * (tee.slope_rating / 113) + (tee.course_rating - holeData.reduce((s,h) => s + h.par, 0)));
  };
  const courseHcp = getCourseHandicap();
  const getStrokesOnHole = (idx) => { if (courseHcp === null || !idx) return 0; const ch = Math.max(0, courseHcp); if (ch >= idx + 18) return 2; if (ch >= idx) return 1; return 0; };
  const getAdjustedScore = (g, p, st) => { if (!g || !p) return 0; return Math.min(g, p + 2 + st); };
  
  const strokes = currentHoleData ? getStrokesOnHole(currentHoleData.handicap_index) : 0;
  const adj = currentScore.score > 0 && currentHoleData ? getAdjustedScore(currentScore.score, currentHoleData.par, strokes) : 0;
  
  const getTotals = () => {
    let gross = 0, adjT = 0, putts = 0, played = 0, parPlayed = 0;
    for (let h = 1; h <= 18; h++) {
      const s = scores[h]; const hd = holeData.find(x => x.hole_number === h);
      if (s?.score > 0) { played++; gross += s.score; const st = hd ? getStrokesOnHole(hd.handicap_index) : 0; adjT += hd ? getAdjustedScore(s.score, hd.par, st) : s.score; if (hd) parPlayed += hd.par; }
      if (s?.putts != null) putts += s.putts;
    }
    return { gross, adj: adjT, putts, played, parPlayed };
  };
  const totals = getTotals();
  const selectedTee = scorecard?.tees?.find(t => t.id === selectedTeeId);

  // Running totals for a player the scorer is keeping the card for (Sept 11,
  // 2026). Same fields as the scorer's own bar — Thru / Gross / Adj / Putts /
  // vs Par — plus F9 and B9 once each nine is complete, because "what did I
  // shoot on the front?" is the question the scorer actually gets asked. Adj is
  // net double bogey against the TARGET's course handicap, derived from their
  // index (target_handicap from /scores-for-player) on the scorer's selected
  // tee — the same tee the save path stamps into round_scores.course_hcp — so
  // it agrees with what the player will see on their own card. Unknown index →
  // Adj shows "—". Self-entered (locked) holes are in targetScores too, so the
  // totals cover the whole card, not just the holes this scorer typed.
  const getTargetTotals = (pid) => {
    const ts = targetScores[pid] || {};
    const hi = parseFloat(targetHandicaps[pid]);
    const coursePar = holeData.reduce((s, h) => s + h.par, 0);
    const tch = (!isNaN(hi) && selectedTee?.slope_rating && selectedTee?.course_rating)
      ? Math.round(hi * (selectedTee.slope_rating / 113) + (selectedTee.course_rating - coursePar))
      : null;
    const strokesOn = (idx) => { if (tch === null || !idx) return 0; const ch = Math.max(0, tch); if (ch >= idx + 18) return 2; if (ch >= idx) return 1; return 0; };
    let gross = 0, adjT = 0, putts = 0, played = 0, parPlayed = 0, f9 = 0, f9n = 0, b9 = 0, b9n = 0;
    for (let h = 1; h <= 18; h++) {
      const s = ts[h]; const hd = holeData.find(x => x.hole_number === h);
      if (s?.score > 0) {
        played++; gross += s.score;
        adjT += hd ? getAdjustedScore(s.score, hd.par, strokesOn(hd.handicap_index)) : s.score;
        if (hd) parPlayed += hd.par;
        if (h <= 9) { f9 += s.score; f9n++; } else { b9 += s.score; b9n++; }
      }
      if (s?.putts != null) putts += s.putts;
    }
    return { gross, adj: adjT, adjKnown: tch !== null, putts, played, parPlayed, f9: f9n === 9 ? f9 : null, b9: b9n === 9 ? b9 : null };
  };
  const renderTargetTotals = (pid) => {
    const t = getTargetTotals(pid);
    if (t.played === 0) return null;
    const vs = t.gross - t.parPlayed;
    const items = [['Thru', t.played], ['Gross', t.gross], ['Adj', t.adjKnown ? t.adj : '—'], ['Putts', t.putts || '—'], ['vs Par', vs === 0 ? 'E' : (vs > 0 ? '+' : '') + vs]];
    if (t.b9 !== null) items.splice(2, 0, ['B9', t.b9]);
    if (t.f9 !== null) items.splice(2, 0, ['F9', t.f9]);
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', width: '100%', marginTop: 2, fontSize: 11, fontWeight: 600, color: '#6b7280' }}>
        {items.map(([l, v]) => <span key={l}>{l} <span style={{ color: l === 'vs Par' && vs !== 0 ? (vs < 0 ? '#b91c1c' : '#1d4ed8') : '#1f2937', fontWeight: 800 }}>{v}</span></span>)}
      </div>
    );
  };

  // ── Picked up ("7x", Sept 27, 2026 — Pick_Up_X_Design_v1) ──
  // The net double bogey for `who` ('self' or a kept player's id) on a hole: your own
  // course handicap, or the kept player's index on the selected tee (as his Adj line).
  const pickupFor = (who, hole) => {
    const hd = holeData.find(x => x.hole_number === hole);
    if (!hd || hd.par == null) return null;
    let ch = null;
    if (who === 'self') ch = courseHcp;
    else {
      const hi = parseFloat(targetHandicaps[who]);
      const coursePar = holeData.reduce((s, h) => s + h.par, 0);
      ch = (!isNaN(hi) && selectedTee?.slope_rating && selectedTee?.course_rating)
        ? Math.round(hi * (selectedTee.slope_rating / 113) + (selectedTee.course_rating - coursePar)) : null;
    }
    return pickupNdb(hd.par, ch, hd.handicap_index);
  };
  const markPicked = (who, hole, ndb) => {
    if (who === 'self') {
      setScores(prev => {
        const cur = prev[hole] || { score: 0, putts: null };
        const updated = { ...prev, [hole]: { ...cur, score: ndb, putts: null, picked: true } };
        saveOffline(selectedTeetimeId, updated);
        return updated;
      });
      setScoresSaved(false); setGhinPosted(false); autoSavedRef.current = false;
    } else {
      setTargetScores(prev => ({ ...prev, [who]: { ...(prev[who] || {}), [hole]: { ...((prev[who] || {})[hole] || {}), score: ndb, putts: null, picked: true } } }));
      setTargetSkips(prev => {
        if (!prev[who] || !prev[who][hole]) return prev;
        const next = { ...prev, [who]: { ...prev[who] } };
        delete next[who][hole];
        return next;
      });
    }
  };
  // X tapped: the first time for this player on this round, explain the net double
  // bogey (Dennis: "that may cause them to enter a real score"); after that, just mark it.
  const pickUp = (who) => {
    const hole = currentHole;
    const ndb = pickupFor(who, hole);
    if (ndb == null) { addToast('This hole has no par on file — enter a score instead', 'error'); return; }
    const key = `tto_pickup_note_${selectedTeetimeId}_${who === 'self' ? (playerData?.id || 'self') : who}`;
    let seen = pickupNoteSeen.current.has(key);
    try { if (!seen && localStorage.getItem(key)) seen = true; } catch (e) { /* storage off: ask again */ }
    if (seen) markPicked(who, hole, ndb);
    else setPickupAsk({ who, hole, ndb, key });
  };
  const pickupBtn = (who, big = false) => (
    <button onClick={(e) => { e.stopPropagation(); pickUp(who); }} disabled={ghinPosted}
      title="Picked up — scored as a net double bogey"
      style={big
        ? { marginTop: 10, padding: '6px 16px', borderRadius: 8, border: '1.5px solid #9ca3af', background: '#fff', color: '#374151', fontSize: 14, fontWeight: 700, cursor: ghinPosted ? 'default' : 'pointer' }
        : { marginLeft: 6, padding: '1px 8px', borderRadius: 6, border: '1.5px solid #9ca3af', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 800, cursor: 'pointer', lineHeight: '18px', verticalAlign: 'middle' }}>
      {big ? 'X — picked up' : 'X'}
    </button>
  );

  const getScoreLabel = (score, par) => { if (!score || !par) return ''; const d = score - par; if (d <= -3) return 'Albatross'; if (d === -2) return 'Eagle'; if (d === -1) return 'Birdie'; if (d === 0) return 'Par'; if (d === 1) return 'Bogey'; if (d === 2) return 'Dbl Bogey'; return '+' + d; };
  const getScoreBg = (score, par) => { if (!score || !par) return '#e5e7eb'; const d = score - par; if (d <= -2) return '#fbbf24'; if (d === -1) return '#dc2626'; if (d === 0) return '#166534'; if (d === 1) return '#3b82f6'; return '#1e3a5f'; };
  const getScoreColor = (score, par) => { if (!score || !par) return '#6b7280'; return '#fff'; };
  
  const formatDate = (ds) => { if (!ds) return ''; const d = new Date(ds.split('T')[0] + 'T12:00:00'); if (isNaN(d.getTime())) return ds; return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };
  
  // ---- RENDER ----
  if (loading && teetimes.length === 0) return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>Loading...</div>;

  // Team-scored round: the team card replaces the individual card entirely.
  if (teamTeetime) {
    return <TeamScoring token={token} playerData={playerData} teetime={teamTeetime} onBack={() => setTeamTeetime(null)} onClose={onClose} />;
  }
  
  // Code session: no round list — loading, or why the card didn't open.
  if (codeSession && !scorecard) {
    return (
      <div style={{ maxWidth: 500, margin: '0 auto', padding: 20 }}>
        {codeError ? (
          <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 16, padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#991b1b', lineHeight: 1.4 }}>{codeError}</div>
            <button onClick={codeSession.onBack} style={{ width: '100%', marginTop: 16, padding: '14px 0', background: '#166534', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>← Back</button>
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>Loading your card…</div>
        )}
      </div>
    );
  }

  // Round selection
  if (!selectedTeetimeId || !scorecard) {
    return (
      <div style={{ maxWidth: 500, margin: '0 auto', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>⛳ Live Scoring</h2>
          <button onClick={onClose} style={{ padding: '10px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>← Back</button>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {teetimes.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: 16 }}>No upcoming rounds found.</p>
          ) : teetimes.map(tt => (
            <button key={tt.id} onClick={() => tt.scoring_mode === 'team' ? setTeamTeetime(tt) : setSelectedTeetimeId(tt.id)}
              style={{ padding: 20, background: '#fff', border: tt.scoring_mode === 'team' ? '2px solid #14532d' : '2px solid #d1d5db', borderRadius: 12, cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{formatDate(tt.date)}</div>
              <div style={{ fontSize: 16, color: '#6b7280', marginTop: 4 }}>{tt.course_name}</div>
              {tt.event_name && <div style={{ fontSize: 14, color: '#166534', marginTop: 4, fontWeight: 600 }}>📅 {tt.event_name}</div>}
              {tt.scoring_mode === 'team' && <div style={{ fontSize: 13, color: '#14532d', marginTop: 6, fontWeight: 700 }}>👥 Team score — one card per team (captain's choice)</div>}
              {tt.scoring_mode !== 'team' && tt.my_scores_entered > 0 && <div style={{ fontSize: 14, color: '#166534', marginTop: 6, fontWeight: 600 }}>✓ {tt.my_scores_entered} holes scored</div>}
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Main scoring UI
  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: '10px 14px 112px', userSelect: 'none' }}>
      {/* Scoring Enhancement A — notable-score banner (May 2026) */}
      <NotableScoreBanner event={activeBanner} onDismiss={() => setActiveBanner(null)} />

      {/* ===== Decision MODAL (Feature A v6): fires once on cold entry when another player
              is already keeping this card. Forces a deliberate choice before the solo
              screen is usable. Follow-along is the prominent default; "score myself" is the
              quiet secondary. After choosing, the banner below is the reversible recovery. ===== */}
      {claimModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 320, width: '100%', textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 26 }}>⛳</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 6 }}>
              {claimModalScorerName || 'Another player'} is scoring for you
            </div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5, marginBottom: 20 }}>
              Someone in your group is keeping your scorecard. Want to follow along and watch your round, or score it yourself?
            </div>
            <button onClick={() => { setClaimModalOpen(false); setClaimModalSeen(true); setFollowAlongOnly(true); setMultiMode(false); setClaimOffer(null); }}
              style={{ width: '100%', padding: '13px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}>
              👁 Follow along &amp; watch
            </button>
            <button onClick={() => { setClaimModalOpen(false); setClaimModalSeen(true); }}
              style={{ width: '100%', padding: '12px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              I'll score it myself
            </button>
            <div onClick={() => { setClaimModalSeen(true); onClose(); }}
              style={{ marginTop: 14, fontSize: 13, color: '#9ca3af', cursor: 'pointer' }}>
              Exit to Main Screen
            </div>
          </div>
        </div>
      )}

      {/* ===== Persistent recovery banner (Feature A claim signal). Shown when the player
              is claimed but chose to score themselves (or was claimed mid-round). Always
              available so an accidental "score myself" is one tap from follow-along. ===== */}
      {claimOffer && !claimOfferDismissed && !multiMode && !followAlongOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '12px 14px', background: '#f0fdf4', border: '1.5px solid #16a34a', borderRadius: 12 }}>
          <span style={{ fontSize: 18 }}>⛳</span>
          <div style={{ flex: 1, fontSize: 14, color: '#14532d' }}>
            <strong>{claimOffer.scorer_name}</strong> is keeping your scores. Follow along?
          </div>
          <button onClick={() => { setFollowAlongOnly(true); setMultiMode(false); setClaimOffer(null); }}
            style={{ padding: '8px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
            Follow along
          </button>
          <button onClick={() => setClaimOfferDismissed(true)}
            style={{ padding: '8px 10px', background: 'transparent', color: '#6b7280', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
            Not now
          </button>
        </div>
      )}

      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{scorecard.tee_time.course_name}</div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>
            {selectedTee?.tee_name} • Par {holeData.reduce((s,h) => s+h.par, 0)} {courseHcp !== null && `• CH: ${courseHcp}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: isOnline ? '#22c55e' : '#ef4444', display: 'inline-block' }}></span>
          {pendingSync && <span style={{ fontSize: 14, color: '#f59e0b' }}>⏳</span>}
          <button onClick={async () => { if (selectedTeetimeId && Object.values(scores).some(s => s.score > 0) && !scoresSaved) { await saveScores(); } if (codeSession) { codeSession.onBack(); return; } setSelectedTeetimeId(null); setScorecard(null); }} style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>{codeSession ? '← Group' : '← Rounds'}</button>
          <button onClick={async () => { if (selectedTeetimeId && Object.values(scores).some(s => s.score > 0) && !scoresSaved) { await saveScores(); } onClose(); }} style={{ padding: '6px 12px', background: '#fff', color: '#111827', border: '2px solid #9ca3af', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Exit</button>
        </div>
      </div>
      
      {/* Tee selector */}
      {scorecard.tees?.filter(t => t.has_hole_data).length > 1 && (
        <select value={selectedTeeId || ''} onChange={(e) => changeTee(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, marginBottom: 8, background: '#fff' }}>
          {scorecard.tees.filter(t => t.has_hole_data).map(t => (
            <option key={t.id} value={t.id}>{t.tee_name} — {t.course_rating} / {t.slope_rating}</option>
          ))}
        </select>
      )}
      
      {/* GHIN Lock indicator */}
      {ghinPosted && (
        <div style={{ background: '#166534', color: '#fff', borderRadius: 10, padding: '10px 16px', marginBottom: 10, textAlign: 'center', fontSize: 14, fontWeight: 700 }}>
          🔒 Posted to GHIN — Scores are locked
        </div>
      )}
      <HoleOutNote info={scorecard?.hole_out} />
      
      {/* Running totals bar - BIG */}
      <div style={{ display: 'flex', justifyContent: 'space-around', background: '#1f2937', color: '#fff', borderRadius: 10, padding: '10px 6px', marginBottom: 10 }}>
        {[
          { label: 'THRU', val: totals.played },
          { label: 'GROSS', val: totals.gross || '—' },
          { label: 'ADJ', val: totals.adj || '—' },
          { label: 'PUTTS', val: totals.putts || '—' },
          { label: 'VS PAR', val: totals.played > 0 && (totals.gross - totals.parPlayed) !== 0 ? ((totals.gross - totals.parPlayed) > 0 ? '+' : '') + (totals.gross - totals.parPlayed) : 'E', color: totals.gross - totals.parPlayed < 0 ? '#f87171' : '#fff' }
        ].map((item, i) => (
          <div key={i} style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 600, letterSpacing: 0.5 }}>{item.label}</div>
            <div style={{ fontWeight: 800, fontSize: 22, color: item.color || '#fff', marginTop: 2 }}>{item.val}</div>
          </div>
        ))}
        <StablefordPtsCell stableford={scorecard?.stableford} token={token} teetimeId={selectedTeetimeId} selfId={playerData?.id} hole={currentHole} />
      </div>
      
      {/* Scorecard / Results / Celebrations tab toggle */}
      <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: '2px solid #e5e7eb', marginBottom: 10 }}>
        <button
          onClick={() => setScoringTab('scorecard')}
          style={{ flex: 1, padding: '10px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer', border: 'none',
            background: scoringTab === 'scorecard' ? '#1f2937' : '#f9fafb',
            color: scoringTab === 'scorecard' ? '#fff' : '#6b7280' }}>
          ⛳ Scorecard
        </button>
        <button
          onClick={async () => {
            // Flush any unsaved scores before loading standings
            const hasAnyScores = Object.values(scores).some(s => s?.score > 0);
            if (hasAnyScores && !scoresSaved) await saveScores();
            setScoringTab('results');
            setLiveStandings(null);
            setStandingsError(null);
            loadLiveStandings();
          }}
          style={{ flex: 1, padding: '10px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer', border: 'none',
            borderLeft: '2px solid #e5e7eb',
            background: scoringTab === 'results' ? '#1f2937' : '#f9fafb',
            color: scoringTab === 'results' ? '#fff' : '#6b7280' }}>
          📊 Leaderboard
        </button>
        {/* Celebrations tab — only shown when org has the feature enabled.
            Opens a modal overlay rather than switching tabs, so the player's
            scorecard view and scroll position are preserved. */}
        {(notificationThreshold > 0 || celebrationsLog.length > 0) && (
          <button
            onClick={() => { setShowCelebrationsModal(true); setUnreadCelebrations(0); }}
            aria-label="Celebrations"
            style={{ flex: '0 0 56px', padding: '10px 0', fontSize: 18, cursor: 'pointer', border: 'none',
              borderLeft: '2px solid #e5e7eb', background: '#f9fafb', color: '#6b7280', position: 'relative' }}>
            🎉
            {unreadCelebrations > 0 && (
              <span style={{ position: 'absolute', top: 4, right: 6, background: '#dc2626', color: '#fff',
                fontSize: 10, fontWeight: 700, borderRadius: 10, minWidth: 18, height: 18,
                padding: '0 5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                {unreadCelebrations > 99 ? '99+' : unreadCelebrations}
              </span>
            )}
          </button>
        )}
      </div>

      {/* ── RESULTS TAB ─────────────────────────────────────────────────── */}
      {scoringTab === 'results' && (
        <div>
          {/* Sub-tabs (Option B): Games (existing standings) + Scoring (leaderboard).
              Step 1: defaults to 'games' so existing behavior is unchanged. The
              Scoring sub-tab loads the leaderboard on first open. */}
          <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: 10 }}>
            <button
              onClick={() => setResultsSubTab('games')}
              style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                background: resultsSubTab === 'games' ? '#166534' : '#f9fafb',
                color: resultsSubTab === 'games' ? '#fff' : '#6b7280' }}>
              💰 Games
            </button>
            <button
              onClick={() => { setResultsSubTab('scoring'); if (!leaderboard) { loadLeaderboard(); } }}
              style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                borderLeft: '1px solid #e5e7eb',
                background: resultsSubTab === 'scoring' ? '#166534' : '#f9fafb',
                color: resultsSubTab === 'scoring' ? '#fff' : '#6b7280' }}>
              🏆 Scores
            </button>
          </div>

          {/* ── GAMES SUB-TAB (existing standings render, unchanged) ── */}
          {resultsSubTab === 'games' && (
          <div>
          {standingsLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 16 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
              Loading results...
            </div>
          ) : standingsError ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
              <div style={{ color: '#dc2626', fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Unable to load results</div>
              <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>{standingsError}</div>
              <button
                onClick={() => { setLiveStandings(null); setStandingsError(null); loadLiveStandings(); }}
                style={{ padding: '10px 24px', background: '#166534', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                🔄 Retry
              </button>
            </div>
          ) : !liveStandings || !liveStandings.games || liveStandings.games.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 15 }}>
              No games found for this round.<br/>
              <span style={{ fontSize: 13, marginTop: 8, display: 'block' }}>Games must be set up by the organizer.</span>
              <button
                onClick={() => { setLiveStandings(null); setStandingsError(null); loadLiveStandings(); }}
                style={{ marginTop: 16, padding: '8px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                🔄 Refresh
              </button>
            </div>
          ) : (
            <div>
              {/* Refresh button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <button
                  onClick={() => { setLiveStandings(null); setStandingsError(null); loadLiveStandings(); }}
                  style={{ padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  🔄 Refresh
                </button>
              </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {liveStandings.games.map((game) => {
                const isExpanded = expandedGames[game.game_id];
                const isSkins = game.game_type?.includes('skins');
                const isPutts = game.game_type?.includes('putts');
                const isPot = game.game_type === 'skins_pot' || game.game_type === 'skins_pot_gross';
                const isStroke = game.game_type === 'stroke_play' || isBestBallGameType(game.game_type);   // Best Ball / Shamble rows are teams (Sept 23 / 28, 2026)
                const isFinalized = game.status === 'completed';
                const noMoney = !(parseFloat(game.bet_amount) > 0) || ((isStroke || game.game_type === 'stableford') && game.results?.results_only); // results-only ($0 wager / placings only)
                const isCtpGame = game.game_type === 'ctp';   // closest to the pin (Sept 28, 2026)
                const isDotsGame = game.game_type === 'dots';   // Oct 4, 2026
                const isNassauGame = game.game_type === 'nassau';   // Oct 4, 2026
                const isSixesGame = game.game_type === 'sixes';     // Oct 5, 2026
                const isStablefordGame = game.game_type === 'stableford';   // Oct 5, 2026
                const sfPerPoint = isStablefordGame && ['per_point_field', 'per_point_pairs', 'per_point_teams'].includes(game.results?.payout_mode);
                const statLabel = isSkins ? 'Total Skins' : isPutts ? 'Total Putts' : isCtpGame ? 'Closest to the pin' : isDotsGame ? 'Dots' : isNassauGame ? 'Bets won–lost' : (isSixesGame || game.game_type === 'match_play') ? 'Matches won–lost–halved' : isStablefordGame ? 'Points' : 'Result';
                const statKey = isSkins ? 'skins_won' : isPutts ? 'total_putts' : isCtpGame ? 'ctp_won' : isDotsGame ? 'total_dots' : (isNassauGame || isSixesGame || game.game_type === 'match_play') ? 'record' : isStablefordGame ? 'points' : 'net_score';
                const players = game.results?.totals || [];
                const holeResults = game.results?.holes || [];
                const holesThru = game.results?.holes_completed || 0;
                const holeTotal = game.results?.hole_count || 18;

                return (
                  <div key={game.game_id} style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
                    {/* Game header */}
                    <div style={{ background: '#1f2937', color: '#fff', padding: '10px 14px' }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{game.display_name || game.game_type}</div>
                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                        {isStroke && game.results ? `${game.results.competition === 'both' ? 'Net & gross' : game.results.competition === 'gross' ? 'Gross' : `Net · ${game.results.allowance_pct}% hcp`}${parseFloat(game.bet_amount) > 0 && !game.results.results_only ? ` · $${parseFloat(game.bet_amount)} buy-in` : ' · placings only'}` : (parseFloat(game.bet_amount) > 0 ? `$${game.bet_amount}` : '')}{!isStroke && !(parseFloat(game.bet_amount) > 0) ? 'Results only' : ''}{!isStroke && parseFloat(game.bet_amount) > 0 && (isPot ? '/player buy-in' : isSkins ? '/hole' : isPutts ? '/putt' : isDotsGame ? '/dot' : isNassauGame ? '/bet' : isStablefordGame ? (sfPerPoint ? '/point' : '/player buy-in') : '')}{isDotsGame && game.results?.mode === 'teams' ? ' · teams' : ''}{isStablefordGame && game.results ? ` · ${game.results.competition === 'gross' ? 'Gross' : `Net · ${game.results.allowance_pct}% hcp`}` : ''}
                        {/* Stroke play scores every player through his own holes — no field-wide
                            "through hole N" (that is a skins idea: a hole counts once everyone has it). */}
                        {isStroke
                          ? (game.results?.is_complete ? ' · Final' : '')
                          : (holesThru > 0 && holesThru < holeTotal
                            ? ` · Through Hole ${holesThru}`
                            : holesThru === holeTotal ? ' · Final' : '')}
                      </div>
                      {isPot && isFinalized && <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>Amounts shown are net of buy-in</div>}
                      {(isStroke || isStablefordGame) && game.results?.money_pending && <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>Money is figured once every card is complete</div>}
                    </div>

                    {gameErrorBox(game)}

                    {/* Leaderboard */}
                    <div>
                      <SegmentSwitch game={game}>{(game) => { const players = game.results?.totals || []; return players.map((player, idx) => {
                        const net = parseFloat(player.net_amount || 0);
                        const isTeamRow = player.player_id == null && player.side_id != null;
                        const isMe = isTeamRow ? (player.members || []).some(m => m.player_id === liveStandings.player_id) : player.player_id === liveStandings.player_id;
                        const dg = (isDotsGame || (isStablefordGame && (game.results?.team_mode === 'teams' || game.results?.mode === 'group'))) ? dotsGroupInfo(players, idx) : null;   // Oct 6, 2026: Dots groups / Stableford teams
                        return (
                          <React.Fragment key={isTeamRow ? `side-${player.side_id}` : player.player_id}>
                          {dg && dg.startsGroup && dotsGroupHeading(dg.name)}
                          <div key={isTeamRow ? `side-${player.side_id}` : player.player_id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 14px',
                            borderBottom: idx < players.length - 1 ? '1px solid #f3f4f6' : 'none',
                            background: isMe ? '#f0fdf4' : 'transparent'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ minWidth: 24, height: 24, padding: '0 4px', borderRadius: 12, background: (isStablefordGame ? player.place === 1 : dg ? dg.place === 1 : idx === 0) ? '#f59e0b' : '#e5e7eb',
                                color: (isStablefordGame ? player.place === 1 : dg ? dg.place === 1 : idx === 0) ? '#fff' : '#6b7280', fontSize: 12, fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{isStroke ? strokePlayPlace(game, player) : isStablefordGame ? (player.place_label || '—') : dg ? dg.place : idx + 1}</span>
                              <div>
                                <div style={{ fontWeight: isMe ? 700 : 600, fontSize: 14, color: '#1f2937' }}>
                                  {player.player_name}{isMe ? ' (you)' : ''}
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                  {isStroke ? strokePlayStat(game, player, false) : <>{statLabel}: {player[statKey] ?? '—'}{isDotsGame && player.breakdown ? ` (${player.breakdown})` : ''}</>}
                                </div>
                                {!isFinalized && player.last_hole_entered != null && (
                                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                                    Last entry: H{player.last_hole_entered}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              {isStroke ? (() => {
                                // The score, big, like the Scores tab — live and after finalize;
                                // the money (if any) sits under it once the round is finalized.
                                const sc = strokePlayScore(game, player);
                                return (
                                  <>
                                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 6 }}>
                                      <span style={{ fontWeight: 800, fontSize: 22, color: '#1f2937', lineHeight: 1.1 }}>{sc.total}</span>
                                      {sc.vs && <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{sc.vs}</span>}
                                    </div>
                                    {isFinalized && !noMoney ? (
                                      <div style={{ fontSize: 12, fontWeight: 800, color: net > 0 ? '#166534' : net < 0 ? '#dc2626' : '#6b7280' }}>
                                        {net === 0 ? 'Even' : `${net > 0 ? '+' : '−'}$${Math.abs(net).toFixed(2)} ${net > 0 ? 'collect' : 'owe'}${isTeamRow && (player.members || []).length > 1 ? ' each' : ''}`}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{sc.comp}{sc.thru ? ` · ${sc.thru}` : ''}</div>
                                    )}
                                  </>
                                );
                              })() : isFinalized ? (
                                noMoney ? (
                                  <div style={{ fontWeight: 700, fontSize: 16, color: '#9ca3af' }} title="Results only — no money settled">—</div>
                                ) : (
                                <>
                                  <div style={{ fontWeight: 800, fontSize: 16,
                                    color: net > 0 ? '#166534' : net < 0 ? '#dc2626' : '#6b7280' }}>
                                    {net > 0 ? '+' : ''}{net === 0 ? 'Even' : `$${Math.abs(net).toFixed(2)}`}
                                  </div>
                                  {net !== 0 && (
                                    <div style={{ fontSize: 11, color: net > 0 ? '#166534' : '#dc2626' }}>
                                      {net > 0 ? 'collect' : 'owe'}
                                    </div>
                                  )}
                                </>
                                )
                              ) : (
                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                  {player[statKey] != null ? `${player[statKey]} ${statLabel.toLowerCase()}` : '—'}
                                </div>
                              )}
                            </div>
                          </div>
                          </React.Fragment>
                        );
                      }); }}</SegmentSwitch>
                      {isNassauGame && <NassauMatches game={game} viewerId={liveStandings.player_id} />}
                      {game.game_type === 'match_play' && <MatchPlayMatches game={game} viewerId={liveStandings.player_id} />}
                      {isSixesGame && <SixesMatches game={game} viewerId={liveStandings.player_id} />}
                      {/* Opted-in players with no scores yet (Sept 7, 2026) — the engine's
                          `totals` only lists players who have scored, so a player who hasn't
                          started (or the whole field, pre-round) vanished from this board. */}
                      {!isFinalized && !game.error && (() => {
                        // Best Ball rows are teams (Sept 23, 2026): its scored players are the teams' members.
                        const scoredIds = new Set(players.flatMap(p => (p.player_id == null && Array.isArray(p.members)) ? p.members.map(m => m.player_id) : [p.player_id]));
                        const notStarted = (game.participants || []).filter(p => !scoredIds.has(p.player_id));
                        if (players.length === 0 && notStarted.length === 0) {
                          return <div style={{ padding: '10px 14px', fontSize: 13, color: '#9ca3af' }}>No one has opted in yet.</div>;
                        }
                        return notStarted.map((p) => {
                          const isMe = p.player_id === liveStandings.player_id;
                          return (
                            <div key={p.player_id} style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '10px 14px', borderTop: '1px solid #f3f4f6',
                              background: isMe ? '#f0fdf4' : 'transparent'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#f3f4f6', color: '#9ca3af', fontSize: 12, fontWeight: 700,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>–</span>
                                <div style={{ fontWeight: isMe ? 700 : 600, fontSize: 14, color: '#6b7280' }}>
                                  {p.player_name}{isMe ? ' (you)' : ''}
                                </div>
                              </div>
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>Not started</div>
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* Hole results toggle — skins only */}
                    {isSkins && holeResults.length > 0 && (
                      <div>
                        <button
                          onClick={() => setExpandedGames(prev => ({ ...prev, [game.game_id]: !prev[game.game_id] }))}
                          style={{ width: '100%', padding: '10px 14px', background: '#f9fafb', border: 'none',
                            borderTop: '1px solid #e5e7eb', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            color: '#374151', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Hole Results</span>
                          <span>{isExpanded ? '▲' : '▼'}</span>
                        </button>

                        {isExpanded && (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                              <thead>
                                <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                                  {['Hole','Par','Player','Gross','Net','$ Amt'].map(h => (
                                    <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Player' ? 'left' : 'center',
                                      fontWeight: 700, color: '#374151', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {holeResults.map((hole, hi) => {
                                  // Engine returns net_scores array — find winner's gross and net
                                  const winnerScores = hole.winner_id
                                    ? hole.net_scores?.find(s => s.player_id === hole.winner_id)
                                    : null;
                                  const grossDisplay = winnerScores?.gross ?? '—';
                                  const netDisplay = winnerScores?.net ?? '—';
                                  // For pot games, compute actual skin value = total pot / total skins won
                                  // For individual skins, skin_value from engine is correct (fixed bet per hole)
                                  const actualSkinValue = (() => {
                                    if (!isPot) return hole.skin_value;
                                    const betAmt3 = parseFloat(game.bet_amount) || 0;
                                    const totalPot3 = betAmt3 * players.length;
                                    const totalSkins3 = players.reduce((s, t) => s + (t.skins_won || 0), 0);
                                    return totalSkins3 > 0 ? totalPot3 / totalSkins3 : 0;
                                  })();
                                  const amtDisplay = actualSkinValue > 0 ? `$${parseFloat(actualSkinValue).toFixed(2)}` : '—';
                                  const isTied = hole.is_tied;
                                  return (
                                  <tr key={hi} style={{ borderBottom: '1px solid #f3f4f6',
                                    background: hi % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#1f2937' }}>{hole.hole_number}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center', color: '#6b7280' }}>{hole.par ?? '—'}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'left', color: isTied ? '#9ca3af' : '#1f2937', fontStyle: isTied ? 'italic' : 'normal' }}>
                                      {isTied ? 'Push' : hole.winner_name ?? '—'}
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center', color: '#374151' }}>{isTied ? '—' : grossDisplay}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center', color: '#374151' }}>{isTied ? '—' : netDisplay}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700,
                                      color: isTied ? '#9ca3af' : '#166534' }}>
                                      {isTied ? '—' : !isFinalized ? '—' : amtDisplay}
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </div>
          )}
          </div>
          )}{/* end Games sub-tab */}

          {/* ── SCORING SUB-TAB (in-app live leaderboard, gross/net) ── */}
          {resultsSubTab === 'scoring' && (() => {
            // getScoreBg is in scope (defined in LiveScoring). vs-par chip color
            // matches the scorecard. Rows are ordered by sortBoardRows (shared with
            // the other renderers), sections preserved.
            const SECTION_LABEL = { live: 'Live Ranking', not_current: 'Scoring Not Current', no_scores: 'No Scores Recorded' };
            const lb = leaderboard;
            // A team board — a Best Ball game on this round (Sept 27, 2026) — renders one
            // row per team through the shared TeamBoardRows, as the other boards do.
            const lbTeam = !!(lb && lb.scoring_mode === 'team');
            const lbShowNet = !lbTeam || lb.has_net === true;
            const isNet = leaderboardView === 'net' && lbShowNet;

            // Format a vs-par number as E / +n / -n
            const fmtVs = (v) => v == null ? '—' : (v === 0 ? 'E' : (v > 0 ? `+${v}` : `${v}`));

            // Build the displayed rows — one shared sort for every board (Sept 23,
            // 2026): the game's finishing order on a view it ranks, the plain key
            // (to-par, then further along) otherwise; sections preserved.
            const displayRows = sortBoardRows(lb?.rows, isNet, lb);
            const rankedView = boardRankedOn(lb, isNet);   // show the game's places + marks
            const lbTieNotes = rankedView ? tieFootnotes(lb, displayRows, isNet ? 'net' : 'gross') : [];

            return (
              <div>
                {leaderboardLoading ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 16 }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                    Loading leaderboard...
                  </div>
                ) : leaderboardError ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
                    <div style={{ color: '#dc2626', fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Unable to load leaderboard</div>
                    <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>{leaderboardError}</div>
                    <button
                      onClick={() => { setLeaderboard(null); setLeaderboardError(null); loadLeaderboard(); }}
                      style={{ padding: '10px 24px', background: '#166534', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                      🔄 Retry
                    </button>
                  </div>
                ) : !lb || !lb.rows || lb.rows.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 15 }}>
                    No players to show yet.
                    <button
                      onClick={() => { setLeaderboard(null); setLeaderboardError(null); loadLeaderboard(); }}
                      style={{ marginTop: 16, padding: '8px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}>
                      🔄 Refresh
                    </button>
                  </div>
                ) : (
                  <div>
                    {/* Header: course, through, updated, refresh */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 16, color: '#1f2937' }}>{lb.course_name}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
                          {lb.through > 0 ? `Through ${lb.through}` : 'Not started'}{holeSetSuffix(lb)}
                          {lbTeam ? boardCountText(lb) : (lb.player_count != null ? ` · ${lb.player_count} players` : '')}
                          {lb.teetime_ids && lb.teetime_ids.length > 1 ? ` · ${lb.teetime_ids.length} tee times` : ''}
                        </div>
                      </div>
                      <button
                        onClick={() => { setLeaderboard(null); setLeaderboardError(null); loadLeaderboard(); }}
                        style={{ padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                        🔄 Refresh
                      </button>
                    </div>

                    {/* Gross / Net toggle — hidden on a team board with no net */}
                    {lbShowNet && (
                    <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: 12 }}>
                      <button
                        onClick={() => setLeaderboardView('gross')}
                        style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                          background: !isNet ? '#1f2937' : '#f9fafb', color: !isNet ? '#fff' : '#6b7280' }}>
                        Gross
                      </button>
                      <button
                        onClick={() => setLeaderboardView('net')}
                        style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                          borderLeft: '1px solid #e5e7eb',
                          background: isNet ? '#1f2937' : '#f9fafb', color: isNet ? '#fff' : '#6b7280' }}>
                        Net
                      </button>
                    </div>
                    )}

                    {/* Rows, grouped by section */}
                    {lbTeam ? (
                      <TeamBoardRows board={lb} viewerId={playerData?.id} onOpenTeam={setOpenPlayerId} isNet={isNet} />
                    ) : (() => {
                      let lastSection = null;
                      return displayRows.map((r, idx) => {
                        const isMe = playerData && r.player_id === playerData.id;
                        const total = isNet ? r.net_total : r.gross_total;
                        const vs = isNet ? r.net_vs_par : r.gross_vs_par;
                        // Score is the hero (mixed-handicap amateurs read the raw
                        // score, not vs-par). vs-par is quiet secondary text; under
                        // par green, over par grey, no colored chip.
                        const vsColor = vs == null ? '#9ca3af' : (vs < 0 ? '#166534' : vs > 0 ? '#9ca3af' : '#6b7280');
                        const thruColor = r.entry_flag === 'red' ? '#dc2626'
                                        : r.entry_flag === 'amber' ? '#f59e0b' : '#6b7280';
                        const showHeader = r.section !== lastSection;
                        lastSection = r.section;
                        // Net view appends the handicap qualifier — but ONLY to the
                        // LIVE RANKING header, where net is actually shown. It would be
                        // meaningless on 'not_current' or 'no_scores' (no net there).
                        const headerText = boardSectionHeader(SECTION_LABEL[r.section], r.section, isNet, lb);
                        return (
                          <React.Fragment key={r.player_id}>
                            {showHeader && (
                              <div style={{ margin: '14px 2px 6px' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                                  color: r.section === 'not_current' ? '#dc2626' : '#9ca3af' }}>
                                  {headerText}
                                </div>
                                {r.section === 'no_scores' && (
                                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginTop: 2, lineHeight: 1.4 }}>
                                    These players either haven't started their round, or aren't recording scores live.
                                  </div>
                                )}
                              </div>
                            )}
                            <div
                              onClick={r.has_scores ? () => setOpenPlayerId(r.player_id) : undefined}
                              style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '10px 12px', borderRadius: 10, marginBottom: 4,
                              background: isMe ? '#f0fdf4' : '#fff',
                              border: isMe ? '1px solid #bbf7d0' : '1px solid #f3f4f6',
                              cursor: r.has_scores ? 'pointer' : 'default'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                {/* The game's place (Sept 23, 2026): "1", "2 SP", "T3" — only on a view the game ranks; "–" for a player not in the game. */}
                                {rankedView && r.section === 'live' && (() => { const pl = boardPlace(r, isNet); return (
                                  <div style={{ minWidth: 30, textAlign: 'center', fontSize: 15, fontWeight: 800, color: pl.label ? '#1f2937' : '#d1d5db', flexShrink: 0 }}>
                                    {pl.label || '–'}<TieMark mark={pl.mark} />
                                  </div>
                                ); })()}
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: isMe ? 700 : 600, fontSize: 14, color: '#1f2937',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {r.name}{isMe ? ' (you)' : ''}
                                  </div>
                                  <div style={{ fontSize: 11, color: thruColor, marginTop: 1, fontWeight: r.entry_flag !== 'none' ? 700 : 400 }}>
                                    {r.has_scores ? `Thru ${r.thru}` : 'No scores'}
                                    {boardHcpText(r, lb) ? ` · ${boardHcpText(r, lb)}` : ''}
                                    {rankedView && boardPlace(r, isNet).status === 'pending' && <span style={{ color: '#b45309', fontWeight: 700 }}> · Playoff pending</span>}
                                  </div>
                                </div>
                              </div>
                              {/* Score is the hero; vs-par is the quiet sidekick; the other basis sits under them. */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                  <span style={{ fontWeight: 800, fontSize: 22, color: '#1f2937', minWidth: 30, textAlign: 'right' }}>
                                    {total != null ? total : '—'}
                                  </span>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: vsColor, minWidth: 30, textAlign: 'right' }}>
                                    {fmtVs(vs)}
                                  </span>
                                </div>
                                {otherBasisText(r, isNet) && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1, whiteSpace: 'nowrap' }}>{otherBasisText(r, isNet)}</div>}
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      });
                    })()}

                    <CtpBoardSection ctp={lb.ctp} nines={lb.course_nines || null} />

                    {lbTieNotes.length > 0 && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, lineHeight: 1.5 }}>
                        {lbTieNotes.map(t => <div key={t}>{t}</div>)}
                      </div>
                    )}

                    <div style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 12 }}>
                      {lb.live ? 'Live · updates when you refresh' : (lb.through > 0 ? 'Round complete' : 'Not started')}
                    </div>

                    {openPlayerId && (
                      <PlayerScorecardModal
                        player={(lb.rows || []).find(r => r.player_id === openPlayerId)}
                        board={lb}
                        hideNet={lbTeam && lb.has_net !== true}
                        onClose={() => setOpenPlayerId(null)}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })()}{/* end Scoring sub-tab */}
        </div>
      )}

      {/* ── SCORECARD TAB (existing UI, show only when scorecard tab active) ─ */}
      {scoringTab === 'scorecard' && (
        <div>

      {/* ===== Follow-along read-only self-view (Feature A / Feature C minimal, Slice 2).
              Shown when the scorer unchecked themselves and selected nobody — they're
              just watching. Read-only card of their own holes + Live badge. Results and
              Celebrations tabs remain fully usable (this only replaces the entry UI). ===== */}
      {followAlongOnly ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1f2937' }}>My Scorecard</div>
              {playerData?.name && (
                <div style={{ fontSize: 13, fontWeight: 400, color: '#6b7280', marginTop: 1 }}>{playerData.name}</div>
              )}
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#15803d', background: '#f0fdf4', padding: '4px 10px', borderRadius: 999, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }}></span>Live
            </span>
          </div>
          {(() => {
            let gross = 0, putts = 0, thru = 0, vsPar = 0;
            for (let h = 1; h <= 18; h++) { const s = scores[h]; const hd = holeData.find(x => x.hole_number === h); if (s?.score > 0) { gross += s.score; thru++; if (hd?.par) vsPar += (s.score - hd.par); if (s.putts != null) putts += s.putts; } }
            const vsParDisplay = thru ? (vsPar === 0 ? 'E' : (vsPar > 0 ? `+${vsPar}` : vsPar)) : '—';
            return (
              <div style={{ display: 'flex', background: '#1f2937', borderRadius: 12, padding: '12px 8px', gap: 4, marginBottom: 14 }}>
                {[['THRU', thru], ['GROSS', gross || '—'], ['PUTTS', putts || '—'], ['VS PAR', vsParDisplay]].map(([lbl, val]) => (
                  <div key={lbl} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: 0.5 }}>{lbl}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: lbl === 'VS PAR' && thru ? (vsPar < 0 ? '#4ade80' : vsPar > 0 ? '#fbbf24' : '#fff') : '#fff' }}>{val}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          {/* Column labels */}
          <div style={{ display: 'grid', gridTemplateColumns: '34px 1fr 56px 50px', alignItems: 'center', gap: 8, padding: '0 6px 6px' }}>
            <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: 0.5 }}>HOLE</div>
            <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: 0.5 }}>PAR</div>
            <div style={{ fontSize: 10, color: '#4b5563', letterSpacing: 0.5, textAlign: 'center' }}>SCORE</div>
            <div style={{ fontSize: 10, color: '#4b5563', letterSpacing: 0.5, textAlign: 'center' }}>PUTTS</div>
          </div>
          {/* Card rows — one per hole. Played holes show a colored score chip; unplayed fade. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Array.from({ length: 18 }, (_, i) => i + 1).map(h => {
              const s = scores[h]; const hd = holeData.find(x => x.hole_number === h);
              const has = s?.score > 0;
              const chipBg = has ? getScoreBg(s.score, hd?.par) : '#f3f4f6';
              return (
                <div key={h} style={{ display: 'grid', gridTemplateColumns: '34px 1fr 56px 50px', alignItems: 'center', gap: 8, background: has ? '#fff' : 'transparent', border: has ? '1px solid #e5e7eb' : '1px solid transparent', borderRadius: 10, padding: '8px 4px 8px 10px', opacity: has ? 1 : 0.4 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>{h}</div>
                  <div style={{ fontSize: 13, color: '#9ca3af' }}>Par {hd?.par || '—'}</div>
                  <div style={{ textAlign: 'center' }}>
                    {has ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 30, height: 30, padding: '0 7px', borderRadius: 8, background: chipBg, color: '#fff', fontSize: 16, fontWeight: 800 }}>{s.score}</span>
                    ) : (
                      <span style={{ color: '#c9cdd3', fontSize: 15 }}>—</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'center', fontSize: 15, fontWeight: 600, color: (s?.putts != null) ? '#374151' : '#c9cdd3' }}>{(s?.putts != null) ? s.putts : '—'}</div>
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: 'center', padding: '12px 0 4px', fontSize: 12, color: '#9ca3af' }}>Updates as your scores are entered · view only</div>
          <button onClick={refreshSelfView} disabled={selfRefreshing}
            style={{ width: '100%', marginTop: 8, padding: '10px', border: '1px solid #d1d5db', borderRadius: 10, background: '#fff', cursor: selfRefreshing ? 'default' : 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>
            {selfRefreshing ? 'Refreshing…' : '↻ Refresh'}
          </button>
          {selfLastUpdated && (
            <div style={{ textAlign: 'center', marginTop: 6, fontSize: 11, color: '#9ca3af' }}>
              Updated {new Date(selfLastUpdated).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
          )}
          <div onClick={openPicker} style={{ marginTop: 10, padding: '12px', border: '1px dashed #d1d5db', borderRadius: 10, textAlign: 'center', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
            Start scoring instead
          </div>
        </div>
      ) : (<>

      {/* Hole navigation (Oct 4, 2026 — hole screen redesign, Option B): two rows of 9, bigger
          boxes for older eyes and bigger fingers; the score colors stay (players read their round
          from them); a stroke hole carries a dot instead of an asterisk — the "you get a stroke
          here" line under the hole title is gone. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, minmax(0, 1fr))', gap: 5, marginBottom: 10 }}>
        {Array.from({length: 18}, (_, i) => i + 1).map(h => {
          const s = scores[h]; const hd = holeData.find(x => x.hole_number === h);
          const hasScore = s?.score > 0;
          const isCurrent = currentHole === h;
          const isBackfill = backfillSet.has(h);  // a kept player is missing a score on this played hole
          const st = hd ? getStrokesOnHole(hd.handicap_index) : 0;   // your course-handicap strokes on this hole (Sept 14, 2026: asterisk, as on a printed card)
          return (
            <button key={h} onClick={() => {
              // Only block forward jumps to an unfilled future hole when current hole has no score.
              // Going back to a previous hole, or jumping to an already-scored hole, is always allowed —
              // players need to be able to correct earlier mistakes (and backfill earlier holes).
              // Multi-aware: in multi-mode the "has a score" test considers self (if scoring self)
              // OR any kept target, matching the Next button's advance-block.
              // Empty card (Oct 15, 2026): nothing to block — the hole you tap is where you start.
              if (liveCardHoles.length > 0 && holePos(h) > holePos(currentHole) && !(scores[h]?.score > 0)) {
                const selfHasHere = scoringSelf ? currentScore.score > 0 : false;
                const anyTargetHere = multiMode && Object.values(targetScores).some(hm => (hm[currentHole]?.score || 0) > 0);
                const blocked = multiMode ? (!selfHasHere && !anyTargetHere) : (currentScore.score === 0);
                if (blocked) { setShowScoreRequired(true); return; }
              }
              setCurrentHole(h);
            }}
              title={isBackfill ? 'A kept player is missing a score on this hole' : (st > 0 ? `You get ${st === 1 ? 'a stroke' : st + ' strokes'} on hole ${h} — course handicap; game strokes may differ` : undefined)}
              aria-label={`Hole ${h}${hasScore ? `, scored ${s.score}` : ''}${st > 0 ? `, ${st === 1 ? 'a stroke' : st + ' strokes'}` : ''}`}
              style={{
                height: 38, borderRadius: 8, position: 'relative',
                border: isCurrent ? '3px solid #111827' : (isBackfill ? '2px solid #EF9F27' : '2px solid #d1d5db'),
                background: isBackfill ? '#FAEEDA' : (hasScore ? getScoreBg(s.score, hd?.par) : '#fff'),
                color: isBackfill ? '#854F0B' : (hasScore ? '#fff' : '#111827'),
                fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >{liveNines && h > 9 ? h - 9 : h}{st > 0 && <span aria-hidden="true" style={{ position: 'absolute', top: 2, right: 3, display: 'flex', gap: 2 }}>{Array.from({ length: Math.min(st, 2) }, (_, k) => <span key={k} style={{ width: 7, height: 7, borderRadius: '50%', background: (hasScore && !isBackfill) ? '#fff' : '#111827', boxShadow: (hasScore && !isBackfill) ? 'none' : '0 0 0 1.5px #fff' }} />)}</span>}</button>
          );
        })}
      </div>

      {/* ===== Backfill nudge (Feature A, Slice 2b). Skippable reminder, never blocks.
              Shows the earliest hole with a gap; tapping jumps there. ===== */}
      {multiMode && backfillHoles.length > 0 && (
        <div
          onClick={() => setCurrentHole(backfillHoles[0])}
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '10px 12px', background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: 10, cursor: 'pointer' }}>
          <span style={{ fontSize: 16 }}>⛳</span>
          <span style={{ fontSize: 13, color: '#633806', flex: 1 }}>
            {backfillHoles.length === 1
              ? `A kept player is missing hole ${backfillHoles[0]}.`
              : `A kept player is missing ${backfillHoles.length} earlier holes.`} Tap to fill in.
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#854F0B', whiteSpace: 'nowrap' }}>{holeText(backfillHoles[0], liveNines)} →</span>
        </div>
      )}
      
      {/* Current hole card */}
      {currentHoleData && (
        <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 14, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          {/* Hole info */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 40, fontWeight: 900, color: '#166534', lineHeight: 1 }}>
              {liveNines && <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>{currentHole <= 9 ? liveNines.front : liveNines.back}</span>}Hole {liveNines && currentHole > 9 ? currentHole - 9 : currentHole}{strokes > 0 && <span title={`You get ${strokes === 1 ? 'a stroke' : strokes + ' strokes'} here${courseHcp !== null ? ` — course handicap ${courseHcp}` : ''}. A game's strokes may differ.`}>{'*'.repeat(strokes)}</span>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, fontSize: 17, color: '#6b7280', marginTop: 8 }}>
              <span>Par <strong style={{ color: '#1f2937', fontSize: 19 }}>{currentHoleData.par}</strong></span>
              <span><strong>{currentHoleData.yardage}</strong> yds</span>
              <span>HCP <strong>{currentHoleData.handicap_index}</strong></span>
            </div>
            {/* Stroke hole: the * on the title and the dots on the hole strip say it (Dennis, Oct 4,
                2026 — the explanation line came off to save room; "game strokes may differ" lives
                in the games' own view and the Player Guide). */}
            {/* Closest to the Pin (Sept 28, 2026): the claim button on a CTP hole — for you, or for a
                player whose card you keep. */}
            <CtpClaimBar ctp={scorecard?.ctp} hole={currentHole} token={token} teetimeId={selectedTeetimeId} selfId={playerData?.id}
              options={[
                ...(scoringSelf ? [{ id: playerData?.id, name: playerData?.name || 'You' }] : []),
                ...(multiMode ? (pairingMembers || []).filter(m => selectedTargets.includes(m.player_id)).map(m => ({ id: m.player_id, name: m.name })) : []),
              ].filter(o => o.id)} />
            {/* Sixes (Oct 5, 2026): the Carts card, before the first score only. */}
            {!ghinPosted && (
              <SixesCartsCard sixes={scorecard?.sixes} token={token} teetimeId={selectedTeetimeId} selfId={playerData?.id}
                anyScore={Object.values(scores || {}).some(v => (v?.score || 0) > 0)} onOpenProfile={onOpenProfile} />
            )}
            {/* ===== Yards-to-Center readout (DB-driven; shows ±accuracy for on-course validation) ===== */}
            {ytcEnabled && (
              <div style={{ marginTop: 6 }}>
                {ytcStatus === 'ok' && ytcYards != null ? (
                  <span style={{ fontSize: 26, fontWeight: 800, color: '#374151' }}>
                    Yards to Center: {ytcYards}
                    {!ytcDemo && (
                      <span style={{ fontSize: 13, color: '#9ca3af', fontWeight: 400 }}>
                        {' '}(±{ytcAcc ? Math.round(ytcAcc) : '?'}m)
                      </span>
                    )}
                  </span>
                ) : (
                  <span style={{ fontSize: 17, color: '#6b7280' }}>
                    {{
                      starting: 'locating…',
                      coarse: `GPS settling (±${ytcAcc ? Math.round(ytcAcc) : '?'}m)…`,
                      denied: 'location off',
                      nodata: `no data hole ${currentHole}`,
                      nofix: 'no GPS',
                      range: 'out of range (>600)',
                      off: '',
                    }[ytcStatus] || ''}
                  </span>
                )}
              </div>
            )}
            {/* ===== END Yards-to-Center readout ===== */}
          </div>
          
          {/* ===== SOLO score/putts entry (Feature A: shown only when NOT multi-mode). ===== */}
          {!multiMode && (<>
          {/* SCORE input */}
          <div style={{ textAlign: 'center', marginBottom: 20, opacity: ghinPosted ? 0.5 : 1, pointerEvents: ghinPosted ? 'none' : 'auto' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#6b7280', marginBottom: 8, letterSpacing: 1 }}>SCORE</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
              <button onClick={() => updateScore(currentHole, 'score', -1)} disabled={ghinPosted}
                style={{ width: 64, height: 64, borderRadius: 12, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 32, fontWeight: 800, cursor: ghinPosted ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>−</button>
              <div
                onClick={() => !ghinPosted && tapParScore(currentHole, currentHoleData.par)}
                style={{
                  width: 96, height: 96, borderRadius: 14,
                  background: currentScore.score > 0 ? getScoreBg(currentScore.score, currentHoleData.par) : '#f3f4f6',
                  color: currentScore.score > 0 ? '#fff' : '#c9cdd3',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  fontSize: currentScore.picked ? 40 : 48, fontWeight: 900, cursor: ghinPosted ? 'default' : 'pointer', lineHeight: 1,
                  transition: 'all 0.15s ease'
                }}>
                {/* Scoring Enhancement C — greyed par default (May 2026):
                    If a score is entered, show it (dark/active styling above).
                    If no score yet but par is known, show par in the inactive (greyed) color
                    so a single tap on the box commits par. Falls back to '—' when par
                    is unavailable for the hole (graceful degradation). */}
                {currentScore.score > 0
                  ? (currentScore.picked ? `${currentScore.score}x` : currentScore.score)
                  : (currentHoleData.par ? currentHoleData.par : '—')}
              </div>
              <button onClick={() => updateScore(currentHole, 'score', 1)} disabled={ghinPosted}
                style={{ width: 64, height: 64, borderRadius: 12, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 32, fontWeight: 800, cursor: ghinPosted ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>+</button>
            </div>
            {/* Picked up ("7x", Sept 27, 2026): scored as a net double bogey */}
            {!ghinPosted && pickupBtn('self', true)}
            {/* Score label & adjusted */}
            {currentScore.score > 0 && (
              <div style={{ marginTop: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: getScoreBg(currentScore.score, currentHoleData.par) }}>{currentScore.picked ? 'Picked up — net double bogey' : getScoreLabel(currentScore.score, currentHoleData.par)}</span>
                {!currentScore.picked && adj > 0 && adj !== currentScore.score && (
                  <span style={{ fontSize: 14, color: '#dc2626', fontWeight: 600, marginLeft: 12 }}>Adj: {adj}</span>
                )}
              </div>
            )}
          </div>
          
          {/* PUTTS input */}
          <div style={{ textAlign: 'center', marginBottom: 20, opacity: ghinPosted ? 0.5 : 1, pointerEvents: ghinPosted ? 'none' : 'auto' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#6b7280', marginBottom: 8, letterSpacing: 1 }}>PUTTS</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
              <button onClick={() => updateScore(currentHole, 'putts', -1)} disabled={ghinPosted}
                style={{ width: 52, height: 52, borderRadius: 10, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 26, fontWeight: 800, cursor: ghinPosted ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>−</button>
              <div
                onClick={() => !ghinPosted && tapZeroPutts(currentHole)}
                style={{
                  width: 72, height: 72, borderRadius: 12,
                  background: '#f3f4f6', border: '2px solid #e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 36, fontWeight: 800,
                  color: (currentScore.putts !== null && currentScore.putts !== undefined) ? '#374151' : '#c9cdd3',
                  cursor: ghinPosted ? 'default' : 'pointer',
                  transition: 'all 0.15s ease'
                }}>
                {/* Putts: mirrors score's greyed-par pattern.
                    null/undefined = not entered → show 0 in greyed color (single tap to commit chip-in).
                    Number (including 0) = entered → show in dark/active color.
                    Tap toggles between 0 (entered chip-in) and null (unentered). */}
                {(currentScore.putts !== null && currentScore.putts !== undefined) ? currentScore.putts : 0}
              </div>
              <button onClick={() => updateScore(currentHole, 'putts', 1)} disabled={ghinPosted}
                style={{ width: 52, height: 52, borderRadius: 10, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 26, fontWeight: 800, cursor: ghinPosted ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>+</button>
            </div>
          </div>
          </>)}{/* end solo entry */}

          {/* ===== MULTI-PLAYER rows (Feature A: shown only in multi-mode). One row per
                  kept player — self (from `scores`) plus each target (from `targetScores`).
                  Same compact −/box/+ controls as solo, stacked per player. ===== */}
          {multiMode && !ghinPosted && (
            <div style={{ marginBottom: 16 }}>
              {/* column header — the PLAYER label is dropped on narrow screens (the name
                  has its own line there) but its cell stays so SCORE/PUTTS keep their tracks */}
              <div style={{ display: 'grid', gridTemplateColumns: multiRowGridCols, gap: '4px 8px', alignItems: 'center', padding: '0 2px 6px', fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5 }}>
                <div>{narrowRows ? '' : 'PLAYER'}</div>
                <div style={{ width: 132, textAlign: 'center' }}>SCORE</div>
                <div style={{ width: 132, textAlign: 'center' }}>PUTTS</div>
              </div>

              {/* self row (only when scoring self) */}
              {scoringSelf && (() => {
                const sc = scores[currentHole] || { score: 0, putts: null };
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: multiRowGridCols, gap: '6px 8px', alignItems: 'center', padding: '8px 2px', borderTop: '1px solid #f3f4f6' }}>
                    <div style={multiNameCellStyle}>
                      <div style={multiNameTextStyle}>You{pickupBtn('self')}</div>
                      {sc.score > 0 && <div style={{ fontSize: 11, color: getScoreBg(sc.score, currentHoleData.par), fontWeight: 600 }}>{sc.picked ? 'Picked up' : getScoreLabel(sc.score, currentHoleData.par)}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: 132 }}>
                      <button onClick={() => updateScore(currentHole, 'score', -1)} style={{ width: 34, height: 38, borderRadius: 8, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 20, fontWeight: 800, cursor: 'pointer', color: '#374151' }}>−</button>
                      <div onClick={() => tapParScore(currentHole, currentHoleData.par)} style={{ width: 44, height: 44, borderRadius: 10, background: sc.score > 0 ? getScoreBg(sc.score, currentHoleData.par) : '#f3f4f6', color: sc.score > 0 ? '#fff' : '#c9cdd3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: sc.picked ? 17 : 22, fontWeight: 900, cursor: 'pointer' }}>{sc.score > 0 ? (sc.picked ? `${sc.score}x` : sc.score) : (currentHoleData.par || '—')}</div>
                      <button onClick={() => updateScore(currentHole, 'score', 1)} style={{ width: 34, height: 38, borderRadius: 8, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 20, fontWeight: 800, cursor: 'pointer', color: '#374151' }}>+</button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: 132 }}>
                      <button onClick={() => updateScore(currentHole, 'putts', -1)} style={{ width: 34, height: 38, borderRadius: 8, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 20, fontWeight: 800, cursor: 'pointer', color: '#6b7280' }}>−</button>
                      <div onClick={() => tapZeroPutts(currentHole)} style={{ width: 44, height: 44, borderRadius: 10, background: '#f3f4f6', border: '2px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: (sc.putts !== null && sc.putts !== undefined) ? '#374151' : '#c9cdd3', cursor: 'pointer' }}>{(sc.putts !== null && sc.putts !== undefined) ? sc.putts : 0}</div>
                      <button onClick={() => updateScore(currentHole, 'putts', 1)} style={{ width: 34, height: 38, borderRadius: 8, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 20, fontWeight: 800, cursor: 'pointer', color: '#6b7280' }}>+</button>
                    </div>
                  </div>
                );
              })()}

              {/* target rows */}
              {Object.keys(targetScores).map(pid => {
                const tc = (targetScores[pid] && targetScores[pid][currentHole]) || { score: 0, putts: null };
                const skipped = !!(targetSkips[pid] && targetSkips[pid][currentHole]);
                // Backfill highlight: this kept player has no score on this already-played hole.
                const missingHere = !skipped && tc.score === 0 && holePlayed(currentHole);
                return (
                  <div key={pid} style={{ display: 'grid', gridTemplateColumns: multiRowGridCols, gap: '6px 8px', alignItems: 'center', padding: '8px 2px', borderTop: '1px solid #f3f4f6', background: missingHere ? '#FEFBF3' : 'transparent', borderLeft: missingHere ? '3px solid #EF9F27' : '3px solid transparent', borderRadius: missingHere ? 4 : 0 }}>
                    <div style={multiNameCellStyle}>
                      <div style={multiNameTextStyle}>{memberName(pid)}{!skipped && pickupBtn(pid)}</div>
                      {skipped
                        ? <div style={{ fontSize: 11, color: '#854d0e', fontWeight: 600 }}>they entered this hole</div>
                        : missingHere
                          ? <div style={{ fontSize: 11, color: '#854F0B', fontWeight: 600 }}>needs a score</div>
                          : (tc.score > 0 && <div style={{ fontSize: 11, color: getScoreBg(tc.score, currentHoleData.par), fontWeight: 600 }}>{tc.picked ? 'Picked up' : getScoreLabel(tc.score, currentHoleData.par)}</div>)}
                      {/* Running totals for this player — the scorer's answer to "how many putts do I have?" */}
                      {renderTargetTotals(pid)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: 132, opacity: skipped ? 0.5 : 1 }}>
                      <button onClick={() => updateTargetScore(pid, 'score', -1)} style={{ width: 34, height: 38, borderRadius: 8, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 20, fontWeight: 800, cursor: 'pointer', color: '#374151' }}>−</button>
                      <div onClick={() => tapParTarget(pid, currentHoleData.par)} style={{ width: 44, height: 44, borderRadius: 10, background: tc.score > 0 ? getScoreBg(tc.score, currentHoleData.par) : '#f3f4f6', color: tc.score > 0 ? '#fff' : '#c9cdd3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: tc.picked ? 17 : 22, fontWeight: 900, cursor: 'pointer' }}>{tc.score > 0 ? (tc.picked ? `${tc.score}x` : tc.score) : (currentHoleData.par || '—')}</div>
                      <button onClick={() => updateTargetScore(pid, 'score', 1)} style={{ width: 34, height: 38, borderRadius: 8, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 20, fontWeight: 800, cursor: 'pointer', color: '#374151' }}>+</button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: 132, opacity: skipped ? 0.5 : 1 }}>
                      <button onClick={() => updateTargetScore(pid, 'putts', -1)} style={{ width: 34, height: 38, borderRadius: 8, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 20, fontWeight: 800, cursor: 'pointer', color: '#6b7280' }}>−</button>
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f3f4f6', border: '2px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: (tc.putts !== null && tc.putts !== undefined) ? '#374151' : '#c9cdd3' }}>{(tc.putts !== null && tc.putts !== undefined) ? tc.putts : 0}</div>
                      <button onClick={() => updateTargetScore(pid, 'putts', 1)} style={{ width: 34, height: 38, borderRadius: 8, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 20, fontWeight: 800, cursor: 'pointer', color: '#6b7280' }}>+</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Dots (Oct 4, 2026): Sandy / Poley for each player on the card in the Dots game. */}
          {!ghinPosted && (
            <DotsTapRow dots={scorecard?.dots} hole={currentHole} token={token} teetimeId={selectedTeetimeId} selfId={playerData?.id}
              options={[
                ...(scoringSelf ? [{ id: playerData?.id, name: playerData?.name || 'You' }] : []),
                ...(multiMode ? (pairingMembers || []).filter(m => selectedTargets.includes(m.player_id)).map(m => ({ id: m.player_id, name: m.name })) : []),
              ].filter(o => o.id)} />
          )}

          {/* ===== "+ Score for another player" affordance / "Edit players" (Feature A). =====
               Solo: prominent green dashed invite. Multi: collapses to a compact link so a
               late-joining player can still be added mid-round (persist-but-change-form). */}
          {!ghinPosted && (
            !multiMode ? (
              <div onClick={openPicker} style={{ marginBottom: 16, padding: '14px 16px', border: '2px dashed #9ca3af', borderRadius: 12, background: '#fafafa', textAlign: 'center', cursor: 'pointer' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>+ Score for another player</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Keep the card for up to 3 players</div>
              </div>
            ) : (
              <div onClick={openPicker} style={{ marginBottom: 16, padding: '10px', border: '1px dashed #d1d5db', borderRadius: 10, textAlign: 'center', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>
                Edit players
              </div>
            )
          )}

          {/* Prev / Next — PINNED to the bottom of the screen (Oct 4, 2026 — hole screen redesign,
              Option B). Every feature added to the hole (CTP, Dots, X, the stroke note) pushed Next
              down until players had to scroll for it; pinned, it's always under the thumb. Above the
              iPhone home bar (safe-area), wide and tall for older eyes and shaky hands. On 18 the
              green button saves the round instead of going grey. The root has bottom padding so
              nothing scrolls out of reach behind it. */}
          {/* Nassau (Oct 4, 2026): the press prompt, just above the Next bar, only when a player on this card is 2 down. */}
          {/* Sixes (Oct 5, 2026): "Match 1 is over" + the next sides, once, on moving off the match's last hole. */}
          <SixesMatchOver sixes={scorecard?.sixes} hole={currentHole} token={token} teetimeId={selectedTeetimeId} selfId={playerData?.id} />
          <NassauPressPrompt game="sixes" nassau={scorecard?.sixes} hole={currentHole} token={token} teetimeId={selectedTeetimeId} selfId={playerData?.id}
            forIds={multiMode ? (pairingMembers || []).filter(m => selectedTargets.includes(m.player_id)).map(m => m.player_id) : []} />
          <NassauPressPrompt nassau={scorecard?.nassau} hole={currentHole} token={token} teetimeId={selectedTeetimeId} selfId={playerData?.id}
            forIds={multiMode ? (pairingMembers || []).filter(m => selectedTargets.includes(m.player_id)).map(m => m.player_id) : []} />
          <div data-testid="hole-nav-bar" style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 0, width: '100%', maxWidth: 500, boxSizing: 'border-box', background: '#fff', borderTop: '1px solid #d1d5db', boxShadow: '0 -4px 14px rgba(0,0,0,0.08)', padding: '10px 14px calc(12px + env(safe-area-inset-bottom, 0px))', display: 'flex', gap: 10, zIndex: 900 }}>
            {(() => { const p = prevLiveHole(currentHole); return (
            <button aria-label={p ? `Back to hole ${p}` : 'Back'} onClick={async () => {
              // Going back is always allowed — players need to be able to correct a previous
              // hole's score. The advance-block stays on the Next button only.
              if (multiMode) { await saveAll(); } else { await saveScores(); }
              if (p) setCurrentHole(p);
            }} disabled={!p}
              style={{ width: 96, flexShrink: 0, height: 60, background: !p ? '#f3f4f6' : '#fff', border: '2px solid #9ca3af', borderRadius: 14, cursor: !p ? 'default' : 'pointer', fontSize: 20, fontWeight: 700, color: !p ? '#d1d5db' : '#374151' }}>
              ← {p ? (liveNines && p > 9 ? p - 9 : p) : ''}</button>
            ); })()}
            <button onClick={async () => {
              // Advance-block: solo blocks on the scorer's own missing score. Multi-mode
              // blocks only when NO kept player (self or any target) has a score this hole —
              // so a scorer who unchecked themselves can still advance on others' scores.
              if (multiMode) {
                // Every player on the card needs a score or an X (Dennis, Sept 27: stop
                // and require an entry). A hole the player entered himself counts.
                const blanks = [];
                if (scoringSelf && !(currentScore.score > 0)) blanks.push('You');
                for (const pid of Object.keys(targetScores)) {
                  const skipped = !!(targetSkips[pid] && targetSkips[pid][currentHole]);
                  if (!skipped && !(((targetScores[pid] || {})[currentHole]?.score || 0) > 0)) blanks.push(memberName(pid));
                }
                if (blanks.length) { setBlankNames(blanks); setShowScoreRequired(true); return; }
                await saveAll();
              } else {
                if (currentScore.score === 0) { setBlankNames([]); setShowScoreRequired(true); return; }
                await saveScores();
              }
              { const n = nextLiveHole(currentHole); if (n) setCurrentHole(n); }   // on the last hole (18, or the one before a shotgun start) the same checks, then it just saves
            }} disabled={saving || (currentHole === lastLiveHole && ghinPosted)}
              style={{ flex: 1, height: 60, background: (currentHole === lastLiveHole && ghinPosted) ? '#d1d5db' : '#166534', border: 'none', borderRadius: 14, cursor: (currentHole === lastLiveHole && ghinPosted) ? 'default' : 'pointer', fontSize: 21, fontWeight: 700, color: (currentHole === lastLiveHole && ghinPosted) ? '#6b7280' : '#fff' }}>
              {currentHole !== lastLiveHole ? `${holeText(nextLiveHole(currentHole), liveNines)} →` : ghinPosted ? '🔒 Posted' : saving ? 'Saving…' : scoresSaved ? '✓ Round saved' : '✓ Save round'}</button>
          </div>
        </div>
      )}
      </>)}{/* end followAlongOnly ternary */}
        </div>
      )}{/* end scoringTab === 'scorecard' hole entry */}

      {/* Save & GHIN — Scorecard tab only (hidden in follow-along: a watcher doesn't save) */}
      {scoringTab === 'scorecard' && !followAlongOnly && (() => {
        // USGA/WGA rule: scores cannot be posted to GHIN until at least 9 holes
        // are completed (any 9 holes — does not have to be the front nine).
        // Hide the Post to GHIN button entirely until that threshold is met.
        // When hidden, the Save Scores button expands to fill the row.
        const holesCompleted = Object.values(scores).filter(s => (s?.score || 0) > 0).length;
        const ghinEligible = holesCompleted >= 9;
        return (
        <div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            {ghinEligible && (() => {
              const hasCreds = !!playerData?.has_ghin_credentials;
              // Tier gate BEFORE credentials — gated on 'scoring' (Starter+), not
              // 'ghin_sync'. See the fuller note in TotalScoreEntry.
              const tierOk = playerData?.scoring_enabled === true;
              let bg = '#d1d5db', col = '#9ca3af', cur = 'default', txt = 'Post to GHIN';
              if (ghinPosted) { bg = '#166534'; col = '#fff'; txt = '🔒 GHIN Posted'; }
              else if (postingGhin) { bg = '#9ca3af'; col = '#fff'; txt = 'Posting...'; }
              else if (scoresSaved) { bg = '#111827'; col = '#fff'; cur = 'pointer'; }
              // Keep tappable when credentials are missing so the tap explains why
              // (disabled button + title tooltip is invisible on touch devices).
              // The round's postable_to_ghin flag decides before anything else (Sept 15, 2026).
              if (scorecard?.tee_time?.postable_to_ghin === false) return <GhinNotPostableNote />;
              // Opened from the scorecard code (Sept 28, 2026): no GHIN posting from here.
              if (codeSession) return (
                <div style={{ flex: 1, padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13, color: '#4b5563', lineHeight: 1.4 }} data-testid="code-session-ghin-note">
                  To post to GHIN, open your own Tee Time Organizer link or go to ghin.com.
                </div>
              );
              const ghinTitle = !scoresSaved ? 'Save scores first' : ghinPosted ? 'Already posted to GHIN' : !tierOk ? "Your current plan doesn't include Scoring. Ask your organizer about upgrading." : !hasCreds ? 'GHIN credentials not set up — visit your Profile to connect GHIN' : '';
              return <button onClick={() => !tierOk ? setShowGhinTierLocked(true) : hasCreds ? setShowGhinConfirm(true) : setShowGhinNoCreds(true)} disabled={!scoresSaved || ghinPosted || postingGhin} title={ghinTitle} style={{ flex: 1, padding: '14px 0', background: bg, color: col, border: 'none', borderRadius: 10, cursor: cur, fontSize: 16, fontWeight: 700 }}>{txt}</button>;
            })()}
            <button onClick={() => multiMode ? saveAll() : saveScores()} disabled={saving || ghinPosted}
              style={{ flex: 1, padding: '14px 0', background: ghinPosted ? '#d1d5db' : saving ? '#9ca3af' : '#166534', color: ghinPosted ? '#9ca3af' : '#fff', border: 'none', borderRadius: 10, cursor: (saving || ghinPosted) ? 'default' : 'pointer', fontSize: 16, fontWeight: 700 }}>
              {ghinPosted ? '🔒 Locked' : saving ? 'Saving...' : scoresSaved ? '✓ Saved' : 'Save Scores'}
            </button>
          </div>

          {/* GHIN Confirmation Modal */}
          {showGhinConfirm && (() => {
            const selectedTee = scorecard?.tees?.find(t => t.id === selectedTeeId);
            const courseName = scorecard?.tee_time?.course_name || '';
            const playedAt = scorecard?.tee_time?.date ? new Date(scorecard.tee_time.date.split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';
            const teeColor = selectedTee?.tee_color || '#000';
            const isPaleTee = teeColor && (teeColor.toLowerCase() === '#f3f4f6' || teeColor.toLowerCase() === '#ffffff' || teeColor.toLowerCase() === 'white');
            return (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
                <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', textAlign: 'center' }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>⛳</div>
                  <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700 }}>Post Score to GHIN?</h3>
                  {/* Round details — verify this is correct before posting */}
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14, textAlign: 'left' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>{courseName}</div>
                    {selectedTee && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 14, height: 14, borderRadius: 3, background: teeColor, border: isPaleTee ? '1px solid #d1d5db' : 'none', flexShrink: 0 }} />
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>{selectedTee.tee_name}</div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>{selectedTee.course_rating} / {selectedTee.slope_rating}</div>
                      </div>
                    )}
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{playedAt}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Score: <b style={{ color: '#1f2937' }}>{totals.gross}</b> ({totals.gross - totals.parPlayed >= 0 ? '+' : ''}{totals.gross - totals.parPlayed})</div>
                  </div>
                  <p style={{ color: '#9ca3af', fontSize: 12, margin: '0 0 16px' }}>Verify the course and tee above. Once posted, scores can only be corrected on ghin.com.</p>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => setShowGhinConfirm(false)} style={{ flex: 1, padding: '12px 0', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>Cancel</button>
                    <button onClick={postToGhin} style={{ flex: 1, padding: '12px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Post to GHIN</button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )})()}{/* end scorecard-only buttons */}

      {/* Score Required modal — shown when player tries to leave a hole
          without entering a score. A hole score is required before moving on;
          putts alone are not saved, and leaving a hole blank would drop it. */}
      {/* ===== Player picker modal (Feature A, Slice 2). Lists the pairing-scoped roster
              from /scoring/pairing-members. Self is pre-checked but toggleable (uncheck =
              follow along). Claim badges ("scored by X") are deferred to a later slice;
              every eligible player shows as available here. ===== */}
      {pickerOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 22, maxWidth: 400, width: '100%' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#1f2937' }}>Who are you scoring for?</h3>
            <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 16px', lineHeight: 1.5 }}>Pick anyone in your group. You can change this during the round.</p>

            {pairingLoading ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#6b7280', fontSize: 14 }}>Loading your group…</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pairingMembers.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 16, color: '#9ca3af', fontSize: 14 }}>No other players found in this round.</div>
                )}
                {pairingMembers.map(m => {
                  const isSelf = m.is_self;
                  const checked = isSelf ? scoringSelf : selectedTargets.includes(m.player_id);
                  return (
                    <div key={m.player_id}
                      onClick={() => isSelf ? setScoringSelf(v => !v) : toggleTarget(m.player_id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: checked ? '1.5px solid #16a34a' : '1px solid #e5e7eb', background: checked ? '#f0fdf4' : '#fff', borderRadius: 10, cursor: 'pointer' }}>
                      <div style={{ width: 26, height: 26, borderRadius: 6, flexShrink: 0, background: checked ? '#16a34a' : '#fff', border: checked ? 'none' : '1.5px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 800 }}>{checked ? '✓' : ''}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937' }}>{isSelf ? `${m.name}` : m.name}</div>
                        {isSelf && <div style={{ fontSize: 12, color: '#15803d' }}>you · uncheck to just follow along</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button onClick={confirmPicker}
              style={{ width: '100%', marginTop: 18, padding: '13px 0', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>
              {(() => {
                const n = (scoringSelf ? 1 : 0) + selectedTargets.length;
                if (!scoringSelf && selectedTargets.length === 0) return 'Follow along';
                return `Start scoring — ${n} player${n === 1 ? '' : 's'}`;
              })()}
            </button>
            <button onClick={() => setPickerOpen(false)}
              style={{ width: '100%', marginTop: 8, padding: '10px 0', background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showScoreRequired && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>⛳</div>
            <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>Hole Score Required</h3>
            <p style={{ color: '#374151', fontSize: 15, margin: '0 0 20px', lineHeight: 1.5 }}>
              {blankNames.length > 0
                ? <>{blankNames.length > 1 ? `${blankNames.slice(0, -1).join(', ')} and ${blankNames[blankNames.length - 1]}` : blankNames[0]} {blankNames.length === 1 && blankNames[0] !== 'You' ? 'has' : 'have'} nothing on hole {currentHole}. Enter a score for each — or <strong>X</strong> for a pick-up.{blankNames.some(n => n !== 'You') ? <> If someone has stopped playing, tap <strong>Edit players</strong> and untick them.</> : null}</>
                : <>Tap the score to confirm par, or use +/− to change it before continuing. Picked up? Tap <strong>X</strong>.</>}
            </p>
            <button onClick={() => setShowScoreRequired(false)}
              style={{ width: '100%', padding: '12px 0', background: '#166534', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Picked up ("7x", Sept 27, 2026): the one-time explanation, per player per round. */}
      {pickupAsk && (() => {
        const self = pickupAsk.who === 'self';
        const nm = self ? null : memberName(pickupAsk.who);
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>{self ? 'Picked up?' : `${nm} picked up?`}</h3>
              <p style={{ color: '#374151', fontSize: 15, margin: '0 0 20px', lineHeight: 1.5 }}>
                An X is scored as a <strong>net double bogey</strong> — a <strong>{pickupAsk.ndb}</strong> on hole {pickupAsk.hole}. {self ? 'If you finished the hole, enter what you made instead.' : `If ${nm} finished the hole, enter that score instead.`}
              </p>
              <button onClick={() => {
                  pickupNoteSeen.current.add(pickupAsk.key);
                  try { localStorage.setItem(pickupAsk.key, '1'); } catch (e) { /* in-memory only */ }
                  markPicked(pickupAsk.who, pickupAsk.hole, pickupAsk.ndb);
                  setPickupAsk(null);
                }}
                style={{ width: '100%', padding: '12px 0', background: '#166534', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>
                Use {pickupAsk.ndb}x
              </button>
              <button onClick={() => setPickupAsk(null)}
                style={{ width: '100%', marginTop: 8, padding: '10px 0', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>
                Enter a score
              </button>
            </div>
          </div>
        );
      })()}

      {/* Scoring Enhancement A — Celebrations log modal (May 2026).
          Lists every notable score from this tee time the player has seen
          this session. Opening clears the unread badge. */}
      {showCelebrationsModal && (
        <div onClick={() => setShowCelebrationsModal(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, padding: 16, maxWidth: 380, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', paddingBottom: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>🎉</span>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>Celebrations</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {celebrationsLog.length === 0 ? 'Nothing yet this round' : `${celebrationsLog.length} this round`}
                  </div>
                </div>
              </div>
              <button onClick={() => setShowCelebrationsModal(false)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, fontSize: 20, color: '#6b7280', lineHeight: 1 }}>
                ✕
              </button>
            </div>
            {/* Scrollable list */}
            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {celebrationsLog.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 12px', color: '#6b7280' }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>⛳</div>
                  <div style={{ fontSize: 14 }}>No celebrations yet — keep an eye out for birdies, eagles, and aces from your group.</div>
                </div>
              ) : (
                celebrationsLog.map((ev, i) => {
                  // Style each row by achievement type
                  let bg = '#f0fdf4', border = '#16a34a', titleColor = '#14532d', bodyColor = '#166534', emoji = '🐦', title = 'BIRDIE';
                  if (ev.type === 'eagle')       { bg = '#fefce8'; border = '#ca8a04'; titleColor = '#713f12'; bodyColor = '#854d0e'; emoji = '🦅'; title = 'EAGLE'; }
                  else if (ev.type === 'albatross') { bg = '#fef3c7'; border = '#a16207'; titleColor = '#713f12'; bodyColor = '#854d0e'; emoji = '🏆'; title = 'ALBATROSS'; }
                  else if (ev.type === 'hole_in_one') { bg = '#fef2f2'; border = '#dc2626'; titleColor = '#7f1d1d'; bodyColor = '#991b1b'; emoji = '🎯'; title = 'HOLE IN ONE'; }
                  else if (ev.type === 'ctp') { bg = '#eff6ff'; border = '#1d4ed8'; titleColor = '#1e3a8a'; bodyColor = '#1e40af'; emoji = '📍'; title = 'CLOSEST TO THE PIN'; }
                  // Format time from saved_at (e.g. "10:48 AM")
                  let timeStr = '';
                  try {
                    const d = new Date(ev.saved_at);
                    timeStr = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                  } catch (e) { /* leave blank */ }
                  // Body text
                  let body;
                  if (ev.type === 'ctp') body = `${ev.player_name} ${ev.from_name ? 'took' : 'got'} closest to the pin on ${ev.hole_text || `#${ev.hole_number}`}${ev.from_name ? ` from ${ev.from_name}` : ''}`;
                  else if (ev.type === 'hole_in_one') body = `${ev.player_name} aced #${ev.hole_number}`;
                  else if (ev.type === 'eagle') body = `${ev.player_name} had an eagle on #${ev.hole_number}`;
                  else if (ev.type === 'albatross') body = `${ev.player_name} had an albatross on #${ev.hole_number}`;
                  else body = `${ev.player_name} birdied #${ev.hole_number}`;
                  return (
                    <div key={ev.dedup_key || i}
                      style={{ background: bg, borderLeft: `4px solid ${border}`, borderRadius: 6, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 22 }}>{emoji}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: titleColor, letterSpacing: 0.5 }}>{title}</div>
                        <div style={{ fontSize: 14, color: bodyColor, marginTop: 2 }}>{body}</div>
                      </div>
                      {timeStr && <div style={{ fontSize: 11, color: bodyColor }}>{timeStr}</div>}
                    </div>
                  );
                })
              )}
            </div>
            {/* Close button */}
            <button onClick={() => setShowCelebrationsModal(false)}
              style={{ marginTop: 12, width: '100%', padding: '12px 0', background: '#1f2937', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>
              Close
            </button>
          </div>
        </div>
      )}

      {showGhinNoCreds && <GhinNoCredsModal onClose={() => setShowGhinNoCreds(false)} />}
      {showGhinTierLocked && <GhinTierLockedModal onClose={() => setShowGhinTierLocked(false)} />}
      {ghinNotMappedMsg && <GhinNotMappedModal message={ghinNotMappedMsg} onClose={() => setGhinNotMappedMsg(null)} />}
      {ghinLoginFailed && <GhinLoginFailedModal failure={ghinLoginFailed} onClose={() => setGhinLoginFailed(null)} onOpenProfile={onOpenProfile} />}
    </div>
  );
};



// ============================================================================
// MY ROUNDS VIEW — Personal rounds logged outside TTO-organized tee times
// Session A: Entry point, personal rounds list, create round flow
// ============================================================================
const MyRoundsView = ({ token, playerData, orgName, onClose, onOpenScoring }) => {
  const [rounds, setRounds] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRound, setShowNewRound] = useState(false);
  const [courseSearch, setCourseSearch] = useState('');
  const [courseSearchResults, setCourseSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTee, setSelectedTee] = useState(null);
  const [roundDate, setRoundDate] = useState(new Date().toISOString().split('T')[0]);
  const [roundTime, setRoundTime] = useState('');
  const [roundNotes, setRoundNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [importingCourse, setImportingCourse] = useState(null);
  const searchTimeout = useRef(null);

  // Load personal rounds and org courses on mount
  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setLoading(true);
      try {
        const [roundsResp, coursesResp] = await Promise.all([
          fetch(`${API_URL}/player/personal-rounds?token=${token}`),
          fetch(`${API_URL}/player/courses?token=${token}`)
        ]);
        if (roundsResp.ok) {
          const data = await roundsResp.json();
          setRounds(data.rounds || []);
        }
        if (coursesResp.ok) {
          const data = await coursesResp.json();
          setCourses(data.courses || []);
        }
      } catch (e) {
        console.error('My Rounds load error:', e);
      }
      setLoading(false);
    };
    load();
  }, [token]);

  // Course search with debounce
  const handleCourseSearch = (term) => {
    setCourseSearch(term);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (term.length < 2) { setCourseSearchResults([]); return; }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await fetch(`${API_URL}/player/courses/search?q=${encodeURIComponent(term)}&token=${token}`);
        if (resp.ok) {
          const data = await resp.json();
          setCourseSearchResults(data.courses || []);
        }
      } catch (e) {
        console.error('Course search error:', e);
      }
      setSearching(false);
    }, 400);
  };

  // Import course from API
  const handleImportCourse = async (apiCourse) => {
    setImportingCourse(apiCourse.name);
    try {
      const resp = await fetch(`${API_URL}/player/courses/import-from-api?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: apiCourse.name,
          city: apiCourse.city,
          state: apiCourse.state,
          address: apiCourse.address,
          tees: apiCourse.tees
        })
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        // Refresh courses list and select the imported course
        const coursesResp = await fetch(`${API_URL}/player/courses?token=${token}`);
        if (coursesResp.ok) {
          const coursesData = await coursesResp.json();
          setCourses(coursesData.courses || []);
          const imported = coursesData.courses?.find(c => c.id === data.course.id);
          if (imported) {
            setSelectedCourse(imported);
            // Auto-select preferred tee or first tee
            const preferredTee = imported.tees?.find(t => t.tee_name === playerData?.preferred_tee) || imported.tees?.[0];
            setSelectedTee(preferredTee || null);
          }
        }
        setCourseSearch('');
        setCourseSearchResults([]);
      } else if (resp.status === 409 && data.existing_id) {
        // Course already exists — select it
        const existing = courses.find(c => c.id === data.existing_id);
        if (existing) selectLocalCourse(existing);
        setCourseSearch('');
        setCourseSearchResults([]);
      } else {
        setError(data.error || 'Failed to import course');
      }
    } catch (e) {
      setError('Failed to import course');
    }
    setImportingCourse(null);
  };

  // Select a local course
  const selectLocalCourse = (course) => {
    setSelectedCourse(course);
    const preferredTee = course.tees?.find(t => t.tee_name === playerData?.preferred_tee) || course.tees?.[0];
    setSelectedTee(preferredTee || null);
    setCourseSearch('');
    setCourseSearchResults([]);
  };

  // Create personal round
  const handleCreateRound = async () => {
    if (!selectedCourse || !selectedTee) { setError('Please select a course and tee'); return; }
    setSaving(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/player/personal-rounds?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course_id: selectedCourse.id,
          course_tee_id: selectedTee.id,
          round_date: roundDate,
          tee_time: roundTime || null,
          notes: roundNotes || null
        })
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        // Refresh rounds list
        const roundsResp = await fetch(`${API_URL}/player/personal-rounds?token=${token}`);
        if (roundsResp.ok) {
          const roundsData = await roundsResp.json();
          setRounds(roundsData.rounds || []);
        }
        // Reset form
        setShowNewRound(false);
        setSelectedCourse(null);
        setSelectedTee(null);
        setRoundDate(new Date().toISOString().split('T')[0]);
        setRoundTime('');
        setRoundNotes('');
      } else {
        setError(data.error || 'Failed to create round');
      }
    } catch (e) {
      setError('Failed to create round');
    }
    setSaving(false);
  };

  // Delete personal round
  const handleDeleteRound = async (roundId) => {
    if (!window.confirm('Delete this personal round? This cannot be undone.')) return;
    try {
      const resp = await fetch(`${API_URL}/player/personal-rounds/${roundId}?token=${token}`, { method: 'DELETE' });
      if (resp.ok) {
        setRounds(prev => prev.filter(r => r.id !== roundId));
      }
    } catch (e) {
      console.error('Delete round error:', e);
    }
  };

  // Format helpers
  const fmtDate = (d) => {
    try {
      const dateOnly = typeof d === 'string' ? d.split('T')[0] : d;
      const [y, m, day] = dateOnly.split('-').map(Number);
      return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return d; }
  };
  const fmtTime = (t) => {
    if (!t) return '';
    try { const [h, m] = t.split(':').map(Number); return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; }
    catch { return t; }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#f9fafb', zIndex: 1000, overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #166534 0%, #15803d 100%)', padding: '20px 16px', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>🏌️ My Rounds</h2>
          <button onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            ✕ Close
          </button>
        </div>
        <div style={{ fontSize: 14, opacity: 0.9, lineHeight: 1.5 }}>
          Add your own tee times for rounds played outside of {orgName || 'your organization'}. Track your scores and they'll automatically count toward your player stats.
        </div>
      </div>

      <div style={{ padding: '16px 14px', maxWidth: 600, margin: '0 auto' }}>
        {/* Add Round Button */}
        {!showNewRound && (
          <button onClick={() => setShowNewRound(true)}
            style={{ width: '100%', padding: 16, background: '#166534', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 16, fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            + Add Personal Round
          </button>
        )}

        {/* New Round Form */}
        {showNewRound && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>New Personal Round</h3>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 14, color: '#dc2626' }}>
                {error}
              </div>
            )}

            {/* Step 1: Course Selection */}
            {!selectedCourse ? (
              <div>
                <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Select Course</label>
                
                {/* Quick-pick from existing courses */}
                {courses.length > 0 && !courseSearch && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>Your courses:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                      {courses.filter(c => c.tees && c.tees.length > 0).map(c => (
                        <button key={c.id} onClick={() => selectLocalCourse(c)}
                          style={{ padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                            {c.city && c.state && <div style={{ fontSize: 12, color: '#6b7280' }}>{c.city}, {c.state}</div>}
                          </div>
                          <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>In TTO</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search for new course */}
                <div style={{ position: 'relative' }}>
                  <input
                    type="text" value={courseSearch} placeholder="Search for a course..."
                    onChange={e => handleCourseSearch(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  />
                  {searching && <div style={{ position: 'absolute', right: 12, top: 12, fontSize: 12, color: '#6b7280' }}>Searching...</div>}
                </div>

                {/* Search results */}
                {courseSearchResults.length > 0 && (
                  <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 280, overflowY: 'auto' }}>
                    {courseSearchResults.map((c, idx) => (
                      <button key={`${c.source}-${c.id || c.api_id}-${idx}`}
                        onClick={() => c.source === 'local' ? selectLocalCourse(c) : handleImportCourse(c)}
                        disabled={importingCourse === c.name}
                        style={{ width: '100%', padding: '10px 12px', background: importingCourse === c.name ? '#f3f4f6' : '#fff', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: importingCourse === c.name ? 'wait' : 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {c.city && c.state ? `${c.city}, ${c.state}` : c.address || ''}
                            {c.tee_count > 0 && ` · ${c.tee_count} tees`}
                          </div>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, flexShrink: 0,
                          background: c.source === 'local' ? '#dcfce7' : '#dbeafe',
                          color: c.source === 'local' ? '#166534' : '#1e40af'
                        }}>
                          {importingCourse === c.name ? 'Importing...' : c.source === 'local' ? 'In TTO' : 'Import'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Selected course display */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{selectedCourse.name}</div>
                    {selectedCourse.city && selectedCourse.state && (
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{selectedCourse.city}, {selectedCourse.state}</div>
                    )}
                  </div>
                  <button onClick={() => { setSelectedCourse(null); setSelectedTee(null); }}
                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Change</button>
                </div>

                {/* Step 2: Tee Selection */}
                {selectedCourse.tees && selectedCourse.tees.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Tee</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {selectedCourse.tees.map(t => (
                        <button key={t.id} onClick={() => setSelectedTee(t)}
                          style={{
                            padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            border: selectedTee?.id === t.id ? '2px solid #166534' : '1px solid #d1d5db',
                            background: selectedTee?.id === t.id ? '#f0fdf4' : '#fff',
                            color: selectedTee?.id === t.id ? '#166534' : '#374151',
                            display: 'flex', alignItems: 'center', gap: 6
                          }}>
                          <span style={{
                            width: 12, height: 12, borderRadius: '50%', display: 'inline-block',
                            background: t.tee_color || '#6b7280',
                            border: (t.tee_color === '#f3f4f6' || t.tee_color === '#ffffff') ? '1px solid #d1d5db' : 'none'
                          }} />
                          {t.tee_name}
                          {t.course_rating > 0 && <span style={{ fontWeight: 400, color: '#6b7280' }}>{t.course_rating}/{t.slope_rating}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3: Date & Time */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Date</label>
                    <input type="date" value={roundDate} onChange={e => setRoundDate(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Tee Time <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                    <input type="time" value={roundTime} onChange={e => setRoundTime(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                </div>

                {/* Notes */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 14, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Notes <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                  <input type="text" value={roundNotes} placeholder="e.g., Played with Dad — windy day"
                    onChange={e => setRoundNotes(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => { setShowNewRound(false); setSelectedCourse(null); setSelectedTee(null); setError(''); }}
                    style={{ flex: 1, padding: '12px 0', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#6b7280' }}>
                    Cancel
                  </button>
                  <button onClick={handleCreateRound} disabled={saving || !selectedCourse || !selectedTee}
                    style={{ flex: 1, padding: '12px 0', background: saving ? '#86efac' : '#166534', color: '#fff', border: 'none', borderRadius: 10, cursor: saving ? 'wait' : 'pointer', fontSize: 15, fontWeight: 700, opacity: (!selectedCourse || !selectedTee) ? 0.5 : 1 }}>
                    {saving ? 'Creating...' : 'Create Round'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rounds List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading your rounds...</div>
        ) : rounds.length === 0 && !showNewRound ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏌️</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No personal rounds yet</div>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              Tap "+ Add Personal Round" to log a round you played outside of {orgName || 'your group'}.
              Your scores will count toward your overall stats.
            </div>
          </div>
        ) : (
          <div>
            {rounds.length > 0 && (
              <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 10 }}>
                {rounds.length} personal round{rounds.length !== 1 ? 's' : ''} logged
              </div>
            )}
            {rounds.map(r => (
              <div key={r.id} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', marginBottom: 10, border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{r.course_name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      {fmtDate(r.round_date)}{r.tee_time ? ` · ${fmtTime(r.tee_time)}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {r.total_gross ? (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#166534' }}>{r.total_gross}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                          {r.tee_par ? `${r.total_gross > r.tee_par ? '+' : ''}${r.total_gross - r.tee_par}` : ''}{r.total_putts > 0 ? ` · ${r.total_putts} putts` : ''}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No scores yet</div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {r.tee_name && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.tee_color || '#6b7280', display: 'inline-block', border: (r.tee_color === '#f3f4f6') ? '1px solid #d1d5db' : 'none' }} />
                      {r.tee_name}
                    </span>
                  )}
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#4b5563', background: '#e5e7eb', padding: '2px 8px', borderRadius: 4 }}>My Round</span>
                  {r.ghin_posted && <span style={{ fontSize: 11, fontWeight: 600, color: '#166534', background: '#dcfce7', padding: '2px 8px', borderRadius: 4 }}>GHIN ✓</span>}
                  {r.notes && <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>{r.notes}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={() => onOpenScoring && onOpenScoring(r)}
                    style={{ padding: '6px 12px', background: '#166534', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {r.total_gross ? 'Edit Scores' : 'Enter Scores'}
                  </button>
                  <button onClick={() => handleDeleteRound(r.id)}
                    style={{ padding: '6px 12px', background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PERSONAL ROUND SCORING — Hole-by-hole score entry for personal rounds
// Simplified version of LiveScoring, saves to personal_round_scores table
// ============================================================================
const PersonalRoundScoring = ({ token, playerData, round, onClose, onSaved, onOpenProfile }) => {
  const [holeData, setHoleData] = useState([]);
  const [scores, setScores] = useState({});
  const [currentHole, setCurrentHole] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scoresSaved, setScoresSaved] = useState(false);
  const [postingGhin, setPostingGhin] = useState(false);
  const [ghinPosted, setGhinPosted] = useState(round?.ghin_posted || false);
  const [showGhinConfirm, setShowGhinConfirm] = useState(false);
  const [showGhinNoCreds, setShowGhinNoCreds] = useState(false);
  const [showGhinTierLocked, setShowGhinTierLocked] = useState(false);
  const [ghinNotMappedMsg, setGhinNotMappedMsg] = useState(null);
  const [ghinLoginFailed, setGhinLoginFailed] = useState(null); // { code, message } from GHIN_INVALID_CREDENTIALS / GHIN_UNAVAILABLE (Sept 10, 2026)
  const [ghinDifferential, setGhinDifferential] = useState(round?.differential || null);

  // Load hole data and existing scores
  useEffect(() => {
    if (!round?.id) return;
    const load = async () => {
      try {
        const resp = await fetch(`${API_URL}/player/personal-rounds/${round.id}?token=${token}`);
        if (resp.ok) {
          const data = await resp.json();
          setHoleData(data.holes || []);
          const s = {};
          for (let h = 1; h <= 18; h++) {
            const existing = (data.scores || []).find(sc => sc.hole_number === h);
            s[h] = { score: existing?.gross_score || 0, putts: existing?.putts || 0 };
          }
          // Check for offline scores that may be newer
          try {
            const stored = JSON.parse(localStorage.getItem('tto_personal_offline') || '{}');
            if (stored[round.id] && stored[round.id].timestamp > Date.now() - 24 * 60 * 60 * 1000) {
              const offlineHasScores = Object.values(stored[round.id].scores).some(v => v.score > 0);
              const serverHasScores = Object.values(s).some(v => v.score > 0);
              if (offlineHasScores && !serverHasScores) {
                setScores(stored[round.id].scores);
                setLoading(false);
                return;
              }
            }
          } catch (e) {}
          setScores(s);
          const firstUnscored = Array.from({ length: 18 }, (_, i) => i + 1).find(h => !s[h]?.score);
          if (firstUnscored) setCurrentHole(firstUnscored);
        }
      } catch (e) { console.error('Load personal round scoring error:', e); }
      setLoading(false);
    };
    load();
  }, [round?.id, token]);

  // Offline: save to localStorage on visibility change (phone lock, app switch)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden' && round?.id) {
        const hasScores = Object.values(scores).some(s => s.score > 0);
        if (hasScores) {
          try {
            const stored = JSON.parse(localStorage.getItem('tto_personal_offline') || '{}');
            stored[round.id] = { scores, timestamp: Date.now() };
            localStorage.setItem('tto_personal_offline', JSON.stringify(stored));
          } catch (e) {}
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [scores, round?.id]);

  const holePar = holeData.find(h => h.hole_number === currentHole)?.par || 4;
  const holeYardage = holeData.find(h => h.hole_number === currentHole)?.yardage || 0;
  const holeHcp = holeData.find(h => h.hole_number === currentHole)?.handicap_index || 0;
  const currentScore = scores[currentHole] || { score: 0, putts: 0 };

  const updateScore = (val) => {
    const newVal = Math.max(1, Math.min(15, val));
    setScores(prev => ({ ...prev, [currentHole]: { ...prev[currentHole], score: newVal } }));
    setScoresSaved(false);
  };
  const updatePutts = (val) => {
    const newVal = Math.max(0, Math.min(9, val));
    setScores(prev => ({ ...prev, [currentHole]: { ...prev[currentHole], putts: newVal } }));
    setScoresSaved(false);
  };
  const tapScore = () => {
    if (currentScore.score === 0) updateScore(holePar);
    else updateScore(0);
  };

  // Score label
  const getScoreLabel = (score, par) => {
    if (!score || score === 0) return '';
    const diff = score - par;
    if (diff <= -3) return 'Albatross';
    if (diff === -2) return 'Eagle';
    if (diff === -1) return 'Birdie';
    if (diff === 0) return 'Par';
    if (diff === 1) return 'Bogey';
    if (diff === 2) return 'Dbl Bogey';
    if (diff === 3) return 'Triple';
    return `+${diff}`;
  };
  const getScoreColor = (score, par) => {
    if (!score || score === 0) return '#d1d5db';
    const diff = score - par;
    if (diff <= -2) return '#f59e0b';
    if (diff === -1) return '#dc2626';
    if (diff === 0) return '#166534';
    if (diff === 1) return '#1e40af';
    if (diff >= 2) return '#6b21a8';
    return '#6b7280';
  };

  // Running totals
  const totalGross = Object.values(scores).reduce((s, v) => s + (v.score || 0), 0);
  const totalPutts = Object.values(scores).reduce((s, v) => s + (v.putts || 0), 0);
  const holesPlayed = Object.values(scores).filter(v => v.score > 0).length;
  const playedPar = holeData.filter(h => scores[h.hole_number]?.score > 0).reduce((s, h) => s + h.par, 0);
  const vsPar = holesPlayed > 0 ? totalGross - playedPar : 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const arr = [];
      for (let h = 1; h <= 18; h++) {
        if (scores[h]?.score > 0) {
          arr.push({ hole_number: h, gross_score: scores[h].score, putts: scores[h].putts > 0 ? scores[h].putts : null });
        }
      }
      if (arr.length === 0) { setSaving(false); return; }
      const resp = await fetch(`${API_URL}/player/personal-rounds/${round.id}/scores?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores: arr })
      });
      if (resp.ok) {
        setScoresSaved(true);
        // Clear offline backup after successful save
        try {
          const stored = JSON.parse(localStorage.getItem('tto_personal_offline') || '{}');
          delete stored[round.id];
          localStorage.setItem('tto_personal_offline', JSON.stringify(stored));
        } catch (e) {}
        if (onSaved) onSaved();
      }
    } catch (e) { console.error('Save personal round scores error:', e); }
    setSaving(false);
  };

  // GHIN posting — backend auto-resolves course/tee IDs
  const postToGhin = async () => {
    setShowGhinConfirm(false);
    try {
      setPostingGhin(true);
      const resp = await fetch(`${API_URL}/player/personal-rounds/${round.id}/post-ghin?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ played_at: round.round_date?.split('T')[0] })
      });
      if (resp.ok) {
        const d = await resp.json();
        setGhinPosted(true);
        setGhinDifferential(d.differential);
        if (onSaved) onSaved();
      // GHIN_COURSE_NOT_MAPPED gets a modal rather than a toast: the server copy
      // tells the player to post in the GHIN app and explicitly NOT to retry
      // (retrying after the organizer sets the id would double-post the round),
      // which is too much for an auto-dismissing toast to carry.
      } else {
        const err = await resp.json();
        if (err.code === 'GHIN_COURSE_NOT_MAPPED') setGhinNotMappedMsg(err.error || true);
        // Already in GHIN (Oct 16, 2026): same modal, its own title — a toast is too easy to miss.
        else if (err.code === 'GHIN_ALREADY_POSTED') setGhinNotMappedMsg({ title: 'Already Posted in GHIN', text: err.error });
        // Login failures get a modal too (Sept 10, 2026) -- see GhinLoginFailedModal.
        else if (err.code === 'GHIN_INVALID_CREDENTIALS' || err.code === 'GHIN_UNAVAILABLE') setGhinLoginFailed({ code: err.code, message: err.error });
        else alert(err.error || 'GHIN posting failed');
      }
    } catch (e) { alert('GHIN posting failed'); }
    finally { setPostingGhin(false); }
  };

  if (loading) return <div style={{ position: 'fixed', inset: 0, background: '#f9fafb', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#f9fafb', zIndex: 1100, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: '#166534', padding: '14px 16px', color: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>{round.course_name}</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {round.tee_name} · My Round{ghinPosted && ghinDifferential ? ` · Diff: ${ghinDifferential}` : ''}
            </div>
          </div>
          <button onClick={() => { if (!scoresSaved && holesPlayed > 0) handleSave().then(onClose); else onClose(); }}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            ✕ Close
          </button>
        </div>
      </div>

      {/* Running Totals Bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 16px', display: 'flex', justifyContent: 'space-around', flexShrink: 0 }}>
        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 11, color: '#6b7280' }}>Thru</div><div style={{ fontSize: 18, fontWeight: 800 }}>{holesPlayed}</div></div>
        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 11, color: '#6b7280' }}>Gross</div><div style={{ fontSize: 18, fontWeight: 800 }}>{totalGross || '—'}</div></div>
        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 11, color: '#6b7280' }}>Putts</div><div style={{ fontSize: 18, fontWeight: 800 }}>{totalPutts || '—'}</div></div>
        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 11, color: '#6b7280' }}>VS PAR</div><div style={{ fontSize: 18, fontWeight: 800, color: vsPar > 0 ? '#dc2626' : vsPar < 0 ? '#166534' : '#374151' }}>{vsPar > 0 ? `+${vsPar}` : vsPar === 0 && holesPlayed > 0 ? 'E' : vsPar || '—'}</div></div>
      </div>

      {/* Hole Indicator Boxes */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '8px 12px', display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        {Array.from({ length: 18 }, (_, i) => i + 1).map(h => {
          const s = scores[h]?.score || 0;
          const p = holeData.find(hd => hd.hole_number === h)?.par || 4;
          return (
            <button key={h} onClick={() => setCurrentHole(h)}
              style={{
                width: 28, height: 28, borderRadius: 4, border: h === currentHole ? '2px solid #166534' : '1px solid #e5e7eb',
                background: s === 0 ? '#f9fafb' : getScoreColor(s, p),
                color: s === 0 ? '#9ca3af' : '#fff',
                fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
              {s > 0 ? s : h}
            </button>
          );
        })}
      </div>

      {/* Current Hole Card */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
        <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Hole {currentHole}</div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 13, color: '#6b7280' }}>
          <span>Par {holePar}</span>
          {holeYardage > 0 && <span>{holeYardage} yds</span>}
          {holeHcp > 0 && <span>HCP {holeHcp}</span>}
        </div>

        {/* Score */}
        <div style={{ marginBottom: 20, opacity: ghinPosted ? 0.5 : 1, pointerEvents: ghinPosted ? 'none' : 'auto' }}>
          <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginBottom: 6 }}>Score</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={() => updateScore((currentScore.score || holePar) - 1)}
              style={{ width: 56, height: 56, borderRadius: '50%', border: '2px solid #d1d5db', background: '#fff', fontSize: 24, fontWeight: 700, cursor: 'pointer', color: '#374151' }}>−</button>
            <button onClick={tapScore}
              style={{ width: 80, height: 80, borderRadius: '50%', border: 'none', background: currentScore.score > 0 ? getScoreColor(currentScore.score, holePar) : '#e5e7eb',
                color: currentScore.score > 0 ? '#fff' : '#9ca3af', fontSize: 36, fontWeight: 800, cursor: 'pointer' }}>
              {currentScore.score || holePar}
            </button>
            <button onClick={() => updateScore((currentScore.score || holePar) + 1)}
              style={{ width: 56, height: 56, borderRadius: '50%', border: '2px solid #d1d5db', background: '#fff', fontSize: 24, fontWeight: 700, cursor: 'pointer', color: '#374151' }}>+</button>
          </div>
          {currentScore.score > 0 && (
            <div style={{ textAlign: 'center', marginTop: 6, fontSize: 14, fontWeight: 600, color: getScoreColor(currentScore.score, holePar) }}>
              {getScoreLabel(currentScore.score, holePar)}
            </div>
          )}
        </div>

        {/* Putts */}
        <div style={{ marginBottom: 20, opacity: ghinPosted ? 0.5 : 1, pointerEvents: ghinPosted ? 'none' : 'auto' }}>
          <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginBottom: 6 }}>Putts</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={() => updatePutts(currentScore.putts - 1)}
              style={{ width: 44, height: 44, borderRadius: '50%', border: '2px solid #d1d5db', background: '#fff', fontSize: 20, fontWeight: 700, cursor: 'pointer', color: '#374151' }}>−</button>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: currentScore.putts > 0 ? '#374151' : '#e5e7eb',
              color: currentScore.putts > 0 ? '#fff' : '#9ca3af', fontSize: 28, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {currentScore.putts || 0}
            </div>
            <button onClick={() => updatePutts(currentScore.putts + 1)}
              style={{ width: 44, height: 44, borderRadius: '50%', border: '2px solid #d1d5db', background: '#fff', fontSize: 20, fontWeight: 700, cursor: 'pointer', color: '#374151' }}>+</button>
          </div>
        </div>

        {/* Prev/Next */}
        <div style={{ display: 'flex', gap: 12, width: '100%', maxWidth: 300 }}>
          <button disabled={currentHole === 1} onClick={() => setCurrentHole(h => h - 1)}
            style={{ flex: 1, padding: '12px 0', background: currentHole === 1 ? '#f3f4f6' : '#fff', border: '1px solid #d1d5db', borderRadius: 10, cursor: currentHole === 1 ? 'default' : 'pointer', fontSize: 15, fontWeight: 600, color: currentHole === 1 ? '#d1d5db' : '#374151' }}>
            ← Prev
          </button>
          <button disabled={currentHole === 18} onClick={() => setCurrentHole(h => h + 1)}
            style={{ flex: 1, padding: '12px 0', background: currentHole === 18 ? '#f3f4f6' : '#fff', border: '1px solid #d1d5db', borderRadius: 10, cursor: currentHole === 18 ? 'default' : 'pointer', fontSize: 15, fontWeight: 600, color: currentHole === 18 ? '#d1d5db' : '#374151' }}>
            Next →
          </button>
        </div>
      </div>

      {/* Action Buttons: Save + GHIN Post */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
        {ghinPosted && ghinDifferential && (
          <div style={{ textAlign: 'center', fontSize: 13, color: '#166534', fontWeight: 600, marginBottom: 8 }}>
            ✅ Posted to GHIN · Differential: {ghinDifferential}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleSave} disabled={saving || ghinPosted || holesPlayed === 0}
            style={{ flex: 1, padding: '14px 0', background: ghinPosted ? '#d1d5db' : scoresSaved ? '#16a34a' : saving ? '#86efac' : '#166534', color: ghinPosted ? '#9ca3af' : '#fff', border: 'none', borderRadius: 10, cursor: (saving || ghinPosted) ? 'default' : 'pointer', fontSize: 16, fontWeight: 700, opacity: holesPlayed === 0 ? 0.5 : 1 }}>
            {ghinPosted ? '🔒 Locked' : scoresSaved ? '✓ Saved' : saving ? 'Saving...' : `Save Scores (${holesPlayed} holes)`}
          </button>
          {(() => {
            const hasCreds = !!playerData?.has_ghin_credentials;
            // Tier gate BEFORE credentials — gated on 'scoring' (Starter+), not
            // 'ghin_sync'. See the fuller note in TotalScoreEntry.
            const tierOk = playerData?.scoring_enabled === true;
            let bg = '#d1d5db', col = '#9ca3af', cur = 'default', txt = 'Post to GHIN';
            if (ghinPosted) { bg = '#166534'; col = '#fff'; txt = '🔒 GHIN Posted'; }
            else if (postingGhin) { bg = '#9ca3af'; col = '#fff'; txt = 'Posting...'; }
            else if (scoresSaved) { bg = '#111827'; col = '#fff'; cur = 'pointer'; }
            // Keep tappable when credentials are missing so the tap explains why
            // (disabled button + title tooltip is invisible on touch devices).
            const ghinTitle = !scoresSaved ? 'Save scores first' : ghinPosted ? 'Already posted to GHIN' : !tierOk ? "Your current plan doesn't include Scoring. Ask your organizer about upgrading." : !hasCreds ? 'GHIN credentials not set up — visit your Profile to connect GHIN' : '';
            return (
              <button onClick={() => !tierOk ? setShowGhinTierLocked(true) : hasCreds ? setShowGhinConfirm(true) : setShowGhinNoCreds(true)} disabled={!scoresSaved || ghinPosted || postingGhin} title={ghinTitle}
                style={{ flex: 1, padding: '14px 0', background: bg, color: col, border: 'none', borderRadius: 10, cursor: cur, fontSize: 16, fontWeight: 700 }}>
                {txt}
              </button>
            );
          })()}
        </div>
      </div>

      {/* GHIN Confirmation Modal */}
      {showGhinConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowGhinConfirm(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 340, width: '100%', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Post to GHIN?</div>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 6 }}>{round.course_name} · {round.tee_name}</div>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>Score: {totalGross} ({holesPlayed} holes)</div>
            <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 16 }}>Once posted, scores will be locked.</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowGhinConfirm(false)} style={{ flex: 1, padding: '12px 0', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600, color: '#6b7280' }}>Cancel</button>
              <button onClick={postToGhin} style={{ flex: 1, padding: '12px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>Post to GHIN</button>
            </div>
          </div>
        </div>
      )}

      {showGhinNoCreds && <GhinNoCredsModal onClose={() => setShowGhinNoCreds(false)} />}
      {showGhinTierLocked && <GhinTierLockedModal onClose={() => setShowGhinTierLocked(false)} />}
      {ghinNotMappedMsg && <GhinNotMappedModal message={ghinNotMappedMsg} onClose={() => setGhinNotMappedMsg(null)} />}
      {ghinLoginFailed && <GhinLoginFailedModal failure={ghinLoginFailed} onClose={() => setGhinLoginFailed(null)} onOpenProfile={onOpenProfile} />}
    </div>
  );
};

// ============================================================================
// LeaderboardModal — standalone in-app live leaderboard (Live Leaderboard Plan v1.0)
// Opened from the "Live – View Leaderboard" buttons (My Tee Times card + the
// Available Tee Times View window). Self-contained: takes token + teetimeId,
// lands on the GROSS leaderboard. The Games sub-tab appears ONLY when the viewer
// is a participant in a game on this tee time (the money game is the players'
// business). Non-rostered spectators never see Games.
//   - Leaderboard data: GET /player/scoring/leaderboard (org-auth, no roster req).
//   - Participant detection + Games standings: GET /player/scoring/standings.
// This is intentionally a separate component from the in-LiveScoring Results tab
// (Option C): the working scorer flow is left untouched; this is purpose-built
// for the spectator / not-scoring viewer.
// ============================================================================
// ============================================================================
// PlayerWhatsNewModal — player-facing "What's New" (bell icon target).
// Fresh component (not the organizer's WhatsNewModal): What's New only, no Tips,
// player-voiced. Shows a persistent reverse-chronological list of player-audience
// announcements; the viewer scrolls and closes. Seen-state (the bell's red dot) is
// handled by the caller via localStorage — this component just renders the list.
// ============================================================================
const PLAYER_WN_CATEGORY_STYLES = {
  'New Feature': { bg: '#dcfce7', color: '#166534', label: '✨ New' },
  'Improvement': { bg: '#dbeafe', color: '#1e40af', label: '⬆️ Improvement' },
  'Bug Fix':     { bg: '#fef3c7', color: '#92400e', label: '🔧 Fix' },
};

const PlayerWhatsNewModal = ({ items, onClose }) => {
  const fmtDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('T')[0].split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ width: '100%', maxWidth: 520, maxHeight: '85vh', background: '#fff', borderRadius: 16,
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '16px 18px', borderBottom: '1px solid #f3f4f6', flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: '#1f2937' }}>🔔 What's New</div>
          <button onClick={onClose} aria-label="Close"
            style={{ width: 32, height: 32, borderRadius: 8, background: '#f3f4f6', border: 'none',
              cursor: 'pointer', fontSize: 18, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ×
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', minHeight: 0 }}>
          {(!items || items.length === 0) ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 }}>
              Nothing new right now — check back soon!
            </div>
          ) : (
            items.map((item, i) => {
              const cs = PLAYER_WN_CATEGORY_STYLES[item.category] || PLAYER_WN_CATEGORY_STYLES['New Feature'];
              return (
                <div key={item.id} style={{ marginBottom: i < items.length - 1 ? 18 : 0,
                  paddingBottom: i < items.length - 1 ? 18 : 0,
                  borderBottom: i < items.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>{fmtDate(item.published_at)}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: cs.bg, color: cs.color }}>{cs.label}</span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 5 }}>{item.title}</div>
                  <div style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{item.body}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Public leaderboard — TV mode (Sept 16, 2026): /lb/<address>/tv (or ?tv=1) for
// the clubhouse screen — ttoleaderboard.com/<address>/tv on a TV remote. Dennis: professional TV leaderboards don't hold the leaders
// and scroll the rest, so this pages the WHOLE field in order — every team gets
// its turn, and at ~8 s a page with 36 teams the leaders are back on screen
// often enough. Built like a broadcast board (Dennis, Sept 16: "a place for TTO
// to really shine"): light type on a dark field; columns POS · TEAM · THRU ·
// SCORE · TOTAL with TO-PAR as the headline number; RED figures under par, white
// even, muted over (the golf convention — the phone/app boards keep TTO's green);
// F in THRU when finished; the leader's position in gold (no ▲▼ movement arrows —
// Dennis, Sept 16: a cleaner view); the active page dot in the footer sweeps like a
// small clock face over the hold (a header timer bar drew the eye — Dennis);
// pages fade in rather than cut; nothing to tap. Rows per page come
// from the screen height (12 on a 1080p set, 6 on 720p); &secs=12 holds a page
// longer; the data refresh is the phone view's 20 s poll. The footer carries the
// address in gold (no QR — Dennis, Sept 16: it made the screen look busy; the
// GET /public/leaderboard/:key/qr.svg endpoint stays for the Event Desk and the
// share panel). A team board shows the game's basis (net for a net scramble); an
// individual board shows gross.
// Getting it onto the wall (Dennis, Sept 16): the page asks the browser to keep
// the screen awake (Wake Lock — a casting laptop or a tablet must not sleep
// mid-round), and for the first 10 s — and again whenever the mouse moves — a
// "Full screen" button sits in the header (browsers only go full screen from a
// click); it hides once the page is full screen, and so does the pointer.
// ============================================================================
const TV_PAGE_MS = 8000;
const TV_HINT_MS = 10000;                              // how long the Full screen button stays after load / a mouse move
const TV_ROW_H = 78;                                   // row (70) + separator gap (8), px — 10 rows on a 1080p set
const TV_HEADER_H = 116, TV_FOOTER_H = 84, TV_BODY_PAD = 20, TV_COLHEAD_H = 34;
const TV_CHROME_H = TV_HEADER_H + TV_FOOTER_H + TV_BODY_PAD + TV_COLHEAD_H + 12 + 12;   // + band margin + panel padding
const tvRowsFor = (h) => Math.max(5, Math.min(14, Math.floor(((h || 1080) - TV_CHROME_H) / TV_ROW_H)));
const tvFmtVs = (v) => v == null ? '–' : (v === 0 ? 'E' : (v > 0 ? `+${v}` : `${v}`));
const TV_RED = '#ff6b6b', TV_GOLD = '#fcd34d', TV_MUTED = 'rgba(255,255,255,0.86)';
// Brighter field (Dennis, Sept 16: the board looked dim on the laptop and on the TV —
// clubhouses are daylit). A mid green field under a deep green header, near-white
// secondary text, a lighter row panel; the red under-par blocks and the gold leader stay.
const TV_BG = 'linear-gradient(180deg, #1f7048 0%, #185c3b 100%)';
const TV_HEADER_BG = '#0d4a2b';
const TV_PANEL = 'rgba(255,255,255,0.10)';
const TV_BAND = 'rgba(0,0,0,0.22)';
const TV_LINE = 'rgba(255,255,255,0.20)';
const TV_DIM = 'rgba(255,255,255,0.6)';
// ---------------------------------------------------------------------------
// Small screens (Sept 19, 2026). The TV board is a FIXED-PIXEL design for a 1080p
// set: TV_COL alone is 554px of columns before the name gets any width, so on a
// phone it overflowed and clipped. Rather than reflow the columns — which would
// change what plays on the clubhouse screen — the whole board is scaled to fit
// inside a letterboxed panel, so a phone shows a miniature of the real thing.
// At or above the design box the scale is exactly 1 and the markup is unchanged,
// so nothing about the television rendering moves.
const TV_DESIGN_W = 1180, TV_DESIGN_H = 664;   // 16:9-ish; 1180 leaves the name column ~546px
// WIDTH is what breaks — TV_COL is 554px of fixed columns — so width alone decides
// whether to scale. Height already adapts through tvRowsFor, and keying off it would
// letterbox a 1280x720 laptop window that renders perfectly well full-screen.
// The scale then has to fit the height too, minus the wrapper's padding, the gap and
// the exit control underneath, or the board clips (landscape phones, Sept 19).
const TV_SMALL_RESERVE = (portrait) => 32 + 18 + (portrait ? 88 : 46);
const tvScaleFor = (vw, vh, portrait) => Math.min(
  (vw || TV_DESIGN_W) / TV_DESIGN_W,
  Math.max(160, (vh || TV_DESIGN_H) - TV_SMALL_RESERVE(portrait)) / TV_DESIGN_H,
  1
);
const useTvScale = () => {
  const read = () => {
    if (typeof window === 'undefined') return { scale: 1, small: false, portrait: false };
    const vw = window.innerWidth, vh = window.innerHeight;
    if (vw >= TV_DESIGN_W) return { scale: 1, small: false, portrait: vh > vw };
    const portrait = vh > vw;
    return { scale: tvScaleFor(vw, vh, portrait), small: true, portrait };
  };
  const [v, setV] = useState(read);
  useEffect(() => {
    const on = () => setV(read());
    window.addEventListener('resize', on);
    window.addEventListener('orientationchange', on);
    return () => { window.removeEventListener('resize', on); window.removeEventListener('orientationchange', on); };
  }, []);
  return v;
};

const tvVsColor = (v) => v == null ? TV_DIM : (v < 0 ? TV_RED : v > 0 ? 'rgba(255,255,255,0.92)' : '#fff');
// The TO-PAR cell: a solid red block with white figures under par (the broadcast
// treatment — reads from across a room), a quiet grey block at or over par.
const tvVsCell = (v) => v == null
  ? { background: 'transparent', color: TV_DIM }
  : (v < 0 ? { background: '#c81e1e', color: '#fff' } : (v === 0 ? { background: 'rgba(255,255,255,0.24)', color: '#fff' } : { background: 'rgba(0,0,0,0.22)', color: '#fff' }));
const TV_CSS = `
@keyframes tvFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes tvDial { from { stroke-dashoffset: 31.4; } to { stroke-dashoffset: 0; } }
@keyframes tvPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
`;

// Screen helpers for the TV view — module-level so the hook's effect has no
// changing dependencies. Every browser call is guarded: a refused wake lock or
// full-screen request leaves the board exactly as it was.
const tvFullscreenEl = () => (typeof document === 'undefined' ? null : (document.fullscreenElement || document.webkitFullscreenElement || null));
const tvEnterFullscreen = () => {
  try {
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (!req) return;
    const p = req.call(el);
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch (e) { /* not allowed here — F11 still works */ }
};
const tvAcquireWakeLock = async (lockRef) => {
  try {
    if (typeof navigator === 'undefined' || !navigator.wakeLock || typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;
    if (lockRef.current && !lockRef.current.released) return;
    lockRef.current = await navigator.wakeLock.request('screen');
  } catch (e) { /* refused (battery saver, browser policy) — the display may still sleep */ }
};
// Keep the display awake and manage the Full screen button while `active`.
// The browser drops a wake lock whenever the tab is hidden; it is taken again
// when the tab is visible, and again on the Full screen click (a user gesture).
// `hint` drives the Full screen button and deliberately stays hidden once the
// display IS full screen — the button has no job there. `chrome` drives the exit
// control, which must still be reachable in full screen, so it shows on any
// activity and then fades on the same 10s timer. Both are off by default on an
// unattended clubhouse board, which is the point: no chrome, and nothing a
// passer-by can click to take the leaderboard off the television.
const useTvScreen = (active) => {
  const [hint, setHint] = useState(!!active);
  const [chrome, setChrome] = useState(!!active);
  const [isFull, setIsFull] = useState(false);
  const lockRef = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    let hideTimer = null;
    const showHint = () => {
      setChrome(true);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => { setChrome(false); setHint(false); }, TV_HINT_MS);
      if (tvFullscreenEl()) return;   // the Full screen button has no job once full screen
      setHint(true);
    };
    const onFs = () => {
      const full = !!tvFullscreenEl();
      setIsFull(full);
      if (full) {
        setHint(false);
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => setChrome(false), TV_HINT_MS);
      }
    };
    const onVis = () => { if (document.visibilityState === 'visible') tvAcquireWakeLock(lockRef); };
    showHint();
    onFs();
    tvAcquireWakeLock(lockRef);
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('mousemove', showHint);
    window.addEventListener('touchstart', showHint);
    return () => {
      clearTimeout(hideTimer);
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('mousemove', showHint);
      window.removeEventListener('touchstart', showHint);
      const lock = lockRef.current;
      lockRef.current = null;
      if (lock && !lock.released && typeof lock.release === 'function') lock.release().catch(() => {});
    };
  }, [active]);
  return {
    hint: !!active && hint && !isFull,
    chrome: !!active && chrome,
    goFull: () => { tvEnterFullscreen(); tvAcquireWakeLock(lockRef); },
  };
};

// Flatten the board's rows for paging: a small label row where a non-live section
// starts; positions from the server on a team board (it ranks on the game's basis),
// computed from the display order on an individual board (T on a shared score).
const tvBuildRows = (rows, isTeamBoard, isNet, board) => {
  const keyOf = (r) => isNet
    ? (r.net_vs_par != null ? r.net_vs_par : (r.net_total != null ? r.net_total + 1000 : null))
    : (r.gross_vs_par != null ? r.gross_vs_par : (r.gross_total != null ? r.gross_total + 1000 : null));
  const liveKeys = isTeamBoard ? [] : rows.filter(r => r.section === 'live' && r.has_scores).map(keyOf).filter(k => k != null);
  // A stroke play game on an individual board (Sept 23, 2026): the SERVER's places —
  // the game's tie-break, SP/PO — on a view the game ranks; a player not in the game
  // has none. Otherwise the plain T-tie count, as before.
  const gameRanked = !isTeamBoard && boardRankedOn(board, isNet);
  const positionOf = (r) => {
    if (isTeamBoard) return isBestBallBoard(board) ? teamBoardPlace(board, r, isNet).label : (r.position_label || null);
    if (r.section !== 'live' || !r.has_scores) return null;
    if (gameRanked) return boardPlace(r, isNet).label;
    const k = keyOf(r);
    if (k == null) return null;
    const better = liveKeys.filter(x => x < k).length;
    const tied = liveKeys.filter(x => x === k).length > 1;
    return `${tied ? 'T' : ''}${better + 1}`;
  };
  // "No scores entered", not "Not started" (Dennis, Sept 16): the team has started —
  // it just isn't scoring live (or its card hasn't been keyed at the desk yet).
  const labelFor = (section) => isTeamBoard
    ? 'No scores entered'
    : (section === 'not_current' ? 'Scoring not current' : 'No scores recorded');
  const out = [];
  let lastSection = null;
  const preRound = !!(board && board.state === 'pre_round');   // Oct 20, 2026: the field before any score — no section label
  rows.forEach((r) => {
    if (r.section !== lastSection) {
      if (r.section && r.section !== 'live' && !preRound) out.push({ kind: 'label', key: `label-${r.section}`, text: labelFor(r.section) });
      lastSection = r.section;
    }
    out.push({ kind: 'row', key: r.player_id, row: r, position: positionOf(r) });
  });
  return out;
};

// Full-screen frame shared by the TV board and the TV messages (not started, closed).
const TvFrame = ({ board, logoSrc, status, subExtra, footerLeft, fsHint, chromeHint, onFullscreen, exitHref, exitLabel, exampleLabel, children }) => {
  const { scale, small, portrait } = useTvScale();
  const sub = board ? [
    board.course_name || null,
    board.through > 0 ? `Through ${board.through}` : null,
    boardCountText(board).replace(/^\s*·\s*/, '') || null,
    subExtra || null,
  ].filter(Boolean).join('  ·  ') : '';
  const frame = (
    <div style={small
      ? { position: 'relative', width: TV_DESIGN_W, height: TV_DESIGN_H, background: TV_BG, color: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden', transform: `scale(${scale})`, transformOrigin: 'top left' }
      : { position: 'fixed', inset: 0, background: TV_BG, color: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden', cursor: chromeHint ? 'default' : 'none' }}>
      <style>{TV_CSS}</style>
      <div style={{ position: 'relative', height: TV_HEADER_H, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 24, padding: '0 40px', background: TV_HEADER_BG, borderBottom: `3px solid ${TV_GOLD}` }}>
        {logoSrc && <img src={logoSrc} alt="" style={{ width: 84, height: 84, objectFit: 'contain', borderRadius: 12, background: '#fff', padding: 4, boxSizing: 'border-box', flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.1, letterSpacing: 1, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(board && board.event_name) || 'Leaderboard'}</div>
          {sub && <div style={{ fontSize: 19, color: 'rgba(255,255,255,0.92)', marginTop: 6, letterSpacing: 1.5, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
        </div>
        {fsHint && onFullscreen && (
          <button type="button" onClick={onFullscreen} title="Show the leaderboard full screen"
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.45)', background: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: 17, fontWeight: 700, cursor: 'pointer', animation: 'tvFade 300ms ease-out' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" style={{ display: 'block' }}>
              <path d="M1 6V1h5M12 1h5v5M17 12v5h-5M6 17H1v-5" fill="none" stroke="#fff" strokeWidth="2" />
            </svg>
            Full screen
            <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.7)' }}>or press F11</span>
          </button>
        )}
        {!small && chromeHint && exitHref && (
          <a href={exitHref} title="Leave the TV view and go back to the leaderboard"
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 9, padding: '9px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.45)', background: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: 17, fontWeight: 700, textDecoration: 'none', animation: 'tvFade 300ms ease-out' }}>
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>&#10005;</span>
            Exit TV view
          </a>
        )}
        {status && (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '8px 18px', borderRadius: 999, background: status.live ? '#dc2626' : 'rgba(255,255,255,0.2)', fontSize: 20, fontWeight: 800, letterSpacing: 1.5 }}>
            {status.live && <span style={{ width: 12, height: 12, borderRadius: 6, background: '#fff', display: 'inline-block', animation: 'tvPulse 1.6s ease-in-out infinite' }} />}
            {status.text}
          </div>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: `${TV_BODY_PAD / 2}px 40px`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: '100%', maxWidth: 1640, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</div>
      </div>
      <div style={{ height: TV_FOOTER_H, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 30, padding: '0 40px', borderTop: `1px solid ${TV_LINE}`, background: 'rgba(0,0,0,0.12)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>{footerLeft}</div>
        {board && board.public_address && (
          <div style={{ flexShrink: 0, textAlign: 'center' }}>
            <div style={{ fontSize: 15, color: TV_MUTED, letterSpacing: 1, textTransform: 'uppercase' }}>Follow along</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: TV_GOLD, lineHeight: 1.15 }}>{board.public_address}</div>
          </div>
        )}
        {/* /try/leaderboard (Sept 20, 2026): an invented board says so where a real one
            says "Follow along". Unset everywhere else, so production boards are untouched. */}
        {!(board && board.public_address) && exampleLabel && (
          <div style={{ flexShrink: 0, textAlign: 'center' }}>
            <div style={{ fontSize: 15, color: TV_MUTED, letterSpacing: 1, textTransform: 'uppercase' }}>{exampleLabel}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: TV_GOLD, lineHeight: 1.2 }}>Invented teams and scores · shown as the product shows them</div>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            <img src="/logo.png" alt="Tee Time Organizer" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div style={{ whiteSpace: 'nowrap', lineHeight: 1.15 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Tee Time Organizer</div>
            <div style={{ fontSize: 16, color: TV_MUTED }}>TeeTimeOrganizer.net</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!small) return frame;

  // Phone / small window: letterbox the scaled board and hang the way out under it.
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0b130e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, padding: 16, boxSizing: 'border-box', overflow: 'hidden' }}>
      <div style={{ width: TV_DESIGN_W * scale, height: TV_DESIGN_H * scale, overflow: 'hidden', borderRadius: 6, boxShadow: '0 20px 50px -20px rgba(0,0,0,0.8)', flexShrink: 0 }}>
        {frame}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
        {portrait && (
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, lineHeight: 1.4, maxWidth: 300 }}>
            This is the clubhouse-screen view. Turn your phone sideways for a bigger board.
          </div>
        )}
        {exitHref && (
          <a href={exitHref} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
            <span aria-hidden="true" style={{ fontSize: 17, lineHeight: 1 }}>&#10005;</span>
            {exitLabel || 'See the full leaderboard'}
          </a>
        )}
      </div>
    </div>
  );
};

const TvMessage = ({ board, logoSrc, status, title, sub, fsHint, chromeHint, onFullscreen, exitHref, exitLabel }) => (
  <TvFrame board={board} logoSrc={logoSrc} status={status} fsHint={fsHint} chromeHint={chromeHint} onFullscreen={onFullscreen} exitHref={exitHref} exitLabel={exitLabel}>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 40 }}>
      <div style={{ fontSize: 48, fontWeight: 800, lineHeight: 1.15 }}>{title}</div>
      {sub && <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.9)', marginTop: 18, lineHeight: 1.4, maxWidth: 1100 }}>{sub}</div>}
    </div>
  </TvFrame>
);

// Column widths shared by the header row and the data rows.
const TV_COL = { pos: 96, thru: 110, score: 150, total: 110, gap: 22 };   // pos fits "T12" + an SP/PO mark

const PublicLeaderboardTv = ({ board, rows, isTeamBoard, isNet, logoSrc, lastUpdated, nowTick, pageMs = TV_PAGE_MS, fsHint, chromeHint, onFullscreen, exitHref, exitLabel, exampleLabel }) => {
  // Scaled to fit a phone, the board renders into the fixed design box, so the page
  // size comes from that box rather than from the real viewport height.
  const { small: tvSmall } = useTvScale();
  const [rowsPerPage, setRowsPerPage] = useState(() => tvRowsFor(typeof window !== 'undefined' ? window.innerHeight : 1080));
  const [page, setPage] = useState(0);
  useEffect(() => {
    const onResize = () => setRowsPerPage(tvRowsFor(tvSmall ? TV_DESIGN_H : window.innerHeight));
    onResize();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => { window.removeEventListener('resize', onResize); window.removeEventListener('orientationchange', onResize); };
  }, [tvSmall]);
  const items = tvBuildRows(rows || [], isTeamBoard, isNet, board);
  const pageCount = Math.max(1, Math.ceil(items.length / rowsPerPage));
  // Turn the page every pageMs (8 s unless ?secs= says otherwise) while there is
  // more than one; a field that shrinks below the current page snaps back to the first.
  useEffect(() => {
    if (pageCount <= 1) return undefined;
    const id = setInterval(() => setPage(p => (p + 1) % pageCount), pageMs);
    return () => clearInterval(id);
  }, [pageCount, pageMs]);
  useEffect(() => { if (page >= pageCount) setPage(0); }, [page, pageCount]);
  const holeCount = HOLE_SET_LABEL[board.competition_hole_set] ? 9 : 18;
  const shown = items.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const footer = boardFooter(board, lastUpdated, nowTick);
  // Pre-round board (Oct 20, 2026): "STARTS 9:00 AM" (with the day when it isn't today),
  // then "UNDERWAY" once the start time passes with nothing entered yet.
  const preRoundPill = (() => {
    if (board.state !== 'pre_round' || !board.starts_at) return null;
    const s = new Date(board.starts_at);
    if (isNaN(s.getTime())) return null;
    if ((nowTick || Date.now()) >= s.getTime()) return { live: false, text: 'UNDERWAY' };
    const sameDay = s.toDateString() === new Date().toDateString();
    const t = s.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return { live: false, text: `STARTS ${sameDay ? '' : `${s.toLocaleDateString([], { weekday: 'short' })} `}${t}`.toUpperCase() };
  })();
  const status = preRoundPill || (board.live ? { live: true, text: 'LIVE' } : (board.through > 0 ? { live: false, text: 'FINAL' } : null));
  const anyOverride = isNet && (rows || []).some(r => r.handicap_source === 'override');
  // Marks: the team board's tie_mark, or — an individual board ranked on a stroke play
  // game (Sept 23, 2026) — the game's net_/gross_ mark for this view.
  const gameRanked = !isTeamBoard && boardRankedOn(board, isNet);
  const bbTv = isTeamBoard && isBestBallBoard(board);   // Best Ball (Sept 27, 2026): this view's places
  const markOf = (r) => isTeamBoard ? (bbTv ? teamBoardPlace(board, r, isNet).mark : r.tie_mark) : (gameRanked ? boardPlace(r, isNet).mark : null);
  const pendingOf = (r) => isTeamBoard ? (bbTv ? teamBoardPlace(board, r, isNet).status === 'pending' : r.tie_status === 'pending') : (gameRanked && boardPlace(r, isNet).status === 'pending');
  const tieLegend = (isTeamBoard || gameRanked) ? [
    (rows || []).some(r => markOf(r) === 'SP') ? 'SP scorecard playoff' : null,
    (rows || []).some(r => markOf(r) === 'PO') ? 'PO playoff' : null,
    gameRanked && (rows || []).some(r => r.has_scores && r.in_game === false) ? '– not in the game' : null,
  ].filter(Boolean).join('  ·  ') : '';
  const g = boardGame(board);
  const subExtra = bbTv
    ? [board.format_label || 'Best ball', isNet ? `Net${board.allowance_pct != null ? ` ${board.allowance_pct}%` : ''}` : 'Gross'].join('  ·  ')
    : isTeamBoard
    ? (isNet ? 'Net · team handicap' : null)
    : (g ? (isNet ? `Net · ${g.allowance_pct}% playing handicap` : 'Gross') : null);
  const footerLeft = (
    <div>
      {pageCount > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 18, fontWeight: 700 }}>
          <span>Page {page + 1} of {pageCount}</span>
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {Array.from({ length: pageCount }, (_, i) => i === page ? (
              // the current page: a ring that sweeps round over the hold — the quiet "it's alive"
              <svg key={`dial-${page}`} width="14" height="14" viewBox="0 0 14 14" style={{ display: 'block' }}>
                <circle cx="7" cy="7" r="5" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5" />
                <circle cx="7" cy="7" r="5" fill="none" stroke="#fff" strokeWidth="2.5" strokeDasharray="31.4" strokeLinecap="round"
                  style={{ strokeDashoffset: 31.4, transform: 'rotate(-90deg)', transformOrigin: '50% 50%', animation: `tvDial ${pageMs}ms linear forwards` }} />
              </svg>
            ) : <span key={i} style={{ width: 10, height: 10, borderRadius: 5, background: 'rgba(255,255,255,0.45)', display: 'inline-block' }} />)}
          </span>
        </div>
      )}
      <div style={{ fontSize: 15, marginTop: pageCount > 1 ? 4 : 0, color: footer.stale ? TV_GOLD : TV_MUTED, fontWeight: footer.stale ? 700 : 400 }}>{footer.text.replace(' Tap Refresh.', '')}{anyOverride ? '  ·  * handicap set by the organizer' : ''}{tieLegend ? `  ·  ${tieLegend}` : ''}</div>
    </div>
  );
  const colHead = (text, width, align) => <div style={{ width, flexShrink: 0, textAlign: align, fontSize: 15, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: TV_MUTED }}>{text}</div>;
  return (
    <TvFrame board={board} logoSrc={logoSrc} status={status} subExtra={subExtra} footerLeft={footerLeft} fsHint={fsHint} chromeHint={chromeHint} onFullscreen={onFullscreen} exitHref={exitHref} exitLabel={exitLabel} exampleLabel={exampleLabel}>
      {items.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, color: TV_MUTED }}>
          {isTeamBoard ? 'No teams yet — the organizer hasn’t saved the pairings.' : 'No players to show yet.'}
        </div>
      ) : (
        <div key={page} style={{ animation: 'tvFade 400ms ease-out' }}>
          <div style={{ height: TV_COLHEAD_H, display: 'flex', alignItems: 'center', gap: TV_COL.gap, padding: '0 22px', marginBottom: 12, borderRadius: 10, background: TV_BAND }}>
            {colHead('Pos', TV_COL.pos, 'center')}
            <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: TV_MUTED }}>{isTeamBoard ? 'Team' : 'Player'}</div>
            {colHead('Thru', TV_COL.thru, 'center')}
            {colHead(isNet ? 'Net' : 'To par', TV_COL.score, 'right')}
            {colHead('Total', TV_COL.total, 'right')}
          </div>
          <div style={{ borderRadius: 14, background: TV_PANEL, padding: '6px 0' }}>
          {shown.map((it, i) => {
            const last = i === shown.length - 1;
            if (it.kind === 'label') {
              return (
                <div key={it.key} style={{ height: TV_ROW_H - 8, marginBottom: 8, display: 'flex', alignItems: 'flex-end', paddingBottom: 8, paddingLeft: 22, fontSize: 17, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)', borderBottom: last ? 'none' : `1px solid ${TV_LINE}` }}>{it.text}</div>
              );
            }
            const r = it.row;
            const total = isNet ? r.net_total : r.gross_total;
            const vs = isNet ? r.net_vs_par : r.gross_vs_par;
            const thruTxt = !r.has_scores ? '–' : (r.thru >= holeCount ? 'F' : String(r.thru));
            const thruColor = r.entry_flag === 'red' ? TV_RED : r.entry_flag === 'amber' ? TV_GOLD : '#fff';
            const leader = it.position === '1' || it.position === 'T1';
            const sub = bbTv
              ? [r.team_label || null, r.start_text ? `Start ${r.start_text}` : null, r.blind_text || null, r.unranked_text || null, pendingOf(r) ? 'Playoff pending' : null].filter(Boolean).join('  ·  ')
              : isTeamBoard
              ? [r.team_label || null, r.start_text ? `Start ${r.start_text}` : null, isNet && r.team_handicap != null ? `Hcp ${r.team_handicap}${r.handicap_source === 'override' ? '*' : ''}` : null, r.tie_status === 'pending' ? 'Playoff pending' : null].filter(Boolean).join('  ·  ')
              : [boardHcpText(r, board) || null, pendingOf(r) ? 'Playoff pending' : null].filter(Boolean).join('  ·  ');
            const other = otherBasisText(r, isNet, false);        // "Gross 66" under the total; no to-par, the column is 110px
            return (
              <div key={it.key} style={{ height: TV_ROW_H - 8, marginBottom: 8, display: 'flex', alignItems: 'center', gap: TV_COL.gap, padding: '0 22px',
                borderBottom: last ? 'none' : `1px solid ${TV_LINE}`, boxShadow: leader ? `inset 4px 0 0 ${TV_GOLD}` : 'none' }}>
                <div style={{ width: TV_COL.pos, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 36, fontWeight: 800, color: it.position ? (leader ? TV_GOLD : '#fff') : TV_DIM, whiteSpace: 'nowrap' }}>{it.position || '–'}<TieMark mark={markOf(r)} tv /></span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: sub ? 30 : 33, fontWeight: 700, lineHeight: 1.15, letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                  {sub && <div style={{ fontSize: 17, marginTop: 2, color: TV_MUTED, letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
                </div>
                <div style={{ width: TV_COL.thru, flexShrink: 0, textAlign: 'center', fontSize: 30, fontWeight: 700, color: thruColor }}>{thruTxt}</div>
                <div style={{ width: TV_COL.score, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                  <span style={{ minWidth: 112, textAlign: 'center', padding: '6px 12px', borderRadius: 8, fontSize: 40, fontWeight: 800, lineHeight: 1, ...tvVsCell(vs) }}>{tvFmtVs(vs)}</span>
                </div>
                <div style={{ width: TV_COL.total, flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.15, color: 'rgba(255,255,255,0.95)' }}>{total != null ? total : '–'}</div>
                  {other && <div style={{ fontSize: 17, marginTop: 2, color: TV_MUTED, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{other}</div>}
                </div>
              </div>
            );
          })}
          </div>
          {/* Closest to the Pin (Sept 28, 2026): one line under the rows, every page. */}
          {board.ctp && Array.isArray(board.ctp.holes) && board.ctp.holes.length > 0 && (
            <div data-testid="ctp-tv" style={{ marginTop: 12, padding: '10px 22px', borderRadius: 10, background: TV_BAND, fontSize: 20, color: '#fff', display: 'flex', flexWrap: 'wrap', gap: '6px 26px', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 800, letterSpacing: 1 }}>📍 CLOSEST TO THE PIN</span>
              {board.ctp.holes.map(h => (
                <span key={h.hole_number}><strong>{board.course_nines ? holeName(h.hole_number, board.course_nines) : `#${h.hole_number}`}</strong> <span style={{ color: h.holder_name ? '#fff' : TV_MUTED }}>{h.holder_name ? String(h.holder_name).trim().split(/\s+/).slice(-1)[0] : '—'}</span></span>
              ))}
            </div>
          )}
        </div>
      )}
    </TvFrame>
  );
};

// ============================================================================
// TryLeaderboard — /try/leaderboard (Sept 20, 2026). The clubhouse TV board,
// rendered by PublicLeaderboardTv — the production renderer, unchanged — against
// the invented event in sampleEvent.js. Nothing is fetched, so nothing expires;
// the footer says it is an example where a real board says "Follow along".
// Dennis: a prospect does not care whether it is live, only that it looks like
// the product and that it never rots. Linked from /events; ?secs= works as on
// the real TV view.
// ============================================================================
const TryLeaderboard = () => {
  const tvScreen = useTvScreen(true);                     // wake lock + the Full screen button, as on the real TV view
  const [searchParams] = useSearchParams();
  const secs = parseInt(searchParams.get('secs'), 10);
  const pageMs = secs >= 3 && secs <= 60 ? secs * 1000 : TV_PAGE_MS;
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNowTick(Date.now()), 15000); return () => clearInterval(id); }, []);
  useEffect(() => { const prev = document.title; document.title = 'Example leaderboard \u2014 Tee Time Organizer'; return () => { document.title = prev; }; }, []);
  return (
    <PublicLeaderboardTv board={SAMPLE_BOARD} rows={SAMPLE_ROWS} isTeamBoard isNet={false}
      logoSrc={null} lastUpdated={null} nowTick={nowTick} pageMs={pageMs}
      fsHint={tvScreen.hint} chromeHint={tvScreen.chrome} onFullscreen={tvScreen.goFull}
      exitHref="/events" exitLabel="Back to Events" exampleLabel="Example board" />
  );
};

// ============================================================================
// PublicLeaderboardLanding — bare /lb (Sept 16, 2026). ttoleaderboard.com with
// nothing after it lands here: one box ("enter your event's name"), which the
// public lookup matches against event addresses (turkey-shoot-2026), plus "or
// scan the code on your scorecard". Deliberately NOT a list of events — a
// public directory of every club's boards is not something orgs want. When
// the typed name matches exactly one event we go straight to its board; a few
// same-day candidates are offered as buttons; otherwise a gentle miss.
// ============================================================================
const PublicLeaderboardLanding = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState(null);   // [] = nothing found; null = not searched
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const find = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const text = q.trim();
    if (!text || busy) return;
    setBusy(true); setMatches(null);
    try {
      const resp = await fetch(`${API_URL}/public/leaderboard/lookup?q=${encodeURIComponent(text)}&t=${Date.now()}`, { cache: 'no-store' });
      const json = await resp.json().catch(() => ({ matches: [] }));
      if (json && json.slug) { navigate(`/lb/${json.slug}`); return; }
      setMatches(Array.isArray(json && json.matches) ? json.matches : []);
    } catch (err) {
      setMatches([]);
    } finally {
      setBusy(false);
    }
  };
  const fmtDate = (ds) => { if (!ds) return ''; const d = new Date(String(ds).slice(0, 10) + 'T12:00:00'); return isNaN(d.getTime()) ? ds : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column',
      justifyContent: 'flex-start', alignItems: 'center', padding: '24px 12px' }}>
      <div style={{ width: '100%', maxWidth: 520, background: '#fff', borderRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,.08)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
          background: '#166534', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#fff', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <img src="/logo.png" alt="Tee Time Organizer" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          {/* Oct 28, 2026: the web address under the name, as on the printed scorecard. */}
          <a href="https://teetimeorganizer.net" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', lineHeight: 1.15 }}>
            <span style={{ display: 'block', fontWeight: 700, fontSize: 15, color: '#fff' }}>Tee Time Organizer</span>
            <span style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>TeeTimeOrganizer.net</span>
          </a>
        </div>
        <div style={{ padding: '28px 22px 24px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937', textAlign: 'center' }}>🏆 Live leaderboards</div>
          <div style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', marginTop: 6, lineHeight: 1.5 }}>
            Enter your event’s name to follow the live scores.
          </div>
          <form onSubmit={find} style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => { setQ(e.target.value); setMatches(null); }}
                placeholder="e.g. Turkey Shoot"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                style={{ flex: 1, minWidth: 0, padding: '12px 14px', fontSize: 17, border: '1px solid #d1d5db', borderRadius: 10, outline: 'none', boxSizing: 'border-box' }}
              />
              <button type="submit" disabled={busy || !q.trim()}
                style={{ padding: '12px 18px', background: '#166534', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
                  cursor: busy || !q.trim() ? 'default' : 'pointer', opacity: busy || !q.trim() ? 0.6 : 1, flexShrink: 0 }}>
                {busy ? '…' : 'Go'}
              </button>
            </div>
          </form>
          {matches && matches.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#9ca3af', marginBottom: 6 }}>Did you mean</div>
              {matches.map((m) => (
                <button key={m.slug} type="button" onClick={() => navigate(`/lb/${m.slug}`)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 6, background: '#fff',
                    border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer' }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>📅 {m.name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{[m.course_name, fmtDate(m.date)].filter(Boolean).join(' · ')} · {PUBLIC_BOARD_HOST}/{m.slug}</div>
                </button>
              ))}
            </div>
          )}
          {matches && matches.length === 0 && (
            <div style={{ marginTop: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
              We couldn’t find an event by that name. Check the address printed on your scorecard — it looks like <strong>{PUBLIC_BOARD_HOST}/turkey-shoot-2026</strong> — and type the part after the slash.
            </div>
          )}
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid #f3f4f6', fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.5 }}>
            Or scan the QR code on your scorecard or cart sign.
          </div>
        </div>
      </div>
      <a href="https://teetimeorganizer.net/organizer"
        style={{ marginTop: 14, fontSize: 12, color: '#6b7280', textDecoration: 'none' }}>
        Live scoring by <span style={{ color: '#166534', fontWeight: 700 }}>teetimeorganizer.net</span>
      </a>
    </div>
  );
};

// ============================================================================
// PublicLeaderboard — standalone public spectator page at /lb/:key
// Enhancement B. No login, no token. Fetches the public endpoint (which reuses
// the SAME buildSpectatorBoard the in-app LeaderboardModal uses), so rows/sort/
// net math are identical to what players see in-app. Renders the same board UI,
// minus the "(you)" highlight (no viewer), Games tab, and modal chrome; adds the
// public state screens (not_started / unavailable / expired / not_found).
// :key is the tee time uuid OR, since Sept 16, 2026, an event's public address
// (ttoleaderboard.com/turkey-shoot-2026 → /lb/turkey-shoot-2026); the server
// resolves either and answers with public_address for the header.
// ============================================================================
// Public results page (Oct 4, 2026 — Event_Recap_Plan_v1.md step 5): after the live board's
// 24 hours, an event whose recap is posted shows its results here for a year — the story,
// the podium and closest to the pin. No hole-by-hole cards, no money. Also the "Results
// posted" card above a still-open live board.
const fmtToPar = (v) => (v == null ? '' : v === 0 ? 'E' : v > 0 ? `+${v}` : `${v}`);
const PublicResultsBody = ({ r, compact = false, nines = null }) => (
  <div style={{ display: 'grid', gap: 14 }}>
    {r.headline && <div style={{ fontSize: compact ? 16 : 21, fontWeight: 800, color: '#111827', lineHeight: 1.25 }}>{r.headline}</div>}
    {(r.paragraphs || []).map((p, i) => <p key={i} style={{ margin: 0, fontSize: 15, color: '#374151', lineHeight: 1.6 }}>{p}</p>)}
    {Array.isArray(r.podium) && r.podium.length > 0 && (
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '8px 14px', background: '#f0fdf4', fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', color: '#166534' }}>FINAL RESULTS{r.format ? ` · ${String(r.format).toUpperCase()}` : ''}</div>
        {r.podium.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderTop: i ? '1px solid #f3f4f6' : 'none' }}>
            <div style={{ width: 34, flexShrink: 0, fontWeight: 800, fontSize: 16, color: '#166534' }}>{p.label}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{p.name}</div>
              {p.members && p.members.length > 0 && <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>{p.members.join(' · ')}</div>}
              {p.decided_by && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Won on the {p.decided_by}</div>}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              {p.to_par != null && <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{fmtToPar(p.to_par)}</div>}
              {p.score != null && <div style={{ fontSize: 12, color: '#6b7280' }}>{p.score}</div>}
            </div>
          </div>
        ))}
      </div>
    )}
    {Array.isArray(r.closest_to_pin) && r.closest_to_pin.length > 0 && (
      <div style={{ fontSize: 14, color: '#374151' }}>
        <strong>Closest to the pin:</strong> {r.closest_to_pin.map(c => `${(nines || r.course_nines) ? holeName(c.hole, nines || r.course_nines) : `#${c.hole}`} ${c.name}`).join(' · ')}
      </div>
    )}
  </div>
);

const PublicLeaderboard = ({ tv = false }) => {
  const { key: ttid } = useParams();
  const [searchParams] = useSearchParams();
  // The clubhouse TV view (paging, big type): /lb/<key>/tv, or ?tv=1 on /lb/<key>.
  const tvMode = tv === true || searchParams.get('tv') === '1';
  // Exit goes back where the viewer came from. `?from=events` is set by the link on
  // the /events marketing page; anyone else — a spectator off a printed card or a
  // shared link — wants the ordinary leaderboard, not a page selling the product.
  const fromEvents = searchParams.get('from') === 'events';
  const tvExitHref = fromEvents ? '/events' : `/lb/${ttid}`;
  const tvExitLabel = fromEvents ? 'Back to the events page' : 'See the full leaderboard';
  // Opened from an event page (Oct 22, 2026): ?event=<address> → a "← Event page" link in the
  // header, so a phone has a way back (the board used to open in a new tab with no Back).
  const eventBack = (() => { const v = searchParams.get('event'); return v && /^[a-z0-9-]{1,48}$/i.test(v) ? v : null; })();
  const tvScreen = useTvScreen(tvMode);            // keep the display awake + the Full screen button
  const tvSecs = parseInt(searchParams.get('secs'), 10);   // optional &secs=12 — seconds a page holds (3–60)
  const tvPageMs = tvSecs >= 3 && tvSecs <= 60 ? tvSecs * 1000 : TV_PAGE_MS;
  const [data, setData] = useState(null);        // full response incl. `state`
  const [loading, setLoading] = useState(true);
  const [netView, setNetView] = useState(false); // false=Gross, true=Net
  const [lastUpdated, setLastUpdated] = useState(null);
  const [openPlayerId, setOpenPlayerId] = useState(null); // scorecard drill-down
  const [nowTick, setNowTick] = useState(Date.now());     // staleness clock (§6.5)
  useEffect(() => { const id = setInterval(() => setNowTick(Date.now()), 15000); return () => clearInterval(id); }, []);
  // A net team event opens on the Net view — that is the basis its places are ranked on.
  // So does an individual board ranked on a net stroke play game (Sept 23, 2026).
  const teamBasis = data && data.scoring_mode === 'team' ? (data.basis || 'gross') : null;
  const gameBasis = data && boardGame(data) ? (boardOpensNet(data) ? 'net' : 'gross') : null;
  useEffect(() => { if (teamBasis === 'net' || gameBasis === 'net') setNetView(true); }, [teamBasis, gameBasis]);

  const fmtVs = (v) => v == null ? '—' : (v === 0 ? 'E' : (v > 0 ? `+${v}` : `${v}`));

  const load = async () => {
    try {
      // Cache-bust: a stable GET URL was being served stale by an edge/browser
      // cache (~5-min TTL), so the 20s poll returned unchanged data between
      // cache expiries. Unique ?t= + no-store forces a fresh origin hit every
      // time. Route reads req.params.ttid and ignores the query string.
      const resp = await fetch(`${API_URL}/public/leaderboard/${ttid}?t=${Date.now()}`, { cache: 'no-store' });
      const json = await resp.json().catch(() => ({ state: 'error' }));
      setData(json);
      setLastUpdated(Date.now());
    } catch (e) {
      setData({ state: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttid]);

  // Auto-refresh every 20s ONLY while the round is live (mirrors the in-app poll
  // cadence). Stop polling once the board is complete/expired/unavailable so a
  // link left open overnight doesn't hammer the endpoint.
  // Oct 20, 2026: also polls (every 60 s) on 'not_started' and 'pre_round', so a TV
  // opened before the round flips to the field and then the live board by itself — it
  // used to sit on "hasn't started yet" until someone reloaded it.
  useEffect(() => {
    const pollState = data && data.state;
    if (pollState !== 'live' && pollState !== 'not_started' && pollState !== 'pre_round') return;
    const pollMs = pollState === 'live' ? 20000 : 60000;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const resp = await fetch(`${API_URL}/public/leaderboard/${ttid}?t=${Date.now()}`, { cache: 'no-store' });
        if (!resp.ok || cancelled) return;
        const json = await resp.json();
        if (cancelled) return;
        setData(json);
        setLastUpdated(Date.now());
      } catch (e) { /* keep last-known */ }
    };
    const interval = setInterval(tick, pollMs);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && data.state, ttid]);

  // ---- page shell (centered card, matches in-app look, no modal overlay) ----
  const Shell = ({ children }) => (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column',
      justifyContent: 'flex-start', alignItems: 'center', padding: '24px 12px' }}>
      <div style={{ width: '100%', maxWidth: 520, background: '#fff', borderRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,.08)', overflow: 'hidden' }}>
        {/* TTO brand header — visible immediately on every state */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
          background: '#166534', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#fff', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <img src="/logo.png" alt="Tee Time Organizer" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          {/* Oct 28, 2026: the web address under the name, as on the printed scorecard. */}
          <a href="https://teetimeorganizer.net" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', lineHeight: 1.15 }}>
            <span style={{ display: 'block', fontWeight: 700, fontSize: 15, color: '#fff' }}>Tee Time Organizer</span>
            <span style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>TeeTimeOrganizer.net</span>
          </a>
          {eventBack && (
            <a href={`/e/${eventBack}`} data-testid="lb-back-to-event"
              style={{ marginLeft: 'auto', minHeight: 36, display: 'inline-flex', alignItems: 'center', padding: '6px 12px', borderRadius: 8,
                background: '#fff', color: '#166534', fontSize: 14, fontWeight: 700, textDecoration: 'none', flexShrink: 0 }}>
              ← Event page
            </a>
          )}
        </div>
        {children}
      </div>
      {/* Footer CTA — links curious spectators to the site */}
      <a href="https://teetimeorganizer.net/organizer"
        style={{ marginTop: 14, fontSize: 12, color: '#6b7280', textDecoration: 'none' }}>
        Live scoring by <span style={{ color: '#166534', fontWeight: 700 }}>teetimeorganizer.net</span>
      </a>
    </div>
  );

  // The event's logo (Sept 16, 2026) — API-relative in the answer; absent → the TTO mark alone.
  const logoSrc = data && data.logo_url ? `${API_URL}${data.logo_url}` : null;

  // ---- centered message for non-live states (full-screen on the TV) ----
  const Message = ({ emoji, title, sub, logo }) => tvMode ? (
    <TvMessage board={data && data.state !== 'error' ? data : null} logoSrc={logo || null} status={null} title={title} sub={sub ? String(sub).replace(/\s*Live scores at .*$/, '') : sub}
      fsHint={tvScreen.hint} chromeHint={tvScreen.chrome} onFullscreen={tvScreen.goFull} exitHref={tvExitHref} exitLabel={tvExitLabel} />
  ) : (
    <Shell>
      <div style={{ padding: '48px 24px', textAlign: 'center' }}>
        {logo
          ? <img src={logo} alt="" style={{ maxWidth: 160, maxHeight: 96, objectFit: 'contain', marginBottom: 14 }} />
          : <div style={{ fontSize: 32, marginBottom: 12 }}>{emoji}</div>}
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>{title}</div>
        {sub && <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5 }}>{sub}</div>}
      </div>
    </Shell>
  );

  if (loading) {
    return <Message emoji="⏳" title="Loading leaderboard…" />;
  }
  if (!data) {
    return <Message emoji="⚠️" title="Something went wrong" sub="Please try again in a moment." />;
  }

  switch (data.state) {
    case 'not_found':
      return <Message emoji="🔍" title="Leaderboard not found" sub="This link doesn’t point to a round we can find." />;
    case 'unavailable':
      return <Message emoji="🔒" title="Leaderboard not available" sub="This round isn’t being shared, or live scoring isn’t enabled for it." />;
    case 'expired':
      return <Message emoji="🏁" logo={logoSrc} title={data.event_name ? `${data.event_name} has closed` : "This leaderboard has closed"} sub="The round is over and results are no longer shown here." />;
    case 'results': {
      if (tvMode) return <Message emoji="🏆" logo={logoSrc} title={`${data.event_name || 'The event'} — final results`} sub={data.headline || ''} />;
      let until = '';
      try { if (data.results_until) until = new Date(data.results_until).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' }); } catch (e) { /* ignore */ }
      return (
        <Shell>
          <div style={{ padding: '18px 18px 8px', display: 'flex', gap: 12, alignItems: 'flex-start', borderBottom: '1px solid #f3f4f6' }}>
            {logoSrc && <img src={logoSrc} alt="" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }} />}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', color: '#166534' }}>RESULTS</div>
              <div style={{ fontSize: 19, fontWeight: 800, color: '#111827' }}>{data.event_name}</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{[data.course_name, data.date_label].filter(Boolean).join(' · ')}</div>
              {data.public_address && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>{data.public_address}</div>}
            </div>
          </div>
          <div style={{ padding: 18 }}>
            <PublicResultsBody r={data} />
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 16 }}>Posted by the event organizer{until ? ` · on this page until ${until}` : ''}.</div>
          </div>
        </Shell>
      );
    }
    case 'pre_round': {
      // Oct 20, 2026: the TV shows the field at any time; the phone page from an hour
      // before the start (server's phone_from) — until then, the "check back" message.
      const phoneFrom = data.phone_from ? new Date(data.phone_from).getTime() : 0;
      if (tvMode || !(nowTick < phoneFrom)) break;   // → render the board below
    }
    // falls through: a phone more than an hour early sees "hasn't started yet"
    // eslint-disable-next-line no-fallthrough
    case 'not_started': {
      let when = '';
      try {
        if (data.starts_at) {
          when = new Date(data.starts_at).toLocaleString([], {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
          });
        }
      } catch (e) { /* ignore */ }
      return <Message emoji="⛳" logo={logoSrc} title={data.event_name ? `${data.event_name} hasn’t started yet` : "This round hasn’t started yet"}
        sub={(when ? `Check back around ${when}.` : 'Check back once the round is underway.') + (data.public_address ? ` Live scores at ${data.public_address}.` : '')} />;
    }
    case 'error':
      return <Message emoji="⚠️" title="Something went wrong" sub="Please try again in a moment." />;
    default:
      break; // 'live' | 'complete' | 'pre_round' → render the board below
  }

  // ---- live / complete: render the board (same logic as LeaderboardModal) ----
  const board = data; // response IS the board payload for live/complete
  const isTeamBoard = board.scoring_mode === 'team';
  const showNetToggle = !isTeamBoard || board.has_net === true;   // Net suppressed for a gross team event (§6.4)
  const footer = boardFooter(board, lastUpdated, nowTick);
  const SECTION_LABEL = { live: 'Live Ranking', not_current: 'Scoring Not Current', no_scores: 'No Scores Recorded' };

  // The TV shows ONE basis: a net team event's net, a stroke play game's basis (net
  // unless gross-only — Sept 23, 2026), otherwise gross. The phone view toggles.
  const tvNet = isTeamBoard ? (board.has_net === true && (board.basis || 'gross') === 'net') : boardOpensNet(board);
  const displayRows = sortBoardRows(board.rows, tvMode ? tvNet : netView, board);
  const rankedView = !isTeamBoard && boardRankedOn(board, netView);
  const pubTieNotes = rankedView ? tieFootnotes(board, displayRows, netView ? 'net' : 'gross') : [];

  if (tvMode) {
    return (
      <PublicLeaderboardTv board={board} rows={isTeamBoard ? (board.rows || []) : displayRows} isTeamBoard={isTeamBoard}
        isNet={tvNet}
        logoSrc={logoSrc} lastUpdated={lastUpdated} nowTick={nowTick} pageMs={tvPageMs}
        fsHint={tvScreen.hint} chromeHint={tvScreen.chrome} onFullscreen={tvScreen.goFull} exitHref={tvExitHref} exitLabel={tvExitLabel} />
    );
  }

  let lastSection = null;

  return (
    <Shell>
      {/* Header */}
      <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          {/* Event logo (Sept 16, 2026) beside the title — the TTO mark stays in the bar above */}
          {logoSrc && (
            <img src={logoSrc} alt="" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }} />
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: '#1f2937' }}>🏆 Leaderboard</div>
            {board.event_name && <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', marginTop: 2 }}>📅 {board.event_name}</div>}
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
              {board.course_name}
              {board.through > 0 ? ` · Through ${board.through}` : ''}{holeSetSuffix(board)}
              {boardCountText(board)}
            </div>
            {board.public_address && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>{board.public_address}</div>}
          </div>
          <button
            onClick={() => load()}
            style={{ flexShrink: 0, padding: '6px 12px', background: '#f3f4f6', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Pre-round board (Oct 20, 2026): the field before the first score */}
      {board.state === 'pre_round' && (() => {
        let when = '';
        try { if (board.starts_at) when = new Date(board.starts_at).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { /* ignore */ }
        const underway = board.starts_at && nowTick >= new Date(board.starts_at).getTime();
        return (
          <div data-testid="pre-round-note" style={{ margin: '12px 16px 0', border: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: 12, padding: '10px 14px', fontSize: 14, color: '#374151', lineHeight: 1.45 }}>
            <strong>{underway ? 'Underway' : `Starting ${when}`}</strong> · Scores show here as {board.scoring_mode === 'team' ? 'teams' : 'players'} post them. This page updates by itself.
          </div>
        );
      })()}

      {/* Step 5 (Oct 4, 2026): the posted results recap, above the board while it's still open */}
      {board.results_recap && (
        <details style={{ margin: '12px 16px 0', border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 12, padding: '10px 14px' }}>
          <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#166534' }}>🏆 Results posted: {board.results_recap.headline}</summary>
          <div style={{ marginTop: 10 }}><PublicResultsBody r={board.results_recap} compact nines={board.course_nines || null} /></div>
        </details>
      )}

      {/* Body */}
      <div style={{ padding: 16 }}>
        {/* Gross/Net toggle — hidden on a team board with no team handicap (gross event) */}
        {showNetToggle && (
        <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: 12 }}>
          <button onClick={() => setNetView(false)}
            style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
              background: !netView ? '#1f2937' : '#f9fafb', color: !netView ? '#fff' : '#6b7280' }}>
            Gross
          </button>
          <button onClick={() => setNetView(true)}
            style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
              borderLeft: '1px solid #e5e7eb',
              background: netView ? '#1f2937' : '#f9fafb', color: netView ? '#fff' : '#6b7280' }}>
            Net
          </button>
        </div>
        )}

        {isTeamBoard ? (
          !displayRows.length ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 15 }}>
              No teams yet — the organizer hasn't saved the pairings.
            </div>
          ) : <TeamBoardRows board={board} viewerId={null} onOpenTeam={setOpenPlayerId} isNet={netView && board.has_net === true} />
        ) : (!displayRows.length) ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 15 }}>
            No players to show yet.
          </div>
        ) : displayRows.map((r) => {
          const total = netView ? r.net_total : r.gross_total;
          const vs = netView ? r.net_vs_par : r.gross_vs_par;
          const vsColor = vs == null ? '#9ca3af' : (vs < 0 ? '#166534' : vs > 0 ? '#9ca3af' : '#6b7280');
          const thruColor = r.entry_flag === 'red' ? '#dc2626' : r.entry_flag === 'amber' ? '#f59e0b' : '#6b7280';
          const showHeader = r.section !== lastSection;
          lastSection = r.section;
          const headerText = boardSectionHeader(SECTION_LABEL[r.section], r.section, netView, board);
          return (
            <React.Fragment key={r.player_id}>
              {showHeader && (
                <div style={{ margin: '14px 2px 6px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: r.section === 'not_current' ? '#dc2626' : '#9ca3af' }}>
                    {headerText}
                  </div>
                  {r.section === 'no_scores' && (
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginTop: 2, lineHeight: 1.4 }}>
                      These players either haven’t started their round, or aren’t recording scores live.
                    </div>
                  )}
                </div>
              )}
              <div
                onClick={r.has_scores ? () => setOpenPlayerId(r.player_id) : undefined}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: 10, marginBottom: 4,
                background: '#fff', border: '1px solid #f3f4f6',
                cursor: r.has_scores ? 'pointer' : 'default' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  {rankedView && r.section === 'live' && (() => { const pl = boardPlace(r, netView); return (
                    <div style={{ minWidth: 30, textAlign: 'center', fontSize: 15, fontWeight: 800, color: pl.label ? '#1f2937' : '#d1d5db', flexShrink: 0 }}>
                      {pl.label || '–'}<TieMark mark={pl.mark} />
                    </div>
                  ); })()}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.name}
                    </div>
                    <div style={{ fontSize: 11, color: thruColor, marginTop: 1, fontWeight: r.entry_flag !== 'none' ? 700 : 400 }}>
                      {r.has_scores ? `Thru ${r.thru}` : 'No scores'}
                      {boardHcpText(r, board) ? ` · ${boardHcpText(r, board)}` : ''}
                      {rankedView && boardPlace(r, netView).status === 'pending' && <span style={{ color: '#b45309', fontWeight: 700 }}> · Playoff pending</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: 22, color: '#1f2937', minWidth: 30, textAlign: 'right' }}>
                      {total != null ? total : '—'}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: vsColor, minWidth: 30, textAlign: 'right' }}>
                      {fmtVs(vs)}
                    </span>
                  </div>
                  {otherBasisText(r, netView) && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1, whiteSpace: 'nowrap' }}>{otherBasisText(r, netView)}</div>}
                </div>
              </div>
            </React.Fragment>
          );
        })}

        <CtpBoardSection ctp={board.ctp} nines={board.course_nines || null} />

        {pubTieNotes.length > 0 && (
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, lineHeight: 1.5 }}>
            {pubTieNotes.map(t => <div key={t}>{t}</div>)}
          </div>
        )}

        <div style={{ textAlign: 'center', fontSize: 11, color: footer.stale ? '#b45309' : '#9ca3af', fontWeight: footer.stale ? 700 : 400, marginTop: 12 }}>
          {footer.text}
        </div>

        {openPlayerId && (
          <PlayerScorecardModal
            player={(board.rows || []).find(r => r.player_id === openPlayerId)}
            board={board}
            hideNet={isTeamBoard && board.has_net !== true}
            onClose={() => setOpenPlayerId(null)}
          />
        )}
      </div>
    </Shell>
  );
};

// ============================================================================
// ROUND RECAP — player app (Oct 3, 2026) — claude/Round_Recap_Plan_v1.md
// Backend: services/recapService.js, routes/recapRoutes.js
//   GET /api/player/recap/latest?token=      newest published recap (3 days)
//   GET /api/player/recap/:teetimeId?token=  one recap + this player's "Your round"
// PlayerRecapCard sits at the top of the portal; RecapTeaser sits in the
// leaderboard header; both open RoundRecapModal. Facts and "Your round" are
// computed on the server; the text was written by Claude and checked there.
// Money (Dennis, Oct 3): the story never mentions it; `recap.games` is present
// only for a player who was in the round's games. Followers (RecapFollowToggle,
// in the profile) get every recap even on days they don't play. When a player's
// round had no live scores, the card says so — the nudge toward live scoring.
// ============================================================================
const RECAP_GREEN = '#1F5C3F';
const RECAP_INK = '#14271D';

const fmtRecapMoney = (v) => {
  if (v == null) return null;
  const a = Math.abs(Number(v)).toFixed(2);
  return Number(v) > 0 ? `+$${a}` : Number(v) < 0 ? `−$${a}` : '$0.00';
};

const RoundRecapModal = ({ token, teetimeId, initialRecap = null, onClose, onOpenLeaderboard }) => {
  const [recap, setRecap] = useState(initialRecap);
  const [loading, setLoading] = useState(!initialRecap);
  const [shareMsg, setShareMsg] = useState('');

  useEffect(() => {
    if (initialRecap) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/player/recap/${teetimeId}?token=${encodeURIComponent(token)}`);
        const data = resp.ok ? await resp.json() : {};
        if (!cancelled) setRecap(data.recap || null);
      } catch (e) { /* shown as "not available" below */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token, teetimeId, initialRecap]);

  const share = async () => {
    if (!recap) return;
    const text = `${recap.headline}\n\n${(recap.paragraphs || []).join('\n\n')}\n\n${recap.course || ''}${recap.date ? ' · ' + recap.date : ''}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: recap.headline, text });
        return;
      }
      await navigator.clipboard.writeText(text);
      setShareMsg('Copied. Paste it into your group text.');
    } catch (e) {
      if (e && e.name === 'AbortError') return; // the player closed the share sheet
      setShareMsg('Could not share from this browser.');
    }
  };

  const you = recap && recap.you;
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: 12 }}
      onClick={onClose}
    >
      <div
        role="dialog" aria-label="Round recap"
        style={{ width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', background: '#F3F5F1', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ background: RECAP_GREEN, color: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, borderRadius: '16px 16px 0 0' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{recap ? (recap.event ? recap.event_name : `${recap.date || ''}${recap.course ? ' · ' + recap.course : ''}`) : 'Round recap'}</div>
            {recap && recap.event ? <div style={{ fontSize: 13, color: '#CFE3D6' }}>{recap.date}{recap.course ? ` \u00b7 ${recap.course}` : ''}{recap.entries ? ` \u00b7 ${recap.entries} ${recap.team_event ? 'teams' : 'players'}` : ''} &middot; Final</div>
              : (recap && recap.field ? <div style={{ fontSize: 13, color: '#CFE3D6' }}>{recap.field} players &middot; Final</div> : null)}
          </div>
          <button onClick={onClose} aria-label="Close recap"
            style={{ minWidth: 44, minHeight: 44, background: 'rgba(255,255,255,.15)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            Close
          </button>
        </div>

        <div style={{ padding: 16, display: 'grid', gap: 14 }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Loading recap...</div>}
          {!loading && !recap && (
            <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>This round's recap isn't available yet.</div>
          )}
          {recap && (
            <>
              <div style={{ background: '#fff', borderRadius: 14, padding: '18px 18px 16px', boxShadow: '0 1px 2px rgba(20,40,30,.08)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: RECAP_GREEN, marginBottom: 10 }}>{recap.event ? 'EVENT RESULTS' : 'ROUND RECAP'}</div>
                <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 25, lineHeight: 1.18, fontWeight: 700, color: RECAP_INK, marginBottom: 12 }}>{recap.headline}</div>
                {(recap.paragraphs || []).map((p, i) => (
                  <p key={i} style={{ margin: '0 0 10px', fontSize: 15, lineHeight: 1.55, color: '#1B1F1C' }}>{p}</p>
                ))}
              </div>

              {(recap.tiles || []).length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                  {recap.tiles.map((t, i) => (
                    <div key={i} style={{ background: '#fff', borderRadius: 12, padding: 14 }}>
                      <div style={{ fontSize: 12, color: '#5B6660', fontWeight: 600 }}>{t.label}</div>
                      <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 28, fontWeight: 700, color: RECAP_INK }}>{t.value}</div>
                      <div style={{ fontSize: 13, color: '#1B1F1C' }}>{t.sub}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Event recap (Oct 4, 2026): this golfer's team (or own result) and place. */}
              {recap.team && recap.team.scored === false && (
                <div style={{ background: '#fff', border: '1px dashed #C9D3CC', borderRadius: 14, padding: '14px 18px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: RECAP_GREEN, marginBottom: 6 }}>{recap.team.team ? 'YOUR TEAM' : 'YOUR RESULT'}</div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: '#33413A' }}>{recap.team.team ? "Your team's card wasn't entered." : "Your card wasn't entered."}</div>
                </div>
              )}
              {recap.team && recap.team.scored !== false && (
                <div style={{ background: '#E3EFE7', borderRadius: 14, padding: '16px 18px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: RECAP_GREEN, marginBottom: 6 }}>{recap.team.team ? 'YOUR TEAM' : 'YOUR RESULT'}</div>
                  {recap.team.team && <div style={{ fontSize: 14, color: '#33413A', marginBottom: 6 }}>{(recap.team.members || []).join(', ')}</div>}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 34, fontWeight: 700, color: RECAP_INK }}>{recap.team.place ? (String(recap.team.place).startsWith('T') ? `T${recap.team.place.slice(1)}` : recap.team.place) : '\u2014'}</div>
                    <div style={{ fontSize: 15, color: '#33413A' }}>{recap.team.basis === 'net' ? 'net ' : ''}{recap.team.score != null ? recap.team.score : ''}{recap.team.toPar != null ? ` (${recap.team.toPar === 0 ? 'E' : recap.team.toPar > 0 ? '+' + recap.team.toPar : recap.team.toPar})` : ''}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#4A5751' }}>place</div>
                </div>
              )}

              {you && you.scored === false && (
                <div style={{ background: '#fff', border: '1px dashed #C9D3CC', borderRadius: 14, padding: '14px 18px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: RECAP_GREEN, marginBottom: 6 }}>YOUR ROUND</div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: '#33413A' }}>Your round wasn't scored in the app. Score on your phone next time and your numbers show up here.</div>
                </div>
              )}

              {you && you.scored !== false && (
                <div style={{ background: '#E3EFE7', borderRadius: 14, padding: '16px 18px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: RECAP_GREEN, marginBottom: 6 }}>YOUR ROUND</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
                    <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 34, fontWeight: 700, color: RECAP_INK }}>{you.gross}</div>
                    <div style={{ fontSize: 15, color: '#33413A' }}>{you.net != null ? `net ${you.net}` : ''}{you.tee ? `${you.net != null ? ' \u00b7 ' : ''}${you.tee} tees` : ''}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                    <div><div style={{ fontSize: 18, fontWeight: 700 }}>{you.putts != null ? you.putts : '\u2014'}</div><div style={{ fontSize: 12, color: '#4A5751' }}>putts</div></div>
                    <div><div style={{ fontSize: 18, fontWeight: 700 }}>{you.birdies}</div><div style={{ fontSize: 12, color: '#4A5751' }}>{you.birdies === 1 ? 'birdie' : 'birdies'}</div></div>
                  </div>
                </div>
              )}

              {recap.games && (
                <div style={{ background: '#FFF8E1', border: '1px solid #F2DFA0', borderRadius: 14, padding: '14px 18px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: '#7A5B00', marginBottom: 6 }}>TODAY'S GAMES</div>
                  {recap.games.biggestWinner && (
                    <div style={{ fontSize: 15, color: '#1B1F1C' }}>Biggest winner: <strong>{recap.games.biggestWinner.name}</strong> {fmtRecapMoney(recap.games.biggestWinner.net)}</div>
                  )}
                  <div style={{ fontSize: 15, color: '#1B1F1C', marginTop: 4 }}>You finished <strong>{fmtRecapMoney(recap.games.you)}</strong> on the day.</div>
                  <div style={{ fontSize: 12, color: '#7A5B00', marginTop: 6 }}>Only players in the games see this. Sharing leaves it out.</div>
                </div>
              )}

              {!recap.games && recap.gamesTeaser && (
                <div style={{ background: '#FFF8E1', border: '1px solid #F2DFA0', borderRadius: 14, padding: '12px 18px', fontSize: 14, color: '#1B1F1C' }}>
                  {recap.gamesTeaser.text}
                </div>
              )}

              <button onClick={share}
                style={{ minHeight: 48, borderRadius: 12, background: RECAP_GREEN, color: '#fff', border: 'none', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
                Share the recap
              </button>
              {shareMsg && <div style={{ fontSize: 13, color: '#166534', textAlign: 'center' }}>{shareMsg}</div>}
              {onOpenLeaderboard && (
                <button onClick={() => onOpenLeaderboard(recap.teetime_id)}
                  style={{ minHeight: 48, borderRadius: 12, background: '#fff', color: RECAP_GREEN, border: '1px solid #C9D3CC', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                  Leaderboard and results
                </button>
              )}
              <div style={{ fontSize: 12, lineHeight: 1.5, color: '#5B6660' }}>
                Written by AI from the final scores. Your organizer can edit or turn off recaps.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Keeps a recap screen current while the player app stays open (Oct 29, 2026 — Dennis:
// the recap written at finalize only showed up after the player closed and reopened the
// app, because these screens fetched once when they opened). Calls load() every
// RECAP_REFRESH_MS while the page is on screen, and at once when the phone brings the
// app back to the front. Nothing runs while the app is in the background. Pass
// stop = true to end the checks (the leaderboard link stops once its recap is found).
const RECAP_REFRESH_MS = 60000;
const useRecapRefresh = (load, enabled, stop = false) => {
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!enabled || stop) return undefined;
    const check = () => { if (document.visibilityState === 'visible') loadRef.current(); };
    const timer = setInterval(check, RECAP_REFRESH_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, [enabled, stop]);
};

// Top-of-portal card for the group's newest recap — every player in the org sees it,
// played or not, until the next round's recap replaces it (Oct 7, 2026). When the
// player's own latest round had no live scores (and is newer), a slim card says so.
// Oct 29, 2026: re-checked while the app is open (useRecapRefresh), so a new recap
// appears without closing the app.
const PlayerRecapCard = ({ token, onOpen }) => {
  const [recap, setRecap] = useState(null);
  const [empty, setEmpty] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const loadLatest = async () => {
    try {
      const resp = await fetch(`${API_URL}/player/recap/latest?token=${encodeURIComponent(token)}`);
      if (!resp.ok) return;
      const data = await resp.json();
      if (mountedRef.current) { setRecap(data.recap || null); setEmpty(data.empty || null); }
    } catch (e) { /* no card */ }
  };
  useEffect(() => {
    if (!token) return;
    loadLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useRecapRefresh(loadLatest, !!token);

  if (!recap && empty) {
    return (
      <div style={{ background: '#fff', border: '1px dashed #C9D3CC', borderRadius: 14, padding: '14px 18px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: RECAP_GREEN }}>ROUND RECAP</span>
          <span style={{ fontSize: 12, color: '#5B6660', marginLeft: 'auto' }}>{empty.date}{empty.course ? ` \u00b7 ${empty.course}` : ''}</span>
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: RECAP_INK, marginBottom: 4 }}>No rounds recorded via live scoring</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: '#33413A' }}>When your group keeps score on their phones, the day's highlights show up here.</div>
      </div>
    );
  }
  if (!recap) return null;
  const you = recap.you;
  const first = (recap.paragraphs || [])[0] || '';
  const teaser = first.length > 120 ? first.slice(0, first.lastIndexOf(' ', 117)) + '\u2026' : first;
  let youLine = null;
  const tm = recap.team;   // event recap (Oct 4, 2026)
  if (tm && tm.scored === false) youLine = <><strong>{tm.team ? 'Your team' : 'You'}:</strong> card not entered</>;
  else if (tm) youLine = <><strong>{tm.team ? 'Your team' : 'You'}:</strong> {tm.place || '\u2014'}{tm.score != null ? ` \u00b7 ${tm.basis === 'net' ? 'net ' : ''}${tm.score}` : ''}{recap.games ? ` \u00b7 ${fmtRecapMoney(recap.games.you)}` : ''}</>;
  else if (you && you.scored === false) youLine = <><strong>You:</strong> not scored in the app</>;
  else if (you) youLine = <><strong>You:</strong> {you.gross}{you.net != null ? ` \u00b7 net ${you.net}` : ''}{recap.games ? ` \u00b7 ${fmtRecapMoney(recap.games.you)}` : ''}</>;
  return (
    <button onClick={() => onOpen(recap)}
      style={{ display: 'block', width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #E2E8E4', borderRadius: 14, padding: 18, marginBottom: 16, cursor: 'pointer', boxShadow: '0 1px 2px rgba(20,40,30,.08)', font: 'inherit', color: '#1B1F1C' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: RECAP_GREEN }}>{recap.event ? 'EVENT RESULTS' : 'ROUND RECAP'}</span>
        <span style={{ fontSize: 12, color: '#5B6660', marginLeft: 'auto' }}>{recap.date}{recap.course ? ` \u00b7 ${recap.course}` : ''}</span>
      </div>
      <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 21, lineHeight: 1.2, fontWeight: 700, color: RECAP_INK, marginBottom: 8 }}>{recap.headline}</div>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: '#33413A', marginBottom: 10 }}>{teaser}</div>
      <div style={{ borderTop: '1px solid #E2E8E4', paddingTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14 }}>{youLine}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: RECAP_GREEN }}>{recap.event ? 'See results' : 'Read recap'} &rsaquo;</div>
      </div>
    </button>
  );
};

// Profile setting: whether the group's round recap also comes by email. Oct 7, 2026
// (Dennis): every player sees the latest recap in the app regardless; this switch is
// only about email, and it is off unless the player turns it on. Three choices:
// no email (the default), rounds I play, or every group round.
const RecapFollowToggle = ({ token }) => {
  const [st, setSt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/player/recap/email-pref?token=${encodeURIComponent(token)}`);
        if (resp.ok) setSt(await resp.json());
      } catch (e) { /* hidden */ }
    })();
  }, [token]);
  if (!st || !st.available) return null;
  const choose = async (pref) => {
    setSaving(true); setSaved(false);
    try {
      const resp = await fetch(`${API_URL}/player/recap/email-pref?token=${encodeURIComponent(token)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pref }),
      });
      if (resp.ok) { setSt({ ...st, pref }); setSaved(true); }
    } catch (e) { /* unchanged */ }
    finally { setSaving(false); }
  };
  const value = ['none', 'mine', 'all'].includes(st.pref) ? st.pref : 'none';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 0', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 220px' }}>
        <span style={{ fontSize: 20 }}>📰</span>
        <div>
          <label htmlFor="recap-email-pref" style={{ fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Round recaps by email</label>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{saved ? 'Saved.' : "The latest recap is always on your app's home screen. Saves right away."}</div>
        </div>
      </div>
      <select id="recap-email-pref" value={value} disabled={saving} onChange={(e) => choose(e.target.value)}
        style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, background: '#fff', minHeight: 40 }}>
        <option value="none">No email</option>
        <option value="mine">Email rounds I play</option>
        <option value="all">Email every group round</option>
      </select>
    </div>
  );
};

// Leaderboard header link to the round's recap, when one is published.
// Oct 29, 2026: keeps checking while the leaderboard is open, so the link appears the
// moment the recap is posted (at finalize); stops checking once it has one.
const RecapTeaser = ({ token, teetimeId }) => {
  const [recap, setRecap] = useState(null);
  const [open, setOpen] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const loadRecap = async () => {
    try {
      const resp = await fetch(`${API_URL}/player/recap/${teetimeId}?token=${encodeURIComponent(token)}`);
      if (!resp.ok) return;
      const data = await resp.json();
      if (mountedRef.current) setRecap(data.recap || null);
    } catch (e) { /* no link */ }
  };
  useEffect(() => {
    if (!token || !teetimeId) return;
    setRecap(null);
    loadRecap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, teetimeId]);
  useRecapRefresh(loadRecap, !!(token && teetimeId), !!recap);
  if (!recap) return null;
  return (
    <>
      <button onClick={() => setOpen(true)}
        style={{ marginTop: 10, width: '100%', minHeight: 44, padding: '8px 12px', background: '#E3EFE7', color: RECAP_GREEN, border: '1px solid #BFD8C8', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, textAlign: 'left' }}>
        Round recap: {recap.headline} &rsaquo;
      </button>
      {open && <RoundRecapModal token={token} teetimeId={teetimeId} initialRecap={recap} onClose={() => setOpen(false)} />}
    </>
  );
};

const LeaderboardModal = ({ token, teetimeId, viewerId, onClose }) => {
  const [board, setBoard] = useState(null);
  const [boardLoading, setBoardLoading] = useState(true);
  const [boardError, setBoardError] = useState(null);
  const [standings, setStandings] = useState(null);
  const [subTab, setSubTab] = useState('scoring');      // 'scoring' | 'games' — lands on scoring (leaderboard)
  const [view, setView] = useState('gross');            // 'gross' | 'net'
  const [expandedGames, setExpandedGames] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);  // last auto/manual refresh time
  const [openPlayerId, setOpenPlayerId] = useState(null); // scorecard drill-down
  const [nowTick, setNowTick] = useState(Date.now());     // staleness clock (§6.5)
  useEffect(() => { const id = setInterval(() => setNowTick(Date.now()), 15000); return () => clearInterval(id); }, []);
  // A net team event opens on the Net view — that is the basis its places are ranked on.
  // So does a board ranked on a stroke play game: net game → Net, gross → Gross (Sept 23, 2026).
  const teamBasis = board && board.scoring_mode === 'team' ? (board.basis || 'gross') : null;
  const gameBasis = board && boardGame(board) ? (boardOpensNet(board) ? 'net' : 'gross') : null;
  useEffect(() => { if (teamBasis === 'net') setView('net'); else if (gameBasis) setView(gameBasis); }, [teamBasis, gameBasis]);

  // vs-par chip helper (matches the in-tab board: under par green, over par grey)
  const fmtVs = (v) => v == null ? '—' : (v === 0 ? 'E' : (v > 0 ? `+${v}` : `${v}`));

  const loadBoard = async () => {
    setBoardLoading(true);
    setBoardError(null);
    try {
      const resp = await fetch(`${API_URL}/player/scoring/leaderboard?token=${token}&teetime_id=${teetimeId}`);
      if (resp.ok) { setBoard(await resp.json()); setLastUpdated(Date.now()); }
      else {
        const err = await resp.json().catch(() => ({}));
        setBoardError(err.error || `Server error (${resp.status})`);
      }
    } catch (e) {
      setBoardError('Network error — check your connection');
    } finally { setBoardLoading(false); }
  };

  // Standings drives BOTH the participant check (whether the Games tab appears)
  // and the Games tab content. Failure is non-fatal — no Games tab, board still shows.
  const loadStandings = async () => {
    try {
      const resp = await fetch(`${API_URL}/player/scoring/standings?token=${token}&teetime_id=${teetimeId}`);
      if (resp.ok) setStandings(await resp.json());
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => {
    loadBoard();
    loadStandings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teetimeId]);

  // Auto-refresh the board every ~20s while the modal is open, mirroring the
  // follow-along self-view's proven pattern. The fetch is INLINED here (not a call
  // to loadBoard) so each tick uses a fresh closure over the current token/teetime
  // — same stale-closure lesson as the self-view poll. Only updates the board data;
  // the viewer's sub-tab and Gross/Net toggle are untouched. The manual Refresh
  // button remains for impatience or a failed poll. ~20s lag is expected (polling,
  // no push) — fine for "follow along."
  useEffect(() => {
    if (!teetimeId || !token) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const resp = await fetch(`${API_URL}/player/scoring/leaderboard?token=${token}&teetime_id=${teetimeId}`);
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (cancelled) return;
        setBoard(data);
        setLastUpdated(Date.now());
      } catch (e) { /* keep last-known board */ }
    };
    const interval = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teetimeId, token]);

  // Viewer is a game participant iff their id appears in any game's totals.
  // Team games (scramble) are NOT shown here — the team board on the Scores tab is
  // their result, and their totals are team rows, not player rows.
  const moneyGames = (standings?.games || []).filter(g => g.competitor_scope !== 'team');
  const viewerInAGame = moneyGames.some(g =>
    // Best Ball rows are teams (Sept 23, 2026): the viewer is in it through a team's members.
    (g.results?.totals || []).some(t => t.player_id === viewerId || (t.player_id == null && (t.members || []).some(m => m.player_id === viewerId)))
  );
  const showGamesTab = viewerInAGame;

  const isTeamBoard = !!(board && board.scoring_mode === 'team');
  const showNetToggle = !isTeamBoard || (board && board.has_net === true);   // Net suppressed for a gross team event (§6.4)
  const isNet = view === 'net' && showNetToggle;
  const footer = boardFooter(board, lastUpdated, nowTick);
  const SECTION_LABEL = { live: 'Live Ranking', not_current: 'Scoring Not Current', no_scores: 'No Scores Recorded' };

  // One shared order for every board (sortBoardRows): the game's finishing order on a
  // view it ranks, the plain key otherwise; sections preserved.
  const displayRows = sortBoardRows(board?.rows, isNet, board);
  const rankedView = !isTeamBoard && boardRankedOn(board, isNet);
  const modalTieNotes = rankedView ? tieFootnotes(board, displayRows, isNet ? 'net' : 'gross') : [];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ width: '100%', maxWidth: 520, maxHeight: '88vh', background: '#fff', borderRadius: 16,
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: '#1f2937' }}>🏆 Leaderboard</div>
              {board && board.event_name && <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', marginTop: 2 }}>📅 {board.event_name}</div>}
              {board && (
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  {board.course_name}
                  {board.through > 0 ? ` · Through ${board.through}` : ''}{holeSetSuffix(board)}
                  {boardCountText(board)}
                </div>
              )}
            </div>
            <button onClick={onClose}
              style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8,
                cursor: 'pointer', fontSize: 14, fontWeight: 600, flexShrink: 0, marginLeft: 12 }}>
              Close
            </button>
          </div>

          {/* Round Recap (Oct 3, 2026): link to the round's recap once it is posted */}
          <RecapTeaser token={token} teetimeId={teetimeId} />

          {/* Sub-tabs: Games shown ONLY to participants */}
          {showGamesTab && (
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', marginTop: 12 }}>
              <button onClick={() => setSubTab('scoring')}
                style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                  background: subTab === 'scoring' ? '#166534' : '#f9fafb', color: subTab === 'scoring' ? '#fff' : '#6b7280' }}>
                🏆 Scores
              </button>
              <button onClick={() => setSubTab('games')}
                style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                  borderLeft: '1px solid #e5e7eb',
                  background: subTab === 'games' ? '#166534' : '#f9fafb', color: subTab === 'games' ? '#fff' : '#6b7280' }}>
                💰 Games
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, minHeight: 0 }}>
          {/* SCORING (leaderboard) */}
          {subTab === 'scoring' && (
            boardLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 16 }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>Loading leaderboard...
              </div>
            ) : boardError ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
                <div style={{ color: '#dc2626', fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Unable to load leaderboard</div>
                <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>{boardError}</div>
                <button onClick={loadBoard}
                  style={{ padding: '10px 24px', background: '#166534', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  🔄 Retry
                </button>
              </div>
            ) : !board || !board.rows || board.rows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 15 }}>
                {isTeamBoard ? "No teams yet — the organizer hasn't saved the pairings." : 'No players to show yet.'}
              </div>
            ) : (
              <div>
                {/* Refresh + Gross/Net toggle */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <button onClick={loadBoard}
                    style={{ padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    🔄 Refresh
                  </button>
                </div>
                {showNetToggle && (
                <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: 12 }}>
                  <button onClick={() => setView('gross')}
                    style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                      background: !isNet ? '#1f2937' : '#f9fafb', color: !isNet ? '#fff' : '#6b7280' }}>
                    Gross
                  </button>
                  <button onClick={() => setView('net')}
                    style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                      borderLeft: '1px solid #e5e7eb',
                      background: isNet ? '#1f2937' : '#f9fafb', color: isNet ? '#fff' : '#6b7280' }}>
                    Net
                  </button>
                </div>
                )}

                {isTeamBoard ? (
                  <TeamBoardRows board={board} viewerId={viewerId} onOpenTeam={setOpenPlayerId} isNet={isNet} />
                ) : (() => {
                  let lastSection = null;
                  return displayRows.map((r) => {
                    const isMe = viewerId && r.player_id === viewerId;
                    const total = isNet ? r.net_total : r.gross_total;
                    const vs = isNet ? r.net_vs_par : r.gross_vs_par;
                    const vsColor = vs == null ? '#9ca3af' : (vs < 0 ? '#166534' : vs > 0 ? '#9ca3af' : '#6b7280');
                    const thruColor = r.entry_flag === 'red' ? '#dc2626' : r.entry_flag === 'amber' ? '#f59e0b' : '#6b7280';
                    const showHeader = r.section !== lastSection;
                    lastSection = r.section;
                    const headerText = boardSectionHeader(SECTION_LABEL[r.section], r.section, isNet, board);
                    return (
                      <React.Fragment key={r.player_id}>
                        {showHeader && (
                          <div style={{ margin: '14px 2px 6px' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                              color: r.section === 'not_current' ? '#dc2626' : '#9ca3af' }}>
                              {headerText}
                            </div>
                            {r.section === 'no_scores' && (
                              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginTop: 2, lineHeight: 1.4 }}>
                                These players either haven't started their round, or aren't recording scores live.
                              </div>
                            )}
                          </div>
                        )}
                        <div
                          onClick={r.has_scores ? () => setOpenPlayerId(r.player_id) : undefined}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 12px', borderRadius: 10, marginBottom: 4,
                          background: isMe ? '#f0fdf4' : '#fff', border: isMe ? '1px solid #bbf7d0' : '1px solid #f3f4f6',
                          cursor: r.has_scores ? 'pointer' : 'default' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            {rankedView && r.section === 'live' && (() => { const pl = boardPlace(r, isNet); return (
                              <div style={{ minWidth: 30, textAlign: 'center', fontSize: 15, fontWeight: 800, color: pl.label ? '#1f2937' : '#d1d5db', flexShrink: 0 }}>
                                {pl.label || '–'}<TieMark mark={pl.mark} />
                              </div>
                            ); })()}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: isMe ? 700 : 600, fontSize: 14, color: '#1f2937',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {r.name}{isMe ? ' (you)' : ''}
                              </div>
                              <div style={{ fontSize: 11, color: thruColor, marginTop: 1, fontWeight: r.entry_flag !== 'none' ? 700 : 400 }}>
                                {r.has_scores ? `Thru ${r.thru}` : 'No scores'}
                                {boardHcpText(r, board) ? ` · ${boardHcpText(r, board)}` : ''}
                                {rankedView && boardPlace(r, isNet).status === 'pending' && <span style={{ color: '#b45309', fontWeight: 700 }}> · Playoff pending</span>}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                              <span style={{ fontWeight: 800, fontSize: 22, color: '#1f2937', minWidth: 30, textAlign: 'right' }}>
                                {total != null ? total : '—'}
                              </span>
                              <span style={{ fontSize: 13, fontWeight: 600, color: vsColor, minWidth: 30, textAlign: 'right' }}>
                                {fmtVs(vs)}
                              </span>
                            </div>
                            {otherBasisText(r, isNet) && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1, whiteSpace: 'nowrap' }}>{otherBasisText(r, isNet)}</div>}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}

                <CtpBoardSection ctp={board.ctp} nines={board.course_nines || null} />

                {modalTieNotes.length > 0 && (
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, lineHeight: 1.5 }}>
                    {modalTieNotes.map(t => <div key={t}>{t}</div>)}
                  </div>
                )}

                <div style={{ textAlign: 'center', fontSize: 11, color: footer.stale ? '#b45309' : '#9ca3af', fontWeight: footer.stale ? 700 : 400, marginTop: 12 }}>
                  {footer.text}
                </div>

                {openPlayerId && (
                  <PlayerScorecardModal
                    player={(board.rows || []).find(r => r.player_id === openPlayerId)}
                    board={board}
                    hideNet={isTeamBoard && board.has_net !== true}
                    onClose={() => setOpenPlayerId(null)}
                  />
                )}
              </div>
            )
          )}

          {/* GAMES (participants only) */}
          {subTab === 'games' && (
            moneyGames.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 15 }}>
                No games found for this round.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {moneyGames.map((game) => {
                  const isSkins = game.game_type?.includes('skins');
                  const isPutts = game.game_type?.includes('putts');
                  const isStroke = game.game_type === 'stroke_play' || isBestBallGameType(game.game_type);   // Best Ball / Shamble rows are teams (Sept 23 / 28, 2026)
                  const isFinalized = game.status === 'completed';
                  const isCtpGame = game.game_type === 'ctp';   // closest to the pin (Sept 28, 2026)
                  const isDotsGame = game.game_type === 'dots';   // Oct 4, 2026
                  const isNassauGame = game.game_type === 'nassau';   // Oct 4, 2026
                  const isSixesGame = game.game_type === 'sixes';     // Oct 5, 2026
                  const isStablefordGame = game.game_type === 'stableford';   // Oct 5, 2026
                  const statLabel = isSkins ? 'Total Skins' : isPutts ? 'Total Putts' : isCtpGame ? 'Closest to the pin' : isDotsGame ? 'Dots' : isNassauGame ? 'Bets won–lost' : (isSixesGame || game.game_type === 'match_play') ? 'Matches won–lost–halved' : isStablefordGame ? 'Points' : 'Result';
                  const statKey = isSkins ? 'skins_won' : isPutts ? 'total_putts' : isCtpGame ? 'ctp_won' : isDotsGame ? 'total_dots' : (isNassauGame || isSixesGame || game.game_type === 'match_play') ? 'record' : isStablefordGame ? 'points' : 'net_score';
                  const holesThru = game.results?.holes_completed || 0;
                  const holeTotal = game.results?.hole_count || 18;
                  return (
                    <div key={game.game_id} style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
                      <div style={{ background: '#1f2937', color: '#fff', padding: '10px 14px' }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{game.display_name || game.game_type}</div>
                        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                          {isStroke && game.results ? `${game.results.competition === 'both' ? 'Net & gross' : game.results.competition === 'gross' ? 'Gross' : `Net · ${game.results.allowance_pct}% hcp`}${game.results.is_complete ? ' · Final' : ''}` : ''}
                          {!isStroke && (holesThru > 0 && holesThru < holeTotal ? `Through Hole ${holesThru}` : holesThru === holeTotal ? 'Final' : '')}
                        </div>
                      </div>
                      {gameErrorBox(game)}
                      <div>
                        <SegmentSwitch game={game}>{(game) => { const players = game.results?.totals || []; return players.map((player, idx) => {
                          const isTeamRow = player.player_id == null && player.side_id != null;
                          const isMe = isTeamRow ? (player.members || []).some(m => m.player_id === viewerId) : player.player_id === viewerId;
                          const sc = isStroke ? strokePlayScore(game, player) : null;
                          const dg = (isDotsGame || (isStablefordGame && (game.results?.team_mode === 'teams' || game.results?.mode === 'group'))) ? dotsGroupInfo(players, idx) : null;   // Oct 6, 2026: Dots groups / Stableford teams
                          return (
                            <React.Fragment key={isTeamRow ? `side-${player.side_id}` : player.player_id}>
                            {dg && dg.startsGroup && dotsGroupHeading(dg.name)}
                            <div key={isTeamRow ? `side-${player.side_id}` : player.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '10px 14px', borderBottom: idx < players.length - 1 ? '1px solid #f3f4f6' : 'none',
                              background: isMe ? '#f0fdf4' : 'transparent' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ minWidth: 24, height: 24, padding: '0 4px', borderRadius: 12, background: (isStablefordGame ? player.place === 1 : dg ? dg.place === 1 : idx === 0) ? '#f59e0b' : '#e5e7eb',
                                  color: (isStablefordGame ? player.place === 1 : dg ? dg.place === 1 : idx === 0) ? '#fff' : '#6b7280', fontSize: 12, fontWeight: 700,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{isStroke ? strokePlayPlace(game, player) : isStablefordGame ? (player.place_label || '—') : dg ? dg.place : idx + 1}</span>
                                <div>
                                  <div style={{ fontWeight: isMe ? 700 : 600, fontSize: 14, color: '#1f2937' }}>
                                    {player.player_name}{isMe ? ' (you)' : ''}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#6b7280' }}>{isStroke ? strokePlayStat(game, player, false) : <>{statLabel}: {player[statKey] ?? '—'}{isDotsGame && player.breakdown ? ` (${player.breakdown})` : ''}</>}</div>
                                </div>
                              </div>
                              {sc && (
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 6 }}>
                                    <span style={{ fontWeight: 800, fontSize: 22, color: '#1f2937', lineHeight: 1.1 }}>{sc.total}</span>
                                    {sc.vs && <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{sc.vs}</span>}
                                  </div>
                                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{sc.comp}{sc.thru ? ` · ${sc.thru}` : ''}</div>
                                </div>
                              )}
                            </div>
                            </React.Fragment>
                          );
                        }); }}</SegmentSwitch>
                        {isNassauGame && <NassauMatches game={game} viewerId={viewerId} />}
                        {game.game_type === 'match_play' && <MatchPlayMatches game={game} viewerId={viewerId} />}
                        {isSixesGame && <SixesMatches game={game} viewerId={viewerId} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const PlayerPortal = ({ token, tokenSource }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playerData, setPlayerData] = useState(null);
  const [teeTimes, setTeeTimes] = useState([]);
  const [myTeeTimes, setMyTeeTimes] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  // Group RSVP — captain-side state (§6.3 modal + §6.4 nested list)
  // captainNominations: { [teetimeId]: [{ ttp_id, player_id, player_name, player_email, status, nomination_expires_at, nominated_at }, ...] }
  const [captainNominations, setCaptainNominations] = useState({});
  const [addPartnersTeetime, setAddPartnersTeetime] = useState(null); // tt object when modal open, null when closed
  const [eligibleNominees, setEligibleNominees] = useState([]);        // [{ id, name, email }] for modal dropdown
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [selectedNominees, setSelectedNominees] = useState([]);         // [''] initial row; array of nominee ids (or '' for empty)
  const [nominateSubmitting, setNominateSubmitting] = useState(false);
  const [nominateError, setNominateError] = useState(null);
  const [nominateSuccess, setNominateSuccess] = useState(null);         // { names: [...] } when invites sent successfully
  const [viewingTeeTime, setViewingTeeTime] = useState(null);
  // Live Leaderboard: teetime_id of the tee time whose leaderboard modal is open (null = closed).
  const [leaderboardTeetimeId, setLeaderboardTeetimeId] = useState(null);
  // Round Recap (Oct 3, 2026): { teetimeId, recap? } while the recap is open
  const [recapOpen, setRecapOpen] = useState(null);
  // My team (Oct 26, 2026 — captainTeamService): teetime_id of the team window a captain has open.
  const [myTeamTeetime, setMyTeamTeetime] = useState(null);
  const teamAutoOpened = useRef(false);
  // Player What's New: modal open state + unread count for the bell's red dot.
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [whatsNewItems, setWhatsNewItems] = useState([]);
  const [whatsNewUnread, setWhatsNewUnread] = useState(0);
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const [confirmRSVPId, setConfirmRSVPId] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [viewingStats, setViewingStats] = useState(false);
  const [viewingScoringStats, setViewingScoringStats] = useState(false);
  const [statsFilter, setStatsFilter] = useState("all");
  const [viewingPhoto, setViewingPhoto] = useState(null); // Photo viewer state
  // Phase 4: Multi-Organization state
  const [linkedOrgs, setLinkedOrgs] = useState([]);           // Linked organizations list
  const [showLinkOrgModal, setShowLinkOrgModal] = useState(false);  // Modal visibility
  const [linkOrgId, setLinkOrgId] = useState('');             // Organization ID input
  const [linkingOrg, setLinkingOrg] = useState(false);        // Loading state for linking
  const [unlinkingOrgId, setUnlinkingOrgId] = useState(null); // Confirmation for unlinking
  // Combine Your Groups (Oct 19, 2026): other player records with this player's email,
  // in groups not yet combined into this app. [{ player_id, player_name, organization_name, declined }]
  const [linkCandidates, setLinkCandidates] = useState([]);
  const [linkAnswering, setLinkAnswering] = useState(false);
  const [linkPromptOff, setLinkPromptOff] = useState(false); // hides the prompt for this visit if saving fails
  // Dashboard access state
  const [dashboardAccess, setDashboardAccess] = useState({ isPlatformAdmin: false, isOrganizer: false });
  // Advertising state
  const [inFeedAds, setInFeedAds] = useState([]);
  const [showRSVPSuccessModal, setShowRSVPSuccessModal] = useState(false);
  const [lastRSVPTeeTime, setLastRSVPTeeTime] = useState(null);
  const [confirmationAds, setConfirmationAds] = useState([]);
  // Available tees for preferred tee selection
  const [availableTees, setAvailableTees] = useState([]);
  // Inline "set your tee" quick-fix (opened from the View Details pairings list when a
  // player's preferred tee couldn't be matched to the course being played). Holds the
  // tee time we came from so we can restore the View Details modal after saving.
  const [teeFixContext, setTeeFixContext] = useState(null); // { courseName, returnTeeTime } | null
  const [teeFixSelectedId, setTeeFixSelectedId] = useState('');
  const [teeFixSaving, setTeeFixSaving] = useState(false);
  // GHIN Sync state
  const [showGhinSyncModal, setShowGhinSyncModal] = useState(false);
  const [ghinSyncForm, setGhinSyncForm] = useState({ email: '', password: '', saveCredentials: false, smartReminder: true });
  const [ghinSyncing, setGhinSyncing] = useState(false);
  const [ghinStatus, setGhinStatus] = useState(null);
  const [ghinSyncResult, setGhinSyncResult] = useState(null); // Success result to display
  // Share modal state (Referral feature)
  const [showShareModal, setShowShareModal] = useState(false);
  // Tee time overlap conflict modal state
  const [showOverlapModal, setShowOverlapModal] = useState(false);
  const [overlapConflict, setOverlapConflict] = useState(null);
  const [showScoring, setShowScoring] = useState(null); // null=off, "live"=live scoring
  const [showMyRounds, setShowMyRounds] = useState(false); // My Rounds view
  const [personalRounds, setPersonalRounds] = useState([]); // Personal rounds for unified view
  const [scoringPersonalRound, setScoringPersonalRound] = useState(null); // Personal round being scored
  const [portalScoringEnabled, setPortalScoringEnabled] = useState(false);
  // Phase 2 Scoring gate (May 16, 2026): commercial tier entitlement flag returned by
  // /player/scoring/status. Separate from portalScoringEnabled (admin operational flag)
  // so the player portal can render Show + Lock + Educate (Rule 12) when admin enabled
  // but tier doesn't qualify, rather than hide the affordance entirely.
  const [portalScoringTierAllowed, setPortalScoringTierAllowed] = useState(false);
  const [portalOrgName, setPortalOrgName] = useState('');
  const [portalGroupId, setPortalGroupId] = useState(null);
  const [portalGamesEnabled, setPortalGamesEnabled] = useState(false);
  const [teeTimeGames, setTeeTimeGames] = useState({}); // { [teetime_id]: games[] }
  const [showGamesPanel, setShowGamesPanel] = useState(null); // teetime_id of open panel
  const [standingsData, setStandingsData] = useState(null);
  const [settlementData, setSettlementData] = useState(null);
  const [gamesView, setGamesView] = useState("optin"); // optin|standings|settlement
  const [gamesLoading, setGamesLoading] = useState(false);
  const [portalRulesGame, setPortalRulesGame] = useState(null); // game to show rules popup for
  const [autoFinalizeState, setAutoFinalizeState] = useState({}); // { [teetime_id]: { auto_finalize, ready, participant_count, is_finalized } }
  const [portalFinalizing, setPortalFinalizing] = useState({}); // { [teetime_id]: bool }
  const detailViewRef = useRef(null);
  const { addToast } = useToast();

  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        setLoading(true);
        
        // Fetch player profile
        const profileRes = await fetch(`${API_URL}/player/profile?token=${token}`);
        
        if (!profileRes.ok) {
          throw new Error('Invalid or expired token');
        }
        
        const profileData = await profileRes.json();
        setPlayerData(profileData);  // Backend returns player object directly, not wrapped in {data: ...}
        
        // Fetch available tee times
        const teeTimesRes = await fetch(`${API_URL}/player/teetimes?token=${token}`);
        
        if (teeTimesRes.ok) {
          const teeTimesData = await teeTimesRes.json();
          console.log('🔍 Available tee times data:', teeTimesData);
          setTeeTimes(teeTimesData.data || []);
        }
        
        // Fetch player's tee times
        const myTeeTimesRes = await fetch(`${API_URL}/player/my-teetimes?token=${token}`);
        let ttListForNoms = [];
        if (myTeeTimesRes.ok) {
          const myTeeTimesData = await myTeeTimesRes.json();
          const ttList = myTeeTimesData.data || [];
          ttListForNoms = ttList;
          setMyTeeTimes(ttList);
          // Load games for all confirmed tee times inline
          if (ttList.length > 0) loadAllTeeTimeGames(ttList);
        }

        // Fetch pending nominations (invitations where this player is the nominee)
        try {
          const pendingRes = await fetch(`${API_URL}/player/my-pending-nominations?token=${token}`);
          if (pendingRes.ok) {
            const pendingData = await pendingRes.json();
            setPendingInvitations(pendingData.pending || []);
          }
        } catch (e) { /* non-critical — pending invitations feature may be off */ }

        // Fetch captain-side nominations per upcoming tee time (§6.4 Pending Partners list).
        // Fires one GET /nominations/:teetimeId per tee time the player is confirmed in.
        // Skips waitlisted rows; endpoint is cheap (single SELECT). Fails soft — feature may be off.
        try {
          const confirmedTeeTimes = ttListForNoms.filter(tt => tt.status !== 'waitlist');
          const noms = {};
          await Promise.all(confirmedTeeTimes.map(async (tt) => {
            try {
              const r = await fetch(`${API_URL}/player/nominations/${tt.id}?token=${token}`);
              if (r.ok) {
                const d = await r.json();
                if (Array.isArray(d.nominations) && d.nominations.length > 0) {
                  noms[tt.id] = d.nominations;
                }
              }
            } catch (_) { /* per-tee soft-fail */ }
          }));
          setCaptainNominations(noms);
        } catch (e) { /* non-critical */ }
        
        // Check if scoring is enabled for this player's org
        try {
          const scoringResp = await fetch(`${API_URL}/player/scoring/status?token=${token}`);
          if (scoringResp.ok) {
            const scoringData = await scoringResp.json();
            setPortalScoringEnabled(scoringData.scoring_enabled || false);
            setPortalScoringTierAllowed(scoringData.scoring_tier_allowed || false);
            setPortalGamesEnabled(scoringData.games_enabled || false);
            if (scoringData.org_name) setPortalOrgName(scoringData.org_name);
            if (scoringData.group_id) setPortalGroupId(scoringData.group_id);
            // Fetch personal rounds if scoring is enabled and the plan includes it
            if (scoringData.scoring_enabled && scoringData.scoring_tier_allowed) {
              try {
                const prResp = await fetch(`${API_URL}/player/personal-rounds?token=${token}`);
                if (prResp.ok) {
                  const prData = await prResp.json();
                  setPersonalRounds(prData.rounds || []);
                }
              } catch (e) { /* personal rounds not available */ }
            }
          }
        } catch (e) { /* scoring not available */ }

        // Phase 4: Fetch linked organizations
        const linkedOrgsRes = await fetch(`${API_URL}/player/linked-organizations?token=${token}`);
        if (linkedOrgsRes.ok) {
          const linkedOrgsData = await linkedOrgsRes.json();
          setLinkedOrgs(linkedOrgsData.linkedOrganizations || []);
        }

        // Combine Your Groups (Oct 19, 2026): asked on every open, not just the first
        try {
          const sugRes = await fetch(`${API_URL}/player/link-suggestions?token=${token}`);
          if (sugRes.ok) {
            const sugData = await sugRes.json();
            setLinkCandidates(sugData.candidates || []);
          }
        } catch (e) { /* non-critical */ }
        
        // Check dashboard access (admin/organizer)
        const dashboardAccessRes = await fetch(`${API_URL}/player/dashboard-access?token=${token}`);
        if (dashboardAccessRes.ok) {
          const accessData = await dashboardAccessRes.json();
          setDashboardAccess(accessData);
        }
        
        // Fetch ads for player portal
        try {
          const orgId = profileData?.group_id || '';
          const cacheBuster = Date.now();
          const [inFeedRes, confirmRes] = await Promise.all([
            fetch(`${API_URL}/ads/active?placement=in_feed&organizationId=${orgId}&limit=5&_t=${cacheBuster}`, { cache: 'no-store' }),
            fetch(`${API_URL}/ads/active?placement=confirmation&organizationId=${orgId}&limit=1&_t=${cacheBuster}`, { cache: 'no-store' })
          ]);
          
          if (inFeedRes.ok) {
            const inFeedData = await inFeedRes.json();
            console.log('In-feed ads loaded:', inFeedData.ads?.length, inFeedData.meta);
            setInFeedAds(inFeedData.ads || []);
          }
          
          if (confirmRes.ok) {
            const confirmData = await confirmRes.json();
            console.log('Confirmation ads loaded:', confirmData.ads?.length, confirmData.meta);
            setConfirmationAds(confirmData.ads || []);
          }
        } catch (adErr) {
          console.log('Ads not loaded:', adErr.message);
          // Non-critical - don't show error to user
        }
        
        // Fetch available tees for preferred tee selection
        try {
          const teesRes = await fetch(`${API_URL}/player/tees?token=${token}`);
          if (teesRes.ok) {
            const teesData = await teesRes.json();
            setAvailableTees(teesData || []);
          }
        } catch (teeErr) {
          console.log('Tees not loaded:', teeErr.message);
          // Non-critical - don't show error to user
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching player data:', err);
        // Graceful fallback: if this token came from localStorage (not a fresh
        // URL/email link) and the backend rejected it, the stored token is
        // stale. Rather than showing the scary "Access Error" to someone who
        // just opened the app/pinned icon, clear the dead token and send them
        // to the landing page. A token that came from the URL (a real emailed
        // link) still shows the honest error so the player knows to get a new
        // link from their organizer.
        if (tokenSource === 'localStorage') {
          console.log('🧹 Stored player token was rejected by backend — clearing and returning to landing page');
          localStorage.removeItem('playerToken');
          localStorage.removeItem('playerTokenTimestamp');
          window.location.href = '/';
          return;
        }
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchPlayerData();
    }
  }, [token]);

  // Auto-refresh player data every 10 seconds to catch organizer changes
  useEffect(() => {
    if (!token) return;

    const refreshData = async () => {
      try {
        // Fetch available tee times
        const teeTimesRes = await fetch(`${API_URL}/player/teetimes?token=${token}`);
        if (teeTimesRes.ok) {
          const teeTimesData = await teeTimesRes.json();
          setTeeTimes(teeTimesData.data || []);
        }
        
        // Fetch player's tee times
        // If stats modal is open, include past tee times
        const includePast = viewingStats ? '&include_past=true' : '';
        const myTeeTimesRes = await fetch(`${API_URL}/player/my-teetimes?token=${token}${includePast}`);
        let ttListForNomsRefresh = [];
        if (myTeeTimesRes.ok) {
          const myTeeTimesData = await myTeeTimesRes.json();
          const ttList = myTeeTimesData.data || [];
          ttListForNomsRefresh = ttList;
          setMyTeeTimes(ttList);
        }

        // Refresh pending nominations
        const pendingRes = await fetch(`${API_URL}/player/my-pending-nominations?token=${token}`);
        if (pendingRes.ok) {
          const pendingData = await pendingRes.json();
          setPendingInvitations(pendingData.pending || []);
        }

        // Refresh captain-side nominations per upcoming tee time (§6.4).
        // Same pattern as initial load; pending→confirmed transitions and
        // cron-driven expiries (deleted rows just disappear) reconcile here.
        try {
          const confirmedTeeTimes = ttListForNomsRefresh.filter(tt => tt.status !== 'waitlist');
          const noms = {};
          await Promise.all(confirmedTeeTimes.map(async (tt) => {
            try {
              const r = await fetch(`${API_URL}/player/nominations/${tt.id}?token=${token}`);
              if (r.ok) {
                const d = await r.json();
                if (Array.isArray(d.nominations) && d.nominations.length > 0) {
                  noms[tt.id] = d.nominations;
                }
              }
            } catch (_) { /* per-tee soft-fail */ }
          }));
          setCaptainNominations(noms);
        } catch (e) { /* non-critical */ }
      } catch (err) {
        console.error('Error refreshing player data:', err);
      }
    };
    
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing player portal data...');
      refreshData();
    }, 10000); // 10 seconds
    
    return () => clearInterval(interval);
  }, [token, viewingStats]);

  // Player What's New: fetch player-audience announcements and compute the unread
  // count for the bell's red dot. Seen-state is client-side (localStorage), keyed per
  // player token — mirrors the organizer dashboard's getUnreadCount pattern. A brand-new
  // player has no stored timestamp, so everything reads as unread (dot shows); opening
  // the modal stamps "now" and clears the dot. Future-dated entries are excluded
  // server-side, so the drip is date-driven.
  useEffect(() => {
    if (!token) return;
    const key = `playerWhatsNewLastRead_${token}`;
    fetch(`${API_URL}/content/player-whats-new`)
      .then(r => r.ok ? r.json() : [])
      .then(items => {
        setWhatsNewItems(items);
        const lastRead = localStorage.getItem(key);
        if (!lastRead) { setWhatsNewUnread(items.length); return; }
        const readDate = new Date(lastRead);
        setWhatsNewUnread(items.filter(it => new Date(it.published_at) > readDate).length);
      })
      .catch(() => {});
  }, [token]);

  // Opening the What's New modal: stamp "now" as last-read and clear the dot.
  const openWhatsNew = () => {
    if (token) localStorage.setItem(`playerWhatsNewLastRead_${token}`, new Date().toISOString());
    setWhatsNewUnread(0);
    setShowWhatsNew(true);
  };

  // Fetch past tee times when stats modal opens
  useEffect(() => {
    if (!viewingStats || !token) return;
    
    const fetchAllTeeTimes = async () => {
      try {
        console.log('📊 Fetching all tee times including past for stats...');
        const res = await fetch(`${API_URL}/player/my-teetimes?token=${token}&include_past=true`);
        if (res.ok) {
          const data = await res.json();
          console.log('📊 Received tee times with past:', data.data?.length);
          setMyTeeTimes(data.data || []);
        }
      } catch (err) {
        console.error('Error fetching all tee times:', err);
      }
    };
    
    fetchAllTeeTimes();
  }, [viewingStats, token]);

  // Round Recap (Oct 3, 2026): the recap email links here with ?recap=<teetime id>
  useEffect(() => {
    if (!playerData) return;
    const recapParam = new URLSearchParams(window.location.search).get('recap');
    if (recapParam && /^[0-9a-f-]{36}$/i.test(recapParam)) setRecapOpen({ teetimeId: recapParam });
  }, [playerData]);

  // A captain's link can open straight to My team (/portal?token=…&team=1, the Captain's
  // Welcome). Once his reservations load, open the first upcoming team he captains. Once only.
  useEffect(() => {
    if (teamAutoOpened.current || !Array.isArray(myTeeTimes) || myTeeTimes.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('team') !== '1') return;
    teamAutoOpened.current = true;
    const wanted = params.get('teetime_id');
    const tt = myTeeTimes.find(t => t.captain_team && (!wanted || t.id === wanted));
    if (tt) setMyTeamTeetime(tt.id);
  }, [myTeeTimes]);

  // Auto-open Edit Profile if URL has ?edit=profile parameter (for Twilio verification)
  useEffect(() => {
    if (!playerData || editingProfile) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const editParam = urlParams.get('edit');
    
    if (editParam === 'profile' || editParam === 'true') {
      // Auto-open the edit profile modal
      setEditForm({
        name: playerData?.name || '',
        email: playerData?.email || '',
        phone: playerData?.phone || '',
        ghin: playerData?.ghin || '',
        handicap: playerData?.handicap || playerData?.hcp || '',
        comm_email: playerData?.comm_email !== undefined ? playerData.comm_email : true,
        comm_text: playerData?.comm_text !== undefined ? playerData.comm_text : false,
        photoUrl: playerData?.photoUrl || '',
        preferred_tee: playerData?.preferred_tee || '',
        gender: playerData?.gender || '',
        notify_ghin_reminder: playerData?.notify_ghin_reminder !== false,
        show_target_score: playerData?.show_target_score !== false
      });
      setEditingProfile(true);
    }
  }, [playerData, editingProfile]);

  const startEditProfile = () => {
    setEditForm({
      name: playerData?.name || '',
      email: playerData?.email || '',
      phone: playerData?.phone || '',
      ghin: playerData?.ghin || '',
      handicap: playerData?.handicap || playerData?.hcp || '',
      comm_email: playerData?.comm_email !== undefined ? playerData.comm_email : true,
      comm_text: playerData?.comm_text !== undefined ? playerData.comm_text : false,
      photoUrl: playerData?.photoUrl || '',
      preferred_tee: playerData?.preferred_tee || '',
      gender: playerData?.gender || '',
      notify_ghin_reminder: playerData?.notify_ghin_reminder !== false,
      show_target_score: playerData?.show_target_score !== false
    });
    setEditingProfile(true);
  };

  const cancelEditProfile = () => {
    setEditingProfile(false);
    setEditForm({});
  };

  const saveProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/player/profile?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }
      
      // Update playerData with the returned data (includes photoUrl)
      setPlayerData({ ...playerData, ...data.data });
      setEditingProfile(false);
      addToast('Profile updated successfully!');
    } catch (err) {
      console.error('Error saving profile:', err);
      addToast(err.message, 'error');
    }
  };

  // Save the player's preferred tee from the inline "set your tee" quick-fix, then
  // refresh /my-teetimes so the View Details badge fills in, and restore that modal.
  // Builds a COMPLETE profile body from playerData (only preferred_tee changes) to
  // mirror the existing profile-save path exactly — never sends a partial body, so no
  // other field (name, handicap, comm prefs) can be inadvertently nulled by the server.
  const saveTeeFix = async () => {
    if (!teeFixSelectedId) return;
    setTeeFixSaving(true);
    try {
      const body = {
        name: playerData?.name || '',
        phone: playerData?.phone || '',
        ghin: playerData?.ghin || '',
        handicap: playerData?.handicap || playerData?.hcp || '',
        comm_email: playerData?.comm_email !== undefined ? playerData.comm_email : true,
        comm_text: playerData?.comm_text !== undefined ? playerData.comm_text : false,
        photoUrl: playerData?.photoUrl || '',
        preferred_tee: teeFixSelectedId,
        gender: playerData?.gender || undefined,
        notify_ghin_reminder: playerData?.notify_ghin_reminder !== false,
        show_target_score: playerData?.show_target_score !== false
      };
      const res = await fetch(`${API_URL}/player/profile?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save tee');

      setPlayerData(prev => ({ ...prev, ...data.data }));

      // Refresh reservations so the resolved tee + course HCP come back from the server.
      const returnId = teeFixContext?.returnTeeTime?.id;
      const includePast = viewingStats ? '&include_past=true' : '';
      try {
        const ttRes = await fetch(`${API_URL}/player/my-teetimes?token=${token}${includePast}`);
        if (ttRes.ok) {
          const ttData = await ttRes.json();
          const fresh = ttData.data || [];
          setMyTeeTimes(fresh);
          // Restore the View Details modal on the same tee time, now with fresh data.
          const updated = fresh.find(t => t.id === returnId);
          if (updated) setViewingTeeTime(updated);
        }
      } catch (e) {
        console.error('Tee fix: refresh failed (tee still saved):', e);
      }

      addToast('Preferred tee saved!', 'success');
      setTeeFixContext(null);
      setTeeFixSelectedId('');
    } catch (err) {
      console.error('Error saving preferred tee:', err);
      addToast(err.message, 'error');
    } finally {
      setTeeFixSaving(false);
    }
  };

  // GHIN Sync functions
  const fetchGhinStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/player/ghin/status?token=${token}`);
      if (res.ok) {
        const data = await res.json();
        setGhinStatus(data);
      }
    } catch (err) {
      console.error('Error fetching GHIN status:', err);
    }
  };

  // ── Games Module handlers ────────────────────────────────────────────────
  // Load games for all upcoming confirmed tee times at once
  const loadAllTeeTimeGames = async (teetimes) => {
    if (!teetimes || teetimes.length === 0) return;
    try {
      const results = await Promise.all(
        teetimes.map(tt =>
          fetch(`${API_URL}/player/scoring/games?token=${token}&teetime_id=${tt.id}`)
            .then(r => r.ok ? r.json() : { games: [] })
            .then(data => ({ id: tt.id, games: data.games || [] }))
            .catch(() => ({ id: tt.id, games: [] }))
        )
      );
      const map = {};
      results.forEach(r => { if (r.games.length > 0) map[r.id] = r.games; });
      setTeeTimeGames(map);
      // Fetch auto-finalize/readiness for tee times that have games (drives the
      // organizer "scores are in" nudge). Fire-and-forget; failures are ignored.
      Object.keys(map).forEach(ttId => { loadAutoFinalizeState(ttId); });
    } catch (e) { /* ignore */ }
  };

  // Load standings or settlement for a specific tee time panel
  const loadGamesData = async (view, teetimeId) => {
    setGamesLoading(true);
    try {
      if (view === 'standings') {
        const res = await fetch(`${API_URL}/player/scoring/standings?token=${token}&teetime_id=${teetimeId}`);
        if (res.ok) setStandingsData(await res.json());
      } else if (view === 'settlement') {
        // Load both settlement and standings — standings needed for push detection
        const [settleRes, standRes] = await Promise.all([
          fetch(`${API_URL}/player/scoring/settlement?token=${token}&teetime_id=${teetimeId}`),
          fetch(`${API_URL}/player/scoring/standings?token=${token}&teetime_id=${teetimeId}`)
        ]);
        if (settleRes.ok) setSettlementData(await settleRes.json());
        if (standRes.ok) setStandingsData(await standRes.json());
      } else {
        const res = await fetch(`${API_URL}/player/scoring/games?token=${token}&teetime_id=${teetimeId}`);
        if (res.ok) {
          const data = await res.json();
          setTeeTimeGames(prev => ({ ...prev, [teetimeId]: data.games || [] }));
        }
      }
    } catch (e) { /* ignore */ }
    finally { setGamesLoading(false); }
  };

  // Load the auto-finalize + readiness state for a tee time (token-authed).
  // Drives the organizer-only "scores are in - finalize now?" nudge.
  const loadAutoFinalizeState = async (teetimeId) => {
    try {
      const res = await fetch(`${API_URL}/player/scoring/auto-finalize?token=${token}&teetime_id=${teetimeId}`);
      if (res.ok) {
        const d = await res.json();
        setAutoFinalizeState(prev => ({ ...prev, [teetimeId]: d }));
      }
    } catch (e) { /* ignore */ }
  };

  // Finalize a round from the portal nudge. Uses the organizer dashboard JWT
  // (present when the organizer is logged in on this browser - the common case).
  // If no dashboard session is available, route them to the dashboard rather than
  // building a separate token-authed finalize path (the Finalize-From-Phone plan
  // owns that; tracked separately).
  const finalizeFromPortal = async (teetimeId) => {
    const jwt = getAuthToken();
    if (!jwt) {
      addToast('Open the organizer dashboard to finalize this round.', 'info');
      return;
    }
    {
      const stops = await readyStops({ teetimeId });   // slice 3: must-fix items ask first
      if (stops.length && !window.confirm(mustFixMessage(stops, 'finalize'))) return;
    }
    setPortalFinalizing(prev => ({ ...prev, [teetimeId]: true }));
    try {
      const res = await fetch(`${API_URL}/scoring/teetimes/${teetimeId}/finalize`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Finalize failed');
      }
      addToast('Round finalized - settlement is ready.', 'success');
      await loadAutoFinalizeState(teetimeId);
      loadGamesData('optin', teetimeId);
    } catch (e) {
      addToast(e.message || 'Could not finalize round', 'error');
    } finally {
      setPortalFinalizing(prev => ({ ...prev, [teetimeId]: false }));
    }
  };

  // Handicap check at opt-in (Oct 5, 2026): { kind: 'missing' | 'stale', game, days, stale_days }
  const [hcpNotice, setHcpNotice] = useState(null);
  const toggleOptIn = async (game, teetimeId) => {
    try {
      const res = await fetch(`${API_URL}/player/scoring/opt-in-game?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: game.id, opt_in: !game.opted_in }),
      });
      if (!res.ok && res.status === 409) {
        const err = await res.json().catch(() => ({}));
        if (err.code === 'NO_HANDICAP') { setHcpNotice({ kind: 'missing', game: game.display_name || 'This game' }); return; }
        addToast(err.error || 'Failed to update opt-in', 'error');
        return;
      }
      if (res.ok) {
        const okData = await res.clone().json().catch(() => ({}));
        if (okData && okData.handicap_notice) setHcpNotice({ kind: 'stale', game: game.display_name || 'this game', days: okData.handicap_notice.days, stale_days: okData.handicap_notice.stale_days });
        setTeeTimeGames(prev => ({
          ...prev,
          [teetimeId]: (prev[teetimeId] || []).map(g => g.id === game.id ? { ...g, opted_in: !game.opted_in } : g),
        }));
        addToast(game.opted_in ? 'Opted out of game' : '✓ Opted in to game!', 'success');
      } else {
        const err = await res.json();
        addToast(err.error || 'Failed to update opt-in', 'error');
      }
    } catch (e) {
      addToast('Failed to update opt-in', 'error');
    }
  };
  // ── End Games Module handlers ────────────────────────────────────────────

  const handleGhinSync = async () => {
    if (!ghinSyncForm.email || !ghinSyncForm.password) {
      addToast('Please enter your GHIN email and password', 'error');
      return;
    }

    setGhinSyncing(true);
    try {
      const res = await fetch(`${API_URL}/player/ghin/sync?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ghin_email: ghinSyncForm.email,
          ghin_password: ghinSyncForm.password,
          save_credentials: ghinSyncForm.saveCredentials,
          smart_reminder: ghinSyncForm.smartReminder
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to sync handicap');
      }

      // Update editForm with new handicap
      setEditForm(prev => ({ ...prev, handicap: data.handicapIndex }));
      // Update playerData. A successful sync means the stored login works
      // again, and the server has just nulled ghin_sync_error -- mirror that
      // locally so the home-screen "can't sign in to GHIN" banner clears
      // without a reload (Sept 10, 2026).
      setPlayerData(prev => ({ ...prev, handicap: data.handicapIndex, ghin_sync_error: null }));
      
      // Show success result in modal instead of closing
      setGhinSyncResult({
        handicapIndex: data.handicapIndex,
        lowHandicapIndex: data.lowHandicapIndex,
        ghinName: data.ghinName,
        club: data.club,
        autoSyncEnabled: ghinSyncForm.saveCredentials,
        smartReminderEnabled: ghinSyncForm.saveCredentials && ghinSyncForm.smartReminder
      });
      
      // Refresh GHIN status
      fetchGhinStatus();
    } catch (err) {
      console.error('GHIN sync error:', err);
      addToast(err.message, 'error');
    } finally {
      setGhinSyncing(false);
    }
  };

  const removeGhinCredentials = async () => {
    try {
      const res = await fetch(`${API_URL}/player/ghin/credentials?token=${token}`, {
        method: 'DELETE'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove credentials');
      }

      addToast('GHIN credentials removed');
      fetchGhinStatus();
    } catch (err) {
      console.error('Error removing GHIN credentials:', err);
      addToast(err.message, 'error');
    }
  };

  // Combine Your Groups (Oct 19, 2026): answer "is that you?" for one other group.
  // 'yes' combines it into this app; 'no' is remembered so he isn't asked again
  // (he can still combine it later from Linked Organizations).
  const answerLinkCandidate = async (cand, answer) => {
    if (!cand || linkAnswering) return;
    setLinkAnswering(true);
    try {
      const res = await fetch(`${API_URL}/player/link-suggestions/${cand.player_id}?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not save your answer');

      if (answer === 'yes') {
        addToast(data.message || `Combined. ${cand.organization_name} tee times now show here.`);
      } else {
        addToast('Got it. If you change your mind, use Linked Organizations in your profile.');
      }

      const [sugRes, linkedOrgsRes] = await Promise.all([
        fetch(`${API_URL}/player/link-suggestions?token=${token}`),
        fetch(`${API_URL}/player/linked-organizations?token=${token}`)
      ]);
      if (sugRes.ok) {
        const sugData = await sugRes.json();
        setLinkCandidates(sugData.candidates || []);
      }
      if (linkedOrgsRes.ok) {
        const linkedOrgsData = await linkedOrgsRes.json();
        setLinkedOrgs(linkedOrgsData.linkedOrganizations || []);
      }

      if (answer === 'yes') {
        // Same refresh linkOrganization does: the combined group's tee times appear now
        const [teeTimesRes, myTeeTimesRes] = await Promise.all([
          fetch(`${API_URL}/player/teetimes?token=${token}`),
          fetch(`${API_URL}/player/my-teetimes?token=${token}`)
        ]);
        if (teeTimesRes.ok) {
          const teeTimesData = await teeTimesRes.json();
          setTeeTimes(teeTimesData.data || []);
        }
        if (myTeeTimesRes.ok) {
          const myTeeTimesData = await myTeeTimesRes.json();
          setMyTeeTimes(myTeeTimesData.data || []);
        }
      }
    } catch (err) {
      console.error('Error answering combine prompt:', err);
      addToast(err.message, 'error');
      setLinkPromptOff(true); // never leave a prompt he can't get past
    } finally {
      setLinkAnswering(false);
    }
  };

  // Phase 4: Link organization function
  const linkOrganization = async () => {
    if (!linkOrgId.trim()) {
      addToast('Please enter an Organization ID', 'error');
      return;
    }
    
    setLinkingOrg(true);
    try {
      const res = await fetch(`${API_URL}/player/linked-organizations?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId: linkOrgId.trim() })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to link organization');
      }
      
      addToast(data.message || 'Organization linked successfully!');
      setLinkOrgId('');
      setShowLinkOrgModal(false);
      
      // Refresh linked organizations
      const linkedOrgsRes = await fetch(`${API_URL}/player/linked-organizations?token=${token}`);
      if (linkedOrgsRes.ok) {
        const linkedOrgsData = await linkedOrgsRes.json();
        setLinkedOrgs(linkedOrgsData.linkedOrganizations || []);
      }
      
      // Refresh tee times to show new organization's tee times
      const [teeTimesRes, myTeeTimesRes] = await Promise.all([
        fetch(`${API_URL}/player/teetimes?token=${token}`),
        fetch(`${API_URL}/player/my-teetimes?token=${token}`)
      ]);
      
      if (teeTimesRes.ok) {
        const teeTimesData = await teeTimesRes.json();
        setTeeTimes(teeTimesData.data || []);
      }
      
      if (myTeeTimesRes.ok) {
        const myTeeTimesData = await myTeeTimesRes.json();
        setMyTeeTimes(myTeeTimesData.data || []);
      }
    } catch (err) {
      console.error('Error linking organization:', err);
      addToast(err.message, 'error');
    } finally {
      setLinkingOrg(false);
    }
  };

  // Phase 4: Unlink organization function
  const unlinkOrganization = async (linkId) => {
    try {
      const res = await fetch(`${API_URL}/player/linked-organizations/${linkId}?token=${token}`, {
        method: 'DELETE'
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to unlink organization');
      }
      
      addToast(data.message || 'Organization unlinked successfully');
      setUnlinkingOrgId(null);
      
      // Refresh linked organizations
      const linkedOrgsRes = await fetch(`${API_URL}/player/linked-organizations?token=${token}`);
      if (linkedOrgsRes.ok) {
        const linkedOrgsData = await linkedOrgsRes.json();
        setLinkedOrgs(linkedOrgsData.linkedOrganizations || []);
      }
      
      // Refresh tee times
      const [teeTimesRes, myTeeTimesRes] = await Promise.all([
        fetch(`${API_URL}/player/teetimes?token=${token}`),
        fetch(`${API_URL}/player/my-teetimes?token=${token}`)
      ]);
      
      if (teeTimesRes.ok) {
        const teeTimesData = await teeTimesRes.json();
        setTeeTimes(teeTimesData.data || []);
      }
      
      if (myTeeTimesRes.ok) {
        const myTeeTimesData = await myTeeTimesRes.json();
        setMyTeeTimes(myTeeTimesData.data || []);
      }
    } catch (err) {
      console.error('Error unlinking organization:', err);
      addToast(err.message, 'error');
    }
  };

  // Stats calculation functions
  const getFilteredRounds = () => {
    const now = new Date();  // Current moment for past/future comparisons
    const nowTimestamp = now.getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp = today.getTime();  // Midnight today for date range comparisons
    
    // Only count rounds where player is actually playing (not on waitlist) AND has a valid date
    const allRounds = myTeeTimes.filter(tt => tt.status !== 'waitlist' && tt.date);
    
    // Helper function to ensure proper date+time sorting
    // For future rounds: oldest/soonest first (ascending)
    // For past rounds: newest first (descending)
    const sortByDateTime = (a, b, ascending = true) => {
      const timestampA = utils.getDateTimestamp(a.date, a.time);
      const timestampB = utils.getDateTimestamp(b.date, b.time);
      return ascending ? timestampA - timestampB : timestampB - timestampA;
    };
    
    let filtered = [];
    let shouldSortAscending = false; // Default: newest first for past rounds
    
    switch(statsFilter) {
      case 'past': {
        filtered = allRounds.filter(tt => {
          const teeTimeTimestamp = utils.getDateTimestamp(tt.date, tt.time);
          return teeTimeTimestamp < nowTimestamp;  // Compare against current moment, not midnight
        });
        shouldSortAscending = false; // Newest first for past rounds
        break;
      }
      case 'future': {
        filtered = allRounds.filter(tt => {
          const teeTimeTimestamp = utils.getDateTimestamp(tt.date, tt.time);
          return teeTimeTimestamp >= nowTimestamp;  // Compare against current moment, not midnight
        });
        shouldSortAscending = true; // Oldest/soonest first for future rounds
        break;
      }
      case 'lastmo': {
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        lastMonth.setHours(0, 0, 0, 0);
        const lastMonthTimestamp = lastMonth.getTime();
        filtered = allRounds.filter(tt => {
          const teeTimeTimestamp = utils.getDateTimestamp(tt.date, tt.time);
          return teeTimeTimestamp >= lastMonthTimestamp && teeTimeTimestamp < todayTimestamp;
        });
        shouldSortAscending = false; // Newest first for past rounds
        break;
      }
      case 'lastqtr': {
        const lastQuarter = new Date();
        lastQuarter.setMonth(lastQuarter.getMonth() - 3);
        lastQuarter.setHours(0, 0, 0, 0);
        const lastQuarterTimestamp = lastQuarter.getTime();
        filtered = allRounds.filter(tt => {
          const teeTimeTimestamp = utils.getDateTimestamp(tt.date, tt.time);
          return teeTimeTimestamp >= lastQuarterTimestamp && teeTimeTimestamp < todayTimestamp;
        });
        shouldSortAscending = false; // Newest first for past rounds
        break;
      }
      case 'all':
      default: {
        filtered = allRounds;
        shouldSortAscending = false; // Newest first for all rounds
        break;
      }
    }
    
    const sorted = filtered.sort((a, b) => sortByDateTime(a, b, shouldSortAscending));
    return sorted;
  };

  const getFilterLabel = () => {
    switch(statsFilter) {
      case 'past': return 'Past Rounds';
      case 'future': return 'Upcoming Rounds';
      case 'lastmo': return 'Last Month';
      case 'lastqtr': return 'Last Quarter';
      case 'all':
      default: return 'All Time';
    }
  };

  const handleRSVP = (teetimeId) => {
    setConfirmRSVPId(teetimeId);
  };

  const handleAcceptInvitation = async (nomination) => {
    // Optimistic removal — the endpoint returns HTML for the email link, so we
    // remove locally and let the next /my-pending-nominations poll (within 10s)
    // reconcile truth. If the POST fails, the row reappears on the next poll.
    setPendingInvitations(prev => prev.filter(n => n.ttp_id !== nomination.ttp_id));
    try {
      // `Accept: application/json` asks the accept handler for JSON instead of
      // its HTML result page (Sept 11, 2026). The one answer we act on is the
      // 409 `tee_time_overlap` the server now returns when the player is
      // already booked within four hours that day: put the invitation back
      // (the server left it pending) and show the same overlap modal the RSVP
      // button uses, instead of silently dropping the row until the next poll.
      const acceptRes = await fetch(`${API_URL}/player/nomination/accept/${nomination.nomination_token}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' }
      });
      if (acceptRes.status === 409) {
        const data = await acceptRes.json().catch(() => ({}));
        if (data.error === 'tee_time_overlap') {
          setPendingInvitations(prev => prev.some(n => n.ttp_id === nomination.ttp_id) ? prev : [nomination, ...prev]);
          setOverlapConflict({ message: data.message });
          setShowOverlapModal(true);
          return;
        }
      }
      // Refresh my-teetimes so the newly-confirmed tee time appears in "My Upcoming Golf"
      const myTeeTimesRes = await fetch(`${API_URL}/player/my-teetimes?token=${token}`);
      if (myTeeTimesRes.ok) {
        const myTeeTimesData = await myTeeTimesRes.json();
        setMyTeeTimes(myTeeTimesData.data || []);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
    }
  };

  const handleDeclineInvitation = async (nomination) => {
    setPendingInvitations(prev => prev.filter(n => n.ttp_id !== nomination.ttp_id));
    try {
      await fetch(`${API_URL}/player/nomination/decline/${nomination.nomination_token}`, {
        method: 'POST'
      });
    } catch (err) {
      console.error('Error declining invitation:', err);
    }
  };

  // ==========================================================================
  // Group RSVP — Captain-side handlers (§6.3 Add Playing Partners modal)
  // ==========================================================================

  // Computes open slots for a given tee time (confirmed + pending count toward "filled"
  // server-side, so backend /api/teetimes already returns the right player array;
  // here we just count the confirmed/non-waitlist player entries).
  const computeOpenSlots = (tt) => {
    if (!tt) return 0;
    const confirmedCount = Array.isArray(tt.players) ? tt.players.length : 0;
    return Math.max(0, (tt.slots || 0) - confirmedCount);
  };

  // Hours until tee time — used for the confirmation-window cutoff gate.
  // Backend enforces the same rule (playerRoutes.js ~line 4563), so we mirror
  // client-side to hide the button when nominations would be rejected.
  const hoursUntilTeeTime = (tt) => {
    if (!tt?.date || !tt?.time) return Infinity;
    try {
      const dateStr = tt.date.split('T')[0];
      const teeStart = new Date(`${dateStr}T${tt.time}`);
      return (teeStart.getTime() - Date.now()) / 3600000;
    } catch {
      return Infinity;
    }
  };

  // Button-visibility predicate — gates the "+ Add Playing Partners" button in
  // both the post-RSVP card action area and the §6.4 Pending Partners list.
  const canShowAddPartners = (tt) => {
    if (!playerData?.can_nominate) return false;
    if (tt?.status === 'waitlist') return false;
    if (computeOpenSlots(tt) <= 0) return false;
    const windowHours = playerData?.group_rsvp_confirmation_hours || 24;
    if (hoursUntilTeeTime(tt) <= windowHours) return false;
    return true;
  };

  const handleOpenAddPartners = async (tt) => {
    setAddPartnersTeetime(tt);
    setSelectedNominees(['']); // start with one empty row
    setNominateError(null);
    setNominateSuccess(null);
    setEligibleLoading(true);
    setEligibleNominees([]);
    try {
      const res = await fetch(
        `${API_URL}/player/eligible-nominees?token=${token}&teetimeId=${tt.id}`
      );
      if (res.ok) {
        const data = await res.json();
        setEligibleNominees(data.eligible || []);
      } else {
        const err = await res.json().catch(() => ({}));
        setNominateError(err.error || 'Could not load eligible players.');
      }
    } catch (err) {
      setNominateError('Network error — please try again.');
    } finally {
      setEligibleLoading(false);
    }
  };

  const handleCloseAddPartners = () => {
    setAddPartnersTeetime(null);
    setEligibleNominees([]);
    setSelectedNominees([]);
    setNominateError(null);
    setNominateSuccess(null);
    setNominateSubmitting(false);
  };

  const handleAddAnotherNomineeRow = () => {
    setSelectedNominees(prev => [...prev, '']);
  };

  const handleRemoveNomineeRow = (idx) => {
    setSelectedNominees(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSelectNominee = (idx, nomineeId) => {
    setSelectedNominees(prev => prev.map((v, i) => (i === idx ? nomineeId : v)));
  };

  const handleSendInvites = async () => {
    if (!addPartnersTeetime) return;
    // Collect selected ids, skip empties, dedupe.
    const ids = Array.from(new Set(selectedNominees.filter(Boolean)));
    if (ids.length === 0) {
      setNominateError('Please select at least one player.');
      return;
    }
    setNominateSubmitting(true);
    setNominateError(null);
    try {
      const res = await fetch(`${API_URL}/player/nominate?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teetime_id: addPartnersTeetime.id,
          nominee_ids: ids
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Backend error text is user-facing (includes the cutoff-window message).
        setNominateError(data.error || 'Could not send invitations.');
        setNominateSubmitting(false);
        return;
      }
      const names = (data.nominations || []).map(n => n.nominee_name).filter(Boolean);
      const failedNames = (data.failed || []).map(f => f.nominee_name).filter(Boolean);
      setNominateSuccess({ names, failedNames });
      // Refresh captain nominations for this tee time so §6.4 list picks up the new pending rows.
      try {
        const r = await fetch(`${API_URL}/player/nominations/${addPartnersTeetime.id}?token=${token}`);
        if (r.ok) {
          const d = await r.json();
          setCaptainNominations(prev => ({
            ...prev,
            [addPartnersTeetime.id]: d.nominations || []
          }));
        }
      } catch (_) { /* soft-fail — next 10s refresh reconciles */ }
    } catch (err) {
      setNominateError('Network error — please try again.');
    } finally {
      setNominateSubmitting(false);
    }
  };

  const executeRSVP = async () => {
    if (!confirmRSVPId) return;
    
    const teetimeId = confirmRSVPId;
    setConfirmRSVPId(null);
    
    try {
      // FIXED: Backend expects ID in URL path (/rsvp/:id), not in body
      const res = await fetch(`${API_URL}/player/rsvp/${teetimeId}?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
        // No body needed - ID is in URL path
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        // Check for tee time overlap conflict (409)
        if (res.status === 409 && data.error === 'tee_time_overlap') {
          setOverlapConflict({ message: data.message });
          setShowOverlapModal(true);
          return;
        }
        throw new Error(data.error || 'Failed to RSVP');
      }
      
      // Store the tee time for the success modal
      const rsvpTeeTime = teeTimes.find(tt => tt.id === teetimeId);
      
      // Refresh data without full page reload
      const [teeTimesRes, myTeeTimesRes] = await Promise.all([
        fetch(`${API_URL}/player/teetimes?token=${token}`),
        fetch(`${API_URL}/player/my-teetimes?token=${token}`)
      ]);
      
      if (teeTimesRes.ok) {
        const teeTimesData = await teeTimesRes.json();
        setTeeTimes(teeTimesData.data || []);
      }
      
      if (myTeeTimesRes.ok) {
        const myTeeTimesData = await myTeeTimesRes.json();
        const freshMyTeeTimes = myTeeTimesData.data || [];
        setMyTeeTimes(freshMyTeeTimes);
        // Re-confirming creates a brand-new confirmed row, but teeTimeGames was
        // built once at portal load and won't include a tee time the player had
        // cancelled (or one they joined after games were set). Refresh the games
        // map so any games already configured for this tee time appear with an
        // opt-in opportunity. (Wurtzel incident, May 30 2026.)
        loadAllTeeTimeGames(freshMyTeeTimes);
      }
      
      // Show success modal with ads (instead of just a toast)
      setLastRSVPTeeTime(rsvpTeeTime);
      setShowRSVPSuccessModal(true);
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleViewTeeTime = async (teeTime) => {
    // If viewing from "My Reservations", the tee time already has full player data.
    // Open immediately from cache for snappiness, then quietly refetch /my-teetimes
    // so saved pairings/tee changes made by the organizer show on this tap (the
    // organizer's save is a DB write; the open portal otherwise holds stale state).
    if (teeTime.players && Array.isArray(teeTime.players)) {
      setViewingTeeTime(teeTime);
      // Background refresh — does not block the modal opening.
      (async () => {
        try {
          // Respect the same include_past behavior as the main loader (line ~25692)
          // so refreshing here never drops past rounds from myTeeTimes when the
          // stats view is open.
          const includePast = viewingStats ? '&include_past=true' : '';
          const res = await fetch(`${API_URL}/player/my-teetimes?token=${token}${includePast}`);
          if (!res.ok) return;
          const data = await res.json();
          const fresh = data.data || [];
          setMyTeeTimes(fresh);
          // If the modal is still open on this same tee time, swap in the fresh copy.
          const updated = fresh.find(t => t.id === teeTime.id);
          if (updated) {
            setViewingTeeTime(prev => (prev && prev.id === teeTime.id ? updated : prev));
          }
        } catch (err) {
          // Silent — the player already sees cached data; a failed refresh is non-fatal.
          console.error('Background refresh of my-teetimes failed:', err);
        }
      })();
      return;
    }
    
    // If viewing from "Available Tee Times", we need to fetch full details
    try {
      const res = await fetch(`${API_URL}/player/teetime/${teeTime.id}?token=${token}`);
      
      if (!res.ok) {
        throw new Error('Failed to fetch tee time details');
      }
      
      const data = await res.json();
      setViewingTeeTime(data.data || teeTime);
    } catch (err) {
      console.error('Error fetching tee time details:', err);
      // Fall back to showing what we have
      setViewingTeeTime(teeTime);
      addToast('Could not load full player details', 'error');
    }
  };

  const handleCancel = async (teetimeId) => {
    try {
      const res = await fetch(`${API_URL}/player/cancel/${teetimeId}?token=${token}`, {
        method: 'DELETE'
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to cancel');
      }
      
      addToast(data.message || 'Cancelled successfully');
      
      // Refresh data without full page reload
      const [teeTimesRes, myTeeTimesRes] = await Promise.all([
        fetch(`${API_URL}/player/teetimes?token=${token}`),
        fetch(`${API_URL}/player/my-teetimes?token=${token}`)
      ]);
      
      if (teeTimesRes.ok) {
        const teeTimesData = await teeTimesRes.json();
        setTeeTimes(teeTimesData.data || []);
      }
      
      if (myTeeTimesRes.ok) {
        const myTeeTimesData = await myTeeTimesRes.json();
        setMyTeeTimes(myTeeTimesData.data || []);
      }
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const confirmCancel = (teetimeId) => {
    setConfirmCancelId(teetimeId);
  };

  const executeCancelRSVP = async () => {
    if (confirmCancelId) {
      await handleCancel(confirmCancelId);
      setConfirmCancelId(null);
    }
  };



  if (loading) {
    return (
      <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>
          Loading your tee times...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
        <Card>
          <div style={{ textAlign: "center", padding: 48 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 8, color: "#dc2626" }}>
              Access Error
            </div>
            <div style={{ color: "#6b7280", marginBottom: 20 }}>
              {error}
            </div>
            <div style={{ fontSize: 14, color: "#6b7280" }}>
              Please contact your golf organizer for a valid link.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const upcomingCount = myTeeTimes.filter(tt => {
    // Compare dates as strings to avoid timezone issues
    const ttDateStr = tt.date?.split('T')[0];
    const todayStr = new Date().toLocaleDateString('en-CA');
    return ttDateStr >= todayStr;
  }).length + personalRounds.filter(pr => {
    const prDateStr = pr.round_date?.split('T')[0] || '';
    const todayStr = new Date().toLocaleDateString('en-CA');
    return prDateStr >= todayStr;
  }).length;

  const firstName = playerData?.name?.split(' ')[0] || 'Golfer';

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: "0 auto", minHeight: "100vh" }}>
      {playerData?.suspended && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '14px 16px', marginBottom: 16, color: '#991b1b' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>⛔ Your access has been suspended</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>
            Your access to {portalOrgName || 'this group'} has been suspended, so you can't sign up for tee times right now. Please contact your organizer for details.
          </div>
        </div>
      )}
      {/* Header */}
      <div className="player-portal-header">
        <div className="player-portal-header-content" style={{ position: 'relative' }}>
          {/* Line 1: logo (left) · "Welcome, Name!" · photo (right).
              width:100% + space-between makes the logo and photo align to the SAME
              left/right edges as the full-width buttons below (which are width:100%
              inside the header's 16px padding). */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="player-portal-logo-circle" style={{ flexShrink: 0 }}>
              <img src="/logo.png" alt="Tee Time Organizer" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <div className="player-portal-welcome" style={{ flex: '1 1 auto', minWidth: 0, fontWeight: 800, fontSize: 22, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: 'nowrap' }}>
              Welcome, {firstName}!
            </div>
            <div style={{ flexShrink: 0 }}>
              {playerData?.photoUrl ? (
                <img 
                  src={playerData.photoUrl} 
                  alt={playerData?.name || 'Player'}
                  style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255, 255, 255, 0.3)' }}
                />
              ) : (
                <PlayerAvatar 
                  name={playerData?.name || 'Player'} 
                  photoUrl=""
                  style={{ width: 44, height: 44, fontSize: 16, border: '2px solid rgba(255, 255, 255, 0.3)' }}
                />
              )}
            </div>
          </div>

          {/* Line 2: subtitle, left-aligned under the logo */}
          <div style={{ width: '100%', fontSize: 14, color: "rgba(255, 255, 255, 0.95)", fontWeight: 600, marginTop: 8 }}>
            {upcomingCount > 0 ? `You have ${upcomingCount} upcoming round${upcomingCount !== 1 ? 's' : ''}` : 'View and manage your tee times'}
          </div>
          <div className="player-portal-buttons" style={{ gap: 10 }}>
            <Button 
              variant="outline" 
              onClick={() => (portalScoringEnabled && portalScoringTierAllowed) ? setViewingScoringStats(true) : setViewingStats(true)}
              style={{ 
                background: "rgba(255, 255, 255, 0.1)", 
                color: "#fff", 
                border: "1px solid rgba(255, 255, 255, 0.3)",
                minHeight: 44
              }}
            >
              📊 Stats
            </Button>
            {portalScoringEnabled && portalScoringTierAllowed && (
            <Button 
              variant="outline" 
              onClick={() => setShowScoring('picker')}
              style={{ 
                background: "rgba(255, 255, 255, 0.1)", 
                color: "#fff", 
                border: "1px solid rgba(255, 255, 255, 0.3)",
                minHeight: 44
              }}
            >
              ⛳ Scoring
            </Button>
            )}
            {/* Phase 2 Scoring gate (May 16, 2026): Show + Lock + Educate when admin
                enabled Scoring but the org's tier doesn't qualify. Click opens upgrade
                modal instead of opening the scoring picker. Parallel pattern to the
                Admin → Settings → Scoring tab gate (line ~22309). */}
            {portalScoringEnabled && !portalScoringTierAllowed && (
            <Button
              variant="outline"
              onClick={() => triggerUpgradeModal({ feature: 'scoring', requiredTier: 'starter' })}
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                color: "#fff",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                minHeight: 44,
                display: "inline-flex",
                alignItems: "center",
                gap: 6
              }}
              title="Your group's plan doesn't include Scoring. Tap to see upgrade options."
            >
              ⛳ Scoring
              <LockBadge feature="scoring" requiredTier="starter" style={{ fontSize: 10, padding: '1px 6px' }} />
            </Button>
            )}
            {!playerData?.email?.includes('@demo.teetimeorganizer.net') && (
            <Button 
              variant="outline" 
              onClick={() => setShowShareModal(true)}
              style={{ 
                background: "rgba(255, 255, 255, 0.1)", 
                color: "#fff", 
                border: "1px solid rgba(255, 255, 255, 0.3)",
                minHeight: 44
              }}
            >
              📤 Share
            </Button>
            )}
            {/* My Rounds is part of Scoring: Starter and up (Sept 27, 2026). */}
            {portalScoringEnabled && portalScoringTierAllowed && (
            <Button 
              variant="outline" 
              onClick={() => setShowMyRounds(true)}
              style={{ 
                background: "rgba(255, 255, 255, 0.1)", 
                color: "#fff", 
                border: "1px solid rgba(255, 255, 255, 0.3)",
                minHeight: 44
              }}
            >
              🏌️ My Rounds
            </Button>
            )}
            <Button 
              variant="outline" 
              onClick={startEditProfile}
              style={{ 
                background: "rgba(255, 255, 255, 0.1)", 
                color: "#fff", 
                border: "1px solid rgba(255, 255, 255, 0.3)",
                minHeight: 44
              }}
            >
              ✏️ Edit Profile
            </Button>
            {/* What's New — same outline-button style as the others, placed after Edit
                Profile so the core actions come first. Red dot overlays when unread. */}
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <Button 
                variant="outline" 
                onClick={openWhatsNew}
                style={{ 
                  background: "rgba(255, 255, 255, 0.1)", 
                  color: "#fff", 
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  minHeight: 44,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                What's New
              </Button>
              {whatsNewUnread > 0 && (
                <span style={{ position: 'absolute', top: 8, right: 12, width: 10, height: 10,
                  borderRadius: '50%', background: '#ef4444', border: '2px solid #2d6a4f', pointerEvents: 'none' }}></span>
              )}
            </div>
            {/* Player Guide (Sept 28, 2026): the public player guide, in its own tab.
                ?from=player shows the guide's Close bar, which falls back to /portal.
                A Button (not a link) so it matches the other buttons exactly. */}
            <Button
              variant="outline"
              onClick={() => window.open('/guides/tee-time-organizer-player-guide?from=player', '_blank', 'noopener')}
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                color: "#fff",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                minHeight: 44
              }}
            >
              📖 Player Guide
            </Button>
            {playerData?.email?.includes('@demo.teetimeorganizer.net') && (
              <a 
                href="/?landing=true&clear=auth#try-demo"
                style={{ 
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "10px 16px",
                  background: "rgba(239, 68, 68, 0.9)", 
                  color: "#fff", 
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 14,
                  textDecoration: "none",
                  minHeight: 44,
                  cursor: "pointer"
                }}
              >
                ← Exit Demo
              </a>
            )}
            {/* Dashboard Access Buttons - Only show for organizers/admins */}
            {dashboardAccess.isOrganizer && (
              <a 
                href="/dashboard"
                style={{ 
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "10px 16px",
                  background: "rgba(45, 106, 79, 0.9)", 
                  color: "#fff", 
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 14,
                  textDecoration: "none",
                  minHeight: 44,
                  cursor: "pointer"
                }}
              >
                📊 Organizer Dashboard
              </a>
            )}
            {dashboardAccess.isPlatformAdmin && (
              <a 
                href="/admin"
                style={{ 
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "10px 16px",
                  background: "rgba(30, 58, 95, 0.9)", 
                  color: "#fff", 
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 14,
                  textDecoration: "none",
                  minHeight: 44,
                  cursor: "pointer"
                }}
              >
                🔒 Admin Dashboard
              </a>
            )}
          </div>
        </div>
      </div>

      {/* PWA Install Banner - Shows on mobile when not installed and not dismissed */}
      {(() => {
        // Detect mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        // Detect if already running as standalone PWA (pinned to home screen)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
        // Check if dismissed via cookie
        const isDismissed = document.cookie.includes('tto_pwa_dismissed=1');
        // Don't show for demo players
        const isDemo = playerData?.email?.includes('@demo.teetimeorganizer.net');
        
        if (!isMobile || isStandalone || isDismissed || isDemo) return null;
        
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        
        return (
          <div id="pwa-install-banner" style={{
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            border: '1px solid #bbf7d0',
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>📱</span>
              <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                Add to Home Screen for quick access
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={() => {
                  const banner = document.getElementById('pwa-install-banner');
                  const instructions = document.getElementById('pwa-install-instructions');
                  if (instructions) {
                    instructions.style.display = instructions.style.display === 'none' ? 'block' : 'none';
                  }
                }}
                style={{
                  background: '#2d6a4f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Show Me
              </button>
              <button
                onClick={() => {
                  // Set cookie to expire in 1 year
                  document.cookie = 'tto_pwa_dismissed=1; path=/; max-age=31536000; SameSite=Lax';
                  const banner = document.getElementById('pwa-install-banner');
                  const instructions = document.getElementById('pwa-install-instructions');
                  if (banner) banner.style.display = 'none';
                  if (instructions) instructions.style.display = 'none';
                }}
                style={{
                  background: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontSize: 15,
                  cursor: 'pointer',
                  lineHeight: 1
                }}
              >
                ✕
              </button>
            </div>
          </div>
        );
      })()}
      
      {/* PWA Install Instructions - Hidden by default, toggled by Show Me button */}
      {(() => {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
        const isDismissed = document.cookie.includes('tto_pwa_dismissed=1');
        const isDemo = playerData?.email?.includes('@demo.teetimeorganizer.net');
        
        if (!isMobile || isStandalone || isDismissed || isDemo) return null;
        
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        
        return (
          <div id="pwa-install-instructions" style={{
            display: 'none',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: 20,
            marginBottom: 16,
            marginTop: -8
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1f2937', marginBottom: 12 }}>
              {isIOS ? '🍎 iPhone / iPad' : '🤖 Android'}
            </div>
            {isIOS ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ background: '#2d6a4f', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>1</span>
                  <span style={{ fontSize: 14, color: '#374151' }}>Tap the <strong>Share</strong> button <span style={{ fontSize: 18, verticalAlign: 'middle' }}>⬆️</span> at the bottom of Safari</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ background: '#2d6a4f', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>2</span>
                  <span style={{ fontSize: 14, color: '#374151' }}>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ background: '#2d6a4f', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>3</span>
                  <span style={{ fontSize: 14, color: '#374151' }}>Tap <strong>"Add"</strong> — and you're done! ⛳</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, fontStyle: 'italic' }}>
                  Note: Must be using Safari (not Chrome or other browsers on iPhone)
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ background: '#2d6a4f', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>1</span>
                  <span style={{ fontSize: 14, color: '#374151' }}>Tap the <strong>three-dot menu</strong> ⋮ in the top right of Chrome</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ background: '#2d6a4f', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>2</span>
                  <span style={{ fontSize: 14, color: '#374151' }}>Tap <strong>"Add to Home Screen"</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ background: '#2d6a4f', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>3</span>
                  <span style={{ fontSize: 14, color: '#374151' }}>Tap <strong>"Add"</strong> — and you're done! ⛳</span>
                </div>
              </div>
            )}
            <button
              onClick={() => {
                // Dismiss both banner and instructions
                document.cookie = 'tto_pwa_dismissed=1; path=/; max-age=31536000; SameSite=Lax';
                const banner = document.getElementById('pwa-install-banner');
                const instructions = document.getElementById('pwa-install-instructions');
                if (banner) banner.style.display = 'none';
                if (instructions) instructions.style.display = 'none';
              }}
              style={{
                marginTop: 16,
                width: '100%',
                padding: '10px 16px',
                background: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Got it, don't show again
            </button>
          </div>
        );
      })()}

      {/* GHIN Login Failing Banner (Sept 10, 2026) — shown on every app open while
          players.ghin_sync_error records a credential rejection.
          Origin: Dennis changed his ghin.com password, TTO kept the old one, and the
          only symptom was a four-second "GHIN login failed" toast at post time. The
          nightly cron had been writing ghin_sync_error the whole time, but the only
          place it appeared was a "Last error:" line inside the Profile editor, and
          the staleness banner below is suppressed when auto-sync is ON — which is
          exactly the population whose password has gone stale. Two customers sat
          frozen 30+ days that way (open item since July 29).
          The server writes ghin_sync_error from three places now — the cron, the
          portal post routes and the organizer post route — and every successful
          login nulls it, so this clears itself the moment the password is fixed.
          Only credential-class errors trigger it: a transient GHIN outage on a
          Monday full sync stamps the whole opted-in population with a network error
          and must NOT tell everyone to change their password. */}
      {(() => {
        const err = playerData?.ghin_sync_error;
        if (!err) return null;
        // Vocabulary from ghinService.login() ("Invalid GHIN credentials" on 401,
        // "GHIN login failed: {...Incorrect Credentials...}" otherwise) and from
        // GHIN_SYNC_ERROR_INVALID_CREDENTIALS written by the post routes.
        if (!/credential|incorrect|invalid|password|unauthori/i.test(err)) return null;
        // Credentials removed since the error was recorded → nothing to fix here.
        if (!playerData?.has_ghin_credentials) return null;
        if (playerData?.email?.includes('@demo.teetimeorganizer.net')) return null;
        if (sessionStorage.getItem('tto_ghin_login_warning_dismissed') === '1') return null;
        return (
          <div id="tto-ghin-login-banner" style={{
            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            border: '1px solid #fca5a5',
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>🔑</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#991b1b', fontWeight: 700 }}>
                  TTO can't sign in to GHIN for you
                </div>
                <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 2 }}>
                  GHIN rejected your saved login — usually a changed GHIN password. Auto-sync and score posting are paused until it's fixed.{' '}
                  <button
                    onClick={() => { startEditProfile(); }}
                    style={{ background: 'none', border: 'none', color: '#991b1b', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0, fontWeight: 600 }}
                  >
                    Update your GHIN password
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                sessionStorage.setItem('tto_ghin_login_warning_dismissed', '1');
                document.getElementById('tto-ghin-login-banner')?.remove();
              }}
              style={{ background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 20, cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
              title="Dismiss for now"
            >
              ×
            </button>
          </div>
        );
      })()}

      {/* Handicap Staleness Banner - shown on every app open when stale and no auto-sync */}
      {(() => {
        // Must have a handicap recorded at all
        if (!playerData?.handicap) return null;
        // Auto-sync ON means GHIN keeps it current automatically — never show the banner
        if (playerData?.ghin_auto_sync) return null;
        // Don't show for demo players
        if (playerData?.email?.includes('@demo.teetimeorganizer.net')) return null;
        // Check if dismissed this session (sessionStorage so it reappears next visit)
        if (sessionStorage.getItem('tto_hcp_warning_dismissed') === '1') return null;

        // "Last fresh" = most recent of either:
        //   - ghin_last_sync (a successful GHIN pull, even if the handicap value didn't change)
        //   - handicap_updated_at (a manual entry or value change)
        // A confirmed-unchanged handicap from GHIN is still fresh data; this avoids
        // false stale warnings for players whose index has been stable.
        const lastFreshAt = [playerData?.ghin_last_sync, playerData?.handicap_updated_at]
          .filter(Boolean)
          .map(d => new Date(d).getTime())
          .reduce((max, t) => Math.max(max, t), 0);
        if (!lastFreshAt) return null;

        const daysSince = Math.floor((Date.now() - lastFreshAt) / (1000 * 60 * 60 * 24));
        const threshold = playerData?.org_handicap_stale_days || 30;
        if (daysSince < threshold) return null;

        return (
          <div style={{
            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            border: '1px solid #fcd34d',
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#92400e', fontWeight: 700 }}>
                  Handicap index last updated {daysSince} day{daysSince !== 1 ? 's' : ''} ago
                </div>
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
                  Check your GHIN Hcp Index to ensure it is current.
                  {playerData?.ghin_sync_enabled === true && (
                    <> Recommend that you activate auto-sync with GHIN.{' '}
                      {playerData?.ghin && !playerData?.ghin_auto_sync && (
                        <button
                          onClick={() => { startEditProfile(); sessionStorage.setItem('tto_hcp_warning_dismissed', '1'); }}
                          style={{ background: 'none', border: 'none', color: '#b45309', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0, fontWeight: 600 }}
                        >
                          Enable auto-sync with GHIN
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                sessionStorage.setItem('tto_hcp_warning_dismissed', '1');
                // Force re-render by toggling a no-op state update
                document.getElementById('tto-hcp-banner')?.remove();
              }}
              style={{ background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 20, cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
              title="Dismiss"
            >
              ×
            </button>
          </div>
        );
      })()}

      {/* Edit Profile Modal */}
      {editingProfile && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20 }} onClick={cancelEditProfile}>
          <Card style={{ width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 20 }}>Edit Profile</div>
            
            <div style={{ display: "grid", gap: 16 }}>
              {/* Photo Upload Section */}
              <div>
                <Label>Profile Photo</Label>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                  <div style={{ width: '160px', height: '160px', flexShrink: 0 }}>
                    <PlayerAvatar 
                      key={editForm.photoUrl || 'no-photo'}
                      name={editForm.name || playerData?.name || ''} 
                      photoUrl={editForm.photoUrl}
                      style={{ width: '160px', height: '160px', fontSize: '52px', minWidth: '160px', minHeight: '160px' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <PlayerPhotoUpload
                      currentPhotoUrl={editForm.photoUrl}
                      onPhotoChange={(photoUrl) => {
                        console.log('📸 Player portal photo changed:', { 
                          hasPhotoUrl: !!photoUrl, 
                          photoUrlLength: photoUrl?.length || 0 
                        });
                        setEditForm({ 
                          ...editForm, 
                          photoUrl: photoUrl || ''
                        });
                      }}
                    />
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                      Upload a profile photo (JPG, PNG). Will be resized to 200x200px.
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Name</Label>
                <Input 
                  value={editForm.name} 
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <Label>Phone</Label>
                <Input 
                  value={editForm.phone} 
                  onChange={(e) => setEditForm({ ...editForm, phone: utils.formatPhone(e.target.value) })}
                  placeholder="(555) 555-5555"
                />
              </div>
              
              <div>
                <Label>GHIN Number</Label>
                <Input 
                  value={editForm.ghin} 
                  onChange={(e) => setEditForm({ ...editForm, ghin: e.target.value })}
                  placeholder="Your GHIN number"
                />
              </div>
              
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <Label style={{ marginBottom: 0 }}>Handicap</Label>
                  {editForm.ghin && (
                    <a
                      href={`https://www.ghin.com/golfer-lookup/all-golfers?searchValue=${editForm.ghin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 12,
                        color: "#2563eb",
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 4
                      }}
                    >
                      🔗 Verify on GHIN.com
                    </a>
                  )}
                </div>
                <Input 
                  type="number"
                  value={editForm.handicap} 
                  onChange={(e) => setEditForm({ ...editForm, handicap: e.target.value })}
                  placeholder="Your handicap"
                  step="0.1"
                  min="-10"
                  max="54"
                />
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  {(playerData?.handicap_updated_at || playerData?.ghin_last_sync) ? (
                    (() => {
                      // Mirror the home-screen banner: use the most recent of ghin_last_sync
                      // (confirmed GHIN pull, even if unchanged) or handicap_updated_at
                      // (manual entry / value change). Avoids false stale messages.
                      const lastFreshAt = [playerData?.ghin_last_sync, playerData?.handicap_updated_at]
                        .filter(Boolean)
                        .map(d => new Date(d).getTime())
                        .reduce((max, t) => Math.max(max, t), 0);
                      const daysSince = Math.floor((Date.now() - lastFreshAt) / (1000 * 60 * 60 * 24));
                      return (
                        <span style={{ color: daysSince >= 30 ? '#b45309' : '#6b7280' }}>
                          Your handicap index was last updated {daysSince === 0 ? 'today' : `${daysSince} day${daysSince !== 1 ? 's' : ''} ago`} — check your GHIN Hcp Index to ensure it is current.
                          {!playerData.ghin_auto_sync && playerData?.ghin_sync_enabled === true && <> Recommend that you activate auto-sync with GHIN.</>}
                        </span>
                      );
                    })()
                  ) : (
                    <>Enter your current USGA Handicap Index (e.g., 12.4)</>
                  )}
                  {editForm.ghin && (
                    <span style={{ marginLeft: 8 }}>• Look up your official handicap on GHIN.com</span>
                  )}
                </div>
                {/* GHIN Sync Button - locked or live depending on org tier (May 13, 2026 — Phase 2 GHIN auto-sync gate) */}
                {/* Default-to-locked-during-load: !== true locks when ghin_sync_enabled is undefined (initial load), matching SMS gate pattern May 10 (better to briefly lock paying tier than flash-unlocked for Free user) */}
                {playerData?.ghin_sync_enabled !== true && playerData?.scoring_enabled === true ? (
                  /* Scoring plan without auto-sync (Starter): connect GHIN for posting only (Sept 27, 2026) */
                  <GhinConnectForPosting token={token} playerData={playerData} setPlayerData={setPlayerData} addToast={addToast} ghinNumber={editForm.ghin} />
                ) : playerData?.ghin_sync_enabled !== true ? (
                  /* Inert locked-state button — org tier does not include ghin_sync (or still loading) */
                  <button
                    disabled
                    title="GHIN auto-sync is not included with your organization's current plan"
                    style={{
                      marginTop: 8,
                      padding: "8px 16px",
                      background: "#e5e7eb",
                      color: "#6b7280",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      cursor: "default",
                      fontSize: 13,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      opacity: 0.85
                    }}
                  >
                    🔒 GHIN Auto-Sync not available on this plan
                  </button>
                ) : (
                  /* Live sync button — always visible to encourage usage */
                  <button
                    onClick={() => {
                      if (!editForm.ghin) {
                        addToast('Please enter your GHIN number first, then click Sync', 'info');
                        return;
                      }
                      fetchGhinStatus();
                      setShowGhinSyncModal(true);
                    }}
                    style={{
                      marginTop: 8,
                      padding: "8px 16px",
                      background: editForm.ghin ? "#2d6a4f" : "#6b7280",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      opacity: editForm.ghin ? 1 : 0.8
                    }}
                  >
                    🔄 Sync Handicap from GHIN
                  </button>
                )}
                {!editForm.ghin && playerData?.ghin_sync_enabled === true && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    Enter your GHIN number above to enable automatic handicap sync
                  </div>
                )}
              </div>
              
              <div>
                <Label>Email</Label>
                {/* Oct 26, 2026: a golfer corrects his own email here (and his name above) until the
                    organizer locks the teams of an event he's on — the server says so if they are.
                    Demo accounts keep their address. */}
                {playerData?.email?.includes('@demo.teetimeorganizer.net') ? (
                  <Input value={playerData?.email || ''} disabled style={{ background: "#f3f4f6", cursor: "not-allowed" }} />
                ) : (
                  <Input
                    type="email"
                    value={editForm.email !== undefined ? editForm.email : (playerData?.email || '')}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="you@example.com"
                    autoCapitalize="none"
                  />
                )}
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  Your event emails and your link go to this address. Leaving it blank keeps the one you have.
                </div>
              </div>

              {/* Preferred Tee Selection */}
              {availableTees.length > 0 && (
                <div>
                  <Label>⛳ Preferred Tee</Label>
                  <Select
                    value={editForm.preferred_tee || ''}
                    onChange={(e) => setEditForm({ ...editForm, preferred_tee: e.target.value })}
                  >
                    <option value="">-- Select your preferred tee --</option>
                    {/* Show only the tees for the player's selected category
                        (Men's / Women's). Women's tees carry a trailing (W)/(L)
                        marker; isWomensTee matches them. Unknown gender => all
                        tees (never strand). Grouped by course. */}
                    {(() => {
                      const g = editForm.gender;
                      const genderTees = (g === 'M' || g === 'W')
                        ? availableTees.filter(t => isWomensTee(t.tee_name) === (g === 'W'))
                        : availableTees;
                      const scoped = genderTees.length > 0 ? genderTees : availableTees;
                      return [...new Set(scoped.map(t => t.course_name))].map(courseName => (
                        <optgroup key={courseName} label={courseName}>
                          {scoped.filter(t => t.course_name === courseName).map(tee => (
                            <option key={tee.id} value={tee.id}>
                              {tee.tee_name}
                            </option>
                          ))}
                        </optgroup>
                      ));
                    })()}
                  </Select>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                    Select which tees you typically play from. This is used to calculate your course handicap.
                  </div>
                  {editForm.preferred_tee && (
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 8, 
                      marginTop: 8, 
                      padding: "8px 12px", 
                      background: "#f0fdf4", 
                      borderRadius: 6,
                      border: "1px solid #bbf7d0"
                    }}>
                      <div style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        background: availableTees.find(t => t.id === editForm.preferred_tee)?.tee_color || '#000',
                        border: availableTees.find(t => t.id === editForm.preferred_tee)?.tee_color === '#f3f4f6' ? '1px solid #d1d5db' : 'none'
                      }} />
                      <span style={{ fontSize: 13, color: "#166534" }}>
                        Playing from {availableTees.find(t => t.id === editForm.preferred_tee)?.tee_name || editForm.preferred_tee} tees
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Handicap category (M/W) — selects which course rating applies
                  where a tee has both a men's and women's rating. Labeled radios
                  (not buttons): this is a supporting single-choice field that
                  should sit UNDER Preferred Tee in visual weight, not compete
                  with it. The label explains WHY we ask. Value -> players.gender. */}
              <div>
                <Label>Tees you play</Label>
                <div style={{ display: "flex", gap: 20, marginTop: 4 }}>
                  {[{ v: 'M', label: "Men's" }, { v: 'W', label: "Women's" }].map(opt => (
                    <label
                      key={opt.v}
                      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#374151", cursor: "pointer" }}
                    >
                      <input
                        type="radio"
                        name="playerGender"
                        value={opt.v}
                        checked={editForm.gender === opt.v}
                        onChange={() => setEditForm({ ...editForm, gender: opt.v })}
                        style={{ cursor: "pointer" }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  Sets which course rating applies to your handicap.
                </div>
              </div>

              {/* ============================================ */}
              {/* COMMUNICATION PREFERENCES - Improved UI */}
              {/* Uses toggle switches for channels, checkboxes for options */}
              {/* SMS consent only shows when SMS is enabled */}
              {/* ============================================ */}
              <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
                <Label>Communication Preferences</Label>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                  Choose how you'd like to receive notifications about tee times
                </div>

                {/* Notification Channels Subsection */}
                <div style={{ 
                  background: "#f9fafb", 
                  borderRadius: 8, 
                  padding: 16, 
                  marginBottom: 12 
                }}>
                  <div style={{ 
                    fontSize: 11, 
                    fontWeight: 600, 
                    color: "#6b7280", 
                    textTransform: "uppercase", 
                    letterSpacing: "0.5px", 
                    marginBottom: 12 
                  }}>
                    Notification Channels
                  </div>

                  {/* Email Toggle */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 0",
                    borderBottom: "1px solid #e5e7eb"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 20 }}>📧</span>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14, color: "#1f2937" }}>Email Notifications</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>Receive updates via email</div>
                      </div>
                    </div>
                    {/* Toggle Switch for Email */}
                    <div
                      onClick={() => setEditForm({ ...editForm, comm_email: !editForm.comm_email })}
                      style={{
                        width: 48,
                        height: 28,
                        borderRadius: 14,
                        background: editForm.comm_email ? "#2d6a4f" : "#d1d5db",
                        padding: 2,
                        cursor: "pointer",
                        transition: "background 0.2s ease",
                        flexShrink: 0
                      }}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 12,
                          background: "#fff",
                          transform: editForm.comm_email ? "translateX(20px)" : "translateX(0)",
                          transition: "transform 0.2s ease",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                        }}
                      />
                    </div>
                  </div>

                  {/* Round Recap email choice (Oct 3, 2026): my rounds / all / never — saves on its own, separate from Save Profile */}
                  <RecapFollowToggle token={token} />

                  {/* SMS Toggle — Phase 2 Player Portal SMS tier-lock (May 23, 2026) */}
                  {/* Default-to-locked-during-load: !== true locks when sms_enabled is undefined (initial load), */}
                  {/* matching the ghin_sync_enabled gate above (better to briefly lock a paying tier than to */}
                  {/* show an opt-in that will never deliver for a Free/Starter org). User-facing copy says */}
                  {/* "Text Messages" per terminology rule; internal flag/key stays sms_enabled / 'sms'. */}
                  {playerData?.sms_enabled !== true ? (
                    /* Locked state — org tier does not include text messages (or still loading) */
                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      padding: "12px 0",
                      opacity: 0.85
                    }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1 }}>
                        <span style={{ fontSize: 20 }}>📱</span>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 14, color: "#1f2937", display: "flex", alignItems: "center", gap: 8 }}>
                            Text Messages
                            <span style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#6b7280",
                              background: "#e5e7eb",
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "1px 8px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}>
                              🔒 Plus
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                            Available on Plus and higher. Your organizer's plan doesn't include text message reminders.
                          </div>
                        </div>
                      </div>
                      {/* Inert locked toggle — visually present, not interactive */}
                      <div style={{
                        width: 48,
                        height: 28,
                        borderRadius: 14,
                        background: "#e5e7eb",
                        border: "1px solid #d1d5db",
                        padding: 2,
                        cursor: "default",
                        flexShrink: 0
                      }}>
                        <div style={{
                          width: 24,
                          height: 24,
                          borderRadius: 12,
                          background: "#f3f4f6",
                          transform: "translateX(0)",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                        }} />
                      </div>
                    </div>
                  ) : (
                    /* Active toggle — org tier includes text messages (unchanged behavior) */
                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      padding: "12px 0"
                    }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1 }}>
                        <span style={{ fontSize: 20 }}>📱</span>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 14, color: "#1f2937" }}>SMS Text Messages</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                            Receive text notifications at your mobile number
                          </div>
                        </div>
                      </div>
                      {/* Toggle Switch for SMS */}
                      <div
                        onClick={() => setEditForm({ ...editForm, comm_text: !editForm.comm_text })}
                        style={{
                          width: 48,
                          height: 28,
                          borderRadius: 14,
                          background: editForm.comm_text ? "#2d6a4f" : "#d1d5db",
                          padding: 2,
                          cursor: "pointer",
                          transition: "background 0.2s ease",
                          flexShrink: 0
                        }}
                      >
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 12,
                            background: "#fff",
                            transform: editForm.comm_text ? "translateX(20px)" : "translateX(0)",
                            transition: "transform 0.2s ease",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* SMS Consent Notice - Only shown when SMS is ON AND tier allows it. */}
                  {/* Tier guard suppresses the TCPA consent block in the locked state: no opt-in */}
                  {/* can occur, so no consent is collected (May 23, 2026 SMS tier-lock). */}
                  {playerData?.sms_enabled === true && editForm.comm_text && (
                    <div style={{
                      background: "#ecfdf5",
                      border: "1px solid #a7f3d0",
                      borderRadius: 8,
                      padding: 12,
                      marginTop: 8
                    }}>
                      <div style={{ fontSize: 12, color: "#166534", lineHeight: 1.5 }}>
                        <strong>✓ SMS Consent</strong> — By enabling SMS, you consent to receive automated text messages 
                        from Tee Time Organizer. Typically 2-6 msgs/week. Msg & data rates may apply.
                        <br />
                        <span style={{ fontSize: 11, color: "#6b7280" }}>
                          Reply <strong>STOP</strong> to opt out. Reply <strong>HELP</strong> for assistance. 
                          <a 
                            href="/sms-terms" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: "#2563eb", marginLeft: 4 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            View SMS Terms
                          </a>
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Reminder Options Subsection */}
                <div style={{ 
                  background: "#f9fafb", 
                  borderRadius: 8, 
                  padding: 16 
                }}>
                  <div style={{ 
                    fontSize: 11, 
                    fontWeight: 600, 
                    color: "#6b7280", 
                    textTransform: "uppercase", 
                    letterSpacing: "0.5px", 
                    marginBottom: 12 
                  }}>
                    Reminder Options
                  </div>

                  {/* GHIN Posting Reminder */}
                  <div
                    onClick={() => setEditForm({ ...editForm, notify_ghin_reminder: !editForm.notify_ghin_reminder })}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: "12px 0",
                      borderBottom: "1px solid #e5e7eb",
                      cursor: "pointer"
                    }}
                  >
                    {/* Custom Checkbox */}
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        border: editForm.notify_ghin_reminder !== false ? "none" : "2px solid #d1d5db",
                        background: editForm.notify_ghin_reminder !== false ? "#2d6a4f" : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.15s ease",
                        marginTop: 2
                      }}
                    >
                      {editForm.notify_ghin_reminder !== false && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 14, color: "#1f2937", display: "flex", alignItems: "center", gap: 6 }}>
                        🏌️ GHIN Posting Reminders
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                        Get a reminder to post your score to GHIN after each round
                      </div>
                    </div>
                  </div>

                  {/* Target Score in Reminders */}
                  <div
                    onClick={() => setEditForm({ ...editForm, show_target_score: !editForm.show_target_score })}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: "12px 0",
                      cursor: "pointer"
                    }}
                  >
                    {/* Custom Checkbox */}
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        border: editForm.show_target_score !== false ? "none" : "2px solid #d1d5db",
                        background: editForm.show_target_score !== false ? "#2d6a4f" : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.15s ease",
                        marginTop: 2
                      }}
                    >
                      {editForm.show_target_score !== false && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 14, color: "#1f2937", display: "flex", alignItems: "center", gap: 6 }}>
                        🎯 Show Target Score in Reminders
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                        Display your target score (par + course handicap) in tee time reminder messages
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Link to Another Organization */}
            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, marginTop: 8 }}>
              <button
                onClick={() => {
                  setEditingProfile(false);
                  setShowLinkOrgModal(true);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#2563eb",
                  fontSize: 14,
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline"
                }}
              >
                🔗 Link to another Tee Time Organizer group
              </button>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                Play with multiple golf groups? Link your account to see tee times from other organizations.
              </div>
            </div>
            
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
              <Button variant="outline" onClick={cancelEditProfile}>Cancel</Button>
              <Button onClick={saveProfile}>Save Changes</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Link Organizations Modal */}
      {/* GHIN Sync Modal */}
      {showGhinSyncModal && (
        <div 
          style={{ 
            position: "fixed", 
            inset: 0, 
            background: "rgba(0,0,0,.4)", 
            backdropFilter: "blur(2px)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            zIndex: 60, 
            padding: 20 
          }} 
          onClick={() => {
            if (!ghinSyncing) {
              setShowGhinSyncModal(false);
              setGhinSyncResult(null);
              setGhinSyncForm({ email: '', password: '', saveCredentials: false, smartReminder: true });
            }
          }}
        >
          <Card 
            style={{ width: "100%", maxWidth: 450, maxHeight: "90vh", overflowY: "auto" }} 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Loading State */}
            {ghinSyncing && (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <div style={{ 
                  width: 60, 
                  height: 60, 
                  border: "4px solid #e5e7eb",
                  borderTop: "4px solid #2d6a4f",
                  borderRadius: "50%",
                  margin: "0 auto 20px",
                  animation: "spin 1s linear infinite"
                }} />
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
                  Syncing with GHIN...
                </div>
                <div style={{ fontSize: 14, color: "#6b7280" }}>
                  Connecting to GHIN and retrieving your handicap index
                </div>
              </div>
            )}

            {/* Success State */}
            {ghinSyncResult && !ghinSyncing && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ 
                  width: 80, 
                  height: 80, 
                  background: "#dcfce7",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  fontSize: 40
                }}>
                  ✅
                </div>
                <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 8, color: "#166534" }}>
                  Handicap Synced Successfully!
                </div>
                
                <div style={{ 
                  background: "#f0fdf4", 
                  border: "2px solid #86efac",
                  borderRadius: 12, 
                  padding: "20px",
                  margin: "20px 0",
                  textAlign: "left"
                }}>
                  <div style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 14, color: "#374151" }}>Handicap Index</span>
                      <span style={{ fontSize: 24, fontWeight: 800, color: "#166534" }}>
                        {ghinSyncResult.handicapIndex}
                      </span>
                    </div>
                    {ghinSyncResult.lowHandicapIndex && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 14, color: "#374151" }}>Low Index</span>
                        <span style={{ fontSize: 16, fontWeight: 600, color: "#16a34a" }}>
                          {ghinSyncResult.lowHandicapIndex}
                        </span>
                      </div>
                    )}
                    {ghinSyncResult.ghinName && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 14, color: "#374151" }}>GHIN Name</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
                          {ghinSyncResult.ghinName}
                        </span>
                      </div>
                    )}
                    {ghinSyncResult.club && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 14, color: "#374151" }}>Club</span>
                        <span style={{ fontSize: 14, color: "#374151" }}>
                          {ghinSyncResult.club}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {ghinSyncResult.autoSyncEnabled && (
                  <div style={{ 
                    background: "#eff6ff", 
                    border: "1px solid #93c5fd",
                    borderRadius: 8, 
                    padding: "12px 16px",
                    marginBottom: 20,
                    textAlign: "left"
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#1e40af", marginBottom: 4 }}>
                      🔄 Automatic Sync Enabled
                    </div>
                    <div style={{ fontSize: 13, color: "#3b82f6", lineHeight: 1.5 }}>
                      Your handicap will automatically sync every <strong>Monday at 5 AM ET</strong>, 
                      and after any day you play. Your credentials are securely encrypted.
                    </div>
                  </div>
                )}
                
                {ghinSyncResult.smartReminderEnabled && (
                  <div style={{ 
                    background: "#fef3c7", 
                    border: "1px solid #fbbf24",
                    borderRadius: 8, 
                    padding: "12px 16px",
                    marginBottom: 20,
                    textAlign: "left"
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#92400e", marginBottom: 4 }}>
                      🔔 Smart Posting Reminder Active
                    </div>
                    <div style={{ fontSize: 13, color: "#b45309", lineHeight: 1.5 }}>
                      You'll get a reminder 24 hours after your round <strong>only if you haven't posted yet</strong>.
                      No annoying reminders if you've already posted your score!
                    </div>
                  </div>
                )}
                
                <Button 
                  onClick={() => {
                    setShowGhinSyncModal(false);
                    setGhinSyncResult(null);
                    setGhinSyncForm({ email: '', password: '', saveCredentials: false, smartReminder: true });
                  }}
                  style={{ width: "100%" }}
                >
                  Done
                </Button>
              </div>
            )}

            {/* Input Form State */}
            {!ghinSyncing && !ghinSyncResult && (
              <>
                <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 8 }}>
                  🔄 Sync Handicap from GHIN
                </div>
                <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 20 }}>
                  Enter your GHIN credentials to automatically sync your handicap index
                </div>
                
                {/* Current Status */}
                {ghinStatus && (
                  <div style={{
                    padding: "12px 16px",
                    background: ghinStatus.hasStoredCredentials ? "#f0fdf4" : "#f9fafb",
                    border: `1px solid ${ghinStatus.hasStoredCredentials ? "#86efac" : "#e5e7eb"}`,
                    borderRadius: 8,
                    marginBottom: 16
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                      Current Status
                    </div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>
                      GHIN #: {ghinStatus.ghinNumber || 'Not set'}
                      {ghinStatus.handicapIndex && ` • Index: ${ghinStatus.handicapIndex}`}
                    </div>
                    {ghinStatus.lastSync && (
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                        Last synced: {new Date(ghinStatus.lastSync).toLocaleString()}
                      </div>
                    )}
                    {ghinStatus.hasStoredCredentials && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "#16a34a" }}>✓ Auto-sync enabled (Mon 5 AM ET + after play days)</span>
                        </div>
                        
                        {/* Smart Reminder Toggle */}
                        <label style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: 8, 
                          cursor: "pointer",
                          padding: "8px 12px",
                          background: ghinStatus.smartReminderEnabled ? "#fef3c7" : "#f9fafb",
                          borderRadius: 6,
                          border: ghinStatus.smartReminderEnabled ? "1px solid #fbbf24" : "1px solid #e5e7eb",
                        }}>
                          <input 
                            type="checkbox" 
                            checked={ghinStatus.smartReminderEnabled || false}
                            onChange={async (e) => {
                              const newValue = e.target.checked;
                              try {
                                const res = await fetch(`${API_URL}/player/ghin/smart-reminder?token=${token}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ enabled: newValue })
                                });
                                if (res.ok) {
                                  setGhinStatus(prev => ({ ...prev, smartReminderEnabled: newValue }));
                                  addToast(newValue ? 'Smart posting reminder enabled' : 'Smart posting reminder disabled', 'success');
                                } else {
                                  addToast('Failed to update preference', 'error');
                                }
                              } catch (err) {
                                console.error('Error updating smart reminder:', err);
                                addToast('Failed to update preference', 'error');
                              }
                            }}
                            style={{ width: 16, height: 16, accentColor: "#f59e0b" }}
                          />
                          <div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: ghinStatus.smartReminderEnabled ? "#92400e" : "#6b7280" }}>
                              🔔 Smart Posting Reminder
                            </span>
                            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                              Remind me to post if round is not in GHIN 24hrs after play
                            </div>
                          </div>
                        </label>
                        
                        <button
                          onClick={removeGhinCredentials}
                          style={{
                            padding: "4px 8px",
                            background: "none",
                            border: "1px solid #dc2626",
                            borderRadius: 4,
                            color: "#dc2626",
                            fontSize: 11,
                            cursor: "pointer",
                            alignSelf: "flex-start",
                            marginTop: 4
                          }}
                        >
                          Disable Autosync
                        </button>
                      </div>
                    )}
                    {ghinStatus.lastError && (
                      <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>
                        Last error: {ghinStatus.lastError}
                      </div>
                    )}
                  </div>
                )}

                {/* Only show form if no credentials stored yet */}
                {!ghinStatus?.hasStoredCredentials && (
                  <>
                    <div style={{ display: "grid", gap: 16 }}>
                      <div>
                        <Label>GHIN Email or Username</Label>
                        <Input 
                          type="email"
                          value={ghinSyncForm.email}
                          onChange={(e) => setGhinSyncForm({ ...ghinSyncForm, email: e.target.value })}
                          placeholder="your-email@example.com"
                        />
                      </div>
                      
                      <div>
                        <Label>GHIN Password</Label>
                        <Input 
                          type="password"
                          value={ghinSyncForm.password}
                          onChange={(e) => setGhinSyncForm({ ...ghinSyncForm, password: e.target.value })}
                          placeholder="Your GHIN password"
                        />
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                          Your password is encrypted and only used to sync your handicap
                        </div>
                      </div>
                      
                      <label style={{ 
                        display: "flex", 
                        alignItems: "flex-start", 
                        gap: 10, 
                        cursor: "pointer",
                        padding: "12px", 
                        background: ghinSyncForm.saveCredentials ? "#f0fdf4" : "#f9fafb", 
                        borderRadius: 8, 
                        border: ghinSyncForm.saveCredentials ? "2px solid #22c55e" : "1px solid #e5e7eb",
                      }}>
                        <input 
                          type="checkbox" 
                          checked={ghinSyncForm.saveCredentials}
                          onChange={(e) => setGhinSyncForm({ ...ghinSyncForm, saveCredentials: e.target.checked })}
                          style={{ width: 18, height: 18, marginTop: 2, accentColor: "#22c55e" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>🔄 Enable Automatic Sync</div>
                          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}>
                            Save credentials securely to automatically sync your handicap.
                            Syncs every <strong>Monday at 5 AM ET</strong> and after days you play.
                            Credentials encrypted with AES-256.
                          </div>
                        </div>
                      </label>
                    </div>
                    
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowGhinSyncModal(false);
                          setGhinSyncForm({ email: '', password: '', saveCredentials: false, smartReminder: true });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleGhinSync}
                        disabled={!ghinSyncForm.email || !ghinSyncForm.password}
                      >
                        Sync Now
                      </Button>
                    </div>
                  </>
                )}

                {/* Show Close button when credentials already stored */}
                {ghinStatus?.hasStoredCredentials && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                    <Button 
                      onClick={() => {
                        setShowGhinSyncModal(false);
                        setGhinSyncForm({ email: '', password: '', saveCredentials: false, smartReminder: true });
                      }}
                    >
                      Close
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      )}

      {/* Combine Your Groups prompt (Oct 19, 2026): one group at a time, big type and
          big buttons. No tap-outside close: Yes or No are the only ways out, and a
          failed save hides it for this visit (linkPromptOff). */}
      {(() => {
        const cand = linkCandidates.find(c => !c.declined);
        if (!cand || linkPromptOff || showLinkOrgModal) return null;
        return (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.45)",
              backdropFilter: "blur(2px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 65,
              padding: 20
            }}
          >
            <Card style={{ width: "100%", maxWidth: 460, textAlign: "center" }}>
              <div style={{ fontSize: 44, marginBottom: 8 }}>🔗</div>
              <div style={{ fontWeight: 800, fontSize: 22, lineHeight: 1.3, marginBottom: 12 }}>
                You're also in {cand.organization_name}
              </div>
              <div style={{ fontSize: 18, color: "#374151", lineHeight: 1.4, marginBottom: 6 }}>
                They have you listed as <strong>{cand.player_name}</strong>.
              </div>
              <div style={{ fontSize: 18, color: "#374151", lineHeight: 1.4, marginBottom: 24 }}>
                Is that you? Yes puts their tee times in this app too.
              </div>
              <div style={{ display: "grid", gap: 12 }}>
                <button
                  onClick={() => answerLinkCandidate(cand, 'yes')}
                  disabled={linkAnswering}
                  style={{
                    minHeight: 60,
                    padding: "14px 20px",
                    background: "#16a34a",
                    border: "none",
                    borderRadius: 12,
                    color: "#fff",
                    fontSize: 20,
                    fontWeight: 800,
                    cursor: linkAnswering ? "wait" : "pointer",
                    opacity: linkAnswering ? 0.7 : 1
                  }}
                >
                  {linkAnswering ? 'Saving…' : "Yes, that's me"}
                </button>
                <button
                  onClick={() => answerLinkCandidate(cand, 'no')}
                  disabled={linkAnswering}
                  style={{
                    minHeight: 60,
                    padding: "14px 20px",
                    background: "#fff",
                    border: "2px solid #d1d5db",
                    borderRadius: 12,
                    color: "#374151",
                    fontSize: 20,
                    fontWeight: 700,
                    cursor: linkAnswering ? "wait" : "pointer",
                    opacity: linkAnswering ? 0.7 : 1
                  }}
                >
                  No, not me
                </button>
              </div>
            </Card>
          </div>
        );
      })()}

      {showLinkOrgModal && (
        <div 
          style={{ 
            position: "fixed", 
            inset: 0, 
            background: "rgba(0,0,0,.4)", 
            backdropFilter: "blur(2px)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            zIndex: 60, 
            padding: 20 
          }} 
          onClick={() => setShowLinkOrgModal(false)}
        >
          <Card 
            style={{ width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 8 }}>
              🔗 Linked Organizations
            </div>
            <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 20 }}>
              Link your account to other golf organizations to see their tee times
            </div>
            
            {/* Primary Organization */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ 
                fontSize: 12, 
                fontWeight: 600, 
                color: "#6b7280", 
                textTransform: "uppercase", 
                marginBottom: 8 
              }}>
                Primary Organization
              </div>
              <div style={{
                padding: "12px 16px",
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                gap: 12
              }}>
                <div style={{ 
                  width: 40, 
                  height: 40, 
                  background: "#22c55e", 
                  borderRadius: "50%", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 18
                }}>
                  🏠
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: "#166534" }}>
                    {playerData?.organization_name || 'Your Organization'}
                  </div>
                  <div style={{ fontSize: 12, color: "#16a34a" }}>
                    Primary • Full access
                  </div>
                </div>
              </div>
            </div>
            
            {/* Linked Organizations */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ 
                fontSize: 12, 
                fontWeight: 600, 
                color: "#6b7280", 
                textTransform: "uppercase", 
                marginBottom: 8 
              }}>
                Linked Organizations ({linkedOrgs.length})
              </div>
              
              {linkedOrgs.length === 0 ? (
                <div style={{
                  padding: "24px",
                  background: "#f9fafb",
                  borderRadius: 8,
                  textAlign: "center",
                  color: "#6b7280"
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🔗</div>
                  <div style={{ fontSize: 14 }}>No linked organizations yet</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    Link an organization to see their available tee times
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {linkedOrgs.map((org) => (
                    <div 
                      key={org.id}
                      style={{
                        padding: "12px 16px",
                        background: org.status === 'verified' ? "#eff6ff" : "#fef3c7",
                        border: `1px solid ${org.status === 'verified' ? "#93c5fd" : "#fcd34d"}`,
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 12
                      }}
                    >
                      <div style={{ 
                        width: 40, 
                        height: 40, 
                        background: org.status === 'verified' ? "#3b82f6" : "#f59e0b", 
                        borderRadius: "50%", 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: 18
                      }}>
                        {org.status === 'verified' ? '🔗' : '⏳'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: org.status === 'verified' ? "#1e40af" : "#92400e" }}>
                          {org.organizationName}
                        </div>
                        <div style={{ fontSize: 12, color: org.status === 'verified' ? "#3b82f6" : "#d97706" }}>
                          {org.status === 'verified' ? 'Linked • View tee times' : 'Pending verification'}
                        </div>
                      </div>
                      <button
                        onClick={() => setUnlinkingOrgId(org.id)}
                        style={{
                          padding: "6px 12px",
                          background: "#fee2e2",
                          border: "1px solid #fecaca",
                          borderRadius: 6,
                          color: "#dc2626",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        Unlink
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Combine Your Groups (Oct 19, 2026): same-email records not yet combined,
                including ones he said "No, not me" to, so a No can be undone here. */}
            {linkCandidates.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ 
                  fontSize: 12, 
                  fontWeight: 600, 
                  color: "#6b7280", 
                  textTransform: "uppercase", 
                  marginBottom: 8 
                }}>
                  Other groups with your email
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {linkCandidates.map((c) => (
                    <div
                      key={c.player_id}
                      style={{
                        padding: "12px 16px",
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap"
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div style={{ fontWeight: 600, fontSize: 16, color: "#111827" }}>
                          {c.organization_name}
                        </div>
                        <div style={{ fontSize: 14, color: "#6b7280" }}>
                          Listed as {c.player_name}{c.declined ? ' • you said not you' : ''}
                        </div>
                      </div>
                      <button
                        onClick={() => answerLinkCandidate(c, 'yes')}
                        disabled={linkAnswering}
                        style={{
                          padding: "10px 16px",
                          minHeight: 44,
                          background: "#16a34a",
                          border: "none",
                          borderRadius: 8,
                          color: "#fff",
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: linkAnswering ? "wait" : "pointer"
                        }}
                      >
                        That's me
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Link New Organization */}
            <div style={{ 
              borderTop: "1px solid #e5e7eb", 
              paddingTop: 20,
              marginBottom: 20
            }}>
              <div style={{ 
                fontSize: 12, 
                fontWeight: 600, 
                color: "#6b7280", 
                textTransform: "uppercase", 
                marginBottom: 8 
              }}>
                Link New Organization
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Input
                  value={linkOrgId}
                  onChange={(e) => setLinkOrgId(e.target.value)}
                  placeholder="Enter Organization ID"
                  style={{ flex: 1 }}
                />
                <Button 
                  onClick={linkOrganization}
                  disabled={linkingOrg || !linkOrgId.trim()}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {linkingOrg ? '...' : '🔗 Link'}
                </Button>
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
                💡 Get the Organization ID from the club organizer. Your email must match a player in their system.
              </div>
            </div>
            
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="outline" onClick={() => setShowLinkOrgModal(false)}>
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Unlink Confirmation Modal */}
      {unlinkingOrgId && (
        <div 
          style={{ 
            position: "fixed", 
            inset: 0, 
            background: "rgba(0,0,0,.5)", 
            backdropFilter: "blur(2px)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            zIndex: 70, 
            padding: 20 
          }} 
          onClick={() => setUnlinkingOrgId(null)}
        >
          <Card 
            style={{ width: "100%", maxWidth: 400 }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
                Unlink Organization?
              </div>
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                You will no longer see tee times from this organization. You can combine it again later from this window.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <Button variant="outline" onClick={() => setUnlinkingOrgId(null)}>
                Cancel
              </Button>
              <Button 
                onClick={() => unlinkOrganization(unlinkingOrgId)}
                style={{ background: "#dc2626", color: "#fff" }}
              >
                Yes, Unlink
              </Button>
            </div>
          </Card>
        </div>
      )}


      {/* ================================================================ */}
      {/* GAMES PANEL - Games Module Session 4                             */}
      {/* ================================================================ */}

      {/* Scoring Mode Picker */}
      {showScoring === 'picker' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowScoring(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 400, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, textAlign: 'center' }}>⛳ Scoring</h2>
            <p style={{ margin: '0 0 20px', fontSize: 15, color: '#6b7280', textAlign: 'center' }}>How would you like to enter scores?</p>
            <div style={{ display: 'grid', gap: 12 }}>
              <button onClick={() => setShowScoring('live')}
                style={{ padding: 20, background: '#166534', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>📱 Live Round</div>
                <div style={{ fontSize: 14, opacity: 0.85 }}>Score hole-by-hole during your round</div>
              </button>
              <button onClick={() => setShowScoring('postround')}
                style={{ padding: 20, background: '#fff', color: '#374151', border: '2px solid #d1d5db', borderRadius: 12, cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>📝 Post-Round Entry — Hole by Hole</div>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Enter all 18 hole scores after your round</div>
              </button>
              <button onClick={() => setShowScoring('totalonly')}
                style={{ padding: 20, background: '#fff', color: '#374151', border: '2px solid #d1d5db', borderRadius: 12, cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>🔢 Post-Round Entry — Total Score</div>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Just enter your total score (e.g. 85)</div>
              </button>
            </div>
            <button onClick={() => setShowScoring(null)}
              style={{ width: '100%', marginTop: 16, padding: '12px 0', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600, color: '#6b7280' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Handicap check at opt-in (Oct 5, 2026): no index → refused; an old one → a reminder. */}
      {hcpNotice && (
        <div data-testid="hcp-optin-notice" onClick={() => setHcpNotice(null)} style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 380, background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>
              {hcpNotice.kind === 'missing' ? 'Add your handicap first' : 'Your handicap index needs updating'}
            </div>
            <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.45 }}>
              {hcpNotice.kind === 'missing'
                ? `${hcpNotice.game} uses handicap strokes, and you don\u2019t have a handicap index on file. Add yours in your Profile, then opt in again.`
                : `You\u2019re in ${hcpNotice.game}. Your handicap index was last updated ${hcpNotice.days} days ago, so it may be out of date — the strokes in this game come from it. Update it in your Profile before the round.`}
            </div>
            <button type="button" onClick={() => { setHcpNotice(null); startEditProfile(); }}
              style={{ width: '100%', height: 50, marginTop: 16, borderRadius: 12, border: 'none', background: '#166534', color: '#fff', fontSize: 17, fontWeight: 700, cursor: 'pointer' }}>Open my Profile</button>
            <button type="button" onClick={() => setHcpNotice(null)}
              style={{ width: '100%', height: 44, marginTop: 8, borderRadius: 12, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {/* Live Scoring */}
      {showScoring === 'live' && (
        <div style={{ position: 'fixed', inset: 0, background: '#f9fafb', zIndex: 1000, overflowY: 'auto' }}>
          <LiveScoring token={token} playerData={playerData} onClose={() => setShowScoring(null)} onOpenProfile={() => { setShowScoring(null); startEditProfile(); }} />
        </div>
      )}

      {/* Post-Round Scorecard Grid */}
      {showScoring === 'postround' && (
        <div style={{ position: 'fixed', inset: 0, background: '#f9fafb', zIndex: 1000, overflowY: 'auto' }}>
          <PostRoundGrid token={token} playerData={playerData} onClose={() => setShowScoring(null)} onOpenProfile={() => { setShowScoring(null); startEditProfile(); }} />
        </div>
      )}

      {/* Total Score Only Entry */}
      {showScoring === 'totalonly' && (
        <div style={{ position: 'fixed', inset: 0, background: '#f9fafb', zIndex: 1000, overflowY: 'auto' }}>
          <TotalScoreEntry token={token} playerData={playerData} onClose={() => setShowScoring(null)} onOpenProfile={() => { setShowScoring(null); startEditProfile(); }} />
        </div>
      )}
      
      {/* Scoring Stats (full screen) */}
      {viewingScoringStats && (
        <div style={{ position: 'fixed', inset: 0, background: '#f9fafb', zIndex: 1000, overflowY: 'auto', padding: '16px 14px' }}>
          <ScoringStats token={token} groupId={portalGroupId} onClose={() => setViewingScoringStats(false)} />
        </div>
      )}

      {/* My Rounds (full screen) */}
      {showMyRounds && (
        <MyRoundsView token={token} playerData={playerData} orgName={portalOrgName} onClose={() => setShowMyRounds(false)} 
          onOpenScoring={(round) => { setScoringPersonalRound(round); }}
        />
      )}

      {/* Personal Round Scoring (full screen, above My Rounds) */}
      {scoringPersonalRound && (
        <PersonalRoundScoring
          token={token} playerData={playerData} round={scoringPersonalRound}
          // The profile editor sits at zIndex 60; My Rounds (1000) and this
          // overlay (1100) would cover it, so both close before it opens.
          onOpenProfile={() => { setScoringPersonalRound(null); setShowMyRounds(false); startEditProfile(); }}
          onClose={() => {
            setScoringPersonalRound(null);
            // Refresh personal rounds for both the unified timeline and MyRoundsView
            (async () => {
              try {
                const prResp = await fetch(`${API_URL}/player/personal-rounds?token=${token}`);
                if (prResp.ok) { const prData = await prResp.json(); setPersonalRounds(prData.rounds || []); }
              } catch (e) {}
            })();
          }}
          onSaved={async () => {
            try {
              const prResp = await fetch(`${API_URL}/player/personal-rounds?token=${token}`);
              if (prResp.ok) { const prData = await prResp.json(); setPersonalRounds(prData.rounds || []); }
            } catch (e) {}
          }}
        />
      )}

      {/* Stats Modal */}
      {viewingStats && (
        <div 
          style={{ 
            position: "fixed", 
            inset: 0, 
            background: "rgba(0,0,0,.4)", 
            backdropFilter: "blur(2px)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            zIndex: 60,
            padding: 20
          }} 
          onClick={() => setViewingStats(false)}
        >
          <Card 
            style={{ width: "100%", maxWidth: 600, maxHeight: "90vh", overflow: "auto" }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 4 }}>
                  📊 Your Golf Stats
                </div>
                <div style={{ fontSize: 14, color: "#6b7280" }}>
                  Track your rounds and playing history
                </div>
              </div>
              <button
                onClick={() => setViewingStats(false)}
                style={{
                  padding: "6px 12px",
                  background: "#fff",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#6b7280",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#f9fafb";
                  e.target.style.borderColor = "#9ca3af";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#fff";
                  e.target.style.borderColor = "#d1d5db";
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* Filter Buttons */}
            <div style={{ 
              display: "flex", 
              gap: 6, 
              flexWrap: "wrap", 
              marginBottom: 20,
              padding: "12px",
              background: "#f9fafb",
              borderRadius: 8
            }}>
              {[
                { value: 'all', label: 'All' },
                { value: 'past', label: 'Past' },
                { value: 'future', label: 'Future' },
                { value: 'lastmo', label: 'Last Mo' },
                { value: 'lastqtr', label: 'Last Qtr' }
              ].map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setStatsFilter(filter.value)}
                  style={{
                    padding: "6px 12px",
                    border: statsFilter === filter.value ? "2px solid #2d6a4f" : "1px solid #d1d5db",
                    background: statsFilter === filter.value ? "#dcfce7" : "#fff",
                    color: statsFilter === filter.value ? "#15803d" : "#6b7280",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: statsFilter === filter.value ? 600 : 400,
                    transition: "all 0.2s"
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Summary Stats */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ 
                background: "linear-gradient(135deg, #2d6a4f, #1b4332)", 
                padding: "20px", 
                borderRadius: 12,
                color: "#fff",
                textAlign: "center"
              }}>
                <div style={{ fontSize: 16, opacity: 0.95, marginBottom: 8, fontWeight: 600 }}>
                  {getFilterLabel()} Total Rounds
                </div>
                <div style={{ fontSize: 48, fontWeight: 800, lineHeight: 1 }}>
                  {getFilteredRounds().length}
                </div>
              </div>
            </div>

            {/* Rounds List */}
            <div>
              <div style={{ 
                fontWeight: 700, 
                fontSize: 14, 
                marginBottom: 12,
                color: "#374151",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>Round Details</span>
                <button
                  onClick={() => setViewingStats(false)}
                  style={{
                    padding: "6px 12px",
                    background: "#fff",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6b7280",
                    textTransform: "none",
                    letterSpacing: "normal",
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#fff";
                    e.target.style.borderColor = "#d1d5db";
                  }}
                >
                  ✕ Close
                </button>
              </div>
              
              {getFilteredRounds().length === 0 ? (
                <div style={{ 
                  textAlign: "center", 
                  padding: 40, 
                  color: "#9ca3af",
                  background: "#f9fafb",
                  borderRadius: 8
                }}>
                  <div style={{ fontSize: 48, marginBottom: 8 }}>⛳</div>
                  <div style={{ fontSize: 14 }}>
                    No rounds found for this period
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {getFilteredRounds().map((tt, idx) => {
                    // Compare dates as strings to avoid timezone issues
                    const ttDateStr = tt.date?.split('T')[0];
                    const todayStr = new Date().toLocaleDateString('en-CA');
                    const isPast = ttDateStr < todayStr;
                    
                    return (
                      <div 
                        key={tt.id || idx}
                        style={{
                          padding: "12px 16px",
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                          background: isPast ? "#f9fafb" : "#fff",
                          display: "flex",
                          alignItems: "center",
                          gap: 12
                        }}
                      >
                        {/* Date Badge */}
                        <div style={{
                          minWidth: 60,
                          padding: "8px",
                          background: isPast ? "#e5e7eb" : "#dcfce7",
                          borderRadius: 6,
                          textAlign: "center"
                        }}>
                          <div style={{ 
                            fontSize: 11, 
                            fontWeight: 600, 
                            color: isPast ? "#6b7280" : "#15803d",
                            textTransform: "uppercase"
                          }}>
                            {(() => {
                              const [y, m, d] = (tt.date || '').split('T')[0].split('-').map(Number);
                              return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short' });
                            })()}
                          </div>
                          <div style={{ 
                            fontSize: 20, 
                            fontWeight: 800,
                            color: isPast ? "#374151" : "#15803d"
                          }}>
                            {(() => {
                              const [y, m, d] = (tt.date || '').split('T')[0].split('-').map(Number);
                              return d;
                            })()}
                          </div>
                        </div>
                        
                        {/* Round Info */}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                            {tt.course || tt.course_name || 'Golf Course'}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 8 }}>
                            <span>🕐 {utils.formatTime(tt.time)}</span>
                            {!isPast && (
                              <>
                                <span>•</span>
                                <span style={{ color: "#2563eb" }}>Upcoming</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Status indicator */}
                        <div style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: isPast ? "#9ca3af" : "#22c55e"
                        }}></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
              <Button variant="outline" onClick={() => setViewingStats(false)}>
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Main Content - Detail View */}
      {/* Round Recap (Oct 3, 2026): the newest recap from the last 3 days, or the
          "No rounds recorded via live scoring" nudge after an unscored round */}
      <PlayerRecapCard token={token} onOpen={(r) => setRecapOpen({ teetimeId: r.teetime_id, recap: r })} />

      {/* My Upcoming Golf — Unified timeline (org tee times + personal rounds) */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16 }}>
          📅 My Upcoming Golf
        </div>
        {(() => {
          // Build unified timeline: org tee times + future personal rounds
          const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
          const orgItems = myTeeTimes.map(tt => ({ ...tt, _type: 'org', _sortDate: tt.date?.split('T')[0] || '', _sortTime: tt.time || '00:00:00' }));
          const prItems = personalRounds
            .filter(pr => {
              const prDate = pr.round_date?.split('T')[0] || '';
              return prDate >= todayStr;
            })
            .map(pr => ({ ...pr, _type: 'personal', _sortDate: pr.round_date?.split('T')[0] || '', _sortTime: pr.tee_time || '99:99:99' }));
          const allItems = [...orgItems, ...prItems].sort((a, b) => {
            if (a._sortDate !== b._sortDate) return a._sortDate.localeCompare(b._sortDate);
            return a._sortTime.localeCompare(b._sortTime);
          });

          if (allItems.length === 0) {
            return (
              <div style={{ textAlign: "center", padding: 32, color: "#6b7280" }}>
                You have no upcoming tee times. Browse available times below to RSVP, or tap My Rounds to log a personal round!
              </div>
            );
          }

          return (
            <div style={{ display: "grid", gap: 16 }}>
              {allItems.map((item, index) => {
                // ── Personal Round Card ──
                if (item._type === 'personal') {
                  const pr = item;
                  const prDateStr = pr.round_date?.split('T')[0] || '';
                  const [y, m, d] = prDateStr.split('-').map(Number);
                  const fmtTime = (t) => { if (!t) return ''; try { const [h, mi] = t.split(':').map(Number); return `${h % 12 || 12}:${String(mi).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; } catch { return t; } };
                  // Google Calendar link
                  const calDate = prDateStr.replace(/-/g, '');
                  const calTitle = encodeURIComponent(`Golf — ${pr.course_name || 'Personal Round'}`);
                  const calLocation = encodeURIComponent([pr.course_name, pr.city, pr.state].filter(Boolean).join(', '));
                  let calUrl = '';
                  // Personal rounds carry no timezone field, so the date/time is a
                  // floating local wall-clock value. Pin it to the viewer's own
                  // timezone via &ctz so Google interprets it correctly instead of
                  // defaulting to GMT/UTC.
                  const calTz = encodeURIComponent(
                    (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; } })()
                  );
                  const ctzParam = calTz ? `&ctz=${calTz}` : '';
                  if (pr.tee_time) {
                    const tParts = pr.tee_time.split(':');
                    const startH = parseInt(tParts[0], 10);
                    const startM = parseInt(tParts[1], 10);
                    const startTime = `${calDate}T${String(startH).padStart(2, '0')}${String(startM).padStart(2, '0')}00`;
                    // Compute end = start + 4h via real date math so late tee times
                    // (e.g. 9:00 PM) roll over to the next day instead of producing
                    // an invalid hour like 25:00.
                    const [py, pm, pd] = prDateStr.split('-').map(Number);
                    const endDate = new Date(py, pm - 1, pd, startH + 4, startM, 0);
                    const pad = (n) => String(n).padStart(2, '0');
                    const endTime = `${endDate.getFullYear()}${pad(endDate.getMonth() + 1)}${pad(endDate.getDate())}T${pad(endDate.getHours())}${pad(endDate.getMinutes())}00`;
                    calUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${calTitle}&dates=${startTime}/${endTime}&location=${calLocation}${ctzParam}`;
                  } else {
                    calUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${calTitle}&dates=${calDate}/${calDate}&location=${calLocation}`;
                  }

                  return (
                    <Card key={`pr-${pr.id}`} style={{ padding: 18, borderLeft: '4px solid #6b7280' }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                            <div style={{ fontWeight: 700, fontSize: 18, color: "#111827" }}>
                              {pr.course_name || 'Golf Course'}
                            </div>
                            <span style={{ fontSize: 11, padding: "2px 8px", background: "#e5e7eb", color: "#4b5563", borderRadius: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                              🏌️ My Round
                            </span>
                          </div>
                          <div style={{ fontSize: 14, color: "#111827", display: "flex", alignItems: "center", gap: 4, fontWeight: 700, flexWrap: "nowrap" }}>
                            📅 <span style={{ whiteSpace: "nowrap" }}>{new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            {pr.tee_time && <> • <span style={{ whiteSpace: "nowrap" }}>{fmtTime(pr.tee_time)}</span></>}
                          </div>
                        </div>
                      </div>

                      {/* Tee badge */}
                      <div className="card-stats" style={{ marginBottom: 8 }}>
                        {pr.tee_name && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6b7280', background: '#f3f4f6', padding: '3px 10px', borderRadius: 6 }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: pr.tee_color || '#6b7280', display: 'inline-block', border: (pr.tee_color === '#f3f4f6') ? '1px solid #d1d5db' : 'none' }} />
                            {pr.tee_name}
                            {pr.course_rating > 0 && <span style={{ color: '#9ca3af', marginLeft: 4 }}>{pr.course_rating}/{pr.slope_rating}</span>}
                          </div>
                        )}
                        {pr.total_gross > 0 && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 700, color: '#166534', background: '#dcfce7', padding: '3px 10px', borderRadius: 6 }}>
                            Score: {pr.total_gross}{pr.tee_par ? ` (${pr.total_gross > pr.tee_par ? '+' : ''}${pr.total_gross - pr.tee_par})` : ''}
                          </div>
                        )}
                      </div>

                      {pr.notes && <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic', marginBottom: 8 }}>{pr.notes}</div>}

                      <div className="card-actions" style={{ marginTop: 12 }}>
                        <button className="btn-primary" onClick={() => setScoringPersonalRound(pr)}>
                          {pr.total_gross ? '📝 Edit Scores' : '⛳ Enter Scores'}
                        </button>
                        {calUrl && (
                          <a href={calUrl} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, textDecoration: 'none', fontSize: 18 }}
                            title="Add to Google Calendar">📅</a>
                        )}
                      </div>
                    </Card>
                  );
                }

                // ── Org Tee Time Card (existing code) ──
                const tt = item;
              // Handle different data formats for players - sort by RSVP time (addedAt)
              const players = [...(tt.players || [])].sort((a, b) => {
                const dateA = a?.addedAt ? new Date(a.addedAt) : new Date(0);
                const dateB = b?.addedAt ? new Date(b.addedAt) : new Date(0);
                return dateA - dateB;
              });
              const playerCount = Array.isArray(players) ? players.length : (tt.player_count || 0);

              // ── "Playing with:" — show the viewer's OWN group once pairings exist ──
              // (Aug 5, 2026) The avatar strip previously showed the first four players
              // by RSVP time. On a 36-player shotgun that is four strangers and "+32" —
              // the first thing a player sees when they open the app before an event.
              //
              // pairingGroup arrives per-player on the /teetimes payload and is null
              // until an organizer saves pairings, so every condition below falls back
              // to the original behaviour: unpaired tee times, a viewer who is not on
              // the sheet, and any field small enough not to need grouping all render
              // exactly as before.
              const myPairingGroup = (() => {
                if (!playerData?.email) return null;
                const me = players.find(p =>
                  typeof p === 'object' &&
                  !!playerData.email && (p.email || '').toLowerCase() === playerData.email.toLowerCase()
                );
                return (me && me.pairingGroup != null) ? me.pairingGroup : null;
              })();
              const myGroupPlayers = myPairingGroup == null ? null
                : players.filter(p => typeof p === 'object' && p.pairingGroup === myPairingGroup);
              // Only take over the strip when the group is a genuine subset — a single
              // group covering the whole field gains nothing from the relabel.
              const showMyGroup = !!(myGroupPlayers && myGroupPlayers.length > 0 && myGroupPlayers.length < players.length);
              const previewPlayers = showMyGroup ? myGroupPlayers : players;
              const myStartingHole = (() => {
                if (!showMyGroup) return null;
                const h = myGroupPlayers.find(p => p.startingHole != null)?.startingHole;
                return h != null ? h : null;
              })();
              
              // Handle different data formats for waitlist
              const waitlist = tt.waitlist || [];
              const waitlistCount = Array.isArray(waitlist) ? waitlist.length : (tt.waitlist_count || 0);
              
              // Calculate days until tee time using string comparison to avoid timezone issues
              const ttDateStr = tt.date?.split('T')[0];
              const todayStr = new Date().toLocaleDateString('en-CA');
              const daysUntil = Math.round((new Date(ttDateStr + 'T12:00:00') - new Date(todayStr + 'T12:00:00')) / (1000 * 60 * 60 * 24));
              
              // Check if player is on waitlist
              const isWaitlisted = Array.isArray(waitlist) && waitlist.some(p => 
                (typeof p === 'string' ? p : p.email) === playerData?.email
              );
              
              // Calculate which ad to show (after every 2nd tee time, max 2 ads)
              // Use ads from END of array so different ads show than in Available section
              const adPosition = Math.floor((index + 1) / 2);
              const showAdAfterThis = (index + 1) % 2 === 0 && adPosition <= 2 && inFeedAds.length > 0;
              const adIndexFromEnd = inFeedAds.length - adPosition;
              const adToShowMyTimes = showAdAfterThis && adIndexFromEnd >= 0 ? inFeedAds[adIndexFromEnd] : null;
              
              return (
                <React.Fragment key={tt.id}>
                <Card id={`teetime-${tt.id}`} className={`tee-time-card ${isWaitlisted ? 'waitlist' : 'upcoming'}`} style={{ padding: 18 }}>
                  <div className={`status-indicator ${isWaitlisted ? 'waitlist' : ''}`}></div>
                  
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#111827" }}>
                          {tt.course || tt.course_name || 'Golf Course'}
                        </div>
                        {/* display_title = the event's name, else the tee time's own title.
                            A player has no use for the distinction — both just say what
                            this round is called. */}
                        {tt.display_title && (
                          <span
                            title={tt.display_title}
                            style={{
                              display: "inline-block", background: "#dcfce7", color: "#166534",
                              padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                              // Sept 22, 2026: no calendar icon here — the date line directly
                              // below carries one, and two on a card reads as a mistake. And one
                              // line: the badge is a label, not a paragraph. A title longer than
                              // the card ellipsises rather than wrapping (full text in `title`).
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              maxWidth: "100%",
                            }}
                          >
                            {tt.display_title}
                          </span>
                        )}
                      </div>
                      {/* Bold date and time + org name (shown uniformly for every row; backend sends display name, falling back to full org name) */}
                      <div style={{ fontSize: 14, color: "#111827", display: "flex", alignItems: "center", gap: 4, fontWeight: 700, flexWrap: "wrap" }}>
                        📅 <span style={{ whiteSpace: "nowrap" }}>{utils.formatDate(tt.date)}</span> • <span style={{ whiteSpace: "nowrap" }}>{utils.formatTime(tt.time)}</span>
                        {tt.organization_name && (
                          <>
                            <span style={{ color: "#9ca3af", fontWeight: 400 }}>•</span>
                            <span style={{ fontWeight: 400, color: "#6b7280", whiteSpace: "nowrap" }}>{tt.organization_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={`status-badge ${isWaitlisted ? 'waitlist' : 'confirmed'}`}>
                      {isWaitlisted ? '⏳ Waitlist' : '✓ Confirmed'}
                    </div>
                  </div>

                  {/* Player count with slots and waitlist on same line */}
                  <div className="card-stats">
                    <div className="stat-item">
                      <span>👥</span>
                      <span>{playerCount} player{playerCount !== 1 ? 's' : ''} / {tt.slots} slot{tt.slots !== 1 ? 's' : ''}</span>
                    </div>
                    {waitlistCount > 0 && (
                      <div className="stat-item">
                        <span>⏳</span>
                        <span>Waitlist: {waitlistCount}</span>
                      </div>
                    )}
                    {hasNote(tt) && (
                      <div className="stat-item">
                        <NoteIcon onClick={() => handleViewTeeTime(tt)} />
                      </div>
                    )}
                  </div>

                  {/* Oct 26, 2026: the captain's door to his team (captainTeamService). Others don't see it. */}
                  {tt.captain_team && (
                    <button
                      type="button"
                      data-testid="manage-my-team"
                      onClick={() => setMyTeamTeetime(tt.id)}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", margin: "10px 0 4px", padding: "12px 14px", background: "#f0fdf4", border: "2px solid #2d6a4f", borderRadius: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left", flexWrap: "wrap" }}
                    >
                      <span style={{ fontSize: 24 }}>👥</span>
                      <span style={{ flex: "1 1 150px", minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 16, fontWeight: 800, color: "#111827" }}>Your team · {tt.captain_team.complete} of {tt.captain_team.size} complete</span>
                        <span style={{ display: "block", fontSize: 14, color: "#4b5563" }}>{tt.captain_team.team_name}{tt.captain_team.locked ? ' · 🔒 locked' : ''}</span>
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 800, color: "#fff", background: "#2d6a4f", borderRadius: 10, padding: "10px 14px", whiteSpace: "nowrap" }}>{tt.captain_team.locked ? 'View my team' : 'Manage my team'}</span>
                    </button>
                  )}

                  {/* Oct 28, 2026 (Captain team Slice 3): "Finish your profile" — only what THIS event
                      requires and his record lacks (captainTeamService.needsFor). Gone once complete. */}
                  {Array.isArray(tt.my_needs) && tt.my_needs.length > 0 && (
                    <div data-testid="event-needs" style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", margin: "10px 0 4px", padding: "12px 14px", background: "#fffbeb", border: "2px solid #f59e0b", borderRadius: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 24 }}>📝</span>
                      <span style={{ flex: "1 1 150px", minWidth: 0, fontSize: 16, fontWeight: 800, color: "#111827", lineHeight: 1.35 }}>
                        {tt.display_title || 'This event'} needs your {(() => { const w = tt.my_needs.map(k => ({ email: 'email', ghin: 'GHIN #', tee: 'tee' }[k] || k)); return w.length > 1 ? `${w.slice(0, -1).join(', ')} and ${w[w.length - 1]}` : w[0]; })()}
                      </span>
                      <button type="button" data-testid="event-needs-update" onClick={startEditProfile}
                        style={{ fontSize: 15, fontWeight: 800, color: "#fff", background: "#b45309", border: "none", borderRadius: 10, padding: "10px 14px", whiteSpace: "nowrap", cursor: "pointer", fontFamily: "inherit" }}>
                        Update my profile
                      </button>
                    </div>
                  )}

                  {tt.weather_display && daysUntil <= 15 && (
                    <div className="weather-compact" style={{ fontSize: 14 }}>
                      <span className="weather-icon" style={{ fontSize: 18 }}>🌤️</span>
                      <div className="weather-text">
                        <span className="weather-temp">{tt.weather_display.split('\n')[0]}</span>
                        {tt.weather_display.includes('\n') && ` • ${tt.weather_display.split('\n').slice(1).join(' • ').trim()}`}
                      </div>
                    </div>
                  )}
                  {/* Sunrise/Sunset display (conditional on org settings from playerData) */}
                  {tt.weather_data && (playerData?.show_sunrise || playerData?.show_sunset) && (() => {
                    try {
                      const wd = typeof tt.weather_data === 'string' ? JSON.parse(tt.weather_data) : tt.weather_data;
                      if (!wd?.sunrise && !wd?.sunset) return null;
                      return (
                        <div style={{ fontSize: 12, color: '#92400e', marginTop: 4, display: 'flex', gap: 10 }}>
                          {playerData?.show_sunrise && wd?.sunrise && (
                            <span style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>🌅 {wd.sunrise}</span>
                          )}
                          {playerData?.show_sunset && wd?.sunset && (
                            <span style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>🌇 {wd.sunset}</span>
                          )}
                        </div>
                      );
                    } catch { return null; }
                  })()}

                  {/* Player Avatars Preview */}
                  {previewPlayers.length > 0 && (
                    <div className="players-preview">
                      <span className="players-label" style={{ fontWeight: 600 }}>
                        {showMyGroup
                          ? <>Your group{myStartingHole != null ? ` · starts ${ninesOf(tt) ? holeName(myStartingHole, ninesOf(tt)) : `hole ${myStartingHole}`}` : ''}:</>
                          : 'Playing with:'}
                      </span>
                      <div className="avatar-group">
                        {previewPlayers.slice(0, 4).map((player, idx) => {
                          // §6.8 Transparent Pending Display (April 2026):
                          // pending_nomination players render at ~0.5 opacity
                          // with a small clock badge overlaid on the avatar.
                          const isPending = typeof player === 'object' && player.status === 'pending_nomination';
                          return (
                            <div
                              key={idx}
                              style={{
                                position: 'relative',
                                display: 'inline-block',
                                opacity: isPending ? 0.5 : 1
                              }}
                              title={isPending ? `${player.name || player} — Invited (awaiting confirmation)` : undefined}
                            >
                              <PlayerAvatar
                                name={player.name || player}
                                photoUrl={typeof player === "object" ? player.photoUrl : ""}
                                size="sm"
                                style={{ width: 32, height: 32 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingPhoto({ 
                                    name: player.name || player, 
                                    photoUrl: typeof player === "object" ? player.photoUrl : "" 
                                  });
                                }}
                              />
                              {isPending && (
                                <span
                                  aria-label="Invitation pending"
                                  style={{
                                    position: 'absolute',
                                    bottom: -2,
                                    right: -2,
                                    width: 14,
                                    height: 14,
                                    borderRadius: '50%',
                                    background: '#fff',
                                    border: '1px solid #d1d5db',
                                    fontSize: 9,
                                    lineHeight: '12px',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  ⏳
                                </span>
                              )}
                            </div>
                          );
                        })}
                        {previewPlayers.length > 4 && (
                          <div className="mini-avatar more-players">+{previewPlayers.length - 4}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Inline Games Strip ── */}
                  {portalGamesEnabled && teeTimeGames[tt.id] && teeTimeGames[tt.id].length > 0 && (() => {
                    const ttGames = teeTimeGames[tt.id];
                    const panelOpen = showGamesPanel === tt.id;
                    return (
                      <div style={{ marginTop: 12, borderTop: '1px solid #d1fae5', paddingTop: 10 }}>
                        {/* Games need live scoring (Dennis, Sept 28, 2026): a player who opts in but
                            doesn't enter scores during the round leaves the leaderboard behind. */}
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                          Available Games
                          <span data-testid="games-live-scoring-note" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 600, color: '#92400e', marginLeft: 6 }}>· requires live scoring</span>
                        </div>
                        {/* Organizer-only nudge: all players finished, round not yet finalized.
                            Shown only to an organizer viewing their own portal. If auto-finalize
                            is on, this is a heads-up; the round will finalize on its own. If off,
                            it offers a one-tap finalize without leaving the app. */}
                        {dashboardAccess.isOrganizer && (() => {
                          const af = autoFinalizeState[tt.id];
                          if (!af || af.is_finalized || !af.ready || af.participant_count === 0) return null;
                          const busy = portalFinalizing[tt.id];
                          return (
                            <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid #fcd34d', background: '#fffbeb' }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 2 }}>
                                ✅ All players have finished
                              </div>
                              <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.4, marginBottom: af.auto_finalize ? 0 : 8 }}>
                                {af.auto_finalize
                                  ? 'Auto-finalize is on — this round will finalize itself shortly.'
                                  : 'Ready to finalize and settle.'}
                              </div>
                              {!af.auto_finalize && (
                                <button
                                  onClick={() => finalizeFromPortal(tt.id)}
                                  disabled={busy}
                                  style={{ width: '100%', padding: '8px', borderRadius: 6, border: 'none', background: '#166534', color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}
                                >
                                  {busy ? 'Finalizing...' : '🏁 Finalize & Settle'}
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        {ttGames.map(game => {
                          const optedIn = game.opted_in;
                          const gameName = game.display_name || game.game_type;
                          const wager = parseFloat(game.bet_amount) || 0;
                          const isLocked = game.status === 'active' || game.status === 'completed';
                          return (
                            <div key={game.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
                                🎮 {gameName}
                                {wager > 0 && <span style={{ fontWeight: 400, color: '#4b5563', marginLeft: 6 }}>${wager.toFixed(2)}{game.game_type === 'putts' ? '/putt' : game.game_type === 'match_play' ? '/match' : (game.game_type === 'nassau' || game.game_type === 'sixes') ? '/bet' : game.game_type === 'stableford' ? (['per_point_field', 'per_point_pairs', 'per_point_teams'].includes(game.settings && game.settings.payout_mode) ? '/point' : '/player') : (game.game_type === 'skins_pot' || game.game_type === 'skins_pot_gross' || game.game_type === 'stroke_play' || isBestBallGameType(game.game_type)) ? '/player' : '/hole'}</span>}
                                {(game.game_type === 'stroke_play' || isBestBallGameType(game.game_type)) && !(wager > 0) && <span style={{ fontWeight: 400, color: '#4b5563', marginLeft: 6 }}>placings only</span>}
                                {isLocked && <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '1px 6px', marginLeft: 6, fontWeight: 600 }}>🔒 Locked</span>}
                                {game.rules_text && (
                                  <button
                                    onClick={() => setPortalRulesGame(game)}
                                    style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', background: '#f3f4f6', border: 'none', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontWeight: 600 }}
                                  >? Rules</button>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {/* Oct 28, 2026: on a Captain's Choice round every player on a team is already in the
                                    closest-to-the-pin game (ctpService.eligibleIds, Oct 22) - no Opt In button. */}
                                {(game.game_type === 'scramble' || (game.game_type === 'ctp' && (ttGames || []).some(g => g.game_type === 'scramble'))) ? (
                                  <span style={{ fontSize: 12, color: '#166534', fontWeight: 700 }} title="A team game — every team on the pairings plays; no opt-in">👥 All teams play</span>
                                ) : game.everyone ? (
                                  <span style={{ fontSize: 12, color: '#166534', fontWeight: 700 }} title="The organizer put everyone on the tee time in this game — no opt-in">👥 Everyone plays</span>
                                ) : isBestBallGameType(game.game_type) ? (
                                  <span style={{ fontSize: 12, color: optedIn ? '#166534' : '#6b7280', fontWeight: 700 }} title="Teams come from the pairings — the organizer sets them">{optedIn ? '👥 With your team' : 'Not on a team'}</span>
                                ) : isLocked ? (
                                  <span style={{ fontSize: 12, color: optedIn ? '#166534' : '#6b7280', fontWeight: 700 }}>
                                    {optedIn ? '✓ Opted In' : '✗ Not Playing'}
                                  </span>
                                ) : optedIn ? (
                                  <>
                                    <span style={{ fontSize: 12, color: '#166534', fontWeight: 700 }}>✓ Opted In</span>
                                    <button
                                      onClick={() => toggleOptIn(game, tt.id)}
                                      style={{ fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                    >
                                      Opt Out
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => toggleOptIn(game, tt.id)}
                                    style={{ fontSize: 12, background: '#166534', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 600 }}
                                  >
                                    Opt In
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {/* Results / Settlement / Scorecard (Oct 6, 2026): real buttons, larger and set
                            apart from the games' "? Rules" chips above, which used to catch near-miss taps.
                            Scorecard opens the whole field's board with the Gross / Net switch (Oct 7,
                            2026 — Dennis); a tap on any name, his own included, opens that card. */}
                        {(() => {
                          // Three equal buttons, icon over label, so the row fits a phone without wrapping.
                          const linkBtn = (active) => ({
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                            minHeight: 54, padding: '6px 4px', minWidth: 0,
                            fontSize: 14, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                            border: `1px solid ${active ? '#1d4ed8' : '#bfdbfe'}`,
                            background: active ? '#1d4ed8' : '#eff6ff', color: active ? '#fff' : '#1d4ed8',
                          });
                          // Oct 28, 2026 (Dennis): no wager on any game → nothing to settle, so no 💰 Settlement
                          // button ("money belongs to settlement"). Results and Scorecard share the row.
                          const hasWager = (ttGames || []).some(g => (parseFloat(g.bet_amount) || 0) > 0);
                          return (
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${hasWager ? 3 : 2}, 1fr)`, gap: 8, marginTop: 14, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                              <button
                                data-testid="portal-results-btn"
                                onClick={() => { if (panelOpen && gamesView === 'standings') { setShowGamesPanel(null); } else { setShowGamesPanel(tt.id); setGamesView('standings'); setStandingsData(null); loadGamesData('standings', tt.id); } }}
                                style={linkBtn(panelOpen && gamesView === 'standings')}
                              >
                                <span style={{ fontSize: 18 }}>📊</span>Results
                              </button>
                              {hasWager && (
                              <button
                                data-testid="portal-settlement-btn"
                                onClick={() => { if (panelOpen && gamesView === 'settlement') { setShowGamesPanel(null); } else { setShowGamesPanel(tt.id); setGamesView('settlement'); setSettlementData(null); loadGamesData('settlement', tt.id); } }}
                                style={linkBtn(panelOpen && gamesView === 'settlement')}
                              >
                                <span style={{ fontSize: 18 }}>💰</span>Settlement
                              </button>
                              )}
                              <button
                                data-testid="portal-scorecard-btn"
                                onClick={() => setLeaderboardTeetimeId(tt.id)}
                                style={linkBtn(false)}
                              >
                                <span style={{ fontSize: 18 }}>📋</span>Scorecard
                              </button>
                            </div>
                          );
                        })()}
                        {/* Expanded panel */}
                        {panelOpen && (
                          <div style={{ marginTop: 10, background: '#f0fdf4', borderRadius: 8, padding: 12 }}>
                            {gamesLoading && <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Loading...</div>}
                            {!gamesLoading && gamesView === 'standings' && standingsData && (
                              standingsData.games?.length === 0
                                ? <div style={{ fontSize: 13, color: '#6b7280' }}>No results yet — scores needed.</div>
                                : standingsData.games?.map(game => (
                                  <div key={game.game_id} style={{ marginBottom: 12 }}>
                                    {/* Game header with status badge */}
                                    {(() => {
                                      const totals = game.results?.totals || [];
                                      const lastHoles = totals.map(t => t.last_hole_entered).filter(v => v != null);
                                      const maxHole = lastHoles.length > 0 ? Math.max(...lastHoles) : null;
                                      const allCurrent = lastHoles.length > 0 && lastHoles.length === totals.length && lastHoles.every(h => h === maxHole);
                                      const isComplete = game.status === 'completed';
                                      let badge = null;
                                      if (!isComplete && maxHole !== null) {
                                        if (allCurrent) {
                                          badge = <span style={{ fontSize: 10, fontWeight: 600, color: '#166534', background: '#dcfce7', borderRadius: 4, padding: '2px 6px', marginLeft: 6 }}>✓ Current thru H{maxHole}</span>;
                                        } else {
                                          badge = <span style={{ fontSize: 10, fontWeight: 600, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '2px 6px', marginLeft: 6 }}>⏳ Scores pending</span>;
                                        }
                                      }
                                      return (
                                        <div style={{ marginBottom: 6 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                                            <span style={{ fontWeight: 700, fontSize: 15 }}>{game.display_name || game.game_type}</span>
                                            {badge}
                                          </div>
                                          {game.game_type === 'putts' && (() => {
                                            const allPutts = totals.map(t => t.total_putts).filter(v => v != null);
                                            if (!allPutts || allPutts.length === 0) return null;
                                            const avg = Math.round(allPutts.reduce((a, b) => a + b, 0) / allPutts.length);
                                            return <div style={{ fontSize: 13, color: '#6b7280', marginTop: 1 }}>Field avg: {avg} putts</div>;
                                          })()}
                                          {/* Oct 7, 2026 (Dennis): "Money belongs to settlement" — Results shows no dollars. */}
                                        </div>
                                      );
                                    })()}
                                    <SegmentSwitch game={game} noMoney>{(game) => (
                                      // Oct 7, 2026: skins rows by most skins; closest to the pin in hole order
                                      // (players without a win last); other games keep the engine's order.
                                      String(game.game_type || '').startsWith('skins')
                                        ? [...(game.results?.totals || [])].sort((a, b) => (b.skins_won || 0) - (a.skins_won || 0))
                                        : game.game_type === 'ctp'
                                        ? [...(game.results?.totals || [])].sort((a, b) => {
                                            const first = (r) => (r.holes_won && r.holes_won.length ? Math.min(...r.holes_won.map(Number)) : 99);
                                            return first(a) - first(b);
                                          })
                                        : (game.results?.totals || [])
                                    ).map((row, i) => {
                                      // Team game (scramble): rows are teams — name is the team label, the
                                      // viewer's team is highlighted, and the stat is the team's place + score.
                                      const isTeamRow = game.game_type === 'scramble';
                                      const isBestBallRow = isBestBallGameType(game.game_type);   // a team row (Sept 23, 2026; Shamble Sept 28)
                                      const isStrokeRow = game.game_type === 'stroke_play' || isBestBallRow;
                                      const mine = (isTeamRow || isBestBallRow)
                                        ? (row.members || []).some(m => m.player_id === standingsData.player_id)
                                        : row.player_id === standingsData.player_id;
                                      const rowName = isTeamRow ? `${row.place_label ? `${row.place_label}${row.tie_mark ? ` ${row.tie_mark}` : ''} · ` : ''}${row.team_name}${row.tie_status === 'pending' ? ' (playoff pending)' : ''}`
                                        : isStrokeRow ? `${strokePlayPlace(game, row)} · ${row.player_name}` : row.player_name;
                                      const rowKey = isTeamRow ? `team-${row.team_id}` : isBestBallRow ? `side-${row.side_id}` : row.player_id;
                                      const teamStat = isTeamRow
                                        ? (game.results?.basis === 'net'
                                            ? (row.net_total != null ? `net ${row.net_total} (gross ${row.gross_total}${row.team_handicap != null ? `, hcp ${row.team_handicap}` : ''})` : '—')
                                            : (row.gross_total != null ? `gross ${row.gross_total}` : '—')) + (row.thru != null && row.thru < (game.results?.hole_count || 18) ? ` · thru ${row.thru}` : '')
                                        : isStrokeRow ? `${strokePlayStat(game, row)}` : null;   // no prize dollars in Results (Oct 7, 2026)
                                      // Stableford within each group (Oct 7, 2026): a heading per group, as Dots.
                                      const sfGroupHead = game.game_type === 'stableford' && game.results?.mode === 'group' && (i === 0 || (game.results.totals[i - 1] || {}).group_id !== row.group_id)
                                        ? <div style={{ fontSize: 12, fontWeight: 800, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.4, padding: i === 0 ? '2px 0 4px 4px' : '12px 0 4px 4px' }}>{row.in_group ? (row.group_name || 'Group') : 'Not in a group'}</div>
                                        : null;
                                      return (
                                      <React.Fragment key={rowKey}>
                                      {sfGroupHead}
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '8px 0', borderBottom: i < game.results.totals.length - 1 ? '1px solid #d1fae5' : 'none', background: mine ? '#dcfce7' : 'transparent', borderRadius: 4, paddingLeft: 4, alignItems: 'center' }}>
                                        <div>
                                          <span style={{ fontSize: 15, fontWeight: mine ? 700 : 400 }}>{rowName}{mine ? ' 👤' : ''}</span>
                                          {game.status !== 'completed' && row.last_hole_entered != null && (
                                            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>Last entry: H{row.last_hole_entered}</div>
                                          )}
                                        </div>
                                        <span style={{ fontSize: 14, color: '#4b5563', textAlign: 'right' }}>
                                          {teamStat != null ? teamStat : game.game_type === 'stableford'
                                            ? (row.points != null ? `${row.place_label ? `${row.place_label} · ` : ''}${row.points} pts${row.thru != null && row.thru < (game.results?.hole_count || 18) ? ` · thru ${row.thru}` : ''}` : '—')
                                            : row.total_dots != null
                                            ? `${row.total_dots} dot${row.total_dots === 1 ? '' : 's'}${row.breakdown ? ` · ${row.breakdown}` : ''}`
                                            : row.record != null
                                            ? `${(game.game_type === 'sixes' || game.game_type === 'match_play') ? 'Matches' : 'Bets'} ${row.record}`
                                            : row.ctp_won != null
                                            ? (row.ctp_won ? `${row.ctp_won} CTP · ${(row.holes_won || []).map(x => `#${x}`).join(', ')}` : '—')
                                            : row.total_putts != null
                                            ? (() => {
                                                const allPutts = game.results?.totals?.map(t => t.total_putts).filter(v => v != null);
                                                const avg = allPutts?.length ? allPutts.reduce((a, b) => a + b, 0) / allPutts.length : null;
                                                const diff = avg != null ? Math.round(row.total_putts - avg) : null;
                                                return `${row.total_putts} putts${diff != null ? ` (${diff > 0 ? '+' : ''}${diff} vs avg)` : ''}`;
                                              })()
                                            : row.skins_won != null
                                              ? (() => {
                                                  const holesWon = row.skins_won || 0;
                                                  const holesWonList = (game.results?.holes || [])
                                                    .filter(h => h.winner_id === row.player_id)
                                                    // "(carried)": the hole won the skins carried into it (Oct 7, 2026).
                                                    .map(h => `H${h.hole_number}${h.carried_over === true ? ' (carried)' : ''}`)
                                                    .join(', ');
                                                  if (holesWon === 0) return '—';
                                                  return `${holesWon} skin${holesWon !== 1 ? 's' : ''}${holesWonList ? ` · ${holesWonList}` : ''}`;
                                                })()
                                              : '—'}
                                        </span>
                                      </div>
                                      </React.Fragment>
                                      );
                                    })}</SegmentSwitch>
                                    {/* Match cards without the bet (Oct 7, 2026): their dollar line reads the
                                        results' bet_amount, so Results passes it as null — money is in Settlement. */}
                                    {game.game_type === 'nassau' && <NassauMatches game={{ ...game, results: { ...game.results, bet_amount: null } }} viewerId={standingsData.player_id} />}
                                    {game.game_type === 'match_play' && <MatchPlayMatches game={{ ...game, results: { ...game.results, bet_amount: null } }} viewerId={standingsData.player_id} />}
                                    {game.game_type === 'sixes' && <SixesMatches game={{ ...game, results: { ...game.results, bet_amount: null } }} viewerId={standingsData.player_id} />}
                                    {/* Opted-in players with no scores yet (Sept 7, 2026). The engine's
                                        `totals` only carries players who have scored (the Aug 12 scoreless
                                        backstop), so before / early in a round this list was empty even
                                        with a full opt-in roster. `game.participants` is the full roster. */}
                                    {game.status !== 'completed' && game.game_type !== 'scramble' && (() => {
                                      // Best Ball rows are teams (Sept 23, 2026) — its scored players are the teams' members.
                                      const scoredIds = new Set((game.results?.totals || []).flatMap(t => (t.player_id == null && Array.isArray(t.members)) ? t.members.map(m => m.player_id) : [t.player_id]));
                                      const notStarted = (game.participants || []).filter(p => !scoredIds.has(p.player_id));
                                      const nobodyYet = scoredIds.size === 0 && notStarted.length === 0;
                                      if (nobodyYet) {
                                        return <div style={{ fontSize: 12, color: '#9ca3af', padding: '4px 0 0 4px' }}>No one has opted in yet.</div>;
                                      }
                                      return notStarted.map((p, i) => (
                                        <div key={p.player_id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '6px 0', borderTop: scoredIds.size > 0 || i > 0 ? '1px solid #d1fae5' : 'none', background: p.player_id === standingsData.player_id ? '#dcfce7' : 'transparent', borderRadius: 4, paddingLeft: 4 }}>
                                          <span style={{ fontSize: 15, color: '#6b7280', fontWeight: p.player_id === standingsData.player_id ? 700 : 400 }}>{p.player_name}{p.player_id === standingsData.player_id ? ' 👤' : ''}</span>
                                          <span style={{ fontSize: 13, color: '#9ca3af' }}>Not started</span>
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                ))
                            )}
                            {!gamesLoading && gamesView === 'settlement' && (
                              settlementData && settlementData.in_games === false
                                ? (
                                  // Oct 7, 2026 (Dennis): a player who sat out the games sees no one's money.
                                  <div data-testid="settlement-not-in-games" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>No game results for you today</div>
                                    <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.45 }}>You weren't in the games for this round. Join the games next time from your tee time in the app.</div>
                                  </div>
                                )
                              : !settlementData || !settlementData.is_finalized
                                ? <div style={{ fontSize: 13, color: '#6b7280' }}>Settlement not finalized yet.</div>
                                : (
                                  <div>
                                    {/* Full organizer settlement, shared component — identical to
                                        the organizer view (everyone's lines + all scorecards),
                                        rendered from the same <SettlementStatement> / buildPlayerSummary
                                        code so the two views can never drift. Player gets no action
                                        buttons (re-finalize/send/reopen) — only a Close button. */}
                                    <SettlementStatement
                                      games={(standingsData?.games || []).filter(g => g.competitor_scope !== 'team')}
                                      playerSummary={buildPlayerSummary(standingsData ? { ...standingsData, games: (standingsData.games || []).filter(g => g.competitor_scope !== 'team') } : standingsData, { settlements: settlementData.settlements || [] })}
                                      scorecard={standingsData?.scorecard}
                                      meta={{
                                        courseName: tt?.course || '',
                                        dateLabel: tt?.date ? new Date(tt.date.split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '',
                                        finalizedAtLabel: '',
                                      }}
                                      collapsibleScorecards={true}
                                      viewerId={settlementData.player_id || playerData?.id}
                                    />
                                    <button
                                      onClick={() => setShowGamesPanel(null)}
                                      style={{ width: '100%', padding: '10px', marginTop: 8, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                                      Close
                                    </button>
                                  </div>
                                )
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── §6.4 Pending Partners (Group RSVP) ──
                      Captain's nominations for this tee time. Pending rows are
                      held/unconfirmed; confirmed rows = accepted nominees.
                      Declined/expired rows are deleted server-side and just
                      disappear from this list on the next 10s refresh. */}
                  {(captainNominations[tt.id]?.length || 0) > 0 && (
                    <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                        🎟️ Pending Partners
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {captainNominations[tt.id].map(nom => {
                          const isPending = nom.status === 'pending_nomination';
                          const isConfirmed = nom.status === 'confirmed';
                          const expiresAt = nom.nomination_expires_at ? new Date(nom.nomination_expires_at) : null;
                          const hoursRem = expiresAt ? Math.max(0, Math.round((expiresAt - new Date()) / 3600000)) : null;
                          const badgeBg = isConfirmed ? '#dcfce7' : isPending ? '#fef3c7' : '#fee2e2';
                          const badgeFg = isConfirmed ? '#166534' : isPending ? '#92400e' : '#991b1b';
                          const badgeLabel = isConfirmed ? '✓ Confirmed' : isPending ? '⏳ Awaiting' : nom.status;
                          return (
                            <div key={nom.ttp_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                              <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>
                                {nom.player_name}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11, padding: '2px 8px', background: badgeBg, color: badgeFg, borderRadius: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {badgeLabel}
                                </span>
                                {isPending && hoursRem !== null && (
                                  <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
                                    {hoursRem}h remaining
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="card-actions" style={{ marginTop: 12 }}>
                    <button 
                      className="btn-primary"
                      onClick={() => handleViewTeeTime(tt)}
                    >
                      👁️ View Details
                    </button>
                    {/* Live Leaderboard entry — shown only when this round is live. */}
                    {tt.live && (
                      <button
                        onClick={() => setLeaderboardTeetimeId(tt.id)}
                        style={{ padding: '10px 16px', background: '#166534', color: 'white', border: 'none',
                          borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        title="View the live leaderboard for this round"
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#86efac', display: 'inline-block' }}></span>
                        Live — View Leaderboard
                      </button>
                    )}
                    {/* Scorecard (Oct 7, 2026 — Dennis): once a round has scores, games or not, the
                        whole field's board with the Gross / Net switch. Rounds with games have this
                        button in their Results / Settlement / Scorecard row instead. */}
                    {!tt.live && tt.has_scores && !(portalGamesEnabled && teeTimeGames[tt.id] && teeTimeGames[tt.id].length > 0) && (
                      <button
                        data-testid="portal-scorecard-plain-btn"
                        onClick={() => setLeaderboardTeetimeId(tt.id)}
                        style={{ padding: '10px 16px', minHeight: 44, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                          borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        title="Everyone's scores for this round, gross or net"
                      >
                        📋 Scorecard
                      </button>
                    )}
                    <AddToCalendarButton teeTimeId={tt.id} playerId={playerData?.id} compact={true} addToast={addToast} />
                    {/* §6.3 Add Playing Partners — gated by can_nominate, open slots, and confirmation-window cutoff */}
                    {canShowAddPartners(tt) && (
                      <button
                        onClick={() => handleOpenAddPartners(tt)}
                        style={{
                          padding: '10px 16px',
                          background: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                        title="Reserve spots for other players"
                      >
                        ➕ Add Playing Partners
                      </button>
                    )}
                    <button 
                      className="btn-danger"
                      onClick={() => confirmCancel(tt.id)}
                      title="Cancel your spot in this tee time"
                    >
                      {isWaitlisted ? 'Leave Waitlist' : 'Cancel My Spot'}
                    </button>
                  </div>

                </Card>
                
                {/* In-Feed Ad (shows after every 2nd tee time, max 2) */}
                {adToShowMyTimes && (
                  <InFeedAd 
                    ad={adToShowMyTimes}
                    organizationId={playerData?.group_id}
                    playerId={playerData?.id}
                  />
                )}
                </React.Fragment>
              );
            })}
          </div>
          );
        })()}
      </Card>

      {/* Portal Rules Popup — outside tee time loop so Got It closes correctly */}
      {portalRulesGame && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 440, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 12 }}>How {portalRulesGame.display_name || portalRulesGame.game_type} Works</div>
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 }}>
              {portalRulesGame.rules_text || 'No rules text available.'}
            </p>
            <button
              onClick={() => setPortalRulesGame(null)}
              style={{ marginTop: 20, width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {/* Player List Modal */}
      {/* Player What's New modal (opened by the header bell) */}
      {showWhatsNew && (
        <PlayerWhatsNewModal items={whatsNewItems} onClose={() => setShowWhatsNew(false)} />
      )}

      {/* Live Leaderboard modal (opened by "Live – View Leaderboard" buttons) */}

      {leaderboardTeetimeId && (
        <LeaderboardModal
          token={token}
          teetimeId={leaderboardTeetimeId}
          viewerId={playerData?.id}
          onClose={() => setLeaderboardTeetimeId(null)}
        />
      )}

      {/* Round Recap (Oct 3, 2026) */}
      {recapOpen && (
        <RoundRecapModal
          token={token}
          teetimeId={recapOpen.teetimeId}
          initialRecap={recapOpen.recap || null}
          onClose={() => setRecapOpen(null)}
          onOpenLeaderboard={(id) => { setRecapOpen(null); setLeaderboardTeetimeId(id); }}
        />
      )}

      {viewingTeeTime && (
        <div 
          style={{ 
            position: "fixed", 
            inset: 0, 
            background: "rgba(0,0,0,.5)", 
            backdropFilter: "blur(4px)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            zIndex: 50,
            padding: 20
          }} 
          onClick={() => setViewingTeeTime(null)}
        >
          <Card 
            style={{ 
              width: "100%", 
              maxWidth: 500, 
              maxHeight: "85vh", 
              display: "flex", 
              flexDirection: "column",
              overflow: "hidden"
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: 16, flexShrink: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>
                {viewingTeeTime.course_name}
              </div>
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                {utils.formatDate(viewingTeeTime.date)} at {utils.formatTime(viewingTeeTime.time)}
              </div>
              {/* Live Leaderboard entry — shown only when this round is live. */}
              {viewingTeeTime.live && (
                <button
                  onClick={() => { setLeaderboardTeetimeId(viewingTeeTime.id); setViewingTeeTime(null); }}
                  style={{ marginTop: 12, width: '100%', padding: '10px 0', background: '#166534', color: '#fff',
                    border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#86efac', display: 'inline-block' }}></span>
                  Live — View Leaderboard
                </button>
              )}
            </div>

            {/* Scrollable Content Area */}
            <div style={{ flex: 1, overflowY: "auto", marginBottom: 16, minHeight: 0 }}>
            {/* Organizer note for this tee time. Rendered only when non-empty — no
                blank heading on the majority of tee times that have none. */}
            {viewingTeeTime.notes && String(viewingTeeTime.notes).trim() && (
              <div style={{ background: '#fffbeb', borderLeft: '4px solid #d97706', borderRadius: 4, padding: '10px 12px', marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: '#92400e', marginBottom: 4 }}>📋 Note from your organizer</div>
                <div style={{ fontSize: 13, color: '#1f2937', whiteSpace: 'pre-wrap' }}><LinkifiedText text={String(viewingTeeTime.notes).trim()} /></div>
              </div>
            )}
            {/* Playing Section */}
            {(() => {
              const allPlayers = Array.isArray(viewingTeeTime.players) ? viewingTeeTime.players : [];

              // Grouped-by-pairings view activates ONLY when the organizer has saved
              // pairings (some confirmed player has a non-null pairing_group). The
              // pairing fields are only present on the /my-teetimes payload (a player's
              // own confirmed reservation), so the "Available Tee Times" view — which is
              // fed by a different endpoint — never has pairing_group and always falls
              // through to the flat RSVP-ordered list below. That matches the rule that
              // only players who are actually playing need to see pairings.
              const hasPairings = allPlayers.some(
                p => typeof p === 'object' && p.pairing_group != null
              );
              const isShotgun = viewingTeeTime.shotgun_mode === true;
              const groupTimes = viewingTeeTime.pairing_group_times || {};

              // Renders one player row. Shared by both the flat and grouped views so the
              // avatar / pending-nomination / "(You)" logic lives in exactly one place.
              const renderPlayerRow = (player, idx, lastInList, showBadges) => {
                const isPending = typeof player === 'object' && player.status === 'pending_nomination';
                const teeName = typeof player === 'object' ? player.tee_name : null;
                const courseHcp = typeof player === 'object' ? player.course_hcp : null;
                const teeUnmatched = typeof player === 'object' ? player.tee_unmatched : false;
                const hasHandicap = typeof player === 'object' ? player.has_handicap : false;
                const isSelf = typeof player === 'object' && player.email && player.email === playerData?.email;
                return (
                  <div
                    key={idx}
                    style={{
                      padding: "10px 12px",
                      borderBottom: !lastInList ? "1px solid #f3f4f6" : "none",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      opacity: isPending ? 0.5 : 1
                    }}
                  >
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <PlayerAvatar
                        name={player.name || player}
                        photoUrl={typeof player === "object" ? player.photoUrl : ""}
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewingPhoto({
                            name: player.name || player,
                            photoUrl: typeof player === "object" ? player.photoUrl : ""
                          });
                        }}
                      />
                      {isPending && (
                        <span
                          aria-label="Invitation pending"
                          style={{
                            position: 'absolute',
                            bottom: -2,
                            right: -2,
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            background: '#fff',
                            border: '1px solid #d1d5db',
                            fontSize: 10,
                            lineHeight: '14px',
                            textAlign: 'center',
                            display: 'inline-block',
                            pointerEvents: 'none'
                          }}
                        >
                          ⏳
                        </span>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, fontStyle: isPending ? 'italic' : 'normal' }}>
                        {player.name || player}
                        {isPending && (
                          <span style={{ fontWeight: 400, color: '#6b7280', fontStyle: 'italic' }}>
                            {' — Invited'}
                          </span>
                        )}
                        {samePlayer(player, playerData) && (
                          <span style={{ fontSize: 12, color: "#2563eb", marginLeft: 6 }}>(You)</span>
                        )}
                        {/* Oct 26, 2026: the team captain — who to ask about the team */}
                        {typeof player === 'object' && player.captain === true && (
                          <span data-testid="player-captain" style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginLeft: 6 }}>(Captain)</span>
                        )}
                      </div>
                      {/* Grouped view: confident tee + course HCP badges. When the tee
                          couldn't be matched to this course we show no handicap (rather
                          than a guessed, wrong one). The viewing player gets an inline
                          "set your tee" fix on their OWN row; other players' rows simply
                          omit the badge. */}
                      {showBadges ? (
                        isPending ? null : (
                          !teeUnmatched && (teeName || courseHcp != null) ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                              {teeName && (
                                <span style={{ background: '#FAEEDA', color: '#854F0B', fontSize: 11, padding: '2px 7px', borderRadius: 6 }}>
                                  {teeName} tees
                                </span>
                              )}
                              {courseHcp != null && (
                                <span style={{ background: '#f3f4f6', color: '#6b7280', fontSize: 11, padding: '2px 7px', borderRadius: 6 }}>
                                  Course HCP: {courseHcp}
                                </span>
                              )}
                            </div>
                          ) : isSelf && hasHandicap ? (
                            <div style={{ marginTop: 4 }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTeeFixContext({
                                    courseName: viewingTeeTime.course || viewingTeeTime.course_name,
                                    returnTeeTime: viewingTeeTime
                                  });
                                  setTeeFixSelectedId('');
                                }}
                                style={{
                                  background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                                  fontSize: 11, padding: '3px 9px', borderRadius: 6, cursor: 'pointer',
                                  fontWeight: 600
                                }}
                              >
                                ⛳ Set your tee
                              </button>
                            </div>
                          ) : null
                        )
                      ) : (
                        player.email && (
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{player.email}</div>
                        )
                      )}
                    </div>
                  </div>
                );
              };

              let headerLabel;
              let body;

              if (hasPairings) {
                // Build ordered groups. Players without a pairing_group (edge case:
                // partial pairings) collect under a trailing "Unassigned" bucket so
                // nobody silently disappears from the list.
                const groupsMap = {};
                const unassigned = [];
                allPlayers.forEach(p => {
                  if (typeof p === 'object' && p.pairing_group != null) {
                    (groupsMap[p.pairing_group] = groupsMap[p.pairing_group] || []).push(p);
                  } else {
                    unassigned.push(p);
                  }
                });

                const sortWithin = (arr) => [...arr].sort((a, b) => {
                  const ao = (a && a.sort_order != null) ? a.sort_order : 9999;
                  const bo = (b && b.sort_order != null) ? b.sort_order : 9999;
                  if (ao !== bo) return ao - bo;
                  return String(a?.name || '').localeCompare(String(b?.name || ''));
                });

                const groupNums = Object.keys(groupsMap).map(Number).sort((a, b) => {
                  if (isShotgun) {
                    const ha = groupsMap[a][0]?.starting_hole ?? 999;
                    const hb = groupsMap[b][0]?.starting_hole ?? 999;
                    if (ha !== hb) return ha - hb;
                  }
                  return a - b;
                });

                const groupCount = groupNums.length;
                headerLabel = isShotgun
                  ? `✓ Playing (${allPlayers.length}) · shotgun start, ${groupCount} ${groupCount === 1 ? 'group' : 'groups'}`
                  : `✓ Playing (${allPlayers.length}) · paired into ${groupCount} ${groupCount === 1 ? 'group' : 'groups'}`;

                body = (
                  <div style={{ display: "grid", gap: 0 }}>
                    {groupNums.map((gn) => {
                      const members = sortWithin(groupsMap[gn]);
                      const startHole = members[0]?.starting_hole;
                      const slotLabel = isShotgun
                        ? `${startHole != null ? holeName(startHole, ninesOf(viewingTeeTime)) : '?'}${members[0]?.group_label || ''}`
                        : String(gn);
                      const gTime = groupTimes[gn] || groupTimes[String(gn)];
                      const headerBase = isShotgun
                        ? `${startHole != null ? holeText(startHole, ninesOf(viewingTeeTime)) : 'Hole ?'}${gTime ? ` · ${utils.formatTime(gTime)} shotgun` : ' shotgun'}`
                        : `Group ${gn}${gTime ? ` · ${utils.formatTime(gTime)}` : ''}`;
                      // Nov 2, 2026: a group that is one registered team shows its team name.
                      const groupTeams = [...new Set(members.map(m => (m && m.team_name) || '').filter(Boolean))];
                      const headerText = groupTeams.length === 1 ? `${headerBase} · ${groupTeams[0]}` : headerBase;
                      return (
                        <div key={`g-${gn}`}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '8px 12px', background: '#eff6ff',
                            borderTop: '1px solid #dbeafe', borderBottom: '1px solid #dbeafe'
                          }}>
                            <span style={{
                              background: '#1e40af', color: '#fff', fontSize: 12, fontWeight: 700,
                              minWidth: 22, height: 22, borderRadius: '50%', display: 'inline-flex',
                              alignItems: 'center', justifyContent: 'center', padding: '0 5px'
                            }}>{slotLabel}</span>
                            <span style={{
                              fontSize: 12, fontWeight: 700, color: '#1e40af',
                              textTransform: 'uppercase', letterSpacing: '0.6px'
                            }}>{headerText}</span>
                          </div>
                          {members.map((player, i) =>
                            renderPlayerRow(player, `${gn}-${i}`, i === members.length - 1, true)
                          )}
                        </div>
                      );
                    })}
                    {unassigned.length > 0 && (
                      <div key="g-unassigned">
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 12px', background: '#f9fafb',
                          borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb'
                        }}>
                          <span style={{
                            fontSize: 12, fontWeight: 700, color: '#6b7280',
                            textTransform: 'uppercase', letterSpacing: '0.6px'
                          }}>Unassigned</span>
                        </div>
                        {sortWithin(unassigned).map((player, i) =>
                          renderPlayerRow(player, `u-${i}`, i === unassigned.length - 1, true)
                        )}
                      </div>
                    )}
                  </div>
                );
              } else {
                // No pairings saved (or Available Tee Times view): flat RSVP-ordered
                // list, identical to prior behavior.
                headerLabel = `✓ Playing (${allPlayers.length})`;
                const sorted = [...allPlayers].sort((a, b) => {
                  const dateA = a?.addedAt ? new Date(a.addedAt) : new Date(0);
                  const dateB = b?.addedAt ? new Date(b.addedAt) : new Date(0);
                  return dateA - dateB;
                });
                // Nov 2, 2026 (Dennis — Turkey Shoot: "The player app does not group players
                // by teams. It should"): before pairings are shared, an event with registered
                // teams lists its field team by team — in the team list's order, captain
                // first, then by name — with anyone not on a team at the end.
                const teamKeyOf = (p) => (typeof p === 'object' && p && p.team_name) ? p.team_name : '';
                const hasTeams = allPlayers.some(p => teamKeyOf(p));
                if (hasTeams && allPlayers.length > 0) {
                  const byTeam = new Map();
                  const noTeam = [];
                  allPlayers.forEach(p => {
                    const k = teamKeyOf(p);
                    if (!k) { noTeam.push(p); return; }
                    if (!byTeam.has(k)) byTeam.set(k, { name: k, order: Number.isFinite(Number(p.team_order)) ? Number(p.team_order) : 9999, members: [] });
                    byTeam.get(k).members.push(p);
                  });
                  const teams = [...byTeam.values()].sort((a, b) => (a.order - b.order) || a.name.localeCompare(b.name));
                  const sortTeam = (arr) => [...arr].sort((a, b) => {
                    if ((a?.captain === true) !== (b?.captain === true)) return a?.captain === true ? -1 : 1;
                    return String(a?.name || '').localeCompare(String(b?.name || ''));
                  });
                  headerLabel = `✓ Playing (${allPlayers.length}) · ${teams.length} ${teams.length === 1 ? 'team' : 'teams'}`;
                  const teamHeader = (label, count, muted) => (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', background: muted ? '#f9fafb' : '#f0fdf4',
                      borderTop: `1px solid ${muted ? '#e5e7eb' : '#bbf7d0'}`, borderBottom: `1px solid ${muted ? '#e5e7eb' : '#bbf7d0'}`
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: muted ? '#6b7280' : '#14532d' }}>{muted ? label : `👥 ${label}`}</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>({count})</span>
                    </div>
                  );
                  body = (
                    <div style={{ display: "grid", gap: 0 }} data-testid="player-list-by-team">
                      {teams.map((t) => {
                        const members = sortTeam(t.members);
                        return (
                          <div key={`t-${t.name}`}>
                            {teamHeader(t.name, members.length, false)}
                            {members.map((player, i) => renderPlayerRow(player, `${t.name}-${i}`, i === members.length - 1, false))}
                          </div>
                        );
                      })}
                      {noTeam.length > 0 && (
                        <div key="t-none">
                          {teamHeader('Not on a team yet', noTeam.length, true)}
                          {sortTeam(noTeam).map((player, i) => renderPlayerRow(player, `nt-${i}`, i === noTeam.length - 1, false))}
                        </div>
                      )}
                    </div>
                  );
                } else
                body = allPlayers.length > 0 ? (
                  <div style={{ display: "grid", gap: 0 }}>
                    {sorted.map((player, idx) =>
                      renderPlayerRow(player, idx, idx === sorted.length - 1, false)
                    )}
                  </div>
                ) : (
                  <div style={{ padding: 20, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                    No players yet
                  </div>
                );
              }

              return (
                <div style={{ marginBottom: 20 }}>
                  <div style={{
                    background: "#dcfce7",
                    padding: "6px 12px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#15803d",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    borderRadius: "6px 6px 0 0"
                  }}>
                    {headerLabel}
                  </div>
                  <div style={{ border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 6px 6px" }}>
                    {body}
                  </div>
                </div>
              );
            })()}

            {/* Waitlist Section */}
            {viewingTeeTime.waitlist && viewingTeeTime.waitlist.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ 
                  background: "#fef3c7", 
                  padding: "6px 12px", 
                  fontSize: 11, 
                  fontWeight: 700, 
                  color: "#92400e", 
                  textTransform: "uppercase", 
                  letterSpacing: "0.5px",
                  borderRadius: "6px 6px 0 0"
                }}>
                  ⏳ Waitlist ({viewingTeeTime.waitlist.length})
                </div>
                <div style={{ border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 6px 6px" }}>
                  <div style={{ display: "grid", gap: 0 }}>
                    {/* Sort waitlist by position (FIFO order) */}
                    {(() => {
                      const sortedWaitlist = Array.isArray(viewingTeeTime.waitlist) 
                        ? [...viewingTeeTime.waitlist].sort((a, b) => {
                            // If waitlist contains objects with position property, sort by that
                            const posA = typeof a === 'object' && a.position ? a.position : 999;
                            const posB = typeof b === 'object' && b.position ? b.position : 999;
                            return posA - posB;
                          })
                        : [];
                      
                      return sortedWaitlist.map((player, idx) => {
                        const position = typeof player === 'object' && player.position ? player.position : (idx + 1);
                        
                        return (
                          <div 
                            key={typeof player === 'object' ? player.id : idx}
                            style={{ 
                              padding: "10px 12px", 
                              borderBottom: idx < sortedWaitlist.length - 1 ? "1px solid #f3f4f6" : "none",
                              display: "flex",
                              alignItems: "center",
                              gap: 10
                            }}
                          >
                            {/* Position Badge */}
                            <div style={{
                              minWidth: 28,
                              height: 28,
                              borderRadius: "50%",
                              background: "#f59e0b",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 12,
                              fontWeight: 700
                            }}>
                              #{position}
                            </div>
                            
                            <PlayerAvatar 
                              name={player.name || player} 
                              photoUrl={typeof player === "object" ? player.photoUrl : ""}
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewingPhoto({ 
                                  name: player.name || player, 
                                  photoUrl: typeof player === "object" ? player.photoUrl : "" 
                                });
                              }}
                            />
                            
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {player.name || player}
                                {samePlayer(player, playerData) && (
                                  <span style={{ fontSize: 12, color: "#2563eb", marginLeft: 6 }}>(You)</span>
                                )}
                              </div>
                              {player.email && (
                                <div style={{ fontSize: 11, color: "#9ca3af" }}>{player.email}</div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            )}

            </div>{/* End Scrollable Content Area */}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="outline" onClick={() => setViewingTeeTime(null)}>
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Inline "set your tee" quick-fix modal — opened from View Details when the
          player's preferred tee couldn't be matched to the course being played. Tee
          selection only (handicap stays read-only; many orgs run games/tournaments and
          don't want last-minute handicap edits). Saving updates the profile-wide
          preferred_tee, then View Details reopens with the resolved course handicap. */}
      {teeFixContext && (() => {
        const courseName = teeFixContext.courseName;
        const courseTees = availableTees.filter(t => t.course_name === courseName);
        const hcpRaw = playerData?.handicap ?? playerData?.hcp;
        const hasHcp = hcpRaw !== undefined && hcpRaw !== null && hcpRaw !== '' && !isNaN(parseFloat(hcpRaw));
        const selectedTee = courseTees.find(t => t.id === teeFixSelectedId);
        // WHS course handicap, same formula/rounding as the tee sheet (App.js ~10112).
        let previewHcp = null;
        if (hasHcp && selectedTee && selectedTee.slope_rating && selectedTee.course_rating && selectedTee.par) {
          previewHcp = Math.round(
            (parseFloat(hcpRaw) * selectedTee.slope_rating / 113) +
            (parseFloat(selectedTee.course_rating) - selectedTee.par)
          );
        }
        return (
          <div
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", backdropFilter: "blur(4px)",
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20
            }}
            onClick={() => { if (!teeFixSaving) { setTeeFixContext(null); setTeeFixSelectedId(''); } }}
          >
            <Card style={{ width: "100%", maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>⛳ Set your tee</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                We couldn't match your preferred tee to {courseName}. Pick the tee you're playing
                to show your course handicap.
              </div>

              <div style={{ fontSize: 13, marginBottom: 12 }}>
                <span style={{ color: "#6b7280" }}>Your handicap index: </span>
                {hasHcp
                  ? <span style={{ fontWeight: 700 }}>{parseFloat(hcpRaw)}</span>
                  : <span style={{ color: "#9ca3af", fontStyle: "italic" }}>not on file</span>}
              </div>

              {courseTees.length > 0 ? (
                <>
                  <Label>Tee</Label>
                  <Select
                    value={teeFixSelectedId}
                    onChange={(e) => setTeeFixSelectedId(e.target.value)}
                  >
                    <option value="">-- Select your tee --</option>
                    {courseTees.map(tee => (
                      <option key={tee.id} value={tee.id}>{tee.tee_name}</option>
                    ))}
                  </Select>

                  {!hasHcp && (
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>
                      Add a handicap index in your Profile to see your course handicap.
                    </div>
                  )}
                  {hasHcp && selectedTee && previewHcp != null && (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8, marginTop: 12,
                      padding: "8px 12px", background: "#f0fdf4", borderRadius: 6, border: "1px solid #bbf7d0"
                    }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 4,
                        background: selectedTee.tee_color || '#000',
                        border: selectedTee.tee_color === '#f3f4f6' ? '1px solid #d1d5db' : 'none'
                      }} />
                      <span style={{ fontSize: 13, color: "#166534" }}>
                        Course handicap from {selectedTee.tee_name}: <strong>{previewHcp}</strong>
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 13, color: "#9ca3af", fontStyle: "italic", padding: "8px 0" }}>
                  No tees are configured for {courseName} yet.
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
                <Button variant="outline" onClick={() => { setTeeFixContext(null); setTeeFixSelectedId(''); }} disabled={teeFixSaving}>
                  Cancel
                </Button>
                <Button onClick={saveTeeFix} disabled={!teeFixSelectedId || teeFixSaving}>
                  {teeFixSaving ? 'Saving…' : 'Save tee'}
                </Button>
              </div>
            </Card>
          </div>
        );
      })()}

      {/* Pending Invitations — someone has reserved you a spot (Group RSVP, April 2026) */}
      {pendingInvitations.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Card>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16 }}>
              ✉️ Pending Invitations
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {pendingInvitations.map(nom => {
                const expiresAt = nom.nomination_expires_at ? new Date(nom.nomination_expires_at) : null;
                const hoursRemaining = expiresAt ? Math.max(0, Math.round((expiresAt - new Date()) / 3600000)) : null;
                return (
                  <div
                    key={nom.ttp_id}
                    style={{
                      background: "#fffbeb",
                      border: "1px solid #fcd34d",
                      borderRadius: 8,
                      padding: 14,
                      display: "grid",
                      gap: 10
                    }}
                  >
                    <div style={{ fontSize: 14, color: "#111827", lineHeight: 1.5 }}>
                      <strong>{nom.captain_name || 'A player'}</strong>
                      {' has reserved a spot for you at '}
                      <strong>{nom.course_name || 'a course'}</strong>
                      {' on '}
                      <strong>{utils.formatDate(nom.date)}</strong>
                      {' at '}
                      <strong>{utils.formatTime(nom.time)}</strong>.
                    </div>
                    {hoursRemaining !== null && (
                      <div style={{ fontSize: 12, color: "#92400e" }}>
                        ⏳ A response is needed within {hoursRemaining} hour{hoursRemaining !== 1 ? 's' : ''} or the spot will be released.
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => handleAcceptInvitation(nom)}
                        style={{
                          background: "#059669",
                          color: "white",
                          border: "none",
                          borderRadius: 6,
                          padding: "8px 16px",
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        ✓ Accept
                      </button>
                      <button
                        onClick={() => handleDeclineInvitation(nom)}
                        style={{
                          background: "white",
                          color: "#6b7280",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          padding: "8px 16px",
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Available Tee Times */}
      <Card>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16 }}>
          🏌️ Available Tee Times
        </div>
        {(() => {
          // Filter out tee times where player is already registered
          // Check both players array and compare with myTeeTimes
          const myTeeTimeIds = new Set(myTeeTimes.map(tt => tt.id));
          const pendingInvitationTeeTimeIds = new Set(pendingInvitations.map(n => n.teetime_id));
          const availableTeeTimes = teeTimes.filter(tt => {
            // Don't show if player is already registered
            if (myTeeTimeIds.has(tt.id)) return false;
            // Don't show if player has a pending invitation for this tee time
            // (captain's invite lives in Pending Invitations above, not here)
            if (pendingInvitationTeeTimeIds.has(tt.id)) return false;
            
            // Don't show if player is in the players array
            if (Array.isArray(tt.players)) {
              const playerInList = tt.players.some(p => 
                p && playerData && samePlayer(p, playerData)
              );
              if (playerInList) return false;
            }
            
            return true;
          });
          
          return availableTeeTimes.length === 0 ? (
            <div>
              {/* Show an ad even in empty state */}
              {inFeedAds.length > 0 && (
                <InFeedAd 
                  ad={inFeedAds[0]}
                  organizationId={playerData?.group_id}
                  playerId={playerData?.id}
                />
              )}
              <div style={{ textAlign: "center", padding: 32, color: "#6b7280" }}>
                No available tee times at the moment. Check back later!
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {/* Always show first ad at the top of available tee times */}
              {inFeedAds.length > 0 && (
                <InFeedAd 
                  ad={inFeedAds[0]}
                  organizationId={playerData?.group_id}
                  playerId={playerData?.id}
                />
              )}
              {[...availableTeeTimes]
                .sort((a, b) => {
                  // Sort by date first (earliest first)
                  const dateA = new Date(a.date);
                  const dateB = new Date(b.date);
                  if (dateA.getTime() !== dateB.getTime()) {
                    return dateA.getTime() - dateB.getTime();
                  }
                  // If dates are the same, sort by time
                  const timeA = a.time || '00:00:00';
                  const timeB = b.time || '00:00:00';
                  return timeA.localeCompare(timeB);
                })
                .map((tt, index) => {
              // Calculate player count from players array - sort by RSVP time
              const players = [...(tt.players || [])].sort((a, b) => {
                const dateA = a?.addedAt ? new Date(a.addedAt) : new Date(0);
                const dateB = b?.addedAt ? new Date(b.addedAt) : new Date(0);
                return dateA - dateB;
              });
              const playerCount = Array.isArray(players) 
                ? players.filter(p => p && p.id).length 
                : (tt.player_count || 0);
              
              // Calculate waitlist count
              const waitlist = tt.waitlist || [];
              const waitlistCount = Array.isArray(waitlist) ? waitlist.length : (tt.waitlist_count || 0);
              
              const spotsLeft = tt.slots - playerCount;
              
              // Calculate days until tee time using string comparison to avoid timezone issues
              const ttDateStr = tt.date?.split('T')[0];
              const todayStr = new Date().toLocaleDateString('en-CA');
              const daysUntil = Math.round((new Date(ttDateStr + 'T12:00:00') - new Date(todayStr + 'T12:00:00')) / (1000 * 60 * 60 * 24));
              
              // Determine if we should show an ad after this tee time
              // Show ad after every 2nd tee time, starting from inFeedAds[1] since [0] is shown at top
              const adIndex = Math.floor((index + 1) / 2);
              const shouldShowAd = (index + 1) % 2 === 0 && adIndex < inFeedAds.length;
              const adToShow = shouldShowAd ? inFeedAds[adIndex] : null;
              
              return (
                <React.Fragment key={tt.id}>
                <Card id={`teetime-${tt.id}`} style={{ border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 600, fontSize: 16 }}>{tt.course || tt.course_name}</div>
                        {tt.display_title && (
                          <span
                            title={tt.display_title}
                            style={{
                              display: "inline-block", background: "#dcfce7", color: "#166534",
                              padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                              // Sept 22, 2026: no calendar icon here — the date line directly
                              // below carries one, and two on a card reads as a mistake. And one
                              // line: the badge is a label, not a paragraph. A title longer than
                              // the card ellipsises rather than wrapping (full text in `title`).
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              maxWidth: "100%",
                            }}
                          >
                            {tt.display_title}
                          </span>
                        )}
                      </div>
                      {/* Bold date and time + org name (shown uniformly for every row; backend sends display name, falling back to full org name) */}
                      <div style={{ fontSize: 13, color: "#111827", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", fontWeight: 700 }}>
                        <span>📅 {utils.formatDate(tt.date)} • {utils.formatTime(tt.time)}{tt.organization_name ? <> • <span style={{ fontWeight: 400, color: "#6b7280" }}>{tt.organization_name}</span></> : null}</span>
                      </div>
                      {/* Player count with slots and waitlist on same line */}
                      <div style={{ fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                        <span>👥 {playerCount} player{playerCount !== 1 ? 's' : ''} / {tt.slots} slot{tt.slots !== 1 ? 's' : ''}</span>
                        {waitlistCount > 0 && (
                          <>
                            <span>•</span>
                            <span>⏳ Waitlist: {waitlistCount}</span>
                          </>
                        )}
                        {spotsLeft > 0 && (
                          <>
                            <span>•</span>
                            <span style={{ color: "#15803d" }}>
                              ✓ {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} available
                            </span>
                          </>
                        )}
                        {spotsLeft <= 0 && (
                          <>
                            <span>•</span>
                            <span style={{ color: "#dc2626" }}>Full</span>
                          </>
                        )}
                        {hasNote(tt) && (
                          <>
                            <span>•</span>
                            <NoteIcon onClick={() => handleViewTeeTime(tt)} />
                          </>
                        )}
                      </div>
                      {tt.weather && daysUntil <= 15 && (
                        <div style={{ fontSize: 12, color: "#1e40af", marginTop: 6, background: "#eff6ff", padding: "4px 8px", borderRadius: 4, display: "inline-block", whiteSpace: "pre-line" }}>
                          🌤️ {tt.weather}
                        </div>
                      )}
                      {daysUntil > 15 && (
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6, fontStyle: "italic" }}>
                          Weather Forecast: Outside of 15 day forecast window
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexDirection: "row" }}>
                      <Button 
                        variant="outline"
                        onClick={() => handleViewTeeTime(tt)}
                        style={{ fontSize: 13, whiteSpace: "nowrap" }}
                      >
                        👥 View
                      </Button>
                      <Button 
                        onClick={() => handleRSVP(tt.id)}
                        style={{ fontSize: 13 }}
                      >
                        Reserve My Spot
                      </Button>
                    </div>
                  </div>
                </Card>
                
                {/* In-Feed Ad (shows after every 2nd tee time, max 3) */}
                {adToShow && (
                  <InFeedAd 
                    ad={adToShow}
                    organizationId={playerData?.group_id}
                    playerId={playerData?.id}
                  />
                )}
                </React.Fragment>
              );
            })}
          </div>
          );
        })()}
      </Card>

      {/* RSVP Confirmation Dialog */}
      {confirmRSVPId && (
        <ConfirmDialog 
          title="Confirm RSVP?"
          message={(() => {
            const teeTime = teeTimes.find(tt => tt.id === confirmRSVPId);
            if (!teeTime) return "Are you sure you want to RSVP for this tee time?";
            // Calculate player count from players array
            const playerCount = Array.isArray(teeTime.players) 
              ? teeTime.players.filter(p => p && p.id).length 
              : 0;
            const spotsLeft = teeTime.slots - playerCount;
            // Backend returns 'course' not 'course_name'
            const courseName = teeTime.course || teeTime.course_name || 'Course';
            return `${courseName} on ${utils.formatDate(teeTime.date)} at ${utils.formatTime(teeTime.time)}. You will be added to the player list (${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} available).`;
          })()}
          onConfirm={executeRSVP}
          onCancel={() => setConfirmRSVPId(null)}
          confirmText="Confirm"
          confirmVariant="default"
        />
      )}

      {/* Cancel Confirmation Dialog */}
      {confirmCancelId && (
        <ConfirmDialog 
          title="Cancel RSVP?"
          message="Are you sure you want to remove your name from this tee time?"
          confirmText="Remove Me"
          onConfirm={executeCancelRSVP}
          onCancel={() => setConfirmCancelId(null)}
        />
      )}
      
      {/* Photo Viewer Modal */}
      <PhotoViewerModal
        isOpen={!!viewingPhoto}
        playerName={viewingPhoto?.name || ""}
        photoUrl={viewingPhoto?.photoUrl || ""}
        onClose={() => setViewingPhoto(null)}
      />
      
      {/* RSVP Success Modal with Ads */}
      {myTeamTeetime && (
        <MyTeam
          apiUrl={API_URL}
          token={token}
          teetimeId={myTeamTeetime}
          onClose={() => setMyTeamTeetime(null)}
          onChanged={(d) => setMyTeeTimes(list => (Array.isArray(list) ? list.map(x => (x.id === myTeamTeetime && x.captain_team)
            ? { ...x, captain_team: { ...x.captain_team, size: d.size, filled: d.members.length, complete: d.complete_count, locked: d.locked } }
            : x) : list))}
        />
      )}

      {showRSVPSuccessModal && lastRSVPTeeTime && (
        <RSVPSuccessModal
          teeTime={lastRSVPTeeTime}
          ads={confirmationAds}
          organizationId={playerData?.group_id}
          playerId={playerData?.id}
          onClose={() => {
            setShowRSVPSuccessModal(false);
            setLastRSVPTeeTime(null);
            addToast('RSVP confirmed!');
          }}
          formatDate={utils.formatDate}
          formatTime={utils.formatTime}
        />
      )}
      
      {/* Share Modal */}
      {showShareModal && (
        <PlayerShareModal 
          token={token} 
          playerData={playerData} 
          onClose={() => setShowShareModal(false)} 
        />
      )}

      {/* Tee Time Overlap Conflict Modal */}
      {showOverlapModal && overlapConflict && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: 32, maxWidth: 480,
            width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h3 style={{ margin: '0 0 16px 0', color: '#b91c1c', fontSize: 20 }}>
              Tee Time Conflict
            </h3>
            <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 24px 0', fontSize: 15 }}>
              {overlapConflict.message}
            </p>
            <button
              onClick={() => { setShowOverlapModal(false); setOverlapConflict(null); }}
              style={{
                padding: '10px 32px', backgroundColor: '#166534', color: 'white',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15,
                fontWeight: 600
              }}
            >
              OK, Got It
            </button>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────
          §6.3 Add Playing Partners Modal (Group RSVP)
          Two views:
            (A) form view — row builder with native <select> dropdowns
            (B) success view — lists invitee names, Done button to close
          Visibility: open when addPartnersTeetime is set.
          ────────────────────────────────────────────────────────────────────── */}
      {addPartnersTeetime && (() => {
        const tt = addPartnersTeetime;
        const openSlots = computeOpenSlots(tt);
        const windowHours = playerData?.group_rsvp_confirmation_hours || 24;
        const courseName = tt.course || tt.course_name || 'Golf Course';
        // Already-selected ids (non-empty) — used to disable duplicate picks across rows.
        const pickedIds = new Set(selectedNominees.filter(Boolean));
        const canAddAnother = selectedNominees.length < openSlots;

        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 16
          }}>
            <div style={{
              background: 'white', borderRadius: 12, padding: 24, maxWidth: 520,
              width: '100%', maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              {!nominateSuccess ? (
                <>
                  {/* ── Form view ── */}
                  <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6, color: '#111827' }}>
                    Reserve spots for your playing partners
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
                    {courseName} on <strong>{utils.formatDate(tt.date)}</strong> at <strong>{utils.formatTime(tt.time)}</strong>.
                    You can add up to <strong>{openSlots}</strong> player{openSlots !== 1 ? 's' : ''}.
                    They will receive an email and must confirm within <strong>{windowHours}</strong> hour{windowHours !== 1 ? 's' : ''}, or the spot will be released.
                  </div>

                  {eligibleLoading ? (
                    <div style={{ textAlign: 'center', padding: 24, color: '#6b7280', fontSize: 14 }}>
                      Loading eligible players…
                    </div>
                  ) : eligibleNominees.length === 0 ? (
                    <div style={{ padding: 16, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: 14, marginBottom: 16 }}>
                      No eligible players available to nominate — everyone in your organization is either already on this tee time or not active.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
                      {selectedNominees.map((selId, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <select
                            value={selId}
                            onChange={(e) => handleSelectNominee(idx, e.target.value)}
                            style={{
                              flex: 1,
                              padding: '10px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: 8,
                              fontSize: 14,
                              background: 'white',
                              color: selId ? '#111827' : '#6b7280'
                            }}
                          >
                            <option value="">— Select a player —</option>
                            {eligibleNominees.map(n => {
                              // Disable options already picked in another row (but keep the current row's own selection enabled)
                              const disabled = pickedIds.has(n.id) && n.id !== selId;
                              return (
                                <option key={n.id} value={n.id} disabled={disabled}>
                                  {n.name}{disabled ? ' (already added)' : ''}
                                </option>
                              );
                            })}
                          </select>
                          {selectedNominees.length > 1 && (
                            <button
                              onClick={() => handleRemoveNomineeRow(idx)}
                              title="Remove this row"
                              style={{
                                padding: '8px 12px',
                                background: 'white',
                                color: '#6b7280',
                                border: '1px solid #d1d5db',
                                borderRadius: 8,
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}

                      {canAddAnother && (
                        <button
                          onClick={handleAddAnotherNomineeRow}
                          style={{
                            alignSelf: 'flex-start',
                            padding: '6px 10px',
                            background: 'transparent',
                            color: '#1d4ed8',
                            border: 'none',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                        >
                          + Add Another
                        </button>
                      )}
                    </div>
                  )}

                  {nominateError && (
                    <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>
                      {nominateError}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      onClick={handleCloseAddPartners}
                      disabled={nominateSubmitting}
                      style={{
                        padding: '10px 16px',
                        background: 'white',
                        color: '#6b7280',
                        border: '1px solid #d1d5db',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: nominateSubmitting ? 'not-allowed' : 'pointer',
                        opacity: nominateSubmitting ? 0.5 : 1
                      }}
                    >
                      Skip
                    </button>
                    <button
                      onClick={handleSendInvites}
                      disabled={nominateSubmitting || eligibleNominees.length === 0 || selectedNominees.filter(Boolean).length === 0}
                      style={{
                        padding: '10px 16px',
                        background: '#059669',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: nominateSubmitting ? 'not-allowed' : 'pointer',
                        opacity: (nominateSubmitting || eligibleNominees.length === 0 || selectedNominees.filter(Boolean).length === 0) ? 0.5 : 1
                      }}
                    >
                      {nominateSubmitting ? 'Sending…' : 'Send Invites'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* ── Success view ── */}
                  <div style={{ fontSize: 42, textAlign: 'center', marginBottom: 12 }}>✉️</div>
                  <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 10, color: '#166534', textAlign: 'center' }}>
                    {nominateSuccess.names.length > 0 ? 'Invitations sent!' : 'Some invitations could not be sent'}
                  </div>
                  {nominateSuccess.names.length > 0 && (
                    <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, marginBottom: 12, textAlign: 'center' }}>
                      Invites sent to <strong>{nominateSuccess.names.join(', ')}</strong>.
                      Their spots are held for <strong>{windowHours}</strong> hour{windowHours !== 1 ? 's' : ''} pending confirmation.
                    </div>
                  )}
                  {nominateSuccess.failedNames && nominateSuccess.failedNames.length > 0 && (
                    <div style={{ padding: 12, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
                      ⚠ Could not invite <strong>{nominateSuccess.failedNames.join(', ')}</strong> — they may already be on this tee time. Please refresh and try again if needed.
                    </div>
                  )}
                  {!(nominateSuccess.failedNames && nominateSuccess.failedNames.length > 0) && (
                    <div style={{ marginBottom: 20 }}></div>
                  )}
                  <button
                    onClick={handleCloseAddPartners}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: '#166534',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// ============================================================================
// ACCEPT TEE TIME INVITE PAGE
// Public page for accepting tee time invitations via shareable links
// ============================================================================
// ============================================================================
// SOCIAL EVENT SIGNUP (Sept 20, 2026) — the public form and its edit mode.
//   /rsvp/:address    a new reservation
//   /rsvp/r/:token    change or cancel an existing one (the link in the email)
// Reached in the wild as ttogolf.com/rsvp/<address>, which server.js redirects.
//
// ONE component for both, because they are the same form with the same fields;
// the only differences are that edit mode arrives pre-filled and locks the two
// identity fields.
//
// NO SEAT COUNT IS EVER SHOWN. Not on load, not while typing. A number appears
// only when the server refuses a party for not fitting, and that number comes
// from the server with the capacity lock held. The page therefore has nothing
// stale to act on, which is the useful side effect of the decision.
//
// Name and email are read-only when editing: email is the key the reservation
// and the player match were made on, and the backend does not accept a change
// to either. Say so rather than showing a field that silently does nothing.
//
// No window.confirm anywhere — a browser dialog blocks everything. Cancelling
// asks inline.
// ============================================================================
const SocialRsvpPage = () => {
  const { address, token } = useParams();
  const editing = !!token;
  // Sept 20, 2026: set when someone arrived from the golf confirmation, which
  // navigates in-app and hands the two fields it already has across in memory.
  // Empty for an email link or a cold tab -- then they simply type them.
  const navState = useLocation().state || {};

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [socialEvent, setSocialEvent] = useState(null);
  const [editable, setEditable] = useState(true);
  const [existing, setExisting] = useState(null);

  const [form, setForm] = useState({
    name: navState.prefillName || '', email: navState.prefillEmail || '', phone: '', covers: 1,
    guest_names: '', dietary: '', is_member: false, member_note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [offerCovers, setOfferCovers] = useState(null);   // set by a NO_ROOM refusal
  const [done, setDone] = useState(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const [closeHint, setCloseHint] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkSent, setLinkSent] = useState(null);
  const [showLinkBox, setShowLinkBox] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // window.close() only works on a tab the page itself opened, and a guest
  // arriving from an email link is not in one. So try it, then say plainly that
  // they can close the tab -- a button that silently does nothing is worse than
  // no button, which is what the golf invite page has.
  const closePage = () => {
    try { window.close(); } catch (e) { /* not our window to close */ }
    setCloseHint(true);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true); setLoadError(null);
        const url = editing ? `${API_URL}/social/r/${token}` : `${API_URL}/social/${address}`;
        const res = await fetch(url);
        const body = await res.json();
        if (cancelled) return;
        if (!res.ok || !body.success) { setLoadError(body.error || 'We could not find that event.'); return; }
        if (editing) {
          const r = body.data.reservation;
          setSocialEvent(body.data.social_event);
          setEditable(body.data.editable);
          setExisting(r);
          setForm({
            name: r.name || '', email: r.email || '', phone: r.phone || '',
            covers: r.covers || 1, guest_names: r.guest_names || '', dietary: r.dietary || '',
            is_member: !!r.is_member, member_note: r.member_note || '',
          });
        } else {
          setSocialEvent(body.data);
        }
      } catch (err) {
        if (!cancelled) setLoadError('We could not load that page. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [address, token, editing]);

  const guestCount = String(form.guest_names || '')
    .split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean).length;
  const expectedGuests = Math.max(0, Number(form.covers) - 1);

  // The server formats this in the ORGANIZATION's timezone and hands us the
  // string. Do NOT format here: the browser would use the reader's zone, so a
  // guest checking the link from a hotel in Arizona would see a different time
  // for the same dinner than the couple across the street. The event happens
  // where it happens. The fallback only covers an older payload.
  const whenText = (ev) => {
    if (!ev) return null;
    if (ev.starts_at_text) return ev.starts_at_text;
    if (!ev.starts_at) return null;
    try {
      return new Date(ev.starts_at).toLocaleString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    } catch { return null; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null); setOfferCovers(null);
    if (!editing && (!form.name.trim() || !form.email.trim())) {
      setFormError('Please enter your name and email.');
      return;
    }
    if (!String(form.phone || '').trim()) {
      setFormError('Please enter a phone number so the organizer can reach you.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = editing
        ? { covers: Number(form.covers), guest_names: form.guest_names, dietary: form.dietary,
            phone: form.phone, is_member: form.is_member, member_note: form.member_note }
        : { name: form.name.trim(), email: form.email.trim().toLowerCase(), phone: form.phone,
            covers: Number(form.covers), guest_names: form.guest_names, dietary: form.dietary,
            is_member: form.is_member, member_note: form.member_note };
      const res = await fetch(
        editing ? `${API_URL}/social/r/${token}` : `${API_URL}/social/${address}/rsvp`,
        { method: editing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
      );
      const body = await res.json();
      if (!res.ok || !body.success) {
        setFormError(body.error || 'We could not save that.');
        // The one place a number is shown, and it came from the server.
        if (body.code === 'NO_ROOM' && body.maxCovers) setOfferCovers(body.maxCovers);
        return;
      }
      setDone({ ...body.data, changed: editing });
    } catch (err) {
      setFormError('We could not reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const doCancel = async () => {
    setSubmitting(true); setFormError(null);
    try {
      const res = await fetch(`${API_URL}/social/r/${token}/cancel`, { method: 'POST' });
      const body = await res.json();
      if (!res.ok || !body.success) { setFormError(body.error || 'We could not cancel that.'); return; }
      setDone({ status: 'cancelled' });
    } catch (err) {
      setFormError('We could not reach the server. Please try again.');
    } finally {
      setSubmitting(false); setConfirmingCancel(false);
    }
  };

  const sendLink = async () => {
    setLinkSent(null);
    try {
      const res = await fetch(`${API_URL}/social/${address}/resend`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: linkEmail.trim().toLowerCase() }),
      });
      const body = await res.json();
      setLinkSent(body.message || 'If we have a reservation for that address, a link is on its way.');
    } catch (err) {
      // Say the same thing on failure — this must never become an oracle for
      // who is attending.
      setLinkSent('If we have a reservation for that address, a link is on its way.');
    }
  };

  const shell = (children, maxWidth = 480) => (
    <div className="tto-public-form"
         style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1b4332, #2d6a4f)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <PublicFormMobileCss />
      <Card style={{ maxWidth, width: '100%' }}>{children}</Card>
    </div>
  );

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1b4332, #2d6a4f)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🥂</div>
          <div style={{ fontSize: 18 }}>Loading…</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return shell(
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>
          {editing ? 'That link is not valid' : 'Event not found'}
        </div>
        <div style={{ color: '#6b7280' }}>{loadError}</div>
      </div>, 400);
  }

  // ---- after submitting ----
  if (done) {
    const cancelled = done.status === 'cancelled';
    const waitlisted = done.status === 'waitlist';
    return shell(
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{cancelled ? '👋' : waitlisted ? '🕐' : '✅'}</div>
        <div style={{ fontWeight: 700, fontSize: 24, marginBottom: 8, color: cancelled ? '#6b7280' : waitlisted ? '#b45309' : '#16a34a' }}>
          {cancelled ? 'Reservation cancelled' : waitlisted ? "You're on the waitlist" : done.changed ? 'Your reservation is updated' : "You're on the list"}
        </div>
        <div style={{ color: '#6b7280', marginBottom: 20 }}>
          {cancelled
            ? 'We have let the organizer know. You are welcome to sign up again if things change.'
            : waitlisted
              // Promise notification, never a seat.
              ? `It is full, so your party of ${done.covers} is on the waitlist${done.waitlistPosition ? ` at number ${done.waitlistPosition}` : ''}. You are not confirmed yet — we will let you know if room opens up.`
              : `We have you down for ${done.covers === 1 ? '1 person' : `${done.covers} people`} at ${socialEvent.title}.`}
        </div>

        {!cancelled && (
          <div style={{ background: '#f0fdf4', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px solid #bbf7d0', textAlign: 'left' }}>
            {whenText(socialEvent) && <div style={{ marginBottom: 4 }}>📅 {whenText(socialEvent)}</div>}
            {socialEvent.location && <div style={{ marginBottom: 4 }}>📍 {socialEvent.location}</div>}
            {socialEvent.description && (
              <div style={{ color: '#374151', fontSize: 14, marginTop: 8, whiteSpace: 'pre-wrap' }}>{socialEvent.description}</div>
            )}
            {socialEvent.payment_note && <div style={{ color: '#374151', fontSize: 14, marginTop: 8 }}>💳 {socialEvent.payment_note}</div>}
          </div>
        )}

        {/* No link is printed here. A 64-character token is not something anyone
            copies off a screen, and the same link is in the confirmation email,
            which is where people go looking when they need to change something. */}
        {!cancelled && (
          <div style={{ background: '#eff6ff', padding: 12, borderRadius: 8, border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: 13, color: '#1e40af' }}>
              📧 A confirmation is on its way to <strong>{form.email}</strong>. If you need to change
              the number coming, swap a guest, or cancel, use the link in that email.
            </div>
          </div>
        )}

        {closeHint ? (
          <div style={{ marginTop: 16, fontSize: 13, color: '#6b7280' }}>All set — you can close this tab.</div>
        ) : (
          <Button onClick={closePage}
                  style={{ marginTop: 16, width: '100%', background: '#2d6a4f', color: '#fff', padding: '12px 24px', fontSize: 16, fontWeight: 600 }}>
            Done
          </Button>
        )}
      </div>, 460);
  }

  // ---- closed ----
  if (socialEvent.closed && !editing) {
    return shell(
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🥂</div>
        <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>{socialEvent.title}</div>
        <div style={{ color: '#6b7280' }}>
          Signup for this event has closed. Please contact {socialEvent.organization} if you would still like to come.
        </div>
      </div>, 440);
  }

  const readOnly = editing && !editable;

  return shell(
    <form onSubmit={submit}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>🥂</div>
        <div style={{ fontWeight: 800, fontSize: 23, marginBottom: 4 }}>{socialEvent.title}</div>
        {socialEvent.event_name && <div style={{ color: '#6b7280', fontSize: 13 }}>{socialEvent.event_name}</div>}
      </div>

      <div style={{ background: '#f0fdf4', padding: 16, borderRadius: 8, marginBottom: 20, border: '1px solid #bbf7d0' }}>
        {whenText(socialEvent) && <div style={{ marginBottom: 4 }}>📅 {whenText(socialEvent)}</div>}
        {socialEvent.location && <div style={{ marginBottom: 4 }}>📍 {socialEvent.location}</div>}
        {socialEvent.payment_note && <div style={{ color: '#374151', fontSize: 14, marginTop: 8 }}>💳 {socialEvent.payment_note}</div>}
        {socialEvent.description && <div style={{ color: '#6b7280', fontSize: 14, marginTop: 8 }}>{socialEvent.description}</div>}
      </div>

      {readOnly && (
        <div style={{ background: '#fef3c7', padding: 12, borderRadius: 8, marginBottom: 16, border: '1px solid #fde68a' }}>
          <div style={{ fontSize: 13, color: '#92400e' }}>
            Changes for this event have closed, so this is how your reservation stands. Please contact {socialEvent.organization} if you need it changed.
          </div>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <Label>Your name</Label>
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} disabled={editing} placeholder="First and last" />
      </div>
      <div style={{ marginBottom: 12 }}>
        <Label>Email</Label>
        <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} disabled={editing} placeholder="you@example.com" />
        {editing && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Need a different name or email? Contact {socialEvent.organization}.
          </div>
        )}
      </div>
      <div style={{ marginBottom: 12 }}>
        <Label>Phone</Label>
        <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} disabled={readOnly} placeholder="(843) 555-0134" />
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
          So {socialEvent.organization} can reach you on the day if anything changes.
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Label>How many are coming, including you?</Label>
        <select
          value={form.covers}
          onChange={(e) => set('covers', Number(e.target.value))}
          disabled={readOnly}
          style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 10, width: '100%', fontSize: 14, boxSizing: 'border-box' }}
        >
          {Array.from({ length: socialEvent.max_covers || 20 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>

      {expectedGuests > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Label>Who is coming with you? One name per line</Label>
          <textarea
            value={form.guest_names}
            onChange={(e) => set('guest_names', e.target.value)}
            disabled={readOnly}
            rows={Math.min(expectedGuests + 1, 6)}
            style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 10, width: '100%', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
          {/* A hint, never a block — the club wants names but a missing one must
              not stop a reservation the night before. */}
          <div style={{ fontSize: 12, color: guestCount === expectedGuests ? '#6b7280' : '#b45309', marginTop: 4 }}>
            {guestCount} of {expectedGuests} {expectedGuests === 1 ? 'guest' : 'guests'} named
          </div>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <Label>Any dietary restrictions or allergies in your party? (optional)</Label>
        <textarea
          value={form.dietary}
          onChange={(e) => set('dietary', e.target.value)}
          disabled={readOnly}
          rows={2}
          style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 10, width: '100%', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' }}
        />
      </div>

      {socialEvent.ask_club_account && (
        <div style={{ marginBottom: 16, background: '#f9fafb', padding: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: readOnly ? 'default' : 'pointer' }}>
            <input type="checkbox" checked={!!form.is_member} disabled={readOnly}
                   onChange={(e) => set('is_member', e.target.checked)} />
            {socialEvent.club_account_prompt}
          </label>
          {form.is_member && (
            <div style={{ marginTop: 8 }}>
              <Input value={form.member_note} disabled={readOnly}
                     onChange={(e) => set('member_note', e.target.value)}
                     placeholder="Member name or account number" />
            </div>
          )}
        </div>
      )}

      {formError && (
        <div style={{ background: '#fef2f2', padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid #fecaca' }}>
          <div style={{ fontSize: 13, color: '#b91c1c' }}>{formError}</div>
          {offerCovers && (
            <Button type="button" onClick={() => { set('covers', offerCovers); setFormError(null); setOfferCovers(null); }}
                    style={{ marginTop: 8, background: '#fff', border: '1px solid #b91c1c', color: '#b91c1c', fontSize: 13 }}>
              Change my party to {offerCovers}
            </Button>
          )}
        </div>
      )}

      {!readOnly && (
        <Button type="submit" disabled={submitting}
                style={{ width: '100%', background: '#2d6a4f', color: '#fff', padding: '12px 24px', fontSize: 16, fontWeight: 600 }}>
          {submitting ? 'Saving…' : editing ? 'Save my changes' : 'Reserve my place'}
        </Button>
      )}

      {editing && (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          {closeHint ? (
            <div style={{ fontSize: 13, color: '#6b7280' }}>You can close this tab.</div>
          ) : (
            <button type="button" onClick={closePage}
                    style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}>
              Close without changing anything
            </button>
          )}
        </div>
      )}

      {editing && !readOnly && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          {!confirmingCancel ? (
            <button type="button" onClick={() => setConfirmingCancel(true)}
                    style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}>
              Cancel my reservation
            </button>
          ) : (
            <div style={{ background: '#fef2f2', padding: 12, borderRadius: 8, border: '1px solid #fecaca' }}>
              <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 8 }}>
                Cancel your whole reservation for {form.covers === 1 ? '1 person' : `${form.covers} people`}?
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <Button type="button" onClick={doCancel} disabled={submitting}
                        style={{ background: '#b91c1c', color: '#fff', fontSize: 13 }}>Yes, cancel it</Button>
                <Button type="button" onClick={() => setConfirmingCancel(false)}
                        style={{ background: '#fff', border: '1px solid #e5e7eb', fontSize: 13 }}>Keep it</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!editing && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
          {!showLinkBox ? (
            <button type="button" onClick={() => setShowLinkBox(true)}
                    style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}>
              Already signed up? Send me my link
            </button>
          ) : linkSent ? (
            <div style={{ fontSize: 13, color: '#374151' }}>{linkSent}</div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <Input value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="you@example.com" />
              <Button type="button" onClick={sendLink} style={{ background: '#fff', border: '1px solid #e5e7eb', fontSize: 13, whiteSpace: 'nowrap' }}>
                Send link
              </Button>
            </div>
          )}
        </div>
      )}
    </form>, 480);
};

const AcceptTeeTimeInvitePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  // Oct 12, 2026: arriving from an event page (?from=<event address>) gives a way back.
  // From an email or text there is nowhere to go back to, so no link -- just the
  // "nothing is sent until you tap" line under the button.
  const [searchParams] = useSearchParams();
  const fromSlug = (searchParams.get('from') || '').trim();
  const backHref = /^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/.test(fromSlug) ? `/e/${fromSlug}` : null;
  const backLinkStyle = {
    display: "inline-flex", alignItems: "center", minHeight: 48, padding: "0 4px",
    background: "none", border: "none", cursor: "pointer",
    color: "#2d6a4f", fontSize: 17, fontWeight: 700, textDecoration: "none"
  };
  const [teeTime, setTeeTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  // Oct 28, 2026: team sign-up. teamSize > 1 when the event is set to "Team of N": the
  // golfer signs up as captain with up to N - 1 teammates, and says whether empty spots
  // are names to come or he wants to be paired up.
  const teamSize = teeTime && teeTime.teamSize > 1 ? teeTime.teamSize : 1;
  const [mates, setMates] = useState([]);
  const [choice, setChoice] = useState('');
  useEffect(() => {
    if (teamSize > 1) setMates(Array.from({ length: teamSize - 1 }, () => ({ name: '', email: '' })));
  }, [teamSize]);
  const setMate = (i, k, v) => setMates(list => list.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  const namedMates = mates.filter(m => m.name.trim()).length;
  // Oct 28, 2026: Done did nothing when the page came from an email link -- window.close()
  // only works on a tab the page itself opened. Try it, then say plainly they can close the
  // tab (same as the reception page).
  const [closeHint, setCloseHint] = useState(false);
  const emptySpots = teamSize > 1 ? teamSize - 1 - namedMates : 0;

  useEffect(() => {
    const fetchInviteDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_URL}/invite/${code}`);
        const data = await response.json();
        
        if (!response.ok) {
          setError(data.error || 'Invalid invitation link');
          return;
        }
        
        setTeeTime(data.data);
      } catch (err) {
        console.error('Error fetching invite:', err);
        setError('Unable to load invitation details');
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      fetchInviteDetails();
    }
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.email.trim()) {
      setError('Please enter your name and email');
      return;
    }
    if (teamSize > 1) {
      const noName = mates.find(m => !m.name.trim() && m.email.trim());
      if (noName) { setError(`Enter a name for ${noName.email.trim()}.`); return; }
      if (emptySpots > 0 && !choice) { setError('Tell us about the empty spots: names to come, or pair me up.'); return; }
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/invite/${code}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          ...(teamSize > 1 ? {
            teammates: mates.map(m => ({ name: m.name.trim(), email: m.email.trim().toLowerCase() })),
            choice: emptySpots > 0 ? choice : undefined
          } : {})
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || 'Failed to accept invitation');
        return;
      }
      
      setSuccess(data);
    } catch (err) {
      console.error('Error accepting invite:', err);
      setError('Unable to process your RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        minHeight: "100vh", 
        background: "linear-gradient(135deg, #1b4332, #2d6a4f)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center" 
      }}>
        <div style={{ textAlign: "center", color: "#fff" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⛳</div>
          <div style={{ fontSize: 18 }}>Loading invitation...</div>
        </div>
      </div>
    );
  }

  // Error state (invalid link)
  if (error && !teeTime) {
    return (
      <div style={{ 
        minHeight: "100vh", 
        background: "linear-gradient(135deg, #1b4332, #2d6a4f)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        padding: 20
      }}>
        <Card style={{ maxWidth: 400, width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
          <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Invalid Invitation</div>
          <div style={{ color: "#6b7280", marginBottom: 20 }}>{error}</div>
          <Button onClick={() => navigate(backHref || '/')} style={{ background: "#2d6a4f", color: "#fff" }}>
            {backHref ? '← Back to event page' : 'Go to Homepage'}
          </Button>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={{ 
        minHeight: "100vh", 
        background: "linear-gradient(135deg, #1b4332, #2d6a4f)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        padding: 20
      }}>
        <Card style={{ maxWidth: 450, width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontWeight: 700, fontSize: 24, marginBottom: 8, color: "#16a34a" }}>
            {success.data.status === 'confirmed' ? "You're In!" : "Added to Waitlist"}
          </div>
          <div style={{ color: "#6b7280", marginBottom: 20 }}>
            {success.message}
          </div>
          
          {/* Tee Time Details */}
          <div style={{ 
            background: "#f0fdf4", 
            padding: 16, 
            borderRadius: 8, 
            marginBottom: 20,
            border: "1px solid #bbf7d0"
          }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{success.data.course}</div>
            <div style={{ color: "#6b7280" }}>{success.data.date}</div>
            <div style={{ color: "#6b7280" }}>{success.data.time}</div>
            <div style={{ color: "#6b7280", fontSize: 12, marginTop: 8 }}>{success.data.organization}</div>
          </div>
          
          {/* Oct 28, 2026: the team he just signed up. */}
          {success.data.team && (
            <div data-testid="invite-team" style={{ background: "#fff", padding: 16, borderRadius: 8, marginBottom: 20, border: "1px solid #d1d5db", textAlign: "left" }}>
              <div style={{ fontWeight: 800, fontSize: 19, color: "#1b4332", marginBottom: 8 }}>👥 Your team: {success.data.team.name}</div>
              {success.data.team.members.map((m, i) => (
                <div key={i} style={{ fontSize: 17, padding: "4px 0", color: m.held ? "#6b7280" : "#111827", fontStyle: m.held ? "italic" : "normal" }}>
                  {m.held ? 'Name to come' : m.name}{m.captain ? ' (captain)' : ''}
                </div>
              ))}
              {success.data.team.open_spots > 0 && (
                <div style={{ fontSize: 17, padding: "4px 0", color: "#6b7280", fontStyle: "italic" }}>{success.data.team.open_spots} open spot{success.data.team.open_spots === 1 ? '' : 's'}</div>
              )}
              {success.data.team.choice === 'pair' && (
                <div style={{ fontSize: 15, color: "#4b5563", marginTop: 8, lineHeight: 1.45 }}>You asked to be paired up. The organizer will do his best to place you with other golfers.</div>
              )}
              <div style={{ fontSize: 15, color: "#4b5563", marginTop: 8, lineHeight: 1.45 }}>
                Add or change names any time from <strong>Open my team</strong> in your confirmation email. Your teammates aren't emailed now — the organizer sends each of them a welcome email before the event.
              </div>
            </div>
          )}

          <div style={{ 
            background: "#eff6ff", 
            padding: 12, 
            borderRadius: 8, 
            marginBottom: 20,
            border: "1px solid #bfdbfe"
          }}>
            <div style={{ fontSize: 13, color: "#1e40af" }}>
              📧 A confirmation email has been sent to <strong>{form.email}</strong>
            </div>
          </div>
          
          {/* Oct 23, 2026 (Dennis): payment links. Present only when this tee time is an event
              whose organizer ticked a way to pay, and only for a confirmed golfer. A golfer
              signing up alone is shown the per-golfer price; the note carries his name. TTO
              never touches the money -- each button opens the organizer's own account. */}
          {success.data.payment && success.data.payment.methods && success.data.payment.methods.length > 0 && (
            <div data-testid="invite-pay" style={{
              background: "#fffbeb",
              padding: 16,
              borderRadius: 8,
              marginBottom: 20,
              border: "1px solid #fcd34d",
              textAlign: "left"
            }}>
              <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 4, color: "#1b4332" }}>
                💳 {money(success.data.payment.amount) ? `Entry: ${money(success.data.payment.amount)}` : 'How to pay'}
              </div>
              {success.data.payment.fee_label && money(success.data.payment.amount) && (
                <div style={{ color: "#4b5563", fontSize: 15, marginBottom: 4 }}>{success.data.payment.fee_label}</div>
              )}
              <div style={{ color: "#4b5563", fontSize: 15, marginBottom: 14 }}>Choose whichever is easiest for you.</div>
              <PayButtons options={payOptions({ methods: success.data.payment.methods, amount: success.data.payment.amount, note: success.data.payment.note })} accent="#2d6a4f" />
              <div style={{ color: "#6b7280", fontSize: 13, marginTop: 14, lineHeight: 1.45 }}>
                Payments go directly to {success.data.payment.payee || 'the organizer'}. Tee Time Organizer does not handle payments — questions about paying go to the organizer. These buttons are in your confirmation email too.
              </div>
            </div>
          )}
          
          {success.data.waitlistPosition && (
            <div style={{ 
              background: "#fef3c7", 
              padding: 12, 
              borderRadius: 8, 
              marginBottom: 20,
              border: "1px solid #fde68a"
            }}>
              <div style={{ fontSize: 13, color: "#92400e" }}>
                You are #{success.data.waitlistPosition} on the waitlist. We'll notify you if a spot opens up!
              </div>
            </div>
          )}
          
          {/* Sept 20, 2026: the reception hand-off. Rendered only when the accept
              response carried a social event -- i.e. this tee time has one, with a
              public address and an RSVP window still open. Violet, not the amber
              above, so it never reads as a waitlist warning.

              navigate() with state rather than an href with query params: the name
              and email ride in memory, never in the URL, so they stay out of browser
              history and server logs. Opened cold in a new tab the fields are simply
              empty, which is the pre-existing behaviour. */}
          {success.data.social && (
            <div style={{
              background: "#faf5ff",
              padding: 16,
              borderRadius: 8,
              marginBottom: 20,
              border: "1px solid #e9d5ff",
              textAlign: "left"
            }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, color: "#581c87" }}>
                🥂 {success.data.social.title}
              </div>
              <div style={{ color: "#6b7280", fontSize: 14, marginBottom: 12 }}>
                There is also a social event with this round. You can reserve a place for yourself and your guests.
              </div>
              <Button
                onClick={() => navigate(`/rsvp/${success.data.social.slug}`, {
                  state: { prefillName: form.name, prefillEmail: form.email }
                })}
                style={{
                  width: "100%",
                  background: "#6b21a8",
                  color: "#fff",
                  padding: "12px 24px",
                  fontSize: 16,
                  fontWeight: 600
                }}
              >
                Reserve my place
              </Button>
              <div style={{ color: "#6b7280", fontSize: 13, marginTop: 10 }}>
                No rush — you can do this now or any time later. The same link is in your confirmation email.
              </div>
            </div>
          )}
          
          {/* Close/Done Button - Added Dec 8, 2025 */}
          {closeHint && !backHref ? (
            <div data-testid="invite-close-hint" style={{ fontSize: 17, fontWeight: 600, color: "#1b4332", padding: "12px 0" }}>✓ All set — you can close this tab.</div>
          ) : (
          <Button 
            onClick={() => {
              if (backHref) { navigate(backHref); return; }
              try { window.close(); } catch (e) { /* not our window to close */ }
              setCloseHint(true);
            }}
            style={{ 
              width: "100%",
              background: "#2d6a4f", 
              color: "#fff",
              padding: "12px 24px",
              fontSize: 16,
              fontWeight: 600
            }}
          >
            {backHref ? '✓ Done — back to event page' : '✓ Done'}
          </Button>
          )}
        </Card>
      </div>
    );
  }

  // Main form
  return (
    <div className="tto-public-form" style={{ 
      minHeight: "100vh", 
      background: "linear-gradient(135deg, #1b4332, #2d6a4f)", 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center",
      padding: 20
    }}>
      <PublicFormMobileCss />
      <Card style={{ maxWidth: 450, width: "100%" }}>
        {backHref && (
          <div style={{ marginTop: -8, marginBottom: 4 }}>
            <a href={backHref} onClick={(e) => { e.preventDefault(); navigate(backHref); }} style={backLinkStyle}>
              ← Back to event page
            </a>
          </div>
        )}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          {/* Oct 1, 2026: TTO logo replaces the golfer emoji on the invite page */}
          <img src="/logo.png" alt="Tee Time Organizer" style={{ width: 72, height: 72, objectFit: 'contain', display: 'block', margin: '0 auto 8px' }} />
          <div style={{ fontWeight: 800, fontSize: 24, marginBottom: 4 }}>{teamSize > 1 ? 'Sign up your team' : "You're Invited!"}</div>
          <div style={{ color: "#6b7280", fontSize: teamSize > 1 ? 17 : undefined }}>
            {teamSize > 1 ? `${teeTime.eventName ? `${teeTime.eventName} · ` : ''}Teams of ${teamSize}` : 'Join us for a round of golf'}
          </div>
        </div>

        {/* Tee Time Info */}
        <div style={{ 
          background: "#f0fdf4", 
          padding: 16, 
          borderRadius: 8, 
          marginBottom: 20,
          border: "1px solid #bbf7d0"
        }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{teeTime.course}</div>
          <div style={{ display: "flex", gap: 8, color: "#6b7280", marginBottom: 8 }}>
            <span>📅 {teeTime.dateFormatted}</span>
            <span>•</span>
            <span>⏰ {teeTime.timeFormatted}</span>
          </div>
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            📍 {teeTime.location}
          </div>
          <div style={{ 
            marginTop: 12, 
            paddingTop: 12, 
            borderTop: "1px solid #bbf7d0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>
              {teeTime.isFull ? '🚫 Full' : `✅ ${teeTime.spotsAvailable} spot${teeTime.spotsAvailable !== 1 ? 's' : ''} available`}
            </span>
            {teeTime.isFull && teeTime.waitlistCount > 0 && (
              <span style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", padding: "2px 8px", borderRadius: 4 }}>
                {teeTime.waitlistCount} on waitlist
              </span>
            )}
          </div>
        </div>

        {/* Error message (a team form shows it by the button instead, where he is looking) */}
        {error && teamSize === 1 && (
          <div style={{ 
            background: "#fee2e2", 
            color: "#991b1b", 
            padding: 12, 
            borderRadius: 8, 
            marginBottom: 16,
            fontSize: 13
          }}>
            {error}
          </div>
        )}

        {/* RSVP Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Your Name <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <Input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="John Smith"
              disabled={submitting}
            />
          </div>
          
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Your Email <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="john@example.com"
              disabled={submitting}
            />
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
              You'll receive a confirmation email at this address
            </div>
          </div>

          {teamSize > 1 && (
            <div data-testid="invite-teammates" style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: "#1b4332", marginBottom: 4 }}>Your teammates</div>
              <div style={{ fontSize: 15, color: "#4b5563", marginBottom: 12, lineHeight: 1.45 }}>
                You are the captain. Add the names you know — email is optional. Leave a spot empty if you don't know yet. Your teammates are not emailed now.
              </div>
              {mates.map((m, i) => (
                <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 6 }}>Golfer {i + 2}</div>
                  <Input type="text" value={m.name} onChange={(e) => setMate(i, 'name', e.target.value)} placeholder="Name" disabled={submitting} />
                  <div style={{ height: 8 }} />
                  <Input type="email" value={m.email} onChange={(e) => setMate(i, 'email', e.target.value)} placeholder="Email (optional)" disabled={submitting} />
                </div>
              ))}
              {emptySpots > 0 && (
                <div data-testid="invite-choice" style={{ marginTop: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 8 }}>
                    {emptySpots === teamSize - 1 ? 'No teammates yet?' : `${emptySpots} spot${emptySpots === 1 ? '' : 's'} empty:`}
                  </div>
                  {[
                    { v: 'names_to_come', t: "My team is set — I'll add the names later", d: `Holds all ${teamSize} spots for your team.` },
                    { v: 'pair', t: 'Please pair me up with other golfers', d: "The organizer will do his best to place you." }
                  ].map(o => (
                    <label key={o.v} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", marginBottom: 8, borderRadius: 10, cursor: "pointer",
                      border: choice === o.v ? "2px solid #2d6a4f" : "1px solid #d1d5db", background: choice === o.v ? "#e8f5ee" : "#fff" }}>
                      <input type="radio" name="team-choice" checked={choice === o.v} onChange={() => { setChoice(o.v); setError(null); }} disabled={submitting} style={{ width: 22, height: 22, marginTop: 2, flex: "none" }} />
                      <span>
                        <span style={{ display: "block", fontSize: 16, fontWeight: 700, color: "#111827" }}>{o.t}</span>
                        <span style={{ display: "block", fontSize: 14, color: "#6b7280" }}>{o.d}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && teamSize > 1 && (
            <div data-testid="invite-error" style={{ background: "#fee2e2", color: "#991b1b", padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 15, fontWeight: 600 }}>
              {error}
            </div>
          )}
          
          <Button 
            type="submit"
            disabled={submitting}
            style={{ 
              width: "100%", 
              background: "#2d6a4f", 
              color: "#fff",
              padding: "14px",
              fontSize: 16,
              fontWeight: 600
            }}
          >
            {submitting ? 'Processing...' : teamSize > 1 ? (namedMates === 0 && choice === 'pair' ? (teeTime.isFull ? '🎟️ Join Waitlist' : '✅ Sign me up') : '✅ Sign up my team') : (teeTime.isFull ? '🎟️ Join Waitlist' : '✅ Accept & RSVP')}
          </Button>
          <div style={{ fontSize: 14, color: "#6b7280", textAlign: "center", marginTop: 12, lineHeight: 1.4 }}>
            Nothing is sent until you tap {teamSize > 1 ? 'the button' : (teeTime.isFull ? 'Join Waitlist' : 'Accept & RSVP')}.
            {backHref ? '' : " Can't make it? Just close this page."}
          </div>
        </form>

        {/* Footer */}
        <div style={{ 
          marginTop: 20, 
          paddingTop: 16, 
          borderTop: "1px solid #e5e7eb",
          textAlign: "center",
          color: "#9ca3af",
          fontSize: 12
        }}>
          Powered by Tee Time Organizer
        </div>
      </Card>
    </div>
  );
};

// ============================================================================
// MAIN APP COMPONENT WITH REACT ROUTER
// Updated: November 27, 2025 - Phase 2C Multi-Tenancy
// ============================================================================
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
        {/* Landing Page - Smart route that redirects pinned players to portal */}
        <Route path="/" element={<SmartLandingRoute />} />
        
        {/* Login Page - /login */}
        <Route path="/login" element={<LoginPage />} />
        
        {/* Founders Landing Page - /founders */}
        <Route path="/founders" element={<FoundersPage />} />

        {/* Ad Paid Social Landing Page - /organizer (noindex, pain-led) */}
        <Route path="/organizer" element={<AdLandingPage />} />

        {/* Events Landing Page - /events (tournaments, outings, member-guests) */}
        <Route path="/events" element={<EventsPage />} />
        {/* Weekly Golf Groups product page (Nov 1, 2026) — the group plans moved here from the home page */}
        <Route path="/groups" element={<GroupsPage />} />
        {/* Games page (Oct 5, 2026) — the web version of the games fact sheet */}
        <Route path="/games" element={<GamesPage />} />
        {/* Event Pass door (Sept 30, 2026): buy a pass with a new one-event account,
            then the Next Steps page. */}
        <Route path="/events/start" element={<EventPassStart />} />
        <Route path="/events/ready" element={<EventPassReady />} />

        {/* Register Page - /register */}
        <Route path="/register" element={<RegisterPage />} />
        
        {/* Welcome Page - /welcome?ref=CODE (Referral landing page) */}
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/welcome/:slug" element={<WelcomePage />} />
        
        {/* Forgot Password Page - /forgot-password */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        
        {/* Reset Password Page - /reset-password?token=xxx */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Player Portal - /portal?token=xxx */}
        <Route path="/portal" element={<PlayerPortalRoute />} />
        
        {/* Organizer Dashboard - /dashboard */}
        <Route path="/dashboard" element={<OrganizerRoute />} />
        
        {/* ============================================================ */}
        {/* PHASE 2E: Admin Panel Routes */}
        {/* ============================================================ */}
        
        {/* Admin Login - /admin/login */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        
        {/* Admin Dashboard - /admin */}
        <Route path="/admin" element={<AdminDashboard />} />
        
        {/* Accept Invitation - /accept-invite?token=xxx */}
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        
        {/* ============================================================ */}
        {/* PHASE 3D: Checkout Routes */}
        {/* ============================================================ */}
        
        {/* Checkout Success - /checkout/success?session_id=xxx */}
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        
        {/* Signup Success - /signup/success (Free-tier post-signup landing — no Stripe step) */}
        <Route path="/signup/success" element={<SignupSuccessPage />} />
        
        {/* Checkout Cancel - /checkout/cancel */}
        <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
        
        {/* Payment Success (Admin-initiated) - /payment-success?session_id=xxx&org_id=xxx */}
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        
        {/* Payment Cancelled (Admin-initiated) - /payment-cancelled?org_id=xxx */}
        <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
        
        {/* Tee Time Invite - /invite/:code */}
        <Route path="/invite/:code" element={<AcceptTeeTimeInvitePage />} />

        {/* Reception signup (Sept 20, 2026). Reached in the wild as
            ttogolf.com/rsvp/<address>, which server.js redirects here; the
            /r/<token> form is the link in a confirmation email. One component
            serves both. */}
        <Route path="/rsvp/r/:token" element={<SocialRsvpPage />} />
        <Route path="/rsvp/:address" element={<SocialRsvpPage />} />

        
        {/* ============================================================ */}
        {/* END PHASE 2E Routes */}
        {/* ============================================================ */}
        
        {/* SMS Terms - /sms-terms (for Twilio verification) */}
        <Route path="/sms-terms" element={<SMSTermsPage />} />
        
        {/* Privacy Policy - /privacy (required for Twilio compliance) */}
        <Route path="/privacy" element={<PrivacyPage />} />
        
        {/* Handicap Guide - Educational page about USGA handicaps */}
        <Route path="/handicap-guide" element={<HandicapGuidePage />} />
        
        {/* Wild Dunes Course Handicap Calculator - Public tool */}
        <Route path="/WDHandicapCalc" element={<WDHandicapCalc />} />
        
        {/* Guides - SEO/AI-search content (index + individual articles) */}
        <Route path="/guides" element={<GuidesIndex />} />
        <Route path="/guides/:slug" element={<GuideArticle />} />
        
        {/* Compare - competitor comparison pages (index + individual articles) */}
        <Route path="/compare" element={<CompareIndex />} />
        <Route path="/compare/:slug" element={<CompareArticle />} />
        
        {/* Catch-all redirect to landing page */}
        {/* PUBLIC - Spectator leaderboard (no login). Enhancement B. Bare /lb (ttoleaderboard.com) = the
            "enter your event's name" landing; /lb/<uuid> and /lb/<event address> open a board (Sept 16, 2026). */}
        {/* PUBLIC - Event page (Oct 11, 2026). TTOgolf.com/<event address> redirects here. */}
        <Route path="/e/:slug" element={<EventPage />} />
        <Route path="/lb" element={<PublicLeaderboardLanding />} />
        <Route path="/lb/:key" element={<PublicLeaderboard />} />
        <Route path="/lb/:key/tv" element={<PublicLeaderboard tv />} />
        {/* PUBLIC - Printed team code → team card (D13, Sept 15, 2026). ttogolf.com/<code> redirects here. */}
        <Route path="/t" element={<TeamCodePage />} />
        <Route path="/t/:code" element={<TeamCodePage />} />
        {/* Event desk (Sept 17, 2026): one event's dashboard for staff with an event desk code */}
        <Route path="/desk" element={<DeskRoute />} />
        {/* PUBLIC - "Try it" screens (Sept 20, 2026): the invented sample event. The QR on every
            marketing image lands on /try/scoring; /events links to /try/leaderboard. */}
        <Route path="/try/scoring" element={<TryScoring />} />
        <Route path="/try/leaderboard" element={<TryLeaderboard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}


// ============================================================================
// SMART LANDING ROUTE - iOS Pin-to-Home-Screen Fix
// If a player previously visited the portal (token saved in localStorage),
// redirect them to /portal instead of showing the landing page.
// iOS always opens "/" for pinned bookmarks regardless of the saved URL.
// ============================================================================
function SmartLandingRoute() {
  const savedToken = localStorage.getItem('playerToken');
  const tokenTimestamp = localStorage.getItem('playerTokenTimestamp');
  
  // If an organizer JWT exists, this is an organizer's browser - never redirect to player portal.
  // This prevents organizers who opened a player's portal link from getting stuck there on next launch.
  const organizerToken = localStorage.getItem('accessToken');
  
  if (savedToken && tokenTimestamp && !organizerToken) {
    const daysSinceStored = (Date.now() - parseInt(tokenTimestamp)) / (1000 * 60 * 60 * 24);
    const isLikelyPinnedLaunch = !document.referrer && !window.location.search && !window.location.hash;
    
    if (daysSinceStored < 90 && isLikelyPinnedLaunch) {
      console.log('🏠 SmartLandingRoute: Detected likely pinned home screen launch, redirecting to portal');
      return <Navigate to={`/portal?token=${savedToken}`} replace />;
    }
  }
  
  return <LandingPage />;
}


// ============================================================================
// PLAYER PORTAL ROUTE COMPONENT
// Handles /portal?token=xxx with localStorage persistence for PWA support
// ============================================================================
function PlayerPortalRoute() {
  const urlParams = new URLSearchParams(window.location.search);
  let playerToken = urlParams.get('token');
  
  // DEBUG MODE
  const debugMode = window.location.search.includes('debug=true');
  
  // Save token to localStorage if present in URL
  useEffect(() => {
    if (playerToken && playerToken.trim() !== '') {
      console.log('💾 Saving player token to localStorage');
      localStorage.setItem('playerToken', playerToken);
      localStorage.setItem('playerTokenTimestamp', Date.now().toString());
      
      if (debugMode) {
        alert('✅ Token saved to localStorage!\nToken: ' + playerToken.substring(0, 20) + '...');
      }
    }
  }, [playerToken, debugMode]);
  
  // Remove manifest on portal page to prevent iOS "Open as Web App" toggle
  // When iOS sees a manifest with display:standalone, it offers PWA mode which
  // strips the URL and breaks pin-to-home-screen for the portal.
  // Without a manifest, iOS treats it as a plain bookmark and preserves the URL.
  useEffect(() => {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.remove();
      console.log('🍎 Removed manifest link on portal to prevent iOS PWA mode');
    }
    
    // Also ensure apple-mobile-web-app-capable is no
    let capableMeta = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    if (capableMeta) {
      capableMeta.setAttribute('content', 'no');
    }
  }, []);
  
  // If no token in URL, try to retrieve from localStorage
  if (!playerToken || playerToken.trim() === '') {
    const savedToken = localStorage.getItem('playerToken');
    const tokenTimestamp = localStorage.getItem('playerTokenTimestamp');
    
    if (debugMode) {
      alert('Checking localStorage...\nSaved token: ' + (savedToken ? savedToken.substring(0, 20) + '...' : 'NULL') + '\nTimestamp: ' + tokenTimestamp);
    }
    
    // Check if saved token exists and is less than 90 days old
    if (savedToken && tokenTimestamp) {
      const daysSinceStored = (Date.now() - parseInt(tokenTimestamp)) / (1000 * 60 * 60 * 24);
      
      if (daysSinceStored < 90) {
        console.log('✅ Retrieved valid player token from localStorage');
        playerToken = savedToken;
        
        if (debugMode) {
          alert('✅ Retrieved token from localStorage!\nDays old: ' + daysSinceStored.toFixed(1));
        }
      } else {
        console.log('⚠️ Stored player token expired (>90 days old), clearing');
        localStorage.removeItem('playerToken');
        localStorage.removeItem('playerTokenTimestamp');
        
        if (debugMode) {
          alert('❌ Token expired (' + daysSinceStored.toFixed(1) + ' days old)');
        }
      }
    } else if (debugMode) {
      alert('❌ No token found in localStorage');
    }
  }
  
  // Whether the token we're about to use came from the URL (fresh email/pin
  // link) or from localStorage (a previously-saved token). The portal uses
  // this to decide how to handle a backend rejection: URL → show honest error;
  // localStorage → clear stale token and fall back to landing page.
  const tokenSource = urlParams.get('token') ? 'URL' : (playerToken ? 'localStorage' : 'none');

  console.log('🔍 PlayerPortalRoute check:', {
    url: window.location.href,
    hasToken: !!playerToken,
    tokenSource: tokenSource
  });
  
  // Show error if no valid token
  if (!playerToken || playerToken.trim() === '') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
        padding: 24
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 48,
          maxWidth: 500,
          textAlign: 'center',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>⛳</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16, color: '#1b4332' }}>
            Invalid or Missing Token
          </h2>
          <p style={{ fontSize: 16, color: '#6b7280', marginBottom: 24 }}>
            Please check your email for the correct player portal link.
          </p>
          <a
            href="/"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: '#2d6a4f',
              color: '#fff',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 600
            }}
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }
  
  // Render PlayerPortal with valid token
  console.log('✅ Showing PlayerPortal with token');
  return (
    <ToastProvider>
      <PlayerPortal token={playerToken} tokenSource={tokenSource} />
    </ToastProvider>
  );
}


// ============================================================================
// ORGANIZER ROUTE COMPONENT
// Handles /dashboard with JWT authentication
// Updated: December 9, 2025 - Added demo auto-login support
// Updated: December 14, 2025 - Added inactivity timeout (30 min)
// ============================================================================
function OrganizerRoute() {
  const { isAuthenticated, isLoading, user, organization, organizations, logout, login, switchOrganization, refreshOrganization } = useAuth();
  const [tab, setTab] = useState("organizer");
  
  // Check for demo mode
  const urlParams = new URLSearchParams(window.location.search);
  const isDemo = urlParams.get('demo') === 'true';

  // On mount: honor ?tab= and ?openCheckout=true from email links
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const openCheckout = params.get('openCheckout');
    if (tabParam) setTab(tabParam);
    // Store openCheckout flag so AuthenticatedApp can act on it after auth
    if (openCheckout === 'true') {
      sessionStorage.setItem('openCheckoutOnLoad', 'true');
    }
  }, []);
  
  // Initialize demoLoggingIn to TRUE if in demo mode to prevent flashing
  // This ensures we show "Loading Demo..." immediately instead of flashing other content
  const [demoLoggingIn, setDemoLoggingIn] = useState(isDemo);
  
  // Inactivity timeout state
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const WARNING_BEFORE = 2 * 60 * 1000; // Show warning 2 minutes before logout
  
  // Inactivity timer effect
  useEffect(() => {
    if (!isAuthenticated) return;
    
    let inactivityTimer;
    let warningTimer;
    
    const resetTimers = () => {
      // Clear existing timers
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (warningTimer) clearTimeout(warningTimer);
      
      // Hide warning if shown
      setShowInactivityWarning(false);
      
      // Set warning timer (fires 2 min before logout)
      warningTimer = setTimeout(() => {
        setShowInactivityWarning(true);
      }, INACTIVITY_TIMEOUT - WARNING_BEFORE);
      
      // Set logout timer
      inactivityTimer = setTimeout(() => {
        console.log('⏰ Inactivity timeout - logging out');
        handleInactivityLogout();
      }, INACTIVITY_TIMEOUT);
    };
    
    const handleInactivityLogout = async () => {
      await logout();
      localStorage.removeItem('playerToken');
      localStorage.removeItem('playerTokenTimestamp');
      window.location.href = '/?landing=true&reason=inactivity';
    };
    
    // Activity events to track
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttle activity detection to avoid excessive resets
    let lastActivity = Date.now();
    const handleActivity = () => {
      const now = Date.now();
      // Only reset if more than 1 second since last activity (throttle)
      if (now - lastActivity > 1000) {
        lastActivity = now;
        resetTimers();
      }
    };
    
    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Initial timer start
    resetTimers();
    
    // Cleanup
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (warningTimer) clearTimeout(warningTimer);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, logout]);
  
  // Auto-login for demo mode
  // Updated: February 26, 2026 - Force demo org even when authenticated as different org
  const DEMO_ORG_ID = '0431809c-366b-48e3-ad8b-cecb9206a467';
  useEffect(() => {
    const autoLoginDemo = async () => {
      // If not demo mode, ensure demoLoggingIn is false and exit
      if (!isDemo) {
        setDemoLoggingIn(false);
        return;
      }
      
      // Wait for auth check to complete
      if (isLoading) {
        return;
      }
      
      // If already authenticated, check if we're in the DEMO org
      if (isAuthenticated) {
        if (organization?.id === DEMO_ORG_ID) {
          // Already authenticated as demo org - proceed
          console.log('✅ Demo mode - already authenticated as Demo Golf Club');
          const params = new URLSearchParams(window.location.search);
          params.delete('demo');
          const newSearch = params.toString();
          const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
          window.history.replaceState({}, '', newUrl);
          setDemoLoggingIn(false);
          return;
        }
        // Authenticated as a DIFFERENT org - must log out first, then login as demo
        console.log('🔄 Demo mode - logged in as different org, switching to demo...');
        await logout();
        // Clear all auth data to ensure clean demo session
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('playerToken');
        localStorage.removeItem('playerTokenTimestamp');
        localStorage.removeItem('user');
        localStorage.removeItem('organization');
        localStorage.removeItem('organizations');
        localStorage.removeItem('impersonatingOrg');
        sessionStorage.removeItem('impersonationToken');
        sessionStorage.removeItem('impersonationOrg');
      }
      
      // Not authenticated (or just logged out), perform demo login
      console.log('🎭 Demo mode detected - auto-logging in as Demo Golf Club...');
      
      const result = await login('demo@teetimeorganizer.net', 'DemoGolf2025!');
      
      if (result.success) {
        console.log('✅ Demo login successful');
        // Clean up URL - remove demo parameter
        const params = new URLSearchParams(window.location.search);
        params.delete('demo');
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
        window.history.replaceState({}, '', newUrl);
      } else {
        console.error('❌ Demo login failed:', result.error);
      }
      setDemoLoggingIn(false);
    };
    
    autoLoginDemo();
  }, [isDemo, isAuthenticated, isLoading, login, logout, organization]);
  
  // Show loading while checking auth or demo logging in
  if (isLoading || demoLoggingIn) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)'
      }}>
        <div style={{ color: '#fff', fontSize: 18 }}>
          {demoLoggingIn ? 'Loading Demo...' : 'Loading...'}
        </div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated (and not demo mode - demo will auto-login)
  if (!isAuthenticated && !isDemo) {
    return <Navigate to="/login" state={{ from: { pathname: '/dashboard', search: window.location.search } }} replace />;
  }
  
  // Handle logout
  const handleLogout = async () => {
    await logout();
    // Clear any player tokens so we don't get redirected to portal
    localStorage.removeItem('playerToken');
    localStorage.removeItem('playerTokenTimestamp');
    // Navigate to landing page
    window.location.href = '/?landing=true';
  };
  
  // Show authenticated dashboard with inactivity warning modal
  return (
    <>
      {/* Inactivity Warning Modal */}
      {showInactivityWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 32,
            maxWidth: 400,
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
            <h2 style={{ margin: '0 0 12px', color: '#1f2937' }}>Session Expiring</h2>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>
              You've been inactive for a while. Your session will expire in about 2 minutes.
            </p>
            <button
              onClick={() => setShowInactivityWarning(false)}
              style={{
                background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
                color: '#fff',
                border: 'none',
                padding: '12px 32px',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}
      <AuthenticatedApp tab={tab} setTab={setTab} onLogout={handleLogout} user={user} organization={organization} organizations={organizations} switchOrganization={switchOrganization} refreshOrganization={refreshOrganization} />
    </>
  );
}


// ============================================================================
// MobileNavDrawer — Slide-in nav drawer for mobile (Phase 1B — May 2026)
// Renders only on mobile (when useIsMobile is true). Slides in from the right
// when the hamburger icon is tapped. Contains the same 5 tabs + org switcher +
// logout — identical actions to the desktop nav, just stacked vertically with
// full-width tap targets per Apple HIG (≥44px tall).
// ============================================================================
function MobileNavDrawer({ open, onClose, tab, setTab, onLogout, user, organization, organizations, switchOrganization, desk = null }) {
  if (!open) return null;

  const navItems = desk ? [
    { key: "organizer",   label: "Tee Time", emoji: "🗓" },
    { key: "pairings",    label: "Pairings", emoji: "⛳" },
    { key: "deskscoring", label: "Scoring",  emoji: "🏌️" },
    { key: "print",       label: "Print",    emoji: "🖨️" },
  ] : [
    { key: "organizer", label: "Tee Times", emoji: "🗓" },
    { key: "pairings",  label: "Pairings",  emoji: "⛳" },
    { key: "print",     label: "Print",     emoji: "🖨️" },
    { key: "tools",     label: "Tools",     emoji: "🛠️" },
    { key: "admin",     label: "Admin",     emoji: "⚙️" },
    { key: "settings",  label: "Settings",  emoji: "🏢" },
  ];

  const handleNav = (key) => {
    setTab(key);
    onClose();
  };

  const handleSwitchOrg = async (orgId) => {
    if (!orgId || orgId === organization?.id) {
      onClose();
      return;
    }
    try {
      const result = await switchOrganization(orgId);
      if (result?.success) {
        window.location.reload();
      } else {
        alert(result?.error || 'Failed to switch organization');
      }
    } catch (err) {
      console.error('Switch org error:', err);
      alert('Failed to switch organization');
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 100
        }}
      />
      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '85%',
          maxWidth: 320,
          background: '#fff',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          zIndex: 101,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto'
        }}
      >
        {/* Drawer header */}
        <div style={{
          background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
          padding: '14px 16px',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Menu</div>
          <button
            onClick={onClose}
            aria-label="Close menu"
            style={{
              background: 'rgba(255,255,255,0.18)',
              border: 'none',
              color: '#fff',
              width: 36,
              height: 36,
              borderRadius: 8,
              fontSize: 22,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ✕
          </button>
        </div>

        {/* Nav items */}
        <div style={{ padding: 12 }}>
          {navItems.map(item => {
            const active = tab === item.key;
            return (
              <button
                key={item.key}
                onClick={() => handleNav(item.key)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '14px 12px',
                  marginBottom: 4,
                  border: 'none',
                  borderLeft: active ? '4px solid #2d6a4f' : '4px solid transparent',
                  background: active ? '#eaf3ee' : 'transparent',
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: active ? 700 : 500,
                  color: active ? '#1b4332' : '#374151',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  minHeight: 48
                }}
              >
                <span style={{ fontSize: 20 }}>{item.emoji}</span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: '#e5e7eb', margin: '8px 12px' }} />

        {/* Org section */}
        <div style={{ padding: '8px 16px 16px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            {desk ? 'Event desk' : 'Organization'}
          </div>
          <div style={{ padding: 12, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
              {desk ? desk.event_name : (organization?.name || 'No organization')}
            </div>
            {desk && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{desk.org_name ? `${desk.org_name} · ` : ''}{desk.name}</div>}
            {user?.email && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, wordBreak: 'break-all' }}>
                {user.email}
              </div>
            )}
          </div>

          {/* Org switcher — only show if user has multiple orgs */}
          {Array.isArray(organizations) && organizations.length > 1 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Switch organization:</div>
              <select
                value={organization?.id || ''}
                onChange={(e) => handleSwitchOrg(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: 14,
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Spacer pushes logout to bottom */}
        <div style={{ flex: 1 }} />

        {/* Logout */}
        <div style={{ padding: 16, borderTop: '1px solid #e5e7eb' }}>
          <button
            onClick={() => { onClose(); onLogout(); }}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              background: '#fff',
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              color: '#374151',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              minHeight: 48
            }}
          >
            <span>🚪</span><span>{desk ? 'Leave desk' : 'Logout'}</span>
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// MobileSectionTitle — Shows the active tab's name below the mobile header
// so users know which section they're in (since hamburger hides the tabs).
// ============================================================================
function MobileSectionTitle({ tab, desk = null }) {
  const titles = {
    organizer: { emoji: '🗓', label: desk ? (desk.event_name || 'Tee Time') : 'Tee Times' },
    deskscoring: { emoji: '🏌️', label: 'Scoring' },
    pairings:  { emoji: '⛳', label: 'Pairings' },
    print:     { emoji: '🖨️', label: 'Print' },
    tools:     { emoji: '🛠️', label: 'Tools' },
    admin:     { emoji: '⚙️', label: 'Admin' },
    settings:  { emoji: '🏢', label: 'Settings' },
  };
  const t = titles[tab] || titles.organizer;
  return (
    <div style={{
      padding: '10px 14px',
      background: '#fff',
      borderBottom: '1px solid #e5e7eb',
      fontSize: 15,
      fontWeight: 700,
      color: '#1b4332',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }}>
      <span>{t.emoji}</span>
      <span>{t.label}</span>
    </div>
  );
}

// ============================================================================
// MobileBottomBar — Sticky bottom action bar (Phase 2 — May 2026)
// Renders only when isMobile is true AND tab is 'organizer' (Tee Times).
// Provides one-tap access to the most common actions during tee time management:
//   - "+ New" jumps to the Create Tee Time form by scrolling to top
//   - "Pairings" switches to the Pairings tab
//   - "Refresh" reloads the page (simplest reliable refresh given useApiData lives
//     in scattered components — full page reload always works and is cheap on mobile).
// Bottom safe-area padding accommodates iPhone home indicator.
// ============================================================================
function MobileBottomBar({ setTab, desk = false }) {
  const handleNew = () => {
    // The create form is folded behind "New tee time" since the dashboard home
    // redesign (Sept 25, 2026): ask the dashboard to open it, then scroll up to it.
    window.dispatchEvent(new CustomEvent('openCreateTeeTime'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePairings = () => {
    setTab('pairings');
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: '#fff',
      borderTop: '1px solid #e5e7eb',
      boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
      display: 'flex',
      gap: 6,
      padding: '8px 10px calc(8px + env(safe-area-inset-bottom)) 10px',
      zIndex: 40
    }}>
      {!desk && <button
        onClick={handleNew}
        aria-label="New tee time"
        style={{
          flex: 1.4,
          background: '#2d6a4f',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '10px 8px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4
        }}
      >
        + New Tee Time
      </button>}
      <button
        onClick={handlePairings}
        aria-label="Pairings"
        style={{
          flex: 1,
          background: '#fff',
          color: '#374151',
          border: '1px solid #d1d5db',
          borderRadius: 8,
          padding: '10px 8px',
          fontSize: 14,
          fontWeight: 500,
          cursor: 'pointer',
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4
        }}
      >
        ⛳ Pairings
      </button>
      <button
        onClick={handleRefresh}
        aria-label="Refresh"
        style={{
          background: '#f9fafb',
          color: '#374151',
          border: '1px solid #d1d5db',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 16,
          cursor: 'pointer',
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        🔄
      </button>
    </div>
  );
}

function AuthenticatedApp({ tab, setTab, onLogout, user, organization, organizations, switchOrganization, refreshOrganization }) {
  const deskSession = useContext(DeskContext);   // Event desk (Sept 17, 2026): one event, three tabs
  const { data: courses, loading: loadingCourses, refresh: refreshCoursesRaw } = useApiData('/courses');
  // Always refresh silently to avoid unmounting child components (SettingsMenu tab reset bug)
  const refreshCourses = () => refreshCoursesRaw(true);
  const { data: groups, loading: loadingGroups, addItem: addGroup, updateItem: updateGroup, deleteItem: deleteGroup } = useGroupsData();
  
  // Organization switcher state
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  
  // Handle organization switch
  const handleSwitchOrg = async (orgId) => {
    if (orgId === organization?.id) {
      setShowOrgDropdown(false);
      return;
    }
    setIsSwitching(true);
    setShowOrgDropdown(false);
    try {
      const result = await switchOrganization(orgId);
      if (result.success) {
        // Full page reload to refresh all data for the new organization
        window.location.reload();
      } else {
        alert(result.error || 'Failed to switch organization');
      }
    } catch (err) {
      console.error('Switch org error:', err);
      alert('Failed to switch organization');
    }
    setIsSwitching(false);
  };
  
  // Close org dropdown when clicking outside
  useEffect(() => {
    if (!showOrgDropdown) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-org-switcher]')) {
        setShowOrgDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOrgDropdown]);
  
  // Subscription status state (for read-only enforcement across all components)
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  
  // Determine if account is in read-only mode (cancelled or expired subscription)
  const isReadOnly = subscriptionStatus === 'cancelled' || subscriptionStatus === 'canceled' || subscriptionStatus === 'expired';
  const isExpired = subscriptionStatus === 'expired';
  
  // Fetch subscription status on mount
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        
        const response = await fetch(`${API_URL}/subscription/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscriptionStatus(data.subscription_status);
          setSubscriptionTier(data.subscription_tier);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };
    
    fetchSubscriptionStatus();
  }, []);

  // If user arrived via trial-expired email link, auto-launch Stripe Checkout
  useEffect(() => {
    if (onDeskPage()) return;
    const shouldOpenCheckout = sessionStorage.getItem('openCheckoutOnLoad');
    if (!shouldOpenCheckout) return;
    sessionStorage.removeItem('openCheckoutOnLoad');

    const launchCheckout = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_URL}/subscription/checkout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            tier: 'starter',
            billingCycle: 'monthly',
            isReactivation: false,
            successUrl: `${window.location.origin}/dashboard?tab=settings&subscription=success`,
            cancelUrl: `${window.location.origin}/dashboard?tab=settings`
          })
        });
        const data = await response.json();
        if (data.url) window.location.href = data.url;
      } catch (err) {
        console.error('Error auto-launching checkout:', err);
      }
    };

    launchCheckout();
  }, []);
  
  // Function to refresh subscription status (e.g., after reactivation)
  const refreshSubscription = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data.subscription_status);
        setSubscriptionTier(data.subscription_tier);
      }
    } catch (error) {
      console.error('Error refreshing subscription status:', error);
    }
  };

  // ==========================================================================
  // Mobile layout (originally Phase 1B May 2026, simplified May 2026)
  // The mobile layout used to be gated by a per-org feature flag during staged
  // rollout. After the flag was retired and the layout enabled for all orgs,
  // useIsMobile now only checks viewport width.
  // ==========================================================================
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isMobile = useIsMobile();

  // Tab hand-off between screens (Sept 17, 2026) — see AppNavContext.
  const navIntentRef = useRef(null);
  const appNav = useMemo(() => ({
    goTo: (nextTab, intent = null) => { navIntentRef.current = intent; setTab(nextTab); },
    readIntent: (kind) => { const i = navIntentRef.current; return i && i.kind === kind ? i : null; },
    clearIntent: (kind) => { if (navIntentRef.current && navIntentRef.current.kind === kind) navIntentRef.current = null; },
  }), [setTab]);

  // Close mobile drawer when tab changes (covers programmatic tab changes)
  useEffect(() => {
    setMobileNavOpen(false);
  }, [tab]);

  return (
    <SubscriptionContext.Provider value={{ subscriptionStatus, subscriptionTier, isReadOnly, isExpired, refreshSubscription }}>
    <OrganizerRefreshContext.Provider value={{ refreshOrganization: refreshOrganization || (() => {}) }}>
    <CourseContext.Provider value={{ courses, loading: loadingCourses, refreshCourses }}>
      <GroupsContext.Provider value={{ groups, loading: loadingGroups, addGroup, updateGroup, deleteGroup }}>
        <MobileContext.Provider value={{ isMobile }}>
        <AppNavContext.Provider value={appNav}>
        <ToastProvider>
          <div style={isMobile
            ? { padding: 0, maxWidth: '100%', margin: 0, minHeight: '100vh' }
            : { padding: 24, maxWidth: 1100, margin: "0 auto", minHeight: "100vh" }
          }>
            {/* MOBILE HEADER (Phase 1B) — Only renders when isMobile is true */}
            {isMobile && (
              <div style={{
                background: "linear-gradient(135deg, #2d6a4f, #1b4332)",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "sticky",
                top: 0,
                zIndex: 50,
                boxShadow: "0 2px 8px rgba(45, 106, 79, 0.2)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 36,
                    height: 36,
                    background: "#fff",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 4,
                    flexShrink: 0
                  }}>
                    <img src="/logo.png" alt="Tee Time Organizer" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#fff", letterSpacing: '0.3px' }}>
                    {deskSession ? 'EVENT DESK' : 'TEE TIME ORGANIZER'}
                  </div>
                </div>
                <button
                  onClick={() => setMobileNavOpen(true)}
                  aria-label="Open menu"
                  style={{
                    background: "rgba(255,255,255,0.18)",
                    border: "none",
                    color: "#fff",
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    fontSize: 22,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0
                  }}
                >
                  ☰
                </button>
              </div>
            )}

            {/* MOBILE SECTION TITLE — shows current tab name below mobile header */}
            {isMobile && <MobileSectionTitle tab={tab} desk={deskSession} />}

            {/* MOBILE NAV DRAWER — slides in from right when hamburger tapped */}
            {isMobile && (
              <MobileNavDrawer
                open={mobileNavOpen}
                onClose={() => setMobileNavOpen(false)}
                tab={tab}
                setTab={setTab}
                onLogout={onLogout}
                user={user}
                organization={organization}
                organizations={organizations}
                switchOrganization={switchOrganization}
                desk={deskSession}
              />
            )}

            {/* Main Header - Logo, Title, Nav, Logout — DESKTOP ONLY */}
            {!isMobile && (
            <div style={{ background: "linear-gradient(135deg, #2d6a4f, #1b4332)", padding: "12px 24px", borderRadius: "12px 12px 0 0", boxShadow: "0 4px 12px rgba(45, 106, 79, 0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 56, height: 56, background: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", padding: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                    <img src="/logo.png" alt="Tee Time Organizer" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 22, color: "#fff" }}>{deskSession ? 'EVENT DESK' : 'TEE TIME ORGANIZER'}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {(deskSession ? [
                    { key: "organizer", label: "🗓 Tee Time" },
                    { key: "pairings", label: "⛳ Pairings" },
                    { key: "deskscoring", label: "🏌️ Scoring" },
                    { key: "print", label: "🖨️ Print" },
                  ] : [
                    { key: "organizer", label: "🗓 Tee Times" },
                    { key: "pairings", label: "⛳ Pairings" },
                    { key: "print", label: "🖨️ Print" },
                    { key: "tools", label: "🛠️ Tools" },
                    { key: "admin", label: "⚙️ Admin" },
                    { key: "settings", label: "🏢 Settings" },
                  ]).map(item => (
                    <button
                      key={item.key}
                      onClick={() => setTab(item.key)}
                      style={{
                        padding: "7px 14px",
                        borderRadius: 20,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        border: tab === item.key ? "2px solid #fff" : "2px solid rgba(255,255,255,0.25)",
                        background: tab === item.key ? "#fff" : "rgba(255,255,255,0.15)",
                        color: tab === item.key ? "#2d6a4f" : "#fff",
                        transition: "all 0.15s ease"
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                  <button
                    onClick={onLogout}
                    style={{
                      padding: "7px 14px",
                      borderRadius: 20,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      border: "2px solid rgba(255,255,255,0.25)",
                      background: "rgba(255,255,255,0.15)",
                      color: "#fff",
                      transition: "all 0.15s ease"
                    }}
                  >
                    {deskSession ? '🚪 Leave desk' : '🚪 Logout'}
                  </button>
                </div>
              </div>
            </div>
            )}
            {/* Sub-header Bar - Organization Name/Switcher + Email — DESKTOP ONLY */}
            {!isMobile && (
            <div style={{ 
              background: '#f8faf9', 
              padding: '8px 24px', 
              borderBottom: '1px solid #e5e7eb',
              borderRadius: '0 0 12px 12px',
              marginBottom: 24,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#6b7280' }}>{deskSession ? '🗝️' : '🏢'}</span>
                {deskSession ? (
                  <span style={{ fontSize: 14, color: '#1f2937' }} data-testid="desk-banner">
                    <strong>{deskSession.event_name}</strong>
                    <span style={{ color: '#6b7280' }}> · event desk{deskSession.org_name ? ` for ${deskSession.org_name}` : ''} · {deskSession.name}</span>
                  </span>
                ) : organizations && organizations.length > 1 ? (
                  <div data-org-switcher style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                      disabled={isSwitching}
                      style={{
                        background: 'none',
                        border: '1px solid #d1d5db',
                        color: '#1f2937',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        padding: '4px 10px',
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        opacity: isSwitching ? 0.6 : 1
                      }}
                    >
                      {isSwitching ? '⏳ Switching...' : (organization?.name || 'Select Organization')}
                      <span style={{ fontSize: 10, color: '#9ca3af' }}>▼</span>
                    </button>
                    {showOrgDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: 4,
                        background: '#fff',
                        borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        minWidth: 280,
                        zIndex: 9999,
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ padding: '8px 12px', fontSize: 11, color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Switch Organization
                        </div>
                        {organizations.map(org => (
                          <button
                            key={org.id}
                            onClick={() => handleSwitchOrg(org.id)}
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '10px 12px',
                              border: 'none',
                              background: org.id === organization?.id ? '#f0fdf4' : '#fff',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: 13,
                              color: '#1f2937',
                              borderBottom: '1px solid #f3f4f6',
                              fontWeight: org.id === organization?.id ? 600 : 400
                            }}
                            onMouseEnter={(e) => { if (org.id !== organization?.id) e.target.style.background = '#f9fafb'; }}
                            onMouseLeave={(e) => { if (org.id !== organization?.id) e.target.style.background = '#fff'; }}
                          >
                            {org.id === organization?.id ? '✓ ' : ''}{org.name}
                            <span style={{ display: 'block', fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{org.role}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{organization?.name || 'Manage your golf outings with ease'}</span>
                )}
              </div>
              {user?.email && (
                <span style={{ fontSize: 13, color: '#6b7280' }}>{user.email}</span>
              )}
            </div>
            )}
            {/* Main content area — mobile-aware padding */}
            {/* Phase 2: extra bottom padding on Tee Times tab so cards aren't hidden behind sticky MobileBottomBar */}
            <div style={isMobile
              ? { padding: 12, paddingBottom: tab === 'organizer' ? 100 : 12 }
              : {}
            }>
            {(loadingCourses || loadingGroups) ? (
              <div style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>Loading...</div>
            ) : (
              <>
                {tab === "organizer" && <OrganizerDashboard />}
                {tab === "pairings" && <PairingsView />}
                {tab === "print" && <PrintCenter />}
                {!deskSession && tab === "tools" && <ToolsView />}
                {!deskSession && tab === "admin" && <SettingsMenu />}
                {!deskSession && tab === "settings" && <OrganizationSettings />}
                {deskSession && tab === "deskscoring" && <PostRoundScoring />}
              </>
            )}
            </div>

            {/* MOBILE BOTTOM BAR (Phase 2) — only on Tee Times tab on mobile */}
            {isMobile && tab === 'organizer' && (
              <MobileBottomBar setTab={setTab} desk={!!deskSession} />
            )}
            
          </div>
        </ToastProvider>
        </AppNavContext.Provider>
        </MobileContext.Provider>
      </GroupsContext.Provider>
    </CourseContext.Provider>
    </OrganizerRefreshContext.Provider>
    </SubscriptionContext.Provider>
  );
}

// Add spinner animation CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  /* Scoring Enhancement A — celebration banner slide-in (May 2026) */
  @keyframes slideDownFade {
    0% { transform: translate(-50%, -120%); opacity: 0; }
    100% { transform: translate(-50%, 0); opacity: 1; }
  }
`;
if (!document.head.querySelector('style[data-spinner]')) {
  style.setAttribute('data-spinner', 'true');
  document.head.appendChild(style);
}

export default App;
